// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
import { fiModuleTips } from './fi-extras.js';
// Sprint 62.57.0 Slice E — R-57: the English, for structural parity.
import { enServiceCertificates, enVendorServiceCertificate } from './en-extras.js';
import { enVehicleMonitoring, enRecordLinks } from './en-extras.js';
export const fi = {
    ai_governance_pages: {
        nav: {
            inventory: 'AI inventory reviews',
            payment: 'Payment authorities',
            interventions: 'Interventions',
            complaints: 'AI complaints',
            framework: 'Framework record and three lines',
            board: 'Board report: AI figures',
        },
        common: {
            loading: 'Loading…',
            loadFailed: 'This could not be read.',
            retry: 'Try again',
            retrySr: 'read it again',
            notRecorded: 'Not recorded',
            dueNotInForce: 'Not counted: the cadence is not yet in force',
            none: 'None',
            choose: 'Choose…',
            cancel: 'Cancel',
            close: 'Close',
            saving: 'Saving…',
            saveFailed: 'The record was not saved.',
            requiredMark: '(required)',
            missingHeading: 'The record cannot be saved yet. These are missing:',
            directoryFailed: 'The list of people could not be read.',
            directoryPartial: 'Showing {shown} people. The list is not complete.',
            yes: 'Yes',
            no: 'No',
            readOnly: 'Read only',
            noAnswer: 'The server did not answer.',
            listTruncated: 'Showing {shown} of {total} rows. The list is not complete.',
            awaitingOtherApprover: 'Awaiting approval by another person',
            obligationsRetrySr: 'read the dated obligations again',
            systemDatesRetrySr: "read this system's dated obligations again",
            actions: 'Actions',
        },
        states: {
            incomplete: 'Incomplete',
            complete: 'Complete',
            not_recorded: 'Not recorded',
            not_evidenced: 'Not evidenced',
            pending_approval: 'Awaiting a second approval',
            expired: 'Expired',
            matches: 'Matches',
            mismatch: 'Mismatch',
            requested: 'Requested',
            confirmed: 'Confirmed',
            recorded: 'Recorded',
            performed: 'Performed',
            not_mandatory: 'Not mandatory',
            not_applicable: 'Not applicable',
            open: 'Open',
            closed: 'Closed',
            routed: 'Routed',
            stale: 'Stale',
            signed: 'Signed',
            current: 'Current',
            overdue: 'Overdue',
            due: 'Due',
            cadence_not_declared: 'No cadence declared',
            not_yet_in_force: 'Not yet in force',
            not_judged: 'Not judged',
            not_required: 'Not required',
            not_trained: 'Not trained',
            completed: 'Completed',
            unsettled: 'Unsettled',
            present: 'Present',
            absent: 'Absent',
            pending: 'Pending',
            ready: 'Ready',
            active: 'Active',
            suspended: 'Suspended',
            revoked: 'Revoked',
            approved: 'Approved',
            reopened: 'Reopened',
            basis_not_retained: 'Basis not retained',
            reason_notified_basis_recorded: 'Reason notified; basis held',
            needs_evidence: 'Needs evidence',
            evidenced: 'Evidenced',
            not_started: 'Not started',
            in_progress: 'In progress',
            evidence_captured: 'Evidence captured',
            answered: 'Answered',
        },
        missing: {
            risk_tier: 'An approved risk level from a reviewed classification is needed.',
            impact_assessment: 'An approved impact assessment is needed for a high-risk system.',
            validation: 'A validation signed for this version is needed.',
            validation_findings: 'Every finding on this version’s validation must be closed on a re-test.',
            security_tests: 'Every attack category needs a record, and none of poisoning, evasion or extraction may be open for a high-risk version.',
            rollback_plan: 'A rollback plan naming an earlier version of this system is needed.',
            senior_management_approval: 'A high-risk go-live needs the senior management body that approved it, and the date.',
            documents_retained_for_audit: 'The documents kept for audit',
            data_disposition: 'How the data was disposed of or isolated',
            model_disposition: 'How the models were disposed of or isolated',
            configuration_disposition: 'How the configuration was disposed of or isolated',
        },
        revalidation: {
            revalidation_required: 'Re-validation required',
            revalidation_not_required: 'Re-validation not required',
        },
        separation: {
            required: 'Required: a different person re-tests',
            not_required: 'Not required: basis stated',
        },
        securityStates: {
            not_mandatory: 'Not mandatory',
            performed: 'Performed',
            not_applicable: 'Not applicable',
            open: 'Open',
        },
        attackCategories: {
            privacy_breach: 'Privacy breach',
            data_poisoning: 'Data poisoning',
            model_poisoning: 'Model poisoning',
            evasion: 'Evasion',
            model_extraction: 'Model extraction',
            prompt_injection: 'Prompt injection',
            hallucination: 'Hallucination',
            bias_in_generated_output: 'Bias in generated output',
        },
        validationAreas: {
            data_fit: 'Fit of training and test data',
            algorithm_soundness: 'Soundness of algorithm and architecture',
            performance_accuracy: 'Performance and accuracy',
            explainability: 'Explainability',
            fairness_bias_control: 'Fairness and bias control',
            stability: 'Stability',
            third_party_purpose_scope_risk: 'Third party: purpose, scope and risk',
            third_party_black_box_on_own_data: 'Third party: black-box tests on the bank data',
            third_party_output_under_operating_conditions: 'Third party: output under operating conditions',
            third_party_provider_documentation: 'Third party: provider documentation',
            third_party_human_controls_and_monitoring: 'Third party: human controls and monitoring',
        },
        versions: {
            heading: 'Versions, change control and rollback',
            description: 'Each change writes a version row; the prior value is kept. A change after release needs a re-validation decision by a named person.',
            add: 'Add a version',
            addTitle: 'Add a version',
            addSubmit: 'Save version',
            empty: 'No version is recorded yet.',
            colSequence: 'No.',
            colKind: 'Kind',
            colModel: 'Model version',
            colApplied: 'Applied',
            colChanged: 'Changed or stopped',
            colConfig: 'Configuration',
            colTests: 'Test result',
            colRollbackPlan: 'Rollback plan',
            colRestored: 'Version restored',
            colRestoredAt: 'Restored at',
            colReason: 'Reason',
            kind: { baseline: 'Baseline', change: 'Change', rollback: 'Rollback' },
            planRecorded: 'Recorded',
            planNotRecorded: 'Not recorded',
            stillInUse: 'In use',
            recordChange: 'Record a change',
            setPlan: 'Set rollback plan',
            fModel: 'Model version',
            fScope: 'Scope affected',
            fPrompt: 'Prompt or instruction set reference',
            fReferenceData: 'Reference-data reference',
            fIntegration: 'Integration reference',
            fConfig: 'Configuration reference',
            fTests: 'Test result reference',
            fAutonomy: 'Autonomy setting',
            fListedDevelopers: 'Listed developers of this version',
            fRevalidation: 'Re-validation decision',
            fRevalidationReason: 'Reason for the decision',
            revalidationRequiredHint: 'Required when the system is in production.',
            revalidationOptionalHint: 'Needed only for a change in production.',
            changeDecisionHint: 'A change needs a re-validation decision and a reason.',
            changeTitle: 'Record a change',
            changeSubmit: 'Save change',
            fChangeSummary: 'What changed',
            fChangedFields: 'Fields changed',
            changedField: {
                model_version: 'Model version',
                prompt_reference: 'Prompt reference',
                reference_data_reference: 'Reference-data reference',
                integration_reference: 'Integration reference',
                autonomy_level: 'Autonomy setting',
                scope_affected: 'Scope affected',
                configuration_reference: 'Configuration reference',
                test_result_reference: 'Test result reference',
                listed_developer_user_ids: 'Listed developers',
            },
            planTitle: 'Rollback plan',
            planSubmit: 'Save plan',
            fLastGoodVersion: 'Last known-good version',
            fSteps: 'Steps to restore',
            fPlanOwner: 'Owner of the plan',
            fLastRehearsal: 'Date of the last rehearsal',
            rollbackHeading: 'Rollback events',
            rollbackEmpty: 'No rollback is recorded.',
            recordRollback: 'Record a rollback',
            rollbackTitle: 'Record a rollback',
            rollbackSubmit: 'Save rollback',
            fRestoredVersion: 'Version restored',
            fRollbackReason: 'Reason',
        },
        security: {
            fNotMandatory: 'Reason not mandatory',
            notMandatoryHint: 'Required when the state is not mandatory. Only poisoning, evasion and extraction may be recorded so, and never for a high-risk system.',
            findingsOpen: 'Findings',
            findingsTitle: 'Security findings',
            findingsDescription: 'A finding closes only when it has a severity, a finding owner, a due date and a change record, and a re-test by someone other than the person who fixed it.',
            findingsEmpty: 'No finding is recorded for this category.',
            addFinding: 'Open a finding',
            fDescription: 'What the finding is',
            fSeverity: 'Severity',
            fFindingOwner: 'Finding owner',
            fDueDate: 'Due date',
            fChangeRecord: 'Change record',
            noChangeRecord: 'No change record yet',
            recordFix: 'Record the fix',
            closeOnRetest: 'Close on re-test',
            setDetails: 'Set severity, owner, due date and change record',
            fFixedBy: 'Fixed by',
            fRetestDate: 'Date of the re-test',
            fRetester: 'Re-tested by',
            fRetestResult: 'Re-test result',
            findingSubmit: 'Save finding',
            findingNotClosed: 'Not closed',
            findingStatus: {
                open: 'Open',
                fixed: 'Fixed',
                closed: 'Closed',
            },
            severities: {
                low: 'Low',
                medium: 'Medium',
                high: 'High',
                critical: 'Critical',
            },
            periodic: {
                heading: 'Periodic security testing',
                description: "The period and the last test date are the bank's own and are entered here. The platform derives the next test date and reads it. It runs no test.",
                nextDate: 'Next test date: {date}',
                noNextDate: 'No schedule is set for this version.',
                set: 'Set the schedule',
                formTitle: 'Periodic security testing schedule',
                fPeriod: 'Period in months',
                periodHint: 'A whole number of months, from 1 to 120.',
                fLastTest: 'Date of the last test',
                submit: 'Save schedule',
                states: {
                    not_scheduled: 'Not scheduled',
                    scheduled: 'Scheduled',
                    due: 'Due',
                    overdue: 'Overdue',
                },
            },
            heading: 'Security testing by attack category',
            description: 'One record per attack category for the current version. A blank category is shown as not recorded. The platform runs no test.',
            empty: 'No security test is recorded.',
            blank: 'Not recorded',
            colCategory: 'Attack category',
            colState: 'State',
            colOwner: 'Owner',
            colTest: 'Test',
            colFindings: 'Findings',
            findingCount: '{open} open of {total}',
            record: 'Record',
            update: 'Update',
            formTitle: 'Security test',
            submit: 'Save test record',
            fState: 'State',
            fNotApplicable: 'Reason not applicable',
            naHint: 'Required when the state is not applicable.',
            fMethod: 'Method',
            fTester: 'Tester',
            fTestDate: 'Date of the test',
            fResult: 'Result',
            fArtefact: 'Artefact reference',
            fPersonalData: 'Does the artefact hold personal data?',
            fOwner: 'Owner of this category',
            fSeparation: 'Separation of duties on a re-test',
            fSeparationBasis: 'Basis for not separating',
            separationBasisHint: 'Required when separation is not required.',
        },
        validation: {
            heading: 'Independent validation',
            description: 'A validator independent of the development, deployment, operation and supplier signs the validation. The platform records the attestation and does not judge it.',
            add: 'Open a validation',
            empty: 'No validation is signed.',
            colScope: 'Scope',
            colValidator: 'Validator',
            colUnit: 'Validating unit',
            colSigned: 'Signed',
            colTests: 'Tests',
            colFindings: 'Findings',
            closeFinding: 'Close finding',
            colStatus: 'Status',
            signNow: 'Sign off',
            signNowTitle: 'Sign off this validation',
            signNowMessage: 'Signing records the sign-off against the version under test and cannot be undone. The validator’s independence statement is recorded as stated.',
            signNowSubmit: 'Sign off',
            respond: 'Respond',
            respondTitle: 'Owner response',
            fResponse: 'Response',
            respondSubmit: 'Save response',
            responseLabel: 'Owner response',
            addFinding: 'Open a finding',
            signTitle: 'Open a validation',
            signSubmit: 'Open validation',
            fScope: 'Scope of the validation',
            fValidator: 'Validator',
            fUnit: 'Validating unit',
            fIndependence: 'Independence statement',
            independenceHint: 'The validator states how the unit is independent of development, deployment, operation and the supplier.',
            testsLegend: 'Test',
            fTestArea: 'Test area',
            fTestName: 'Test name',
            fTestMethod: 'Method',
            fTestData: 'Data',
            fTestResult: 'Result',
            confirmTitle: 'Confirm the validation',
            confirmMessage: 'Opening records this validation against the version under test. It is not signed until the validator signs it, and signing needs at least one test on the record.',
            findingTitle: 'Finding',
            openSubmit: 'Open finding',
            closeSubmit: 'Close finding',
            fFindingDescription: 'Description',
            fFindingOwner: 'Owner of the finding',
            fDueDate: 'Due date',
            fRetestDate: 'Date of the re-test',
            fRetestResult: 'Result of the re-test',
            retestHint: 'A finding closes only on a re-test dated after the fix.',
        },
        release: {
            heading: 'Release approval',
            description: 'A release is refused until each precondition is recorded. The approver cannot be the owner, and a listed developer cannot approve the release of their own version.',
            add: 'Approve a release',
            empty: 'No release is approved.',
            colStatus: 'Status',
            colGoLive: 'Go-live date',
            colLabel: 'Approval timing',
            colApprover: 'Approved by',
            colApproved: 'Approved on',
            colStale: 'Stale because',
            beforeGoLive: 'Before go-live',
            afterTheFact: 'After the fact',
            staleBecause: 'Changed:',
            formTitle: 'Approve a release',
            formDescription: 'The seven preconditions are checked when you save. A refused release names each one that is missing.',
            checklistLegend: 'Preconditions',
            checklistUnmet: 'Missing',
            checklistNotChecked: 'Checked on save',
            preconditions: {
                risk_tier: 'A risk level from an approved classification run',
                impact_assessment: 'An approved impact assessment, for a high-risk system',
                validation: 'A validation signed for this version',
                validation_findings: 'Every finding on the signed validation is closed on a re-test',
                security_tests: 'A security-test record for every category, with no open poisoning, evasion or extraction category',
                rollback_plan: 'A rollback plan naming an earlier version',
                senior_management_approval: 'For a high-risk go-live, the senior management body and the decision date',
            },
            rehearsalLabel: 'Rollback rehearsal',
            rehearsalNotDone: 'Not rehearsed',
            rehearsalDone: 'Rehearsed on',
            openFindingsLabel: 'Open validation findings',
            gateUnreadable: 'The gate for this version could not be read. Choose the version again to retry.',
            fVersion: 'Version to release',
            fGoLive: 'Go-live date',
            fSeniorBody: 'Senior management body',
            fSeniorDate: 'Date of the senior management decision',
            seniorHint: 'Required for a high-risk go-live.',
            submit: 'Approve release',
            confirmTitle: 'Approve this release',
            confirmMessage: 'This records the release approval for the chosen version. Approving does not deploy the system; the release is made in the bank’s own systems.',
        },
        impact: {
            heading: 'Impact assessment',
            description: 'The five contents of the assessment, the fairness section and the approval. A change of function, data or operating conditions reopens an approved assessment.',
            add: 'Record an impact assessment',
            empty: 'No impact assessment is recorded.',
            colStatus: 'Status',
            colPurpose: 'Purpose',
            colDpia: 'DPIA or reason',
            colFairness: 'Fairness section',
            colApproval: 'Approval',
            approve: 'Approve',
            fairnessAction: 'Fairness section',
            fairnessRecorded: 'Recorded',
            fairnessNotRecorded: 'Not recorded',
            addTitle: 'Record an impact assessment',
            addSubmit: 'Save assessment',
            fPurpose: 'Purpose',
            fScope: 'Scope',
            fMechanism: 'How the system works',
            fOversight: 'How a person oversees and intervenes',
            oversightHint: 'Describe the human oversight and the means of intervention.',
            fFairness: 'Fairness risk',
            fTransparency: 'Transparency risk',
            fExplainability: 'Explainability risk',
            fDiscrimination: 'Risk of discrimination against customers',
            fDpia: 'DPIA record identifier',
            dpiaHint: 'The identifier of the DPIA record. The DPIA text is not copied.',
            fPersonalDataNotApplicable: 'Reason the personal-data assessment does not apply',
            personalDataHint: 'Give a DPIA identifier, or a reason it does not apply.',
            fControls: 'Controls',
            fMonitoring: 'Monitoring plan',
            fIncident: 'Incident response',
            fairnessTitle: 'Fairness section',
            fairnessSubmit: 'Save fairness section',
            fFairnessAssessment: 'Fairness assessment',
            fBiasControls: 'Bias controls',
            fVulnerable: 'Vulnerable groups',
        },
        inventory: {
            heading: 'Annual inventory review',
            description: 'The annual review of this system for the year shown. A profile not confirmed by its due date reads overdue.',
            confirm: 'Confirm review',
            empty: 'This system has no review for this year.',
            colPeriod: 'Period',
            colAnchor: 'Anchor',
            colDue: 'Due',
            colReading: 'Reading',
            anchorKind: {
                first_recorded: 'First recorded',
                last_confirmation: 'Last confirmation',
                review_duty_effective: 'Review duty took effect',
            },
            formTitle: 'Confirm the annual review',
            submit: 'Confirm review',
            fOutcome: 'Outcome',
            outcome: { confirmed: 'Confirmed without change', changed: 'Changed' },
            fChangeSummary: 'What changed',
        },
        components: {
            heading: 'AI component register',
            description: 'Libraries, models, datasets and dependencies, each row with its integrity check. A row missing a field reads incomplete.',
            empty: 'No component register is recorded.',
            noRows: 'The register lists no component yet.',
            record: 'Record the register',
            integrity: 'Record an integrity check',
            advisory: 'Record an advisory',
            supplyReview: 'Record a supply-chain review',
            incompleteCount: '{count} rows are incomplete.',
            supplyReviewReading: 'Supply-chain review',
            colKind: 'Kind',
            colName: 'Name',
            colSupplier: 'Supplier or developer',
            colVersion: 'Version',
            colIntegrity: 'Integrity check',
            colReading: 'Reading',
            missingFields: 'Missing',
            integrityHeading: 'Integrity checks',
            integrityEmpty: 'No integrity check is recorded.',
            advisoryHeading: 'Advisories',
            advisoryEmpty: 'No advisory is recorded.',
            kind: { library: 'Library', model: 'Model', dataset: 'Dataset', dependency: 'Dependency' },
            formTitle: 'Record the component register',
            formDescription: 'Saving appends a new snapshot. The earlier register stays readable.',
            submit: 'Save',
            rowLegend: 'Component {n}',
            fKind: 'Kind',
            fName: 'Name',
            fSupplier: 'Supplier or developer',
            fVersion: 'Version',
            fLicence: 'Licence',
            fCheck: 'Integrity check value',
            fOrigin: 'Origin',
            fAffected: 'Attack categories this component affects',
            addRow: 'Add a component',
            removeRow: 'Remove',
            integrityFormTitle: 'Record an integrity check',
            fComponent: 'Component',
            fMethod: 'Method of check',
            fCheckValue: 'Check value',
            fCheckedOn: 'Date checked',
            advisoryFormTitle: 'Record an advisory',
            fAdvisoryRef: 'Advisory reference',
            fReceivedOn: 'Date received',
            fRiskDecision: 'Risk decision',
            reviewFormTitle: 'Record a supply-chain review',
            fReviewSummary: 'Summary of the review',
        },
        provenance: {
            heading: 'Data provenance and operational logs',
            description: 'The sources of training, test and operating data, the quality and leakage references, and the log retention with its citation.',
            record: 'Record provenance',
            empty: 'No provenance is recorded.',
            sources: 'Sources',
            thirdParty: 'Third party',
            synthetic: 'Synthetic data',
            qualityPolicy: 'Data-quality policy',
            leakageControl: 'Leakage control',
            retention: 'Log retention',
            citation: 'Citation',
            citationMissing: 'No citation recorded',
            revision: 'Revision',
            dataUse: { training: 'Training', test: 'Test', operating: 'Operating' },
            formTitle: 'Record data provenance',
            submit: 'Save provenance',
            fDataUse: 'Data use',
            fSource: 'Source of the data',
            fLawfulBasis: 'Lawful-basis statement',
            fThirdParty: 'Supplied by a third party',
            fSupplier: 'Supplier name',
            fWarranty: 'Warranty of source',
            fSharing: 'Right to share',
            fSynthetic: 'Uses synthetic data',
            fSyntheticAssessment: 'Synthetic-data assessment',
            fInputLog: 'Input-processing log reference',
            fQualityPolicy: 'Data-quality policy reference',
            fLeakage: 'Leakage control reference',
            fRetention: 'Log retention period',
            retentionHint: 'The period the bank sets for operational logs.',
            fCitation: 'Citation of the rule that sets the period',
            citationHint: 'Required with a retention period.',
        },
        decisions: {
            heading: 'Decision records',
            description: 'For each automated decision that affects an obligation, a financial position or access to a product: the basis and key factors, and the explanation given.',
            add: 'Record a decision',
            empty: 'No decision is recorded.',
            colDate: 'Decision date',
            colRef: 'Reference',
            colEffect: 'Effect',
            colBasis: 'Basis',
            colExplanation: 'Explanation given',
            colVersion: 'Version on the decision date',
            versionLegacy: 'Version current when recorded',
            versionNone: 'No version was in force on the decision date',
            basisRuleHeading: "The platform's reading",
            basisRule: "ComplianceOne's reading of Điều 20 khoản 5: for every automated decision that directly affects a customer's legal liability, financial obligations, or access to or use of a product or service, the basis and the main factors are kept, whatever the decision's purpose. For a decision made to prevent fraud, scams, money laundering or terrorism financing, or to ensure cyber security, your organisation need only tell the customer a reason suited to its risk review and to keeping the customer's assets safe; it may tell the customer more. No explanation may disclose source code, detailed algorithms, parameter sets, or business or technology secrets. This is the platform's reading of the text, not a legal review.",
            effect: {
                legal_or_financial_obligation: 'Legal liability or financial obligation',
                customer_access: 'Access to a product or service',
            },
            category: {
                standard: 'Standard',
                fraud: 'Fraud',
                scam: 'Scams',
                money_laundering: 'Money laundering',
                terrorism_financing: 'Terrorism financing',
                cyber_security: 'Cyber-security',
            },
            formTitle: 'Record a decision',
            submit: 'Save decision',
            fDate: 'Decision date',
            fRef: 'Decision reference',
            fEffect: 'Effect',
            fCategory: 'Decision category',
            categoryHint: 'For fraud, scams, money laundering, terrorism financing or cyber security, the institution need only tell the customer a reason; the basis and key factors are still stored.',
            fBasis: 'Basis for the decision',
            basisHint: 'Leave empty only if the basis was not kept; the record then shows basis not retained.',
            fKeyFactors: 'Key factors',
            factorsHint: 'One factor per line.',
            fExplanation: 'Explanation given to the customer',
            fDelivered: 'Date delivered',
            fChannel: 'Channel',
            fStatedReason: 'Stated reason',
            restrictedHint: 'What the customer is told. For this category the institution need only tell the customer a reason; if it told the customer more, record that explanation too.',
            restrictedBasisHint: 'Stored, not told to the customer. Leave empty only if the basis was not kept; the record then shows basis not retained.',
        },
        disclosures: {
            heading: 'AI disclosures in use',
            description: 'The text in use on the date shown, per kind, product and channel. A new text is recorded beside the old one; it never replaces it.',
            add: 'Record a disclosure',
            notRecorded: 'No disclosure is recorded for this date.',
            colKind: 'Kind',
            colProduct: 'Product',
            colChannel: 'Channel',
            colVersion: 'Version',
            colGoLive: 'Go-live date',
            colText: 'Text',
            kind: {
                pre_service_information: 'Information before the service',
                ai_interaction_statement: 'Statement that the customer is dealing with AI',
                internal_user_information: 'Information for internal users',
            },
            formTitle: 'Record a disclosure',
            submit: 'Save disclosure',
            fOn: 'Date in use',
            fKind: 'Kind',
            fProduct: 'Product',
            fChannel: 'Channel',
            fGoLive: 'Go-live date',
            fText: 'Text in use',
            textHint: 'Enter or paste the text. The platform does not write it.',
            fPurpose: 'Purpose',
            fScope: 'Scope',
            fLimits: 'Limits',
            fGuidance: 'Interpretation guidance',
            fRoute: 'Route to ask for human intervention',
            fFeedback: 'Feedback channel',
            fSameDayReason: 'Reason for a second version on the same day',
            sameDayHint: 'Required only when another version of this text goes live on the same day.',
            noChannels: 'Record a channel before recording a disclosure.',
            pendingHeading: 'Versions awaiting a second person',
            pendingDescription: 'A version takes effect only when a second person approves it. The person who recorded it cannot approve it.',
            pendingEmpty: 'No version is awaiting approval.',
            inUseHeading: 'In use on the date shown',
            approve: 'Approve version',
        },
        payment: {
            heading: 'Automated payment authority',
            description: 'An authority per customer agreement: the eleven elements, the limits against the ceiling the installed framework declares, and suspension or revocation. The platform does not execute or stop a payment.',
            add: 'Record an authority',
            empty: 'No payment authority is recorded for this system.',
            colState: 'State',
            colAgreement: 'Agreement',
            colAutonomy: 'Autonomy on the system',
            colPerDay: 'Per-day limit',
            colCeiling: 'Ceiling source',
            colMissing: 'Missing elements',
            colRelief: 'Confirmation relief',
            relief: {
                heading: 'Transaction confirmation relief',
                open: 'Confirmation relief',
                openSr: 'for agreement {agreement}',
                close: 'Close the relief',
                intro: 'What the installed framework declares about relief from transaction confirmation for payments the AI system executes within this agreement, and which of its conditions are recorded on this authority.',
                notApplied: 'The platform records what a person states. It does not apply the relief, confirm, count or classify a transaction, or compute any total the referenced instrument defines.',
                statedBy: 'Stated by {framework}, {provision}.',
                refersTo: 'Refers to',
                amendedBy: 'as amended by {list}',
                lifts: 'Confirmation it lifts',
                quantities: 'Totals the value is left out of',
                annexVersion: 'Annex versions',
                conditionsHeading: 'Conditions',
                colCondition: 'Condition',
                colBasis: 'Basis',
                colRecorded: 'On this authority',
                colMissing: 'Still to record',
                recorded: 'Recorded',
                notRecorded: 'Not recorded',
                effectHeading: 'Effect',
                notLiftedHeading: 'What it does not lift',
                reachHeading: 'Reach',
                namedSection: '{section}: {framework}, {provision}',
                reading: {
                    conditions_not_recorded: 'Conditions not recorded',
                    conditions_recorded: 'Conditions recorded',
                    relied_on: 'Relied on',
                    relied_on_conditions_no_longer_recorded: 'Relied on; conditions no longer recorded',
                },
                reliance: 'Relied on by {who} on {when}. Reason: {reason}',
                rely: 'Record that the unit relies on this relief',
                relyDisabled: 'Reliance can be recorded once every condition is recorded on this authority.',
                relyTitle: 'Record reliance on the relief',
                relyDescription: 'Records who relies on the relief for this agreement, when and why. It cannot be recorded while no approved terms are in force, by the person who wrote the terms in force, or by anyone who wrote an approved version that set or changed either of the two statements. The platform does not apply the relief.',
                relySubmit: 'Record reliance',
                fReason: 'Why the unit relies on the relief',
                relied: 'Reliance on the relief is recorded.',
                statements: 'Record the statements',
                statementsTitle: 'Record the statements the relief rests on',
                statementsDescription: 'Saved as a change to the authority. It takes effect when a second person approves it.',
                statementsSubmit: 'Save as a change',
                statementsSaved: 'The change is saved. A second person must approve it.',
                statementsPending: 'The statements can be recorded once a second person approves the change pending on this authority.',
                fAiExecution: 'How the unit tells a transaction the system executed',
                aiExecutionHint: 'The reference of the marker, field or log that shows a transaction was executed by the system and not by a person.',
                fAutomationConditions: 'Statement that the conditions for automation hold',
                automationConditionsHint: 'A person states that each condition the instrument sets for automating this system holds.',
                evidence: {
                    ai_execution_reference: 'Statement of how AI-executed transactions are identified',
                    automation_conditions_statement: 'Statement that the conditions for automation hold',
                    terms_approved: 'Terms approved by a second person',
                    ceiling_checked: 'Per-day limit checked against the declared ceiling',
                    authority_active: 'Authority active (not suspended, revoked or expired)',
                },
                noneDeclared: 'No installed framework declares a relief from transaction confirmation for payment authorities.',
                unreadable: 'A confirmation relief an installed framework declares could not be read.',
                notDeclared: 'Not declared',
                listReferenceOnly: 'Declared by a framework installed for reference only',
                listNotOperational: 'Declared by a framework that is not operational',
                listUnreadable: 'Declared, but could not be read',
                activationUpcoming: 'The framework that declares this relief is not yet in force. The relief applies from the date it comes into force; reliance can be recorded before then.',
            },
            scopeHeading: 'Scope flagged for legal review',
            scopeIntro: 'An organisation the installed framework names in one provision and leaves out of its scope provision. The platform treats it as neither covered nor excluded.',
            scopeNamed: 'Named in {named_in}; not named in {absent_from}.',
            scopeStatus: { under_legal_review: 'Flagged for legal review' },
            scopeSource: 'Stated by {framework}',
            scopeUnreadable: 'A scope qualification an installed framework declares could not be read.',
            scopeLoadFailed: 'The scope notice could not be read.',
            scopeRetrySr: 'read the scope notice again',
            recordEvent: 'Record an event',
            approve: 'Approve this change',
            exportHistory: 'Export authority history',
            customerClass: { individual: 'Individual', organisation: 'Organisation' },
            element: {
                customer_class: 'Customer class',
                agreement_reference: 'Agreement reference',
                automated_transaction_scope: 'Scope of automated transactions',
                per_transaction_limit_vnd: 'Per-transaction limit',
                per_day_limit_vnd: 'Per-day limit',
                valid_from: 'Valid from',
                valid_until: 'Valid until',
                suspension_conditions: 'Suspension conditions',
                customer_termination_clause_reference: 'Customer termination clause',
                identity_verification_statement: 'Identity-verification statement',
                pause_or_cancel_tool_reference: 'Customer pause or cancel tool',
            },
            formTitle: 'Record a payment authority',
            formDescription: 'A per-day limit above the ceiling the installed framework declares is refused, and the refusal names that framework and its clause.',
            submit: 'Save authority',
            fCustomerClass: 'Customer class',
            fAgreement: 'Agreement reference (a file or a reference)',
            agreementHint: 'The agreement is recorded by reference.',
            fScope: 'Scope of automated transactions',
            fScopeItem: 'Transaction type',
            scopeHint: 'Add each type, one at a time.',
            addScope: 'Add to the scope',
            removeScope: 'Remove',
            fPerTransaction: 'Per-transaction limit (VND)',
            fPerDay: 'Per-day limit (VND)',
            perDayHint: 'Checked against the ceiling the installed framework declares.',
            fValidFrom: 'Valid from',
            fValidUntil: 'Valid until',
            fSuspension: 'Suspension conditions',
            fTermination: 'Customer termination clause reference',
            fIdentity: 'Identity-verification statement',
            identityHint: 'Entered by the person who checked the verification. The platform does not check biometrics.',
            fPauseTool: 'Customer pause or cancel tool reference',
            fLead: 'Payments lead',
            eventTitle: 'Record an event',
            eventSubmit: 'Save event',
            eventKind: {
                activate: 'Activate',
                suspend: 'Suspend',
                revoke: 'Revoke',
                execution_summary: 'Execution summary',
            },
            fEventKind: 'Event',
            fReportedAt: 'Time reported',
            reportedHint: 'The time the stop was reported.',
            fImplementedAt: 'Time implemented',
            implementedHint: 'Leave empty until the stop is implemented; the state then reads not evidenced.',
            fReason: 'Reason',
            fCustomerRef: 'Customer reference',
            fExecutedOn: 'Date executed',
            fTxCount: 'Number of transactions',
            fLargest: 'Largest transaction (VND)',
            fLargestRef: 'Reference of the largest transaction',
            fDailyTotal: 'Daily total (VND)',
        },
        interventions: {
            heading: 'Human oversight and interventions',
            description: 'Each intervention is recorded with its reason. A pause or stop reads requested until a person enters the confirmed time.',
            add: 'Record an intervention',
            empty: 'No intervention is recorded.',
            colKind: 'Kind',
            colContent: 'Content changed',
            colReason: 'Reason',
            colReading: 'State',
            colRequested: 'Requested at',
            colConfirmed: 'Confirmed at',
            kind: {
                content_change: 'Content change',
                override: 'Override',
                pause: 'Pause',
                stop: 'Stop',
                emergency_stop: 'Emergency stop',
                manual_operation: 'Manual operation',
                resume: 'Resume',
            },
            confirm: 'Enter confirmed time',
            formTitle: 'Record an intervention',
            submit: 'Save intervention',
            fKind: 'Kind',
            fContent: 'Content changed',
            fReason: 'Reason',
            fRequestedAt: 'Time requested',
            requestedHint: 'Required for a pause, stop, emergency stop or manual operation.',
            confirmTitle: 'Enter the confirmed time',
            confirmSubmit: 'Save confirmed time',
            fConfirmedAt: 'Time confirmed in the bank’s system',
            confirmedHint: 'Taken from the bank’s own system; the platform does not read it.',
            alreadyRetired: 'This system is retired.',
            retireHeading: 'Retirement',
            retireSectionDescription: 'Retire this system when it is taken out of service; retiring it needs a decommissioning record.',
            retire: 'Retire this system',
            retireTitle: 'Retire this system',
            retireDescription: 'A retired system needs a decommissioning record: the documents kept for audit, and how the data, models and configuration were disposed of or isolated.',
            retireSubmit: 'Retire system',
            decom: {
                documents_retained_for_audit: 'Documents retained for audit',
                data_disposition: 'Disposition of the data',
                model_disposition: 'Disposition of the models',
                configuration_disposition: 'Disposition of the configuration',
            },
            colChannel: 'Channel',
            colDecision: 'Decision',
            fChannel: 'Channel',
            fDecision: 'Decision identifier',
            decisionHint: 'Required for a content change, an override or a manual operation. Leave blank for a pause, stop, emergency stop or resume.',
            fResumes: 'Intervention resumed',
            resumeHint: 'The pause, stop or emergency stop this resume follows.',
            requestedNote: 'The time of the request is recorded when you save.',
            approve: 'Approve',
            awaitingApproval: 'Awaiting a second person',
            colApproval: 'Second person',
            approved: 'Approved',
            notRequired: 'Not required',
        },
        channels: {
            heading: 'Channels and supervisor lines',
            description: 'Each AI-assisted channel of this system, with the supervisor’s line over it. Interventions and disclosures name a channel.',
            add: 'Record a channel',
            empty: 'No channel is recorded for this system.',
            colName: 'Channel',
            colLine: 'Supervisor’s line',
            colRecorded: 'Recorded on',
            export: 'Export log and disclosures',
            formTitle: 'Record a channel',
            submit: 'Save channel',
            fName: 'Channel name',
            fLine: 'Supervisor’s line',
            lineHint: 'The unit or role that supervises this channel.',
            exportTitle: 'Export the channel log',
            exportSubmit: 'Export',
            exportFrom: 'From',
            exportTo: 'To',
            colDualControl: 'Dual control',
            fDualControl: 'A second person approves each pause, stop and resume',
            dualControlHint: 'When set, a pause, a stop and a resume take effect only after a second person approves it. An emergency stop is never held.',
            registerExport: 'Export the pause and stop register',
            registerTitle: 'Export the pause and stop register',
        },
        channelStatus: {
            heading: 'Channel status',
            description: 'The state of each channel today, read from its record: the disclosures in use, the versions awaiting a second person, and the interventions still open. Read only.',
            empty: 'No channel is recorded for this system.',
            colChannel: 'Channel',
            colState: 'State',
            colDisclosures: 'Disclosures in use',
            colAwaiting: 'Awaiting a second person',
            colOpen: 'Open interventions',
            noneOpen: 'None',
            openAwaiting: 'awaiting a second person',
            openUnanswered: 'escalation unanswered',
            openRequested: 'awaiting the bank’s confirmation',
            state: {
                running: 'Running',
                paused: 'Paused',
                stopped: 'Stopped',
                emergency_stopped: 'Emergency stopped',
            },
        },
        escalations: {
            heading: 'Escalations and responses',
            description: 'Every intervention is escalated to the system owner for cause analysis and to the second line for the board report. Each escalation stays open until its addressee records a response.',
            empty: 'No escalation is raised.',
            colAddressee: 'Addressed to',
            colIntervention: 'Intervention',
            colRaised: 'Raised on',
            colResponse: 'Response',
            none: 'No one is named; the task went to the person who recorded it.',
            respond: 'Record a response',
            respondTitle: 'Record a response',
            respondSubmit: 'Save response',
            fResponse: 'Response',
            responseHint: 'Recorded once, with the addressee’s name and the time.',
            addressee: {
                owner: 'System owner',
                second_line: 'Second line',
            },
            colAnsweredAs: 'Answered as',
            answeredAs: {
                owner: 'System owner',
                attest_fallback: 'Attest holder, in place of the owner',
            },
        },
        briefings: {
            heading: 'Staff briefings on the system’s limits',
            description: 'Who was briefed, when, and on which version of the procedure. Completion of training is held in the training register.',
            add: 'Record a briefing',
            empty: 'No briefing is recorded.',
            colDate: 'Briefed on',
            colProcedure: 'Procedure version',
            colAttendees: 'Briefed',
            formTitle: 'Record a briefing',
            submit: 'Save briefing',
            fDate: 'Day of the briefing',
            fLimits: 'Limits covered',
            fProcedure: 'Procedure version',
            fAttendees: 'Staff briefed (user IDs, separated by commas)',
            fTraining: 'Training record reference (optional)',
            addAttendee: 'Add this person',
            removeAttendee: 'Remove',
        },
        monitoring: {
            heading: 'Performance and behaviour monitoring',
            description: 'Comparisons of predicted and actual outcomes entered or imported, the owner’s thresholds, and events that stay open until a person closes them.',
            addComparison: 'Record a comparison',
            addEvent: 'Record an event',
            setThresholds: 'Set thresholds',
            eventsHeading: 'Events',
            eventsEmpty: 'No event is recorded.',
            comparisonsHeading: 'Comparisons',
            comparisonsEmpty: 'No comparison is recorded.',
            colReading: 'State',
            colKind: 'Kind',
            colDetected: 'Detected on',
            colDescription: 'Description',
            colResponse: 'Response',
            colMetric: 'Metric',
            colMeasured: 'Measured on',
            colPredicted: 'Predicted',
            colActual: 'Actual',
            colDeviation: 'Deviation',
            colCrossed: 'Threshold crossed',
            crossedYes: 'Yes',
            crossedNo: 'No',
            close: 'Close with a response',
            kind: {
                output_deviation: 'Output deviation',
                quality_decline: 'Quality decline',
                bias: 'Bias',
                model_drift: 'Model drift',
                data_drift: 'Data drift',
                threshold_crossing: 'Threshold crossed',
            },
            source: { entered: 'Entered by a person', imported: 'Imported' },
            eventTitle: 'Record an event',
            submitEvent: 'Save event',
            fKind: 'Kind',
            fDescription: 'Description',
            fMetric: 'Metric',
            comparisonTitle: 'Record a comparison',
            submitComparison: 'Save comparison',
            fMeasuredOn: 'Measured on',
            fPredicted: 'Predicted value',
            fActual: 'Actual value',
            fSource: 'Source',
            closeTitle: 'Close the event',
            submitClose: 'Close event',
            fResponse: 'Response',
            thresholdsTitle: 'Thresholds',
            submitThresholds: 'Save thresholds',
            thresholdLegend: 'Threshold {n}',
            fMaxDeviation: 'Largest permitted deviation',
            addThreshold: 'Add a threshold',
        },
        duties: {
            heading: 'Duty holders and competence',
            description: 'The people named against a duty to monitor, detect deviation or intervene. Training is read against a completed assignment; the review falls due yearly.',
            add: 'Name a duty holder',
            capacity: 'Record capacity',
            empty: 'No duty holder is named.',
            colPerson: 'Person',
            colDuty: 'Duty',
            colTraining: 'Training',
            colCompetence: 'Competence review',
            colDue: 'Review due',
            duty: { monitor: 'Monitor', detect_deviation: 'Detect deviation', intervene: 'Intervene' },
            review: 'Record a competence review',
            addTitle: 'Name a duty holder',
            submit: 'Save duty holder',
            fPerson: 'Person',
            fDuty: 'Duty',
            fTraining: 'Training assignment reference (optional)',
            trainingHint: 'A training completion counts only against a completed assignment.',
            reviewTitle: 'Competence review',
            submitReview: 'Save review',
            fReviewedOn: 'Date reviewed',
            fSummary: 'Summary of the review',
            capacityTitle: 'Capacity record',
            submitCapacity: 'Save capacity',
            fBasis: 'Basis',
            basis: {
                unit_builds_or_runs: 'The unit builds or runs the system',
                outsourced: 'Outsourced',
            },
            fCapacity: 'Capacity statement',
            fSupplierName: 'Supplier name',
            supplierHint: 'Required for an outsourced system.',
            fSupplierCapacity: 'Supplier capacity statement',
            fAssessedOn: 'Date the supplier was assessed',
        },
        bankingTriggers: {
            heading: 'Banking triggers',
            intro: 'The trigger facts recorded for this system, compared with the thresholds the installed framework declares. Where the triggers reach the system and a fact exceeds a threshold, the platform raises a classification proposal, shown here and in the classification review. A person classifies; the platform sets no tier.',
            listLabel: 'Within a high-risk entry of the official list (conditions other than value)',
            listStatus: { listed: 'Within an entry', not_listed: 'Not within an entry' },
            listNotRecorded: 'Not recorded',
            listRecorded: 'Recorded by {user} on {date}',
            listHint: 'Recorded on the system form: whether the system meets the conditions of a high-risk entry of the official list other than the transaction or loan value, which the thresholds supply. You record the answer; the platform does not decide it.',
            preconditionLabel: 'Precondition',
            precondition: "The triggers reach only a system within an entry of {list}, on that entry's conditions other than value ({provision}).",
            preconditionNone: 'No installed framework declares a precondition: the triggers reach every system.',
            scopeLabel: 'Reach',
            scope: {
                applies: 'The triggers reach this system',
                not_applicable: 'Not within an entry of the list: no banking trigger applies',
                not_recorded: 'Not recorded: whether the triggers reach this system is not known',
                precondition_unreadable: 'The precondition an installed framework declares could not be read, so the platform cannot show whether the triggers reach this system, and raises no proposal',
            },
            factsNotRecorded: 'No trigger facts are recorded for this system.',
            noThresholds: 'No installed framework declares a banking threshold.',
            unreadable: '{count} declaration(s) an installed framework made could not be read.',
            exceedancesHeading: 'Thresholds exceeded',
            noExceedance: 'No recorded fact exceeds a declared threshold.',
            colThreshold: 'Trigger',
            colValue: 'Recorded value (VND)',
            colAmount: 'Threshold amount (VND)',
            colProvision: 'Provision',
            measure: {
                per_transaction_payment: 'One payment',
                daily_total_per_customer: "One customer's daily total",
                credit_amount: 'Credit amount',
            },
            customerClass: {
                individual: 'individual customer',
                organisation: 'organisation customer',
                any: 'any customer',
            },
            comparison: { greater_than: 'over', greater_or_equal: 'at least' },
            thresholdLabel: '{measure}, {class}: {comparison} the amount',
            proposalHeading: 'Classification proposal raised',
            proposalText: 'Run a classification screening to review them: the classification review shows them beside the tier control. The tier is the one a reviewer records there.',
            noProposal: 'No classification proposal is raised.',
            reviewProposalHeading: 'Banking-trigger proposal for this review',
            reviewProposalIntro: 'This system is recorded as within a high-risk entry of the official list the installed framework names, and a recorded fact exceeds the thresholds below. Weigh them in this review; the tier is the one you record.',
            reviewProposalIntroNoPrecondition: 'A recorded fact on this system exceeds the thresholds below, which the installed framework applies to every system. Weigh them in this review; the tier is the one you record.',
            reviewProposalItem: '{threshold}: {value} VND against {amount} VND ({provision})',
        },
        systemDates: {
            heading: 'Dated obligations for this system',
            intro: 'The commencement dates the installed framework declares, as they fall on this system: whether it was in operation before the framework took effect, and whether your organisation has a system of the tier a carve-out names.',
            inOperationSince: 'In official operation since',
            inOperationNotRecorded: 'Not recorded. Set it on the system form; until then a date that depends on it is not shown.',
            empty: 'No installed framework declares a dated obligation for AI systems.',
            colReference: 'Commencement provision',
            colDate: 'Date for this system',
            colReason: 'Why',
            colSource: 'From',
            noDate: 'No date',
            notStated: 'a date not stated',
            partNotNamed: 'the provisions the transitional stage does not move',
            decidedBy: 'For a system put into official operation before {cutoff}, {reference} sets another date for all or part of this.',
            decidedByRow: 'For a system put into official operation before {cutoff}, {reference} sets another date for all or part of this — see that row.',
            reason: {
                every_system: 'The same date for every system',
                unit_condition_not_held: 'No system of your organisation is recorded as {tier}, the tier the carve-out names, so the stated date applies to every system',
                unit_condition_undetermined: 'Depends on classification: {count} system of your organisation not yet classified. If it is classified {tier}, {cutoffDate} applies; otherwise {date}.',
                unit_condition_undetermined_plural: 'Depends on classification: {count} systems of your organisation not yet classified. If any is classified {tier}, {cutoffDate} applies; otherwise {date}.',
                new_systems_only: 'Applies only to a system put into operation on or after {cutoff}',
                new_system_in_stated_period: 'Put into operation on or after {cutoff}: this date applies if that was within the period the provision states',
                existing_system: 'In operation before {cutoff}',
                existing_system_moved: 'In operation before {cutoff}: the transitional date applies',
                existing_system_partly_moved: 'In operation before {cutoff}: this date applies to the provisions the transitional stage does not move; see that stage for the rest',
                new_system: 'Put into operation on or after {cutoff}',
                unit_holds_tier: 'Your organisation has a system classified {tier}, the tier the carve-out names',
                existing_systems_only: 'Applies only to a system in operation before {cutoff}',
                operation_start_not_recorded: 'Depends on when the system went into operation, which is not recorded',
                every_system_in_part: 'Applies to {part} for every system; for the rest it depends on when the system went into operation, which is not recorded',
                declaration_unreadable: 'The commencement the installed framework declares for this could not be read',
                applies_through_condition_met: 'In operation before {cutoff}, and its recorded autonomy level automates the execution of decisions: the provision applies to it through another from this date',
                applies_through_condition_not_met: 'In operation before {cutoff}, and its recorded autonomy level does not automate the execution of decisions: the date for systems already in operation applies',
                applies_through_condition_not_recorded: 'Depends on the autonomy level, which is not recorded: {dateMet} if the system automates the execution of decisions, {dateNotMet} if not. Nothing reads overdue.',
            },
            // fix-sprint-20261031-1200 F39(e) — a provision brought in through another, with its reading.
            throughNote: '{provision} through {via}: {reading}',
        },
        inventoryRegister: {
            heading: 'Inventory completeness',
            statement: 'This register holds the AI systems recorded in ComplianceOne and the legacy entries imported from your organisation’s existing inventory. It can show only what has been recorded: a system nobody recorded is not in it.',
            recordedSystems: 'Systems recorded',
            legacyEntries: 'Imported legacy entries',
            noImport: 'No legacy import has been made.',
            lastImport: 'Last import',
            received: 'Received',
            imported: 'Imported',
            notImported: 'Not imported',
            refusalHeading: 'Rows not imported',
            colRow: 'Row',
            colName: 'Name',
            colReason: 'Reason',
            refusal: {
                name_missing: 'No name',
                already_registered: 'Already registered',
                duplicate_in_batch: 'Repeated in this file',
            },
            importHeading: 'Legacy import',
            importHint: 'One system per line: name | legacy reference | owner as recorded | status as recorded. A name already in the register, or repeated in the file, is not imported.',
            importField: 'Rows from the existing inventory',
            importSource: 'Source label',
            importSubmit: 'Import rows',
            importDone: 'Import recorded.',
            importFailed: 'The import was not recorded.',
            importEmpty: 'Enter at least one row.',
            legacyHeading: 'Imported legacy entries',
            legacyEmpty: 'No legacy entry is imported.',
            colLegacyRef: 'Legacy reference',
            colOwnerRecorded: 'Owner as recorded',
            colStatusRecorded: 'Status as recorded',
            colImportedOn: 'Imported on',
            colTier: 'Risk tier',
            notClassified: 'Not classified',
            importedMarker: 'Imported',
            obligationsHeading: 'Dated obligations',
            obligationsHint: 'The dates the installed AI instruments state, each with the status and the source it is read from. What each date covers, including any transitional rule or carve-out, is in its own column, worded as the installed framework words it.',
            obligationsEmpty: 'No installed AI instrument states a dated obligation.',
            colReference: 'Article',
            colFramework: 'Instrument',
            colStatus: 'Status',
            colDate: 'Date or period',
            colReasonNotEnforced: 'Why not enforced',
            colCarveOut: 'What the date covers',
            colSource: 'Pack source',
            noPeriod: 'The framework states no date or period.',
            reasonNotApplicable: 'Not applicable',
            annualRecurrence: 'Each year, by {day}/{month}',
            unitUnknown: '{amount} ({unit})',
            unit: {
                hours: 'hours',
                days: 'days',
                working_days: 'working days',
                calendar_days: 'calendar days',
                weeks: 'weeks',
                months: 'months',
                years: 'years',
                annual: 'years',
                working_days_before_event: 'working days before the event',
                calendar_days_before_event: 'calendar days before the event',
                calendar_days_after_event: 'calendar days after the event',
                before_use: 'Before the system is put into use',
            },
            unitOne: {
                hours: 'hour',
                days: 'day',
                working_days: 'working day',
                calendar_days: 'calendar day',
                weeks: 'week',
                months: 'month',
                years: 'year',
                annual: 'year',
                working_days_before_event: 'working day before the event',
                calendar_days_before_event: 'calendar day before the event',
                calendar_days_after_event: 'calendar day after the event',
            },
            periodRecurring: 'Every {period}',
            noCarveOut: 'The installed framework states no separate scope for this date.',
            unitHoldsTier: 'Your organisation has a system classified at the tier the carve-out names, so the carve-out date applies. Without one, the date is {stated}.',
            status: {
                in_force: 'In force',
                upcoming: 'Upcoming',
                not_recorded: 'Not recorded',
                not_enforced: 'Not enforced',
            },
            reason: {
                reference_install: 'Installed for reference only',
                not_operational: 'Not operational for this organisation',
            },
            exportHeading: 'Export the inventory',
            exportIntro: 'The organisation-wide register as a file: every system and imported entry, with the completeness limit in its header. The platform assigns no tier.',
            exportButton: 'Export inventory',
            exportFailed: 'The inventory could not be exported.',
        },
        evidenceIndex: {
            heading: 'Regulator evidence index',
            intro: 'The regulator-facing evidence this system holds, by area, with where each area sits.',
            colArea: 'Area',
            colCount: 'Records',
            colLatest: 'Latest',
            colWhere: 'Where it sits',
            none: 'None recorded',
            area: {
                classification: 'Classification runs',
                validation: 'Validation',
                security_tests: 'Security tests',
                interventions: 'Interventions',
                complaints: 'Complaints',
                supplier_arrangements: 'Supplier arrangements',
            },
            where: {
                classification: 'This page, classification section',
                validation: 'This page, validation section',
                security_tests: 'This page, security section',
                interventions: 'This page, interventions section, and the interventions register',
                complaints: 'Complaints register',
                supplier_arrangements: 'This page, supplier section',
            },
        },
        suppliers: {
            heading: 'Supplier arrangements',
            description: 'The arrangement with a vendor, read on the vendor record. A term with no state reads not recorded. The register counts the terms recorded present, out of the eight Điều 18 khoản 3 names; it does not judge the contract.',
            add: 'Record an arrangement',
            empty: 'No arrangement is recorded with this vendor.',
            vendorHint: 'Choose the vendor to read its arrangements.',
            colPeriod: 'Period',
            colClauses: 'Clauses',
            colCompliant: 'Terms recorded',
            colAssurance: 'Assurance report',
            colTraining: 'Training data',
            compliantYes: 'All eight terms recorded present',
            compliantNo: 'Not all eight recorded',
            expires: 'expires',
            clause: {
                assurance_evidence: 'Assurance evidence',
                version_reporting: 'Version reporting',
                monitoring_access: 'Monitoring access',
                continuity_and_transition: 'Continuity and transition',
                regulatory_cooperation: 'Regulatory cooperation',
                training_data_commitment: 'Training-data commitment',
                it_service_contract_rules: 'IT-service contract rules',
                technical_information_and_limits: 'Technical information, model limits and security measures',
            },
            clauseState: { present: 'Present', absent: 'Absent', pending: 'Pending' },
            clauseHint: 'Choose a state; leave empty for not recorded.',
            training: {
                commitment: 'Commitment',
                consent: 'Written consent',
                not_recorded: 'Not recorded',
            },
            // Fix-sprint-20261031-1200 F32 — the supplier page's boundary statement.
            boundary: "ComplianceOne records what the contract says and what the supplier reported. It does not connect to the supplier's systems, does not verify a model's contents and does not judge the contract: monitoring access is evidenced by your organisation's record.",
            // F4 — the assurance warning period, and where its figure comes from.
            fWarnDays: 'Warning period (days before expiry)',
            warnDaysDefault: '30 days unless your organisation sets another figure. This is a ComplianceOne default: the installed framework sets no warning period.',
            warnDaysReading: 'Warning period: {days} days ({source})',
            warnDaysSource: {
                organisation: 'set by your organisation',
                platform_default: 'ComplianceOne default',
            },
            // F35 — the training-data term read with its recorded position.
            trainingBasis: "Điều 18 khoản 3 điểm h: the third party commits not to use your organisation's data to train, fine-tune or improve its model without your organisation's written consent.",
            trainingTerm: {
                recorded: 'Recorded with its position',
                position_not_recorded: 'Position not recorded: the eight terms do not read as recorded',
                clause_not_present: 'The clause is not recorded present',
            },
            fConsentReference: 'Written consent reference',
            fConsentBasis: "Your organisation's basis for consenting",
            consentBasisHint: 'Where personal data is involved, the DPO reviews this basis.',
            // F7 — the supplier record export per period.
            exportRecord: 'Export the supplier record',
            exportTitle: 'Export the supplier record',
            exportDescription: "The arrangements overlapping the period, their terms, the events dated in it, and each system's component register at the end of the period.",
            fExportPeriod: 'Period',
            exportPeriodHint: 'A year (YYYY) or a month (YYYY-MM).',
            fExportFormat: 'File format',
            exportFormat: { csv: 'CSV', xlsx: 'Excel (XLSX)', pdf: 'PDF' },
            exportSubmit: 'Export',
            exportFailed: 'The supplier record could not be exported.',
            formTitle: 'Record a supplier arrangement',
            submit: 'Save arrangement',
            fVendor: 'Vendor',
            fPeriodStart: 'Period starts',
            fPeriodEnd: 'Period ends',
            fTrainingPosition: 'Training-data position',
            fTrainingScope: 'Scope of the consent',
            colVersions: 'Version reports',
            validationSigned: 'validated',
            validationUnknown: 'validation unknown',
            recordedOn: 'Recorded',
            fReviewCycle: 'Review cycle (months)',
            fReviewCycleHint: 'The bank’s review cycle for this arrangement. Leave empty for no cycle.',
            fAssuranceRef: 'Assurance report reference',
            fAssuranceStart: 'Report period starts',
            fAssuranceEnd: 'Report period ends',
            fAssuranceExpires: 'Report expires on',
            colReadings: 'Readings',
            readingReview: 'Review',
            reviewNotSet: 'no cycle set',
            reviewCurrent: 'on cycle',
            reviewOverdue: 'overdue',
            reviewDueOn: 'due',
            assurance: {
                current: 'current',
                expiring: 'expiring',
                expired: 'expired',
            },
            terminatedOn: 'Terminated on',
            chainFlagged: 'Sub-processor change not yet reviewed',
            continuityTested: 'Continuity last tested',
            monitoringReviewed: 'Monitoring access last reviewed',
            regulatorContact: 'Regulator contact',
            regulatorRequests: 'Regulator requests logged',
            addEvent: 'Record a supplier event',
            eventFormTitle: 'Record a supplier event',
            eventSubmit: 'Save event',
            fEventArrangement: 'Arrangement',
            fEventKind: 'Event',
            eventKind: {
                model_version: 'Model version reported',
                sub_processor_change: 'Sub-processor change',
                termination: 'Termination',
                regulator_request: 'Regulator request',
                regulator_contact: 'Regulator contact named',
                continuity_test: 'Continuity test',
                monitoring_access_review: 'Monitoring access review',
                review: 'Arrangement review',
            },
            fModelVersion: 'Model version',
            fSupplierReportedOn: 'Supplier reported on',
            fSubProcessor: 'Sub-processor',
            fComponentRef: 'Component changed',
            fChangedOn: 'Changed on',
            fSummary: 'Summary',
            fEffectiveOn: 'Effective on',
            fReason: 'Reason',
            fRequestReference: 'Request reference',
            fReceivedOn: 'Received on',
            fContactUserId: 'Named contact (a person of this organisation)',
            fRoute: 'Route a regulator request takes',
            fTestedOn: 'Tested on',
            fReviewedOn: 'Reviewed on',
            fOutcome: 'Outcome',
        },
        panels: {
            heading: 'Duties by obligation kind',
            description: 'Each kind lists its duties with the status and the evidence state. A duty marked complete without evidence needs evidence.',
            kind: {
                human_oversight: 'Oversight',
                transparency_labeling: 'Transparency and labelling',
                monitoring: 'Monitoring',
                incident: 'Incident',
            },
            count: '{count} duties',
            empty: 'No duty of this kind is attached.',
            colDuty: 'Duty',
            colStatus: 'Status',
            colEvidence: 'Evidence',
            evidence: {
                needs_evidence: 'Needs evidence',
                evidenced: 'Evidence recorded',
                not_recorded: 'Not recorded',
            },
            needsEvidenceWhy: 'The status cannot stand as complete without an evidence record.',
            edit: 'Edit',
        },
        registers: {
            backToRegister: 'Back to the register',
            inventoryGuide: 'Use this page once a year: choose the period, then confirm each system or record what changed. The full guide explains each outcome and when a review reads overdue.',
            paymentGuide: "Read the payment authorities an AI system holds, with their limits and what each one still lacks. Authorities are recorded and approved on the system's own page.",
            interventionsGuide: "Read the pauses, stops and other human interventions recorded for an AI system. Interventions are recorded on the system's own page; the full guide explains when one reads confirmed.",
            complaintsGuide: 'Record a complaint about an AI-influenced decision, route it to an independent reviewer, note any item withheld from the customer, then record the re-review and the close. The counts below show when a product reaches the threshold your organisation sets.',
            frameworkGuide: 'Record the approved risk-management framework for AI and the map of the three lines of defence. The full guide explains what makes a line complete.',
            boardGuide: 'These are the AI figures the board report carries, read from the registers now. The table shows the time they were read. To change a figure, change the register it is read from.',
            systemsLoading: 'Loading the AI systems…',
            systemsFailed: 'The list of AI systems could not be read.',
            systemsRetrySr: 'read the list of AI systems again',
            systemsEmpty: 'No AI system is recorded in the register yet.',
            thresholdCurrent: 'Current threshold',
            thresholdComplaints: '{count} complaints',
            thresholdLoading: 'Loading the threshold…',
            thresholdFailed: 'The threshold setting could not be read.',
            thresholdRetrySr: 'read the threshold setting again',
            fSystem: 'AI system',
            inventoryTitle: 'AI inventory reviews',
            inventorySubtitle: 'Annual review of every AI system in the register',
            inventorySummary: 'Each system is confirmed or changed by a named person. A system not confirmed by its due date reads overdue.',
            inventoryHeading: 'Reviews for the period',
            inventoryEmpty: 'No system has a review for this period.',
            fPeriod: 'Period',
            colSystem: 'System',
            colPeriod: 'Period',
            colDue: 'Due',
            colReading: 'Reading',
            confirm: 'Confirm',
            confirmTitle: 'Confirm the annual review',
            submit: 'Save',
            fOutcome: 'Outcome',
            paymentTitle: 'Payment authorities',
            paymentSubtitle: 'Automated payment authorities, by system',
            paymentSummary: 'Choose a system to read its payment authorities. The platform records each authority; it does not execute a payment.',
            paymentHeading: 'Authorities for the system',
            paymentEmpty: 'No payment authority is recorded for this system.',
            openSystem: 'Open the system record',
            interventionsTitle: 'Interventions',
            interventionsSubtitle: 'Human oversight records, by system',
            interventionsSummary: 'Choose a system to read its interventions. A pause or stop reads requested until a person enters the confirmed time.',
            interventionsHeading: 'Interventions for the system',
            interventionsEmpty: 'No intervention is recorded for this system.',
            complaintsTitle: 'AI complaints',
            complaintsSubtitle: 'Complaints about AI-influenced decisions, and their human re-review',
            complaintsSummary: 'A complaint is routed to a reviewer who is neither the system owner nor a listed developer. It closes only with a recorded re-review.',
            complaintsHeading: 'Complaints',
            complaintAdd: 'Record a complaint',
            complaintsEmpty: 'No complaint is recorded.',
            colReceived: 'Received',
            colProduct: 'Product',
            colCategory: 'Decision category',
            colStatus: 'Status',
            colReviewer: 'Reviewer',
            colWithheld: 'Withheld items',
            colReReview: 'Re-review',
            reReviewRecorded: 'Recorded',
            route: 'Route',
            withhold: 'Withhold an item',
            reReview: 'Re-review',
            close: 'Close',
            countsHeading: 'Complaints by system and product',
            countsEmpty: 'No complaint is counted yet.',
            colCount: 'Count',
            colThreshold: 'Threshold',
            thresholdNotSet: 'Not set',
            colCrossed: 'Threshold crossed',
            crossedYes: 'Yes',
            crossedNo: 'No',
            setThreshold: 'Set the threshold',
            createTitle: 'Record a complaint',
            fCategory: 'Decision category',
            fReceivedOn: 'Date received',
            fSummary: 'Summary of the complaint',
            fProduct: 'Product',
            fStatedReason: 'Stated reason',
            fExplanation: 'Explanation given',
            colBasis: 'Decision basis',
            basisReading: {
                recorded: 'Recorded',
                basis_not_retained: 'Basis not retained',
                reason_notified_basis_recorded: 'Reason notified; basis held',
            },
            noDecision: 'No decision linked',
            fDecision: 'Decision complained of',
            fDecisionHint: 'Optional. The decision as recorded in the AI decision register.',
            fExplanationDay: 'Date the explanation was delivered',
            fExplanationChannel: 'Channel the explanation was delivered by',
            fNotifiedMethod: 'How the customer was notified',
            fNotifiedOn: 'Date the customer was notified',
            exportFile: 'Export the complaint file',
            routeTitle: 'Route the complaint',
            fReviewer: 'Reviewer',
            reviewerHint: 'Not the system owner or a listed developer.',
            submitRoute: 'Route complaint',
            withholdTitle: 'Withhold an item',
            fWithheldItem: 'Item withheld',
            fWithheldReason: 'Reason for withholding',
            fWithheldCategory: 'Category',
            withheldCategory: {
                source_code_algorithm_or_parameters: 'Source code, detailed algorithm or parameter set',
                business_or_technology_secret: 'Business or technology secret',
                third_party_information: 'Third-party information',
                security_sensitive: 'Security-sensitive',
                restricted_decision: 'Restricted decision',
                other: 'Other',
            },
            submitWithhold: 'Save item',
            reviewTitle: 'Re-review the complaint',
            fOutcomeSummary: 'Outcome of the re-review',
            reviewHint: 'The re-review is the final basis for the reply. Its outcome is recorded for every category.',
            reviewReasonHint: 'What the customer is told. For this category the institution need only tell the customer a reason; the outcome above is held internally.',
            submitReview: 'Save re-review',
            thresholdTitle: 'Complaint threshold',
            fThresholdCount: 'Complaints that raise a task',
            thresholdHint: 'The organisation sets this figure; the regulation states none.',
            fSecondLine: 'Second-line holder',
            submitThreshold: 'Save threshold',
            closeTitle: 'Close the complaint',
            closeHint: 'The server refuses a close with no re-review recorded.',
            submitClose: 'Close complaint',
            complaintsBoundary: 'ComplianceOne holds the complaint, the decision basis and the re-review. It does not decide a complaint and does not write the explanation to the customer.',
            closeNeedsReReview: 'No re-review is recorded for this complaint, so it cannot be closed yet. Record the re-review first.',
            fComplaintReason: 'Reason for the complaint',
            complaintReasonHint: "ComplianceOne's own categories; the Circular names none.",
            colComplaintReason: 'Reason',
            complaintReason: {
                decision_outcome: 'Disagrees with the outcome',
                explanation: 'Asks for an explanation, or finds it inadequate',
                unfair_treatment: 'Unfair treatment or discrimination',
                data_error: 'Wrong or outdated data',
                ai_interaction: 'Dealing with an AI system',
                service_failure: 'System error or interruption',
                other: 'Other',
                not_recorded: 'Not recorded',
            },
            colAiSystem: 'AI system',
            colCountInPeriod: 'Complaints in the period',
            colByReason: 'By reason',
            thresholdWithWindow: '{count} in {months} month(s)',
            thresholdNoWindow: 'counted over every complaint recorded; no window set',
            periodFrom: 'From month',
            periodTo: 'To month',
            periodHint: 'The default is the six months ending this month.',
            exportTrend: 'Export the trend by month',
            fThresholdWindow: 'Window, in months',
            thresholdWindowHint: 'Optional, from 1 to 12 months ending with the month counted. Left empty, every complaint recorded is counted.',
            patternHeading: 'Responses to a pattern of complaints',
            patternLabel: "Your organisation's response to a pattern of complaints. The Circular sets no threshold and no response for a pattern; ComplianceOne decides neither.",
            patternEmpty: 'No response to a pattern of complaints is recorded.',
            patternRespond: 'Record the response',
            patternTitle: 'Record the response to a pattern of complaints',
            patternSubmit: 'Save response',
            fPatternKind: 'What your organisation did',
            patternKind: {
                no_change_with_reason: 'No change, with the reason',
                revalidation_requested: 'Re-validation requested',
                intervention_recorded: 'Intervention recorded',
                incident_recorded: 'Incident recorded',
                change_recorded: 'Change recorded',
                other: 'Other',
            },
            fPatternLinked: 'The record it refers to',
            patternLinkedEmpty: 'This system has no record of this kind to link.',
            patternRespondSr: 'for {system}, {product}',
            patternRespondSrCrossed: 'for {system}, {product}, crossed on {date}',
            patternCrossedOn: 'Crossed on {date}.',
            patternCrossingNote: 'The response answers the latest complaint that brought this count to the threshold. ComplianceOne finds that complaint and records its day with the response.',
            patternWho: "Only the system's owner, the second-line holder the threshold names, or a person on the second line of the current three-lines map whose role in the register is P-VN-42 can record the response.",
            patternLinkedLoading: 'Reading the records of this system…',
            patternLinkedFailed: 'The records of this system could not be read.',
            patternLinkedRetrySr: 'read the records to link again',
            patternAlreadyAnswered: 'This crossing of the threshold already has its response.',
            periodInvalid: 'A period covers at most 60 months.',
            lineAddedInVersion: 'Attested by someone added to this line in the version they attested.',
            fPatternSummary: 'What was done, and why',
            fRespondedOn: 'Day of the response',
            colCrossedOn: 'Threshold crossed on',
            colResponseKind: 'Response',
            colRespondedBy: 'Responded by',
            colRespondedOn: 'Responded on',
            colExplanations: 'Explanations',
            explanationAdd: 'Record an explanation',
            explanationTitle: 'Record an explanation after the re-review',
            explanationHint: 'Record each explanation delivered to the customer after the re-review, as it was delivered. None is required to close the complaint.',
            fExplanationText: 'Explanation as delivered',
            explanationSubmit: 'Save explanation',
            explanationStage: {
                at_intake: 'At intake',
                after_re_review: 'After the re-review',
            },
            frameworkTitle: 'Framework record and three lines',
            frameworkSubtitle: 'The risk-management framework and the three lines of defence',
            frameworkSummary: 'The framework is approved by a named body and person. The three-lines map shows each line’s unit, people and attestation.',
            frameworkHeading: 'Risk-management framework',
            frameworkAdd: 'Record a framework',
            frameworkEmpty: 'No framework is recorded.',
            frameworkFormTitle: 'Record the framework',
            fFrameworkTitle: 'Framework title',
            fApproverBody: 'Approving body',
            fApprover: 'Approver',
            fApprovedOn: 'Date approved',
            fRiskCode: 'Risk type code',
            fDefinition: 'Definition',
            fThresholds: 'Thresholds',
            fControls: 'Controls',
            colTitle: 'Title',
            colApprover: 'Approver',
            colApproved: 'Approved on',
            colRiskTypes: 'Risk types',
            linesHeading: 'Three lines of defence',
            linesAdd: 'Record the map',
            linesEmpty: 'No three-lines map is recorded.',
            linesFormTitle: 'Record the three-lines map',
            linesFormDescription: 'Each line names its unit and its holders. A line is attested by one of its own holders; a line not attested, or attested by someone not on it, reads incomplete.',
            colVersion: 'Version',
            colIncomplete: 'Incomplete lines',
            colLines: 'Lines',
            attestedYes: 'attested',
            attestedNo: 'not attested',
            line: { first: 'First line', second: 'Second line', third: 'Third line' },
            fUnit: 'Unit',
            fHolder: 'Person',
            fRole: 'Role',
            fPersona: 'Role in the register',
            fAttestation: 'Attestation statement',
            attestationHint: 'Entered by the signatory.',
            fAttested: 'Attested',
            // Fix-sprint-20261031-1200 F32 — the framework and three-lines page's boundary statement.
            frameworkBoundary: "ComplianceOne holds the framework record, the three-lines map and each line's attestation, as the people named entered them. It approves no framework, judges no line's independence and attests nothing on anyone's behalf.",
            // F6 — one line per attestation, by one of that line's own holders.
            attestLine: 'Attest this line',
            attestTitle: 'Attest the {line}',
            attestDescription: 'You are named on this line. Your attestation is recorded in your name and dated today.',
            submitAttest: 'Save attestation',
            lineAttestedOn: 'Attested on {date}',
            lineCarried: 'Attested on {date} (carried from version {n})',
            lineNotAttested: 'Not attested',
            lineNonHolder: 'Attested by someone not named on this line: re-attest.',
            sharedHolder: '{name} is named on the {lineA} and the {lineB}. Điều 14 khoản 2 asks for independent lines.',
            lineLower: { first: 'first line', second: 'second line', third: 'third line' },
            lineJoin: ', the ',
            boardTitle: 'Board report: AI figures',
            boardSubtitle: 'The AI figures in the board report, read from the AI registers',
            boardSummary: 'Overdue inventory reviews count the reviews of the current calendar year. A pause, stop, emergency stop, manual operation or resume counts as an open intervention from when it is recorded until a person enters its confirmed time.',
            boardHeading: 'Current figures',
            boardEmpty: 'The register returned no AI figures.',
            colFigure: 'Figure',
            colValue: 'Value',
            figSystems: 'AI systems',
            figLow: 'Low risk',
            figMedium: 'Medium risk',
            figHigh: 'High risk',
            figUnclassified: 'Unclassified',
            figOverdue: 'Overdue inventory reviews',
            figFindings: 'Open validation findings',
            figInterventions: 'Open interventions',
            figDisclosures: 'Disclosures in use',
            figAsOf: 'As of',
        },
        // fix-sprint-20261031-1200 F1 and F34 — the periodic report contents, retirement and a change's significance.
        boardReport: {
            heading: 'Periodic report contents',
            intro: 'The contents the installed framework names for the report to the senior management body, read from the registers for the window below. ComplianceOne counts what the registers hold; it does not write or present the report.',
            windowFrom: 'Window from',
            windowTo: 'Window to',
            applyWindow: 'Read for this window',
            windowInvalid: 'Enter a window that opens on or before the day it closes.',
            statusComplete: 'Reads the five contents Điều 12 khoản 6 names — operation, risk, serious incidents, complaints and drift trends — from the registers, for {from} to {to}. ComplianceOne does not present the report to the senior management body or the parent bank.',
            statusPartial: 'Partial against Điều 12 khoản 6: {gaps}. The figures below are what the registers hold for {from} to {to}.',
            gapUnclassified: "{n} systems have no recorded tier, so they cannot be placed in or out of the report's scope",
            gapSignificance: '{n} changes to high-risk systems have no significance answer',
            gapOperationDate: '{n} systems have no operation date, so the day the report is first owed is not known for them',
            gapUnreadable: '{content} could not be read',
            windowLonger: 'This window is longer than the six months the text sets as the minimum frequency.',
            nextDue: 'Next report due by {date} (from {anchor})',
            anchorLastExport: 'the last board report export on {date}',
            anchorFirstApplies: 'the earliest day the report applies to a system in scope, {date}',
            nextDueNotDeclared: 'No next due date: no installed framework in force sets the report interval.',
            nextDueNoScope: "No next due date: no system of your organisation is in the report's scope.",
            nextDueUnknown: 'The next due date is not known: no system in scope has an operation date.',
            autonomyNotRecorded: '{n} systems in scope have no recorded autonomy level; the earlier of the two possible days is counted.',
            colContent: 'Content',
            opProduction: 'Systems in scope in production',
            opInterventionsKind: 'Interventions recorded in the window: {kind}',
            opChanges: 'Version changes recorded in the window',
            riskTier: 'Systems in scope: {tier}',
            incSerious: 'Incidents determined serious in the window',
            incUndetermined: 'Incidents opened in the window whose seriousness is not determined',
            incCompletion: 'Serious incidents with a completion report',
            cReceived: 'Complaints received in the window',
            cClosed: 'Complaints closed in the window',
            cOpen: 'Complaints open now',
            cCrossings: 'Complaint threshold crossings in the window',
            driftRow: '{kind}: {now} in the window; {before} from {from} to {to}',
            driftUnreadable: 'Monitoring events by kind, in the window and the window before it',
            nextDueUndated: 'No next due date: the installed framework states no date from which this applies.',
            retrySr: 'read the periodic report contents again',
            gapRecordNotInstalled: 'No installed framework declares the report to the senior management body, so its scope, contents and interval are not known',
            changesHeading: 'Change records',
            changesEmpty: 'No change is recorded.',
            colChangeRecorded: 'Recorded on',
            significanceRecord: 'Record significance',
            significanceRecordSr: 'for the change recorded on {date}',
            significanceBasisShort: 'Basis',
            significanceChoose: 'Choose yes or no.',
            significanceAnsweredBy: 'Answered by {user} on {date}',
            significanceAlreadyRecorded: 'This change already carries a significance answer.',
            significanceBasisRequired: 'Record the basis with the answer.',
            sigHeading: 'Significant changes to high-risk systems',
            sigSince: 'Recorded after the last report export on {date}.',
            sigSinceNone: 'No report export has carried these contents yet, so every change is listed.',
            sigEmpty: 'No change is recorded as significant.',
            sigRow: '{system}: {change} ({date})',
            sigNotRecorded: 'Changes with no significance answer',
            retiredFigure: 'Retired systems',
            retiredOn: 'Retired on {date}: no annual review is due after retirement.',
            retiredNoDay: 'Retired; retirement day not recorded.',
            retiredReading: 'Retired',
            significanceLabel: 'Significant change',
            significanceHint: 'Whether this is a significant change within Điều 2 khoản 9, which refers to the re-assessment cases of the decree that implements the AI law. A person records the answer and its basis; ComplianceOne does not decide it.',
            significanceYes: 'Yes, significant',
            significanceNo: 'No, not significant',
            significanceBasis: 'Basis for the answer',
            significanceBasisHint: 'Required with a yes or no answer.',
        },
        incidentAi: {
            heading: 'AI system in this incident',
            description: 'Link the incident to the AI system it concerns, and record the serious-incident duties of the AI record. The platform submits no report to a State authority.',
            fLink: 'AI system',
            linkHint: 'Choose the AI system whose operation this incident concerns.',
            noProfiles: 'No AI system is recorded for your organisation.',
            saveLink: 'Save link',
            linkSaved: 'Linked to this incident.',
            recordHeading: 'Serious-incident record',
            fSerious: 'Serious incident',
            seriousYes: 'Serious',
            seriousNo: 'Not serious',
            notDetermined: 'Not determined',
            fSeriousBasis: 'Basis for the determination',
            basisHint: 'A serious determination raises one reassessment task for the system owner.',
            reassessment: 'Reassessment task raised',
            reassessmentYes: 'Yes, to the system owner',
            reassessmentNo: 'No',
            deadline: 'Report deadline',
            deadlineStatedOne: 'Not computed. {framework} states no period for this report.',
            deadlineStatedMany: 'Not computed. These frameworks state no period for this report: {frameworks}.',
            deadlineNoneDeclared: 'Not computed. The platform sets no report date for this incident.',
            deadlineSeparateOne: '{framework} states reporting periods for serious AI incidents. The period that applies depends on how this incident is determined, which a person records, so none is applied here.',
            deadlineSeparateMany: 'Reporting periods for serious AI incidents are stated in {frameworks}. The period that applies depends on how this incident is determined, which a person records, so none is applied here.',
            deadlineRecordBelow: 'Record the report you made below.',
            notInForce: 'not yet in force',
            stateReports: 'Reports to the authorities',
            authority: {
                competent_state_authority: 'Competent State authority',
                state_bank_it_department: 'State Bank IT Department',
            },
            fRemediation: 'Remediation',
            fAuthority: 'Authority the report was made to',
            authorityHint: 'Record the reference of a report made by a person outside the platform.',
            fReportedOn: 'Date reported',
            fReference: 'Reference of the report',
            referenceHint: 'The reference the authority gave, or the reference in your own file.',
            completion: 'Completion report',
            fCause: 'Cause',
            fDamage: 'Damage',
            fCompletedOn: 'Date completed',
            emergency: 'Emergency stop or manual operation',
            emergencyNote: 'Recorded as an intervention of the AI system, with its time and person.',
            notification: 'Customer notification',
            notificationNote: 'Read from the notification statuses of this incident, not copied here.',
            status: 'Serious-incident state',
            recordAction: 'Record the serious-incident duties',
            recordTitle: 'Serious-incident record',
            saveRecord: 'Save record',
        },
    },
    // Sprint 63.24.0 Slice A — controlled-testing case pages. English copy until the R-57 deferral (sprint 57.7.0).
    controlled_testing: {
        // Sprint 63.36.0: authored English fallback for this module; locale translation remains separate.
        dossierChecklist: {
            heading: 'Dossier documentary reference',
            reference: 'Current amended source reference',
            regime: 'Whether this source regime governed this case is not determined.',
            unassigned: 'No documentary route is assigned. Source routes below are unselected references only.',
            selected: 'Selected documentary route',
            unselected: 'Unselected source reference — no case coverage is assigned',
            observations: 'Current documentary observations may include facts recorded after the filing or role reference date. These counts do not describe the dossier as it stood at filing or assess legal completeness.',
            recorded: 'Recorded fact items',
            partial: 'Partial items',
            pending: 'Pending items',
            historical: 'Historical items',
            source: 'Source',
            pages: 'PDF pages',
            referenceOn: 'Reference date',
            filing: 'Filing date used for route and role context',
            observedOn: 'Current observations as of',
            as_of: 'Current date used for route and role context',
            context: 'Documentary route context',
            form: 'Application form',
            kind: 'Applicant kind',
            unknown: 'Not recorded or not determinable',
            noProjection: 'No matching documentary projection is available; review required.',
            person: 'Personnel record',
            factDate: 'Actual fact reference date',
            status: {
                available: 'Source reference available',
                undeclared: 'No dossier checklist is declared.',
                unavailable: 'Dossier checklist is unavailable; review required.',
                not_applicable: 'This is not an admission-form documentary route.',
            },
            codes: {
                case_anchor: 'Immutable case form anchor',
                filed_snapshot: 'Immutable filed snapshot',
                draft_record: 'Current draft record',
                reported_registry_information: 'Reported registry information',
                none: 'No supported fact source',
                required: 'Source-listed component',
                conditional_if_any: 'Conditional source component',
                one_of: 'Documentary alternatives',
                historical_repealed: 'Historical separate attachment — repealed',
                documentary_route: 'Documentary route',
                unknown: 'Applicability not determined',
                historical: 'Historical reference',
                recorded_facts: 'Recorded bounded facts',
                recorded_fact: 'Recorded bounded fact',
                partial: 'Partial documentary observation',
                not_recorded: 'Not recorded',
                not_determinable: 'Not determinable',
                recorded: 'Recorded',
                pending: 'Pending',
                conflict: 'Conflicting facts',
                withheld: 'Withheld',
                not_held: 'Not held',
                field_only: 'Descriptive field only',
                filing_metadata_only: 'Filing metadata only; not a completed signed application',
                document_presence_only: 'Held document presence only; contents not assessed',
                classification_reference_only: 'Classification reference only; no approval or compliance assessment',
                statement_date_only: 'Statement date only; no held document inferred',
                reported_information_only: 'Reported information only',
                not_declared: 'No source declaration',
                invalid_declaration: 'Invalid source declaration',
                invalid_declaration_context: 'Invalid source context',
                ambiguous_declaration: 'Ambiguous source declarations',
                applicant_kind_required: 'Explicit applicant context is missing',
                track_not_declared: 'No matching documentary route is declared',
                ambiguous_track: 'Multiple matching documentary routes',
                not_admission_form: 'Not an admission form; no parent route is substituted',
                coverage_limit_exceeded: 'Documentary projection exceeds its technical bound',
                unsupported_binding: 'No supported documentary binding',
                missing_record: 'Required fact is not recorded',
                missing_person: 'Relevant person is not recorded',
                unknown_applicability: 'Conditional applicability is not determined',
                missing_actual_date: 'Actual date is not recorded',
                information_pending: 'Information remains pending; a request is not the information',
                legacy_unconverted: 'Legacy facts have not been converted',
            },
        },
        annexGuidance: {
            heading: 'Source guidance',
            undeclared: 'Guidance is not declared by the installed framework.',
            unavailable: 'Declared guidance is unavailable; review its source declaration.',
            internalSummary: 'Internal source summary. This guidance does not assess a submitted document, signature or filing.',
            sourceDocument: 'Source document',
            sourcePages: 'Source PDF pages',
            legacy: 'Historical record: a provisional report-format absence marker was stored. Current guidance is shown separately.',
            reasons: {
                not_declared: 'No guidance declaration is installed.',
                invalid_declaration: 'The guidance declaration is invalid.',
                invalid_declaration_context: 'The guidance source context is invalid.',
                ambiguous_declaration: 'Multiple guidance declarations require review.',
            },
        },
        parentCopy: {
            operationCapturedAt: 'Creation request captured at',
            acceptedAt: 'Creation accepted at',
            initiatedBy: 'Creation initiated by',
            operationPage: 'Recorded creations page',
            previous: 'Previous page',
            next: 'Next page',
            listFailed: 'Recorded child creations could not be read. Retry the status check; do not submit another creation request.',
            reviewOperations: 'Review recorded child creations',
            knownOperation: 'Recorded operation reference',
            noResume: 'The selected recorded creation has no resume action available.',
            heading: 'Frozen parent solution evidence',
            capturedVersion: 'Captured source solution version',
            capturedAt: 'Source captured at',
            captureHint: 'This snapshot preserves the source version and capture time shown here.',
            copy_absent: 'No parent solution copy recorded.',
            not_recorded: 'The copied solution has no recorded technical-statement file.',
            available: 'The original copied file is available.',
            withheld: 'Parent solution content is withheld. Independent parent read access is required.',
            legacy_lineage_unverified: 'The earlier copy has no verified source lineage. Its file cannot be downloaded through this snapshot.',
            source_unavailable: 'The source solution is unavailable. The copied reference is preserved.',
            lineage_invalid: 'The source lineage needs review. No file is available through this snapshot.',
            not_held: 'The copied file is not currently held.',
            deleted: 'The copied file is deleted. Its original reference remains recorded.',
            released: 'The copied file was released under retention rules. Its original reference remains recorded.',
            release_in_progress: 'Release or destruction is in progress. The copied file is unavailable.',
            loading: 'Checking copied evidence availability…',
            loadFailed: 'Copied evidence availability could not be checked.',
            refresh: 'Reload copied evidence',
            download: 'Download original copied statement',
            downloadFailed: 'The copied file could not be downloaded. Reload its availability and retry.',
            downloadUnavailable: 'Download is unavailable with the current access.',
            opening: 'Creating the child case…',
            creationPending: 'Child creation is awaiting completion. This form keeps the original request; retrying does not create another child.',
            creationUnknown: 'The result of this request is uncertain. Check its status before creating another child. Your original form is preserved.',
            checkCreation: 'Check child creation status',
            resume: 'Complete the recorded child creation',
            operationChoice: 'Recorded child creation',
            chooseOperation: 'Choose the recorded operation to review',
            preparing: 'Creation is being prepared',
            committed: 'Creation accepted; completion pending',
            published: 'Child case is available',
            cancelled: 'Creation was cancelled',
            integrity_conflict: 'Recorded creation needs integrity review',
            resumeFailed: 'The recorded child creation could not be completed.',
            originalRequest: 'Original child request',
            newRequest: 'Return to a new request after review',
            recoveryHint: 'Review the recorded operations explicitly. No operation is selected from its date or name.',
            noOperations: 'No recoverable operation is listed. Review the child list before starting a new request.',
        },
        stopReasonMessages: {
            framework_not_operational: 'The framework is not operational for this case. No transitional determination is made.',
            transitional_rule_ambiguous: 'Multiple transitional rule declarations apply. Review their source declarations.',
            transitional_rule_invalid: 'The declared receipt or confirmation references need source review.',
            transitional_documentary_sources_conflict: 'The actual receipt or confirmation sources disagree or are ambiguous. Compare the separate documentary records.',
            confirmation_mapping_not_declared: 'No source-declared confirmation reference is available. A receipt cannot replace it.',
            legacy_confirmation_claim_not_verified: 'A prior confirmation-date claim exists, but its documentary evidence is not verified. Review that claim separately from receipt.',
            confirmation_scope_conflict: 'The confirmation evidence and its applicable recording scope conflict. Review the preserved sources.',
            historical_recording_scope_review_required: 'The historical processing regime still needs source review. Verified receipt does not resolve that applicability question.',
            authored_documentary_counterpart_not_verified: 'A declared receipt or confirmation counterpart still lacks verified evidence. Review the separate source records.',
            confirmation_regime_not_verified: 'The actual confirmation is recorded, but its processing regime or commencement boundary is not verified.',
            main_and_nested_proceedings: 'Main and nested STOP facts both exist. Review the proceeding identity.',
            stop_reference_not_declared: 'The installed framework does not identify this STOP proceeding.',
            stop_reference_invalid: 'The declared STOP procedure or receipt reference needs review.',
            stored_stop_procedure_changed: 'The installed target differs from the preserved proceeding. Recorded facts have not been moved.',
            stop_lifecycle_not_declared: 'The source-declared proceeding steps are unavailable. Review the declaration.',
            receipt_occurrences_conflict: 'Multiple or conflicting receipt facts need documentary review.',
            receipt_not_verified: 'The actual receipt evidence still needs verification.',
            receipt_not_recorded: 'No actual receipt fact is recorded. Record it from its evidence when known.',
            multiple_decision_occurrences: 'Multiple lifecycle decision facts need documentary review.',
            decision_dates_disagree: 'The actual decision dates differ across documentary sources.',
            decision_references_disagree: 'The decision references differ across documentary sources.',
            decision_evidence_conflict: 'Decision evidence contains a conflict. Preserve the actual sources for review.',
            completion_and_termination_recorded: 'A completion certificate and termination facts are both recorded. Review their documentary conflict.',
            stop_context_unresolved: 'The proceeding context is unresolved. New requests need review; actual facts remain preserved.',
            grant_not_declared: 'No grant declaration is available for a new voluntary request.',
            grant_not_recorded: 'The declared grant has not been recorded. No grant date is inferred.',
            completion_certified: 'An actual completion certificate is recorded. A new voluntary stop request is unavailable.',
            termination_recorded_or_unresolved: 'Termination facts are recorded or need review. New initiation is unavailable.',
            stop_already_filed: 'A voluntary request is already recorded.',
            voluntary_ground_not_declared_or_ambiguous: 'The voluntary-request ground is missing or ambiguous in the installed source.',
            completion_already_filed: 'A completion request is already recorded.',
        },
        stopProceeding: {
            documentaryEnding: 'Documentary ending for this clock',
            verified: 'Evidence verified',
            pending_anchor: 'Required scope anchor not recorded',
            requestFiled: 'Applicant request recorded',
            heading: 'STOP proceeding and documentary decisions',
            intro: 'Record actual documentary acts in their declared proceeding. Applicant requests, receipt, authority decisions and post-stop duties remain separate records.',
            context: 'Proceeding context',
            main: 'Main case proceeding',
            nested: 'Separate proceeding within this case',
            not_declared: 'Not declared by the installed framework',
            unresolved: 'Proceeding declaration needs review',
            ambiguous: 'More than one proceeding context needs review',
            request: 'Voluntary applicant request',
            notFiled: 'No applicant request recorded',
            eligibility: 'Eligibility for a new voluntary request',
            allowed: 'New request available',
            refused: 'New request unavailable',
            review: 'Review the recorded facts and source declarations before this action.',
            receipt: 'Actual stop-dossier receipt',
            receiptHint: 'Enter the authority receipt date and its evidence. Filing, complete-valid confirmation and recording dates remain distinct.',
            pending: 'Pending evidence or applicability',
            recorded: 'Recorded',
            pending_evidence: 'Evidence needs verification',
            conflict: 'Documentary facts disagree or are ambiguous',
            none: 'No documentary decision recorded',
            recordReceipt: 'Record actual receipt',
            recordEvent: 'Record proceeding fact',
            recordTransition: 'Record actual proceeding step',
            recordAuthority: 'Record actual authority decision',
            authority: 'Authority termination record',
            legacy: 'Preserved earlier revocation record',
            raw: 'Decision in the proceeding history',
            decisions: 'Documentary decision comparison',
            decisionHint: 'Each source remains independent. A recorded decision does not fill a closure anchor or erase continuing duties.',
            protocol: 'Saving held evidence',
            ready: 'Held evidence can be linked',
            not_ready: 'Held evidence cannot be linked yet',
            protocolHint: 'Keep this draft and retry after evidence binding becomes available. Uploading holds a file; it does not file a request or record a decision.',
            noInference: 'Receipt does not confirm a complete-valid dossier. A pending phase does not replace an actual receipt date.',
            phase: 'Recorded proceeding state',
            history: 'Immutable proceeding history',
            events: 'Actual proceeding facts',
            clocks: 'Source-declared proceeding clocks',
            noFacts: 'No fact recorded',
            noStates: 'No declared next step available',
            event: 'Declared event',
            state: 'Declared next step',
            date: 'Actual documentary date',
            receivedOn: 'Actual receipt date',
            evidence: 'Evidence references',
            evidenceHint: 'One actual reference per line. A local file reference is subject to its held-evidence checks.',
            ground: 'Declared ground',
            groundNotRecorded: 'Ground not recorded',
            source: 'Source citation',
            actor: 'Documentary actor',
            recordedBy: 'Recorded by',
            recordedAt: 'Recorded at',
            fileHeld: 'Held',
            fileUnavailable: 'File unavailable',
            submit: 'Record documentary fact',
            saved: 'Documentary fact recorded',
            failed: 'The documentary fact could not be recorded.',
            refresh: 'Reload current proceeding',
            stale: 'The case changed. Your draft is retained. Reload before reviewing and submitting again.',
            draftChanged: 'The proceeding or selected declaration changed. Your retained draft has not been reinterpreted.',
            reset: 'Discard unavailable selection',
            close: 'Cancel',
            recipients: 'Actual notice recipients',
            recipient: 'Recipient description',
            addRecipient: 'Add recipient',
            removeRecipient: 'Remove recipient',
            channel: 'Actual notification channel',
            recipientsHint: 'Record actual recipients and channel. This does not establish complete customer coverage.',
            notRecorded: 'Not recorded',
            advisory: 'Source notice advisory',
            invalid_declaration: 'Advisory declaration needs review',
            not_determinable: 'Applicable rule is not determined',
            received_not_confirmed: 'Receipt recorded; complete-valid confirmation not recorded',
            review_required: 'Historical source applicability requires review',
            confirmation: 'Actual complete-valid confirmation',
            transitionalReceipt: 'Actual dossier receipt evidence',
            authorityHint: 'Record an actual authority act independently of applicant filing. Conflicting existing documentary sources remain visible.',
            decisionRef: 'Authority decision reference',
            decidedOn: 'Actual decision date',
            decisionText: 'Authority decision text',
            file: 'Held decision evidence',
            filingHint: 'A voluntary filing records the applicant request only; receipt and authority decision are recorded separately.',
            groundRequired: 'Choose the declared voluntary ground',
            noClock: 'No numeric clock output available',
            reason: 'Review reason',
            confirm: 'Confirm voluntary filing',
            confirmationHint: 'Confirm this applicant request with the entered date and held files. This does not record authority receipt or a decision.',
        },
        registryFacts: {
            correction: 'Outcome data correction',
            correctionHint: 'An evidence-backed correction identifies an erroneous previous fact. It is not a waiver or a successful retrieval.',
            correctionIntent: 'This change corrects an erroneous previous fact',
            correctionReason: 'Correction reason',
            outcomeHistory: 'Previous registry outcome facts',
            person: 'Exact linked personnel record',
            choosePerson: 'Choose a personnel record explicitly',
            personHint: 'Names do not identify a record. Choose the exact record ID; no person is inferred. An unassigned legacy row can be linked once.',
            personUnavailable: 'Personnel choices could not be read or no declared eligible role is available.',
            linkedOutcome: 'Outcome held only on the linked personnel record',
            outcomeHint: 'Record or correct this outcome in the personnel section. This registry row does not write a second outcome.',
            legacy: 'Preserved nonauthoritative registry outcome',
            retrievedOn: 'Actual source-retrieval date',
            suppliedOn: 'Actual component supplied date',
            requestHeading: 'Documentary follow-up requests',
            requestHint: 'Record an existing written request. A request is not the information supplied and does not waive missing information. Requests are append-only.',
            requestOn: 'Actual written-request issue date',
            reference: 'Written-document reference',
            request: 'Append documentary request',
            requestMissing: 'No declared source-specific request is available.',
            information: 'Information availability',
            available_as_reported: 'Available as reported at the recorded date; no eligibility conclusion',
            pending: 'Information remains pending',
            undetermined: 'Information availability is undetermined',
            requestRecorded: 'Documentary request recorded; this does not establish information receipt',
            evidenceNoLongerHeld: 'Request attachment is no longer held',
            noRequest: 'No documentary request is recorded',
            copy: 'Applicant-copy file',
            copyHint: 'Upload holds a file only. Bind it explicitly to this row. A held copy does not override the linked personnel outcome.',
            upload: 'Upload a registry documentary file',
            uploaded: 'Uploaded but not bound: {name}',
            bind: 'Bind uploaded applicant copy',
            unbind: 'Unbind applicant copy',
            held: 'Bound file is held for this exact row',
            notHeld: 'Bound file is not held for this row',
            notBound: 'No applicant-copy file is bound',
            useRequestFile: 'Use this upload as the request attachment',
            noRequestFile: 'No request attachment selected',
            decisionHint: 'A separately recorded decision is preserved as a fact; it does not waive request or information requirements.',
            coverage: 'Requirement coverage',
            count: 'Recorded rows: {count}; unassigned rows: {unassigned}',
            people: 'Eligible people: {eligible}; linked people: {linked}',
            scopeUnavailable: 'Requirement scope is not declared; person linkage remains undetermined',
            refresh: 'Read current registry row',
            refreshed: 'Current row read. Your entries are retained; review current scope, person and source declarations before retrying.',
            stale: 'The row changed. Read it again before retrying; your entries are retained.',
            unavailableDraft: 'Your unsaved person choice is no longer available or differs from a now-fixed link. Clear the draft choice to retain the current link.',
            keepLink: 'Use the current recorded link',
            failed: 'The documentary change could not be recorded.',
            history: 'Immutable request history',
            date: 'Actual date',
            actor: 'Recorded by',
            recordedAt: 'Recorded at',
            classificationOn: 'Outcome classification uses the recorded outcome date',
            outcomeUnresolved: 'The linked outcome or personnel scope is unresolved; the stored code is not treated as available information.',
            requestDeclarationChanged: 'The source-specific request declaration changed. Your draft is preserved and cannot be silently recorded as another kind of request.',
            resetRequestDraft: 'Discard this request draft and use the current declaration',
        },
        applicantSolution: {
            kind: 'Recorded applicant kind',
            kindHint: 'Choose the applicant kind explicitly. The shared application form does not determine it. Leaving it unrecorded keeps the track undetermined and does not create a filing requirement.',
            choose: 'Not recorded',
            kindLocked: 'Applicant kind is locked after filing or authority facts. The recorded value is preserved.',
            keepRecorded: 'Use the current recorded kind and discard the unsaved kind change',
            filedKind: 'Applicant kind in the immutable filed snapshot',
            absentAtFiling: 'Not captured in this legacy filed snapshot',
            track: 'Declared dossier track',
            recorded: 'Recorded using the current declaration',
            not_recorded: 'Applicant kind is not recorded',
            recorded_not_declared: 'Stored value is not resolved by the current declaration',
            not_declared: 'No usable declaration is available',
            invalid_declaration: 'The installed declaration requires review',
            resolved: 'Track resolved from the recorded form and applicant kind',
            applicant_kind_required: 'Record applicant kind to determine the track',
            applicant_kind_not_declared: 'The stored applicant kind cannot resolve a current track',
            track_not_declared: 'No matching declared track is available',
            ambiguous: 'Several conflicting declarations match; no choice is made',
            conditional: 'The cited dossier component includes an investment registration certificate if any. This does not establish that this applicant has or must obtain one.',
            notListed: 'An investment registration certificate is not listed in this cited dossier provision. This is not a general exemption from investment-registration obligations.',
            scheme: 'Referenced information-system security scheme',
            declared: 'The exact referenced scheme is installed',
            named_not_installed: 'The named scheme is declared but not installed. Install the referenced framework; no alternative scheme is substituted.',
            schemeNotDeclared: 'No usable scheme reference is declared. No other installed scheme is substituted.',
            reference: 'Declared target',
            source: 'Source',
            legacyLevel: 'This original security-level link is preserved. It has not been converted or assigned missing source provenance.',
            levelHint: 'New selections record the declaring framework, scheme and tier explicitly. Changing the live solution does not refresh existing frozen child copies or certify system compliance.',
            loadFailed: 'Applicant-kind declarations could not be read. The kind remains unrecorded; no choice is inferred.',
        },
        filing: {
            heading: 'Organisation filing record',
            record: 'Record a filing already made',
            recordHint: 'Record the filing the organisation already made. This action does not send documents or record authority receipt.',
            notDeclared: 'The installed procedure declares no filing requirements. A reviewed filing declaration is needed before a filing can be recorded.',
            immutable: 'The filed snapshot is preserved. This page cannot change a filing already recorded.',
            frozenGroup: 'Draft customer group in the frozen parent-solution copy',
            filedGroup: 'Customer group actually filed',
            filedReport: 'Results-report file reference at filing',
            notRecorded: 'Not recorded',
            notRecordedAtFiling: 'Not recorded in the filed snapshot',
            reference: 'Organisation’s filing reference',
            submittedOn: 'Actual filing date',
            childGroupHint: 'Prefilled from the child’s frozen parent-solution copy. Enter what was actually filed, including a missing draft group. This does not change that copy or the parent’s draft solution.',
            parentGroupHint: 'Enter the customer group actually filed. This records the filed snapshot and does not change the draft solution.',
            approvalReady: 'Internal sign-off is recorded as complete for this child.',
            approvalRequired: 'The child’s internal sign-off must be recorded as complete before its filing can be recorded.',
            draftReport: 'Draft results report',
            reportHint: 'Uploading holds a file. Attach it to this draft as a separate step. Neither action records a filing or verifies the results-report format.',
            reportNotBound: 'No results report is attached to this draft.',
            reportBound: 'Draft report attached and held (file reference: {reference}).',
            reportNotHeld: 'The draft report is no longer held or does not belong to this child. Detach or replace it before recording a filing.',
            reportUnknown: 'The draft report’s held status could not be determined. Read the case again before recording a filing.',
            reportChangeFailed: 'The draft report attachment could not be changed.',
            uploadReport: 'Upload a draft results report',
            uploadHeld: 'Upload completed: {name}. It is not yet attached to this draft.',
            bindReport: 'Attach uploaded report to draft',
            unbindReport: 'Detach draft report',
            leaveUnbound: 'Leave this upload unattached',
            saved: 'The filing record was saved.',
            failed: 'The filing record could not be saved.',
            refreshFailed: 'The current case could not be read. Reload the case before retrying.',
            stale: 'The case changed. Its current version has been read and your entries are retained. Review the current report and sign-off before retrying.',
        },
        lifecycle: {
            heading: 'Documentary facts and their actual dates',
            intro: 'Record what the evidence shows happened, including facts received after an authority outcome. The occurrence date is distinct from when this entry is recorded. The platform makes no authority decision.',
            record: 'Record a documentary fact',
            event: 'Declared fact',
            occurredOn: 'Actual occurrence date',
            evidence: 'Evidence references',
            verification: 'Verification against declared prerequisites',
            recordedAt: 'Entry recorded at',
            actor: 'Documentary actor: {actor}',
            actorAuthority: 'Authority',
            actorOrganisation: 'Organisation',
            actorRelevantMinistries: 'Relevant ministries',
            source: 'Source: {source}',
            recordedBy: 'Entry recorded by: {actor}',
            scopeVerified: 'This application’s recorded anchor is within a reviewed recording period.',
            scopePending: 'Recording period pending: record the declared anchor fact with its actual date and evidence.',
            scopeReview: 'This application’s recording period requires review. No historical regime is inferred.',
            scopeConflict: 'Recorded application anchors conflict. Review their dates and evidence.',
            scopeNotDeclared: 'No reviewed recording period is declared by the installed procedure.',
            scopeAnchor: 'Recorded application anchor: {date}',
            notDeclared: 'No documentary fact declared by the installed procedure',
            empty: 'No documentary facts recorded.',
            verified: 'Prerequisites verified from the recorded facts',
            pending: 'Pending prerequisite facts or a countable declared window',
            conflict: 'Dates or prerequisite facts conflict',
            verificationHint: 'Review the actual dates, supporting evidence and prerequisites in the source. A pending or conflicting fact cannot satisfy a transition prerequisite.',
            choose: 'Choose a declared fact',
            saved: 'The documentary fact was recorded.',
            failed: 'The documentary fact could not be recorded.',
            transitionRemedy: 'Only declared next steps are offered. If a prerequisite is missing, record the documentary fact and its actual date, then retry this step.',
            noNextStep: 'No next step is declared from the recorded state. Independent documentary facts remain recordable.',
            clockEnded: 'Clock ended',
            clockEndedOn: 'Ended on the source-specific actual date: {date}',
            clockRecordedStart: 'Recorded start date: {date}',
            clockHistoricalDue: 'Historical due date: {date}',
            clockConflict: 'Clock dates conflict',
            clockConflictHint: 'The recorded end precedes the start. Review the source evidence; no active due date is shown.',
            clockReview: 'Clock requires review',
            clockReviewHint: 'The installed declaration requires an anchor reading before this clock can be dated.',
            notRecorded: 'Not recorded',
        },
        title: 'Controlled-testing cases',
        subtitle: 'The organisation’s own record of each application to the controlled-testing mechanism. The authority records each step; the platform keeps it with its evidence and derives the clocks the installed record declares. The platform decides nothing.',
        moduleGuideSummary: 'Hold each controlled-testing application: the procedure it follows, the steps the authority has recorded and the clocks the installed record declares. The platform does not assess eligibility or decide the outcome.',
        restricted: 'You need permission to read controlled-testing cases to see them.',
        loading: 'Loading',
        reload: 'Reload',
        notYetRecorded: 'Not yet recorded by the authority',
        notDeclared: 'Not declared by the installed record',
        noChannel: 'No filing channel recorded',
        yes: 'Yes',
        no: 'No',
        list: {
            filtersLabel: 'Filter cases',
            chooseFrameworkFirst: 'Choose a framework first',
            chooseProcedureFirst: 'Choose a procedure first',
            noMatch: 'No match',
            heading: 'Cases',
            openCase: 'Open a case',
            filterFramework: 'Framework',
            filterProcedure: 'Procedure',
            filterStep: 'Recorded step',
            anyFramework: 'Any framework',
            anyProcedure: 'Any procedure',
            anyStep: 'Any step',
            clearFilters: 'Clear filters',
            colApplicant: 'Applicant',
            colProcedure: 'Procedure',
            colStep: 'Recorded step',
            colUpdated: 'Last updated',
            total: '{count} case(s) in total',
            empty: 'No case is held yet. Open one on an installed procedure to start.',
            emptyFiltered: 'No case matches these filters.',
            loadError: 'The cases could not be loaded.',
            retrySubject: 'the case list',
            pagination: 'Pages of cases',
            procedureLabelError: 'The procedure could not be read from the installed record.',
            stepLabelError: 'The step name could not be read from the installed record.',
        },
        pick: {
            frameworkHint: 'Only frameworks that declare a controlled-testing procedure are listed.',
            noCaseFrameworks: 'No installed framework declares a procedure that can carry a case.',
            noFormsOption: '{label} (no application form declared)',
            noFormsInFramework: 'No procedure in this framework declares an application form, so no case can be opened on it. A framework that amends it may declare the forms.',
            framework: 'Installed framework',
            procedure: 'Procedure',
            chooseFramework: 'Choose a framework',
            chooseProcedure: 'Choose a procedure',
            noProcedures: 'This framework declares no procedure that can carry a case.',
            noOpenableFrameworks: 'No installed framework can take a case: a framework installed for reference only does not.',
            hiddenUndeclared: '{count} procedure(s) in this framework declare no lifecycle and are not offered.',
            loadFrameworksError: 'The installed frameworks could not be read.',
            loadProceduresError: 'The procedures of this framework could not be read.',
            needFrameworkRead: 'Reading a framework’s procedures needs the framework read permission.',
        },
        open: {
            noForms: 'This procedure declares no application form, so a case cannot be opened on it. Choose another procedure.',
            title: 'Open a case',
            hint: 'Choose the installed procedure this application follows. The case opens with no authority step recorded.',
            form: 'Application form',
            formHint: 'The forms this procedure declares.',
            applicant: 'Applicant name',
            notes: 'Notes',
            submit: 'Open case',
            cancel: 'Cancel',
            saving: 'Saving…',
            blocked: 'Choose a framework, a procedure and a form, and enter the applicant name.',
            chooseForm: 'Choose a form',
            required: 'Required',
            opened: 'The case was opened.',
            failed: 'The case could not be opened.',
        },
        detail: {
            back: 'Back to cases',
            heading: 'Case',
            applicant: 'Applicant',
            procedure: 'Procedure',
            form: 'Application form',
            framework: 'Framework',
            channel: 'Filing channel',
            usb: 'USB copy',
            usbRecorded: 'USB copy recorded',
            usbNotRecorded: 'USB copy not yet recorded',
            receivedOn: 'Received on',
            notRecorded: 'Not recorded',
            state: 'Recorded step',
            stateUndeclared: 'The recorded step is no longer declared by the installed procedure.',
            history: 'Recorded steps',
            historyEmpty: 'No step has been recorded.',
            colStep: 'Step',
            colEnteredOn: 'Date on the authority’s document',
            colEvidence: 'Evidence',
            colRecordedAt: 'Recorded on',
            clocks: 'Clocks',
            clocksNone: 'The installed record declares no clock for this case.',
            clockRunning: 'Running',
            clockNotStarted: 'Not started',
            clockNoNumeric: 'No numeric period',
            clockNotStartedText: 'The step the clock starts from has no recorded entry, so nothing is counted yet.',
            clockNoNumericText: 'The installed record states no numeric period for this clock.',
            clockPeriod: '{period} {unit}',
            clockStartsOn: 'Counted from {date}',
            clockDue: 'Due {date}',
            clockPaused: 'Paused for {days} day(s)',
            clockSource: 'Source: computed from the recorded anchor',
            clockOwner: 'Obligation owner: {owner}',
            clockName: 'Clock',
            clockStatusHeading: 'Status',
            clockDueHeading: 'Due',
            clockSourceHeading: 'Source',
            clockUnitCalendarDays: 'calendar days',
            clockUnitWorkingDays: 'working days',
            clockUnitMonths: 'months',
            suppressedFrameworkNotOperational: 'Clocks are not computed: the framework is installed for reference only.',
            suppressedProcedureNotDeclared: 'Clocks are not computed: the installed procedure declares no lifecycle.',
            suppressedNotInForce: 'Clocks are not computed: the framework is not in force on {date}.',
            suppressedActivationUnknown: 'Clocks are not computed: the date they bind from is not stated by the installed record.',
            transitionalTitle: 'Transitional status',
            transitionalNotApplicable: 'This procedure carries no transitional rule.',
            transitionalNotDeterminable: 'The installed record does not let the platform determine the transitional status.',
            transitionalAnchorNotRecorded: 'The transitional status waits for the anchor date to be recorded.',
            transitionalYes: 'Transitional: the anchor date is before {cutoff}. No numeric clock is shown for it.',
            transitionalNo: 'Not transitional: the anchor date is on or after {cutoff}.',
            completeness: 'Open items',
            completenessOther: 'An open item applies to this case. The installed record states it.',
            completenessUsb: 'The filing channel requires a USB copy of the dossier set. Record it once it is received. An open item does not block a step.',
            channelUndeclared: 'The recorded channel is not declared by the installed framework.',
            asOf: 'Clocks computed on {date}',
            edit: 'Edit notes and channel',
            editNotes: 'Edit notes',
            recordStep: 'Record a step',
            loadError: 'The case could not be loaded.',
            frameworkUnreadable: 'The framework’s procedure and channel names could not be read, so steps and channels are shown by their identifiers.',
        },
        transition: {
            title: 'Record an authority step',
            hint: 'Enter the step as the authority’s own document states it, with the date on that document and the evidence that shows it. The platform records the step; it does not decide one.',
            step: 'Step',
            chooseStep: 'Choose a step',
            enteredOn: 'Date on the authority’s document',
            evidence: 'Evidence, one reference per line',
            evidenceHint: 'Name the document or attachment that shows this step. At least one is required.',
            submit: 'Record step',
            cancel: 'Cancel',
            saving: 'Saving…',
            recorded: 'The step was recorded.',
            failed: 'The step could not be recorded.',
            blocked: 'Choose a step, enter its date and at least one evidence reference.',
            required: 'Required',
        },
        edit: {
            title: 'Notes and channel',
            titleNotes: 'Notes',
            applicant: 'Applicant name',
            notes: 'Notes',
            channel: 'Filing channel',
            noChannel: 'No channel chosen',
            usbRecorded: 'A USB copy came with the filing',
            usbNotRecorded: 'No USB copy recorded',
            receivedOn: 'Date received',
            submit: 'Save changes',
            saving: 'Saving…',
            saved: 'The case was saved.',
            failed: 'The case could not be saved.',
        },
        stale: 'The case changed since it was opened. It has been reloaded and what you entered is still here. Check it, then submit again.',
        refusedUndeclared: 'The installed procedure does not declare that step. Nothing was recorded.',
        solution: {
            heading: 'Solution evidence',
            intro: 'The one solution this application proposes to test: its type, the customer group it reaches, the technical statement it rests on and the security level it is held to. The platform records what the solution states. It does not decide whether the solution is suitable.',
            loading: 'Loading the solution record…',
            loadError: 'The solution record could not be loaded.',
            retrySubject: 'the solution record',
            readOnly: 'You can read this record. Recording it, changing it and attaching the technical statement need the manage permission.',
            add: 'Record the solution',
            edit: 'Change the solution',
            notRecorded: 'Not yet recorded',
            typeLabel: 'Solution type',
            typeChosen: 'Chosen: {label}',
            typeNotUsed: 'Not used: {label}',
            typeNoneDeclared: 'The installed record declares no solution type, so none can be recorded yet.',
            typeSingleDeclared: 'The installed record declares one solution type only: {label}. The solution description states a choice between two.',
            typeUndeclared: 'Not declared by the installed record ({code})',
            customerGroupLabel: 'Prospective customer group',
            statementLabel: 'Technical statement',
            statementNotAttached: 'Not yet attached',
            statementAttached: 'Attached: {name}',
            statementAttachedNoName: 'Attached. Its name needs the permission to download attachments.',
            statementUpload: 'Attach the technical statement',
            statementRejected: 'The technical statement could not be attached.',
            statementNotBound: 'The file is held for this solution but is not yet attached to it. {message}',
            levelLabel: 'Security level',
            levelRecorded: '{label} ({scheme}; basis: {basis})',
            levelUndeclaredTier: 'Not declared by the installed record ({code})',
            levelNotDeclared: 'Not declared by an installed framework',
            criteriaLabel: 'Technical criteria',
            criteriaTextNotInCorpus: 'Text not in corpus. Source: {source}. No criterion is shown here as a verified requirement.',
            criteriaNotDeclared: 'Not declared by an installed framework.',
            dossierLabel: 'Solution record',
            dossierComplete: 'All four items are recorded. The authority decides what follows.',
            dossierIncomplete: 'Not yet complete. Missing: {items}.',
            item_solution_type: 'the solution type',
            item_customer_group: 'the customer group',
            item_technical_statement: 'the technical statement',
            item_security_level: 'the security level',
            editor_title: 'Record the solution',
            editor_titleEdit: 'Change the solution',
            editor_hint: 'Choose the solution type the installed record declares, enter the customer group as the solution description states it, and choose the security level an installed framework declares. Attach the technical statement after the solution is saved.',
            editor_typeChoose: 'Choose a solution type',
            editor_typeNone: 'The installed record declares no solution type, so none can be chosen.',
            editor_customerGroup: 'Prospective customer group',
            editor_levelChoose: 'Choose a security level',
            editor_levelNone: 'No installed framework declares a security level, so none can be chosen.',
            editor_levelOption: '{label} ({scheme})',
            editor_submit: 'Save the solution',
            editor_saving: 'Saving…',
            editor_cancel: 'Cancel',
            editor_saved: 'The solution was saved.',
            editor_failed: 'The solution could not be saved.',
            editor_stale: 'The solution changed since it was read. It has been reloaded and what you entered is still here. Check it, then submit again.',
        },
        registry: {
            heading: 'Authoritative-source records',
            intro: 'Documentary source records for this application. Person-scoped rows link an exact personnel record; its criminal outcome remains in the personnel section. Requests, source retrieval and actual component supply are separate dated facts. A request or decision does not replace missing information.',
            loading: 'Loading the registry records…',
            loadError: 'The registry records could not be loaded.',
            retrySubject: 'the registry records',
            readOnly: 'You can read these records. Recording a document, changing it and attaching a supplement file need the manage permission.',
            add: 'Record a document',
            edit: 'Change',
            notRecorded: 'Not yet recorded',
            stateBankRetrieves: 'The State Bank retrieves this record. The platform does not call the registry, and the outcome shown is the one recorded from the State Bank’s answer.',
            requirementLabel: 'Requirement',
            requirementNotDeclared: 'Not declared by an installed framework',
            sourceLabel: 'Source',
            sourceRegistry: 'Retrieved from the registry by the State Bank',
            sourceApplicant: 'A copy the organisation supplies',
            outcomeLabel: 'State Bank outcome',
            outcomeClass_retrieved: 'Counts as retrieved',
            outcomeClass_not_retrieved: 'Does not count as retrieved',
            outcomeClass_incomplete_or_inaccurate: 'Does not count as retrieved: incomplete or inaccurate',
            outcomeSeen: 'Date seen: {date}',
            outcomeNotDeclared: 'Not declared by an installed framework, so no outcome can be recorded.',
            outcomeUndeclared: 'Not declared by the installed record ({code})',
            outcomeNotApplicable: 'Not applicable: the document is a copy the organisation supplies.',
            recorderLabel: 'Recorded by',
            supplementLabel: 'Supplement or decision',
            supplementNotNeeded: 'Not needed: the outcome counts as retrieved.',
            supplementRecorded: 'Supplement dated {date}: {reference}',
            supplementFileOnly: 'Supplement dated {date}, with its file attached.',
            supplementFileAttached: 'File: {name}',
            supplementFileNoName: 'File attached. Its name needs the permission to download attachments.',
            decisionRecorded: 'Decision dated {date}: {text}',
            supplementMissing: 'Needed. Record a dated supplement with its reference or file, or a dated decision that none is required.',
            limbLabel: 'Investment registration limb',
            limbApplies: 'Applies on this track. Source: {source}.',
            item_scope_declaration: 'declared requirement scope',
            item_personnel_link: 'exact personnel link',
            item_retrieved_on: 'actual source-retrieval date',
            item_failure_follow_up_declaration: 'source-specific follow-up declaration',
            item_failure_follow_up_request: 'documentary follow-up request',
            item_supplement: 'actual supplied component',
            item_applicant_copy_file: 'held applicant-copy file',
            item_information_pending: 'information remains pending',
            limbConditional: 'This cited dossier component includes an investment registration certificate if any. It does not establish that this applicant has or must obtain one. Source: {source}.',
            limbNotListed: 'This cited dossier component does not list an investment registration certificate. This is not a general investment-registration exemption. Source: {source}.',
            limbNotOnTrack: 'Does not apply on this track.',
            limbTrackNotDeclared: 'The track or source-component declaration is undetermined. No document obligation or exemption is inferred.',
            dossierLabel: 'Record',
            dossierComplete: 'Complete: the row holds what its source and outcome require. The authority decides what follows.',
            dossierIncomplete: 'Not yet complete. Missing: {items}.',
            item_source: 'the source',
            item_outcome: 'the State Bank outcome and the date it was seen',
            item_supplement_or_decision: 'a dated supplement or a recorded decision',
            editor_title: 'Record a document',
            editor_titleEdit: 'Change the document',
            editor_hint: 'Choose the requirement, the document’s source, and the outcome the State Bank answered with the date it was seen. Where the State Bank did not retrieve a document in full, it needs a dated supplement, or a dated decision that none is required.',
            editor_requirement: 'Requirement',
            editor_requirementChoose: 'Choose a requirement',
            editor_requirementNone: 'Every requirement the installed framework declares is already recorded, or none is declared.',
            editor_source: 'Source',
            editor_sourceChoose: 'Choose a source',
            editor_outcome: 'State Bank outcome',
            editor_outcomeChoose: 'Choose an outcome',
            editor_outcomeNone: 'The installed record declares no outcome for this requirement, so none can be chosen.',
            editor_outcomeOn: 'Date the outcome was seen',
            editor_supplementOn: 'Actual component supplied date',
            editor_supplementReference: 'Supplement reference',
            editor_supplementReferenceHint: 'Up to 500 characters: the letter or file reference.',
            editor_decisionOn: 'Decision date',
            editor_decisionText: 'Decision text',
            editor_decisionTextHint: 'Up to 1000 characters: preserve the actual documentary decision and explanation. It does not waive source-specific requests or missing information.',
            editor_submit: 'Save the document',
            editor_saving: 'Saving…',
            editor_cancel: 'Cancel',
            editor_saved: 'The document was saved.',
            editor_failed: 'The document could not be saved.',
            editor_stale: 'The document changed since it was read. It has been reloaded and what you entered is still here. Check it, then submit again.',
            supplementUpload: 'Attach the supplement file',
            supplementRejected: 'The supplement file could not be attached.',
            supplementNotBound: 'The file is held for this document but is not yet attached to it. {message}',
        },
        personnel: {
            generic: {
                heading: 'Declared documentary conditions',
                hint: 'Record each declared answer with its own evidence reference. These are documentary records, not eligibility decisions. Changing a choice requires its evidence to be entered again.',
                evidence: 'Evidence reference for this condition',
                required: 'required when an answer is chosen',
                source: 'Source',
                notDeclared: 'No usable condition declaration is available. No eligibility conclusion is made.',
                not_recorded: 'Not recorded',
                recorded_with_evidence: 'Answer and its evidence reference recorded',
                evidence_missing: 'Recorded answer has no evidence reference',
                option_not_declared: 'The recorded option is no longer declared',
                does_not_apply: 'Does not apply to this record’s declared form and roles',
                not_declared: 'Condition declaration requires review',
                clear: 'Clear this condition answer explicitly',
                legacyHeading: 'Preserved earlier condition content',
                legacyHint: 'Earlier content is preserved separately and has not been converted into current condition answers.',
                legacyBasis: 'Preserved earlier role qualification',
                clearBasis: 'Clear earlier role qualification',
                history: 'Immutable earlier role-qualification history',
                historyNone: 'No earlier role snapshot is recorded.',
                role: 'Role',
                qualification: 'Earlier qualification code',
                dates: 'Recorded role dates',
                evidenceIds: 'Recorded evidence references; current held status is not asserted',
                actor: 'Recorded by',
                time: 'Recorded at',
                versions: 'Record versions',
                reload: 'Read current personnel record',
                stale: 'The record changed. Read the current record, then review your retained entries before retrying.',
                reloaded: 'Current record read. Your entries remain unchanged; review the declared choices and current version before retrying.',
                retiredAnswer: 'Preserved answer not declared by the installed record',
                retainedDraft: 'Retained draft change needs review against the current declaration. It has not been saved.',
                discardDraft: 'Discard this unsaved draft change',
            },
            heading: 'Personnel eligibility evidence',
            intro: 'The people this application names, the dated roles each one holds and the evidence each role rests on. The platform records what the evidence shows. It does not decide whether anyone is eligible.',
            loading: 'Loading the personnel records…',
            loadError: 'The personnel records could not be loaded.',
            retrySubject: 'the personnel records',
            vocabError: 'The roles and outcomes declared by the installed record could not be read.',
            notDeclaredRoles: 'The installed record declares no role for this framework, so no role can be recorded yet.',
            notDeclaredBases: 'The installed record declares no qualification alternative for this framework.',
            notDeclaredOutcomes: 'The installed record declares no criminal-record outcome for this framework, so none can be recorded yet.',
            empty: 'No person is recorded for this case yet.',
            total: '{count} personnel record(s) in total',
            pagination: 'Pages of personnel records',
            readOnly: 'You can read these records. Recording, changing them and attaching evidence need the manage permission.',
            add: 'Add a person',
            personName: 'Name of the person',
            personHint: 'Type the name as it appears on the personal record statement.',
            statementSignedOn: 'Date the personal record statement was signed',
            statementNotRecorded: 'Statement date not recorded',
            edit: 'Edit this record',
            colRole: 'Role',
            colBasis: 'Qualification alternative the evidence shows',
            colDates: 'Dates held',
            colApplicability: 'Applies to the application form',
            colEvidence: 'Evidence',
            noRoles: 'No role is recorded for this person yet.',
            basisNotRecorded: 'Not yet recorded',
            undeclaredRole: 'Role not declared by the installed record ({code})',
            undeclaredBasis: 'Alternative not declared by the installed record ({code})',
            undeclaredOutcome: 'Outcome not declared by the installed record ({code})',
            heldFrom: 'From {from}',
            heldUntil: 'to {to}',
            heldOpen: 'still held',
            applies: 'Applies to this application form',
            doesNotApply: 'Does not apply to this application form',
            applicabilityNotDeclared: 'Not declared by the installed record',
            evidenceNone: 'No evidence is attached to this role.',
            evidenceCount: '{count} file(s) attached. Their names need the permission to download attachments.',
            evidenceUpload: 'Attach evidence to {role}',
            evidenceRejected: 'The evidence could not be attached.',
            evidenceNotBound: 'The file is held for this record but is not yet attached to the role. {message}',
            currency: {
                heading: 'Currency of the statement',
                current: 'Within the declared limit until {date}.',
                older: 'Evidence is older than the declared limit.',
                notDeterminable: 'Not determinable: {reason}.',
                reasonNoStatementDate: 'no statement date is recorded',
                reasonNoLimit: 'the installed record declares no currency limit',
                reasonNoLimitInForce: 'the declared limit is not in force on this date',
                reasonActivationUnknown: 'the date the declared limit takes effect is not stated',
                reasonNotOperational: 'the framework is installed for reference only',
                basisReceipt: 'Judged on the receipt date, {date}.',
                basisAsOf: 'Judged on the date of this view, {date}.',
            },
            outcome: {
                heading: 'Criminal-record check outcome',
                recordOutcome: 'Record the authority’s outcome',
                changeOutcome: 'Change the recorded outcome',
                recordedOn: '{outcome}, reported on {date}',
                title: 'Record the criminal-record outcome the authority gave',
                hint: 'Choose the outcome the State Bank reported and enter the date the authority gave it. The platform keeps that outcome only. The content of the check stays with the authority that holds it, and none of it can be entered here.',
                code: 'Outcome',
                chooseCode: 'Choose an outcome',
                on: 'Date the authority gave the outcome',
                submit: 'Record outcome',
                saving: 'Saving…',
                cancel: 'Cancel',
                saved: 'The outcome was recorded.',
                failed: 'The outcome could not be recorded.',
                blocked: 'Choose an outcome and enter the date the authority gave it.',
            },
            record: {
                addTitle: 'Add a person',
                editTitle: 'Edit this personnel record',
                hint: 'Enter the person’s name and each role with the dates it was held, as the evidence states them. For each role, record the qualification alternative the evidence shows. The platform records that choice; it does not decide whether the person qualifies.',
                roleRow: 'Role {n}',
                role: 'Role',
                chooseRole: 'Choose a role',
                basis: 'Qualification alternative the evidence shows',
                basisNotRecorded: 'Not yet recorded',
                from: 'Held from',
                to: 'Held until',
                toOpen: 'Still held (leave empty)',
                removeRole: 'Remove this role',
                addRole: 'Add a role',
                submitAdd: 'Record person',
                submitEdit: 'Save record',
                saving: 'Saving…',
                cancel: 'Cancel',
                required: 'Required',
                blocked: 'Enter the person’s name and, for each role, the role and the date it was held from.',
                added: 'The person was recorded.',
                saved: 'The record was saved.',
                failed: 'The record could not be saved.',
                stale: 'The record changed since it was read. It has been reloaded and what you entered is still here. Check it, then submit again.',
            },
            conditions: {
                heading: 'Conditions of the legal representative and general director',
                recordConditions: 'Record the conditions',
                changeConditions: 'Change the recorded conditions',
                notRecorded: 'Not recorded',
                nationality: 'Nationality',
                conviction: 'Criminal record',
                sanction: 'Administrative sanction in finance, banking or cybersecurity',
                notDeclared: 'The installed record declares no condition for this application form.',
                title: 'Record what the evidence shows about each condition',
                hint: 'For each condition, choose what the evidence shows. Leave a condition empty when it is not recorded. The platform keeps the choice the evidence shows. It does not decide on the condition.',
                chooseOption: 'Not recorded',
                undeclared: 'Not an option the installed record declares ({code})',
                submit: 'Record conditions',
                saving: 'Saving…',
                cancel: 'Cancel',
                saved: 'The conditions were recorded.',
                failed: 'The conditions could not be recorded.',
                clearRetired: 'Clear the retired criminal-record value',
                clearRetiredHint: 'The criminal-record code was retired, and the installed record no longer declares it. Clearing removes it from this record. The two conditions above are not changed.',
                clearedRetired: 'The retired value was cleared.',
            },
        },
        copy: {
            recordedBy: 'Recorded by',
            notice: 'Platform notice',
        },
        outcome: {
            heading: 'Stop, completion and closure',
            intro: 'A stop is the only way to end a case early. The platform records what the organisation files and what the State Bank decides. The State Bank decides every stop, revocation and completion, and issues every decision and certificate. The platform holds the copy the authority gives and says where that copy came from.',
            readOnly: 'You can read these records. Filing or recording needs the manage permission.',
            // Sprint 62.51.0 (CL-3) — a procedure that declares no organisation outcome filing.
            stopNotOffered: 'The installed procedure of this case offers no stop filing by the organisation, so none is recorded here.',
            completionNotOffered: 'The installed procedure of this case offers no completion filing by the organisation, so none is recorded here.',
            recordedOnIntro: 'Its outcome is recorded from the authority’s and the body’s own documents in:',
            recordedOnNone: 'The installed procedure names no register where its outcome is recorded.',
            loadError: 'The stop, completion, closure and requests could not be read.',
            retry: 'Try again',
            notDeclared: 'Not declared by an installed framework.',
            stop: {
                heading: 'Stop',
                none: 'No stop has been filed for this case.',
                filed: 'Stop filed. The State Bank’s decision is awaited. The platform records no decision of its own.',
                decided: 'The State Bank’s decision is recorded, as the authority gave it.',
                stopDate: 'Stop date, as a person entered it: {date}.',
                groundsHeading: 'Grounds',
                viewGrounds: 'View the grounds ({count})',
                groundsNone: 'Not declared by an installed framework. The platform records no ground.',
                filesHeading: 'Files the stop is filed with',
                role_application: 'Application',
                role_results_report: 'Results report',
                role_stop_plan: 'Stop plan',
                held: 'Held: {name}.',
                notHeld: 'Not held under this case.',
                notFiled: 'Not filed.',
                missing: 'Still missing: {roles}.',
                complete: 'Every file the stop needs is held.',
                endedByCertificate: 'The authority’s certificate of completion is recorded, so no stop is filed on this case.',
                decisionEndedByCertificate: 'The authority’s certificate of completion is recorded, so no decision to stop and revoke the test is recorded on this case.',
                filedEndedByCertificate: 'Stop filed. The authority’s certificate of completion is recorded, so no decision on this stop is awaited.',
                decisionHeading: 'The State Bank’s decision',
                decisionNone: 'No decision is recorded. The platform records one only from the authority’s document.',
                decisionRef: 'Decision reference: {ref}',
                decidedOn: 'Date of the decision: {date}',
                decisionText: 'Text of the decision, as the authority gave it:',
                evidence: 'Evidence: {names}',
            },
            completion: {
                heading: 'Completion',
                none: 'No completion request has been filed.',
                filed: 'Completion request filed. The State Bank evaluates it. The platform decides nothing.',
                role_evaluation_report: 'Evaluation report',
                role_results_report: 'Results report',
                endedByRevocation: 'The State Bank’s decision to stop and revoke the test is recorded, so no completion request or copy of a certificate is recorded on this case.',
                certificateNone: 'No copy of the certificate is recorded. The platform issues no certificate.',
                certificateRecorded: 'The authority’s copy is recorded.',
                copySource: 'Source: the authority’s copy.',
                copyDate: 'Date on the authority’s copy: {date}.',
            },
            closure: {
                heading: 'Closure',
                unavailable: 'Closure is recorded after the authority’s decision is recorded.',
                noticesHeading: 'Stop notices',
                noticesNone: 'No stop notice is recorded.',
                notice: 'Notice sent {date}: {name}.',
                liquidationHeading: 'Liquidation record',
                liquidationNone: 'No liquidation record is recorded.',
                liquidation: 'Liquidation record of {date}: {name}.',
                dutiesHeading: 'Post-stop duties',
                dutiesIntro: 'These duties come from the base instrument. The platform records them and does not create them. Each runs from its own anchor date, which a person enters on the closure record. A duty whose anchor date is not entered shows no date.',
                duty_entitlements_settled: 'Customer rights and obligations are settled',
                duty_settlement_report: 'The settlement is reported in writing',
                citation: 'Source: {citation}',
                dutyNotDeclared: 'Not declared by an installed framework. No period is shown and no date is computed.',
                dutyAmbiguous: 'More than one installed record declares this duty. The platform does not choose between them.',
                dutyNotCountable: 'The installed period is not one the platform counts. No date is computed.',
                dutyDueFromAnchor: 'Period: {period} {unit} from {anchor}. Due {due}.',
                dutyAnchorNotRecorded: 'Anchor not recorded ({anchor}). No date is shown.',
                dutyNotInForce: 'The installed clock for this duty is not in force yet, or the record states no start date from which it can be read. No date is shown.',
                dutyContinuingNoPeriod: 'Continuing duty. The text states no period for a solution outside the scope of this duty, so no period and no date are shown.',
                dutySolutionPointNotRecorded: 'Solution point not recorded. The case’s application form does not show which solution point it is, so no date is computed.',
                anchor_stop_decision: 'the State Bank stop decision date',
                anchor_settlement_completion: 'the settlement completion date',
                anchorLabel_stop_decision: 'State Bank stop decision date',
                anchorLabel_settlement_completion: 'Settlement completion date',
                anchorsHeading: 'Anchor dates',
                settlementReferencePending: 'Settlement reference: pending until the State Bank stop decision date is recorded.',
                settlementReferenceRecorded: 'Settlement reference: {date}, the recorded State Bank stop decision date.',
                anchorRecorded: '{label}: {date}. Evidence: {evidence}.',
                anchorNotRecorded: '{label}: not recorded.',
                unit_months: 'months',
                unit_calendar_days: 'calendar days',
                unit_working_days: 'working days',
            },
            directions: {
                heading: 'The authority’s requests and on-site notices',
                intro: 'Each is recorded in the direction register. Its response period is read from the installed record that declares its kind.',
                none: 'None recorded.',
                kindUndeclared: 'Recorded as “{kind}”, a kind no installed framework of this case declares.',
                registerWithheld: 'The reference, authority, receipt and response period are held in the direction register, which needs its own read permission.',
                manageNeeded: 'Recording a request or notice also needs the direction register’s manage permission.',
                ref: 'Reference: {ref}',
                authority: 'Authority: {name}',
                received: 'Received {date}',
                clockRunning: 'Response due {due}, from the installed record.',
                clockNotDeclared: 'Response period: not declared by an installed framework.',
                clockOther: 'Response period not computed: {reason}.',
                clockNotInForceFrom: 'Response period not computed: the framework that declares it applies only from {date}.',
            },
            actions: {
                fileStop: 'File a stop',
                recordDecision: 'Record the authority’s decision',
                fileCompletion: 'File a completion request',
                recordCertificate: 'Record the authority’s copy',
                recordClosure: 'Record closure',
                recordDirection: 'Record a request or notice',
                chooseFile: 'Choose a file',
                uploading: 'Holding the file…',
                uploaded: 'Held under this case.',
                uploadFailed: 'The file could not be held under this case.',
                cancel: 'Cancel',
                saving: 'Saving…',
                continue: 'Continue to confirmation',
            },
            stopForm: {
                title: 'File a stop',
                hint: 'The stop needs the application, the results report and the stop plan, each held under this case, and the stop date as a person knows it.',
                incomplete: 'Hold the application, the results report and the stop plan, and enter the stop date, before continuing.',
                stopDate: 'Stop date',
            },
            stopConfirm: {
                title: 'Confirm the stop',
                text: 'Filing records the organisation’s stop. The State Bank decides whether the test is stopped and whether the certificate is revoked. Nothing on this page decides for it.',
                confirm: 'File the stop',
                done: 'The stop is filed.',
                failed: 'The stop could not be filed.',
            },
            revoke: {
                title: 'Record the State Bank’s decision',
                hint: 'Record only the authority’s own decision, from its document. The reference and the text are kept as the authority gave them.',
                incomplete: 'Enter the decision reference, the date, the text and the decision file. Each is kept as the authority gave it.',
                ref: 'Decision reference',
                decidedOn: 'Date of the decision',
                text: 'Text of the decision',
                evidence: 'Decision file',
                submit: 'Record the decision',
                done: 'The decision is recorded.',
                failed: 'The decision could not be recorded.',
            },
            completionForm: {
                title: 'File a completion request',
                hint: 'The request needs the evaluation report and the results report, each held under this case.',
                incomplete: 'Hold both reports before filing the request.',
                submit: 'File the request',
                done: 'The completion request is filed.',
                failed: 'The completion request could not be filed.',
            },
            certificate: {
                title: 'Record the authority’s copy',
                hint: 'Record only a copy the authority gave. The platform issues no certificate.',
                incomplete: 'Hold the authority’s copy and enter its date.',
                copyDate: 'Date on the authority’s copy',
                file: 'The authority’s copy',
                submit: 'Record the copy',
                done: 'The authority’s copy is recorded.',
                failed: 'The authority’s copy could not be recorded.',
            },
            anchorCorrection: {
                heading: 'Anchor and documentary-event comparison',
                limitation: 'This compares declared events on this case only. Correcting an anchor does not change a lifecycle event or revocation record. Wrong lifecycle facts require a separate correction path.',
                not_declared: 'No same-case event comparison is declared.',
                pending_anchor: 'Documentary event recorded; an explicit anchor is still required.',
                anchor_only: 'Explicit anchor recorded; no counterpart event is recorded.',
                pending_fact: 'Counterpart evidence is pending verification. No affected due date is shown.',
                agreed: 'The explicit anchor and verified documentary event agree.',
                conflict: 'The records conflict. Both dates are preserved; no affected due date is shown.',
                mappingInvalid: 'The installed comparison declaration needs review.',
                multipleEvents: 'Several counterpart occurrences are recorded. None is selected as authoritative.',
                datesDisagree: 'The recorded actual dates differ.',
                evidenceReview: 'Review the counterpart event’s evidence and declared prerequisites.',
                anchorDate: 'Explicit anchor date',
                eventDate: 'Documentary event occurrence date',
                event: 'Declared event',
                source: 'Source',
                notRecorded: 'Not recorded',
                anchorNotDeclared: 'The installed duty declares no anchor. No due date is computed.',
                dutyConflict: 'The anchor conflicts with its documentary event. No due date is computed.',
                dutyPending: 'The anchor’s counterpart evidence is pending verification. No due date is computed.',
                declaredPeriod: 'Declared period: {period} {unit}.',
                settlementConflict: 'Settlement reference: conflicting documentary dates. No reference date is selected.',
                correct: 'Correct anchor',
                title: 'Correct an explicitly recorded anchor',
                hint: 'Enter the actual corrected date, evidence and justification. The previous record is preserved in immutable history. This action does not record or correct an authority decision, lifecycle fact or revocation.',
                date: 'Corrected actual date',
                evidence: 'Evidence reference for the correction',
                reason: 'Reason for correction',
                required: 'required',
                submit: 'Record anchor correction',
                saved: 'The anchor correction was recorded.',
                failed: 'The anchor correction could not be recorded.',
                stale: 'The case changed. Reload its closure record before trying again. Your entries are retained.',
                reload: 'Reload closure record',
                reloaded: 'Current closure read. Review the current anchor and retained entries before retrying.',
                history: 'Immutable anchor correction history',
                historyNone: 'No anchor correction is recorded.',
                previous: 'Previous anchor record',
                corrected: 'Corrected anchor record',
                recordedBy: 'Recorded by',
                recordedAt: 'Recorded at',
                versions: 'Case versions',
            },
            closureForm: {
                title: 'Record closure',
                hint: 'Record a stop notice, the liquidation record, or both, as each was made.',
                incomplete: 'Hold a stop notice with its date, the liquidation record with its date, or both.',
                noticeFile: 'Stop notice file',
                noticeDate: 'Date the notice was sent',
                liquidationFile: 'Liquidation record file',
                liquidationDate: 'Date of the liquidation record',
                anchorsHint: 'Enter each date as the record states it, with the evidence it rests on. No date is computed here.',
                stopDecisionDate: 'State Bank stop decision date',
                stopDecisionEvidence: 'Evidence of the stop decision',
                settlementDate: 'Settlement completion date',
                settlementEvidence: 'Evidence of the settlement',
                noticeIncomplete: 'A stop notice needs its file and the date it was sent. Enter both, or leave the notice empty.',
                liquidationIncomplete: 'The liquidation record needs its file and its date. Enter both, or leave it empty.',
                anchorIncomplete: 'Each anchor date needs its date and its evidence. Enter both, or leave that anchor empty.',
                anchorRecordedNote: '{label}: already recorded. It is not entered again.',
                refusal: {
                    STOP_DECISION_IN_FUTURE: 'The stop decision date cannot be after today.',
                    SETTLEMENT_BEFORE_STOP: 'The settlement completion date cannot be before the stop it follows.',
                    POST_STOP_ANCHOR_ALREADY_RECORDED: 'This date is already recorded. It is not entered again.',
                },
                submit: 'Record',
                done: 'The closure is recorded.',
                failed: 'The closure could not be recorded.',
            },
            directionForm: {
                kindsLoading: 'Reading the kinds the installed record declares…',
                kindsNone: 'No installed framework of this case declares a kind of request or notice, so none can be recorded here.',
                kindsLoadError: 'The kinds of request or notice could not be read.',
                kindChoose: 'Choose a kind',
                title: 'Record a request or notice',
                hint: 'Recorded in the direction register. Its clock is read from the installed record, not set here.',
                incomplete: 'Complete each field, and give at least one receipt reference.',
                kind: 'Kind',
                ref: 'Reference',
                authority: 'Authority',
                scope: 'Scope',
                received: 'Date and time received',
                receivedDay: 'Date received',
                receivedTime: 'Time received',
                receipt: 'Receipt references, one per line',
                submit: 'Record',
                done: 'The request or notice is recorded.',
                failed: 'The request or notice could not be recorded.',
            },
            fieldLabels: {
                application_file_id: 'Application',
                results_report_file_id: 'Results report',
                stop_plan_file_id: 'Stop plan',
                stop_date: 'Stop date',
                decision_ref: 'Decision reference',
                decided_on: 'Date of the decision',
                decision_text: 'Text of the decision',
                evidence_file_ids: 'Decision file',
                evaluation_report_file_id: 'Evaluation report',
                file_id: 'The authority’s copy',
                copy_dated_on: 'Date on the authority’s copy',
                notice: 'Stop notice',
                liquidation: 'Liquidation record',
                kind: 'Kind',
                direction_ref: 'Reference',
                authority_name: 'Authority',
                scope_description: 'Scope',
                received_at: 'Date and time received',
                receipt_evidence_refs: 'Receipt references',
                'anchors.stop_decision.date': 'State Bank stop decision date',
                'anchors.stop_decision.evidence_ref': 'Evidence of the stop decision',
                'anchors.settlement_completion.date': 'Settlement completion date',
                'anchors.settlement_completion.evidence_ref': 'Evidence of the settlement',
                'anchors.stop_decision': 'State Bank stop decision date',
                'anchors.settlement_completion': 'Settlement completion date',
            },
        },
        children: {
            heading: 'Adjustments and extensions',
            intro: 'A child case changes or continues an application this case already holds. The platform records the steps the State Bank reports and computes the periods the installed record declares. The State Bank decides each request.',
            kindAdjustment: 'Adjustment',
            kindExtension: 'Extension',
            openAdjustment: 'Open an adjustment',
            openExtension: 'Open an extension',
            openTitleAdjustment: 'Open an adjustment',
            openTitleExtension: 'Open an extension',
            openHint: 'The platform copies this case’s solution record as it stands now. The copy does not change when the solution is edited later.',
            formLabel: 'Application form',
            formNone: 'The installed record declares no application form for this kind.',
            notesLabel: 'Notes (optional)',
            submit: 'Open the case',
            saving: 'Opening…',
            cancel: 'Cancel',
            opened: 'The child case is open.',
            openFailed: 'The child case could not be opened.',
            empty: 'No adjustment or extension is recorded on this case.',
            loadError: 'The adjustments and extensions could not be read.',
            unavailable: 'No child case can be opened on this case. {reason}',
            refusedReason: {
                grant_not_declared: 'The parent case has no grant recorded by an installed declaration.',
                grant_not_recorded: 'The parent case has no grant recorded yet.',
                parent_procedure_not_declared: 'The parent case’s procedure declares no lifecycle, so no child case can open on it.',
                framework_not_operational: 'The framework is installed for reference, so no child case can open on it.',
                parent_framework_not_installed: 'The framework is not installed for this organisation.',
                kind_not_declared: 'No installed record declares this kind of child case.',
                kind_ambiguous: 'More than one installed record declares this kind of child case.',
                parent_stopped_and_revoked: 'The State Bank’s decision to stop and revoke the parent test is recorded, so no child case can open on it.',
            },
            readOnly: 'You can read these cases. Opening a child case needs the manage permission.',
            clocksHeading: 'Clocks',
            noClocks: 'No clock applies to this request under the installed record.',
            clockRunning: '{name}: due {due}. Counted {period} {unit} from {start}.',
            clockPaused: '{name}: due {due}. Counted {period} {unit} from {start}, with {paused} {unit} paused.',
            clockNotStarted: '{name}: not started. No entry is recorded for its start step.',
            clockNoNumeric: '{name}: the installed record declares no numeric period.',
            clockSource: 'Computed from the recorded step date. It is not a date set by the authority.',
            clockOwnerAuthority: 'Owed by the State Bank.',
            clockOwnerOrganisation: 'Owed by the organisation.',
            unitWorkingDays: 'working days',
            unitCalendarDays: 'calendar days',
            unitMonths: 'months',
            resultsHeading: 'Results report',
            resultsProvisional: 'Format provisional. The results-report format is in Phụ lục V of Decree 94, which is not in the corpus. No format is shown as verified.',
            filingHeading: 'Filing',
            filingNotRecorded: 'Not recorded. A filing is recorded only against an internal sign-off that is recorded as complete.',
            filingRecorded: 'Recorded by the organisation: reference {reference}, filed {date}.',
            signoffNone: 'No internal sign-off has been requested for this case.',
            signoffPending: 'Internal sign-off: pending. A filing cannot be recorded until the sign-off is recorded as complete.',
            signoffComplete: 'Internal sign-off: recorded as complete.',
            signoffNotComplete: 'Internal sign-off: recorded as not complete. A filing cannot be recorded on it.',
            solutionHeading: 'Solution copied from the parent',
            solutionCopied: 'Copied from the parent solution record at version {version}.',
            solutionNone: 'The parent holds no solution record, so nothing was copied.',
            recordStep: 'Record a step',
            retry: 'Try again',
            currentStep: 'Current step: {step}',
            noStep: 'No step is recorded yet.',
            chooseForm: 'Choose a form',
        },
    },
    // Sprint 63.23.0 — the OAuth consent screen at /oauth/consent.
    oauth_consent: {
        error_request_not_found: 'This request has expired or was already answered. Return to the app and start connecting it again.',
        error_request_already_decided: 'This request was already answered. Return to the app; if it is not connected, start connecting it again.',
        error_client_revoked: 'This app has been revoked, so it cannot connect. Ask an administrator of your organisation if you need it.',
        page_title: 'Approve an app',
        title: 'Allow {client} to use ComplianceOne?',
        title_pending: 'Approve an app',
        loading: 'Loading the request…',
        intro: '{client} is asking to use ComplianceOne on your behalf.',
        client_label: 'App',
        origin_label: 'Registered by',
        origin_admin: 'An administrator of your organisation',
        origin_dynamic: 'The app itself — no administrator has reviewed it',
        redirect_label: 'Sends you back to',
        organisation_label: 'Organisation',
        scopes_heading: 'It will be able to',
        scope_read: 'Read your organisation’s records (mcp:read)',
        scope_write: 'Propose changes for approval (mcp:write)',
        scope_write_note: 'A proposed change is not made until an administrator approves it in the approval queue.',
        scope_other: 'Another permission: {scope}',
        tools_heading: 'Tools it can use',
        tools_none: 'No tools.',
        acts_as_you: 'It acts as you, with no more access than your own role gives you. You can revoke it at any time under Connected apps on your profile.',
        approve: 'Allow',
        deny: 'Deny',
        submitting: 'Sending your decision…',
        missing_request: 'This page was opened without a request. Return to the app and start connecting it again.',
        error_load: 'The request could not be read. It may have expired or already been answered; return to the app and start connecting it again, or reload the request.',
        reload: 'Reload the request',
        error_decide: 'Your decision could not be sent.',
        redirecting: 'Returning you to the app…',
        // Gate E12.5 pass 1 (P2-8) — a redirect whose "host" says nothing about
        // who receives the approval.
        redirect_warning_title: 'Check where this approval goes',
        redirect_warning_loopback: 'This app receives the approval at an address on your own computer ({host}). Any program running on this computer could be listening there. Allow it only if you started this connection yourself, on this computer.',
        redirect_warning_private_use_scheme: 'This app receives the approval through a link that an app installed on your device opens ({host}). Any installed app can claim such a link. Allow it only if you started this connection yourself, from the app you expect.',
    },
    login: {
        title: 'AesirX ComplianceOne',
        subtitle: 'Kirjaudu tilillesi',
        email: 'Sähköposti',
        password: 'Salasana',
        sign_in: 'Kirjaudu sisään',
        signing_in: 'Kirjaudutaan...',
        mfa_code: 'MFA-koodi',
        mfa_placeholder: 'Syötä 6-numeroinen koodi',
        mfa_instruction: 'Syötä 6-numeroinen koodi todennussovelluksestasi.',
        // ── Sprint 63.7.0: organisation-level MFA enforcement + IP allowlist enforcement ──
        password_change_required_title: 'Change your password',
        password_change_required_intro: 'Your administrator requires you to choose a new password before you continue. Nothing else is available until you do.',
        password_change_current: 'Current password',
        password_change_current_hint: 'The password you signed in with.',
        password_change_new: 'New password',
        password_change_new_hint: 'At least 12 characters, with an upper-case letter, a lower-case letter, a digit and a special character. It must differ from your last 12 passwords.',
        password_change_confirm: 'Confirm new password',
        password_change_mismatch: 'The two new passwords do not match.',
        password_change_field_required: 'This field is required.',
        password_change_submit: 'Change password',
        password_change_failed: 'Your password could not be changed.',
        password_change_done: 'Your password has been changed and you have been signed out everywhere. Sign in with your new password.',
        password_change_sign_out: 'Sign out',
        mfa_enrolment_required_title: 'Set up two-factor authentication',
        mfa_enrolment_required_intro: 'Your organisation requires a second factor for your account. Set one up to continue — it takes a minute and you can finish it here.',
        mfa_enrolment_required_sign_out: 'Sign out instead',
        mfa_enrolment_required_continue: 'Continue to the platform',
        mfa_enrolment_required_blocked: 'Until this is set up, the rest of the platform is unavailable to you.',
        // ── Sprint 63.6.3, gate E12.5 pass 2: the TOTP step-up page for a LIVE session held at MFA_REQUIRED ──
        mfa_step_up_title: 'Enter your verification code',
        mfa_step_up_intro: 'Your organisation asks for a code from your authenticator app before this session can continue. Enter it below to carry on where you were.',
        mfa_step_up_instruction: 'Enter the 6-digit code from your authenticator app, or one of your backup codes.',
        mfa_step_up_locked: 'Too many incorrect codes were entered. Wait a few minutes, then try again.',
        mfa_step_up_not_enrolled: 'This account has no authenticator set up, so there is no code to enter. Sign out and sign in again.',
        mfa_step_up_accepted: 'Code accepted. Continuing.',
        mfa_step_up_code_label: 'Verification code',
        mfa_step_up_placeholder: 'Authenticator code or backup code',
        mfa_step_up_refresh_failed: 'Your code was accepted, but the page could not confirm it with the server. Try again to continue.',
        verify: 'Vahvista',
        verifying: 'Vahvistetaan...',
        forgot_password: 'Unohditko salasanan?',
        account_locked: 'Tili on lukittu. Yritä myöhemmin uudelleen.',
        account_deactivated: 'Tili on poistettu käytöstä. Ota yhteyttä järjestelmänvalvojaan.',
        invalid_mfa: 'Virheellinen MFA-koodi. Yritä uudelleen.',
        invalid_credentials: 'Virheellinen sähköposti tai salasana.',
        login_failed: 'Kirjautuminen epäonnistui',
        session_limit_reached: 'Tilisi on saavuttanut aktiivisten istuntojen rajan. Kirjaudu ulos toisella laitteella tai pyydä järjestelmänvalvojaa peruuttamaan olemassa oleva istunto ja yritä uudelleen.',
        session_expired: 'Istuntosi on vanhentunut. Lataa sivu uudelleen ja yritä uudelleen.',
        sso_divider: 'or',
        sso_sign_in_with: 'Sign in with',
        sso_checking: 'Checking sign-in options…',
        sso_completing: 'Completing sign-in…',
        sso_failed: 'Single sign-on did not complete. Contact your administrator.',
        sso_required: 'This organisation requires single sign-on. Use the link your administrator gave you.',
        sso_options_label: 'Single sign-on options',
        sso_provider_session_not_ended: 'You are signed out of ComplianceOne, but your identity provider session was not ended. If you share this device, sign out at your identity provider as well.',
    },
    auth: {
        forgot_password: {
            title: 'Unohtunut salasana',
            subtitle: 'Syötä sähköpostiosoitteesi, niin lähetämme sinulle linkin salasanan nollaamiseksi.',
            send: 'Lähetä nollauslinkki',
            sending: 'Lähetetään...',
            success: 'Jos kyseinen sähköpostiosoite on olemassa, salasanan nollauslinkki on lähetetty.',
            back_to_login: 'Takaisin kirjautumiseen',
        },
        reset_password: {
            title: 'Nollaa salasana',
            subtitle: 'Syötä uusi salasanasi alle.',
            new_password: 'Uusi salasana',
            confirm_password: 'Vahvista salasana',
            reset: 'Nollaa salasana',
            resetting: 'Nollataan...',
            success: 'Salasanasi on nollattu onnistuneesti.',
            go_to_login: 'Siirry kirjautumiseen',
            password_mismatch: 'Salasanat eivät täsmää.',
            invalid_token: 'Virheellinen tai puuttuva nollausavain. Pyydä uusi salasanan nollauslinkki.',
            requirements_title: 'Salasanavaatimukset:',
            password_requirements: 'Salasanan on täytettävä seuraavat vaatimukset:',
            rules: {
                min_length: 'Vähintään 12 merkkiä',
                uppercase: 'Vähintään yksi iso kirjain',
                lowercase: 'Vähintään yksi pieni kirjain',
                digit: 'Vähintään yksi numero',
                special: 'Vähintään yksi erikoismerkki',
            },
        },
        verify_email: {
            title: 'Sähköpostin vahvistus',
            verifying: 'Vahvistetaan sähköpostiosoitettasi...',
            success: 'Sähköpostiosoitteesi on vahvistettu onnistuneesti.',
            invalid_token: 'Virheellinen tai puuttuva vahvistusavain.',
            failed: 'Sähköpostin vahvistus epäonnistui. Avain voi olla virheellinen tai vanhentunut.',
            go_to_login: 'Siirry kirjautumiseen',
        },
        offline: {
            title: 'Olet offline-tilassa',
            message: 'Näyttää siltä, että internetyhteytesi on katkennut. Tarkista verkkoyhteytesi ja yritä uudelleen.',
        },
    },
    intake: {
        title: 'Tietopyyntö',
        subtitle: 'Lähetä tietopyyntö organisaatiollemme',
        fields: {
            full_name: 'Koko nimi',
            email: 'Sähköpostiosoite',
            phone: 'Puhelinnumero (valinnainen)',
            request_type: 'Pyynnön tyyppi',
            description: 'Kuvaile pyyntöäsi',
            preferred_language: 'Ensisijainen kieli',
            consent: 'Suostun tämän pyynnön käsittelyyn',
        },
        submit: 'Lähetä pyyntö',
        validation: {
            name_required: 'Koko nimi on pakollinen',
            email_required: 'Kelvollinen sähköpostiosoite on pakollinen',
            type_required: 'Valitse pyynnön tyyppi',
            description_required: 'Kuvaile pyyntöäsi',
            consent_required: 'Sinun on suostuttava käsittelyyn jatkaaksesi',
        },
        error_title: 'Virhe',
        config_load_error: 'Konfiguraation lataaminen epäonnistui. Tarkista URL-osoite.',
        organization_label: 'Organisaatio',
        customer_reference: 'Asiakasviitenumero',
        country: 'Maa / Lainkäyttöalue',
        preferred_channel: 'Ensisijainen vastauskanava',
        channels: {
            email: 'Sähköposti',
            postal: 'Posti',
            phone: 'Puhelin',
            portal: 'Portaali',
        },
        confirmation: {
            // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
            next_steps_legal_matter: 'Your request will be handled as a legal matter: the organisation will review it and contact you about it. Please keep your reference number.',
            title: 'Pyyntö vastaanotettu',
            message: 'Pyyntösi on tallennettu onnistuneesti. Viitenumerosi on:',
            next_steps: 'Käsittelemme pyyntösi mahdollisimman pian sovellettavan lainsäädännön mukaisesti. Arvioitu käsittelyaika on 30 työpäivää.',
        },
        // English until Phase 25 (R-57).
        // fix-sprint #431 (owner ruling 2026-10-03) — the acknowledgement emailed
        // to the requester when Settings › Notifications "Send confirmation email to
        // requester" is on. {name}, {organization} and {reference} are filled in.
        acknowledgement_email: {
            subject: 'We received your request (reference {reference})',
            greeting: 'Hello,',
            body: '{organization} has received your request concerning your personal data.',
            reference: 'Your reference is {reference}. Please quote it in any message about this request.',
            closing: 'We will contact you about the next steps. This message was sent automatically.',
        },
        request_type_not_offered: "This kind of request is not one this organisation's installed frameworks provide for. Please choose another request type.",
        // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
        legal_matter_right: 'What do you wish to raise?',
        legal_matter_right_hint: 'This request is received as a legal matter. Choose which of these you wish to raise.',
        legal_matter_right_not_sure: 'Not sure',
        error_summary: 'Your request was not sent. Please correct the following:',
        request_type_not_enabled: 'This form does not accept that request type. Please choose another request type.',
        submit_failed: 'Your request could not be submitted: {reason}',
    },
    request_types: {
        access: 'Pääsy (DSAR)',
        deletion: 'Poistaminen',
        correction: 'Oikaisu / päivitys',
        objection: 'Vastustaminen',
        portability: 'Tietojen siirrettävyys',
        restriction: 'Käsittelyn rajoittaminen',
        consent_withdrawal: 'Suostumuksen peruuttaminen',
        protective_measures: 'Pyydä henkilötietojen suojatoimia ja ratkaisuja',
        processing_information: 'Be informed about the processing of personal data',
        consent_decision: 'Consent or refuse consent to processing',
        complaint_or_compensation: 'Complaint, denunciation, lawsuit or claim for damages',
    },
    statuses: {
        new: 'Uusi',
        in_review: 'Arvioitavana',
        waiting_on_requester: 'Odotetaan pyytäjää',
        waiting_on_internal_data: 'Odotetaan sisäistä tietoa',
        approved: 'Hyväksytty',
        closed: 'Suljettu',
        rejected: 'Hylätty',
    },
    admin: {
        nav: {
            rights_requests: 'Tietopyynnöt',
            templates: 'Mallipohjat',
            response_templates: 'Vastausmallit',
            data_mapping: 'Tietokartoitus',
            governance: 'Hallinto',
            consent: 'Suostumus',
            assessments: 'Arvioinnit',
            classification: 'Luokitus',
            monitoring: 'Valvonta',
            incidents: 'Tapaukset',
            onboarding: 'Käyttöönotto',
            connectors: 'Liittimet',
            vendor_risk: 'Toimittajariski',
            deletion_orchestration: 'Poiston orkestrointi',
            discovery: 'Tietojen löytäminen',
            incident_ops: 'Tapausten hallinta',
            program_governance: 'Ohjelmahallinto',
            access_accountability: 'Pääsyn vastuullisuus',
            consent_properties: 'Suostumuksen ominaisuudet',
            localization: 'Lokalisointi',
            regulatory: 'Sääntely',
            partner_workspace: 'Kumppanityötila',
            audit_trail: 'Tarkastuspolku',
            forseti: 'Forseti',
            settings: 'Asetukset',
            help_center: 'Ohjekeskus',
            dashboard: 'Yhteenveto',
            platform: 'Alusta',
            integrations: 'Integraatiot',
            cmp_dsar_sync: 'CMP-DSAR-synkronointi',
            cmp_integration: 'CMP-integraatio',
            purpose_mapping: 'Tarkoitusten kytkentä',
            webhook_config: 'Webhook-asetukset',
            bulk_sync: 'Joukkosynkronointi',
            privacy_scanner: 'Tietosuojaskanneri',
            privacy_monitoring: 'Tietosuojavalvonta',
            real_time_scanner: 'Reaaliaikaskanneri',
            compliance_forms: 'Vaatimustenmukaisuuslomakkeet',
            forms_dashboard: 'Lomakkeiden yhteenveto',
            form_templates: 'Mallipohjat',
            form_catalog: 'Lomakekatalogi',
            pack_catalog: 'Pakettikatalogi',
            org_groups: 'Organisaatioryhmät',
            consolidation: 'Konsolidointi',
            organizations: 'Organisaatiot',
            org_list: 'Kaikki organisaatiot',
            intake_configs: 'Vastaanottomääritykset',
            task_management: 'Tehtävähallinta',
            unified_audit: 'Yhdistetty Tarkastus',
            evidence_packs: 'Todistuspaketit',
            framework_evidence_packs: 'Framework evidence packs',
            audit_periods: 'Tarkastusjaksot',
            regulatory_updates: 'Sääntelypäivitykset',
            departments: 'Osastot',
        },
        sidebar_category_data_privacy_rights: 'Tietosuoja & Oikeudet',
        sidebar_category_vendor_third_party: 'Toimittaja & Kolmannen Osapuolen Hallinta',
        sidebar_category_risk_assessment: 'Riski & Arviointi',
        sidebar_category_operations_automation: 'Toiminnot & Automaatio',
        sidebar_category_sector_overlays: 'Sector Overlays',
        sidebar_category_task_workflow: 'Tehtävä & Työnkulku',
        sidebar_groups: {
            consent_cmp_webhooks: 'CMP ja Webhooks',
            // Sprint 62.28.0 — marketing-communications registers.
            marketing_communications: 'Marketing Communications',
            data_mapping_ropa: 'RoPA-rekisteröinti',
            data_mapping_classification: 'Luokittelu',
            data_mapping_governance: 'Hallinto',
            access_live_monitoring: 'Live-seuranta',
            access_reviews_investigations: 'Arvostelut ja tutkimukset',
            access_evidence_policies: 'Todisteet ja käytännöt',
            classification_taxonomies: 'Taksonomiat ja etiketit',
            classification_workflow: 'Työnkulku',
            classification_insights: 'Näkemyksiä',
            connectors_runtime: 'Suoritusaika',
            connectors_due_diligence: 'Due Diligence',
            connectors_coverage: 'Kattavuus',
            cp_definitions: 'Määritelmät',
            cp_lifecycle: 'Elinkaari',
            cp_quality: 'Laatu ja todisteet',
            deletion_execution: 'Toteutus',
            deletion_vendors: 'Myyjät',
            deletion_evidence: 'Todisteet ja integraatio',
            discovery_rules: 'Säännöt ja mallit',
            discovery_scanning: 'Skannaus',
            discovery_review: 'Tarkista työnkulku',
            discovery_insights: 'Näkemyksiä',
            governance_oversight: 'Valvonta',
            governance_reporting: 'Raportointi',
            governance_vendors: 'Myyjät ja rajat ylittävät',
            governance_lifecycle: 'Elinkaari',
            incident_ops_dashboards: 'Kojelaudat',
            incident_ops_sla: 'SLA Konfig',
            incident_ops_playbooks: 'Toimintakirjat',
            incident_ops_vendor_regulator: 'Myyjä ja sääntelijä',
            incident_ops_evidence: 'Todisteet ja arvostelu',
            incidents_workload: 'Työmäärä',
            incidents_regulatory: 'Sääntely',
            incidents_reporting: 'Raportointi',
            localization_content: 'Sisältö',
            regulatory_frameworks: 'Frameworks',
            localization_regulatory_registers: 'Regulatory Registers',
            localization_penalties_enforcement: 'Penalties & Enforcement',
            localization_sector_overlays: 'Sector Overlays',
            // Sprint 63.43.0 — Sector Overlays (Module 30): management and sector groups.
            sector_overlays_management: 'Overlay Management',
            sector_overlays_banking_payments: 'Banking & Payments',
            sector_overlays_crypto_assets: 'Crypto Assets',
            sector_overlays_manufacturing: 'Manufacturing',
            sector_overlays_food_traceability: 'Food Traceability',
            sector_overlays_transport_logistics: 'Transport & Logistics',
            sector_overlays_public_administration: 'Public Administration',
            sector_overlays_digital_technology: 'Digital Technology',
            sector_overlays_publishing: 'Publishing',
            sector_overlays_education: 'Education',
            sector_overlays_cross_sector: 'Cross-Sector',
            localization_data_exploitation: 'Data Exploitation',
            localization_cybersecurity_assurance_index: 'Cybersecurity Assurance Index',
            localization_food_traceability: 'Food Traceability',
            localization_observability: 'Havaittavuus',
            monitoring_metrics: 'Mittarit',
            monitoring_programs: 'Ohjelmat',
            monitoring_tasks: 'Tehtävät',
            monitoring_controls: 'Säätimet',
            org_entities: 'Entiteetit',
            org_config: 'Kokoonpano',
            org_frameworks: 'Viitekehykset ja koonti',
            partner_overview: 'Yleiskatsaus',
            partner_partners: 'Yhteistyökumppanit',
            partner_delivery: 'Toimitus',
            partner_support: 'Tuki',
            platform_identity: 'Identiteetti ja roolit',
            platform_access_control: 'Kulunvalvonta',
            platform_auth_session: 'Todennus ja istunto',
            platform_access_reviews: 'Pääsy arvosteluihin',
            platform_files: 'Tiedostot',
            platform_notifications: 'Ilmoitukset',
            platform_module_config: 'Moduulin kokoonpano',
            platform_ops: 'Ops & lokit',
            pg_health: 'Ohjelma Terveys',
            pg_maturity: 'Kypsyys',
            pg_audit: 'Tarkastus ja todisteet',
            pg_training: 'Koulutus',
            pg_privacy_office: 'Privacy Office',
            pg_obligations: 'Velvollisuudet',
            pg_documents: 'Asiakirjat',
            vr_overview: 'Yleiskatsaus',
            vr_onboarding: 'Käyttöönotto',
            vr_assessment: 'Arviointi',
            vr_oversight: 'Valvonta',
        },
        tickets: {
            title: 'Tietopyynnöt',
            create: 'Luo pyyntö',
            filters: 'Suodattimet',
            search_placeholder: 'Hae pyynt?j?...',
            export_csv: 'Vie CSV',
            exporting: 'Viedään...',
            bulk_export_failed: 'Joukkovienti epäonnistui',
            bulk_export: {
                label: 'Vie',
                format: {
                    pdf: 'PDF Yhteenveto',
                    zip: 'ZIP (tapauskohtaiset todisteet)',
                    csv: 'CSV',
                },
                warning_over_50: 'Tämä luo noin {count} kotelopakettia - voi kestää useita minuutteja. Jatkaa?',
                blocked_over_500: 'Rajaa suodatin 500 tapaukseen tai vähemmän ennen ZIP-tiedoston vientiä (nykyinen: {count}).',
                success: 'Joukkovienti tallennettu tiedostoselaimeen ({count} tapausta)',
                failed: 'Joukkovienti epäonnistui',
                continue: 'Jatka',
                cancel: 'Peruuta',
            },
            no_results: 'Pyyntöjä ei löytynyt',
            ticket_id: 'Pyynnön tunnus',
            request_type: 'Pyynnön tyyppi',
            requester: 'Pyytäjä',
            source: 'lähde',
            all_sources: 'Kaikki lähteet',
            assigned_to: 'Vastuuhenkilö',
            created: 'Luotu',
            load_error: 'Tapausten lataaminen epäonnistui. Yritä uudelleen.',
            case_creation_failed: 'Tapauksen luonti epäonnistui',
            business_unit: 'Liiketoimintayksikkö',
            reference: 'Viite',
            sla: {
                title: 'SLA-tila',
                on_track: '{days} päivää jäljellä',
                approaching: 'Lähestyvä määräaika',
                overdue: '{days} päivää myöhässä',
                ack_overdue: '{hours}t kuittausmääräajan yli',
                ack_warning: 'Kuittaus pian erääntyvä',
                closed: 'Suljettu',
                completed_in: 'Valmistunut {days} päivässä',
                source_statutory: 'Statutory period',
                source_tenant_target: 'Your SLA target',
                source_platform_default: 'Platform default, not a legal period',
                awaiting_activation: 'Awaiting activation from {date}',
                no_installed_clock: 'No installed clock applies',
                // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
                handled_in_legal_matters: 'Handled in Legal Matters',
            },
        },
        templates: {
            title: 'Vastausmallipohjat',
            new: 'Uusi mallipohja',
            edit: 'Muokkaa mallipohjaa',
            all_types: 'Kaikki tyypit',
            all_locales: 'Kaikki kielet',
            type_access: 'Pääsy/DSAR',
            type_deletion: 'Poistaminen',
            type_correction: 'Oikaisu',
            type_objection: 'Vastustaminen',
            type_portability: 'Siirrettävyys',
            type_other: 'Muu',
            type_restriction: 'Rajoitus',
            type_all: 'Kaikki',
            locale_en: 'Englanti',
            locale_vi: 'Vietnam',
            locale_all: 'Molemmat',
            name: 'Nimi',
            request_type: 'Pyynnön tyyppi',
            locale: 'Kieli',
            subject: 'Aihe',
            body: 'Sisältö',
            name_placeholder: 'Mallipohjan nimi...',
            subject_placeholder: 'Sähköpostin/kirjeen aihe...',
            body_placeholder: 'Mallipohjan sisältö. Käytä paikkamerkkeinä: {{requester_name}}, {{ticket_id}}, {{request_type}}, {{org_name}}, {{response_date}}.',
            available_placeholders: 'Käytettävissä olevat paikkamerkit:',
            create_template: 'Luo mallipohja',
            preview: 'Esikatselu',
            close_preview: 'Sulje esikatselu',
            default_badge: 'Oletus',
            no_templates: 'Mallipohjia ei löytynyt',
            confirm_delete: 'Poistetaanko tämä mallipohja?',
            load_error: 'Mallipohjien lataaminen epäonnistui. Yritä uudelleen.',
            create_error: 'Mallipohjan luominen epäonnistui',
            update_error: 'Mallipohjan päivittäminen epäonnistui',
            delete_error: 'Mallipohjan poistaminen epäonnistui',
            preview_error: 'Mallipohjan esikatselu epäonnistui',
            response_templates_badge: 'Vastausmallit',
            browse_form_templates: 'Selaa Vaatimustenmukaisuuslomakepohjia',
            templates_loaded: 'Mallit ladattu',
            sample_organization: 'Esimerkkiorganisaatio',
        },
        detail: {
            requester_info: 'Pyytäjän tiedot',
            status: 'Tila',
            assignment: 'Vastuutus',
            notes: 'Muistiinpanot',
            audit_trail: 'Tarkastuspolku',
            add_note: 'Lisää muistiinpano',
            case_not_found: 'Tapausta ei löytynyt',
            load_error: 'Tapauksen lataaminen epäonnistui. Yritä uudelleen.',
            partial_load_failed: 'Some information for this case could not be loaded, so parts of this page may be incomplete.',
            partial_load_parts: 'Not loaded: {parts}.',
            note_error: 'Muistiinpanon lisääminen epäonnistui. Yritä uudelleen.',
            submitted_from: 'Lähetetty kohteesta',
            via_cmp: 'AesirX CMP:n kautta',
            cmp_ticket: 'CMP-lippu:',
            select_template: 'Valitse malli',
            direction_inbound: 'CMP → GRC',
            direction_outbound: 'GRC → CMP',
            tabs: {
                details: 'Tiedot',
                evidence: 'Todistusaineisto',
                communications: 'Viestintä',
                escalations: 'Eskalaatiot',
                audit_trail: 'Tarkastuspolku',
                fulfillment: 'Täyttäminen',
                response: 'Vastaus',
                extension: 'Jatkoaika',
            },
            requester: {
                name: 'Nimi',
                email: 'Sähköposti',
                phone: 'Puhelin',
                language: 'Kieli',
                source: 'Lähde',
                customer_ref: 'Asiakasviite',
                country: 'Maa',
                // fix-sprint #431 — the country as received, where it was stored as a code.
                country_as_received: '(received as “{value}”)',
                preferred_channel: 'Ensisijainen kanava',
            },
            // fix-sprint #447 W9 (owner ruling OR3) — processor or third party must act
            third_party: {
                label: 'Processor or third party must act',
                hint: 'The installed deadline records may state a different period when a processor or third party must act. Changing this dates the case again from the day it was received, by the record that matches; an approved extension and closed pauses are carried onto the new date.',
                closed: 'The case is closed, so whether a processor or third party must act can no longer be changed.',
                confirm_title: 'Date the case again?',
                confirm_body: 'The case will be dated again from the day it was received, by the installed deadline record that matches. An approved extension and closed pauses are carried onto the new date. The old and the new due date are recorded.',
                confirm_label: 'Change and date again',
                saved: 'Saved. The case shows its old and new due date.',
                redated: 'Changed on {date}: dated again from receipt. Due date was {old}, now {new}.',
                not_redated_no_installed_record: 'Changed on {date}. No installed framework dates this case, so its due date ({due}) is unchanged.',
                not_redated_no_due_date: 'Changed on {date}. The case holds no due date, so nothing was dated again.',
                carried_extension: '{days} approved extension day(s) carried onto the new date.',
                carried_paused: '{amount} paused {unit} carried onto the new date.',
                units: {
                    calendar_days: 'calendar day(s)',
                    working_days: 'working day(s)',
                    hours: 'hour(s)',
                },
                mapping_invalid: 'An installed deadline record for this kind of request cannot be read as a period, so the case could not be dated again and the change was not saved.',
            },
            sla: {
                title: 'SLA',
                on_track: 'Aikataulussa',
                due_soon: 'Erääntyy pian',
                overdue: 'Myöhässä',
                days_remaining: 'pv jäljellä',
                due_date: 'Eräpäivä',
                breached: 'Ylitetty',
                paused: 'Keskeytetty',
                total_paused_days: 'Keskeytyspäivät yhteensä',
                pause_intervals: 'Pause Intervals',
                pause_intervals_help: 'Each period the clock was stopped, so waiting-on-requester time can be told apart from processing time.',
                pause_intervals_none: 'No pauses recorded on this case.',
                no_due_date: 'No due date recorded',
                stopped_case_closed: 'SLA stopped — case closed',
                pause_interval_open: 'Paused since {from} — still waiting',
                pause_interval_closed: '{from} to {to} — {days} day(s)',
                pause_interval_by: 'Paused by {who}',
                pause_interval_actor_unknown: 'Paused by: not recorded',
                pause_interval_from_status: 'Paused from {status}',
                pause_interval_from_status_unknown: 'Paused from: not recorded',
                pause_interval_reason_unknown: 'Reason: not recorded',
                pause_interval_resumed_by: 'Resumed by {who}',
                processing_days: 'Processing time',
                waiting_days: 'Waiting on requester',
                days_elapsed: 'Kuluneita päiviä',
                progress: 'Edistyminen',
                prov_heading_ack: 'Acknowledgement deadline',
                prov_heading_fulfil: 'Fulfilment deadline',
                prov_source_statutory: 'Statutory period: {reference}',
                prov_source_statutory_from_activation: 'Statutory period: {reference}, running from {date}, the day it came into force',
                prov_source_tenant_target: 'Your SLA target: {days} days from receipt',
                prov_source_platform_default: 'Platform default, not a legal period: {days} days from receipt',
                prov_source_none: 'Not dated: no installed clock, organisation target or platform default applies',
                prov_due: 'Due {date}',
                prov_awaiting_activation: 'Awaiting activation from {date}: the installed clock {reference} applies from that date, and the case will be dated by it then.',
                prov_no_installed_clock: 'No installed clock applies to this kind of request.',
                // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
                prov_handled_in_legal_matters: 'Handled in Legal Matters: this request is received as a legal matter, which is the record that is worked. No deadline applies to this intake record.',
                prov_open_legal_matter: 'Open the legal matter',
                prov_framework_unnamed: 'an installed framework',
                prov_legal_matter_no_access: 'The legal matter is visible to people who hold the permission to read legal matters.',
                prov_excluded: 'Installed clocks that could not be dated and took no part: {reasons}',
                prov_excluded_activation_date_not_stated: '{reference}: no effective date stated',
                prov_excluded_framework_record_unreadable: '{reference}: framework record could not be read',
                prov_excluded_phase_reference_unresolved: '{reference}: phase start not dated',
            },
            identity: {
                title: 'Henkilöllisyyden vahvistus',
                verified: 'Vahvistettu',
                not_verified: 'Ei vahvistettu',
                method: 'Menetelmä',
                notes: 'Muistiinpanot',
                verified_by: 'Vahvistaja',
                verified_at: 'Vahvistettu',
                warning: 'Henkilöllisyyttä ei ole vielä vahvistettu.',
                verify_button: 'Vahvista henkilöllisyys',
                select_method: '-- Valitse --',
                methods: {
                    email_confirmation: 'Sähköpostivahvistus',
                    id_document: 'Henkilöllisyystodistus',
                    phone_call: 'Puhelinsoitto',
                    in_person: 'Henkilökohtaisesti',
                    other: 'Muu',
                },
            },
            closure: {
                reason: 'Sulkemisen syy',
                summary: 'Sulkemisen yhteenveto',
                summary_placeholder: 'Yhteenveto pyynnön ratkaisusta...',
                reason_required: 'Sulkemisen yhteenveto ja syy ovat pakollisia',
                response_not_sent: 'Virallinen vastaus on lähetettävä rekisteröidylle ennen tapauksen sulkemista',
                reasons: {
                    fulfilled: 'Toteutettu',
                    partially_fulfilled: 'Osittain toteutettu',
                    unfounded: 'Perusteeton',
                    excessive: 'Kohtuuton',
                    withdrawn: 'Peruutettu',
                    duplicate: 'Kaksoiskappale',
                    identity_unverified: 'Henkilöllisyys vahvistamatta',
                },
            },
            // English until Phase 25 (R-57).
            // fix-sprint #431 — moving a case into a waiting status stops its clock,
            // and the server requires the reason (`pause_reason`).
            pause: {
                reason_label: 'Why is the case waiting?',
                reason_placeholder: 'Select a reason',
                reason_required: 'Choose why the case is waiting before moving it to a waiting status.',
                reasons: {
                    awaiting_subject: 'Waiting for the data subject',
                    awaiting_verification: 'Waiting for identity verification',
                    third_party_dependency: 'Waiting for a third party',
                    other: 'Other',
                },
            },
            // fix-sprint #431 — rejecting a case requires a reason code
            // (`rejection_reason_code`).
            rejection: {
                code_label: 'Rejection reason',
                code_placeholder: 'Select a reason',
                code_required: 'Choose a rejection reason before rejecting the case.',
                description_label: 'Rejection details (optional)',
                codes: {
                    identity_not_verified: 'Identity not verified',
                    manifestly_unfounded: 'Manifestly unfounded',
                    repetitive: 'Repeated request',
                    exempt: 'Exempt',
                    out_of_scope: 'Out of scope',
                    duplicate: 'Duplicate request',
                },
            },
            // English until Phase 25 (R-57).
            // fix-sprint #431 — what the cross-border detector found when the case
            // was created (`cross_border_finding`). A record; it decides nothing.
            cross_border_finding: {
                title: 'Cross-border check at creation',
                result: 'Finding',
                evaluated_at: 'Checked at',
                triggered: 'The rule matched: the data subject’s country ({subject}) differs from the organisation’s ({org}).',
                reasons: {
                    request_type_not_disclosing: 'Not checked further: this request type discloses no data.',
                    subject_country_unknown: 'Not matched: the data subject’s country is not recorded.',
                    same_country: 'Not matched: the data subject is in the organisation’s country ({org}).',
                    // fix-sprint #431 — the organisation records no country: never assumed.
                    org_country_not_recorded: 'Not evaluated: the organisation’s country is not recorded.',
                    // fix-sprint #431 — a recorded country that reads as no code is not compared.
                    subject_country_not_recognised: 'Not evaluated: the data subject’s country, as recorded, is not a country code or name the platform recognises.',
                },
                other_reason: 'Not matched ({reason}).',
                org_country_pointer: 'The cross-border check compares the data subject’s country with the organisation’s. The organisation’s country is recorded on its record: Organizations › All Organizations, then the organisation’s Country field.',
                org_country_link: 'Record the organisation’s country',
                not_evaluated: 'Could not be evaluated when the case was created.',
                not_recorded: 'Not recorded: this case was created before the check ran on creation.',
                decides_nothing: 'This records what the rule found. It changes no status, deadline or assignment, and opens no cross-border dossier.',
                // fix-sprint #431 (owner ruling 2026-10-03) — viewing a case opens
                // nothing; a person opens the cross-border dossier with the button.
                current: 'Assessment now',
                dossier: 'Cross-border dossier',
                dossier_reading: 'Reading…',
                dossier_unreadable: 'Whether a dossier is open for this case could not be read.',
                no_dossier: 'No cross-border dossier has been opened for this case.',
                dossier_opened: 'Dossier {dossier}, opened {at}.',
                filing: 'Statutory filing',
                filing_no_form: 'None opened: no installed framework declares a filing form for this transfer.',
                filing_not_opened: 'None opened: the filing could not be created when the dossier was opened.',
                filing_not_recorded: 'None recorded.',
                open_button: 'Open cross-border dossier',
                open_hint: 'Opening the dossier is your action, recorded in the case audit log under your name. Where an installed framework declares a filing form for this transfer, the statutory filing is opened with it.',
                opening: 'Opening…',
                confirm_title: 'Open cross-border dossier?',
                confirm_message: 'This opens a cross-border dossier for case {case} and, where an installed framework declares a filing form for this transfer, a statutory filing. It is recorded under your name.',
                confirm_label: 'Open dossier',
                opened_toast: 'Cross-border dossier {dossier} opened.',
                already_open_toast: 'A cross-border dossier was already open for this case: {dossier}.',
                open_error: 'The cross-border dossier could not be opened.',
                // fix-sprint #431 (gate E12.5 remediation 1) — why opening the dossier was refused.
                not_detected: 'No cross-border transfer is detected for this case now, so no dossier was opened.',
                case_not_found: 'This case could not be found.',
                // gate E12.5 remediation 1 — retrying a filing that could not be opened; the subject country compared and its source.
                filing_opening: 'Being opened now.',
                retry_filing_button: 'Retry the statutory filing',
                retry_filing_hint: 'The statutory filing could not be opened with this dossier. Retrying it is your action, recorded in the case audit log under your name.',
                retry_filing_failed: 'The statutory filing still could not be opened.',
                filing_retried_toast: 'Statutory filing {filing} opened.',
                // Gate E12.5 remediation 2 — a stopped opening, and what a retry answered.
                filing_opening_stalled: 'None opened: the request that was opening it stopped before it finished.',
                retry_filing_in_progress: 'Another request is opening the statutory filing now, so this retry started nothing.',
                retry_filing_no_form: 'No statutory filing was opened: no installed framework declares a filing form for this transfer now.',
                // Gate E12.5 pass 3 — a filing retry that could not be attempted, and why.
                retry_filing_unavailable_service: 'This retry started nothing: statutory filings cannot be opened on this installation.',
                retry_filing_unavailable_org_country: 'This retry started nothing: the dossier records no organisation country, which the statutory filing needs.',
                confirm_retry_title: 'Retry the statutory filing?',
                confirm_retry_message: 'This tries again to open the statutory filing for dossier {dossier} of case {case}. It is recorded under your name.',
                confirm_retry_label: 'Retry filing',
                subject_compared: 'Data subject’s country compared',
                subject_compared_value: '{country}, from {source}',
                subject_sources: {
                    residence_country: 'the country of residence recorded with the request',
                    case_country: 'the case’s Country field',
                    address_country: 'the country in the data subject’s address',
                },
            },
            export_title: 'Vie',
            export_pdf: 'Vie PDF',
            export_zip: 'Vie ZIP',
            case_file_pdf: 'Vie PDF (tapaustiedosto)',
            evidence_pack_zip: 'Vie ZIP (todistepaketti)',
            cross_border_warning: 'This request may involve a cross-border transfer. Review whether your transfer impact assessment covers it.',
            breach: {
                flag_btn: 'Merkitse rikkomuksia aiheuttavaksi',
                modal: {
                    title: 'Merkitse kotelo rikkoutumisen aiheuttajana',
                    type_label: 'Rikkomuksen tyyppi',
                    type: {
                        misdirected_response: 'Väärin suunnattu vastaus',
                        unauthorised_disclosure: 'Luvaton paljastaminen',
                        identity_mismatch: 'Identiteetti ei täsmää',
                        other: 'muu',
                    },
                    description: 'Kuvaus (valinnainen)',
                    description_placeholder: 'Mitä tapahtui? (enintään 1000 merkkiä)',
                    confirm: 'Merkitse ja käynnistä ilmoituskello',
                    cancel: 'Peruuta',
                    flagging: 'Merkitään...',
                },
                banner: {
                    title: 'Rikkomus ilmoitettu: {type}',
                    detected_at: 'Havaittu {timestamp}',
                    filing_deadline: 'Ilmoitusmääräaika: {hours} tunnin kuluttua',
                    overdue: 'Ilmoitusmääräaika: MYÖHÄSSÄ',
                },
                toasts: {
                    flagged: 'Tapaus merkitty rikkomuksen aiheuttavaksi - ilmoituskello käynnistyi',
                    flag_failed: 'Tapauksen merkitseminen rikkomuksen aiheuttavaksi epäonnistui',
                },
            },
            export: {
                generating: 'Luodaan... odota',
                success: 'Vie tallennettu tiedostoselaimeen',
                failed: 'Vienti epäonnistui - tarkista tarkastusloki',
            },
            no_notes: 'Ei muistiinpanoja',
            assign_placeholder: 'Käyttäjätunnus vastuutettavaksi...',
            reassign: 'Vastuuta uudelleen',
            evidence: {
                checklist_title: 'Todistusaineiston tarkistuslista',
                complete_pct: '% valmis',
                no_checklist: 'Ei tarkistuslistan kohteita',
                attachments: 'Liitteet',
                upload_attachment: 'Lataa liite',
                upload_button: 'Lataa',
                uploading: 'Ladataan...',
                attachment_uploaded: 'Liite ladattu onnistuneesti',
                upload_error: 'Liitteen lataus epäonnistui',
                category: 'Kategoria',
                categories: {
                    identity_proof: 'Henkilöllisyystodistus',
                    correspondence: 'Kirjeenvaihto',
                    internal_data: 'Sisäiset tiedot',
                    legal: 'Oikeudellinen',
                    other: 'Muu',
                },
                no_attachments: 'Ei liitteitä',
                references_title: 'Todistusaineiston viitteet',
                no_references: 'Ei viitteitä',
                add_reference: 'Lisää viite',
                ref_type: 'Tyyppi',
                ref_value: 'Arvo',
                ref_desc: 'Kuvaus',
                ref_value_placeholder: 'URL, sähköposti-ID tai viite...',
                ref_desc_placeholder: 'Kuvaus...',
                ref_types: {
                    url: 'URL',
                    email_id: 'Sähköposti-ID',
                    crm_record: 'CRM-tietue',
                    ticket_ref: 'Tikettviite',
                    other: 'Muu',
                },
            },
            communications: {
                title: 'Viestintä',
                add: 'Lisää viesti',
                no_records: 'Ei tallennettuja viestejä',
                direction: 'Suunta',
                channel: 'Kanava',
                visibility: 'Näkyvyys',
                subject: 'Aihe',
                body: 'Sisältö',
                send: 'Lähetä',
                inbound: 'Saapuva',
                outbound: 'Lähtevä',
                internal: 'Sisäinen',
                external: 'Ulkoinen',
                tab_all: 'All',
                tab_internal: 'Sisäinen',
                tab_external: 'Ulkoinen',
                subject_placeholder: 'Aihe (valinnainen)',
                body_placeholder: 'Viestin sisältö...',
                channels: {
                    email: 'Sähköposti',
                    portal: 'Portaali',
                    phone: 'Puhelin',
                    postal: 'Posti',
                    in_person: 'Henkilökohtaisesti',
                },
                apply_template: 'Käytä Mallia',
                select_template: 'Valitse malli...',
                no_templates: 'Ei malleja saatavilla',
                templates_load_error: 'Templates could not be loaded',
                template_applied: 'Malli sovellettu vastaukseen.',
                template_apply_error: 'Mallia ei voitu soveltaa.',
            },
            escalations: {
                title: 'Eskalaatiohistoria',
                no_events: 'Ei eskalaatiotapahtumia',
                tier: 'Taso',
                threshold: 'Kynnysarvo',
                action: 'Toiminto',
                target: 'Kohde',
                triggered_at: 'Lauennut',
                // fix-sprint #431 (owner ruling 2026-10-03) — what a tier did. English until
                // Phase 25 (R-57).
                outcome: 'Outcome',
                outcome_acted: 'Acted',
                outcome_baselined: 'Passed before automatic escalation was on',
                // fix-sprint #431 — a tier recorded because a ladder edit added, switched on or lowered it.
                outcome_baselined_ladder_edit: 'Passed before this tier was added, switched on or lowered',
                outcome_baselined_detail: 'No one was notified and the case was not reassigned.',
            },
            cmp_sync: {
                title: 'AesirX CMP -synkronointi',
                sync_status: 'Synkronoinnin tila',
                last_synced: 'Viimeksi synkronoitu',
                not_synced: 'Ei synkronoitu',
                resync: 'Synkronoi uudelleen',
                syncing: 'Synkronoidaan...',
                sync_history: 'Synkronointihistoria (10 viimeisintä)',
                direction: 'Suunta',
                time: 'Aika',
                retries: 'Uudelleenyritykset',
                error_col: 'Virhe',
            },
            audit: {
                filter_event_placeholder: 'Suodata tapahtumatyypin mukaan...',
                filter_actor_placeholder: 'Suodata tekijän mukaan...',
            },
            fulfillment: {
                title: 'Osastotäyttäminen',
                assign_departments: 'Määritä osastot',
                department: 'Osasto',
                assignee: 'Vastuuhenkilö',
                deadline: 'Määräaika',
                evidence_count: 'Todistusaineisto',
                signoff: 'Hyväksyntä',
                signed_off: 'Hyväksytty',
                pending_signoff: 'Odottaa',
                start_button: 'Aloita',
                complete_button: 'Merkitse valmiiksi',
                signoff_button: 'Kirjaudu pois',
                no_tasks: 'Ei täyttämistehtäviä vielä.',
                tasks_table: 'Täyttämistehtävät',
                progress_label: 'Täyttämisen edistyminen',
                overdue: 'myöhässä',
                assigned_success: 'Osastotäyttämistehtävä määritetty onnistuneesti.',
                assign_error: 'Täyttämistehtävän määrittäminen epäonnistui.',
                signoff_success: 'Osastohyväksyntä kirjattu.',
                signoff_error: 'Hyväksynnän kirjaaminen epäonnistui.',
                identity_required: 'Identity verification required before fulfillment actions.',
                remove_task: 'Remove Task',
                remove_success: 'Fulfillment task removed successfully.',
                add_evidence_btn: 'Lisää todiste',
                evidence_uploaded: 'Todiste ladattu ja linkitetty',
                evidence_upload_error: 'Todisteen lataus epäonnistui',
                suggest_data_stores: 'Ehdota tietovarastoja',
                suggest_error: 'Tietovarastoehdotusten lataaminen epäonnistui.',
                suggest_stores_title: 'Ehdotetut tietovarastot',
                suggest_stores_none: 'Yksikään tietovarasto ei vastaa tätä tapausta. Paranna ehdotuksia määrittämällä järjestelmät Data Mappingissa.',
                suggest_stores_add: 'Lisää koteloon',
                suggest_stores_added: 'Tietovarasto lisätty koteloon',
                no_departments_title: 'Mitään osastoja ei ole määritetty',
                no_departments_body: 'Osastojen avulla voit reitittää DSR-täyttötehtävät toimintoon (tietosuoja, lakiasiat, HR, IT-palvelut). Asenna esimerkkiosastot tai pyydä järjestelmänvalvojaa määrittämään osastot Organisaation asetuksissa.',
                no_departments_cta: 'Asenna esimerkkitiedot',
                department_placeholder: 'Osaston nimi...',
                assignee_placeholder: 'Käyttäjätunnus määritettäväksi...',
                assign_button: 'Määritä',
                status_pending: 'Odottaa',
                status_in_progress: 'Käynnissä',
                status_completed: 'Valmis',
                status_verified: 'Vahvistettu',
                confirm_approve: 'Haluatko varmasti hyväksyä tämän jatkoajan?',
                confirm_deny: 'Haluatko varmasti hylätä tämän jatkoajan?',
            },
            response: {
                title: 'Vastauksen luominen',
                select_template: 'Vastausmalli',
                choose_template: 'Valitse malli...',
                generate: 'Luo vastaus',
                preview: 'Vastauksen esikatselu',
                send: 'Lähetä vastaus',
                delivery_method: {
                    label: 'Toimitustapa',
                    email: 'Sähköposti',
                    portal: 'Portaali',
                    postal: 'Postin posti',
                    in_person: 'Henkilökohtaisesti',
                },
                recipient: {
                    email: 'Vastaanottajan sähköposti',
                    portal: 'Portaalin käyttäjätunnus',
                    address: 'Postiosoite',
                },
                auto_generate_btn: 'Luo ja lähetä vastaus',
                auto_generate_success: 'Vastaus luotu ja toimitettu',
                auto_generate_missing_recipient: 'Ilmoita vastaanottaja ennen lähettämistä.',
                auto_fields: 'Automaattisesti täytetyt kentät',
                case_id: 'Tapaus-ID',
                requester_name: 'Pyytäjän nimi',
                rights_type: 'Oikeustyyppi',
                history: 'Lähetetyt vastaukset',
                generated_at: 'Luotu',
                confirmation: 'Vahvistus',
                sent_label: 'Lähetetty',
                draft_label: 'Luonnos',
                generated_success: 'Vastaus luotu onnistuneesti.',
                generate_error: 'Vastauksen luominen epäonnistui.',
                sent_success: 'Vastaus lähetetty onnistuneesti.',
                send_error: 'Vastauksen lähettäminen epäonnistui.',
                load_error: 'Vastausmallien lataaminen epäonnistui.',
                pdf_title: 'Vastauskirje rekisteröidylle',
                download_pdf: 'Lataa PDF',
                download_all: 'Lataa kaikki',
                download_error: 'Vastauksen lataaminen epäonnistui',
            },
            extension: {
                title: 'Määräajan jatkaminen',
                acknowledgment_deadline: 'Kuittausmääräaika',
                fulfillment_deadline: 'Täyttämismääräaika',
                request_extension: 'Pyydä jatkoaikaa',
                reason: 'Syy',
                reason_placeholder: 'Syy lisäajan pyytämiseen...',
                additional_days: 'Lisäpäivät',
                submit_request: 'Lähetä jatkoaikapyyntö',
                history: 'Jatkoaikahistoria',
                requested_at: 'Pyydetty',
                approve: 'Hyväksy',
                deny: 'Hylkää',
                request_success: 'Jatkoaikapyyntö lähetetty.',
                request_error: 'Jatkoaikapyynnön lähettäminen epäonnistui.',
                approved_success: 'Jatkoaika hyväksytty.',
                denied_success: 'Jatkoaika hylätty.',
                decision_error: 'Jatkoaikapäätöksen käsittely epäonnistui.',
                status_pending: 'Odottaa',
                status_approved: 'Hyväksytty',
                status_denied: 'Hylätty',
                confirm_approve: 'Haluatko varmasti hyväksyä tämän jatkoaikapyynnön?',
                confirm_deny: 'Haluatko varmasti hylätä tämän jatkoaikapyynnön?',
                decided_by: 'Päättäjä',
            },
            // fix-sprint #431 (gate E12.5 remediation 1) — the banner’s link to the check; why a status change was refused.
            cross_border_warning_link: 'See the cross-border check',
            status_errors: {
                invalid_transition: 'The case cannot move to this status from its current status.',
                invalid_sub_status: 'The chosen sub-status does not apply to this status.',
                assignment_required: 'Assign the case before moving it to In Review.',
                identity_verification_required: 'Verify the requester’s identity before fulfilling the request.',
                closure_delivery_required: 'Record how and when the response was delivered before closing the case.',
                invalid_value: 'The change was not saved: a value is missing or not accepted.',
            },
            evidence_unlink_read_failed: 'The attachment was deleted, but the fulfilment tasks could not be read, so it may still be listed as evidence on a task.',
            partial_load_part_names: {
                advisor_summary: 'advisor summary',
                sla_status: 'SLA status',
                attachments: 'attachments',
                fulfilment_tasks: 'fulfilment tasks',
                checklist: 'evidence checklist',
                references: 'references',
                escalations: 'escalations',
                cmp_sync_history: 'CMP sync history',
                flagged_breaches: 'flagged breaches',
                audit_trail: 'audit trail',
                communications: 'communications',
            },
            partial_load_reload: 'Reload the missing parts',
            request_type_not_granted: 'This kind of request is not a right under the frameworks this organisation has installed. The request is kept and can still be worked.',
        },
        audit: {
            export_csv: 'Vie CSV',
            exporting: 'Viedään...',
            search_placeholder: 'Etsi tarkastustiedoista...',
            filter_by_actor: 'Suodata tekijän mukaan...',
            all_event_types: 'Kaikki tapahtumatyypit',
            seq: '#',
            timestamp: 'Aikaleima',
            event: 'Tapahtuma',
            case_col: 'Tapaus',
            actor: 'Tekijä',
            summary: 'Yhteenveto',
            no_events: 'Tarkastustapahtumia ei löytynyt',
            export_success: 'Kirjausketjun vienti onnistui.',
            export_error: 'Tarkastus-CSV:n vienti epäonnistui',
            load_error: 'Tarkastuspolun lataaminen epäonnistui. Yritä uudelleen.',
            announce_loaded: 'Tarkastuspolku ladattu',
            start_date: 'Alkamispäivä',
            end_date: 'Päättymispäivä',
            per_page: '{count} / sivu',
            unknown: 'Tuntematon',
            source_filter: {
                label: 'lähde',
                all: 'Kaikki',
                module: 'Moduuli',
                forseti: 'Forseti',
                aria_label: 'Suodata tarkastustapahtumat lähteen mukaan',
            },
            event_types: {
                case_created: 'Tapaus luotu',
                status_changed: 'Tila muutettu',
                case_assigned: 'Tapaus vastuutettu',
                note_added: 'Muistiinpano lisätty',
                attachment_added: 'Liite lisätty',
                attachment_deleted: 'Liite poistettu',
                reference_added: 'Viite lisätty',
                checklist_updated: 'Tarkistuslista päivitetty',
                identity_verified: 'Henkilöllisyys vahvistettu',
                closure_summary_added: 'Sulkemisen yhteenveto',
                approval_granted: 'Hyväksyntä myönnetty',
                template_applied: 'Mallipohja sovellettu',
                export_generated: 'Vienti luotu',
                sla_warning: 'SLA-varoitus',
                sla_overdue: 'SLA myöhässä',
                sla_flag_revisited: 'SLA flag revisited',
                third_party_involvement_changed: 'Processor or third-party involvement changed',
                aml_diligence_linked: 'AML due diligence record linked',
                aml_diligence_confirmed: 'AML due diligence record confirmed',
                aml_scoping_recorded: 'AML reporting-entity scoping recorded',
                aml_scoping_deactivation_refused: 'AML programme-inactive role refused',
                aml_two_person_policy_changed: 'AML two-person rule changed',
                aml_retention_scope_recorded: 'AML records retention scope recorded',
                aml_retention_scope_updated: 'AML records retention scope updated',
                aml_indicator_row_updated: 'AML indicator coverage row updated',
                aml_indicator_entity_row_recorded: 'AML organisation-defined indicator recorded',
                aml_indicator_config_approved: 'AML indicator configuration approved',
                aml_case_opened: 'Suspicious-activity case opened',
                aml_case_updated: 'Suspicious-activity case edited',
                aml_case_viewed: 'Suspicious-activity case read',
                aml_case_listed: 'Suspicious-activity case list read',
                aml_case_read_denied: 'Suspicious-activity case read refused',
                aml_case_determined: 'Suspicious-activity case determination recorded',
                aml_case_approved: 'Suspicious-activity case determination approved',
                aml_case_self_approval_refused: 'Suspicious-activity case approval by the determiner refused',
                aml_case_approval_reverted: 'Suspicious-activity case approval taken back',
                aml_case_filing_recorded: 'Suspicious-activity case report reference recorded',
                aml_case_closed: 'Suspicious-activity case closed',
                aml_case_cdd_refresh_requested: 'Due diligence refresh asked for from a suspicious-activity case',
                aml_diligence_refresh_requested: 'AML due diligence refresh asked for',
                aml_beneficial_owner_recorded: 'Beneficial owners recorded',
                aml_beneficial_owner_updated: 'Beneficial owners updated',
                aml_launch_recorded: 'AML launch record created',
                aml_launch_scope_determined: 'AML launch scope determination recorded',
                aml_launch_assessment_recorded: 'AML pre-launch risk assessment recorded',
                aml_launch_assessment_decided: 'AML pre-launch risk assessment decision recorded',
                aml_launch_approved: 'AML launch approved',
                aml_launch_approval_refused: 'AML launch approval refused',
                aml_inspection_pack_exported: 'AML inspection pack exported',
                aml_indicator_config_self_approval_refused: 'AML indicator configuration approval by an editor of the draft refused',
                aml_indicator_review_task_raised: 'AML indicator review task raised from a launch assessment',
                aml_indicator_review_task_completed: 'AML indicator review recorded',
                aml_retention_scope_approved: 'AML records retention scope signed',
                aml_retention_scope_self_approval_refused: 'Second signature of an AML records retention scope entry by the same person refused',
                aml_case_related_action_recorded: 'Suspicious-activity case related action recorded',
                aml_case_retention_start_recorded: 'Suspicious-activity case retention start recorded',
                aml_launch_self_approval_refused: 'AML launch approval by the assessment decider refused',
                aml_launch_post_launch_review_set: 'AML launch post-launch review date recorded',
                aml_case_reads_retention_swept: 'Suspicious-activity case read log retention sweep',
                org_profile_statement_recorded: 'Organization profile statement recorded',
                dossier_authority_communication_recorded: 'Authority communication recorded on a filing',
                dossier_authority_communication_exported: 'Authority communications of a filing exported',
                framework_summary_exported: 'Framework summary exported',
                framework_briefing_recorded: 'Briefing or consultation comment recorded on a framework',
                framework_briefing_approved: 'Briefing on a framework approved',
                dossier_receipt_evidence_recorded: 'Receipt of a dossier recorded on a filing',
                dossier_handling_statement_recorded: 'Handling authority recorded on a filing',
                dossier_result_recorded: 'Authority’s result recorded on a filing',
                dossier_result_supplement_request_recorded: 'Supplement request after a result recorded on a filing',
                dossier_failure_follow_up_recorded: 'Follow-up to a result recorded on a filing',
                dossier_authority_stated_result_date_recorded: 'Result date stated by the authority recorded on a filing',
                dossier_completion_request_recorded: 'Completion request recorded on a filing',
                dossier_completion_submitted: 'Completion submitted on a filing',
                dossier_update_filing_created: 'Update filing created for a filing',
                dossier_update_addressee_recorded: 'Recipient and competent authority recorded on an update filing',
                regulatory_update_comparison_reviewed: 'Comparison review recorded on a regulatory update',
                access_statement_recorded: 'Access statement recorded on a processing activity',
            },
            unified_audit: {
                title: 'Yhdistetty Tarkastuspolku',
                module_filter: 'Moduuli',
                framework_filter: 'Viitekehys',
                select_framework: 'Valitse viitekehys...',
                entity_type_filter: 'Entiteettityyppi',
                action_filter: 'Toiminto',
                actor_filter: 'Toimija',
                select_actor: 'Valitse toimija...',
                search_filter: 'Haku',
                search_placeholder: 'Hae tarkastustapahtumia...',
                filters_heading: 'Suodattimet',
                date_range: 'Aikaväli',
                no_events_found: 'Tarkastustapahtumia ei löytynyt.',
                event_count: 'Tapahtumien määrä',
                action: 'Toiminto',
                actor: 'Toimija',
                date_range_start: 'Ajanjakson alku',
                date_range_end: 'Ajanjakson loppu',
                filtering_events: 'Suodatetaan tarkastustapahtumia',
                framework_codes: 'Viitekehyskoodit',
                entity_type_col: 'Entiteettityyppi',
                aria_table: 'Yhdistetyt tarkastustapahtumat',
                page_of: 'Sivu {page} / {total}',
                entity_types: {
                    user: 'Käyttäjä',
                    case: 'Asia',
                    incident: 'Tapahtuma',
                    assessment: 'Arviointi',
                    vendor: 'Myyjä',
                    consent: 'Suostumus',
                    policy: 'politiikka',
                    data_map: 'Tietokartta',
                    approval: 'Approval',
                    consent_record: 'Consent record',
                    data_flow: 'Data flow',
                    org_profile_statement: 'Organization profile statement',
                    dpia: 'DPIA',
                    dpia_mitigation: 'DPIA mitigation',
                    evidence_item: 'Evidence item',
                    processing_activity: 'Processing activity',
                    transfer_mechanism: 'Transfer mechanism',
                    consent_policy: 'Consent policy',
                    data_category: 'Data category',
                    drift_event: 'Drift event',
                    maturity_model: 'Maturity model',
                    program: 'Monitoring program',
                    purpose: 'Processing purpose',
                    review_record: 'Review record',
                    risk_score: 'Risk score',
                    system: 'System',
                    taxonomy: 'Taxonomy',
                },
                actions: {
                    // Sprint 62.57.0 Slice E — the processing-service certificate register's audit events.
                    service_certificate_certificate_recorded: 'Processing service certificate recorded',
                    service_certificate_certificate_corrected: 'Processing service certificate corrected',
                    service_certificate_certificate_document_added: 'Document added to a processing service certificate',
                    service_certificate_certificate_revocation_recorded: 'Certificate revocation recorded',
                    service_certificate_certificate_return_recorded: 'Return of the certificate recorded',
                    service_certificate_certificate_publication_recorded: 'Publication of a revocation recorded',
                    service_certificate_certificate_communication_recorded: 'Authority communication recorded on a certificate',
                    service_certificate_application_created: 'Certificate application opened',
                    service_certificate_application_updated: 'Certificate application updated',
                    service_certificate_application_part_recorded: 'Dossier part recorded',
                    service_certificate_application_condition_links_recorded: 'Impact-assessment filings linked',
                    service_certificate_application_filed: 'Certificate application filing recorded',
                    service_certificate_application_receipt_recorded: 'Receipt of the application recorded',
                    service_certificate_application_communication_recorded: 'Authority communication recorded on an application',
                    service_certificate_application_supplement_request_recorded: 'Deficiency notice recorded',
                    service_certificate_application_supplement_sent: 'Supplement sent recorded',
                    service_certificate_application_evaluation_recorded: 'Evaluation of the application recorded',
                    service_certificate_application_decision_recorded: 'Decision on the application recorded',
                    service_certificate_application_withdrawn: 'Certificate application withdrawn',
                    service_certificate_register_exported: 'Certificate register exported',
                    form_dossier_statement_changed: 'Dossier statement changed',
                    create: 'Luo',
                    update: 'Päivitä',
                    delete: 'Poista',
                    view: 'Näytä',
                    export: 'Vie',
                    status_change: 'Tilan muutos',
                    // Sprint 63.25.0, gate E12.5 remediation 1 (P1-1) — the verbs producers write.
                    created: 'Created',
                    updated: 'Updated',
                    deleted: 'Deleted',
                    viewed: 'Viewed',
                    exported: 'Exported',
                    approved: 'Approved',
                    rejected: 'Rejected',
                    assigned: 'Assigned',
                    completed: 'Completed',
                    closed: 'Closed',
                    status_changed: 'Status changed',
                    submitted_for_review: 'Submitted for review',
                    risk_added: 'Risk added',
                    mitigation_added: 'Mitigation added',
                    evidence_linked: 'Evidence linked',
                    scored: 'Scored',
                    reviewed: 'Reviewed',
                    activated: 'Activated',
                    period_started: 'Period started',
                    version_created: 'Version created',
                    evaluated: 'Evaluated',
                    triage_completed: 'Triage completed',
                    impact_assessed: 'Impact assessed',
                    breach_assessed: 'Breach assessed',
                    case_created: 'Case created',
                    assignment_changed: 'Assignment changed',
                    case_closed: 'Case closed',
                    identity_verified: 'Identity verified',
                    maturity_model_created: 'Maturity model created',
                    maturity_model_activated: 'Maturity model activated',
                    maturity_model_evaluated: 'Maturity model evaluated',
                    maturity_model_approved: 'Maturity model approved',
                    response_generated: 'Response generated',
                    response_delivered: 'Response delivered',
                    assembled: 'Assembled',
                    reassembled: 'Reassembled',
                    verified: 'Verified',
                    returned: 'Returned',
                    recorded_as_submitted: 'Recorded as submitted',
                    audit_evidence_pack_assembled: 'Evidence pack assembled',
                    audit_evidence_pack_reassembled: 'Evidence pack reassembled',
                    audit_evidence_pack_partial: 'Evidence pack partial',
                    audit_evidence_pack_drift_checked: 'Evidence pack sources checked for changes',
                    audit_evidence_pack_verified: 'Evidence pack verified',
                    audit_evidence_pack_returned: 'Evidence pack returned',
                    audit_evidence_pack_recorded_as_submitted: 'Evidence pack recorded as submitted',
                    audit_evidence_pack_transition_refused: 'Evidence pack status change refused',
                    audit_evidence_pack_exported: 'Evidence pack exported',
                    // Sprint 63.37.0 — the form-instance events the sample packages write.
                    instance_created: 'Form created',
                    instance_updated: 'Form updated',
                    consent_captured: 'Consent captured',
                    revoked: 'Revoked',
                    mitigation_status_changed: 'Mitigation status changed',
                    ai_advisor_record_summary_fetched: 'Forseti read the record summary',
                    regulatory_guidance_installed: 'Regulatory guidance installed',
                    regulatory_guidance_version_bumped: 'Regulatory guidance moved to a new version',
                    unknown: 'Action not recorded',
                },
                additional_filters: 'Additional audit filters',
                sample_marker: 'Sample',
                sample_marker_label: 'Sample data',
                sample_package_label: 'Sample data from the industry package {package}',
                actor_system: 'The platform',
                entity_none: 'No record type',
                summary_composed: '{action} · {entity}',
                // Sprint 63.29.0 (#585, AC3/AC6) — the server export and the integrity check.
                export_title: 'Unified Audit Trail export',
                export_generated: 'Generated: {date}',
                export_filters: 'Filters: {filters}',
                export_no_filters: 'Filters: none',
                export_count: 'Events: {count}',
                export_source_unreadable: 'Source {source} ({collection}): could not be read; its events are not in this file.',
                list_sources_unreadable: 'These audit sources could not be read, so their events are neither listed nor counted: {sources}.',
                list_page_beyond_window: 'Pages beyond the first {max} events cannot be listed. Narrow the filters, or export the filtered set.',
                list_page_beyond_window_no_export: 'Pages beyond the first {max} events cannot be listed. Narrow the filters.',
                list_window_note: 'Only the first {max} of {total} events can be paged through here. To see the rest, narrow the filters or export the filtered set.',
                list_window_note_no_export: 'Only the first {max} of {total} events can be paged through here. To see the rest, narrow the filters.',
                export_col_entity_id: 'Record ID',
                export_col_actor_id: 'Actor ID',
                export_menu_label: 'Export',
                integrity: {
                    column: 'Integrity',
                    check: 'Check integrity',
                    check_aria: 'Check the integrity of the audit events of record {id}',
                    checking: 'Checking…',
                    heading: 'Integrity check: {type}',
                    heading_untyped: 'Integrity check',
                    record_ref: 'Record ID: {id}',
                    case_ref: 'Case ID: {case}',
                    chain_verified: 'Hash chain verified, {verified} of {events} events.',
                    chain_broken: '{verified} of {events} events verified before the break.',
                    chain_legacy_unverifiable: '{count} events were written before the current link rule; their content matches the stored checksum, but their order in the chain cannot be verified.',
                    no_chain: 'No hash chain names this record.',
                    break_checksum: 'Break found in case {case} at sequence {sequence}: the stored checksum does not match the one recomputed from the event before it.',
                    break_gap: 'Break found in case {case}: sequence {sequence} is missing from the chain.',
                    break_withheld: 'A break was found in the chain of case {case} at an event you are not permitted to read, so its position is not shown. The chain cannot be relied on; a holder of the restricted grant can see where it breaks.',
                    no_break: 'No break found.',
                    not_chained: '{source}: {events} events. Not chained; consistency checked only.',
                    not_chained_inconsistent: '{source}: {events} events. Not chained; consistency checked only. {inconsistent} carry an unreadable or future date (first: {at}).',
                    proofs_link: 'Proofs and anchors are verified in the Integrity Center.',
                    failed: 'The integrity check could not be run.',
                    close: 'Close the integrity result',
                    // Gate E12.5 pass 1 (P2-3) — a source the check could not read.
                    unreadable: 'The check could not read {sources}; the result does not cover the events held there.',
                },
            },
            evidence_packs: {
                title: 'Tarkastuksen Todistuspaketit',
                assemble_pack: 'Kokoa Todistuspaketti',
                framework: 'Viitekehys',
                select_framework: 'Valitse viitekehys...',
                framework_code_label: 'Kehyskoodi',
                entity_types: 'Entiteettityypit',
                select_entity_types: 'Valitse entiteettityypit...',
                period: 'Jakso',
                period_start: 'Jakson alku',
                period_end: 'Jakson loppu',
                completeness: 'Kattavuus',
                no_packs_found: 'Todistuspaketteja ei löytynyt.',
                verify: 'Vahvista',
                export_pack: 'Vie',
                exporting: 'Viedään...',
                pack_assembled: 'Todistepaketti koottu',
                pack_exported: 'Todistepaketti viety',
                aria_table: 'Tarkastuksen todistepaketit',
                aria_export_pack: 'Vie paketti',
                export_format: 'Vientimuoto',
                export_in_progress: 'Vienti käynnissä...',
                status_draft: 'Luonnos',
                status_assembled: 'Koottu',
                status_verified: 'Vahvistettu',
                status_submitted: 'Lähetetty',
                entity_type_labels: {
                    user: 'Käyttäjä',
                    case: 'Asia',
                    incident: 'Tapahtuma',
                    assessment: 'Arviointi',
                    vendor: 'Myyjä',
                    consent: 'Suostumus',
                    policy: 'politiikka',
                    data_map: 'Tietokartta',
                },
                template_id: 'Malline-ID',
                csv_headers: {
                    // Sprint 63.29.0 (#542) — the actor's name from the user directory, beside the id.
                    actor_name: 'Actor Name',
                    module: 'Moduuli',
                    action: 'Toiminta',
                    actor_id: 'näyttelijä',
                    actor_role: 'Actor Role',
                    entity_type: 'Entiteettityyppi',
                    entity_id: 'Entity ID',
                    summary: 'Yhteenveto',
                    timestamp: 'Aikaleima',
                    record_type: 'Record Type',
                    framework_code: 'puitteet',
                },
                // Sprint 63.25.0 — versioned packs: statuses, record types and the export header (spec G1, G2).
                status_partial: 'Partial',
                status_returned: 'Returned',
                status_recorded_as_submitted: 'Recorded as submitted',
                status_legacy_unversioned: 'Assembled before versioning',
                record_type_labels: {
                    consent: 'Consent',
                    data_map: 'Data Map',
                    assessment: 'Assessment',
                    vendor: 'Vendor',
                    incident: 'Incident',
                    case: 'Case',
                    policy: 'Policy',
                    deletion: 'Deletion',
                    monitoring: 'Monitoring',
                    forms: 'Forms',
                    task: 'Task',
                    access: 'Access',
                    user: 'User',
                    platform: 'Platform',
                    evidence_pack_lifecycle: 'Evidence pack lifecycle',
                },
                framework_none: 'no framework filter',
                export_header_pack: 'Pack {pack_id} — version {version}',
                export_header_name: 'Name: {name}',
                export_header_manifest: 'Manifest SHA-256: {sha}',
                export_header_period_tz: 'Period: {start} to {end} ({timezone})',
                export_header_untagged_included: 'Events not tagged to a framework: included',
                export_header_untagged_excluded: 'Events not tagged to a framework: not included',
                export_header_record_types: 'Record types: {types}',
                export_header_modules: 'Modules: {names}',
                export_header_all_modules: 'Modules: all',
                export_header_counts: 'Events: {total} ({tagged} tagged to a framework, {untagged} untagged)',
                export_header_coverage: 'Record-type coverage: {covered} of {denominator} selected record types have at least one event',
                export_header_coverage_missing: 'Selected record types with no event: {types}',
                export_header_unresolved: 'References not resolved: {count}',
                export_header_unresolved_unchecked: 'References not resolved: not checked',
                export_header_review: 'Review: {decision} by {reviewer} on {date}',
                export_header_review_note: 'Review note: {note}',
                export_header_not_reviewed: 'Review: not reviewed',
                export_header_submission: 'Recorded as submitted: to {recipient} on {sent_on} via {channel}, reference {reference}',
                export_header_submission_disclaimer: 'This records that the pack was sent. It is not proof that the recipient received or accepted it.',
                export_header_no_submission: 'Submission: none recorded',
                export_header_source_collected: 'Source {source} ({collection}): {count} events collected',
                export_header_source_failed: 'Source {source} ({collection}): could not be read ({error})',
                export_header_sources_not_read: 'Sources not read: {count} (not selected)',
                export_header_excluded_by_rule: 'Excluded by rule: record-summary read events ({count})',
                export_header_excluded_by_rule_uncounted: 'Excluded by rule: record-summary read events (not counted)',
                export_header_excluded_by_rule_not_applied: 'Excluded by rule: record-summary read events (rule not applied: this version was assembled before the rule existed)',
                value_none: 'none',
                // Gate E12.5 remediation 1 (P1-2): an event whose producer wrote no sentence.
                summary_composed: '{action} · {record_type}',
                export_doc_title: 'Evidence Pack {pack_id}',
                export_header_framework: 'Todistepaketti — Viitekehys: {framework}',
                export_header_period: 'Jakso: {start} - {end}',
                export_header_completeness: 'Kattavuus: {score}%',
                export_header_status: 'Tila: {status}',
                col_entity_type: 'Entity Type',
                events_collected: 'events collected',
                missing_label: 'Missing',
                no_events_in_pack: 'No audit events found for this evidence pack.',
                // Sprint 63.25.0 (Slices C3, C4, F, H) — the versioned audit evidence pack page.
                page_description: 'Collect the audit events recorded in a period into a versioned dossier. Each version is a snapshot: its count, its event list, its coverage and every export read the same events.',
                cross_ref_audit: "Audit evidence packs collect the audit events recorded in a period; framework evidence packs gather one framework's evidence items for a regulator.",
                cross_ref_open_framework: 'Go to framework evidence packs',
                cross_ref_framework: "Framework evidence packs gather one framework's evidence items for a regulator; audit evidence packs collect the audit events recorded in a period.",
                cross_ref_open_audit: 'Go to audit evidence packs',
                filter_status: 'Status',
                filter_all_statuses: 'All statuses',
                col_name: 'Name',
                col_events: 'Events',
                col_version: 'Version',
                col_coverage: 'Record-type coverage',
                col_assembled: 'Assembled',
                coverage_short: '{covered} of {denominator}',
                not_a_member_count: 'Not a list of events',
                show_details: 'Show details for {name}',
                list_load_failed: 'The evidence pack list could not be read.',
                list_retry_subject: 'the evidence pack list',
                list_pagination: 'Pages of evidence packs',
                field_name: 'Name',
                name_hint: 'A name people will recognise, for example "Q3 inspection".',
                framework_hint: 'Optional. Only frameworks installed for your organisation are listed.',
                no_framework_option: 'No framework filter',
                include_untagged: 'Include events not tagged to any framework',
                include_untagged_hint: 'Most audit events carry no framework tag. Leave this ticked unless you want only the events tagged to the chosen framework. The pack shows how many of each it holds either way.',
                include_untagged_no_effect: 'With no framework chosen, every event in the period is included and this box has no effect.',
                period_hint: "Dates are read in your organisation's timezone, and the end date is included in full.",
                record_types_legend: 'Record types',
                record_types_hint: 'Choose at least one.',
                modules_legend: 'Modules',
                modules_hint: 'Optional. Leave every box unticked to include all modules.',
                error_name_required: 'Enter a name.',
                error_period_start_required: 'Choose a start date.',
                error_period_end_required: 'Choose an end date.',
                error_period_order: 'The end date must be on or after the start date.',
                error_record_types_required: 'Choose at least one record type.',
                assembling: 'Assembling…',
                detail_loading: 'Loading the pack…',
                detail_load_failed: 'This pack could not be read.',
                detail_retry_subject: 'this evidence pack',
                scope_heading: 'Scope and counts',
                meta_period: 'Period',
                meta_untagged: 'Events not tagged to a framework',
                meta_record_types: 'Record types',
                meta_modules: 'Modules',
                meta_counts: 'Events',
                meta_manifest: 'Manifest SHA-256',
                period_value: '{start} to {end} ({timezone})',
                untagged_included: 'Included',
                untagged_excluded: 'Not included',
                all_modules: 'All modules',
                counts_value: '{total} ({tagged} tagged to a framework, {untagged} untagged)',
                not_checked: 'Not checked',
                legacy_notice: 'This pack was assembled before versioning. Its stored count is not a list of events, so it cannot be reviewed or exported. Re-assemble it to create a reviewable version.',
                version_select: 'Version shown',
                version_option: 'Version {version}',
                reassemble: 'Re-assemble',
                reassembling: 'Re-assembling…',
                reassembled: 'Version {version} assembled.',
                drift_check: 'Check for changes since assembly',
                drift_checking: 'Checking…',
                drift_none: 'Nothing has changed since version {version} was assembled.',
                drift_new: '{count} events recorded in this period since assembly. Re-assemble to include them.',
                drift_gone: '{count} events in this version no longer exist at their source.',
                drift_changed: '{count} events in this version have changed at their source.',
                drift_failed_sources: 'Sources that could not be read during the check: {sources}.',
                drift_failed: 'The check for changes could not be run.',
                coverage_heading: 'Record-type coverage',
                coverage_sentence: '{covered} of {denominator} selected record types have at least one event.',
                coverage_missing: 'No event for: {types}.',
                coverage_not: 'This shows which selected record types have events. It does not measure whether the evidence is sufficient, or whether the organisation complies with any law.',
                history_heading: 'Version history',
                col_assembled_by: 'Assembled by',
                col_assembled_at: 'Assembled on',
                col_reviewer: 'Reviewer',
                col_decision: 'Decision',
                col_note: 'Note',
                col_reviewed_at: 'Reviewed on',
                col_submission: 'Recorded as submitted',
                decision_verified: 'Verified',
                decision_returned: 'Returned',
                not_reviewed: 'Not reviewed',
                no_submission: 'None recorded',
                submission_line: 'To {recipient} on {sent_on} via {channel}',
                items_heading: 'Events in version {version}',
                items_loading: 'Loading the events of this version…',
                items_empty_scope: 'It was assembled for {period}; framework: {framework}; record types: {types}; modules: {module_list}.',
                items_denied: 'You do not have permission to read the events of this pack. Reading them needs the "Read audit events" permission (audit:events:read).',
                items_failed: 'The events of this version could not be read.',
                items_retry_subject: 'the events of this version',
                items_populated: '{count} events in this version.',
                partial_banner: 'Some sources could not be read when this version was assembled, so it is partial and cannot be verified. Sources not read: {sources}.',
                partial_reassemble_hint: 'A person with permission to export audit events can re-assemble it.',
                partial_no_members: 'No events were collected from the sources that answered.',
                source_item: '{source} ({collection})',
                col_record_type: 'Record type',
                col_actor: 'Actor',
                col_timestamp: 'Time',
                col_module: 'Module',
                col_action: 'Action',
                col_summary: 'Summary',
                items_pagination: 'Pages of events',
                review: 'Review',
                review_title: 'Review version {version}',
                review_decision: 'Decision',
                review_option_verified: 'Verify this version',
                review_option_returned: 'Return it to the person who assembled it',
                review_note: 'Note',
                review_note_hint: 'Required when you return a version: say what needs to change.',
                error_decision_required: 'Choose a decision.',
                error_note_required: 'Enter a note saying why the version is returned.',
                review_submit: 'Record decision',
                review_recorded: 'Your decision on version {version} was recorded.',
                review_own_version: 'You assembled this version, so another person must review it.',
                record_submission: 'Record as submitted',
                submission_title: 'Record version {version} as submitted',
                submission_disclaimer: 'This records that the pack was sent. It is not proof that the recipient received or accepted it.',
                submission_recipient: 'Recipient',
                submission_sent_on: 'Sent on',
                submission_channel: 'Channel',
                submission_channel_hint: 'How it was sent, for example by email or by hand.',
                submission_reference: 'Reference',
                submission_reference_hint: 'Optional. For example a letter or ticket number.',
                submission_proof: 'Proof of sending',
                submission_proof_hint: 'Optional. A file showing the pack was sent, such as a dispatch receipt.',
                error_recipient_required: 'Enter the recipient.',
                error_sent_on_required: 'Choose the date it was sent.',
                error_channel_required: 'Enter the channel.',
                submission_recorded: 'Version {version} was recorded as submitted.',
                export_version: 'Export version {version}',
                // Sprint 63.25.0 (spec E3) — references that did not resolve at assembly.
                reference_not_found: 'Reference not found',
                reference_not_found_record: 'The source event names no record.',
                reference_not_found_actor: 'The person who acted is not in the user directory.',
                unresolved_line: 'References not resolved: {count}',
                unresolved_not_checked: 'References not resolved: not checked',
                unresolved_breakdown: '{records} with no record named; {actors} whose actor is not in the user directory.',
                unresolved_breakdown_actors_unchecked: '{records} with no record named; actors not checked.',
                server_error_reviewer_is_assembler: 'You assembled this version, so you cannot review it. Another person with the review permission must record the decision.',
                server_error_evidence_pack_version_not_found: 'This version of the pack was not found. The pack may have been re-assembled or removed.',
                server_error_submission_proof_not_owned: 'The proof file is not held under this version. Upload it to this version first.',
                server_error_evidence_pack_legacy: 'This pack was assembled before versioning. Re-assemble it to create a version that can be checked.',
                server_error_evidence_pack_too_large: 'More events match this scope than one pack can hold. Choose a shorter period or a narrower filter.',
                server_error_evidence_pack_definition_invalid: "This pack's stored definition cannot be assembled again. Create a new pack.",
            },
            audit_periods: {
                title: 'Tarkastusjaksot',
                create_period: 'Luo Tarkastusjakso',
                framework_code_label: 'Kehyskoodi',
                select_framework: 'Valitse viitekehys...',
                period_label: 'Jakson nimi',
                period_start: 'Jakson alku',
                period_end: 'Jakson loppu',
                start: 'Aloita',
                end: 'Loppu',
                framework: 'Kehys',
                retention_days: 'Säilytyspäivät',
                section_period_dates: 'Jakson päivämäärät',
                no_periods_found: 'Tarkastusjaksoja ei löytynyt.',
                period_created: 'Tarkastuskausi luotu',
                aria_table: 'Tarkastuskaudet',
                suggested_boundaries: 'Ehdotetut rajat',
                suggested_boundaries_info: 'Ehdotetut jaksorajat johdetaan kunkin viitekehyksen sääntelyraportoinnin syklistä. Muokkaa tarvittaessa organisaatiollesi sopivaksi.',
                reporting_cycle: 'Raportointisykli',
                suggested_start: 'Ehdotettu alku',
                suggested_end: 'Ehdotettu loppu',
                suggested_next: 'Suositeltu seuraavaksi kaudeksi',
                cycle_monthly: 'Kuukausittain',
                cycle_quarterly: 'Neljännesvuosittain',
                cycle_semi_annual: 'Puolivuosittain',
                cycle_annual: 'Vuosittainen',
                cycle_custom: 'Mukautettu',
                period_deleted: 'Tarkastusjakso poistettu',
                list_truncated: 'Only {shown} of {total} audit periods could be read, so this list is incomplete.',
            },
        },
        create_case: {
            customer_ref: 'Asiakasviite',
            country: 'Maa / lainkäyttöalue',
            preferred_channel: 'Ensisijainen kanava',
            channels: {
                email: 'Sähköposti',
                postal: 'Posti',
                phone: 'Puhelin',
                portal: 'Portaali',
            },
            source: 'lähde',
            sources: {
                admin_manual: 'Järjestelmänvalvojan käsikirja',
                public_form: 'Julkinen lomake',
                cmp_gateway: 'CMP Yhdyskäytävä',
                aesirx_cmp: 'AesirX CMP',
                phone_received: 'Puhelin vastaanotettu',
                postal_received: 'Posti vastaanotettu',
                email_received: 'Sähköposti vastaanotettu',
                in_person: 'Henkilökohtaisesti',
                legacy_import: 'Vanha tuonti',
            },
            required_fields: 'Pyynnön tyyppi, nimi ja sähköposti ovat pakollisia',
            create_error: 'Tapauksen luominen epäonnistui',
            // fix-sprint #447 W9 (owner ruling OR3)
            third_party_involved: 'A processor or third party must act on this request',
            third_party_involved_hint: 'The installed deadline records may state a different period when a processor or third party must act. The case is dated by the record that matches. It can be changed on the case later, which dates the case again from receipt.',
            request_type_not_offered: 'This kind of request is not a right under the frameworks your organisation has installed, so it cannot be recorded as a new request.',
            request_type_no_installed_clock: 'No installed framework states a period for this kind of request. The case will be dated from your SLA target for it or, failing that, the platform default, which is not a legal period.',
            // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
            request_type_handled_in_legal_matters: 'This kind of request is received as a legal matter in Legal Matters. No deadline is set for it.',
            legal_matter_right: 'Which right it raises',
            legal_matter_right_optional_hint: 'Optional. If it is not yet known, leave it empty: a person records it on the legal matter.',
            legal_matter_right_not_known: 'Not yet known',
        },
        settings: {
            title: 'Asetukset',
            request_types: 'Pyyntötyypit',
            request_types_desc: 'Määritä mitkä pyyntötyypit ovat käytössä julkisessa lomakkeessa.',
            notifications: 'Ilmoitukset',
            ticket_numbering: 'Tikettien numerointi',
            ticket_format: 'Nykyinen muoto',
            ticket_prefix: 'Etuliite',
            ticket_prefix_note: 'ei muutettavissa',
            sla_deadlines: 'SLA-määräajat',
            sla_desc: 'Aseta kunkin pyyntötyypin enimmäisvastausaika (kalenteripäivinä). Tapaukset, jotka lähestyvät raja-arvoa, näyttävät varoituksia.',
            days: 'päivää',
            warning_threshold: 'Varoituskynnys',
            escalation_label: 'Ota automaattinen eskalaatio käyttöön SLA-ylityksissä',
            // fix-sprint #431 F1 — English until Phase 25 (R-57).
            escalation_hint: 'Runs the escalation ladder below. When off, no tier acts; warnings and breaches are still flagged.',
            warning_threshold_hint: 'A case no installed framework dates shows a warning at this share of its period and is flagged critical at 90%, or at this share if it is higher. A case an installed framework dates is warned at 75% and flagged critical at 90%.',
            // Gate E12.5 remediation 1 (fix-sprint #431) — escalation settings need sla:config:write.
            escalation_settings_need_write: 'Changing automatic escalation or the warning threshold needs the SLA configuration permission.',
            // Gate E12.5 remediation 2 — neither sla:config:write nor case:update: nothing here can be saved.
            sla_settings_read_only: 'You can view these settings but not change them: saving them needs the SLA configuration permission, or the case update permission for the deadlines alone.',
            sla_save_forbidden: 'The SLA settings were not saved: changing automatic escalation or the warning threshold needs the SLA configuration permission.',
            sla_save_invalid: 'The SLA settings were not saved: a deadline is set for something that is not a request type, or a value is out of range.',
            save_sla: 'Tallenna SLA-asetukset',
            saving: 'Tallennetaan...',
            saved: 'Tallennettu onnistuneesti',
            announce_loaded: 'Asetukset ladattu',
            email_on_creation: 'Sähköposti-ilmoitus tiketin luonnissa',
            send_confirmation: 'Lähetä vahvistussähköposti pyytäjälle',
            users: 'Käyttäjät',
            roles: 'Roolit',
            api_keys: 'API Näppäimet',
            mfa: 'MFA',
            sessions: 'Istunnot',
            webhooks: 'Webhooks',
            modules: 'Moduulit',
            // Sprint 62.45.0 (AC17) — the organisation calendar section.
            calendar: {
                heading: 'Working days and holidays',
                effect_notice: 'Every working-day deadline in the platform counts only the working days below, skips these holidays and counts these make-up working days — rights requests and their pauses, incidents, obligations, enforcement limitation periods, dossier filings and supervisory reports among them. A deadline worked out after you save uses the new calendar; a due date already recorded on a record keeps its date.',
                no_list_notice: "The platform supplies no public-holiday list: enter your organisation's own days off, public holidays included.",
                working_days_legend: 'Working days of the week',
                working_days_hint: 'Working-day deadlines count only these days.',
                working_days_required: 'Select at least one working day.',
                day_0: 'Sunday',
                day_1: 'Monday',
                day_2: 'Tuesday',
                day_3: 'Wednesday',
                day_4: 'Thursday',
                day_5: 'Friday',
                day_6: 'Saturday',
                holidays_heading: 'Holidays',
                holidays_caption: 'Holidays entered: {count}',
                empty: 'No holidays entered: working-day deadlines skip only the days outside the working week.',
                add_legend: 'Add a holiday',
                date_label: 'Date',
                label_label: 'Label (optional)',
                label_hint: 'A short name for the day, for example why the office is closed.',
                add: 'Add date',
                remove: 'Remove',
                remove_aria: 'Remove {date}',
                col_date: 'Date',
                col_label: 'Label',
                col_actions: 'Actions',
                no_label: 'No label',
                date_required: 'Enter a date.',
                date_out_of_range: 'Enter a date between {min} and {max}.',
                duplicate: '{date} is already in the list.',
                limit_reached: 'At most {max} holidays can be entered. Remove one before adding another.',
                save: 'Save calendar',
                saved: 'Calendar saved. Working-day deadlines worked out from now on count these working days, skip these holidays and count these make-up working days.',
                save_error: 'The calendar was not saved. Your entries are still here.',
                conflict: 'Someone else saved the calendar after you opened it, so your changes were not saved. Reload it to see their version, then enter your changes again.',
                reload: 'Reload calendar',
                load_error: 'The calendar could not be read, so it is not shown and cannot be changed here.',
                retry: 'Try again',
                last_saved: 'Last saved {date}.',
                never_saved: 'This calendar has not been saved here yet.',
                read_only: 'You can view this calendar but not change it.',
                unsaved: 'Unsaved changes.',
                holiday_is_extra: '{date} is entered as a make-up working day. A date is either a holiday or a make-up working day.',
                extra_heading: 'Make-up working days',
                extra_intro: 'A day your organisation works although it falls outside the working week, for example a Saturday worked in exchange for a day off beside a public holiday. Every working-day deadline counts it as a working day.',
                extra_caption: 'Make-up working days entered: {count}',
                extra_empty: 'No make-up working days entered.',
                extra_add_legend: 'Add a make-up working day',
                extra_date_label: 'Date worked',
                extra_label_label: 'Label for the day worked (optional)',
                extra_label_hint: 'A short name for the day, for example the day off it makes up for.',
                extra_add: 'Add day worked',
                extra_remove_aria: 'Remove make-up working day {date}',
                extra_is_holiday: '{date} is entered as a holiday. A date is either a holiday or a make-up working day.',
                extra_limit_reached: 'At most {max} make-up working days can be entered. Remove one before adding another.',
                extra_is_working_day: '{date} is a {weekday}, which is already a working day. A make-up working day is a date outside the working days of the week.',
                extra_on_working_weekday: 'These make-up working days fall on a working day of the week, so they would change nothing: {dates}. Remove them, or remove that day from the working days.',
            },
            // fix-sprint #431 F1 — Settings › Escalation ladder. English until Phase 25 (R-57).
            escalation_ladder: {
                heading: 'Escalation ladder',
                intro: "When a rights request reaches a tier's share of its period, the background SLA check acts on it once: it notifies the case's assignee and the people and role the tier names, in the platform or by email, as far as each person's own notification preferences allow. A reassign tier also moves the case. The share is the time from receipt of the request to the due date the case shows. Paused cases and cases with no due date are not escalated.",
                off_notice: 'Automatic escalation is off, so no tier acts. Warnings and breaches are still flagged. Turn it on under SLA Deadlines.',
                on_notice: 'Automatic escalation is on. The background SLA check runs these tiers.',
                suggested_notice: 'Your organisation has not saved a ladder, so these suggested tiers run. Save them, or change them and save, to make them your own.',
                caption: 'Escalation tiers: {count}',
                col_threshold: 'Share of period',
                col_name: 'Name',
                col_action: 'Action',
                col_targets: 'Notifies',
                col_channels: 'Sent by',
                col_actions: 'Actions',
                action_notify: 'Notify',
                action_escalate_to_role: 'Escalate to a role',
                action_reassign: 'Reassign',
                channel_in_app: 'In the platform',
                channel_email: 'Email',
                target_assignee: 'The assignee',
                target_role: 'Holders of {role}',
                target_users: 'Named: {names}',
                reassign_to: 'Moves the case to: {target}',
                threshold_breach: '100% (due date passed)',
                inactive: 'Inactive',
                edit: 'Edit',
                edit_aria: 'Edit the tier at {pct}%',
                remove: 'Remove',
                remove_aria: 'Remove the tier at {pct}%',
                add: 'Add tier',
                add_legend: 'Add a tier',
                edit_legend: 'Edit the tier at {pct}%',
                threshold_label: 'Share of the period elapsed (%)',
                threshold_hint: 'A whole number from 1 to 100. At 100 the tier acts once the due date has passed.',
                name_label: 'Name (optional)',
                name_hint: "Shown in notifications and in the case's escalation history. Left blank, the tier is named by its share.",
                action_label: 'Action',
                action_hint: 'Every tier notifies the assignee and the people it names. Escalate to a role also notifies everyone holding the role. Reassign moves the case to the first active person named, or else to the first person holding the role, unless the case is already with one of them.',
                role_label: 'Role',
                role_none: 'No role',
                users_label: 'Named people',
                users_hint: 'For a reassign tier, the case goes to the first active person in this list.',
                user_select_label: 'Person to add',
                user_placeholder: 'Choose a person',
                user_add: 'Add person',
                user_remove_aria: 'Remove {name}',
                users_empty: 'No one named.',
                users_retry_subject: 'the list of people',
                channels_legend: 'Send by',
                channels_hint: 'Each person still receives only what their own notification preferences allow.',
                active_label: 'Active',
                apply: 'Apply to the ladder',
                cancel: 'Cancel',
                save: 'Save escalation ladder',
                saved: 'Escalation ladder saved. The background SLA check runs it from its next sweep.',
                unsaved: 'Unsaved changes.',
                save_error: 'The escalation ladder was not saved. Your changes are kept.',
                conflict: 'Someone else saved the escalation ladder after you opened this page, so your changes were not saved. Reload the ladder to see that version, then make your changes again.',
                reload: 'Reload ladder',
                load_error: 'The escalation ladder could not be read, so it is not shown and cannot be changed here.',
                retry: 'Try again',
                read_only: 'You can view the escalation ladder but not change it.',
                empty: 'No tiers. Add at least one tier, or turn automatic escalation off under SLA Deadlines.',
                last_saved: 'Last saved {date}.',
                err_ladder_empty: 'Add at least one tier. To stop automatic escalation, turn it off under SLA Deadlines.',
                err_too_many_tiers: 'A ladder holds at most {max} tiers.',
                err_invalid_value: 'Tier {n}: a value is not valid.',
                err_threshold_out_of_range: 'Tier {n}: the share must be a whole number from 1 to 100.',
                err_threshold_duplicate: 'Tier {n}: another tier already acts at this share.',
                err_tier_id_duplicate: 'Tier {n}: this tier appears twice.',
                err_channels_required: 'Tier {n}: choose at least one way to send.',
                err_target_role_required: 'Tier {n}: choose the role to escalate to.',
                err_target_role_unknown: 'Tier {n}: the role is not an active role of your organisation.',
                err_reassign_target_required: 'Tier {n}: name a person or a role to reassign the case to.',
                err_target_user_unknown: 'Tier {n}: {name} is not an active user of your organisation.',
            },
            deadline_labels: {
                access: 'Pääsy (DSAR)',
                deletion: 'Poistaminen',
                correction: 'Oikaisu',
                portability: 'Siirrettävyys',
                objection: 'Vastustaminen',
                restriction: 'Rajoittaminen',
                consent_withdrawal: 'Suostumuksen peruuttaminen',
                protective_measures: 'Suojatoimet',
                processing_information: 'Information about Processing',
                consent_decision: 'Consent Decision',
                complaint_or_compensation: 'Complaint, Denunciation, Lawsuit or Damages Claim',
            },
            sla_source_platform_default: 'Not saved: cases of this type that no installed clock dates are dated from the platform default of {days} days, which is not a legal period. Save a value to set your own target.',
            sla_source_tenant_target: 'Your SLA target: it dates cases of this type that no installed clock in force dates.',
            request_type_enabled_not_offered: 'Enabled, but not a right under the frameworks this organisation has installed: the public form does not offer it. Untick it to clear the setting.',
            request_types_from_frameworks: 'The request types listed are the rights the installed frameworks grant.',
        },
        section_assignments: {
            title: 'Osion tehtäväjaot',
            assign_section: 'Määritä osio',
            section_ref: 'Osioviite',
            section_label: 'Osion nimi',
            department_id: 'Osasto',
            assignee_id: 'Vastuuhenkilö',
            mandatory: 'Pakollinen',
            assign: 'Määritä',
            no_assignments: 'Ei osion tehtäväjaoistoa',
            status: 'Tila',
            completion: 'Valmistuminen',
            mark_complete: 'Merkitse valmiiksi',
        },
        consolidation: {
            title: 'Konsolidoinnin edistyminen',
            add_child: 'Lisää alitehtävä',
            child_task_id: 'Alitehtävän tunnus',
            department_id: 'Osasto',
            link: 'Linkki',
            overall_completion: 'Kokonaisvalmistuminen',
            departments_complete: 'Valmiit osastot',
            overdue: 'Myöhässä',
            ready: 'Valmis',
            not_ready: 'Ei valmis',
            master_status: 'Pääasiallinen tila',
            department_breakdown: 'Osastoerittely',
            department: 'Osasto',
            status: 'Tila',
            completion: 'Valmistuminen',
            due_date: 'Eräpäivä',
            overdue_flag: 'Myöhässä',
            child_tasks: 'Alitehtävät',
            task: 'Tehtävä',
        },
        conflict_detection: {
            title: 'Ristiriitojen tunnistus',
            unresolved: 'ratkaisematta',
            no_conflicts: 'Ristiriitoja ei havaittu',
            section: 'Osio',
            field: 'Kenttä',
            dept_a: 'Osasto A',
            value_a: 'Arvo A',
            dept_b: 'Osasto B',
            value_b: 'Arvo B',
            severity: 'Vakavuus',
            status: 'Tila',
            resolved: 'Ratkaistu',
            open: 'Avoin',
            resolve: 'Ratkaise',
        },
        org_maturity: {
            title: 'Organisaation Kypsyys',
            departments: 'Osastot',
            department_hierarchy: 'Osastohierarkia',
            create_department: 'Luo Osasto',
            department_name: 'Osaston Nimi',
            department_code: 'Koodi',
            level: 'Taso',
            division: 'Toimiala',
            department: 'Osasto',
            team: 'Tiimi',
            parent_department: 'Yläosasto',
            head_user: 'Osastopäällikkö',
            no_departments: 'Osastoja ei ole määritetty.',
            user_mappings: 'Käyttäjäliitokset',
            map_user: 'Liitä Käyttäjä',
            user_id: 'Käyttäjä-ID',
            role: 'Rooli',
            no_users: 'Tälle osastolle ei ole liitetty käyttäjiä.',
            remove: 'Poista',
            framework_assignments: 'Viitekehystilaukset',
            assign_framework: 'Tilaa Viitekehys',
            entity_id: 'Entiteetti-ID',
            entity_type: 'Entiteettityyppi',
            framework_id: 'Viitekehys-ID',
            framework_code: 'Viitekehyskoodi',
            no_assignments: 'Ei viitekehystilauksia.',
            compliance_rollup: 'Vaatimustenmukaisuusyhteenveto',
            entity_name: 'Entiteetti',
            total_assessments: 'Arvioinnit yhteensä',
            completed: 'Valmis',
            completion_pct: 'Valmistumis-%',
            open_gaps: 'Avoimet Puutteet',
            critical_gaps: 'Kriittiset',
            compliance_score: 'Pisteet',
            no_rollup: 'Vaatimustenmukaisuusyhteenvetotietoja ei saatavilla.',
            entity_summary: 'Entiteetin Vaatimustenmukaisuusyhteenveto',
            frameworks_assigned: 'Viitekehykset',
            overall_score: 'Kokonaispisteet',
            load_departments_error: 'Osastojen lataaminen epäonnistui',
            load_users_error: 'Käyttäjien lataaminen epäonnistui',
            department_created: 'Osasto luotu',
            department_created_announce: 'Osasto luotu',
            create_department_error: 'Osaston luominen epäonnistui',
            user_mapped: 'Käyttäjä yhdistetty',
            user_mapped_announce: 'Käyttäjä yhdistetty osastoon',
            map_user_error: 'Käyttäjän yhdistäminen epäonnistui',
            user_removed: 'Käyttäjä poistettu',
            load_rollup_error: 'Yhteenvedon lataaminen epäonnistui',
            load_assignments_error: 'Määritysten lataaminen epäonnistui',
            framework_assigned: 'Kehys kohdistettu',
            framework_assigned_announce: 'Kehys kohdistettu entiteetille',
            assign_framework_error: 'Kehyksen määrittäminen epäonnistui',
            assignment_removed: 'Kohdistus poistettu',
            enter_entity_id_placeholder: 'Syötä entiteetin tunnus nähdäksesi kohdistukset',
            entity_type_organization: 'Organisaatio',
            entity_type_department: 'Osasto',
        },
        onboarding_maturity: {
            title: 'Aloituskysymys',
            quick_starts: 'Pikakäynnistykset',
            progress: 'Käyttöönoton edistyminen',
            personas: 'Personan polut',
            persona_onboarding: 'Personan käyttöönotto',
            quick_start_scenarios: 'Pika-aloitusskenaariot',
            start: 'Aloita',
            restart: 'Käynnistä uudelleen',
            estimated_minutes: 'Arvioidut minuutit',
            steps: 'Vaiheet',
            scenario: 'Skenaario',
            no_scenarios: 'Pikakäynnistysskenaarioita ei ole saatavilla.',
            persona_id: 'Henkilötunnus',
            persona_label: 'Persoona',
            role_description: 'Roolin kuvaus',
            primary_modules: 'Ensisijaiset moduulit',
            recommended_tours: 'Suositellut retket',
            sample_data_modules: 'Esimerkkitietomoduulit',
            quick_start_scenario: 'Pika-aloitusskenaario',
            no_personas: 'Henkilöpolkuja ei ole saatavilla.',
            framework_setup: 'Sääntelykehykset',
            framework_code: 'Puitekoodi',
            framework_name: 'Kehyksen nimi',
            related_modules: 'Liittyvät moduulit',
            persona_relevance: 'Asiaankuuluvat persoonat',
            no_frameworks: 'Kehysasetusvaihtoehtoja ei ole saatavilla.',
            tier_config: 'Tasomoduulin konfigurointi',
            tier: 'Taso',
            available_modules: 'Saatavilla olevat moduulit',
            module_count: 'Moduulien määrä',
            overall_completion: 'Yleinen valmistuminen',
            wizard_completed: 'Ohjattu toiminto valmis',
            tours_completed: 'Retket suoritettu',
            quick_starts_completed: 'Pikakäynnistykset suoritettu',
            selected_frameworks: 'Valitut puitteet',
            no_progress: 'Käyttöönoton edistymistietoja ei ole saatavilla.',
            detected_persona: 'Havaittu Persona',
            current_tier: 'Nykyinen taso',
            completion_pct: 'Valmistuminen',
            progress_page: {
                loaded: 'Edistyminen ladattu',
                no_progress: 'Ei perehdytyksen edistymistietoja saatavilla.',
                overall: 'Kokonaisedistyminen',
                detected_persona: 'Tunnistettu persona',
                tier: 'Taso',
                modules: 'moduulit',
                wizard: 'Ohjattu toiminto',
                completed: 'Valmis',
                pending: 'Odottaa',
                tours: 'Kierrokset',
                quick_starts: 'Pikaaloitukset',
                frameworks: 'Valitut viitekehykset',
                no_frameworks: 'Ei viitekehyksiä valittuna.',
                go_to_wizard: 'Suorita ohjattu asennus loppuun',
                framework_selection: 'Viitekehyksen valinta',
                framework_toggle: 'Ota viitekehyksiä käyttöön tai poista niitä vaatimustenmukaisuusohjelmastasi.',
                framework_updated: 'Viitekehyksen valinta päivitetty.',
                view_tours: 'Näytä kierrokset',
                view_quick_starts: 'Näytä pikaaloitukset',
                // English until Phase 25 (R-57).
                // fix-sprint #447 W5 — no progress record yet; reading the page creates none.
                not_started: 'Not started',
                not_started_body: 'No onboarding progress record exists for you yet. Opening this page does not create one: it is created when you complete the Setup Wizard. The figures below count the tours and quick starts you have already completed.',
            },
            persona_page: {
                description: 'Valitse persona nähdäksesi suositellut moduulit, kierrokset ja pikaaloitusskenaariot.',
                no_personas: 'Ei personapolkuja saatavilla.',
                modules: 'Moduulit',
                tours: 'Kierrokset',
                quick_start: 'Pikaaloitus',
                details: 'Tiedot',
                primary_modules: 'Ensisijaiset moduulit',
                recommended_tours: 'Suositellut kierrokset',
                framework_options: 'Sääntelykehykset',
                no_frameworks: 'Ei viitekehyksen asetusvalintoja saatavilla.',
                no_frameworks_explain: 'Organisaatiollesi ei ole vielä asennettu sääntelykehyksiä, joten täällä ei ole mitään määritettävää.',
                no_frameworks_action: 'Asenna markkinapaketti lisätäksesi sinua koskevat kehykset',
                start_tour: 'Aloita',
                no_tours: 'Ei kierroksia saatavilla.',
                help_center: 'Ohjekeskus',
                no_articles: 'Artikkeleita ei löytynyt.',
                primary: 'Ensisijainen',
                framework_code: 'Viitekehyskoodi',
                framework_label: 'Nimike',
                region: 'Alue',
                category: 'Luokka',
                enabled: 'Käytössä',
                disabled: 'Pois käytöstä',
                tier_modules: 'Tasomoduulikonfiguraatio',
                tiers_loaded: 'Tasokonfiguraatiot ladattu',
                no_tier_config: 'Ei tasokonfiguraatiota saatavilla.',
                view_tours: 'Näytä moduulikierrokset',
                view_quick_start: 'Aloita pikaaloitus',
                toggle_framework: 'Vaihda viitekehys',
                framework_updated: 'Viitekehyksen valinta päivitetty.',
                tier_desc_light: 'Yksityisyyden perusasiat — PDPL-vaatimustenmukaisuuden perusteet',
                tier_desc_standard: 'Täydellinen vaatimustenmukaisuuspaketti — kaikki moduulit työnkuluilla',
                tier_desc_enterprise: 'Edistynyt hallinto ja automaatio — moniyksikkö, ryhmäraportointi',
                persona_highlight: 'Sinun moduulisi',
                aria_go_to_module: '{name} — siirry moduuliin',
                aria_start_tour: 'Aloita {title}',
                aria_quick_start: 'Pika-aloitus: {title}',
                aria_help_article: 'Ohjeartikkeli: {title}',
                aria_view_framework: '{label}{primary} — katso puitteet',
                aria_primary_for_persona: '{name} (ensisijainen tälle henkilölle)',
                primary_for_persona_suffix: ' (ensisijainen tälle henkilölle)',
                aria_view_in_persona_paths: '{name} — näytä Persona Pathsissa',
                progress_of_total: '{completed}/{total} suoritettu',
            },
            // Sprint 63.28.0 — Persona Paths revised. English copy until the R-57 deferral (sprint 57.7.0).
            persona_paths: {
                page_description: 'Find your role among the personas your organisation can use. The roles your installed frameworks name come first. Choose one to see the frameworks that name it, the registers it works in and its quick start.',
                recommended_heading: 'Recommended for you',
                your_persona: 'Your persona',
                source_chosen: 'Chosen by you',
                source_detected: 'Detected automatically',
                source_detected_role: 'Detected from your user role',
                source_detected_default: 'Suggested as a starting point, because nothing else matched',
                chosen_unavailable: 'The persona you chose is no longer available to your organisation, because the market pack it needs was removed. The detected persona is shown instead.',
                recommended_none_ranked: 'No installed framework names a role yet, so only your persona is shown.',
                recommended_failed: 'Your persona could not be read.',
                recommended_loading: 'Reading your persona…',
                recommended_ranked: 'Named by your installed frameworks',
                toolbar_label: 'Filter roles',
                search_label: 'Search roles',
                search_placeholder: 'Role, description, sector, framework name or ID',
                family_label: 'Role family',
                family_all: 'All',
                sector_label: 'Sector',
                sector_placeholder: 'All sectors',
                market_label: 'Market',
                market_all: 'All markets',
                named_toggle: 'Named by my installed frameworks',
                sort_label: 'Sort by',
                sort_relevance: 'Relevance',
                sort_name: 'Name',
                sort_id: 'Persona ID',
                showing: 'Showing {shown} of {total} roles',
                clear_filters: 'Clear filters',
                list_label: 'Roles by family',
                list_heading: 'All roles',
                list_heading_named: 'Roles named by your installed frameworks',
                group_count_one: '1 role',
                group_count: '{count} roles',
                relevance_named_one: 'Named by 1 installed framework',
                relevance_named: 'Named by {n} installed frameworks',
                relevance_legacy_only: 'Only legacy or draft frameworks installed',
                selected: 'selected',
                load_failed: 'The list of roles could not be loaded.',
                reload: 'Reload',
                none_available_title: 'No roles are available to your organisation',
                none_available_description: 'Install a market pack to make its roles available.',
                no_match_title: 'No role matches these filters',
                no_match_description: 'Change or clear the filters to see more roles.',
                no_match_outside_one: '1 role matches outside your installed frameworks.',
                no_match_outside: '{count} roles match outside your installed frameworks.',
                hidden_matches_one: '1 more matches outside your installed frameworks.',
                hidden_matches: '{count} more match outside your installed frameworks.',
                show_all: 'Show all',
                notice_unavailable: '{name} ({id}) needs a market pack for {market}, which your organisation has not installed.',
                notice_unknown: 'No persona has the ID "{id}", so nothing was opened.',
                notice_unknown_use_case: 'No use case has the ID "{id}", so nothing was opened.',
                detail_label: 'Role details',
                detail_back: 'Back to the list of roles',
                detail_loading: 'Loading this role…',
                detail_failed: 'The details of this role could not be loaded.',
                detail_market: 'Market',
                detail_sectors: 'Sectors',
                detail_family: 'Role family',
                detail_persona_id: 'Persona ID',
                frameworks_heading: 'Frameworks for this role',
                frameworks_description: 'Read from the regulatory packs: every framework that names this role, as primary or supporting.',
                frameworks_installed: 'Installed',
                frameworks_not_installed: 'Not installed',
                frameworks_not_installed_count: 'Not installed ({count})',
                frameworks_none_installed: 'None of your installed frameworks names this role.',
                frameworks_only_retired_installed: 'Only retired or draft instruments are installed.',
                frameworks_none_not_installed: 'Every framework that names this role is installed.',
                frameworks_retired: 'Retired and draft instruments ({count})',
                role_primary: 'Primary',
                role_supporting: 'Supporting',
                status_active: 'Active',
                status_upcoming: 'Upcoming, in force from {date}',
                status_upcoming_undated: 'Upcoming',
                status_legacy: 'Legacy',
                status_draft: 'Draft',
                status_unknown: 'Status not stated',
                status_temporary_active: 'Temporarily in force',
                find_in_catalog: 'Find in the Market Packs Catalog',
                find_in_catalog_aria: 'Find in the Market Packs Catalog: {name}',
                basis_label: 'Basis: {article}',
                basis_link_aria: 'Open {article} of {name} in the Source tab',
                attached_through: 'Attached through {parent}',
                second_read_pending: 'Second reading pending',
                basis_missing: 'No article recorded',
                destinations_heading: 'Where the work happens',
                destinations_description: 'The registers this role works in, each marked by whether your tier includes it.',
                in_tier: 'In your tier',
                not_in_tier: 'Not in your tier',
                tier_checking: 'Checking your tier…',
                tier_unchecked: 'Your tier could not be checked, so no register is marked.',
                tier_not_permitted: 'Your user role cannot see which modules your tier includes, so no register is marked.',
                destination_no_access: 'Your user role cannot open this register',
                quick_start_heading: 'Your quick start',
                quick_start_none: 'No quick start has been written for this role yet.',
                quick_start_not_listed: 'The quick start "{id}" is not in your list, so nothing is highlighted.',
                tours_heading: 'Guided tours',
                tour_title: '{name} tour',
                start_tour: 'Start',
                start_tour_aria: 'Start: {title}',
                starting: 'Starting…',
                no_tours: 'No tours for this role.',
                help_heading: 'Help articles',
                help_none: "No help articles for this role's modules.",
                use_cases_heading: 'Use cases',
                use_cases_none: 'No validated use case names this role as its primary persona.',
                about_link: 'About this persona',
                save_action: 'Use as my onboarding persona',
                save_action_aria: 'Use as my onboarding persona: {name}',
                saving: 'Saving…',
                save_success: '{name} is now your onboarding persona.',
                already_yours: 'This is your onboarding persona.',
                profile_label: 'Onboarding persona',
                change_in_persona_paths: 'Change it in Persona Paths',
            },
            // Sprint 63.28.0 (B8) — the shared persona select. English copy until the R-57 deferral (sprint 57.7.0).
            persona_select: {
                placeholder: 'Choose a persona',
                no_options: 'No persona matches',
                checking: 'Checking which personas your organisation can use…',
                availability_failed: 'Which personas your organisation can use could not be checked, so every persona is listed.',
                retry: 'Try again',
            },
            quick_starts_page: {
                description: 'Harjoittele yleisiä työnkulkuja opastetuilla pikaaloitusskenaarioilla.',
                no_scenarios: 'Ei pikaaloitusskenaarioita saatavilla.',
                started: 'Pikaaloitus aloitettu',
                status_completed: 'Valmis',
                status_in_progress: 'Käynnissä',
                status_not_started: 'Ei aloitettu',
                minutes: 'min',
                steps: 'vaiheet',
                step: 'Vaihe',
                restart: 'Aloita alusta',
                start: 'Aloita',
                go_to_module: 'Siirry moduuliin',
                next_step: 'Seuraava vaihe',
                complete: 'Valmis',
                reset: 'Nollaa',
                step_of: '/',
                action_navigate: 'Siirry',
                action_form_fill: 'Täytä lomake',
                action_review: 'Tarkista',
                action_confirm: 'Vahvista',
                step_changed: 'Vaihe {current}/{total}: {title}',
                completed_message: 'Pikaaloitus suoritettu onnistuneesti!',
                reset_message: 'Pikaaloitus on nollattu.',
            },
            persona_p_vn_01_label: 'Yritys DPO',
            persona_p_vn_01_description: 'Data Protection Officer running the enterprise-wide personal data protection programme: impact assessments and their filings, rights requests, breach decisions and liaison with the authority',
            persona_p_vn_02_label: 'Osaston omistaja',
            persona_p_vn_02_description: 'Osaston johtaja, joka vastaa osien vaatimustenmukaisuuden raportoinnista',
            persona_p_vn_03_label: 'Sähköisen kaupankäynnin vaatimustenmukaisuus',
            persona_p_vn_03_description: 'Compliance manager at an e-commerce platform owning its registration and change notifications, the registration dossier, and the seller and traceability information it supplies when an authority requests it',
            persona_p_vn_04_label: 'Telecom Regulatory Affairs Manager',
            persona_p_vn_04_description: 'Regulatory affairs lead at a telecom operator handling authority requests for subscriber information and disclosure governance',
            persona_p_vn_05_label: 'Tietohallintojohtaja',
            persona_p_vn_05_description: 'Lead owning data mapping, classification and the annual governance attestation, and the data-service provider notification and reports where they apply',
            persona_p_vn_06_label: 'Kyberturvallisuus ja lakipalvelut',
            persona_p_vn_06_description: 'Cybersecurity lead owning incident reporting decisions, authority requests and directions, disclosure logging, and data localisation and local presence readiness',
            persona_p_vn_07_label: 'AI Hallintojohtaja',
            persona_p_vn_07_description: 'Johda AI järjestelmän hallinnan, riskinarvioinnin ja luokituksen valvontaa',
            persona_p_vn_08_label: 'Rajat ylittävä ja myyjien kuvernööri',
            persona_p_vn_08_description: 'Lead owning cross-border personal data transfer impact assessments and their filings, recipient and vendor safeguards, and data localisation checks',
            persona_p_vn_09_label: 'Rikkomusvastausjohto',
            persona_p_vn_09_description: 'Johda rikkomusilmoitusten ja tapahtumien reagoinnin koordinointia',
            persona_p_vn_10_label: 'Sisäisen tarkastuksen päällikkö',
            persona_p_vn_10_description: 'Lead running internal audits and regulator inspections across frameworks: evidence completeness, gaps, audit packs and sign-off',
            persona_p_vn_11_label: 'HR- ja työntekijätietojen vastuuhenkilö',
            persona_p_vn_11_description: 'HR manager handling employee consent records, electronic labour contracts and internal DSARs',
            persona_p_vn_12_label: 'Monikansallisen tytäryhtiön vaatimustenmukaisuuspäällikkö',
            persona_p_vn_12_description: 'Tietosuojavastaava ulkomaisen yrityksen Vietnamin tytäryhtiössä, joka tarvitsee paikallisen vaatimustenmukaisuuskerroksen',
            persona_p_vn_13_label: 'Koulutuksen vaatimustenmukaisuuspäällikkö',
            persona_p_vn_13_description: 'Vaatimustenmukaisuuspäällikkö ulkomaisessa koulussa, joka hallinnoi oppilaiden ja vanhempien tietosuojaa',
            qs_hr_consent_title: 'Rekisteröi työntekijöiden suostumukset',
            qs_hr_consent_description: 'Käy läpi työntekijöiden suostumuksen rekisteröinti ja kattavuusraportin luominen.',
            qs_subsidiary_dpia_title: 'Luo tytäryhtiön DPIA-arviointi',
            qs_subsidiary_dpia_description: 'Käy läpi DPIA:n luominen tytäryhtiön käsittelytoiminnalle.',
            qs_student_consent_title: 'Määritä oppilaiden suostumuksen kerääminen',
            qs_student_consent_description: 'Käy läpi vanhempien suostumuskäytäntöjen luominen oppilastiedoille.',
            persona_p_vn_14_label: 'Ulkopuolinen lainopillinen neuvonantaja',
            persona_p_vn_14_description: 'Vanhempi asianajotoimistokumppani, joka neuvoo useita asiakasorganisaatioita PDPL:n noudattamisessa',
            persona_p_vn_15_label: 'Asianajotoimiston toimintapäällikkö',
            persona_p_vn_15_description: 'Asiakasvastuukoordinaattori, joka hallitsee suoritteiden seurantaa ja määräaikojen koordinointia',
            persona_p_vn_16_label: 'Yrityksen omistaja / CEO',
            persona_p_vn_16_description: 'CEO tai GM, joka on laillisesti rekisterinpitäjä PDPL:n mukaisesti ja joka on vastuussa vaatimustenmukaisuusinvestoinneista',
            qs_dpia_filing_title: 'Täytä ensimmäinen DPIA hakemuksesi',
            qs_dept_compliance_title: 'Lähetä ensimmäinen osastosi vaatimustenmukaisuusosio',
            qs_ecommerce_consent_title: 'Prepare your e-commerce platform registration dossier',
            qs_telecom_data_title: 'Ota käyttöön televiestintätilaajatietojen käsittely',
            qs_data_flow_title: 'Kartoita ensimmäinen tietovirtasi',
            qs_incident_response_title: 'Määritä tapausvastauksen työnkulku',
            qs_ai_risk_title: 'Suorita AI järjestelmäriskin arviointi',
            qs_cross_border_title: 'Arvioi rajat ylittävä tiedonsiirto',
            qs_breach_notification_title: 'Simuloi rikkomusilmoitusten työnkulkua',
            qs_audit_trail_title: 'Tarkista vaatimustenmukaisuuden kirjausketju',
            qs_legal_advisor_portfolio_title: 'Tarkista usean asiakkaan vaatimustenmukaisuusportfolio',
            qs_legal_advisor_portfolio_description: 'Tarkista vaatimustenmukaisuuden tila useissa asiakasorganisaatioissa.',
            qs_law_firm_ops_title: 'Seuraa asiakkaan toimituksen tilaa',
            qs_law_firm_ops_description: 'Seuraa neuvontatoimien edistymistä ja asiakkaiden sitoutumista.',
            qs_ceo_dashboard_title: 'Näytä johtajien vaatimustenmukaisuuden hallintapaneeli',
            qs_ceo_dashboard_description: 'Tarkastele korkean tason vaatimustenmukaisuuden tilaa ja sijoitusmittareita.',
        },
        rights_requests: {
            response_templates: {
                title: 'Vastausmallit',
                subtitle: 'Hallinnoi DSR-vastauskirjemalleja, joita ResponseGenerator käyttää vastausten laatimiseen rekisteröidyille.',
                create_btn: 'Uusi malli',
                edit_btn: 'Muokkaa mallia',
                filter_right_type: 'Suodata oikean tyypin mukaan',
                filter_locale: 'Suodata kieliasetuksen mukaan',
                filter_status: 'Suodata tilan mukaan',
                all_right_types: 'Kaikki oikeat tyypit',
                any_right_type: 'Mikä tahansa (koskee kaikkia tyyppejä)',
                all_locales: 'Kaikki alueet',
                all_statuses: 'Kaikki',
                status_active: 'Aktiivinen',
                status_inactive: 'Ei-aktiivinen',
                empty_state: 'Yksikään malli ei vastaa nykyisiä suodattimia.',
                columns: {
                    template_id: 'Mallin tunnus',
                    outcome: 'Tulos',
                    locale: 'Alue',
                    right_type: 'Oikea tyyppi',
                    is_active: 'Tila',
                    last_updated: 'Viimeksi päivitetty',
                    actions: 'Toiminnot',
                },
                actions: {
                    edit: 'Muokkaa',
                    clone: 'Klooni',
                    deactivate: 'Poista käytöstä',
                    reactivate: 'Aktivoi uudelleen',
                    preview: 'Esikatselu',
                },
                form: {
                    template_id: 'Mallin tunnus',
                    template_id_help: 'Pienet kirjaimet, esim. dsr_access_fulfilled_vi',
                    outcome: 'Tulos',
                    locale: 'Alue',
                    right_type: 'Oikea tyyppi',
                    subject: 'Aihe',
                    body: 'Runkomalli',
                    body_help: 'Käytä paikkamerkkejä, kuten {case_reference}, {data_subject_name}, {fulfilled_at}. Vähintään 15 riviä moniosaisille kirjeille.',
                    is_active: 'Aktiivinen',
                    official_form_id: 'Virallinen lomaketunnus (valinnainen)',
                },
                outcomes: {
                    fulfilled: 'Täytetty',
                    partially_fulfilled: 'Osittain täytetty',
                    denied: 'Kielletty',
                },
                clone_modal_title: 'Kloonaa malli',
                clone_target_locale: 'Kohdealue',
                clone_help: 'Kohdealueelle luodaan uusi malli käyttämällä tätä mallia aloituskohtana.',
                preview_modal_title: 'Mallin esikatselu',
                preview_note: 'Esikatselu käyttää esittelytapauksen DSR-DEMO-0020 paikkamerkkejä. Todellinen toimitus korvaa live-tapaustiedot.',
                preview_unavailable: 'Esikatselu vaatii olemassa olevan tapauksen. Liitä tämä malli tapaukseen ja käytä Vastausluonnos -toimintoa tapauksen lisätietosivulla.',
                deactivate_confirm: 'Poistetaanko tämä malli? Sitä sovelletaan edelleen olemassa oleviin tapauksiin, joissa siihen viitataan.',
                toasts: {
                    created: 'Malli luotu',
                    updated: 'Malli päivitetty',
                    deactivated: 'Malli poistettu käytöstä',
                    reactivated: 'Malli aktivoitu uudelleen',
                    cloned: 'Malli kloonattu',
                    load_error: 'Mallien lataaminen epäonnistui',
                    save_error: 'Mallin tallennus epäonnistui',
                    clone_error: 'Mallin kloonaus epäonnistui',
                },
            },
        },
        organizations: {
            departments: {
                title: 'Osastot',
                subtitle: 'Hallinnoi osastoja, jotka reitittävät DSR-tapauksia, DPIA tehtäviä ja vaatimustenmukaisuuden työnkulkuja.',
                create_btn: 'Uusi osasto',
                edit_btn: 'Muokkaa osastoa',
                columns: {
                    name: 'Nimi',
                    description: 'Kuvaus',
                    active_cases: 'Aktiiviset tapaukset',
                    members: 'jäsenet',
                    status: 'Tila',
                    actions: 'Toiminnot',
                },
                status_active: 'Aktiivinen',
                status_inactive: 'Ei-aktiivinen',
                form: {
                    name: 'Nimi',
                    description: 'Kuvaus',
                },
                actions: {
                    edit: 'Muokkaa',
                    deactivate: 'Poista käytöstä',
                    reactivate: 'Aktivoi uudelleen',
                    delete: 'Poista',
                    manage_members: 'Hallitse jäseniä',
                },
                delete_modal_title: 'Poista osasto',
                delete_warning: 'Tällä osastolla on edelleen aktiivisia tapauksia. Valitse osasto, johon ne siirretään ennen poistamista.',
                delete_no_cases: 'Tällä osastolla ei ole aktiivisia tapauksia, ja se voidaan poistaa turvallisesti.',
                reassign_prompt: 'Määritä aktiiviset tapaukset uudelleen seuraaville:',
                reassign_none_available: 'Muita aktiivisia osastoja ei ole saatavilla. Luo ensin toinen osasto.',
                member_section_title: 'jäsenet',
                add_member: 'Lisää jäsen',
                remove_member: 'Poista',
                member_role: 'Rooli',
                no_members: 'Tälle osastolle ei ole yhdistetty yhtään jäsentä.',
                toasts: {
                    created: 'Osasto luotu',
                    updated: 'Osasto päivitetty',
                    deactivated: 'Osasto poistettu käytöstä',
                    reactivated: 'Osasto aktivoitu uudelleen',
                    deleted: 'Osasto poistettu',
                    reassigned_count: '{count} tapausta siirretty',
                    member_added: 'Jäsen lisätty',
                    member_removed: 'Jäsen poistettu',
                    load_error: 'Osastojen lataaminen epäonnistui',
                    save_error: 'Osaston tallentaminen epäonnistui',
                    delete_error: 'Osaston poistaminen epäonnistui',
                },
            },
            retention_policies: {
                title: 'Säilytyskäytännöt',
                subtitle: 'Määritä kohdetyyppiset säilytysikkunat. Henkilöllisyyden vahvistusasiakirjat käyttävät tätä leimaamaan retention_expires_at tuleviin latauksiin.',
                create_btn: 'Uusi politiikka',
                edit_btn: 'Muokkaa käytäntöä',
                columns: {
                    target_type: 'Kohdetyyppi',
                    retention_days: 'Säilytys (päiviä)',
                    owner: 'Käytännön omistaja',
                    effective_from: 'Voimassa alkaen',
                    last_reviewed: 'Viimeksi tarkistettu',
                    status: 'Tila',
                    actions: 'Toiminnot',
                },
                status_active: 'Aktiivinen',
                status_inactive: 'Ei-aktiivinen',
                form: {
                    target_type: 'Kohdetyyppi',
                    target_type_help: 'Mitä tietoluokkaa säilytysikkuna koskee. Tällä hetkellä tuetaan vain DSR-henkilöllisyyden todentamisasiakirjoja.',
                    retention_days: 'Säilytysikkuna (päiviä)',
                    retention_days_help: '1–3 650 päivää (enintään 10 vuotta).',
                    owner: 'Käytännön omistaja',
                    owner_help: 'Yleensä DPO. Vastuussa DG-2:n mukaisesta säilyttämispäätöksestä.',
                    effective_from: 'Voimassa alkaen',
                },
                target_types: {
                    dsr_identity_verification_document: 'DSR-henkilöllisyyden todentamisasiakirjat',
                },
                deactivate_notice: 'Deaktivointi ei vaikuta jo leimattuihin liitteisiin – vain tulevat lataukset eivät enää saa säilytysikkunaa.',
                deactivate_confirm: 'Poistetaanko tämä säilytyskäytäntö?',
                toasts: {
                    created: 'Säilytyskäytäntö luotu',
                    updated: 'Säilytyskäytäntö päivitetty',
                    deactivated: 'Säilytyskäytäntö poistettu käytöstä',
                    load_error: 'Säilytyskäytäntöjen lataaminen epäonnistui',
                    save_error: 'Säilytyskäytännön tallentaminen epäonnistui',
                },
            },
        },
    },
    data_mapping: {
        // Sprint 62.58.0 (Slice N) — English for key parity until Phase 25 (R-57).
        deployment_privacy: {
            audience_kinds: {
                public: 'The public',
                data_subject_group: 'Named data subjects',
            },
            channels: {
                website: 'Website',
                point_of_collection: 'At the point of collection',
                employment_documentation: 'Employment documentation',
                contract_or_terms: 'Contract or terms',
                in_app: 'In the app',
                email: 'Email',
                other: 'Other',
            },
            access_kinds: {
                view: 'View',
                extract: 'Extract',
                export: 'Export',
                delete: 'Delete',
                configure: 'Configure',
            },
            export: {
                coverage_heading: 'Privacy notices by data subject',
                coverage_withheld: 'Notice details are shown only to a reader who may read the privacy notice register.',
                col_activity: 'Processing activity',
                col_data_subject: 'Data subject',
                col_notice: 'Notice',
                col_revision: 'Revision',
                col_channels: 'Channels',
                col_effective_from: 'In effect from',
                col_status: 'Status',
                not_recorded: 'Not recorded',
                no_current_notice: 'No current notice: every notice that names this data subject is withdrawn, superseded or replaced by a newer revision',
                status_replaced: '{status}, replaced by a newer revision',
                not_stated: 'Not stated',
                access_heading: 'Who may access the data',
                col_version: 'Version',
                col_reviewed_on: 'Reviewed on',
                col_role_or_position: 'Role or position',
                col_access_kinds: 'Permitted actions',
                col_means: 'By what means',
                col_conditions: 'On what conditions',
                no_access_statement: 'No access statement recorded',
                no_data_subjects: 'No data subjects recorded',
                public_linked: 'Also linked: {count} public notices, which name no data subject',
                public_linked_one: 'Also linked: 1 public notice, which names no data subject',
                coverage_truncated: 'More notices are linked to some processing activities than this export lists, so for those activities the notices are listed published first, then approved, draft, withdrawn and superseded, the most recently drafted first within each. The processing activities concerned: {activities}. A data subject reads "Not recorded" only when no linked notice names it.',
                access_withheld: 'Access statements are shown only to a reader who may read processing activities.',
                col_approved_by: 'Approved by',
                col_systems: 'Systems',
            },
            notice_form: {
                heading: 'Audience, channels and deployment',
                intro: 'Say whom this revision is given to, how they are given it, from which day, and the processing activities and systems it is given for.',
                audience_label: 'Whom the notice is given to',
                audience_public_hint: 'Anyone can read it, for example on your website. A public notice names no data subject, so the notice coverage of a processing activity does not list it against any data subject.',
                audience_group_hint: 'Named data subjects of the linked processing activities, such as drivers, passengers or staff.',
                audience_group_need_grant: 'Naming data subjects needs the permission to read processing activities, which your role does not hold: the data subjects are chosen from the linked processing activities. The notice can be given to the public.',
                data_subjects_label: 'Data subjects the notice is given to',
                data_subjects_hint: 'Chosen from the data subjects recorded on the linked processing activities.',
                data_subjects_need_activity: 'Link at least one processing activity first: the data subjects are chosen from those it records.',
                data_subjects_none: 'The linked processing activities record no data subjects.',
                data_subjects_required: 'Choose at least one data subject.',
                data_subjects_unknown: '{value} (not recorded on the linked processing activities)',
                channels_label: 'How the audience is given the notice',
                channels_hint: 'Choose at least one. A sticker or a screen where the data is collected, such as inside a vehicle, is "At the point of collection".',
                channels_required: 'Choose at least one channel.',
                channel_note_label: 'Describe the other channel',
                channel_note_required: 'Describe the other channel.',
                effective_from_label: 'In effect from',
                effective_from_hint: 'The day the notice takes effect, as your organization states it. Leave it empty if the day is not yet known.',
                activities_label: 'Processing activities the notice is given for',
                activities_hint: 'The data subjects of these activities are the ones the notice can name.',
                systems_label: 'Data-map systems the notice covers',
                links_need_grant: 'Linking a notice to processing activities or systems needs the permission to read processing activities, which your role does not hold. The notice can be drafted without links.',
                systems_need_grant: 'Choosing data-map systems needs the permission to read systems, which your role does not hold.',
                picker_loading: 'Loading the list…',
                activities_empty: 'No processing activity is recorded.',
                systems_empty: 'No data-map system is recorded.',
                activities_failed: 'The processing activities could not be read.',
                systems_failed: 'The data-map systems could not be read.',
                activities_manual_label: 'Processing activity IDs',
                systems_manual_label: 'Data-map system IDs',
                manual_hint: 'Enter the IDs separated by commas; they are checked when you save.',
                off_list: '{value} (not in the list read)',
                retry: 'Reload',
                reloading: 'Reloading…',
                activities_retry_subject: 'the processing activities',
                systems_retry_subject: 'the data-map systems',
                error_link_grant: 'Your role cannot link a notice to processing activities or systems. Remove the links, or ask for the permission to read processing activities.',
                error_link_not_found: 'A linked processing activity or system was not found in this organization. What you entered is kept.',
                error_audience_invalid: 'A chosen data subject is not recorded on the linked processing activities. What you entered is kept.',
                col_audience: 'Audience',
                col_channels: 'Channels',
                col_effective_from: 'In effect from',
                col_links: 'Linked to',
                links_activities: 'Processing activities: {names}',
                links_activities_count: '{count} processing activities',
                links_activities_count_one: '1 processing activity',
                links_systems: 'Systems: {names}',
                links_systems_count: '{count} systems',
                links_systems_count_one: '1 system',
                links_none: 'None',
                other_channel: 'Other: {note}',
                data_subjects_unconfirmed: '{value} (not confirmed: a linked processing activity could not be read)',
                error_systems_grant: 'Your role cannot link a notice to data-map systems. Remove the systems, or ask for the permission to read systems.',
                audience_with_subjects: '{label}: {subjects}',
            },
            coverage: {
                heading: 'Notices',
                intro: 'For each data subject recorded on this activity, the linked privacy notices that name it, with revision, channels, date in effect and status. Nothing here is counted or scored.',
                not_visible: 'Your role cannot read the privacy notice register, so the notices are not shown here. This does not say whether a notice exists.',
                no_subjects: 'This activity records no data subjects.',
                list_label: 'Notices by data subject',
                notice_line: '{title}, revision {revision}',
                notice_meta: 'Channels: {channels} · In effect from {date} · Status: {status}',
                load_failed: 'The notices could not be read.',
                retry_subject: 'the notices of this activity',
                pagination: 'Pages of data subjects',
                public_heading: 'Also linked: public notices',
                public_intro: 'These notices are addressed to the public. A public notice names no data subject, so it is not listed against any data subject above.',
                public_failed: 'The public notices linked to this activity could not be read.',
                public_retry_subject: 'the public notices',
                truncated: 'More notices are linked to this activity than are listed here, so they are listed published first, then approved, draft, withdrawn and superseded, the most recently drafted first within each. A data subject reads "Not recorded" only when no linked notice names it.',
                subject_state: '{subject}: {state}',
            },
            access: {
                intro: 'Who may view, extract, export, delete or configure the data of this activity, by what means and on what conditions. Every revision is kept; the latest is first.',
                record: 'Record a revision',
                record_first: 'Record the access statement',
                current: 'Current',
                version: 'Version {version}',
                reviewed: 'Reviewed on {date}',
                approved: 'Approved by {name}',
                not_approved: 'No approver recorded',
                recorded: 'Recorded by {name} on {date}',
                systems: 'Systems: {names}',
                entries_label: 'Entries of version {version}',
                evidence_count: 'Documents: {count}',
                evidence_none: 'No document attached',
                evidence_open: 'Documents',
                evidence_open_aria: 'Documents of version {version}',
                load_failed: 'The access statement could not be read.',
                retry_subject: 'the access statement',
                pagination: 'Pages of access statement revisions',
                history_label: 'Revisions of the access statement',
                unknown_person: 'Unknown person',
            },
            access_form: {
                title: 'Record a revision of the access statement',
                replaces: 'This revision replaces version {version}. The earlier versions are kept.',
                first: 'This is the first revision of the access statement for this activity.',
                entries_heading: 'Who may access the data',
                entry_legend: 'Entry {n}',
                role_label: 'Role or position',
                role_hint: 'A role or a position, such as "Fleet safety officer". A person\'s name is not needed.',
                access_kinds_label: 'Permitted actions',
                means_label: 'By what means',
                means_hint: 'For example, the extraction software named, a console, or a request to IT.',
                conditions_label: 'On what conditions',
                conditions_hint: 'For example, a ticket, two people present, or a stated purpose.',
                add_entry: 'Add an entry',
                remove_entry: 'Remove entry {n}',
                reviewed_on_label: 'Reviewed on',
                reviewed_on_hint: 'The day the statement was reviewed. It cannot be later than today.',
                approver_label: 'Approved by (optional)',
                approver_none: 'No approver',
                approver_retry_subject: 'the list of people',
                systems_label: 'Data-map systems the statement covers',
                systems_kept: 'The systems the current version covers are kept, because your role cannot read the data-map systems.',
                missing: 'Give every entry a role or position and at least one permitted action, and give the review date.',
                submit: 'Record the revision',
                saving: 'Recording…',
                recorded: 'Revision recorded.',
                record_failed: 'The revision could not be recorded. What you entered is kept.',
                conflict: 'Someone recorded version {version} of this statement while you were editing, so yours was not recorded. What you entered now builds on version {version}: the entries and systems you did not change read as in version {version}, and your additions, changes and removals are kept. Check the entries, then record again.',
                conflict_reread_failed: 'Someone recorded a newer revision of this statement while you were editing, so yours was not recorded, and the newer revision could not be read. What you entered is kept; record again to read it once more.',
            },
            access_files: {
                heading: 'Documents',
                intro: "The documents behind this revision, such as the access policy, the extraction software's role list or the signed approval.",
                slot: 'Documents of this revision',
                none: 'No document is attached to this revision.',
                file_hash: 'SHA-256: {hash}',
                not_held: 'Document no longer held',
                download: 'Download',
                remove: 'Remove',
                upload: 'Add a document',
                bound: 'Document added.',
                removed: 'Document removed.',
                bind_failed: 'The document could not be added.',
                remove_failed: 'The document could not be removed.',
                not_owned: 'The revision this document belongs to was not found.',
                remove_confirm: 'Remove this document from the revision and delete it?',
                load_failed: 'The documents could not be loaded.',
            },
        },
        lawful_basis: {
            title: 'Lawful basis',
            load_failed: 'The lawful bases your installed frameworks declare could not be read.',
            none_declared: 'No installed framework declares a lawful-basis vocabulary. Record the basis and the article it rests on below.',
            declared_by: 'Declared by {instrument}',
            citation: 'Citation',
            statement_label: 'Lawful basis (recorded by you)',
            statement_citation_label: 'Article this basis rests on',
            other_ground: 'Another lawful basis, recorded by you',
            needs_redetermination: 'This record carries "{basis}", which no installed framework declares. It needs re-determining.',
            needs_redetermination_declared: 'This record carries "{basis}" and is marked as needing re-determining. Confirm this basis or choose another, then save.',
            stored_unverified: 'This record carries "{basis}". Some installed frameworks have not been verified yet, so whether one of them declares it is not known.',
            instruments_heading: 'Instruments checked',
            instrument_declares_none: '{instrument} — verified: declares no lawful basis',
            instrument_not_verified: '{instrument} — not yet verified',
            retry: 'Try again',
            list_needs_redetermination: 'Needs re-determining: {basis}',
            declared_version_changed: 'Recorded against {framework} {version}; installed now: {installed}.',
            declared_version_withdrawn: 'Recorded against {framework} {version}; no installed framework declares this basis now.',
            loading: 'Loading the lawful bases your installed frameworks declare.',
            save_blocked_unread: 'A lawful basis is required, and the bases your installed frameworks declare have not been read yet.',
            citation_blank: 'Enter the article this basis rests on. Spaces alone are not an article.',
            citation_without_statement: 'An article is entered but no basis is recorded. Record the basis it supports, or clear the article.',
        },
        // OD42 (fix-sprint-20261003-0625 F28) — the transfer-mechanism control and
        // its list sentences. The mechanism is what the installed frameworks declare.
        transfer_mechanism_vocabulary: {
            title: 'Transfer mechanism',
            load_failed: 'The transfer mechanisms your installed frameworks declare could not be read.',
            loading: 'Loading the transfer mechanisms your installed frameworks declare.',
            retry: 'Try again',
            none_declared: 'No installed framework declares a transfer mechanism. Describe the transfer in the detail field.',
            none_declared_unverified: 'No installed framework has declared a transfer mechanism, and some have not been verified yet. Describe the transfer in the detail field.',
            declared_by: 'Declared by {instrument}',
            citation: 'Citation',
            needs_redetermination: 'This record carries "{mechanism}", which no installed framework declares. It needs re-determining.',
            needs_redetermination_declared: 'This record carries "{mechanism}" and is marked as needing re-determining. Confirm this mechanism or choose another, then save.',
            unverified: 'This record carries "{mechanism}". No verified installed framework declares it; not yet verified: {instruments}.',
            instruments_heading: 'Instruments checked',
            instrument_declares_none: '{instrument} — verified: declares no transfer mechanism',
            instrument_not_verified: '{instrument} — not yet verified',
            list_needs_redetermination: 'Needs re-determining: {mechanism}',
            list_unverified: '{mechanism} (not yet verified: {instruments})',
            stored_unmeasured: 'This record carries "{mechanism}". No installed framework has stated which transfer mechanisms it provides yet, so whether it is declared has not been checked.',
        },
        nav: {
            systems: 'Järjestelmät',
            categories: 'Tietoluokat',
            registry: 'Tietorekisteri',
            purposes: 'Käsittelytarkoitukset',
            retention: 'Säilytys',
            flows: 'Tietovirrat',
            activities: 'Käsittelytoimet',
            evidence: 'Todistusaineisto',
            exports: 'Viennit',
            attestation: 'Todistusjaksot',
            governance: 'Hallinto',
            review: 'Tarkistustehtävät',
            inventory: 'Inventaario',
            exceptions: 'Poikkeukset',
            history: 'Historia',
            ropa_completeness: 'ROPA-täydellisyys',
        },
        ropa_completeness_autofix: {
            ask_button: 'Kysy Forsetilta',
            drafting: 'Luonnostetaan…',
            accept: 'Hyväksy',
            reject: 'Hylkää',
            accepted: 'Hyväksytty — hae aktiviteettieditorin kautta.',
            rejected: 'Hylätty.',
            needs_human_input: 'Forseti tarvitsee lisää toimintakontekstia (esim. dokumentoitua tarkoitusta) ennen tämän kentän laatimista.',
            ask_again: 'Ask again',
            draft_failed: 'Forseti could not draft a value.',
            decision_failed: 'The decision could not be saved.',
        },
        ropa_completeness: {
            rules: {
                nd165_scope_rationale: 'Confirm and evidence the transfer scope; review mixed data before selecting the applicable assessment route.',
                nd165_scope_condition: 'Important/core cross-border data with mixed or unresolved personal-data scope',
                nd165_personal_dpia_rationale: 'Record the processing impact assessment reference when required, including confirmed personal core/important cross-border data.',
                nd165_personal_dpia_condition: 'Confirmed personal important/core data + cross-border transfer',
                nd165_personal_transfer_rationale: 'Record the separate personal-data cross-border transfer impact assessment dossier reference.',
                nd165_personal_transfer_condition: 'Confirmed personal important/core data + cross-border transfer',
                nd165_nonpersonal_assessment_rationale: 'Core non-personal data requires a regulator-issued assessment for cross-border transfers.',
                nd165_nonpersonal_assessment_condition: 'Confirmed non-personal core data + cross-border transfer',
            },
            title: 'ROPA:n täydellisyysraportti',
            subtitle: 'Kenttäkohtainen hygieniatarkastus käsittelytoimintojesi tiedoista, sääntelykehyksen piirissä.',
            framework_label: 'puitteet',
            kpi_total: 'Aktiviteetit pisteytetty',
            kpi_critical: 'Kriittinen',
            kpi_high: 'Korkea',
            kpi_conditional: 'Ehdollinen',
            field_chart_heading: 'Puuttuvat kentät taajuuden mukaan',
            drilldown_heading: 'Toiminta, jossa on aukkoja',
            col_activity: 'Toiminta',
            col_field: 'Kenttä',
            col_severity: 'Vakavuus',
            col_condition: 'Kunto',
            col_action: 'Fix',
            action_fix: 'Fix',
            severity_critical: 'Kriittinen',
            severity_high: 'Korkea',
            severity_conditional: 'Ehdollinen',
            severity_informational: 'Tiedollinen',
            include_informational: 'Sisällytä tiedotus',
            export_csv: 'Lataa CSV',
            exporting: 'Viedään...',
            loading: 'Ladataan…',
            empty_state: 'Kaikilla aktiivisilla toimilla on täydelliset ROPA-tietueet tälle viitekehykselle.',
            error_fetch: 'Täydellisyysraportin lataaminen epäonnistui.',
            error_unsupported_framework: 'Tätä kehystä ei tueta.',
            error_framework_not_installed: 'Tätä kehystä ei ole asennettu organisaatiollesi.',
            no_misses_for_field: 'Tästä kentästä ei puutu mitään toimintoja.',
            framework_PDPL: 'PDPL (laki 91/2025 + asetus 356)',
            framework_GDPR: 'GDPR 30 artikla',
            framework_DECREE_165: 'Asetus 165 (Vietnamin tietolaki)',
            framework_ISO_27701: 'ISO 27701 (tiedot)',
        },
        registry: {
            // Sprint 64.72.0 — tietorekisterin viennin sarakeotsikot.
            export_col: {
                registry_item_id: 'Rekisterimerkinnän tunnus',
                name: 'Nimi',
                description: 'Kuvaus',
                field_identifiers: 'Kenttätunnisteet',
                category_id: 'Luokan tunnus',
                category_name: 'Luokan nimi',
                data_type: 'Tietotyyppi',
                effective_sensitivity: 'Voimassa oleva arkaluonteisuus',
                sensitivity_override: 'Ohitettu arkaluonteisuus',
                data_subjects: 'Rekisteröidyt',
                volume_band: 'Määräluokka',
                source_system_ids: 'Lähdejärjestelmien tunnukset',
                processing_activity_ids: 'Käsittelytoimien tunnukset',
                retention_policy_id: 'Säilytyskäytännön tunnus',
                effective_retention_policy_id: 'Voimassa olevan säilytyskäytännön tunnus',
                purposes: 'Käyttötarkoitukset',
                collection_method: 'Keräystapa',
                legal_basis: 'Oikeusperuste',
                data_tier: 'Tietotaso',
                data_tier_source: 'Tietotason lähde',
                storage_locations: 'Säilytyspaikat',
                cross_border: 'Rajat ylittävä',
                status: 'Tila',
                source_form_submission_id: 'Lähdelomakkeen lähetystunnus',
                source_form_template_id: 'Lähdelomakkeen mallitunnus',
                last_reviewed_at: 'Viimeksi tarkastettu',
                next_review_at: 'Seuraava tarkastus',
                created_at: 'Luotu',
                updated_at: 'Päivitetty',
                data_element: 'Tietoelementti',
                data_category: 'Tietoluokka',
                systems: 'Järjestelmät',
                retention_policy: 'Säilytyskäytäntö',
                name_of_processing: 'Käsittelyn nimi',
                categories_of_personal_data: 'Henkilötietoryhmät',
                categories_of_data_subjects: 'Rekisteröityjen ryhmät',
                recipients: 'Vastaanottajat',
                third_country_transfers: 'Siirrot kolmansiin maihin',
                retention_period: 'Säilytysaika',
                doc_title: 'Tietorekisteri',
                doc_generated: 'Vienti luotu {timestamp}',
                doc_item_count: '{count} rekisterimerkintää',
                doc_template_full: 'Täydellinen',
            },
            export_vocab: {
                data_type: {
                    text: 'Teksti',
                    number: 'Luku',
                    date: 'Päivämäärä',
                    datetime: 'Päivämäärä ja kellonaika',
                    boolean: 'Kyllä/ei',
                    binary: 'Binääri',
                    structured: 'Rakenteinen',
                    file: 'Tiedosto',
                    identifier: 'Tunniste',
                    geolocation: 'Sijaintitieto',
                },
                collection_method: {
                    direct_from_subject: 'Suoraan rekisteröidyltä',
                    third_party: 'Kolmannelta osapuolelta',
                    derived: 'Johdettu muista tiedoista',
                    observed: 'Havainnoitu',
                    inferred: 'Päätelty',
                    public_source: 'Julkinen lähde',
                },
                data_subject_type: {
                    customer: 'Asiakas',
                    employee: 'Työntekijä',
                    contractor: 'Urakoitsija',
                    prospect: 'Mahdollinen asiakas',
                    minor: 'Alaikäinen',
                    patient: 'Potilas',
                    student: 'Opiskelija',
                    beneficiary: 'Edunsaaja',
                    vendor_contact: 'Toimittajan yhteyshenkilö',
                    website_visitor: 'Verkkosivuston kävijä',
                    other: 'Muu',
                },
                volume_band: {
                    lt_1k: 'Alle 1 000 tietuetta',
                    '1k_to_10k': '1 000–10 000 tietuetta',
                    '10k_to_100k': '10 000–100 000 tietuetta',
                    '100k_to_1m': '100 000–1 miljoona tietuetta',
                    gt_1m: 'Yli 1 miljoona tietuetta',
                    unknown: 'Ei tiedossa',
                },
                tier_source: {
                    assessed_and_approved: 'Arvioitu ja hyväksytty',
                    manual_legacy: 'Otettu käyttöön vanhasta arvosta',
                    legacy_platform_field: 'Peritty alustan arvo',
                    scheme_scoped_only: 'Arvioitu järjestelmäkohtaisten tasojen mukaan — mikään yksittäinen koodi ei kata useaa lakisääteistä tasoasteikkoa',
                },
            },
            page: {
                title: 'Tietorekisteri',
                subtitle: 'Organisaatiosi hallussa olevat henkilötietoelementit on järjestetty Tietoluokkiin.',
                empty_state: 'Ei vielä rekisteritietoja. Lisää tuote manuaalisesti tai tuo viranomaisrekisteristä.',
                empty_filter_state: 'Yksikään kohde ei vastaa suodattimiasi.',
            },
            filter: {
                category: 'Luokka',
                sensitivity: 'Herkkyys',
                data_subject: 'Rekisteröity',
                status: 'Tila',
                data_tier: 'Datataso',
                cross_border: 'Rajat ylittävä',
                has_owner: 'On omistaja',
                has_form_source: 'Tuotu lomakkeesta',
                search_placeholder: 'Hae nimi, kuvaus tai kentän tunniste…',
                clear: 'Selkeät suodattimet',
                option_all: 'Kaikki',
            },
            col: {
                name: 'Nimi',
                category: 'Luokka',
                sensitivity: 'Herkkyys',
                data_subjects: 'Rekisteröidyt',
                systems: 'Järjestelmät',
                activities: 'Aktiviteetit',
                owner: 'Omistaja',
                retention: 'Säilytys',
                data_tier: 'Datataso',
                status: 'Tila',
                last_reviewed: 'Viimeksi tarkistettu',
                actions: 'Toiminnot',
            },
            action: {
                add_manual: 'Lisää kohde',
                add_from_form: 'Lisää viranomaisrekisteristä',
                import_history: 'Tuo aiemmat lähetykset',
                export: 'Vie',
                archive: 'Arkisto',
                restore: 'Palauta',
                edit: 'Muokkaa',
                assign_owner: 'Määritä omistaja',
                attach_evidence: 'Liitä todisteet',
                open_lineage: 'Avoin sukulinja',
            },
            modal_editor: {
                section_identity: 'Identiteetti',
                section_classification: 'Luokittelu',
                section_subjects: 'Aiheet ja määrä',
                section_location: 'Sijainti ja siirto',
                section_systems: 'Järjestelmät ja toiminnot',
                section_retention: 'Säilytys ja laki',
                section_governance: 'Hallinto',
                section_notes: 'Huomautuksia',
                hint_inherited: '(peritty kategoriasta)',
                hint_overridden: '(ohitattu)',
                placeholder_field_identifiers: 'esim. asiakkaat.primary_email, hris.employee_id',
                validation_name_required: 'Nimi vaaditaan',
                validation_category_required: 'Luokka vaaditaan',
                validation_data_subjects_required: 'Vähintään yksi rekisteröidyn tyyppi vaaditaan',
                label_data_type: 'Tietotyyppi',
                label_field_identifiers: 'Kenttätunnisteet (pilkuilla erotettu)',
                label_data_subject_types: 'Rekisteröityjen tyypit',
                label_volume_band: 'Volyymibändi',
                label_storage_locations: 'Tallennuspaikat (pilkuilla erotettu)',
                placeholder_storage_locations: 'ISO-3166 koodit tai vapaa teksti (esim. VN, Hetzner EU)',
                label_cross_border: 'Rajat ylittävä siirto',
                label_source_systems: 'Lähdejärjestelmät',
                label_processing_activities: 'Käsittelytoiminnot',
                label_collection_method: 'Keräysmenetelmä',
                label_purposes: 'Tarkoitukset',
                hint_multi_select: 'Valitse useita pitämällä Cmd/Ctrl-näppäintä painettuna.',
                option_inherit_category: '- (periä luokasta)',
                option_dash: '—',
                // fix-sprint-20260925-2123 (Finding 11) — a stale save refused with 409.
                error_conflict: 'This registry item was changed by someone else while you were editing, so your change was not saved. Close this form and open the item again to see the current version.',
                // The form was re-read and now shows the saved version.
                error_conflict_refreshed: 'This registry item was changed by someone else while you were editing, so your change was not saved. The form now shows the current version, and your unsaved entries were not kept. Make your change again and save.',
            },
            modal_import: {
                scan_none: 'No mapped regulatory-register templates are registered yet.',
                scan_count: 'Scans {count} template(s) for historical submissions and imports items.',
                dry_run_heading: 'Tuo esikatselu',
                label_to_create: 'luoda',
                label_to_update: 'Päivittämään',
                label_skipped_archived: 'Ohitettu (arkistoitu)',
                label_conflicts: 'Konfliktit',
                commit: 'Tuo',
                cancel: 'Peruuta',
                conflict_new_item: 'New item',
                conflict_value_own_words: 'a ground recorded in a person’s own words: “{statement}”',
                conflict_reason: 'Reason: {reason}',
                conflict_row_kept_edit: '{field} — the value you edited was kept: {current}. The form’s value was not applied: {mapper}.',
                conflict_row_kept_current: '{field} — the current value was kept: {current}. The form’s value was refused: {mapper}.',
                conflict_row_not_created: '{field} — the item was not created. The form’s value was refused: {mapper}.',
                conflict_reasons: {
                    field_user_edited: 'A person edited this field after an earlier import, and an import never overwrites a person’s edit.',
                    basis_not_declared: 'No installed framework declares a lawful basis, so a coded basis cannot be recorded.',
                    basis_required: 'This record needs a lawful basis, and the form gave none.',
                    basis_needs_person: 'Only an authenticated person may record or change this lawful basis, and the import did not run as one.',
                    basis_statement_rules: 'A lawful basis is an option your installed frameworks declare, or a ground in your own words with the article it rests on; the form’s value is neither.',
                    unknown: 'The import did not apply this value.',
                },
                conflict_fields: {
                    description: 'Description',
                    field_identifiers: 'Field identifiers',
                    data_type: 'Data type',
                    sensitivity_override: 'Sensitivity (override)',
                    data_subject_types: 'Data subject types',
                    volume_band: 'Volume band',
                    source_system_ids: 'Source systems',
                    processing_activity_ids: 'Processing activities',
                    retention_policy_id: 'Retention policy',
                    purposes: 'Purposes',
                    collection_method: 'Collection method',
                    legal_basis: 'Lawful basis',
                    data_tier: 'Data tier',
                    storage_locations: 'Storage locations',
                    cross_border: 'Cross-border transfer',
                },
            },
            drawer: {
                section_category: 'Luokka',
                section_systems: 'Lähdejärjestelmät',
                section_activities: 'Käsittelytoiminnot',
                section_flows: 'Tietovirrat',
                section_retention: 'Tehokas säilytys',
                section_governance: 'Hallinto',
                section_evidence: 'Todisteet',
                section_audit: 'Viimeaikainen kirjausketju',
                section_source_form: 'Lähdelomakkeen lähetys',
                badge_inherited: 'Peritty',
                badge_overridden: 'Ohitettu',
            },
            toast: {
                created: 'Rekisterikohde luotu.',
                updated: 'Rekisterikohde päivitetty.',
                archived: 'Rekisterikohde arkistoitu.',
                imported: '{count} tuotetta tuotu.',
                conflict_warning: '{count}-kentät säilyttivät muokkauksesi – katso ristiriitaluettelo.',
            },
            // Sprint 62.35.0 (AC6) — English placeholder; locale work beyond EN/VI defers to Phase 25 (R-57).
            evidence: {
                section_title: 'Provenance, notice and quality',
                section_intro: 'Evidence the organisation records about this data. These are records, not verdicts: an empty field means not recorded.',
                not_recorded: 'Not recorded',
                group_provenance: 'Provenance and authorisation',
                group_notice: 'Notice',
                group_quality: 'Authenticity and quality',
                label_source_description: 'Source of the data',
                hint_source_description: 'Collected directly, received from whom, or created by whom.',
                label_data_owner: 'Data owner',
                label_authorisation_basis: 'Authorisation relied on',
                label_authorisation_evidence_refs: 'Where the authorisation evidence is held (comma-separated)',
                label_notified_purpose: 'Purpose notified',
                label_notified_scope: 'Scope notified',
                label_notice_reference: 'Notice reference',
                label_authenticity_checked: 'Authenticity checked',
                label_authenticity_method: 'How authenticity was checked, and the result',
                label_quality_checked: 'Quality checked',
                label_quality_method: 'How quality was checked, and the result',
                label_matches_source: 'Collected data matches the source data',
                label_checked_on: 'Checked on',
                hint_checked_on: 'The day the check was made. It cannot be after today.',
                label_checked_by: 'Checked by',
                checked_by_retry_subject: 'the list of people who can be named as the checker',
                error_checked_on_in_future: "The check cannot be dated after today in the organisation's calendar ({date}).",
                error_checked_by_not_found: 'The person named as the checker is not a user of this organisation.',
                label_recorded_by: 'Check recorded by',
                label_recorded_at: 'Check recorded at',
                hint_recorded_stamp: 'Recorded automatically when any check field changes.',
                option_yes: 'Yes',
                option_no: 'No',
                option_not_determined: 'Not determined',
                vocabulary_none: 'No installed framework declares an authorisation vocabulary, so this can only be left not recorded.',
                vocabulary_not_in_force: 'Declared by a framework that is not yet in force (effective {date}).',
                vocabulary_load_failed: 'The authorisation options could not be loaded.',
                value_undeclared: '{value} (not declared by any installed framework)',
            },
        },
        systems: {
            title: 'Järjestelmärekisteri',
            new: 'Uusi järjestelmä',
            edit: 'Muokkaa järjestelmää',
            name: 'Nimi',
            owner: 'Omistaja',
            criticality: 'Kriittisyys',
            dpa_status: 'HTS-tila',
            location: 'Sijainti',
            system_type: 'Järjestelmätyyppi',
            metadata: 'Metatiedot (JSON)',
            department: 'Osasto',
            hosting_location: 'Ylläpidon Sijainti',
            hosting_provider: 'Ylläpitotarjoaja',
            data_residency_country: 'Tietojen Sijaintimaa',
            hosting: 'Ylläpito',
            no_systems: 'Järjestelmiä ei löytynyt',
            confirm_archive: 'Arkistoi järjestelmä "{name}"?',
            count: 'järjestelmää',
            count_one: 'järjestelmä',
            type_internal: 'Sisäinen',
            type_external: 'Ulkoinen',
            type_cloud_service: 'Pilvipalvelu',
            type_on_premise: 'Paikallinen',
            type_hybrid: 'Hybridi',
            status_active: 'Aktiivinen',
            status_decommissioning: 'Käytöstäpoistossa',
            status_decommissioned: 'Poistettu Käytöstä',
            status_planned: 'Suunniteltu',
            dpa_not_required: 'Ei Vaadittu',
            dpa_required_missing: 'Vaadittu Puuttuu',
            dpa_in_progress: 'Käynnissä',
            dpa_signed: 'Allekirjoitettu',
            all_criticalities: 'Kaikki Kriittisyystasot',
            status_inactive: 'Inactive',
        },
        categories: {
            title: 'Tietokategoriat',
            new: 'Uusi kategoria',
            edit: 'Muokkaa kategoriaa',
            name: 'Nimi',
            sensitivity: 'Arkaluonteisuus',
            tags: 'Tunnisteet',
            used_in: 'Käytössä',
            examples: 'Esimerkit',
            examples_hint: 'Esimerkit (pilkulla erotettu)',
            default: 'Oletus',
            all_sensitivities: 'Kaikki Arkaluonteisuustasot',
            all_statuses: 'Kaikki tilat',
            no_categories: 'Kategorioita ei löytynyt',
            confirm_delete: 'Poista kategoria "{name}"?',
            count: 'kategoriaa',
            count_one: 'kategoria',
            status_draft: 'Luonnos',
            status_proposed: 'Ehdotettu',
            status_under_review: 'Tarkistettavana',
            status_approved: 'Hyväksytty',
            status_deprecated: 'Vanhentunut',
            status_updated: 'Kategorian tila päivitetty',
            propose: 'Ehdota',
            approve: 'Hyväksy',
            deprecate: 'Vanhenna',
            items: 'Registry Items',
        },
        purposes: {
            title: 'Käsittelyn tarkoitukset',
            new: 'Uusi tarkoitus',
            edit: 'Muokkaa tarkoitusta',
            name: 'Nimi',
            legal_basis: 'Oikeusperuste',
            retention_period: 'Säilytysaika (päiviä)',
            justification: 'Oikeutetun edun perustelu',
            legal_basis_detail: 'Oikeusperusteen Yksityiskohdat',
            all_legal_bases: 'Kaikki Oikeusperusteet',
            no_purposes: 'Tarkoituksia ei löytynyt',
            confirm_delete: 'Poista "{name}"?',
            default: 'Oletus',
            count: '{count} tarkoitusta',
            count_one: '{count} tarkoitus',
        },
        retention: {
            title: 'Säilytyskäytännöt',
            new: 'Uusi käytäntö',
            edit: 'Muokkaa käytäntöä',
            name: 'Nimi',
            retention_days: 'Säilytyspäivät',
            disposal_method: 'Hävitystapa',
            data_categories: 'Tietokategoriat',
            activities: 'Toimet',
            review_frequency: 'Tarkistustiheys',
            review_frequency_days: 'Tarkistustiheys (päiviä)',
            no_policies: 'Säilytyskäytäntöjä ei löytynyt',
            confirm_delete: 'Poista "{name}"?',
            count: '{count} käytäntöä',
            count_one: '{count} käytäntö',
            days: 'päivää',
            disposal_deletion: 'Poisto',
            disposal_anonymization: 'Anonymisointi',
            disposal_archival: 'Arkistointi',
            review_overdue: 'Tarkistus myöhässä',
            review_status: 'Tarkistuksen tila',
            review_ok: 'Aikataulussa',
            department: 'Osasto',
            all_departments: 'Kaikki osastot',
            status_draft: 'Draft',
            status_active: 'Active',
            status_superseded: 'Superseded',
            status_retired: 'Retired',
        },
        flows: {
            view_tabs_aria: 'Flows-näkymä',
            view_list: 'Luettelo',
            view_map: 'Kartta',
            title: 'Tietovirrat',
            new: 'Uusi tietovirta',
            edit: 'Muokkaa tietovirtaa',
            name: 'Nimi',
            from_system: 'Lähdejärjestelmä',
            to_system: 'Kohdejärjestelmä',
            flow_type: 'Tietovirran tyyppi',
            cross_border: 'Rajat ylittävä',
            risk_level: 'Riskitaso',
            mechanism: 'Mekanismi',
            transfer_mechanism: 'Siirtomekanismi',
            frequency: 'Tiheys',
            source_country: 'Lähtömaa',
            destination_country: 'Kohdemaa',
            transfer_safeguard: 'Siirron Suojatoimi',
            transfer_safeguard_detail: 'Transfer Safeguard Detail',
            all_risk_levels: 'Kaikki Riskitasot',
            no_flows: 'Tietovirtoja ei löytynyt',
            confirm_archive: 'Arkistoi "{name}"?',
            count: '{count} tietovirtaa',
            count_one: '{count} tietovirta',
            status_active: 'Aktiivinen',
            status_inactive: 'Ei Aktiivinen',
            status_under_review: 'Tarkistettavana',
            mechanism_api: 'API',
            mechanism_file_transfer: 'Tiedostonsiirto',
            mechanism_database_replication: 'Tietokantareplikointi',
            mechanism_manual: 'Manuaalinen',
            mechanism_email: 'Sähköposti',
            mechanism_other: 'Muu',
            frequency_real_time: 'Reaaliaikainen',
            frequency_hourly: 'Tunneittain',
            frequency_daily: 'Päivittäin',
            frequency_weekly: 'Viikoittain',
            frequency_monthly: 'Kuukausittain',
            frequency_on_demand: 'Pyynnöstä',
            frequency_one_time: 'Kertaluonteinen',
            data_categories_label: 'Data Categories',
            purposes_label: 'Purposes',
            safeguard: 'Safeguard',
            section_scope_title: 'Processing scope',
            section_scope_description: 'Data categories and purposes carried by this flow.',
            jurisdiction: 'Lainkäyttöalue',
        },
        activities: {
            title: 'Käsittelytoimet',
            new: 'Uusi käsittelytoimi',
            edit: 'Muokkaa käsittelytoimea',
            activity_title: 'Otsikko',
            category: 'Kategoria',
            legal_basis: 'Oikeusperuste',
            dpa_required: 'DPIA Required',
            ropa_generated: 'Seloste luotu',
            version: 'Versio',
            generate_ropa: 'Luo seloste',
            controller: 'Rekisterinpitäjä',
            controller_name: 'Rekisterinpitäjän Nimi',
            controller_contact: 'Rekisterinpitäjän Yhteystieto',
            legal_basis_detail: 'Oikeusperusteen Yksityiskohdat',
            organization_info: 'Organization Information',
            org_address: 'Organisaation osoite',
            tax_code: 'Verokoodi',
            contact_person: 'Yhteyshenkilö',
            contact_email: 'Sähköpostiosoite',
            contact_phone: 'Puhelinnumero',
            select_entity: 'Valitse…',
            no_entities: 'Kohteita ei löytynyt',
            dpo_name: 'DPO:n Nimi',
            dpo_contact: 'DPO:n Yhteystieto',
            processor_name: 'Käsittelijän Nimi',
            processor_contact: 'Käsittelijän Yhteystieto',
            data_subjects: 'Rekisteröidyt',
            data_subjects_hint: 'Rekisteröidyt (pilkulla erotettu)',
            recipients: 'Vastaanottajat',
            recipients_hint: 'Vastaanottajat (pilkulla erotettu)',
            security_measures: 'Turvatoimet',
            dpia_reference: 'DPIA-viite',
            no_activities: 'Käsittelytoimia ei löytynyt',
            confirm_archive: 'Arkistoi "{name}"?',
            count: '{count} käsittelytoimea',
            count_one: '{count} käsittelytoimi',
            ropa_success: 'Seloste luotu. Siirry Vienteihin ladataksesi sen.',
            ropa_error: 'Selosteen luonti epäonnistui',
            required_label: 'Vaaditaan',
            status_draft: 'Luonnos',
            status_active: 'Aktiivinen',
            status_under_review: 'Tarkistettavana',
            status_archived: 'Arkistoitu',
            decree_165_heading: 'Asetuksen 165 tietotaso (Vietnamin tietolaki)',
            decree_165_helper: 'Record the data tier and whether the assessed transfer data is personal. Decree 356 Article 42(3) routes impact assessments for core or important personal data through personal-data protection law. Other applicable Data Law duties remain subject to review.',
            personal_data_scope: 'Personal-data scope of assessed transfer data',
            personal_data_scope_personal: 'Confirmed personal data',
            personal_data_scope_non_personal: 'Confirmed non-personal data',
            personal_data_scope_mixed: 'Mixed personal and non-personal data',
            personal_data_scope_unknown: 'Not yet determined',
            personal_data_scope_hint: 'Classify the assessed transfer data and record evidence before determining which impact-assessment route applies. No scope is inferred from the data tier.',
            personal_data_scope_evidence: 'Scope evidence and rationale',
            personal_data_scope_evidence_required: 'Record evidence before confirming personal or non-personal data scope.',
            cross_border_impact_assessment_reference: 'Cross-border impact-assessment reference',
            cross_border_impact_assessment_reference_label: 'Cross-border impact-assessment reference',
            cross_border_impact_assessment_reference_placeholder: 'Reference to the assessment for the applicable legal route',
            scope_review: 'Mixed or undetermined scope requires legal review before an impact-assessment route is selected. Record how each data component is assessed.',
            security_assessment_reference_help: 'Use only where the Decree 165 security-assessment route applies. Core or important personal-data impact assessments follow personal-data protection law under Decree 356 Article 42(3).',
            data_tier_label: 'Datataso',
            data_tier_help: 'Record regular, important or core data separately from personal-data scope. A tier alone does not determine the impact-assessment route.',
            data_tier_column_header: 'Datataso',
            data_tier_regular: 'Säännöllinen',
            data_tier_important: 'Tärkeää',
            data_tier_core: 'Core',
            transfer_mechanism_label: 'Rajat ylittävä siirtomekanismi',
            transfer_mechanism_placeholder: 'e.g. the transfer mechanism your installed frameworks provide, with its article',
            security_assessment_reference_label: 'Turvallisuusarvioinnin viite',
            security_assessment_reference_placeholder: 'esim. MPS-SA-2026-001',
            data_categories_label: 'Data Categories',
            purposes_label: 'Purposes',
            registry_items_label: 'Specific registry items (optional)',
            registry_items_hint: 'Most activities leave this empty — data categories already cover RoPA. Set it when an inspection asks which exact fields an activity touches.',
            registry_items_pending: 'Loading the registry items currently linked to this activity. Saving before they load will leave those links unchanged.',
            registry_items_unavailable: 'The registry items currently linked to this activity could not be loaded. Saving will leave those links unchanged.',
            registry_items_unlisted_option: 'Linked item {id} (not in the list)',
            retention_period_description: 'Retention Period Description',
        },
        evidence: {
            title: 'Todistusaineistoholvi',
            new: 'Uusi todistusaineisto',
            edit: 'Muokkaa todistusaineistoa',
            name: 'Nimi',
            evidence_type: 'Todistusaineiston tyyppi',
            linked_entities: 'Linkitetyt kohteet',
            uploaded: 'Ladattu',
            expiry: 'Vanheneminen',
            file_url: 'Tiedoston URL',
            source_mode_label: 'Todistelähde',
            source_mode_file: 'Lähetä tiedosto',
            source_mode_url: 'Linkitä ulkoinen URL',
            attachment_label: 'Liite',
            link_to: 'Linkitä todistusaineistoon',
            entity_type: 'Kohteen tyyppi',
            entity_id: 'Kohteen tunnus',
            no_evidence: 'Todistusaineistoa ei löytynyt',
            manage_links: 'Hallinnoi linkkejä',
            unlink: 'Poista linkitys',
            confirm_delete: 'Poista todistusaineisto "{name}"?',
            count: '{count} kohdetta',
            count_one: '{count} kohde',
            type_document: 'Asiakirja',
            type_screenshot: 'Kuvakaappaus',
            type_policy: 'Käytäntö',
            type_contract: 'Sopimus',
            type_assessment: 'Arviointi',
            type_correspondence: 'Kirjeenvaihto',
            type_other: 'Muu',
            linked_type_system: 'Järjestelmä',
            linked_type_data_flow: 'Tietovirta',
            linked_type_processing_activity: 'Käsittelytoiminto',
            linked_type_data_category: 'Tietoluokka',
            linked_type_purpose: 'Tarkoitus',
            linked_type_retention_policy: 'Säilytyskäytäntö',
            link_entity: 'Linkki entiteettiin',
            link_success: 'Entiteetti linkitetty onnistuneesti',
            confirm_unlink: 'Haluatko varmasti poistaa tämän kokonaisuuden linkityksen?',
            unlink_success: 'Kokonaisuuden linkityksen poistaminen onnistui',
            no_linked_entities: 'Ei linkitettyjä kokonaisuuksia',
            select_entity: 'Valitse…',
            no_entities: 'Ei kohteita',
        },
        exports: {
            title: 'Tietojen viennit',
            recent: 'Viimeisimmät viennit',
            export_type: 'Viennin tyyppi',
            format: 'Muoto',
            requested_by: 'Pyytäjä',
            requested_at: 'Pyydetty',
            download: 'Lataa',
            csv_export: 'CSV-vienti',
            csv_description: 'Lataa tiedot CSV-muodossa taulukkolaskenta-analyysia varten.',
            pdf_reports: 'PDF-raportit',
            pdf_description: 'Luo muotoiltuja raportteja vaatimustenmukaisuusdokumentaatiota varten.',
            csv_systems: 'Järjestelmärekisteri',
            csv_categories: 'Tietokategoriat',
            csv_flows: 'Tietovirrat',
            csv_activities: 'Käsittelytoimet',
            csv_cross_border: 'Rajat Ylittävät Tietovirrat',
            csv_retention_policies: 'Säilytyskäytännöt',
            csv_evidence: 'Todisteet',
            csv_governance_assignments: 'Hallintotehtävät',
            pdf_ropa: 'Seloste',
            pdf_systems: 'Järjestelmäraportti',
            pdf_cross_border: 'Rajat Ylittävien Raportti',
            pdf_flow_map: 'Tietovirtakartan Data',
            export_failed: 'Vienti epäonnistui',
            pdf_generated: 'PDF luotu onnistuneesti.',
            ropa: 'RoPA-rekisteröinti',
            retention_policies: 'Säilytyskäytännöt',
            governance_assignments: 'Hallintotehtävät',
            col_entity: 'Vie kokonaisuus',
            col_description: 'Kuvaus',
            desc_ropa: 'Täydellinen ROPA-rekisteri kaikista käsittelytoimista, joilla on oikeusperusta, DPIA tila ja valvonta.',
            desc_systems: 'Järjestelmäluettelo, jossa on omistaja, tietoluokkatunnisteet ja vaatimustenmukaisuustila.',
            desc_activities: 'Yksittäiset käsittelytoimet rekisterinpitäjän, oikeusperustan ja DPIA luokituksen kanssa.',
            desc_categories: 'Luokittelussa ja tarkastuksissa käytetyt tietoluokat ja herkkyystasot.',
            desc_purposes: 'Käsittelytarkoitukset ja kullekin tarkoitukselle kirjattu oikeusperusta.',
            desc_flows: 'Tietovirrat järjestelmien välillä, mukaan lukien sisäiset ja rajat ylittävät reitit.',
            desc_cross_border: 'Vain rajat ylittävät virrat siirtomekanismin ja riskinarvioinnin kanssa.',
            desc_retention_policies: 'Säilytyskäytännöt, hävitysmenetelmät ja tarkistustahti.',
            desc_evidence: 'Käsittelytoimiin liitetyt todisteet: todistukset, tietosuojasopimukset, DPIA-raportit.',
            desc_governance_assignments: 'Hallintotoimeksiannot: omistajat, hyväksyjät ja ISO 27001 -viitteet.',
            unified_heading: 'Vie kokonaisuudet',
            unified_description: 'Vie mikä tahansa datakartoituskokonaisuus muodossa CSV, XLSX, PDF tai DOCX. Kaikki viennit tallennetaan tiedostoselaimeen.',
            export_button_label: 'Vie',
            csv_download_success: 'CSV ladattu onnistuneesti.',
            col_action: 'Export',
        },
        attestation: {
            title: 'Todennusjaksot',
            create: 'Uusi jakso',
            name: 'Jakson nimi',
            year: 'Vuosi',
            deadline: 'Määräaika',
            scope: 'Laajuus',
            status: 'Tila',
            tasks: 'Osastotehtävät',
            assign_tasks: 'Määritä tehtävät',
            department: 'Osasto',
            assignee: 'Vastuuhenkilö',
            progress: 'Edistyminen',
            stale_entries: 'Vanhentuneet merkinnät',
            detect_stale: 'Tunnista vanhentuneet',
            export_bundle: 'Vie paketti',
            no_cycles: 'Ei todennusjaksoja vielä.',
            create_success: 'Todennusjakso luotu.',
            assign_success: 'Tehtävät määritetty.',
            complete_success: 'Tehtävä valmis.',
            stale_detected: 'Vanhentuneita merkintöjä havaittu.',
            stale_systems: 'Vanhentuneet järjestelmät',
            stale_flows: 'Vanhentuneet tietovirrat',
            no_stale: 'Vanhentuneita merkintöjä ei havaittu.',
            stale_name: 'Nimi',
            stale_announcement: 'Löydettiin {0} vanhentunutta järjestelmää ja {1} vanhentunutta tietovirtaa',
            last_updated: 'Viimeksi päivitetty',
            status_draft: 'Luonnos',
            status_active: 'Aktiivinen',
            status_completed: 'Valmis',
            task_status_pending: 'Odottaa',
            task_status_in_progress: 'Käynnissä',
            task_status_completed: 'Valmis',
            scope_systems: 'Järjestelmät',
            scope_flows: 'Tietovirrat',
            scope_activities: 'Aktiviteetit',
            scope_categories: 'Kategoriat',
            scope_purposes: 'Tarkoitukset',
            scope_retention: 'Säilytys',
            complete_cycle: 'Complete Cycle',
            confirm_activate: 'Are you sure you want to activate this cycle?',
            confirm_complete: 'Are you sure you want to complete this cycle? This action cannot be undone.',
            cycle_complete_success: 'Cycle completed successfully',
            task_status_verified: 'Verified',
            activate_success: 'Cycle activated successfully',
            activate: 'Activate Cycle',
        },
        governance: {
            title: 'Hallintomääritykset',
            assign: 'Määritä omistajuus',
            entity_type: 'Kohdetyyppi',
            entity: 'Kohde',
            role: 'Rooli',
            assigned_by: 'Määrittäjä',
            assigned_at: 'Määritetty',
            remove: 'Poista',
            no_assignments: 'Ei hallintomäärityksiä.',
            assign_success: 'Omistajuus määritetty.',
            remove_success: 'Määritys poistettu.',
            assignee: 'Vastuuhenkilö',
            department: 'Osasto',
            all_departments: 'Kaikki osastot',
            roles: {
                data_owner: 'Tiedon omistaja',
                data_steward: 'Tiedon hallinnoija',
                data_custodian: 'Tiedon säilyttäjä',
                informed: 'Tiedotettu',
            },
            entity_types: {
                system: 'Järjestelmä',
                flow: 'Tietovirta',
                activity: 'Aktiviteetti',
                category: 'Kategoria',
                purpose: 'Tarkoitus',
                retention_policy: 'Säilytyskäytäntö',
            },
        },
        risk_assessment: {
            title: 'Riskinarviointi',
            create: 'Uusi arviointi',
            safeguard_type: 'Suojatyyppi',
            adequacy_status: 'Riittävyys',
            risk_factors: 'Riskitekijät',
            mitigations: 'Toimenpiteet',
            reviewer: 'Arvioija',
            decision: 'Päätös',
            approve: 'Hyväksy',
            reject: 'Hylkää',
            no_assessments: 'Ei riskinarviointeja.',
            create_success: 'Riskinarviointi luotu.',
            approve_success: 'Arviointi hyväksytty.',
            reject_success: 'Arviointi hylätty.',
            one_per_line: 'Yksi per rivi',
            adequacy_adequate: 'Riittävä',
            adequacy_inadequate: 'Riittämätön',
            adequacy_under_review: 'Arvioitavana',
            adequacy_not_assessed: 'Ei arvioitu',
            decision_approved: 'Hyväksytty',
            decision_rejected: 'Hylätty',
        },
        review: {
            title: 'Tarkistustehtävät',
            description: 'Tarkistustehtävät luodaan automaattisesti, kun datakategoria ehdotetaan. Luo uusi tehtävä siirtymällä Datakategorioihin ja napsauttamalla Ehdota luonnoskategoriassa.',
            count: 'tarkistustehtävää',
            summary: 'Tarkistuksen yhteenveto',
            pending: 'Odottaa',
            resolved_today: 'Ratkaistu tänään',
            escalated: 'Eskaloitu',
            overdue: 'Myöhässä (>5 päivää)',
            all_statuses: 'Kaikki tilat',
            status_pending: 'Odottaa',
            status_resolved: 'Ratkaistu',
            status_escalated: 'Eskaloitu',
            category_name: 'Kategoria',
            system_name: 'Järjestelmä',
            owner: 'Omistaja',
            resolve: 'Ratkaise',
            resolve_task: 'Ratkaise tarkistustehtävä',
            resolution: 'Ratkaisu',
            resolution_approve: 'Hyväksy',
            resolution_modify: 'Muokkaa ja hyväksy',
            resolution_reject: 'Hylkää',
            resolution_escalate: 'Eskaloi',
            notes: 'Ratkaisun huomautukset',
            notes_required: 'Huomautukset vaaditaan (vähintään 10 merkkiä) Muokkaa, Hylkää tai Eskaloi -toiminnoille',
            resolved_success: 'Tarkistustehtävä ratkaistu',
            bulk_approve: 'Joukkohyväksyntä',
            bulk_reject: 'Joukkohylkäys',
            bulk_success: '{count} tehtävää käsitelty',
            no_tasks: 'Tarkistustehtäviä ei löytynyt',
        },
        inventory: {
            title: 'Luokittelun inventaario',
            count: 'järjestelmää',
            summary: 'Inventaarion yhteenveto',
            total_systems: 'Järjestelmiä yhteensä',
            total_labels: 'Tunnisteita yhteensä',
            system_name: 'Järjestelmä',
            label_count: 'Tunnisteet',
            approved_count: 'Hyväksytty',
            pending_count: 'Odottaa',
            no_items: 'Inventaarion kohteita ei löytynyt',
        },
        exceptions: {
            title: 'Luokittelun poikkeukset',
            count: 'poikkeusta',
            all_severities: 'Kaikki vakavuustasot',
            all_statuses: 'Kaikki tilat',
            system_name: 'Järjestelmä',
            severity_label: 'Vakavuus',
            status_open: 'Avoin',
            status_resolved: 'Ratkaistu',
            status_accepted_risk: 'Hyväksytty riski',
            resolve: 'Ratkaise',
            resolve_exception: 'Ratkaise poikkeus',
            resolution_type: 'Ratkaisutyyppi',
            notes: 'Huomautukset',
            justification_required: 'Perustelu vaaditaan (vähintään 10 merkkiä) hyväksytylle riskille',
            resolved_success: 'Poikkeus ratkaistu',
            no_exceptions: 'Poikkeuksia ei löytynyt',
        },
        history: {
            title: 'Luokittelun historia',
            count: 'tapahtumaa',
            actor: 'Toimija',
            action: 'Toiminto',
            entity_type: 'Entiteetin tyyppi',
            changes: 'Muutokset',
            view_lineage: 'Näytä alkuperä',
            lineage_title: 'Luokittelun alkuperä',
            no_events: 'Historiatietoja ei löytynyt',
            no_lineage: 'Alkuperätietoja ei saatavilla',
            modified: 'Muokattu',
            created: 'Luotu',
            removed: 'Poistettu',
            action_created: 'Luotu',
            action_updated: 'Päivitetty',
            action_archived: 'Arkistoitu',
            action_approved: 'Hyväksytty',
            action_deprecated: 'Vanhentunut',
            entity_label: 'Tunniste',
            entity_taxonomy: 'Taksonomia',
            entity_review_task: 'Tarkistustehtävä',
            entity_exception: 'Poikkeus',
            before: 'Ennen',
            after: 'Jälkeen',
        },
    },
    governance: {
        // Sprint 62.57.0 Slice E (§12.13.9) — the controller-side vendor certificate record.
        vendor_service_certificate: enVendorServiceCertificate,
        audit_trail_title: 'Hallinnan auditointiloki',
        audit_trail_subtitle: 'Poikkileikkaava auditointivirta hallinnan toimenpiteille: hyväksynnät, playbook-suoritukset, todistelinkit ja lisää.',
        audit_trail_item_count: 'auditointitapahtumaa',
        audit_trail_fetch_error: 'Auditointitapahtumien haku epäonnistui.',
        audit_trail_no_events: 'Nykyisillä suodattimilla ei löytynyt auditointitapahtumia.',
        audit_trail_entity_type: 'Entiteettityyppi',
        audit_trail_entity_id: 'Entiteetin tunnus',
        audit_trail_action: 'Toimenpide',
        audit_trail_actor: 'Toimija',
        audit_trail_timestamp: 'Aikaleima',
        audit_trail_date_from: 'Alkaen',
        audit_trail_date_to: 'Päättyen',
        audit_trail_no_rows_to_export: 'Ei vietäviä rivejä.',
        // Gate E12.5 remediation A (Sprint 63.8.2) — the audit trail filter options.
        audit_trail_entity_types: {
            vendor: 'Vendor',
            vendor_subprocessor: 'Sub-processor',
            transfer_mechanism: 'Transfer mechanism',
            drift_event: 'Drift event',
            playbook: 'Playbook',
            execution: 'Execution',
            approval: 'Approval',
        },
        audit_trail_actions: {
            created: 'Created',
            updated: 'Updated',
            status_changed: 'Status changed',
            assigned: 'Assigned',
            approved: 'Approved',
            rejected: 'Rejected',
            evidence_linked: 'Evidence linked',
            step_completed: 'Step completed',
            executed: 'Executed',
            terminated: 'Terminated',
            exported: 'Exported',
        },
        nav: {
            vendors: 'Toimittajat',
            subprocessors: 'Alikäsittelijät',
            cross_border: 'Rajat ylittävä',
            drift: 'Poikkeamavalvonta',
            scores: 'Vaatimustenmukaisuuspisteet',
            playbooks: 'Toimintaohjeet',
            approvals: 'Hyväksynnät',
            audit_trail: 'Auditointiloki',
            board_report: 'Hallitusraportti',
            evidence: 'Todistusaineisto',
            onboarding: 'Käyttöönottoprosessi',
            dpa_reviews: 'DPA-katselmukset',
            periodic_reviews: 'Määräaikaiskatselmukset',
            offboarding: 'Käytöstäpoisto',
        },
        vendors: {
            data_categories_processed: 'Data categories processed',
            title: 'Toimittajarekisteri',
            new: 'Uusi toimittaja',
            edit: 'Muokkaa toimittajaa',
            name: 'Nimi',
            jurisdiction: 'Lainkäyttöalue',
            dpa_status: 'HTS-tila',
            risk_level: 'Riskitaso',
            onboarding_status: 'Käyttöönoton tila',
            contact_email: 'Yhteyshenkilön sähköposti',
            contact_name: 'Yhteyshenkilön nimi',
            contact_phone: 'Yhteyshenkilön puhelin',
            website: 'Verkkosivusto',
            services_csv: 'Palvelut (pilkulla erotettu)',
            country: 'Maa',
            country_placeholder: 'esim. FI',
            last_reviewed: 'Viimeksi Tarkistettu',
            offboard: 'Lopeta',
            offboard_vendor: 'Lopeta Toimittaja',
            offboard_reason: 'Lopetuksen syy',
            no_vendors: 'Toimittajia ei löytynyt',
            vendor_count: '{count} toimittaja(a)',
            all_dpa_status: 'Kaikki DPA-tilat',
            all_risk_levels: 'Kaikki Riskitasot',
            all_onboarding: 'Kaikki Käyttöönoton Tilat',
            fetch_error: 'Epäonnistui. Yritä uudelleen.',
            save_error: 'Toimittajan tallennus epäonnistui',
            offboard_error: 'Lopetus epäonnistui',
            reach: {
                action: 'Foreign reach',
                title: 'Foreign reach: {name}',
                intro: "An installed framework asks whether this vendor is within its foreign reach. A person records the answer and the reason; the vendor's country and processing locations do not decide it.",
                none_declared: 'No installed framework asks a foreign-reach question.',
                not_determined: 'Not determined',
                within: 'Within reach',
                not_within: 'Not within reach',
                decided: 'Recorded {date}',
                related: 'Provisions that act on a party within it: {list}',
                question: 'Question',
                answer: 'Is this vendor within the reach?',
                rationale: 'Reason',
                citation: 'Citation (optional)',
                save: 'Record determination',
                saved: 'Foreign-reach determination recorded.',
                load_error: 'The foreign-reach questions could not be read.',
                retry: 'Reload',
                retry_sr: 'the foreign-reach questions',
                loading: 'Reading the foreign-reach questions…',
                retry_busy: 'Reloading…',
                stale: 'This vendor was changed by someone else. The latest version has been loaded; review it and record the determination again.',
                stale_unreadable: 'This vendor was changed by someone else, and the latest version could not be read. Close this dialog and open it again before recording.',
                save_error: 'The determination could not be recorded. Try again.',
                answer_required: 'Choose an answer and give a reason.',
                country_context: 'Country on record: {country}',
                read_only: 'Recording a determination needs permission to edit vendors.',
            },
        },
        vendor_types: {
            processor: 'Tietojenkäsittelijä',
            sub_processor: 'Alikäsittelijä',
            controller: 'Rekisterinpitäjä',
            joint_controller: 'Yhteisrekisterinpitäjä',
        },
        onboarding_statuses: {
            pending_review: 'Odottaa Tarkistusta',
            approved: 'Hyväksytty',
            active: 'Aktiivinen',
            suspended: 'Keskeytetty',
            offboarded: 'Lopetettu',
        },
        dpa_statuses: {
            not_started: 'Not Started',
            in_progress: 'In Progress',
            not_required: 'Ei Vaadittu',
            required_missing: 'Vaadittu Puuttuu',
            draft: 'Luonnos',
            under_review: 'Tarkistettavana',
            signed: 'Allekirjoitettu',
            expired: 'Vanhentunut',
        },
        risk_levels: {
            critical: 'Kriittinen',
            high: 'Korkea',
            medium: 'Keskitaso',
            low: 'Matala',
        },
        approval_statuses: {
            pending: 'Odottaa',
            approved: 'Hyväksytty',
            rejected: 'Hylätty',
            expired: 'Vanhentunut',
        },
        approval_action_types: {
            vendor_onboarding: 'Toimittajan Käyttöönotto',
            vendor_offboarding: 'Toimittajan Lopetus',
            cross_border_acceptance: 'Rajat Ylittävä Hyväksyntä',
            playbook_activation: 'Playbook-aktivointi',
            drift_accept_risk: 'Poikkeama Hyväksytty Riski',
            activity_activation: 'Toiminnan Aktivointi',
        },
        drift_check_types: {
            dpa_expiry: 'DPA-vanheneminen',
            dpa_missing: 'DPA Puuttuu',
            review_overdue: 'Tarkistus Myöhässä',
            cross_border_gap: 'Rajat Ylittävä Puute',
            sub_processor_chain: 'Alikäsittelijäketju',
            risk_assessment_overdue: 'Riskinarviointi Myöhässä',
            retention_policy_missing: 'Säilytyskäytäntö Puuttuu',
            evidence_expiring: 'Todistusaineisto Vanhenemassa',
        },
        drift_statuses: {
            open: 'Avoin',
            acknowledged: 'Vahvistettu',
            in_progress: 'Käynnissä',
            resolved: 'Ratkaistu',
            accepted_risk: 'Hyväksytty Riski',
        },
        drift_entity_types: {
            vendor: 'Toimittaja',
            system: 'Järjestelmä',
            data_flow: 'Tietovirta',
            processing_activity: 'Käsittelytoiminta',
            evidence_item: 'Todistusaineisto',
        },
        playbook_statuses: {
            draft: 'Luonnos',
            active: 'Aktiivinen',
            archived: 'Arkistoitu',
        },
        playbook_action_types: {
            delete_data: 'Poista Tiedot',
            verify_deletion: 'Vahvista Poisto',
            notify_vendor: 'Ilmoita Toimittajalle',
            confirm_receipt: 'Vahvista Vastaanotto',
            document_evidence: 'Dokumentoi',
            other: 'Muu',
        },
        playbook_target_types: {
            system: 'Järjestelmä',
            vendor: 'Toimittaja',
        },
        dpa_chain_statuses: {
            complete: 'Valmis',
            incomplete: 'Puutteellinen',
            unknown: 'Tuntematon',
        },
        sub_processor_statuses: {
            active: 'Aktiivinen',
            terminated: 'Lopetettu',
        },
        evidence_types: {
            dpa_agreement: 'DPA-sopimus',
            risk_assessment: 'Riskinarviointi',
            audit_report: 'Tarkastusraportti',
            certification: 'Sertifiointi',
            deletion_confirmation: 'Poistonvahvistus',
            training_record: 'Koulutuskirjaus',
            incident_report: 'Tapahtumaraportti',
            document: 'Asiakirja',
            screenshot: 'Kuvakaappaus',
            policy: 'Käytäntö',
            contract: 'Sopimus',
            assessment: 'Arviointi',
            correspondence: 'Kirjeenvaihto',
            other: 'Muu',
        },
        linkable_entity_types: {
            vendor: 'Toimittaja',
            transfer_mechanism: 'Siirtomekanismi',
            playbook: 'Toimintasuunnitelma',
            execution: 'Suoritus',
            system: 'Järjestelmä',
            activity: 'Toiminta',
        },
        subprocessors: {
            title: 'Alikäsittelijäsuhteet',
            new: 'Uusi linkki',
            vendor: 'Toimittaja',
            subprocessor: 'Alikäsittelijä',
            relationship_type: 'Suhteen tyyppi',
            countries: 'Maat',
            countries_csv: 'Maat (pilkulla erotettu)',
            dpa_chain: 'DPA-ketju',
            dpa_chain_status: 'DPA-ketjun tila',
            no_processors: 'Käsittelijöitä ei löytynyt.',
            no_subprocessors: 'Alikäsittelijöitä ei löytynyt.',
            processor_vendor: 'Käsittelijätoimittaja',
            select_processor: 'Valitse Käsittelijä',
            select_vendor: 'Valitse Toimittaja',
            services: 'Palvelut',
            sub_processor_vendor_id: 'Alikäsittelijän Toimittajatunnus',
            terminate: 'Lopeta',
            confirm_terminate: 'Haluatko varmasti lopettaa tämän suhteen?',
            create_error: 'Linkin luonti epäonnistui.',
            fetch_error: 'Alikäsittelijöiden haku epäonnistui.',
            processor: 'Käsittelijä',
            services_csv: 'Palvelut (pilkulla erotettu)',
            terminate_error: 'Lopetus epäonnistui.',
            version_history_heading: 'Aliprosessorin muutoksen versio',
            change_tracking_note: 'Kaikki aliprosessorisuhteen muutokset on versioitu. Jokainen muutos tallentaa kirjausketjun ennen/jälkeen-tilan.',
            history_retention_note: 'Muutoshistoria säilytetään koko tietojen säilytysajan. Tarkista muutoslokit toimittajan tietonäkymän kautta.',
            vendor_risk_cross_view_heading: 'Alikäsittelijärekisteri (Vendor Risk)',
            vendor_risk_cross_view_source: 'Lähde: Vendor Risk -rekisteri',
            vendor_risk_cross_view_empty: 'Ei merkintöjä Vendor Risk -rekisterissä.',
            vendor_risk_cross_view_load_error: 'The Vendor Risk sub-processor register could not be read.',
            partial_load: 'The sub-processors of {count} processor(s) could not be read, so the register below is incomplete.',
        },
        cross_border: {
            title: 'Rajat ylittävän hallinnan raportti',
            flows: 'Rajat ylittävät tietovirrat',
            transfers: 'Rajat ylittävät siirrot',
            detected_gaps: 'Havaitut puutteet',
            safeguards: 'Suojatoimet',
            cross_border_vendors: 'Rajat Ylittävät Toimittajat',
            destination: 'Kohde',
            no_cross_border_vendors: 'Rajat ylittäviä toimittajia ei löytynyt.',
            no_safeguard: 'Ei suojatoimea',
            gap_needs_redetermination: 'Recorded mechanism needs re-determining',
            gap_unverified: 'Recorded mechanism not yet verified',
            gap_recorded_mechanism: 'Recorded: {mechanism}',
            redetermine_mechanism: 'Re-determine',
            redetermine_mechanism_title: "Re-determine the vendor's transfer mechanism",
            redetermine_mechanism_intro: '{vendor} records the transfer mechanism "{mechanism}" for {source} to {destination}, and no installed framework declares it as it stands. Choose a mechanism an installed framework declares. Only the mechanism is saved.',
            redetermine_choose_first: 'Choose a transfer mechanism first.',
            redetermine_success: 'Transfer mechanism re-determined.',
            redetermine_error: 'Could not re-determine the transfer mechanism. Please try again.',
            processing_locations: 'Käsittelypaikat',
            export_error: 'Raportin vienti epäonnistui.',
            export_pdf: 'Vie PDF',
            export_success: 'Raportti vietiin onnistuneesti.',
            exporting: 'Viedään...',
            fetch_error: 'Rajat ylittävien tietojen haku epäonnistui.',
            source: 'Lähde',
            pdpl_p2_heading: 'PDPL-P2 rajat ylittävien siirtojen noudattaminen',
            dossier_prep_note: 'Asiakirja-aineiston valmistelu: kokoa vaikutustenarviointi, oikeusperusta ja suoja-asiakirjat ennen arkistointia.',
            filing_tracking_note: 'Arkistoinnin seuranta: seuraa lähetyksen tilaa viranomaiselle (PDPC) alkuperäisestä hakemuksesta hyväksymiseen.',
            supplement_loop_note: 'Täydennyssilmukka: vastaa viranomaisen tietopyyntöihin ja lähetä muutetut asiakirjat tarvittaessa uudelleen.',
            official_forms_note: 'Official forms: choose the official form this filing uses in the Form type selector when you create it.',
            vendor_search_label: 'Toimittajan haku',
            vendor_search_placeholder: 'Hae toimittajan nimellä…',
            jurisdiction_filter_label: 'Lainkäyttöalue',
            filings_section_heading: 'Rajat ylittävät hakemukset',
            filings_empty: 'Ei ilmoituksia vielä. Luo sellainen aloittaaksesi asiakirjan.',
            filings_create_button: 'Uusi arkistointi',
            filings_table_id: 'Arkistointitunnus',
            filings_table_jurisdiction: 'Toimivalta',
            filings_table_type: 'Lomakkeen tyyppi',
            filings_table_status: 'Tila',
            filings_table_updated: 'Päivitetty',
            filings_table_open: 'Avaa',
            filings_detail_title: 'Rajat ylittävä arkistointi',
            filings_back_to_list: 'Takaisin rajan yli',
            filings_status_heading: 'Elinkaari',
            filings_evidence_heading: 'Todisteet',
            filings_history_heading: 'Kirjausketju',
            filings_field_filing_id: 'Arkistointitunnus',
            filings_field_jurisdiction: 'Toimivalta',
            filings_field_type: 'Lomakkeen tyyppi',
            filings_field_status: 'Tila',
            filings_field_reference: 'Viranomaisen viite',
            filings_field_mps_receipt: 'MPS kuitti',
            filings_field_submitted_at: 'Lähetetty klo',
            filings_field_acknowledged_at: 'Hyväksytty klo',
            filings_field_archived_at: 'Arkistoitu klo',
            filings_field_version: 'Versio',
            filings_field_notes: 'Huomautuksia',
            filings_action_advance: 'Ennakkotila',
            filings_action_reject: 'Hylkää',
            filings_action_archive: 'Arkisto',
            filings_action_submit: 'Merkki lähetetty',
            filings_action_acknowledge: 'Mark myönsi',
            filings_action_resume_draft: 'Jatka luonnoksena',
            filings_status_draft: 'Luonnos',
            filings_status_assessed: 'Arvioitu',
            filings_status_pending_review: 'Odottaa tarkistusta',
            filings_status_ready_to_file: 'Valmiina tiedostoon',
            filings_status_submitted: 'Lähetetty',
            filings_status_acknowledged: 'Tunnustettu',
            filings_status_rejected: 'Hylätty',
            filings_status_archived: 'Arkistoitu',
            filings_type_none_installed: 'No fileable form is installed for this organisation',
            filings_new_dialog_title: 'Uusi rajat ylittävä hakemus',
            filings_new_form_jurisdiction: 'Lainkäyttöalue',
            filings_new_form_type: 'Lomakkeen tyyppi',
            filings_new_form_notes: 'Huomautuksia (valinnainen)',
            filings_new_submit: 'Luo',
            filings_new_cancel: 'Peruuta',
            filings_reason_label: 'Syy',
            filings_reference_label: 'Viranomaisen viitenumero',
            filings_mps_receipt_label: 'MPS kuitin viite',
            filings_create_error: 'Tiedoston luominen epäonnistui.',
            filings_create_success: 'Tiedosto luotu.',
            filings_update_error: 'Ilmoituksen päivittäminen epäonnistui.',
            filings_update_success: 'Arkistointi päivitetty.',
            filings_fetch_error: 'Ilmoitusten nouto epäonnistui.',
            filings_archive_confirm: 'Arkistoidaanko tämä tiedosto? Tätä ei voi kumota.',
            filings_status_rejected_detour: 'Hakemus hylättiin tarkastelun jälkeen. Jatka luonnoksena aloittaaksesi kulku uudelleen.',
            recent_exports_heading: 'Viimeaikainen vienti',
            recent_exports_empty: 'Ei vientiä vielä. Luo sellainen napsauttamalla "Vie PDF".',
            filings_forms_empty: 'No regulatory forms generated yet. Use the Forms module to generate the required outputs for this filing.',
            filings_forms_heading: 'Regulatory Forms',
            filings_type_placeholder: 'Select a form type…',
        },
        drift: {
            title: 'Konfiguraation poikkeamavalvonta',
            drift_type: 'Poikkeaman tyyppi',
            severity: 'Vakavuus',
            detected_at: 'Havaittu',
            resolve: 'Ratkaise',
            all_check_types: 'Kaikki Tarkistustyypit',
            all_entity_types: 'Kaikki Kohdetyypit',
            event_count: 'Tapahtumien Lukumäärä',
            accept_risk: 'Hyväksy Riski',
            acknowledge: 'Vahvista',
            assign_error: 'Määritys epäonnistui.',
            assign_event: 'Määritä',
            assigned: 'Määritetty',
            enter_resolution_notes: 'Syötä ratkaisumuistiinpanot...',
            enter_user_id: 'Syötä käyttäjätunnus...',
            entity: 'Kohde',
            fetch_error: 'Poikkeamatapahtumien haku epäonnistui.',
            no_events: 'Poikkeamatapahtumia ei löytynyt.',
            resolution_notes: 'Ratkaisumuistiinpanot',
            update_status_error: 'Tilan päivitys epäonnistui.',
        },
        scores: {
            title: 'Vaatimustenmukaisuuspisteiden yhteenveto',
            overall: 'Kokonaisvaatimustenmukaisuuspisteet',
            breakdown: 'Pisteet moduuleittain',
            trend: 'Historiallinen trendi',
            module: 'Moduuli',
            score: 'Pisteet',
            grade: 'Arvosana',
            calculated_at: 'Laskettu',
            generate_board_report: 'Luo Hallitusraportti',
            lang_english: 'Englanti',
            lang_vietnamese: 'Vietnam',
            fetch_error: 'Vaatimustenmukaisuuspisteiden haku epäonnistui.',
            generate_report: 'Luo Raportti',
            generating: 'Luodaan...',
            recommendations: 'Suositukset',
            report_error: 'Raportin luonti epäonnistui.',
            factor_owner_assigned: 'Omistaja määritetty',
            factor_data_categories_linked: 'Tietoluokat linkitetty',
            factor_purposes_linked: 'Käyttötarkoitukset linkitetty',
            factor_data_flows_documented: 'Tietovirrat dokumentoitu',
            factor_processing_activity_linked: 'Käsittelytoiminta linkitetty',
            factor_dpa_status: 'DPA-tila',
            factor_retention_policy_linked: 'Säilytyskäytäntö linkitetty',
            factor_last_reviewed: 'Viimeksi tarkistettu kynnysarvon sisällä',
            factor_risk_assessment_current: 'Riskinarviointi ajan tasalla',
            factor_sub_processors_documented: 'Alikäsittelijät dokumentoitu',
            factor_cross_border_safeguards: 'Rajat ylittävät suojatoimet',
            factor_contact_info_complete: 'Yhteystiedot täydelliset',
            factor_onboarding_approved: 'Käyttöönotto hyväksytty',
            factor_avg_system_score: 'Keskimääräinen järjestelmäpistemäärä',
            factor_avg_vendor_score: 'Keskimääräinen toimittajapistemäärä',
            factor_processing_activity_coverage: 'Käsittelytoimintojen kattavuus',
            factor_ropa_completeness: 'RoPA-kattavuus',
            factor_open_drift_events: 'Avoimet poikkeamatapahtumat',
        },
        playbooks: {
            title: 'Poiston toimintaohjeet',
            new: 'Uusi toimintaohje',
            edit: 'Muokkaa toimintaohjetta',
            steps: 'Vaiheet',
            add_step: 'Lisää vaihe',
            remove_step: 'Poista vaihe',
            data_categories_csv: 'Tietokategoriat (pilkulla erotettu)',
            estimated_duration: 'Arvioitu Kesto',
            estimated_duration_label: 'Arvioitu Kesto',
            evidence_description: 'Todistusaineiston Kuvaus',
            no_playbooks: 'Toimintaohjeita ei löytynyt.',
            playbook_count: 'Toimintaohjeiden Lukumäärä',
            requires_evidence: 'Vaatii Todistusaineiston',
            step_label: 'Vaiheen Nimi',
            target_name: 'Kohteen nimi',
            target_type: 'Kohteen Tyyppi',
            action_type: 'Toimintotyyppi',
            archive_error: 'Arkistointi epäonnistui.',
            confirm_archive: 'Haluatko varmasti arkistoida tämän toimintaohjeen?',
            data_categories: 'Tietokategoriat',
            days: 'päivää',
            fetch_error: 'Toimintaohjeiden haku epäonnistui.',
            sla_days: 'SLA (päivää)',
            target_id: 'Kohteen Tunnus',
            template_class_heading: 'Mallin luokitus',
            official: 'Virallinen',
            internal: 'Sisäinen',
            classification: 'Luokittelu',
            official_note: 'Virallinen: sääntelyn edellyttämät mallit, jotka on yhdenmukaistettu PDPL-, GDPR- tai alakohtaisten vaatimusten kanssa.',
            internal_note: 'Sisäinen: organisaatiokohtaiset menettelyt, joita ei ole johdettu sääntelymalleista.',
            active_executions_heading: 'Aktiiviset ajot',
            no_active_executions: 'Ei aktiivisia ajoja.',
            playbook_label: 'Pelikirja',
            ticket_id_label: 'Tiketti',
            progress_label: 'Edistyminen',
            actions_label: 'Toiminnot',
            view_steps_label: 'Näytä vaiheet',
            execution_detail_title: 'Ajon tiedot',
            playbook_missing: 'Playbookia ei löydy.',
            evidence_required_label: 'Todiste vaaditaan',
            upload_evidence_label: 'Lataa todiste',
            mark_complete_label: 'Merkitse valmiiksi',
            evidence_required_toast: 'Todiste vaaditaan ennen vaiheen valmistumista.',
            evidence_blocking_message: 'Liitä todistetiedosto jatkaaksesi.',
            step_completed_toast: 'Vaihe valmis.',
            executions_fetch_error: 'Ajojen lataus epäonnistui.',
            step_evidence_default_name: 'Ajovaiheen todiste',
            attached_evidence_label: 'Liitetyt todisteet',
        },
        approvals: {
            // Sprint 63.30.0 (#485) — approval expiry.
            expiry_setting_heading: 'Approval expiry',
            expiry_setting_label: 'Approval requests expire after (days)',
            expiry_setting_hint: 'Whole days from {min} to {max}. Applies to requests made after the change. A request still undecided at its expiry is marked Expired and its requester is told.',
            expiry_setting_value: 'Approval requests expire after {days} days.',
            expiry_setting_save: 'Save expiry',
            expiry_setting_saved: 'Approval expiry set to {days} days',
            expiry_setting_load_failed: 'The approval expiry setting could not be read.',
            expiry_setting_save_failed: 'The approval expiry setting could not be saved.',
            expires_on: 'Expires {date}',
            expired_undecided_note: 'Past its expiry; it can no longer be decided.',
            already_decided: 'This request has already been decided. The list has been refreshed to show its decision.',
            expired_refused: 'This request is past its expiry and can no longer be decided. The list has been refreshed.',
            title: 'Hyväksyntätyönkulut',
            new: 'Uusi hyväksyntä',
            edit: 'Muokkaa hyväksyntää',
            request_type: 'Pyynnön tyyppi',
            requester: 'Pyytäjä',
            approve: 'Hyväksy',
            reject: 'Hylkää',
            justification: 'Perustelu',
            all_action_types: 'Kaikki Toimintotyypit',
            decided_by: 'Päättäjä',
            no_approvals: 'Hyväksyntöjä ei löytynyt.',
            approval_count: 'Hyväksyntöjen Lukumäärä',
            approval_notes_optional: 'Muistiinpanot (valinnainen)',
            approve_error: 'Hyväksyntä epäonnistui.',
            approve_request: 'Hyväksy Pyyntö',
            decision: 'Päätös',
            entity: 'Kohde',
            fetch_error: 'Hyväksyntöjen haku epäonnistui.',
            reject_error: 'Hylkäys epäonnistui.',
            reject_request: 'Hylkää Pyyntö',
            rejection_notes_required: 'Hylkäysmuistiinpanot vaaditaan',
            rejection_notes_required_msg: 'Anna syy hylkäykselle.',
            requested_at: 'Pyydetty',
            decision_rules_heading: 'How decisions are recorded',
            decision_rule_second_person: 'A request is decided by someone other than the person who made it. You cannot approve your own request.',
            decision_rule_notes: 'Rejecting needs a written reason. Approving can carry notes, and either decision can attach supporting evidence.',
            decision_rule_scope: 'A decision is recorded here and in the governance audit trail. It does not carry out the action the request is about.',
            own_request_note: 'Your request. Another person decides it.',
            supporting_evidence_label: 'Tukitodiste (valinnainen)',
            decision_evidence_name: 'Päätöstodiste',
        },
        evidence: {
            title: 'Hallinnan todistusaineisto',
            new: 'Uusi todistusaineisto',
            link_to: 'Linkitä kohteeseen',
            entity_id_placeholder: 'Kohteen Tunnus',
            file_path_placeholder: '/polku/tiedostoon',
            evidence_label: 'Todistusaineiston Nimi',
            file_path: 'Tiedostopolku',
            source_mode_label: 'Todistelähde',
            source_mode_file: 'Lähetä tiedosto',
            source_mode_url: 'Linkitä ulkoinen URL',
            attachment_label: 'Liite',
            item_count: 'Kohteiden Lukumäärä',
            link_button: 'Linkitä Todistusaineisto',
            confirm_delete: 'Haluatko varmasti poistaa tämän todistusaineiston?',
            enter_entity_id: 'Syötä kohteen tunnus...',
            entity_id: 'Kohteen Tunnus',
            entity_type: 'Kohteen Tyyppi',
            expires: 'Vanhenee',
            expires_at: 'Vanhenee',
            fetch_error: 'Todistusaineiston haku epäonnistui.',
            link_error: 'Todistusaineiston linkitys epäonnistui.',
            no_evidence: 'Todistusaineistoa ei löytynyt.',
            tags_csv: 'Tunnisteet (pilkulla erotettu)',
            uploaded: 'Ladattu',
            url: 'URL',
            framework_tag_heading: 'Framework-taged Evidence',
            framework_tag_note: 'Jokainen todistetietue kaappaa kehyskoodin, näyttelijän ja aikaleiman. Suodata kehyksen mukaan eristääksesi tarkastuksen kannalta merkitykselliset tietueet.',
            framework_code: 'Puitekoodi',
            filter_by_framework: 'Suodata kehyskoodin mukaan...',
            actor: 'näyttelijä',
        },
        certification: {
            not_applicable: 'Ei sovellettavissa',
            not_certified: 'Ei sertifioitu',
            pending: 'Odottaa',
            certified: 'Sertifioitu',
            revoked: 'Peruutettu',
        },
        onboarding_pipeline: {
            title: 'Käyttöönottoprosessi',
            new_workflow: 'Uusi käyttöönottotyönkulku',
            vendor_id: 'Toimittajan tunnus',
            stages: 'Vaiheet',
            current_stage: 'Nykyinen vaihe',
            advance: 'Edistä vaihetta',
            advance_success: 'Vaihe edistetty onnistuneesti',
            workflow_status: 'Työnkulun tila',
            initiated: 'Aloitettu',
            in_progress: 'Käynnissä',
            completed: 'Valmis',
            cancelled: 'Peruutettu',
            no_workflows: 'Käyttöönottotyönkulkuja ei löytynyt',
            stages_placeholder: 'Vaihe 1, Vaihe 2, Vaihe 3',
            assigned_to: 'Vastuuhenkilö',
            notes: 'Muistiinpanot',
            pdpl_lifecycle_heading: 'PDPL Toimittajan elinkaari – käyttöönotto',
            due_diligence_note: 'Due diligence -vaihe on pakollinen ennen prosessorin käyttöönottoa kohdassa PDPL. Tarkista tietojen käsittelyn laajuus, oikeusperusta ja turvallisuustoimenpiteet.',
            contract_review_note: 'Sopimuksen tarkastelun on vahvistettava DPA-lausekkeet, tietojen säilytysrajat ja alikäsittelijän rajoitukset.',
            create_dpa_review_cta: 'Luo DPA-arviointi',
            dpa_review_linked: 'DPA-arviointi linkitetty',
        },
        dpa_review: {
            title: 'DPA-katselmukset',
            new_review: 'Uusi DPA-katselmus',
            review_type: 'Katselmuksen tyyppi',
            findings: 'Havainnot',
            outcome: 'Lopputulos',
            complete_review: 'Suorita katselmus',
            complete_success: 'Katselmus suoritettu onnistuneesti',
            link_assessment: 'Linkitä turvallisuusarviointi',
            link_success: 'Arviointi linkitetty onnistuneesti',
            assessment_id: 'Arvioinnin tunnus',
            combined_title: 'Yhdistetty arviointi',
            no_reviews: 'DPA-katselmuksia ei löytynyt',
            telecom_disclosure_heading: 'Telecomin tietojen julkistamisen hallinto',
            disclosure_trigger_note: 'Tietojen paljastamiseen perustuvat arvostelut tallentavat televiestintätilaajien tietojen jakamistapahtumat VN Telecom -lain mukaisesti.',
            telecom_t2_note: 'TELECOM-T2-tason hyväksyntä vaaditaan ennen tilaajatietojen luovuttamista viranomaisille.',
            quarterly_log_note: 'Käytä neljännesvuosittaista lokia dokumentoidaksesi kaikki ilmoitustapahtumat ja ylläpitääksesi jatkuvaa kirjausketjua.',
        },
        periodic_review: {
            title: 'Määräaikaiskatselmukset',
            new_review: 'Uusi määräaikaiskatselmus',
            review_type: 'Katselmuksen tyyppi',
            due_date: 'Eräpäivä',
            next_review: 'Seuraava katselmuspäivä',
            trigger_reassessment: 'Käynnistä uudelleenarviointi',
            reassessment_reason: 'Uudelleenarvioinnin syy',
            reassessment_success: 'Uudelleenarviointi käynnistetty onnistuneesti',
            complete_success: 'Katselmus suoritettu onnistuneesti',
            scheduled: 'Ajoitettu',
            in_progress: 'Käynnissä',
            completed: 'Valmis',
            overdue: 'Myöhässä',
            material_change: 'Olennainen muutos',
            no_reviews: 'Määräaikaiskatselmuksia ei löytynyt',
            mark_complete: 'Merkitse valmiiksi',
            pdpl_periodic_heading: 'PDPL Toimittajan elinkaari — määräaikaistarkistukset',
            scheduled_review_note: 'Säännölliset tarkastukset on ajoitettava vähintään kerran vuodessa kaikille aktiivisille prosessoreille. Myöhästyneet tarkastukset laukaisevat vaatimustenmukaisuusvaroitukset.',
            material_change_note: 'Olennaiset muutokset tietojenkäsittelyssä vaativat välitöntä uudelleenarviointia aikataulusta riippumatta.',
            overdue_badge: 'Myöhässä',
            overdue_aria: 'Myöhästynyt arviointi',
            only_overdue_filter: 'Vain myöhässä',
        },
        offboarding: {
            title: 'Toimittajan käytöstäpoisto',
            initiate: 'Aloita käytöstäpoisto',
            initiate_success: 'Käytöstäpoisto aloitettu onnistuneesti',
            reason: 'Käytöstäpoiston syy',
            data_return_required: 'Tietojen palautus vaaditaan',
            verify_data: 'Vahvista tietojen palautus',
            verify_success: 'Tietojen palautus vahvistettu onnistuneesti',
            sign_off: 'Hyväksy',
            sign_off_success: 'Käytöstäpoisto hyväksytty onnistuneesti',
            status_initiated: 'Aloitettu',
            status_data_pending: 'Tietojen palautus odottaa',
            status_data_verified: 'Tietojen palautus vahvistettu',
            status_completed: 'Valmis',
            status_cancelled: 'Peruutettu',
            task_id: 'Tehtävätunnus',
            no_tasks: 'Käytöstäpoistotehtäviä ei löytynyt',
            evidence_id: 'Todistusaineiston tunnus',
            pdpl_offboarding_heading: 'PDPL Toimittajan elinkaari – käytöstä poistaminen ja poistaminen',
            deletion_capture_note: 'Offboardingin on saatava tietojen poistamisen vahvistus toimittajalta. Tarkista tietojen palautus ennen kirjautumista.',
            evidence_attachment_note: 'Liitä poistotodistukset (sertifikaatit, kuvakaappaukset, toimittajan vahvistukset) täydentääksesi offboard-tietueen.',
            verify_mode_label: 'Varmistustapa',
            verify_mode_upload: 'Lähetä uusi tiedosto',
            verify_mode_link: 'Linkitä olemassa oleva todiste',
            verify_mode_none: 'Ei dataa palautettavaksi',
            upload_data_return_evidence: 'Lähetä datan palautustodiste',
            no_data_justification_label: 'Perustelu',
            no_data_justification_placeholder: 'Selitä miksi dataa ei ole palautettavaksi',
            no_data_justification_hint: 'Vähintään 20 merkkiä',
            upload_required_toast: 'Liitä tiedosto jatkaaksesi',
            evidence_link_required_toast: 'Valitse todiste jatkaaksesi',
            no_data_justification_required_toast: 'Anna vähintään 20 merkin perustelu',
            data_return_evidence_name: 'Datan palautustodiste',
            verify_before_signoff: 'Verify data return before sign-off',
        },
        dpa_review_types: {
            initial: 'Alkuperäinen',
            annual: 'Vuosittainen',
            renewal: 'Uusinta',
            incident_triggered: 'Tapahtumalaukaisu',
        },
        periodic_review_types: {
            initial: 'Alkuperäinen',
            annual: 'Vuosittainen',
            renewal: 'Uusinta',
            incident_triggered: 'Tapahtumalaukaisu',
            material_change: 'Olennainen muutos',
            disclosure_triggered: 'Tiedonantolaukaisu',
            quarterly_log: 'Neljännesvuosiraportti',
        },
        dpa_review_statuses: {
            pending: 'Odottaa',
            in_progress: 'Käynnissä',
            completed: 'Valmis',
            cancelled: 'Peruutettu',
        },
        onboarding_stage_names: {
            due_diligence: 'Huolellisuustarkistus',
            contract_review: 'Sopimustarkistus',
            security_assessment: 'Turvallisuusarviointi',
            data_mapping: 'Tietojen kartoitus',
            approval: 'Hyväksyntä',
            integration: 'Integraatio',
        },
        access_denied_title: 'Pääsy estetty',
        access_denied: 'Sinulla ei ole oikeutta tarkastella tätä sivua. Ota yhteyttä järjestelmänvalvojaan.',
    },
    consent: {
        nav: {
            policies: 'Käytännöt',
            purposes: 'Tarkoitusluettelo',
            workflows: 'Työnkulut',
            snapshots: 'CMP-tilannekuvat',
            change_awareness: 'Muutostietoisuus',
            audit: 'Suostumuksen tarkastus',
            evidence: 'Consent Evidence',
            marketing_consent: 'Marketing Consent',
            marketing_suppression: 'Suppression Register',
            marketing_suppression_enforcement: 'Suppression Enforcement',
            marketing_sender_name_certificates: 'Sender-name Certificates',
        },
        gap_analysis: {
            nav_label: 'Aukon analyysi',
            page_title: 'Suostumusvaje-analyysi',
            page_subtitle: 'Forseti checks your active consent purposes against the consent families of your installed frameworks, and suggests a remediation for each critical or high gap. Only installed families are analysed.',
            framework_picker_label: 'Kehykset',
            org_flags_label: 'Organisaation konteksti',
            flag_withdrawal: 'Siinä on dokumentoitu globaali nostomekanismi',
            flag_parental: 'Sisältää vanhempien suostumuksen alle 16-vuotiaille',
            flag_cross_border: 'Rajat ylittävä arkistointi on kirjattu',
            run_label: 'Suorita aukkoanalyysi',
            running_label: 'Juokse…',
            requires_advisor_label: 'Vaatii ai_advisor:chat-luvan.',
            results_heading: 'Aukkoja ilmestyi ({count})',
            tokens_label: '≈ {count}-merkkejä',
            empty_label: 'Valituissa kehyksissä ei havaittu aukkoja.',
            remediation_label: 'Korjausehdotus:',
            dismiss_label: 'Hylkää soveltumattomana',
            resolve_label: 'Ratkaise',
            dismissed_label: 'Hylätty soveltumattomana',
            resolved_label: 'Ratkaistu',
            // Sprint 62.27.0 gate E12.5 pass 2 — R-57: English until Phase 25.
            families_loading: 'Reading the installed frameworks…',
            families_load_error: 'The installed frameworks could not be read, so no family can be offered for analysis.',
            families_retry_subject: 'the installed frameworks',
            families_none: 'None of your installed frameworks belongs to a family this analysis reads, so there is nothing to analyse.',
            family_instruments: 'Installed: {codes}',
            analysed_label: 'Analysed: {families}',
            not_analysed_label: 'Not installed, so not analysed: {families}',
            nothing_analysed: 'Nothing was analysed: none of the selected families is installed, so no gap can be reported.',
            acknowledged_count_label: '{count} acknowledged finding(s), not counted as open gaps',
            run_failed: 'The gap analysis could not be run.',
            triage_failed: 'The decision could not be recorded.',
            purpose_scope: 'Purpose: {name}',
            org_wide_scope: 'Organisation-wide',
            attributes: {
                legal_basis_set: 'Lawful basis recorded',
                withdrawal_mechanism: 'Withdrawal mechanism',
                granular_per_purpose: 'Consent separable per purpose',
                parental_consent_for_minors: 'Consent for children',
                cross_border_filing: 'Cross-border transfer record',
                retention_period: 'Retention period',
                sensitive_data_ground_review: 'Sensitive-data ground review',
            },
            acknowledge_label: 'Acknowledge review',
            acknowledge_title: 'Acknowledge the sensitive-data ground review',
            acknowledge_description: 'Purpose: {name}. Record that you reviewed whether your installed frameworks set a condition for processing sensitive data for this purpose beyond its lawful basis and, if so, which. Later analyses show the finding as acknowledged until the purpose’s sensitivity or its recorded ground changes, or a framework family you did not review is installed.',
            acknowledge_note_label: 'Note (required)',
            acknowledge_note_hint: 'What you reviewed, and whether a condition is recorded on the purpose and, if so, which, with its article.',
            acknowledge_submit: 'Acknowledge',
            acknowledge_done: 'Review acknowledged',
            acknowledge_failed: 'The acknowledgement could not be recorded.',
            acknowledged_by: 'Acknowledged by {name} on {date}: {note}',
            acknowledgement_lapsed: 'An acknowledgement by {name} on {date} lapsed because {reasons}.',
            lapse_sensitivity_changed: 'the purpose’s sensitivity changed',
            lapse_basis_changed: 'its recorded ground changed',
            lapse_family_added: 'a family it did not review was installed: {families}',
            lapse_reason_joiner: ' and ',
        },
        import: {
            title: 'Tuo suostumustyönkulkuja',
            nav_title: 'Massatuonti',
            description: 'Lataa CSV-tiedosto suostumustyönkulkujen massatuontia varten.',
            select_file: 'Valitse CSV-tiedosto',
            file_help: 'CSV otsikoineen, jotka vastaavat työnkulkukenttiä.',
            column_count_help: 'A row whose number of values differs from the number of columns in the header is not imported; it is listed under Errors with its row number.',
            required_columns_help: 'Pakolliset sarakkeet: Työnkulun tyyppi, Rekisteröity, Kanava.',
            file_too_large: 'Tiedosto on liian suuri. Enimmäiskoko on 2 Mt.',
            preview: 'Esikatselu',
            columns_detected: 'Havaitut sarakkeet: {count}',
            column_mapping: 'Sarakemääritys',
            import_button: 'Tuo',
            import_complete: 'Tuonti valmis',
            import_error: 'Tuonti epäonnistui',
            result: 'Tuontitulos',
            total: 'Yhteensä',
            imported: 'Tuotu',
            skipped: 'Ohitettu',
            failed: 'Epäonnistunut',
            errors: 'Virheet',
            error_row: 'Rivi',
            error_field: 'Kenttä',
            error_message: 'Viesti',
            history: 'Tuontihistoria',
            no_history: 'Ei tuontihistoriaa.',
            history_file: 'Tiedostonimi',
            history_date: 'Päivämäärä',
            create_profiles_label: 'Luo Rekisteröidyn profiilit uusille kohteille',
            create_profiles_help: 'Tuonnit ilman vastaavaa profiilia luovat sellaisen automaattisesti. Poista valinta tuodaksesi vain työnkulkuja.',
            profiles_created: 'Luodut profiilit',
            profiles_matched: 'Vastaavat profiilit',
            subject_kind_help: 'Valinnaiset sarakkeet: subject_kind, subject_email, subject_display_name, subject_phone, external_ref_key.',
            error_subject_invalid_email: 'Virheellinen sähköpostiosoite subject_email-sarakkeessa.',
            error_subject_unknown_kind: 'Tuntematon subject_kind-arvo. Odotettu: email, visitor_uuid, employee_id, student_id, external_ref.',
            error_missing_external_ref_key: 'external_ref_key on pakollinen, kun subject_kind=external_ref',
        },
        policies: {
            title: 'Suostumuskäytännöt',
            new: 'Uusi käytäntö',
            edit: 'Muokkaa käytäntöä',
            name: 'Nimi',
            version: 'Versio',
            policy_text: 'Käytännön teksti',
            publish: 'Julkaise',
            versions: 'Versiot',
            hide_versions: 'Piilota versiot',
            submit_for_review: 'Lähetä tarkistettavaksi',
            suspend: 'Keskeytä',
            retire: 'Poista käytöstä',
            jurisdictions: 'Lainkäyttöalueet',
            jurisdictions_csv: 'Lainkäyttöalueet (pilkuin erotetut)',
            approval_mode: 'Hyväksyntätila',
            reviewer_ids: 'Tarkastajien tunnukset (pilkuin erotetut)',
            tags_csv: 'Tunnisteet (pilkuin erotetut)',
            effective_date: 'Voimaantulopäivä',
            review_date: 'Tarkistuspäivä',
            consent_model_statement: 'Suostumusmallin lausunto',
            consent_model_placeholder: 'Valinnainen suostumusmallin lausunto...',
            default_settings: 'Oletusasetukset (JSON)',
            changes: 'Muutokset',
            fetch_error: 'Suostumuskäytäntöjen hakeminen epäonnistui. Yritä uudelleen.',
            save_error: 'Suostumuskäytännön tallentaminen epäonnistui. Yritä uudelleen.',
            submit_review_error: 'Käytännön lähettäminen tarkistettavaksi epäonnistui. Yritä uudelleen.',
            activate_error: 'Käytännön aktivoiminen epäonnistui. Yritä uudelleen.',
            suspend_error: 'Käytännön keskeyttäminen epäonnistui. Yritä uudelleen.',
            retire_error: 'Käytännön käytöstä poistaminen epäonnistui. Yritä uudelleen.',
            submit_review_success: 'Käytäntö lähetetty tarkistettavaksi.',
            activate_success: 'Käytäntö aktivoitu.',
            suspend_success: 'Käytäntö jäädytetty.',
            retire_success: 'Politiikka jäi eläkkeelle.',
            versions_error: 'Käytännön versioiden hakeminen epäonnistui. Yritä uudelleen.',
            invalid_json: 'Virheellinen JSON Oletusasetukset-kentässä. Yritä uudelleen.',
            page_title: 'Suostumuskäytännöt',
            version_updated: 'Päivitetty',
            version_initial: 'Alkuperäinen versio',
            json_format: 'JSON muodossa',
            sensitive: 'Herkkä',
            flag_sensitive: 'Lippuherkkä',
            flag_sensitive_title: 'Merkitse käytäntö arkaluonteiseksi',
            require_enhanced_review: 'Edellyttää tehostettua tarkistusta',
            enhanced_review_success: 'Tehostettu tarkistusvaatimus käytössä.',
            return_to_draft: 'Return to draft',
            return_to_draft_hint: 'End this review round and make the policy editable again',
            return_to_draft_title: 'Return policy to draft',
            return_to_draft_body: "The decisions recorded in this review round are kept in the policy's approval history. The policy becomes a draft, to be edited and submitted for review again.",
            return_to_draft_reason: 'Reason (required)',
            return_to_draft_reason_required: 'Enter the reason for returning this policy to draft.',
            return_to_draft_success: 'Policy returned to draft.',
            return_to_draft_error: 'The policy could not be returned to draft.',
            in_review_locked_note: 'In review: the policy cannot be edited, flagged sensitive or routed to enhanced review. Return it to draft to change it.',
            refusal_policy_in_review: 'This policy is in review and cannot be changed. Return it to draft to change it.',
            refusal_policy_not_in_review: 'This policy is no longer in review. The list has been refreshed.',
            refusal_policy_changed_concurrently: 'The policy changed while this was being saved. The list has been refreshed; check the policy and try again.',
            refusal_not_a_named_reviewer: 'Only the reviewers this policy names can decide it.',
            select_categories: 'Valitse sovellettavat PDPL artiklan 2 mukaiset arkaluontoiset tietoluokat:',
            no_policies: 'Suostumuskäytäntöjä ei löytynyt. Luo ensimmäinen käytäntösi aloittaaksesi.',
            approval_success: 'Käytäntö hyväksytty.',
            reviewers_label: 'Arvioijat',
            enhanced_review_title: 'Laajennettu tarkistus vaaditaan',
            enhanced_review_badge: 'Laajennettu tarkistus vaaditaan',
            enhanced_review_status_pending: 'Tarkistus odottaa',
            enhanced_review_status_approved: 'Tarkistus hyväksytty',
            enhanced_review_status_rejected: 'Tarkistus hylätty',
            enhanced_review_reviewed_by: 'Tarkistanut',
            enhanced_review_notes: 'Tarkistusmuistiinpanot',
            enhanced_review_approve: 'Hyväksy tarkistus',
            enhanced_review_reject: 'Hylkää tarkistus',
            enhanced_review_notes_required: 'Tarkistusmuistiinpanot ovat pakollisia',
            enhanced_review_approved: 'Laajennettu tarkistus hyväksytty',
            enhanced_review_rejected: 'Laajennettu tarkistus hylätty — käytäntö palautettu luonnokseksi',
            enhanced_review_reset_to_draft: 'Laajennettu tarkistus hylätty — käytäntö palautettu luonnokseksi',
            enhanced_review_reject_warning: 'Hylkääminen palauttaa tämän käytännön Luonnos-tilaan. DPO:n on toimitettava se uudelleen tarkistettavaksi ongelmien ratkaisemisen jälkeen.',
            submitted_modal_title: 'Lähetetty laajennettua tarkistusta varten',
            submitted_modal_body: 'Tämä käytäntö on lähetetty. Oikeudellisen/Tietosuoja-arvioijan on hyväksyttävä se ennen kuin se voidaan aktivoida.',
            activate_hint: 'Make this policy effective',
            approval_comments: 'Comments (optional)',
            approve_title: 'Approve Policy',
            flag_sensitive_hint: 'Mark as involving sensitive data',
            reactivate_hint: 'Resume this suspended policy',
            reject_hint: 'Decline this policy and return it to draft',
            reject_title: 'Reject Policy',
            rejection_reason: 'Rejection reason (required)',
            rejection_success: 'Policy rejected',
            require_enhanced_review_hint: 'Route to a second-level reviewer',
            retire_hint: 'Permanently retire this policy',
            review_context_description: 'Confirm the policy scope and review controls before saving.',
            review_context_hint: 'Reviewers, jurisdictions, dates, and consent defaults determine how this policy is governed.',
            review_context_title: 'Review context',
            sensitive_filter: 'Sensitive',
            suspend_hint: 'Temporarily pause this policy',
            suspend_reason_label: 'Suspension reason (required)',
            suspend_title: 'Suspend Policy',
            type_content_edit: 'Content Edit',
            type_status_change: 'Status Change',
            reactivate: 'Re-activate',
            no_versions: 'No version history yet.',
            approval_mode_hint: 'All: every named reviewer must approve, and only a named reviewer may decide. Any: one approval decides. In both, one rejection rejects the policy.',
            refusal_use_enhanced_review: 'This policy is routed to enhanced review, so it is decided there, not through the standard approval. The list has been refreshed.',
            refusal_reviewer_cannot_decide_named: 'These named reviewers could never decide this policy: {names}. Every named reviewer must be an active member of the organisation who holds the permission to approve consent policies, or, for a policy routed to enhanced review, the permission to carry out enhanced review.',
            refusal_reviewer_cannot_decide_none: 'This policy names no reviewer, and every named reviewer must approve it. Name at least one reviewer before submitting.',
            refusal_invalid_status_transition: 'This policy is no longer in a status that allows this. The list has been refreshed.',
            approval_waiting: 'Your approval is recorded. The policy is approved once {count} more named reviewer(s) approve.',
            enhanced_review_waiting: 'Your enhanced-review approval is recorded. The policy is approved once {count} more named reviewer(s) approve.',
            decision_recorded_in_review: 'Your decision is recorded. The policy is still in review.',
            decision_recorded: 'Your decision is recorded.',
            review_history: 'Review history',
            review_history_current: 'Decisions in the current review round',
            review_history_earlier: 'Earlier review rounds',
            review_history_empty: 'No review decision has been recorded for this policy yet.',
            history_col_reviewer: 'Reviewer',
            history_col_decision: 'Decision',
            history_col_review_kind: 'Review',
            history_col_decided_at: 'Decided',
            history_col_notes: 'Notes',
            history_kind_standard: 'Standard approval',
            history_kind_enhanced: 'Enhanced review',
            history_decision_approved: 'Approved',
            history_decision_rejected: 'Rejected',
            history_col_ended_at: 'Round ended',
            history_col_outcome: 'How it ended',
            history_col_ended_by: 'Ended by',
            history_col_reason: 'Reason',
            history_col_round_decisions: 'Decisions in the round',
            history_ended_resubmitted: 'Submitted for review again',
            history_ended_returned: 'Returned to draft',
            history_no_decisions: 'No decision was recorded',
            history_decision_by: '{decision} by {name}',
        },
        sensitive_categories: {
            health: 'Terveystiedot',
            biometric: 'Biometriset tiedot',
            political: 'Poliittiset mielipiteet',
            ethnic: 'Etninen alkupera',
            genetic: 'Geneettiset tiedot',
            criminal: 'Rikosrekisterit',
        },
        purposes: {
            title: 'Tarkoitusluettelo',
            new: 'Uusi tarkoitus',
            edit: 'Muokkaa tarkoitusta',
            name: 'Nimi',
            legal_basis: 'Oikeusperuste',
            retention_days: 'Säilytysaika (päiviä)',
            is_active: 'Aktiivinen',
            filter_by_category: 'Suodata kategorian mukaan',
            policy_ids: 'Käytäntötunnukset (pilkuin erotetut)',
            ropa_activity_ids: 'ROPA-toimintatunnukset (pilkuin erotetut)',
            policies: 'Käytännöt',
            deprecate: 'Merkitse vanhentuneeksi',
            fetch_error: 'Suostumustarkoitusten hakeminen epäonnistui. Yritä uudelleen.',
            save_error: 'Suostumustarkoituksen tallentaminen epäonnistui. Yritä uudelleen.',
            deprecate_error: 'Suostumustarkoituksen vanhentaminen epäonnistui. Yritä uudelleen.',
            page_title: 'Tarkoitusluettelo',
            no_purposes: 'Suostumustarkoituksia ei löytynyt. Luo ensimmäinen tarkoituksesi aloittaaksesi.',
            category_personal_data: 'Henkilötiedot',
            category_sensitive_data: 'Arkaluonteiset tiedot',
            category_health_data: 'Terveystiedot',
            category_financial_data: 'Taloustiedot',
            category_biometric_data: 'Biometriset tiedot',
            category_children_data: 'Lasten tiedot',
            category_location_data: 'Sijaintitiedot',
            category_communications_data: 'Viestintätiedot',
            category_other: 'muu',
        },
        workflows: {
            title: 'Suostumuksen työnkulut',
            new: 'Uusi työnkulku',
            edit: 'Muokkaa työnkulkua',
            workflow_type: 'Työnkulun tyyppi',
            user_id: 'Käyttäjätunnus',
            purpose_id: 'Tarkoitustunnus',
            purpose_ids_csv: 'Tarkoitustunnus (pilkuin erotetut)',
            reason: 'Syy',
            process: 'Käsittele',
            data_subject_ref: 'Rekisteröidyn viite',
            data_subject: 'Rekisteröity',
            channel: 'Kanava',
            policy_id: 'Käytäntötunnus',
            scope: 'Laajuus',
            legal_grounds: 'Oikeusperusteet',
            assessment: 'Arviointi',
            select_outcome: 'Valitse tulos',
            partially_approved: 'Osittain hyväksytty',
            denied: 'Evätty',
            outcome_upheld: 'Vahvistettu',
            outcome_pending: 'Odottaa',
            outcome_denied: 'Evätty',
            status_count: 'Tilamäärät',
            channel_filter: 'Suodata kanavalla',
            add_action: '+ Toiminto',
            by_type: 'Tyypin mukaan',
            by_status: 'Tilan mukaan',
            downstream_actions: 'Jatkotoimenpiteet',
            downstream_actions_for: 'Jatkotoimenpiteet kohteelle {subject} ({type})',
            add_downstream_action: 'Lisää jatkotoimenpide',
            assigned_to: 'Vastuuhenkilö',
            fetch_error: 'Suostumuksen työnkulkujen hakeminen epäonnistui. Yritä uudelleen.',
            dashboard_error: 'Työnkulkujen yhteenvedon hakeminen epäonnistui. Yritä uudelleen.',
            save_error: 'Suostumuksen työnkulun tallentaminen epäonnistui. Yritä uudelleen.',
            status_update_error: 'Työnkulun tilan päivittäminen epäonnistui. Yritä uudelleen.',
            assessment_error: 'Työnkulun arvioinnin päivittäminen epäonnistui. Yritä uudelleen.',
            add_action_error: 'Työnkulun toiminnon lisääminen epäonnistui. Yritä uudelleen.',
            update_action_error: 'Työnkulun toiminnon päivittäminen epäonnistui. Yritä uudelleen.',
            type_refusal: 'Kieltäytyminen',
            type_withdrawal: 'Peruutus',
            type_restriction: 'Rajoitus',
            type_objection: 'Vastustaminen',
            status_received: 'Vastaanotettu',
            status_processing: 'Käsittelyssä',
            status_under_assessment: 'Arvioitavana',
            status_implemented: 'Toteutettu',
            evidence_button: 'Todisteet',
            evidence_modal_title: 'Työnkulun todisteet',
            no_evidence: 'Tähän työnkulkuun ei ole liitetty todisteita.',
            upload_evidence_label: 'Lataa työnkulun todisteet',
            completed_at: 'Valmistunut klo',
            effective_date: 'Voimaantulopäivä',
            page_title: 'Suostumustyönkulut',
            no_workflows: 'Suostumustyönkulkuja ei löytynyt. Työnkulut luodaan, kun rekisteröidyt käyttävät oikeuksiaan.',
            type_grant: 'Myöntäminen',
            propagation_none: 'Ei mitään',
            propagation_pending: 'Odottaa',
            propagation_pending_warning: 'Ei voida viimeistellä — peruutuksen eteneminen on vielä kesken',
            propagation_completed: 'Valmis',
            propagation_partial: 'Osittainen',
            propagation_incomplete_error: 'Työnkulkua ei voi viimeistellä, kun peruutuksen eteneminen on vielä kesken',
            propagation_status: 'Etenemisen tila',
        },
        snapshots: {
            title: 'CMP-tilannekuvat',
            new: 'Uusi tilannekuva',
            label: 'Tunniste',
            snapshot_date: 'Tilannekuvan päivämäärä',
            purposes_count: 'Tarkoitusten lukumäärä',
            compare: 'Vertaa',
            snapshot_data: 'Tilannekuvan tiedot (JSON)',
            select_left: 'Valitse vasen tilannekuva',
            select_right: 'Valitse oikea tilannekuva',
            comparison_results: 'Vertailutulokset',
            field: 'Kenttä',
            left: 'Vasen',
            right: 'Oikea',
            close_comparison: 'Sulje vertailu',
            trigger: 'Laukaisin',
            trigger_manual: 'Manuaalinen',
            trigger_policy_approval: 'Käytännön hyväksyntä',
            cmp_version: 'CMP-versio',
            policy_version_id: 'Käytännön versiotunnus',
            banner_config: 'Banneriasetukset (JSON)',
            active_purposes: 'Aktiiviset tarkoitukset (JSON)',
            active_legal_bases: 'Aktiiviset oikeusperusteet (JSON)',
            domain_scope: 'Verkkotunnuksen laajuus',
            domain_scope_csv: 'Verkkotunnuksen laajuus (pilkuin erotettu)',
            raw_config: 'Raaka-asetukset (JSON)',
            checksum: 'Tarkistussumma',
            created: 'Luotu',
            fetch_error: 'CMP-tilannekuvien hakeminen epäonnistui. Yritä uudelleen.',
            save_error: 'CMP-tilannekuvan tallentaminen epäonnistui. Yritä uudelleen.',
            compare_error: 'Tilannekuvien vertailu epäonnistui. Yritä uudelleen.',
            page_title: 'Suostumuksen tilannekuvat',
            json_format: 'JSON muodossa',
            json_array_format: 'JSON-taulukkomuoto',
            manual_snapshot_note: 'Vain järjestelmänvalvojille/kehittäjille. Normaalitilanteessa tilannekuvat luodaan automaattisesti CMP-webhookin tai käytäntöjen hyväksynnän kautta. Liitä JSON CMP-toimittajasi viennistä (esim. OneTrust, Cookiebot).',
            banner_config_hint: 'Liitä bannerin konfiguraatio-objekti CMP-viennistäsi.',
            active_purposes_hint: 'JSON-taulukko aktiivisista tarkoitusobjekteista CMP-viennistäsi.',
            active_legal_bases_hint: 'JSON-taulukko aktiivisista oikeusperusteista CMP-viennistäsi.',
            raw_config_hint: 'Täydellinen raakamuotoinen konfigurations-JSON CMP:stäsi. Muut JSON-kentät poimitaan tästä. SHA-256-tarkistussumma lasketaan automaattisesti.',
            changed_field: 'Muutettu kenttä: ',
            no_snapshots: 'CMP-tilannekuvia ei löytynyt. Tilannekuvat luodaan, kun käytännöt hyväksytään tai ne käynnistetään manuaalisesti.',
            detail_title: 'Tilannekuvan tiedot',
            view_snapshot_detail: 'Näytä tilannekuvan tiedot',
            policy_version: 'Käytäntöversio',
            removed: 'Ennen',
            added: 'Jälkeen',
        },
        change_awareness: {
            title: 'Suostumuksen muutostietoisuus',
            change_type: 'Muutoksen tyyppi',
            entity_type: 'Kohteen tyyppi',
            entity_id: 'Kohteen tunnus',
            detected_at: 'Havaittu',
            acknowledge: 'Kuittaa',
            new_alert: 'Uusi hälytys',
            new_alert_form: 'Uusi muutostietoisuushälytys',
            total_open: 'Avoimet yhteensä',
            by_severity: 'Vakavuuden mukaan',
            snapshot_id: 'Tilannekuvan tunnus',
            update_status: 'Päivitä tila',
            resolution_notes: 'Ratkaisumuistiinpanot',
            resolution_required: 'Ratkaisumuistiinpanot (pakollinen)',
            resolution_required_msg: 'Ratkaisumuistiinpanot ovat pakollisia kohteen ratkaisemisessa.',
            status_open: 'Avoin',
            status_under_review: 'Tarkistettavana',
            status_resolved: 'Ratkaistu',
            status_accepted: 'Hyväksytty',
            severity_informational: 'Tiedoksi',
            severity_requires_review: 'Vaatii tarkistuksen',
            severity_policy_violation: 'Käytäntörikkomus',
            create_reconciliation: 'Luo sovittelutehtävä',
            reconciliation_created: 'Täsmäytystehtävä luotiin onnistuneesti.',
            reconciliation_error: 'Täsmäytystehtävän luominen epäonnistui. Yritä uudelleen.',
            fetch_error: 'Muutostietoisuuskohteiden hakeminen epäonnistui. Yritä uudelleen.',
            dashboard_error: 'Muutostietoisuuden yhteenvedon hakeminen epäonnistui. Yritä uudelleen.',
            create_error: 'Muutostietoisuuskohteen luominen epäonnistui. Yritä uudelleen.',
            status_update_error: 'Muutostietoisuuden tilan päivittäminen epäonnistui. Yritä uudelleen.',
            page_title: 'Muutostietoisuus',
            no_items: 'Muutostietoisuuselementtejä ei löytynyt. Elementit luodaan, kun suostumuskonfiguraation muutoksia havaitaan.',
            affected_policies: 'Affected Policies',
            affected_purposes: 'Affected Purposes',
            estimated_affected_subjects: 'Estimated Affected Subjects',
            impact_scope: 'Impact Scope',
            affected_policies_short: 'Policies',
            affected_purposes_short: 'Purposes',
            affected_subjects_short: 'Subjects',
        },
        audit: {
            title: 'Suostumuksen tarkastuspolku',
            event_type: 'Tapahtuman tyyppi',
            user_id: 'Käyttäjätunnus',
            timestamp: 'Aikaleima',
            details: 'Tiedot',
            actor: 'Tekijä',
            filter_by_entity_type: 'Suodata kohdetyypin mukaan',
            filter_by_action: 'Suodata toiminnon mukaan',
            fetch_error: 'Suostumuksen tarkastuspolun hakeminen epäonnistui. Yritä uudelleen.',
            page_title: 'Suostumuksen tarkastus',
            export_csv: 'Vie CSV',
            exporting: 'Viedään…',
            export_success: 'Kirjausketjun vienti onnistui.',
            export_error: 'Suostumuksen kirjausketjun vienti epäonnistui. Yritä uudelleen.',
            no_entries: 'Tarkastusmerkintöjä ei löytynyt. Tarkastustapahtumat kirjataan automaattisesti, kun suostumustiedot muuttuvat.',
            entity_policy: 'Käytäntö',
            entity_purpose: 'Tarkoitus',
            entity_workflow: 'Työnkulku',
            entity_consent_record: 'Suostumustietue',
            entity_change_awareness: 'Muutostietoisuus',
            entity_cmp_snapshot: 'CMP-tilannekuva',
            action_created: 'Luotu',
            action_updated: 'Päivitetty',
            action_status_changed: 'Tila muuttunut',
            action_drift_detected: 'Poikkeama havaittu',
            action_snapshot_created: 'Tilannekuva luotu',
            action_approval_submitted: 'Hyväksyntä lähetetty',
            action_archived: 'Arkistoitu',
            action_purpose_auto_mapped: 'Tarkoitus liitetty automaattisesti',
            action_purpose_mapping_updated: 'Tarkoitusliitos päivitetty',
            action_webhook_configured: 'Webhook määritetty',
            entity_propagation: 'Lisääntyminen',
            entity_reconciliation: 'Sovittelu',
            entity_marketing_consent: 'Marketing consent',
            entity_marketing_suppression: 'Marketing suppression',
            entity_marketing_suppression_enforcement: 'Suppression enforcement check',
            action_propagation_confirmed: 'Levitys vahvistettu',
            action_propagation_failed: 'Levitys epäonnistui',
            action_consent_captured: 'Suostumus tallennettu',
            action_reconciliation_created: 'Sovitus luotu',
            action_reconciliation_resolved: 'Sovittelu ratkaistu',
            action_submitted_for_review: 'Lähetetty tarkistettavaksi',
            action_activated: 'Aktivoitu',
            action_suspended: 'Keskeytetty',
            action_retired: 'Poistettu käytöstä',
            action_enhanced_review_required: 'Tehostettu tarkistus vaaditaan',
            action_enhanced_review_approved: 'Laajennettu tarkistus hyväksytty',
            action_enhanced_review_rejected: 'Laajennettu tarkistus hylätty — käytäntö palautettu luonnokseksi',
            action_consented: 'Suostunut',
            action_consented_customize: 'Suostumus räätälöity',
            action_revoked: 'Peruutettu',
            action_rejected: 'Hylätty',
        },
        web_records: {
            title: 'Verkkosuostumustiedot',
            page_title: 'Verkkosuostumustiedot',
            domain: 'Verkkotunnus',
            visitor_id: 'Vierailijatunnus',
            consent_action: 'Suostumustoimi',
            received_at: 'Vastaanotettu klo',
            admin_status: 'Järjestelmänvalvojan tila',
            revoke: 'Peruuta',
            reject: 'Hylkää',
            reason: 'Syy',
            reason_placeholder: 'Syy tähän toimenpiteeseen (valinnainen)',
            revoke_confirm: 'Perutaanko tämä suostumustietue?',
            reject_confirm: 'Hylätäänkö tämä suostumustietue?',
            override_success: 'Tietueen tilan päivitys onnistui.',
            override_error: 'Tietueen tilan päivittäminen epäonnistui.',
            no_records: 'Verkkosuostumustietueita ei löytynyt.',
            fetch_error: 'Verkkosuostumustietueiden lataaminen epäonnistui.',
            filter_by_domain: 'Suodata verkkotunnuksen mukaan',
            filter_by_action: 'Suodata toiminnon mukaan',
            active: 'Aktiivinen',
        },
        legal_bases: {
            all: 'Kaikki oikeusperusteet',
        },
        common_statuses: {
            suspended: 'Keskeytetty',
            retired: 'Poistettu käytöstä',
            deprecated: 'Vanhentunut',
        },
        approval_modes: {
            all: 'Kaikki',
            any: 'Mikä tahansa',
        },
        cmp_integration: {
            title: 'AesirX CMP -integraatio',
            back_to_consent: 'Takaisin suostumukseen',
            back_to_integration: 'Takaisin integraatioon',
            webhook_status: 'Webhook-tila',
            events_received: 'Vastaanotetut tapahtumat',
            web_consent_records: 'Verkkosuostumustietueet',
            unmapped_purposes: 'Kytkemättömät tarkoitukset',
            integration_management: 'Integraation hallinta',
            webhook_configuration: 'Webhook-asetukset',
            webhook_config_desc: 'Määritä CMP-webhook-päätepiste ja hallitse HMAC-salaisuutta.',
            purpose_mapping: 'Tarkoitusten kytkentä',
            purpose_mapping_desc: 'Kytke CMP-tarkoitustunnisteet GRC-suostumuskatalogiin.',
            bulk_sync: 'Joukkosynkronointi',
            bulk_sync_desc: 'Sovella tarkoituskytkennät uudelleen kaikkiin kytkemättömiin suostumustietueisiin.',
            consent_analytics: 'Verkkosuostumusanalytiikka (viimeiset 30 päivää)',
            consent_grant_rate: 'Suostumusten myöntämisprosentti',
            top_domain: 'Suosituin verkkotunnus',
            records: 'tietuetta',
            banner_interactions: 'Bannerin vuorovaikutukset',
            consent_actions: 'Suostumustoimenpiteet',
            opt_in_trend: 'Opt-in/Opt-out-trendi',
            records_by_domain: 'Records by Domain',
            date: 'Päivämäärä',
            grants: 'Myönnöt',
            denials: 'Kiellot',
            not_configured: 'Ei määritetty',
            load_error: 'Integraatiotietojen lataaminen epäonnistui',
            webhook_not_configured: 'Webhookia ei ole määritetty.',
            webhook_not_configured_msg: 'Ole hyvä ja {link} ensin.',
            configure_webhook_link: 'määritä webhook',
            setup_wizard: 'Asennusvelho',
            step_configure_webhook: 'Määritä webhook',
            step_map_purposes: 'Kytke tarkoitukset',
            step_run_sync: 'Suorita ensimmäinen joukkosynkronointi',
            step_verify_data: 'Tarkista data yhteenvedossa',
            configure: 'Määritä',
            view_dashboard: 'Näytä yhteenveto',
            syncing: 'Synkronoidaan...',
            sync_now: 'Synkronoi nyt',
            sync_completed: 'Synkronointi valmis: {imported} tuotu, {skipped} ohitettu, {failed} epäonnistunut.',
            sync_failed: 'Synkronointi epäonnistui',
            last_sync_result: 'Viimeisin synkronointitulos',
            imported: 'Tuotu',
            skipped: 'Ohitettu',
            errors: 'Virheet',
            index: 'Indeksi',
            error: 'Virhe',
            sync_history: 'Synkronointihistoria',
            no_sync_jobs: 'Synkronointitehtäviä ei ole vielä tallennettu.',
            load_sync_error: 'Synkronointitietojen lataaminen epäonnistui',
            purpose_mapping_title: 'CMP-tarkoitusten kytkentä',
            auto_mapping: 'Automaattinen kytkentä...',
            auto_map_purposes: 'Kytke tarkoitukset automaattisesti',
            unmapped_warning: '{count} kytkemätöntä tarkoitusta -- nämä CMP-tarkoitukset tulee kytkeä GRC-tarkoituksiin.',
            mapping_saved: 'Kytkentä tallennettu.',
            mapping_deleted: 'Kytkentä poistettu.',
            auto_map_complete: 'Automaattinen kytkentä valmis: {mapped} kytketty, {unmatched} kytkemättä.',
            auto_map_failed: 'Automaattinen kytkentä epäonnistui',
            save_mapping_error: 'Kytkennän tallentaminen epäonnistui',
            delete_mapping_error: 'Kytkennän poistaminen epäonnistui',
            load_data_error: 'Tietojen lataaminen epäonnistui',
            cmp_purpose: 'CMP-tarkoitus',
            grc_purpose: 'GRC-tarkoitus',
            select_grc_purpose: '-- Valitse GRC-tarkoitus --',
            auto_mapped: 'Automaattisesti kytketty',
            mapped: 'Kytketty',
            unmapped: 'Kytkemätön',
            no_mappings: 'Tarkoituskytkentöjä ei löytynyt. Kytkennät luodaan automaattisesti, kun suostumustapahtumia vastaanotetaan.',
            total_mappings: 'Yhteensä: {count} kytkentä(ä)',
            webhook_config_title: 'Webhook-asetukset',
            current_configuration: 'Nykyinen määritys',
            setup_webhook: 'Määritä webhook',
            setup_webhook_desc: 'Määritä AesirX CMP -webhook vastaanottamaan suostumustapahtumia.',
            allowed_domains: 'Sallitut verkkotunnukset (yksi per rivi)',
            webhook_secret_warning: 'Webhook-salaisuus (tallenna tämä nyt -- sitä ei näytetä uudelleen):',
            last_event: 'Viimeisin tapahtuma',
            none_received: 'Ei mitään',
            update_domains: 'Päivitä verkkotunnukset',
            pause_webhook: 'Keskeytä webhook',
            resume_webhook: 'Jatka webhookia',
            regenerate_secret: 'Luo salaisuus uudelleen',
            configuring: 'Määritetään...',
            create_webhook_config: 'Luo webhook-asetukset',
            webhook_endpoint: 'Webhook-päätepiste',
            webhook_endpoint_desc: 'Määritä AesirX CMP lähettämään suostumustapahtumia osoitteeseen:',
            webhook_signature_note: 'Lisää HMAC-SHA256-allekirjoitus {header}-otsikkoon.',
            configured_success: 'Webhook määritetty onnistuneesti.',
            config_updated: 'Asetukset päivitetty.',
            config_error: 'Webhookin määrittäminen epäonnistui',
            update_error: 'Asetusten päivittäminen epäonnistui',
            toggle_error: 'Webhookin vaihtaminen epäonnistui',
            deactivated: 'Webhook poistettu käytöstä.',
            deactivate_error: 'Webhookin poistaminen käytöstä epäonnistui',
            page_title: 'CMP Integrointi',
            webhook_config_page_title: 'CMP Webhook-määritykset',
            bulk_sync_page_title: 'Karttaa tietueet uudelleen',
            purpose_mapping_page_title: 'Tarkoituksen kartoitus',
            data_loaded: 'CMP integrointitiedot ladattu',
            sync_all: 'Synkronoi kaikki',
            sync_selected: 'Synkronoi valitut',
            sync_status: 'Synkronointitila',
            last_sync: 'Viimeisin synkronointi',
            no_records: 'Tietueita ei löytynyt.',
            sync_in_progress: 'Synkronointi käynnissä...',
            sync_complete: 'Synkronointi valmis.',
            sync_error: 'Synkronointi epäonnistui. Yritä uudelleen.',
            map_purpose: 'Liitä tarkoitus',
            unmap: 'Poista liitos',
            mapping_status: 'Liitoksen tila',
            save_mapping: 'Tallenna liitos',
            remap_unmapped_records: 'Karttaa kartoittamattomat tietueet uudelleen',
            remap_records: 'Karttaa tietueet uudelleen',
            add_mappings_first: 'Lisää ensin käyttötarkoituskartoitukset (vaihe 2)',
            remap_result: 'Uudelleenkartoituksen tulos',
            remap_result_desc: 'Käsiteltiin kaikki suostumustietueet kartoittamattomilla tai osittain kartoitetuilla tarkoituksilla.',
            total_processed: 'Käsitelty yhteensä',
            newly_mapped: 'Vasta kartoitettu',
            still_unmapped_label: 'Vielä kartoittamaton',
            still_unmapped_msg: '{count} tietueilla on edelleen kartoittamattomia tarkoituksia. {link} ja napsauta sitten Kartta uudelleen.',
            add_more_mappings: 'Lisää lisää kartoituksia',
            remap_complete: 'Uudelleen kartoitus valmis: {remapped} on juuri kartoitettu, {still_unmapped} vielä kartoittamaton.',
            add_mapping: 'Lisää kartoitus',
            cmp_purpose_id_label: 'CMP Käyttötunnus *',
            cmp_purpose_id_required: 'CMP Käyttötunnus vaaditaan',
            cmp_purpose_name_label: 'CMP Käyttötarkoituksen nimi',
            grc_purpose_label: 'GRC:n tarkoitus',
        },
        propagation: {
            title: 'Peruutuksen levitys',
            systems: 'Yhdistetyt järjestelmät',
            status: 'Tila',
            confirmed: 'Vahvistettu',
            pending: 'Odottaa',
            failed: 'Epäonnistunut',
            confirm: 'Vahvista peruutus',
            fail: 'Merkitse epäonnistuneeksi',
            failure_reason: 'Epäonnistumisen syy',
            can_close: 'Kaikki järjestelmät vahvistettu — työnkulku voidaan sulkea',
            cannot_close: 'Odotetaan järjestelmävahvistuksia',
            no_tasks: 'Ei levitystehtäviä.',
            confirm_success: 'Järjestelmän peruutus vahvistettu.',
            fail_success: 'Järjestelmä merkitty epäonnistuneeksi.',
            propagate_success: 'Peruutus levitetty yhdistetyille järjestelmille.',
            propagate: 'Levitä järjestelmiin',
            add_system: 'Lisää järjestelmä',
            remove_system: 'Poista',
            system_id: 'Järjestelmän tunnus',
            system_name: 'Järjestelmän nimi',
            owner_id: 'Omistajan tunnus',
            propagate_error: 'Peruuttamisen levittäminen epäonnistui. Yritä uudelleen.',
            no_systems: 'Lisää vähintään yksi järjestelmä.',
            page_title: 'Noston leviäminen',
            workflow_id: 'Valitse Noston työnkulku',
            system_id_hint: 'esim. crm-001, erp-prod',
            owner_id_hint: 'Järjestelmän omistaja',
            elapsed: 'Kulunut',
        },
        sensitive: {
            title: 'Arkaluonteisten tietojen käytännöt',
            flag: 'Merkitse arkaluonteiseksi',
            categories: 'Arkaluonteiset luokat',
            enhanced_review: 'Vaadi laajennettu tarkistus',
            enhanced_review_success: 'Laajennettu tarkistus vaadittu.',
            flag_success: 'Käytäntö merkitty arkaluonteiseksi.',
            no_sensitive: 'Ei arkaluonteisten tietojen käytäntöjä.',
        },
        reconciliation: {
            title: 'Poikkeamien täsmäytys',
            create: 'Uusi täsmäytystehtävä',
            system: 'Järjestelmä',
            owner: 'Omistaja',
            description: 'Kuvaus',
            resolution: 'Ratkaisu',
            resolve: 'Ratkaise',
            update_grc: 'Päivitä GRC',
            update_cmp: 'Päivitä CMP',
            accept_difference: 'Hyväksy ero',
            escalate: 'Eskaloi',
            resolution_notes: 'Ratkaisuhuomautukset',
            no_tasks: 'Ei täsmäytystehtäviä.',
            resolve_success: 'Täsmäytystehtävä ratkaistu.',
            create_success: 'Täsmäytystehtävä luotu.',
            open: 'Avoin',
            resolved: 'Ratkaistu',
            escalated: 'Eskaloitu',
            dashboard_open: 'Avoimet tehtävät',
            dashboard_resolved: 'Ratkaistut tehtävät',
            dashboard_escalated: 'Eskaloidut tehtävät',
            page_title: 'Epäsovitus täsmäytys',
            my_tasks: 'Omat tehtäväni',
            all_tasks: 'Kaikki tehtävät',
            severity: 'Vakavuus',
        },
        sync: {
            title: 'CMP-synkronointi',
            direction: 'Suunta',
            cmp_to_grc: 'CMP → GRC',
            grc_to_cmp: 'GRC → CMP',
            conflict: 'Ristiriita',
            resolve_conflict: 'Ratkaise ristiriita',
            no_events: 'Ei synkronointitapahtumia.',
            resolve_success: 'Synkronointiristiriita ratkaistu.',
            create_success: 'Synkronointitapahtuma tallennettu.',
            page_title: 'CMP Synkronoi',
            detect_drift: 'Tunnista ristiriidat',
            detecting: 'Havaitaan...',
            drift_result: '{detected} ristiriitaa havaittu / {total} verratusta tarkoituksesta.',
            no_drift: 'Uusia ristiriitoja ei havaittu.',
            drift_error: 'Ristiriitatunnistuksen suorittaminen epäonnistui.',
            resolve_modal_title: 'Ratkaise ristiriita',
            resolve_field: 'Kenttä',
            resolve_grc_value: 'GRC-arvo',
            resolve_cmp_value: 'CMP Arvo',
            accept_cmp: 'Hyväksy CMP-arvo',
            accept_cmp_desc: 'Päivitä GRC vastaamaan CMP - CMP arvo muuttuu uudeksi GRC-arvoksi.',
            keep_grc: 'Säilytä GRC-arvo',
            keep_grc_desc: 'Sulje tämä ristiriita muuttamatta GRC:tä – ilmoita CMP:n omistajalle tasaamaan.',
            resolve_applied: 'Konflikti ratkaistu. GRC:n tarkoitus päivitetty vastaamaan CMP.',
        },
    },
    assessments: {
        /*
         * Sprint 65.4.0 (AC5) — the DPIA review-reminder task body.
         *
         * It used to be an English sentence concatenated inside
         * `dpia-review-scheduler-service.ts`, asserting a "6-month review" over a
         * record free to declare any cadence at all. The period is gone from the
         * wording because the record owns it, and the string lives here so a
         * Vietnamese tenant does not read one English line in its task list.
         */
        review_reminder: {
            task_title: 'DPIA review due: {title}',
            task_description: 'The review scheduled on this assessment is due on {date}. Confirm the safeguards remain adequate, or file an updated assessment.',
        },
        // ── Sprint 65.4.0 (AC2) — the operator-recorded commencement moment ──
        //
        // Shared by the DPIA and cross-border transfer surfaces: one fact, one
        // vocabulary. The undated wording is deliberately explicit that a
        // lifecycle timestamp is NOT the answer, because a blank date field
        // reads as "nobody filled this in" while the truth is "the clock cannot
        // lawfully start yet".
        commencement: {
            section_title_dpia: 'When the processing began',
            section_title_transfer: 'When the transfer began',
            loading: 'Loading the recorded moment',
            load_failed: 'The recorded commencement moment could not be loaded. Refresh the page to try again.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retry_subject: 'the recorded commencement moment',
            undated_label: 'Statutory clock undated',
            undated_icon: 'Undated',
            undated_reason: 'No one has recorded when this began, so the statutory period has nothing lawful to count from. It is never counted from the date this record was created, approved or last edited — those are the moments somebody opened a form here, not the moment the activity started.',
            recorded_label: 'Statutory clock anchored',
            recorded_icon: 'Anchored to a recorded moment',
            occurred_at_label: 'Began',
            recorded_by_label: 'Stated by',
            recorded_at_label: 'Stated on',
            source_field_label: 'Recorded in',
            correction_note_label: 'Reason for the restatement',
            instant_label: 'Date and time it began',
            instant_hint: 'Entered in your own time zone. The statutory period is counted from this moment, not from when you enter it.',
            note_label: 'Why is this being restated?',
            note_hint: 'Required. The record has to be able to explain why its due date moved.',
            save: 'Record the moment',
            save_correction: 'Correct the moment',
            saving: 'Saving',
            saved: 'Commencement moment recorded',
            saved_correction: 'Commencement moment corrected',
            saved_unchanged: 'That is already what this record states. Nothing was changed.',
            error_not_iso: 'Enter a full date and time. The moment is stored as an exact instant, so a time without a time zone cannot be accepted.',
            error_future: 'That moment is in the future. A statutory period cannot be counted from something that has not happened yet.',
            error_note_required: 'This record already states when it began. Say why you are restating it before saving.',
            error_note_not_correction: 'Nothing has been recorded here yet, so there is nothing to correct. Remove the note and record the moment.',
            error_generic: 'The commencement moment could not be recorded.',
        },
        // ── Sprint 65.7.0 — the two moments a FILED dossier carries ──
        //
        // Shared by the DPIA and cross-border transfer surfaces, like the
        // commencement vocabulary above: one fact, one wording. The first filing is
        // write-once, so this block carries NO edit copy for it — a "Change the
        // filing date" control would offer an act the server refuses, and the
        // sentence that explains why is the affordance instead.
        dossier_update: {
            section_title_dpia: 'The dossier filed for this assessment',
            section_title_transfer: 'The dossier filed for this transfer',
            loading: 'Loading the recorded dossier moments',
            load_failed: 'The recorded dossier moments could not be loaded. Refresh the page to try again.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retry_subject: 'the recorded dossier moments',
            filing_heading: 'First filing',
            filing_absent_label: 'No first filing recorded',
            filing_absent_icon: 'No filing recorded',
            filing_absent_reason: 'No one has recorded when this dossier was first filed, so anything counted from that filing has nothing to count from. It is never counted from the date this record was created, approved or last edited — a dossier is filed by a person, on a day, outside this platform, and told to the platform afterwards.',
            filing_recorded_label: 'First filing recorded',
            filing_recorded_icon: 'Anchored to a recorded filing',
            filing_write_once_notice: 'Recorded once, and never moved. The update cycle is counted from the first filing, so changing it here would silently move every date derived from it. If the dossier was filed again, record what changed in it below.',
            filed_at_label: 'Filed on',
            filing_instant_label: 'Date and time it was first filed',
            filing_instant_hint: 'Entered in your own time zone. This is the moment the dossier was filed, not the moment you enter it here.',
            filing_save: 'Record the first filing',
            filing_saving: 'Saving',
            filing_saved: 'First filing recorded',
            filing_saved_unchanged: 'That is already what this record states. Nothing was changed.',
            change_heading: 'Change to the registered particulars',
            change_blocked_label: 'Nothing filed yet',
            change_blocked_reason: 'There are no registered particulars to change until a dossier has been filed. Record the first filing above, then record what changed in it.',
            change_absent_label: 'No change recorded',
            change_absent_reason: 'The dossier has been filed, and no one has recorded a change to the particulars registered in it.',
            change_recorded_label: 'Change recorded',
            change_latest_notice: 'The most recent change stands here. Recording another replaces it, and each statement is kept as its own fact in the record history.',
            changed_at_label: 'Changed on',
            change_description_value_label: 'What changed',
            change_instant_label: 'Date and time the particulars changed',
            change_instant_hint: 'Entered in your own time zone.',
            change_description_label: 'What changed?',
            change_description_hint: 'Required. Say what changed in your own words. Which changes oblige an update is stated by the frameworks you have installed, and the person acting on it needs your account of what happened.',
            change_note_label: 'Why is this being restated?',
            change_note_hint: 'Optional. Use it when this statement replaces one that already stood.',
            change_save: 'Record the change',
            change_save_restate: 'Replace the recorded change',
            change_saving: 'Saving',
            change_saved: 'Change to the registered particulars recorded',
            change_saved_unchanged: 'That is already what this record states. Nothing was changed.',
            recorded_by_label: 'Stated by',
            recorded_at_label: 'Stated on',
            source_field_label: 'Recorded in',
            correction_note_label: 'Reason for the restatement',
            error_not_iso: 'Enter a full date and time. The moment is stored as an exact instant, so a time without a time zone cannot be accepted.',
            error_future: 'That moment is in the future. Nothing can be counted from something that has not happened yet.',
            error_already_recorded: 'This record already states when its dossier was first filed, and that statement cannot be moved — every date counted from it would move too. If the dossier was filed again, record what changed in it instead.',
            error_before_first_filing: 'No dossier has been filed for this record yet, so there are no registered particulars to change. Record the first filing first.',
            error_description_required: 'Say what changed. A change with no account of what changed cannot be acted on by the person who has to review it.',
            error_generic: 'The dossier moment could not be recorded.',
        },
        dossier_preflight: {
            advisory_notice: 'Tämä valmiusraportti on neuvoa-antava. Se ei hyväksy, kokoa, muodosta, toimita eikä lähetä asiakirja-aineistoa.',
            run_label: 'Juokse ennen lentoa',
            running: 'Käynnissä ennen lentoa…',
            assemble_label: 'Automaattisesti koottava paketti',
            assembling: 'Kokoonpano…',
            verdict_label: 'Tuomio ennen lentoa',
            tokens_label: '≈ {count}-merkkejä',
            remediation_label: 'Korjaus',
            no_gaps: 'Lentoa edeltäviä aukkoja ei havaittu. Arkistointi on valmis koottavaksi.',
            override_label: 'Ohita lohkon päätös – kokoonpano on tarkastusleimattu.',
            accept_label: 'Hyväksy raportti',
            reject_label: 'Hylkää raportti',
            assembled_label: 'Lähetyspaketti koottu',
            package_id_label: 'Paketin tunnus',
            package_filename_label: 'Tiedostonimi',
            package_hash_label: 'Paketin hash',
            evidence_count_label: '{count} todisteita niputettuna',
            error_generic: 'Ei voitu suorittaa ennen lentoa tai koota pakettia. Yritä uudelleen tai tarkista arkistoinnin tila.',
        },
        errors: {
            fetch: 'Arviointien lataaminen epäonnistui.',
            save: 'Arvioinnin tallentaminen epäonnistui.',
            fetch_gap_report_failed: 'Puuteraportin hakeminen epäonnistui.',
            fetch_transfers_failed: 'Siirtojen hakeminen epäonnistui.',
            save_transfer_failed: 'Siirron tallentaminen epäonnistui.',
            submit_review_failed: 'Tarkistettavaksi lähettäminen epäonnistui.',
            process_decision_failed: 'Päätöksen käsittely epäonnistui.',
            deprecate_failed: 'Vanhentaminen epäonnistui.',
            evaluate_risk_failed: 'Riskin arviointi epäonnistui.',
            fetch_safeguards_failed: 'Suojatoimien hakeminen epäonnistui.',
            save_safeguard_failed: 'Suojatoimen tallentaminen epäonnistui.',
            update_safeguard_status_failed: 'Suojatoimen tilan päivittäminen epäonnistui.',
        },
        nav: {
            dpias: 'Tietosuojan vaikutustenarvioinnit',
            transfers: 'Siirtoarvioinnit',
            dashboard: 'Yhteenveto',
            gap_report: 'Puuteraportti',
        },
        risk_level_critical: 'Kriittinen riski',
        risk_level_high: 'Korkea riski',
        risk_level_medium: 'Keskitason riski',
        risk_level_low: 'Matala riski',
        severity_critical: 'Kriittinen vakavuus',
        severity_high: 'Korkea vakavuus',
        severity_medium: 'Keskitason vakavuus',
        severity_low: 'Matala vakavuus',
        overdue: 'Myöhässä',
        framework_filter_label: 'puitteet',
        aria_filter_by_framework: 'Suodata kehyksen mukaan',
        dashboard: {
            title: 'Arviointien yhteenveto',
            page_title: 'Arviointien hallintapaneeli',
            announce_loaded: 'Arviointien hallintapaneeli ladattu',
            announce_chart_view: 'Näytetään kaavionäkymä',
            announce_table_view: 'Näytetään taulukonäkymä',
            aria_metrics_table: 'Arviointien hallintapaneelin mittarit',
            metric_col: 'Mittari',
            value_col: 'Arvo',
            dpia_prefix: 'DPIA',
            transfer_prefix: 'Siirto',
            risk_prefix: 'Riski',
            aria_dpias_by_status: 'DPIAt tilan mukaan',
            aria_transfers_by_status: 'Siirrot tilan mukaan',
            aria_top_risks: 'Suurimmat riskit',
            aria_top_risks_table: 'Suurimmat riskit DPIOissa',
            overdue_dpias_attention: '{count} myöhässä olevaa DPIA-arviointia vaatii huomiota',
            no_overdue_dpias: 'Ei myöhässä olevia DPIA-arviointeja',
            overdue_transfers_attention: '{count} myöhässä olevaa siirtoarviointia vaatii huomiota',
            no_overdue_transfers: 'Ei myöhässä olevia siirtoarviointeja',
            dpias_overview: 'Vaikutustenarviointien yleiskatsaus',
            transfers_overview: 'Siirtoarviointien yleiskatsaus',
            overdue_items: 'Myöhästyneet kohteet',
            overdue_dpias: 'Myöhästyneet vaikutustenarvioinnit',
            overdue_transfers: 'Myöhästyneet siirtoarvioinnit',
            count: 'Lukumäärä',
            no_data: 'Ei tiedot available. löytynyt',
            no_risks: 'Ei riskit found. löytynyt',
            no_dashboard_data: 'Ei kojelauta tiedot available. löytynyt',
            fetch_error: 'Epäonnistui: fetch kojelauta tiedot. Yritä uudelleen.',
        },
        dpias: {
            // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
            // no locale declared, so each fell through to its hardcoded English third
            // argument and rendered English in all 16 locales. The two DPIA row-action
            // tooltips were worse: their fallbacks are themselves translated keys, so the
            // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
            // action read 'Approve/Reject' where it intends 'Review'.
            continue: 'Continue',
            review: 'Review',
            section_basic_info: 'Basic Information',
            page_title: 'DPIAt',
            announce_submitted_for_review: 'DPIA lähetetty tarkistettavaksi',
            announce_deprecated: 'DPIA vanhentunut',
            announce_risks_loaded: '{count} riski(ä) ladattu',
            announce_mitigations_loaded: '{count} toimenpide(ttä) ladattu',
            announce_risk_deleted: 'Riski poistettu',
            announce_mitigation_status_updated: 'Toimenpiteen tila päivitetty',
            aria_filter: 'Suodata DPIAt',
            aria_filter_by_status: 'Suodata tilan mukaan',
            aria_filter_by_lead: 'Suodata arvioinnin vetäjän mukaan',
            aria_table: 'Tietosuojan vaikutustenarvioinnit',
            announce_found: '{count} DPIA(ta) löydetty',
            announce_decision: 'DPIA {decision}',
            mitigation_start: 'Aloita',
            mitigation_mark_implemented: 'Merkitse toteutetuksi',
            mitigation_verify: 'Vahvista',
            title: 'Tietosuojan vaikutustenarvioinnit',
            new: 'Uusi vaikutustenarviointi',
            edit: 'Muokkaa vaikutustenarviointia',
            dpia_title: 'Otsikko',
            edit_dpia_aria: 'Edit DPIA: {title}',
            approve_or_reject_aria: 'Approve or reject: {title}',
            more_actions_for_aria: 'More actions for {title}',
            risk_details_aria: 'Risk details for {title}',
            edit_risk_aria: 'Edit risk: {title}',
            delete_risk_aria: 'Delete risk: {title}',
            mitigation_details_aria: 'Mitigation details for {title}',
            edit_mitigation_aria: 'Edit mitigation: {title}',
            dossier_filing_details_aria: 'Dossier filing details for {title}',
            activity: 'Käsittelytoimi',
            risk_score: 'Riskipisteet',
            risks: 'Riskit',
            mitigations: 'Lieventävät toimet',
            add_risk: 'Lisää riski',
            add_mitigation: 'Lisää lieventävä toimi',
            necessity: 'Tarpeellisuus / oikeasuhtaisuus',
            likelihood: 'Todennäköisyys',
            impact: 'Vaikutus',
            severity_label: 'Vakavuus',
            purpose: 'Tarkoitus',
            consultation_notes: 'Consultation Huomautukset',
            dpo_opinion: 'Tietosuojavastaavan lausunto',
            processing_activity_ids: 'Käsitellään Aktiviteetti Tunniste (comma-separated)',
            system_ids: 'Järjestelmä Tunniste (comma-separated)',
            vendor_ids: 'Toimittajatunnukset (pilkuilla erotettu)',
            data_types: 'Tiedot Tyypit (comma-separated)',
            data_types_short: 'Tiedot Tyypit',
            data_subjects: 'Tiedot Subjects (comma-separated)',
            assessment_lead_id: 'Arviointi Lead Tunniste',
            assessment_lead: 'Arviointi Lead',
            dpo_id: 'DPO Tunniste',
            reviewer_ids: 'Arvioija Tunniste (comma-separated)',
            approval_mode: 'Hyväksyntätila',
            approval_all: 'All',
            approval_any: 'Any',
            require_dpo_signoff: 'Vaadi tietosuojavastaavan hyväksyntä',
            review_frequency_days: 'Review cadence (days)',
            review_frequency_days_hint: 'Optional. Days between reviews of this DPIA once it is approved. Leave empty and the DPIA gets no review date.',
            error_dpo_required: 'This DPIA requires a DPO sign-off. Choose the DPO before submitting it for review.',
            error_dpo_invalid: 'The DPO named on this DPIA is not an active user of this organisation. Choose an active DPO before submitting it for review.',
            error_dpo_cannot_approve: 'The DPO named on this DPIA does not hold the permission to approve DPIAs, so the sign-off could never be given. Choose a DPO who holds it before submitting it for review.',
            decision_awaiting_dpo: 'Your decision is recorded. The DPIA stays in review until its DPO signs it off.',
            withdraw_review: 'Withdraw from review',
            withdraw_review_hint: 'Return this DPIA to draft so it can be edited',
            withdraw_review_error: 'Failed to withdraw DPIA from review. Please try again.',
            announce_withdrawn: 'DPIA withdrawn from review',
            review_date: 'Katselmointia Päivämäärä',
            submit_for_review: 'Lähetä for Katselmointia',
            approve_reject: 'Hyväksy/Hylkää',
            reopen: 'Reopen as draft',
            reopen_hint: 'Start a new review round. The rejection is recorded on the record and no longer counts.',
            reopen_error: 'Could not reopen the DPIA. Please try again.',
            announce_reopened: 'DPIA reopened as a draft',
            decision_named_reviewers_note: 'This DPIA names its reviewers. A decision from anyone else is recorded in the approval log, but counts toward neither approval nor rejection.',
            decision_not_counted: 'Your decision was recorded, but it does not count: only the named reviewers decide this DPIA.',
            approval_incomplete_badge: 'Approval incomplete',
            approval_incomplete_note: 'This DPIA is marked approved, but its approval log does not show an approval from the named reviewers. It keeps its status. Whether to review it again is for your organisation to decide.',
            approval_rounds_count: 'Earlier review rounds recorded on this record: {count}',
            deprecate: 'Vanhenna',
            deprecated: 'Vanhentunut',
            hide_risks: 'Piilota Riskit',
            hide_mitigations: 'Piilota Lieventämiset',
            comments: 'Kommentit',
            submit_decision: 'Lähetä Päätös',
            category_label: 'Kategoria',
            category_data_breach: 'Tiedot Tietoturvaloukkaus',
            category_unauthorized_access: 'Unauthorized Pääsy',
            category_function_creep: 'Toimintojen laajeneminen',
            category_re_identification: 'Uudelleentunnistaminen',
            category_discrimination: 'Syrjintä',
            category_surveillance: 'Valvonta',
            category_other: 'Muu',
            affected_rights: 'Vaikutetut oikeudet (pilkuilla erotettu)',
            no_risks: 'Ei riskit found. löytynyt',
            no_mitigations: 'Ei mitigations found. löytynyt',
            no_dpias: 'Ei dpia found. löytynyt',
            mitigation_type: 'Tyyppi',
            mitigation_technical: 'Tekninen',
            mitigation_organizational: 'Organisatorinen',
            mitigation_contractual: 'Sopimukseen perustuva',
            risk_ids: 'Riski Tunniste (comma-separated)',
            responsible_party: 'Vastuutaho',
            responsible_party_placeholder: 'Tai syötä nimi manuaalisesti',
            affected_rights_placeholder: 'Valitut oikeudet näkyvät tässä',
            affected_rights_freetext_placeholder: 'Syötä vaikutetut oikeudet (pilkulla erotettuna)',
            responsible: 'Vastuullinen',
            target_date: 'Tavoite Päivämäärä',
            created: 'Luotu',
            fetch_error: 'Epäonnistui: fetch DPIA. Yritä uudelleen.',
            save_error: 'Epäonnistui: save DPIA. Yritä uudelleen.',
            submit_review_error: 'Epäonnistui: submit DPIA for katselmointi. Yritä uudelleen.',
            decision_error: 'Epäonnistui: process decision. Yritä uudelleen.',
            deprecate_error: 'Epäonnistui: deprecate DPIA. Yritä uudelleen.',
            fetch_risks_error: 'Epäonnistui: fetch riskit. Yritä uudelleen.',
            save_risk_error: 'Epäonnistui: save riski. Yritä uudelleen.',
            delete_risk_error: 'Epäonnistui: delete riski. Yritä uudelleen.',
            fetch_mitigations_error: 'Epäonnistui: fetch mitigations. Yritä uudelleen.',
            save_mitigation_error: 'Epäonnistui: save mitigation. Yritä uudelleen.',
            update_mitigation_status_error: 'Lieventämistoimenpiteen tilan päivittäminen epäonnistui. Yritä uudelleen.',
            link_form_template: 'Linkin sääntelylomakemalli',
            link_form_template_placeholder: 'Valitse DPIA-lomakemalli (valinnainen)',
            link_form_template_none: 'Ei mitään',
            linked_form: 'Linkitetty lomake',
            linked_form_creating: 'Luodaan linkitettyä lomaketta...',
            linked_form_error: 'Linkitetyn lomakkeen esiintymän luominen epäonnistui. DPIA luotiin ilman linkitettyä lomaketta.',
            view_linked_form: 'Näytä lomake',
            announce_created_with_form: 'DPIA luotu linkitetyllä lomake-instanssilla',
            creation_mode: 'DPIA Luontitila',
            mode_standard: 'Vakio DPIA',
            mode_template: 'Käytä sääntelylomakemallia',
            select_form_template: 'Valitse Lomakemalli',
            create_and_fill_form: 'Luo ja täytä lomake',
            template_sections_title: 'Mallin osiot',
            field_singular: 'kenttä',
            field_plural: 'kentät',
            no_sections: 'Tästä mallista ei löytynyt osioita.',
            template_mode_hint: 'Luomisen jälkeen sinut ohjataan täyttämään koko sääntelylomake.',
            linked_dossier_filings: 'Linkitetyt dossier-arkistoinnit',
            no_linked_dossier_filings: 'Ei dossier-arkistointeja linkitetty tähän DPIA:han.',
            view_dossier_filing: 'Näytä arkistointi',
            hide_dossier_filings: 'Piilota dossier-arkistoinnit',
            supporting_evidence: 'Tukevat todisteet',
            hide_supporting_evidence: 'Piilota todisteet',
            no_supporting_evidence: 'Ei liitteenä todisteita.',
            upload_supporting_evidence: 'Lataa tukevat todisteet',
            view_form_responses: 'Näytä lomakevastaukset',
            regulatory_framework: 'Sääntelykehys',
            all_frameworks: 'Kaikki kehykset',
            no_templates_for_market: 'Valitulle markkinalle ei ole malleja saatavilla. Valitse toinen markkina tai luo malli.',
            affected_rights_freetext_hint: 'No framework rights available — enter manually (comma-separated)',
            ai_draft_eyebrow: 'AI assist',
            deprecate_hint: 'Retire this DPIA without deleting it',
            inherent_risk_level: 'Inherent Risk',
            mitigation_contractual_hint: 'DPA clauses, SCCs, contractual obligations',
            mitigation_description_placeholder: 'Describe the mitigation measure in detail',
            mitigation_organizational_hint: 'Policies, training, procedures, staff awareness',
            mitigation_technical_hint: 'Encryption, pseudonymisation, access controls, system hardening',
            mitigation_title_placeholder: 'e.g. Encrypt data at rest',
            no_risks_to_link: 'No risks found for this DPIA. Add risks first.',
            score_high: 'High',
            score_low: 'Low',
            score_medium: 'Medium',
            score_very_high: 'Very High',
            score_very_low: 'Very Low',
            section_assessment_details: 'Assessment Details',
            section_implementation: 'Implementation',
            section_processing: 'Processing Details',
            section_scoring: 'Risk Scoring',
            section_team: 'Team & Approval',
            selected_rights_count: 'Selected',
            submit_for_review_hint: 'Advance this DPIA to review',
        },
        transfers: {
            // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
            // no locale declared, so each fell through to its hardcoded English third
            // argument and rendered English in all 16 locales. The two DPIA row-action
            // tooltips were worse: their fallbacks are themselves translated keys, so the
            // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
            // action read 'Approve/Reject' where it intends 'Review'.
            description_placeholder: 'Brief summary of this transfer',
            title_placeholder: 'e.g. EU–US CRM Data Transfer',
            vendor_id_placeholder: 'Vendor ID (optional)',
            page_title: 'Tiedonsiirrot',
            announce_found: '{count} siirtoarviointi(a) löydetty',
            announce_submitted_for_review: 'Siirto lähetetty tarkistettavaksi',
            announce_deprecated: 'Siirto vanhentunut',
            announce_decision: 'Siirto {decision}',
            announce_risk_eval_completed: 'Riskiarviointi valmis',
            announce_safeguard_status_updated: 'Suojatoimenpiteen tila päivitetty',
            announce_safeguards_loaded: '{count} suojatoimenpide(ttä) ladattu',
            aria_filter: 'Suodata siirtoarviointeja',
            aria_filter_by_status: 'Suodata tilan mukaan',
            aria_filter_by_mechanism: 'Suodata siirtomekanismin mukaan',
            aria_table: 'Tiedonsiirtoarvioinnit',
            title: 'Rajat ylittävien siirtojen arvioinnit',
            new: 'Uusi siirto',
            edit: 'Muokkaa siirtoa',
            name: 'Nimi',
            from_country: 'Lähtömaa',
            to_country: 'Kohdemaa',
            safeguards: 'Suojatoimet',
            risk_level: 'Riskitaso',
            assessment_notes: 'Arvioinnin muistiinpanot',
            mechanism: 'Mekanismi',
            transfer_mechanism: 'Siirtomekanismi',
            transfer_mechanism_detail: 'Siirtomekanismin yksityiskohta',
            all_mechanisms: 'Kaikki mekanismit',
            mechanism_other: 'Muu',
            data_types: 'Tiedot Tyypit (comma-separated)',
            data_subjects: 'Tiedot Subjects (comma-separated)',
            purpose: 'Tarkoitus',
            recipient_name: 'Recipient Nimi',
            create_success: 'Siirtoarviointi luotu.',
            update_success: 'Siirtoarviointi päivitetty.',
            recipient_details: 'Vastaanottajan tiedot',
            recipient_details_placeholder: 'Vastaanottajan rooli, sijainti ja järjestelmät sekä edelleensiirron tiedot',
            vendor_id: 'Toimittajatunnus',
            processing_activity_ids: 'Käsitellään Aktiviteetti Tunniste (comma-separated)',
            reviewer_ids: 'Arvioija Tunniste (comma-separated)',
            approval_mode: 'Hyväksyntätila',
            approval_all: 'All',
            approval_any: 'Any',
            require_dpo_signoff: 'Vaadi tietosuojavastaavan hyväksyntä',
            review_date: 'Katselmointia Päivämäärä',
            created: 'Luotu',
            deprecated: 'Vanhentunut',
            submit_for_review: 'Lähetä for Katselmointia',
            approve_reject: 'Hyväksy/Hylkää',
            reopen: 'Reopen as draft',
            reopen_hint: 'Start a new review round. The rejection is recorded on the record and no longer counts.',
            reopen_error: 'Could not reopen the transfer assessment. Please try again.',
            announce_reopened: 'Transfer assessment reopened as a draft',
            redetermine_mechanism: 'Re-determine mechanism',
            redetermine_mechanism_title: 'Re-determine the transfer mechanism',
            redetermine_mechanism_intro: 'This record is {status}, so it cannot be edited, but its transfer mechanism is waiting for a person to re-determine it. Choose a mechanism an installed framework declares. Only the mechanism is saved; the status and the approval record do not change.',
            redetermine_choose_first: 'Choose a transfer mechanism first.',
            redetermine_success: 'Transfer mechanism re-determined.',
            redetermine_error: 'Could not re-determine the transfer mechanism. Please try again.',
            withdraw_review: 'Withdraw from review',
            withdraw_review_hint: 'Return this transfer assessment to draft. The review round is closed and its decisions no longer count. Use this when a named reviewer can no longer decide.',
            withdraw_review_error: 'Could not withdraw the transfer assessment from review. Please try again.',
            announce_withdrawn: 'Transfer assessment withdrawn from review',
            decision_named_reviewers_note: 'This transfer assessment names its reviewers. A decision from anyone else is recorded in the approval log, but counts toward neither approval nor rejection.',
            decision_not_counted: 'Your decision was recorded, but it does not count: only the named reviewers decide this transfer assessment.',
            approval_incomplete_badge: 'Approval incomplete',
            approval_incomplete_note: 'This transfer assessment is marked approved, but its approval log does not show an approval from the named reviewers. It keeps its status, but a cross-border identity exchange flow cannot rest on it. Whether to review it again is for your organisation to decide.',
            approval_rounds_count: 'Earlier review rounds recorded on this record: {count}',
            deprecate: 'Vanhenna',
            risk_eval: 'Riski Eval',
            hide_safeguards: 'Piilota Suojatoimet',
            mechanism_proof: 'Mekanismin todiste',
            hide_mechanism_proof: 'Piilota mekanismin todiste',
            mechanism_proof_hint: 'Upload the signed document the transfer rests on (for example the contract, or the impact-assessment dossier your installed frameworks require) to prove this transfer assessment.',
            no_mechanism_proof: 'Ei kiinnitetty mekanismitodistusta.',
            upload_mechanism_proof: 'Lataa mekanismin todiste',
            comments: 'Kommentit',
            submit_decision: 'Lähetä Päätös',
            data_sensitivity: 'Tiedot Sensitivity (1-5)',
            country_risk: 'Maa Riski (1-5)',
            safeguard_adequacy: 'Suojatoimi Adequacy (1-5)',
            volume_factor: 'Tilavuustekijä (1-5)',
            evaluate: 'Arvioi',
            add_safeguard: 'Lisää suojatoimi',
            responsible_party: 'Vastuutaho',
            responsible_party_placeholder: 'Tai syötä nimi manuaalisesti',
            responsible: 'Vastuullinen',
            implementation_date: 'Implementation Päivämäärä',
            implementation: 'Toteutus',
            expiry_date: 'Expiry Päivämäärä',
            expiry: 'Vanheneminen',
            expire: 'Vanhenna',
            activate_unavailable_expired: 'An expired safeguard cannot be reactivated. Record a new safeguard instead.',
            no_safeguards: 'Ei safeguards found. löytynyt',
            no_transfers: 'Ei transfer arviointi found. löytynyt',
            fetch_error: 'Epäonnistui: fetch transfer arviointi. Yritä uudelleen.',
            save_error: 'Epäonnistui: save transfer arviointi. Yritä uudelleen.',
            submit_review_error: 'Epäonnistui: submit for katselmointi. Yritä uudelleen.',
            decision_error: 'Epäonnistui: process decision. Yritä uudelleen.',
            deprecate_error: 'Epäonnistui: deprecate transfer. Yritä uudelleen.',
            risk_eval_error: 'Epäonnistui: evaluate riski. Yritä uudelleen.',
            fetch_safeguards_error: 'Epäonnistui: fetch safeguards. Yritä uudelleen.',
            save_safeguard_error: 'Epäonnistui: save safeguard. Yritä uudelleen.',
            update_safeguard_status_error: 'Suojatoimenpiteen tilan päivittäminen epäonnistui. Yritä uudelleen.',
            safeguard_type_encryption_in_transit: 'Salaus in Transit',
            safeguard_type_encryption_at_rest: 'Salaus at Rest',
            safeguard_type_access_controls: 'Pääsynhallintatoimet',
            safeguard_type_contractual_clauses: 'Contractual Lausekkeet',
            safeguard_type_data_minimization: 'Tiedot Minimization',
            safeguard_type_pseudonymization: 'Pseudonymisointi',
            safeguard_type_audit_rights: 'Tarkastus Oikeudet',
            safeguard_type_breach_notification: 'Tietoturvaloukkaus Ilmoitus',
            safeguard_type_data_localization: 'Tiedot Localization',
            safeguard_type_other: 'Muu',
            activate: 'Aktivoi',
            all_statuses: 'Kaikki tilat',
            any: 'Any',
            mechanism_detail: 'Mekanismin yksityiskohta',
            safeguard_types: {
                encryption_in_transit: 'Salaus siirron aikana',
                encryption_at_rest: 'Salaus levossa',
                access_controls: 'Pääsynhallinta',
                contractual_clauses: 'Sopimuslausekkeet',
                data_minimization: 'Tietojen minimointi',
                pseudonymization: 'Pseudonymisointi',
                audit_rights: 'Tarkastusoikeudet',
                breach_notification: 'Tietomurtoilmoitus',
                data_localization: 'Tietojen lokalisointi',
                other: 'Muu',
            },
            activity_ids_placeholder: 'Activity IDs (optional)',
            deprecate_hint: 'Retire this assessment without deleting it',
            file_dossier: 'File Dossier',
            file_dossier_hint: 'Start a regulatory filing from this assessment',
            mark_implemented: 'Implement',
            mark_verified: 'Verify',
            mechanism_detail_placeholder: 'e.g. the contract or document the transfer rests on',
            purpose_placeholder: 'e.g. CRM, analytics, support',
            csv_hint: '(comma-separated)',
            data_subjects_placeholder: 'e.g. employees, customers',
            data_types_placeholder: 'e.g. name, email, phone',
            from_country_placeholder: 'e.g. EU',
            to_country_placeholder: 'e.g. US',
            recipient_placeholder: 'e.g. Salesforce Inc.',
            reviewers_placeholder: 'Select reviewers…',
            risk_eval_hint: 'Score data sensitivity, country & safeguards',
            section_mechanism: 'Mechanism & Data',
            section_review: 'Review & Approval',
            section_route: 'Transfer Route',
        },
        gap_report: {
            page_title: 'Arviointien aukkoselvitys',
            announce_loaded: 'Aukkoselvitys ladattu. {count} aukko(a) tunnistettu',
            aria_activities_without_dpia: 'Toiminnot ilman DPIA-arviointia',
            aria_activities_unclassified: 'Toimet, joiden DPIA-vaatimusta ei ole luokiteltu',
            activities_unclassified_section_title: 'Toimet, joiden DPIA-vaatimusta ei ole luokiteltu',
            activities_unclassified_description: 'Näitä toimia ei ole arvioitu sen määrittämiseksi, vaaditaanko DPIA lain mukaan. Luokittele jokainen signaalin poistamiseksi.',
            activities_unclassified_empty: 'Kaikki aktiiviset toimet on luokiteltu.',
            aria_unmitigated_risks: 'Käsittelemättömät korkeat ja kriittiset riskit',
            aria_transfers_without_safeguards: 'Siirrot ilman riittäviä suojatoimenpiteitä',
            aria_expired_assessments: 'Vanhentuneet arvioinnit jotka vaativat uusintaa',
            title: 'Arvioinnin puuteraportti',
            no_dpia: 'Toimet ilman vaikutustenarviointia',
            no_assessment: 'Siirrot ilman arviointia',
            unmitigated: 'Vaikutustenarvioinnit, joissa on lieventämättömiä riskejä',
            activity_id: 'Aktiviteettitunnus',
            dpia_id: 'DPIA Tunniste',
            assessment_id: 'Arviointi Tunniste',
            expired_assessments: 'Vanhentunut Arvioinnit',
            no_gaps_activities: 'Ei puute found. all activities have dpia. löytynyt',
            no_unmitigated_risks: 'Ei unmitigated high/critical riskit found. löytynyt',
            no_gaps_safeguards: 'Ei puute found. all transfers have adequate safeguards. löytynyt',
            no_expired: 'Ei expired arviointi found. löytynyt',
            no_report_data: 'Ei puute raportti tiedot available. löytynyt',
            fetch_error: 'Epäonnistui: fetch puute raportti. Yritä uudelleen.',
            no_expired_assessments: 'No expired arviointi.',
            no_gaps_dpia: 'Ei dpia puute found. löytynyt',
            no_data: 'Ei tiedot available. löytynyt',
            all_clear: 'No compliance gaps detected.',
            list_truncated_notice: 'Only the first 50 activities are listed; more match. Resolve these, then reload the report to see the rest.',
            subtitle: 'Identify compliance gaps across DPIAs, risks, transfers, and assessments.',
        },
        dossier_filings: {
            // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
            // no locale declared, so each fell through to its hardcoded English third
            // argument and rendered English in all 16 locales. The two DPIA row-action
            // tooltips were worse: their fallbacks are themselves translated keys, so the
            // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
            // action read 'Approve/Reject' where it intends 'Review'.
            aria_filters: 'Filter dossier filings',
            description_placeholder: 'Optional summary of this filing',
            title_placeholder: 'e.g. DPIA Filing – Annex I',
            page_title_detail: 'Dossierin jättämisen yksityiskohdat',
            page_title_list: 'Dossierin jättämistapaukset',
            announce_filings_found: '{count} jättämis(tä) löydetty',
            announce_results: '{count} tulosta',
            aria_filter_by_status: 'Suodata tilan mukaan',
            title: 'Asiakirjojen arkistointiasiat',
            nav_label: 'Asiakirja-aineistot',
            create: 'Luo asiakirja-aineisto',
            form_type: 'Lomakkeen tyyppi',
            phu_luc_i_label: 'Standard assessment workflow (internal)',
            phu_luc_ii_label: 'Cross-border / high-risk assessment workflow (internal)',
            dpia_reference: 'DPIA Viite',
            transfer_reference: 'Transfer Assessment Reference',
            transfer_select_placeholder: 'Link to a transfer assessment',
            arrived_parent_heading: 'Filing for',
            prior_filing_heading: 'This organisation has already filed this dossier',
            prior_filing_body: 'The installed framework provides for one dossier of this kind per organisation, updated afterwards ({citation}). This organisation already filed one: {filing}. For a new purpose or another change to its content, start the update on this assessment instead. You can still open a first filing, for example to file again after the authority assessed the earlier dossier as not meeting the requirements.',
            prior_filing_start_update: 'Start the update: {form}',
            prior_filing_no_update_form: 'No update form is installed for this kind of dossier.',
            arrived_parent_loading: 'Looking up the assessment record…',
            arrived_parent_unresolved: 'The assessment this filing was started from could not be found. It may have been deleted, or it may belong to another organisation. Choose a parent below before filing.',
            parent_none: 'No assessment linked',
            parent_unresolved: 'linked record not found ({id})',
            error_parent_required: 'Choose the assessment this filing discharges — either an impact assessment or a transfer assessment.',
            error_transfer_parent_not_found: 'The transfer assessment this filing names no longer exists. Pick another one, or reopen the filing from the transfer register.',
            error_dpia_parent_not_found: 'The impact assessment this filing names no longer exists. Pick another one.',
            submitted_date: 'Lähetyspäivämäärä',
            outcome: 'Tulos',
            parent_filing: 'Vanhempi',
            no_filings: 'Asiakirja-aineistoa ei löytynyt.',
            fetch_error: 'Asiakirja-aineiston nouto epäonnistui. Yritä uudelleen.',
            save_error: 'Asiakirja-aineiston luominen epäonnistui. Yritä uudelleen.',
            created: 'Asiakirja-aineisto luotu.',
            loaded: 'Asiakirja-aineisto ladattu.',
            not_found: 'Asiakirja-aineistoa ei löytynyt.',
            back_to_list: 'Takaisin luetteloon',
            section_assignments: 'Osion Tehtävät',
            assign_sections: 'Määritä osiot',
            section_id: 'osio',
            department: 'osasto',
            assignee: 'Valtuutettu',
            deadline: 'Määräaika',
            no_assignments: 'Ei vielä osiotehtäviä.',
            sections_assigned_msg: 'Osuudet määrätty.',
            assign_error: 'Osioiden määrittäminen epäonnistui. Yritä uudelleen.',
            lifecycle: 'Arkistoinnin elinkaari',
            submission: 'Lähetyksen tiedot',
            submission_channel: 'Lähetyskanava',
            confirmation_number: 'Vahvistusnumero',
            // Fix sprint #447 (round 2) — the Submit dialog offers the declared channels.
            select_channel: 'Select a channel',
            submission_note: 'This records the submission your organisation made. The platform files nothing with the authority.',
            channels_load_failed: 'The submission channels could not be read.',
            channel_refusal_no_framework: 'No installed framework declares an authority filing, so no submission can be recorded.',
            channel_refusal_no_channels: 'The filing framework declares no submission channels, so no submission can be recorded.',
            channel_refusal_no_authority: 'The filing framework names no authority that accepts filings, so no submission can be recorded.',
            status_submitted: 'Filed — no result recorded',
            submit_for_review: 'Lähetä tarkistettavaksi',
            submitted_for_review: 'Lähetetty sisäiseen tarkasteluun.',
            submit_review_error: 'Lähetys tarkistettavaksi epäonnistui. Yritä uudelleen.',
            legal_signoff: 'Laillinen allekirjoitus',
            legal_signoff_done: 'Oikeudellinen allekirjoitus suoritettu.',
            legal_signoff_error: 'Laillisen allekirjoituksen viimeistely epäonnistui. Yritä uudelleen.',
            approve: 'Approve internally',
            approved_msg: 'Filing approved internally. It has not been filed with the authority.',
            approve_error: 'Ilmoituksen hyväksyminen epäonnistui. Yritä uudelleen.',
            materialisation_status_column: 'Lähetys artefakti',
            materialisation_na: 'Ei sovellu',
            materialisation_pending: 'Odottaa',
            materialisation_success: 'Materialisoitunut',
            materialisation_failed: 'Epäonnistui',
            materialisation_banner_title: 'Lähetyksen artefaktia ei voitu luoda',
            materialisation_banner_body: 'The filing was approved internally, but the underlying dossier record could not be created. Cover-letter and submission exports are unavailable until you retry.',
            materialisation_retry_button: 'Yritä uudelleen',
            materialisation_retry_count: 'Aiemmat uudelleenyritykset:',
            materialisation_retry_success: 'Lähetetty artefakti toteutui onnistuneesti.',
            materialisation_retry_announce: 'Uudelleenyritys onnistui',
            filter_materialisation_failed: 'Näytä vain toteutusvirheet',
            column_title: 'Otsikko',
            column_form_type: 'Lomake',
            submit_to_mps: 'Record filing with the authority',
            submitted_to_mps_msg: 'Your organisation’s submission to the authority is recorded.',
            submit_mps_error: 'Failed to record the filing with the authority. Please try again.',
            supplement_request: 'Täydennyspyyntö',
            supplement_requests: 'Täydennyspyynnöt',
            supplement_response: 'Täydentävä vastaus',
            record_supplement: 'Tietueen täydennyspyyntö',
            supplement_recorded: 'Täydennyspyyntö kirjattu.',
            supplement_absence_declaring_framework_unreadable: 'No deadline: the framework that states the period could not be read.',
            supplement_absence_declaring_framework_not_in_force: 'No deadline: the framework that states the period is not yet in force.',
            supplement_absence_uncomputable_period: 'No deadline: the installed period cannot be counted as a date.',
            supplement_absence_multiple_candidate_clocks: 'No deadline: more than one installed period could apply and the platform does not choose between them. Enter the deadline stated in the authority’s letter.',
            supplement_absence_no_installed_clock: 'No deadline: no installed framework states a period for responding.',
            supplement_absence_receipt_not_evidenced: 'No deadline: the date the applicant received the authority’s result has not been recorded.',
            supplement_absence_declaring_framework_reference_mode: 'No deadline: the framework that states the period is installed as Reference only, so its clocks are not run.',
            supplement_absence_declaring_framework_not_operational: 'No deadline: the framework that states the period is superseded or retired, so it no longer sets a deadline.',
            supplement_deadline_warning_differs_from_installed: 'The date you entered falls on a different day from the one the installed period gives. The date from the authority’s letter stands.',
            supplement_deadline_warning_in_the_past: 'The date you entered was already past when it was recorded.',
            supplement_deadline_platform_estimate: 'Platform estimate recorded earlier: not a period stated by an installed framework or by the authority’s letter.',
            supplement_deadline_installed_due: 'The installed period gives {date}.',
            supplement_deadline_none: 'No deadline is recorded for this request.',
            supplement_deadline_source_unknown: 'The source of this deadline was not recorded',
            supplement_deadline_source_operator_anon: 'Stated in the authority’s letter; entered by a user',
            supplement_deadline_source_operator: 'Stated in the authority’s letter; entered by {user}',
            supplement_deadline_source_installed: 'Counted from the date of receipt, using the period in the installed framework',
            supplement_response_received: 'Responded',
            supplement_response_pending: 'Awaiting response',
            supplement_response_deadline: 'Response deadline',
            supplement_items_requested: 'Items requested',
            supplement_received_future: 'The date of receipt cannot be in the future.',
            supplement_deadline_help: 'A completion period is counted from a communication your organisation recorded, once you state that it asks for the dossier to be completed, or that it states the dossier is incomplete, invalid or not in accordance with the regulations. With a deadline stated in the authority’s letter, the platform records that deadline and who entered it. With neither, the request has no deadline and the page says why.',
            supplement_response_deadline_input: 'Deadline stated in the authority’s letter (optional)',
            supplement_received_at: 'Date the applicant received the authority’s result',
            supplement_items: 'Items the authority requested (one per line)',
            supplement_error: 'Täydennyspyynnön tallentaminen epäonnistui. Yritä uudelleen.',
            submit_supplement: 'Lähetä täydennysvastaus',
            supplement_submitted_msg: 'Täydennysvastaus lähetetty.',
            supplement_submit_error: 'Täydennysvastauksen lähettäminen epäonnistui. Yritä uudelleen.',
            response_status: 'Vastauksen tila',
            response_notes: 'Vastaushuomautukset',
            resubmit: 'Lähetä uudelleen',
            resubmitted_msg: 'Asiakirja lähetetty uudelleen.',
            resubmit_error: 'Asiakirjan uudelleenlähetys epäonnistui. Yritä uudelleen.',
            outcome_error: 'Tuloksen tallennus epäonnistui. Yritä uudelleen.',
            accepted: 'Hyväksytty',
            rejected: 'Hylätty',
            close: 'Sulje',
            closed_msg: 'Arkistointi suljettu.',
            close_error: 'Arkistoinnin sulkeminen epäonnistui. Yritä uudelleen.',
            change_filing: 'Luo muutostiedosto',
            change_filing_created: 'Muutostiedosto luotu.',
            change_filing_error: 'Muutostiedoston luominen epäonnistui. Yritä uudelleen.',
            change_reason: 'Syy muutokseen',
            filing_lineage: 'Arkistointilinja',
            timers: 'Aktiiviset ajastimet',
            timer_mps_review: 'Authority review',
            timer_supplement_response: 'Täydennyksen määräaika',
            timer_resubmission_review: 'Uudelleenlähetyksen määräaika',
            timer_department_input: 'Osaston määräaika',
            days_remaining: 'päivää jäljellä',
            days_overdue: 'päivää myöhässä',
            cover_letter: 'Saatekirje',
            checklist: 'Tarkistuslista',
            evidence_index: 'Todisteiden indeksi',
            status_draft: 'Luonnos',
            status_internal_review: 'Sisäinen tarkastus',
            status_legal_signoff: 'Laillinen allekirjoitus',
            status_approved: 'Approved internally — not filed',
            status_supplement_requested: 'Täydennys pyydetty',
            status_supplement_submitted: 'Täydennys lähetetty',
            status_resubmitted: 'Uudelleenlähetetty',
            status_accepted: 'Result recorded: Accepted',
            status_rejected: 'Result recorded: Rejected',
            status_closed: 'Suljettu',
            // Sprint 62.57.0 (gate E12.5 pass 1, D10) — step_approved, step_submitted,
            // step_accepted and step_rejected are English until Phase 25 (R-57); the
            // other step_* keys keep this locale's translation.
            step_draft: 'Luonnos',
            step_internal_review: 'Sisäinen tarkastus',
            step_legal_signoff: 'Laillinen allekirjoitus',
            step_approved: 'Approved internally',
            step_submitted: 'Filed',
            step_supplement_requested: 'Täydennys pyydetty',
            step_supplement_submitted: 'Täydennys lähetetty',
            step_resubmitted: 'Uudelleenlähetetty',
            step_accepted: 'Result recorded: Accepted',
            step_rejected: 'Result recorded: Rejected',
            step_closed: 'Suljettu',
            linked_dpia: 'Linkitetty DPIA',
            linked_dpia_none: 'Ei DPIA:ta linkitetty tähän arkistointiin.',
            linked_parents: 'Linked assessment',
            linked_parents_none: 'This filing names no assessment.',
            parent_kind_dpia: 'Impact assessment',
            parent_kind_transfer: 'Transfer assessment',
            view_linked_dpia: 'Näytä DPIA',
            linked_dpia_label: 'Tämä dossier-arkistointi on linkitetty seuraavaan DPIA:han:',
            all_sections_must_complete: 'All sections must be completed or reviewed before submitting',
            change_section_status: 'Change status for',
            dpia_select_placeholder: 'Link to a DPIA (optional)',
            no_departments_hint: 'No departments available. You can still assign by selecting a user below.',
            no_users_hint: 'Unable to load users. Please check your connection and try again.',
            phu_luc_i_hint: 'Internal workflow category. Statutory processing notices are Mẫu số 02a for organisations and 02b for individuals, with report 10; cross-border notices are 01a/01b, with report 09. Select the formal form separately.',
            phu_luc_ii_hint: 'Internal workflow category. Statutory processing notices are Mẫu số 02a for organisations and 02b for individuals, with report 10; cross-border notices are 01a/01b, with report 09. Select the formal form separately.',
            section_add_new: 'Add new section...',
            section_assignments_explanation: 'Assign sections of this dossier filing to departments for completion. Each assigned section creates a task for the assignee with a deadline. All sections must be completed before the filing can proceed to internal review.',
            section_consultation: 'Consultation & Stakeholders',
            section_custom_placeholder: 'Enter custom section name',
            section_data_categories: 'Data Categories & Processing',
            section_data_subject_rights: 'Data Subject Rights',
            section_impact_assessment: 'Impact Assessment',
            section_legal_basis: 'Legal Basis & Necessity',
            section_risk_mitigation: 'Risk Mitigation Measures',
            section_status_label: 'Section status',
            section_status_updated: 'Section status updated to',
            transition_to: 'Transition to...',
            view_filing: 'View',
            // Sprint 63.37.0 (Stream J) — the forms a filing files, and the dossier assembled from it.
            filing_forms: 'Forms in this filing',
            filing_forms_none: 'No form is linked to this filing yet.',
            filing_forms_failed: 'The forms of this filing could not be read.',
            filing_form_label: 'Form this filing records',
            assembled_dossier: 'Assembled dossier {reference}',
            dossier_role_primary_application: 'Primary form of the dossier',
            dossier_role_supporting_document: 'Supporting document',
            dossier_role_authority_response: 'Authority response',
            dossier_role_supplement: 'Supplement',
            // Sprint 63.37.0 (Stream K, UC-VN-01) — the person who recorded the submission; the platform records, the organisation files.
            submission_recorded_by: 'Recorded by',
            submission_notes: 'Notes',
            // Sprint 63.37.0 (Stream K, UC-VN-01) — the four section statuses (DossierSectionStatus); the table printed the stored code.
            section_status_assigned: 'Assigned',
            section_status_in_progress: 'In Progress',
            section_status_completed: 'Completed',
            section_status_reviewed: 'Reviewed',
            timer_estimate_passed: '{days} days past the estimate',
            timer_platform_estimate: 'Platform estimate — not a statutory period',
            reload: 'Reload',
            reloading: 'Reloading…',
            timers_load_failed: 'The filing timers could not be read, so none are shown. That does not mean the filing has none.',
            retry_subject_timers: 'the filing timers',
            lineage_load_failed: 'The filing lineage could not be read, so earlier and later filings are not shown.',
            retry_subject_lineage: 'the filing lineage',
            form_status_load_failed: 'The status of the forms could not be read, so the Forms column may be incomplete.',
            form_status_not_read: 'Not read',
            retry_subject_form_status: 'the form status',
            supplement_absence_declaring_framework_phase_reference_unresolved: 'No deadline: the period applies from a phase of its framework, and the framework does not date that phase.',
            // Sprint 62.57.0 (Slice R) — authority communications on a filing, recorded as
            // documents; the limitation notice; the latest-communication column and filter.
            result_recorded_qualifier: 'as your organisation recorded it from the authority’s notice',
            limitation_heading: 'What ComplianceOne records about the authority',
            limitation_body: 'ComplianceOne records the authority’s written communications as documents, and records the handling body, the result and any completion request as your organisation states them from the authority’s communications, and an update’s recipient and competent authority as your organisation records them. Completion dates are provisional where the governing text’s start event is pending legal confirmation, and ComplianceOne never decides which body handles a dossier or an update.',
            comms_heading: 'Authority communications',
            comms_intro: 'The written communications your organisation received from an authority about this filing, as a person in your organisation recorded them. Recording one changes nothing else on the filing: no status, result or deadline.',
            comms_record: 'Record a communication',
            comms_export: 'Export communications',
            comms_export_done: 'The communications were exported.',
            comms_export_failed: 'The communications could not be exported.',
            comms_none: 'No communication has been recorded on this filing.',
            comms_loading: 'Loading the communications…',
            comms_load_failed: 'The communications could not be read, so none are shown. That does not mean none were recorded.',
            retry_subject_comms: 'the authority communications',
            comms_pagination: 'Pages of authority communications',
            comms_col_kind: 'Kind',
            comms_col_sender: 'Sender',
            comms_col_communication_date: 'Date on the communication',
            comms_col_received_at: 'Received',
            comms_col_wording: 'The authority’s wording',
            comms_col_evidence: 'Evidence',
            comms_col_correction: 'Correction',
            comms_col_recorded: 'Recorded',
            comms_row_statement: 'Recorded by {person} from the authority’s communication — ComplianceOne has not verified it.',
            comms_reference: 'Reference: {reference}',
            comms_stated_items: 'Items stated',
            comms_stated_period: 'Period stated: {period}',
            comms_notes: 'Notes: {notes}',
            comms_no_wording: 'No wording recorded',
            comms_not_evidenced: 'Not evidenced',
            comms_evidence_count: '{count} file(s)',
            comms_evidence_open: 'Files',
            comms_evidence_open_label: 'Evidence files of the communication received on {date}',
            comms_evidence_load_failed: 'The evidence files could not be read.',
            comms_corrects: 'Corrects the entry of {date}',
            comms_corrects_unknown: 'Corrects an earlier entry',
            comms_corrected_by: 'Corrected by a later entry',
            comms_export_corrects: 'Corrects the entry received {date} ({kind})',
            comms_export_corrected_by: 'Corrected by a later entry (received {date})',
            comms_correction_note: 'Why: {note}',
            comm_kind_receipt_acknowledgement: 'Receipt acknowledgement',
            comm_kind_transfer_notice: 'Transfer notice',
            comm_kind_result_notice: 'Result notice',
            comm_kind_supplement_request: 'Supplement request',
            comm_kind_refusal: 'Refusal',
            comm_kind_other_written_communication: 'Other written communication',
            comms_form_intro: 'Record what the authority sent, as it is written. This record changes no status, result or deadline on the filing.',
            comms_form_evidence_hint: 'Evidence files are attached after the communication is recorded, from its row in the history.',
            comms_field_kind: 'Kind of communication',
            comms_field_kind_placeholder: 'Choose the kind',
            comms_field_sender: 'Sender, as the communication names it',
            comms_field_reference: 'Reference on the communication',
            comms_field_communication_date: 'Date on the communication',
            comms_field_received_at: 'Date your organisation received it',
            comms_field_wording: 'The authority’s wording, verbatim',
            comms_field_wording_hint: 'Up to {max} characters. Required for a result notice or a refusal.',
            comms_field_wording_count: '{count} of {max} characters used',
            comms_field_items: 'Items the communication states',
            comms_field_item: 'Item {n}',
            comms_add_item: 'Add an item',
            comms_remove_item: 'Remove item {n}',
            comms_items_none: 'No item added.',
            comms_field_period: 'Period the communication states, verbatim',
            comms_field_period_hint: 'Recorded as written. No deadline is computed from it.',
            comms_field_notes: 'Internal notes',
            comms_field_corrects: 'Corrects an earlier entry',
            comms_field_corrects_none: 'Not a correction',
            comms_field_corrects_option: '{kind}, received {date}',
            comms_field_corrects_truncated: 'Only the {count} most recent entries are listed.',
            comms_field_corrects_failed: 'The earlier entries could not be read, so none can be chosen.',
            comms_field_correction_note: 'Why the earlier entry is corrected',
            comms_error_required: 'Enter this before recording.',
            comms_error_kind_required: 'Choose the kind of communication.',
            comms_error_wording_required: 'Enter the authority’s wording for a result notice or a refusal.',
            comms_error_correction_note_required: 'Say why the earlier entry is corrected.',
            comms_error_receipt_invalid: "The date of receipt must not be later than today in your organisation's time zone.",
            comms_error_not_submitted: 'This filing has not been filed, so no communication can be recorded. The page has updated the filing’s details; what you entered is kept.',
            comms_record_failed: 'The communication could not be recorded. What you entered is kept.',
            comms_recorded: 'Communication recorded.',
            comms_recording: 'Recording…',
            comms_files_heading: 'Evidence files',
            comms_files_intro: 'The files this communication rests on, as your organisation attached them. ComplianceOne has not verified them.',
            comms_files_slot: 'Files of this communication',
            comms_files_none: 'No file is attached. This communication is not evidenced.',
            comms_file_hash: 'SHA-256: {hash}',
            comms_file_not_held: 'File no longer held',
            comms_file_download: 'Download',
            comms_file_remove: 'Remove',
            comms_file_upload: 'Attach a file',
            comms_file_bound: 'File attached.',
            comms_file_removed: 'File removed.',
            comms_file_bind_failed: 'The file could not be attached.',
            comms_file_remove_failed: 'The file could not be removed.',
            comms_file_not_owned: 'The communication this file belongs to was not found.',
            comms_file_remove_confirm: 'Remove this file from the communication and delete it?',
            latest_communication: 'Latest communication',
            latest_communication_none: 'None recorded',
            latest_communication_value: '{kind}, received {date}',
            aria_filter_by_communication: 'Filter by latest communication',
            filter_communication_none: 'No communication recorded',
            comms_export_heading: 'Communications recorded by the organisation',
            comms_export_filing: 'Filing: {filing}',
            comms_export_entries: 'Entries: {count}',
            comms_export_col_reference: 'Reference',
            comms_export_col_statement: 'Recorded',
            comms_export_truncated: 'Only the newest {shown} of {total} entries are included.',
            // Sprint 62.57.0 (Slices A and B) — the governing text and receipt, the handling
            // authority, the result from pack options, the post-result supplement and follow-up,
            // the provisional authority period and its legal reservation.
            // English until this locale is translated (R-57).
            status_result_recorded: 'Result recorded',
            status_result_supplement_required: 'Result recorded — supplement required',
            status_result_supplement_submitted: 'Result recorded — supplement submitted',
            step_result_recorded: 'Result recorded',
            step_result_supplement_required: 'Supplement required after the result',
            step_result_supplement_submitted: 'Supplement submitted after the result',
            timer_authority_review: 'Authority review',
            timer_provisional: 'Provisional — pending legal confirmation',
            timer_obligation_authority: 'The authority’s period, not your organisation’s',
            timer_calendar: 'Counted on the installed public-service calendar of working days and public holidays',
            timer_authority_stated: 'Result date stated by the authority',
            timer_provisional_passed: 'Provisional period passed — no result recorded',
            timer_absence_complete_receipt: 'No provisional date — complete-dossier receipt not recorded',
            timer_absence_calendar: 'No provisional date — the public-service calendar for {year} is not installed',
            timer_absence_no_period: 'No authority period declared',
            timer_absence_governing_text: 'No provisional date — governing text not determined',
            timer_absence_multiple_periods: 'No provisional date — the installed content declares more than one period, and none is chosen',
            timer_absence_uncomputable: 'No provisional date — the declared period cannot be counted as a date',
            timer_absence_calendar_not_declared: 'No provisional date — the declared period names no calendar to count on',
            timer_absence_calendar_unavailable: 'No provisional date — the public-service calendar is not installed',
            timer_absence_reference_mode: 'No provisional date — the content that states the period is installed for reference only',
            timer_absence_unreadable: 'No provisional date — the content that states the period could not be read',
            timer_absence_reappraisal_not_declared: 'No provisional date after the completion — not declared',
            timer_absence_completed_receipt: 'No provisional date — receipt of the completed dossier not recorded',
            timer_period_closed: 'A result is recorded; the period no longer runs',
            timer_absence_heading: 'Authority review',
            reservation_pending: 'Pending legal confirmation',
            comm_pick_empty: 'No communication is recorded on this filing yet. Record it under Authority communications first.',
            comm_pick_failed: 'The communications on this filing could not be read.',
            comm_pick_manual_label: 'Communication record id',
            comm_pick_manual_hint: 'Enter the id of a communication recorded on this filing; it is checked when you save.',
            comm_pick_off_list: 'Communication {value} (not in the list)',
            comm_pick_none: 'No communication',
            comm_pick_option_reference: '{kind}, received {date}, reference {reference}',
            error_communication_invalid: 'Choose a communication recorded on this filing.',
            error_corrected_entry_not_found: 'The entry this corrects was not found on this filing. Choose it again.',
            error_entry_already_corrected: 'That entry has already been corrected. Correct the later entry instead.',
            correction_target_moved: 'The entry you chose was corrected by another entry; this now corrects that entry. Check it before you save.',
            correction_target_required: 'Choose the entry this corrects.',
            receipt_error_already_recorded: 'A receipt of this kind is already recorded. To change it, correct that entry.',
            error_assignee_not_in_organisation: 'A person chosen is not a member of your organisation. Choose someone else.',
            error_filing_changed: 'The filing changed while you were entering this. The page has read it again; what you entered is kept. Save again.',
            error_not_submitted: 'This filing has not been filed, so this cannot be recorded. The page has updated the filing’s details; what you entered is kept.',
            error_field_refused: 'This value was not accepted. Check it and try again.',
            gov_heading: 'Governing text',
            gov_intro: 'The text that governed this dossier when the authority received it, matched from the receipt date your organisation recorded. It is never worked out from the date the filing was created or submitted.',
            gov_not_determined: 'Governing text not determined — record the receipt date',
            gov_reason_receipt_not_evidenced: 'No receipt date has been recorded.',
            gov_reason_no_declaration: 'The installed content declares no governing text for this kind of dossier.',
            gov_reason_receipt_outside_declared_windows: 'The receipt date falls outside every period the installed content declares.',
            gov_reason_declaration_unreadable: 'The installed declaration could not be read.',
            gov_requirement: 'Practice requirement: {title}',
            gov_requirement_framework: 'Practice requirement: {title} ({framework})',
            gov_receipt_date: 'Receipt date used',
            gov_evidenced_by: 'Receipt recorded by',
            gov_end_label: 'End of this period',
            gov_declared_by: 'Declared by',
            gov_declared_by_not_installed: 'A framework that is not installed',
            gov_review_required: 'The stored governing text no longer matches the installed declaration — review.',
            receipt_heading: 'Receipt by the authority',
            receipt_record: 'Record the receipt date',
            receipt_correct: 'Correct the receipt date',
            receipt_form_intro: 'Record the date the authority received the dossier, as its receipt or your evidence shows it. The date decides which text governs the dossier. A correction is a new entry; the earlier one stays in the history.',
            receipt_field_event: 'What the authority received',
            receipt_event_dossier_received: 'Dossier received',
            receipt_event_complete_dossier_received: 'Complete and valid dossier received',
            receipt_field_received_on: 'Date the authority received it',
            receipt_field_postmarked_on: 'Postmark date (dossier sent by post)',
            receipt_field_postmarked_hint: 'Only for a dossier sent by post. Kept beside the receipt date, and read only where the installed text counts the postmark.',
            receipt_field_communication: 'Communication that evidences it (optional)',
            receipt_field_corrects: 'Entry this corrects',
            receipt_field_corrects_none: 'Not a correction',
            receipt_corrects_option: '{event}, {date}',
            receipt_error_date_invalid: 'The date cannot be later than today, and the dates must run in order: postmark, dossier received, then complete and valid dossier received.',
            receipt_error_event_required: 'Choose what the authority received.',
            receipt_error_event_mismatch: 'A correction must state the same thing the authority received as the entry it corrects.',
            receipt_record_failed: 'The receipt could not be recorded. What you entered is kept.',
            receipt_recorded: 'Receipt date recorded.',
            receipt_history: 'Receipt entries',
            receipt_col_event: 'Event',
            receipt_col_received_on: 'Received on',
            receipt_col_postmarked_on: 'Postmark',
            receipt_col_communication: 'Communication',
            receipt_col_correction: 'Correction',
            receipt_col_recorded: 'Recorded',
            receipt_source_submission_acknowledgment: 'Carried over from the date of the authority’s acknowledgement of receipt, recorded with the submission',
            receipt_row_statement: 'Recorded by {person} — ComplianceOne has not verified it.',
            handling_heading: 'Handling authority',
            handling_intro: 'The body the dossier was filed with is the one the installed form names. The body that handles the dossier is recorded only by a person in your organisation, from the authority’s own communication; ComplianceOne never works it out or proposes one.',
            handling_filed_with: 'Filed with',
            handling_filed_with_unknown: 'Not declared by the installed form',
            handling_handled_by: 'Handled by',
            handling_not_known: 'Not known — no transfer recorded',
            handling_other_body: 'Other body named',
            handling_option_not_named: 'A body the installed content no longer names',
            handling_class_label: 'Body',
            handling_named_as: 'Named in the communication',
            handling_undeclared: 'The installed form for this filing declares no handling authority, so none can be recorded here.',
            handling_transfer_basis: 'Transfer basis',
            handling_transfer_none_stated: 'No transfer is provided for in this text',
            handling_transfer_awaiting_receipt: 'The transfer basis is shown once the receipt is recorded',
            handling_criteria: 'Criteria for a transfer',
            handling_may_transfer_to: 'Bodies the installed form names',
            handling_applies_from: 'The installed form states this applies to dossiers received from {date}.',
            handling_record: 'Record the handling authority',
            handling_history: 'Statements recorded',
            handling_none: 'No statement has been recorded.',
            handling_loading: 'Loading the statements…',
            handling_load_failed: 'The statements could not be read, so none are shown. That does not mean none were recorded.',
            retry_subject_handling: 'the handling statements',
            handling_pagination: 'Pages of handling statements',
            handling_col_body: 'Body',
            handling_col_basis: 'Basis',
            handling_col_communication: 'Communication',
            handling_col_evidence: 'Evidence',
            handling_col_correction: 'Correction',
            handling_col_recorded: 'Recorded',
            handling_basis_transfer_notice: 'Transfer notice',
            handling_basis_authority_result: 'The authority’s result',
            handling_basis_other_written_communication: 'Other written communication',
            handling_source_corrected: 'Source communication corrected — review',
            handling_communication_value: '{kind}, received {date}',
            handling_communication_unknown: 'A communication not on the first page of the history',
            handling_form_intro: 'State the body that handles the dossier as the authority’s communication names it. Choose it from the bodies the installed form names, or name another body. Nothing is filled in for you.',
            handling_field_basis: 'Basis for the statement',
            handling_field_basis_placeholder: 'Choose the basis',
            handling_field_communication: 'Communication it rests on',
            handling_field_option: 'Body',
            handling_field_option_placeholder: 'Choose the body',
            handling_option_other: 'Another body (name it below)',
            handling_field_body_name: 'The body’s name, as the communication gives it',
            handling_field_body_name_hint: 'Up to {max} characters, copied from the communication.',
            handling_field_corrects: 'Statement this corrects',
            handling_field_corrects_none: 'Not a correction',
            handling_corrects_option: '{body}, recorded {date}',
            handling_error_basis_required: 'Choose the basis for the statement.',
            handling_error_option_required: 'Choose the body, or choose another body and name it.',
            handling_error_option_not_declared: 'That body is not among those the installed form names. Choose another, or name it as another body.',
            handling_record_failed: 'The statement could not be recorded. What you entered is kept.',
            handling_recorded: 'Handling authority recorded.',
            result_heading: 'The authority’s result',
            result_frame: 'Result recorded: {label} — as your organisation recorded it from the authority’s notice',
            result_legacy_frame: 'Result recorded: {label} — recorded before results followed the governing text',
            result_none: 'No result has been recorded.',
            result_label_not_installed: 'Result recorded — its label is not in the installed content',
            result_different_text: 'Recorded under a different governing text — review',
            result_date: 'Date on the notice',
            result_notice_received: 'Date your organisation received the notice',
            result_communication: 'Communication',
            result_notes: 'Notes',
            result_record: 'Record the authority’s result',
            result_needs_receipt: 'Recording a result needs the receipt date first: the result options are those of the text that governed the dossier when the authority received it.',
            result_absent: 'No installed declaration offers result options for this filing, so no result can be recorded here. Record the authority’s notice under Authority communications.',
            result_absent_update: 'No result is recorded on an update dossier. Record the authority’s notice under Authority communications.',
            result_form_intro: 'Record the result as the authority’s notice states it. The options are those of the text that governed the dossier when the authority received it.',
            result_field_option: 'Result, as the notice states it',
            result_field_option_placeholder: 'Choose the result',
            result_field_communication: 'Communication the result comes from',
            result_error_option_required: 'Choose the result the notice states.',
            result_error_option_not_offered: 'That result is not offered for this filing’s governing text.',
            result_error_date_invalid: 'The date must not be later than today or earlier than the day the filing was submitted.',
            result_error_notice_before_result: 'The date your organisation received the notice cannot be before the date on the notice.',
            result_error_not_determined: 'The governing text is not determined. Record the receipt date first.',
            result_error_declaration_absent: 'No installed declaration offers result options for this filing.',
            result_error_status: 'A result cannot be recorded in the filing’s current state. The page has read the filing again; what you entered is kept.',
            result_record_failed: 'The result could not be recorded. What you entered is kept.',
            result_saved: 'The result is recorded.',
            result_correct: 'Correct this result',
            result_correct_intro: 'Record the result again as the authority’s notice states it, and say why. The earlier entry stays in the history. A result can be corrected only while it is the latest result, the filing is not closed, and no supplement request, supplement submission or follow-up has been recorded for it.',
            result_correcting_target: 'You are correcting: {result}, dated {date}',
            result_field_correction_note: 'Why the result is corrected',
            result_corrects_earlier: 'Corrects an earlier entry. Reason: {note}',
            result_earlier_heading: 'Earlier entries',
            result_corrected: 'The result is corrected.',
            result_error_correction_not_open: 'This result can no longer be corrected: it is not the latest result, the filing is closed, or a supplement request, supplement submission or follow-up has been recorded for it. The page has read the filing again.',
            result_error_not_found: 'The result being corrected was not found on this filing. The page has read the filing again; what you entered is kept.',
            result_error_already_corrected: 'This result has already been corrected by another entry. The page is reading the filing again.',
            result_error_correction_blocked: 'Nothing can be saved from this dialog. Close it; what you entered will not be recorded. If the page still offers a correction, start it again from the current result.',
            result_row_statement: 'Recorded by {person} from the authority’s notice — ComplianceOne has not verified it.',
            post_result_citation: 'What the governing text provides after this result: {citation}',
            stated_date_heading: 'Result dates stated by the authority',
            stated_date_record: 'Record a result date the authority stated',
            stated_date_form_intro: 'Record the result date the authority itself states, on a receipt slip or a later notice. It replaces the provisional date. A correction is a new entry.',
            stated_date_field_date: 'Result date the communication states',
            stated_date_field_communication: 'Communication that states it',
            stated_date_field_corrects: 'Entry this corrects',
            stated_date_corrects_option: '{date}, recorded {recorded}',
            stated_date_value: 'Stated by the authority: {date}',
            stated_date_recorded: 'Stated result date recorded.',
            stated_date_record_failed: 'The stated date could not be recorded. What you entered is kept.',
            rsupp_heading: 'Supplement after the result',
            rsupp_intro: 'The result and any concrete request are separate records. Record the items and any period the notice states; ComplianceOne computes no date from the result.',
            rsupp_no_request: 'Supplement required — no request recorded',
            rsupp_requested: 'Supplement requested — not yet submitted',
            rsupp_submitted: 'Supplement submitted',
            rsupp_record: 'Record the supplement request',
            rsupp_no_period: 'No period stated in the notice',
            rsupp_stated_deadline: 'Date stated in the notice: {date}',
            rsupp_item_owner: 'Owner: {person}',
            rsupp_item_task: 'Task assigned',
            rsupp_col_items: 'Items requested',
            rsupp_col_communication: 'Communication',
            rsupp_col_period: 'Period',
            rsupp_col_recorded: 'Recorded',
            rsupp_form_intro: 'Record what the notice asks for, item by item. An item with an owner becomes a task for that person. Only a date the notice itself states is recorded; none is computed.',
            rsupp_field_communication: 'Communication the request comes from',
            rsupp_field_items: 'Items the notice asks for',
            rsupp_field_item: 'Item {n}',
            rsupp_field_item_owner: 'Owner of item {n} (optional)',
            rsupp_add_item: 'Add an item',
            rsupp_remove_item: 'Remove item {n}',
            rsupp_field_period: 'Period the notice states, verbatim',
            rsupp_field_deadline: 'Date the notice states (optional)',
            rsupp_field_deadline_hint: 'Only a date the notice itself states. Each item with an owner becomes a task due on this date, or with no due date when the notice states none.',
            rsupp_error_items_required: 'Add at least one item the notice asks for.',
            rsupp_error_not_open: 'No supplement is open after this result. The page has read the filing again; what you entered is kept.',
            rsupp_record_failed: 'The supplement request could not be recorded. What you entered is kept.',
            rsupp_recorded: 'Supplement request recorded.',
            followup_heading: 'Follow-up after the result',
            followup_intro: 'This result leaves the filing open. Record the reasons the authority stated, the remediation your organisation chose and any enforcement risk. Nothing on any other record changes.',
            followup_not_recorded: 'No follow-up recorded yet',
            followup_refusal: 'The authority’s communication',
            followup_reasons: 'Reasons stated',
            followup_remediation: 'Remediation',
            followup_actions: 'Remediation actions',
            followup_action_due: 'Your organisation’s own date: {date}',
            followup_action_owner: 'Owner: {person}',
            followup_enforcement_risk: 'Potential enforcement risk',
            followup_enforcement_risk_none: 'None stated',
            followup_revision: 'Recorded by {person} on {date}',
            followup_record: 'Record the follow-up',
            followup_revise: 'Revise the follow-up',
            followup_form_intro: 'Record the reasons the notice states and the remediation your organisation chose. Each action’s owner and date are your organisation’s own choice; ComplianceOne computes no date.',
            followup_field_reasons: 'Reasons the notice states',
            followup_field_summary: 'Remediation',
            followup_field_actions: 'Remediation actions',
            followup_field_action: 'Action {n}',
            followup_field_action_title: 'What is to be done (action {n})',
            followup_field_action_owner: 'Owner (action {n})',
            followup_field_action_due: 'Your organisation’s own date (action {n})',
            followup_add_action: 'Add an action',
            followup_remove_action: 'Remove action {n}',
            followup_actions_none: 'No action added.',
            followup_field_enforcement: 'Potential enforcement risk (optional)',
            followup_field_enforcement_hint: 'Leave it empty when none is stated; the record then says so.',
            followup_field_correction_note: 'Why the follow-up is revised',
            followup_error_not_open: 'No follow-up is open for this filing. The page has read the filing again; what you entered is kept.',
            followup_record_failed: 'The follow-up could not be recorded. What you entered is kept.',
            followup_recorded: 'Follow-up recorded.',
            close_title: 'Close the filing',
            close_intro: 'Closing records that your organisation has finished with this filing. It is not a decision of the authority.',
            close_field_reason: 'Closing reason',
            close_field_reason_hint: 'Required where the page says so; otherwise optional.',
            close_refusal_DOSSIER_RESULT_SUPPLEMENT_OPEN: 'The supplement after the result is still open. Record it as submitted before closing.',
            close_refusal_DOSSIER_FAILURE_FOLLOW_UP_OPEN: 'No follow-up is recorded for this result. Record the follow-up, or give the reason for closing without one.',
            close_refusal_DOSSIER_CLOSE_REASON_REQUIRED: 'Give the reason for closing this filing.',
            close_closing: 'Closing…',
            close_failed: 'The filing could not be closed. What you entered is kept.',
            closing_reason_value: 'Closing reason: {reason}',
            column_handled_by: 'Handled by',
            aria_filter_by_handling: 'Filter by handling authority',
            filter_handling_not_known: 'Not known — no transfer recorded',
            filter_handling_other: 'Other body named',
            prior_filing_open_follow_up: 'Open — follow-up recorded',
            prior_filing_open_no_follow_up: 'Open — the result did not pass, and no follow-up is recorded yet',
            prior_filing_body_open: 'The installed framework provides for one dossier of this kind per organisation, updated afterwards ({citation}). This organisation filed one: {filing}. A result is recorded on it. For a new purpose or another change to its content, start the update on this assessment instead.',
            // Sprint 62.57.0 (Slices A, B, F; A-1) — the filing record export’s labels.
            record_export_receipt_heading: 'Receipt of the dossier and the governing text',
            record_export_receipt_none: 'No receipt of the dossier has been recorded.',
            record_export_col_event: 'Receipt',
            record_export_col_received_on: 'Received by the authority',
            record_export_col_postmarked_on: 'Postmark',
            record_export_col_status: 'Entry',
            record_export_entry_standing: 'Standing',
            record_export_entry_corrected: 'Corrected by a later entry',
            record_export_governing_text: 'Governing text: {label}',
            record_export_governing_end: 'End of the window: {label}',
            governing_text_not_determined: 'Governing text not determined — record the receipt date',
            record_export_governing_review: 'The recorded governing text no longer matches the installed declaration — review',
            record_export_reservation: 'Pending legal confirmation: {note}',
            record_export_handling_heading: 'Handling authority',
            record_export_filed_with: 'Filed with: {body}',
            record_export_handled_by_unknown: 'Handled by: not known — no transfer recorded',
            record_export_transfer_basis: 'Transfer basis: {text}',
            record_export_transfer_none_stated: 'No transfer is provided for in this text',
            record_export_transfer_no_stamp: 'The transfer basis is shown once the receipt is recorded',
            record_export_col_body: 'Body named in the communication',
            record_export_col_option: 'Declared body',
            record_export_col_basis: 'Basis',
            record_export_col_source: 'Source communication',
            record_export_other_body: 'Other body named',
            record_export_source_corrected: 'Source communication corrected — review',
            record_export_result_heading: 'Result recorded',
            record_export_result_none: 'No result has been recorded.',
            record_export_result_corrected_by: 'Corrected by a later entry',
            record_export_result_corrects: 'Corrects an earlier entry. Reason: {note}',
            record_export_result_label: 'Result recorded: {label} — as your organisation recorded it from the authority’s notice',
            record_export_result_legacy: 'Result recorded: {legacy} — recorded before results followed the governing text',
            record_export_result_dates: 'Date on the notice: {result_date}. Received by the organisation: {received}.',
            result_recorded_under_different_governing_text: 'Recorded under a different governing text — review',
            record_export_result_follow_up: 'What the result opens: {citation}',
            record_export_supplement_heading: 'Supplement requests after the result',
            result_supplement_no_request: 'Supplement required — no request recorded',
            record_export_supplement_period: 'Period stated in the notice: {period}',
            no_period_stated_in_notice: 'No period stated in the notice',
            record_export_follow_up_heading: 'Follow-up to the result',
            record_export_follow_up_none: 'No follow-up has been recorded.',
            record_export_follow_up_reasons: 'Reasons stated: {text}',
            record_export_follow_up_remediation: 'Remediation: {text}',
            record_export_follow_up_risk: 'Potential enforcement risk: {text}',
            record_export_col_action: 'Remediation action',
            record_export_col_owner: 'Person responsible',
            record_export_col_own_date: 'Your organisation’s own date',
            record_export_period_heading: 'The authority’s period',
            record_export_period_stated: 'Result date stated by the authority: {date}',
            record_export_period_counted: 'Counted from {start} on the {calendar} calendar: {date}',
            record_export_period_passed: 'Provisional period passed — no result recorded',
            record_export_period_not_dated: 'No provisional date ({reason})',
            // Sprint 62.57.0 (Slices C and D, list half of AC-F5) — the completion period, the
            // completion request and submission, the update's recipient and competent authority,
            // the list's governing-text column and filters, the requirement link.
            status_result_completion_submitted: 'Completion submitted — no further result recorded',
            step_result_completion_submitted: 'Completion submitted',
            receipt_event_completed_dossier_received: 'Completed dossier received',
            receipt_field_completion_submission: 'Completion submission received',
            receipt_completion_option: 'Completion submitted on {date}',
            receipt_completion_option_recorded: 'Completion recorded on {date}',
            receipt_error_completion_invalid: 'Choose the completion submission the authority received.',
            completion_heading: 'Completion period',
            completion_intro: 'A completion period opens only when your organisation records that a communication asks it to complete the dossier, or records a result for which the governing text provides one. The dates are worked out from the installed regulatory content each time the filing is read.',
            completion_none: 'No completion period is open on this filing.',
            completion_current: 'Current period',
            completion_history: 'Earlier periods',
            completion_date_provisional: 'Provisional date — start event pending legal confirmation',
            completion_date_confirmed: 'Completion date',
            completion_date_label: 'Completion date',
            completion_passed_provisional: 'Provisional completion date passed',
            completion_passed_confirmed: 'Completion period passed',
            completion_running: 'Within the period',
            completion_completed: 'Completion submitted on {date}',
            completion_completed_no_date: 'Completion submitted',
            completion_not_dated: 'No completion date',
            completion_state: 'State',
            completion_notice_date: 'Notice date',
            completion_received_on: 'Received by your organisation',
            completion_authority_stated: 'Date stated by the authority',
            completion_trigger: 'Opened by',
            completion_trigger_completion_request: 'A completion request your organisation recorded',
            completion_trigger_result: 'The result your organisation recorded',
            completion_source: 'Source communication',
            completion_deficiency: 'What the governing text provides',
            completion_citation: 'Citation',
            completion_start_event: 'Start event',
            completion_period_length: 'Period: {period} {unit}',
            completion_unit_calendar_days: 'calendar days',
            completion_unit_working_days: 'working days',
            completion_end_roll_none: 'ComplianceOne shows the end date as counted and does not move it to the next working day when it falls on a day off.',
            completion_end_roll_next_statutory_working_day: 'An end that falls on a day off moves to the next working day.',
            completion_obligation: 'Your organisation’s period, not the authority’s',
            completion_task_raised: 'A task was raised when the date passed.',
            completion_col_period: 'Period',
            completion_absence_trigger_not_documented: 'No completion period — this request cites no recorded communication',
            completion_absence_trigger_not_declared: 'No completion period is declared for this result under the governing text',
            completion_absence_governing_text_not_determined: 'No completion date — the governing text is not determined; record the receipt date',
            completion_absence_no_period_declared: 'No completion period is declared in the installed content for this dossier',
            completion_absence_multiple_candidate_periods: 'No completion date — the installed content declares more than one period and none is chosen',
            completion_absence_statutory_calendar_unavailable: 'No completion date — the statutory calendar is not installed',
            completion_absence_receipt_not_evidenced: 'No completion date — your organisation’s receipt of the communication is not recorded',
            completion_absence_declaring_framework_reference_mode: 'No completion date — the content that declares the period is installed for reference only',
            completion_absence_declaring_framework_not_in_force: 'No completion date — the content that declares the period was not in force at the dossier’s receipt',
            completion_absence_declaring_framework_unreadable: 'No completion date — the content that declares the period could not be read',
            completion_absence_no_installed_clock: 'No completion period is declared in the installed content for this dossier',
            completion_absence_multiple_candidate_clocks: 'No completion date — the installed content declares more than one period and none is chosen',
            completion_absence_uncomputable_period: 'No completion date — the declared period cannot be counted as a date',
            completion_absence_declaring_framework_phase_reference_unresolved: 'No completion date — the content that declares the period does not date the phase it names',
            completion_absence_declaring_framework_not_operational: 'No completion date — the content that declares the period is not in operation',
            completion_consequence_heading: 'Potential enforcement risk',
            completion_body_handled_by: '{name} — from your recorded handling statement',
            completion_body_filed_with: '{name} — the body the dossier was filed with; no handling statement recorded',
            completion_fine_area_absent: 'The penalties framework that declares this is not installed in your organisation',
            completion_fine_area_articles: 'Articles: {articles}',
            completion_fine_area_reference: 'Installed for reference only',
            completion_fine_area_link: 'Open {area} in Penalty Readiness',
            completion_requests_heading: 'Completion requests recorded',
            completion_requests_none: 'No completion request has been recorded.',
            completion_col_communication: 'Communication',
            completion_col_recorded: 'Recorded',
            completion_col_correction: 'Correction',
            completion_request_record: 'Record a completion request',
            completion_request_correct: 'Correct this request',
            completion_request_intro: 'Choose the communication and state what it asks. ComplianceOne does not read the communication’s wording to decide this.',
            completion_field_communication: 'Communication',
            completion_statement: 'The communication asks your organisation to complete the dossier, or states that it is incomplete, invalid or not in accordance with the regulations',
            completion_statement_required: 'Tick the statement to record the request.',
            completion_context_heading: 'What the governing text provides (for reference)',
            completion_field_corrects: 'Request this corrects',
            completion_field_corrects_none: 'Not a correction — a new request',
            completion_corrects_option: '{communication}, recorded {date}',
            completion_request_recorded: 'Completion request recorded.',
            completion_request_failed: 'The completion request could not be recorded. What you entered is kept.',
            completion_error_not_open: 'A completion request can be recorded here only while a supplement request or a result is recorded on the filing. Before a result, record it with the supplement request.',
            completion_request_row: 'Recorded by {person} on {date}',
            completion_request_withdraw: 'Withdraw this entry',
            completion_request_withdraw_title: 'Withdraw this completion request entry',
            completion_request_withdraw_intro: 'Withdraw an entry that recorded a request in error: no completion period is counted from it any more. The entry stays in the history with your note. If another communication asks your organisation to complete the dossier, record a new request instead.',
            completion_request_withdraw_target: 'Entry withdrawn',
            completion_request_withdraw_note: 'Why this entry is withdrawn',
            completion_request_withdraw_note_required: 'Say why the entry is withdrawn.',
            completion_request_withdrawing: 'Withdrawing…',
            completion_request_withdrawn_msg: 'Completion request entry withdrawn.',
            completion_request_withdraw_failed: 'The withdrawal could not be recorded. What you entered is kept.',
            completion_request_withdraw_gone: 'This entry has been corrected or withdrawn meanwhile, or is no longer on this filing. The page has read the filing again.',
            completion_request_withdrawn: 'Withdrawn',
            completion_request_withdraws: 'Withdraws the entry recorded on {date}',
            completion_request_withdrawn_by: 'Withdrawn by a later entry',
            supplement_field_communication: 'Communication that asks for the supplement',
            supplement_communication_hint: 'A completion period is counted only from a recorded communication. Record the communication first under Authority communications.',
            supplement_error_completion_needs_communication: 'Choose the communication before you tick the statement.',
            supplement_request_failed: 'The supplement request could not be recorded. What you entered is kept.',
            result_completion_hint: 'Tick only if this communication asks your organisation to complete the dossier, or states that it is incomplete, invalid or not in accordance with the regulations. A result alone opens a completion period only where the governing text provides for one.',
            completion_submit: 'Record completion submitted',
            completion_submitted_on: 'Date submitted',
            completion_submit_intro: 'Record the day your organisation submitted the completed dossier to the authority. A further result may then be recorded on this filing.',
            completion_submit_intro_supplement: 'Record the day your organisation submitted the completed dossier in answer to the supplement request, and what it submitted.',
            supplement_submit_intro: 'Record what your organisation submitted in answer to the supplement request.',
            completion_submitted_msg: 'Completion submitted recorded.',
            completion_submit_failed: 'The submission could not be recorded. What you entered is kept.',
            completion_error_date_invalid: 'The date cannot be in the future or before your organisation received the request.',
            completion_error_status: 'This filing’s state no longer allows this. The page has been read again; what you entered is kept.',
            column_governing_text: 'Governing text',
            aria_filter_by_governing_text: 'Filter by governing text',
            filter_governing_not_determined: 'Not determined',
            filter_completion_passed: 'Completion period: date passed',
            list_completion_due_provisional: 'Provisional completion date: {date}',
            list_completion_due: 'Completion date: {date}',
            chip_governed_by_requirement: 'Filings governed by the requirement you opened',
            chip_governed_by_requirement_title: 'Filings governed by: {title}',
            chip_updates_of: 'Updates of the original filing',
            chip_remove: 'Remove filter: {label}',
            requirement_filings_link: 'Filings this governs: {n}',
            requirement_filings_link_failed: 'The filings this governs could not be counted.',
            change_revise: 'Revise this filing',
            change_update: 'Prepare an update filing',
            change_mode_legend: 'What do you want to do?',
            change_update_form: 'Update form',
            change_update_loading: 'Reading the original filing and the installed update forms…',
            change_update_load_failed: 'The original filing or the installed update forms could not be read.',
            change_update_intro: 'The update is a new filing in ComplianceOne that names this one; preparing it sends nothing to the authority. Choose each body yourself; nothing is chosen for you.',
            update_sent_to: 'Sent to',
            update_sent_to_help: 'The body your organisation files this update with; the cover letter is addressed to it when it is not the body the original was filed with.',
            update_competent: 'Competent to handle',
            update_competent_help: 'The body your organisation records, from the authority’s communications, as competent to handle the dossier. ComplianceOne does not determine it.',
            update_context: 'Original dossier — filed with: {filed_with}. Handled by: {handled_by}.',
            update_context_not_known: 'not known — no transfer recorded',
            update_context_filed_with_not_known: 'not declared by the installed form',
            update_choice_filed_with: 'The body the original dossier was filed with: {name}',
            update_choice_handled_by: 'The body recorded as handling the original dossier: {name}',
            update_choice_named: 'Another body (name it as the communication does)',
            update_named_label: 'Name of the body, as the communication gives it',
            update_reason: 'Why this differs from the body handling the original dossier',
            update_reason_now_required: 'A reason is now required: Why this differs from the body handling the original dossier',
            update_error_choice_required: 'Choose a body.',
            update_error_name_required: 'Enter the body’s name.',
            update_error_reason_required: 'Say why this differs from the body handling the original dossier.',
            update_error_body_invalid: 'This body can no longer be chosen: the original dossier has no recorded handling body. Choose another.',
            update_error_not_declared: 'No installed update form of this kind provides for recording the recipient and competent authority.',
            update_error_form_invalid: 'The update form could not be used for this filing. Start again.',
            update_error_target_invalid: 'An update cannot be prepared from an update. Open the original filing.',
            update_error_locked: 'These can no longer be changed: the update dossier has been prepared for filing or filed.',
            update_error_original_not_found: 'The original filing was not found. Choose it again.',
            update_error_original_invalid: 'This filing cannot be chosen as the original. Choose another.',
            update_error_forms_denied: 'Your role does not give you access to forms, so an update filing cannot be prepared here. Ask an administrator for access to forms.',
            update_original_choose_first: 'Choose the original filing first.',
            update_error_not_submitted: 'The original filing has not been filed, so no update can be prepared from it.',
            update_create_failed: 'The update could not be created. What you entered is kept.',
            update_form_kept: 'An update form was started but not linked to this filing. Prepare the update again from this filing to use it; do not complete it from the assessment’s forms, which would create an update that names no original filing.',
            update_form_reused: 'This update reuses the update form already started from this filing: {form}.',
            completion_mode_changed: 'The completion period on this filing changed while you were entering this. The dialog now records the supplement response only; what you entered is kept.',
            completion_mode_changed_completion: 'The completion period on this filing changed while you were entering this. The dialog now records the completion submitted and asks for the date submitted; what you entered is kept.',
            supplement_request_no_longer_open: 'A supplement request can no longer be recorded on this filing: its status changed meanwhile (for example, another person recorded one). The page has read it again. Close this dialog.',
            update_form_no_longer_offered: 'The update form started earlier is no longer offered by your installed content; choose a form.',
            update_created: 'Update filing created. Fill in the update form.',
            update_creating: 'Creating…',
            update_section_heading: 'Recipient and competent authority',
            update_section_intro: 'Your organisation records where it files the update, and, from the authority’s communications, which body is competent to handle it. ComplianceOne never decides which body handles a dossier or an update.',
            update_not_recorded: 'Not recorded yet.',
            update_record: 'Record the recipient and competent authority',
            update_edit: 'Change the recipient and competent authority',
            update_saving: 'Saving…',
            update_recorded_msg: 'Recipient and competent authority recorded.',
            update_record_failed: 'The recipient and competent authority could not be recorded. What you entered is kept.',
            update_history: 'Earlier records',
            update_recorded_by: 'Recorded by {person} on {date}',
            update_source_filed_with: 'The body the original dossier was filed with',
            update_source_handled_by: 'The body recorded as handling the original dossier',
            update_source_named: 'Named by your organisation',
            update_addressee_review_prompt: 'The body recorded as handling the original dossier has changed since this was recorded — review',
            update_window_rule_flag: 'The text that governs this update provides for filing it with {body}; your organisation recorded {sent_to} — review',
            update_window_rule_flag_unnamed: 'The text that governs this update provides for filing it with another body than the one your organisation recorded ({sent_to}) — review',
            update_bases_disclosure: 'Which body an update is filed with depends on when the authority receives it',
            update_basis_own_window: 'What the text that governs this update provides',
            update_original: 'Updates the filing',
            update_original_open: 'Open the original filing',
            updates_of_this: 'Updates of this dossier ({n})',
            gov_update_own_receipt: 'An update is dated by its own receipt, not by the original dossier’s.',
            completion_task_title_provisional: 'Provisional completion date passed: {filing}',
            completion_task_title_confirmed: 'Completion period passed: {filing}',
            update_original_pick_loading: 'Reading the filings…',
            update_original_pick_empty: 'No filed filing can be chosen as the original.',
            update_original_pick_failed: 'The filings could not be read.',
            update_original_pick_manual_label: 'Original filing record ID',
            update_original_pick_manual_hint: 'Enter the ID of a filed filing; it is checked when you save.',
            update_original_pick_off_list: 'Filing {value} (not in the list)',
            followup_completion_link: 'See the completion period',
            record_export_completion_heading: 'Completion period',
            record_export_completion_none: 'No completion request has been recorded.',
            record_export_completion_trigger_request: 'Completion request recorded from the communication {reference}',
            record_export_completion_trigger_result: 'Result recorded: {label}',
            record_export_completion_dates: 'Notice date: {notice}. Received by your organisation: {received}.',
            record_export_completion_provisional: 'Provisional date — start event pending legal confirmation: {date}',
            record_export_completion_confirmed: 'Completion date: {date}',
            record_export_completion_stated: 'Date stated by the authority: {date}',
            record_export_completion_passed_provisional: 'Provisional completion date passed',
            record_export_completion_passed: 'Completion period passed',
            record_export_completion_completed: 'Completion submitted: {date}',
            record_export_completion_body_not_recorded: 'the body competent to handle the dossier (not recorded on this filing)',
            record_export_completion_area: 'Penalty area: {name} ({articles})',
            record_export_completion_area_absent: 'The penalties framework that declares this is not installed in your organisation',
            record_export_update_heading: 'Recipient and competent authority',
            record_export_update_sent_to: 'Sent to: {body}',
            record_export_update_competent: 'Competent to handle: {body}',
            record_export_update_reason: 'Why this differs from the body handling the original dossier: {reason}',
            record_export_update_basis: '{window}: {basis}',
            record_export_update_window_flag: 'The text that governs this update provides for filing it with another body than the one recorded — review',
        },
        materialisation_health: {
            nav_label: 'Lähetä artefakti terveys',
            page_title: 'Lähetys Artefact Health',
            page_description: 'Havaitse ja korjaa aineiston toteutumiseen liittyvät ongelmat. Listat koskevat organisaatiotasi.',
            failed_title: 'Epäonnistuneet materialisaatiot',
            failed_empty: 'Ei epäonnistuneita materialisaatioita – hyvä.',
            pending_title: 'Hyväksytty, mutta ei toteutunut',
            pending_empty: 'Kaikilla hyväksytyillä hakemuksilla on tukiasiakirja.',
            orphan_title: 'Orpoasiakirjat',
            orphan_description: 'Toteutumalla luodut asiakirjat, joihin ei enää viitata missään tiedostossa – tyypillisesti samanaikaisen kilpailun häviäjä. Listattu tutkittavaksi; puhdistus on manuaalinen.',
            orphan_empty: 'Ei havaittu orpoasiakirjoja.',
            materialise_button: 'Materialisoi nyt',
            column_failure_reason: 'Epäonnistumisen syy',
            column_failed_at: 'Epäonnistui klo',
            column_retries: 'Uudelleenyritykset',
            column_approved_at: 'Hyväksytty klo',
            column_dossier_id: 'Asiakirjan tunnus',
            column_source_filing_id: 'Lähteen arkistointi',
            column_dossier_type: 'Kirjoita',
            column_created_at: 'Luotu klo',
        },
        // Sprint 62.35.0 (Slice C) — structural parity; English until Phase 25 (R-57).
        pre_collection: {
            nav_label: 'Pre-Collection Assessments',
            page_title: 'Pre-Collection Assessments',
            page_description: 'An assessment of the protection measures for a data registry item, made before that data is first collected. The completion date may not be after the first collection date.',
            module_guide: 'Record, for each data registry item, the assessment of protection measures made before collection begins: the collection procedure and its version, the assessment, the measures applied, the date the assessment was completed and the date the data was first collected. Whether an assessment is required is set by your installed frameworks.',
            new_button: 'New assessment',
            create_title: 'New pre-collection assessment',
            edit_title: 'Edit pre-collection assessment',
            edit: 'Edit',
            field_registry_item: 'Data registry item',
            registry_item_placeholder: 'Select a registry item',
            registry_item_unresolved: 'Registry item not found',
            registry_load_error: 'The data registry could not be read, so no registry item can be offered.',
            registry_empty: 'The data registry has no items yet.',
            field_procedure_reference: 'Collection procedure reference',
            field_procedure_version: 'Procedure version',
            field_summary: 'Assessment',
            field_measures: 'Measures applied',
            field_measures_hint: 'One measure per line.',
            field_completed_on: 'Assessment completed on',
            field_first_collection_on: 'First collected on',
            date_hint: 'The assessment must be completed on or before the first collection date. Leave the first collection date empty if collection has not begun.',
            error_after_first_collection: 'The assessment must be made before the data is first collected: the completion date is after the first collection date.',
            field_status: 'Status',
            status_draft: 'Draft',
            status_approved: 'Approved',
            status_deprecated: 'Deprecated',
            column_registry_item: 'Registry item',
            column_procedure: 'Collection procedure',
            column_actions: 'Actions',
            not_recorded: 'Not recorded',
            empty: 'No pre-collection assessments have been recorded yet.',
            list_load_error: 'The pre-collection assessments could not be read.',
            reload: 'Reload',
            reload_sr: 'reload the pre-collection assessments',
            registry_reload_sr: 'reload the data registry',
            save: 'Save',
            saving: 'Saving…',
            cancel: 'Cancel',
            created_toast: 'Pre-collection assessment recorded.',
            updated_toast: 'Pre-collection assessment updated.',
            save_error: 'The assessment could not be saved.',
            total: 'Total',
            section_scope: 'What is assessed',
            section_assessment: 'Assessment and dates',
            approved_locked: 'This assessment is approved, so its content cannot be changed. It can only be deprecated.',
            error_conflict: 'This assessment was changed by someone else while you were editing, so your change was not saved. Close this form and open the assessment again to see the current version.',
            error_conflict_refreshed: 'This assessment was changed by someone else while you were editing, so your change was not saved. The form now shows the current version, and your unsaved entries were not kept. Make your change again and save.',
            registry_not_readable: 'Your role cannot read the data registry, so no registry item can be offered here.',
            create_needs_registry_read: 'Your role cannot read the data registry, so it cannot choose the registry item a new assessment needs. Existing assessments can still be edited.',
            registry_item_name_hidden: 'Name not visible to your role',
        },
    },
    classification: {
        access_denied_title: 'Pääsy kielletty',
        access_denied: 'Sinulla ei ole tarvittavia oikeuksia käyttää luokitusmoduulia. Ota yhteyttä järjestelmänvalvojaan.',
        nav: {
            taxonomies: 'Luokittelut',
            labeling: 'Merkinnät',
            lifecycle: 'Elinkaari',
            gaps: 'Puuteraportti',
            exports: 'Viennit',
        },
        exports: {
            title: 'Luokitteluviennit',
            csv_section: 'CSV-viennit',
            pdf_section: 'PDF-raportit',
            inventory: 'Inventaario',
            classification_register: 'Luokittelurekisteri',
            raci: 'RACI-matriisi',
            exceptions: 'Poikkeukset',
            evidence_pack: 'Todistepaketti',
            delta_report: 'Deltaraportti',
            executive_summary: 'Johdon yhteenveto',
            exporting: 'Viedään…',
            export_success: 'Vienti onnistui.',
            export_error: 'Vienti epäonnistui. Yritä uudelleen.',
            csv_download: 'CSV-lataus valmis.',
            pdf_download: 'PDF-luonti aloitettu.',
            no_data: 'Ei vietäviä tietoja.',
        },
        delta: {
            title: 'Muutostiedot',
            added: 'Lisätyt',
            removed: 'Poistetut',
            changed: 'Muutetut',
            added_count: 'Lisätyt ({count})',
            removed_count: 'Poistetut ({count})',
            changed_count: 'Muutetut ({count})',
            object_id: 'Kohdetunnus',
            object_type: 'Kohdetyyppi',
            taxonomy: 'Taksonomia',
            level: 'Taso',
            previous: 'Edellinen',
            current: 'Nykyinen',
            no_records: 'Ei tietueita tässä osiossa.',
            empty_section: 'Tämä osio on tyhjä.',
        },
        schedule: {
            title: 'Ajoita löytösykli',
            enabled: 'Käytössä',
            interval_days: 'Väli (päivää)',
            interval_label: 'Suorita N päivän välein',
            manual_only: 'Vain manuaalinen',
            every_n_days: '{n} päivän välein',
            save: 'Tallenna aikataulu',
            cancel: 'Peruuta',
            schedule_updated: 'Aikataulu päivitetty.',
        },
        taxonomies: {
            title: 'Luokittelutaksonomiat',
            new: 'Uusi taksonomia',
            edit: 'Muokkaa taksonomiaa',
            name: 'Nimi',
            levels: 'Luokitustasot',
            seed_defaults: 'Alusta Oletus',
            scope: 'Laajuus',
            scope_organization: 'Organisaatio',
            scope_module_specific: 'Moduuli Tarkka',
            deprecate: 'Vanhenna',
            hide_levels: 'Piilota Tasot',
            versions: 'Versiot',
            hide_versions: 'Piilota Versiot',
            add_level: 'Lisää taso',
            new_level: 'Uusi Taso',
            edit_level: 'Muokkaa Taso',
            code: 'Koodi',
            label: 'Tunniste',
            color: 'Väri',
            ordinal: 'Järjestysluku',
            handling_requirements: 'Handling Vaatimukset',
            version_history: 'Versiohistoria',
            changes: 'Muutokset',
            failed_fetch: 'Epäonnistui: fetch taxonomies. Yritä uudelleen.',
            failed_save: 'Epäonnistui: save taxonomy. Yritä uudelleen.',
            failed_activate: 'Epäonnistui: activate taxonomy. Yritä uudelleen.',
            failed_deprecate: 'Epäonnistui: deprecate taxonomy. Yritä uudelleen.',
            failed_seed: 'Epäonnistui: seed default taxonomies. Yritä uudelleen.',
            failed_fetch_levels: 'Epäonnistui: fetch taxonomy taso. Yritä uudelleen.',
            failed_save_level: 'Epäonnistui: save taxonomy taso. Yritä uudelleen.',
            failed_delete_level: 'Epäonnistui: delete taxonomy taso. Yritä uudelleen.',
            failed_fetch_versions: 'Epäonnistui: fetch taxonomy versions. Yritä uudelleen.',
            loaded_count: 'Ladattu {count} taksonomiaa',
            defaults_seeded: 'Oletustaksonomiat alustettu.',
            no_levels: 'Ei luokitustasoja määritelty — lisää tasoja luokitushierarkian rakentamiseksi.',
            no_versions: 'Ei versiohistoriaa vielä — versiot luodaan, kun aktiivista taksonomiaa päivitetään.',
        },
        labeling: {
            title: 'Tietojen merkintä',
            new: 'Uusi merkintä',
            edit: 'Muokkaa merkintää',
            entity_type: 'Kohteen tyyppi',
            entity_id: 'Kohteen tunnus',
            taxonomy: 'Taksonomia',
            classification_level: 'Luokitustaso',
            applied_by: 'Merkitsijä',
            applied_at: 'Merkitty',
            bulk_apply: 'Joukkomerkkaus',
            bulk_remove: 'Joukkopoisto',
            all_taxonomies: 'Kaikki taksonomiat',
            system: 'Järjestelmä',
            dataset: 'Tietojoukko',
            level_code_placeholder: 'Taso koodi...',
            select_taxonomy: 'Valitse taxonomy...',
            select_level: 'Valitse taso...',
            rationale: 'Perustelu',
            review_date: 'Katselmointia Päivämäärä',
            review: 'Katselmointia',
            selected: 'valittu',
            select_all_labels: 'Valitse kaikki tunnisteet',
            loaded_count: 'Ladattu {count} tunnistetta',
            hide_stale_labels: 'Piilota Vanhentunut Tunnisteet',
            show_conflicts: 'Näytä Conflicts',
            hide_conflicts: 'Piilota Conflicts',
            label_conflicts: 'Tunnisteristiriidat',
            conflict_reason: 'Ristiriidan syy',
            labels: 'Tunnisteet',
            failed_fetch: 'Epäonnistui: fetch labels. Yritä uudelleen.',
            failed_fetch_taxonomies: 'Epäonnistui: fetch taxonomies. Yritä uudelleen.',
            failed_fetch_levels: 'Epäonnistui: fetch taxonomy taso. Yritä uudelleen.',
            failed_save: 'Epäonnistui: save label. Yritä uudelleen.',
            failed_review: 'Epäonnistui: katselmointi label. Yritä uudelleen.',
            failed_bulk: 'Epäonnistui: bulk label. Yritä uudelleen.',
            failed_stale: 'Epäonnistui: fetch stale labels. Yritä uudelleen.',
            failed_conflicts: 'Epäonnistui: fetch label conflicts. Yritä uudelleen.',
            review_permission_denied: 'Sinulla ei ole oikeutta tarkistaa tätä tunnistetta.',
            review_not_found: 'Tunnistetta ei löytynyt — se on ehkä poistettu.',
            approve_selected: 'Hyväksy valitut',
            reject_selected: 'Hylkää valitut',
            approved: 'Hyväksytty',
            rejected: 'Hylätty',
            labels_count: 'tunnisteita',
            failed_count: 'epäonnistui',
            confirm_approve: 'Vahvista hyväksyntä',
            confirm_reject: 'Vahvista hylkäys',
            approve_n_labels: 'Hyväksy {count} tunnistetta?',
            reject_n_labels: 'Hylkää {count} tunnistetta?',
            submit_for_review: 'Lähetä tarkistettavaksi',
            approve: 'Hyväksy',
            reject: 'Hylkää',
            parent_system: 'Ylajarjestelma',
            parent_system_placeholder: 'Valitse ylajarjestelma...',
            parent_system_help: 'Kaytetaan ristiriitojen havaitsemiseen.',
            dataset_level: 'Tietojoukon taso',
            parent_level: 'Ylataso',
            bulk_created: 'Luotu {created} tunniste(tta)',
            bulk_skipped: '{skipped} ohitettu',
            bulk_all_skipped: 'Kaikki {skipped} ohitettu',
            ids_hint: 'Syota tunnukset pilkuilla erotettuna',
            delete_confirm: 'Poista tunniste kohteelle "{id}"?',
        },
        lifecycle: {
            title: 'Tietojen elinkaaren valvonta',
            new: 'Uusi merkintä',
            current_stage: 'Nykyinen vaihe',
            last_transition: 'Viimeisin siirtymä',
            transition: 'Siirtymä',
            view_history: 'Näytä historia',
            stages: {
                creation: 'Luominen',
                collection: 'Kerääminen',
                processing: 'Käsittely',
                storage: 'Tallennus',
                retention: 'Säilytys',
                disposal: 'Hävittäminen',
                archived: 'Arkistoitu',
                active: 'Aktiivinen',
                pending_deletion: 'Odottaa Deletion',
                deleted: 'Poistettu',
                on_hold: 'On Pidätys',
            },
            all_stages: 'Kaikki vaiheet',
            object_id: 'Object Tunniste',
            object_type: 'Object Tyyppi',
            policy_reference: 'Käytäntö Viite',
            retention_period_days: 'Säilytys Kausi (päivää)',
            retention_basis: 'Säilytys Basis',
            deletion_method: 'Deletion Menetelmä',
            deletion_secure_erase: 'Turvallinen poisto',
            deletion_anonymization: 'Anonymisointi',
            deletion_physical_destruction: 'Fyysinen tuhoaminen',
            deletion_other: 'Muu',
            responsible_party: 'Vastuutaho',
            responsible_user_id: 'Responsible Käyttäjä Tunniste',
            retention_expiry: 'Säilytys Expiry',
            hold_reason: 'Pidätys Reason',
            hold: 'Pidätys',
            release: 'Vapauta',
            hide_events: 'Piilota Tapahtumat',
            events_timeline: 'Tapahtumat Timeline',
            add_event: 'Lisää tapahtuma',
            event_type: 'Tapahtumatyyppi',
            create_event: 'Luo Tapahtuma',
            from_stage: 'From Vaihe',
            to_stage: 'To Vaihe',
            actor: 'Toimija',
            reason_placeholder: 'Syy...',
            go: 'Go',
            show_expired_retentions: 'Näytä Vanhentunut Säilytys',
            hide_expired_retentions: 'Piilota Vanhentunut Säilytys',
            expired_retentions_alert: 'Vanhentunut Säilytys Hälytys',
            failed_fetch: 'Epäonnistui: fetch lifecycle tietueet. Yritä uudelleen.',
            failed_save: 'Epäonnistui: save lifecycle tietue. Yritä uudelleen.',
            failed_transition: 'Epäonnistui: transition lifecycle stage. Yritä uudelleen.',
            failed_hold: 'Epäonnistui: place hold. Yritä uudelleen.',
            failed_release: 'Epäonnistui: release hold. Yritä uudelleen.',
            failed_fetch_events: 'Epäonnistui: fetch lifecycle tapahtuma. Yritä uudelleen.',
            failed_create_event: 'Epäonnistui: create lifecycle tapahtuma. Yritä uudelleen.',
            failed_expired: 'Epäonnistui: fetch expired retentions. Yritä uudelleen.',
            hold_reason_prompt: 'Syötä hold reason:',
            loaded_count: 'Ladattu {count} elinkaaren tietuetta',
            stage_transition_for: 'Vaiheen siirtymä kohteelle {id}',
            event_created: 'Luotu',
            event_updated: 'Päivitetty',
            event_approved: 'Hyväksytty',
            event_deprecated: 'Vanhentunut',
            event_proposed: 'Ehdotettu',
            event_reviewed: 'Tarkistettu',
            event_archived: 'Arkistoitu',
            event_restored: 'Palautettu',
        },
        gap_report: {
            title: 'Luokituksen puuteraportti',
            unlabeled: 'Merkitsemättömät tiedot',
            missing_lifecycle: 'Puuttuva elinkaari',
            stale_labels: 'Vanhentuneet merkinnät',
            run_detection: 'Suorita Puute Detection',
            detecting: 'Havaitaan...',
            detection_complete: 'Puute detection complete. {count} gap(s) detected.',
            detection_failed: 'Puute detection epäonnistunut.',
            by_severity: 'By Vakavuus',
            by_gap_type: 'By Puute Tyyppi',
            all_gap_types: 'Kaikki puutetyypit',
            conflicting_labels: 'Conflicting Tunnisteet',
            expired_retention: 'Vanhentunut Säilytys',
            missing_handling: 'Puuttuvien käsittely',
            info: 'Tiedot',
            gap_type: 'Puutetyyppi',
            object_id: 'Object Tunniste',
            object_type: 'Object Tyyppi',
            detected_at: 'Havaittu',
            open: 'Avaa',
            acknowledged: 'Kuitattu',
            remediated: 'Korjattu',
            accepted: 'Hyväksytty',
            acknowledge: 'Kuittaa',
            remediate: 'Korjaa',
            accept_risk: 'Hyväksy riski',
            accept_risk_prompt: 'Syötä riski acceptance perustelu:',
            failed_fetch: 'Epäonnistui: fetch puute. Yritä uudelleen.',
            failed_acknowledge: 'Epäonnistui: acknowledge puute. Yritä uudelleen.',
            failed_remediate: 'Epäonnistui: remediate puute. Yritä uudelleen.',
            failed_accept_risk: 'Epäonnistui: accept puute riski. Yritä uudelleen.',
            failed_detect: 'Epäonnistui: run puute detection. Yritä uudelleen.',
            loaded_count: 'Ladattu {count} luokitteluaukkoa',
            related_object_id: 'Liittyvän kohteen tunnus',
            related_object_type: 'Liittyvän kohteen tyyppi',
            gap_types: {
                unlabeled_asset: 'Tunnisteeton kohde',
                missing_lifecycle: 'Puuttuva elinkaari',
                expired_retention: 'Vanhentunut sailytys',
                extended_pending_deletion: 'Pidennetty poisto odottaa',
            },
        },
        label_workflow: {
            title: 'Tunnisteen työnkulku',
            proposed: 'Ehdotettu',
            under_review: 'Arvioitavana',
            approved: 'Hyväksytty',
            deprecated: 'Vanhentunut',
            submit_review: 'Lähetä arvioitavaksi',
            approve: 'Hyväksy tunniste',
            deprecate: 'Merkitse vanhentuneeksi',
            bulk_approve: 'Joukkohyväksyntä',
            bulk_approve_success: 'Tunnisteet hyväksytty.',
            submit_success: 'Tunniste lähetetty arvioitavaksi.',
            approve_success: 'Tunniste hyväksytty.',
            deprecate_success: 'Tunniste merkitty vanhentuneeksi.',
        },
        review_tasks: {
            title: 'Arviointitehtävät',
            create: 'Uusi arviointitehtävä',
            system: 'Järjestelmä',
            owner: 'Omistaja',
            description: 'Kuvaus',
            resolution: 'Ratkaisu',
            resolve: 'Ratkaise',
            approve: 'Hyväksy',
            modify: 'Muokkaa',
            reject: 'Hylkää',
            escalate: 'Eskaloi',
            escalated: 'Eskaloidut tehtävät',
            no_tasks: 'Ei arviointitehtäviä.',
            resolve_success: 'Arviointitehtävä ratkaistu.',
            status_pending: 'Odottaa',
            status_resolved: 'Ratkaistu',
            status_escalated: 'Eskaloitu',
            notes: 'Muistiinpanot',
            create_success: 'Tarkistustehtävä luotu.',
            no_tasks_detail: 'Ei tarkistustehtäviä vielä — luo tehtävä tunnisteiden tarkistusten osoittamiseksi järjestelmänomistajille.',
            label_id: 'Tunnisteen tunnus',
            system_id: 'Järjestelmätunnus',
            system_name: 'Järjestelmän nimi',
            owner_id: 'Omistajan tunnus',
            resolution_col: 'Ratkaisu',
            notes_col: 'Muistiinpanot',
        },
        exceptions: {
            title: 'Poikkeusjono',
            create: 'Uusi poikkeus',
            severity: 'Vakavuus',
            system: 'Järjestelmä',
            owner: 'Omistaja',
            resolve: 'Ratkaise',
            no_exceptions: 'Poikkeuksia ei löytynyt.',
            resolve_success: 'Poikkeus ratkaistu.',
            dashboard_open: 'Avoimet poikkeukset',
            dashboard_resolved: 'Ratkaistut poikkeukset',
            dashboard_total: 'Yhteensa',
            dashboard_by_severity: 'Vakavuuden mukaan',
            status_open: 'Avoin',
            status_resolved: 'Ratkaistu',
            status_accepted_risk: 'Hyväksytty riski',
            create_success: 'Poikkeus luotu.',
            no_exceptions_detail: 'Ei poikkeuksia vielä — poikkeukset luodaan vakavista puutteista, jotka vaativat erityiskäsittelyä.',
            gap_id: 'Puutteen tunnus',
            system_id: 'Järjestelmätunnus',
            system_name: 'Järjestelmän nimi',
            owner_id: 'Omistajan tunnus',
            resolution_notes: 'Ratkaisun muistiinpanot',
            resolution_notes_placeholder: 'Kuvaile kuinka tämä poikkeus ratkaistiin...',
            dashboard_accepted_risk: 'Accepted Risk',
            resolution_outcome: 'Outcome',
            resolve_success_accepted_risk: 'Exception closed as accepted risk.',
        },
        enterprise_map: {
            title: 'Yrityksen datakartta',
            inventory: 'Dataluettelo',
            total_systems: 'Järjestelmät yhteensä',
            total_labels: 'Tunnisteet yhteensä',
            approved_count: 'Hyväksytyt tunnisteet',
            proposed_count: 'Ehdotetut tunnisteet',
            no_data: 'Luokittelutietoja ei saatavilla.',
            no_approved_labels: 'Ei vielä merkittyjä kohteita — kohde näkyy datakartalla heti, kun sillä on luokittelutunniste, olipa se ehdotettu, tarkistettavana tai hyväksytty.',
            loaded_count: 'Yritystietokartta ladattu',
        },
        discovery: {
            title: 'Löytösyklit',
            create: 'Uusi sykli',
            cycle_name: 'Syklin nimi',
            run_delta: 'Suorita deltatunnistus',
            delta_success: 'Deltatunnistus valmis.',
            create_success: 'Löytösykli luotu.',
            no_cycles: 'Ei löytösyklejä.',
            status_created: 'Luotu',
            status_scanning: 'Skannaus',
            status_review: 'Tarkista',
            status_approved: 'Hyväksytty',
            status_published: 'Julkaistu',
            status_draft: 'Luonnos',
            running: 'Käynnissä',
            completed: 'Valmis',
            cancelled: 'Peruutettu',
            delta_result_summary: 'Löydettiin {added} lisättyä, {removed} poistettua, {changed} muutettua tunnistetta.',
            delta_results: 'Deltatuloukset',
            view_results: 'Näytä tulokset',
            scope_type: 'Laajuustyyppi',
            scope_type_all: 'Kaikki',
            scope_type_systems: 'Järjestelmät',
            scope_type_datasets: 'Tietojoukot',
            scope_ids: 'Laajuustunnukset',
            scope_ids_placeholder: 'Anna tunnukset pilkuilla erotettuina',
            cycle_explanation: 'Etsintäsykli skannaa tietoinfrastruktuurisi tietoresurssien tunnistamiseksi, luokittelemiseksi ja seuraamiseksi. Määritä laajuus kohdistamaan tiettyjä järjestelmiä tai tietojoukkoja.',
            empty_state_title: 'Ei vielä löytösyklejä',
            empty_state_description: 'Luo ensimmäinen etsintäjakso aloittaaksesi tietoresurssien skannauksen ja luokittelun.',
            empty_state_cta: 'Luo ensimmäinen sykli',
            validation_name_required: 'Jakson nimi vaaditaan',
            validation_scope_type_required: 'Laajuustyyppi vaaditaan',
            validation_scope_ids_required: 'Vähintään yksi laajuustunnus vaaditaan',
        },
        regulatory: {
            sensitivity_importance_info: 'Tietolaki (laki 60/2024/QH15, vaatimus 18e4bfd1) edellyttää luokittelua sekä herkkyyden että tärkeyden mukaan erillisinä ulottuvuuksina. Luo yksi taksonomia kullekin ulottuvuudelle täyttääksesi tämän vaatimuksen.',
            handling_rules_info: 'Jokaisella luokitustasolla tulisi määritellä käsittelysäännöt pyyntöä kohti. b89981ac ("Määritä käsittelysäännöt luokitustason mukaan").',
            template_internal_ids_info: 'Kaikki luokitusmallit käyttävät sisäisiä tunnuksia (esim. vn_data_law_data_classification_record). Mitään mallia ei esitetä virallisena ministeriön lomakkeena.',
            ai_risk_tiers_info: 'AI-laki (laki 134/2025/QH15, vaatimus 3a87ad63) määrittelee neljä AI-järjestelmän riskitasoa: minimaalinen, rajoitettu, korkea ja ei-hyväksyttävä. Luo AI-riskitaksonomia näillä tasoilla.',
            ai_risk_tier_minimal: 'Minimaalinen',
            ai_risk_tier_limited: 'Rajoitettu',
            ai_risk_tier_high: 'Korkea',
            ai_risk_tier_unacceptable: 'Ei-hyvaksyttava',
            ai_register_info: 'AI luokitustietueiden tulee linkittää AI järjestelmärekisterimerkintään (req. 0f4475d3), joka kaappaa: järjestelmän nimi, omistaja, tarkoitus, käyttöönottokonteksti, riskitaso, toimittajan osallistuminen.',
            ai_template_internal_info: 'AI-luokitusmallit käyttävät sisäisiä tunnuksia (esim. AI-RISK-01). Virallisia AI-paikkamerkkitunnuksia (AI-HC-01 – AI-HC-08) ei käytetä.',
            ai_audit_trail_info: 'AI luokittelumuutokset tuottavat versioidun kirjausketjun UC-VN-06: toimija, aikaleima, edellinen arvo, uusi arvo ja perustelut.',
            lifecycle_data_d1_info: 'Elinkaarisiirtymät kartoitetaan Data Law -menettelyyn DATA-D1 vaiheet: luetteloi, luokittele, määritä käsittelysäännöt, kerää todisteet, ajoita tarkistukset.',
            lifecycle_deletion_link_info: 'Luokiteltu tietoresurssi, joka saavuttaa käyttöikänsä lopun, käynnistää poistoaikataulutietueen deletion Orchestration -moduulin kautta.',
            lifecycle_evidence_info: 'Jokainen elinkaaritilan muutos (luonnos, aktiivinen, aliarvioitu, arkistoitu, poistettu) tallentaa toimijan, aikaleiman ja perustelun kirjausketjun merkinnäksi.',
            escalation_timers_info: 'UC-VN-10 eskaloinnin määräajat: 5 arkipäivää omistajan tarkastukselle, 48 tuntia poikkeuseskalaatiolle, 72 tuntia korkean riskin/arkaluonteisille kohteille.',
            escalation_owner_review: '5 arkipaivaa',
            escalation_exception: '48 tuntia',
            escalation_high_risk: '72 tuntia',
            gap_risk_areas_info: 'Tietolaki req. f6c3060a ("Datariskin arviointi") vaatii puutteiden analysoinnin neljältä riskialueelta: yksityisyys, kyberturvallisuus, kulunvalvonta ja toimintariski.',
            gap_risk_area_privacy: 'Tietosuoja',
            gap_risk_area_cybersecurity: 'Kyberturvallisuus',
            gap_risk_area_access: 'Paasyinhallinta',
            gap_risk_area_operational: 'Operatiivinen',
            gap_framework_filter_info: 'Suodata aukkoja sääntelykehyksen (VN_DATA_LAW tai VN_AI_LAW_2026) mukaan nähdäksesi tarvittavat aukot kullekin sääntelyalueelle.',
            framework_vn_data_law: 'VN Tietolaki',
            framework_vn_ai_law: 'VN Tekoalylaki 2026',
            enterprise_map_approved_info: 'Tämä datakartta näyttää vain hyväksytyt luokitustietueet UC-VN-10-työnkulun vaiheessa 10 ("Hyväksyttyjen tarrojen tulee nykyinen yrityksen totuus"). Luonnokset ja ehdotetut tietueet eivät sisälly.',
            exports_bundle_info: 'Vientipaketit sisältävät: luokitusrekisterin, käsittelysäännöt, omistajuuden/RACI:n ja todistepaketin – jotka vastaavat UC-VN-10 vientivaatimuksia ja DATA-EVD-01 todistelomakkeen muotoa.',
        },
    },
    monitoring: {
        no_permission_title: 'Riittamattomat oikeudet',
        no_permission_message: 'Sinulla ei ole oikeutta kayttaa tata valvontamoduulia.',
        errors: {
            fetch: 'Epäonnistui: load. Yritä uudelleen.',
            save: 'Epäonnistui: save. Yritä uudelleen.',
            fetch_programs_failed: 'Epäonnistui: fetch programs. Yritä uudelleen.',
            fetch_kpi_failed: 'Epäonnistui: fetch KPI tiedot. Yritä uudelleen.',
            create_snapshot_failed: 'Epäonnistui: create snapshot. Yritä uudelleen.',
            fetch_periods_failed: 'Epäonnistui: fetch periods. Yritä uudelleen.',
            create_period_failed: 'Epäonnistui: create period. Yritä uudelleen.',
            auto_generate_failed: 'Epäonnistui: auto-generate periods. Yritä uudelleen.',
            start_period_failed: 'Epäonnistui: start period. Yritä uudelleen.',
            close_period_failed: 'Epäonnistui: close period. Yritä uudelleen.',
            save_program_failed: 'Epäonnistui: save program. Yritä uudelleen.',
            activate_program_failed: 'Epäonnistui: activate program. Yritä uudelleen.',
            pause_program_failed: 'Epäonnistui: pause program. Yritä uudelleen.',
            complete_program_failed: 'Epäonnistui: complete program. Yritä uudelleen.',
            archive_program_failed: 'Epäonnistui: archive program. Yritä uudelleen.',
            fetch_ric_templates_failed: 'Epäonnistui: fetch RIC mallit. Yritä uudelleen.',
            fetch_ric_cases_failed: 'Epäonnistui: fetch RIC cases. Yritä uudelleen.',
            create_ric_case_failed: 'Epäonnistui: create RIC case. Yritä uudelleen.',
            transition_ric_failed: 'Epäonnistui: transition RIC case. Yritä uudelleen.',
            add_follow_up_failed: 'Epäonnistui: add follow-up. Yritä uudelleen.',
            respond_follow_up_failed: 'Epäonnistui: respond to follow-up. Yritä uudelleen.',
            link_evidence_failed: 'Epäonnistui: link todistusaineisto. Yritä uudelleen.',
            fetch_task_templates_failed: 'Epäonnistui: fetch tehtävä mallit. Yritä uudelleen.',
            save_task_template_failed: 'Epäonnistui: save tehtävä malli. Yritä uudelleen.',
            delete_task_template_failed: 'Epäonnistui: delete tehtävä malli. Yritä uudelleen.',
            delete_framework_link_failed: 'Kehyslinkin poistaminen epäonnistui.',
            seed_defaults_failed: 'Epäonnistui: seed defaults. Yritä uudelleen.',
            fetch_tasks_failed: 'Epäonnistui: fetch tehtävä. Yritä uudelleen.',
            create_task_failed: 'Epäonnistui: create tehtävä. Yritä uudelleen.',
            instantiate_tasks_failed: 'Epäonnistui: instantiate tehtävä. Yritä uudelleen.',
            start_task_failed: 'Epäonnistui: start tehtävä. Yritä uudelleen.',
            complete_task_failed: 'Epäonnistui: complete tehtävä. Yritä uudelleen.',
            defer_task_failed: 'Epäonnistui: defer tehtävä. Yritä uudelleen.',
            block_task_failed: 'Epäonnistui: block tehtävä. Yritä uudelleen.',
            override_complete_failed: 'Epäonnistui: override complete. Yritä uudelleen.',
            assign_task_failed: 'Epäonnistui: assign tehtävä. Yritä uudelleen.',
            update_checklist_failed: 'Epäonnistui: update checklist. Yritä uudelleen.',
        },
        nav: {
            programs: 'Ohjelmat',
            periods: 'Kaudet',
            task_templates: 'Tehtävämallit',
            tasks: 'Tehtävät',
            kpi: 'KPI-yhteenveto',
            ric: 'Viranomaistiedustelut',
            framework_link: 'Kehysvalvonta',
            authority_readiness: 'Viranomaisvalmius',
            change_review: 'Muutostarkastus',
            raci: 'RACI Matrix',
        },
        programs: {
            page_title: 'Valvontaohjelmat',
            title: 'Valvontaohjelmat',
            new: 'Uusi ohjelma',
            edit: 'Muokkaa ohjelmaa',
            name: 'Nimi',
            scope: 'Laajuus',
            frequency: 'Tiheys',
            owner: 'Omistaja',
            activate: 'Aktivoi',
            pause: 'Keskeytä',
            resume: 'Jatka',
            complete: 'Valmis',
            archive: 'Arkistoi',
            custom_frequency_days: 'Mukautettu Tiheys (päivää)',
            select_user: 'Valitse käyttäjä…',
            assign_training: 'Määritä koulutus',
            linked_forms: 'Linkitetyt lomakkeet',
            linked_forms_none: 'Ei linkitettyjä lomakkeita vielä',
            loaded_count: 'Ladattu {0} ohjelmaa',
            scope_labels: {
                full: 'Täysi',
                partial: 'Osittainen',
                targeted: 'Kohdennettu',
                consent: 'Suostumus',
                dpia: 'DPIA',
                vendors: 'Toimittajat',
                classification: 'Luokitus',
                rights_requests: 'Oikeuspyynnöt',
                data_mapping: 'Tietokartoitus',
                transfers: 'Siirrot',
                security_monitoring: 'Turvallisuuden valvonta',
                authority_response: 'Viranomaisen vastaus',
                evidence_preservation: 'Todisteiden säilyttäminen',
                localization_review: 'Lokalisoinnin tarkistus',
                ai_system_registration: 'AI Järjestelmän rekisteröinti',
                ai_pre_deployment: 'AI Käyttöönottoa edeltävä arviointi',
                ai_transparency: 'AI Läpinäkyvyystarkastus',
                ai_incident_review: 'AI Tapahtumakatsaus',
            },
            framework_cadence_info: 'Cybersecurity Law programs typically use continuous/monthly/quarterly cadences. AI Law programs typically use annual review cadences.',
        },
        periods: {
            page_title: 'Valvontajaksot',
            title: 'Tarkastuskaudet',
            new: 'Uusi kausi',
            label: 'Kauden nimi',
            start_date: 'Alkamispäivä',
            end_date: 'Päättymispäivä',
            reviewers: 'Tarkastajat',
            auto_generate: 'Luo kaudet automaattisesti',
            start: 'Aloita',
            close: 'Sulje',
            change_digest: 'Muutoskooste',
            no_digest: 'Ei digest available. löytynyt',
            reviewers_count: 'Arvioija Lukumäärä',
            select_program: 'Valitse ohjelma',
            select_program_prompt: 'Valitse a program to view periods.',
            hide_digest: 'Piilota Digest',
            loaded_count: '{0} jaksoa ladattu',
            periods_auto_generated: 'Jaksot autogeneroitu',
            label_placeholder: 'esim. 2026-Q1',
        },
        task_templates: {
            page_title: 'Tehtävämallit',
            title: 'Tehtävämallit',
            new: 'Uusi malli',
            edit: 'Muokkaa mallia',
            category: 'Kategoria',
            priority: 'Prioriteetti',
            checklist_items: 'Tarkistuslistan kohteet',
            effort: 'Arvioitu työmäärä (tuntia)',
            pre_seeded: 'Esitäytetty',
            seed_defaults: 'Lataa oletusarvot',
            add_checklist: 'Lisää tarkistuslistan kohde',
            guidance: 'Ohjeet',
            all_categories: 'Kaikki kategoriat',
            loaded_count: '{0} tehtävämallia ladattu',
            defaults_seeded: 'Oletustehtävämallit luotu',
            checklist_item_label: 'Tarkistuslistan kohta {0}',
            remove_checklist_item: 'Poista tarkistuslistan kohta {0}',
            category_info: 'Categories cover PDPL compliance, Cybersecurity Law tasks, and AI Law tasks.',
        },
        tasks: {
            page_title: 'Tarkistusetehtävät',
            title: 'Tarkastustehtävät',
            new: 'Uusi tehtävä',
            assigned_to: 'Vastuuhenkilö',
            due_date: 'Määräaika',
            checklist: 'Tarkistuslista',
            start: 'Aloita',
            complete: 'Valmis',
            defer: 'Lykkää',
            block: 'Estä',
            override_complete: 'Pakota valmiiksi',
            link_evidence: 'Linkitä todistusaineisto',
            direct_upload_label: 'Lataa todistetiedosto',
            instantiate: 'Luo malleista',
            defer_reason: 'Lykkäyksen syy',
            override_justification: 'Pakottamisen perustelu',
            filter_assignee: 'Suodata by assignee',
            select_period: 'Valitse kausi',
            select_program: 'Valitse ohjelma',
            select_program_period_prompt: 'Valitse a program and period to view tehtävä.',
            assign_placeholder: 'Määritä to...',
            loaded_count: 'Ladattu {0} tehtävää',
            tasks_instantiated: 'Tehtävät luotu malleista',
            overdue: 'Myöhässä',
            checklist_for: 'tarkistuslista kohteelle',
            manage_checklist: 'Hallitse tarkistuslistaa',
            add_checklist_item: 'Lisää kohde',
            checklist_item_label: 'Kohde',
            checklist_item_description: 'Kuvaus',
            remove_checklist_item: 'Poista kohde',
            save_checklist: 'Tallenna tarkistuslista',
            no_checklist_items: 'Ei vielä tarkistuslistan kohteita.',
            confirm_delete_attachment: 'Haluatko varmasti poistaa tämän todistetiedoston? Tätä ei voi kumota.',
        },
        kpi: {
            page_title: 'KPI-näkymä',
            title: 'KPI-yhteenveto',
            kpi_name: 'KPI:n nimi',
            current_value: 'Nykyinen arvo',
            target: 'Tavoite',
            trends: 'Trendit',
            capture_snapshot: 'Capture Tilannekuva',
            select_program: 'Valitse ohjelma',
            select_program_prompt: 'Valitse a program to view kpi.',
            snapshot: 'Tilannekuva',
            kpi_labels: {
                on_track: 'Aikataulussa',
                at_risk: 'At Riski',
                behind: 'Jäljessä',
                rights_request_sla_rate: 'Oikeuspyyntöjen SLA-aste',
                vendor_review_currency: 'Toimittajatarkistuksen ajantasaisuus',
                dpia_completion_rate: 'DPIA-valmistumisaste',
                classification_coverage: 'Luokituskattavuus',
                consent_policy_currency: 'Suostumuspolitiikan ajantasaisuus',
                security_event_detection_rate: 'Turvatapahtuman havaitsemisprosentti',
                evidence_preservation_compliance_rate: 'Todisteiden säilyttämisen vaatimustenmukaisuusaste',
                authority_response_timeliness: 'Viranomaisten vastauksen oikea-aikaisuus',
                localization_readiness_score: 'Lokalisoinnin valmiuspisteet',
                ai_register_completeness: 'AI Järjestelmärekisterin täydellisyys',
                pre_deployment_gate_pass_rate: 'Käyttöönottoa edeltävä portin pääsyprosentti',
                transparency_disclosure_coverage: 'Avoimuustietojen kattavuus',
                ai_incident_readiness_score: 'AI Tapahtumavalmiuden pisteet',
            },
            pdpl_kpi_info: 'The predefined KPI codes are PDPL-specific metrics. For Cybersecurity Law or AI Law monitoring, use framework-specific KPI codes.',
            cybersec_kpi_info: 'Kyberturvallisuuslain KPI:t: turvallisuustapahtumien havaitsemisprosentti, todisteiden säilyttämisen vaatimustenmukaisuusaste, viranomaisten vastauksen oikea-aikaisuus, lokalisointivalmiuspisteet.',
            ai_law_kpi_info: 'AI-lain KPI:t: AI-järjestelmärekisterin täydellisyys, käyttöönottoa edeltävän portin läpäisyprosentti, läpinäkyvyysilmoitusten kattavuus, AI-häiriövalmiuden pistemäärä.',
            no_data: 'Ei tiedot available. löytynyt',
            snapshot_date: 'Tilannekuva Päivämäärä',
            loaded_count: 'Ladattu {0} KPI-tilannekatsa­usta',
            snapshot_captured: 'KPI-tilannekatsaus otettu',
            showing_chart: 'Näytetään kaavionäkymä',
            showing_table: 'Näytetään taulukonäkymä',
            aria_current_values: 'KPI nykyiset arvot',
            kpi_label: 'KPI',
            actual: 'Toteutunut',
            trend: 'Suuntaus',
            notes: 'Huomiot',
            trend_up: 'Nouseva',
            trend_down: 'Laskeva',
            trend_stable: 'Vakaa',
        },
        ric: {
            page_title: 'RIC-tapaukset',
            title: 'Viranomaistiedustelut',
            new: 'Uusi tiedustelutapaus',
            reference: 'Viite',
            regulator: 'Viranomainen',
            inquiry_type: 'Tiedustelun tyyppi',
            deadline: 'Vastauksen määräaika',
            lead: 'Vastuuhenkilö',
            follow_ups: 'Jatkotoimet',
            add_follow_up: 'Lisää jatkotoimi',
            respond: 'Vastaa',
            question: 'Kysymys',
            response: 'Vastaus',
            asked_at: 'Kysytty',
            no_follow_ups: 'Ei vielä jatkotoimenpiteitä.',
            received_date: 'Received Päivämäärä',
            responded_at: 'Vastattu',
            hide_follow_ups: 'Piilota Follow-ups',
            inquiry_types: {
                general: 'Yleinen',
                investigation: 'Tutkimus',
                audit: 'Tarkastus',
                complaint: 'Valitus',
                general_audit: 'Yleinen tarkastus',
                rights_requests_review: 'Oikeuspyyntöjen tarkastus',
                cross_border_transfer_review: 'Rajat ylittävien siirtojen tarkastus',
                vendor_governance_review: 'Toimittajien hallinnan tarkastus',
                consent_management_review: 'Suostumustenhallinnan tarkastus',
                data_mapping_ropa_review: 'Tietokartoituksen / ROPA-tarkastus',
                incident_readiness_review: 'Häiriövalmiuden tarkastus',
                security_access_control_review: 'Tietoturvan ja pääsynhallinnan tarkastus',
                targeted_processing_review: 'Kohdennetun käsittelyn tarkastus',
                bank_readiness_pack: 'Pankkivalmiuspaketti',
            },
            link_evidence: 'Linkitä Todistusaineisto',
            loaded_count: '{0} RIC-tapausta ladattu',
            to_follow_up: 'seurantakysymykseen',
            overdue: 'Myöhässä',
            overdue_indicator: 'Vastausaika ylitetty',
            template_info: 'RIC templates are internal platform templates. They are not official government-mandated forms.',
            status_labels: {
                received: 'Vastaanotettu',
                in_triage: 'Lajittelussa',
                evidence_gathering: 'Todisteiden keruu',
                response_drafting: 'Vastausluonnos',
                internal_review: 'Sisäinen tarkistus',
                submitted: 'Lähetetty',
                follow_up: 'Seuranta',
                closed: 'Suljettu',
            },
        },
        framework_link: {
            title: 'Kehyslinkitetty valvonta',
            link_program: 'Linkitä ohjelma kehykseen',
            framework_code: 'Kehyskoodi',
            select_framework: 'Valitse viitekehys...',
            select_program: 'Valitse ohjelma...',
            readiness_score: 'Valmiuspisteet',
            no_links: 'Kehyslinkitettyjä ohjelmia ei löytynyt',
            seed_defaults: 'Luo oletusmallit',
            program_id: 'Ohjelma-ID',
            program: 'Ohjelma',
        },
        authority_readiness: {
            title: 'Viranomaispyyntövalmius',
            create_entry: 'Uusi valmiusmerkintä',
            select_framework: 'Valitse viitekehys...',
            framework_code: 'Kehys',
            readiness_status: 'Valmiustila',
            readiness_package: 'Valmiuspaketti',
            run_drill: 'Suorita valmiusharjoitus',
            drill_success: 'Valmiusharjoitus kirjattu',
            last_drill: 'Viimeisin harjoitus',
            never_drilled: 'Ei koskaan',
            no_readiness: 'Viranomaisvalmiustietoja ei löytynyt',
            entry_created: 'Valmiusmerkintä luotu',
            ready: 'Valmis',
            partial: 'Osittainen',
            not_ready: 'Ei valmis',
            items: 'kohteet',
        },
        change_review: {
            title: 'Sääntelymuutosten tarkastus',
            new_review: 'Uusi muutostarkastus',
            change_id: 'Muutos-ID',
            select_change: 'Valitse muutos...',
            select_framework: 'Valitse viitekehys...',
            impact_assessment: 'Vaikutusarviointi',
            decision: 'Päätös',
            no_action: 'Ei toimenpiteitä',
            internal_update: 'Sisäinen päivitys',
            authority_filing: 'Viranomaishakemus',
            no_reviews: 'Muutostarkastuksia ei löytynyt',
            auto_cycle: 'Automaattinen tarkastuskierto',
            cadence: 'Tarkastustiheys',
            framework: 'Viitekehys',
        },
        export_csv: 'Vie CSV',
        exporting: 'Viedään…',
        export_success: 'CSV vietiin onnistuneesti.',
        export_failed: 'CSV vienti epäonnistui.',
        export_attestation_pack: 'Vientitodistuspaketti',
        staleness: {
            badge_label: '{0} vanhentunutta kohdetta',
            days_since: '{0} päivää edellisestä päivityksestä',
            detail_title: 'Vanhentuneet esineet',
            accept_justification: 'Hyväksy perustelulla',
            justification_label: 'Perustelut',
            no_stale: 'Vanhoja kohteita ei löytynyt.',
            threshold_label: 'Vanhentumiskynnys (päivää)',
            threshold_min: 'Vähintään 30 päivää',
            threshold_max: 'Enintään 730 päivää',
            threshold_saved: 'Vanhentumiskynnys päivitetty.',
        },
        csv_columns: {
            name: 'Nimi',
            status: 'Tila',
            owner: 'Omistaja',
            created_at: 'Luotu klo',
            updated_at: 'Päivitetty klo',
            scope: 'Laajuus',
            frameworks: 'Kehykset',
            period_label: 'Kauden etiketti',
            start_date: 'Aloituspäivämäärä',
            end_date: 'Päättymispäivämäärä',
            task_count: 'Tehtävien määrä',
            completion_rate: 'Valmistumisaste',
            title: 'Otsikko',
            assigned_to: 'Määrätty vastaanottajalle',
            due_date: 'Eräpäivä',
            priority: 'Prioriteetti',
            completed_at: 'Valmistunut klo',
            kpi_code: 'KPI Koodi',
            kpi_label: 'KPI Tunniste',
            target: 'Kohde',
            actual: 'Todellinen',
            trend: 'Trendi',
            reference: 'Viite',
            regulator: 'Säädin',
            inquiry_type: 'Kyselyn tyyppi',
            lead: 'Lyijy',
            received_date: 'Vastaanottopäivämäärä',
            deadline: 'Määräaika',
            framework_code: 'Puitekoodi',
            readiness_status: 'Valmiustila',
            package_count: 'Pakettien määrä',
            change_id: 'Vaihda ID',
            impact_assessment: 'Vaikutusten arviointi',
            decision: 'Päätös',
            program: 'Ohjelma',
            readiness_score: 'Valmiuspisteet',
            category: 'Luokka',
            checklist_count: 'Tarkistuslistan määrä',
            effort_hours: 'Ponnistelu (tunteja)',
            version: 'Versio',
            department: 'osasto',
            function_area: 'Toimintoalue',
            role: 'Rooli',
            assignee: 'Valtuutettu',
            frequency: 'Taajuus',
        },
        raci: {
            page_title: 'Hallinto RACI Matrix',
            title: 'Hallinto RACI Matrix',
            department: 'osasto',
            function_area: 'Toimintoalue',
            responsible: 'Vastuullinen',
            accountable: 'Vastuullinen',
            consulted: 'Konsultoitu',
            informed: 'Tietoinen',
            assign_role: 'Määritä RACI-rooli',
            select_program: 'Valitse Ohjelma',
            select_program_prompt: 'Valitse ohjelma tarkastellaksesi RACI-matriisia.',
            no_assignments: 'RACI-tehtäviä ei löytynyt.',
            role_updated: 'RACI-tehtävä päivitetty.',
            role_labels: {
                R: 'Vastuullinen',
                A: 'Vastuullinen',
                C: 'Konsultoitu',
                I: 'Tietoinen',
            },
            loaded_count: 'Ladattu {0} RACI-tehtäviä',
            single_accountability_warning: 'Vain yksi vastuullinen (A) rooli on sallittu toimintoaluetta kohden. Uuden vastuullisen roolin määrittäminen korvaa nykyisen.',
            cybersec_function_areas: 'Kyberturvallisuuslain toimintoalueet: Tapauksiin reagointi, todisteiden säilyttäminen, viranomaisten yhteistyö, lokalisoinnin tarkistus.',
            ai_law_function_areas: 'AI Lain toiminta-alueet: järjestelmän rekisteröinti, käyttöönottoa edeltävä arviointi, läpinäkyvyys, tapausten käsittely.',
            suggested_function_areas: 'Suositellut toiminta-alueet',
        },
        prior_cycle: {
            link_label: 'Linkki aikaisempaan kiertoon',
            select_prior: 'Valitse edellinen sykli…',
            linked: 'Vuodesta toiseen',
            no_prior: 'Aikaisempia jaksoja ei ole saatavilla.',
            linked_success: 'Aikaisempi sykli yhdistetty.',
        },
        digest_table: {
            module: 'Moduuli',
            entity_type: 'Entiteettityyppi',
            change_type: 'Muuta tyyppiä',
            description: 'Kuvaus',
            changed_at: 'Vaihdettu klo',
        },
        view_related_ric: 'Katso liittyviä RIC-tapauksia',
        linked_program: 'Linkitetty ohjelma',
        nav_raci: 'RACI Matrix',
    },
    incidents: {
        authority_response_draft: {
            draft_label: 'Vastausluonnos',
            drafting: 'Luonnostetaan…',
            results_heading: 'Luonnolliset vastaukset ({count})',
            tokens_label: '≈ {count}-merkkejä',
            counsel_label: 'Asianajajan arviointi vaaditaan:',
            counsel_default: 'Vähintään yksi vastaus vaatii neuvonantajan tarkastuksen ennen lähettämistä.',
            requires_counsel_label: 'Vaatii neuvonantajan tarkastuksen.',
            accept_label: 'Hyväksy luonnos',
            reject_label: 'Hylkää luonnos',
            error_generic: 'Vastausta ei voitu laatia. Yritä uudelleen tai tarkenna kyselytekstiä.',
            question_action_accepted: 'Accept answer',
            question_action_rejected: 'Reject answer',
            question_action_needs_counsel: 'Needs counsel',
            question_action_aria: '{action} ({question})',
            question_decision_label: 'Decision on this answer: {decision}',
            question_decision_accepted: 'accepted',
            question_decision_rejected: 'rejected',
            question_decision_needs_counsel: 'sent for counsel review',
            download_docx_label: 'Download response template (DOCX)',
            draft_resolved_accepted: 'Every answer is decided. The draft is accepted.',
            draft_resolved_rejected: 'Every answer is decided. The draft is rejected.',
        },
        breach_timeline: {
            compute_label: 'Laske rikkomusilmoitusten aikajana',
            computing: 'Lasketaan…',
            results_heading: 'Rikkomusilmoitusten määräajat ({count})',
            tokens_label: '≈ {count}-merkkejä',
            handoff_label: 'Forseti ehdottaa:',
            empty_label: 'Mikään asennettu kehys ei ilmoita tätä tapausta koskevaa ilmoitusvelvollisuutta.',
            mandatory_label: 'Pakolliset kentät:',
            draft_label: 'Ilmoitusluonnos',
            remaining_label: '{count} tuntia jäljellä',
            overdue_label: 'MYÖNNETTY {count}h',
            acknowledge_label: 'Kuittaa aikajana',
            acknowledging: 'Tunnustetaan…',
            error_generic: 'Rikkomisen aikajanaa ei voitu laskea. Yritä uudelleen.',
            tasks_note: 'Urgent tasks were raised for the duties that match this incident’s jurisdictions. Computing again reuses them instead of creating more.',
        },
        breach_determination: {
            yes: 'Confirmed Tietoturvaloukkaus',
            no: 'Not a Tietoturvaloukkaus',
            uncertain: 'Odottaa',
        },
        errors: {
            fetch: 'Epäonnistui: load vaaratilanne. Yritä uudelleen.',
            save: 'Epäonnistui: save vaaratilanne. Yritä uudelleen.',
            fetch_failed: 'Epäonnistui: fetch vaaratilanne. Yritä uudelleen.',
            save_failed: 'Epäonnistui: save vaaratilanne. Yritä uudelleen.',
            transition_failed: 'Epäonnistui: transition vaaratilanne. Yritä uudelleen.',
            close_failed: 'Epäonnistui: close vaaratilanne. Yritä uudelleen.',
            fetch_timeline_failed: 'Epäonnistui: fetch timeline. Yritä uudelleen.',
            fetch_decisions_failed: 'Epäonnistui: fetch decisions. Yritä uudelleen.',
            triage_failed: 'Epäonnistui: update lajittelu. Yritä uudelleen.',
            impact_update_failed: 'Epäonnistui: update impact arviointi. Yritä uudelleen.',
            breach_update_failed: 'Epäonnistui: update tietoturvaloukkaus arviointi. Yritä uudelleen.',
            create_timeline_failed: 'Epäonnistui: create timeline merkintä. Yritä uudelleen.',
            create_decision_failed: 'Epäonnistui: create decision. Yritä uudelleen.',
            notification_update_failed: 'Epäonnistui: update notification. Yritä uudelleen.',
            // Sprint 62.35.0 gate E12.5 pass 2 (P2-1) — English until Phase 25 (R-57)
            breach_conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so the breach assessment was not saved. The latest version of the incident is being loaded and your entries are kept: check them and save again.",
            notification_status_conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so the notification status was not changed. The latest version of the incident is being loaded: check the status and change it again if it still needs changing.",
            fetch_tasks_failed: 'Epäonnistui: fetch tehtävä. Yritä uudelleen.',
            create_task_failed: 'Epäonnistui: create tehtävä. Yritä uudelleen.',
            update_task_status_failed: 'Tehtävän tilan päivittäminen epäonnistui. Yritä uudelleen.',
            update_checklist_failed: 'Epäonnistui: update checklist. Yritä uudelleen.',
            assign_task_failed: 'Epäonnistui: assign tehtävä. Yritä uudelleen.',
            escalate_task_failed: 'Epäonnistui: escalate tehtävä. Yritä uudelleen.',
            fetch_templates_failed: 'Epäonnistui: fetch mallit. Yritä uudelleen.',
            permission_denied: 'Sinulla ei ole oikeutta tahan osioon. Ota yhteytta yllapitajaan pyytaaksesi kayttooikeutta.',
        },
        harm_likelihood: {
            unlikely: 'Epätodennäköinen',
            possible: 'Mahdollinen',
            likely: 'Todennäköinen',
            very_likely: 'Erittäin todennäköinen',
        },
        // Sprint 63.37.0 (legal second read S12) — the impact assessment's two vocabularies as words.
        data_category_options: {
            personal_data: 'Personal data',
            sensitive_personal_data: 'Sensitive personal data',
            financial_data: 'Financial data',
            health_data: 'Health data',
            childrens_data: "Children's personal data",
            other: 'Other personal data',
        },
        consequence_options: {
            identity_theft: 'Identity theft',
            financial_loss: 'Financial loss',
            reputational_damage: 'Reputational damage',
            discrimination: 'Discrimination',
            emotional_distress: 'Emotional distress',
            physical_harm: 'Physical harm',
            loss_of_confidentiality: 'Loss of confidentiality',
            loss_of_access: 'Loss of access to the data',
        },
        reported_via: {
            email: 'Sähköposti',
            phone: 'Puhelin',
            internal_system: 'Sisäinen Järjestelmä',
            third_party: 'Kolmas osapuoli',
            automated_detection: 'Automaattinen Detection',
        },
        task_transition_labels: {
            start: 'Aloita',
            complete: 'Viimeistele',
            block: 'Estä',
            defer: 'Lykkää',
            cancel: 'Peruuta',
            unblock: 'Poista esto',
        },
        transition_labels: {
            start: 'Aloita',
            complete: 'Viimeistele',
            block: 'Estä',
            defer: 'Lykkää',
        },
        nav: {
            all: 'Kaikki tapaukset',
            tasks: 'Vastaustoimet',
            reports: 'Raportit',
            breach_notifications: 'Tietoturvaloukkasilmoitukset',
            supplements: 'Täydennykset',
            remediation: 'Korjaustoimet',
            authority_requests: 'Viranomaiskyselyt',
            disclosure_register: 'Julkistamisrekisteri',
        },
        list: {
            page_title: 'Tapahtumat',
            title: 'Tapaukset',
            new: 'Uusi tapaus',
            edit: 'Muokkaa tapausta',
            incident_title: 'Otsikko',
            severity: 'Vakavuus',
            reported_at: 'Ilmoitettu',
            reported_by: 'Ilmoittaja',
            assigned_to: 'Vastuuhenkilö',
            view_detail: 'Näytä tiedot',
            reference: 'Viite',
            lead: 'Vetäjä',
            reported_via: 'Raportoitu kautta',
            discovery_date: 'Discovery Päivämäärä',
            // fix-sprint #447 W10 — discovery is a date and time
            discovery_hint: 'The date and time the incident was discovered. Hour-based notification clocks run from this moment.',
            discovery_no_time: '(no time of day recorded)',
            discovery_day_only_hint: 'Discovery was recorded as the day {day}, with no time of day. Enter the date and time to record it, or leave it empty to keep the day.',
            hide_details: 'Piilota Yksityiskohdat',
            close_incident: 'Sulje Vaaratilanteita',
            close_justification_prompt: 'Please provide a perustelu for closing this vaaratilanne.',
            justification: 'Perustelu',
            lead_id: 'Lead Tunniste',
            lead_not_assigned: 'Not assigned',
            date_not_recorded: 'Not recorded',
            contact: 'Yhteyshenkilö',
            category: 'Kategoria',
            not_assessed: 'Ei arvioitu',
        },
        detail: {
            page_title: 'Tapahtuman yksityiskohta',
            title: 'Tapauksen tiedot',
            page_description: 'One incident in full: its overview, triage, impact and breach assessments, timeline, decisions, evidence and audit trail.',
            forseti_heading: 'Forseti for this incident',
            tabs: {
                overview: 'Yleiskatsaus',
                triage: 'Arviointi',
                impact: 'Vaikutusarviointi',
                breach: 'Tietoturvaloukkauksen arviointi',
                timeline: 'Aikajana',
                decisions: 'Päätökset',
                evidence: 'Todisteet',
                audit_trail: 'Audit Trail',
            },
            evidence_upload_label: 'Liitä todistetiedosto',
            evidence_uploaded: 'Todiste ladattu',
            evidence_deleted: 'Todiste poistettu',
            evidence_delete_error: 'Todisteen poisto epäonnistui',
            evidence_uploaded_at: 'Ladattu',
            evidence_empty: 'Tähän tapahtumaan ei ole liitetty todistetiedostoja.',
            evidence_size: 'Koko',
            triage_notes: 'Arvioinnin muistiinpanot',
            initial_classification: 'Alustava luokitus',
            individuals_affected: 'Henkilöiden lukumäärä',
            data_categories: 'Käsitellyt tietokategoriat',
            systems_compromised: 'Vaarantuneet järjestelmät',
            geographic_scope: 'Maantieteellinen laajuus',
            breach_determination: 'Tietoturvaloukkauksen määrittely',
            // fix-sprint #447 W10 — the notification clock runs from discovery
            notification_clock: {
                label: 'Notification clock',
                started_from_discovery: 'Running from the recorded discovery: {date}',
                started_at_triage: 'Running from {date}, when the breach was determined; no discovery was recorded then. Recording the discovery dates the clock from it.',
                started_from_earlier_discovery: 'Running from {date}, the discovery recorded when the breach was determined. The discovery recorded now is different; the clock is not moved.',
                not_started_no_discovery: 'Not started: a breach was determined, but no discovery date and time is recorded. The clock runs from discovery and is never started from triage. Record when the incident was discovered to start it.',
                not_started_no_determination: 'Not started: no breach has been determined. Once one is, the clock runs from the recorded discovery.',
            },
            notification_required: 'Ilmoitus vaaditaan',
            notification_deadline: 'Ilmoituksen määräaika',
            pdpc_reportable: 'Ilmoitettava tietosuojaviranomaiselle',
            add_timeline: 'Lisää aikajanan merkintä',
            add_decision: 'Lisää päätös',
            not_found: 'Vaaratilanteita not found.',
            team_members: 'Tiimin jäsenet',
            checklist: 'Tarkistuslista',
            completed: 'Valmis',
            impact_severity_score: 'Calculated Vaikutus Vakavuus Pistemäärä',
            records_affected: 'Tietueet Affected',
            potential_consequences: 'Mahdolliset seuraukset',
            harm_likelihood: 'Harm Todennäköisyys',
            legal_basis: 'Oikeusperuste',
            legal_basis_not_recorded: 'Not recorded',
            jurisdictions: 'Toimivalta',
            jurisdictions_comma: 'Toimivalta (comma-separated)',
            jurisdiction: 'Toimivalta',
            authority_status: 'Valtuudet Tila',
            subject_status: 'Subject Tila',
            authority_deadline: 'Valtuudet Deadline',
            subject_deadline: 'Kohteen määräaika',
            mark_authority_done: 'Mark Valtuudet Done',
            mark_subject_done: 'Merkitse kohde valmiiksi',
            // Sprint 62.35.0 gate E12.5 pass 2 (P2-2) — English until Phase 25 (R-57)
            requirement_statuses: {
                not_required: 'Not required',
                required: 'Required',
                in_progress: 'In progress',
                completed: 'Completed',
                overdue: 'Overdue',
            },
            status_contradiction: {
                declared_required: 'The assessment now declares the {notification} required; its recorded status is {status}.',
                declared_not_required: 'The assessment now declares the {notification} not required; its recorded status is {status}.',
                notifications: {
                    authority_status: 'notification to the authority',
                    subject_status: 'notification to the data subjects',
                    other_status: 'notification to other parties',
                },
            },
            // Sprint 62.35.0 (AC9) — English until Phase 25 (R-57)
            affected_parties: {
                title: 'Affected parties notified',
                intro: 'Organisations and individuals concerned, other than the data subjects, who were told of this incident under this requirement, who are still to be told, or who need not be told.',
                requirement_heading: 'Requirement: {jurisdiction}',
                no_requirements: 'No notification requirement is recorded on this incident, so there is no requirement to record affected parties against.',
                empty: 'No affected party is recorded for this requirement.',
                not_recorded: 'Not recorded',
                add: 'Add affected party',
                add_aria: 'Add affected party for requirement {jurisdiction}',
                edit: 'Edit',
                edit_aria: 'Edit affected party: {name}',
                remove: 'Remove',
                remove_aria: 'Remove affected party: {name}',
                remove_confirm_title: 'Remove this affected party?',
                remove_confirm_message: 'The row is removed from this requirement. The audit trail keeps what it recorded.',
                modal_add_title: 'Add affected party',
                modal_edit_title: 'Edit affected party',
                table_label: 'Affected parties for requirement {jurisdiction}',
                col_party: 'Party',
                col_type: 'Type',
                col_status: 'Status',
                col_date: 'Date notified',
                field_party_name: 'Party name',
                field_party_type: 'Party type',
                field_contact: 'Contact',
                field_status: 'Notification status',
                field_notified_on: 'Date notified',
                field_reason: 'Why notification is not required',
                field_note: 'Note',
                types: {
                    data_owner: 'Data owner',
                    partner: 'Partner',
                    handover_recipient: 'Recipient of a handover',
                    other: 'Other',
                },
                statuses: {
                    pending: 'Pending',
                    notified: 'Notified',
                    not_required: 'Not required',
                },
                errors: {
                    party_name_required: 'Enter the name of the party.',
                    notified_on_required: 'Enter the date the party was notified.',
                    notified_on_in_future: 'The date notified cannot be in the future.',
                    reason_required: 'Enter why notification is not required.',
                    save_failed: 'The affected party could not be saved.',
                    remove_failed: 'The affected party could not be removed.',
                    conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so nothing was saved. The latest version of the incident is being loaded and your entries are kept: check them and save again.",
                    remove_conflict: "This incident's notification requirements were changed by another save after this page last loaded them, so the party was not removed. The latest version of the incident is being loaded: check the list and remove the party again if it is still there.",
                    conflict_party_changed: 'Another save changed this affected party after you opened it, so nothing was saved. The form now shows what that save recorded, and the entries you made were not kept: review the values and enter your changes again before saving.',
                    conflict_party_gone: 'Another save removed this affected party after you opened it, so nothing was saved and the form was closed.',
                    conflict_requirement_gone: 'Another save removed this notification requirement from the incident after you opened the form, so nothing was saved and the form was closed.',
                },
                saved: 'Affected party saved',
                removed: 'Affected party removed',
            },
            // Sprint 62.45.0 — the service-interruption window on the incident record
            interruption_window: {
                title: 'Interruption window',
                intro: 'When a system or service stopped, when it came back, and when remediation was completed. Recorded once, here, so anything that follows from these times reads them from this record rather than retyping them. Remediation completed is not the same as closing the incident.',
                // Gate E12.5 pass 3 (QA P2) — shown only to a viewer who can read the register.
                intro_register: 'When a supervised system or service stopped, when it came back, and when remediation was completed. Recorded once, here: the Supervisory Reporting register reads these times for the interruption notice and the incident report and never retypes them. Remediation completed is not the same as closing the incident.',
                started_at: 'Interruption started',
                ended_at: 'Interruption ended',
                remediation_completed_at: 'Remediation completed',
                not_recorded: 'Not recorded',
                ongoing: 'No end is recorded, so the interruption is treated as continuing.',
                edit: 'Edit interruption window',
                save: 'Save interruption window',
                saving: 'Saving…',
                cancel: 'Cancel',
                local_time_hint: 'Your local date and time. Leave a field empty to clear it.',
                unchanged: 'Change a time to save.',
                saved: 'Interruption window saved',
                save_failed: 'The interruption window could not be saved.',
                errors: {
                    start_first: 'Record when the interruption started first.',
                    before_start: 'This cannot be earlier than the interruption start.',
                    invalid: 'The interruption window is not in order, so nothing was saved.',
                },
            },
            not_assessed: 'Ei arvioitu',
            event_type: 'Tapahtumatyyppi',
            timestamp: 'Aikaleima',
            actor: 'Toimija',
            no_timeline_events: 'No timeline tapahtuma yet.',
            decision_type: 'Päätös Tyyppi',
            decision: 'Päätös',
            rationale: 'Perustelu',
            decision_made: 'Päätös Made',
            no_decisions: 'Ei vielä tallennettuja päätöksiä.',
            no_audit_events: 'Tarkastustapahtumia ei ole vielä tallennettu.',
            export_pdf: 'Vie PDF',
            pdf_title: 'Tapahtumaraportti',
            description: 'Kuvaus',
            linked_records: 'Linkitetyt Tietueet',
            export_success: 'Incident report exported',
        },
        tasks: {
            page_title: 'Hätätilanteiden torjuntatehtävät',
            title: 'Tapauksen vastaustoimet',
            new: 'Uusi tehtävä',
            edit: 'Muokkaa tehtävää',
            task_type: 'Tehtävän tyyppi',
            assignee: 'Vastuuhenkilö',
            due_date: 'Määräaika',
            dependencies: 'Riippuvuudet',
            mark_complete: 'Merkitse valmiiksi',
            templates: 'Tehtävämallit',
            select_incident: 'Valitse tapaus',
            all_statuses: 'Kaikki tilat',
            all_categories: 'Kaikki kategoriat',
            new_response_task: 'Uusi vastaustehtävä',
            category: 'Kategoria',
            priority: 'Prioriteetti',
            depends_on: 'Riippuu (pilkuin erotetut tehtävätunnukset)',
            checklist_items: 'Tarkistuslistan kohteet',
            remove: 'Poista',
            add: 'Lisää',
            new_checklist_item: 'Uusi tarkistuslistan kohde',
            no_templates: 'Malleja ei saatavilla.',
            assign_task: 'Vastuuta tehtävä',
            assign_to: 'Vastuuhenkilö',
            assign: 'Vastuuta',
            select_incident_prompt: 'Valitse tapaus nähdäksesi sen vastaustoimet.',
            checklist: 'Tarkistuslista',
            hide_checklist: 'Piilota tarkistuslista',
            escalate: 'Eskaloi',
            escalations: 'Eskalaatiot',
            no_checklist_items: 'Ei tarkistuslistan kohteita.',
        },
        reports: {
            page_title: 'Tapahtumaraportit',
            title: 'Tapausraportit',
            summary: 'Yhteenvetotilastot',
            by_severity: 'Vakavuuden mukaan',
            by_status: 'Tilan mukaan',
            recent: 'Viimeisimmät tapaukset',
            total_incidents: 'Tapauksia yhteensä',
            open: 'Avoimet',
            closed: 'Suljetut',
            export_csv: 'Vie CSV',
            export_pdf: 'Vie PDF',
            export_success: 'Raportti viety onnistuneesti.',
            export_error: 'Raportin vienti epäonnistui.',
            reference: 'Viite',
            view_report: 'Näytä raportti',
        },
        statuses: {
            reported: 'Ilmoitettu',
            triage: 'Arviointi',
            contained: 'Rajattu',
            investigation: 'Tutkinta',
            remediation: 'Korjaus',
            closed: 'Suljettu',
            false_positive: 'Väärä hälytys',
        },
        task_statuses: {
            pending: 'Odottaa',
            in_progress: 'Käynnissä',
            completed: 'Valmis',
            blocked: 'Estetty',
            cancelled: 'Peruutettu',
        },
        severity: {
            low: 'Matala',
            medium: 'Keskitaso',
            high: 'Korkea',
            critical: 'Kriittinen',
        },
        task_types: {
            containment: 'Rajaaminen',
            investigation: 'Tutkinta',
            remediation: 'Korjaus',
            communication: 'Viestintä',
            documentation: 'Dokumentointi',
            notification: 'Ilmoitus',
        },
        categories: {
            ai_system: 'AI System',
            data_breach: 'Tiedot Tietoturvaloukkaus',
            unauthorized_access: 'Unauthorized Pääsy',
            data_loss: 'Tiedot Loss',
            system_compromise: 'Järjestelmä Compromise',
            malware: 'Haittaohjelma',
            phishing: 'Tietojenkalastelu',
            insider_threat: 'Sisäinen uhka',
            other: 'Muu',
        },
        reported_via_options: {
            email: 'Sähköposti',
            phone: 'Puhelin',
            internal_system: 'Sisäinen Järjestelmä',
            third_party: 'Kolmas osapuoli',
            automated_detection: 'Automaattinen Detection',
        },
        harm_likelihood_options: {
            unlikely: 'Epätodennäköinen',
            possible: 'Mahdollinen',
            likely: 'Todennäköinen',
            very_likely: 'Erittäin todennäköinen',
        },
        breach_determination_options: {
            confirmed_breach: 'Confirmed Tietoturvaloukkaus',
            suspected_breach: 'Suspected Tietoturvaloukkaus',
            not_a_breach: 'Not a Tietoturvaloukkaus',
            pending: 'Odottaa',
        },
        timeline_event_types: {
            detection: 'Havaitseminen',
            escalation: 'Eskalaatio',
            containment: 'Rajaus',
            investigation_start: 'Tutkimus Aloita',
            evidence_collected: 'Todistusaineisto Collected',
            notification_sent: 'Ilmoitus Sent',
            remediation_action: 'Korjaus Toiminto',
            status_change: 'Tila Change',
            communication: 'Viestintä',
            other: 'Muu',
        },
        decision_types: {
            containment: 'Rajaus',
            notification: 'Ilmoitus',
            remediation: 'Korjaus',
            escalation: 'Eskalaatio',
            closure: 'Sulkeminen',
            other: 'Muu',
        },
        task_transitions: {
            start: 'Aloita',
            complete: 'Viimeistele',
            block: 'Estä',
            cancel: 'Peruuta',
            unblock: 'Poista esto',
        },
        announce: {
            loaded: '{count} tapahtumaa ladattu',
            reports_loaded: '{count} tapahtumaraporttia ladattu',
            tasks_loaded: '{count} tehtävää ladattu',
            created: 'Tapahtuma luotu onnistuneesti',
            updated: 'Tapahtuma päivitetty onnistuneesti',
            closed: 'Tapahtuma päättyi onnistuneesti',
            status_changed: 'Tapahtumatilanne muuttunut',
            triage_completed: 'Triage suoritettu onnistuneesti',
            impact_updated: 'Vaikutustenarviointi päivitetty',
            breach_updated: 'Rikkomusarvio päivitetty',
            timeline_added: 'Aikajanan tapahtuma lisätty',
            decision_recorded: 'Päätös kirjattiin',
            notification_updated: 'Ilmoituksen tila päivitetty',
            task_created: 'Tehtävän luominen onnistui',
            task_status_changed: 'Tehtävän tila muuttunut',
            checklist_completed: 'Tarkistuslistan kohta suoritettu',
            checklist_unchecked: 'Tarkistusluettelon kohdetta ei ole valittu',
            task_assigned: 'Tehtävän määritys onnistui',
            task_escalated: 'Tehtävä eskaloitui',
            export_initiated: 'Vienti aloitettu',
        },
        breach_notifications: {
            page_title: 'Rikkomusilmoitukset',
            title: 'Rikkomusilmoitukset',
            new_notification: 'Uusi ilmoitus',
            no_notifications: 'Rikkomusilmoituksia ei löytynyt',
            status: 'Tila',
            submit_to_mps: 'Lähetä MPS',
            decision: 'Päätös',
            decision_maker: 'Päätöksentekijä',
            notify: 'Ilmoita',
            do_not_notify: 'Älä ilmoita',
            rationale: 'Perustelut',
            filing_deadline: 'Arkistoinnin määräaika',
            overdue: 'Myöhässä',
            due_soon: 'Pian erääntyvä',
            on_track: 'Aikataulussa',
            legal_review: 'Lakitarkastus',
            incident_id: 'Tapahtuman tunnus',
            // Fix sprint #447 W1 — the decision moves the status; submission through /submit.
            decision_hint: 'Notify moves the notification to Decision to Notify, ready to draft. Do Not Notify closes it; the decision and its rationale are kept.',
            rationale_required: 'Enter the rationale for this decision.',
            mark_drafted: 'Mark notification drafted',
            submission_channel: 'Submission channel',
            submission_confirmation: 'Confirmation or reference',
            submission_note: 'This records the submission your organisation made. The platform files nothing with the authority.',
            select_channel: 'Select a channel',
            channels_load_failed: 'The submission channels could not be read.',
            channel_refusal_no_framework: 'No installed framework declares an authority filing, so no submission can be recorded.',
            channel_refusal_no_channels: 'The filing framework declares no submission channels, so no submission can be recorded.',
            channel_refusal_no_authority: 'The filing framework names no authority that accepts filings, so no submission can be recorded.',
            notification_statuses: {
                assessment: 'Arviointi',
                decision_to_notify: 'Päätös ilmoittaa',
                notification_drafted: 'Ilmoitus laadittu',
                legal_review: 'Lakitarkastus',
                submitted_to_authority: 'Jätetty viranomaiselle',
                supplement_requested: 'Täydennys pyydetty',
                supplement_submitted: 'Täydennys lähetetty',
                acknowledged: 'Tunnustettu',
                closed: 'Suljettu',
            },
            select_incident: 'Linked Incident',
            // Breach notifications page layout — summary band, details dialog, forms dialog.
            page_description: 'Decide whether each personal data breach has to be notified, record that decision with its rationale, and follow every filing deadline through to the submission your organisation makes to the authority.',
            kpi_total: 'Notifications',
            kpi_awaiting_decision: 'Awaiting decision',
            kpi_deadline_at_risk: 'Due soon or overdue',
            kpi_submitted: 'Submitted',
            filter_toolbar: 'Filter breach notifications',
            results_count: 'Showing {shown} of {total} notifications',
            incident: 'Incident',
            forms: 'Forms',
            forms_count_one: '1 form',
            forms_count: '{count} forms',
            no_forms: 'No forms',
            forms_dialog_title: 'Forms filed on {incident}',
            view_details: 'View details',
            details_title: 'Breach notification details',
            details_decision_section: 'Decision and submission',
            created_at: 'Created',
            submitted_at: 'Submitted on',
        },
        supplements: {
            page_title: 'Täydennyspyynnöt',
            title: 'Täydennyspyynnöt',
            new_supplement: 'Uusi Täydennys',
            no_supplements: 'Täydennyspyyntöjä ei löytynyt',
            request_details: 'Pyydä lisätietoja',
            response_package: 'Vastauspaketti',
            submit_response: 'Lähetä vastaus',
            deadline: 'Määräaika',
            notification_id: 'Ilmoituksen tunnus',
            computed_deadline: 'Computed deadline',
            computed_deadline_none: 'None computed',
            computed_deadline_pending: 'Not started yet',
            enter_notification_id: 'Anna ilmoituksen tunnus nähdäksesi lisäykset.',
            supplement_statuses: {
                requested: 'Pyydetty',
                drafting: 'Luonnos',
                submitted: 'Lähetetty',
                accepted: 'Hyväksytty',
                rejected: 'Hylätty',
            },
        },
        remediation: {
            page_title: 'Korjaustoimet',
            title: 'Korjaustoimet',
            new_action: 'Uusi toiminta',
            no_actions: 'Korjaustoimenpiteitä ei löytynyt',
            description: 'Kuvaus',
            owner: 'Omistaja',
            department: 'Osasto',
            deadline: 'Määräaika',
            statuses: {
                planned: 'Suunniteltu',
                in_progress: 'Meneillään',
                completed: 'Valmis',
                verified: 'Vahvistettu',
            },
            verify: 'Vahvista',
            incident_id: 'Tapahtuman tunnus',
            enter_incident_id: 'Anna tapauksen tunnus nähdäksesi korjaustoimenpiteet.',
            start: 'Aloita',
            complete_action: 'Valmis',
        },
        authority_requests: {
            page_title: 'Viranomaisten pyynnöt',
            title: 'Viranomaisten pyynnöt',
            new_request: 'Uusi pyyntö',
            no_requests: 'Viranomaisten pyyntöjä ei löytynyt',
            authority_name: 'Valtuutetun nimi',
            request_details: 'Pyydä lisätietoja',
            response_package: 'Vastauspaketti',
            submission_channel: 'Lähetyskanava',
            submit_response: 'Lähetä vastaus',
            deadline: 'Määräaika',
            confirmation: 'Vahvistus',
            incident_id_optional: 'Tapahtumatunnus (valinnainen)',
            response_context_label: 'Vastauksen konteksti (toimitusasiakirjan luomiseen)',
            response_context_none: '(ohita materialisointi)',
            response_context_supplement: 'Täydennysvastaus',
            response_context_information: 'Tietopyyntöön vastaus',
            response_context_inspection: 'Tarkastukseen vastaus',
            materialisation_status_column: 'Toimitusasiakirja',
            materialisation_pending: 'Odottaa',
            materialisation_success: 'Materialisoitu',
            materialisation_failed: 'Epäonnistunut',
            materialisation_retry_button: 'Yritä uudelleen',
            ar_statuses: {
                received: 'Vastaanotettu',
                verified: 'Vahvistettu',
                assembling: 'Kokoaminen',
                submitted: 'Lähetetty',
                closed: 'Suljettu',
                response_packaged: 'Response Packaged',
                scope_assessed: 'Scope Assessed',
                legal_review: 'Lakitarkastus',
            },
            channel_in_person: 'In Person',
            channel_mps_portal: 'MPS Portal',
            channel_postal: 'Postal',
            channel_email: 'Email',
            verification_checklist_title: 'Verification Checklist',
            checklist: 'Tarkistuslista',
            no_checklist_items: 'No checklist items yet. Add items below.',
            add_checklist_item: 'Add checklist item...',
            advance_to_verified: 'Advance to Verified',
            advance_to_scope_assessed: 'Advance to Scope Assessed',
            advance_to_assembling: 'Advance to Assembling',
            advance_to_response_packaged: 'Advance to Response Packaged',
            advance_to_legal_review: 'Advance to Legal Review',
            advance_to_submitted: 'Advance to Submitted',
            advance_to_closed: 'Advance to Closed',
        },
        disclosure_register: {
            page_title: 'Ilmoitusrekisteri',
            title: 'Ilmoitusrekisteri',
            new_entry: 'Uusi merkintä',
            no_entries: 'Ilmoitusrekisterimerkintöjä ei löytynyt',
            disclosure_type: 'Ilmoituksen tyyppi',
            disclosure_date: 'Ilmoituspäivämäärä',
            recipient: 'Vastaanottaja',
            authority_name: 'Valtuutetun nimi',
            channel: 'kanava',
            content_summary: 'Sisällön yhteenveto',
            incident_id_optional: 'Tapahtumatunnus (valinnainen)',
            channel_in_person: 'In Person',
            channel_mps_portal: 'MPS Portal',
            channel_postal: 'Postal',
            type_court_order: 'Court-Ordered Disclosure',
            type_data_breach: 'Data Breach Notification',
            type_regulatory_response: 'Regulatory Response',
            type_voluntary: 'Voluntary Disclosure',
            channel_email: 'Email',
        },
        log_disclosure: 'Log Disclosure',
    },
    onboarding: {
        nav: {
            wizard: 'Asennusvelho',
            tours: 'Moduuliesittelyt',
            sample_data: 'Esimerkkidata',
        },
        wizard: {
            title: 'Asennusvelho',
            welcome: 'Tervetuloa AesirX ComplianceOneen',
            welcome_message: 'Määritetään organisaatiosi. Tämä velho opastaa sinut alkukonfiguraation läpi.',
            setup_complete: 'Asennus valmis',
            skipped_message: 'Asennusvelho ohitettiin. Voit suorittaa sen milloin tahansa organisaatiosi määrittämiseksi.',
            start_setup: 'Aloita asennus',
            skip_wizard: 'Ohita velho',
            go_to_admin: 'Siirry kohtaan Admin',
            skip_for_now: 'Ohita toistaiseksi',
            next_step: 'Seuraava vaihe',
            previous_step: 'Edellinen vaihe',
            finish_setup: 'Viimeistele asennus',
            rerun: 'Suorita asennusvelho uudelleen',
            step_label: 'Vaihe {number}',
            progress_label: 'Setup progress: Step {number} of {total}',
            validation_org_name_required: 'Organisaation nimi on pakollinen.',
            validation_admin_email_required: 'Ylläpitäjän sähköposti on pakollinen.',
            validation_admin_email_invalid: 'Anna kelvollinen sähköpostiosoite.',
            validation_cmp_endpoint_required: 'CMP:n osoite on pakollinen, kun CMP on käytössä.',
            steps: {
                license_activation: 'Lisenssin aktivointi',
                org_profile: 'Organisaation profiili',
                module_selection: 'Moduulivalinta',
                cmp_integration: 'CMP-integraatio',
                notification_preferences: 'Ilmoitukset',
                review: 'Tarkista ja vahvista',
            },
            step_descriptions: {
                license_activation: 'Aktivoi paketti ja määritä saatavilla olevat moduulit antamalla lisenssiavaimesi.',
                org_profile: 'Määritä organisaation nimi, lainkäyttöalue, toimiala, koko ja ensisijainen kieli.',
                module_selection: 'Valitse, mitkä vaatimustenmukaisuusmoduulit aktivoidaan organisaatiollesi.',
                cmp_integration: 'Määritä Consent Management Platform -integraatio (valinnainen).',
                notification_preferences: 'Määritä miten vastaanotat ilmoituksia vaatimustenmukaisuustapahtumista.',
                review: 'Tarkista asetukset ja viimeistele asennus.',
            },
            license_step: {
                validate_button: 'Vahvista lisenssi',
                skip_warning: 'Moduulien käyttöoikeus rajoitetaan oletuspakettitasoon. Voit aktivoida lisenssin myöhemmin kohdassa Alusta > Lisenssiasetukset.',
            },
            module_descriptions: {
                rights_requests: 'Käsittele rekisteröityjen pääsy- ja poistopyyntöjä.',
                data_mapping: 'Kartoita järjestelmät, tietovirrat ja käsittelytoiminnot.',
                governance: 'Valvo toimittajia, hyväksyntöjä, poikkeamia ja näyttörekistereitä.',
                consent: 'Hallinnoi suostumuskäytäntöjä ja käyttötarkoitusluetteloita.',
                consent_properties: 'Hallinnoi suostumusta useiden digitaalisten omaisuuserien välillä.',
                assessments: 'Suorita vaikutustenarviointeja ja rajat ylittäviä tiedonsiirtoja.',
                classification: 'Luokittele ja merkitse tietovaroja elinkaariseurannalla.',
                monitoring: 'Ajoitetut vaatimustenmukaisuuskatselmukset ja KPI-seuranta.',
                incidents: 'Raportoi ja hallinnoi tietoturva- ja tietosuojatapahtumia.',
                connectors: 'Yhdistä ulkoisiin järjestelmiin automaattiseen tiedonvaihtoon.',
                vendor_risk: 'Pisteytä toimittajia, seuraa sopimuksia ja hallinnoi due diligence -prosesseja.',
                deletion_orchestration: 'Koordinoi tietojen poistoa järjestelmien ja toimittajien välillä.',
                discovery: 'Löydä ja luokittele tietoja koko infrastruktuuristasi.',
                incident_ops: 'Edistyneet tapahtumaohjekirjat, SLA-seuranta ja eskaloinnit.',
                program_governance: 'Kypsyysarvioinnit, johdon raportointi ja auditointivalmius.',
                access_accountability: 'Valvo käyttöoikeuksia, istuntoja ja perustelutyönkulkuja.',
                localization: 'Markkinarakokohtaiset vaatimustenmukaisuuspaketit ja sääntelykehykset.',
                sector_overlays: 'Install sector overlays and open the registers an activated overlay brings into use, by sector.',
                partner_workspace: 'Kumppani- ja jälleenmyyjätyötila.',
                privacy_scanner: 'Reaaliaikainen tietosuojavalvonta digitaalisille omaisuuserille.',
                onboarding: 'Asennusvelho, opastetut kierrokset ja esimerkkitietojen hallinta.',
                compliance_forms: 'Interaktiiviset vaatimustenmukaisuusmallit ja asiakirjojen luonti.',
                audit_trail: 'Kattava muuttumaton auditointikirjaus ja säilytys.',
                organizations: 'Moniorganisaatio- ja ryhmähallinta.',
                task_management: 'Yhtenäinen tehtäväsuunnittelu, työnkulkumoottori ja kalenterinäkymät.',
            },
            module_categories: {
                data_privacy_rights: 'Tietosuoja ja oikeudet',
                vendor_third_party: 'Toimittajan ja kolmannen osapuolen hallinta',
                risk_assessment: 'Riskit ja arviointi',
                operations_automation: 'Toiminta ja automaatio',
                platform_admin: 'Alusta ja hallinto',
            },
            package_tier_label: 'Pakettisi',
            package_module_unavailable: 'Ei sisälly pakettiisi',
            select_all: 'Valitse Kaikki',
            deselect_all: 'Poista Kaikki valinnat',
            load_sample_data: 'Lataa esimerkkitiedot asennuksen jälkeen',
            load_sample_data_description: 'Asenna realistiset esittelytiedot alustan tutkimiseen. Voidaan poistaa myöhemmin kohdasta Käyttöönotto > Esimerkkitiedot.',
            language_label: 'Ensisijainen Kieli',
            language_description: 'Valitse organisaatiosi oletuskieli.',
            admin_account_title: 'Ylläpitäjätili',
            admin_account_created: 'Ylläpitäjätili luotu',
            admin_full_name: 'Koko nimi',
            admin_email: 'Sähköpostiosoite',
            admin_password: 'Salasana',
            admin_confirm_password: 'Vahvista salasana',
            admin_show_password: 'Näytä salasana',
            admin_hide_password: 'Piilota salasana',
            admin_name_placeholder: 'esim. Jane Smith',
            admin_email_placeholder: 'admin@example.com',
            admin_password_placeholder: 'Vähintään 8 merkkiä',
            admin_password_confirm_placeholder: 'Syötä salasana uudelleen',
            validation_admin_name_required: 'Ylläpitäjän nimi on pakollinen',
            validation_admin_password_required: 'Salasana on pakollinen',
            validation_admin_password_min_length: 'Salasanan on oltava vähintään 8 merkkiä',
            validation_passwords_mismatch: 'Salasanat eivät täsmää',
            notification_email_enabled: 'Ota Sähköposti-ilmoitukset Käyttöön',
            notification_email_description: 'Vastaanota sähköposti-ilmoituksia tehtävien jakamisesta, SLA-rikkomuksista ja vaatimustenmukaisuustapahtumista.',
            notification_digest_frequency: 'Yhteenvedon Tiheys',
            notification_digest_daily: 'Päivittäin',
            notification_digest_weekly: 'Viikoittain',
            notification_digest_none: 'Ei yhteenvetoa',
            notification_in_app_enabled: 'Ota Alustailmoitukset Käyttöön',
            notification_in_app_description: 'Näytä ilmoitusmerkki ja -laatikko reaaliaikaisille hälytyksille.',
            jurisdictions: {
                vietnam: 'Vietnam',
                asean: 'ASEAN',
                eu_eea: 'EU/ETA',
                global: 'Maailmanlaajuinen',
            },
            languages: {
                en: 'englanti',
                vi: 'Tiếng Việt',
                da: 'Dansk',
                sv: 'Svenska',
                nb: 'Norsk Bokmål',
                nn: 'Norsk Nynorsk',
                de: 'Deutsch',
                fi: 'Suomi',
                nl: 'Nederlands',
                fr: 'Français',
                lb: 'Lëtzebuergesch',
                ga: 'Gaeilge',
                es: 'español',
                ca: 'Català',
                eu: 'Euskara',
                pt: 'portugali',
            },
            industries: {
                financial_services: 'Rahoituspalvelut',
                healthcare: 'Terveydenhuolto',
                technology: 'Teknologia',
                telecommunications: 'Televiestintä',
                government: 'Julkishallinto',
                education: 'Koulutus',
                manufacturing: 'Valmistus',
                retail: 'Vähittäiskauppa',
                other: 'Muu',
            },
            placeholders: {
                org_name: 'esim. ABC',
                admin_email: 'admin@company.vn',
                team_emails: 'käyttäjä1@yritys.vn, käyttäjä2@yritys.vn',
                cmp_endpoint: 'https://cmp.example.com/api',
            },
            fields: {
                org_name: 'Organisaation nimi',
                jurisdiction: 'Lainkäyttöalue',
                industry: 'Toimiala',
                org_size: 'Organisaation koko',
                language: 'Ensisijainen Kieli',
                admin_email: 'Ylläpitäjän sähköposti',
                team_emails: 'Tiimin jäsenten sähköpostit (pilkuin erotetut)',
                enable_cmp: 'Ota CMP-integraatio käyttöön',
                cmp_endpoint: 'CMP:n API-osoite',
                email_notifications: 'Sähköposti-ilmoitukset',
                in_app_notifications: 'Alustailmoitukset',
                digest_frequency: 'Yhteenvedon Tiheys',
            },
            validation_jurisdiction_required: 'Jurisdiction is required.',
        },
        tours: {
            title: 'Moduulien esittelykierrokset',
            start: 'Aloita esittely',
            continue_tour: 'Jatka esittelyä',
            replay: 'Toista esittely',
            exit: 'Poistu esittelystä',
            skip: 'Ohita esittely',
            next: 'Seuraava',
            previous: 'Edellinen',
            finish: 'Lopeta esittely',
            step_of: 'Vaihe {current}/{total}',
            info: 'Tutustu kunkin moduulin keskeisiin ominaisuuksiin ja työnkulkuihin opastetuilla kierroksilla. Esittelyt voi toistaa milloin tahansa.',
            autoplay: 'Automaattinen toisto',
            interactive: 'Interaktiivinen',
            play_all: 'Toista kaikki',
            search_tours: 'Hae kiertomatkoja...',
            duration: 'Kesto',
            steps_label: '{count} askelta',
            mode: 'tila',
            pause: 'Tauko',
            resume: 'Jatka',
            speed: 'Nopeus',
            restart: 'Käynnistä uudelleen',
            reset: 'Nollaa',
            reset_success: 'Kiertueen eteneminen on nollattu.',
            category_core_privacy: 'Ydintietosuoja',
            category_governance: 'Hallinto',
            category_operations: 'Toiminnot',
            category_platform: 'Alusta',
            category_advanced: 'Edistynyt',
            category_data_privacy_rights: 'Tietosuoja ja oikeudet',
            category_vendor_third_party: 'Toimittajan ja kolmannen osapuolen hallinta',
            category_risk_assessment: 'Riskit ja arviointi',
            category_operations_automation: 'Toiminta ja automaatio',
            category_platform_admin: 'Alusta ja hallinto',
            steps_count: '{count} askelta',
            autoplay_mode: 'Auto-play mode',
            interactive_mode: 'Interactive mode',
            mode_switch_name: '{current}: switch to {other}',
            revision_conflict: 'This tour has changed since you started it. Restart the tour to see the current version.',
        },
        sample_data: {
            evaluation_title: 'Forseti Evaluation — 100 Vietnam Cases',
            evaluation_info: 'Install synthetic linked assessment records for repeatable evaluation. Loading makes no AI calls. Ordinary sample data is unchanged. Run evaluations separately; legal review is still required.',
            evaluation_loaded: '{count} of {total} cases installed',
            evaluation_load: 'Install evaluation cases',
            evaluation_clear: 'Reset evaluation cases',
            evaluation_confirm_load: 'Install all 100 synthetic Vietnam cases without replacing existing evaluation work? Installation does not run AI.',
            evaluation_confirm_clear: 'Remove only this evaluation dataset’s activity and assessment records? AI runs and audit history remain.',
            title: 'Esimerkkidatan hallinta',
            select_modules: 'Valitse moduulit',
            current_status: 'Nykyinen tila',
            operation_history: 'Toimintahistoria',
            load: 'Lataa esimerkkidata',
            clear: 'Tyhjennä esimerkkidata',
            processing: 'Käsitellään...',
            loaded: 'Ladattu',
            not_loaded: 'Ei ladattu',
            records: 'Esimerkkitietueet',
            confirm_clear_all: 'Clear the sample data of every module? Only cross-industry sample records are removed; real records are not affected. Industry packages are kept: remove a package from its own card.',
            confirm_clear: 'Haluatko varmasti tyhjentää kaiken esimerkkidatan valituista moduuleista?',
            confirm_clear_title: 'Tyhjennä esimerkkidata',
            confirm_load_title: 'Lataa esimerkkitiedot',
            confirm_load: 'Haluatko varmasti ladata esimerkkitiedot valituille moduuleille?',
            action_load: 'Lataa',
            action_clear: 'Tyhjennä',
            info: 'Lataa realistista esittelydataa koulutusta ja esittelyjä varten. Esimerkkidata on merkittyä ja voidaan poistaa turvallisesti ilman vaikutusta oikeaan dataan.',
            clear_success: 'Sample data cleared successfully',
            load_success: 'Sample data loaded successfully',
            action_install: 'Install package',
            action_remove: 'Remove package',
            // Sprint 63.37.0 (I2) — the forms module's standing counts in the cross-industry status table.
            status_forms_counts: '{official} official, {internal} internal',
            cross_industry_title: 'Cross-industry sample data',
            cross_industry_info: 'Fictional records for every module, shared by all industries. Choose the modules to load or clear below.',
            packages: {
                title: 'Industry sample data',
                info: 'Each package is one connected, fictional scenario named for its industry, never for a customer. Installing writes sample records in this organisation only, and sends no notification, e-mail, webhook, connector run or AI call.',
                list_aria: 'Industry sample-data packages',
                list_failed: 'The industry packages could not be read.',
                list_retry_subject: 'the industry package list',
                list_forbidden: 'Reading industry packages needs the {permission} permission.',
                empty: 'No industry package is available on this platform.',
                synthetic_badge: 'Synthetic data',
                industry: 'Industry',
                line_of_business: 'Line of business',
                version: 'Version',
                reference_prefix: 'Reference prefix',
                contents_heading: 'Contents',
                contents_item: '{area}: {count} records',
                branches_heading: 'Optional parts',
                branch_default: 'chosen by default',
                branches_none: 'None',
                prerequisites_heading: 'Prerequisites',
                prerequisite_frameworks: 'Frameworks the package reads',
                prerequisite_modules: 'Modules the package writes',
                prerequisite_present: 'Installed',
                prerequisite_missing: 'Not installed',
                prerequisite_module_available: 'Included in your plan',
                prerequisite_module_unavailable: 'Not included in your plan',
                prerequisite_note: 'Records that need a missing framework or module are left out at install and listed with the reason.',
                status_heading: 'Status',
                status_not_installed: 'Not installed',
                status_installed: 'Installed',
                installed_language: 'Content language',
                installed_branches: 'Optional parts',
                installed_at: 'Installed on',
                installed_by: 'Installed by',
                installed_records: 'Records written',
                evidence_pack: 'Audit evidence pack',
                evidence_pack_link: 'Open audit evidence packs (pack {id})',
                evidence_pack_none: 'None assembled',
                attachments: 'Stored synthetic files',
                upgrade_available: 'Version {version} is available. Reinstall to replace the installed version {installed}.',
                install: 'Install',
                reinstall: 'Reinstall',
                remove: 'Remove',
                installing: 'Installing…',
                removing: 'Removing…',
                load_needs: 'Installing needs the {permission} permission.',
                clear_needs: 'Removing needs the {permission} permission.',
                dialog_install_title: 'Install {name}',
                dialog_reinstall_title: 'Reinstall {name}',
                language_legend: 'Content language',
                language_en: 'English',
                language_vi: 'Vietnamese',
                language_required: 'Choose the content language.',
                branches_legend: 'Optional parts',
                branches_none_declared: 'This package has no optional parts.',
                will_write: 'Writes up to {count} sample records in these modules:',
                will_drop: 'A record whose framework is not installed, or whose module your plan does not include, is left out and listed with the reason.',
                nothing_sent: 'No notification, e-mail, webhook, connector run or AI call is sent. When the Immutable Audit Trail is on, the audit entries for the stored files and the evidence pack are anchored like any other audit entry.',
                no_accounts: 'No sign-in accounts are created.',
                reinstall_note: "With the same language and parts, nothing changes. With different ones, the package's records are removed and written again.",
                remove_confirm_title: 'Remove {name}',
                remove_confirm: "Remove {name}? This deletes the {count} records the package wrote in its modules and its {files} stored synthetic files. Its audit evidence pack is deleted too, unless a person reviewed it or a framework evidence pack collected it: then it is kept as your organisation's own record. Audit entries recording the install and the removal stay. Cross-industry sample data and your real records stay.",
                install_failed: 'The package could not be installed.',
                remove_failed: 'The package could not be removed.',
                result_heading: 'Result of the last action',
                result_installed: '{name} installed.',
                result_reinstalled: '{name} reinstalled.',
                result_unchanged: 'Nothing changed: {name} is already installed at this version, in this language, with these parts.',
                result_removed: '{name} removed.',
                result_written: 'Records written: {count}',
                result_removed_first: 'Records removed before writing: {count}',
                result_attachments: 'Synthetic files stored: {count}',
                result_dropped_heading: 'Records left out ({count})',
                result_dropped_none: 'No record was left out.',
                drop_framework_not_installed: 'a framework it needs is not installed ({detail})',
                drop_requirement_not_installed: 'the requirement it maps to is not in the installed framework ({detail})',
                drop_module_not_available: 'your plan does not include this module ({detail})',
                drop_reference_dropped: 'it refers to a record that was left out ({detail})',
                dropped_item: '{record} ({area}): {reason}',
                // Sprint 63.37.0 (I2) — the Forms sub-section of a package card and the install result.
                forms_heading: 'Forms',
                forms_counts: '{official} official, {internal} internal, {not_installed} not installed on this organisation',
                forms_list_aria: 'Forms of {name}',
                form_official: 'Official',
                form_internal: 'Internal — no official form declares this',
                form_not_installed: 'Not installed on this organisation; it needs {framework}',
                form_not_installed_unnamed: 'Not installed on this organisation',
                form_restricted: 'Not available on this organisation: the form it needs is restricted',
                form_not_fileable: 'Not available on this organisation: the installed form cannot be filed',
                forms_failed: 'The forms of this package could not be read.',
                forms_retry_subject: 'the package forms',
                result_forms_written: 'Forms written ({count})',
                result_forms_dropped: 'Forms not written ({count})',
                result_form_written_item: '{record}: {form} ({standing})',
                drop_form_not_installed: 'no installed form answers it ({detail})',
                drop_form_restricted: 'the form it needs is restricted on this organisation ({detail})',
                drop_form_not_fileable: 'the installed form cannot be filed ({detail})',
                evidence_pack_assembled: 'Audit evidence pack assembled, not verified: {id}.',
                evidence_pack_failed: 'The audit evidence pack could not be assembled: {error}. The records stand.',
                evidence_pack_not_assembled: 'No audit evidence pack was assembled.',
                guide_heading: 'Scenario guide',
                guide_intro: 'The demonstration path, in order. Each step opens the record or its list.',
                guide_language: 'Guide language: {language}',
                guide_no_accounts: 'Sample data includes no sign-in accounts. Present with your own account; a record that names a person by account names the user who installed the package.',
                guide_reviewers: "Pending approvals and the impact assessment are decided by your organisation's own reviewers, not during the demonstration.",
                guide_point: 'Point',
                guide_do_not_claim: 'Do not claim',
                guide_step_unavailable: "This step's record was not installed.",
                guide_failed: 'The scenario guide could not be read.',
                guide_retry_subject: 'the scenario guide',
                result_removed_records: 'Records deleted from the modules: {count}',
                result_removed_pack: 'Audit evidence pack removed ({count} stored rows)',
                kept_heading: 'Audit evidence pack versions kept ({count})',
                kept_reviewed: "Pack {pack_id}, version {version}: kept because it was reviewed. It is now your organisation's own record. The events it lists were sample rows and went with the package, so Check for changes since assembly reports them as gone, or as changed where a reinstall wrote them again.",
                kept_collected: "Pack {pack_id}, version {version}: kept because a framework evidence pack collected it. It is now your organisation's own record. The events it lists were sample rows and went with the package, so Check for changes since assembly reports them as gone, or as changed where a reinstall wrote them again.",
                reinstall_failed_removed: 'The reinstall failed after the previous install was removed: {count} records were deleted and nothing was written. The package is no longer installed.',
                kept_outputs_heading: 'Exports of sample forms kept ({count})',
                kept_output_item: "Export {output_id} (file {file_id}): kept because {cited_by} names its file. It is now your organisation's own file. The sample form it was printed from went with the package.",
                kept_output_by_compliance_execution_proof: 'compliance proof {id}',
                kept_output_by_cross_border_filing_evidence: 'cross-border filing {id}',
                kept_output_by_handbook_assessment_evidence: 'handbook assessment {id}',
                kept_output_by_ai_obligation_evidence: 'AI obligation record {id}',
                kept_output_by_evidence_pack_dispatch_proof: 'evidence pack dispatch {id}',
                kept_output_by_legal_hold: 'legal hold {id}',
                kept_output_by_other: 'record {id}',
                kept_output_by_legal_hold_policy: 'the legal hold set on {id} files',
                kept_held_files_heading: 'Sample files kept, not deleted ({count})',
                kept_held_file_item: "File {file_id}: kept, not deleted, because {cited_by} names it. It stays as your organisation's file, and can be deleted in Files once nothing names it.",
            },
            // Sprint 63.37.0 (rehearsal J2) — the forms row when the load left forms out because their framework is not installed.
            status_forms_counts_not_installed: '{official} official, {internal} internal, {not_installed} not installed on this organisation',
        },
        help: {
            title: 'Ohjekeskus',
            search: 'Haku',
            search_placeholder: 'Etsi ohjeartikkeleita...',
            clear: 'Tyhjennä',
            all_categories: 'Kaikki kategoriat',
            all_modules: 'Kaikki Moduulit',
            back: 'Takaisin ohjekeskukseen',
            callout_important: 'Tärkeää',
            callout_tip: 'Vihje',
            no_results: 'Hakuasi vastaavia artikkeleita ei löytynyt.',
            no_articles: 'Ohjeartikkeleita ei ole vielä saatavilla. Artikkelit ilmestyvät tähän, kun sisältöä lisätään.',
            results_for: '{count} tulos(ta) haulle "{query}"',
            quick_links: {
                getting_started: 'Aloitusopas',
                getting_started_desc: 'Asennus, käyttöönotto ja pikaopas.',
                user_guide: 'Käyttöopas',
                user_guide_desc: 'Moduulikohtainen käyttödokumentaatio.',
                admin_guide: 'Ylläpito-opas',
                admin_guide_desc: 'Konfigurointi, ylläpito ja vianmääritys.',
                faq: 'UKK',
                faq_desc: 'Usein kysytyt kysymykset ja vastaukset.',
            },
            search_articles_aria: 'Etsi ohjeartikkeleita',
            article_metadata_aria: 'Artikkelin metatiedot',
            faq_editor_title: 'Ohjekeskuksen UKK-editori',
            faq_editor_description: 'Lisää, muokkaa ja poista kysymys-vastaus-pareja missä tahansa ohjekeskuksen artikkelissa. Editori ei ole vielä käytettävissä; tämä osoite on varattu sille.',
            faq_editor_empty: 'UKK-editori ei ole vielä käytettävissä. Pyydä siihen asti järjestelmänvalvojaltasi UKK-osioiden lisäämistä ohjekeskuksen artikkeliin puolestasi.',
            articles_list_aria: 'Ohjeartikkelit',
            category_nav_aria: 'Luokkanavigaatio',
            quick_links_aria: 'Pikalinkit ohjeluokkiin',
            returned_to_list: 'Palattiin artikkelilistaan',
            opening_article: 'Avataan artikkeli: {title}',
            read_article_aria: 'Lue artikkeli: {title}',
            pagination_nav_aria: 'Artikkelien sivutus',
            error_unavailable: 'Ohjekeskus ei ole tällä hetkellä käytettävissä. Yritä myöhemmin uudelleen.',
            retry: 'Yritä uudelleen',
            list_truncated: 'Only {shown} of {total} help articles are listed. Use search to find the rest.',
        },
        // Sprint 63.11.0 (#34) — the persona and use-case detail pages. Every
        // value these pages show ABOUT a persona or a use case is read from the
        // shared registries; these strings are only the frame around it.
        // English seed for structural parity (R-57): translation is
        // docs/product/phases/phase-25/sprint-58.7.0.md.
        persona_detail: {
            page_title: 'Persona',
            text_loading: 'Loading the role text…',
            text_failed: 'The role text could not be loaded.',
            not_found_title: 'Persona not found',
            not_found_body: 'No persona in the reference set has the identifier "{slug}". Choose one from the persona paths instead.',
            back_to_list: 'Back to persona paths',
            open_path: 'Open onboarding path',
            profile_heading: 'Profile',
            field_persona_id: 'Persona ID',
            field_market: 'Market',
            field_quick_start: 'Suggested first task',
            modules_heading: 'Primary modules',
            modules_description: 'The modules this persona works in most, limited to those enabled for your organisation.',
            modules_checking: 'Checking which of these modules are enabled for your organisation…',
            modules_unchecked: 'We could not check which of these modules are enabled for your organisation, so all of them are listed.',
            modules_not_permitted: 'Your role cannot see which modules are enabled for your organisation, so all of them are listed.',
            modules_not_enabled: 'Not enabled for your organisation: {names}',
            modules_none_enabled: "None of this persona's primary modules is enabled for your organisation.",
            use_cases_heading: 'Use cases',
            use_cases_description: 'Validated use cases that name this persona as their primary persona.',
            use_cases_none: 'No validated use case names this persona as its primary persona.',
            availability_heading: 'Availability',
            availability_checking: 'Checking whether this persona is available to your organisation…',
            availability_available: 'Available to your organisation.',
            availability_ungated: 'Available to every organisation — this persona is not tied to one market.',
            availability_unavailable: 'This persona belongs to a market your organisation has not installed ({market}). Its onboarding path, quick start and recommendations are not offered to your organisation until a market pack for that market is installed.',
            availability_failed: 'We could not check whether this persona is available to your organisation, so this page does not say either way.',
            retry: 'Try again',
            retrying: 'Checking…',
            market_vn: 'Vietnam',
            market_eu: 'European Union / EEA',
            market_uk: 'United Kingdom',
            market_gl: 'Global — not tied to one market',
        },
        use_case_detail: {
            page_title: 'Use case {id}',
            description: 'A validated use case from the reference set and the persona it is written for. The full narrative is in the reference document named below; this page does not restate it.',
            not_found_title: 'Use case not found',
            not_found_body: 'No use case in the reference set has the identifier "{id}". Open a persona to see the use cases written for it.',
            back_to_list: 'Back to persona paths',
            open_path: 'Open the onboarding path for its primary persona',
            details_heading: 'Details',
            field_use_case_id: 'Use case ID',
            field_primary_persona: 'Primary persona',
            field_persona_id: 'Persona ID',
            field_market: 'Market',
            availability_heading: 'Availability',
            availability_checking: 'Checking whether this use case is available to your organisation…',
            availability_available: 'Available to your organisation.',
            availability_ungated: 'Available to every organisation — its primary persona is not tied to one market.',
            availability_unavailable: 'This use case belongs to a market your organisation has not installed ({market}). Its primary persona and onboarding path are not offered to your organisation until a market pack for that market is installed.',
            availability_failed: 'We could not check whether this use case is available to your organisation, so this page does not say either way.',
            retry: 'Try again',
            retrying: 'Checking…',
        },
    },
    common: {
        vendor: 'Vendor',
        // R-57 — structural parity with English values; real translations are EN and VI.
        list_truncation: {
            loading: 'Loading the list…',
            failed: 'The list could not be loaded, so nothing can be chosen from it right now.',
            partial_with_total: 'Showing {shown} of {total}. Narrow the search to reach the rest.',
            partial_without_total: 'Showing the first {shown}. Narrow the search to reach the rest — how many more there are is not known.',
        },
        // fix-sprint-20261013-0200 R1 — the remedy a failed directory read offers, said once for every
        // DirectoryNotice and UserSelect (components/register/DirectoryNotice.tsx `directoryRetry`).
        directory_retry: 'Reload',
        directory_retrying: 'Reloading…',
        directory_retry_subject: 'the user directory',
        // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
        // no locale declared, so each fell through to its hardcoded English third
        // argument and rendered English in all 16 locales. The two DPIA row-action
        // tooltips were worse: their fallbacks are themselves translated keys, so the
        // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
        // action read 'Approve/Reject' where it intends 'Review'.
        change: 'Change',
        current: 'current',
        next_page: 'Next page',
        selected: 'selected',
        // Fix sprint F30 — the shared "for example" wrapper. Three pages built
        // `e.g. ${code}` as a template literal, so the words rendered in English in
        // all 16 locales; the abbreviation differs per language (see
        // `escalation_contact_placeholder`, which already had it right).
        example_value: 'esim. {value}',
        /* R-57 — structural parity; real translation deferred to the locale programme. */
        export_incomplete_heading: 'This export is incomplete',
        export_incomplete_body: '{count} value(s) in {fieldCount} field(s) ({fields}) were withheld because you are not authorized to view them. The file marks each one.',
        export_not_scanned: 'This export was produced from pre-formatted content, so its completeness was not checked.',
        save: 'Tallenna',
        cancel: 'Peruuta',
        system_actor: 'Järjestelmä',
        delete: 'Poista',
        edit: 'Muokkaa',
        create: 'Luo',
        update: 'Päivitä',
        loading: 'Ladataan...',
        error: 'Tapahtui virhe',
        success: 'Onnistui',
        logout: 'Kirjaudu ulos',
        actions: 'Toiminnot',
        retrying: 'Yritetään uudelleen…',
        permission_denied: 'Sinulla ei ole pääsyä tälle sivulle.',
        status: 'Tila',
        description: 'Kuvaus',
        name: 'Nimi',
        created_at: 'Luotu',
        updated_at: 'Päivitetty',
        yes: 'Kyllä',
        no: 'Ei',
        confirm: 'Vahvista',
        confirm_dialog_title: 'Confirm action',
        back: 'Takaisin',
        next: 'Seuraava',
        previous: 'Edellinen',
        search: 'Haku',
        filter: 'Suodatin',
        page_x_of_y: 'Sivu {page}/{total}',
        pagination_label: 'Sivutus',
        view_mode_label: 'Näyttötila',
        export: 'Vie',
        exporting: 'Viedään…',
        sidebar_navigation: 'Järjestelmänvalvojan sivupalkin navigointi',
        items_count: '{count} kohdetta',
        items_count_one: '{count} kohde',
        export_success: 'Asiakirjan luominen onnistui.',
        generate_pdf: 'Luo PDF',
        import: 'Tuo',
        download: 'Lataa',
        upload: 'Lataa palvelimelle',
        view: 'Näytä',
        close: 'Sulje',
        close_notification: 'Close notification',
        discard_prompt: {
            title: 'Discard unsaved changes?',
            message: 'This dialog has unsaved changes. Closing it now discards them.',
            confirm: 'Discard',
            cancel: 'Keep editing',
        },
        submit: 'Lähetä',
        saved: 'Tallennettu',
        saving: 'Tallennetaan...',
        submitting: 'Lähetetään...',
        created: 'Luotu onnistuneesti',
        all: 'Kaikki',
        none: 'Ei mitään',
        select: 'Valitse',
        select_user: 'Valitse käyttäjä...',
        custom_input: 'Kirjoita manuaalisesti',
        required: 'Pakollinen',
        optional: 'Valinnainen',
        notes: 'Muistiinpanot',
        tags: 'Tunnisteet',
        archive: 'Arkistoi',
        clone: 'Kloonaa',
        refresh: 'Päivitä',
        retry: 'Yritä uudelleen',
        // fix-sprint-20260810-2128 residue — English placeholder for structural
        // parity; locale work beyond EN/VI defers to Phase 25 (R-57, sprint-57.7.0).
        options_unreadable: 'The choices for this field could not be read. That does not mean there are none.',
        options_unreadable_pointer: 'Could not be read — see the message below.',
        reset: 'Nollaa',
        add: 'Lisää',
        remove: 'Poista',
        removing: 'Poistetaan…',
        attachment: 'Liite',
        upload_file: 'Lataa tiedosto',
        upload_file_required: 'Lataa tiedosto (pakollinen)',
        // fix-sprint-20260808-0511-followup-2 F1 — FileUpload.tsx drop-zone,
        // progress and validation-toast strings (were hardcoded EN in a shared
        // component reached from every file_upload form field).
        upload_drag_or_browse: 'Vedä ja pudota tai selaa napsauttamalla',
        upload_max_size: 'Enimmäiskoko: {size} Mt',
        upload_accepted_types: 'Hyväksytyt: {types}',
        upload_in_progress: 'Ladataan {name}...',
        upload_size_exceeded: 'Tiedoston koko ylittää suurimman sallitun koon {size} Mt.',
        upload_type_not_accepted: 'Tiedostotyyppiä ei hyväksytä. Hyväksytyt tyypit: {types}',
        upload_succeeded: 'Tiedosto "{name}" ladattiin onnistuneesti.',
        upload_failed: 'Tiedoston lataaminen epäonnistui.',
        select_entity_type_first: 'Valitse ensin entiteettityyppi',
        failed_to_remove_file: 'Tiedostoa ei voitu poistaa.',
        entity_type_label: 'Entiteettiryhmä',
        entity_label: 'Entiteetti',
        entity_reference: 'Entiteettiviittaus',
        failed_to_load_entities: 'Entiteettien lataaminen epäonnistui',
        enable: 'Ota käyttöön',
        disable: 'Poista käytöstä',
        no_data: 'Tietoja ei löytynyt',
        confirm_delete: 'Haluatko varmasti poistaa tämän kohteen?',
        confirm_action: 'Haluatko varmasti jatkaa?',
        save_success: 'Tallennettu onnistuneesti',
        save_error: 'Tallennus epäonnistui',
        delete_success: 'Poistettu onnistuneesti',
        delete_error: 'Poistaminen epäonnistui',
        hour: 'tunti',
        hours: 'tuntia',
        load_error: 'Tietojen lataus epäonnistui',
        operation_error: 'Toiminto epäonnistui',
        something_went_wrong: 'Jokin meni pieleen',
        go_home: 'Siirry yhteenvetoon',
        access_denied: 'Pääsy estetty',
        no_permission: 'Sinulla ei ole oikeutta käyttää tätä sivua.',
        no_permission_title: 'Riittämättömät oikeudet',
        no_permission_message: 'Sinulla ei ole oikeutta käyttää tätä sivua.',
        unknown: 'Tuntematon',
        back_to_dashboard: 'Takaisin yhteenvetoon',
        id: 'Tunnus',
        type: 'Tyyppi',
        date: 'Päivämäärä',
        created_by: 'Luonut',
        updated_by: 'Päivittänyt',
        page_of: 'Sivu {page}/{total}',
        showing_items: 'Näytetään {count} kohdetta',
        items_per_page: 'Kohteita sivulla',
        no_results: 'Suodattimia vastaavia tietueita ei löytynyt',
        no_items: 'Kohteita ei löytynyt',
        sort_by: 'Lajittele',
        ascending: 'Nouseva',
        descending: 'Laskeva',
        all_statuses: 'Kaikki tilat',
        all_types: 'Kaikki tyypit',
        all_categories: 'Kaikki kategoriat',
        all_severities: 'Kaikki vakavuudet',
        date_range: 'Aikaväli',
        from_date: 'Alkaen',
        to_date: 'Päättyen',
        clear_filters: 'Tyhjennä suodattimet',
        apply_filters: 'Käytä suodattimia',
        save_changes: 'Tallenna muutokset',
        discard_changes: 'Hylkää muutokset',
        confirm_discard: 'Sinulla on tallentamattomia muutoksia. Haluatko hylätä ne?',
        copy: 'Kopioi',
        duplicate: 'Monista',
        move: 'Siirrä',
        assign: 'Vastuuta',
        unassign: 'Poista vastuutus',
        approve: 'Hyväksy',
        reject: 'Hylkää',
        deleting: 'Poistetaan...',
        processing: 'Käsitellään...',
        loading_page: 'Ladataan sivua...',
        operation_success: 'Toiminto suoritettu onnistuneesti',
        operation_failed: 'Toiminto epäonnistui. Yritä uudelleen.',
        /*
         * Sprint 64.x-batch-17 — moved out of two inline `{ en, vi }` maps.
         *
         * `CHECKED_DOWNLOAD_FAILED_COPY` lived in `useCheckedDownload.ts` and is
         * the fallback toast for EVERY one of the 26 migrated download surfaces;
         * `RETRIEVAL_DENIED_COPY` lived in `EvidencePackDetailPage.tsx`. I18N.md
         * names `{ en, vi }` lookup objects as the documented root cause of "14
         * languages showing only English" and bans them outright, and neither
         * string had a key in any of the 16 bundles — so both were invisible to
         * the Phase 25 translation batches and could never reach parity.
         *
         * The hook's docblock justified the inline map with "a key earns its place
         * when more than one surface needs the same string", a rule that is not in
         * I18N.md; the string was in fact read by every migrated call site.
         */
        download_failed: 'Download failed.',
        download_forbidden: 'The file could not be downloaded: downloading files needs a permission your role does not hold.',
        export_needs_download: 'Exporting needs the permission to download files, which your role does not hold.',
        export_download_forbidden: 'The export was made, but the file could not be downloaded: downloading files needs a permission your role does not hold.',
        retrieval_denied: 'Retrieving this version requires a permission your role does not hold.',
        retrieval_denied_generic: 'Downloading this file requires a permission your role does not hold.',
        /* Sprint 62.58.0 PR 3 (product-qa walk N-2) — a stored file refused because
           it printed sections read under grants the role does not all hold; the
           grants named in plain words (`download_permission_names`). */
        download_refusal_sections: 'The file could not be downloaded: it includes sections that need these permissions, which your role does not hold: {permissions}.',
        /* Step-2 confirmation P3-4 — the same refusals in *File details*, which
           the reader only opened: nothing was downloaded. */
        details_refusal_sections: 'The file details cannot be shown: the file includes sections that need these permissions, which your role does not hold: {permissions}.',
        details_refused_generic: 'The file details cannot be shown: viewing this file needs a permission your role does not hold.',
        download_permission_names: {
            privacy_notice_read: 'read privacy notices',
            dm_activity_read: 'read processing activities',
            dm_system_read: 'read data-map systems',
            biometric_capture_read: 'read biometric capture components',
            gov_vendor_read: 'read vendors',
            vendor_questionnaire_read: 'read vendor questionnaires',
            ai_governance_read: 'read AI systems',
            dpia_read: 'read data processing impact assessments',
            incident_read: 'read incidents',
            dm_evidence_read: 'read Data Mapping evidence',
            vehicle_monitoring_read: 'read vehicle monitoring devices',
            forms_instance_read: 'read form records',
        },
        date_cleared: 'Date cleared',
        clear_date: 'Clear date',
        date_picker: 'Date picker',
        date_selected: 'Selected',
        not_measured: 'Not measured',
        empty_dash: '—',
        network_error: 'Verkkovirhe. Tarkista yhteytesi.',
        session_expired: 'Istunto vanhentunut. Kirjaudu sisään uudelleen.',
        test: 'Testaa',
        pause: 'Keskeytä',
        resume: 'Jatka',
        activate: 'Aktivoi',
        last_sync: 'Viimeisin synkronointi',
        health: 'Terveydentila',
        email: 'Sähköposti',
        role: 'Rooli',
        title: 'Otsikko',
        owner: 'Omistaja',
        priority: 'Prioriteetti',
        category: 'Kategoria',
        details: 'Tiedot',
        overview: 'Yleiskatsaus',
        configuration: 'Asetukset',
        history: 'Historia',
        new_item: 'Uusi',
        edit_item: 'Muokkaa',
        select_option: 'Valitse...',
        total: 'Yhteensä',
        active: 'Aktiivinen',
        inactive: 'Ei aktiivinen',
        enabled: 'Käytössä',
        disabled: 'Ei käytössä',
        last_login: 'Viimeisin kirjautuminen',
        never: 'Ei koskaan',
        password_changed: 'Salasana vaihdettu. Sinut kirjataan ulos.',
        password_change_failed: 'Salasanan vaihto epäonnistui',
        passwords_mismatch: 'Salasanat eivät täsmää',
        install: 'Asenna',
        install_named: 'Asenna {name}',
        uninstall: 'Poista asennus',
        installed: 'Asennettu',
        available: 'Saatavilla',
        catalog: 'Katalogi',
        published: 'Julkaistu',
        version: 'Versio',
        market: 'Markkina-alue',
        language: 'Kieli',
        framework: 'Sääntelykehys',
        template: 'Mallipohja',
        connector_type: 'Liitintyyppi',
        polling_interval: 'Kyselyväli (minuuttia)',
        new_instance: 'Uusi instanssi',
        change_password: 'Vaihda salasana',
        current_password: 'Nykyinen salasana',
        new_password: 'Uusi salasana',
        confirm_password: 'Vahvista salasana',
        comments: 'Kommentit',
        completed: 'Valmis',
        contact: 'Yhteyshenkilö',
        hide: 'Piilota',
        hide_details: 'Piilota Yksityiskohdat',
        label: 'Tunniste',
        max: 'Max',
        min: 'Min',
        na: 'N/A',
        of: 'of',
        showing: 'Näytetään',
        page: 'Sivu',
        preview: 'Esikatselu',
        print: 'Tulosta',
        show: 'Näytä',
        status_active: 'Aktiivinen',
        status_completed: 'Valmis',
        status_draft: 'Luonnos',
        status_in_progress: 'Käynnissä',
        status_pending: 'Odottaa',
        view_details: 'Näytä yksityiskohdat',
        all_priorities: 'Kaikki prioriteetit',
        pipeline: 'Putki',
        all_requests: 'Kaikki pyynnöt',
        schedule: 'Aikataulu',
        deploy: 'Ota käyttöön',
        rollback: 'Palautus',
        advance_stage: 'Edistä vaihetta',
        submit_for_approval: 'Lähetä for Hyväksyntä',
        approval_log: 'Hyväksyntäloki',
        approver: 'Hyväksyjä',
        decision: 'Päätös',
        decided_at: 'Päätetty',
        rollout_stages: 'Rollout Vaiheet',
        approval_comment: 'Hyväksyntäkommentti',
        optional_comment: 'Valinnainen comment...',
        impact_summary: 'Vaikutus Yhteenveto',
        target_property_ids: 'Tavoite Ominaisuus Tunniste (comma-separated)',
        policy_id: 'Käytäntö Tunniste',
        target_version: 'Tavoite Versio',
        target_version_placeholder: 'esim. 2.0.0',
        scheduled_label: 'Aikataulutettu',
        scheduled_start: 'Aikataulutettu Aloita',
        scheduled_end: 'Aikataulutettu End',
        properties_count: '{count} ominaisuutta',
        results: 'Tulokset',
        comparison_results: 'Comparison Tulokset',
        past_comparison_reports: 'Past Comparison Raportit',
        scanning: 'Skannataan...',
        run_inconsistency_scan: 'Suorita Inconsistency Skannaus',
        property_ids_label: 'Ominaisuus Tunniste (comma-separated, min 2)',
        comparison_type: 'Comparison Tyyppi',
        golden_template_id: 'Golden Malli Tunniste (optional)',
        leave_blank_compare: 'Jätä tyhjäksi verrataksesi ominaisuuksia toisiinsa',
        add_notes_placeholder: 'Lisää analyysimuistiinpanosi tähän...',
        covered_of_active: '{covered}/{active} aktiivista ominaisuutta katettu',
        fetch_error: 'Epäonnistui: fetch tiedot. Yritä uudelleen.',
        run_comparison_error: 'Epäonnistui: run comparison. Yritä uudelleen.',
        save_notes_error: 'Epäonnistui: save notes. Yritä uudelleen.',
        scan_complete: 'Inconsistency skannaus valmis successfully.',
        scan_error: 'Epäonnistui: run inconsistency skannaus. Yritä uudelleen.',
        min_two_properties: 'Please provide at least 2 property Tunniste for comparison.',
        comma_separated: 'pilkulla erotettu',
        language_english: 'Englanti',
        language_vietnamese: 'Vietnamilainen',
        language_vietnamese_native: 'Tiếng Việt',
        author: 'Tekijä',
        dependencies: 'Riippuvuudet',
        checksum: 'Tarkistussumma',
        changelog: 'Muutosloki',
        tier: 'Taso',
        phone: 'Puhelin',
        launch: 'Käynnistä',
        complete: 'Viimeistele',
        back_to_list: 'Takaisin to Lista',
        clear_selection: 'Tyhjennä valinta',
        deactivate: 'Poista käytöstä',
        scope: 'Laajuus',
        deadline: 'Määräaika',
        progress: 'Edistyminen',
        frequency: 'Tiheys',
        reviewer: 'Arvioija',
        user: 'Käyttäjä',
        campaign: 'Kampanja',
        items: 'Kohteet',
        next_run: 'Seuraava Suorita',
        last_run: 'Last Suorita',
        overdue: 'OVERDUE',
        creating: 'Luodaan...',
        launching: 'Käynnistetään...',
        loaded: 'loaded',
        page_size: 'Sivun koko',
        per_page: '/ sivu',
        urgent: 'Kiireellinen',
        pagination: 'Sivutus',
        metric: 'Metrinen',
        value: 'Arvo',
        country: 'Maa',
        showing_chart_view: 'Näytetään kaavionäkymä',
        showing_table_view: 'Näytetään datataulukkonäkymä',
        policies: 'Käytännöt',
        compliant: 'Yhteensopiva',
        non_compliant_status: 'Ei yhteensopiva',
        all_data: 'Kaikki tiedot',
        sr_urgent: 'Kiireellinen',
        n_per_page: '{n} / sivu',
        select_user_placeholder: '-- Valitse käyttäjä --',
        field_required: 'Tämä kenttä on pakollinen',
        results_found: 'tulosta löytyi',
        select_placeholder: '-- Valitse --',
        unknown_user: 'Tuntematon käyttäjä',
        // fix-sprint #431 F7 — the names of the platform's own actors
        // (`SystemActorId`), as the audit trail and an assignee column show them.
        // English until Phase 25 (R-57).
        system_actors: {
            intake: 'System Intake',
            sla_engine: 'SLA Engine',
            export_job: 'Export Job',
            cmp_gateway: 'CMP Gateway',
            notification: 'Notification System',
            migration: 'System Migration',
            other: 'System: {name}',
        },
        export_csv: 'Vie CSV',
        not_applicable: 'Ei sovellettavissa',
        aria_overdue_since: 'Myöhässä {date} alkaen',
        placeholders: {
            enter_justification: 'Syötä perustelu',
            justification: 'Perustelu',
            campaign_name_example: 'Q1 2026 Pääsy Katselmointia',
            optional_description: 'Valinnainen kuvaus',
            enter_user_id: 'Syötä the käyttäjä tunniste of the reviewer',
            enter_user_id_short: 'Syötä käyttäjä tunniste',
            schedule_name_example: 'Neljännesvuosittain Pääsy Todistus',
            enter_property_id: 'Syötä property tunniste',
            enter_policy_id: 'Syötä käytäntö tunniste',
            property_or_group_id: 'Ominaisuus or group Tunniste',
            describe_template_purpose: 'Describe the malli purpose...',
            step_title: 'Vaihe otsikko...',
            detailed_instructions: 'Yksityiskohtaiset ohjeet...',
            filter_by_taxonomy_id: 'Suodata by Taksonomia Tunniste...',
            describe_classification_rule: 'Kuvaile luokittelusääntö...',
            describe_dedup_rule: 'Kuvaile deduplikointisääntö...',
            describe_import_template: 'Describe the import malli...',
            reason_for_rejection: 'Syy hylkäämiselle...',
            jurisdiction_codes_example: 'VN, SG, EU',
            filter_by_locale: 'Suodata by locale...',
            filter_by_event_type: 'Suodata by tapahtuma tyyppi',
            filter_by_actor_id: 'Suodata by actor Tunniste',
            filter_by_market_code: 'Suodata by market koodi...',
            filter_by_framework_id: 'Suodata by kehys Tunniste...',
            add_comment: 'Lisää kommentti...',
            provide_rejection_reason: 'Anna syy lähetyksen hylkäämiselle...',
            filter_by_jurisdiction: 'Suodata by jurisdiction koodi...',
            enter_report_title: 'Syötä raportti otsikko',
            enter_name: 'Syötä nimi',
            enter_description: 'Syötä kuvaus',
            enter_org_id: 'Syötä org tunniste to add',
            describe_evidence_gap: 'Describe the todistusaineisto puute...',
            expected_evidence: 'Mitä todisteita odotetaan',
            entity_identifier: 'Entiteetin tunniste',
            remediation_steps: 'Vaiheet to remediate this puute',
            user_id_or_name: 'Käyttäjätunnus tai nimi',
            reporting_period_id: 'Raportointi period Tunniste',
            maturity_model_id: 'Kypsyys model Tunniste',
            describe_maturity_model: 'Kuvaile tämä kypsyysmalli...',
            describe_role_purpose: "Describe this role's purpose...",
            provide_deny_reason: 'Anna syy pyynnön hylkäämiselle',
            describe_emergency: 'Describe the emergency situation requiring elevated pääsy (minimum 20 characters)',
            enter_delegate_id: 'Syötä käyttäjä tunniste of the delegate',
            provide_delegation_reason: 'Anna syy tälle delegoinnille',
            client_identifier: 'Asiakas identifier',
            client_secret: 'Asiakas secret',
            time_format: 'HH:MM',
            describe_sod_rule: 'Kuvaile tämän SoD-säännön tarkoitus...',
            enter_role_id: 'Syötä rooli tunniste',
            explain_exception: 'Selitä, miksi tämä poikkeus tarvitaan...',
            describe_compensating_controls: 'Describe compensating hallinta in place...',
            full_name: 'Täysi nimi',
            min_chars: 'Vähintään 12 merkkiä',
            enter_override_justification: 'Syötä perustelu for overriding this gate evaluation',
            enter_evidence_id: 'Anna todisteiden tunnus linkitettäväksi:',
            date_format: 'PP/KK/VVVV',
            describe_pack_contents: 'Kuvaile paketin sisältö ja tarkoitus',
            select_reviewers: 'Valitse tarkastajat...',
        },
        aria_previous_page: 'Edellinen sivu',
        aria_next_page: 'Seuraava sivu',
        aria_results_per_page: 'Tulokset per sivu',
        per_page_25: '25 / sivu',
        per_page_50: '50 / sivu',
        per_page_100: '100 / sivu',
        select_language: 'Valitse kieli',
        today: 'Tänään',
        tomorrow: 'Huomenna',
        dashboard_load_error: 'Kojelautaa ei voitu ladata. Yritä uudelleen.',
        enum_grade: {
            red: 'Punainen',
            amber: 'Amber',
            yellow: 'Keltainen',
            green: 'Vihreä',
        },
        enum_severity: {
            critical: 'Kriittinen',
            high: 'Korkea',
            medium: 'Keskitaso',
            low: 'Matala',
        },
        enum_priority: {
            critical: 'Kriittinen',
            high: 'Korkea',
            medium: 'Keskitaso',
            low: 'Matala',
        },
        enum_status: {
            pending: 'Odottaa',
            in_progress: 'Käynnissä',
            completed: 'Valmis',
            blocked: 'Estetty',
            escalated: 'Eskaloitu',
            cancelled: 'Peruutettu',
            overdue: 'Myöhässä',
            not_started: 'Ei aloitettu',
            new: 'Uusi',
            open: 'Avaa',
            closed: 'Suljettu',
            acknowledged: 'Tunnustettu',
            resolved: 'Ratkaistu',
        },
        enum_department: {
            unassigned: 'Ei määritetty',
        },
        enum_form_category: {
            assessment: 'Arviointi',
            assessment_report: 'Arviointiraportti',
            dpia: 'DPIA',
            authority_submission: 'Viranomaisen esitys',
            cross_border_transfer: 'Rajat ylittävä siirto',
            vendor: 'Myyjä',
            breach: 'Rikkominen',
            consent: 'Suostumus',
            governance: 'Hallinto',
            register: 'Rekisteröidy',
            monitoring: 'Valvonta',
            dpa: 'DPA',
            dsr: 'DSR',
            privacy: 'Yksityisyys',
            incident: 'Tapahtuma',
            org: 'Organisaatio',
            sector: 'sektori',
            classification: 'Luokittelu',
            discovery: 'Löytö',
            // Sprint 63.37.0 (Stream J) — the pack categories the forms dashboard showed as raw codes.
            evidence_record: 'Evidence Record',
            application: 'Application',
            report: 'Report',
            data_mapping: 'Data Mapping',
            record: 'Record',
            evidence: 'Evidence',
            certificate: 'Certificate',
            log: 'Log',
            sandbox: 'Controlled Testing',
            decision: 'Decision',
            licence: 'Licence',
            declaration: 'Declaration',
            administrative_notice: 'Administrative Notice',
            plan: 'Plan',
            dossier: 'Dossier',
            training: 'Training',
            checklist: 'Checklist',
            training_report: 'Training Report',
            reference_list: 'Reference List',
            evidence_pack: 'Evidence Pack',
            forms: 'Forms',
            authority_response_record: 'Authority Response Record',
            request: 'Request',
            attestation: 'Attestation',
            notification: 'Notification',
            evidence_pack_manifest: 'Evidence Pack Manifest',
            email_submission_record: 'Email Submission Record',
            evidence_file: 'Evidence File',
            worksheet: 'Worksheet',
            uncategorized: 'Uncategorized',
        },
        enum_module: {
            rights_requests: 'Oikeuspyynnöt',
            assessments: 'Arvioinnit',
            incidents: 'Tapahtumat',
            incident_ops: 'Tapahtumavalvonta',
            monitoring: 'Valvonta',
            vendor_risk: 'Myyjän riski',
            governance: 'Hallinto',
            deletion_orchestration: 'Poistaminen',
            access_review: 'K?ytt?oikeustarkastus',
            consent: 'Suostumus',
            consent_properties: 'Suostumuksen ominaisuudet',
            forms: 'Lomakkeet',
            localization: 'Lokalisointi',
            program_governance: 'Ohjelman hallinto',
            privacy_scanner: 'Yksityisyysskanneri',
            classification: 'Luokittelu',
            discovery: 'Löytö',
            manual: 'Manuaalinen',
            data_mapping: 'Tietojen kartoitus',
        },
        enum_risk_level: {
            critical: 'Kriittinen',
            high: 'Korkea',
            medium: 'Keskitaso',
            low: 'Matala',
        },
        enum_request_type: {
            all: 'All',
            access: 'Pääsy (DSAR)',
            deletion: 'Poistaminen',
            correction: 'Korjaus',
            objection: 'Vastalause',
            portability: 'Siirrettävyys',
            restriction: 'Rajoitus',
            consent_withdrawal: 'Suostumuksen peruuttaminen',
            protective_measures: 'Suojatoimet',
            processing_information: 'Information about Processing',
            consent_decision: 'Consent Decision',
            complaint_or_compensation: 'Complaint, Denunciation, Lawsuit or Damages Claim',
        },
        enum_locale: {
            en: 'englanti',
            vi: 'vietnamilainen',
            all: 'Molemmat',
        },
        enum_event_type: {
            case_created: 'Tapaus luotu',
            status_changed: 'Tila muutettu',
            case_assigned: 'Tapaus määrätty',
            note_added: 'Huomautus lisätty',
            attachment_added: 'Liite lisätty',
            attachment_deleted: 'Liite poistettu',
            reference_added: 'Viite lisätty',
            checklist_updated: 'Tarkistuslista päivitetty',
            identity_verified: 'Henkilöllisyys vahvistettu',
            closure_summary_added: 'Sulkemisen yhteenveto',
            approval_granted: 'Hyväksyntä myönnetty',
            template_applied: 'Malli käytetty',
            export_generated: 'Vienti luotu',
            sla_warning: 'SLA Varoitus',
            sla_overdue: 'SLA Myöhässä',
            sla_flag_revisited: 'SLA flag revisited',
            third_party_involvement_changed: 'Processor or third-party involvement changed',
        },
        more: 'more',
        use: 'Use',
        ok: 'OK',
        back_to: 'Return to',
        click_to_copy: 'click to copy',
        copied: 'Copied',
        more_actions: 'More actions',
        actions_for: 'Actions for {title}',
        previous_page: 'Previous page',
        lookup_read_failed: 'A list this page offers for choosing could not be loaded, so a picker may be empty or incomplete.',
        lookup_read_subject: 'the lists offered for choosing',
        export_error: 'Vienti epäonnistui',
        you: 'You',
        export_failed: 'Vienti epäonnistui. Yritä uudelleen.',
        filters: 'Suodattimet',
        failed: 'Epäonnistunut',
        attachment_release_action: 'Release and destroy the file',
        attachment_release_title: 'Release this evidence file',
        attachment_release_body: 'Releasing destroys the file and records the destruction on the deletion proof chain. Every entry that cites the file will then say it was destroyed, and when. This cannot be undone.',
        attachment_release_basis_legend: 'Why is the file released?',
        attachment_release_basis_retention: 'Its retention period has ended',
        attachment_release_basis_erasure: 'An erasure request reached it',
        attachment_release_reason: 'Reason (required)',
        attachment_release_retention_date: 'Date the retention period ended (required)',
        attachment_release_retention_date_hint: 'No retention period is installed for this evidence, so state the date its retention ended. The file cannot be released before that date.',
        attachment_release_erasure_request: 'Erasure request (required)',
        attachment_release_erasure_placeholder: 'Choose the erasure request',
        attachment_release_erasure_none: 'No erasure request is recorded.',
        attachment_release_erasure_none_not_decided: 'An erasure request about this person is recorded but has not been decided for fulfilment yet, so none is listed. Release the file once the erasure is decided.',
        attachment_release_erasure_none_not_qualifying: 'No erasure request recorded about this person qualifies.',
        attachment_release_confirm: 'Release and destroy',
        attachment_release_releasing: 'Releasing…',
        attachment_release_done: 'The file was released and destroyed.',
        attachment_release_failed: 'The file was not released.',
        attachment_release_not_ended: 'The retention period ends on {date}, so the file cannot be released before then.',
        attachment_release_under_hold: 'The file is under a hold, so it is not released.',
        attachment_release_not_needed: 'No evidence entry rests on this file, so it is not released: delete it the ordinary way.',
        attachment_release_decided_by_register: 'This file is evidence on a register whose own retention rule decides when its evidence is released, so it is not released by a stated date.',
        attachment_release_date_installed: 'The installed retention period ends on {date}; a different date cannot be stated.',
        attachment_released_retention: 'Evidence destroyed at the end of its retention period on {date}',
        attachment_released_erasure: 'Evidence destroyed for erasure request {request}',
        attachment_released_erasure_unnamed: 'Evidence destroyed for an erasure request',
        attachment_release_erasure_label_none: 'Erasure request',
        attachment_released_on: 'Destroyed on {date}',
        attachment_released_heading: 'Files destroyed at the end of their retention or for an erasure request',
        attachment_released_retention_stated: 'Evidence destroyed at the end of its retention period on {date}, a date stated by the person who released it',
        attachment_release_installed_date: 'Date the installed retention period ends',
        attachment_release_installed_date_hint: 'This date comes from the retention period installed for this evidence, so it is not stated here.',
        attachment_release_installed_floor_hint: 'A retention period installed for other evidence citing this file ends on {date}; the date stated may not be before it.',
        attachment_release_date_before_installed: 'A retention period installed for evidence citing this file ends on {date}; a date before it cannot be stated.',
        attachment_release_in_progress: 'This file is already being released by another request, so it was not released twice.',
        // fix-sprint-20261026-0900 F10 — the 502 the release returns when the stored object cannot be deleted.
        attachment_release_storage_delete_failed: 'The stored file could not be destroyed, so the file was not released. Submit the release again to retry it.',
        attachment_release_not_computable: 'A retention period is installed for this evidence, but when it ends cannot be computed, so the file is not released.',
        attachment_release_erasure_hint: "Only requests for erasure about the person this evidence concerns are listed: decided for fulfilment, not rejected, withdrawn or closed without being fulfilled, and with the requester's identity verified.",
        attachment_release_erasure_subject_not_named: 'The register this evidence is on does not record which person it concerns, so the file cannot be released for an erasure request.',
        attachment_release_erasure_subject_not_linked: 'The entry this evidence is on is not linked to a data subject profile, so the file cannot be released for an erasure request.',
        attachment_release_erasure_different_subjects: 'This file is evidence for entries about different people, so it cannot be released for one erasure request.',
        attachment_release_erasure_other_subject: 'That erasure request is about a different person from the one this evidence concerns.',
        attachment_release_erasure_not_actionable: "That erasure request was rejected, withdrawn or closed without being fulfilled, or the requester's identity is not verified.",
        attachment_release_erasure_not_decided: 'That erasure request has not been decided for fulfilment yet, so it does not reach this evidence. Release the file once the erasure is decided.',
        attachment_release_erasure_read_required: 'Naming a rights request as the reason for a release needs the permission to read rights requests, which you do not hold.',
        attachment_release_erasure_withheld: 'You do not hold the permission to read rights requests, so no erasure request can be listed or named here. A person who holds it can release the file for an erasure request.',
        attachment_release_preview_load_error: "What this file's release rests on could not be read.",
    },
    common_statuses: {
        removed: 'Poistettu',
        draft: 'Luonnos',
        active: 'Aktiivinen',
        inactive: 'Ei aktiivinen',
        pending: 'Odottaa',
        in_progress: 'Käynnissä',
        completed: 'Valmis',
        archived: 'Arkistoitu',
        published: 'Julkaistu',
        approved: 'Hyväksytty',
        rejected: 'Hylätty',
        cancelled: 'Peruutettu',
        blocked: 'Estetty',
        deferred: 'Lykätty',
        not_started: 'Ei aloitettu',
        dismissed: 'Ohitettu',
        failed: 'Epäonnistunut',
        in_review: 'In Katselmointia',
        deprecated: 'Vanhentunut',
        scheduled: 'Aikataulutettu',
        deploying: 'Otetaan käyttöön',
        deployed: 'Otettu käyttöön',
        paused: 'Keskeytetty',
        rolled_back: 'Rolled Takaisin',
        received: 'Vastaanotettu',
        processing: 'Käsitellään',
        under_assessment: 'Arvioinnin alla',
        implemented: 'Toteutettu',
        pending_approval: 'Odottaa Hyväksyntä',
        pending_deletion: 'Odottaa Deletion',
        on_hold: 'On Pidätys',
        deleted: 'Poistettu',
        expired: 'Vanhentunut',
        outdated: 'Vanhentunut',
        drifted: 'Poikennut',
        decommissioned: 'Käytöstä poistettu',
        match: 'Vastaavuus',
        different: 'Eri',
        missing: 'Puuttuu',
        confirmed: 'Vahvistettu',
        revoked: 'Peruutettu',
        escalated: 'Eskaloitu',
        system: 'Järjestelmä',
        custom: 'Mukautettu',
        warning: 'Varoitus',
        blocking: 'Estävä',
        analyst: 'Analyytikko',
        viewer: 'Katsoja',
        daily: 'Päivittäin',
        weekly: 'Viikoittain',
        policies: 'Käytännöt',
        denied: 'Evätty',
        verified: 'Vahvistettu',
        detected: 'Havaittu',
        ignored: 'Ohitettu',
        open: 'Avaa',
        resolved: 'Ratkaistu',
        suspended: 'Keskeytetty',
        retired: 'Eläkkeellä',
        review: 'Tarkista',
        generated: 'Luotu',
        exported: 'Vietiin',
        // Sprint 63.37.0 (Stream J) — the embedded form statuses a page prints.
        finalized: 'Finalized',
        superseded: 'Superseded',
    },
    severity: {
        low: 'Matala',
        medium: 'Keskitaso',
        high: 'Korkea',
        critical: 'Kriittinen',
    },
    sensitivity: {
        standard: 'Perus',
        sensitive: 'Arkaluonteinen',
        special_category: 'Erityinen henkilötietoryhmä',
        badge_sensitive: 'Arkaluonteinen',
    },
    change_types: {
        policy_update: 'Käytäntö Päivitä',
        new_binding: 'Uusi Binding',
        remove_binding: 'Poista Binding',
        configuration_change: 'Konfiguraatio Change',
        banner_update: 'Banner Päivitä',
    },
    comparison_types: {
        full: 'Täysi',
        banner_only: 'Vain banneri',
        purposes_only: 'Tarkoitukset Only',
        legal_bases_only: 'Legal Perusteet Only',
        consent_settings: 'Suostumus Asetukset',
    },
    lifecycle_stages: {
        active: 'Aktiivinen',
        archived: 'Arkistoitu',
        pending_deletion: 'Odottaa Deletion',
        deleted: 'Poistettu',
        on_hold: 'On Pidätys',
        creation: 'Creation',
        exception_hold: 'On Hold',
    },
    object_types: {
        personal_data: 'Personal Tiedot',
        consent_record: 'Suostumus Tietue',
        user_account: 'Käyttäjätili',
        audit_log: 'Tarkastus Loki',
        document: 'Asiakirja',
        backup: 'Varmuuskopio',
    },
    risk_categories: {
        data_breach: 'Tiedot Tietoturvaloukkaus',
        unauthorized_access: 'Unauthorized Pääsy',
        data_loss: 'Tiedot Loss',
        regulatory_non_compliance: 'Säännösten noudattamattomuus',
        third_party_risk: 'Third-Party Riski',
        operational: 'Toiminnallinen',
        technical: 'Tekninen',
        legal: 'Lakiin perustuva',
    },
    mitigation_types: {
        technical: 'Tekninen',
        organizational: 'Organisatorinen',
        contractual: 'Sopimukseen perustuva',
        procedural: 'Menettelyllinen',
    },
    safeguard_types: {
        encryption_in_transit: 'Salaus siirrossa',
        encryption_at_rest: 'Salaus lepotilassa',
        access_controls: 'Kulunvalvonta',
        contractual_clauses: 'Sopimuslausekkeet',
        data_minimization: 'Tietojen minimointi',
        pseudonymization: 'Pseudonymisointi',
        audit_rights: 'Tilintarkastusoikeudet',
        breach_notification: 'Rikkomusilmoitus',
        data_localization: 'Tietojen lokalisointi',
        other: 'muu',
    },
    scoring_methods: {
        weighted_average: 'Painotettu keskiarvo',
        simple_average: 'Yksinkertainen Keskiarvo',
        maximum: 'Enimmäismäärä',
        manual: 'Manuaalinen',
    },
    dpa_status: {
        not_required: 'Ei vaadita',
        required: 'Vaaditaan',
        in_progress: 'Käynnissä',
        completed: 'Valmis',
        signed: 'Allekirjoitettu',
    },
    disposal_methods: {
        anonymize: 'Anonymisoi',
        pseudonymize: 'Pseudonymisoi',
        aggregate: 'Aggregoi',
        delete: 'Poista',
    },
    dashboard: {
        workspace: {
            tabs_label: 'Dashboard sections',
            overview_heading: 'Your compliance posture',
            overview_description: 'Score, recommendations, coverage and operational signals.',
            tasks_heading: 'Turn priorities into progress',
            tasks_description: 'Ownership, overdue work, upcoming tasks and department delivery.',
            insights_heading: 'The full compliance picture',
            insights_description: 'Filings, third-party risk, assessments, incidents and regulatory activity.',
            visible_widgets: '{count} widgets visible',
            widgets_view: 'Widgets',
            config_description: 'Choose which widgets to show and their order. Changes are saved when you apply them.',
            search_widgets: 'Find a widget',
            search_widgets_placeholder: 'Search widgets',
            density: 'Widget spacing',
            density_comfortable: 'Comfortable',
            density_compact: 'Compact',
            default_tab: 'Open dashboard on',
            apply_configuration: 'Apply configuration',
            cancel_configuration: 'Cancel',
            move_up: 'Move {widget} up',
            move_down: 'Move {widget} down',
            enabled_count: '{enabled} of {total} items enabled',
            empty_section: 'No widgets are visible in this section. Use Configure Dashboard to choose available widgets.',
        },
        title: 'Yhteenveto',
        no_data: 'Yhteenvetotietoja ei saatavilla.',
        no_data_heading: 'Ei reaaliaikaista dataa vielä',
        no_data_detail: 'Live figures could not be loaded. The layout is shown; values will appear once the connection is restored.',
        consent_coverage: 'Suostumuskattavuus',
        ctia_filings: 'CTIA-ilmoitukset',
        next_dpia_review: 'Seuraava DPIA-tarkistus',
        next_review_none: 'Ei aikataulutettu',
        /*
         * Sprint 65.4.0 (AC5) — why the next-review tile has four states.
         *
         * A blank date reads as "nothing due soon". Measured, it almost always
         * meant something else: the only production writer of
         * `grc_dpia_records.next_review_at` was the demo seed, so every real
         * tenant saw the same dash. The tile now says which absence it is.
         */
        next_review_no_dpias: 'No DPIAs recorded',
        next_review_cadence_absent: 'No review cadence declared',
        next_review_not_scheduled: 'Not yet scheduled',
        incident_mps_filings: 'Tapahtumien MPS-ilmoitukset',
        compliance_score: 'Vaatimustenmukaisuuspisteet',
        grade: 'Arvosana',
        no_score: 'Vaatimustenmukaisuuspisteitä ei ole vielä laskettu.',
        rights_requests: 'Tietopyynnöt',
        open: 'Avoimet',
        sla_breached: 'SLA ylitetty',
        sla_approaching: 'SLA lähestyy',
        closed_month: 'Suljetut (kuukausi)',
        incidents: 'Tapaukset ja tietoturvaloukkaukset',
        active: 'Aktiiviset',
        breaches: 'Tietoturvaloukkaukset',
        overdue_tasks: 'Myöhästyneet tehtävät',
        breach_72h_notice: '72 tunnin viranomaisilmoitusaika koskee aktiivisia rikkomuksia',
        recommendations: 'Suositukset',
        take_action: 'Toimi',
        drift_events: 'Poikkeamat ja hyväksynnät',
        open_events: 'Avoimet tapahtumat',
        pending_approvals: 'Odottavat hyväksynnät',
        monitoring: 'Valvontatehtävät',
        programs: 'Ohjelmat',
        overdue: 'Myöhässä',
        pending: 'Odottaa',
        ric_cases: 'Viranomaistiedustelut',
        coverage: 'Tietojen kattavuus',
        systems: 'Järjestelmät',
        activities: 'Käsittelytoimet',
        vendors: 'Toimittajat',
        dpias: 'Vaikutustenarvioinnit',
        open_gaps: 'avoimet puutteet',
        view_as_table: 'Nayta taulukkona',
        view_as_chart: 'Nayta kaaviona',
        table_metric_header: 'Mittari',
        table_value_header: 'Arvo',
        announce_chart_view: 'Nayttaa kaavionakyman',
        announce_table_view: 'Nayttaa taulukkonakyman',
        section_core_metrics: 'Ydintunnusluvut',
        section_task_management: 'Tehtavien hallinta',
        section_compliance_insights: 'Vaatimustenmukaisuusnakymat',
        configure_dashboard: 'Maarita kojelauta',
        config_col_active: 'Aktiivinen',
        config_col_widget: 'Widget',
        collapse_section: 'Pienenna',
        expand_section: 'Laajenna',
        reset_to_defaults: 'Palauta oletukset',
        preferences_saved: 'Kojelaudan asetukset tallennettu',
        preferences_reset: 'Kojelauta palautettu roolioletuksiin',
        preferences_load_error: 'Kojelaudan asetusten lataus epäonnistui',
        preferences_save_error: 'Dashboard preferences could not be saved.',
        factor_avg_system_score: 'Järjestelmän keskimääräinen pistemäärä',
        factor_avg_vendor_score: 'Myyjän keskimääräinen pistemäärä',
        factor_processing_coverage: 'Käsittelytoiminnan kattavuus',
        factor_ropa_completeness: 'RoPA täydellisyys',
        factor_drift_penalty: 'Drift-tapahtumien rangaistus',
        rec_improve_system_gov: 'Paranna järjestelmän hallintadokumentaatiota',
        rec_improve_vendor_gov: 'Paranna toimittajan hallintadokumentaatiota',
        rec_document_processing: 'Asiakirjojen käsittelytoiminnot peittämättömille järjestelmille',
        rec_generate_ropa: 'Luo RoPA-tietueita aktiivisille käsittelytoiminnoille',
        rec_resolve_drift: 'Ratkaise avoimet kriittiset ja erittäin vakavat poikkeamat',
        score_good: 'Hyvä',
        score_fair: 'Kohtalainen',
        score_poor: 'Heikko',
        score_critical: 'Kriittinen',
        drag_to_reorder: 'Vedä järjestääksesi uudelleen',
        widget_moved: 'Widget siirretty',
        press_space_to_grab: 'Paina välilyöntiä tarttuaksesi, nuolinäppäimet siirtämiseen, välilyönti pudottamiseen',
        reorder_instructions: 'Käytä vetämistä ja pudottamista tai pikanäppäimiä järjestääksesi widgetit uudelleen',
        page_title: 'Compliance Dashboard',
        welcome_back: 'Welcome back, {name}',
        subtitle: 'Your real-time view of compliance posture, risk, and progress across your organization.',
        section_overview: 'Overview',
        kpi_strip_label: 'KPI overview strip',
        today_label: 'Today',
        avatar_aria: 'Signed in as {name}',
        no_prior_period: 'No prior period yet',
        period_current: 'Current period',
        gauge_aria: '{score} out of 100, {label}',
        donut_aria: '{total} vendors by risk level',
        bar_aria: '{label}: {value} percent',
        config_saving: 'Saving…',
        task_widgets: {
            tasks_overview: 'Tehtävien yleiskatsaus',
            pending: 'Odottaa',
            in_progress: 'Meneillään',
            overdue: 'Myöhässä',
            done_7d: 'Valmis (7p)',
            by_priority: 'Prioriteetin mukaan',
            view_all_tasks: 'Näytä kaikki tehtävät',
            upcoming_tasks: 'Tulevat tehtävät (seuraavat 7 päivää)',
            no_upcoming: 'Ei tehtäviä seuraavan 7 päivän aikana.',
            view_calendar: 'Näytä kalenteri',
            overdue_alerts: 'Myöhässä',
            no_overdue: 'Ei myöhässä olevia tehtäviä. Kaikki on raiteilla.',
            assigned_to: 'Määrätty',
            unassigned_actor: 'Unassigned',
            untitled_task: 'Untitled task',
            view_all_overdue: 'Näytä kaikki erääntyneet',
            notifications: 'Viimeaikaiset ilmoitukset',
            unread: 'unread',
            no_notifications: 'Ei ilmoituksia. Olette kaikki kiinni!',
            view_all_notifications: 'Näytä kaikki ilmoitukset',
            load_error: 'Tietojen lataaminen epäonnistui.',
            day_late: 'päivän myöhässä',
            days_late_plural: 'päivää myöhässä',
            due_today: 'Erään tänään',
            just_now: 'juuri nyt',
            minutes_short: 'm',
            hours_short: 'h',
            days_short: 'd',
            cross_org_stats: 'Organisaatioiden väliset tilastot',
            by_organization: 'Organisaation mukaan',
            overdue_across_orgs: 'myöhässä olevia tehtäviä eri organisaatioissa',
            overdue_count_label: '{count} overdue tasks',
            total: 'Yhteensä',
            tasks: 'tasks',
            organization: 'Organisaatio',
            current_org: 'Nykyinen organisaatio',
            all_orgs: 'Kaikki organisaatiot',
            all_organizations: 'Kaikki organisaatiot',
            filter_by_org: 'Suodata organisaation mukaan',
            cross_org: 'Cross-org',
            empty_calendar: 'Ei tehtäviä tälle ajanjaksolle. Luo ensimmäinen tehtäväsi tai tarkista suodattimet.',
            empty_my_tasks: 'Sinulle ei ole määritetty tehtäviä. Hienoa työtä!',
            empty_overdue: 'Ei myöhässä olevia tehtäviä. Kaikki on raiteilla.',
            empty_notifications: 'Ei ilmoituksia. Olette kaikki kiinni!',
            empty_workflows: 'Työnkulkuja ei ole määritetty. Aloita selaamalla malleja.',
            remediation_pipeline: 'Tasks & Remediation Pipeline',
            no_open_tasks: 'No open tasks — the pipeline is clear.',
            open_tasks: 'Open tasks',
        },
        my_tasks_widget: {
            title: 'Omat Tehtävät',
            no_tasks: 'Sinulle ei ole osoitettu tehtäviä.',
            load_error: 'Tehtävien lataaminen epäonnistui.',
            overdue: 'myöhässä',
            remaining: 'jäljellä',
            view_all: 'Näytä Kaikki Tehtävät',
        },
        my_section_status_widget: {
            title: 'Oman Osion Tila',
            overall_completion: 'Kokonais',
            sections_count: 'Osiot',
            no_sections: 'Osastoosi ei ole osoitettu osioita.',
            load_error: 'Osion tilan lataaminen epäonnistui.',
            department_tasks_title: 'Osaston tehtävät',
            no_department_tasks: 'Osastollasi ei ole tehtäviä.',
            department_tasks_error: 'Osaston tehtävien lataaminen epäonnistui.',
            no_department: 'Sinua ei ole liitetty osastoon.',
        },
        revision_requests_widget: {
            title: 'Tarkistuspyynnöt',
            no_revisions: 'Ei tarkistuspyyntöjä. Kaikki osiot ovat aikataulussa.',
            load_error: 'Tarkistuspyyntöjen lataaminen epäonnistui.',
        },
        consent_summary: {
            title: 'Suostumus',
            active: 'Aktiivinen',
            withdrawn_30d: 'Peruttu (30pv)',
            expiring_7d: 'Vanhenee (7pv)',
        },
        transfer_assessments: {
            title: 'Siirtoarviot',
            open: 'Avaa',
            approved: 'Hyväksytty',
            gaps_flagged: 'Aukkoja merkitty',
        },
        breach_notifications: {
            title: 'Rikkomusilmoitukset',
            deadline_72h: '72h määräaika',
            submitted: 'Lähetetty',
            pending: 'Odottaa',
        },
        authority_requests: {
            title: 'Viranomaisten pyynnöt',
            pending: 'Odottaa',
            overdue: 'Myöhässä',
            responded_30d: 'Vastattu (30p)',
        },
        data_subjects_widget: {
            title: 'Rekisteröidyt',
            total: 'Yhteensä',
            high_risk: 'Korkea riski',
            active_30d: 'Aktiivinen (30 pv)',
        },
        audit_activity_summary: {
            title: 'Tarkastustoiminta',
            events_today: 'Tämän päivän tapahtumat',
            events_7d: 'Tapahtumat (7p)',
            unique_actors_30d: 'Ainutlaatuiset näyttelijät (30d)',
        },
        onboarding_progress: {
            title: 'Käyttöönoton edistyminen',
            completion_pct: 'Täydellinen',
            status_label: 'Tila',
            current_step: 'Nykyinen vaihe',
            status: {
                in_progress: 'Käynnissä',
                completed: 'Valmis',
                skipped: 'Ohitettu',
                not_started: 'Ei aloitettu',
            },
        },
        sector_overlay_reports_due: {
            title: 'Erääntyvät toimialalisäosan raportit',
        },
        kpi: {
            framework_coverage: 'Framework Coverage',
            open_actions: 'Open Actions',
            high_priority: '{count} high priority',
            vendor_health: 'Vendor Health',
            at_risk: '{count} at risk',
            open_count: '{count} open',
            active_count: '{count} active',
            grade_label: 'Grade: {grade}',
            consent_coverage_hint: 'of active consent workflows',
            consent_coverage_source_web_consent_records: 'of web consent records, last 30 days',
            consent_coverage_source_web_consent_events: 'of web consent events, last 30 days',
            consent_coverage_source_consent_records: 'of consent records, last 30 days',
            of_installed_frameworks: 'of installed frameworks',
            no_frameworks_installed: 'No frameworks installed',
            unavailable: 'Not available',
            consent_no_records: 'No consent records in the last 30 days',
            tile_hint: {
                no_score: 'Not calculated yet',
                load_error: 'Could not be loaded',
                no_frameworks_installed: 'Mandatory: none installed',
                vendor_at_risk: '{at_risk} of {total} high or critical',
                vendor_load_error: 'Could not be read',
                no_vendor_data: 'No vendor data',
                consent_no_records: 'No records, last 30 days',
                consent_source_web_consent_records: 'of web consent records',
                consent_source_web_consent_events: 'of web consent events',
                consent_source_consent_records: 'of consent records',
                not_reported: 'Not reported',
                delta_since: 'points since {period}',
            },
        },
        summary_reloaded: 'Dashboard reloaded.',
        scope_incidents: 'Incidents still open. Breaches are the subset flagged as a personal-data breach.',
        scope_breach_response: 'The same open breaches, by notification and remediation state.',
        scope_breach_notifications: 'Notification filings. Due in 72h counts those with a filing deadline inside the next 72 hours; submitted and pending count every filing in that state, whatever its deadline.',
        vendor_health_hint: '{at_risk} of {total} vendors at high or critical risk',
        vendor_health_load_error: 'Vendor data could not be read.',
        figure_not_reported: 'This figure was not reported in the last dashboard read.',
        framework_coverage: {
            title: 'Framework Coverage',
            overall: 'Overall coverage',
            none: 'The installed frameworks declare no requirements yet.',
            none_installed: 'No frameworks installed. Install a regulatory pack to see its coverage here.',
            requirements_mapped: '{mapped} of {total} requirements mapped',
            no_requirements: 'No requirements declared',
            overall_denominator: 'across {count} frameworks that declare requirements',
            no_requirements_count: '{count} installed frameworks declare no requirements yet',
            no_requirements_count_one: '1 installed framework declares no requirements yet',
            showing_lowest: 'Showing the {shown} lowest-covered of {total}',
            gaps: '{count} open gaps',
            view_all: 'View frameworks',
            load_error: 'Framework coverage could not be loaded.',
        },
        automation_impact: {
            title: 'Automation Impact',
            scope: 'The controls your installed frameworks declare as applicable to this organization, and how many of them have automated work recorded.',
            load_error: 'Automation impact could not be loaded.',
            no_frameworks_installed: 'No installed framework is one your organization must comply with',
            not_declared: 'No installed framework states which of its controls apply to this organization',
            profile_incomplete: 'Record your organization profile to see which controls apply',
            none_applicable: 'No declared control applies to this organization',
            no_emitter: 'No automated work is recorded against controls yet',
            no_point: 'No completed month has a figure to show yet',
            figure_hint: '{automated} of {applicable} applicable controls automated in {period}',
            figure_hint_one: '{automated} of 1 applicable control automated in {period}',
            delta_since: 'percentage points since {period}',
            bucket_applicable: 'Applicable',
            bucket_out_of_scope: 'Declared not to apply to you',
            bucket_not_declared: 'Applicability not declared',
            bucket_profile_incomplete: 'Awaiting your organization profile',
            applicable_from: 'The applicable controls come from {count} installed frameworks',
            applicable_from_one: 'The applicable controls come from 1 installed framework',
            review_profile: 'Review your organization profile',
            tile_hint: {
                load_error: 'Could not be loaded',
                no_frameworks_installed: 'No mandatory framework installed',
                not_declared: 'Applicability not declared',
                profile_incomplete: 'Organization profile needed',
                none_applicable: 'No declared control applies',
                no_emitter: 'No automated work recorded',
                no_point: 'No month to show yet',
                figure: '{automated} of {applicable} controls, {period}',
                figure_one: '{automated} of 1 control, {period}',
            },
        },
        recommendations_widget: {
            title: 'Key Recommendations',
            view_all: 'View all ({count})',
            priority_high: 'High',
            priority_medium: 'Medium',
            priority_low: 'Low',
            none: 'No recommendations. Every score factor is within its healthy range.',
        },
        rights_status: {
            title: 'Rights Requests',
            open_requests: 'Open requests',
        },
        module_status: {
            title: 'Module Status',
            enabled: 'Enabled',
            modules_count: '{count} modules',
            modules_count_one: '{count} module',
            none: 'No modules are enabled for this organization.',
        },
        recent_activity: {
            title: 'Recent Activity',
            view_all: 'View all',
            empty: 'No recent activity.',
            load_error: 'Recent activity could not be loaded.',
        },
        persona_widgets: {
            framework_filter: 'Suodata kehyksen mukaan',
            all_frameworks: 'Kaikki kehykset',
            filing_lifecycle: 'Ilmoitusten elinkaari',
            filing_pipeline: 'Ilmoitusputki',
            draft: 'Luonnos',
            internal_review: 'Sisäinen tarkistus',
            approved: 'Hyväksytty',
            submitted: 'Lähetetty',
            supplement_requested: 'Täydennys pyydetty',
            resubmitted: 'Lähetetty uudelleen',
            accepted: 'Hyväksytty',
            rejected: 'Hylätty',
            closed: 'Suljettu',
            deadline: 'Määräaika',
            days_remaining: 'päivää jäljellä',
            days_overdue: 'päivää myöhässä',
            no_filings: 'Ei aktiivisia ilmoituksia.',
            department_consolidation: 'Osastojen konsolidointi',
            departments_completed: 'Osastot valmiit',
            departments_pending: 'Osastot odottavat',
            departments_overdue: 'Osastot myöhässä',
            no_consolidation_cases: 'Ei usean osaston tapauksia käynnissä.',
            vendor_risk: 'Toimittaja- ja kolmannen osapuolen riski',
            pending_assessment: 'Odottava arviointi',
            overdue_questionnaires: 'Myöhässä olevat kyselyt',
            dpa_review_queue: 'DPA-tarkistusjono',
            risk_distribution: 'Riskien jakautuminen',
            total_vendors: 'Toimittajat yhteensä',
            by_risk_level: 'Riskitason mukaan',
            no_vendor_data: 'Toimittajariskitietoja ei saatavilla.',
            discovery_summary: 'Tietojen l?ydett?vyys',
            discovery_classification: 'Tietojen löytäminen ja luokittelu',
            active_scans: 'Aktiiviset skannaukset',
            labels_pending: 'Tarrat odottavat tarkistusta',
            exceptions_unresolved: 'Poikkeukset Ratkaisematta',
            pending_review: 'Odottava tarkistus',
            exceptions: 'Poikkeukset',
            approved_labels: 'Hyväksytyt merkinnät',
            total_classified: 'Yhteensä luokiteltu',
            no_discovery_data: 'Löytämistietoja ei saatavilla.',
            ai_governance: 'Tekoälyjärjestelmien hallinto',
            systems_inventory: 'Järjestelmät varastossa',
            systems_in_inventory: 'Järjestelmät inventaariossa',
            classification_breakdown: 'Luokituksen erittely',
            dossier_completion: 'Asiakirjakansion valmius',
            by_classification: 'Luokittelun mukaan',
            labeling_status: 'Merkintätila',
            not_started: 'Ei aloitettu',
            no_ai_data: 'Tekoälyhallintotietoja ei saatavilla.',
            obligations_complete: 'Obligations Complete',
            obligations_complete_of: '{complete} of {applicable} applicable obligations',
            no_obligations_apply: 'No applicable obligation recorded',
            ai_register_restricted: 'Your role cannot read the AI system register.',
            breach_response: 'Tietoturvaloukkaus- ja poikkeamavaste',
            active_breaches: 'Aktiiviset loukkaukset',
            notification_status: 'Ilmoituksen tila',
            remediation_progress: 'Korjauksen edistyminen',
            supplement_loops: 'Täydennyssilmukat',
            no_breach_data: 'Ei aktiivisia loukkoustapauksia.',
            audit_readiness: 'Tarkastus- ja arviointivalmius',
            assessments: 'Arvioinnit',
            gap_findings: 'Puutelöydökset',
            evidence_completion: 'Todisteiden valmius',
            export_ready: 'Vientivalmis',
            export_ready_yes: 'Ready',
            export_ready_no: 'Not ready',
            unspecified_framework_group: 'No framework recorded',
            not_ready: 'Ei valmis',
            no_audit_data: 'Tarkastusvalmiustietoja ei saatavilla.',
            framework_label_vn_pdpl: 'VN PDPL 2025',
            framework_label_vn_cybersecurity: 'VN Kyberturvallisuus 2018',
            framework_label_vn_ecommerce: 'VN Sahkoinen kauppa 2023',
            framework_label_vn_telecom: 'VN Televiestinta 2023',
            framework_label_vn_ai: 'VN Tekoalylaki 2025',
            framework_label_gdpr: 'GDPR',
            framework_label_unspecified: 'Määrittelemätön',
            my_department: 'Oma osasto',
            my_tasks: 'Omat tehtävät',
            section_status: 'Osion tila',
            revision_requests: 'Muutospyynnöt',
            completion: 'Valmius',
            reason: 'Syy',
            requested_at: 'Pyydetty',
            no_tasks_assigned: 'Ei tehtäviä sinulle.',
            no_sections: 'Ei osioita osastollesi.',
            no_revisions: 'Ei muutospyyntöjä.',
            load_error: 'Tietojen lataaminen epäonnistui.',
            vendor_health_needs_attention: '{count} vendors require immediate attention',
            review_vendors: 'Review Vendors',
            total_vendors_label: 'Total Vendors',
        },
    },
    connectors: {
        nav: {
            instances: 'Instanssit',
            credentials: 'Tunnisteholvi',
            event_bus: 'Tapahtumaväylä',
            definitions: 'Määritelmät',
            brds: 'BRD-keruu',
            security_reviews: 'Tietoturva-arvioinnit',
            poc_scorecards: 'POC-tuloskortit',
            automation_planning: 'Automaatiosuunnittelu',
            system_inventory: 'Järjestelmäinventaario',
            traceability_matrix: 'Jäljitettävyysmatriisi',
            gap_register: 'Puuterekisteri',
        },
        instances: {
            title: 'Liitininstanssit',
            page_title: 'Liittimet',
            add: 'Lisää instanssi',
            test: 'Testaa yhteys',
            sync_now: 'Synkronoi nyt',
            new_instance: 'Uusi Liitin Instance',
            no_instances: 'Ei connector instances löytynyt',
            instance_count: '{count} esiintymää',
            confirm_delete: 'Poista connector instance "{name}"?',
            all_connector_types: 'Kaikki liitintyypit',
            select_connector: 'Valitse a connector...',
            fetch_error: 'Epäonnistui: fetch connector instances. Yritä uudelleen.',
            fetch_definitions_error: 'Epäonnistui: fetch connector definitions. Yritä uudelleen.',
            create_error: 'Epäonnistui: create connector instance. Yritä uudelleen.',
            test_error: 'Epäonnistui: test connector. Yritä uudelleen.',
            status_error: 'Yhdistimen tilan päivittäminen epäonnistui. Yritä uudelleen.',
            delete_error: 'Epäonnistui: delete connector instance. Yritä uudelleen.',
            confirm_disable: 'Disable "{name}"? Sync will stop until the instance is reactivated.',
            credentials_label: 'Credentials',
            instance_created: 'Connector instance created',
            base_url_label: 'Base URL',
            base_url_hint: "The https address of this connection's endpoint. Test sends the connector type's declared health check to a path on it.",
            base_url_invalid: 'Enter an https address, such as https://api.example.com.',
            edit_instance: 'Edit Connector Instance',
            edit_instance_action: 'Edit',
            instance_updated: 'Connector instance updated',
            update_error: 'The connector instance could not be updated.',
            no_credentials_hint: 'No credentials available. Create one in the Credential Vault first.',
            test_passed: 'Test passed',
            test_failed: 'Test failed',
            status_changed: 'Tila päivitetty',
            reactivate: 'Aktivoi uudelleen',
            mark_ready_for_testing: 'Mark ready for testing',
            marked_ready: 'Marked ready for testing. No connection check was made: this connector type declares no health check.',
            test_healthy: 'Health check passed ({detail})',
            test_unhealthy: 'Health check failed ({detail})',
            probe_detail_expected_status: 'the endpoint answered with the expected status',
            probe_detail_unexpected_status: 'the endpoint answered with a status other than the expected one',
            probe_detail_timeout: 'no answer before the timeout',
            probe_detail_network_error: 'the endpoint could not be reached',
            probe_detail_target_refused: "the endpoint's address is not allowed",
            record_sync: 'Record sync result',
            record_sync_title: 'Record sync result: {name}',
            record_sync_note: 'Record the outcome of a sync run that ran outside the platform. The platform does not run the sync itself.',
            sync_outcome: 'Outcome',
            sync_outcome_success: 'Success',
            sync_outcome_partial: 'Partial',
            sync_outcome_failure: 'Failure',
            records_processed: 'Records processed',
            records_created: 'Records created',
            records_failed: 'Records failed',
            sync_recorded: 'Sync result recorded.',
            sync_record_error: 'Failed to record the sync result.',
            recording: 'Recording…',
            refusal_base_url_invalid: 'This base URL cannot be used: enter the https address of an endpoint the platform may call, such as https://api.example.com. Private and local addresses are not allowed.',
            refusal_base_url_missing: 'This connection has no base URL, so its health check has nowhere to go. Use Edit to add the base URL, then test again.',
            refusal_base_url_missing_ask_admin: 'This connection has no base URL, so its health check has nowhere to go. Ask an administrator who can edit connections to add the base URL, then test again.',
            refusal_base_url_invalid_ask_admin: "This connection's base URL cannot be used: it must be the https address of an endpoint the platform may call, and private and local addresses are not allowed. Ask an administrator who can edit connections to correct it, then test again.",
        },
        credentials: {
            title: 'Tunnisteholvi',
            page_title: 'Liittimen tunnistetiedot',
            add: 'Lisää tunniste',
            rotate: 'Kierrätä',
            expired: 'Vanhentunut',
            expiring: 'Vanhenemassa pian',
            orphaned: 'Orpo',
            new_credential: 'Uusi Tunniste',
            no_credentials: 'Ei credentials löytynyt',
            no_expiry: 'Ei vanhenemista',
            linked_connectors: 'Linked Liittimet',
            expires_at: 'Vanhenee',
            value_label: 'Arvo * (vain kirjoitus)',
            confirm_delete_title: 'Vahvista poisto',
            confirm_delete_message: 'Poista tämä tunniste? Yhdistetyt liittimet menettävät pääsyn.',
            rotate_title: 'Kierrätä tunniste',
            new_credential_value: 'Uusi tunnistearvo',
            fetch_error: 'Epäonnistui: load credentials. Yritä uudelleen.',
            health_error: 'Epäonnistui: load health. Yritä uudelleen.',
            create_error: 'Epäonnistui: create credential. Yritä uudelleen.',
            rotate_error: 'Epäonnistui: rotate credential. Yritä uudelleen.',
            delete_error: 'Epäonnistui: delete credential. Yritä uudelleen.',
        },
        event_bus: {
            title: 'Tapahtumaväylä',
            page_title: 'Tapahtumaväylä',
            events: 'Tapahtumat',
            subscriptions: 'Tilaukset',
            dlq: 'Virheviestijono',
            publish: 'Julkaise tapahtuma',
            retry: 'Yritä uudelleen',
            dismiss: 'Ohita',
            search_events: 'Hae tapahtuma...',
            filter_event_type: 'Suodata by tapahtuma tyyppi...',
            event_type: 'Tapahtumatyyppi',
            source_module: 'Lähde Moduuli',
            timestamp: 'Aikaleima',
            delivery_status: 'Delivery Tila',
            correlation_id: 'Korrelaatio Tunniste',
            tracking_id: 'Seurantatunnus',
            delivery_status_hint: 'Toimituksen tila päivittyy automaattisesti tapahtumien käsittelyn yhteydessä.',
            no_events: 'Ei tapahtuma löytynyt',
            total_with_count: '{count} yhteensä',
            prev: 'Edellinen',
            new_subscription: '+ Uusi tilaus',
            subscriber_type: 'Subscriber Tyyppi',
            subscriber_id: 'Subscriber Tunniste',
            event_type_pattern: 'Tapahtuma Tyyppi Malli',
            event_type_pattern_placeholder: 'esim. incident.*',
            filter_expression: 'Suodata Expression',
            filter_expression_placeholder: 'Valinnainen filter',
            subscriber_type_hint: 'Valitse tapahtumien toimitustapa: webhook lähettää HTTP-pyyntöjä, sähköposti lähettää ilmoituksia, jono ohjaa viestijonoon.',
            subscriber_id_hint: 'Syötä tapahtumien kohde. Webhookeille käytä URL-osoitetta. Sähköpostille käytä osoitetta. Jonoille käytä jonon nimeä.',
            event_type_pattern_hint: 'Täsmää tapahtumia tyypin mukaan jokerimerkeillä. Käytä * minkä tahansa segmentin täsmäämiseen (esim. incident.* täsmää incident.created, incident.resolved).',
            filter_expression_hint: 'Valinnainen. Suodata tapahtumia sisällön mukaan (esim. severity=high).',
            subscriber_id_placeholder_webhook: 'esim. https://sovelluksesi.com/webhook',
            subscriber_id_placeholder_email: 'esim. halytykset@yrityksesi.com',
            subscriber_id_placeholder_queue: 'esim. tapahtumajoono',
            event_pattern: 'Tapahtuma Malli',
            filter: 'Suodata',
            enabled: 'Käytössä',
            delivery_stats: 'Toimitustilastot',
            no_subscriptions: 'Ei subscriptions löytynyt',
            on: 'ON',
            off: 'OFF',
            delivered_failed: '{delivered} toimitettu / {failed} epäonnistunut',
            confirm_delete_subscription: 'Poista this subscription?',
            unresolved: 'Ratkaisematon',
            resolved: 'Ratkaistu',
            event_id: 'Tapahtuma Tunniste',
            subscription_id: 'Tilaus Tunniste',
            error_code: 'Virhe Koodi',
            error_message: 'Virhe Message',
            attempts: 'Yritykset',
            last_failed: 'Last Epäonnistunut',
            no_dlq_entries: 'Ei dead letter entries löytynyt',
            dlq_count: '{count} merkintää yhteensä',
            fetch_events_error: 'Epäonnistui: fetch tapahtuma. Yritä uudelleen.',
            fetch_subscriptions_error: 'Epäonnistui: fetch subscriptions. Yritä uudelleen.',
            fetch_dlq_error: 'Epäonnistui: fetch DLQ entries. Yritä uudelleen.',
            create_subscription_error: 'Epäonnistui: create subscription. Yritä uudelleen.',
            toggle_subscription_error: 'Epäonnistui: toggle subscription. Yritä uudelleen.',
            delete_subscription_error: 'Epäonnistui: delete subscription. Yritä uudelleen.',
            retry_error: 'Yritä uudelleen epäonnistunut',
            dismiss_error: 'Dismiss epäonnistunut',
            event_published: 'Tapahtuma julkaistu',
            publish_event_error: 'Tapahtuman julkaisu epäonnistui. Yritä uudelleen.',
            priority_label: 'Prioriteetti',
            priority_low: 'Matala',
            priority_normal: 'Normaali',
            priority_high: 'Korkea',
            payload_helper_text: 'Syötä tapahtumatiedot JSON-muodossa. Esimerkki: {"incident_id": "INC-001", "severity": "high"}',
            subscription_created: 'Tilaus luotu',
            subscription_toggled: 'Tilaus vaihdettu',
            subscription_deleted: 'Tilaus poistettu',
            dlq_retried: 'DLQ-viestiä yritettiin uudelleen',
            dlq_dismissed: 'DLQ-viesti hylätty',
        },
        definitions: {
            title: 'Liittimen määritelmät',
            page_title: 'Liitinmäärittelyt',
            add: 'Lisää määritelmä',
            new_definition: 'Uusi liittimen määritelmä',
            edit_definition: 'Muokkaa liittimen määritelmää',
            no_definitions: 'Liitinmääritelmiä ei löytynyt',
            definition_count: '{count} määritelmä(t)',
            all_categories: 'Kaikki luokat',
            select_category: 'Valitse luokka...',
            capabilities: 'Ominaisuudet',
            capabilities_hint: 'Ominaisuudet (pilkuilla erotettu)',
            capabilities_placeholder: 'esim. lukea, kirjoittaa, synkronoida',
            created: 'Liittimen määritelmä luotu',
            updated: 'Liittimen määritelmä päivitetty',
            fetch_error: 'Liitinmääritelmien noutaminen epäonnistui. Yritä uudelleen.',
            create_error: 'Liittimen määrityksen luominen epäonnistui',
            update_error: 'Yhdistimen määrityksen päivittäminen epäonnistui',
            health_check: 'Health check',
            health_check_none: 'None declared',
            health_check_declare: 'This connector type declares a health check',
            health_check_method: 'Method',
            health_check_path: 'Path',
            health_check_expected_status: 'Expected HTTP status',
            health_check_timeout: 'Timeout (seconds)',
            health_check_hint: "Test sends this one request to the connection's base URL (its base_url setting) and records it as healthy only when the answer has the expected status. Without a health check, Test only marks a connection ready for testing.",
        },
        brds: {
            title: 'BRD-keruu',
            page_title: 'BRD-tallennus',
            create_brd: 'Luo BRD',
            brd_title: 'BRD-otsikko',
            description: 'Kuvaus',
            requirements: 'Vaatimukset',
            status: 'Tila',
            stakeholders: 'Sidosryhmät',
            connector_label: 'Liitin',
            select_connector: 'Valitse liitin...',
            approval: 'Hyväksyntä',
            no_brds_found: 'BRD:itä ei löytynyt',
            decision_records: 'Päätöstietueet',
            create_decision: 'Luo päätös',
            alternatives_considered: 'Harkitut vaihtoehdot',
            alternative_name: 'Vaihtoehdon nimi',
            reason_rejected: 'Hylkäyksen syy',
            add_alternative: 'Lisää vaihtoehto',
            remove_alternative: 'Poista vaihtoehto {name}',
            alternatives: 'Vaihtoehdot',
            rationale: 'Perustelu',
            selected_connector: 'Valittu liitin',
            comma_separated_names: 'Pilkuilla erotetut nimet',
            brd_created: 'BRD luotu',
            decision_record_created: 'Päätöstietue luotu',
            no_decision_records_found: 'Ei päätöstietueita löytynyt',
            aria_brds_table: 'BRD:t',
            aria_decision_records_table: 'Päätöstietueet',
            aria_approve: 'Hyväksy {name}',
            aria_archive: 'Arkistoi {name}',
            brd_title_help: 'Lyhyt, ihmisen luettavissa oleva nimi tälle BRD:lle – esim. "Jira-lippujen integrointi", "Okta SSO:n käyttöönotto".',
            brd_title_placeholder: 'esim. Jiran lippujen integrointi',
            description_help: 'Liiketoimintaongelman tämä liitin ratkaisee. Millaista tulosta odotat sen jälkeen, kun se on live-tilassa?',
            description_placeholder: 'esim. Automatisoi tapahtumalipun luominen valvontahälytyksistä, jotta päivystys haetaan 60 sekunnissa.',
            connector_help: 'Valinnainen. Valitse liitin, johon tämä BRD on tarkoitettu – jätä tyhjäksi, jos liitintä ei ole vielä valittu.',
            requirements_help: 'Pilkuilla eroteltu luettelo pakollisista ominaisuuksista – esim. "OAuth2-kirjautuminen, webhook-toimitus, tarkastuksen vienti".',
            requirements_placeholder: 'OAuth2-kirjautuminen, webhook-toimitus, tarkastuksen vienti',
            stakeholders_help: 'Pilkuilla erotetut nimet tai sähköpostiosoitteet henkilöistä, joiden on kirjauduttava ulos.',
            selected_connector_help: 'Valitse liitin, jota tämä päätös koskee.',
            rationale_help: 'Miksi tämä liitin valittiin? Käsittele ratkaisevat tekijät, jotta tuleva lukija ymmärtää kutsun.',
            rationale_placeholder: 'esim. Valitsi Jiran, koska sillä on jo lippuhistoriamme ja joukkue on koulutettu siihen.',
            alternatives_considered_help: 'Muut liittimet arvioitiin ja miksi niitä ei valittu. Lisää puolustettavuutta tarkastukseen.',
            alternative_name_placeholder: 'esim. ServiceNow',
            reason_rejected_placeholder: 'esim. Hinta 3 kertaa korkeampi, ei etua käyttötapauksellemme',
        },
        security_reviews: {
            title: 'Tietoturva-arvioinnit',
            page_title: 'Turvallisuuskatsaukset',
            create_review: 'Luo arviointi',
            connector_name: 'Liittimen nimi',
            connector_label: 'Liitin',
            select_connector: 'Valitse liitin...',
            reviewer: 'Arvioija',
            select_reviewer: 'Valitse arvioija...',
            findings: 'Havainnot (yksi per rivi)',
            risk_level: 'Riskitaso',
            risk_level_placeholder: 'esim. korkea, keskitaso, matala',
            notes: 'Muistiinpanot',
            review_status: 'Arvioinnin tila',
            checklist: 'Tarkistuslista',
            privacy_impact: 'Tietosuojavaikutus',
            approval_gate: 'Hyväksyntäportti',
            no_reviews_found: 'Arviointeja ei löytynyt',
            review_created: 'Turvallisuuskatsaus luotu',
            create_control_mapping: 'Luo kontrollikartoitus',
            control_mapping_created: 'Kontrollikartoitus luotu',
            connector_id_label: 'Liittimen tunnus',
            control_id_label: 'Kontrollin tunnus',
            control_name_label: 'Kontrollin nimi',
            aria_approve: 'Hyväksy {name}',
            aria_reject: 'Hylkää {name}',
            scan_imported: 'Skannaus tuotu onnistuneesti',
            import_scan: 'Tuo skannaus',
            scan_format: 'Skannausmuoto',
            scan_data: 'Skannausdata',
            import_results: 'Tuontitulokset',
            recommendations: 'Suositukset',
            auto_populated_from: 'Täytetty automaattisesti kohteesta {tool}',
            manual_entry: 'Manuaalinen syöttö',
            automation_type: 'Automaatiotyyppi',
            select_automation_type: 'Valitse automaatiotyyppi...',
            automation_full: 'Full — liitin täysin automatisoi tämän ohjauksen',
            automation_partial: 'Osittainen — liitin antaa todisteita; ihmiset toimivat edelleen',
            automation_monitoring: 'Vain valvonta – liitin tarkkailee, ei pakota',
            control_id_help: 'Käytä vaatimustenmukaisuusjärjestelmäsi koodia (esim. ISO-27001-A.12.1, SOC2-CC6.1 tai sisäisen valvontatunnuksesi).',
            control_name_help: 'Lyhyt ihmisen luettavissa oleva nimi (esim. "Verkon käyttörajoitus").',
            description_label: 'Kuvaus',
            load_sarif_example: 'Lataa SARIF-esimerkki',
            load_json_example: 'Lataa mukautettu esimerkki JSON',
            scan_format_help: 'SARIF on useimpien skannerien (Snyk, CodeQL, Semgrep) lähettämä OASIS-standardi. Käytä mukautettua JSON työkaluille, jotka eivät lähetä SARIF:ia.',
            scan_data_help: 'Liitä koko skannaustuloste, lähetä .sarif/.json-tiedosto tai napsauta Lataa esimerkki -painiketta nähdäksesi muodon.',
            upload_file_label: 'Lataa .sarif- tai .json-tiedosto',
            invalid_scan_json: 'Skannaustietoja ei voitu jäsentää muodossa JSON. Tarkista, onko korostetun merkin lähellä syntaksivirheitä.',
            save_as_review: 'Tallenna suojausarvioksi',
            discard_preview: 'Hylkää esikatselu',
            preview_hint: 'Tämä on esikatselu – mitään ei ole vielä tallennettu. Napsauta "Tallenna suojausarvioksi" luodaksesi odottavan tarkistuksen, joka on esitäytetty näillä arvoilla.',
            reviewer_required_for_save: 'Valitse tarkistaja tallentaaksesi tämän skannauksen tietoturvatarkistuksena.',
        },
        poc_scorecards: {
            title: 'POC-tuloskortit',
            page_title: 'POC-tuloskortit',
            create_scorecard: 'Luo tuloskortti',
            connector_label: 'Liitin',
            select_connector: 'Valitse liitin...',
            vendor_name: 'Toimittajan nimi',
            recommendation: 'Suositus',
            recommendation_placeholder: 'esim. Jatka, Älä jatka, Vaatii lisäarviointia',
            criteria: 'Arviointikriteerit',
            criterion_name: 'Kriteerin nimi',
            score: 'Pisteet',
            max_score: 'Enimmäispisteet',
            notes: 'Muistiinpanot',
            add_criterion: 'Lisää kriteeri',
            remove_criterion: 'Poista kriteeri {name}',
            total_score: 'Kokonaispisteet',
            api_quality: 'API-laatu',
            reliability: 'Luotettavuus',
            support_quality: 'Tuen laatu',
            cost_score: 'Kustannusarvio',
            overall_score: 'Kokonaisarvio',
            evidence: 'Todisteet',
            no_scorecards_found: 'Tuloskortteja ei löytynyt',
            scorecard_created: 'POC-tuloskortti luotu',
            connector_help: 'Valitse liitin, jolle tämä POC-arviointi on tarkoitettu.',
            vendor_name_help: 'Myyjä tai arvioitava tuote – esim. "Okta", "Datadog", "Lumihiutale".',
            vendor_name_placeholder: 'esim. Okta, Datadog, Snowflake',
            recommendation_help: 'Viimeinen puhelusi arvioinnin jälkeen. Pidä se lyhyenä ja päättäväisenä.',
            criteria_help: 'Anna toimittajalle pisteet jokaisesta arviointikriteeristä. Lisää yksi rivi ehtoa kohden (API laatu, luotettavuus, tuki, hinta jne.).',
            criterion_name_help: 'Mitä pisteet - esim. "API Laatu", "Luotettavuus", "Tuki", "Hinta".',
            criterion_name_placeholder: 'esim. API Laatu',
            score_help: 'Tästä kriteeristä myönnetty piste. Ei saa ylittää enimmäispistemäärää.',
            max_score_help: 'Parhaat pisteet tällä kriteerillä. Käytä samaa asteikkoa eri riveillä, jotta kokonaissummat ovat vertailukelpoisia.',
            notes_help: 'Valinnainen. Lisää todisteita, varoituksia tai linkkejä – mitä tahansa, mikä selittää tuloksen.',
        },
        common_labels: {
            aria_filter_instances: 'Suodata liitininstanssit',
            aria_filter_definitions: 'Suodata liitinmäärittelyt',
            aria_filter_credentials: 'Suodata tunnistetiedot',
            aria_filter_events: 'Suodata tapahtumat',
            aria_filter_dlq: 'Suodata kuolleiden kirjeiden jono',
            aria_instance_summary: 'Liitininstanssin yhteenveto',
            aria_active_connectors: 'Aktiiviset liittimet',
            aria_error_connectors: 'Virheelliset liittimet',
            aria_credential_health: 'Tunnistetietojen tilayhteenveto',
            aria_items_per_page: 'Kohteet sivua kohti',
            show_password: 'Näytä salasana',
            hide_password: 'Piilota salasana',
            payload_json_label: 'Hyötykuorma (JSON)',
            payload_invalid_json: 'Hyötykuorman on oltava kelvollinen JSON',
            placeholder_event_type: 'esim. incident.created',
            placeholder_source_module: 'esim. monitoring',
            placeholder_version: 'esim. 1.0',
        },
        statuses: {
            configuring: 'Määritetään',
            testing: 'Testaus',
            active: 'Aktiivinen',
            paused: 'Keskeytetty',
            error: 'Virhe',
            disabled: 'Ei käytössä',
            draft: 'Luonnos',
            approved: 'Hyväksytty',
            archived: 'Arkistoitu',
            pending: 'Odottaa',
            rejected: 'Hylätty',
        },
        system_types: {
            application: 'Sovellus',
            database: 'Tietokanta',
            service: 'Service',
            infrastructure: 'Infrastruktuuri',
            third_party: 'Third party',
        },
        hosting_types: {
            on_premise: 'Paikallinen',
            cloud: 'Pilvi',
            hybrid: 'Hybridi',
            managed: 'Hallittu',
        },
        system_statuses: {
            active: 'Aktiivinen',
            decommissioned: 'Poistettu käytöstä',
            planned: 'Suunniteltu',
            under_review: 'Tarkistuksessa',
        },
        automation_statuses: {
            planning: 'Suunnittelu',
            in_progress: 'Käynnissä',
            completed: 'Valmis',
            on_hold: 'Odottaa',
        },
        gap_types: {
            functional: 'Toimiva',
            technical: 'Tekninen',
            security: 'Turvallisuus',
            compliance: 'Vaatimustenmukaisuus',
            process: 'Prosessi',
        },
        severity_labels: {
            critical: 'Kriittinen',
            high: 'Korkea',
            medium: 'Keskitaso',
            low: 'Matala',
        },
        gap_statuses: {
            open: 'Avoin',
            in_progress: 'Käynnissä',
            mitigated: 'Lievennetty',
            closed: 'Suljettu',
            accepted: 'Hyväksytty',
        },
        verification_statuses: {
            not_started: 'Ei aloitettu',
            in_progress: 'Käynnissä',
            verified: 'Vahvistettu',
            failed: 'Epäonnistunut',
        },
        subscriber_types: {
            webhook: 'Webhook',
            internal: 'Sisäinen',
            email: 'Sähköposti',
            queue: 'Jono',
        },
        credential_types: {
            password: 'Salasana',
            api_key: 'API-avain',
            oauth2_client: 'OAuth2-asiakas',
            certificate: 'Sertifikaatti',
            token: 'Token',
            ssh_key: 'SSH-avain',
            imap_credentials: 'IMAP-tunnisteet',
        },
        categories: {
            hr: 'HR',
            crm: 'CRM',
            erp: 'ERP',
            cloud: 'Pilvi',
            security: 'Turvallisuus',
            itsm: 'ITSM',
            custom: 'Mukautettu',
        },
        delivery_statuses: {
            pending: 'Odottaa',
            delivered: 'Toimitettu',
            partial: 'Osittainen',
            failed: 'Epäonnistunut',
        },
        automation_planning: {
            title: 'Automaatiosuunnittelu',
            page_title: 'Automaatiosuunnittelu',
            program_name: 'Ohjelman nimi',
            program_status: 'Tila',
            description: 'Kuvaus',
            linked_artifacts: 'Linkitetyt artefaktit',
            create_program: 'Luo ohjelma',
            edit_program: 'Muokkaa ohjelmaa',
            no_programs_found: 'Ei ohjelmia',
            program_created: 'Ohjelma luotu',
            program_updated: 'Ohjelma päivitetty',
            target_date: 'Tavoitepäivä',
            owner: 'Omistaja',
            select_status: 'Valitse tila...',
            aria_programs_table: 'Automaatio-ohjelmat',
            aria_edit: 'Muokkaa {name}',
            export_programs: 'Vie ohjelmat',
            title_label: 'Ohjelman nimi',
            title_help: 'Lyhyt, ihmisen luettava nimi automaatioohjelmalle – esim. "Q3 incident-to-Jira-käyttöönotto", "PDPL pääsy-pyyntöautomaatio".',
            title_placeholder: 'esim. Q3-tapahtuma-Jiran käyttöönotto',
            description_help: 'Mitä tämä ohjelma kattaa ja mitä tuloksia odotat sen ollessa livenä. Valinnainen mutta suositeltavaa.',
            description_placeholder: 'esim. Johdinvalvonta hälyttää Jiraan, joten päivystys haetaan 60 sekunnissa.',
            owner_label: 'Ohjelman omistaja',
            owner_help: 'Tästä ohjelmasta vastuussa oleva henkilö. He saavat ilmoituksia edistymisestä ja porteista.',
            select_owner: 'Valitse omistaja...',
            frameworks_in_scope: 'Kehykset laajuudessa',
            frameworks_in_scope_help: 'Yhdenmukaisuuskehykset, joita tämä ohjelma tukee. Auttaa kirjausketjua näyttämään, mitkä säädökset automaatiota säätelevät.',
            select_frameworks: 'Valitse puitteet...',
            status_draft: 'Luonnos',
            status_active: 'Aktiivinen',
            status_completed: 'Valmis',
            status_archived: 'Arkistoitu',
            change_status: 'Muuta tilaa',
            status_updated: 'Tila päivitetty',
            aria_change_status: 'Muuta tilaa {name}',
        },
        system_inventory: {
            title: 'Järjestelmäinventaario',
            page_title: 'Järjestelmäinventaario',
            system_name: 'Järjestelmän nimi',
            system_type: 'Järjestelmätyyppi',
            hosting: 'Isännöinti',
            status: 'Tila',
            description: 'Kuvaus',
            create_system: 'Luo järjestelmä',
            edit_system: 'Muokkaa järjestelmää',
            no_systems_found: 'Ei järjestelmiä',
            system_created: 'Järjestelmä luotu',
            system_updated: 'Järjestelmä päivitetty',
            select_type: 'Valitse tyyppi...',
            select_hosting: 'Valitse hosting...',
            select_status: 'Valitse tila...',
            owner: 'Omistaja',
            department: 'Osasto',
            data_categories: 'Tietoluokat',
            data_categories_placeholder: 'Pilkuilla erotettu, esim. PII, Talous, Terveys',
            jurisdiction: 'Toimivalta',
            jurisdiction_placeholder: 'esim. EU, US, Globaali',
            interfaces: 'Liitännät',
            interfaces_placeholder: 'Pilkuilla erotettu, esim. REST API, SFTP, JDBC',
            owner_placeholder: 'Valitse vastuullinen omistaja...',
            department_placeholder: 'Valitse vastuullinen organisaatioyksikkö...',
            system_name_hint: 'Käytä liiketoiminnan tunnustamaa järjestelmän tai sovelluksen nimeä.',
            system_type_hint: 'Luokittele järjestelmä, jotta raportointi voi ryhmitellä vastaavia resursseja.',
            hosting_hint: 'Tunnista, missä järjestelmä sijaitsee asuinpaikan, turvallisuuden ja toimittaja-arvioinnin vuoksi.',
            status_hint: 'Aseta elinkaaren tila, jota arvostelut, viennit ja tarkastustodisteet käyttävät.',
            owner_hint: 'Valitse aktiivinen käyttäjä, joka vastaa järjestelmän hallinnasta ja seurantatehtävistä.',
            department_hint: 'Käytä tämän järjestelmän toiminnasta tai hallinnasta vastaavaa organisaatioyksikköä.',
            data_categories_hint: 'Luettele tämän järjestelmän käsittelemät tietoluokat pilkuilla erotettuina.',
            jurisdiction_hint: 'Tallenna tärkein laillinen tai hosting-toimivalta vaatimustenmukaisuuden kartoitusta varten.',
            interfaces_hint: 'Luettele tiedonvaihtoon käytetyt liitin- tai integraatiopolut.',
            description_hint: 'Yhteenveto siitä, mitä järjestelmä tekee ja miksi sillä on merkitystä tietokartalle.',
            owner_department_required: 'Omistaja ja osasto vaaditaan.',
            aria_systems_table: 'Järjestelmäinventaario',
            aria_edit: 'Muokkaa {name}',
            export_systems: 'Vie järjestelmät',
            aria_filter_systems: 'Filter systems',
            governance_context: 'Governance context',
            governance_context_hint: 'Confirm ownership, jurisdiction, data categories, and interfaces before saving.',
            governance_context_summary: 'This context drives data mapping, residency review, and connector accountability.',
        },
        traceability_matrix: {
            title: 'Jäljitettävyysmatriisi',
            page_title: 'Jäljitettävyysmatriisi',
            brd_selector: 'Valitse BRD',
            requirement: 'Vaatimus',
            linked_artifact: 'Linkitetty artefakti',
            verification_status: 'Todennustila',
            verified_by: 'Todennut',
            create_entry: 'Luo merkintä',
            edit_entry: 'Muokkaa merkintää',
            no_entries_found: 'Ei merkintöjä',
            entry_created: 'Merkintä luotu',
            entry_updated: 'Merkintä päivitetty',
            select_brd: 'Valitse BRD...',
            select_status: 'Valitse tila...',
            aria_matrix_table: 'Jäljitettävyysmatriisi',
            aria_edit: 'Muokkaa: {name}',
            export_matrix: 'Vie matriisi',
            brd_selector_hint: 'Select the Business Requirement Document (BRD) this entry belongs to.',
            fetch_brds_error: 'Failed to load BRD list',
            requirement_id_hint: 'Unique reference code from the BRD or regulatory source (e.g. REQ-001).',
            requirement_text: 'Requirement Text',
            requirement_text_hint: 'Detailed description of the requirement as defined in the source document.',
            verification_method: 'Verification Method',
            verification_method_hint: 'Process used to confirm the requirement is met (e.g. Unit Test, Audit).',
            verification_method_placeholder: 'e.g. test, inspection, review',
            verification_status_hint: 'Current verification state of the requirement implementation.',
            requirement_id: 'Vaatimuksen tunnus',
        },
        gap_register: {
            title: 'Puuterekisteri',
            page_title: 'Puuterekisteri',
            gap_title: 'Puutteen otsikko',
            gap_type: 'Puutetyyppi',
            severity: 'Vakavuusaste',
            status: 'Tila',
            description: 'Kuvaus',
            remediation_plan: 'Korjaussuunnitelma',
            assigned_to: 'Vastuuhenkilö',
            due_date: 'Eräpäivä',
            create_gap: 'Luo puute',
            edit_gap: 'Muokkaa puutetta',
            no_gaps_found: 'Ei puutteita',
            gap_created: 'Puute luotu',
            gap_updated: 'Puute päivitetty',
            select_type: 'Valitse tyyppi...',
            select_severity: 'Valitse vakavuusaste...',
            select_status: 'Valitse tila...',
            aria_gaps_table: 'Puuterekisteri',
            aria_edit: 'Muokkaa {name}',
            export_gaps: 'Vie puutteet',
            gap_title_hint: 'Provide a concise name for the identified gap.',
            gap_type_hint: 'Select the nature of the gap (e.g., missing coverage or integration issue).',
            severity_hint: 'The impact of this gap on compliance and operations.',
            status_hint: 'Current stage of the gap resolution lifecycle.',
            owner_hint: 'The person responsible for tracking and remediating this gap.',
            description_hint: 'Detailed context regarding the gap, its root cause, and impact.',
            mitigation_plan_hint: 'Steps planned or taken to address and close the gap.',
            linked_requirement_ids_hint: 'Comma-separated requirement codes this gap affects.',
            linked_system_ids_hint: 'Comma-separated system IDs associated with this gap.',
            gap_types: {
                coverage: 'Coverage',
                capability: 'Capability',
                integration: 'Integration',
                compliance: 'Compliance',
            },
            linked_ids_placeholder: 'Comma-separated IDs',
            linked_requirement_ids: 'Linked Requirement IDs',
            linked_system_ids: 'Linked System IDs',
            mitigation_plan: 'Mitigation Plan',
            select_owner: 'Select owner',
        },
        regulatory: {
            system_inventory_governance_info: 'System inventory aligns with Data Law enterprise governance. System types map to regulatory categories, hosting classification determines jurisdiction-specific controls. Use framework linkage to connect systems to applicable regulatory frameworks.',
            automation_planning_workflow_info: 'Automation programs align with compliance workflow obligations defined by applicable regulatory frameworks. Framework scope determines which requirements the program addresses.',
            traceability_regulatory_source_info: 'Each traceability entry connects a BRD requirement to its regulatory source. Verification status tracks whether implementation satisfies the regulatory obligation.',
            security_reviews_control_mapping_info: 'Security reviews evaluate framework-specific controls applicable to each connector. Risk level captures assessed exposure, findings document deviations from control requirements.',
            gap_register_framework_info: 'Gap Register captures compliance gaps per regulatory framework. Use framework filter to view gaps for a specific framework. Severity levels align with framework risk classification.',
            poc_scorecards_criteria_info: 'POC scorecards evaluate connectors against regulatory criteria. Criteria structure supports weighted scoring with evidence notes. Vendor linkage connects to vendor record.',
            evidence_trail_info: 'All connector module actions generate audit events for cross-module evidence trail integrity. CSV exports preserve referential integrity.',
        },
        system_inventory_extra: {
            framework_linkage: 'Kehyslinkitys',
            framework_linkage_placeholder: 'esim. GDPR, ISO 27001, NIS2',
        },
        automation_planning_extra: {
            framework_scope: 'Kehyksen laajuus',
            framework_scope_placeholder: 'esim. GDPR, PCI-DSS',
            procedure_linkage: 'Menettelylinkitys',
            procedure_linkage_placeholder: 'esim. SOP-001, PROC-DATA-003',
        },
        traceability_matrix_extra: {
            regulatory_source: 'Sääntelylähde',
            regulatory_source_placeholder: 'esim. GDPR Art. 30, ISO 27001 A.8',
        },
        security_reviews_extra: {
            control_mapping_col: 'Kontrollimappaus',
            control_mappings_section: 'Ohjauskartoitukset',
            no_control_mappings: 'Ei vielä ohjauskartoituksia. Lisää yksi napsauttamalla "Luo ohjauskartoitus".',
            automation_type_col: 'Automaatio',
        },
        gap_register_extra: {
            framework_filter: 'Kehys',
            framework_id: 'Kehystunnus',
            framework_id_placeholder: 'esim. GDPR, ISO 27001',
            all_frameworks: 'Kaikki kehykset',
        },
        poc_scorecards_extra: {
            criteria_count: 'Kriteerit',
        },
        export_labels: {
            export_brds: 'Vie BRD:t',
            export_decision_records: 'Vie päätösrekisterit',
            export_security_reviews: 'Vie tietoturvakatsaukset',
            export_poc_scorecards: 'Vie POC-tuloskortit',
            export_instances: 'Vie esiintymiä',
            export_definitions: 'Vientimääritelmät',
            export_credentials: 'Vie valtuustiedot',
            export_events: 'Vie tapahtumat',
            exporting: 'Viedään...',
            export_success: 'Vienti valmis',
            export_error: 'Vienti epäonnistui',
        },
    },
    // fix-sprint-20260506-2121-followup F16 — the contracts module's own
    // section (it was filed under vendor_risk).
    contracts: {
        obligation_extraction: {
            extract_label: 'Pura velvoitteet',
            extracting: 'Puretaan…',
            results_heading: 'Poimitut velvoitteet ({count})',
            tokens_label: '≈ {count}-merkkejä',
            no_obligations: 'Tässä sopimustekstissä ei havaittu velvoitteita.',
            col_class: 'Luokka',
            col_summary: 'Yhteenveto (muokattava)',
            col_excerpt: 'Lauseen ote',
            col_due_date: 'Eräpäivä (muokattava)',
            col_priority: 'Prioriteetti',
            due_date_placeholder: 'YYYY-MM-DD',
            class_notice: 'Irtisanomisaika',
            class_audit: 'Tarkastus oikein',
            class_insurance: 'Vakuutus',
            class_sla: 'Vastaus SLA',
            class_breach: 'Rikkomusilmoitus',
            class_renewal: 'Uusintaikkuna',
            citations_label: 'Lainaukset',
            accept_hint: 'Hyväksy luo yhden tehtävän jokaista velvoitetta kohden (Cap 8) prioriteetin + eräpäivän ollessa näkyvissä.',
            accept_label: 'Hyväksy velvoitteet',
            reject_label: 'Hylkää raportti',
            resolve_summary: 'Ratkaisu: {status} · {tasks} tehtävä(t) luotu',
            error_generic: 'Velvoitteita ei voitu poimia. Yritä uudelleen tai tarkista sopimusteksti.',
            summary_input_label: 'Summary for obligation {n}: {classification}',
            due_date_input_label: 'Due date for obligation {n}: {classification}',
            tasks_dropped: '{count} task(s) could not be created. Create them from the obligations above.',
        },
    },
    vendor_risk: {
        risk_acceptance_reasoning: {
            draft_label: 'Perusteluluonnos',
            drafting: 'Perustelua laaditaan…',
            results_heading: 'AI-muotoiltu perustelu ({risk_level})',
            tokens_label: '≈ {count}-merkkejä',
            narrative_label: 'Perustelujen selostus',
            controls_heading: 'Kompensoivat säätimet',
            no_controls: 'Kompensoivia säätöjä ei ehdotettu.',
            citations_label: 'Lainaukset',
            accept_label: 'Hyväksy perustelut',
            reject_label: 'Hylkää luonnos',
            accept_hint: 'Hyväksy tallentaa perustelut IAT-perinteen säilyneen luonnoksen perusteella. Selostus + -säätimet eivät päivitä hyväksyntietuetta automaattisesti – operaattorin liittäminen takaisin vaaditaan.',
            resolve_summary: 'Päätöslauselma: {status}',
            error_generic: 'Perustelua ei voitu laatia. Yritä uudelleen tai tarkista riskin hyväksymistila.',
        },
        redline_assistant: {
            run_label: 'Redline kirjastolla',
            running: 'Käynnissä punalinjaa…',
            results_heading: 'Redline-ehdotukset ({count})',
            tokens_label: '≈ {count}-merkkejä',
            no_suggestions: 'Standardilausekkeissa ei havaittu aukkoja.',
            jurisdiction_label: 'Toimivalta',
            col_kind: 'Ystävällinen',
            col_severity: 'Vakavuus',
            col_title: 'Ehdotus',
            col_excerpt: 'Osuva ote',
            col_apply: 'Käytä',
            kind_missing: 'Puuttuu standardi',
            kind_weaker: 'Normaalia heikompi',
            kind_jurisdiction: 'Toimivallan peittokuva',
            proposed_label: 'Ehdotettu teksti',
            citations_label: 'Lainaukset',
            accept_label: 'Hyväksy valitut',
            reject_label: 'Hylkää raportti',
            partial_label: 'Käytä valintaa',
            accept_hint: 'Käytä valintatietueita IAT-perinteen luonnoksessa valitut ehdotukset. Sopimustekstiin ei sovelleta automaattista muokkausta – operaattori ajaa punaisen linjan.',
            resolve_summary: 'Päätöslauselma: {status} · {count} ehdotuksia otettu käyttöön',
            error_generic: 'Punaviivaa ei voitu luoda. Yritä uudelleen tai tarkista sopimuksen tila.',
            clause_library_link: 'Hallinnoi lausekirjastoa',
            text_source_extracted: 'the extracted contract text',
            text_source_inline: 'the text supplied for this scan',
            text_source_summary: 'the contract description',
            scanned_text_note: 'Scanned {source} ({count} characters). The uploaded contract file is not read.',
            rescan_button: 'Scan again',
            rescan_label: 'Scan for another jurisdiction (country code)',
            jurisdiction_provided: '{code} (chosen for this scan)',
            jurisdiction_no_overlay: '{code} (no jurisdiction overlay exists for it; only your clause library was checked)',
            jurisdiction_assumed: '{code} (assumed: the contract records no jurisdiction)',
        },
        clause_library: {
            page_title: 'Lausekirjasto',
            add_label: 'Lisää lauseke',
            edit_label: 'Muokkaa',
            delete_label: 'Poista',
            slug_label: 'Slug',
            title_label: 'Otsikko',
            body_label: 'Runko',
            jurisdiction_label: 'Toimivalta',
            tags_label: 'Tunnisteet (pilkuilla erotettuna)',
            must_contain_label: 'Pakolliset merkit (pilkuilla erotettuna)',
            severity_label: 'Vakavuus puuttuessa',
            no_clauses: 'Ei lausekkeita vielä. Lisää yksi punaviiva-avustajan siementämiseksi.',
            save_label: 'Tallenna',
            cancel_label: 'Peruuta',
            load_error: 'Lausekirjastoa ei voitu ladata.',
            save_error: 'Lauseketta ei voitu tallentaa.',
            delete_error: 'Lauseketta ei voitu poistaa.',
            delete_confirm: 'Poistetaanko lause "{slug}"? Tätä ei voi kumota.',
        },
        subprocessor_impact: {
            assess_label: 'Arvioi vaikutus',
            assessing: 'Vaikutusten arviointi…',
            results_heading: 'Vaikutus aliprosessoriin ({severity})',
            tokens_label: '≈ {count}-merkkejä',
            risk_delta_label: 'Riskin delta',
            notification_heading: 'Asiakasilmoitus',
            subject_label: 'Aihe',
            deadline_label: 'Ilmoituksen määräaika',
            body_label: 'Ilmoituselin',
            required_fields_label: 'Ilmoituksen kentät (Art.28(2))',
            mitigation_heading: 'Lieventämisvaiheet',
            citations_label: 'Lainaukset',
            accept_label: 'Hyväksy arviointi',
            reject_label: 'Hylkää arviointi',
            accept_hint: 'Hyväksy luo asiakasilmoitustehtävän, jonka eräpäivänä on SLA.',
            resolve_summary: 'Päätöslauselma: {status}{task}',
            error_generic: 'Tätä aliprosessorin muutosta ei voitu arvioida. Yritä uudelleen tai tarkista muutostietue.',
        },
        onboarding_evidence: {
            collect_label: 'Suorita AI todisteiden kerääminen',
            collecting: 'Todisteiden kerääminen…',
            results_heading: 'Todisteiden kerääminen ({tier})',
            tokens_label: '≈ {count}-merkkejä',
            findings_heading: 'Olemassa olevat todisteet',
            missing_heading: 'Puuttuvat esineet',
            no_findings: 'Olemassa olevaa näyttöä ei havaittu.',
            no_missing: 'Tästä riskitasosta ei puutu vaadittuja esineitä.',
            next_step_label: 'Seuraava askel',
            class_dpa: 'Tietojenkäsittelysopimus',
            class_soc2: 'SOC 2 -raportti',
            class_iso27001: 'ISO 27001 sertifikaatti',
            class_insurance: 'Kybervakuutus',
            class_incident_history: 'Aikaisempi tapaus',
            accept_hint: 'Accepting creates a task for each missing artefact (up to 5) and records each document found as draft evidence on the vendor. Draft evidence stays unapproved until a person with evidence approval rights reviews it. Prior incidents are listed for your review only.',
            accept_label: 'Hyväksy raportti',
            reject_label: 'Hylkää raportti',
            resolve_summary: 'Resolution: {status} · {tasks} task(s) created · {evidence} draft evidence record(s) attached',
            error_generic: 'AI todisteiden keräämistä ei voitu suorittaa. Yritä uudelleen tai tarkista aloitustapauksen tila.',
        },
        no_permission_title: 'Riittämättömät käyttöoikeudet',
        no_permission_message: 'Sinulla ei ole lupaa käyttää toimittajan riskienhallintaa.',
        unknown_vendor: 'Tuntematon myyjä',
        nav: {
            dashboard: 'Riskikojelauta',
            scoring: 'Riskipisteytys',
            questionnaires: 'Kyselylomakkeet',
            contracts: 'Sopimukset',
            gates: 'Hyväksyntäportit',
            reviews: 'Toimittaja-arvioinnit',
            signals: 'Riskisignaalit',
            reassessments: 'Uudelleenarvioinnit',
            portfolio: 'Riskiportfolio',
            acceptances: 'Riskien hyväksyntä',
            review_routing: 'Tarkistuksen reititys',
            onboarding_cases: 'Käyttöönottotapaukset',
            sub_processor_register: 'Alikäsittelijärekisteri',
        },
        dashboard: {
            title: 'Toimittajariskikojelauta',
            page_title: 'Myyjän riskien hallintapaneeli',
            loading: 'Ladataan kojelauta tiedot...',
            risk_tier_header: 'Riskitaso',
            count_header: 'Count',
            percentage_header: 'Prosenttiosuus',
            sections_nav: 'Myyjän riskiosat',
            urgent_expiry: 'Kiireellinen: vanhenee 14 päivän kuluessa',
            no_previous_tier: 'Ei aiempaa tasoa',
            view_as_chart: 'Näytä kaaviona',
            view_as_table: 'Näytä taulukkona',
            showing_chart: 'Näytetään kaavionäkymä',
            showing_table: 'Näytetään taulukonäkymä',
            load_error: 'Epäonnistui: load toimittaja riski kojelauta. Yritä uudelleen.',
            risk_distribution: 'Riski Distribution Yleiskatsaus',
            recent_tier_changes: 'Viimeaikainen Taso Changes',
            no_tier_changes: 'Ei viimeaikaisia tason muutoksia.',
            review_kpis: 'Katselmointia KPI',
            total_vendors_reviewed: 'Tarkastetut toimittajat yhteensä',
            reviews_by_outcome: 'Katselmoinnit by Outcome',
            contract_coverage: 'Sopimus Kattavuus',
            total_vendors: 'Toimittajat yhteensä',
            with_active_dpa: 'Aktiivisella DPA:lla',
            without_dpa: 'Ilman DPA:ta',
            dpa_coverage: 'DPA Kattavuus',
            active_contracts_by_type: 'Aktiiviset sopimukset tyypeittäin',
            expiring_contracts: 'Expiring Sopimukset (Seuraava 90 päivää)',
            no_expiring_contracts: 'No contracts expiring within the next 90 päivää.',
            vendor: 'Toimittaja',
            previous_tier: 'Edellinen Taso',
            current_tier: 'Nykyinen Taso',
            changed_at: 'Muutettu',
            expires_at: 'Vanhenee',
            days_remaining: 'päivää jäljellä',
            days: 'päivää',
            vendors_label: 'toimittajat',
            no_data: 'No tiedot',
            critical: 'Kriittinen',
            high: 'Korkea',
            medium: 'Keskitaso',
            low: 'Matala',
            expiring_acceptances_heading: 'Vanhenevat riskihyväksynnät',
            expiring_30d_label: 'Vanhenee 30 päivässä',
            expiring_60d_label: 'Vanhenee 60 päivässä',
            expiring_90d_label: 'Vanhenee 90 päivässä',
        },
        scoring: {
            title: 'Riskipisteytys',
            page_title: 'Myyjän riskipisteytys',
            filter_scores: 'Suodata riskipisteet',
            filter_by_tier: 'Suodata riskitason mukaan',
            scores_pagination: 'Riskipisteiden sivutus',
            config_dimensions: 'Scoring dimensions configuration',
            scores_loaded: '{count} risk scores loaded',
            weight_must_equal_100: 'Kokonaispainon on oltava 100 (nykyinen: {current})',
            out_of_10: 'out of 10, rated {tier}',
            dimension_name: 'Dimension {index} name',
            dimension_weight: '{name} weight (percentage)',
            must_equal_100: 'pitää olla 100',
            config_title: 'Scoring Konfiguraatio',
            scores_title: 'Toimittajien riskipistemäärät',
            new_score_title: 'Pistemäärä a Toimittaja',
            new: 'Uusi Pistemäärä',
            vendor_id: 'Toimittajatunnus',
            risk_tier: 'Riski Taso',
            score: 'Pistemäärä',
            scored_at: 'Pisteytetty',
            no_scores: 'Ei riski pistemäärä found. löytynyt',
            all_tiers: 'Kaikki tasot',
            load_config_error: 'Epäonnistui: load riski scoring konfiguraatio. Yritä uudelleen.',
            update_config: 'Päivitä pisteytyskonfiguraatio',
            update_config_error: 'Epäonnistui: update riski scoring konfiguraatio. Yritä uudelleen.',
            load_scores_error: 'Epäonnistui: load toimittaja riski pistemäärä. Yritä uudelleen.',
            calculate_error: 'Epäonnistui: calculate toimittaja riski pistemäärä. Yritä uudelleen.',
            weight: 'weight',
            score_range: 'Pistemäärä (1-5)',
            method: 'Menetelmä',
        },
        questionnaires: {
            title: 'Kyselylomakkeet',
            page_title: 'Myyjän kyselylomakkeet',
            filter_questionnaires: 'Kyselylomakkeen suodattimet',
            questionnaire_views: 'Kyselylomakkeen näkymät',
            templates_list: 'Kyselypohjien luettelo',
            instances_list: 'Kyselyn esiintymien luettelo',
            filter_by_status: 'Suodata kyselylomakkeen tilan mukaan',
            templates_per_page: 'Malleja per sivu',
            instances_per_page: 'Tapauksia sivua kohden',
            select_template: 'Valitse kyselylomake',
            template_category: 'Mallin luokka',
            templates_tab: 'Mallit',
            instances_tab: 'Esiintymät',
            new: 'Uusi Kysely',
            new_template: 'Uusi malli',
            new_instance: 'Uusi ilmentymä',
            vendor_id: 'Toimittajatunnus',
            template_id: 'Malli Tunniste',
            assigned_to: 'Määritetty To',
            due_date: 'Due Päivämäärä',
            responses: 'Vastaukset',
            risk_score: 'Valmistuminen',
            no_questionnaires: 'Ei questionnaires found. löytynyt',
            load_templates_error: 'Epäonnistui: load questionnaire mallit. Yritä uudelleen.',
            load_instances_error: 'Epäonnistui: load questionnaires. Yritä uudelleen.',
            create_template_error: 'Epäonnistui: create questionnaire malli. Yritä uudelleen.',
            initiate_error: 'Epäonnistui: initiate questionnaire. Yritä uudelleen.',
            submit_error: 'Epäonnistui: submit questionnaire. Yritä uudelleen.',
            review_error: 'Epäonnistui: katselmointi questionnaire. Yritä uudelleen.',
            resubmit: 'Lähetä uudelleen',
            approve_error: 'Kyselylomakkeen hyväksyminen epäonnistui',
            reject_error: 'Kyselylomakkeen hylkääminen epäonnistui',
            resubmit_error: 'Kyselylomakkeen uudelleenlähetys epäonnistui',
            templates_loaded: '{count} kyselylomakemallia ladattu',
            instances_loaded: '{count} kyselylomaketta ladattu',
            fill_responses_cta: 'Täytä vastaukset',
            fill_responses_title: 'Täytä kyselyn vastaukset',
            template_missing: 'Mallia ei löydy.',
            responses_saved: 'Vastaukset tallennettu.',
            load_template_error: 'Mallin lataus epäonnistui.',
            version_history: 'Versiohistoria',
            download_previous_version: 'Lataa aiempi versio',
            trigger: 'Liipaisin',
            template_label: 'Malli',
            reviewer: 'Arvostelija',
            reviewer_required: '{trigger}-kyselylomakkeille on määrättävä arvioija (tehtävien erottaminen).',
            select_option: 'Valitse vaihtoehto',
            multi_choice_hint: 'Valitse kaikki sopivat',
            category_initial_onboarding: 'Alkuperäinen käyttöönotto',
            category_annual_review: 'Vuosikatsaus',
            category_incident_triggered: 'Tapahtuman aiheuttama',
            category_contract_renewal: 'Sopimuksen uusiminen',
            template_editor: {
                page_title: 'Muokkaa kyselylomakemallia',
                preview_page_title: 'Esikatsele kyselylomakemallia',
                edit_structure: 'Muokkaa rakennetta',
                preview: 'Esikatselu',
                actions: 'Toiminnot',
                back_to_list: 'Takaisin malleihin',
                load_error: 'Mallin lataaminen epäonnistui',
                save_error: 'Mallin rakenteen tallentaminen epäonnistui',
                saved: 'Mallin rakenne tallennettu.',
                sections_heading: 'Osat',
                questions_heading: 'Kysymyksiä',
                add_section: 'Lisää osio',
                edit_section: 'Muokkaa osiota',
                delete_section: 'Poista osio',
                move_section_up: 'Siirrä osio ylöspäin',
                move_section_down: 'Siirrä osio alas',
                confirm_delete_section: 'Poistetaanko osio "{title}"? Kaikki sen kysymykset poistetaan.',
                section_title: 'Osion otsikko',
                section_description: 'Osion kuvaus',
                section_id: 'Osion tunnus',
                section_id_hint: 'Luodaan automaattisesti, ellet määritä sitä erikseen.',
                no_sections: 'Ei osioita vielä. Aloita lisäämällä osio.',
                no_questions: 'Tässä osiossa ei ole vielä kysymyksiä.',
                add_question: 'Lisää kysymys',
                edit_question: 'Muokkaa kysymystä',
                delete_question: 'Poista kysymys',
                move_question_up: 'Siirrä kysymys ylös',
                move_question_down: 'Siirrä kysymys alas',
                confirm_delete_question: 'Poistetaanko kysymys "{text}"?',
                question_text: 'Kysymysteksti',
                question_type: 'Kysymystyyppi',
                question_id: 'Kysymystunnus',
                evidence_required: 'Todisteet vaaditaan',
                risk_weight: 'Riskipaino',
                scoring_map: 'Pisteytyskartta',
                scoring_map_json_hint: 'JSON oliokartoituksen vastausarvot numeerisiksi arvoiksi.',
                scoring_map_invalid_json: 'Pisteytyskartan on oltava voimassa JSON.',
                options_heading: 'Vastausvaihtoehdot',
                add_option: 'Lisää vaihtoehto',
                remove_option: 'Poista vaihtoehto',
                option_label: 'Label',
                option_value: 'Arvo',
                option_risk_weight: 'Riskipaino',
                min_options_error: 'Valintakysymyksissä on oltava vähintään 2 vaihtoehtoa.',
                option_label_required: 'Jokainen vaihtoehto tarvitsee etiketin.',
                option_value_required: 'Jokainen vaihtoehto tarvitsee arvon.',
                option_value_unique: 'Vaihtoehtojen arvojen on oltava yksilöllisiä.',
                branching_rules_heading: 'Haaroittamisen säännöt',
                branching_rules_hint: 'Näytä tai piilota tämä kysymys aikaisemman kysymyksen vastauksen perusteella.',
                add_branching_rule: 'Lisää haarautumissääntö',
                remove_branching_rule: 'Poista haarautumissääntö',
                branching_condition_question: 'Jos kysymys',
                branching_condition_value: 'on arvoa',
                branching_action: 'sitten',
                branching_action_show: 'näytä tämä kysymys',
                branching_action_hide: 'piilota tämä kysymys',
                branching_select_condition_question: 'Valitse aikaisempi kysymys',
                branching_select_condition_value: 'Valitse arvo',
                save: 'Tallenna rakenne',
                saving: 'Tallennetaan...',
                cancel: 'Peruuta',
                close: 'Sulje',
                question_text_required: 'Kysymysteksti on pakollinen.',
                section_title_required: 'Osion otsikko vaaditaan.',
                type_single_choice: 'Yksittäinen valinta',
                type_multi_choice: 'Monivalinta',
                type_free_text: 'Vapaa teksti',
                type_numeric: 'Numeerinen',
                type_date: 'Päivämäärä',
                type_file_upload: 'Tiedoston lataus',
                type_yes_no: 'Kyllä / Ei',
                preview_heading: 'Mallin esikatselu',
                preview_subtitle: 'Vain luku -esikatselu siitä, miltä kyselylomake näyttää toimittajalle.',
                preview_no_content: 'Tässä mallissa ei ole vielä osioita tai kysymyksiä.',
                preview_branching_notice: 'Haaroittumissäännöt arvioidaan täytön aikana; esikatselu näyttää jokaisen kysymyksen.',
            },
            review_modal: {
                title: 'Tarkista kyselylomakkeen vastaukset',
                open_cta: 'Tarkista',
                loading: 'Ladataan kyselylomaketta...',
                template_missing: 'Mallia ei löydy.',
                load_error: 'Kyselylomakkeen lataaminen tarkistettavaksi epäonnistui.',
                annotation_heading: 'Huomautukset',
                annotation_placeholder: 'Lisää tähän kysymykseen arvioijan huomautus...',
                annotation_status_label: 'Huomautuksen tila',
                annotation_status_acceptable: 'Hyväksyttävä',
                annotation_status_concern: 'Huoli',
                annotation_status_requires_action: 'Vaatii toimia',
                review_comments_label: 'Yleisarvostelun kommentit',
                review_comments_placeholder: 'Tee yhteenveto arvosteluhavainnoistasi...',
                justification_label: 'Päätöksen perustelut',
                justification_placeholder: 'Perustele päätöksesi perustelut...',
                reject_requires_comments: 'Kyselylomakkeen hylkääminen edellyttää tarkistuskommentteja.',
                decision_heading: 'Päätös',
                decision: {
                    approved: 'Hyväksy',
                    rejected: 'Hylkää',
                    additional_info_requested: 'Pyydä lisätietoja',
                },
                submit: 'Lähetä arvostelu',
                submitting: 'Lähetetään arvostelua...',
                cancel: 'Peruuta',
                submit_success: 'Arvostelu lähetetty.',
                submit_error: 'Arvostelun lähettäminen epäonnistui.',
                separation_of_duties_error: 'Et voi tarkistaa kyselyä, joka sinulle on määrätty täyttämään. Tehtävien erottaminen edellyttää eri arvioijaa.',
                decision_required: 'Valitse tarkistuspäätös.',
                announce_opened: 'Tarkistusmodaali avattu.',
                announce_submitted: 'Arvostelu lähetetty.',
            },
            instance_detail: {
                submitted_at: 'Lähetetty',
                reviewed_at: 'Arvosteltu',
                reviewer: 'Arvostelija',
                review_decision: 'Päätös',
                review_comments: 'Tarkista kommentit',
                decision_approved: 'Hyväksytty',
                decision_rejected: 'Hylätty',
                decision_additional_info_requested: 'Lisätietoja pyydetään',
                decision_pending: 'Odottaa tarkistusta',
                expand_comments: 'Näytä täydelliset kommentit',
                collapse_comments: 'Piilota täydelliset kommentit',
                no_reviewer: 'Arvostelijaa ei ole määrätty',
                earlier_reviews: 'Earlier reviews',
                earlier_review_entry: '{decision} by {name} on {date}.',
                earlier_review_comments: 'Comments: {comments}',
                earlier_review_question_note: 'Note on {question}: {comments}',
                earlier_review_note: 'Question note: {comments}',
                earlier_review_unknown_person: 'an unrecorded reviewer',
                earlier_review_unknown_date: 'an unrecorded date',
                // Sprint 62.58.0 gate E12.5 pass 1 (P3-4) — the instance export's line
                // for one earlier review: its number, then the page's own sentence
                // (`earlier_review_entry`), so the two never word a review two ways.
                earlier_review_export_line: 'Earlier review {index}: {entry}',
                earlier_review_no_decision: 'No decision recorded',
            },
            instance_statuses: {
                draft: 'Luonnos',
                in_progress: 'Käynnissä',
                submitted: 'Submitted',
                under_review: 'Under review',
                approved: 'Hyväksytty',
                rejected: 'Hylätty',
                expired: 'Vanhentunut',
            },
            export: {
                portfolio_label: 'Vie portfolio',
                instance_label: 'Vie',
                portfolio_success: 'Portfolion vienti valmis.',
                instance_success: 'Kyselylomakkeen vienti valmis.',
                portfolio_error: 'Kyselylomakesalkun vienti epäonnistui.',
                instance_error: 'Kyselylomakkeen vienti epäonnistui.',
                header_line: '{label}: {value}',
                default_title: 'Vendor questionnaire',
                unassigned: 'Unassigned',
                risk_score_none: 'Not scored',
                column_question: 'Question',
                column_answer: 'Answer',
                column_evidence: 'Evidence',
                column_annotation: 'Reviewer annotation',
                section_no_questions: 'No questions in this section.',
                portfolio_title: 'Vendor questionnaires portfolio',
                portfolio_generated: 'Generated',
                portfolio_rows: 'Rows',
                portfolio_status_filter: 'Status filter',
                portfolio_vendor_filter: 'Vendor filter',
                portfolio_assignee_filter: 'Assignee filter',
            },
            branching: {
                hidden_notice: 'Osa kysymyksistä on piilotettu haarautumissäännöillä.',
                cycle_warning: 'Haaroitussäännöissä havaittiin sykli. Vaikuttavat kysymykset ovat oletuksena näkyvissä.',
            },
        },
        contracts: {
            edit_error: 'Failed to update contract',
            edit_success: 'Contract updated',
            edit_contract: 'Edit contract details',
            record_signature: 'Record signature',
            signature_section: 'Signatures on this contract',
            signature_none: 'No signature has been recorded for this contract.',
            signatory_name: 'Signatory name',
            signatory_title: 'Signatory title',
            signatory_party: 'Signing party',
            signatory_party_organization: 'Our organisation',
            signatory_party_counterparty: 'Counterparty',
            signed_on: 'Date signed',
            signature_evidence: 'Evidence reference',
            signature_evidence_none: 'No evidence reference is recorded on this contract yet. Attach the signed document below instead.',
            signature_evidence_attachment: 'Signed document',
            signature_evidence_attachment_hint: 'Upload the signed document when there is no evidence reference to select.',
            signature_evidence_hint: 'A contract enters into force on a recorded signature, so a signature needs evidence: reference evidence already on this contract, or attach the signed document.',
            signature_saved: 'Signature recorded',
            signature_error: 'Failed to record the signature',
            title: 'Sopimukset',
            page_title: 'Myyjäsopimukset',
            new: 'Uusi Sopimus',
            vendor_id: 'Toimittajatunnus',
            filter_contracts: 'Suodata sopimukset',
            filter_by_document_type: 'Suodata asiakirjatyypin mukaan',
            filter_by_status: 'Suodata sopimuksen tilan mukaan',
            per_page: 'Sopimukset per sivu',
            expired_notice: 'Tämä sopimus on päättynyt',
            transition_reason: 'Reason for this change',
            transition_reason_required: 'A reason is required to take this contract out of force.',
            transition_reason_placeholder: 'Why is this contract changing status?',
            transition_none_available: 'This status is final. A successor contract is a new record.',
            document_type: 'Asiakirjatyyppi',
            expiry_date: 'Expiry Päivämäärä',
            no_contracts: 'Ei contracts found. löytynyt',
            all_document_types: 'Kaikki asiakirjatyypit',
            load_error: 'Epäonnistui: load contracts. Yritä uudelleen.',
            create_error: 'Epäonnistui: create contract. Yritä uudelleen.',
            delete_error: 'Epäonnistui: delete contract. Yritä uudelleen.',
            update_error: 'Epäonnistui: update tila. Yritä uudelleen.',
            confirm_delete: 'Poista contract "{title}"?',
            renewal_date: 'Uusiminen Päivämäärä',
            effective_date: 'Effective Päivämäärä',
            parties: 'Osapuolet',
            summary: 'Yhteenveto',
            n_contracts: '{count} sopimusta',
            n_contract: '{count} sopimus',
            contracts_loaded: '{count} sopimusta ladattu',
            doc_type_dpa: 'DPA',
            doc_type_security_addendum: 'Turvallisuusliite',
            doc_type_scc_annex: 'SCC-liite',
            doc_type_sub_processor_list: 'Alikäsittelijäluettelo',
            doc_type_bcr: 'BCR',
            doc_type_nda: 'NDA',
            doc_type_msa: 'MSA',
            doc_type_sla: 'SLA',
            doc_type_other: 'Muu',
            status_draft: 'Luonnos',
            status_under_negotiation: 'Neuvottelussa',
            status_pending_signature: 'Odottaa allekirjoitusta',
            status_active: 'Aktiivinen',
            status_expired: 'Vanhentunut',
            status_terminated: 'Lopetettu',
            status_superseded: 'Korvattu',
            contract_file: 'File',
            upload_contract_file: 'Contract File',
            auto_renew: 'Renews automatically',
            auto_renew_hint: 'On the expiry date the contract renews for one more term of the same length, recorded in its history. It does not renew if its effective date or renewal notice period is missing, or if it is not active.',
            renewal_period_days: 'Renewal notice (days)',
            renewal_period_days_hint: 'A renewal reminder task is raised this many days before the expiry date.',
            clauses_heading: 'Clauses',
            clause_title: 'Clause title',
            clause_title_required: 'A clause with text needs a title.',
            clause_content: 'Clause text',
            clause_mandatory: 'Mandatory clause',
            add_clause: 'Add clause',
            remove_clause: 'Remove clause',
            contract_file_save_first: 'Save the contract first. Its file can be attached once the contract exists.',
            expiry_date_keep_hint: 'An expiry date, once set, can be changed but not removed.',
            renewal_history: 'Renewal history',
            renewal_history_note: 'Each row is an automatic renewal the platform made because the contract renews automatically: the expiry date moved by one term.',
            renewal_col_previous_expiry: 'Previous expiry',
            renewal_col_new_expiry: 'New expiry',
            renewal_col_term: 'Term',
            renewal_col_renewed_on: 'Renewed on',
            renewal_term_months: '{count} month(s)',
            renewal_term_days: '{count} day(s)',
            clause_field_label: 'Clause {n}, {field}',
            field_other: 'Another field',
        },
        gates: {
            title: 'Hyväksyntäportit',
            page_title: 'Portin arvioinnit',
            new: 'Uusi Portti Evaluation',
            vendor_id: 'Toimittajatunnus',
            gate_name: 'Portti Nimi',
            outcome: 'Tulos',
            evaluator: 'Arvioija',
            no_gates: 'Ei gate evaluations found. löytynyt',
            load_error: 'Epäonnistui: load gate evaluations. Yritä uudelleen.',
            evaluate_error: 'Epäonnistui: evaluate toimittaja gate. Yritä uudelleen.',
            detail_error: 'Epäonnistui: fetch evaluation detail. Yritä uudelleen.',
            override_error: 'Epäonnistui: override gate evaluation. Yritä uudelleen.',
            criteria_results: 'Kriteerit Tulokset',
            evaluate_form: 'Arvioi myyjäportti',
            per_page: 'Arviot per sivu',
            passed: 'Hyväksytty',
            failed: 'Epäonnistunut',
            override: 'Ohita',
            override_justification: 'Ohita Perustelu',
            override_placeholder: 'Syötä perustelu for overriding this gate evaluation',
            approval_id: 'Hyväksyntätunnus',
            overall_passed: 'Hyväksytty',
            overall_failed: 'Hylätty',
            overall_overridden: 'Ohitettu',
            evaluations_loaded: '{count} porttiarviointia ladattu',
            create_approval_cta: 'Luo hyväksyntä',
        },
        reviews: {
            title: 'Toimittaja-arvioinnit',
            page_title: 'Myyjien arvostelut',
            new: 'Uusi Katselmointia',
            vendor_id: 'Toimittajatunnus',
            filter_by_outcome: 'Suodata arvostelun tuloksen mukaan',
            per_page: 'Arvostelut per sivu',
            kpi_summary: 'Tarkista KPI yhteenveto',
            review_outcome_label: 'Tarkista tulos',
            program_unresolved: 'Program {id} (name not available)',
            programs_unavailable: 'Programs could not be listed. Enter the program ID.',
            edit_outcome: 'Muokkaa arvostelun tulosta',
            reviews_loaded: '{count} toimittajaarvostelut ladattu',
            reviewer: 'Arvioija',
            review_type: 'Katselmointia Tyyppi',
            outcome: 'Tulos',
            findings: 'Havainnot',
            no_reviews: 'Ei toimittaja katselmointi found. löytynyt',
            load_kpis_error: 'Epäonnistui: fetch toimittaja katselmointi KPI. Yritä uudelleen.',
            load_error: 'Epäonnistui: fetch toimittaja katselmointi. Yritä uudelleen.',
            update_error: 'Toimittaja-arvion päivittäminen epäonnistui',
            create_error: 'Epäonnistui: create toimittaja katselmointi. Yritä uudelleen.',
            total_reviewed: 'Tarkastetut toimittajat yhteensä',
            total_reviews: 'Katselmoinnit yhteensä',
            satisfactory: 'Tyydyttävä',
            concerns_identified: 'Havaitut huolenaiheet',
            remediation_required: 'Korjaus Pakollinen',
            escalated: 'Eskaloitu',
            program_id: 'Ohjelma Tunniste',
            period_id: 'Kausi Tunniste',
            task_id: 'Tehtävä Tunniste',
            recommendations: 'Suositukset',
            reviewed_at: 'Reviewed At',
        },
        risk_signals: {
            title: 'Riskisignaalit',
            page_title: 'Riskisignaalit',
            new_signal: 'Uusi riskisignaali',
            signal_type: 'Signaalityyppi',
            severity: 'Vakavuus',
            description: 'Kuvaus',
            source: 'Lähde',
            vendor_id: 'Toimittajan tunnus',
            certification_expiry: 'Sertifioinnin päättyminen',
            breach_notification: 'Tietoturvaloukkauilmoitus',
            financial_instability: 'Taloudellinen epävakaus',
            compliance_change: 'Vaatimustenmukaisuuden muutos',
            custom: 'Mukautettu',
            active: 'Aktiivinen',
            acknowledged: 'Kuitattu',
            resolved: 'Ratkaistu',
            no_signals: 'Riskisignaaleja ei löytynyt',
            source_external_feed: 'Ulkoinen syöte',
            source_manual: 'Manuaalinen',
            source_integration: 'Integraatio',
            source_regulatory: 'Sääntelyyn liittyvä',
            source_media: 'Media',
            trigger_reassessment_prompt: 'Tämä signaali on vakavuudeltaan korkea. Käynnistä toimittajan uudelleenarviointi nyt?',
            reassessment_triggered: 'Uudelleenarviointi käynnistetty.',
            reassessment_trigger_error: 'Uudelleenarvioinnin käynnistys epäonnistui.',
        },
        reassessments: {
            title: 'Uudelleenarvioinnit',
            page_title: 'Uudelleenarviointitehtävät',
            trigger: 'Käynnistä uudelleenarviointi',
            trigger_success: 'Uudelleenarviointi käynnistetty onnistuneesti',
            reason: 'Syy',
            deadline: 'Määräaika',
            assigned_to: 'Vastuuhenkilö',
            vendor_id: 'Toimittajan tunnus',
            outcome: 'Tulos',
            new_risk_level: 'Uusi riskitaso',
            deadline_hint: 'The deadline is the end of the chosen day (UTC).',
            reason_required: 'Enter a reason.',
            vendor_required: 'Choose a vendor.',
            already_completed: 'This reassessment has already been completed.',
            complete: 'Suorita uudelleenarviointi',
            complete_success: 'Uudelleenarviointi suoritettu onnistuneesti',
            pending: 'Odottaa',
            in_progress: 'Käynnissä',
            completed: 'Valmis',
            cancelled: 'Peruutettu',
            no_tasks: 'Uudelleenarviointitehtäviä ei löytynyt',
            risk_trend: 'Riskitrendi',
            notes: 'Muistiinpanot',
        },
        portfolio: {
            title: 'Riskiportfolio',
            page_title: 'Riskiportfolio',
            dashboard: 'Portfolion yhteenveto',
            vendor_id: 'Toimittajan tunnus',
            total_vendors: 'Toimittajia yhteensä',
            average_score: 'Keskimääräinen riskipistemäärä',
            high_risk_count: 'Korkean riskin toimittajat',
            trend_direction: 'Trendin suunta',
            improved: 'Parantunut',
            stable: 'Vakaa',
            degraded: 'Heikentynyt',
            vendor_trend: 'Toimittajatrendi',
            outstanding_questionnaires: 'Avoimet kyselylomakkeet',
            overdue_assessments: 'Myöhässä olevat arvioinnit',
            no_outstanding: 'Ei avoimia kyselylomakkeita',
            no_overdue: 'Ei myöhässä olevia arviointeja',
            due_date: 'Eräpäivä',
        },
        risk_acceptance: {
            expiring_within: 'Expiring within {days} days',
            title: 'Riskien hyväksyntärekisteri',
            page_title: 'Riskien hyväksymisrekisteri',
            new_acceptance: 'Uusi riskien hyväksyntä',
            vendor_id: 'Toimittajan tunnus',
            risk_level: 'Riskitaso',
            conditions: 'Ehdot',
            justification: 'Perustelu',
            expiry_date: 'Päättymispäivä',
            create_success: 'Riskien hyväksyntä luotu onnistuneesti',
            review_condition: 'Tarkista ehto',
            condition_met: 'Ehto täytetty',
            condition_not_met: 'Ehto ei täytetty',
            review_success: 'Ehto tarkistettu onnistuneesti',
            export_register: 'Vie rekisteri',
            active: 'Aktiivinen',
            expired: 'Vanhentunut',
            revoked: 'Peruutettu',
            no_acceptances: 'Riskien hyväksyntöjä ei löytynyt',
            met_count: '{met}/{total} tapasi',
        },
        review_routing: {
            title: 'Tarkistuksen reititys',
            page_title: 'Tarkista reititys',
            route_to_reviewers: 'Lähetä tarkistajille',
            route_success: 'Lähetetty tarkistajille onnistuneesti',
            vendor_id: 'Toimittajan tunnus',
            reviewer_id: 'Tarkistajan tunniste',
            reviewer_role: 'Tarkistajan rooli',
            questionnaire_id: 'Kyselylomakkeen tunniste',
            review_status: 'Tarkistuksen tila',
            reviewers_label: 'Arvostelijat (user_id, rooli riviä kohden)',
            pending: 'Odottaa',
            in_review: 'Tarkistuksessa',
            completed: 'Valmis',
            no_routing: 'Tarkistuksen reititystä ei löytynyt',
            primary_reviewer: 'Ensisijainen tarkistaja',
            secondary_reviewer: 'Toissijainen tarkistaja',
            approver: 'Hyväksyjä',
        },
        onboarding: {
            title: 'Toimittajien käyttöönottotapaukset',
            page_title: 'Toimittajien käyttöönottotapaukset',
            new_case: 'Uusi käyttöönottotapaus',
            case_detail: 'Käyttöönottotapaus',
            open_mps_dossier_filing: 'Avaa MPS-ilmoitus',
            dossier_filing_description: 'Luo uusi MPS-ilmoitus, esitäytetty tästä onboarding-tapauksesta. Ilmoitus linkitetään tapaukseen.',
            dossier_filing_dpia_id: 'DPIA-tunnus',
            dossier_filing_dpia_id_placeholder: 'esim. dpia_01TEST000000000000000001',
            dossier_filing_title: 'Ilmoituksen otsikko',
            dossier_filing_form_type: 'Lomaketyyppi',
            dossier_filing_submit: 'Luo ilmoitus',
            dossier_filing_validation_error: 'DPIA-tunnus ja otsikko vaaditaan.',
            dossier_filing_created: 'MPS-ilmoitus luotu',
            dossier_filing_error: 'MPS-ilmoitusta ei voitu luoda.',
            linked_questionnaire_due_date: 'Eräpäivä',
            linked_questionnaire_assignee: 'Vastuuhenkilö',
            linked_questionnaire_submitted_at: 'Lähetetty',
            score_last_scored: 'Viimeksi pisteytetty',
            score_dimensions: 'Ulottuvuudet',
            linked_contract_document_type: 'Asiakirjatyyppi',
            linked_contract_effective_date: 'Voimaantulopäivä',
            linked_contract_expiry_date: 'Päättymispäivä',
            linked_sub_processor_countries: 'Käsittelymaat',
            linked_sub_processor_last_assessment: 'Viimeisin arviointi',
            linked_sub_processor_next_assessment: 'Seuraava arviointi',
            case_id: 'Tapaus-ID',
            vendor: 'Toimittaja',
            case_type: 'Tapaustyyppi',
            service_description: 'Palvelun kuvaus',
            data_categories: 'Tietokategoriat',
            systems_accessed: 'Käytetyt järjestelmät',
            jurisdiction: 'Lainkäyttöalue',
            hosting_model: 'Hosting-malli',
            controller_role: 'Rekisterinpitäjän/käsittelijän rooli',
            status_pipeline: 'Tilaputki',
            tabs_label: 'Käyttöönottotapauksen osiot',
            per_page: 'Tapauksia sivulla',
            filter_cases: 'Suodata käyttöönottotapauksia',
            cases_count: 'tapausta',
            cases_loaded: 'käyttöönottotapausta ladattu',
            no_cases: 'Käyttöönottotapauksia ei löytynyt.',
            vendor_lookup_placeholder: 'Syötä toimittajan ID',
            data_categories_placeholder: 'esim. henkilötiedot, talous, terveys',
            systems_placeholder: 'esim. CRM, ERP, HR-järjestelmä',
            status_open: 'Avoin',
            status_in_progress: 'Käynnissä',
            status_awaiting_approval: 'Odottaa hyväksyntää',
            status_approved: 'Hyväksytty',
            status_remediation_required: 'Korjaus vaaditaan',
            status_closed: 'Suljettu',
            type_new_vendor: 'Uusi toimittaja',
            type_service_change: 'Palvelumuutos',
            type_renewal: 'Uusinta',
            type_reassessment: 'Uudelleenarviointi',
            tab_overview: 'Yleiskatsaus',
            tab_due_diligence: 'Huolellisuus',
            tab_security_assessment: 'Turvallisuusarviointi',
            tab_contractual_controls: 'Sopimusvalvonta',
            tab_sub_processors: 'Alikäsittelijät',
            tab_approval_gates: 'Hyväksyntäportit',
            tab_evidence: 'Todisteet',
            tab_placeholder: 'Tämän osion sisältö täytetään käyttöönottoprosessin aikana.',
            completion_not_started: 'Ei aloitettu',
            completion_in_progress: 'Käynnissä',
            completion_completed: 'Valmis',
            completion_requiring_attention: 'Vaatii huomiota',
            gate_name: 'Portin nimi',
            approver: 'Hyväksyjä',
            decision: 'Päätös',
            comments: 'Kommentit',
            decide: 'Tee päätös',
            decision_approved: 'Hyväksytty',
            decision_rejected: 'Hylätty',
            decision_deferred: 'Lykätty',
            decision_pending: 'Pending',
            no_approvals: 'Tälle tapaukselle ei ole konfiguroitu hyväksyntäportteja.',
            approval_success: 'Hyväksyntäpäätös kirjattu onnistuneesti',
            approval_error: 'Hyväksyntäpäätöstä ei voitu kirjata',
            transition_status: 'Vaihda tilaa',
            confirm_transition: 'Vaihda tapaus tilasta "{from}" tilaan "{to}"?',
            transition_success: 'Tapauksen tila vaihdettu onnistuneesti',
            transition_error: 'Tapauksen tilaa ei voitu vaihtaa',
            create_success: 'Käyttöönottotapaus luotu onnistuneesti',
            create_error: 'Käyttöönottotapausta ei voitu luoda',
            load_error: 'Käyttöönottotapauksia ei voitu ladata',
            detail_error: 'Tapauksen tietoja ei voitu ladata',
            export: 'Vie',
            export_error: 'Käyttöönottotapausta ei voitu viedä',
            evidence_tab_description: 'Lataa ja vie todisteita perehdytystapaukseen.',
            upload_evidence_label: 'Lähetä todiste',
            attached_evidence_heading: 'Liitetyt todisteet',
            no_evidence_attached: 'Ei vielä liitettyjä todisteita.',
            export_bundle_button: 'Vie todistepaketti',
            load_attachments_error: 'Todisteiden lataus epäonnistui.',
            linked_questionnaires_heading: 'Linkitetyt kyselyt',
            no_linked_questionnaires: 'Ei linkitettyjä kyselyjä.',
            linked_scores_heading: 'Linkitetyt riskipisteet',
            no_linked_scores: 'Ei linkitettyjä pisteitä.',
            latest_score_label: 'Viimeisin pistemäärä',
            linked_contracts_heading: 'Linkitetyt sopimukset',
            no_linked_contracts: 'Ei linkitettyjä sopimuksia.',
            linked_sub_processors_heading: 'Linkitetyt alikäsittelijät',
            no_linked_sub_processors: 'Ei linkitettyjä alikäsittelijöitä.',
            approval_created: 'Approval created',
            create_approval: 'Create Approval',
            export_bundle_success: 'Evidence bundle generated',
            gate_type: 'Gate Type',
            no_recent_bundles: 'No evidence bundles generated yet.',
            recent_bundles: 'Recent Bundles',
            tab_completed: 'Section marked complete',
            tab_gate_not_met: 'This section is already marked complete.',
            tab_linked_records_missing: 'This section cannot be marked complete yet: it needs at least one {kind}.',
            linked_kind_questionnaire: 'due-diligence questionnaire for this vendor that has not expired',
            linked_kind_risk_score: 'risk score for this vendor',
            linked_kind_contract: 'active contract with this vendor that is in force today',
            linked_kind_sub_processor: 'sub-processor entry recorded for this case',
            decide_again: 'Decide again',
            resolve_rejection: 'Resolve rejection',
            resolution_notes: 'Documented resolution',
            resolution_notes_hint: 'Required. Say how the reason for the rejection was addressed. The rejection stays in the gate history and the gate returns to undecided, to be decided again.',
            resolution_notes_required: 'Enter the resolution before reopening the gate.',
            resolve_success: 'Rejection resolved. The gate can be decided again.',
            resolve_error: 'The rejection could not be resolved.',
            prior_gate_blocked: 'An earlier gate is rejected or deferred. Decide it again, or resolve its rejection, first.',
            case_gate_blocked: 'The case cannot be approved while a gate is rejected or deferred.',
            case_gate_undecided: 'The case cannot be approved while a gate is still undecided. Decide every gate first.',
            case_final_gate_missing: 'The case cannot be approved until its final gate has been created and approved.',
            case_status_changed: 'The case or its gates changed while this was being saved. The case has been reloaded; check it and try again.',
            gate_already_decided: 'This gate has already been decided. Reload to see the decision.',
            deferred_gate_hint: 'Deferred: this gate and every gate after it wait until it is decided again.',
            mark_complete: 'Merkitse valmiiksi',
            earlier_decisions: 'Earlier decisions on this gate',
            history_entry: '{decision} by {name} on {date}.',
            history_notes: 'Notes: {notes}.',
            history_conditions: 'Conditions: {conditions}.',
            history_redecided: 'Decided again on {date}.',
            history_resolved: 'Rejection resolved by {name} on {date}: {notes}',
            history_unknown_person: 'a person not recorded',
            history_unknown_date: 'a date not recorded',
        },
        sub_processors: {
            appointment_decision: 'Decision to appoint anyway',
            appointment_decision_required: 'The DPA chain is not resolved. Record who is accepting that, and why.',
            appointment_decision_placeholder: 'Why is this sub-processor being appointed before the chain is in place?',
            title: 'Alikäsittelijärekisteri',
            page_title: 'Alikäsittelijärekisteri',
            new_record: 'Uusi alikäsittelijä',
            edit_record: 'Muokkaa alikäsittelijää',
            onboarding_case: 'Käyttöönottotapaus',
            services: 'Tarjotut palvelut',
            data_categories: 'Tietokategoriat',
            processing_countries: 'Käsittelymaat',
            dpa_chain_status: 'DPA-ketjun tila',
            certifications: 'Sertifioinnit',
            last_assessment: 'Viimeisin arviointi',
            next_assessment: 'Seuraava arviointi',
            change_history: 'Muutoshistoria',
            history: 'Historia',
            changed_by: 'Muuttanut',
            comma_separated: 'Pilkuilla erotetut arvot',
            filter_records: 'Suodata tietueita',
            filter_by_dpa: 'Suodata DPA-ketjun tilan mukaan',
            all_dpa_statuses: 'Kaikki DPA-tilat',
            per_page: 'Tietueita sivulla',
            records_count: 'alikäsittelijää',
            records_loaded: 'tietuetta ladattu',
            no_records: 'Alikäsittelijöitä ei löytynyt.',
            no_history: 'Muutoshistoriaa ei ole saatavilla.',
            dpa_active: 'Aktiivinen',
            dpa_pending: 'Odottaa',
            dpa_expired: 'Vanhentunut',
            dpa_not_required: 'Ei vaadittu',
            create_success: 'Alikäsittelijä luotu onnistuneesti',
            create_error: 'Alikäsittelijää ei voitu luoda',
            update_success: 'Alikäsittelijä päivitetty onnistuneesti',
            update_error: 'Alikäsittelijää ei voitu päivittää',
            load_error: 'Rekisteriä ei voitu ladata',
            history_error: 'Muutoshistoriaa ei voitu ladata',
            export_csv: 'Vie CSV',
            export_json: 'Vie JSON',
            export_error: 'Tietoja ei voitu viedä',
        },
    },
    deletion_orchestration: {
        nav: {
            dashboard: 'Orkestroinnin kojelauta',
            runs: 'Orkestroinnin ajot',
            tasks: 'Suoritustehtävät',
            templates: 'Tarkistuslistamallipohjat',
            vendor_requests: 'Toimittajapyynnöt',
            proof_chain: 'Todistusketju',
            dsr_integration: 'DSR-integraatio',
            vendor_verification: 'Toimittajavahvistus',
            completeness: 'Kattavuusnäkymä',
        },
        dashboard: {
            title: 'Orkestroinnin kojelauta',
            loading: 'Ladataan kojelauta tiedot...',
            active_runs: 'Aktiiviset ajot',
            completion_rate: 'Completion Taso',
            avg_completion_time: 'Avg Completion Aika',
            overdue_tasks: 'Myöhässä Tehtävät',
            vendor_compliance: 'Toimittajan vaatimustenmukaisuus',
            vendor_compliance_none: 'No vendor request sent in the last 90 days',
            recent_runs: 'Viimeaikainen Suorita',
            no_recent_runs: 'Ei viimeaikaisia orkestroinnin ajoja.',
            run_id: 'Suorita Tunniste',
            ticket_id: 'Tiketti Tunniste',
            progress: 'Edistyminen',
            systems: 'Järjestelmät',
            vendors: 'Toimittajat',
            load_error: 'Orkestroinnin hallintapaneelin lataaminen epäonnistui. Yritä uudelleen.',
            aria_table: 'Viimeaikaiset orkestraatiot',
        },
        runs: {
            title: 'Orkestroinnin ajot',
            new: 'Uusi Suorita',
            run_id: 'Suorita Tunniste',
            ticket_id: 'Tiketti Tunniste',
            progress: 'Edistyminen',
            systems: 'Järjestelmät',
            vendors: 'Toimittajat',
            data_subject_ref: 'Tiedot Subject Ref',
            data_categories: 'Tiedot Kategoriat',
            enable_proof_chain: 'Ota käyttöön Todiste Ketju',
            start: 'Aloita',
            started: 'Juoksu alkoi',
            mark_ready: 'Merkitse valmiiksi',
            marked_ready: 'Ajo merkitty valmiiksi',
            complete: 'Täydellinen',
            complete_exceptions: 'Täydellinen ilman poikkeuksia',
            pause: 'Tauko',
            resume: 'Jatka',
            cancel: 'Peruuta',
            no_runs: 'Ei orchestration runs found. löytynyt',
            load_error: 'Orkestrointiajojen lataaminen epäonnistui. Yritä uudelleen.',
            create_error: 'Orkesteriajon luominen epäonnistui',
            aria_filter_status: 'Suodata ajon tilan mukaan',
            aria_table: 'Orkestrointi toimii',
            placeholder_ticket_id: 'esim. TKT-000123',
            placeholder_data_subject_ref: 'esim. DS-REF-001',
            placeholder_data_categories: 'esim. personal_data, talous, terveys',
            cancel_confirm_title: 'Cancel this run?',
            cancel_confirm_body: 'Run {run} will be cancelled. A cancelled run cannot be started or resumed again.',
            cancel_reason_label: 'Reason (optional)',
            cancel_reason_hint: 'Recorded with the cancellation.',
            cancel_keep: 'Keep run',
            cancel_confirm: 'Cancel run',
            cancelling: 'Cancelling…',
            cancelled_success: 'Run cancelled',
            cancel_error: 'The run could not be cancelled.',
            title_label: 'Title',
            run_type: 'Run Type',
            run_type_labels: {
                data_subject_request: 'Data Subject Request',
                retention_expiry: 'Retention Expiry',
                manual: 'Manual',
                scheduled: 'Scheduled',
            },
            field_guidelines: {
                title: 'A descriptive name for this orchestration run.',
                run_type: 'The origin or trigger for the deletion process.',
                ticket_id: 'The associated ticket ID from your DSR or support system.',
                data_subject_ref: 'A unique identifier for the data subject (e.g., internal ID, email hash).',
                data_categories: 'Specific categories of personal data to be deleted (comma-separated).',
                proof_enabled: 'Maintains an immutable cryptographic chain of evidence for this run.',
            },
            actions: 'Actions',
            progress_guideline: 'Calculated based on completed system tasks and vendor requests.',
            placeholder_title: 'e.g., Q2 2026 Customer Deletion Batch',
            refusal_run_executing: 'This run is executing and cannot be cancelled. Pause it first, then cancel it.',
            refusal_invalid_status_transition: 'The run is no longer in a status that allows this change. The list has been refreshed.',
            refusal_run_status_changed: 'The run changed status while this was being saved. The list has been refreshed; check the run and try again.',
            refusal_cancel_permission_required: 'Cancelling a deletion run needs the permission to cancel deletion runs, which you do not hold.',
        },
        tasks: {
            title: 'Suoritustehtävät',
            task_id: 'Tehtävä Tunniste',
            run_id: 'Suorita Tunniste',
            system_name: 'Järjestelmä Nimi',
            assigned_to: 'Määritetty To',
            sla_deadline: 'SLA-määräaika',
            progress: 'Edistyminen',
            enter_run_id: 'Syötä suorita tunniste to filter...',
            select_run: 'Valitse juoksu nähdäksesi tehtävät',
            unassigned: 'Määrittämätön',
            overdue: 'Myöhässä',
            assign_task: 'Määritä Tehtävä',
            assignee_id: 'Vastuuhenkilö Tunniste',
            update_status: 'Päivitä tila',
            new_status: 'Uusi Tila',
            no_transitions: 'Tämä tehtävä on lopullisessa tilassa eikä sen tilaa voi muuttaa.',
            select_status_placeholder: 'Valitse uusi tila',
            skip_justification: 'Ohituksen perustelu',
            failure_reason: 'Epäonnistumisen syy',
            no_tasks: 'Ei execution tehtävä found. löytynyt',
            new_task: 'Uusi tehtävä',
            system_id: 'Järjestelmän tunnus',
            data_classification: 'Tietojen luokitus',
            load_error: 'Kohteen execution tasks lataaminen epäonnistui. Yritä uudelleen.',
            assign_error: 'assign task epäonnistui',
            status_error: 'Tehtävän tilan päivittäminen epäonnistui',
            no_tasks_for_run: 'Tällä poistoajolla ei ole vielä suoritustehtäviä.',
            aria_filter_run_id: 'Suodata suoritustunnuksen mukaan',
            aria_filter_status: 'Suodata tehtävän tilan mukaan',
            aria_table: 'Toteutustehtävät',
            view_steps: 'Näytä vaiheet',
            steps_modal_title: 'Suoritusvaiheet',
            step_title: 'Vaihe',
            step_status: 'Vaiheen tila',
            step_notes: 'Muistiinpanot',
            step_evidence: 'Todistetiedostot',
            step_evidence_required: 'Tälle vaiheelle vaaditaan todiste',
            step_evidence_missing: 'Todisteita ei ole vielä ladattu',
            step_save: 'Tallenna vaihe',
            step_saved: 'Vaihe päivitetty',
            step_save_error: 'Vaiheen päivitys epäonnistui',
            create_success: 'Suoritustehtävä luotu',
            create_error: 'Suoritustehtävän luonti epäonnistui',
            step_upload_label: 'Liitä todiste',
            // fix-sprint #431 (gate E12.5 remediation 1) — separation of duties on verification.
            verify_own_task: 'You are assigned to this task, so another person verifies it.',
            verify_separation_of_duties: 'You cannot verify a task you are assigned to: another person must verify it.',
        },
        templates: {
            title: 'Tarkistuslistamallipohjat',
            new: 'Uusi Malli',
            template_id: 'Malli Tunniste',
            system_type: 'Järjestelmätyyppi',
            est_duration: 'Est. Kesto',
            steps: 'Vaiheet',
            action_type: 'Toimintotyyppi',
            instructions: 'Ohjeet',
            mandatory: 'Pakollinen',
            add_step: 'Lisää vaihe',
            step_n: 'Vaihe',
            no_templates: 'Ei checklist mallit found. löytynyt',
            all_system_types: 'Kaikki järjestelmätyypit',
            load_error: 'Tarkistuslistamallien lataaminen epäonnistui. Yritä uudelleen.',
            create_error: 'Tarkistuslistamallin luominen epäonnistui',
            aria_filter_system_type: 'Suodata järjestelmätyypin mukaan',
            aria_filter_status: 'Suodata mallin tilan mukaan',
            aria_table: 'Tarkistuslistamallit',
            placeholder_name: 'esim. tietokannan poistamisen tarkistuslista',
            guideline_name: 'Use a clear operating name, for example “Database Deletion Checklist”.',
            guideline_system_type: 'Choose the system category this checklist will usually run against.',
            guideline_status: 'Draft templates can be reviewed before activation; active templates can be reused in runs.',
            guideline_description: 'Summarise scope, retention context, and when teams should select this template.',
            update_success: 'Mallipohja päivitetty onnistuneesti',
            create_success_msg: 'Mallipohja luotu onnistuneesti',
            update_error: 'Mallipohjan päivittäminen epäonnistui',
            activate_success: 'Mallipohja aktivoitu onnistuneesti',
            activate_error: 'Mallipohjan aktivointi epäonnistui',
            archive_success: 'Mallipohja arkistoitu onnistuneesti',
            archive_error: 'Mallipohjan arkistointi epäonnistui',
            clone_success: 'Mallipohja kloonattu onnistuneesti',
            clone_error: 'Mallipohjan kloonaus epäonnistui',
        },
        vendor_requests: {
            title: 'Toimittaja Deletion Pyynnöt',
            new: 'Uusi Pyyntö',
            run: 'Orchestration Run',
            no_run: 'Not part of a run',
            run_hint: 'Optional. A request recorded against a run counts toward that run’s progress.',
            request_id: 'Pyyntö Tunniste',
            vendor_name: 'Toimittajan nimi',
            vendor_id: 'Toimittajatunnus',
            data_subject_ref: 'Tiedot Subject Ref',
            data_categories: 'Tiedot Kategoriat',
            legal_basis: 'Oikeusperuste',
            sla_days: 'SLA päivää',
            requested_completion: 'Pyydetty valmistuminen',
            communications: 'Viestinnät',
            total_requests: 'Pyynnöt yhteensä',
            no_requests: 'Ei toimittaja deletion requests found. löytynyt',
            filter_vendor_id: 'Suodata by Toimittaja Tunniste...',
            change_status: 'Vaihda Tila',
            new_status: 'Uusi Tila',
            actions: 'Toiminnot',
            load_error: 'Toimittajan poistopyyntöjen lataaminen epäonnistui. Yritä uudelleen.',
            create_error: 'Toimittajan poistopyynnön luominen epäonnistui',
            dashboard_error: 'Toimittajan poiston hallintapaneelin lataaminen epäonnistui. Yritä uudelleen.',
            aria_filter_status: 'Suodata pyynnön tilan mukaan',
            aria_filter_vendor_id: 'Suodata toimittajan tunnuksen mukaan',
            aria_table: 'Toimittajan poistopyynnöt',
            placeholder_vendor_id: 'esim. VND-001',
            placeholder_vendor_name: 'esim. Acme Analytics',
            placeholder_data_subject_ref: 'esim. DS-REF-001',
            placeholder_data_categories: 'esim. personal_data, analytiikka',
            placeholder_legal_basis: 'esim. PDPL 19 artikla',
            // fix-sprint #431 (gate E12.5 remediation 1) — record the vendor’s confirmation; a failed runs read.
            runs_read_error: 'The list of orchestration runs could not be read, so no run can be chosen. Retry, or record the request without a run.',
            confirm_action: 'Record confirmation',
            confirm_title: 'Record the vendor’s confirmation',
            confirm_intro: 'Record that {vendor} confirmed the deletion for request {request}. Recording it completes the request.',
            confirm_date: 'Date confirmed',
            confirm_method: 'How the vendor confirmed',
            confirm_method_choose: 'Choose how…',
            confirm_methods: {
                email: 'Email',
                portal: 'Vendor portal',
                letter: 'Letter',
                phone: 'Phone',
                other: 'Other',
            },
            confirm_contact: 'Vendor contact person',
            confirm_systems: 'Systems affected',
            confirm_systems_hint: 'Optional. Separate systems with commas.',
            confirm_notes: 'Notes',
            confirm_submit: 'Record confirmation',
            confirm_success: 'Vendor confirmation recorded; the request is completed.',
            confirm_refused_status: 'This request cannot receive a confirmation: it is a draft, cancelled or already completed.',
            confirm_missing_fields: 'Enter the date confirmed, how the vendor confirmed and the vendor’s contact person.',
            confirm_not_found: 'This request no longer exists.',
            confirm_error: 'The confirmation could not be recorded.',
            confirm_changed: 'Nothing was recorded: the request changed on the server while this confirmation was being saved. The list behind this dialog is read again; check the request there, and record the confirmation again if it still needs one.',
        },
        proof_chain: {
            title: 'Todiste Ketju Katsoja',
            enter_run_id: 'Syötä suorita tunniste...',
            load_chain: 'Lataa Ketju',
            verify_chain: 'Vahvista ketju',
            verifying: 'Vahvistetaan...',
            chain_valid: 'Ketju is valid',
            chain_invalid: 'Ketju is broken',
            chain_length: 'Ketju Pituus',
            broken_at_index: 'Rikkoutunut indeksissä',
            no_proof_records: 'Ei todiste tietueet found for this run. löytynyt',
            proof_id: 'Todiste Tunniste',
            entity_type: 'Entity Tyyppi',
            entity_id: 'Entity Tunniste',
            event_hash: 'Tapahtuma Tiiviste',
            previous_hash: 'Edellinen Tiiviste',
            attestation: 'Todistus',
            attestation_actor: 'Suorittaja',
            attestation_signed_at: 'Allekirjoitettu',
            attestation_signature: 'Allekirjoitus',
            attestation_evidence_refs: 'Todisteviittaukset',
            run_field_label: 'Poistoajo',
            select_run: 'Valitse poistoajo…',
            aria_select_run: 'Poistoajo todisteketjun hakua varten',
            chain_broken_here: 'Ketju broken here',
            load_error: 'Vetoketjun lataaminen epäonnistui. Yritä uudelleen.',
            verify_error: 'Todistusketjun vahvistaminen epäonnistui. Yritä uudelleen.',
            aria_run_id: 'Suorita tunnus todisteketjun hakuun',
            // Sprint 62.35.0 — a displayed value the event hash does not cover.
            not_covered_by_hash: 'not covered by the hash',
            // Sprint 62.35.0 (AC8) — English until Phase 25 (R-57).
            destruction: {
                action: 'Record destruction',
                modal_title: 'Record destruction',
                intro: 'Identify the data this run destroyed. The record is added to the proof chain and covered by its hash, so it cannot be changed once saved.',
                section_title: 'Destruction record',
                name: 'Name',
                type: 'Type',
                size: 'Size (bytes)',
                format: 'Format',
                locations: 'Locations',
                locations_hint: 'One location per line. List every copy, including backups, replicas and exports.',
                identifier: 'Identifier',
                hash: 'Hash',
                hash_algorithm: 'Hash algorithm',
                hash_algorithm_hint: 'Required when a hash is given, for example SHA-256.',
                lawful_data_unaffected: 'Lawful data left unaffected',
                lawful_yes: 'Yes, lawful data was left unaffected',
                lawful_no: 'No, lawful data was affected',
                lawful_not_recorded: 'Not recorded',
                lawful_data_note: 'Note on lawful data',
                lawful_data_note_hint: 'Required when lawful data was affected.',
                yes: 'Yes',
                no: 'No',
                not_recorded: 'Not recorded',
                submit: 'Record destruction',
                saving: 'Recording…',
                saved: 'Destruction record added to the proof chain.',
                save_error: 'The destruction record could not be saved.',
                err_required: 'This field is required.',
                err_locations: 'Enter at least one location.',
                err_size: 'Enter a number of bytes, zero or more.',
                err_hash_algorithm: 'Enter the algorithm used to compute this hash.',
                err_algorithm_without_hash: 'Remove the algorithm, or enter the hash it was used for.',
                err_lawful_note: 'Explain how lawful data was affected.',
            },
        },
        dsr_integration: {
            change_status: 'Muuta tilaa',
            request_type: 'Pyyntötyyppi',
            data_subject_hint: 'Sen rekisteröidyn tunniste, jonka tiedot poistetaan (esim. sähköposti tai asiakastunnus). Vastaa yleensä valitun DSR-tapauksen pyytäjää.',
            title: 'DSR-poistointegraatio',
            create_run: 'Luo DSR-poistoajo',
            dsr_case_id: 'DSR-tapauksen tunnus',
            data_subject_id: 'Rekisteröidyn tunnus',
            systems: 'Kohdejärjestelmät',
            route_to_department: 'Ohjaa osastolle',
            confirm_deletion: 'Vahvista poisto',
            no_runs: 'DSR-poistoajoja ei löytynyt',
            status: 'Poiston tila',
            deadline: 'Määräaika',
            no_further_status_changes: 'No further status changes',
            refusal_run_executing: 'This deletion run is in progress and cannot be cancelled. Complete it instead, with or without exceptions.',
        },
        vendor_verification: {
            title: 'Toimittajan poistovahvistus',
            create_followup: 'Luo seuranta',
            deadline: 'Vahvistuksen määräaika',
            escalation_contact: 'Eskalointiyhteys',
            escalate: 'Eskaloi',
            confirm_escalate_followup: 'Escalate this follow-up? Its status becomes Escalated. Nothing is sent to the vendor or the escalation contact.',
            no_followups: 'Toimittajaseurantoja ei löytynyt',
            vendor_request: 'Myyjän pyyntö',
            vendor: 'Toimittaja',
            unknown_vendor: 'Tuntematon toimittaja',
            overdue_badge: 'Myöhässä',
            mark_confirmed: 'Merkitse vahvistetuksi',
            mark_confirmed_for: 'Merkitse poisto vahvistetuksi toimittajalle {vendor}',
            confirm_mark_followup_confirmed: 'Mark this follow-up confirmed? The vendor deletion request is not changed; record the vendor’s confirmation on Vendor Requests as well.',
            confirmation_success: 'Toimittajan poisto vahvistettu',
            confirmation_failed: 'Toimittajan poiston vahvistaminen epäonnistui',
            select_request: 'Valitse myyjän pyyntö…',
            escalation_contact_placeholder: 'esim. dpo@vendor.example',
            create_success: 'Toimittajan seuranta luotiin onnistuneesti',
            create_failed: 'Toimittajan seurannan luominen epäonnistui',
            deadline_help: 'The date by which you expect the vendor to confirm. Every follow-up is listed; a pending one past this date is marked Overdue.',
            escalation_success: 'Toimittajan poistaminen eskaloitui onnistuneesti',
            escalation_failed: 'Toimittajan poistamisen eskalointi epäonnistui',
            overdue_filter: 'Overdue (pending, deadline passed)',
            status_labels: {
                pending: 'Pending',
                confirmed: 'Confirmed',
                escalated: 'Escalated',
            },
        },
        completeness: {
            title: 'Poiston kattavuus',
            check_conflicts: 'Tarkista säilytysristiriidat',
            no_conflicts: 'Säilytysristiriitoja ei löytynyt',
            conflict_resolution: 'Ristiriidan ratkaisu',
            extend_retention: 'Pidennä säilytysaikaa',
            proceed_deletion: 'Jatka poistoa',
            escalate_conflict: 'Eskaloi',
            resolve: 'Ratkaise ristiriita',
            data_subject_id: 'Rekisteröidyn tunnus',
            placeholder_data_subject_id: 'Syötä rekisteröidyn tunnus',
            data_subject_hint: 'Sen henkilön tunniste, jonka poistoa tarkistat (esim. sähköposti, asiakastunnus tai DSR-viite). Valitse tunnettu kohde tai kirjoita tunniste.',
            guidance: 'Anna tai valitse rekisteröidyn tunnus, valitse halutessasi järjestelmät ja suorita tarkistus nähdäksesi poiston kattavuuden ja säilytysristiriidat.',
            completion: 'Poiston valmius',
            pending_items: 'Odottavat kohteet',
            system: 'Järjestelmä',
            policy: 'Käytäntö',
            retention_end: 'Säilytys loppu',
            completed_items: 'Valmiit Kohteet',
            last_review_label: 'Viimeisin arvostelu',
            days_ago: 'päivää sitten',
            review_overdue: 'Tarkistusjakso ylitetty. Uudelleentarkistus vaaditaan.',
            conflicts_not_checked: 'Retention conflicts were not checked because no system is selected. Choose at least one system and run the check again to see them.',
        },
        run_status_labels: {
            pending: 'Odottaa',
            planning: 'Suunnittelu',
            ready: 'Valmis',
            in_progress: 'Käynnissä',
            paused: 'Keskeytetty',
            completed: 'Valmis',
            completed_with_exceptions: 'Valmis poikkeuksin',
            cancelled: 'Peruutettu',
        },
        task_status_labels: {
            pending: 'Odottaa',
            assigned: 'Osoitettu',
            in_progress: 'Käynnissä',
            awaiting_verification: 'Odottaa vahvistusta',
            verified: 'Vahvistettu',
            failed: 'Epäonnistunut',
            skipped: 'Ohitettu',
        },
        system_type_labels: {
            database: 'Tietokanta',
            saas_application: 'SaaS-sovellus',
            file_storage: 'Tiedostotallennus',
            email_system: 'Sähköpostijärjestelmä',
            analytics_platform: 'Analytiikka-alusta',
            backup_system: 'Varmuuskopiojärjestelmä',
            vendor_managed: 'Toimittajan hallinnoima',
            custom: 'Mukautettu',
        },
        template_status_labels: {
            draft: 'Luonnos',
            active: 'Aktiivinen',
        },
        action_type_labels: {
            manual_check: 'Manuaalinen tarkistus',
            api_call: 'API-kutsu',
            sql_query: 'SQL-kysely',
            script_execution: 'Skriptin suoritus',
            file_deletion: 'Tiedoston poisto',
            verification: 'Vahvistus',
            notification: 'Ilmoitus',
            approval: 'Hyväksyntä',
        },
        request_status_labels: {
            draft: 'Luonnos',
            sent: 'Lähetetty',
            acknowledged: 'Kuitattu',
            in_progress: 'Käynnissä',
            completed: 'Valmis',
            overdue: 'Myöhässä',
            escalated: 'Eskaloitu',
            disputed: 'Kiistetty',
            cancelled: 'Cancelled',
        },
        event_type_labels: {
            initiation: 'Aloitus',
            data_deletion: 'Tietojen poisto',
            completion: 'Valmistuminen',
            verification: 'Vahvistus',
            rollback: 'Peruutus',
        },
        count_labels: {
            one_template: '1 malli',
            n_templates: '{n} mallia',
            one_run: '1 suoritus',
            n_runs: '{n} suoritusta',
            one_task: '1 tehtävä',
            n_tasks: '{n} tehtävää',
            one_request: '1 pyyntö',
            n_requests: '{n} pyyntöä',
        },
        aria: {
            completion_rate: 'Valmistumisaste: {pct}%',
            vendor_compliance: 'Toimittajan vaatimustenmukaisuus: {pct}%',
            run_progress: 'Suorituksen edistyminen: {pct}%',
            run_progress_systems: 'Edistyminen: {completed}/{total} järjestelmää käsitelty',
            chain_broken: 'Ketju katkennut tässä tietueessa',
            chain_valid: 'Ketju kelvollinen tässä tietueessa',
            chain_unknown: 'Ketjun tila tuntematon',
            escalate_vendor: 'Eskaloi {id}',
            resolve_conflict: 'Ratkaise ristiriita kohteelle {system}',
        },
        regulatory: {
            annual_review_cycle_info: 'Poiston täydellisyys on tarkistettava vähintään 365 päivän välein. Tämän kynnyksen ylittävät tietueet merkitään pakolliseen uudelleentarkastukseen säännöstenmukaisuuden säilyttämiseksi.',
            runs_data_d1_lifecycle_info: 'Orkestroinnin ajon tilat liittyvät DATA-D1-elinkaariin: suunnittelu = luettelo, valmis = luokittele, in_progress = käytä sääntöjä, valmis = todisteet luotu, completed_with_exceptions = tarkistus vaaditaan. Jokainen siirtymä kirjataan vedosketjuun.',
            vendor_record_linkage_info: 'Jokainen toimittajan poistopyyntö on linkitetty sen toimittajatietueeseen vendor_id:n kautta. Täytäntöönpanon vahvistus vaatii salaustodisteita todistusketjulta ennen kuin pyyntö voidaan merkitä suoritetuksi.',
            templates_internal_classification_info: 'Kaikki poistojen tarkistuslistamallit luokitellaan organisaation sisäisiksi asiakirjoiksi. They do not carry official regulatory form IDs and should not be submitted as regulatory filings.',
            proof_chain_evidence_info: 'Jokainen todistetietue kaappaa rekisteröidyn/luokan, aikaleiman, toimijan, oikeusperustan ja SHA-256-tiivisteen. Yhdessä nämä kentät tuottavat salauksen poistamisen todisteita, jotka täyttävät säännösten auditointivaatimukset.',
            retention_data_law_info: 'Tämän kojelaudan säilytysaikataulut on johdettu kullekin tietokategorialle määritetystä Data Law -luokituksesta. Kun luokitus muuttuu, linkitetyt säilytyssäännöt päivittyvät automaattisesti säännösten mukautumisen varmistamiseksi.',
            dsr_erasure_route_info: 'DSR erasure runs are linked to the originating Rights Request case. The regulatory deadline (typically 30 days under GDPR Art. 17) is tracked automatically. Myöhästyneet tapaukset on merkitty eskaloitaviksi.',
        },
    },
    discovery: {
        nav: {
            dashboard: 'Löytökojelauta',
            scans: 'Löytöskannaukset',
            staging_area: 'Väliaikaisalue',
            dedup_rules: 'Deduplikointisäännöt',
            classification_rules: 'Luokittelusäännöt',
            suggestions: 'Ehdotusjono',
            analytics: 'Ehdotusanalytiikka',
            import_templates: 'Tuontimallipohjat',
            connectors: 'Liitinhallinta',
            discovery_cycles: 'Löytösyklit',
            owner_review: 'Omistajan tarkistus',
            classification_lineage: 'Luokitteluhistoria',
        },
        dashboard: {
            title: 'Löytökojelauta',
            loading: 'Ladataan kojelauta tiedot...',
            total_scans: 'Skannaukset yhteensä',
            active_scans: 'Aktiiviset skannaukset',
            completed_30d: 'Valmis (30d)',
            failed_30d: 'Epäonnistunut (30d)',
            systems_discovered: 'Järjestelmät Discovered',
            pending_staged_records: 'Odottaa Staged Tietueet',
            recent_scans: 'Viimeaikainen Skannaukset',
            no_recent_scans: 'No recent skannaus.',
            scan_id: 'Skannaus Tunniste',
            discovered: 'Löydetty',
            view_all_scans: 'Näytä kaikki skannaukset',
            staging_area: 'Väliaikainen alue',
            suggestion_queue: 'Ehdotus Jono',
            records_reviewed: 'Tarkastetut tietueet',
            records_approved: 'Hyväksytyt tietueet',
            exceptions_pending: 'Odottavat poikkeukset',
            view_as_chart: 'Näytä kaaviona',
            view_as_table: 'Näytä taulukkona',
        },
        scans: {
            title: 'Löytöskannaukset',
            new: 'Uusi Skannaus',
            scan_id: 'Skannaus Tunniste',
            scan_type: 'Skannaustyyppi',
            progress: 'Edistyminen',
            discovered: 'Löydetty',
            no_scans: 'Ei discovery skannaus found. löytynyt',
            all_scan_types: 'Kaikki skannaustvypit',
            source_config: 'Lähde Config (JSON)',
            invalid_json: 'Virheellinen JSON in source config',
            load_error: 'Epäonnistui: load discovery skannaus. Yritä uudelleen.',
            create_error: 'Epäonnistui: create discovery skannaus. Yritä uudelleen.',
            change_status: 'Muuta tilaa',
            new_status: 'Uusi tila',
            actions: 'Toiminnot',
            type_endpoint: 'Päätepiste',
            type_spreadsheet_import: 'Taulukkotuonti',
            type_cmdb_sync: 'CMDB-synkronointi',
            type_cloud_sync: 'Pilvisynkronointi',
            status_pending: 'Odottaa',
            status_running: 'Käynnissä',
            status_completed: 'Valmis',
            status_failed: 'Epäonnistunut',
            status_cancelled: 'Peruutettu',
            source_config_placeholder: '{"isäntä": "...", "portti": 443}',
            adapter_not_implemented_toast: 'Tämä skannaustyyppi ei ole vielä käytettävissä. Vain päätepisteskannauksia voi luoda.',
            adapter_not_implemented_warning: 'Ei vielä käytettävissä. Tämän tyyppisiä skannauksia ei voi luoda, ennen kuin niiden tuki julkaistaan.',
            source_config_endpoint_help: 'Provide a list of FQDNs in the "domains" array. The worker resolves DNS (CNAME / MX / TXT / A) and probes HTTPS / HTTP for each domain.',
            source_config_example_label: 'Example:',
            source_config_spreadsheet_help: 'Provide the file_id of a previously uploaded CSV / XLSX and the template_id of the import template whose column mappings apply.',
            placeholder_source_config: '{"domains": ["example.com"]}',
        },
        staging_area: {
            title: 'Väliaikaisalue',
            record_id: 'Tietuetunnus',
            source_scan: 'Lähde Skannaus',
            entity_type: 'Entity Tyyppi',
            suggested_name: 'Suggested Nimi',
            confidence: 'Luottamus',
            no_records: 'Ei staged tietueet found. löytynyt',
            promote: 'Ylennä',
            discard: 'Hylkää',
            review: 'Katselmointia',
            merge: 'Yhdistä',
            merge_title: 'Yhdistä tietue',
            merge_target_id: 'Yhdistä Tavoite Tunniste',
            reject_reason_placeholder: 'Syy hylkäämiselle...',
            promotion_status: 'Promotion Tila',
            dedup_status: 'Deduplikointi Tila',
            all_source_types: 'Kaikki lähdetyypit',
            all_promotion_statuses: 'Kaikki ylennystilat',
            all_dedup_statuses: 'Kaikki deduplikointitilat',
            load_error: 'Epäonnistui: load staged tietueet. Yritä uudelleen.',
            promote_error: 'Epäonnistui: promote tietue. Yritä uudelleen.',
            reject_error: 'Epäonnistui: reject tietue. Yritä uudelleen.',
            merge_error: 'Epäonnistui: merge tietue. Yritä uudelleen.',
            review_error: 'Epäonnistui: mark tietue for katselmointi. Yritä uudelleen.',
            bulk_promote_error: 'Epäonnistui: bulk promote tietueet. Yritä uudelleen.',
            bulk_reject_error: 'Epäonnistui: bulk reject tietueet. Yritä uudelleen.',
            service_type_web_server: 'Verkkopalvelin',
            service_type_database: 'Tietokanta',
            service_type_api_gateway: 'API-yhdyskäytävä',
            service_type_storage: 'Tallennustila',
            service_type_authentication: 'Todennus',
            service_type_analytics: 'Analytiikka',
            service_type_unknown: 'Tuntematon',
            source_type_endpoint_scan: 'Päätepisteskannaus',
            source_type_spreadsheet: 'Taulukko',
            source_type_cmdb: 'CMDB',
            source_type_cloud_inventory: 'Pilvi-inventaario',
            promotion_status_new: 'Uusi',
            promotion_status_under_review: 'Tarkistettavana',
            promotion_status_promoted: 'Ylennetty',
            promotion_status_rejected: 'Hylätty',
            promotion_status_merged: 'Yhdistetty',
            dedup_status_unmatched: 'Ei vastaavuutta',
            dedup_status_potential_match: 'Mahdollinen vastaavuus',
            dedup_status_exact_match: 'Tarkka vastaavuus',
            dedup_status_resolved: 'Ratkaistu',
            confirm_bulk_reject: 'Haluatko varmasti hylätä valitut tietueet?',
            scan_batch_id: 'Erätunnus',
            promote_to_registry_title: 'Siirry tietorekisteriin',
            promote_modal_instructions: 'Creates a Data Registry item with collection_method: observed and provenance linkage to this discovery record. Kohde alkaa luonnostilassa tarkistettavaksi.',
            promote_label_category: 'Luokka',
            promote_label_name_override: 'Nimi (valinnainen ohitus)',
            placeholder_name_override: 'Oletusarvo on etsintätietueen nimi/isäntänimi',
            promote_label_sensitivity: 'Ehdotettu herkkyys',
            inherit_from_category: 'Peri luokasta',
            dedup_status_help: 'Whether this discovered item already exists in inventory. Unmatched = safe to promote; Potential / Exact Match = review or merge first.',
            promote_to_registry: 'Promote to Registry',
            promotion_status_help: 'Where this record is in the review pipeline: New → Under Review → Promoted (or Rejected / Merged).',
            route_to_owner: 'Route to Owner',
            route_to_owner_error: 'Failed to route to owner.',
            route_to_owner_success: 'Owner review task created. Open Owner Review to resolve it.',
            service_type: 'Service Type',
        },
        dedup_rules: {
            title: 'Deduplikointisäännöt',
            new: 'Uusi Sääntö',
            rule_name: 'Sääntö Nimi',
            match_fields: 'Match Kentät',
            threshold: 'Kynnysarvo',
            no_rules: 'Ei dedup rules found. löytynyt',
            seed_defaults: 'Alusta Oletus',
            match_type: 'Match Tyyppi',
            similarity_threshold: 'Similarity Kynnysarvo',
            field_name: 'Kenttä Nimi',
            load_error: 'Epäonnistui: load dedup rules. Yritä uudelleen.',
            create_error: 'Epäonnistui: create dedup rule. Yritä uudelleen.',
            toggle_error: 'Epäonnistui: toggle dedup rule. Yritä uudelleen.',
            seed_error: 'Epäonnistui: seed defaults. Yritä uudelleen.',
            priority: 'Prioriteetti',
            enabled_status: 'Käytössä',
            disabled_status: 'Ei käytössä',
            default_badge: 'Oletus',
            enable: 'Ota käyttöön',
            disable: 'Poista käytöstä',
            match_type_exact: 'Tarkka',
            match_type_fuzzy: 'Sumea',
            match_type_regex: 'Regex',
            match_type_ip_range: 'IP-alue',
            threshold_validation: 'Must be a number between 0 and 1',
        },
        classification_rules: {
            title: 'Luokittelusäännöt',
            new: 'Uusi Sääntö',
            rule_name: 'Sääntö Nimi',
            pattern: 'Malli',
            classification: 'Luokittelu',
            no_rules: 'Ei classification rules found. löytynyt',
            seed_defaults: 'Alusta Oletus',
            run_rules_now: 'Suorita säännöt nyt',
            run_rules_success: 'Loi {count} uutta luokitusehdotusta',
            run_rules_no_results: 'Uusia ehdotuksia ei luotu – kaikilla vastaavilla säännöillä on jo odottavia ehdotuksia',
            run_rules_error: 'Luokittelusääntöjen suorittaminen epäonnistui. Yritä uudelleen.',
            taxonomy_id: 'Taksonomia Tunniste',
            level_id: 'Taso Tunniste',
            condition_logic: 'Ehtojen logiikka',
            weight: 'Paino',
            conditions: 'Ehdot',
            condition_field: 'Kenttä',
            condition_operator: 'Operaattori',
            condition_value: 'Arvo',
            effectiveness: 'Tehokkuus',
            match_count: 'Vastaavuudet',
            approval_count: 'Hyväksynnät',
            rejection_count: 'Hylkäykset',
            filter_taxonomy: 'Suodata by Taksonomia Tunniste...',
            load_error: 'Epäonnistui: load classification rules. Yritä uudelleen.',
            create_error: 'Epäonnistui: create classification rule. Yritä uudelleen.',
            seed_error: 'Epäonnistui: seed defaults. Yritä uudelleen.',
            toggle_error: 'Epäonnistui: toggle rule. Yritä uudelleen.',
            priority: 'Prioriteetti',
            enabled_status: 'Käytössä',
            disabled_status: 'Ei käytössä',
            enable: 'Ota käyttöön',
            disable: 'Poista käytöstä',
            logic_and: 'JA',
            logic_or: 'TAI',
            operator_equals: 'Yhtä suuri kuin',
            operator_contains: 'Sisältää',
            operator_matches_regex: 'Vastaa Regexia',
            operator_in_list: 'Listalla',
            operator_not_equals: 'Ei yhtä suuri kuin',
            operator_not_contains: 'Ei sisällä',
            enabled_label: 'Käytössä',
            disabled_label: 'Pois käytöstä',
            taxonomy_label: 'Taksonomia',
            operator_label: 'Operaattori',
            value_label: 'Arvo',
            conditions_description: 'Define the signals that must match before this classification is applied.',
        },
        suggestions: {
            title: 'Ehdotusjono',
            entity_type: 'Entity Tyyppi',
            suggested_name: 'Suggested Nimi',
            source: 'Lähde',
            confidence: 'Luottamus',
            accept: 'Hyväksy',
            reject: 'Hylkää',
            no_suggestions: 'Ei ehdotuksia jonossa.',
            modify_approve: 'Modify & Hyväksy',
            delegate: 'Delegoi',
            delegate_user_id: 'Delegoi Käyttäjä Tunniste',
            new_level_id: 'Uusi Taso Tunniste',
            reject_reason_placeholder: 'Syy hylkäämiselle...',
            current_level: 'Nykyinen Taso',
            suggested_level: 'Suggested Taso',
            filter_taxonomy: 'Suodata by Taksonomia Tunniste...',
            all_confidence: 'Kaikki luottamusvälit',
            load_error: 'Epäonnistui: load suggestions. Yritä uudelleen.',
            approve_error: 'Epäonnistui: approve suggestion. Yritä uudelleen.',
            reject_error: 'Epäonnistui: reject suggestion. Yritä uudelleen.',
            modify_error: 'Epäonnistui: modify & approve suggestion. Yritä uudelleen.',
            delegate_error: 'Epäonnistui: delegate suggestion. Yritä uudelleen.',
            bulk_approve_error: 'Epäonnistui: bulk approve suggestions. Yritä uudelleen.',
            bulk_reject_error: 'Epäonnistui: bulk reject suggestions. Yritä uudelleen.',
            suggestion_type_new_label: 'Uusi tunniste',
            suggestion_type_validation: 'Validointi',
            suggestion_type_reclassification: 'Uudelleenluokittelu',
            status_pending: 'Odottaa',
            status_approved: 'Hyväksytty',
            status_rejected: 'Hylätty',
            status_superseded: 'Korvattu',
            confidence_high: 'Korkea',
            confidence_medium: 'Keskitaso',
            confidence_low: 'Matala',
            confirm_bulk_reject: 'Haluatko varmasti hylätä valitut ehdotukset?',
            unknown_level_tooltip: 'This rule references a level that is no longer defined in the taxonomy.',
            all_taxonomies: 'Kaikki taksonomiat',
        },
        analytics: {
            title: 'Ehdotusanalytiikka',
            total_suggestions: 'Ehdotukset yhteensä',
            accepted: 'Hyväksytty',
            rejected: 'Hylätty',
            pending: 'Odottaa',
            acceptance_rate: 'Hyväksyntäaste',
            by_entity_type: 'By Entity Tyyppi',
            by_source: 'By Lähde',
            avg_decision_time: 'Avg Päätös Aika',
            rule_effectiveness: 'Sääntö Effectiveness',
            by_status: 'By Tila',
            by_confidence: 'Luottamusvälin mukaan',
            load_error: 'Epäonnistui: load suggestion analytics. Yritä uudelleen.',
            confidence_high: 'Korkea',
            confidence_medium: 'Keskitaso',
            confidence_low: 'Matala',
            by_confidence_band: 'Luottamustason mukaan',
            approval_rate: 'Hyväksyntäaste',
            by_label_state: 'Merkinnän tilan mukaan',
            no_data_hint: 'No suggestions yet. Promote a staged record or click "Run Rules Now" on the Classification Rules page to generate the first batch.',
            rejection_rate: 'Rejection Rate',
        },
        import_templates: {
            title: 'Tuontimallipohjat',
            new: 'Uusi Malli',
            template_name: 'Malli Nimi',
            file_format: 'Tiedosto Muoto',
            column_mappings: 'Column Kartoitukset',
            no_templates: 'Ei import mallit found. löytynyt',
            source_column: 'Lähde Column',
            target_field: 'Tavoite Kenttä',
            transform: 'Muunna',
            source_column_label: 'Lähde sarake',
            target_field_label: 'Kohdekenttä',
            transform_label: 'Muunna',
            confirm_delete: 'Haluatko varmasti poistaa this import template?',
            load_error: 'Epäonnistui: load import mallit. Yritä uudelleen.',
            save_error: 'Epäonnistui: save import malli. Yritä uudelleen.',
            delete_error: 'Epäonnistui: delete import malli. Yritä uudelleen.',
            file_type_csv: 'CSV',
            file_type_xlsx: 'XLSX',
        },
        connectors: {
            title: 'Liitinhallinta',
            new: 'Uusi Liitin',
            connector_name: 'Liitin Nimi',
            connector_type: 'Tyyppi',
            last_sync: 'Viimeisin synkronointi',
            no_connectors: 'Ei connectors found. löytynyt',
            cmdb_syncs: 'CMDB-synkronoinnit',
            cloud_syncs: 'Pilvisynkronoinnit',
            new_sync: 'Uusi Sync',
            no_syncs: 'Ei syncs found. löytynyt',
            cmdb_error: 'Epäonnistui: load CMDB skannaus. Yritä uudelleen.',
            cloud_error: 'Epäonnistui: load cloud skannaus. Yritä uudelleen.',
            trigger_error: 'Epäonnistui: trigger sync. Yritä uudelleen.',
            trigger_sync_error: 'trigger {type} epäonnistui',
            cmdb_section_title: 'CMDB-synkronoinnit',
            cloud_section_title: 'Pilvisynkronoinnit',
            column_connector: 'Liitin',
            action_start: 'Aloita',
            confirm_cancel_sync: 'Peruuta tämä synkronointi?',
            start_error: 'Synkronoinnin aloitus epäonnistui',
            cancel_error: 'Synkronoinnin peruutus epäonnistui',
            type_endpoint: 'Päätepisteen skannaus',
            type_spreadsheet_import: 'Taulukkotuonti',
            type_cmdb_sync: 'CMDB-synkronointi',
            type_cloud_sync: 'Pilvisynkronointi',
        },
        discovery_cycles: {
            title: 'Löytösyklit',
            new_cycle: 'Uusi löytösykli',
            cycle_name: 'Syklin nimi',
            scope_type: 'Laajuustyyppi',
            scope_ids: 'Laajuustunnukset',
            cadence_days: 'Tiheys (päivää)',
            status: 'Tila',
            no_cycles: 'Löytösyklejä ei löytynyt',
            compare: 'Vertaa syklejä',
            delta_report: 'Muutosraportti',
            new_records: 'Uudet tietueet',
            changed_records: 'Muuttuneet tietueet',
            removed_records: 'Poistetut tietueet',
            schedule: 'Ajasta skannaus',
            scope_organization: 'Organisaatio',
            scope_department: 'osasto',
            scope_system: 'Järjestelmä',
            scope_systems: 'Järjestelmät',
            bulk_actions: 'Joukkotoiminnot',
            selected: '{count} valittu',
            bulk_create: 'Joukkoluo',
            bulk_assign_scope: 'Joukkoosoita laajuus',
            bulk_advance: 'Joukkosiirrä tilaa',
            confirm_bulk_action: 'Vahvista joukkotoiminto',
            bulk_confirm_message: 'Oletko varma, että haluat suorittaa tämän toiminnon?',
            cycles_affected: '{count} sykliä vaikuttaa',
            select_all: 'Valitse kaikki',
            select_cycle: 'Valitse sykli {name}',
            from_template: 'mallista',
            bulk_results: 'Massatulokset',
            failed_label: 'epäonnistui',
            template_id: 'Mallin tunnus',
            count: 'Lukumäärä',
            advance_to: 'Kohdetila',
            bulk_success: 'Massatoiminto suoritettu',
        },
        owner_review: {
            title: 'Omistajan tarkistustehtävät',
            assign_owner: 'Määritä omistajalle',
            owner_id: 'Omistaja',
            decision: 'Päätös',
            approve: 'Hyväksy',
            modify: 'Muokkaa',
            reject: 'Hylkää',
            escalate: 'Eskaloi',
            no_tasks: 'Tarkistustehtäviä ei löytynyt',
            bulk_resolve: 'Massaratkaisu',
            exceptions: 'Poikkeuskohteet',
            no_exceptions: 'Poikkeuskohteita ei löytynyt',
            record_id_label: 'Tietueen tunnus',
            selected: 'valittu',
            notes: 'Muistiinpanot',
            select_all: 'Valitse Kaikki',
            select: 'Valitse',
            confirm_resolve: 'Vahvista',
        },
        classification_lineage: {
            title: 'Luokitteluhistoria',
            link_suggestion: 'Linkitä ehdotettuun tunnisteeseen',
            suggestion_id: 'Ehdotus',
            proposed_label_id: 'Ehdotettu tunniste',
            no_lineage: 'Historiatietoja ei löytynyt',
            full_lineage: 'Täydellinen historia',
            record_id_label: 'Tietueen tunnus',
            staged_record_label: 'Lavastettu levy',
            enter_staged_record_id: 'Anna vaiheittaisen tietueen tunnus',
            scan_id_label: 'Skannaustunnus',
            select_record: 'Valitse tietue',
            select_suggestion: 'Valitse ehdotus',
            select_label: 'Valitse tunniste',
        },
        common_labels: {
            n_rules: '{n} säännöt',
            one_rule: '1 sääntö',
            n_scans: '{n} skannaa',
            one_scan: '1 skannaus',
            n_syncs: '{n} synkronoi',
            one_sync: '1 synkronointi',
            n_records: '{n} tallentaa',
            one_record: '1 ennätys',
            n_templates: '{n}-malleja',
            one_template: '1 malli',
            n_suggestions: '{n} ehdotuksia',
            one_suggestion: '1 ehdotus',
            n_selected: '{n} valittu',
            dashboard_loaded: 'Discovery-hallintapaneeli ladattu',
            n_scans_loaded: '{n} skannausta ladattu',
            n_staged_records_loaded: '{n} lavastettuja tietueita ladattu',
            confirm_delete_import_template: 'Haluatko varmasti poistaa tämän tuontimallin?',
            invalid_json_source_config: 'Virheellinen JSON kohteessa source_config',
            scope_organization: 'Organisaatio',
            scope_department: 'osasto',
            scope_system: 'Järjestelmä',
            aria_filter_scans: 'Suodatinskannaukset',
            aria_filter_staged_records: 'Suodata vaiheittaiset tietueet',
            aria_filter_suggestions: 'Suodattimet',
            aria_filter_by_taxonomy_id: 'Suodata taksonomiatunnuksen mukaan',
            aria_filter_by_enabled_status: 'Suodata käytössä-tilan mukaan',
            aria_filter_by_source_type: 'Suodata lähdetyypin mukaan',
            aria_filter_by_promotion_status: 'Suodata tarjouksen tilan mukaan',
            aria_filter_by_dedup_status: 'Suodata dedup-tilan mukaan',
            aria_filter_by_scan_type: 'Suodata skannaustyypin mukaan',
            aria_filter_by_status: 'Suodata tilan mukaan',
            aria_filter_by_confidence: 'Suodata luottamusalueen mukaan',
            aria_select_all_records: 'Valitse kaikki tietueet',
            aria_select_record: 'Valitse tietue {id}',
            aria_select_all_suggestions: 'Valitse kaikki ehdotukset',
            aria_select_suggestion: 'Valitse ehdotus {id}',
            aria_promote_record: 'Mainosta tietuetta {id}',
            aria_discard_record: 'Hylkää tietue {id}',
            aria_merge_record: 'Yhdistä tietue {id}',
            aria_review_record: 'Tarkista tietue {id}',
            aria_accept_suggestion: 'Hyväksy ehdotus {id}',
            aria_reject_record: 'Hylkää tietue',
            aria_merge_record_dialog: 'Yhdistä tietue',
            aria_create_new_scan: 'Luo uusi skannaus',
            aria_sync_progress: 'Synkronoinnin edistyminen: {processed} / {total} kohdetta käsitelty',
            aria_scan_progress: 'Tarkistuksen edistyminen: {processed} / {total} kohdetta käsitelty',
            aria_confidence: 'Luottamus: {pct} %',
            aria_effectiveness: 'Tehokkuus: {pct}',
            aria_promote_n_records: 'Mainosta {n} valittua tietuetta',
            aria_discard_n_records: 'Hylkää {n} valitut tietueet',
            placeholder_pii_detection_rule: 'esim. henkilökohtaisten tunnistetietojen havaitsemissääntö',
            placeholder_tax_id: 'esim. TAX-001',
            placeholder_lvl_id: 'esim. LVL-001',
            placeholder_service_type: 'esim. service_type',
            placeholder_database: 'esim. tietokanta',
            placeholder_hostname_match: 'esim. isäntänimen tarkka vastaavuus',
            placeholder_hostname: 'esim. isäntänimi',
            placeholder_hr_systems_csv: 'esim. HR Systems CSV tuonti',
            placeholder_server_name: 'esim. palvelimen nimi',
            placeholder_hostname_field: 'esim. isäntänimi',
            placeholder_lowercase: 'esim. pienet kirjaimet',
            placeholder_source_config: '{"isäntä": "...", "portti": 443}',
            placeholder_stg_id: 'esim. STG-000123',
            placeholder_lvl_002: 'esim. LVL-002',
            placeholder_usr_id: 'esim. USR-000123',
            decision_for: 'Päätös {id}',
        },
        exports: {
            title: 'Vie',
            inventory: 'Tietoinventaario',
            classification_register: 'Luokitusrekisteri',
            raci: 'RACI-matriisi',
            exceptions: 'Poikkeusraportti',
            evidence_pack: 'Todistepaketti',
            delta_report: 'Muutosraportti',
            executive_summary: 'Johdon yhteenveto',
            export_button: 'Vie',
            downloading: 'Ladataan...',
            download_complete: 'Lataus valmis.',
            download_error: 'Viennin lataus epäonnistui. Yritä uudelleen.',
        },
        flags: {
            title: 'Jatkotoimenpiteiden merkinnät',
            pending: 'Odottavat merkinnät',
            cross_border_review_needed: 'Rajat ylittävä tarkastus tarvitaan',
            sharing_disclosure_needed: 'Jakamisen ilmoitus tarvitaan',
            dpia_review_needed: 'DPIA-tarkastus tarvitaan',
            acknowledge: 'Kuittaa',
            resolve: 'Ratkaise',
            no_flags: 'Ei jatkotoimenpiteiden merkintöjä.',
        },
        delta: {
            title: 'Muutosyhteenveto',
            new_count: '{n} uutta',
            removed_count: '{n} poistettua',
            changed_count: '{n} muutettua',
            re_review_triggered: 'Uudelleentarkastus käynnistetty',
            create_re_review: 'Luo uudelleentarkastus',
            recent_alerts: 'Viimeaikaiset muutoshälytykset',
            no_alerts: 'Ei viimeaikaisia muutoshälytyksiä.',
            threshold_exceeded: 'Muutoskynnys ylitetty (>10 %)',
        },
        regulatory: {
            dashboard_governance_info: 'Tämä kojelauta näyttää syklitason hallinnan tilan: järjestelmät tarkistettu, tietueet vaiheistettu, tietueet tarkistettu, tietueet hyväksytty ja poikkeukset odottavat. Tämä vastaa hallinnointiliidin onnistumiskriteerejä: "suora hallintapaneeli siitä, kuka on päivittänyt varastonsa, luokituksensa ja jakamislokit" (P-VN-05).',
            export_artifacts_info: 'Löytöjen vienti sisältää kaikki vaaditut artefaktityypit kohtia UC-VN-10 ja DATA-EVD-01: hyväksytty tietovarasto, luokitusrekisteri, omistajuus/RACI-kartoitus, poikkeusjono, yhteenveto, todistepaketti ja delta-raportti.',
            scan_inventory_pipeline_info: 'Jokainen valmis skannaus tuottaa vn_data_law_data_inventory_register-mallin kanssa yhteensopivia tietueita (vaatimus 88dd9877). Löydetyt tietovarat täytetään automaattisesti tietovarastorekisteriin täyttääkseen tietolain velvoitteen ylläpitää digitaalista tietovarastoa.',
            connector_linkage_info: 'Jokaisen liitinkokoonpanon on sisällettävä järjestelmätyyppi (esim. CMDB, pilvialusta), tietovaraston viite (tietokanta, tiedostosäilö, SaaS) ja liiketoimintaprosessien linkitys (UC-VN-10 vaaditut tukitietueet). Nämä kentät varmistavat, että löydetyt resurssit voidaan jäljittää niiden lähdejärjestelmään ja liiketoimintakontekstiin.',
            owner_routing_info: 'Tarkastustehtävät reititetään oikealle osastolle tai sovelluksen omistajalle järjestelmän/tietojen omistajuuden perusteella (UC-VN-10 vaihe 5). Reititys käyttää järjestelmän omistajuusrekisteriä, ei ad hoc -määritystä.',
            owner_actions_audit_info: 'Omistajat voivat hyväksyä, muokata, hylätä tai laajentaa kunkin luokituksen (UC-VN-10 vaihe 6). Jokainen toiminto tallentaa toimijan, aikaleiman ja syyn ja tuottaa hallinnon tarkastuksen todisteita vaatimuksen f12fc090 (vuosittainen hallintotodistus) mukaisesti. Kirjausketju näyttää, kuka on tarkistanut, mitä on muutettu ja milloin.',
            escalation_timers_info: 'Eskalointiajastimet: omistajan tarkastelun määräaika on 5 arkipäivää; poikkeuksen eskalaatio on 48 tuntia (UC-VN-10 ajastimet). Ratkaisemattomat kohteet eskaloituvat automaattisesti, kun määräaika umpeutuu. Ajastimet ovat määritettävissä organisaatiokäytännön mukaan.',
            original_suggestion_preserved_info: 'Alkuperäinen koneen/järjestelmän luokitusehdotus säilytetään jokaisen omistajan ohituksen yhteydessä (UC-VN-10 todistevaatimus). Alkuperäistä ehdotusta ei koskaan kirjoiteta päälle, ja se pysyy näkyvissä tarkastusta varten.',
            dedup_lineage_info: 'Päällekkäisyyden poistamissäännöt estävät päällekkäiset inventaariomerkinnät, kun sama tietoresurssi löydetään useista tarkistuksista tai liittimistä. Viimeisimmät skannauksen metatiedot säilytetään ensisijaisena tietueena, samalla kun säilytetään viittaukset kaikkiin aikaisempiin löytöihin tarkastuksen jäljitettävyyttä varten.',
            label_states_info: 'Analytics erottaa 4 hallinnon tarkistuksen tunnistetilaa: koneen ehdottama (odottaa), omistajan hyväksymä, omistajan muokkaama ja hylätty. Näiden tilojen avulla tilintarkastajat voivat arvioida automaation tarkkuutta ja omistajan sitoutumista.',
            import_pipeline_info: 'Tuontimallien on tuotettava tietueita, jotka ovat yhteensopivia etsintäputken kanssa. Tuoduilla tietueilla on samat metatietovaatimukset kuin skannatut tietueet: lähdejärjestelmän tunniste, erätunnus ja aikaleima. Sarakekartoitusten tulee kohdistaa vn_data_law_data_inventory_register:ssa määritettyihin kenttiin.',
            lineage_full_path_info: 'Luokittelulinja jäljittää koko polun: skannaus/tuonti -> vaiheistus -> omistajan tarkistus -> hyväksytty etiketti -> rekisterimerkintä (UC-VN-10 vaiheet 1-10). Jokainen vaihe näyttää tietueen tunnuksen, toimijan, aikaleiman ja tilasiirtymän täydellisen tarkastuksen jäljitettävyyden varmistamiseksi.',
            staging_workflow_info: 'Vaiheittaiset tietueet erotetaan visuaalisesti hyväksytyistä varastomerkinnöistä niiden ylennyksen tilatunnuksen perusteella (UC-VN-10 vaihe 3). Jokainen tietue säilyttää alkuperän metatiedot: lähdejärjestelmän, skannauserän tunnuksen ja haun aikaleiman täydellisen tarkastuksen jäljitettävyyden varmistamiseksi.',
            suggestion_data_d1_info: 'Ehdotukset vastaavat tietolain luokittelumenettelyä DATA-D1: (1) luettelotietovarat, (2) luokitella herkkyystason mukaan, (3) määrittää käsittelysäännöt. Jokainen ehdotus sisältää herkkyys- ja tärkeysmitat vaatimuksen 18e4bfd1 mukaan.',
            downstream_flags_info: 'Kohteet, jotka on merkitty jaettaviksi, rajat ylittäväksi siirroksi tai suuren riskin käsittelyyn, käynnistävät linkitetyt loppupään työnkulkuja: rajat ylittävän siirron arvioinnin, jakamisen riskinarvioinnin ja DPIA tarkistuksen (UC-VN-10 vaihe 8).',
            delta_detection_info: 'Etsintäjaksot tukevat delta-tunnistusta UC-VN-10-vaiheessa 11: jokainen sykli näyttää, mitkä tietueet ovat uusia, muuttuneita tai poistettuja viimeisen hyväksytyn perusviivan jälkeen. Materiaalimuutokset (>10 % delta) käynnistävät automaattisesti uudelleenarvioinnin työnkulkuja.',
        },
    },
    incident_ops: {
        errors: {
            fetch: 'Epäonnistui: load. Yritä uudelleen.',
            save: 'Epäonnistui: save. Yritä uudelleen.',
            fetch_escalation_log_failed: 'Epäonnistui: fetch escalation loki. Yritä uudelleen.',
            fetch_activations_failed: 'Epäonnistui: fetch activations. Yritä uudelleen.',
            deactivate_failed: 'Epäonnistui: deactivate. Yritä uudelleen.',
            complete_activation_failed: 'Epäonnistui: complete activation. Yritä uudelleen.',
            fetch_playbook_templates_failed: 'Epäonnistui: fetch playbook mallit. Yritä uudelleen.',
            create_playbook_template_failed: 'Epäonnistui: create playbook malli. Yritä uudelleen.',
            clone_template_failed: 'Epäonnistui: clone malli. Yritä uudelleen.',
            update_status_failed: 'Epäonnistui: update tila. Yritä uudelleen.',
            deprecate_template_failed: 'Epäonnistui: deprecate malli. Yritä uudelleen.',
            seed_defaults_failed: 'Epäonnistui: seed defaults. Yritä uudelleen.',
            fetch_regulator_exports_failed: 'Epäonnistui: fetch regulator exports. Yritä uudelleen.',
            create_regulator_export_failed: 'Epäonnistui: create regulator export. Yritä uudelleen.',
            update_submission_status_failed: 'Epäonnistui: update submission tila. Yritä uudelleen.',
            fetch_response_metrics_failed: 'Epäonnistui: fetch response metrics. Yritä uudelleen.',
            fetch_sla_dashboard_failed: 'Epäonnistui: fetch SLA kojelauta. Yritä uudelleen.',
            fetch_sla_definitions_failed: 'Epäonnistui: fetch SLA definitions. Yritä uudelleen.',
            create_sla_definition_failed: 'Epäonnistui: create SLA definition. Yritä uudelleen.',
            seed_sla_defaults_failed: 'Epäonnistui: seed SLA defaults. Yritä uudelleen.',
            fetch_vendor_coordination_failed: 'Epäonnistui: fetch toimittaja coordination. Yritä uudelleen.',
            fetch_vendor_detail_failed: 'Epäonnistui: fetch toimittaja detail. Yritä uudelleen.',
            create_vendor_coordination_failed: 'Epäonnistui: create toimittaja coordination. Yritä uudelleen.',
            add_communication_failed: 'Epäonnistui: add communication. Yritä uudelleen.',
            add_info_request_failed: 'Epäonnistui: add info request. Yritä uudelleen.',
            add_action_request_failed: 'Epäonnistui: add toiminto request. Yritä uudelleen.',
        },
        nav: {
            playbooks: 'Toimintaohjeiden mallipohjat',
            sla_definitions: 'SLA-määritelmät',
            sla_dashboard: 'SLA-kojelauta',
            escalation_log: 'Eskalointiloki',
            regulator_exports: 'Viranomaisviennit',
            deadlines: 'Ilmoitus Deadlines',
            vendor_coordination: 'Toimittajakoordinointi',
            metrics: 'Vastemittarit',
            playbook_links: 'Toimintaohjelinkit',
            regulatory_sla: 'Sääntelyllinen SLA',
            coordination_dashboard: 'Koordinointi',
            evidence_chain: 'Todisteketju',
            post_review: 'Jälkiarviointi',
        },
        playbooks: {
            page_title: 'Ohjekirjan mallit',
            activations_page_title: 'Ohjekirjan aktivointi',
            title: 'Toimintaohjeiden mallipohjat',
            new: 'Uusi Malli',
            seed_defaults: 'Alusta Oletus',
            scenario_type: 'Skenaariotyyppi',
            severity_applicability: 'Vakavuus Applicability',
            all_scenario_types: 'Kaikki skenaariotyypit',
            new_playbook: 'Uusi Toimintasuunnitelma Malli',
            clone: 'Kloonaa',
            clone_template: 'Kloonaa Malli',
            template_name: 'Malli nimi',
            deprecate: 'Vanhenna',
            confirm_deprecate: 'Vanhenna malli "{name}"?',
            create_template: 'Luo Malli',
            update_status: 'Päivitä tila',
            no_templates: 'Ei playbook mallit löytynyt',
            deactivation_reason_placeholder: 'Syy käytöstä poistoon...',
            description_placeholder: 'Toimintasuunnitelma kuvaus...',
            name_placeholder: 'Toimintasuunnitelma nimi...',
            procedure_steps: 'Procedure Steps',
        },
        sla_definitions: {
            page_title: 'SLA-määrittelyt',
            title: 'SLA-määritelmät',
            new: 'Uusi SLA Määritelmä',
            sla_name: 'SLA Nimi',
            target_hours: 'Tavoite Hours',
            escalation_after: 'Eskalaatio After',
            applies_to: 'Koskee',
            no_definitions: 'Ei sla definitions found. löytynyt',
            target_value_placeholder: 'Tavoite value',
            name_placeholder: 'SLA nimi...',
            threshold_hours_label: 'Kynnystunnit',
            severity_label: 'Vakavuusaste',
            all_types: 'Kaikki tyypit',
            all_severities: 'Kaikki vakavuusasteet',
            default_label: 'Oletus',
            bound_heading: 'Regulatory clocks behind the default notification target',
            bound_capped: 'Default notification-time targets are capped at {hours} h — the shortest notification period among the installed clocks already in force.',
            bound_not_capped: 'No installed notification clock in force states a period, so default notification-time targets are not capped.',
            bound_bounding_label: 'Bounded by — in force',
            bound_upcoming_label: 'Left out — not yet in force',
            bound_unreadable_label: 'Left out — timing unknown',
            bound_date_not_stated_label: 'Left out — no effective date stated',
            bound_hours: '{hours} h',
            bound_unreadable: 'The regulatory clocks behind the default notification target could not be read.',
            bound_retry_subject: 'the notification clocks',
            load_retry_subject: 'the SLA definitions',
            bound_phase_unresolved_label: 'Left out — phase start not dated',
        },
        sla_dashboard: {
            compliant: 'Yhteensopiva',
            page_title: 'SLA-näkymä',
            title: 'SLA-kojelauta',
            loading: 'Ladataan sla tiedot...',
            compliance_rate: 'Vaatimustenmukaisuus Taso',
            breached: 'Rikottu',
            at_risk: 'At Riski',
            on_track: 'Aikataulussa',
            by_severity: 'By Vakavuus',
            total_records: 'Tietueiden määrä',
            met: 'Täytetty',
            sla_records: 'SLA-tietueet',
            sla_reference: 'SLA-viite',
            record_id: 'Tietue-ID',
            metric_type: 'Mittarityyppi',
            severity: 'Vakavuus',
            elapsed: 'Kulunut',
            clock_start: 'Kellon aloitus',
            statutory_baseline: 'Statutory baseline: the notification deadline required by the frameworks this organization has installed.',
            statutory_baseline_heading: 'Statutory Baseline',
        },
        escalation_log: {
            page_title: 'Eskalointiloki',
            title: 'Eskalointiloki',
            incident_id: 'Vaaratilanteita Tunniste',
            escalated_by: 'Eskaloitu By',
            escalated_to: 'Eskaloitu To',
            reason: 'Syy',
            escalated_at: 'Eskaloitu At',
            no_escalations: 'Ei escalations found. löytynyt',
            filter_incident_id: 'Suodata by Vaaratilanteita Tunniste',
            escalation_id: 'Eskalointi-ID',
            level: 'Taso',
            level_range: 'Taso (L1–L4)',
            create_escalation: 'Luo eskalointi',
            incident_id_placeholder: 'esim. INC-001',
            target_role_placeholder: 'esim. dpo, incident_manager',
            select_placeholder: '— valitse —',
        },
        regulator_exports: {
            page_title: 'Viranomaisviennit',
            title: 'Viranomaisviennit',
            new: 'Uusi Vie',
            incident_id: 'Vaaratilanteita Tunniste',
            regulator: 'Valvontaviranomainen',
            export_format: 'Muoto',
            exported_at: 'Viety',
            no_exports: 'Ei regulator exports found. löytynyt',
            enter_incident_id: 'Syötä vaaratilanteita tunniste',
            export_id: 'Vienti-ID',
            watermark: 'Vesileima',
            submission_status: 'Lähetystila',
            all_jurisdictions: 'Kaikki lainkäyttöalueet',
            no_transitions: 'Ei muita siirtymiä saatavilla kohteesta',
            select_new_status: '— valitse uusi tila —',
            sections_included: 'Sisällytetyt osiot',
            form_classification: 'Luokittelu',
            submission_channel: 'kanava',
        },
        deadlines: {
            title: 'Ilmoitus Deadlines',
            incident_id: 'Vaaratilanteita Tunniste',
            deadline: 'Määräaika',
            regulator: 'Valvontaviranomainen',
            remaining: 'jäljellä',
            no_deadlines: 'Ei notification deadlines found. löytynyt',
        },
        vendor_coordination: {
            page_title: 'Toimittajakoordinointi',
            title: 'Toimittajakoordinointi',
            new: 'Uusi Koordinointi',
            incident_id: 'Vaaratilanteita Tunniste',
            vendor_name: 'Toimittajan nimi',
            contact: 'Yhteyshenkilö',
            coordination_status: 'Koordinointi Tila',
            status_labels: {
                initiated: 'Initiated',
                in_progress: 'In progress',
                awaiting_vendor: 'Awaiting vendor',
                vendor_responded: 'Vendor responded',
                resolved: 'Resolved',
                closed: 'Closed',
                escalated: 'Escalated',
            },
            no_items: 'Ei toimittaja coordination items found. löytynyt',
            assessment_placeholder: 'Initial arviointi from toimittaja...',
            coordination_id: 'Koordinointitunnus',
            communications: 'Viestintä',
            info_requests: 'Tietopyynnöt',
            action_requests: 'Toimenpidepyynnöt',
            direction: 'Suunta',
            channel_label: 'Kanava',
            no_transitions: 'Ei lisäsiirtymiä saatavilla.',
            vendor_ref_number: 'Viitenumero',
            report_date: 'Raporttipäivä',
            contact_email: 'Sähköposti',
            contact_phone: 'Puhelin',
            channel_email: 'Sähköposti',
            channel_meeting: 'Kokous',
            channel_phone: 'Puhelin',
            channel_portal: 'Portaali',
            incident_id_placeholder: 'Suodata by Vaaratilanteita Tunniste...',
            priority_critical: 'Kriittinen',
            priority_high: 'Korkea',
            priority_low: 'Low',
            priority_medium: 'Keskitaso',
            vendor_id_placeholder: 'Suodata by Toimittaja Tunniste...',
            description_placeholder: 'Kuvaus...',
            filter_vendor_id: 'Suodata by Toimittaja Tunniste',
            inbound: 'Saapuva',
            outbound: 'Lähtevä',
            summary_placeholder: 'Yhteenveto...',
            priority_label: 'Prioriteetti',
            due_date_label: 'Eräpäivä',
            info_request_desc: 'Tietopyynnön kuvaus',
            action_request_desc: 'Toimintopyynnön kuvaus',
            content_label: 'Sisältö',
            select_incident: 'Valitse tapaus',
            select_vendor: 'Valitse Toimittaja',
            discovery_date: 'Discovery Päivämäärä',
            contact_role: 'Contact Role',
            processor_role: 'Processor Role',
            sla_elapsed: 'SLA Elapsed',
            overdue: 'Overdue',
            actor: 'Tekijä',
        },
        metrics: {
            page_title: 'Vastausmittarit',
            title: 'Vastemittarit',
            loading: 'Ladataan metrics tiedot...',
            avg_response_time: 'Avg Vastaus Aika',
            avg_resolution_time: 'Avg Resolution Aika',
            total_incidents: 'Vaaratilanteet yhteensä',
            by_severity: 'By Vakavuus',
            by_status: 'By Tila',
            trend: 'Trendi',
            start_sla_clock: 'Käynnistä SLA-kello',
            sla_records: 'SLA Records',
            no_sla_records: 'Ei SLA-tietueita vielä.',
            record_id: 'Tietueen tunnus',
            severity: 'Vakavuus',
            progress: 'Edistyminen',
            incident_id_required: 'Häiriö-ID on pakollinen',
            sla_definition_required: 'SLA-määrittely on pakollinen',
            metric_type_required: 'Mittarityyppi on pakollinen',
            threshold_required: 'Kynnystunnit ovat pakollisia',
            sla_clock_started: 'SLA-kello käynnistetty',
            sla_definition: 'SLA-määrittely',
            no_definitions: 'SLA-määrittelyjä ei löytynyt.',
            select_definition: 'Valitse SLA-määrittely...',
            select_metric_type: 'Valitse mittarityyppi...',
            select_severity: 'Valitse vakavuusaste...',
            threshold_hours: 'Kynnys (tunnit)',
            warning_threshold_pct: 'Varoituskynnys (%)',
            start_clock: 'Käynnistä kello',
            starting: 'Käynnistetään...',
            metric_label: 'Mittari',
            value_label: 'Arvo',
            incident_id: 'Häiriö-ID',
            threshold_placeholder: 'esim. 24',
            metric_types: {
                triage_time: 'Lajitteluaika',
                containment_time: 'Rajoittamisaika',
                notification_time: 'Ilmoitusaika',
                resolution_time: 'Ratkaisuaika',
            },
            severities: {
                critical: 'Kriittinen',
                high: 'Korkea',
                medium: 'Keskitaso',
                low: 'Low',
            },
            sla_reference: 'SLA-viite',
        },
        playbook_links: {
            page_title: 'Playbook-linkit',
            title: 'Toimintaohje-ilmoituslinkit',
            new_link: 'Linkitä toimintaohje ilmoitukseen',
            activation_id: 'Aktivointitunnus',
            notification_id: 'Ilmoitustunnus',
            select_activation: 'Valitse aktivointi...',
            select_notification: 'Valitse ilmoitus...',
            no_links: 'Ei toimintaohjelinkkejä löytynyt',
            seed_pdpl: 'Lisää toimintaohjeiden mallilinkit',
            created: 'Ohjekirjan linkki luotu',
            seeded: 'Toimintaohjeiden mallilinkit lisätty',
            create_failed: 'Pelikirjan linkin luominen epäonnistui',
            seed_failed: 'Toimintaohjeiden mallilinkkien lisääminen epäonnistui',
            seed_already: 'The template playbook links are already present; nothing was added',
            template_activation: 'Template (not an activation of your own)',
            template_notification: 'Template (not a notification of your own)',
        },
        regulatory_sla: {
            // fix-sprint-20260811-2123-followup — keyed on the route's
            // `skipped_reason_code`. The English prose the route also returns is a
            // fallback for an unrecognised code, never the operator's explanation.
            // fix-sprint-20260808-2111 (finding #35) — the declared incident-notification
            // deadlines a seed did not turn into a mapping, and why.
            not_seeded_heading: 'Declared deadlines that were not added',
            not_seeded_intro: 'These incident-notification deadlines are declared by your installed frameworks, but no mapping was added for them:',
            not_seeded_reason: {
                unclassified: 'its deadline type is not one this table records',
                no_numeric_period: 'it states its period in words, not as a number of hours or days, so a mapping cannot hold it',
                not_yet_in_force: 'it is not yet in force: its clock starts to bind on {date}',
                activation_not_determinable: 'neither the framework nor the phase it names states when its clock starts to bind',
            },
            seed_skipped: {
                no_governing_framework: 'Mikään asennettu viitekehys ei ohjaa häiriötilanteiden hallintaa, joten lisättäviä määräaikoja ei ole.',
                declared_but_none_active: 'Häiriötilanteiden hallintaa ohjaava viitekehys on asennettu, mutta ei voimassa.',
                no_declared_deadlines: 'Asennetut häiriönhallinnan viitekehykset eivät määrittele ilmoitusmääräaikoja.',
                declared_deadlines_withheld: 'The installed frameworks declare notification deadlines, but none could be added yet. They are listed below under the deadlines that were not added.',
                already_seeded: 'Every notification deadline the installed frameworks declare already has its mapping. Nothing new was added.',
            },
            // Fix sprint F25 — the period is rendered WITH the unit the framework
            // declared. The table hardcoded an `h` suffix, so a deadline the pack
            // states as 15 working days was shown to the operator as "15h": a
            // misstated statutory period on a regulator-facing screen. `deadline_unit`
            // had one writer and zero readers; this is the reader.
            //
            // The pack unit vocabulary is open (26 distinct values in the corpus), so
            // `unit_unknown` carries anything not listed rather than guessing hours.
            deadline_period_label: 'Määräaika',
            deadline_units: {
                hours: '{count} tuntia',
                working_days: '{count} työpäivää',
                calendar_days: '{count} kalenteripäivää',
                days: '{count} päivää',
                weeks: '{count} viikkoa',
                months: '{count} kuukautta',
                years: '{count} vuotta',
            },
            deadline_unit_unknown: '{count} {unit}',
            // English until Phase 25 (R-57).
            // fix-sprint #447 W6 (owner ruling OR2) — a reference table: it sets no clock,
            // and a row is refused without the unit its period is stated in.
            reference_notice: 'This is a reference table. A mapping sets no clock and escalates nothing: no incident, breach notification or SLA reads these rows. Every regulatory deadline in the product comes from the deadline records your installed frameworks declare.',
            seed_only_conditional: 'The installed frameworks declare notification deadlines only for specific activities or conditions, so none was added automatically.',
            seed_conditional_heading: 'Deadlines not added automatically',
            seed_conditional_intro: 'These installed deadlines apply only to a stated activity or condition. The platform cannot tell whether your organisation carries it, so a person decides: if it does, add the mapping from here.',
            seed_conditional_scope_activity: 'Applies to the activity: {value}',
            seed_conditional_scope_condition: 'Applies only if: {value}',
            seed_conditional_add: 'Add this mapping',
            deadline_amount: 'Period',
            deadline_unit: 'Unit',
            select_deadline_unit: 'Select a unit...',
            deadline_unit_options: {
                hours: 'Hours',
                working_days: 'Working days',
                calendar_days: 'Calendar days',
                days: 'Days',
                weeks: 'Weeks',
                months: 'Months',
                years: 'Years',
            },
            page_title: 'Sääntelylliset SLA-yhdistämiset',
            title: 'Sääntelylliset SLA-kohdistukset',
            new_mapping: 'Uusi SLA-kohdistus',
            sla_definition: 'SLA-määritelmä',
            select_sla_definition: 'Valitse SLA-määritelmä...',
            framework_code: 'Viitekehyskoodi',
            deadline_type: 'Määräaikatyyppi',
            deadline_hours: 'Määräaika tunteina',
            seed_pdpl: 'Lisää kohdistukset asennetuista määräajoista',
            no_mappings: 'Ei sääntelyllisiä SLA-kohdistuksia löytynyt',
            dashboard: 'Sääntelyllinen SLA-kojelauta',
            deadline_types: {
                initial_notification: 'Ensimmäinen ilmoitus',
                supplement_response: 'Täydennysvastaus',
                authority_response: 'Viranomaisen vastaus',
                remediation_report: 'Korjausraportti',
                subject_notification: 'Data Subject Notification',
            },
            statutory_reference: 'Lakisääteinen viite',
        },
        coordination_dashboard: {
            page_title: 'Koordinointinäkymä',
            title: 'Koordinointikojelauta',
            incident_id: 'Vaaratilanne-tunnus',
            select_incident: 'Valitse häiriö...',
            team_role: 'Tiimirooli',
            members: 'Jäsenet',
            tasks: 'Tehtävät',
            sla_status: 'SLA-tila',
            no_teams: 'Ei koordinointitiimejä löytynyt',
            new_team: 'Uusi koordinointitiimi',
            enter_incident_id: 'Syötä häiriö-ID nähdäksesi koordinointitiimit.',
            member_ids_placeholder: 'käyttäjä1, käyttäjä2',
            edit_team: 'Muokkaa tiimiä',
            task_count: 'Tehtävien määrä',
            tasks_completed: 'Valmis',
            team_roles: {
                security: 'Tietoturva',
                legal: 'Oikeudellinen',
                it: 'IT',
                management: 'Johto',
                department_owner: 'Osaston omistaja',
                external: 'Ulkoinen',
            },
        },
        evidence_chain: {
            page_title: 'Todisteketju',
            title: 'Todisteketju',
            new_entry: 'Uusi todistemerkintä',
            select_incident: 'Valitse häiriö...',
            actor: 'Toimija',
            select_actor: 'Valitse toimija...',
            evidence_id: 'Todistetunnus',
            action: 'Toiminto',
            recipient: 'Vastaanottaja',
            no_entries: 'Ei todisteketjumerkintöjä löytynyt',
            enter_incident_id: 'Syötä häiriö-ID nähdäksesi todisteketju.',
            evidence_id_placeholder: 'esim. EVD-2026-0001',
            evidence_id_help: 'Yksilöllinen viite todistusaineistolle – lokitiedosto, levykuva tai kuvakaappaus. Ehdotus on täytetty valmiiksi; muokkaa se vastaamaan todisteluetteloasi.',
            select_incident_first: 'Valitse yllä oleva häiriö ennen todistemerkinnän lisäämistä.',
            for_incident: 'Häiriölle',
            actions: {
                collected: 'Kerätty',
                shared: 'Jaettu',
                exported: 'Vietiin',
                archived: 'Arkistoitu',
            },
        },
        post_review: {
            page_title: 'Jälkihäiriötarkastus',
            title: 'Jälkiarviointi',
            new_review: 'Uusi arviointi',
            select_incident: 'Valitse häiriö...',
            lessons_learned: 'Opitut asiat',
            recommendations: 'Suositukset',
            improvement_actions: 'Parannustoimenpiteet',
            submit_for_review: 'Lähetä arvioitavaksi',
            aria_submit_review: 'Lähetä arviointi {name} arvioitavaksi',
            aria_sign_off: 'Hyväksy arviointi {name}',
            sign_off: 'Hyväksyntä',
            no_reviews: 'Ei jälkiarviointeja löytynyt',
            enter_incident_id: 'Syötä häiriö-ID nähdäksesi jälkihäiriötarkastus.',
            one_per_line: 'Yksi per rivi',
            statuses: {
                draft: 'Luonnos',
                in_review: 'Arvioitavana',
                signed_off: 'Hyväksytty',
                closed: 'Suljettu',
            },
        },
        activations: {
            title: 'Ohjekirjan aktivointi',
            activation_id: 'Aktivointitunnus',
            phases: 'Vaiheet',
            activated_at: 'Aktivoitu klo',
            activated_by: 'Aktivoi',
            deactivate: 'Poista käytöstä',
            complete: 'Valmis',
            deactivation_reason: 'Deaktivoinnin syy',
            evidence_button: 'Todisteet',
            evidence_modal_title: 'Aktivointitodistus',
            no_evidence: 'Tälle aktivoinnille ei ole liitetty todisteita.',
            upload_evidence_label: 'Lataa aktivointitodistus',
            procedure_followed: 'Procedure Followed',
            phases_completed_sr: '{completed} of {total} phases completed',
        },
    },
    program_governance: {
        // ── Sprint 65.8.0 (AC9) — the audit engagement register ─────
        //
        // The record an audit is planned, scoped, staffed, worked and closed as.
        // Every gate this copy names is the SERVER's — the lifecycle, the two
        // mandatory reasons, the calendar-date period, and the refusal to close an
        // engagement over live findings. Each is stated BEFORE the operator meets
        // it, because a rule an operator first learns as a 409 is a rule the
        // product kept to itself.
        audit_engagements: {
            title: 'Audit Engagements',
            description: 'Plan an audit, record what it covers and who is performing it, raise findings inside it, and close it once every finding has been resolved.',
            module_guide_summary: 'An audit engagement carries its scope, the period audited, who is auditing, and a lifecycle that runs from planning through fieldwork and reporting to closed. Findings are raised inside an engagement and never on their own, and an engagement cannot be closed while any of its findings is still unresolved.',
            load_error: 'The audit engagements could not be loaded. Try again.',
            counts_error: 'The engagement counts could not be read, so no totals are shown.',
            counts_label: 'Engagements by state',
            total: 'All engagements',
            filter_state: 'State',
            filter_all_states: 'All states',
            filter_auditor_kind: 'Audit type',
            filter_all_auditor_kinds: 'All audit types',
            filters_label: 'Filter audit engagements',
            col_title: 'Engagement',
            col_period: 'Period audited',
            col_auditor: 'Auditor',
            col_state: 'State',
            no_engagements: 'No audit engagements have been planned yet.',
            no_engagements_filtered: 'No audit engagement matches these filters.',
            select_hint: 'Select an engagement to see its findings and the actions available on it.',
            open_engagement: 'Open this engagement',
            selected_label: 'Selected engagement',
            new_engagement: 'Plan an engagement',
            create_title: 'Plan an audit engagement',
            section_scope: 'What is being audited',
            section_auditor: 'Who is auditing',
            field_title: 'Title',
            field_scope: 'Scope',
            field_scope_hint: 'What this engagement covers, in the words of whoever scoped it.',
            field_period_start: 'Period start',
            field_period_end: 'Period end',
            period_hint: 'Calendar dates. The period audited is a span of days somebody wrote down, so it carries no time of day.',
            period_invalid: 'The period end must not fall before the period start.',
            period_required: 'Both period dates are required, each as a calendar date.',
            field_auditor_kind: 'Audit type',
            auditor_internal: 'Internal',
            auditor_external: 'External',
            field_auditor_name: 'Auditor',
            auditor_name_hint_internal: 'The person or team performing the audit.',
            auditor_name_hint_external: 'The firm performing the audit. An external auditor has no account on this platform, so no user is named.',
            field_auditor_user: 'Platform user performing the audit',
            auditor_user_placeholder: 'Select a user…',
            auditor_user_required: 'An internal audit must name the platform user performing it.',
            created: 'Audit engagement planned',
            create_error: 'The audit engagement could not be created.',
            lifecycle: 'Lifecycle',
            lifecycle_actions: 'Actions on this engagement',
            advance_to: 'Move to {state}',
            transitioned: 'Engagement moved to {state}',
            transition_error: 'The engagement could not be moved.',
            terminal_notice: 'This engagement is {state}. Its scope, period and auditor can no longer be changed.',
            cancel_engagement: 'Cancel engagement',
            cancel_title: 'Cancel this audit engagement',
            cancel_reason: 'Why is it being cancelled?',
            cancel_reason_required: 'Cancelling an audit engagement requires a reason.',
            cancel_confirm: 'Cancel the engagement',
            cancellation_reason: 'Cancellation reason',
            close_engagement: 'Close engagement',
            close_gate_label: 'Whether this engagement can be closed',
            close_blocked: 'This engagement cannot be closed: {count} finding(s) raised inside it are still unresolved. Close or reject each one first.',
            close_ready: 'Every finding raised inside this engagement is closed or rejected, so the engagement can be closed.',
            close_gate_unknown: 'The unresolved-finding count could not be read, so the platform cannot say whether this engagement may be closed.',
            closed_on: 'Closed on',
            cancelled_on: 'Cancelled on',
            created_on: 'Planned on',
            states: {
                planned: 'Planned',
                in_fieldwork: 'In fieldwork',
                reporting: 'Reporting',
                closed: 'Closed',
                cancelled: 'Cancelled',
            },
            directory_truncated: 'Only the first {count} users are listed here.',
            directory_unavailable: 'The user directory could not be loaded, so no user can be chosen right now.',
            directory_reload: 'Reload',
            directory_reloading: 'Reloading…',
            directory_retry_subject_auditor: 'the user directory for the auditor',
            directory_retry_subject_owner: 'the user directory for the finding owner',
            reload: 'Reload',
            list_retry_subject: 'the audit engagements',
            findings_retry_subject: 'the findings',
            findings: {
                title: 'Findings',
                section_label: 'Findings raised inside this engagement',
                unresolved: 'Unresolved',
                load_error: 'The findings could not be loaded. Try again.',
                no_findings: 'No findings have been raised inside this engagement.',
                new_finding: 'Raise a finding',
                create_title: 'Raise a finding inside this engagement',
                engagement: 'Engagement',
                anchor_hint: 'A finding is always raised inside an engagement. It cannot be recorded without one.',
                field_title: 'Title',
                field_observation: 'What was observed',
                field_severity: 'Severity',
                field_owner: 'Owner',
                owner_placeholder: 'Select a user…',
                col_title: 'Finding',
                col_severity: 'Severity',
                col_owner: 'Owner',
                col_state: 'State',
                created: 'Finding raised',
                create_error: 'The finding could not be raised.',
                transitioned: 'Finding moved to {state}',
                transition_error: 'The finding could not be moved.',
                accept: 'Accept',
                start_remediation: 'Start remediation',
                close: 'Close finding',
                reject: 'Reject',
                reject_title: 'Reject this finding',
                reject_reason: 'Why is this not a finding?',
                reject_reason_required: 'Rejecting an audit finding requires a reason.',
                reject_confirm: 'Reject the finding',
                rejection_reason: 'Rejection reason',
                reject_only_from_open: 'A finding can only be rejected while it is open. Once it has been accepted, the organisation has already said it is a finding.',
                states: {
                    open: 'Open',
                    accepted: 'Accepted',
                    remediating: 'Remediating',
                    closed: 'Closed',
                    rejected: 'Rejected',
                },
            },
        },
        maturity_walkthrough: {
            run_label: 'Kävele minua aukkojen läpi',
            running: 'Kävelyvälit…',
            results_heading: 'Parannussuunnitelma ({count} aukko{plural})',
            tokens_label: '≈ {count}-merkkejä',
            no_gaps: 'Aukkoja ei havaittu – kaikki verkkotunnukset tavoitteessa tai sen yläpuolella.',
            col_priority: 'Prioriteetti',
            col_domain: 'Verkkotunnus',
            col_levels: 'Nykyinen → Kohde',
            col_severity: 'Vakavuus',
            col_actions: 'Korjaustoimenpiteet',
            citations_label: 'Lainaukset',
            accept_label: 'Hyväksy tiekartta',
            reject_label: 'Hylkää luonnos',
            export_label: 'Vie DOCX',
            exported_label: 'Tiekartta viety',
            accept_hint: 'Accepting records your decision on the saved draft; it does not change the assessment. Use Export DOCX to download the roadmap, then carry the actions you choose into tasks or your own plan.',
            resolve_summary: 'Päätöslauselma: {status}',
            resolve_status_accepted: 'Hyväksytty',
            resolve_status_rejected: 'Hylätty',
            error_generic: 'Aukon läpikulkua ei voitu suorittaa. Yritä uudelleen tai tarkista arvioinnin tila.',
        },
        policy_gap: {
            run_label: 'Havaitse politiikan aukot',
            running: 'Havaitaan aukkoja…',
            results_heading: 'Käytäntöaukot ({count})',
            tokens_label: '≈ {count}-merkkejä',
            no_gaps: 'Kattavuusaukkoja ei havaittu.',
            col_severity: 'Vakavuus',
            col_kind: 'Ystävällinen',
            col_clause: 'Lause / vaatimus',
            kind_missing: 'Lause puuttuu',
            kind_weak: 'Heikko peitto',
            kind_stale: 'Vanhentunut (arvostelu myöhässä)',
            remediation_label: 'Ehdotettu korjaus',
            citations_label: 'Lainaukset',
            accept_label: 'Hyväksy aukkoraportti',
            reject_label: 'Hylkää raportti',
            export_label: 'Vie DOCX',
            accept_hint: 'Hyväksy tallentaa säilytetyn luonnoksen aukkoraportin. Käytä Export DOCX käytäntöjen tarkastusraportin luomiseen. operaattori tekee varsinaisen käytäntömuokkauksen.',
            resolve_summary: 'Päätöslauselma: {status}',
            error_generic: 'Käytäntöaukkoja ei voitu havaita. Yritä uudelleen tai tarkista asiakirjan tila.',
        },
        remediation_plan_generator: {
            run_label: 'Luo suunnitelma aukoista',
            running: 'Luodaan suunnitelmaa…',
            results_heading: 'Korjaussuunnitelma ({phase_count} vaihe{plural})',
            tokens_label: '≈ {count}-merkkejä',
            phase_label: 'Vaihe {index}',
            window_label: 'Ikkuna',
            owner_label: 'Omistajan rooli',
            evidence_label: 'Vaaditut todistetyypit',
            tasks_label: 'Tehtävät',
            no_phases: 'Vaiheita ei synny – tulovälisarja voi olla tyhjä.',
            citations_label: 'Lainaukset',
            accept_label: 'Hyväksy suunnitelma',
            reject_label: 'Hylkää suunnitelma',
            export_label: 'Vie DOCX',
            accept_hint: "Accepting creates one task for each task listed (up to 5 per phase and 12 in all). Each task's description names this plan; the tasks are not linked to a record in the Remediation Plans register and have no assignee, so give each one an owner from the task list. Use Export DOCX for the full deliverable.",
            resolve_summary: 'Ratkaisu: {status} · {tasks} tehtävä(t) luotu',
            error_generic: 'Korjaussuunnitelmaa ei voitu luoda. Yritä uudelleen tai tarkista aukko-asetettu tulo.',
            source_picker_label: 'lähde',
            source_all: 'Kaikki',
            source_consent: 'Suostumuksen erojen analyysi',
            source_policy: 'Käytäntöaukon havaitseminen',
            source_vendor: 'Toimittajan perehdytys',
            load_upstream_label: 'Kuorma ylävirrasta',
            loading_upstream: 'Ladataan…',
            upstream_count: '{count} aukko(t) ladattu',
            export_done: 'Saved {filename} to your downloads.',
            no_gaps_in_source: 'The selected source holds no gaps, so nothing was generated and no tasks can be created. Run that analysis first, or choose another source.',
            no_gaps: 'No gaps are loaded, so there is nothing to plan. Choose a source and click Load from upstream.',
        },
        nav: {
            audit_engagements: 'Audit Engagements',
            handbook_assessments: 'Handbook Assessments',
            maturity_models: 'Kypsyysmallit',
            maturity_assessments: 'Kypsyysarvioinnit',
            trend_dashboard: 'Trendikojelauta',
            reporting_periods: 'Raportointikaudet',
            executive_summaries: 'Johdon yhteenvedot',
            audit_readiness: 'Tarkastusvalmius',
            evidence_gaps: 'Todistusaukot',
            program_health: 'Ohjelman tila',
            obligation_mappings: 'Velvoitekartoitukset',
            governance_documents: 'Hallintodokumentit',
            remediation_plans: 'Korjaussuunnitelmat',
            governance_model: 'Hallintomalli',
            document_drafting: 'Asiakirjan luonnostelu',
            document_issuance: 'Asiakirjan julkaisu',
            acknowledgment_tracking: 'Kuittausseuranta',
            change_log: 'Muutosloki',
            review_cadence: 'Tarkistuskadenssi',
        },
        maturity_models: {
            title: 'Kypsyysmallit',
            new: 'Uusi Model',
            seed_defaults: 'Alusta Oletus',
            domains: 'Verkkotunnukset',
            activate: 'Aktivoi',
            no_models: 'Ei maturity models löytynyt',
            new_maturity_model: 'Uusi Kypsyys Model',
            create_model: 'Luo Model',
            load_error: 'Kypsyysmallien lataus epäonnistui. Yritä uudelleen.',
            create_error: 'Kypsyysmallin luonti epäonnistui',
            status_error: 'Mallin tilan päivitys epäonnistui',
            seed_error: 'Oletusarvojen luonti epäonnistui',
            aria_seed_defaults: 'Luo oletuskypsyysmallit',
            aria_create_new: 'Luo uusi kypsyysmalli',
            loading_sr: 'Ladataan kypsyysmalleja',
            model_name_placeholder: 'esim., Tietosuojan kypsyysmalli',
            status_changed: 'Mallin tila muutettu tilaan {status}',
        },
        maturity_assessments: {
            models_load_error: 'The maturity model list could not be loaded, so model names may show as unknown.',
            no_models_action: 'Open Maturity Models',
            no_models_body: 'An assessment scores your programme against a maturity model, and this organization has none. Create one first.',
            no_models_title: 'No maturity models yet',
            title: 'Kypsyysarvioinnit',
            new: 'Uusi Arviointi',
            model: 'Malli',
            assessor: 'Arvioija',
            overall_score: 'Kokonaispistemäärä',
            overall_score_help: 'Painotettu kypsyysarvio välillä 0–5, joka lasketaan automaattisesti, kun arviointi viimeistellään. Kasvata sitä pisteyttämällä kukin osa-alue korkeammalle arvioinnin aikana ja viemällä arviointi sitten vaiheiden Lähetä tarkistettavaksi → Viimeistele läpi.',
            assessed_at: 'Arvioitu',
            no_assessments: 'Ei maturity arviointi found. löytynyt',
            load_error: 'Kypsyysarviointien lataus epäonnistui. Yritä uudelleen.',
            create_error: 'Arvioinnin luonti epäonnistui',
            start_error: 'Arvioinnin aloitus epäonnistui',
            archive_error: 'Arvioinnin arkistointi epäonnistui',
            eval_error: 'Arvioinnin lataus arviointia varten epäonnistui',
            eval_min_score: 'Arvioi vähintään yksi toimialue',
            eval_save_error: 'Toimialuearviointien tallennus epäonnistui',
            review_error: 'Lähetys tarkistettavaksi epäonnistui',
            finalize_error: 'Arvioinnin viimeistely epäonnistui',
            start: 'Aloita',
            start_confirm_title: 'Aloita arviointi',
            start_confirm_message: 'Haluatko varmasti aloittaa tämän arvioinnin?',
            archive_confirm_title: 'Arkistoi arviointi',
            archive_confirm_message: 'Haluatko varmasti arkistoida tämän arvioinnin?',
            evaluate_domains: 'Arvioi',
            evaluate_domains_title: 'Arvioi toimialueet',
            submit_for_review: 'Lähetä tarkistettavaksi',
            finalize: 'Viimeistele',
            archive: 'Arkistoi',
            all_reviewers: 'Kaikkien tarkastajien on hyväksyttävä',
            any_reviewer: 'Kuka tahansa tarkastaja voi hyväksyä',
            select_level: 'Valitse kypsyystaso...',
            findings_placeholder: 'Havainnot, muistiinpanot, todistusviitteet...',
            no_domains: 'Tälle mallille ei löytynyt toimialueita.',
        },
        trend_dashboard: {
            title: 'Trendikojelauta',
            loading: 'Ladataan trend tiedot...',
            score_trend: 'Pistemäärä Trendi',
            domain_comparison: 'Verkkotunnus Comparison',
            period: 'Kausi',
            load_error: 'Trendiyhteenvedon lataus epäonnistui. Yritä uudelleen.',
            metrics_load_error: 'Trendimittareiden lataus epäonnistui. Yritä uudelleen.',
            module: 'Moduuli',
            metric: 'Mittari',
            value: 'Arvo',
            trend: 'Trendi',
            aria_filters: 'Trendimittareiden suodattimet',
            showing_chart: 'Näytetään kaavionäkymä',
            showing_table: 'Näytetään taulukonäkymä',
            create_metric: 'Luo mittari',
            data_points: 'Tietopisteet',
            trend_direction: 'Trendin suunta',
            unit: 'Yksikkö',
        },
        reporting_periods: {
            title: 'Raportointikaudet',
            new: 'Uusi Kausi',
            period_name: 'Kausi Nimi',
            start_date: 'Aloituspäivä',
            end_date: 'Päättymispäivä',
            no_periods: 'Ei reporting periods found. löytynyt',
            load_error: 'Raportointijaksojen lataus epäonnistui. Yritä uudelleen.',
            create_error: 'Raportointijakson luonti epäonnistui',
            auto_generate_error: 'Raportointijaksojen automaattinen luonti epäonnistui',
            status_error: 'Jakson tilan päivitys epäonnistui',
            start_collecting: 'Aloita keräys',
            mark_as_draft: 'Merkitse luonnokseksi',
            submit_for_review: 'Lähetä tarkistettavaksi',
            publish: 'Julkaise',
            auto_generate: 'Luo raportointijaksot automaattisesti',
            period_label_placeholder: 'esim., Q1 2026',
            status_updated: 'Jakson tila päivitetty',
        },
        executive_summaries: {
            title: 'Johdon yhteenvedot',
            new: 'Uusi Yhteenveto',
            period: 'Kausi',
            generated_at: 'Luotu',
            generated_by: 'Luonut',
            no_summaries: 'Ei executive summaries found. löytynyt',
            generate: 'Luo',
            load_error: 'Johdon yhteenvetojen lataus epäonnistui. Yritä uudelleen.',
            generate_error: 'Johdon yhteenvedon luonti epäonnistui',
            save_error: 'Johdon yhteenvedon tallennus epäonnistui',
        },
        audit_readiness: {
            title: 'Tarkastusvalmius',
            loading: 'Ladataan tarkastus readiness tiedot...',
            overall_readiness: 'Kokonaisvalmius',
            by_domain: 'By Verkkotunnus',
            evidence_coverage: 'Todistusaineisto Kattavuus',
            gaps: 'Puutteet',
            load_error: 'Viimeisimmän valmiuden lataus epäonnistui. Yritä uudelleen.',
            history_error: 'Valmiushistorian lataus epäonnistui. Yritä uudelleen.',
            calculate_error: 'Auditoinnin valmiuden laskenta epäonnistui',
            aria_recalculate: 'Laske auditoinnin valmiuspisteet uudelleen',
            aria_current_readiness: 'Nykyinen auditoinnin valmius',
            aria_history: 'Valmiuspisteiden historia',
            recalculated: 'Auditoinnin valmius laskettu uudelleen',
            loading_sr: 'Ladataan auditoinnin valmiustietoja',
        },
        evidence_gaps: {
            title: 'Todistusaukot',
            domain: 'Verkkotunnus',
            control: 'Hallinta',
            gap_type: 'Puutetyyppi',
            severity: 'Vakavuus',
            no_gaps: 'Ei todistusaineisto puute found. löytynyt',
            load_error: 'Todistusaukkojen lataus epäonnistui. Yritä uudelleen.',
            stats_error: 'Aukkotilastojen lataus epäonnistui. Yritä uudelleen.',
            create_error: 'Todistusaukon luonti epäonnistui',
            update_error: 'Todistusaukon päivitys epäonnistui',
            status_error: 'Tilan päivitys epäonnistui',
            aria_create_new: 'Luo uusi todistusaukko',
            aria_statistics: 'Todistusaukkotilastot',
            loading_sr: 'Ladataan todistusaukkoja',
            status_updated: 'Aukon tila päivitetty',
            entity_type_placeholder: 'esim., toimittaja, järjestelmä',
            all_modules: 'Kaikki alueet',
            all_severities: 'Kaikki vakavuudet',
        },
        program_health: {
            title: 'Ohjelman tila',
            loading: 'Ladataan program health tiedot...',
            overall_health: 'Overall Terveys',
            by_module: 'By Moduuli',
            risk_areas: 'Riski Areas',
            recommendations: 'Suositukset',
            load_error: 'Ohjelman terveystietojen lataus epäonnistui. Yritä uudelleen.',
            aria_key_metrics: 'Ohjelman terveyden avainmittarit',
            aria_module_scorecard: 'Moduulin tuloskortin erittely',
            loading_sr: 'Ladataan ohjelman terveystietoja',
            trending_up: 'Nouseva trendi',
            trending_down: 'Laskeva trendi',
            trending_stable: 'Vakaa',
            data_loaded: 'Ohjelman terveystiedot ladattu',
        },
        obligation_mappings: {
            title: 'Velvoitekartoitukset',
            create_mapping: 'Luo velvoitekartoitus',
            framework: 'Viitekehys',
            select_framework: 'Valitse viitekehys...',
            obligation: 'Velvoite',
            obligation_ref: 'Velvoiteviite',
            obligation_description: 'Velvoitteen kuvaus',
            role: 'Rooli',
            assigned_role: 'Määrätty rooli',
            select_role: 'Valitse rooli...',
            assigned_department: 'Määrätty osasto',
            select_department: 'Valitse osasto...',
            control_ids: 'Kontrolli-ID:t',
            control_ids_placeholder: 'esim. A.5.1, CC6.1',
            control_ids_hint: 'Pilkuin erotetut kontrollien viitekoodit.',
            raci_assignments: 'RACI-kohdistukset',
            create_raci: 'Luo RACI-kohdistus',
            activity: 'Toiminto',
            activity_name: 'Toiminnon nimi',
            activity_description: 'Toiminnon kuvaus',
            responsible: 'Vastuullinen',
            select_users: 'Valitse käyttäjät...',
            accountable: 'Tilivelvollinen',
            select_user: 'Valitse käyttäjä...',
            consulted: 'Konsultoitu',
            informed: 'Tiedotettu',
            review_frequency: 'Tarkastustiheys',
            frequency_monthly: 'Kuukausittain',
            frequency_quarterly: 'Neljännesvuosittain',
            frequency_semi_annual: 'Puolivuosittain',
            frequency_annual: 'Vuosittain',
            no_mappings_found: 'Velvoitekartoituksia ei löytynyt.',
            no_raci_found: 'RACI-kohdistuksia ei löytynyt.',
            mapping_created: 'Velvoitemääritys luotu',
            mapping_updated: 'Velvoitekartoitus päivitetty',
            mapping_deleted: 'Velvoitekartoitus poistettu',
            raci_created: 'RACI-tehtävä luotu',
            raci_updated: 'RACI-kohdistus päivitetty',
            raci_deleted: 'RACI-kohdistus poistettu',
            aria_edit_mapping: 'Muokkaa kartoitusta {name}',
            aria_delete_mapping: 'Poista kartoitus {name}',
            aria_edit_raci: 'Muokkaa RACI {name}',
            aria_delete_raci: 'Poista RACI {name}',
            confirm_delete_mapping: 'Poista tämä velvoitekartoitus?',
            confirm_delete_raci: 'Poista tämä RACI-kohdistus?',
            table_view: 'Taulukkonäkymä',
            matrix_view: 'Matriisiinäkymä',
            print_export: 'Tulosta / Vie',
            filter_framework: 'Suodata viitekehyksen mukaan',
            no_matrix_data: 'Ei RACI-osoituksia näytettäväksi matriisiinäkymässä.',
            aria_print_export: 'Tulosta tai vie RACI-matriisi',
        },
        governance_documents: {
            title: 'Hallintodokumentit',
            create_document: 'Luo hallintodokumentti',
            document_title: 'Dokumentin otsikko',
            document_type: 'Dokumenttityyppi',
            content_url: 'Sisältö-URL',
            content_url_placeholder: 'https://...',
            framework_codes: 'Viitekehyskoodit',
            select_frameworks: 'Valitse viitekehykset...',
            effective_date: 'Voimaantulopäivä',
            review_date: 'Tarkastuspäivä',
            status: 'Tila',
            version: 'Versio',
            acknowledgments: 'Kuittaukset',
            acknowledge: 'Kuittaa',
            acknowledged_by: 'Kuitannut',
            department: 'Osasto',
            no_documents_found: 'Hallintodokumentteja ei löytynyt.',
            draft: 'Luonnos',
            review: 'Tarkistus',
            approved: 'Hyväksytty',
            issued: 'Julkaistu',
            superseded: 'Korvattu',
            document_created: 'Asiakirja luotu',
            document_acknowledged: 'Asiakirja kuitattu',
            policy: 'Käytäntö',
            procedure: 'Menettely',
            standard: 'Standardi',
            guideline: 'Ohje',
        },
        governance_model: {
            title: 'Hallintomalli',
            create_model: 'Hallintomalli',
            edit_model: 'Hallintomalli',
            model_name: 'Model',
            description: 'Kuvaus',
            governance_type: 'Type',
            type_centralized: 'Keskitetty',
            type_federated: 'Liittynyt',
            type_hybrid: 'Hybridi',
            framework_codes: 'Framework',
            select_frameworks: '...',
            review_frequency: 'Frequency',
            freq_monthly: 'Kuukausittain',
            freq_quarterly: 'Neljännesvuosittain',
            freq_semi_annual: 'Puolivuosittain',
            freq_annual: 'Vuosittainen',
            committees: 'komiteat',
            committee_name: 'Name',
            committee_members: 'Members',
            committee_mandate: 'Mandaatti',
            add_committee: '+',
            no_committees: '—',
            delegation_rules: 'Delegation',
            from_role: 'From',
            to_role: 'To',
            scope: 'Laajuus',
            add_delegation_rule: '+',
            control_owner_summary: 'RACI',
            no_raci_data: '—',
            activity: 'Toiminta',
            responsible: 'R',
            accountable: 'A',
            consulted: 'C',
            informed: 'I',
            no_models_found: '—',
            model_created: 'OK',
            model_updated: 'OK',
            model_deleted: 'OK',
            confirm_delete: '?',
            expand: '+',
            collapse: '-',
            aria_expand: '{name}',
            aria_edit: '{name}',
            aria_delete: '{name}',
            aria_export_csv: '{name} CSV',
            aria_export_json: '{name} JSON',
            framework_read_permission_warning: 'Tarvitset regulatory_framework:lukuoikeuden kehysten valitsemiseen. Pyydä järjestelmänvalvojaa myöntämään se.',
            select_members: 'Valitse toimikunnan jäsenet',
            select_from_role: 'Valitse delegoiva rooli',
            select_to_role: 'Valitse vastaanottava rooli',
            mandate_markdown_hint: 'Tuettu merkintä: **lihavoitu**, luettelot, linkit.',
            scope_tab_obligation: 'Velvollisuus',
            scope_tab_framework: 'viitekehys',
            scope_tab_free_text: 'Vapaa teksti',
            select_obligation: 'Valitse velvoite',
            select_framework: 'Valitse kehys',
            aria_remove_committee: 'Poista komitea {n}',
            aria_remove_delegation_rule: 'Poista delegointisääntö {n}',
            no_frameworks_available: 'Ei kehyksiä saatavilla. Lisää ensin sääntelykehykset.',
            confirm_discard_changes: 'Discard your unsaved governance model changes?',
        },
        document_drafting: {
            title: 'Asiakirjan luonnostelu',
            document_title: 'Document',
            document_type: 'Type',
            status: 'Tila',
            drafter: 'Luonnostelija',
            deadline: 'Määräaika',
            assign_drafter: 'Assign',
            select_drafter: '...',
            assign: 'Määritä',
            submit_for_review: 'Submit',
            legal_review: 'Review',
            reviewer: 'Arvostelija',
            select_reviewer: '...',
            comments: 'Kommentit',
            review_action: 'Action',
            approve: 'Hyväksy',
            request_changes: 'Pyydä muutoksia',
            submit_review: 'Submit',
            executive_approval: 'Approval',
            no_drafts_found: '—',
            drafter_assigned: 'OK',
            status_updated: 'OK',
            review_approved: 'OK',
            changes_requested: 'OK',
            document_approved: 'OK',
            aria_assign: '{name}',
            aria_submit_review: '{name}',
            aria_review: '{name}',
            aria_approve: '{name}',
        },
        document_issuance: {
            title: 'Asiakirjan julkaisu',
            document_title: 'Document',
            document_type: 'Type',
            status: 'Tila',
            version: 'Versio',
            effective_date: 'Date',
            distribution_scope: 'Distribution',
            distribution_placeholder: '...',
            issuance_number: 'Number',
            issuance_number_placeholder: 'ISS-2026-001',
            supersedes: 'Supersedes',
            none: '—',
            issue_document: 'Issue',
            ready_for_issuance: 'Ready',
            issued_documents: 'Issued',
            no_issued_documents: '—',
            view_lineage: 'Lineage',
            supersession_lineage: 'Supersession',
            document_issued: 'OK',
            document_superseded: 'OK',
            aria_issue: '{name}',
            aria_view_lineage: '{name}',
            no_approved_documents: 'No approved documents available to issue.',
        },
        acknowledgment_tracking: {
            title: 'Kuittausseuranta',
            document_title: 'Asiakirja',
            acknowledged_count: 'Tunnustettu',
            pending_count: 'Odottaa',
            percentage: '%',
            details: 'Yksityiskohdat',
            send_reminder: 'Remind',
            acknowledgment_details: 'Details',
            user: 'Käyttäjä',
            department: 'osasto',
            acknowledged_at: 'Date',
            status: 'Tila',
            acknowledged: 'OK',
            no_issued_documents: '—',
            no_acknowledgments: '—',
            reminder_sent: 'OK',
            aria_expand: '{name}',
            aria_send_reminder: '{name}',
            // Fix sprint #447 W3 — recipients, acknowledging, and the reminder count.
            // English until Phase 25 (R-57).
            recipients: 'Recipients',
            record_recipients: 'Record recipients',
            aria_record_recipients: 'Record recipients for {name}',
            recipients_dialog_title: 'Recipients of {name}',
            recipients_dialog_help: 'Choose the people asked to acknowledge this document. Only people whose role can open Acknowledgment Tracking are listed, because only they can acknowledge.',
            recipients_none_eligible: 'No one in your organization holds a role that can acknowledge documents.',
            recipients_saved: 'Recipients recorded',
            recipients_not_recorded: 'Not recorded',
            acknowledge: 'Acknowledge',
            aria_acknowledge: 'Acknowledge {name}',
            acknowledge_dialog_title: 'Acknowledge {name}',
            acknowledge_dialog_body: 'You confirm that you have read this document. Your name and the time are recorded.',
            acknowledge_department: 'Department (optional)',
            reminder_sent_count: 'Reminder sent to {count} of {pending} people who have not acknowledged',
            export_documents_count: 'Documents',
            export_acknowledgments_count: 'Acknowledgments',
        },
        change_log: {
            title: 'Muutosloki',
            date: 'Päivämäärä',
            document: 'Asiakirja',
            change_type: 'Type',
            actor: 'näyttelijä',
            summary: 'Yhteenveto',
            export_csv: 'Vie CSV',
            filters: 'Filters',
            filter_document: 'Document',
            all_documents: 'All',
            filter_change_type: 'Type',
            all_types: 'All',
            date_from: 'From',
            date_to: 'To',
            version_history: 'History',
            version_archive: 'Archive',
            no_entries: '—',
            no_history: '—',
            exported: 'OK',
            aria_export: 'CSV',
            aria_view_history: '{name}',
            // English until Phase 25 (R-57).
            // fix-sprint #447 W6 — `DocumentChangeType` labels; the page showed raw codes.
            type_created: 'Created',
            type_status_transition: 'Status changed',
            type_fields_updated: 'Fields updated',
            type_superseded: 'Superseded',
            type_acknowledged: 'Acknowledged',
            // fix-sprint #447 round 2 — metadata line of the server-rendered export.
            export_generated_at: 'Generated at',
        },
        review_cadence: {
            title: 'Tarkistuskadenssi',
            configure_cadence: 'Tarkistuskadenssi',
            edit_cadence: 'Tarkistuskadenssi',
            document: 'Asiakirja',
            select_document: '...',
            review_frequency: 'Frequency',
            freq_30: '30',
            freq_60: '60',
            freq_90: '90',
            freq_180: '180',
            freq_365: '365',
            last_reviewed: 'Last',
            next_review: 'Next',
            status: 'Tila',
            overdue: 'Myöhässä',
            on_track: 'OK',
            days: 'days',
            horizon: 'Horizon',
            overdue_warning: 'Overdue',
            overdue_count: '{count}',
            no_cadences: '—',
            cadence_created: 'OK',
            cadence_updated: 'OK',
            aria_edit: '{name}',
        },
        remediation_plans: {
            title: 'Korjaussuunnitelmat',
            create_plan: 'Luo korjaussuunnitelma',
            gap_id: 'Puuteviite',
            select_gap: 'Valitse puute...',
            plan_title: 'Otsikko',
            description: 'Kuvaus',
            priority: 'Prioriteetti',
            priority_critical: 'Kriittinen',
            priority_high: 'Korkea',
            priority_medium: 'Keskitaso',
            priority_low: 'Matala',
            assigned_to: 'Vastuuhenkilö',
            select_user: 'Valitse käyttäjä...',
            target_date: 'Tavoitepäivä',
            framework_code: 'Viitekehys',
            select_framework: 'Valitse viitekehys...',
            gap_finding: 'Puutehavainto',
            action_items: 'Toimenpiteet',
            owner: 'Omistaja',
            deadline: 'Määräaika',
            planned: 'Suunniteltu',
            in_progress: 'Käynnissä',
            completed: 'Valmis',
            verified: 'Vahvistettu',
            no_plans_found: 'Korjaussuunnitelmia ei löytynyt.',
            evidence: 'Todistusaineisto',
            plan_created: 'Korjaussuunnitelma luotu',
            action_items_hint: 'Toimenpiteet (yksi per rivi)',
        },
        statuses: {
            identified: 'Tunnistettu',
            assigned: 'Määrätty',
            in_remediation: 'Kohdassa Remediation',
            resolved: 'Ratkaistu',
            accepted_risk: 'Hyväksytty riski',
            planned: 'Suunniteltu',
            in_progress: 'Meneillään',
            under_review: 'Tarkistuksessa',
            finalized: 'Viimeistelty',
            archived: 'Arkistoitu',
            draft: 'Luonnos',
            active: 'Aktiivinen',
            upcoming: 'Tuleva',
            collecting: 'Keräily',
            published: 'Julkaistu',
            review: 'Tarkista',
            approved: 'Hyväksytty',
            issued: 'Myönnetty',
            superseded: 'Korvattu',
            completed: 'Valmis',
            verified: 'Vahvistettu',
        },
        severities: {
            critical: 'Kriittinen',
            high: 'Korkea',
            medium: 'Keskikokoinen',
            low: 'Low',
        },
        priorities: {
            critical: 'Kriittinen',
            high: 'Korkea',
            medium: 'Keskikokoinen',
            low: 'Low',
        },
        gap_types: {
            missing: 'Puuttuu',
            expired: 'Vanhentunut',
            insufficient: 'Riittämätön',
        },
        document_types: {
            policy: 'politiikka',
            procedure: 'Menettely',
            standard: 'Vakio',
            guideline: 'Ohje',
            template: 'Malli (sisäinen)',
        },
        maturity_levels: {
            level_1: 'Taso 1 – Alkuperäinen',
            level_2: 'Taso 2 – Kehittyvät',
            level_3: 'Taso 3 — määritelty',
            level_4: 'Taso 4 – Hallittu',
            level_5: 'Taso 5 – Optimointi',
        },
        grades: {
            excellent: 'Erinomainen',
            good: 'Hyvä',
            fair: 'Reilu',
            poor: 'Köyhä',
            critical: 'Kriittinen',
            needs_improvement: 'Tarvitsee parannusta',
            not_assessed: 'Ei arvioitu',
        },
        modules: {
            data_mapping: 'Tietojen kartoitus',
            consent: 'Suostumus',
            governance: 'Hallinto',
            incidents: 'Tapahtumat',
            monitoring: 'Valvonta',
            classification: 'Luokittelu',
            vendor_risk: 'Toimittajariski',
            discovery: 'Löytö',
            deletion: 'Poistaminen',
        },
        cadences: {
            monthly: 'Kuukausittain',
            quarterly: 'Neljännesvuosittain',
            semi_annual: 'Puolivuosittain',
            annual: 'Vuosittainen',
        },
        section_labels: {
            opening: 'Avaaminen',
            key_metrics: 'Keskeiset mittarit',
            highlights: 'Kohokohdat',
            concerns: 'Huoli',
            recommendations: 'Suositukset',
            outlook: 'Outlook',
        },
        out_of_100: '/ 100',
        count_gaps: '{count} ero',
        count_gaps_plural: '{count} aukkoja',
        count_summaries: '{count} yhteenveto',
        count_summaries_plural: '{count} yhteenvedot',
        count_assessments: '{count} arviointi',
        count_assessments_plural: '{count} arvioita',
        count_models: '{count} malli',
        count_models_plural: '{count} mallia',
        count_periods: '{count} ajanjaksolla',
        count_periods_plural: '{count} jaksoa',
        count_metrics: '{count} mittari',
        count_metrics_plural: '{count} mittareita',
        unknown_user: 'Tuntematon käyttäjä',
        unknown_gap: 'Tuntematon väli',
        no_permission_title: 'Riittämättömät käyttöoikeudet',
        no_permission_message: 'Sinulla ei ole lupaa käyttää tätä sivua.',
        ac_vn_frameworks_title: 'VN-kehyksen kattavuus',
        ac_vn_frameworks_body: 'Kypsyysarvioinnit kattavat kaikki kuusi Vietnamin sääntelykehystä: PDPL, tietolaki, televiestintälaki, kyberturvallisuuslaki, AI-laki ja sähköisen kaupankäynnin laki – jokaisella on puitekohtaiset arviointikriteerit.',
        ac_controller_processor_title: 'Ohjain vs. prosessori',
        ac_controller_processor_body: 'Velvoitekartoitukset erottavat rekisterinpitäjän ja tietojen käsittelijän vastuut. Määritä roolit RACI-matriisin avulla varmistaaksesi, että jokaisella velvoitteella on selkeä omistaja riippumatta siitä, toimiiko organisaatio rekisterinpitäjänä vai käsittelijänä.',
        ac_versioned_evidence_title: 'Versioitu todisteet',
        ac_versioned_evidence_body: 'Jokainen tilasiirtymä (Luonnos, Tarkista, Hyväksytty, Myönnetty) luo aikaleimatun, versioidun tietueen, joka toimii tarkastettavissa olevana todisteena. Muutosloki tallentaa asiakirjan koko elinkaaren historian.',
        ac_remediation_gap_link_title: 'Liittyy arviointipuutteisiin',
        ac_remediation_gap_link_body: 'Jokainen kunnostussuunnitelma liittyy suoraan tunnistettuun arviointivajeeseen. Määritä omistaja ja aseta tavoitemääräaika seurataksesi ratkaisun etenemistä aukosta sulkemiseen.',
        ac_annual_attestation_title: 'Vuotuinen todistussykli',
        ac_annual_attestation_body: 'Raportointijaksot vastaavat Vietnamin tietolain vuotuista todistusvaatimusta. Käytä "Vuosittaista"-poljinta (365 päivän jakso) luodaksesi todistuksen mukaiset jaksot automaattisesti.',
        ac_evidence_per_obligation_title: 'Todistus puitevelvoitetta kohti',
        ac_evidence_per_obligation_body: 'Todisteiden puutteet osoittavat puuttuvat, vanhentuneet tai riittämättömät todisteet kustakin puitevelvollisuudesta. Suodata verkkotunnuksen mukaan nähdäksesi, mistä sääntelyvaatimuksista puuttuu todisteita.',
        ac_cross_framework_title: 'Kehysten välinen täydellisyys',
        ac_cross_framework_body: 'Tarkastusvalmius tarjoaa kehysten välisen täydellisyyden näkymän. Komponenttien pisteet heijastavat kattavuutta kaikissa kartoitetuissa viitekehyksessä ja korostavat alueita, jotka vaativat huomiota ennen auditointia.',
        ac_cadence_default_title: 'Oletusarvostelun poljinnopeus',
        ac_cadence_default_body: 'Oletusarvoinen tarkistustahti on 180 päivää. Myöhästyneet arvostelut merkitään automaattisesti. Säädä tiheyttä asiakirjaa kohden tarpeen mukaan – vaihtoehdot vaihtelevat 30–365 päivää.',
        ac_templates_internal_title: 'Mallin luokitus',
        ac_templates_internal_body: 'Mallit luokitellaan sisäisiksi asiakirjoiksi. Käytä "Malli" -dokumenttityyppiä uudelleenkäytettävien hallinta-artefaktien merkitsemiseen vain sisäiseen jakeluun.',
        // fix-sprint-20260822-2115 F1/F3 — this panel used to carry a module-local
        // `const COPY = { en: {…}, vi: {…} }` and pick a branch with
        // `locale.startsWith('vi') ? 'vi' : 'en'`. Both shapes are named in
        // I18N.md §2 and §3 as the root cause of 14 locales rendering English, and
        // the strings were registered in no bundle at all, so deepMerge had nothing
        // to fall back to. The four enum label maps below close F3: `status`,
        // `legal_review_status`, `evidence_state` and `answer_state` were rendering
        // raw slugs next to an already-labelled badge on the same row.
        handbook_assessment: {
            ask_forseti: 'Ask Forseti about this checklist',
            candidate_link: 'Link this record',
            candidate_linked: 'Already linked',
            candidate_linking: 'Linking…',
            candidate_no_date: 'No date recorded',
            candidate_open: 'Open record',
            evidence_kind_manual: 'Manual entry',
            evidence_kind_platform: 'Platform record',
            link_manual_fallback: 'If none of the above applies, record the evidence manually.',
            reason_module_not_readable: 'The regulatory pack names a module this platform build cannot read for evidence. Link the record manually instead.',
            reason_module_not_permitted: 'Your role cannot read the module that holds this evidence. Ask an administrator for access, or link the record manually.',
            reason_no_matching_record: 'No record of this kind exists in this organization yet.',
            reason_record_not_accepted: 'A record exists but is not in an accepted state. Complete or approve it, then resolve again.',
            source_form: 'Form',
            source_module: 'Module',
            source_role: 'Evidence role',
            sources_error: 'The platform sources for this question could not be loaded.',
            sources_heading: 'Evidence the platform can supply',
            sources_intro: 'The regulatory pack declares which of your own records can satisfy this question. These are the matching records this organization holds.',
            sources_loading: 'Loading platform sources…',
            unreconciled_question: 'This question is not on this assessment\u2019s own record and cannot be answered here: {question}',
            sources_toggle: 'Show platform sources',
            collapse_all: 'Collapse all questions',
            crosslink_action: 'Open handbook assessments',
            crosslink_body: 'A maturity assessment scores your programme against a maturity model. The official handbook checklist is a separate record: it collects evidence against a regulatory handbook and deliberately produces no score.',
            crosslink_title: 'Official handbook evidence checklist',
            expand_all: 'Expand all questions',
            group_progress: '{answered} of {total} answered',
            module_guide_summary: 'Complete an official regulatory handbook evidence checklist. Each question records an answer, an evidence state and the supporting records held in the platform. The checklist produces no compliance score — a human legal review closes it.',
            page_description: 'Official regulatory handbook evidence checklists. Each question records an answer, an evidence state, and the platform records that support it.',
            page_title: 'Handbook Assessments',
            question_fields: 'Question response',
            loading: 'Loading official handbook assessment',
            load_error: 'The official handbook assessment could not be loaded.',
            start: 'Start official handbook assessment',
            empty: 'No official handbook assessment has been started for this organization.',
            progress: 'questions answered',
            no_score: 'This evidence checklist does not calculate or claim a legal-compliance score. Human legal review is required.',
            save: 'Save answer',
            saving: 'Saving…',
            resolve: 'Resolve existing evidence',
            resolving: 'Resolving…',
            evidence: 'Evidence state',
            answer: 'Answer',
            notes: 'Notes and applicability rationale',
            linked: 'Linked evidence',
            no_evidence: 'No evidence is linked yet.',
            manual: 'Link manual evidence',
            description: 'Evidence description',
            source: 'Evidence source',
            owner: 'Evidence owner',
            date: 'Evidence date',
            url: 'Evidence URL',
            invalid_url: 'Enter a complete http:// or https:// URL.',
            link: 'Link evidence',
            linking: 'Linking…',
            remediate: 'Create remediation task',
            remediating: 'Creating task…',
            review_question: 'Mark question reviewed',
            reviewing: 'Reviewing…',
            submit: 'Submit all 30 questions for review',
            finalize: 'Complete legal review',
            return_revision: 'Return for revision',
            export: 'Export reviewed evidence report',
            exporting: 'Exporting…',
            status: 'Assessment status',
            legal_review: 'Legal review',
            saved: 'Answer saved.',
            resolved: 'Existing evidence resolution completed.',
            linked_done: 'Evidence linked for review.',
            remediation_done: 'Remediation task created.',
            review_done: 'Review state updated.',
            exported: 'Reviewed evidence report exported.',
            export_format: 'Format',
            export_language: 'Language',
            export_format_pdf: 'PDF',
            export_format_docx: 'DOCX',
            export_language_en: 'English',
            export_language_vi: 'Tiếng Việt',
            export_language_bilingual: 'English / Tiếng Việt',
            answer_state: {
                unanswered: 'Unanswered',
                yes: 'Yes',
                no: 'No',
                unknown: 'Unknown',
                not_applicable: 'Not applicable',
            },
            assessment_status: {
                in_progress: 'In progress',
                under_review: 'Under review',
                reviewed: 'Reviewed',
                archived: 'Archived',
            },
            legal_review_status: {
                pending: 'Pending',
                reviewed: 'Reviewed',
                revision_requested: 'Revision requested',
            },
            evidence_state: {
                resolved: 'Resolved',
                missing: 'Missing',
                stale: 'Stale',
                unsupported: 'Unsupported',
                manual_pending_review: 'Manual — pending review',
            },
        },
    },
    mcp_pending: {
        title: 'MCP — Odottavat kirjoitukset',
        subtitle: 'Ulkoiset Claude-asiakkaat ehdottavat kirjoituksia tässä. Hyväksy tai hylkää jokainen merkintä — Forseti suorittaa vasta järjestelmänvalvojan hyväksynnän jälkeen.',
        no_pending: 'Ei odottavia kirjoituksia.',
        filter_status_label: 'Filter by status',
        filter_all: 'All',
        no_results_for_status: 'No writes with this status.',
        col_tool: 'Työkalu',
        col_preview: 'Esikatselu',
        col_key: 'API-näppäintä',
        col_requested: 'Pyydetty',
        col_expires: 'Vanhenee',
        col_status: 'Tila',
        status_pending: 'Odottaa',
        status_approved: 'Hyväksytty',
        status_declined: 'Hylätty',
        status_expired: 'Vanhentunut',
        status_executed: 'Suoritettu',
        status_failed: 'Failed — nothing written',
        approve_failed: 'Approved, but the write did NOT happen — {tool} was not carried out ({code}). Nothing was created or changed.',
        approve_executed: '{tool} was carried out.',
        approve_label: 'Hyväksy',
        decline_label: 'Hylkää',
        refresh_label: 'Päivitä',
        error_generic: 'Odottavia kirjoituksia ei voitu ladata.',
        confirm_approve: 'Hyväksyminen suorittaa työkalun organisaatiossasi API key -haltijan oikeuksilla. Jatketaanko?',
        confirm_decline: 'Hylätäänkö tämä odottava kirjoitus? Tätä ei voi peruuttaa.',
        // Sprint 64.123.0 — approving a queued MCP write now mints the human
        // confirmation the tool manifest requires, so the surface has to say what
        // that approval is worth, and when it is self-attestation.
        confirmation_note: 'Approving records the human confirmation these tools require, and the write is then carried out with the API key holder’s permissions — not yours. Where you proposed the write yourself, your approval is recorded as self-approved and is not an independent review.',
        self_approval_badge: 'You proposed this write — approving it is self-attestation, not a second review.',
        confirm_approve_self: 'You proposed this write. Approving it records YOUR OWN confirmation — no second person will review it. The audit trail will show it as self-approved. Continue?',
        advisory_banner: 'Odottavat kirjoitukset vanhenevat 24 tunnin kuluttua luonnista. Tunnin välein suoritettava GC-pyyhkäisy hylkää vanhentuneet merkinnät automaattisesti.',
        consequential_label: 'Regulatorily consequential filing',
        consequential_target: 'Target record',
        consequential_authority: 'Authority',
        consequential_authority_unknown: 'Not declared by the installed template',
        consequential_prior: 'Prior',
        consequential_proposed: 'Proposed',
        consequential_empty: '(none)',
        consequential_unresolved: 'The target record could not be read when this was queued —',
        consequential_captured_at: 'Values read at',
        // fix-sprint-20261003-1200 F5 — the approve refusals, by server code.
        refusal_proposer_lacks_forseti: 'This write was not carried out: the role of the person who proposed it no longer allows Forseti, so it cannot run as them. Nothing was changed. Decline it to remove it from the queue.',
        refusal_proposer_inactive: 'This write was not carried out: the person who proposed it is no longer an active member of your organisation. Nothing was changed. Decline it to remove it from the queue.',
        refusal_key_inactive: 'This write was not carried out: the API key or connected app that proposed it may no longer make it. Nothing was changed. Decline it to remove it from the queue.',
        refusal_already_resolved: 'Someone has already resolved this write. Refresh to see its current status.',
        refusal_expired: 'This write has expired and can no longer be approved.',
        refusal_state_changed: 'This write changed status while you were approving it. Refresh to see its current status.',
        decline_dialog_title: 'Decline pending write',
        decline_reason_label: 'Reason for declining',
        decline_reason_hint: 'Required, up to 500 characters. It is recorded with your decision.',
        decline_submit: 'Decline write',
        decline_cancel: 'Keep pending',
        col_actions: 'Toiminnot',
    },
    audit_walkthrough: {
        title: 'Forseti — Auditointiläpikäynnin generaattori',
        subtitle: 'Luo jäsennelty läpikäynti auditoinnin aloitukseen: avauskysymys per kontrolli, odotettu näyttö, jatkokysymykset, mahdolliset puutteet.',
        audit_picker_label: 'Auditointi',
        audit_picker_hint: 'Valitse suunniteltu auditointi läpikäynnin luomiseksi.',
        run_label: 'Luo läpikäynti',
        running: 'Luodaan…',
        results_heading: 'Läpikäynti — {count} kontrolli(a)',
        tokens_label: '≈ {count} tokenia',
        no_controls: 'Ei kontrolleja laajuudessa. Lisää kontrollit auditointitietueeseen.',
        col_control: 'Kontrolli',
        col_question: 'Avauskysymys',
        col_evidence: 'Näyttö',
        col_gaps: 'Mahdolliset puutteet',
        expected_evidence_label: 'Odotettu näyttö',
        followup_probes_label: 'Jatkokysymykset',
        accept_label: 'Hyväksy läpikäynti',
        reject_label: 'Hylkää',
        export_label: 'Vie DOCX-muotoon',
        resolve_summary: 'Tulos: {status}',
        resolve_status_accepted: 'Hyväksytty',
        resolve_status_rejected: 'Hylätty',
        controls_range: 'Controls {from}–{to} of {total}',
        next_page_label: 'Generate next controls',
        error_generic: 'Läpikäyntiä ei voitu luoda. Yritä uudelleen tai tarkista auditoinnin tila.',
    },
    privilege_creep: {
        title: 'Forseti — Oikeuksien karttumisen havaitseminen',
        subtitle: 'Roolimääritysten määräaikainen tarkistus. Havainnot ovat ohjeellisia — Forseti ei koskaan peruuta oikeuksia automaattisesti.',
        run_label: 'Skannaa nyt',
        running: 'Skannataan…',
        findings_heading: '{count} havainto(a)',
        no_findings: 'Ei havaittua oikeuksien karttumista.',
        col_user: 'Käyttäjä',
        col_job_function: 'Työtehtävä',
        col_severity: 'Vakavuus',
        col_unexpected: 'Odottamattomat oikeudet',
        col_remediation: 'Ehdotettu korjaus',
        col_status: 'Tila',
        severity_critical: 'Kriittinen',
        severity_high: 'Korkea',
        severity_medium: 'Keskitaso',
        severity_low: 'Matala',
        status_open: 'Avoin',
        status_dismissed: 'Hylätty',
        status_remediated: 'Korjattu',
        dismiss_label: 'Hylkää',
        remediate_label: 'Merkitse korjatuksi',
        note_label: 'Muistiinpano (valinnainen)',
        error_generic: 'Oikeuksien karttumista ei voitu skannata. Yritä myöhemmin uudelleen.',
        advisory_banner: 'Nämä havainnot ovat ohjeellisia. Forseti ei koskaan peruuta oikeuksia automaattisesti — käy ne läpi roolin omistajan kanssa ennen toimenpiteitä.',
    },
    compliance_rules: {
        rule_mapping: {
            title: 'Automaattinen vaatimustenmukaisuussäännön kartoitus',
            subtitle: 'Forseti käy läpi asennettua säädöspakettia ja ehdottaa uusia tai päivitettyjä vaatimustenmukaisuussääntöjä, jotka perustuvat lähdeartikkeleihin.',
            pack_picker_label: 'Sääntelypaketti',
            pack_picker_hint: 'Valitse kartoitettava asennettu paketti.',
            run_label: 'Kartta paketista',
            running: 'Kartoitus…',
            results_heading: '{count} ehdotuksia',
            tokens_label: '≈ {count} tokeneita kuukausittain',
            no_suggestions: 'Ei ehdotuksia – pakkauksen sisältö saattaa jo olla täysin nykyisten sääntöjen mukainen.',
            kind_new: 'Uusi sääntö',
            kind_refresh: 'Already covered (review only)',
            kind_no_change: 'Muutosta ei vaadita',
            col_kind: 'Ystävällinen',
            col_title: 'Ehdotettu otsikko',
            col_existing: 'Olemassa oleva sääntö',
            col_source: 'lähde',
            proposed_body_label: 'Ehdotettu runko',
            source_quote_label: 'Lähdelainaus',
            accept_selected: 'Hyväksy valitut',
            reject_all: 'Hylkää kaikki',
            accept_all: 'Hyväksy kaikki',
            resolve_summary: 'Resolution: {status} · {created} draft rule(s) created · {skipped} left unchanged because a rule already covers the requirement',
            error_generic: 'Sääntökartoitusta ei voitu suorittaa. Yritä uudelleen tai tarkista pakkauskoodi.',
            write_permission_hint: 'Accepting suggestions needs permission to edit regulatory frameworks. You can still reject the run.',
            examined_label: 'Read passages {from}–{to} of the {total} that this pack search returns, ranked by one fixed search. A new run starts again at passage 1.',
            examined_remaining: '{remaining} passage(s) have not been read yet. Use Read next passages to continue from where this run stopped.',
            read_next_label: 'Read next passages',
            read_next_blocked: 'Accept or reject the suggestions above first; the next passages are read after this draft is resolved.',
            run_blocked: 'Accept or reject the suggestions above first; mapping again from this pack replaces the draft on screen.',
            drafts_hint: 'Accepted suggestions are saved as draft rules in the rule library. A draft rule is not active until someone activates it, and the product has no page for compliance rules yet. A passage that an existing rule already covers is shown for review only and creates nothing.',
        },
    },
    access_accountability: {
        nav: {
            session_dashboard: 'Istunnon kojelauta',
            access_log: 'Pääsyloki',
            justification_review: 'Perustelutarkistus',
            investigations: 'Tutkimukset',
            pattern_analysis: 'Kuvioanalyysi',
            anchoring_status: 'Ankkurointitila',
            configuration: 'Asetukset',
            access_policies: 'Käyttöoikeuskäytännöt',
            review_campaigns: 'Tarkistuskampanjat',
            evidence_packages: 'Todistuspaketit',
        },
        session_dashboard: {
            title: 'Istunnon kojelauta',
            loading: 'Ladataan kojelaudan tietoja...',
            total_sessions: 'Istunnot yhteensä',
            active_sessions: 'Aktiiviset istunnot',
            avg_duration: 'Keskim. kesto',
            peak_day_sessions: 'Vilkkain päivä',
            auth_method_breakdown: 'Jakauma todennustavan mukaan',
            geo_country_breakdown: 'Jakauma maittain',
            recent_session_events: 'Viimeaikaiset istuntotapahtumat',
            user_id: 'Käyttäjätunnus',
            session_id: 'Istuntotunniste',
            ip_address: 'IP-osoite',
            auth_method: 'Todennustapa',
            started_at: 'Aloitettu',
            event_type: 'Tapahtumatyyppi',
            all_event_types: 'Kaikki tapahtumatyypit',
            filter_by_user: 'Suodata käyttäjän mukaan',
            no_active_sessions: 'Ei aktiivisia istuntoja',
            no_session_events: 'Istuntotapahtumia ei löytynyt',
            revoke: 'Peruuta',
            revoke_session: 'Peruuta istunto',
            aria_revoke_session: 'Peruuta istunto {user}',
            revoke_reason: 'Syy',
            revoke_placeholder: 'Anna syy tämän istunnon peruuttamiselle...',
            session_label: 'Istunto:',
            load_failed: 'Istunnon kojelaudan lataaminen epäonnistui. Yritä uudelleen.',
            events_load_failed: 'Istuntotapahtumien lataaminen epäonnistui. Yritä uudelleen.',
            revoke_failed: 'Istunnon peruuttaminen epäonnistui. Yritä uudelleen.',
            session_events_loaded: '{n} istuntotapahtumaa ladattu',
            event_types: {
                session_started: 'Istunto aloitettu',
                session_refreshed: 'Istunto päivitetty',
                session_expired: 'Istunto vanhentunut',
                session_revoked: 'Istunto peruutettu',
                session_terminated: 'Istunto lopetettu',
            },
        },
        access_log: {
            title: 'Pääsyloki',
            user_id: 'Käyttäjätunnus',
            resource: 'Resurssi',
            action: 'Toiminto',
            ip_address: 'IP Osoite',
            timestamp: 'Aikaleima',
            no_logs: 'Ei pääsy loki entries found. löytynyt',
            filter_by_user: 'Suodata by Käyttäjä Tunniste...',
            load_failed: 'Epäonnistui: load pääsy loki. Yritä uudelleen.',
            export_success: 'Vie initiated successfully',
            export_failed: 'Epäonnistui: export pääsy loki. Yritä uudelleen.',
            record_count: 'tietue',
            record_count_plural: 'tietueet',
            unclassified: 'Luokittelematon',
            classification_missing: 'Luokitus puuttuu',
            classification_level: 'Luokitustaso',
            response_code: 'Vastauskoodi',
            access_surface: 'Käyttökanava',
            api_key: 'API-avain',
            classification_levels: {
                public: 'Julkinen',
                internal: 'Sisäinen',
                confidential: 'Luottamuksellinen',
                restricted: 'Rajoitettu',
                unclassified: 'Luokittelematon',
            },
        },
        justification_review: {
            title: 'Perustelutarkistus',
            user_id: 'Käyttäjätunnus',
            resource: 'Resurssi',
            justification: 'Perustelu',
            submitted_at: 'Lähetetty',
            reviewer: 'Arvioija',
            no_reviews: 'Ei perustelu katselmointi found. löytynyt',
            load_failed: 'Epäonnistui: load perustelut. Yritä uudelleen.',
            review_failed: 'Epäonnistui: katselmointi perustelu. Yritä uudelleen.',
            decline_failed: 'Epäonnistui: decline pääsy. Yritä uudelleen.',
            justification_count: 'perustelu',
            justification_count_plural: 'perustelut',
            reviewed: 'Katselmoitu',
            concerning: 'Huolestuttava',
            escalated: 'Eskaloitu',
            link_investigation_placeholder: 'Linkitä to an tutkimus (optional)',
            review_queue: 'Katselmointia Jono',
            access_id_label: 'Käyttötunnus',
            access_id_placeholder: 'Käyttölokin merkinnän tunnus',
            resource_type_label: 'Resurssityyppi',
            resource_type_placeholder: 'esim. document, database',
            resource_id_label: 'Resurssin tunnus',
            resource_id_placeholder: 'Resurssin tunniste',
            reason_placeholder: 'Syy tämän resurssin käyttämiseen...',
            create_failed: 'create justification epäonnistui',
            unclassified: 'Luokittelematon',
            review_statuses: {
                pending: 'Odottaa',
                reviewed: 'Arvosteltu',
                concerning: 'Mitä tulee',
                escalated: 'Eskaloitunut',
            },
            reason_codes: {
                business_need: 'Liiketoiminnan tarve',
                legal_obligation: 'Laillinen velvoite',
                data_subject_request: 'Rekisteröidyn pyyntö',
                audit_requirement: 'Tarkastusvaatimus',
                incident_response: 'Tapahtumavastaus',
                research: 'Tutkimus',
                administrative: 'Hallinnollinen',
                other: 'muu',
            },
            classification_level: 'Luokitustaso',
            review_decision: 'Review Decision',
        },
        investigations: {
            title: 'Tutkimus Workspace',
            new: 'Uusi Tutkimus',
            subject: 'Kohde',
            investigator: 'Tutkija',
            opened_at: 'Avattu At',
            findings: 'Havainnot',
            no_investigations: 'Ei tutkimukset found. löytynyt',
            load_failed: 'Epäonnistui: load tutkimukset. Yritä uudelleen.',
            create_failed: 'Epäonnistui: create tutkimus. Yritä uudelleen.',
            timeline_add_failed: 'Epäonnistui: add timeline merkintä. Yritä uudelleen.',
            link_access_failed: 'Epäonnistui: link pääsy Tunniste. Yritä uudelleen.',
            link_justification_failed: 'Epäonnistui: link perustelu Tunniste. Yritä uudelleen.',
            close_failed: 'Epäonnistui: close tutkimus. Yritä uudelleen.',
            export_success: 'Vie initiated successfully',
            export_failed: 'Epäonnistui: export tutkimus. Yritä uudelleen.',
            refresh_failed: 'Epäonnistui: refresh tutkimus detail. Yritä uudelleen.',
            update_failed: 'Tutkinnan päivittäminen epäonnistui',
            investigation_count: 'tutkimus',
            investigation_count_plural: 'tutkimukset',
            title_placeholder: 'Tutkimus otsikko',
            description_placeholder: 'Detailed kuvaus of the tutkimus...',
            assignee_placeholder: 'Vastuuhenkilön käyttäjätunnus (valinnainen)',
            timeline_action_placeholder: 'esim. Tarkistetut pääsylokit',
            timeline_note_placeholder: 'Valinnainen note',
            access_ids_placeholder: 'pääsytunnus-1, pääsytunnus-2',
            justification_ids_placeholder: 'perustelutunnus-1, perustelutunnus-2',
            findings_placeholder: 'Summarize the tutkimus findings...',
            by_actor: 'by',
            close_investigation: 'Sulje Tutkimus',
            add_timeline_failed: 'Epäonnistui: add timeline merkintä. Yritä uudelleen.',
            fetch_failed: 'Epäonnistui: fetch tutkimukset. Yritä uudelleen.',
            change_status: 'Muuta tutkimuksen tilaa',
            details: 'Tutkimuksen tiedot',
            link_evidence: 'Linkitä todiste',
            link_justifications: 'Linkitä perustelut',
            access_ids_label: 'Käyttötunnukset',
            justification_ids_label: 'Perustelutunnukset',
            access_ids_link_placeholder: 'pääsytunnus-1, pääsytunnus-2',
            justification_ids_link_placeholder: 'perustelutunnus-1, perustelutunnus-2',
            investigations_loaded: '{n} investigations ladattu',
            status_changed_to: 'Tila muutettu: {status}',
            add_timeline_entry: 'Lisää aikajanan merkintä',
            add_access_ids: 'Lisää käyttötunnukset',
            add_justification_ids: 'Lisää perustelutunnukset',
            investigation_types: {
                anomaly_review: 'Anomaly Review',
                complaint_driven: 'Valitus ajettu',
                scheduled_review: 'Aikataulutettu tarkistus',
                whistleblower_report: 'Whistleblower-raportti',
            },
            severity_labels: {
                low: 'Matala',
                medium: 'Keskikokoinen',
                high: 'Korkea',
                critical: 'Kriittinen',
            },
            status_labels: {
                open: 'Avaa',
                in_progress: 'Meneillään',
                pending_review: 'Odottaa tarkistusta',
                closed_substantiated: 'Suljettu (perusteltu)',
                closed_unsubstantiated: 'Suljettu (ei perusteltu)',
                closed_inconclusive: 'Suljettu (epäselvä)',
            },
            disposition_labels: {
                substantiated: 'Perusteltu',
                unsubstantiated: 'Perusteeton',
                inconclusive: 'Epäselvä',
            },
        },
        pattern_analysis: {
            accesses_count: '{n} accesses',
            title: 'Kuvioanalyysi',
            resource_type_header: 'Resource type',
            resource_id_header: 'Resource ID',
            loading: 'Ladataan pattern tiedot...',
            anomalies_detected: 'Havaitut poikkeamat',
            patterns: 'Mallit',
            by_user: 'By Käyttäjä',
            by_resource: 'Resurssin mukaan',
            time_range: 'Aika Alue',
            load_failed: 'Epäonnistui: analyze patterns. Yritä uudelleen.',
            top_resources: 'Top 10',
            access_by_hour: 'Pääsy vuorokaudenajan mukaan',
            access_by_day: 'Pääsy viikonpäivän mukaan',
            fetch_failed: 'Epäonnistui: fetch pattern analysis. Yritä uudelleen.',
            analysis_loaded: 'Kuvioanalyysi ladattu käyttäjälle {user}, {n} istuntoa löytyi',
            date_format_placeholder: 'PP/KK/VVVV',
            access_by_hour_heading: 'Käyttö vuorokaudenajoin',
            access_by_day_heading: 'Käyttö viikonpäivittäin',
            by_classification: 'Luokitustason mukaan',
            top_accessed_resources: 'Eniten käytetyt resurssit',
            user_access_summary: 'Käyttöoikeuksien yhteenveto',
            days: {
                sunday: 'Sunnuntai',
                monday: 'Maanantai',
                tuesday: 'Tiistai',
                wednesday: 'Keskiviikko',
                thursday: 'Torstai',
                friday: 'Perjantai',
                saturday: 'Lauantai',
            },
            accesses_in_period: 'Accesses in period',
            sessions_label: 'Sessions',
            recent_accesses: 'Recent accesses in the period',
            recent_accesses_desc: "This user's most recent access log entries in the analysed period, newest first (up to {n}).",
        },
        anchoring_status: {
            kpi_summary_label: 'Anchoring status summary',
            title: 'Ankkurointitila',
            loading: 'Ladataan anchoring tiedot...',
            anchored: 'Ankkuroitu',
            pending: 'Odottaa',
            failed: 'Epäonnistunut',
            last_anchor: 'Last Ankkuri',
            no_records: 'Ei anchoring tietueet found. löytynyt',
            status_load_failed: 'Epäonnistui: load anchoring tila. Yritä uudelleen.',
            proofs_load_failed: 'Epäonnistui: load pääsy todisteet. Yritä uudelleen.',
            verify_failed: 'Epäonnistui: verify todiste chain. Yritä uudelleen.',
            anchor_failed: 'Epäonnistui: anchor pääsy todiste. Yritä uudelleen.',
            batch_anchor_failed: 'Epäonnistui: batch anchor pääsy todisteet. Yritä uudelleen.',
            proof_count: 'todiste',
            proof_count_plural: 'todisteet',
            anchor_single: 'Ankkuroi yksittäinen',
            anchor_batch: 'Ankkurierä',
            access_id_placeholder: 'Syötä pääsy tunniste to anchor',
            batch_ids_placeholder: 'pääsytunnus-1, pääsytunnus-2, pääsytunnus-3',
            verify_chain: 'Vahvista ketju',
            chain_breaks: 'Ketju Breaks',
            total_checked: 'Checked yhteensä',
            anchor_placeholder: 'Syötä pääsy tunniste to anchor',
            fetch_failed: 'Epäonnistui: fetch anchoring tila. Yritä uudelleen.',
            select_to_anchor: 'Select access log entry to anchor',
            select_to_anchor_batch: 'Select entries to anchor',
            no_unanchored_entries: 'No unanchored access-log entries found.',
            loading_unanchored: 'Loading access log entries...',
            fetch_unanchored_failed: 'Failed to load unanchored access log entries.',
            fetch_proofs_failed: 'Epäonnistui: fetch todisteet. Yritä uudelleen.',
            access_id: 'Käyttötunnus',
            chain_sequence: 'Ketjujärjestys',
            proof_hash_label: 'Todisteen tiiviste',
            batch_id: 'Erätunnus',
            proofs_loaded: '{n} access proofs ladattu',
            proof_version_label: 'Version',
            proof_version_value: 'Version {n}',
            verify_valid_summary: 'The chain is intact. Every link and hash recomputes.',
            verify_invalid_summary: 'The chain does not verify. The proofs listed below no longer match what was anchored.',
            verify_proofs_v1: 'Version 1 proofs',
            verify_proofs_v2: 'Version 2 proofs',
            verify_content_verified: 'Log rows matching their proof',
            verify_content_destroyed: 'Content destroyed after retention',
            verify_content_destroyed_note: 'The chain still verifies for these proofs. The access-log row each one anchors has passed its retention date and been destroyed, so its content can no longer be proven.',
            verify_content_removed_sample: 'Demo log rows removed when sample data was cleared',
            verify_content_removed_sample_note: 'The chain still verifies for these proofs. Each one anchors a demo access-log row that was removed when the sample data was cleared; the removal is recorded by the platform. These are not retention destructions.',
            verify_v1_note: 'Version 1 proofs protect the order of the chain and the fact that an access event existed. They do not cover the content of the log row.',
            verify_v2_note: 'A version 2 proof also covers the content of its log row, for as long as the row exists.',
            verify_breaks_heading: 'Proofs that failed',
            verify_break_types: {
                field_mismatch: 'Log row changed after it was anchored',
                hash_mismatch: 'Proof hash does not recompute',
                chain_link_mismatch: 'Link to the previous proof is broken',
                unsupported_proof_version: 'Proof version is not supported',
                content_missing: 'Log row missing — the platform has no record of removing it',
            },
            anchor_statuses: {
                chained: 'Chained',
                pending: 'Odottaa',
                confirmed: 'Vahvistettu',
                failed: 'Epäonnistui',
            },
            anchor_types: {
                sha256_chain: 'SHA-256 Ketju',
                blockchain_anchor: 'Blockchain-ankkuri',
                zkp_stub: 'ZKP Stub',
            },
        },
        configuration: {
            title: 'Asetukset',
            session_settings: 'Istunto Asetukset',
            log_retention: 'Loki Säilytys (päivää)',
            anomaly_threshold: 'Poikkeamakynnys',
            anchoring_enabled: 'Ankkurointi käytössä',
            save_config: 'Tallenna Konfiguraatio',
            load_failed: 'Epäonnistui: load konfiguraatio. Yritä uudelleen.',
            save_success: 'Konfiguraatio saved successfully',
            save_failed: 'Epäonnistui: save konfiguraatio. Yritä uudelleen.',
            justification_rules: 'Perustelu Säännöt',
            reason_codes: 'Reason Koodi',
            anchoring_settings: 'Ankkurointiasetukset',
            anchoring_method: 'Ankkurointimenetelmä',
            anchoring_frequency: 'Ankkurointitiheys',
            batch_interval: 'Erä Aikaväli (minutes)',
            eligibility_criteria: 'Kelpoisuus Kriteerit',
            add_criterion_placeholder: 'Lisää kelpoisuuskriteeri...',
            retention_settings: 'Säilytys Asetukset',
            standard_retention: 'Standardi Säilytys (päivää)',
            sensitive_retention: 'Sensitive Säilytys (päivää)',
            working_hours: 'Työtunnit',
            work_start: 'Aloita Aika',
            work_end: 'End Aika',
            timezone: 'Aikavyöhyke',
            timezone_placeholder: 'esim. Asia/Ho_Chi_Minh',
            working_days: 'Työpäivät',
            concurrent_sessions: 'Concurrent Istunto Raja',
            concurrent_threshold: 'Concurrent Istunto Kynnysarvo',
            classification_level: 'Luokittelu Taso',
            prompt_mode: 'Kehote-tila',
            grace_period: 'Grace Kausi (s)',
            code_placeholder: 'koodi',
            is_custom: 'Mukautettu',
            label_placeholder: 'Tunniste',
            description_placeholder: 'Kuvaus',
            custom: 'Mukautettu',
            fetch_failed: 'Epäonnistui: fetch konfiguraatio. Yritä uudelleen.',
            classification_levels: {
                public: 'Julkinen',
                internal: 'Sisäinen',
                confidential: 'Luottamuksellinen',
                restricted: 'Rajoitettu',
                unclassified: 'Luokittelematon',
            },
            prompt_modes: {
                reason_only: 'Vain syy',
                reason_text: 'Syy + teksti',
                reason_text_supervisor: 'Syy + teksti + valvoja',
            },
            anchoring_methods: {
                sha256_chain: 'SHA-256 Ketju',
                blockchain_anchor: 'Blockchain-ankkuri',
                zkp_stub: 'ZKP Stub',
            },
            anchoring_frequencies: {
                realtime: 'Reaaliaikainen',
                batched: 'Erässä',
                on_demand: 'On Demand',
            },
            retention_position: {
                title: 'Log Retention Position',
                description: 'What the retention sweep would destroy, and what it would keep. Reading this changes nothing.',
                as_of: 'As of',
                window_in_force: 'Window in force',
                days: '{n} days',
                swept: 'Past its window',
                retained_held: 'Kept — legal hold or retention rule',
                retained_anchored: 'Kept — anchored by an older proof',
                retained_investigation: 'Kept — linked to an open access investigation',
                unstamped: 'No retention date recorded',
                skipped_swept: 'Already accounted for',
                eligible_sample: 'Oldest entries past their window',
                resource: 'Resource',
                expires_at: 'Window ended',
                run_sweep: 'Run retention sweep',
                running: 'Running...',
                sweep_success: 'Retention sweep complete: {swept} destroyed, {retained} kept.',
                sweep_failed: 'The retention sweep could not be run. Please try again.',
                load_failed: 'The retention position could not be read. Please try again.',
                reload: 'Reload',
                empty: 'No access-log entry is past its retention window.',
                unstamped_note: 'An entry with no retention date is never destroyed. It is counted above so the number is visible rather than assumed.',
                anchored_note: 'An entry anchored by a version 1 proof (made before proofs covered content) is kept past its window, because destroying it would leave that proof pointing at nothing. An entry anchored by a newer proof is destroyed on its date and the chain still verifies.',
            },
        },
        access_policies: {
            title: 'Käyttöoikeuskäytännöt',
            create_policy: 'Luo käyttöoikeuskäytäntö',
            policy_name: 'Käytännön nimi',
            framework_code: 'Viitekehys',
            select_framework: 'Valitse viitekehys...',
            review_frequency: 'Tarkistustiheys',
            scope: 'Laajuus',
            no_policies_found: 'Käyttöoikeuskäytäntöjä ei löytynyt.',
            policy_created: 'Käyttöoikeuskäytäntö luotu',
            frequency_labels: {
                monthly: 'Kuukausittainen',
                quarterly: 'Neljännesvuosittainen',
                semi_annual: 'Puolivuosittainen',
                annual: 'Vuosittainen',
            },
        },
        review_campaigns: {
            title: 'Käyttöoikeustarkistuskampanjat',
            create_campaign: 'Luo tarkistuskampanja',
            reviewer: 'Tarkistaja',
            due_date: 'Eräpäivä',
            certify: 'Vahvista',
            no_campaigns_found: 'Käyttöoikeustarkistuskampanjoita ei löytynyt.',
            status_open: 'Avoin',
            status_in_review: 'Tarkistuksessa',
            status_completed: 'Valmis',
            campaign_created: 'Tarkistuskampanja luotu',
            review_certified: 'Käyttöoikeuskatsaus sertifioitu',
            certify_campaign: 'Sertifioi kampanja {id}',
            policy_id: 'Käytännön tunnus',
            select_policy: 'Valitse käytäntö...',
            reviewer_ids_label: 'Tarkastajat',
            select_reviewers: 'Valitse tarkastajat...',
            status_labels: {
                open: 'Avoin',
                in_review: 'Tarkistettavana',
                in_progress: 'Käynnissä',
                cancelled: 'Peruutettu',
                completed: 'Valmis',
            },
        },
        evidence_packages: {
            title: 'Todistuspaketit',
            generate_package: 'Luo todistuspaketti',
            export_type: 'Vientityyppi',
            package_type: 'Paketin tyyppi',
            framework_scope: 'Viitekehyksen laajuus',
            period: 'Ajanjakso',
            generated_by: 'Luonut',
            generated_at: 'Luotu',
            no_packages_found: 'Todistuspaketteja ei löytynyt.',
            package_generated: 'Todistepaketti luotu',
            export_type_full: 'Täysi vienti',
            export_type_summary: 'Yhteenveto',
            export_type_certification_only: 'Vain sertifiointi',
            package_type_access_review: 'Käyttöoikeuskatsaus',
            package_type_investigation: 'Tutkimus',
            package_type_certification: 'Sertifiointi',
            package_type_authority_request: 'Viranomaispyyntö',
            period_start: 'Jakson alku',
            period_end: 'Jakson loppu',
            select_frameworks: 'Valitse kehykset...',
            completeness_col: 'Kattavuus',
            status_col: 'Tila',
            generated_by_col: 'Luonut',
        },
        evidence_package_detail: {
            title: 'Todistepaketin tiedot',
            not_found: 'Todistepakettia ei löytynyt.',
            package_metadata: 'Paketin metatiedot',
            status: 'Tila',
            item_count: 'Kohteiden määrä',
            disposition: 'Ratkaisu',
            closed_at: 'Suljettu',
            completeness_dashboard: 'Kattavuusnäkymä',
            no_completeness_data: 'Kattavuustietoja ei saatavilla.',
            controls_covered: 'kontrollia katettu',
            missing: 'Puuttuu',
            sign_off_tracker: 'Hyväksynnän seuranta',
            stage_framework_owner: 'Kehyksen omistaja',
            stage_legal_compliance: 'Laki ja vaatimustenmukaisuus',
            stage_internal_audit: 'Sisäinen tarkastus',
            approved: 'Hyväksytty',
            returned: 'Palautettu',
            pending: 'Odottaa',
            approve: 'Hyväksy',
            return_btn: 'Palauta',
            sign_off_submitted: 'Hyväksyntä lähetetty',
            artifacts: 'Artefaktit',
            no_artifacts: 'Artefakteja ei löytynyt.',
            template_id: 'Mallin tunnus',
            description: 'Kuvaus',
            classification: 'Luokitus',
            evidence_type: 'Todistetyyppi',
            framework_section: 'Kehysosio',
            history: 'Hyväksyntähistoria',
            expand: 'Laajenna',
            collapse: 'Tiivistä',
            action: 'Toiminto',
            actor: 'Tekijä',
            timestamp: 'Aikaleima',
            comment: 'Kommentti',
            close_case: 'Sulje tapaus',
            confirm_close: 'Vahvista sulkeminen',
            closure_notes: 'Sulkemismuistiinpanot',
            disposition_compliant: 'Vaatimustenmukainen',
            disposition_non_compliant: 'Vaatimustenvastainen',
            disposition_partial: 'Osittainen',
            case_closed: 'Todistepaketti suljettu',
            export_package: 'Vie paketti',
            export_complete: 'Todistepaketti viety',
            completeness_explainer: "A control is one of the organisation's active access policies for the framework. It is covered when at least one access-review certification was recorded in the package period under a campaign of that policy.",
            completeness_not_measured: 'No active access policy is declared for this framework, so there is no control to measure coverage against.',
            artifacts_explainer: "Each artifact is an access-review certification recorded in the package period under a campaign of one of the framework's access policies.",
            policy: 'Access policy',
            decision: 'Decision',
            certified_at: 'Certified at',
            certified_by: 'Certified by',
            decision_approved: 'Access approved',
            decision_revoked: 'Access revoked',
            evidence_type_access_certification: 'Access certification',
        },
    },
    consent_properties: {
        nav: {
            dashboard: 'Kojelauta',
            properties: 'Ominaisuudet',
            groups: 'Ryhmät',
            bindings: 'Sidokset',
            change_requests: 'Muutospyynnöt',
            comparison: 'Vertailu',
            inconsistencies: 'Epäjohdonmukaisuudet',
            snapshot_schedules: 'Tilannevedosaikataulut',
            golden_templates: 'Kultaiset mallipohjat',
            evidence_packs: 'Todistuspaketit',
            audit: 'Tarkastus',
        },
        select_property: 'Valitse ominaisuus…',
        select_policy: 'Valitse käytäntö…',
        select_properties: 'Valitse ominaisuudet…',
        select_target_properties: 'Valitse kohdeominaisuudet…',
        select_owner: 'Valitse omistaja…',
        select_technical_contact: 'Valitse tekninen yhteyshenkilö…',
        property_statuses: {
            active: 'Aktiivinen',
            inactive: 'Ei aktiivinen',
            pending: 'Odottaa',
            decommissioned: 'Käytöstä poistettu',
        },
        dashboard: {
            title: 'Ominaisuuksien yhteiskojelauta',
            loading: 'Ladataan kojelauta tiedot...',
            total_properties: 'Ominaisuudet yhteensä',
            active_properties: 'Aktiiviset ominaisuudet',
            snapshot_coverage: 'Tilannevedoksen kattavuus',
            properties_covered: 'ominaisuutta katettu',
            total_bindings: 'Sidoksia yhteensä',
            deployed_bindings: 'Käyttöönotettu',
            pending_bindings: 'Odottaa käyttöönottoa',
            deployment_progress: 'Käyttöönoton edistyminen',
            on_target: 'Tavoitteessa',
            below_target: 'Alle tavoitteen',
            target_label: 'Tavoite',
            total_groups: 'Ryhmät yhteensä',
            pending_changes: 'Odottaa Changes',
            inconsistencies: 'Epäjohdonmukaisuudet',
            recent_activity: 'Viimeaikainen Aktiviteetti',
            no_recent_activity: 'Ei viimeaikaista toimintaa.',
            fetch_error: 'Epäonnistui: load kojelauta. Yritä uudelleen.',
        },
        properties: {
            title: 'Ominaisuusrekisteri',
            new: 'Uusi Ominaisuus',
            edit: 'Muokkaa Ominaisuus',
            property_name: 'Nimi',
            property_type: 'Tyyppi',
            identifier: 'Tunniste',
            environment: 'Ympäristö',
            owner_id: 'Omistaja Tunniste',
            technical_contact_id: 'Technical Contact Tunniste',
            decommission: 'Poista käytöstä',
            confirm_decommission: 'Haluatko varmasti decommission this property?',
            no_properties: 'Ei properties found. löytynyt',
            all_environments: 'Kaikki ympäristöt',
            all_types: 'Kaikki tyypit',
            cmp_instance_id: 'CMP Instanssitunnus',
            deployment_date: 'Käyttöönottopäivämäärä',
            identifier_placeholder: 'esim. www.example.com tai com.example.app',
            owner_name: 'Omistaja',
            technical_contact_name: 'Tekninen yhteyshenkilö',
            sensitive_data_categories: 'Arkaluonteiset tietokategoriat',
            sensitive_data_categories_placeholder: 'Valitse arkaluontoisten tietojen luokat',
            requires_enhanced_review: 'Edellyttää tehostettua tarkistusta',
            sensitive_review_status: 'Arkaluonteisten tietojen tarkistus',
            sensitive_review_pending: 'Odottaa tarkistusta',
            sensitive_review_approved: 'Hyväksytty',
            sensitive_review_rejected: 'Hylätty',
            sensitive_review_submit: 'Lähetä arvostelu',
            sensitive_review_approve: 'Hyväksy',
            sensitive_review_reject: 'Hylkää',
            sensitive_review_notes: 'Tarkista muistiinpanot',
            sensitive_review_notes_placeholder: 'Kirjoita arvosteluhuomautukset...',
            record_withdrawal: 'Record Nosto',
            withdrawal_subject_id: 'Aiheen tunnus',
            withdrawal_consent_id: 'Suostumustunnus',
            withdrawal_reason: 'Syy',
            withdrawal_downstream_systems: 'Downstream Systems',
            withdrawal_downstream_placeholder: 'järjestelmä-1, järjestelmä-2',
            withdrawal_success: 'Kotiuttaminen onnistui.',
            withdrawal_error: 'Peruuttamisen levittäminen epäonnistui.',
            withdrawal_data_subject_id: 'Rekisteröidyn tunnus',
            withdrawal_data_subject_id_placeholder: 'esim. usr_abc123',
            withdrawal_consent_record_id: 'Suostumustietueen tunnus',
            withdrawal_consent_record_id_placeholder: 'esim. consent_xyz789',
            withdrawal_purposes: 'Peruutetut tarkoitukset',
            withdrawal_purposes_placeholder: 'Valitse suostumuksen peruuttamisen syyt...',
            withdrawal_withdrawn_at: 'Nostopäivä',
            withdrawal_tasks_created: 'Tehtävät luotu',
            withdrawal_affected_bindings: 'Vaikuttavat sidokset',
            withdrawal_affected_activities: 'Käsittelytoiminnot, joita tämä koskee',
            withdrawal_history: 'Kotiutushistoria',
        },
        groups: {
            title: 'Ominaisuusryhmät',
            new: 'Uusi Ryhmä',
            group_name: 'Ryhmä Nimi',
            member_count: 'Jäsenet',
            no_groups: 'Ei property groups found. löytynyt',
            policies: 'Käytännöt',
            confirm_delete: 'Haluatko varmasti poistaa tämän ryhmän?',
        },
        bindings: {
            title: 'Sidosmatriisi',
            property: 'Ominaisuus',
            purpose: 'Tarkoitus',
            legal_basis: 'Oikeusperuste',
            bound: 'Sidottu',
            no_bindings: 'Ei bindings found. löytynyt',
            bind_policy: 'Sidontakäytäntö',
            bulk_bind: 'Bulk Bind',
            bind_policy_to_property: 'Sido käytäntö omaisuuteen',
            bulk_bind_policy: 'Joukkosidontakäytäntö',
            unbound: 'Ei sitoumuksia',
            policy_id: 'Käytäntötunnus',
            version_placeholder: 'esim. 1.0.0',
            property_ids_placeholder: 'prop-id-1, prop-id-2, prop-id-3',
            binding_name: 'Sidonta',
            property_name: 'Ominaisuuden nimi',
            policy_name: 'Käytännön nimi',
            bindings_list: 'Sidontalistaus',
            binding_actions: {
                start_deploy: 'Käynnistä käyttöönotto',
                mark_deployed: 'Merkitse käyttöönotetuksi',
                rollback: 'Peruuta',
            },
        },
        change_requests: {
            title: 'Muutospyyntöprosessi',
            new: 'Uusi Change Pyyntö',
            requester: 'Pyytäjä',
            change_type: 'Change Tyyppi',
            property: 'Ominaisuus',
            submitted_at: 'Lähetetty',
            no_requests: 'Ei change requests found. löytynyt',
            fetch_pipeline_error: 'Epäonnistui: fetch pipeline. Yritä uudelleen.',
            fetch_list_error: 'Epäonnistui: fetch change requests. Yritä uudelleen.',
            create_error: 'Epäonnistui: create change request. Yritä uudelleen.',
            action_error: 'Epäonnistui: perform toiminto. Yritä uudelleen.',
            approver_name: 'Hyväksyjä',
            placeholder_target_props: 'prop-id-1, prop-id-2',
            view_all: 'Kaikki',
            view_my_properties: 'Omat Ominaisuudet',
            stage_requests: '{stage}-pyynnöt',
            policy_required: "Choose the policy this change applies to. A policy update, a new binding or a binding removal changes that policy's bindings.",
        },
        comparison: {
            title: 'Vertailutyötila',
            select_properties: 'Valitse ominaisuudet to compare',
            compare: 'Vertaa',
            no_comparison: 'Valitse at least two properties to compare.',
            fetch_error: 'Epäonnistui: fetch comparison raportti. Yritä uudelleen.',
            run_error: 'Epäonnistui: run comparison. Yritä uudelleen.',
            save_notes_error: 'Epäonnistui: save notes. Yritä uudelleen.',
            scan_complete: 'Inconsistency skannaus valmis successfully.',
            scan_error: 'Epäonnistui: run inconsistency skannaus. Yritä uudelleen.',
            min_two_properties: 'Please provide at least 2 property Tunniste for comparison.',
            view_inconsistencies: 'Näytä epäjohdonmukaisuudet',
            click_to_select: 'Valitse kaksi tai useampia ominaisuuksia napsauttamalla',
            no_properties_available: 'Ei ominaisuuksia saatavilla',
            no_matching_properties: 'Ei vastaavia ominaisuuksia',
            select_at_least_two_props: 'Valitse vähintään kaksi ominaisuutta ylläolevasta pudotusvalikosta.',
            no_differences_found: 'Ei eroja löydetty valittujen ominaisuuksien välillä.',
            found_inconsistencies: 'Löydettiin {count} epäjohdonmukaisuutta {properties} ominaisuuden ja {bindings} sidoksen välillä.',
        },
        inconsistencies: {
            title: 'Epäjohdonmukaisuuslista',
            property: 'Ominaisuus',
            field: 'Kenttä',
            expected: 'Odotettu',
            actual: 'Todellinen',
            severity: 'Vakavuus',
            no_inconsistencies: 'Ei inconsistencies found. löytynyt',
            trend: 'Epäjohdonmukaisuustrendi',
            trend_chart_label: 'Epäjohdonmukaisuustrendikaavio, joka näyttää laskut ajan mittaan',
            acknowledge: 'Kuittaa',
            resolve: 'Ratkaise',
            suppress: 'Piilota',
            cr_id_placeholder: 'CR Tunniste',
            view_all: 'Kaikki',
            view_my_properties: 'Omat Ominaisuudet',
            view_dpo_review: 'DPO Tarkistusjono',
            change_request_id_required: 'Tämän epäjohdonmukaisuuden ratkaisemiseksi vaaditaan muutospyyntötunnus.',
        },
        snapshot_schedules: {
            title: 'Tilannevedosaikataulut',
            new: 'Uusi Aikataulu',
            schedule_name: 'Aikataulu Nimi',
            frequency: 'Tiheys',
            next_run: 'Seuraava Suorita',
            last_run: 'Last Suorita',
            no_schedules: 'Ei snapshot schedules found. löytynyt',
            target_type: 'Kohdetyyppi',
            target_id: 'Kohteen tunnus',
            custom_frequency_days: 'Muokattu tiheys (päiviä)',
            time_of_day: 'Kellonaika',
            time: 'Aika',
            retention_days: 'Säilytys (päiviä)',
            execute_now: 'Suorita nyt',
            placeholder_days: 'esim. 10',
            target_name: 'Kohteen nimi',
        },
        golden_templates: {
            title: 'Kultaiset mallipohjat',
            new: 'Uusi Malli',
            template_name: 'Malli Nimi',
            property_type: 'Ominaisuus Tyyppi',
            no_templates: 'Ei golden mallit found. löytynyt',
            applicable_group_ids: 'Sovellettavat ryhmätunnukset (pilkuilla erotettuna)',
            applicable_groups: 'Sovellettavat ryhmät',
            purposes: 'Tarkoitukset',
            legal_bases: 'Oikeusperustat',
            banner_config: 'Bannerin määritys',
            consent_settings: 'Suostumusasetukset',
            group_ids_placeholder: 'ryhmätunnus-1, ryhmätunnus-2',
            apply_to_property: 'Käytä Ominaisuuteen',
            apply_to_group: 'Käytä Ryhmään',
            apply_success: 'Malli käytetty onnistuneesti.',
            applications: 'Käyttöhistoria',
            no_applications: 'Ei mallin käyttökertoja vielä.',
            no_configuration_details: 'Mallissa ei ole konfigurointitietoja. Muokkaa mallia lisätäksesi tarkoituksia, oikeusperusteita, banner-asetuksia tai suostumusasetuksia.',
        },
        rollouts: {
            title: 'Mallien käyttöönotot',
            rollout_status: 'Käyttöönoton tila',
            confirmed: 'Vahvistettu',
            pending: 'Odottaa',
            failed: 'Epäonnistunut',
            confirm: 'Vahvista Ominaisuus',
            confirm_success: 'Ominaisuus vahvistettu käyttöönotossa.',
            can_close: 'Kaikki ominaisuudet vahvistettu — käyttöönotto valmis',
            cannot_close: 'Odotetaan ominaisuusvahvistuksia',
            no_rollouts: 'Ei käyttöönottoja löytynyt.',
            in_progress: 'Käynnissä',
            completed: 'Valmis',
            cancelled: 'Peruutettu',
            reject: 'Hylkää Ominaisuus',
            reject_success: 'Ominaisuus hylätty käyttöönotosta.',
            confirm_all: 'Vahvista Kaikki',
            reject_all: 'Hylkää Kaikki',
            confirm_label: 'Vahvista käyttöönotto kohteelle',
            reject_label: 'Hylkää käyttöönotto kohteelle',
            confirm_all_label: 'Vahvista Kaikki Odottavat Käyttöönotot',
            reject_all_label: 'Hylkää Kaikki Odottavat Käyttöönotot',
            no_properties_in_rollout: 'Tällä käyttöönotolla ei ole ominaisuuksia.',
        },
        analytics: {
            title: 'Ominaisuusanalytiikka',
            opt_in_rate: 'Opt-in-prosentti',
            opt_out_rate: 'Opt-out-prosentti',
            withdrawal_rate: 'Peruutusprosentti',
            total_consents: 'Suostumukset yhteensä',
            total_withdrawals: 'Peruutukset yhteensä',
            compliance_score: 'Vaatimustenmukaisuuspisteet',
            non_compliant: 'Vaatimustenvastaiset ominaisuudet',
            no_non_compliant: 'Kaikki ominaisuudet ovat vaatimustenmukaisia.',
            threshold: 'Vaatimustenmukaisuuskynnys',
            property_id: 'Kiinteistön tunnus',
            compliant: 'Yhteensopiva',
            non_compliant_status: 'Ei yhteensopiva',
            compliance_help_summary: 'Miten vaatimustenmukaisuuspisteet lasketaan ja kuinka korjaan sen?',
            compliance_help_intro: 'Kiinteistö merkitään vaatimustenvastaiseksi, kun sen pistemäärä on alle 80 %. Pisteet lasketaan seuraavasti:',
            compliance_help_with_template_label: 'Jos käytetään kultaista mallia:',
            compliance_help_with_template_text: '% määritysavaimista (tarkoitukset, legal_bases, banner_config, consent_settings), jotka vastaavat mallia tarkasti.',
            compliance_help_no_template_label: 'Jos kultaista mallia ei käytetä:',
            compliance_help_no_template_text: '% sidoksista (omaisuudelle määrätyt suostumustyypit) deployment_status = otettu käyttöön. Ominaisuus, jolla ei ole sidoksia, on oletuksena 100 %.',
            compliance_help_fix_intro: 'Jos haluat poistaa omaisuuden tästä luettelosta, tee jokin seuraavista:',
            compliance_help_fix_deploy_label: 'Ota sen siteet käyttöön',
            compliance_help_fix_deploy_text: 'avaa Binding Matrix -sivu ja aseta kunkin sidoksen käyttöönottotilaksi käytössä.',
            compliance_help_fix_template_label: 'Käytä kultaista mallia',
            compliance_help_fix_template_text: 'Käytä mallissa Käytä ryhmään ja vahvista sitten tämän omaisuuden käyttöönotto. Varmista, että omaisuuden määritykset vastaavat mallia.',
            compliance_help_fix_decommission_label: 'Poistaa kiinteistön käytöstä',
            compliance_help_fix_decommission_text: 'käytä käytöstäpoistopainiketta kiinteistötietojen modaalissa, jos se ei ole enää käytössä.',
            compliance_help_fix_no_bindings_label: 'Poista kaikki sidokset',
            compliance_help_fix_no_bindings_text: 'jos ominaisuudella ei ole mallia eikä sidoksia, se saa oletusarvoisesti 100 %.',
        },
        evidence_packs: {
            title: 'Todistuspaketit',
            new: 'Uusi Pack',
            pack_name: 'Pack Nimi',
            properties: 'Ominaisuudet',
            generated_at: 'Luotu',
            no_packs: 'Ei todistusaineisto packs found. löytynyt',
            generate: 'Luo',
            content_type_consent_records: 'Suostumus Tietueet',
            content_type_policy_versions: 'Käytäntö Versiot',
            content_type_binding_history: 'Binding Historia',
            content_type_change_requests: 'Muutospyynnöt',
            content_type_inconsistency_reports: 'Inconsistency Raportit',
            content_type_snapshot_data: 'Tilannekuva Tiedot',
            content_type_audit_trail: 'Tarkastus Trail',
        },
        audit: {
            title: 'Suostumusominaisuustarkastus',
            event_type: 'Tapahtumatyyppi',
            entity_type: 'Entity Tyyppi',
            entity_id: 'Entity Tunniste',
            actor: 'Toimija',
            timestamp: 'Aikaleima',
            no_events: 'Ei tarkastus tapahtuma found. löytynyt',
            entity_type_property: 'Ominaisuus',
            entity_type_group: 'Ryhmä',
            entity_type_binding: 'Sidonta',
            entity_type_change_request: 'Muutospyyntö',
            entity_type_golden_template: 'Kultainen malli',
            entity_type_snapshot_schedule: 'Tilannevedosaikataulu',
            entity_type_inconsistency: 'Epäjohdonmukaisuus',
            entity_type_evidence_pack: 'Todistepaketti',
            action_created: 'Luotu',
            action_updated: 'Päivitetty',
            action_deleted: 'Poistettu',
            action_decommissioned: 'Poistettu käytöstä',
            action_status_changed: 'Tila muutettu',
            action_submitted: 'Lähetetty',
            action_approved: 'Hyväksytty',
            action_rejected: 'Hylätty',
            action_deployed: 'Otettu käyttöön',
            action_rolled_back: 'Palautettu',
            action_paused: 'Keskeytetty',
            action_resumed: 'Jatkettu',
            action_cancelled: 'Peruutettu',
            action_acknowledged: 'Kuitattu',
            action_resolved: 'Ratkaistu',
            action_suppressed: 'Piilotettu',
            action_executed: 'Suoritettu',
        },
        property_types: {
            website: 'Verkkosivusto',
            mobile_app: 'Mobiilisovellus',
            ott: 'OTT',
            iot_device: 'IoT-laite',
            kiosk: 'Kioski',
            api_service: 'API-palvelu',
            sdk: 'SDK',
        },
        environments: {
            production: 'Tuotanto',
            staging: 'Vaiheistus',
            development: 'Kehitys',
            testing: 'Testaus',
        },
        group_types: {
            brand: 'Brändi',
            region: 'Alue',
            product_line: 'Tuotelinja',
            business_unit: 'Liiketoimintayksikkö',
            custom: 'Mukautettu',
        },
        target_types: {
            property: 'Ominaisuus',
            group: 'Ryhmä',
            all: 'Kaikki',
        },
        frequencies: {
            hourly: 'Tunneittain',
            daily: 'Päivittäin',
            weekly: 'Viikoittain',
            monthly: 'Kuukausittain',
            custom: 'Mukautettu',
        },
        binding_statuses: {
            current: 'Nykyinen',
            outdated: 'Vanhentunut',
            pending: 'Odottava',
            deploying: 'Käyttöönotto',
            rolled_back: 'Palautettu',
        },
        sensitive_categories: {
            health: 'Terveystiedot',
            biometric: 'Biometriset tiedot',
            genetic: 'Geneettinen data',
            racial_ethnic: 'Rotu/etninen alkuperä',
            political_opinions: 'Poliittiset mielipiteet',
            religious_beliefs: 'Uskonnolliset uskomukset',
            trade_union: 'Ammattiliiton jäsenyys',
            sexual_orientation: 'Seksuaalinen suuntautuminen',
            criminal_records: 'Rikosrekisterit',
            financial: 'Taloustiedot',
            children: 'Lasten tiedot',
            geolocation: 'Tarkka Geolocation',
        },
        export_csv: 'Vie CSV',
        exporting: 'Viedaan...',
        page_x_of_y: 'Sivu {page} / {total}',
        regulatory: {
            capture_method_guidance: 'PDPL vaatii suostumustodistuksen, jotta se vastaa keräyskanavaa. Kiinteistötyypit liittyvät suostumuksen talteenottomenetelmiin: verkkosivusto/OTT = banneripohjainen suostumus, mobiilisovellus/SDK = sovelluksen sisäinen suostumuskulku, kioski = fyysinen/näytöllä näkyvä suostumuksen kaappaus, API palvelu = ohjelmallinen suostumus, IoT-laite = laitetason suostumusmekanismi.',
            dashboard_metrics_info: 'Tämä hallintapaneeli tarjoaa DPO:lle yhden näkymän suostumusasennosta kaikissa digitaalisissa omaisuuksissa. Keskeisiä mittareita ovat: sitomisen kattavuuden prosenttiosuus (käyttöön otettujen sidosten kokonaismäärä), mallien noudattaminen epäjohdonmukaisuuswidgetin kautta, tarkistusta vaativat odottavat muutospyynnöt ja omaisuuden tilan erittely, joka näyttää kiinteistöjen aktiivisen, ei-aktiivisen tai käytöstä poistetun tilan. Kiinteistöjä, joissa on arkaluontoisia tietoluokkia, seurataan kiinteistörekisterin kautta.',
            change_request_impact_info: 'Suostumusominaisuuksia koskevissa muutospyynnöissä on dokumentoitava sääntelyn vaikutus: mitä PDPL vaatimuksia tämä koskee, vaikuttaako muutos arkaluontoisten tietojen käsittelyyn (Art. 2.4) ja vaatiiko muutos rekisteröityjen uudelleensuostumusta. Kunkin muutospyynnön vaikutusten yhteenvedossa on yksityiskohtaiset tiedot PDPL-viittauksista tarkastuksen todisteisiin.',
            outdated_binding_warning: 'Sidotukset, joiden tila on "vanhentunut", osoittavat, että omaisuus käyttää käytäntöversiota, joka ei ole ajan tasalla. Nämä ominaisuudet eivät välttämättä vastaa viimeisimpiä PDPL vaatimuksia tai organisaatiokäytäntöjen päivityksiä. Tarkista ja päivitä vanhentuneet sidokset viipymättä säilyttääksesi PDPL vaatimustenmukaisuuden kaikissa suostumusomaisuuksissa.',
        },
    },
    localization: {
        nav: {
            dashboard: 'Kojelauta',
            market_packs: 'Markkinapaketit',
            language_packs: 'Kielipaketit',
            frameworks: 'Sääntelykehykset',
            mappings: 'Moduulimääritykset',
            submissions: 'Pakettilähetykset',
            sector_overlay_catalog: 'Overlay Catalog',
            translations: 'Käännöksen esikatselu',
            audit: 'Tarkastusjälki',
        },
        dashboard: {
            title: 'Lokalisointikojelauta',
            loading: 'Ladataan kojelauta tiedot...',
            active_market_packs: 'Aktiiviset markkinapaketit',
            published_packs_available: 'Julkaistu market packs available',
            language_packs: 'Kieli Packs',
            total_language_packs: 'Rekisteröityjä kielipaketteja yhteensä',
            active_regulatory_frameworks: 'Aktiiviset sääntelykehykset',
            frameworks_active: 'Kehykset with active tila',
            pending_pack_submissions: 'Odottaa Pack Submissions',
            submissions_awaiting_review: 'Submissions awaiting katselmointi',
            recent_audit_events: 'Viimeaikainen Tarkastus Tapahtumat',
            no_recent_audit: 'No recent tarkastus tapahtuma.',
            event_type: 'Tapahtumatyyppi',
            entity_type: 'Entity Tyyppi',
            entity_id: 'Entity Tunniste',
            actor: 'Toimija',
            timestamp: 'Aikaleima',
        },
        market_packs: {
            title: 'Markkinapakettikatalogi',
            new: 'Uusi Markkina Pack',
            pack_name: 'Pack Nimi',
            market: 'Markkina',
            locale: 'Maa-asetus',
            no_packs: 'Ei market packs found. löytynyt',
        },
        language_packs: {
            title: 'Kielipaketit',
            new: 'Uusi Kieli Pack',
            language: 'Kieli',
            locale_code: 'Maa-asetus Koodi',
            completeness: 'Täydellisyys',
            completeness_detail: 'Täydellisyyden yksityiskohta',
            overall_completeness: 'Yleinen täydellisyys',
            total_keys: 'Avaimet yhteensä',
            translated_keys: 'Käännetyt avaimet',
            missing_keys: 'Avaimet puuttuvat',
            namespace_breakdown: 'Nimitilan erittely',
            namespace: 'Nimiavaruus',
            translated: 'Käännetty',
            no_packs: 'Ei language packs found. löytynyt',
            install_language_pack: 'Asenna kielipaketti',
            available_packs: 'Saatavilla olevat kielipaketit',
            install: 'Asenna',
            all_installed: 'Kaikki kielipaketit on jo asennettu.',
            install_success: 'Kielipaketti asennettu onnistuneesti.',
            market: 'Markkina',
            featured: 'Esitelty',
        },
        frameworks: {
            // Sprint 63.32.0 (#543) — every framework_type code, in the reader's language.
            type_national_law: 'National law',
            type_parent_framework: 'Parent framework',
            type_child_decree: 'Child decree',
            type_child_decision: 'Child decision',
            type_child_procedure_decree: 'Procedural decree',
            type_conditional_child_decree: 'Conditional child decree',
            type_implementing_decree: 'Implementing decree',
            type_enforcement_decree: 'Sanctions decree',
            type_sanctions_reference: 'Sanctions reference',
            type_official_guidance: 'Official guidance',
            type_guidance_overlay: 'Guidance overlay',
            type_sector_overlay: 'Sector overlay',
            type_security_overlay: 'Security overlay',
            type_security_control_overlay: 'Security control overlay',
            type_temporary_procedural_overlay: 'Temporary procedural overlay',
            type_cross_sector: 'Cross-sector',
            type_directive: 'Directive',
            type_regulation: 'Regulation',
            type_national_transposition: 'National transposition',
            type_international_standard: 'International standard',
            title: 'Sääntelykehykset',
            new: 'Uusi Kehys',
            framework_name: 'Kehys Nimi',
            jurisdiction: 'Toimivalta',
            effective_date: 'Effective Päivämäärä',
            no_frameworks: 'Ei regulatory kehys found. löytynyt',
            view_requirements: 'Näytä vaatimukset',
            hide_requirements: 'Piilota vaatimukset',
            show_legacy: 'Näytä vanhat kehykset',
            effective_until: 'Voimassa asti',
            effective_on: 'Voimassa alkaen',
            replaced_by: 'Korvattu',
            replaces_label: 'Korvaa',
            status_active: 'Aktiivinen',
            status_draft: 'Luonnos',
            status_unclassified: 'Unrecognised status',
            status_legacy: 'Vanha',
            status_upcoming: 'Tuleva',
            status_active_reference: 'In force, reference only',
            status_temporary_active: 'Temporarily in force',
            watchlist: 'Tarkkailulista',
            watchlist_empty: 'Ei luonnosta tai tulevia puitteita',
            watchlist_empty_desc: 'Kehykset, joiden tila on "luonnos" tai "tuleva", näkyvät täällä seurantaa varten.',
            expected_effective: 'Odotettu tehokas',
            effective_date_tbd: 'Voimaantulopäivä: TBD',
            activate: 'Aktivoi',
            ai_law_title: 'Vietnamin AI laki',
            ai_risk_classification: 'AI Riskiluokitus',
            ai_system_dossier: 'AI Järjestelmän asiakirjat',
            ai_labeling_checklist: 'AI Merkinnät ja tiedot',
            ai_incident_report: 'AI Tapahtumaraportti',
            ai_impact_assessment: 'AI Vaikutusten arviointi',
            ai_sandbox_application: 'Hiekkalaatikkosovellus',
            ai_sandbox_monitoring: 'Hiekkalaatikon valvonta',
            ai_sandbox_completion: 'Hiekkalaatikon valmistuminen',
            ai_submission_tracker: 'Lähetyksen seuranta',
            ai_evidence_capture: 'Todisteiden talteenotto',
            risk_level_minimal: 'Minimaalinen riski',
            risk_level_limited: 'Rajoitettu riski',
            risk_level_high: 'Korkea riski',
            risk_level_unacceptable: 'Hyväksymätön riski',
            submission_status_draft: 'Luonnos',
            submission_status_submitted: 'Lähetetty',
            submission_status_returned: 'Palautettu muutosta varten',
            submission_status_accepted: 'Hyväksytty',
            submission_status_rejected: 'Hylätty',
            submission_status_closed: 'Suljettu',
            ecommerce_law_title: 'Vietnamin sähköisen kaupan laki',
            ecommerce_platform_classification: 'Alustan luokitus',
            ecommerce_seller_register: 'Myyjärekisteri',
            ecommerce_complaint_register: 'Valitusrekisteri',
            ecommerce_takedown_register: 'Poistorekisteri',
            ecommerce_submission_tracking: 'Lähetyksen seuranta',
            ecommerce_dossier_checklist: 'Asiakirjojen tarkistuslista',
            ecommerce_cross_border: 'Rajat ylittävä noudattaminen',
            ecommerce_trustmark: 'Trustmark-ohjelma',
            ecommerce_notification: 'Ilmoitus',
            ecommerce_registration: 'Rekisteröityminen',
            ecommerce_termination: 'Irtisanominen',
            ecommerce_reporting_readiness: 'Raportointivalmius',
            ecommerce_evidence_capture: 'Todisteiden talteenotto',
            submission_status_approved: 'Hyväksytty',
            cybersec_law_2025_title: 'Vietnamin kyberturvallisuuslaki (2025)',
            cybersec_system_classification: 'Järjestelmän luokitus',
            cybersec_risk_assessment: 'Riskinarviointi',
            cybersec_incident_report: 'Tapahtumaraportti',
            cybersec_authority_cooperation: 'Viranomaisten yhteistyö',
            cybersec_data_localization: 'Tietojen lokalisointi',
            cybersec_vendor_security: 'Myyjän turvallisuus',
            cybersec_submission_tracker: 'Lähetyksen seuranta',
            cybersec_evidence_capture: 'Todisteiden talteenotto',
            cybersec_dossier_checklist: 'Asiakirjojen tarkistuslista',
            cybersec_classification_general: 'Kenraali',
            cybersec_classification_important: 'Tärkeää',
            cybersec_classification_critical: 'Kriittinen',
            data_law_title: 'Vietnamin tietolaki',
            data_inventory: 'Tietovarasto',
            data_classification: 'Tietojen luokitus',
            data_sharing: 'Tietojen jakaminen',
            data_governance: 'Tietojen hallinta',
            data_submission_tracker: 'Lähetyksen seuranta',
            data_evidence_capture: 'Todisteiden talteenotto',
            data_dossier_checklist: 'Asiakirjojen tarkistuslista',
            telecom_law_title: 'Vietnamin televiestintälaki',
            telecom_authority_request: 'Viranomaisten pyyntö',
            telecom_disclosure: 'Tietojen paljastaminen',
            telecom_confidentiality: 'Luottamuksellisuus',
            telecom_submission_tracker: 'Lähetyksen seuranta',
            telecom_evidence_capture: 'Todisteiden talteenotto',
            telecom_dossier_checklist: 'Asiakirjojen tarkistuslista',
            pdpl_law_title: 'Vietnamin henkilötietojen suojalaki',
            pdpl_breach_notification: 'Rikkomusilmoitus',
            pdpl_dpo_notification: 'DPO Tapaamisilmoitus',
            pdpl_data_subject_notification: 'Rekisteröidyn ilmoitus',
            pdpl_submission_tracker: 'Lähetyksen seuranta',
            pdpl_evidence_capture: 'Todisteiden talteenotto',
            pdpl_dossier_checklist: 'Asiakirjojen tarkistuslista',
            pdpl_authority_filing: 'Viranomaisten arkistointi',
            pdpl_mops_filing: 'MoPS-arkistointi',
        },
        mappings: {
            title: 'Moduulimääritykset',
            new: 'Uusi Kartoitus',
            module: 'Moduuli',
            // fix-sprint #447 W8 — module labels, from the bundle (no `{en, vi}` map on the page).
            module_consent: 'Suostumus',
            module_dpia: 'DPIA',
            module_incident: 'Tapahtuma',
            module_rights_request: 'Oikeuspyyntö',
            module_data_mapping: 'Tietojen kartoitus',
            module_classification: 'Luokittelu',
            module_monitoring: 'Valvonta',
            module_governance: 'Hallinto',
            module_vendor: 'Myyjä',
            module_risk: 'Riski',
            module_sector_licensing: 'Sector Licensing',
            module_investor_verification: 'Investor Verification',
            module_account_data_governance: 'Account Data Governance',
            module_regulatory_disclosure: 'Regulatory Disclosure',
            module_authority_direction: 'Authority Direction',
            module_issued_record: 'Issued Records',
            module_publication_assessment: 'Publication Assessment',
            module_legal_deposit: 'Legal Deposit',
            module_legal_representative: 'Legal Representative',
            module_service_certificate: 'Processing Service Certificate',
            market_pack: 'Markkinapaketti',
            mapping_status: 'Kartoitus Tila',
            // fix-sprint #431 — the list count, chosen by `count === 1` (the bundle's `count_one` convention).
            count: '{count} mappings',
            count_one: '{count} mapping',
            no_mappings: 'Ei module mappings found. löytynyt',
            incomplete_entries_warning: '{count} kartoitus(ta) piilotettu puuttuvan kehys-ID:n, vaatimus-ID:n tai moduulin vuoksi.',
            // fix-sprint #431 — seeding is a person's action, never a side effect of opening the page.
            seed_action: 'Create mappings from installed frameworks',
            seed_hint: 'Creates a direct mapping for each requirement that names a module, in every installed framework that has no mappings yet. Opening this page creates nothing.',
            seed_confirm_title: 'Create mappings from installed frameworks?',
            seed_confirm: 'This creates a mapping for every requirement that names a module, in each installed framework that has no mappings yet. It can add many rows, and each can be edited or deleted afterwards.',
            seed_done: '{count} mapping(s) created.',
            seed_none: 'No mappings were created: every installed framework already has mappings, or none of its requirements names a module.',
            seed_error: 'The mappings could not be created.',
            // fix-sprint #431 (gate E12.5 remediation 1) — a failed list read says so, and nothing else.
            load_error: 'The module mappings could not be read, so none are shown and none can be created from installed frameworks here.',
        },
        submissions: {
            title: 'Pakettilähetykset',
            new: 'Uusi Submission',
            submitter: 'Lähettäjä',
            pack: 'Paketti',
            submitted_at: 'Lähetetty',
            no_submissions: 'Ei pack submissions found. löytynyt',
            installed_sector_overlays_title: 'Toimialalisäosat ({count})',
            installed_sector_overlays_aria: 'Asennetut toimialalisäosat',
            installed_col_name: 'Nimi',
            installed_col_code: 'Koodi',
            installed_col_sector: 'Toimiala',
            installed_col_mode: 'Tila',
            installed_col_status: 'Status',
            installed_refresh: 'Päivitä',
            installed_count_one: '1 installed pack',
            installed_count: '{count} installed packs',
            installed_market_packs_title: 'Market Packs ({count})',
            installed_market_packs_aria: 'Installed Market Packs',
            installed_language_packs_title: 'Language Packs ({count})',
            installed_language_packs_aria: 'Installed Language Packs',
            installed_frameworks_title: 'Regulatory Frameworks ({count})',
            installed_frameworks_aria: 'Installed Regulatory Frameworks',
            installed_col_market_code: 'Market Code',
            installed_col_display_name: 'Display Name',
            installed_col_locale: 'Locale',
            installed_col_version: 'Version',
            tab_submissions: 'Submissions',
            tab_install_zip: 'Install from Zip',
            tab_installed: 'Installed Packs',
        },
        translations: {
            title: 'Käännöksen esikatselu',
            select_locale: 'Valitse maa-asetus',
            select_module: 'Valitse moduuli',
            key: 'Key',
            value: 'Arvo',
            no_translations: 'Ei translations found. löytynyt',
        },
        audit: {
            title: 'Localization Tarkastus Trail',
            event_type: 'Tapahtumatyyppi',
            // Gate E12.5 remediation 1 (fix-sprint #431) — Module Mappings audit events: the key is
            // `event_type_` + the recorded action with `.` read as `_`.
            event_type_regulatory_mapping_created: 'Module mapping created',
            event_type_regulatory_mapping_updated: 'Module mapping updated',
            event_type_regulatory_mapping_deleted: 'Module mapping deleted',
            event_type_regulatory_mapping_seeded_from_frameworks: 'Mappings created from installed frameworks',
            // Gate E12.5 remediation 2 — every other event the localization services record (event_type, else action).
            event_type_market_pack_created: 'Market pack created',
            event_type_market_pack_updated: 'Market pack updated',
            event_type_market_pack_status_changed: 'Market pack status changed',
            event_type_market_pack_installed: 'Market pack installed',
            event_type_market_pack_uninstalled: 'Market pack uninstalled',
            event_type_pack_created: 'Market pack created',
            event_type_pack_updated: 'Market pack updated',
            event_type_pack_status_changed: 'Market pack status changed',
            event_type_pack_installed: 'Market pack installed',
            event_type_pack_uninstalled: 'Market pack uninstalled',
            event_type_cluster_installed: 'Market pack cluster installed',
            event_type_pack_installed_from_zip: 'Pack installed from an uploaded file',
            event_type_pack_zip_install_refused: 'Uploaded pack refused: it carries no signature',
            event_type_pack_submission_created: 'Pack submission created',
            event_type_pack_submission_review_started: 'Pack submission review started',
            event_type_pack_submission_comment_added: 'Comment added to a pack submission',
            event_type_pack_submission_approved: 'Pack submission approved',
            event_type_pack_submission_rejected: 'Pack submission rejected',
            event_type_pack_artifact_created: 'Pack artifact created',
            event_type_pack_artifact_updated: 'Pack artifact updated',
            event_type_pack_artifact_deleted: 'Pack artifact deleted',
            event_type_regulatory_framework_updated: 'Framework updated',
            event_type_regulatory_framework_status_changed: 'Framework status changed',
            event_type_regulatory_framework_requirement_added: 'Requirement added to a framework',
            event_type_regulatory_framework_requirement_removed: 'Requirement removed from a framework',
            event_type_regulatory_framework_stack_layer_status_changed: 'Framework Stack layer status changed',
            event_type_regulatory_framework_transition_review_updated: 'Transition review updated',
            event_type_language_pack_created: 'Language pack created',
            event_type_language_pack_updated: 'Language pack updated',
            event_type_language_pack_activated: 'Language pack activated',
            event_type_language_pack_deactivated: 'Language pack deactivated',
            event_type_language_pack_installed: 'Language pack installed',
            event_type_language_pack_uninstalled: 'Language pack uninstalled',
            event_type_sector_overlay_installed: 'Sector overlay installed',
            event_type_sector_overlay_uninstalled: 'Sector overlay uninstalled',
            event_type_regulatory_overlay_installed: 'Regulatory overlay installed',
            event_type_regulatory_overlay_uninstalled: 'Regulatory overlay uninstalled',
            event_type_installed_framework_mode_changed: 'Installed framework mode changed',
            event_type_phased_obligation_early_implemented: 'Phased obligation marked early-implemented',
            entity_type: 'Entity Tyyppi',
            entity_id: 'Entity Tunniste',
            entity_name: 'Entiteetin nimi',
            actor: 'Toimija',
            timestamp: 'Aikaleima',
            payload: 'Hyötykuorma',
            collapse_payload: 'Supista hyötykuorman tiedot',
            payload_none: 'No details recorded',
            payload_one_field_recorded: '1 field recorded',
            payload_fields_recorded: '{count} fields recorded',
            payload_one_field_changed: '1 field changed',
            payload_fields_changed: '{count} fields changed',
            payload_one_field_removed: '1 field removed',
            payload_fields_removed: '{count} fields removed',
            payload_col_field: 'Field',
            payload_col_value: 'Value',
            payload_col_before: 'Before',
            payload_col_after: 'After',
            payload_empty_value: '(empty)',
            payload_not_set: 'Not set',
            proof_not_anchored: 'Not anchored',
            proof_not_anchored_tooltip: 'No proof record exists for this event. Events written before Immutable Audit Trails was enabled are not anchored, and events in batch mode appear here once their batch is sealed.',
            expand_payload: 'Laajenna hyötykuorman tiedot',
            collapse: 'Supista',
            expand: 'Laajenna',
            all_entity_types: 'Kaikki entiteettityypit',
            no_events: 'Ei tarkastus tapahtuma found. löytynyt',
        },
        market_packs_detail: {
            installed_at: 'Asennettu',
            pack_details_label: 'Paketin tiedot',
            market_code: 'Markkinakoodi',
            supported_locales: 'Tuetut kielet',
            min_platform_version: 'Minimialustanversio',
            pack_information: 'Paketin tiedot',
            pack_contents: 'Pakkauksen sisältö',
            included_frameworks: 'Sisältyvät sääntelykehykset',
            pack_artifacts: 'Paketin artefaktit',
            no_artifacts: 'Ei artefakteja tälle paketille',
            pack_not_found: 'Pakettia ei löytynyt',
        },
        mappings_form: {
            framework_id: 'Kehystunnus',
            requirement_id: 'Vaatimuksen tunnus',
            module_label: 'Moduuli',
            mapping_type: 'Kartoitustyyppi',
            configuration_json: 'Määritys (JSON)',
            conflict_resolution: 'Ristiriidan ratkaisu',
            is_override: 'On ohitus',
            all_modules: 'Kaikki moduulit',
            all_mapping_types: 'Kaikki kartoitustyypit',
        },
        submissions_detail: {
            submission_id: 'Lähetyksen tunnus',
            validation: 'Validointi',
            reviewers: 'Tarkastajat',
            start_review: 'Aloita tarkistus',
            hide_comments: 'Piilota kommentit',
            start_review_named: 'Start Review for {name}',
            approve_submission_named: 'Approve submission {name}',
            reject_submission_named: 'Reject submission {name}',
            comments_named: 'Comments for {name}',
            hide_comments_named: 'Hide comments for {name}',
            review_comments: 'Tarkistuskommentit',
            loading_comments: 'Ladataan kommentteja...',
            no_comments: 'Ei vielä kommentteja.',
            add_comment: 'Lisää kommentti',
            reject_submission: 'Hylkää lähetys',
            reason_for_rejection: 'Hylkäyksen syy',
            reviewer_ids: 'Tarkastajien tunnukset (pilkuilla erotetut)',
            submit_new_pack: 'Lähetä uusi paketti',
            pack_type: 'Pakettityyppi',
            market_code: 'Markkinakoodi',
            target_locales: 'Kohdekielet',
            pack_file: 'Pakettitiedosto',
            pack_format_info: 'Paketin muoto: Market-pakettien tulee sisältää kielikohtaiset JSON-tiedostot nimiavaruus-/avainkäännöksineen. Kielipakettien tulee tarjota käännökset määritetyille kohdealueille. Katso yksityiskohtaiset kaaviot dokumentaatiosta.',
            accepted_formats: 'Hyväksytyt muodot: .json tai .zip. JSON-tiedostojen tulee sisältää käännösavain-arvoparien tasainen tai nimiavaruusobjekti. ZIP-tiedostojen tulee sisältää vähintään yksi JSON aluetiedosto.',
            comma_separated_locales: 'Pilkuilla erotetut kielikoodit tälle kielipaketille',
            confirm_approval: 'Vahvista hyväksyntä',
            approve_message: 'Hyväksy tämä pakettiehdotus?',
            submission_label: 'Lähetys',
            all_pack_types: 'Kaikki pakettityypit',
            passed: 'hyväksytty',
            failed_label: 'epäonnistui',
            market_code_required: 'Markkinakoodi on pakollinen',
            version_required: 'Versio on pakollinen',
            pack_type_regulatory: 'Sääntelyyn liittyvä',
            framework_code: 'Viitekehyskoodi',
            jurisdiction_code: 'Lainkäyttöaluekoodi',
            pack_source: 'Lähde',
            pack_source_manual: 'Manuaalinen',
            pack_version: 'Paketin versio',
            install_regulatory_pack: 'Asenna sääntelypaketti',
            regulatory_pack_desc: 'Lataa sääntelykehyspaketti (.zip)',
            framework_required: 'Sääntelykehyksen koodi vaaditaan',
            jurisdiction_required: 'Lainkäyttöalueen koodi vaaditaan',
        },
        catalog_detail: {
            packs_installed_of: '{total} paketista asennettu',
            install_all: 'Asenna kaikki',
            installing: 'Asennetaan...',
            removing: 'Poistetaan...',
            install_all_named: 'Install all packs in {name}',
            installing_named: 'Installing... {name}',
            uninstall_named: 'Uninstall {name}',
            removing_named: 'Removing... {name}',
            manage_no_permission: 'Installing or uninstalling a market pack needs permission to manage market packs.',
            available: 'Available',
            cluster_installed_count: '({installed}/{total} installed)',
            frameworks_count_one: '1 framework',
            frameworks_count: '{count} frameworks',
            languages_count_one: '1 language',
            languages_count: '{count} languages',
            forms_count_one: '1 form',
            forms_count: '{count} forms',
            artifacts_count_one: '1 artifact',
            artifacts_count: '{count} artifacts',
            market_packs_count_one: '1 market pack',
            market_packs_count: '{count} market packs',
            install_success: 'Installed {market}: {frameworks}, {items}.',
            cluster_install_success: 'Cluster installed: {frameworks}, {forms}.',
            uninstall_success: 'Uninstalled {market}: removed {frameworks} and {artifacts}.',
            uninstall_confirm: 'Uninstall {market}? This removes all frameworks, artifacts and language packs for this market.',
            dependents_exist: 'Cannot uninstall {market}: other installed packs depend on it.',
            dependencies_required: '{market} needs {dependencies} installed first.',
            dependencies_required_unnamed: '{market} needs another market pack installed first.',
            requires: 'Vaatii',
            tab_catalog: 'Luettelo',
            tab_installed: 'Asennettu',
            details: 'Tiedot',
        },
        // Sprint 63.33.0 (#594) — standards packs installed on their own.
        standalone_packs: {
            title: 'Standards',
            description: 'International standards are installed on their own: no market pack includes them. Installing one adds its frameworks, requirements and forms for your organisation.',
            install: 'Install',
            install_named: 'Install {name}',
            installing: 'Installing…',
            installing_named: 'Installing… {name}',
            installed: 'Installed',
            installed_other_route: 'Installed by another route',
            installed_other_route_hint: 'Its frameworks are present for your organisation, but this standard did not install them: another pack or a record created by hand did. There is nothing for it to remove here.',
            confirm_title: 'Install {name}?',
            confirm_message: 'This installs {name} for your organisation, with the frameworks, requirements and forms it carries.',
            confirm_label: 'Install',
            install_success: '{name} installed: {frameworks}, {forms}.',
            install_failed: 'The standard could not be installed.',
            install_no_permission: 'Installing a standard needs permission to change regulatory frameworks.',
            remove: 'Remove',
            remove_named: 'Remove {name}',
            removing: 'Removing…',
            removing_named: 'Removing… {name}',
            remove_confirm_title: 'Remove {name}?',
            remove_confirm_message: 'This removes {name} from your organisation, with the frameworks, requirements, deadlines and forms it installed, and every Module Mapping recorded against its frameworks, including mappings your organisation created or edited. Installing it again does not restore those mappings. Forms your organisation has filled in are kept. A removal cannot be undone.',
            remove_confirm_label: 'Remove',
            remove_success: '{name} removed: {frameworks}, {forms}.',
            remove_failed: 'The standard could not be removed.',
            remove_no_permission: 'Removing a standard needs permission to change regulatory frameworks.',
            refusal_already_installed: 'This standard is already installed for your organisation.',
            refusal_not_installable_alone: 'This pack is not installed on its own here: it is installed with its market, or from the sector overlay catalogue.',
            refusal_not_found: 'This standard is no longer in the catalogue. Reload the page.',
            refusal_not_installed: 'This standard is not installed for your organisation, so there is nothing to remove.',
            refusal_installed_by_another_route: 'This standard did not install its frameworks for your organisation, so it has nothing to remove. Remove them where they were installed.',
            refusal_forbidden: 'You do not have permission to change regulatory frameworks.',
            refusal_custody: 'This standard installs restricted registers; installing it requires the regulatory-artifact custody permission.',
        },
        translations_detail: {
            source_locale: 'Lähdekieli',
            target_locale: 'Kohdekieli',
            locales_must_differ: 'Lähde- ja kohdekielen on oltava erilaisia.',
            all_keys_translated: 'Kaikki avaimet käännetty',
            keys_missing: '{count} avaimet puuttuvat',
            show_missing_only: 'Näytä vain puuttuvat',
            namespace: 'Nimiavaruus',
            source: 'Lähde',
            target: 'Kohde',
        },
        frameworks_detail: {
            // fix-sprint-20260923-2136 — the detail page's own states, before the
            // framework record has been read (title, missing code, not found, failed).
            page_title_fallback: 'Framework',
            page_unavailable_heading: 'Framework unavailable',
            page_missing_code: 'No framework code was given in the address.',
            page_not_found: 'Framework {code} not found.',
            page_not_found_generic: 'Framework not found.',
            page_load_failed: 'The framework could not be loaded.',
            tab_frameworks: 'Kehykset',
            tab_watchlist: 'Seurantalista',
            authority: 'Viranomainen',
            requirements_count: 'Vaatimukset',
            all_types: 'Kaikki tyypit',
            collapse_all: 'Supista kaikki',
            expand_all: 'Laajenna kaikki',
            all_sectors: 'All sectors',
            filter_by_sector: 'Filter by sector',
            search_frameworks: 'Search by name or code...',
            sef_url_label: 'Framework URL',
            sef_url_copy: 'Copy',
            sef_url_copy_label: 'Copy framework URL',
            sef_url_copied: 'Framework URL copied',
            sef_url_copy_failed: 'Could not copy the framework URL',
            filtered_to: 'Suodatettu',
            clear_filter: 'Tyhjennä suodatin',
            no_requirements: 'Ei vaatimuksia tälle kehykselle.',
            requirements_label: 'Vaatimukset',
            status_updated: 'Tila päivitetty onnistuneesti',
            overview_tab: 'Yleiskatsaus',
            forms_tab: 'Lomakkeet',
            procedures_tab: 'Menettelyt',
            fines_risks_tab: 'Sakot ja riskit',
            deadlines_tab: 'Määräajat',
            guidance_tab: 'Ohjaus',
            guidance_empty: 'Tälle kehykselle ei ole ohjesisältöä.',
            guidance_loading: 'Ladataan ohjetta…',
            guidance_section: 'Ohjausosio',
            guidance_view: 'Näytä opastus',
            guidance_map_to_workflow: 'Kartta työnkulkuun',
            guidance_link_evidence: 'Linkitä todisteet',
            guidance_filter_type: 'Kirjoita',
            guidance_filter_category: 'Luokka',
            guidance_filter_module: 'Moduuli',
            guidance_filter_status: 'Tila',
            guidance_filter_search: 'Hakuohjeet',
            guidance_filter_all: 'Kaikki',
            guidance_table_reference: 'Viite',
            guidance_table_name: 'Ohjeen nimi',
            guidance_table_type: 'Kirjoita',
            guidance_table_category: 'Luokka',
            guidance_table_effective_date: 'Voimaantulopäivä',
            guidance_table_modules: 'Liittyvät moduulit',
            guidance_table_actions: 'Toiminnot',
            guidance_reference_only_banner: 'Vain viite – koskee {{sector}}-sektoria, mutta ei organisaatiotasi.',
            guidance_open_official_source: 'Avaa virallinen lähde',
            guidance_no_detail: 'Tälle ohjeelle ei ole lisätietoja saatavilla.',
            enum_guidance_type_cross_framework_note: 'Cross-Framework Note',
            enum_guidance_type_draft_status_note: 'Draft Status Note',
            enum_guidance_type_framework_hierarchy_note: 'Framework Hierarchy Note',
            enum_guidance_type_framework_overlay_note: 'Framework Overlay Note',
            enum_guidance_type_implementing_decree_summary: 'Implementing Decree Summary',
            enum_guidance_type_practical_unofficial_practice: 'Practical (Unofficial) Practice',
            enum_guidance_category_auction: 'Auction',
            enum_guidance_category_complaint_dispute: 'Complaint & Dispute',
            enum_guidance_category_draft_governance: 'Draft Governance',
            enum_guidance_category_email_zip_submission: 'Email/ZIP Submission',
            enum_guidance_category_exchange_classification: 'Exchange Classification',
            enum_guidance_category_framework_hierarchy: 'Framework Hierarchy',
            enum_guidance_category_high_risk_ai: 'High-Risk AI',
            enum_guidance_category_lifecycle_controls: 'Lifecycle Controls',
            enum_guidance_category_listing_review: 'Listing Review',
            enum_guidance_category_national_database_access: 'National Database Access',
            enum_guidance_category_outsourced_processing: 'Outsourced Processing',
            enum_guidance_category_participant_registration: 'Participant Registration',
            enum_guidance_category_personal_data_linkage: 'Personal Data Linkage',
            enum_guidance_category_personnel_conditions: 'Personnel Conditions',
            enum_guidance_category_pricing_and_public_asset: 'Pricing & Public Asset',
            enum_guidance_category_procedural_overlay: 'Procedural Overlay',
            enum_guidance_category_public_sector_ai: 'Public-Sector AI',
            enum_guidance_category_requirement_detail: 'Requirement Detail',
            enum_guidance_category_responsibilities: 'Responsibilities',
            enum_guidance_category_risk_and_remediation: 'Risk & Remediation',
            enum_guidance_category_sandbox: 'Sandbox',
            enum_guidance_category_scope: 'Scope',
            enum_guidance_category_security_drills_incidents: 'Security Drills & Incidents',
            enum_guidance_category_submission_channel: 'Submission Channel',
            enum_guidance_category_transaction_evidence: 'Transaction Evidence',
            enum_guidance_category_transparency_labeling: 'Transparency Labeling',
            enum_module_evidence_management: 'Evidence Management',
            enum_module_security: 'Security',
            form_category_access: 'Access',
            form_category_application: 'Application',
            form_category_authority_response_record: 'Authority Response Record',
            form_category_business_plan: 'Business Plan',
            form_category_capital_adequacy: 'Capital Adequacy',
            form_category_capital_adequacy_report: 'Capital Adequacy Report',
            form_category_certificate: 'Certificate',
            form_category_classification: 'Classification',
            form_category_compliance: 'Compliance',
            form_category_cross_border_transfer: 'Cross-Border Transfer',
            form_category_decision: 'Decision',
            form_category_email_submission_record: 'Email Submission Record',
            form_category_evidence: 'Evidence',
            form_category_evidence_pack: 'Evidence Pack',
            form_category_evidence_pack_manifest: 'Evidence Pack Manifest',
            form_category_forms: 'Forms',
            form_category_information_system: 'Information System',
            form_category_internal_audit: 'Internal Audit',
            form_category_internal_audit_report: 'Internal Audit Report',
            form_category_internal_control: 'Internal Control',
            form_category_internal_control_report: 'Internal Control Report',
            form_category_model_risk: 'Model Risk',
            form_category_notification: 'Notification',
            form_category_official_certificate: 'Official Certificate',
            form_category_official_reference: 'Official Reference',
            form_category_policy: 'Policy',
            form_category_record: 'Record',
            form_category_remediation: 'Remediation',
            form_category_report: 'Report',
            form_category_reporting: 'Reporting',
            form_category_risk: 'Risk',
            form_category_risk_data: 'Risk Data',
            form_category_risk_management_report: 'Risk Management Report',
            form_category_sandbox: 'Sandbox',
            form_category_security_plan: 'Security Plan',
            form_category_technical_plan: 'Technical Plan',
            form_category_training: 'Training',
            unit_absolute_date: 'Absolute date',
            unit_annual: 'Annual',
            unit_before_transfer: 'Before transfer',
            unit_before_use: 'Before use',
            unit_calendar_days_after_event: 'Calendar days after event',
            unit_calendar_days_before_event: 'Calendar days before event',
            unit_date: 'Date',
            unit_days: 'Days',
            unit_event: 'Event',
            unit_hours_from_authentication: 'Hours from authentication',
            unit_month: 'Month',
            unit_months: 'Months',
            unit_months_from_future_effective_date: 'Months from future effective date',
            unit_on_request: 'On request',
            unit_quarterly: 'Quarterly',
            unit_upon_request: 'Upon request',
            unit_weeks: 'Weeks',
            unit_without_undue_delay: 'Without undue delay',
            unit_working_days_before_event: 'Working days before event',
            unit_year: 'Year',
            unit_year_or_material_change: 'Year or material change',
            unit_years: 'Years',
            unit_years_minimum: 'Years (minimum)',
            unit_day: 'Day',
            unit_week: 'Week',
            sla_type_phased_installation_obligation: 'Phased installation obligation',
            sla_type_transitional_window_end: 'Transitional period ends',
            sla_type_data_retention_minimum: 'Data retention, minimum',
            sla_type_authority_appraisal_result_d356_as_issued: 'Authority’s appraisal result (text as issued)',
            sla_type_authority_appraisal_result_amended: 'Authority’s appraisal result (amended text)',
            sla_type_authority_appraisal_result_nq22: 'Authority’s appraisal result (transitional text)',
            sla_type_transitional_end_date: 'Automatic-debit transition ends',
            sla_type_pre_launch_report_submission: 'Pre-launch report to the State Bank',
            sla_type_testing_term_ceiling: 'Maximum testing term',
            sla_type_end_of_purpose_disposition: 'Return, deletion or destruction when the purpose ends',
            sla_type_publicity_on_regulator_requirement: "Notice and publicity on the regulator's requirement",
            sla_type_information_on_regulator_request: "Information provided on the regulator's request",
            sla_type_depositor_mismatch_handling_period: "Handling period for a depositor's mismatch report",
            sla_type_depositor_lookup_start: 'Depositor lookup start',
            sla_type_periodic_security_assessment: 'Annual security assessment',
            sla_type_periodic_software_list_review: 'Approved software list review',
            sla_type_periodic_risk_review: 'Risk situation review',
            sla_type_periodic_continuity_drill: 'Continuity drill',
            sla_type_periodic_staff_training: 'Staff security training',
            sla_type_record_retention_minimum: 'Online log retention, minimum',
            sla_type_record_backup_minimum: 'Log backup retention, minimum',
            sla_type_periodic_vulnerability_scan: 'Internet-facing vulnerability scan',
            sla_type_remediation_patch_window_critical_internet_facing: 'Critical vulnerability patch, component directly connected to the Internet',
            sla_type_remediation_patch_window_critical_other: 'Critical vulnerability patch, other components',
            sla_type_remediation_patch_window_high_internet_facing: 'High-rated vulnerability patch, component directly connected to the Internet',
            sla_type_remediation_patch_window_high_other: 'High-rated vulnerability patch, other components',
            sla_type_periodic_security_review: 'Mobile app security review',
            sla_type_credential_max_validity: 'PIN and password, maximum validity',
            sla_type_credential_default_validity: 'Credential, default validity',
            sla_type_otp_max_validity_sms: 'SMS OTP, maximum validity',
            sla_type_otp_max_validity_voice: 'Voice OTP, maximum validity',
            sla_type_otp_max_validity_email: 'Email OTP, maximum validity',
            sla_type_otp_max_validity_matrix_card: 'OTP from an OTP matrix card, maximum validity',
            sla_type_otp_max_validity_soft: 'Soft OTP code, maximum validity',
            sla_type_otp_max_validity_token: 'Token OTP, maximum validity',
            sla_type_card_max_usable_life: 'OTP matrix card, maximum usable life',
            sla_type_confirmation_request_validity: 'Two-channel confirmation request, validity',
            sla_type_biometric_match_time_limit_device: 'Device biometric match, time limit',
            sla_type_biometric_match_time_limit_stored_by_organisation: 'Biometric match against data held by the organisation, time limit',
            sla_type_ai_risk_classification_before_use: 'AI risk classification (before use)',
            sla_type_annual_risk_assessment_report: 'Annual risk assessment report',
            sla_type_annual_training_readiness: 'Annual training readiness',
            sla_type_art_30_records_availability_to_dpa: 'Art. 30 records availability to DPA',
            sla_type_audit_window_type_i: 'Audit window (Type I)',
            sla_type_audit_window_type_ii_min: 'Audit window (Type II, minimum)',
            sla_type_audit_window_type_ii_typical: 'Audit window (Type II, typical)',
            sla_type_authentication_result_retention_readiness: 'Authentication result retention readiness',
            sla_type_breach_communication_to_data_subjects: 'Breach communication to data subjects',
            sla_type_breach_notification_to_supervisory_authority: 'Breach notification to supervisory authority',
            sla_type_classification_lifecycle_review: 'Classification lifecycle review',
            sla_type_consultation_end: 'Consultation end',
            sla_type_annual_activity_report_proposed: 'Annual activity report (proposed)',
            sla_type_approved_list_publication_proposed: 'Approved list publication (proposed)',
            sla_type_course_result_report_proposed: 'Course result report (proposed)',
            sla_type_foreign_certificate_application_proposed: 'Foreign certificate recognition application (proposed)',
            sla_type_network_registration_review_proposed: 'Training network registration review (proposed)',
            sla_type_recognised_body_change_notice_proposed: 'Recognised body change notice (proposed)',
            sla_type_agency_label_publication_proposed: 'Agency publication of the label and DPID (proposed)',
            sla_type_label_dpid_update_proposed: 'Label update with the DPID (proposed)',
            sla_type_inspection_annual_report_proposed: 'Annual inspection report to the Ministry (proposed)',
            sla_type_content_takedown_response: 'Content takedown response',
            sla_type_cookie_consent_reconfirmation: 'Cookie consent reconfirmation',
            sla_type_corrective_action_default: 'Corrective action (default)',
            sla_type_cross_border_core_authority_review_complex: 'Cross-border core data authority review (complex)',
            sla_type_cross_border_core_authority_review_standard: 'Cross-border core data authority review (standard)',
            sla_type_cross_border_exception_postfiling: 'Cross-border exception (post-filing)',
            sla_type_cross_border_important_prefiling: 'Cross-border important data (pre-filing)',
            sla_type_ctia_dossier_filing: 'CTIA dossier filing',
            sla_type_cross_border_dossier_applicant_completion: 'Applicant completes the cross-border transfer impact dossier',
            sla_type_database_update: 'Database update',
            sla_type_decision_effective_date: 'Decision effective date',
            sla_type_decree_effective_date: 'Decree effective date',
            sla_type_circular_effective_date: 'Circular effective date',
            sla_type_system_upgrade_completion: 'System upgrade completion',
            sla_type_incident_recovery_resumption: 'Resumption after an incident is fixed',
            sla_type_report_correction_resubmission: 'Report correction and resubmission',
            sla_type_regulator_review_period: 'Authority review period',
            sla_type_submission_window_lock: 'Submission window lock',
            sla_type_transitional_reporting_regime: 'Transitional reporting arrangement',
            sla_type_staged_commencement_date: 'Staged commencement date',
            sla_type_decree_issued: 'Decree issued',
            sla_type_direct_marketing_opt_out_honor: 'Direct marketing opt-out honoring',
            sla_type_document_review_annual: 'Document review (annual)',
            sla_type_dpia_prior_consultation_dpa_response: 'DPIA prior consultation — DPA response',
            sla_type_dsar_refusal_notification: 'DSAR refusal notification',
            sla_type_dsar_response: 'DSAR response',
            sla_type_dsar_response_extension: 'DSAR response extension',
            sla_type_emergency_drill_cadence: 'Emergency drill cadence',
            sla_type_entrusted_processing_record_retention: 'Entrusted processing record retention',
            sla_type_event_review: 'Event review',
            // Sprint 62.49.1 (DB-10) — the consumer-rights pack's acknowledgement-of-receipt row.
            sla_type_consumer_complaint_receipt_acknowledgement: 'Notice to the consumer that a report, request or complaint was received',
            sla_type_food_traceability_records_request: 'Food traceability records request (24 hours)',
            sla_type_food_traceability_system_update: 'Food traceability system update',
            sla_type_evidence_currency_limit: 'Personal record statement currency (no more than 6 months before filing)',
            sla_type_framework_effective_date: 'Framework effective date',
            sla_type_high_risk_conformity_assessment_before_use: 'High-risk conformity assessment (before use)',
            sla_type_ict_systems_yearly_testing: 'ICT systems yearly testing',
            sla_type_important_core_transfer_approval_readiness: 'Important/core data transfer approval readiness',
            sla_type_incident_early_warning: 'Incident early warning',
            sla_type_incident_final_report: 'Incident final report',
            sla_type_incident_initial_notification: 'Incident initial notification',
            sla_type_cybersecurity_law_art45_1_assigned_level_protection_transition: 'Information systems with an assigned security level — protection conditions transition',
            sla_type_incident_initial_notification_outer_limit: 'Incident initial notification — outer limit from awareness',
            sla_type_incident_intermediate_report: 'Incident intermediate report',
            sla_type_incident_notification: 'Incident notification',
            sla_type_information_sharing_arrangement_notification: 'Information-sharing arrangement notification',
            sla_type_internal_audit_annual: 'Internal audit (annual)',
            sla_type_internal_post_consultation_review: 'Internal post-consultation review',
            sla_type_internal_post_session_review: 'Internal post-session review',
            sla_type_internal_review: 'Internal review',
            sla_type_law_effective_date: 'Law effective date',
            sla_type_lawful_deletion_request: 'Lawful deletion request',
            sla_type_management_body_training_cycle: 'Management body training cycle',
            sla_type_management_review_annual: 'Management review (annual)',
            sla_type_national_assembly_session: 'National Assembly session',
            sla_type_national_assembly_plenary_submission: 'Government submission heard by the National Assembly',
            sla_type_national_synthetic_database_response: 'National synthetic database response',
            sla_type_personnel_reassessment_readiness: 'Personnel reassessment readiness',
            sla_type_platform_registration_amendment: 'Platform registration amendment',
            sla_type_processing_log_retention: 'Processing log retention',
            sla_type_recertification_annual: 'Recertification (annual)',
            sla_type_recertification_cycle: 'Recertification cycle',
            sla_type_reclassification_authority_notification: 'Reclassification authority notification',
            sla_type_recurring_review: 'Recurring review',
            sla_type_reference_issued: 'Reference issued',
            sla_type_remediation_default: 'Remediation (default)',
            sla_type_risk_management_review_cycle: 'Risk management review cycle',
            sla_type_risk_management_self_assessment_cycle: 'Risk management self-assessment cycle',
            sla_type_roadmap_issued: 'Roadmap issued',
            sla_type_ropa_review_cycle: 'RoPA review cycle',
            sla_type_sandbox_certificate_review_ministry: 'Sandbox certificate review (ministry)',
            sla_type_sandbox_certificate_review_multi_authority: 'Sandbox certificate review (multi-authority)',
            sla_type_sandbox_certificate_review_provincial: 'Sandbox certificate review (provincial)',
            sla_type_sandbox_completion_report: 'Sandbox completion report',
            sla_type_sandbox_completion_certificate_review: 'Sandbox completion certificate review',
            sla_type_sandbox_dossier_validity_check: 'Sandbox dossier validity check',
            sla_type_sandbox_extension_application: 'Sandbox extension application',
            sla_type_sandbox_extension_decision: 'Sandbox extension decision',
            sla_type_sandbox_transitional_extension_request: 'Transitional operation extension request',
            sla_type_sandbox_transitional_extension_decision: 'Transitional operation extension decision',
            sla_type_sandbox_periodic_report_level_1_2: 'Sandbox periodic report (level 1–2)',
            sla_type_sandbox_periodic_report_level_3: 'Sandbox periodic report (level 3)',
            sla_type_sandbox_serious_incident_report: 'Sandbox serious incident report',
            sla_type_self_sovereign_log_sync_readiness: 'Self-sovereign log sync readiness',
            sla_type_serious_incident_final_report: 'Serious incident final report',
            sla_type_serious_incident_preliminary_other: 'Serious incident preliminary report (other)',
            sla_type_serious_incident_preliminary_severe: 'Serious incident preliminary report (severe)',
            sla_type_state_agency_ai_impact_assessment_before_use: 'State-agency AI impact assessment (before use)',
            sla_type_supply_chain_reassessment_cycle: 'Supply-chain reassessment cycle',
            sla_type_surveillance_audit_annual: 'Surveillance audit (annual)',
            sla_type_telecom_breach_authority_notification: 'Telecom breach — authority notification',
            sla_type_telecom_breach_authority_supplement: 'Telecom breach — authority supplement',
            sla_type_telecom_breach_subject_notification: 'Telecom breach — subject notification',
            sla_type_third_party_register_annual_reporting: 'Third-party register annual reporting',
            sla_type_tlpt_cycle: 'TLPT cycle',
            sla_type_traceability_system_connection_start: 'Traceability system connection start',
            sla_type_traffic_data_erasure_review: 'Traffic data erasure review',
            sla_type_transition_existing_systems: 'Transition — existing systems',
            sla_type_transition_new_systems: 'Transition — new systems',
            sla_type_transition_readiness: 'Transition readiness',
            guidance_related_link: 'Aiheeseen liittyvät ohjeet',
            deadlines_empty: 'Tälle sääntelykehykselle ei löytynyt sääntelymääräaikoja.',
            deadlines_sla_type: 'SLA-tyyppi',
            deadlines_value: 'Määräaika',
            deadlines_unit: 'Yksikkö',
            deadlines_extension: 'Jatkoaika',
            deadlines_last_day: 'Last day: {date}',
            deadlines_date_basis_computed: 'computed from the {period} period under the counting rule the legal reference names; not printed in the text',
            deadlines_date_basis_printed: 'printed in the signed text',
            deadlines_date_basis_next_working_day: 'an end on a non-working day moves to the next working day',
            deadlines_reference: 'Oikeudellinen viite',
            framework_stack: 'Kehysrakenne',
            layer_parent_law: 'Peruslaki',
            layer_implementing_decree: 'Täytäntöönpanoasetus',
            layer_enforcement_decree: 'Rangaistusasetus',
            layer_admin_procedures: 'Hallintomenettelyt',
            // fix-sprint-20260912-1003 — Framework Stack layer groups and row states.
            layer_prime_minister_decision: 'Prime Minister Decision',
            layer_official_roadmap: 'Reference',
            layer_child_instrument: 'Alisteiset säädökset',
            stack_status_in_force: 'In force',
            stack_status_in_force_reference: 'In force (reference)',
            stack_status_in_force_until: 'In force to {date}',
            stack_status_from_date: 'From {date}',
            stack_status_draft: 'Draft',
            stack_status_promulgated: 'Promulgated {date}',
            stack_status_superseded: 'Superseded {date}',
            stack_status_reference: 'Reference',
            stack_status_unknown: 'Status not recognised',
            stack_show_lineage: 'Show lineage',
            stack_became: 'enacted as this instrument on {date}',
            stack_replaced_by: 'replaced by this instrument on {date}',
            stack_became_law: 'enacted as this law on {date}',
            stack_replaced_by_law: 'replaced by this law on {date}',
            stack_has_lineage_promulgated: 'enacted from a draft',
            stack_has_lineage_superseded: 'replaced an earlier instrument',
            stack_no_framework_page: 'no framework page',
            status_active: 'Aktiivinen',
            status_draft: 'Luonnos',
            draft_enforcement_badge: 'Luonnos — ei vielä säädetty',
            effective_date_draft: 'Luonnos — ei vielä voimassa',
            effective_date: 'Voimaantulopäivä',
            forms_table_id: 'Lomake-ID',
            forms_table_name: 'Lomakkeen nimi',
            forms_table_source: 'Lähde',
            forms_table_category: 'Kategoria',
            forms_table_type: 'Tyyppi',
            fill_form: 'Täytä lomake',
            view_reference: 'Näytä viite',
            forms_empty: 'Lomakkeita ei löytynyt tälle kehykselle.',
            forms_loading: 'Ladataan lomakkeita...',
            filter_by_source_layer: 'Suodata lähdekerroksen mukaan',
            all_layers: 'Kaikki kerrokset',
            procedures_id: 'Menettely-ID',
            procedures_name: 'Menettely',
            procedures_trigger: 'Laukaisija',
            procedures_steps: 'Vaiheet',
            procedures_outputs: 'Tulokset',
            procedures_related_forms: 'Liittyvät lomakkeet',
            procedures_submission_channels: 'Lähetyskanavat',
            procedures_empty: 'Menettelyjä ei löytynyt tälle kehykselle.',
            fines_area: 'Rikkomusalue',
            fines_articles: 'Artiklat',
            fines_individual: 'Sakko (yksityishenkilö)',
            fines_organization: 'Sakko (organisaatio)',
            fines_revenue: 'Liikevaihtoeskalaatio',
            fines_deadlines: 'Määräajat',
            fines_remediation: 'Korjaustoimenpiteet',
            fines_related: 'Liittyvät menettelyt',
            fines_empty: 'Tälle kehykselle ei ole saatavilla täytäntöönpanotietoja.',
            // fix-sprint D1 F22 — `fetchWatchlistFrameworks`'s catch set an empty
            // watchlist with no flag, so a failed read printed "No draft or upcoming
            // frameworks" — a POSITIVE finding about the regulatory horizon, and the
            // most consequential thing that tab can say wrongly.
            watchlist_load_failed: 'The draft and upcoming frameworks could not be read. Reload to try again.',
            watchlist_retry_subject: 'the regulatory watchlist',
            /*
              fix-sprint D1 F9 — the six regulatory-content tabs on the framework
              detail view swallowed every read failure into an empty array and then
              STATED absence: "No requirements found for this framework.", "No forms
              found…", "No procedures found…", "No guidance content…". On a
              regulatory surface "this framework declares no requirements" is a claim
              an operator acts on, and a failed read is not that claim. Each tab now
              states the failure instead, beside the reload that clears it.
            */
            content_reload: 'Reload',
            content_reloading: 'Reloading…',
            requirements_load_failed: 'The requirements for this framework could not be read. Reload to try again.',
            forms_load_failed: 'The forms for this framework could not be read. Reload to try again.',
            procedures_load_failed: 'The procedures for this framework could not be read. Reload to try again.',
            deadlines_load_failed: 'The deadlines for this framework could not be read. Reload to try again.',
            fines_load_failed: 'The enforcement data for this framework could not be read. Reload to try again.',
            guidance_load_failed: 'The guidance for this framework could not be read. Reload to try again.',
            content_partial_failed: 'Part of this content could not be read, so what is shown may be incomplete. Reload to try again.',
            requirements_retry_subject: 'the requirements for this framework',
            forms_retry_subject: 'the forms for this framework',
            procedures_retry_subject: 'the procedures for this framework',
            deadlines_retry_subject: 'the deadlines for this framework',
            fines_retry_subject: 'the enforcement data for this framework',
            guidance_retry_subject: 'the guidance for this framework',
            fines_risk_type: 'Riskityyppi',
            fines_risk_triggers: 'Mikä sen laukaisee',
            fines_risk_only_note: 'Nämä merkinnät kuvaavat vaatimustenmukaisuus- ja toimintariskiä, eivät rahamääräisiä seuraamuksia. Mahdolliset sakot määritellään emossäädöksessä.',
            fines_enforcement_pending: 'Täytäntöönpanokehystä ei ole vielä julkaistu. Seuraamustiedot lisätään, kun täytäntöönpanosäädös on annettu.',
            view_details: 'Näytä tiedot',
            back_to_list: 'Takaisin luetteloon',
            description: 'Kuvaus',
            jurisdiction: 'Toimivalta',
            lineage: 'Alkuperä',
            replaces: 'Korvaa',
            replaced_by: 'Korvattu',
            requirements: 'Vaatimukset',
            tab_navigation: 'Kehyksen yksityiskohdat -välilehdet',
            forms_table_actions: 'Toiminnot',
            create_form: 'Luo lomake',
            mark_promulgated: 'Merkitse julkaistuksi',
            status_promulgated: 'Julkaistu',
            promulgated_success: 'Kerroksen tila päivitetty julkaistuksi',
            transition_review: 'Siirtymäkatselmus',
            transition_review_desc: 'Tällä sääntelykehyksellä on odottava siirtymäkatselmus seuraajalain käyttöönottoa varten.',
            transition_review_by: 'Katselmuksen määräaika',
            transition_review_status: 'Katselmuksen tila',
            transition_review_notes: 'Huomautukset',
            transition_review_pending: 'Odottaa',
            transition_review_in_progress: 'Käynnissä',
            transition_review_completed: 'Valmis',
            transition_review_successor: 'Seuraava kehys',
            transition_review_lead_time: 'Valmisteluaika',
            transition_review_lead_time_days: '{days} päivää ennen voimaantulopäivää',
            transition_review_overdue: 'overdue',
            transition_review_forms_heading: 'Lomakkeiden siirtymäkatselmukset',
            transition_review_forms_desc: 'Tarkista kunkin operatiivisen mallin tarpeellisuus seuraavan kehyksen alla.',
            transition_review_outcome: 'Lopputulos',
            transition_review_outcome_carry_forward: 'Säilytetään',
            transition_review_outcome_carry_forward_revised: 'Säilytetään (muokattuna)',
            transition_review_outcome_retire: 'Poistetaan',
            transition_review_outcome_split: 'Jaetaan',
            transition_review_outcome_awaiting_decree: 'Odottaa asetusta',
            transition_review_no_forms: 'Lomakkeiden siirtymäkatselmuksia ei ole määritetty.',
            transition_review_update_success: 'Siirtymäkatselmus päivitetty onnistuneesti',
            transition_review_operational_note: 'Nämä ovat prosessia tukevia operatiivisia malleja, eivät lakisääteisiä lomakemääriä. Oikeudellinen luokitus pysyy vanhassa kehyksessä seuraajalain voimaantuloon asti.',
            rights_tab: 'Oikeudet',
            rights_empty: 'Tälle kehykselle ei ole määritelty rekisteröidyn oikeuksia.',
            rights_code: 'Koodi',
            rights_name: 'Oikein',
            rights_legal_basis: 'Oikeusperusta',
            rights_elaborating_articles: 'Artikkelien laatiminen',
            rights_description: 'Kuvaus',
            requirements_reference: 'Viite',
            requirements_mandatory: 'Pakollinen',
            // Sprint 62.58.0 (AC-B1, AC-B3) — whom each requirement addresses, and the filter over the declared classes.
            requirements_addressed_to: 'Addressed to',
            requirements_addressee_filter_hint: 'Shows the requirements addressed to any class you choose. Nothing is chosen for you.',
            requirements_addressee_count: 'Showing {shown} of {total} requirements',
            requirements_addressee_clear: 'Show all requirements',
            requirements_basis_note_show: 'Read the whole note',
            requirements_basis_note_hide: 'Show less',
            requirements_yes: 'Yes',
            requirements_no: 'No',
            requirements_source: 'lähde',
            category_legal_basis: 'Oikeusperusta',
            category_data_subject_rights: 'Rekisteröidyn oikeudet',
            category_notification: 'Ilmoitus',
            category_assessment: 'Arviointi',
            category_governance: 'Hallinto',
            category_record_keeping: 'Kirjanpito',
            category_cross_border: 'Rajat ylittävä',
            category_technical_measures: 'Tekniset toimenpiteet',
            category_incident_reporting: 'Tapahtumaraportointi',
            category_risk_management: 'Riskienhallinta',
            category_supply_chain: 'Toimitusketju',
            category_certification: 'Sertifiointi',
            child_frameworks: '{count} alikehys',
            child_frameworks_plural: '{count} alikehykset',
            replaces_prefix: 'Korvaa:',
            replaced_by_prefix: 'Korvattu:',
            responsible_prefix: 'Vastuuhenkilö:',
            watchlist_empty_hint: 'Kehykset, joiden tila on "luonnos" tai "tuleva", näkyvät täällä seurantaa varten.',
            framework_count: '{count}',
            framework_count_plural: '{count} kehyksiä',
            hiding_legacy: '(piilotetaan {count}:n perintö)',
            label_VN_PDPD_DECREE_13: 'PDPD-asetus 13/2023',
            label_VN_PDPL_LAW_2025: 'PDPL Laki 91/2025',
            label_VN_PDPL_DECREE_356_2025: 'Asetus 356/2025/ND-CP',
            label_VN_PDPL_ADMIN_PROCEDURES_778_2026: 'QĐ 778/QĐ-BCA-A05',
            label_VN_PDPL_ENFORCEMENT_DRAFT_2026: 'PDPL Täytäntöönpanoehdotus 2026',
            label_VN_MOPS_ADMIN_SIMPLIFICATION_NQ22_2026: 'NQ22 Temporary Overlay 2026',
            label_VN_DATA_LAW: 'Tietolaki',
            label_VN_DATA_LAW_DECREE_165_2025: 'Tietolakiasetus 165/2025',
            label_VN_DATA_EXCHANGES_DRAFT_2026: 'Datapörssiluonnos 2026',
            label_VN_TELECOM_LAW_2023: 'Televiestintälaki 2023',
            label_VN_TELECOM_DECREE_163_2024: 'Televiestintäasetus 163/2024',
            label_VN_CYBERSEC_LAW: 'Kyberturvalaki (24/2018)',
            label_VN_CYBERSEC_DECREE_53: 'Cybersec-asetus 53/2022',
            label_VN_CYBERSEC_LAW_2025: 'Kyberturvallisuuslaki 116/2025',
            label_VN_ECOMMERCE_LAW_2025: 'Sähköistä kaupankäyntiä koskeva laki 122/2025',
            label_VN_AI_LAW_2026: 'AI Laki 2026',
            label_VN_AI_DECREE_142_2026: 'AI Decree 142/2026/NĐ-CP',
            issuer_company: 'Yritys',
            issuer_authority_a05_mps: 'MPS/A05',
            issuer_authority_mic: 'MIC',
            issuer_authority_mps_ndc: 'MPS/NDC',
            issuer_other_authority: 'Viranomainen',
            sla_type_rights_request_ack: 'Oikeuspyynnön vahvistus',
            'sla_type_Access control review': 'Kulunvalvonnan tarkistus',
            'sla_type_Annual governance attestation': 'Vuosittainen hallintotodistus',
            'sla_type_Audit review cycle': 'Tarkastuskierros',
            'sla_type_Authority request response': 'Viranomaisten vastauspyyntö',
            'sla_type_Change notification processing': 'Muuta ilmoitusten käsittelyä',
            'sla_type_Complaint acknowledgment': 'Valituksen kuittaus',
            'sla_type_Complaint resolution': 'Valituksen ratkaisu',
            'sla_type_Confidentiality policy review': 'Luottamuksellisuuskäytännön tarkistus',
            'sla_type_Cybersecurity incident full report': 'Kyberturvallisuushäiriön täydellinen raportti',
            'sla_type_Cybersecurity incident initial report': 'Kyberturvallisuushäiriön alustava raportti',
            'sla_type_Data inventory review': 'Tietojen inventaarion tarkistus',
            'sla_type_Disclosure log review cycle': 'Ilmoituslokin tarkistusjakso',
            'sla_type_Incident classification decision': 'Tapahtuman luokittelupäätös',
            'sla_type_Information system classification review': 'Tietojärjestelmän luokituksen tarkistus',
            'sla_type_Notification processing (sales website/app)': 'Ilmoitusten käsittely (myyntisivusto/sovellus)',
            'sla_type_Pen test cycle': 'Kynän testisykli',
            'sla_type_Registration processing (service platform)': 'Rekisteröinnin käsittely (palvelualusta)',
            'sla_type_Risk assessment cycle': 'Riskinarviointisykli',
            'sla_type_Takedown decision (after review)': 'Poistopäätös (tarkistuksen jälkeen)',
            'sla_type_Termination notice (public)': 'Irtisanominen (julkinen)',
            sla_type_ai_impact_assessment_completion: 'AI vaikutustenarvioinnin päätökseen',
            sla_type_ai_sandbox_application_review: 'AI hiekkalaatikkohakemuksen tarkistus',
            sla_type_ai_serious_incident_report: 'AI raportti vakavasta tapauksesta',
            sla_type_ai_system_registration: 'AI järjestelmän rekisteröinti',
            sla_type_authority_request_response: 'Viranomaisten vastauspyyntö',
            sla_type_certificate_issuance_review: 'Varmenteen myöntämisen tarkistus',
            sla_type_complaint_handling_approach_notice: 'Huomio valituksen käsittelystä',
            sla_type_complaint_incomplete_info_request: 'Valitus epätäydellinen tietopyyntö',
            sla_type_data_governance_attestation: 'Tietojen hallinnoinnin todistus',
            sla_type_dsr_acknowledgment: 'DSR-tunnustus',
            sla_type_dsr_cl2_withdraw_restrict_object: 'DSR (Cl.2) — Poista / rajoita / objekti',
            sla_type_dsr_cl2_withdraw_restrict_object_third_party: 'DSR (Cl.2) — Kolmas osapuoli',
            sla_type_dsr_cl3_access_correct_provide: 'DSR (Cl.3) — Käytä / korjaa / tarjoa',
            sla_type_dsr_cl3_access_correct_provide_third_party: 'DSR (Cl.3) — Kolmas osapuoli',
            sla_type_dsr_cl4_deletion: 'DSR (Cl.4) — Poisto',
            sla_type_dsr_cl4_deletion_third_party: 'DSR (Cl.4) — Poisto, kolmas osapuoli',
            sla_type_dsr_cl3_access_third_party: 'DSR (Cl.3) — Access, third party',
            sla_type_chemical_incident_system_declaration: 'Chemical incident — declaration to the System',
            sla_type_code_termination_explanation_window: 'Code termination — window to explain',
            sla_type_code_restoration_by_managing_body: 'Code restoration by the managing body',
            sla_type_traceability_data_update_frequency: 'Traceability data — update frequency',
            sla_type_authority_decision_deadline: 'Authority decision — due date',
            sla_type_authority_list_update_cycle: 'Authority list — update cycle',
            sla_type_dsr_cl4_provide_restrict_third_party: 'DSR (Cl.4) — Restriction, third party',
            sla_type_dsr_cl5_protective_measures: 'DSR (Cl.5) — Suojatoimenpiteet',
            sla_type_telecom_authority_request_response: 'Televiestintäviranomaisen vastauspyyntö',
            sla_type_rights_request_completion: 'Oikeuspyynnön täyttö',
            sla_type_incident_authority_notification: 'Viranomaisilmoitus tapahtumasta',
            sla_type_incident_subject_notification: 'Tapahtuman aiheilmoitus',
            sla_type_dpia_completion: 'DPIA suoritettu',
            sla_type_data_retention_review: 'Tietojen säilyttämisen tarkistus',
            sla_type_authority_filing_review: 'Viranomaisen hakemuksen tarkistus',
            sla_type_authority_supplement_response: 'Viranomaisen täydennysvastaus',
            sla_type_authority_resubmission: 'Viranomaisen uudelleenlähetys',
            sla_type_authority_filing_supplement_response: 'Viranomaisen täydennysvastaus',
            sla_type_authority_filing_resubmission: 'Viranomaisen hakemuksen uudelleenlähetys',
            sla_type_authority_delivery_window: 'Authority delivery window',
            sla_type_authority_examination_window: 'Authority examination window',
            sla_type_authority_notice_trigger: 'Authority notice trigger',
            sla_type_authority_processing_window: 'Authority processing window',
            sla_type_before_event: 'Before the event',
            sla_type_instalment_maximum_period: 'Instalment maximum period',
            sla_type_lead_time_before_event: 'Lead time before the event',
            sla_type_reporting_cycle: 'Reporting cycle',
            sla_type_reporting_period: 'Reporting period',
            sla_type_statutory_filing_window: 'Statutory filing window',
            sla_type_statutory_payment_deadline: 'Statutory payment deadline',
            sla_type_taxpayer_filing_window: 'Taxpayer filing window',
            sla_type_taxpayer_notification_window: 'Taxpayer notification window',
            sla_type_taxpayer_response_window: 'Taxpayer response window',
            sla_type_taxpayer_supplementation_window: 'Taxpayer supplementation window',
            sla_type_transition_deadline: 'Transition deadline',
            sla_type_provision_end_date: 'Provision ceases to apply',
            sla_type_authority_notification_window: 'Authority notification window',
            sla_type_enforcement_trigger: 'Enforcement applies from',
            sla_type_authority_advance_notice: 'Authority advance notice',
            sla_type_refund_limitation_period: 'Refund limitation period',
            sla_type_reinspection_limitation_period: 'Re-inspection limitation period',
            sla_type_violation_classification_threshold: 'Violation classification threshold',
            sla_type_write_off_eligibility_threshold: 'Write-off eligibility threshold',
            // Sprint 62.57.0 Slice E — the certificate register's period rows (stream P's sla_types).
            sla_type_certificate_application_evaluation: 'Evaluation of the certificate application',
            sla_type_certificate_deficiency_notice: 'Notice that the certificate application must be supplemented',
            sla_type_certificate_replacement_decision: 'Decision on re-issue or exchange of the certificate',
            sla_type_certificate_revoked_return: 'Return of a revoked certificate',
            unit_working_days: 'työpäiviä',
            unit_calendar_days: 'kalenteripäiviä',
            unit_hours: 'tuntia',
            form_category_dpa: 'DPA',
            form_category_dsr: 'DSR',
            form_category_consent: 'Suostumus',
            form_category_privacy: 'Yksityisyys',
            form_category_breach: 'Rikkominen',
            form_category_dpia: 'DPIA',
            form_category_register: 'Rekisteröidy',
            form_category_monitoring: 'Valvonta',
            form_category_vendor: 'Myyjä',
            form_category_org: 'Organisatorinen',
            form_category_sector: 'sektori',
            form_category_data_mapping: 'Tietojen kartoitus',
            form_category_governance: 'Hallinto',
            form_category_incident: 'Tapahtuma',
            form_category_authority_submission: 'Viranomaisen esitys',
            form_category_notice: 'Huomaa',
            form_category_assessment_report: 'Arviointiraportti',
            form_category_request: 'Pyyntö',
            form_category_dossier: 'Asiakirja',
            form_category_filing: 'Arkistointi',
            module_consent: 'Suostumus',
            module_rights_request: 'Oikeuspyyntö',
            module_classification: 'Luokittelu',
            module_dpia: 'DPIA',
            module_governance: 'Hallinto',
            module_monitoring: 'Valvonta',
            module_register: 'Rekisteröidy',
            module_risk: 'Riski',
            module_data_mapping: 'Tietojen kartoitus',
            module_incident: 'Tapahtuma',
            module_assessment: 'Arviointi',
            module_vendor: 'Myyjä',
            module_policy: 'politiikka',
            module_audit: 'Tarkastus',
            module_training: 'Koulutus',
            module_discovery: 'Löytö',
            // Sprint 62.58.0 (walk O-2) — requirement applicable_module values with no label
            module_sector_licensing: 'Sector Licensing',
            module_electronic_transactions: 'Electronic Transactions',
            module_consumer_protection: 'Consumer Protection',
            module_ecommerce_governance: 'E-Commerce Governance',
            module_identity_verification: 'Identity Verification',
            module_program_governance: 'Program Governance',
            module_compliance_operations: 'Compliance Operations',
            module_vendor_governance: 'Vendor Governance',
            module_cybersecurity: 'Cybersecurity',
            module_authority_direction: 'Authority Direction',
            module_incident_response: 'Incident Response',
            module_data_governance: 'Data Governance',
            module_incident_operations: 'Incident Operations',
            module_regulatory_disclosure: 'Regulatory Disclosure',
            module_customer_protection: 'Customer Protection',
            module_regulatory_change: 'Regulatory Change',
            module_third_party_risk: 'Third-Party Risk',
            module_legal_register: 'Legal Register',
            module_change_control: 'Change Control',
            module_records: 'Records',
            enum_guidance_type_calculation_guidance: 'Laskentaopas',
            enum_guidance_type_implementation_guidance: 'Käyttöönotto-opas',
            enum_guidance_type_interpretive_note: 'tulkitseva huomautus',
            enum_guidance_type_legal_guidance: 'Oikeudelliset ohjeet',
            enum_guidance_type_procedural_guidance: 'Menettelyohjeet',
            enum_guidance_type_sector_guidance: 'Alakohtaiset ohjeet',
            enum_guidance_type_technical_methodology: 'Tekniset menetelmät',
            enum_guidance_category_annual_reporting: 'Vuosiraportointi',
            enum_guidance_category_authority_cooperation: 'Viranomaisten yhteistyö',
            enum_guidance_category_authority_coordination: 'Viranomaisten koordinointi',
            enum_guidance_category_business_continuity: 'Liiketoiminnan jatkuvuus',
            enum_guidance_category_capital_adequacy: 'Vakavaraisuus',
            enum_guidance_category_complaint_handling: 'Valituksen käsittely',
            enum_guidance_category_consent: 'Suostumus',
            enum_guidance_category_consent_management: 'Suostumuksen hallinta',
            enum_guidance_category_consumer_protection: 'Kuluttajansuoja',
            enum_guidance_category_control_environment: 'Ohjausympäristö',
            enum_guidance_category_cross_border: 'Rajat ylittävä',
            enum_guidance_category_cross_border_transfers: 'Rajat ylittävät siirrot',
            enum_guidance_category_data_classification: 'Tietojen luokitus',
            enum_guidance_category_data_localization: 'Tietojen lokalisointi',
            enum_guidance_category_data_protection_impact_assessment: 'Tietosuojavaikutusten arviointi',
            enum_guidance_category_direct_marketing: 'Suoramarkkinointi',
            enum_guidance_category_dpia: 'DPIA',
            enum_guidance_category_governance: 'Hallinto',
            enum_guidance_category_ict_risk_management: 'ICT-riskinhallinta',
            enum_guidance_category_incident_management: 'Tapahtumanhallinta',
            enum_guidance_category_internal_audit: 'Sisäinen tarkastus',
            enum_guidance_category_isms_documentation: 'ISMS-dokumentaatio',
            enum_guidance_category_lawful_basis: 'Laillinen perusta',
            enum_guidance_category_licensing: 'Lisensointi',
            enum_guidance_category_local_presence: 'Paikallinen läsnäolo',
            enum_guidance_category_logging_and_retention: 'Kirjaaminen ja säilyttäminen',
            enum_guidance_category_logical_and_physical_access: 'Looginen ja fyysinen pääsy',
            enum_guidance_category_operational_resilience_testing: 'Käyttökestävyystestaus',
            enum_guidance_category_outsourcing: 'Ulkoistaminen',
            enum_guidance_category_platform_registration: 'Alustan rekisteröinti',
            enum_guidance_category_quality_of_service: 'Palvelun laatu',
            enum_guidance_category_risk_classification: 'Riskiluokitus',
            enum_guidance_category_risk_management: 'Riskienhallinta',
            enum_guidance_category_scope_definition: 'Soveltamisalan määritelmä',
            enum_guidance_category_sectoral_localisation: 'Sektoraalinen lokalisointi',
            enum_guidance_category_subscriber_protection: 'Tilaajan suojaus',
            enum_guidance_category_supply_chain: 'Toimitusketju',
            enum_guidance_category_system_classification: 'Järjestelmän luokitus',
            enum_guidance_category_system_operations: 'Järjestelmän toiminnot',
            enum_guidance_category_telecommunications_data: 'Tietoliikennetiedot',
            enum_guidance_category_third_party_risk: 'Kolmannen osapuolen riski',
            enum_guidance_category_transitional_provisions: 'Siirtymäsäännökset',
            enum_guidance_category_vulnerability_management: 'Haavoittuvuuden hallinta',
            enum_guidance_status_active: 'Aktiivinen',
            enum_guidance_status_draft: 'Luonnos',
            // Sprint 62.58.0 (walk D-1) — every guidance status a pack declares
            enum_guidance_status_deprecated: 'Deprecated',
            enum_guidance_status_superseded: 'Superseded',
            enum_guidance_status_upcoming: 'Upcoming',
            enum_guidance_status_legacy: 'Legacy',
            enum_guidance_status_temporary_active: 'Temporarily in effect',
            enum_guidance_status_active_reference: 'In force, reference only',
            enum_module_access_management: 'Käyttöoikeuksien hallinta',
            enum_module_ai_governance: 'AI Hallinto',
            enum_module_asset_management: 'Omaisuudenhoito',
            enum_module_audit_trail: 'Audit Trail',
            enum_module_breach_notification: 'Rikkomusilmoitus',
            enum_module_capital_adequacy: 'Vakavaraisuus',
            enum_module_classification: 'Luokittelu',
            enum_module_consent: 'Suostumus',
            enum_module_consent_management: 'Suostumuksen hallinta',
            enum_module_cross_border_transfers: 'Rajat ylittävät siirrot',
            enum_module_data_localization: 'Tietojen lokalisointi',
            enum_module_data_mapping: 'Tietojen kartoitus',
            enum_module_data_protection: 'Tietosuoja',
            enum_module_data_subject_rights: 'Rekisteröidyn oikeudet',
            enum_module_dpia: 'DPIA',
            enum_module_encryption: 'Salaus',
            enum_module_governance: 'Hallinto',
            enum_module_incident: 'Tapahtuma',
            enum_module_incident_management: 'Tapahtumanhallinta',
            enum_module_internal_audit: 'Sisäinen tarkastus',
            enum_module_isms_documentation: 'ISMS-dokumentaatio',
            enum_module_isms_governance: 'ISMS:n hallinto',
            enum_module_logging_and_monitoring: 'Kirjaaminen ja seuranta',
            enum_module_monitoring: 'Valvonta',
            enum_module_policy_management: 'Politiikan hallinta',
            enum_module_risk: 'Riski',
            enum_module_risk_management: 'Riskienhallinta',
            enum_module_training: 'Koulutus',
            enum_module_vendor: 'Myyjä',
            enum_module_vendor_management: 'Myyjän hallinta',
            enum_module_vendor_risk: 'Myyjän riski',
            enum_module_vulnerability_management: 'Haavoittuvuuden hallinta',
            // Sprint 62.58.0 (walk O-6) — English copy; translated in Phase 25 (R-57).
            enum_guidance_type_annex_reference: 'Annex Reference',
            enum_guidance_type_boundary_guidance: 'Boundary Guidance',
            enum_guidance_type_calculation_methodology: 'Calculation Methodology',
            enum_guidance_type_classification_guidance: 'Classification Guidance',
            enum_guidance_type_cross_framework_mapping: 'Cross-Framework Mapping',
            enum_guidance_type_enforcement_mechanics: 'Enforcement Mechanics',
            enum_guidance_type_follow_on_instrument_watchlist: 'Follow-On Instrument Watchlist',
            enum_guidance_type_framework_overview: 'Framework Overview',
            enum_guidance_type_framework_relationship: 'Framework Relationship',
            enum_guidance_type_framework_relationship_note: 'Framework Relationship Note',
            enum_guidance_type_handling_guidance: 'Handling Guidance',
            enum_guidance_type_implementation_note: 'Implementation Note',
            enum_guidance_type_implementation_timeline: 'Implementation Timeline',
            enum_guidance_type_obligation_map: 'Obligation Map',
            enum_guidance_type_official_reference_artifact: 'Official Reference Artifact',
            enum_guidance_type_operational_boundary: 'Operational Boundary',
            enum_guidance_type_operational_guidance: 'Operational Guidance',
            enum_guidance_type_platform_boundary: 'Platform Boundary',
            enum_guidance_type_readiness_mapping: 'Readiness Mapping',
            enum_guidance_type_reference_note: 'Reference Note',
            enum_guidance_type_regulatory_overview: 'Regulatory Overview',
            enum_guidance_type_requirement_detail: 'Requirement Detail',
            enum_guidance_type_scope_and_applicability: 'Scope and Applicability',
            enum_guidance_type_scope_guidance: 'Scope Guidance',
            enum_module_compliance: 'Compliance',
            enum_guidance_type_source_limitation: 'Source Limitation',
            enum_guidance_type_source_limits: 'Source Limits',
            enum_guidance_type_source_note: 'Source Note',
            enum_guidance_type_technical_reference: 'Technical Reference',
            enum_guidance_type_timeline: 'Timeline',
            enum_guidance_type_transition_guidance: 'Transition Guidance',
            enum_guidance_type_transition_note: 'Transition Note',
            enum_guidance_category_access: 'Access',
            enum_guidance_category_account_eligibility: 'Account Eligibility',
            enum_guidance_category_account_linkage: 'Account Linkage',
            enum_guidance_category_ai_functions: 'AI Functions',
            enum_guidance_category_amendments: 'Amendments',
            enum_guidance_category_annex: 'Annex',
            enum_guidance_category_assessment: 'Assessment',
            enum_guidance_category_audit: 'Audit',
            enum_guidance_category_authority: 'Authority',
            enum_guidance_category_authority_submission: 'Authority Submission',
            enum_guidance_category_boundary: 'Boundary',
            enum_guidance_category_certification: 'Certification',
            enum_guidance_category_claims: 'Platform Claims',
            enum_guidance_category_classification: 'Classification',
            enum_guidance_category_confidentiality: 'Confidentiality',
            enum_guidance_category_connection_procedure: 'Connection Procedure',
            enum_guidance_category_coordination: 'Coordination',
            enum_guidance_category_data_disclosure: 'Data Disclosure',
            enum_guidance_category_data_lifecycle: 'Data Lifecycle',
            enum_guidance_category_data_protection_and_security: 'Data Protection and Security',
            enum_guidance_category_database_regime: 'Database Regime',
            enum_guidance_category_deadlines: 'Deadlines',
            enum_guidance_category_dispute_support: 'Dispute Support',
            enum_guidance_category_document_reuse: 'Document Reuse',
            enum_guidance_category_duty_allocation: 'Duty Allocation',
            enum_guidance_category_effective_date: 'Effective Date',
            enum_guidance_category_electronic_enforcement: 'Electronic Enforcement',
            enum_guidance_category_electronic_submission: 'Electronic Submission',
            enum_guidance_category_enforcement: 'Enforcement',
            enum_guidance_category_enforcement_authority: 'Enforcement Authority',
            enum_guidance_category_enforcement_measures: 'Enforcement Measures',
            enum_guidance_category_evidence: 'Evidence',
            enum_guidance_category_exploitation_duties: 'Exploitation Duties',
            enum_guidance_category_forms_and_transition: 'Forms and Transition',
            enum_guidance_category_framework_map: 'Framework Map',
            enum_guidance_category_framework_mapping: 'Framework Mapping',
            enum_guidance_category_framework_relationships: 'Framework Relationships',
            enum_guidance_category_handling_measures: 'Handling Measures',
            enum_guidance_category_identification: 'Identification',
            enum_guidance_category_identification_code: 'Identification Code',
            enum_guidance_category_implementation: 'Implementation',
            enum_guidance_category_incident: 'Incident',
            enum_guidance_category_legal_basis: 'Legal Basis',
            enum_guidance_category_lifecycle: 'Lifecycle',
            enum_guidance_category_listing: 'Listing',
            enum_guidance_category_mapping: 'Mapping',
            enum_guidance_category_marketing_communications_boundary: 'Marketing Communications Boundary',
            enum_guidance_category_methodology: 'Methodology',
            enum_guidance_category_monitoring: 'Monitoring',
            enum_guidance_category_obligations: 'Obligations',
            enum_guidance_category_penalties: 'Penalties',
            enum_guidance_category_personal_data: 'Personal Data',
            enum_guidance_category_personal_data_protection: 'Personal Data Protection',
            enum_guidance_category_phase_in_timeline: 'Phase-In Timeline',
            enum_guidance_category_platform_boundary: 'Platform Boundary',
            enum_guidance_category_platform_duties: 'Digital Platform Duties',
            enum_guidance_category_platform_operation: 'Digital Platform Operation',
            enum_guidance_category_pricing: 'Pricing',
            enum_guidance_category_privacy: 'Privacy',
            enum_guidance_category_procedures: 'Procedures',
            enum_guidance_category_product_scope: 'Product Scope',
            enum_guidance_category_provenance: 'Provenance',
            enum_guidance_category_public_sector: 'Public Sector',
            enum_guidance_category_readiness: 'Readiness',
            enum_guidance_category_record_keeping: 'Record Keeping',
            enum_guidance_category_reference_data: 'Reference Data',
            enum_guidance_category_regulatory_watch: 'Regulatory Watch',
            enum_guidance_category_related_instruments: 'Related Instruments',
            enum_guidance_category_remediation: 'Remediation',
            enum_guidance_category_reporting: 'Reporting',
            enum_guidance_category_sanctions: 'Sanctions',
            enum_guidance_category_source: 'Source',
            enum_guidance_category_status: 'Status',
            enum_guidance_category_system_connection: 'System Connection',
            enum_guidance_category_technical_measures: 'Technical Measures',
            enum_guidance_category_testing: 'Testing',
            enum_guidance_category_timeline: 'Timeline',
            enum_guidance_category_transition: 'Transition',
            enum_guidance_category_transitional: 'Transitional Rules',
            enum_module_access: 'Access',
            enum_module_access_review: 'Access Reviews',
            enum_module_ai_advisor: 'AI Advisor',
            enum_module_assessment: 'Assessment',
            enum_module_audit: 'Audit',
            enum_module_authority_operations: 'Authority Operations',
            enum_module_authority_direction: 'Authority Direction',
            enum_module_continuity: 'Business Continuity',
            enum_module_deidentification: 'De-identification',
            enum_module_deletion_orchestration: 'Deletion Orchestration',
            enum_module_identity_verification: 'Identity Verification',
            enum_module_legal_matters: 'Legal Matters',
            enum_module_program_governance: 'Program Governance',
            enum_module_rights_request: 'Rights Request',
            enum_module_sector_licensing: 'Sector Licensing',
            enum_module_technical_measures: 'Technical Measures',
            category_access: 'Pääsy',
            category_availability: 'Saatavuus',
            category_change_management: 'Muutosten hallinta',
            category_classification: 'Luokittelu',
            category_communication_and_information: 'Viestintä ja tiedotus',
            category_compliance: 'Vaatimustenmukaisuus',
            category_confidentiality: 'Luottamuksellisuus',
            category_control_activities: 'Ohjaustoiminnot',
            category_control_environment: 'Ohjausympäristö',
            category_incident: 'Tapahtuma',
            category_internal_audit: 'Sisäinen tarkastus',
            category_internal_control: 'Sisäinen valvonta',
            category_logical_and_physical_access: 'Looginen ja fyysinen pääsy',
            category_mis_data: 'Hallintotietojärjestelmän tiedot',
            category_monitoring: 'Valvonta',
            category_monitoring_activities: 'Seurantatoiminnot',
            category_outsourcing: 'Ulkoistaminen',
            category_privacy: 'Yksityisyys',
            category_processing_integrity: 'Käsittelyn eheys',
            category_reporting: 'Raportointi',
            category_risk_assessment: 'Riskinarviointi',
            category_risk_mitigation: 'Riskien vähentäminen',
            category_sector_applicability: 'Toimialan soveltuvuus',
            category_system_operations: 'Järjestelmän toiminnot',
            category_training: 'Koulutus',
            category_vendor: 'Myyjä',
            guidance_related_forms_advisory: 'Joillakin lomakkeilla on asiaan liittyviä ohjeita – katso',
            guidance_related_procedures_advisory: 'Joihinkin toimenpiteisiin liittyy ohjeita – katso kohdasta',
            guidance_related_requirements_advisory: 'Joihinkin vaatimuksiin liittyy ohjeita – katso kohdasta',
        },
        channels: {
            title: 'Lähetyskanavat ja määräajat',
            channels: 'Lähetyskanavat',
            add_channel: 'Lisää kanava',
            channel_type: 'Tyyppi',
            name: 'Nimi',
            instructions: 'Ohjeet',
            active: 'Aktiivinen',
            no_channels: 'Ei lähetyskanavia määritetty',
            portal: 'Portaali',
            in_person: 'Henkilökohtaisesti',
            postal: 'Posti',
            email: 'Sähköposti',
            add: 'Lisää',
            deadlines: 'Sääntelymääräajat',
            add_deadline: 'Lisää määräaika',
            label: 'Tunniste',
            days: 'Päivät',
            day_type: 'Päivätyyppi',
            calendar_days: 'Kalenteripäivät',
            working_days: 'Työpäivät',
            no_deadlines: 'Ei määräaikoja määritetty',
            description: 'Kuvaus',
            failed_load: 'load channel data epäonnistui',
            channel_added: 'Kanava lisätty',
            channel_added_announce: 'Lähetyskanava lisätty',
            failed_add_channel: 'add channel epäonnistui',
            deadline_added: 'Määräaika lisätty',
            deadline_added_announce: 'Sääntelymääräaika lisätty',
            failed_add_deadline: 'add deadline epäonnistui',
            channel_code: 'Declared channel',
            channel_code_none: 'Not bound to a declared channel',
            channel_code_hint: 'Which channel the instrument provides. Only a channel bound here can be used on a filing.',
            framework_declares_no_channels: 'This framework declares no submission channels',
            framework_not_installed: 'This framework is not installed for your organisation',
        },
        module_links: {
            title: 'Kehyslinkitys',
            modules: 'Linkitetyt moduulit',
            add_module: 'Lisää moduuli',
            module_code: 'Moduulikoodi',
            required: 'Pakollinen',
            link: 'Linkitä',
            no_modules: 'Ei linkitettyjä moduuleita',
            notes: 'Muistiinpanot',
            personas: 'Linkitetyt persoonat',
            add_persona: 'Lisää persoona',
            persona_id: 'Persoonan tunnus',
            role_description: 'Roolin kuvaus',
            no_personas: 'Ei linkitettyjä persoonia',
            failed_load: 'load framework links epäonnistui',
            module_linked: 'Moduuli linkitetty',
            module_linked_announce: 'Moduuli linkitetty onnistuneesti',
            failed_link_module: 'link module epäonnistui',
            persona_linked: 'Persoona linkitetty',
            persona_linked_announce: 'Persoona linkitetty onnistuneesti',
            failed_link_persona: 'link persona epäonnistui',
        },
        regulatory_updates: {
            statuses: {
                pending: 'Odottaa',
                assessed: 'Arvioitu',
                applied: 'Käytetty',
                dismissed: 'Hylätty',
            },
            title: 'Sääntelypäivitykset ja kehysten kojelauta',
            page_description: 'Yhdell? silm?yksell? n?kyv? kattavuus kaikille asennetuille s??ntelyviitekehyksille sek? tuotteen sis?inen muutosloki s??ntelyp?ivityksist?: muutoksista, voimaantulop?ivist? ja vaikutusarvioista.',
            coverage_dashboard: 'Kehyskattavuuden kojelauta',
            no_frameworks: 'Ei kehyksiä löytynyt',
            framework: 'Kehys',
            modules: 'Moduulit',
            templates: 'Mallit',
            forms: 'Lomakkeet',
            channels: 'Kanavat',
            deadlines: 'Määräajat',
            pending: 'Odottavat päivitykset',
            updates: 'Sääntelypäivitykset',
            create_update: 'Luo päivitys',
            framework_id: 'Kehystunnus',
            update_title: 'Otsikko',
            description: 'Kuvaus',
            effective_date: 'Voimaantulopäivä',
            amendment_ref: 'Muutoksen viite',
            create: 'Luo',
            no_updates: 'Ei sääntelypäivityksiä',
            status: 'Tila',
            impact: 'Vaikutus',
            change_summary: 'Muutosyhteenveto',
            assess: 'Arvioi',
            failed_load: 'load data epäonnistui',
            update_created: 'Päivitys luotu',
            update_created_announce: 'Sääntelypäivitys luotu',
            failed_create: 'create update epäonnistui',
            update_assessed: 'Päivitys arvioitu',
            failed_assess: 'assess update epäonnistui',
            col_pack_version: 'Pack Version',
            amendment_ref_inline: 'Amendment reference: {reference}',
            default_assess_note: 'Impact assessed — review complete',
        },
        framework_relationships: {
            panel_title: 'Liittyvät viitekehykset',
            panel_intro: 'Miten tämä viitekehys liittyy muihin. Yksi overlay voi soveltua usean ylemmän viitekehyksen alle samanaikaisesti; kukin näkyy tässä täsmälleen kerran.',
            outgoing_heading: 'Tämä viitekehys koskee',
            permalink_aria: 'Link to {name} in this list',
            incoming_heading: 'Soveltuu tämän viitekehyksen alle',
            outgoing_caption: 'Viitekehykset, joihin tämä viitekehys viittaa',
            incoming_caption: 'Viitekehykset, jotka kuuluvat tämän alle',
            col_framework: 'Viitekehys',
            col_relationship: 'Suhde',
            col_scope: 'Lähde',
            scope_global: 'Paketin sisällöstä',
            scope_org: 'Organisaatiokohtainen',
            load_failed: 'Liittyvien viitekehysten lataus epäonnistui',
            relationship_kinds: {
                parent: 'Ylempi viitekehys',
                applies_to: 'Koskee',
                cross_reference: 'Ristiviittaus',
                supersedes: 'Korvaa',
            },
        },
        sector_overlay: {
            sector_labels: {
                banking: 'Pankkiala',
                insurance: 'Vakuutusala',
                healthcare: 'Terveydenhuolto',
                education: 'Education',
                ict_critical: 'Kriittinen ICT',
                telecom: 'Teleala',
                energy: 'Energia',
                transport: 'Liikenne',
                digital_services: 'Digitaaliset palvelut',
                digital_technology_industry: 'Digitaaliteknologian toimiala',
                manufacturing: 'Manufacturing',
                public_administration: 'Julkishallinto',
                payments: 'Maksupalvelut',
                publishing: 'Kustannusala',
                crypto_assets: 'Kryptovarat',
                other: 'Muu',
            },
            scope_labels: {
                cross_sector: 'Cross-sector',
                security_overlay: 'Security overlay',
                security_control_overlay: 'Security control overlay',
                activity_scope: 'Activity scope',
                process_scope: 'Process scope',
                consumer_facing_process_scope: 'Consumer-facing processes',
                platform_and_einvoice_process_scope: 'Platform and e-invoice processes',
                regulatory_overlay: 'Regulatory overlay',
            },
            sector_label: 'Toimiala',
            direct_scope_label: 'Suora lakis??teinen soveltuvuus',
            chip_direct: 'Soveltuu suoraan',
            chip_reference: 'Vain viitteeksi',
            banner_direct_copy: 'Soveltuu suoraan organisaatioonne. Toimialaraportit ja m??r?ajat ovat k?yt?ss?.',
            banner_reference_copy: 'Vain viitteeksi ? organisaatiotanne ei ole luokiteltu t?h?n toimialaan.',
            edit_mode_button: 'Muokkaa tilaa',
            edit_form_title: 'Vaihda asennustila',
            mode_direct_label: 'Suora ? lakis??teisesti soveltuva',
            mode_direct_help: 'Ottaa k?ytt??n toimialan m??r?ajat ja koontin?yt?n pienoisohjelmat.',
            mode_reference_label: 'Vain viitteeksi',
            mode_reference_help: 'Selattavissa, mutta ilman m??r?aikoja tai pienoisohjelmia.',
            reason_note_label: 'Syy (valinnainen)',
            reason_note_placeholder: 'esim. Hankittiin pankkialan tyt?ryhti?',
            cancel: 'Peruuta',
            confirm: 'Vahvista',
            confirming: 'Tallennetaan?',
            save: 'Tallenna',
            saving: 'Tallennetaan?',
            toast_mode_changed: 'Toimialalis?osan tila p?ivitettiin.',
            toast_installed: 'Toimialalis?osa asennettiin.',
            toast_sectors_saved: 'Toimialaomam??ritys tallennettiin.',
            install_modal_title: 'Asenna toimialalis?osa',
            install_modal_intro: 'Toimialalis?osat ovat valinnaisia s??ntelypaketteja, jotka koskevat yht? toimialaa.',
            install_modal_footer: 'Voit vaihtaa asennustilaa my?hemmin viitekehyksen tietosivulta.',
            install_button: 'Asenna',
            installed_label: 'Asennettu',
            uninstall_button: 'Poista asennus',
            uninstall_confirm: 'Vahvista asennuksen poistaminen',
            uninstall_cancel: 'Peruuta',
            suggested_label: 'Suositeltu',
            catalog_page_title: 'Toimialalis?osat',
            catalog_page_intro: 'Valinnaiset s??ntelypaketit, jotka on rajattu yhteen toimialaan.',
            catalog_empty_title: 'Toimialalis?osia ei ole saatavilla',
            catalog_empty_description: 'Alustallasi julkaistut toimialalis?osapaketit n?kyv?t t??ll?.',
            authority_label: 'Viranomainen',
            org_sectors_title: 'Toimialat',
            org_sectors_intro: 'Valitse toimialat, joilla organisaatiosi toimii.',
            widget_title: 'Er??ntyv?t toimialalis?osan raportit',
            widget_aria_label: 'Er??ntyv?t toimialalis?osan raportit',
            widget_empty: 'Tulevia toimialalis?osan m??r?aikoja ei ole.',
            audit_event_mode_changed: 'Toimialalis?osan asennustila muuttui',
            audit_event_installed: 'Toimialalis?osa asennettiin',
            view_details_button: 'N?yt? tiedot ?',
            banner_aria_label: 'Toimialalis?osan tila',
            toast_load_failed: 'Toimialalis?osien lataaminen ep?onnistui',
            toast_install_failed: 'Toimialalis?osan asennus ep?onnistui',
            toast_uninstall_confirm_required: 'Vahvista asennuksen poisto kirjoittamalla "{packCode}".',
            toast_uninstalled: 'Poistettiin {packCode}.',
            toast_uninstall_failed: 'Toimialalis?osan poistaminen ep?onnistui',
            uninstall_warning_destructive_label: 'Tuhoava toimenpide.',
            uninstall_warning_body: 'Poistaminen poistaa viitekehystietueen sekä kaikki {frameworkCode}-viitekehykseen liittyvät tallennetut sääntelymääräajat tästä organisaatiosta.',
            uninstall_warning_type_to_confirm: 'Vahvista kirjoittamalla {packCode}.',
            uninstall_in_progress: 'Poistetaan asennusta?',
            widget_view_all: 'N?yt? kaikki toimialalis?osat ?',
            widget_due_in_days: 'Er??ntyy {days} pv kuluttua',
            widget_overdue_days: 'My?h?ss? {days} pv',
            widget_due_today: 'Er??ntyy t?n??n',
            widget_due_unknown: '?',
            // Catalog overview, filters and feature-views modal
            catalog_summary_total: 'Overlays',
            catalog_summary_installed: 'Installed',
            catalog_summary_direct: 'Direct',
            catalog_summary_reference: 'Reference only',
            catalog_summary_available: 'Not installed',
            filter_toolbar_label: 'Filter the overlay catalog',
            filter_search_label: 'Search overlays',
            filter_search_placeholder: 'Search by name, code or authority',
            filter_sector_all: 'All sectors and scopes',
            filter_status_all: 'All statuses',
            filter_status_installed: 'Installed',
            filter_status_not_installed: 'Not installed',
            filter_mode_label: 'Install mode',
            filter_mode_all: 'All modes',
            filter_clear: 'Clear filters',
            filter_results: 'Showing {shown} of {total} overlays',
            filter_no_match_title: 'No overlay matches these filters',
            filter_no_match_description: 'Change or clear the filters to see more of the catalog.',
            col_overlay: 'Overlay',
            col_scope: 'Sector or scope',
            col_mode: 'Mode',
            col_authority: 'Authority',
            col_dates: 'Dates',
            col_version: 'Version',
            col_status: 'Status',
            col_views: 'Feature views',
            col_actions: 'Actions',
            date_effective: 'Effective',
            date_issued: 'Issued',
            date_released: 'Released',
            desc_expand: 'Show description',
            desc_collapse: 'Hide description',
            status_not_installed: 'Not installed',
            views_none: 'None',
            views_none_title: 'This overlay declares nothing that a dedicated screen reads.',
            views_button: '{enabled} of {total} enabled',
            views_button_aria: 'Show the feature views that {name} brings into use ({enabled} of {total} enabled)',
            views_modal_title: 'Feature views of {name}',
            views_modal_intro: 'The registers and pages that read what the pack of this overlay declares. A view is enabled once the overlay is installed as Direct and the module it belongs to is switched on.',
            views_col_view: 'View',
            views_col_menu: 'Where in the menu',
            views_col_state: 'State',
            views_open: 'Open',
            views_state_enabled: 'Enabled',
            views_state_reference: 'Reference only — shows nothing until installed as Direct',
            views_state_not_installed: 'Not enabled — overlay not installed',
            views_state_module_off: 'Not enabled — module switched off',
            views_no_access: 'Your role cannot open this view',
            views_module_state_unconfirmed: 'Module settings could not be read, so a switched-off module is not shown here.',
            views_unclaimed_heading: 'Declared without a dedicated screen',
            views_unclaimed_description: 'The pack also declares these record kinds, but no dedicated screen reads them for this overlay: {kinds}.',
            declared_kind_labels: {
                biometric_technical_regulation_commencement: 'Start of the voice and iris biometric technical regulations',
                chemical_traceability_code_termination: 'Chemical traceability code termination',
                chemical_traceability_commencement: 'Start of chemical traceability',
                chemical_traceability_incident_declaration: 'Chemical traceability incident declaration',
                consumer_complaint_receipt_acknowledgement: 'Notice to the consumer that a report, request or complaint was received',
                cybersecurity_fee_schedule_commencement: 'Start of the cybersecurity fee schedule',
                food_database_traceability_commencement: 'Start of food database traceability',
                food_traceability_connection_start: 'Start of the food traceability connection',
                food_traceability_records_request: 'Food traceability records request',
                food_traceability_system_update: 'Food traceability system update',
                product_identification_certificate_receipt: 'Certificate synchronisation receipt',
                product_identification_connection_readiness: 'Connection and transition readiness record',
                product_identification_event_record: 'Significant supply-chain event record',
                product_identification_provider_agreement: 'Service-provider data-responsibility agreement',
                product_identification_scope_determination: 'Product scope and risk-tier determination',
                product_identification_uid_register: 'UID and master-data register entry',
            },
            views_declared_only: 'No dedicated screen',
            views_declared_only_aria: 'Show what {name} declares without a dedicated screen',
            views_basis_records: 'Reads records the pack declares',
            views_basis_regime: 'Reads a regime the pack declares',
            views_basis_fine_areas: 'Uses fine areas the pack declares',
            views_open_framework: 'Open the framework page',
            views_independent_note: 'Screens that work the same with or without an overlay, such as Legal Holds, are not listed.',
            menu_payments_evidence: 'Payments & eKYC Evidence',
            menu_trust_evidence: 'Trust & Identity Evidence',
            menu_platform_evidence: 'Platform Evidence',
            menu_public_data: 'Public Data Exchange',
            menu_product_compliance: 'Product Compliance & Traceability',
            menu_producer_substance: 'Producer & Substance Obligations',
            menu_trade_customs: 'Trade & Customs Evidence',
            menu_parcel_delivery: 'Parcel & Delivery Evidence',
            view_labels: {
                controlled_testing_cases: 'Controlled-Testing Cases',
                credit_information_supply: 'Credit-Information Supply Register',
                authority_directions: 'Authority Directions',
                regulated_accounts: 'Regulated Accounts',
                sector_licences: 'Sector Licences',
                licence_applications: 'Licence Applications and Fees',
                fee_lines_by_period: 'Fee Lines by Period',
                data_service_providers: 'Data-Service Provider Records',
                pcf_shared_information: 'Shared Supervisory Information',
                pcf_branch_publicity: 'Branch Publicity Evidence',
                pcf_branch_information_requests: 'Branch Information Requests',
                pcf_depositor_enquiries: 'Depositor Enquiries',
                pcf_supervision_system_records: 'Supervision System Records',
                data_exploitation_routes: 'Exploitation Routes',
                data_exploitation_requests: 'Requests and Grants',
                data_exploitation_testing: 'Controlled Testing Records',
                data_exploitation_notices: 'Incident Notices',
                data_exploitation_dispositions: 'End-of-Purpose Disposition',
                data_exploitation_incubator: 'Incubator Records',
                data_exploitation_foreign_cooperation: 'Foreign Cooperation Assessments',
                cybersecurity_assurance_index_periods: 'Assessment Periods',
                cybersecurity_assurance_index_statistics: 'Direction and Management Statistics',
                cybersecurity_assurance_index_supplier_requests: 'Supplier Evidence Requests',
                food_traceability_establishment: 'Food Establishment',
                food_traceability_lot_results: 'Lot Test Results',
                food_traceability_inspections: 'Food Inspections',
                food_traceability_warnings: 'Warnings',
                food_traceability_notices: 'Notices and Quantities',
                food_traceability_decisions: 'Violation Decisions',
                food_traceability_corrections: 'Corrections and Supplier Corrections',
                food_traceability_complaints: 'Complaints',
                food_traceability_routes: 'Connection Routes',
                biometric_capture_components: 'Biometric Capture Components',
                biometric_chain_of_custody: 'Biometric Chain-of-Custody Records',
                biometric_readiness: 'Biometric Readiness Reviews',
                biometric_supervision_records: 'Biometric Supervision and Inspection Records',
                product_classifications: 'Product Classifications',
                conformity_certificates: 'Conformity Certificates',
                student_records: 'Student Records',
                publications: 'Publications',
                publication_assessments: 'Publication Assessments',
                regulatory_placeholders: 'Regulatory Placeholders',
                supervisory_reporting: 'Supervisory Reporting',
                aml_programme: 'AML Programme',
                aml_cases: 'Suspicious-Activity Cases',
                identity_exchanges: 'Cross-Border Identity Exchanges',
                marketing_approvals: 'Marketing Approvals',
                investor_verifications: 'Investor Verifications',
                account_data_disclosures: 'Account Data Disclosures',
                regulatory_disclosures: 'Regulatory Disclosures',
                legal_representatives: 'Legal Representatives',
                penalty_readiness: 'Penalty Readiness',
                exposure_inputs: 'Exposure Inputs',
                enforcement_register: 'Enforcement Register',
                enforcement_cases: 'Enforcement Cases',
                identity_register: 'Identity Register',
                trust_providers: 'Trust Providers',
                electronic_submissions: 'Electronic Submissions',
                electronic_agreements: 'Electronic Agreements',
                payment_services: 'Payment Services',
                ekyc_evidence: 'eKYC Evidence',
                exception_review: 'Exception Review',
                retention_reconciliation: 'Retention Reconciliation',
                seller_register: 'Seller Register',
                transaction_reconciliation: 'Transaction Reconciliation',
                authority_correspondence: 'Authority Correspondence',
                dataset_catalog: 'Dataset Catalog',
                sharing_register: 'Sharing Register',
                open_data_releases: 'Open Data Releases',
                product_identification_scope_determinations: 'Product Scope Determinations',
                product_identification_sector_watch: 'Sector Watch',
                product_register: 'Product Register',
                batch_register: 'Batch & Serial Register',
                trace_timeline: 'Trace Timeline',
                conformity_evidence: 'Conformity Evidence',
                epr_reconciliation: 'EPR Reconciliation',
                chemical_register: 'Chemical Register',
                chemical_traceability_declarations: 'Chemical Traceability Declarations',
                customs_evidence: 'Customs Evidence',
                customs_tax_dossiers: 'Tax Dossiers and Received Documents',
                customs_tax_payments: 'Tax Payments and Due Dates',
                customs_tax_duty_free_lists: 'Duty-Free Lists and Usage Notifications',
                customs_tax_examinations: 'Tax Examinations',
                customs_tax_transition: 'Transition and Applicability',
                parcel_claims: 'Claims',
                postal_authorisations: 'Postal Authorisations',
            },
        },
        phased_obligations: {
            panel_title: 'Vaiheistetut velvoitteet',
            panel_aria_label: 'Vaiheistetut velvoitteet',
            panel_intro: 'Jotkin t?m?n viitekehyksen lausekkeet tulevat voimaan my?hemmin kuin viitekehys itse.',
            phased_effective_date_label: 'Vaiheistettu voimaantulo:',
            status_pending: 'Odottaa',
            status_active: 'Aktiivinen',
            status_early: 'Aktiivinen (ennenaikaisesti k?ytt??notettu)',
            notice_window_label: 'Viranomaisen ilmoitusaika:',
            days_label: 'p?iv??',
            recipient_label: 'Vastaanottaja:',
            early_implement_button: 'Merkitse ennenaikaisesti k?ytt??n otetuksi',
            early_implement_closed: 'Early implementation can be recorded only before the phased effective date, {date}.',
            effective_at_before_hint: 'Choose a date before the phased effective date, {date}.',
            effective_at_too_late: 'The date must be before the phased effective date, {date}.',
            early_implement_modal_title: 'Merkitse vaiheistettu velvoite ennenaikaisesti k?ytt??n otetuksi',
            early_implement_modal_intro: 'Recording an early implementation stores the date your organisation began applying this obligation. This panel then shows the obligation as active from that date, and shows the date the notice is due, counted from the recorded date. The platform does not send the notice and keeps no record of it being sent.',
            effective_at_label: 'Voimassa alkaen',
            reason_label: 'Syy (valinnainen)',
            reason_placeholder: 'esim. Hallitus hyv?ksyi varhaisen k?ytt??noton 2026-09-01',
            confirm: 'Vahvista',
            toast_early_implemented: 'Ennenaikainen k?ytt??notto kirjattiin.',
            early_read_failed: 'The early implementations recorded for this framework could not be read. Until they are, the status of each obligation is not shown and none can be marked early-implemented.',
            early_read_subject: 'the early implementations',
            audit_event_early_implemented: 'Vaiheistettu velvoite merkittiin ennenaikaisesti k?ytt??n otetuksi',
            widget_rolling_window_label: 'Vaiheistetut aktivoitumiset 90 p?iv?n sis?ll?',
            notice_due_label: 'Notice due by the end of',
            notice_due_time_suffix: '(organisation time)',
            notice_rolled_note: 'The last day fell on a weekend or holiday, so it moved to the next working day.',
            notice_platform_sends_nothing: 'The platform does not send the notice and keeps no record of it being sent.',
            notice_basis: {
                day_after_is_day_one_last_day_rolls_past_non_working_day: 'Computed: the day after implementation is day 1, the last day moves past a weekend or holiday.',
                day_after_is_day_one: 'Computed: the day after implementation is day 1.',
            },
            notice_gap: {
                no_notice_clock_declared: 'No notice due date: no installed record states the notice period.',
                notice_clock_ambiguous: 'No notice due date: more than one installed record states a notice period, and none says which one applies.',
                notice_clock_not_computable: 'No notice due date: the platform does not compute the period the installed record states.',
                implemented_on_unreadable: 'No notice due date: the recorded date could not be read.',
                notice_clock_not_in_force: 'No notice due date: the framework is not yet in force on the day the notice would fall due.',
                framework_installed_for_reference: 'No notice due date: this framework is installed for reference, so its clocks are not run.',
                notice_clock_activation_unknown: 'No notice due date: whether the clock binds is not known, because the framework record states no date or could not be read.',
                implemented_on_not_before_phased_date: 'No notice due date: the recorded date is on or after the phased effective date, so the obligation applies from then anyway and was not implemented early.',
            },
        },
        deadline_activation: {
            token_live: 'In force',
            token_upcoming: 'Not yet in force',
            in_force_from: 'In force from {date}',
            start_date_not_stated: 'Start date not stated',
            token_unknown: 'Timing unknown',
            unknown_sentence: 'The framework record this deadline belongs to could not be read, so whether it applies yet is not known.',
            retry: 'Retry',
            retry_busy: 'Retrying…',
            // Gate E12.5 pass 1 (P2-3) — the framework record was read and states no date.
            token_phase_unresolved: 'Phase start not dated',
            phase_unresolved_sentence: 'This deadline applies from a phase of its framework, and the framework does not date that phase, so it is not treated as in force.',
            token_not_stated: 'No effective date stated',
            not_stated_sentence: 'The framework states no date from which this deadline applies, so it is not treated as in force.',
            retry_subject: 'the regulatory deadlines',
            column_heading: 'When it applies',
            widget_overlays_failed: 'The installed sector overlays could not be read.',
            widget_reads_failed: 'Deadlines for {count} sector overlay(s) could not be read.',
            widget_retry_subject: 'the sector overlay deadlines',
            settings_sources_label: 'Deadlines behind this suggestion',
            settings_upcoming_not_counted: 'Not used for the suggestion until it is in force.',
            settings_unknown_not_counted: 'Not used for the suggestion.',
            settings_not_calendar_days_not_counted: 'Not used for the suggestion: its period is not counted in calendar days.',
            settings_retry_subject: 'the framework deadlines',
            channels_retry_subject: 'the submission channels and deadlines',
            token_not_owed: 'Not owed',
            not_owed_framework_status: 'The framework is not in force.',
            not_owed_row_suspended: 'This duty is suspended.',
            not_owed_superseded_by: 'Superseded by {framework}.',
            not_owed_superseded_unnamed: 'Superseded by another framework.',
            not_owed_row_legacy: 'Legacy — no longer in force.',
            not_owed_unrecognised: 'The record states that this duty is not owed.',
            settings_not_owed_not_counted: 'Not used for the suggestion: this duty is not owed.',
        },
        // Sprint 62.58.0 (Slice C) — English for key parity until Phase 25 (R-57).
        framework_briefings: {
            summary_export: {
                title: 'Framework summary: {name}',
                section_status: 'Status and dates',
                col_field: 'Field',
                col_value: 'Value',
                field_code: 'Framework code',
                field_status: 'Status',
                field_issued: 'Issued',
                field_effective: 'In force from',
                draft_not_in_force: 'Draft — not yet in force',
                not_stated: 'Not stated',
                section_parent: 'Parent framework',
                parent_line: "This framework is read with its parent framework, {name} ({code}), status: {status}. The dated rows below are the parent framework's, not this framework's.",
                parent_not_installed: 'The parent framework {code} is not installed in this organization, so its dated rows are not shown.',
                no_parent: 'This framework names no parent framework.',
                parent_no_rows: 'No dated rows of the parent framework are in use in this organization.',
                col_belongs_to: 'Belongs to',
                section_guidance: 'Guidance',
                no_guidance: 'No guidance notes are installed for this framework.',
                col_title: 'Title',
                col_summary: 'Summary',
                section_requirements: 'Requirements',
                no_requirements: 'No requirements are installed for this framework.',
                col_requirement_id: 'ID',
                col_reference: 'Reference',
                col_addressed_to: 'Addressed to',
                col_mandatory: 'Mandatory',
                mandatory_yes: 'Yes',
                mandatory_no: 'No',
                mandatory_not_stated: 'Not stated',
                mandatory_no_draft: 'No — proposed in a draft',
                mandatory_no_legacy: 'No — the framework is legacy',
                mandatory_no_suspended: "No — the framework's application is suspended",
                section_deadlines: 'Deadlines',
                no_deadlines: 'No deadline rows are installed for this framework.',
                col_deadline: 'Deadline',
                col_date_or_period: 'Date or period',
                col_note: 'Note',
                stated_date: '{date} (as stated)',
                authority_clock: "The authority's period, not a duty of the organization.",
                section_pack: 'Pack',
                pack_line: 'Installed from pack {code}, version {version}.',
                pack_not_recorded: 'The pack this framework was installed from is not recorded.',
                generated_line: "Generated on {date} from this organization's installed framework record. This summary is for reading only; ComplianceOne submits nothing to any authority.",
            },
            record: {
                tab: 'Briefings',
                heading: 'Briefings and consultation comments',
                intro: 'Record the briefings your organization held about this framework and the consultation comments it sent. An entry is kept as recorded; a correction is a new entry that names the one it corrects.',
                boundary: 'A consultation comment records what your organization sent, to whom and when. ComplianceOne submits nothing to any authority.',
                kind_briefing: 'Briefing',
                kind_consultation_comment: 'Consultation comment',
                channel_meeting: 'Meeting',
                channel_email: 'Email',
                channel_document: 'Document',
                channel_webinar: 'Webinar',
                channel_other: 'Other',
                col_kind: 'Kind',
                col_audience: 'Audience',
                col_held_on: 'Date',
                col_channel: 'Channel',
                col_approver: 'Approver',
                col_approval: 'Approval',
                col_pack_version: 'Pack version',
                approval_none: 'No approver named',
                approval_pending: 'Awaiting approval',
                approval_approved: 'Approved on {date}',
                record: 'Record an entry',
                recording: 'Recording…',
                approve: 'Approve',
                approving: 'Approving…',
                approve_aria: 'Approve the entry of {date} for {audience}',
                field_kind: 'Kind',
                field_audience: 'Audience',
                field_audience_hint_comment: 'For a consultation comment: to whom your organization sent it.',
                field_held_on: 'Date held or sent',
                field_held_on_hint: 'A calendar date, not after today.',
                field_channel: 'Channel',
                field_channel_note: 'Channel details',
                field_channel_note_required: 'Describe the channel when it is Other.',
                field_reference: 'Reference',
                field_reference_hint: 'The reference of what was sent, such as a letter number or a portal receipt.',
                field_summary: 'Summary',
                field_approver: 'Approver',
                field_correction_of: 'Corrects entry',
                field_correction_note: 'Correction note',
                empty: 'No briefings or consultation comments are recorded for this framework.',
                load_failed: 'The briefings and consultation comments could not be loaded.',
                record_failed: 'The entry could not be recorded.',
                approve_failed: 'The entry could not be approved.',
                recorded_toast: 'Entry recorded.',
                approved_toast: 'Entry approved.',
                pack_changed: 'Recorded against pack version {version}; this framework has changed since — see Regulatory Updates',
                corrected_by: 'Corrected by a later entry',
                correction_label: 'Correction',
                evidence_count: 'Documents: {count}',
                attach_summary_offer: 'Attach this summary to an entry',
                kind_placeholder: 'Choose a kind',
                channel_placeholder: 'Choose a channel',
                approver_placeholder: 'No approver',
                correction_none: 'Not a correction',
                correction_option: '{kind} of {date} for {audience}',
                correction_picker_failed: 'The earlier entries could not be read, so a correction cannot be chosen now.',
                error_required: 'This field is required.',
                error_held_on_future: 'The date cannot be after today.',
                error_correction_note_required: 'Explain what this entry corrects.',
                error_approver_not_found: 'The approver is not a user of this organization.',
                error_not_approver: 'Only the named approver can approve this entry.',
                error_already_approved: 'This entry is already approved.',
                error_self_approval: 'The approver must be someone other than the person recording the entry.',
                form_intro: 'Record what was held or sent. Documents are added after the entry is recorded, from Documents on its row.',
                summary_count: '{count} of {max} characters',
                loading: 'Loading the entries…',
                retry_subject: 'the entries',
                reload: 'Reload',
                reloading: 'Reloading…',
                pagination: 'Pages of entries',
                col_summary: 'Summary',
                col_documents: 'Documents',
                documents_open: 'Documents',
                documents_open_aria: 'Documents of the entry of {date} for {audience}',
                reference_line: 'Reference: {reference}',
                channel_note_line: 'Channel details: {note}',
                correction_note_line: 'Correction note: {note}',
                corrects_line: 'Corrects the entry of {date}',
                corrects_unknown: 'Corrects an earlier entry',
                pack_changed_open_comparison: 'See what changed',
                pack_changed_open_updates: 'Open Regulatory Updates',
                replaced_since: 'Recorded against this text; it has since been replaced by another framework —',
                pack_version_not_recorded: 'Not recorded',
                recorded_while: 'Framework status when recorded: {status}',
                framework_status_suspended: 'Suspended',
                recorded_line: 'Recorded by {person} on {date}',
                files_heading: 'Documents',
                files_intro: 'The documents behind this entry, such as the slides, the letter sent or the downloaded framework summary.',
                files_slot: 'Documents of this entry',
                files_none: 'No document is attached to this entry.',
                file_hash: 'SHA-256: {hash}',
                file_not_held: 'Document no longer held',
                file_download: 'Download',
                file_remove: 'Remove',
                file_upload: 'Add a document',
                file_bound: 'Document added.',
                file_removed: 'Document removed.',
                file_bind_failed: 'The document could not be added.',
                file_remove_failed: 'The document could not be removed.',
                file_not_owned: 'The entry this document belongs to was not found.',
                file_remove_confirm: 'Remove this document from the entry and delete it?',
                files_approved_kept: 'This entry has been approved, so its documents are kept as approved and cannot be removed. To correct it, record a correction entry.',
                documents_load_failed: 'The documents could not be loaded.',
                export_label: 'Export summary',
                export_done: 'Framework summary exported.',
                export_failed: 'The framework summary could not be exported.',
                attach_summary_hint: 'To keep this summary with an entry, open the Briefings tab, choose Documents on the entry and add the downloaded file.',
                directory_unavailable: 'The list of people could not be read, so an approver cannot be chosen now.',
                approver_retry_subject: 'the list of people',
            },
        },
        // Sprint 62.58.0 (Slice F) — the regulatory-update comparison and its review.
        update_comparison: {
            page: {
                module_guide: "What changed between the text installed before and the text installed now, requirement by requirement, with your organization's reading of it and the records filled under the earlier text.",
                title: 'What changed: {name}',
                title_fallback: 'What changed',
                description: 'The requirements of the text installed before, beside the text installed now. This comparison is information: it does not change what your organization owes.',
                back: 'Back to Regulatory Updates',
                loading: 'Loading the comparison…',
                load_failed: 'The comparison could not be loaded.',
                not_found: 'This regulatory update was not found.',
                retry_subject: 'the comparison',
                reload: 'Reload',
                reloading: 'Reloading…',
                no_comparison: 'This regulatory update holds no requirement comparison. An update recorded by hand, or one installed before comparisons were kept, has none.',
                status_heading: 'Status change',
                status_change: '{from} → {to}',
                replaced_by: 'Replaced by {name}',
                status_not_recorded: 'Not recorded',
                basis_computed: 'Compared requirement by requirement between the two installed versions of this framework.',
                basis_declared: 'Compared using the correspondence the replacing framework declares.',
                derived_on_read: 'These rows were worked out when this page was opened, from the frameworks installed now.',
                from_heading: 'Earlier text',
                to_heading: 'Text installed now',
                side_status: 'Status: {status}',
                side_pack_version: 'Pack version: {version}',
                counts_heading: 'Summary of changes',
                count_line: '{label}: {count}',
                change_changed: 'Changed',
                change_removed: 'Removed',
                change_new: 'New',
                change_unchanged: 'Unchanged',
                rows_heading: 'Requirements',
                filter_label: 'Show',
                filter_all: 'All requirements',
                showing: 'Showing {shown} of {total}',
                col_requirement: 'Requirement',
                col_change: 'Change',
                col_from_title: 'Title in the earlier text',
                col_to_title: 'Title in the text installed now',
                col_mandatory: 'Mandatory',
                col_note: 'Note',
                mandatory_yes: 'Yes',
                mandatory_no: 'No',
                mandatory_not_stated: 'Not stated',
                mandatory_change: '{from} → {to}',
                not_in_text: 'Not in this text',
                also_corresponds: 'Also corresponds to: {ids}',
                rows_empty: 'No requirement matches this filter.',
                rows_pagination: 'Pages of requirements',
                superseded_texts_not_installed: 'No comparison: one of the two texts is no longer installed in your organization, or the replacing framework no longer declares that it replaces the earlier text. This regulatory update is kept as it was recorded.',
                withheld_other_pack: 'No comparison: the previous record was installed from another pack ({code}).',
                withheld_other_pack_unknown: 'No comparison: the previous record was not installed from this pack.',
            },
            review: {
                heading: "Your organization's reading",
                intro: "Record how each requirement that changed, was removed or is new affects your organization, and what you concluded. A reading is your organization's own record: it does not change what the framework requires, its status or any deadline.",
                record: 'Record comparison review',
                recording: 'Recording…',
                none: 'No comparison review is recorded yet.',
                current_heading: 'Current review',
                recorded_line: 'Recorded by {person} on {date}',
                conclusion: 'Conclusion',
                next_steps: 'Next steps',
                no_next_steps: 'No next steps recorded.',
                readings_heading: 'Readings',
                col_reading: 'Reading',
                reading_affects_us: 'Affects us',
                reading_does_not_affect_us: 'Does not affect us',
                reading_to_assess: 'To assess',
                no_rows_read: 'This review gives no reading for a single requirement.',
                history_heading: 'Earlier reviews',
                history_pagination: 'Pages of earlier reviews',
                col_recorded: 'Recorded',
                col_recorded_by: 'Recorded by',
                col_conclusion: 'Conclusion',
                col_readings: 'Readings',
                readings_summary: '{affects} affect us, {not_affect} do not, {to_assess} to assess',
                modal_intro: 'Give a reading for every requirement that changed, was removed or is new, then state your conclusion.',
                row_legend: '{id}: {title}',
                field_reading: 'Reading',
                field_reading_placeholder: 'Choose a reading',
                field_note: 'Note',
                field_conclusion: 'Conclusion',
                field_next_steps: 'Next steps',
                text_count: '{count} of {max} characters',
                error_reading_required: 'Choose a reading for this requirement.',
                error_conclusion_required: 'State your conclusion.',
                record_failed: 'The comparison review could not be recorded.',
                recorded_toast: 'Comparison review recorded.',
                no_comparison_error: 'This update holds no comparison to review.',
                rows_loading: 'Loading the requirements to read…',
                rows_load_failed: 'The requirements to read could not be loaded.',
                no_rows_to_read: 'Every requirement is unchanged, so only a conclusion is needed.',
            },
            reread: {
                heading: 'Records filled under the replaced text',
                intro: 'These records were filled while the earlier text applied. They are kept as they are: nothing in them is changed, scored or reopened. Re-read each one against the final text.',
                forms_heading: 'Form records',
                col_record: 'Record',
                col_form: 'Form',
                col_status: 'Status',
                col_updated: 'Last updated',
                forms_none: 'No form record was filled under the replaced text.',
                forms_withheld: 'Form records are listed only to people who may read form records.',
                untitled: 'Untitled record',
                forms_pagination: 'Pages of form records',
                questionnaires_heading: 'Vendor questionnaires',
                notice: 'Filled under {from}, which has been replaced by {to} — re-read it against the final text',
                notice_link: 'See what changed',
                notice_link_aria: 'See what changed between {from} and {to}',
                notice_same_text: 'Filled under pack version {version} of {name}; this text has changed since — re-read it against the current text',
                notice_same_text_unversioned: 'Filled under an earlier pack version of {name}; this text has changed since — re-read it against the current text',
                notice_same_text_link_aria: 'See what changed in {name}',
            },
            recheck: {
                will_draft: 'The pack publisher will recheck this draft on {date}.',
                will_legacy: 'The pack publisher will recheck this legacy framework on {date}.',
                will_suspended: 'The pack publisher will recheck this framework, whose application is suspended, on {date}.',
                was_due_draft: 'The pack publisher was due to recheck this draft on {date}; this record has not yet been updated.',
                was_due_legacy: 'The pack publisher was due to recheck this legacy framework on {date}; this record has not yet been updated.',
                was_due_suspended: 'The pack publisher was due to recheck this framework, whose application is suspended, on {date}; this record has not yet been updated.',
                records_line: "This framework's record changes only when a new pack version is installed; your own records are never changed.",
            },
            list: {
                col_changed: 'Changed requirements',
                no_comparison_short: 'No comparison',
                pagination: 'Pages of regulatory updates',
                view_comparison: 'View comparison',
                view_comparison_aria: 'View comparison for {title}',
                reviews_count: 'Reviews: {count}',
            },
        },
        // Sprint 62.49.1 (DB-2) — Framework Detail's Suspension section.
        framework_suspension: {
            heading: 'Suspension',
            intro: 'As the record states it: when the suspension of this framework’s application began, each instrument that suspended it, and what ends it.',
            start_label: 'Began',
            start_value: 'Suspended from {date}',
            basis_label: 'Basis for that day',
            steps_caption: 'Instruments that suspended this framework, in the order they took effect',
            col_instrument: 'Instrument',
            col_issued: 'Issued',
            col_effective: 'Took effect',
            col_until: 'Suspends until',
            col_replaces: 'Replaces',
            replaces_none: 'None',
            until_date: 'Until {date}',
            source_line: 'Read from: {source}',
            end_label: 'Ends',
            end_date: 'On {date}',
            end_event: 'When this happens: {event}',
            event_not_occurred: 'Not yet occurred',
            event_occurred_on: 'Occurred on {date}',
        },
        market_pack_installed: 'Market pack installed',
        missing_market_code: 'Cannot uninstall: missing market code',
        pack_installed_announce: 'Pack installed successfully',
        install_zip_refused_title: 'Uploaded pack files are not installed',
        install_zip_refused_body: 'An uploaded pack .zip must be signed, and no pack signing key is configured for this installation, so every uploaded zip is refused. Packs that ship with the release are installed from Localization (Market Packs, Language Packs and the Overlay Catalog), and each one is checked against the release before it installs.',
        pack_uninstalled: 'Language pack uninstalled',
    },
    partner_marketplace: {
        /**
         * fix-sprint-20260828-2118 F20 — promoted out of
         * `pages/admin/partner-marketplace/strings.ts`.
         *
         * That file is an inline `{ en, vi }` pair resolved by `extractLocale`, so
         * the 14 non-EN/VI locales received the English sentence at all 25 of this
         * module's call sites — on pages whose every other string comes from `t.*`.
         * The wording is 62.5.0's, unchanged.
         */
        selector_truncated: 'Showing {shown} of {total}. Narrow the search to reach the rest.',
        nav: {
            dashboard: 'Kojelauta',
            partner_orgs: 'Kumppaniorganisaatiot',
            service_tiers: 'Palvelutasot',
            deliverable_templates: 'Tuotosmallipohjat',
            deliverables: 'Tuotokset',
            brand_profiles: 'Brändiprofiilit',
            support_tickets: 'Tukipyynnöt',
            workflow_templates: 'Työnkulkumallipohjat',
            workflows: 'Työnkulut',
            escalation: 'Eskalointi',
            reports: 'Raportit',
            audit: 'Tarkastusjälki',
        },
        dashboard: {
            title: 'Kumppanikojelauta',
            loading: 'Ladataan kojelauta tiedot...',
            total_managed_tenants: 'Managed Tenants yhteensä',
            avg_health_score: 'Avg Terveys Pistemäärä',
            programme_health_score: 'Partner Programme Health',
            health_band_green: 'Healthy',
            health_band_amber: 'Needs attention',
            health_band_red: 'Critical',
            health_coverage_note: 'Scored from your own partner operations: open tickets, resolution times against target, deliverable timeliness and escalations. It does not cover the compliance posture or data freshness of the tenants you manage.',
            open_tickets: 'Avaa Tiketit',
            upcoming_deliverables: 'Tulevat toimitukset',
            escalated_tickets: 'Eskaloitu Tiketit',
            managed_tenants: 'Hallinnoidut vuokralaiset',
            partner_tier: 'Partner Taso',
            managed_tenants_count: 'Managed Tenants Lukumäärä',
            onboarding_date: 'Onboarding Päivämäärä',
            view_details: 'Näytä yksityiskohdat',
            no_partner_orgs: 'Ei partner organizations found. löytynyt',
        },
        partner_orgs: {
            title: 'Kumppaniorganisaatiot',
            new: 'Uusi Partner Org',
            contact_name: 'Contact Nimi',
            contact_email: 'Yhteyssähköposti',
            partner_tier: 'Partner Taso',
            managed_orgs: 'Hallinnoidut organisaatiot',
            onboarding_date: 'Onboarding Päivämäärä',
            no_orgs: 'Ei partner organizations found. löytynyt',
            partner_type: 'Partner Type',
            type_consulting: 'Consulting Firm',
            type_technology: 'Technology Provider',
            type_legal: 'Legal Firm',
            type_audit: 'Audit & Assurance',
            type_managed_services: 'Managed Services Provider',
            type_reseller: 'Reseller',
            contact_phone: 'Contact Puhelin',
            metadata_json: 'Metatiedot (JSON)',
            change_status: 'Change Tila',
            new_status: 'Uusi Tila',
            invalid_json_metadata: 'Virheellinen JSON in metadata field',
            suspended: 'Jäädytetty',
            offboarded: 'Poistettu käytöstä',
            all_tiers: 'Kaikki tasot',
            silver: 'Hopea',
            gold: 'Kulta',
            platinum: 'Platina',
            status_offboarded: 'Poistettu käytöstä',
            status_suspended: 'Jäädytetty',
            tier_gold: 'Kulta',
            tier_platinum: 'Platina',
            tier_silver: 'Hopea',
            org_status_updated: 'Organization status updated',
        },
        service_tiers: {
            title: 'Palvelutasot',
            new: 'Uusi Taso',
            tier_name: 'Taso Nimi',
            monthly_fee: 'Kuukausittain Fee',
            features: 'Ominaisuudet',
            no_tiers: 'Ei service tiers found. löytynyt',
            tier_code: 'Taso Koodi',
            response_time_hrs: 'Vastaus Aika (hrs)',
            resolution_time_hrs: 'Resolution Aika (hrs)',
            review_frequency: 'Katselmointia Tiheys',
            sla_response_time: 'SLA Vastaus Aika (hours)',
            sla_resolution_time: 'SLA Resolution Aika (hours)',
            module_profile: 'Included Modules',
            module_profile_hint: 'The modules this tier covers. Descriptive only — it records what the tier is sold as, and does not grant or restrict access.',
            module_profile_selected: '{count} of {total} selected',
            module_profile_select_all: 'Select all',
            module_profile_clear_all: 'Clear all',
            module_profile_unknown: 'Unrecognised — kept from an earlier version of this tier',
            comparing: 'Verrataan...',
            compare: 'Vertaa',
            comparison: 'Palvelu Taso Comparison',
            attribute: 'Attribuutti',
            deprecate: 'Vanhenna',
            deprecated: 'Vanhentunut',
            select_compare: 'Valitse at least 2 tiers to compare',
            comparison_title: 'Palvelu Taso Comparison',
            status_deprecated: 'Vanhentunut',
            activate: 'Aktivoi',
            resolution_time: 'Resolution Aika',
            response_time: 'Vastaus Aika',
            select_at_least_two: 'Valitse at least 2 tiers',
        },
        deliverable_templates: {
            title: 'Tuotosmallipohjat',
            new: 'Uusi Malli',
            template_name: 'Malli Nimi',
            deliverable_type: 'Toimitus Tyyppi',
            no_templates: 'Ei deliverable mallit found. löytynyt',
            frequency: 'Tiheys',
            est_effort_hrs: 'Arvioitu työmäärä (t)',
            estimated_effort: 'Arvioitu työmäärä (tuntia)',
            associated_module: 'Associated Moduuli',
            linked_tiers_count: 'Linked Tasot Lukumäärä',
            evidence_requirements: 'Todistusaineisto Vaatimukset (comma-separated)',
            tier_ids: 'Taso Tunniste (comma-separated)',
            module_none: 'No module',
            module_unrecognised: '{value} — not a platform module; kept as stored',
            frequency_one_time: 'One Aika',
            frequency_monthly: 'Kuukausittain',
            frequency_quarterly: 'Neljännesvuosittain',
            frequency_annually: 'Vuosittain',
            type_report: 'Raportti',
            type_review: 'Katselmointia',
            type_assessment: 'Arviointi',
            type_meeting: 'Kokous',
            type_configuration: 'Konfiguraatio',
            freq_one_time: 'One Aika',
            freq_monthly: 'Kuukausittain',
            freq_quarterly: 'Neljännesvuosittain',
            freq_annually: 'Vuosittain',
            status_deprecated: 'Vanhentunut',
        },
        deliverables: {
            title: 'Tuotokset',
            new: 'Uusi Toimitus',
            partner: 'Kumppani',
            deliverable_type: 'Toimitus Tyyppi',
            due_date: 'Due Päivämäärä',
            no_deliverables: 'Ei deliverables found. löytynyt',
            assigned_to: 'Määritetty To',
            completion_date: 'Completion Päivämäärä',
            update_status: 'Päivitä tila',
            hide_details: 'Piilota Yksityiskohdat',
            view_details: 'Näytä yksityiskohdat',
            evidence_ids: 'Todistusaineisto Tunniste',
            evidence_expected: 'Evidence expected',
            evidence_expected_none: 'No evidence requirements are recorded on this deliverable.',
            client_approval: 'Asiakas Hyväksyntä',
            bulk_update: 'Bulk Päivitä',
            bulk_update_status: 'Bulk Päivitä Tila',
            generate_schedule: 'Luo Aikataulu',
            generate_deliverable_schedule: 'Luo Toimitus Aikataulu',
            unassigned: 'Unassigned',
            assigned_to_user: 'Assigned to {name}.',
            assignee_cleared: 'Deliverable is now unassigned.',
            edit_notes: 'Edit notes',
            edit_evidence: 'Edit evidence',
            evidence_select_label: 'Select evidence items',
            evidence_none_available: 'No evidence items are available. Add them in the evidence repository first.',
            approval_note_label: 'Note (optional)',
            approval_status_approved: 'Approved',
            approval_status_rejected: 'Rejected',
            approval_by_at: 'by {name} on {date}',
            approval_recorded: 'Client approval recorded.',
            approval_rejection_recorded: 'Client rejection recorded.',
            notes_saved: 'Notes saved.',
            evidence_saved: 'Evidence updated.',
            generated_count: 'Generated {count} deliverables.',
            generated_none: 'No deliverables were generated. No active deliverable template is linked to this tier — create one under Deliverable Templates and link it to this tier code first.',
            organization_id: 'Organisaatiotunnus',
            service_tier_id: 'Palvelu Taso Tunniste',
            filter_assigned_to: 'Suodata by Määritetty To',
            select_status: 'Valitse tila...',
            new_status: 'Uusi Tila',
            upcoming: 'Tuleva',
            overdue: 'Myöhässä',
            status_deferred: 'Lykätty',
            status_overdue: 'Myöhässä',
            status_upcoming: 'Tuleva',
        },
        brand_profiles: {
            title: 'Brändiprofiilit',
            new: 'Uusi Brand Profiili',
            partner: 'Kumppani',
            brand_name: 'Brand Nimi',
            primary_color: 'Ensisijainen Väri',
            logo_url: 'Logon URL',
            no_profiles: 'Ei brand profiles found. löytynyt',
            secondary_color: 'Toissijainen Väri',
            header_text: 'Otsikko Text',
            footer_text: 'Alatunnisteen teksti',
            contact_email: 'Yhteyssähköposti',
            contact_phone: 'Contact Puhelin',
            contact_website: 'Contact Verkkosivusto',
            contact_address: 'Contact Osoite',
            is_default: 'Is Oletus',
            set_default: 'Set Oletus',
            preview: 'Esikatselu',
            report_header_preview: 'Raportti Otsikko Preview',
            report_footer_preview: 'Raportti Footer Preview',
            preview_description: 'This is a preview of the raportti header with the valittu brand colors.',
            contact_website_short: 'Verkkosivusto',
            logo_preview: 'Logon esikatselu',
        },
        support_tickets: {
            title: 'Tukipyynnöt',
            new: 'Uusi Tiketti',
            partner: 'Kumppani',
            subject: 'Kohde',
            assigned_to: 'Määritetty To',
            no_tickets: 'Ei support tickets found. löytynyt',
            category: 'Kategoria',
            priority: 'Prioriteetti',
            escalation_tier: 'Eskalaatio Taso',
            sla_status: 'SLA Tila',
            update_status: 'Päivitä tila',
            escalate: 'Eskaloidu',
            add_note: 'Lisää huomautus',
            escalate_ticket: 'Eskaloidu Tiketti',
            escalate_to_tier: 'Eskaloidu To Taso',
            notes_timeline: 'Huomautukset Timeline',
            no_notes: 'Ei vielä huomautuksia.',
            attachments: 'Liitteet',
            no_attachments: 'Ei liitteitä.',
            client_org_id: 'Asiakas Org Tunniste',
            content: 'Sisältö',
            note_type: 'Tyyppi',
            view_detail: 'Näytä yksityiskohta',
            hide: 'Piilota',
            new_status: 'Uusi Tila',
            all_categories: 'Kaikki kategoriat',
            all_priorities: 'Kaikki prioriteetit',
            all_tiers: 'Kaikki tasot',
            ticket_not_found: 'Tiketti not found',
            ticket_id: 'Tiketti Tunniste',
            client_org: 'Asiakas Org',
            deescalate: 'Purkaa eskalointi',
            deescalate_ticket: 'De-escalate Tiketti',
            deescalate_to_tier: 'De-escalate To Taso',
            add_attachment: 'Lisää liite',
            file: 'Tiedosto',
            linked_entity: 'Linkitetty entiteetti',
            sla_target: 'Tavoite',
            breached: 'Rikottu',
            remaining_hours: 'jäljellä',
            na: 'N/A',
            tickets_found: 'tikettiä löytyi',
            reason: 'Syy',
            target: 'Tavoite',
            upload: 'Lataa',
        },
        workflow_templates: {
            title: 'Työnkulkumallipohjat',
            new: 'Uusi Malli',
            template_name: 'Malli Nimi',
            steps: 'Vaiheet',
            no_templates: 'Ei workflow mallit found. löytynyt',
            category: 'Kategoria',
            view_steps: 'Näytä vaiheet',
            hide_steps: 'Piilota Vaiheet',
            step: 'Vaihe',
            step_title: 'Otsikko',
            step_description: 'Kuvaus',
            assignee_role: 'Vastuuhenkilö Rooli',
            sla_hours: 'SLA-tunnit',
            evidence_required: 'Todistusaineisto Pakollinen',
            guidance_notes: 'Opastus Huomautukset',
            add_step: 'Lisää vaihe',
            no_steps: 'Ei määriteltyjä vaiheita.',
            no_steps_defined: 'Ei määriteltyjä vaiheita.',
            templates_found: 'mallia löytyi',
            all_categories: 'Kaikki kategoriat',
        },
        workflows: {
            client_org: 'Asiakas Org',
            title: 'Työnkulku Instances',
            new: 'Uusi Työnkulku',
            template: 'Malli',
            partner: 'Kumppani',
            current_step: 'Nykyinen Vaihe',
            progress: 'Edistyminen',
            no_workflows: 'Ei workflow instances found. löytynyt',
            instances_found: 'esiintymää löytyi',
        },
        escalation: {
            title: 'Eskalaatio Kojelauta',
            ticket: 'Tiketti',
            partner: 'Kumppani',
            escalated_at: 'Eskaloitu At',
            reason: 'Syy',
            no_escalations: 'Ei escalations found. löytynyt',
            time_in_tier: 'Aika in Taso',
            auto_evaluate: 'Automaattinen arviointi',
            priority_critical: 'Kriittinen',
            priority_high: 'Korkea',
            priority_medium: 'Keskitaso',
            priority_low: 'Low',
            deescalated_from_dashboard: 'De-escalated from kojelauta',
            sla_breached: 'SLA rikottu',
            sla_remaining: 'SLA jäljellä: {hours}h',
            sr_total_escalated: 'Total escalated',
            sr_by_tier: 'By tier',
            sr_by_priority: 'By priority',
        },
        reports: {
            client_org: 'Asiakas Org',
            title: 'Raportit',
            partner: 'Kumppani',
            report_type: 'Raportti Tyyppi',
            generated_at: 'Luotu',
            no_reports: 'Ei raportti found. löytynyt',
            generate: 'Luo Raportti',
        },
        audit: {
            title: 'Partner Tarkastus Trail',
            event_type: 'Tapahtumatyyppi',
            entity_type: 'Entity Tyyppi',
            entity: 'Entiteetti',
            entity_id: 'Entity Tunniste',
            actor: 'Toimija',
            timestamp: 'Aikaleima',
            no_events: 'Ei tarkastus tapahtuma found. löytynyt',
            events_found: 'tapahtumaa löytyi',
            client_org: 'Asiakasorganisaatio',
            all_actors: 'Kaikki toimijat',
            event_type_search: 'Hae tapahtumatyyppiä',
            event_type_created: 'Luotu',
            event_type_updated: 'Päivitetty',
            event_type_deleted: 'Poistettu',
            event_type_set_default: 'Set as Default',
            event_type_status_changed: 'Status Changed',
            event_type_status_updated: 'Status Updated',
            event_type_generated: 'Generated',
            event_type_export_generated: 'Export Generated',
            event_type_escalated: 'Escalated',
            event_type_auto_escalated: 'Auto-Escalated',
            event_type_deescalated: 'De-escalated',
            event_type_managed_org_added: 'Managed Client Added',
            event_type_managed_org_removed: 'Managed Client Removed',
            event_type_versioned: 'New Version',
            event_type_attachment_added: 'Attachment Added',
            event_type_note_added: 'Note Added',
            event_type_step_advanced: 'Step Advanced',
            event_type_cancelled: 'Cancelled',
            entity_type_partner_org: 'Partner Org',
            entity_type_service_tier: 'Palvelutaso',
            entity_type_deliverable_template: 'Toimitettava malli',
            entity_type_deliverable_instance: 'Toimitettava esimerkki',
            entity_type_brand_profile: 'Tuotemerkkiprofiili',
            entity_type_support_ticket: 'Tukilippu',
            entity_type_workflow_template: 'Työnkulkumalli',
            entity_type_workflow_instance: 'Työnkulun ilmentymä',
            entity_type_generated_report: 'Luotu raportti',
            entity_type_audit_export: 'Auditointivienti',
        },
        no_permission_title: 'Riittämättömät käyttöoikeudet',
        no_permission_message: 'Sinulla ei ole lupaa käyttää kumppanin työtilaa.',
        status_active: 'Aktiivinen',
        status_suspended: 'Keskeytetty',
        status_offboarded: 'Offboard',
        status_completed: 'Valmis',
        status_cancelled: 'Peruutettu',
        status_deprecated: 'Käytöstä poistettu',
        status_new: 'Uusi',
        status_triaged: 'Triaged',
        status_in_progress: 'Meneillään',
        status_waiting_on_client: 'Asiakasta odotellessa',
        status_waiting_on_vendor: 'Myyjää odotellessa',
        status_escalated: 'Eskaloitunut',
        status_resolved: 'Ratkaistu',
        status_closed: 'Suljettu',
        status_pending: 'Odottaa',
        status_skipped: 'Ohitettu',
        client_org_column: 'Client org',
        client_org_unresolved: 'Client org not recorded',
        category_onboarding: 'Käyttöönotto',
        category_incident_escalation: 'Tapahtuman eskaloituminen',
        category_config_change: 'Konfiguroinnin muutos',
        category_periodic_review: 'Säännöllinen tarkistus',
        category_offboarding: 'Offboarding',
        category_custom: 'Mukautettu',
        category_configuration: 'Kokoonpano',
        category_incident: 'Tapahtuma',
        category_compliance_question: 'Vaatimustenmukaisuuskysymys',
        category_feature_request: 'Ominaisuuspyyntö',
        category_bug_report: 'Virheraportti',
        priority_critical: 'Kriittinen',
        priority_high: 'Korkea',
        priority_medium: 'Keskikokoinen',
        priority_low: 'Matala',
        tier_silver: 'Hopeaa',
        tier_gold: 'Kulta',
        tier_platinum: 'Platina',
        report_type_compliance_summary: 'Yhteenveto vaatimustenmukaisuudesta',
        report_type_risk_overview: 'Riskien yleiskatsaus',
        report_type_incident_report: 'Tapahtumaraportti',
        report_type_consent_status: 'Suostumuksen tila',
        report_type_program_maturity: 'Ohjelman kypsyys',
        report_type_executive_pack: 'Executive Pack',
        note_type_internal: 'Sisäinen',
        note_type_client_visible: 'Asiakas näkyvissä',
        status_updated: 'Tilan päivitys onnistui',
        deliverable_status_updated: 'Toimitettavan tilan päivitys onnistui',
        ticket_status_updated: 'Lipun tilan päivitys onnistui',
        ticket_escalated: 'Lippu eskaloitui onnistuneesti',
        note_added: 'Huomautus lisätty onnistuneesti',
        attachment_uploaded: 'Liite lähetetty onnistuneesti',
        ticket_deescalated: 'Lippujen eskaloituminen onnistui',
        auto_evaluation_completed: 'Automaattinen arviointi suoritettu',
        template_created: 'Työnkulkumallin luominen onnistui',
        template_updated: 'Työnkulkumallin päivitys onnistui',
        brand_set_default: 'Brändiprofiilin asettaminen oletukseksi onnistui',
        brand_created: 'Brändiprofiilin luominen onnistui',
        brand_updated: 'Brändiprofiilin päivitys onnistui',
        tier_created: 'Palvelutaso luotiin onnistuneesti',
        tier_updated: 'Palvelutason päivitys onnistui',
        tier_deprecated: 'Palvelutaso poistettu käytöstä',
        tier_activated: 'Palvelutaso aktivoitu onnistuneesti',
        period: 'Kausi',
        format: 'Muoto',
        period_start: 'Jakson alku',
        period_end: 'Jakson loppu',
        reports_count: '{count} raportoi',
        reports_count_one: '{count} raportti',
        metric: 'Metrinen',
        value: 'Arvo',
        showing_chart_view: 'Näytetään karttanäkymä',
        showing_table_view: 'Näytetään tietotaulukkonäkymä',
        evidence_ids_placeholder: 'esim. ev-001, ev-002',
        confirm_cancel_workflow: 'Haluatko varmasti peruuttaa tämän työnkulun? Tätä toimintoa ei voi kumota.',
        export_csv: 'Vie CSV',
        compliance_qualifications_title: 'Vaatimustenmukaisuuspalvelun pätevyys',
        compliance_qualifications_desc: "The partner tier (Silver, Gold or Platinum) is a label on the partner record. The platform reads it in one place: a support ticket opened for an organisation the partner manages takes its resolution-time target from the partner's active service tier with the same code, or 24 hours when there is none. The tier grants no access and does not qualify a partner for any kind of engagement.",
        regulatory_output_title: 'Sääntelyn tuotosten kohdistus',
        regulatory_output_desc: 'Jokainen mallityyppi liittyy sääntelyn tulosluokkaan: Raportit tuottavat vaatimustenmukaisuusdokumentaatiota, Katsaukset vahvistavat valvonnan tehokkuuden, Arvioinnit kattavat riskiarvioinnit (DPIA, aukkojen analyysi), Kokoukset dokumentoivat hallintoistunnot. Todistusvaatimukset määrittelevät artefaktit, joihin kunkin toimituksen on viitattava tarkastusvalmiutta varten.',
        sla_compliance_title: 'SLA Seuranta vaatimustenmukaisuuden toimittamiseksi',
        sla_compliance_desc: 'Vastaus- ja ratkaisuaika SLA-sopimukset ovat sopimusvelvollisia vaatimustenmukaisuuspalvelun toimittamisessa. Tarkistustiheys määrittää, kuinka usein kumppanien vaatimustenmukaisuuden tuotoksia arvioidaan. SLA-rikkomukset vaatimustenmukaisuuden kannalta kriittisissä lipuissa laukaisevat automaattisen eskaloinnin, ja ne kirjataan kumppanin seurantaketjuun säännöstenmukaista näyttöä varten.',
        regulatory_engagement_title: 'Sääntelyn sitoutumismallit',
        regulatory_engagement_desc: 'Työnkulun luokat kohdistetaan säännösten mukaisiin sitouttamistyyppeihin: Onboarding kattaa alkuperäisen vaatimustenmukaisuuden määrityksen ja puutteiden analyysin, Periodic Review käsittelee toistuvat vaatimustenmukaisuusarvioinnit, Incident Escalation hallitsee säännösten rikkomiseen reagointia, Offboarding varmistaa tietojen säilyttämisvelvoitteiden täyttymisen. Jokainen vaihe voi edellyttää todisteiden keräämistä kirjausketjun täydellisyydestä.',
        compliance_escalation_title: 'Vaatimustenmukaisuuskriittiset eskalointimenettelyt',
        compliance_escalation_desc: 'L1 käsittelee normaalit toimintaongelmat. L2 kattaa vaatimustenmukaisuuteen vaikuttavat viat (säännösten noudattamatta jättäminen, valvontapuutteet). L3 on varattu säännösten rikkomisskenaarioihin, jotka vaativat välitöntä johdon huomiota. SLA rikkomus L2/L3-lipuissa luo kirjausketjun automaattisesti. Kaikki eskalointi- ja eskalointitoimenpiteet kirjataan pysyvästi lokiin.',
        audit_regulatory_title: 'Sääntelyn todisteiden polku',
        audit_regulatory_desc: 'Tämä kirjausketju tarjoaa peukaloituja tietueita säännösten noudattamisesta. Jokainen kumppanitoiminto (luo, päivitä, poista, eskalointi, tilanmuutos) kirjataan lokiin toimijan identiteetillä, aikaleimalla ja täyden hyötykuorman erolla. Käytä Entity Type -suodattimia todisteiden eristämiseen vaatimustenmukaisuusalueen mukaan. Vie suodatetut tulokset tuottaaksesi auditointivalmiita asiakirjoja viranomaistarkastuksia varten.',
        evidence_trail_title: 'Cross-Module Evidence Trail',
        evidence_trail_desc: 'Jokainen työnkulun vaihe voi kerätä todistetunnuksia, jotka linkittävät suoritteisiin, tarkastustapahtumiin ja vaatimustenmukaisuusartefakteihin moduulien välillä. Edistyminen todisteiden tunnuksilla luo todennettavissa olevan ketjun, joka yhdistää kumppanien työnkulut viranomaisdokumentaatioihin. Täydelliset työnkulut täydellisillä todisteketjuilla toimivat todisteena vaatimustenmukaisuuden sitouttamisesta.',
        evidence_ids_label: 'Todistustodistukset',
    },
    org_groups: {
        title: 'Organisaatioryhmät',
        new: 'Luo Ryhmä',
        group_name: 'Ryhmä Nimi',
        member_count: 'Member Lukumäärä',
        root_org: 'Juuriorganisaatio',
        no_groups: 'Ei org groups found. löytynyt',
        add_organization: 'Lisää Organisaatio',
        select_organization: 'Valitse organisaatio...',
        no_available_orgs: 'Ei lisättäviä organisaatioita saatavilla.',
        no_members: 'Ei jäsenorganisaatioita tässä ryhmässä.',
        member_added: 'Organisaatio lisätty ryhmään.',
        member_removed: 'Organisaatio poistettu ryhmästä.',
        already_in_group: 'Tämä organisaatio on jo ryhmässä.',
        org_not_found: 'Organisaatiota ei löytynyt.',
        add_member_error: 'Organisaation lisääminen ryhmään epäonnistui.',
        remove_member_error: 'Organisaation poistaminen ryhmästä epäonnistui.',
        confirm_remove_member: 'Haluatko varmasti poistaa "{name}" tästä ryhmästä?',
        load_error: 'Organisaatioryhmän tietojen lataaminen epäonnistui.',
        page_title_list: 'Organisaatioryhmät',
        page_title_detail: 'Organisaatioryhmän tiedot',
        page_title_create: 'Luo organisaatioryhmä',
        page_title_settings: 'Organisaatioryhmän asetukset',
        page_title_users: 'Ryhmän käyttäjät',
        page_title_reports: 'Ryhmäraportit',
        page_title_dashboard: 'Ryhmän hallintapaneeli',
        page_title_audit: 'Ryhmän tarkastus',
        page_title_org_audit: 'Organisaatioryhmän tarkastus',
        page_title_shared_vendors: 'Jaetut myyjät',
        page_title_shared_frameworks: 'Jaetut puitteet',
        page_title_shared_forms: 'Jaetut lomakemallit',
        page_title_merged_vendors: 'Yhdistetyt myyjät',
        page_title_settings_inheritance: 'Asetukset Periytys',
        announce_loaded_groups: 'Ladattu {count} organisaatioryhmää',
        announce_loaded_group: 'Ladattu ryhmä {name}',
        announce_loaded_users: 'Ladattu {count} ryhmän käyttäjää',
        announce_loaded_reports: 'Ladattu {count} raportteja',
        announce_loaded_vendors: 'Ladattu {count} jaettua toimittajaa',
        announce_loaded_frameworks: 'Ladattu {count} jaettua kehystä',
        announce_loaded_templates: 'Ladattu {count} jaetut lomakemallit',
        announce_loaded_audit: 'Ladattu {count} tarkastustapahtumaa',
        announce_loaded_merged: 'Ladattu {shared} jaettua ja {local} paikallista toimittajaa',
        announce_dashboard_loaded: 'Kojelauta ladattu.',
        announce_dashboard_refreshed: 'Kojelauta päivitetty.',
        announce_dashboard_refresh_failed: 'Hallintapaneelin päivittäminen epäonnistui.',
        announce_refreshing: 'Päivitetään kojelautaa...',
        announce_settings_loaded: 'Asetusten perintö ladattu.',
        announce_settings_saved: 'Asetukset tallennettu onnistuneesti.',
        announce_saving_settings: 'Tallennetaan asetuksia...',
        announce_error_saving: 'Virhe asetusten tallentamisessa.',
        announce_group_dissolved: 'Ryhmä purettu.',
        announce_creating_group: 'Luodaan organisaatioryhmää...',
        announce_group_created: 'Organisaatioryhmän luominen onnistui.',
        announce_error_creating: 'Virhe luotaessa organisaatioryhmää.',
        announce_vendor_published: 'Toimittaja julkaistu onnistuneesti.',
        announce_vendor_retired: 'Myyjä jäi eläkkeelle.',
        announce_framework_published: 'Framework julkaistu onnistuneesti.',
        announce_framework_retired: 'Framework eläkkeelle.',
        announce_template_published: 'Lomakemallin julkaisu onnistui.',
        announce_template_retired: 'Lomakemalli poistettu käytöstä.',
        announce_role_assigned: 'Roolin määrittäminen onnistui.',
        announce_error_assign_role: 'Virhe määritettäessä roolia.',
        announce_role_revoked: 'Rooli peruutettu onnistuneesti.',
        announce_report_generating: 'Raporttia luodaan...',
        announce_report_requested: 'Raportin luonti pyydetty.',
        announce_error_report: 'Virhe raporttia pyydettäessä.',
        announce_report_deleted: 'Raportti poistettu.',
        announce_search_applied: 'Haku käytössä.',
        announce_search_cleared: 'Haku tyhjennetty.',
        announce_tab_selected: '{tab}-välilehti valittu',
        announce_error_add_member: 'Virhe lisättäessä jäsentä ryhmään.',
        announce_category_reverted: '{category} palautettiin vanhemmalta perityksi.',
        announce_category_overridden: '{category} asetettu paikalliseen ohitukseen.',
        announce_override_saved: '{category} ohitus tallennettu.',
        announce_export_started: 'Viedään tarkastus nimellä {format}...',
        announce_export_completed: 'Vienti {format} valmis.',
        announce_export_failed: 'Vienti epäonnistui.',
        announce_filter_org: 'Suodatettu organisaation mukaan {name}',
        announce_filter_org_cleared: 'Organisaation suodatin tyhjennetty',
        announce_filter_event: 'Suodatettu tapahtumatyypin {type}',
        announce_filter_cleared: 'Suodatin tyhjennetty',
        announce_download: 'Ladataan {name} nimellä {format}',
        error_load_groups: 'Organisaatioryhmien lataaminen epäonnistui. Yritä uudelleen.',
        error_load_users: 'Ryhmän käyttäjien lataaminen epäonnistui.',
        error_load_reports: 'Ryhmäraporttien lataaminen epäonnistui.',
        error_load_dashboard: 'Ryhmän hallintapaneelin lataaminen epäonnistui.',
        error_load_settings: 'Ryhmäasetusten lataaminen epäonnistui.',
        error_load_vendors: 'Yhdistettyjen toimittajien lataaminen epäonnistui.',
        error_save_settings: 'Asetusten tallentaminen epäonnistui.',
        error_dissolve_group: 'Ryhmän purkaminen epäonnistui.',
        error_assign_role: 'Roolin määrittäminen epäonnistui.',
        error_create_group: 'Organisaatioryhmän luominen epäonnistui. Yritä uudelleen.',
        error_request_report: 'Raportin pyytäminen epäonnistui.',
        error_group_name_required: 'Ryhmän nimi vaaditaan.',
        success_settings_saved: 'Asetukset tallennettu onnistuneesti.',
        confirm_retire_vendor: 'Haluatko varmasti lopettaa toimittajan {name}?',
        confirm_retire_framework: 'Haluatko varmasti poistaa puitteet {name}?',
        confirm_retire_template: 'Haluatko varmasti poistaa lomakemallin {name}?',
        confirm_dissolve_group: 'Haluatko varmasti purkaa kohteen {name}? Tätä toimintoa ei voi kumota.',
        confirm_revoke_role: 'Haluatko varmasti peruuttaa roolin {role} asemalta {name}?',
        confirm_delete_report: 'Haluatko varmasti poistaa raportin "{name}"?',
        dissolve_group: 'Pura ryhmä',
        dissolve_warning: 'Tämä purkaa organisaatioryhmän pysyvästi. Kaikki jäsenjärjestöt poistetaan.',
        count_groups: '{count} ryhmä',
        count_groups_plural: '{count} ryhmää',
        count_users: '{count} käyttäjä',
        count_users_plural: '{count} käyttäjää, joilla on ryhmärooleja',
        count_events: '{count} tapahtuma',
        count_events_plural: '{count} tapahtumia',
        count_reports: '{count} raportti',
        count_reports_plural: '{count} raportoi',
        count_vendors: '{count} toimittaja jaettu ryhmässä',
        count_vendors_plural: '{count} toimittajaa jaettu ryhmässä',
        count_frameworks: '{count} puitteet jaetaan koko ryhmälle',
        count_frameworks_plural: '{count} puitteet jaetaan koko ryhmälle',
        count_templates: '{count} malli jaettu koko ryhmälle',
        count_templates_plural: '{count} mallia jaetaan koko ryhmälle',
        count_shared_vendors: '{count} jaettua toimittajaa',
        count_local_vendors: '{count} paikallisia myyjiä',
        merged_combined_view: 'Yhdistetty näkymä {shared} jaetuista ja {local} paikallisista toimittajista',
        dashboard: {
            title: 'Ryhmän kojelauta',
            loading: 'Ladataan kojelauta tiedot...',
            total_groups: 'Ryhmät yhteensä',
            total_members: 'Members yhteensä',
            active_groups: 'Aktiiviset ryhmät',
            overall_compliance_score: 'Yleinen vaatimustenmukaisuuspisteet',
            group_risk_level: 'Ryhmän riskitaso',
            sla_compliance: 'SLA Vaatimustenmukaisuus',
            compliance_posture: 'Vaatimustenmukaisuusasento',
            risk_heatmap: 'Riskien lämpökartta',
            sla_performance: 'SLA Suorituskyky',
            recent_incidents: 'Viimeaikaiset tapahtumat',
            recent_activity: 'Viimeaikainen toiminta',
            refresh_label: 'Päivitä kojelaudan tiedot',
            score_good: 'Hyvä',
            score_needs_attention: 'Vaatii huomiota',
            score_critical: 'Kriittinen',
            trend_up: 'Nousussa',
            trend_down: 'Trendikäs alaspäin',
            trend_stable: 'Vakaa',
            risk_low: 'Low',
            risk_medium: 'Keskikokoinen',
            risk_high: 'Korkea',
            risk_critical: 'Kriittinen',
            risk_unknown: 'Tuntematon',
            col_organization: 'Organisaatio',
            col_score: 'Pisteet',
            col_trend: 'Trendi',
            col_open_issues: 'Avoimet ongelmat',
            col_critical_issues: 'Kriittiset kysymykset',
            col_health: 'Terveys',
            col_vendor_risk: 'Myyjän riski',
            col_data_risk: 'Tietojen riski',
            col_compliance_risk: 'Vaatimustenmukaisuusriski',
            col_total_cases: 'Tapauksia yhteensä',
            col_on_time: 'Ajoissa',
            col_breached: 'Rikkoutunut',
            col_sla_percent: 'SLA %',
            col_avg_days: 'Päivien keskiarvo',
            sr_vendor_risk: 'Toimittajan riskipisteet: {score}',
            sr_data_risk: 'Tietojen riskipisteet: {score}',
            sr_compliance_risk: 'Vaatimustenmukaisuuden riskipisteet: {score}',
            sr_sla_breaches: 'Varoitus: {count} SLA rikkoo',
            sr_severity: 'Vakavuus: {severity}',
        },
        detail: {
            title: 'Ryhmän tiedot',
            members: 'Jäsenet',
            settings: 'Asetukset',
            audit: 'Tarkastus',
            sr_loading: 'Ladataan organisaatioryhmän tietoja',
            sr_loading_hierarchy: 'Ladataan hierarkiaa',
            tab_tasks: 'Shared tasks',
            tasks_intro: "The tasks of every organization in this group, read across the group because task sharing is switched on in this group's settings.",
            tasks_total: '{count} tasks across the group',
            tasks_overdue: '{count} overdue',
            tasks_by_status: 'By status',
            tasks_by_org: 'By organization',
            tasks_latest: 'Latest tasks',
            tasks_showing: 'Showing the {shown} most recent of {total}.',
            tasks_empty: 'No organization in this group has any tasks.',
            col_title: 'Title',
            col_org: 'Organization',
            col_status: 'Status',
            col_priority: 'Priority',
            col_due: 'Due',
            tasks_load_failed: "The group's shared tasks could not be read, so nothing is shown for them.",
            tasks_subject: 'shared tasks',
            sr_loading_tasks: 'Loading shared tasks',
            member_org: 'jäsenjärjestö',
            root_org_label: 'juuriorganisaatio',
            hierarchy: 'Hierarchy',
        },
        users: {
            title: 'Ryhmän käyttäjät',
            add_user: 'Lisää käyttäjä',
            no_users: 'No käyttäjä in this group.',
            role_group_admin: 'Ryhmän ylläpitäjä',
            role_group_reader: 'Ryhmälukija',
            sr_loading: 'Ladataan ryhmän käyttäjiä',
        },
        reports: {
            title: 'Ryhmän raportit',
            no_reports: 'Ei raportti found. löytynyt',
            generate_report: 'Luo uusi raportti',
            report_type_compliance: 'Yhteenveto vaatimustenmukaisuudesta',
            report_type_risk: 'Riskien yleiskatsaus',
            report_type_sla: 'SLA Suorituskyky',
            report_type_incident: 'Tapahtumaraportti',
            report_type_executive: 'Executive Lyhyt',
            report_type_board: 'Hallituksen raportti',
            status_queued: 'Jonossa',
            status_generating: 'Luodaan',
            status_completed: 'Valmis',
            status_failed: 'Epäonnistui',
            period_start: 'Jakson alku',
            period_end: 'Jakson loppu',
            sr_loading: 'Ladataan ryhmäraportteja',
            /*
             * The two row actions' accessible names, interpolated rather than built
             * as English template literals.
             *
             * They shipped as `aria-label={`Delete report ${…}`}` and
             * `aria-label={`Download ${…} as ${…}`}` — an `aria-label` is
             * user-visible text, so 15 non-English locales heard these in English.
             * The same defect was fixed by hand one page over in the same commit
             * that touched these two lines and left them.
             */
            download_report_aria: 'Download {title} as {format}',
            delete_report_aria: 'Delete report {title}',
        },
        shared_vendors: {
            title: 'Jaetut toimittajat',
            no_vendors: 'Ei shared toimittajat found. löytynyt',
            merged_vendors: 'Merged Toimittajat',
            publish_form_label: 'Julkaise jaettu toimittaja',
            sr_loading: 'Ladataan jaettuja toimittajia',
            sr_loading_merged: 'Ladataan yhdistettyjä toimittajia',
            local_vendors: 'Paikalliset myyjät',
            source_shared: 'Jaettu ryhmästä',
            source_local: 'Paikallinen myyjä',
            category_technology: 'Tekniikka',
            category_consulting: 'Konsultointi',
            category_outsourcing: 'Ulkoistaminen',
            category_cloud: 'Pilvi',
            category_data_processing: 'Tietojen käsittely',
            category_other: 'muu',
            risk_tier_low: 'Low',
            risk_tier_medium: 'Keskikokoinen',
            risk_tier_high: 'Korkea',
            risk_tier_critical: 'Kriittinen',
            compliance_compliant: 'Yhteensopiva',
            compliance_non_compliant: 'Ei yhteensopiva',
            compliance_pending: 'Odottaa',
            compliance_under_review: 'Tarkistuksessa',
            status_active: 'Aktiivinen',
            status_retired: 'Eläkkeellä',
        },
        shared_frameworks: {
            // Fix sprint F28 (WCAG 3.3.2) — the framework-code field was labelled
            // `common.type` ("Type"), so its accessible name said nothing about what
            // it takes. With no installed framework the placeholder resolves to '',
            // leaving a required field with neither a meaningful name nor a hint.
            framework_code_label: 'Kehyskoodi',
            title: 'Jaetut kehykset',
            no_frameworks: 'Ei shared kehys found. löytynyt',
            publish_form_label: 'Julkaise jaettu kehys',
            sr_loading: 'Ladataan jaettuja kehyksiä',
            status_active: 'Aktiivinen',
            status_retired: 'Eläkkeellä',
        },
        shared_forms: {
            title: 'Jaetut lomakemallipohjat',
            no_forms: 'Ei shared form mallit found. löytynyt',
            publish_form_label: 'Julkaise jaettu lomakemalli',
            sr_loading: 'Ladataan jaettuja lomakemalleja',
            status_active: 'Aktiivinen',
            status_retired: 'Eläkkeellä',
        },
        audit: {
            title: 'Ryhmän tarkastus',
            consolidated_title: 'Consolidated Tarkastus Trail',
            consolidated_subtitle: 'Cross-organization tarkastus tapahtuma for this group',
            audit_log_title: 'Ryhmä Tarkastus Loki',
            event_type: 'Tapahtumatyyppi',
            actor: 'Toimija',
            timestamp: 'Aikaleima',
            source_org: 'Lähde Org',
            org: 'Org',
            summary: 'Yhteenveto',
            details: 'Yksityiskohdat',
            no_events: 'Ei tarkastus tapahtuma found. löytynyt',
            all_events: 'Kaikki tapahtumat',
            all_orgs: 'Kaikki organisaatiot',
            organization: 'Organisaatio',
            from: 'Lähettäjä',
            to: 'To',
            search_events: 'Hae tapahtuma...',
            clear: 'Tyhjennä',
            events_24h: 'Tapahtumat (24h)',
            events_7d: 'Tapahtumat (7d)',
            active_orgs: 'Aktiiviset organisaatiot',
            exporting: 'Viedään...',
            export_csv: 'Vie CSV',
            export_json: 'Vie JSON',
            back_to_group: 'Takaisin to Ryhmä',
            back_to_detail: 'Takaisin to Yksityiskohta',
            sr_loading: 'Ladataan tarkastustapahtumia',
            sr_loading_summary: 'Ladataan tarkastuksen yhteenvetoa',
            disabled_by_settings: 'The consolidated audit is switched off in the settings of this group. The root organization of the group can switch it back on under Settings.',
        },
        settings: {
            title: 'Ryhmä Asetukset',
            shared_tasks: 'Share task lists, statistics and calendars across the member organizations of this group',
            inheritance: 'Asetukset Inheritance',
            inheritance_subtitle: 'Määritä, mitkä asetusluokat perivät ryhmältä vai käyttävät paikallisia ohituksia',
            sla_settings: 'SLA Asetukset',
            calendar_settings: 'Calendar Asetukset',
            notification_settings: 'Ilmoitusasetukset',
            intake_settings: 'Intake Asetukset',
            cmp_integration: 'CMP Integraatio',
            overridden: 'Ohitettu',
            inheriting: 'Periytyvä',
            hide_defaults: 'Piilota Oletus',
            show_group_defaults: 'Näytä Ryhmä Oletus',
            group_defaults: 'Ryhmä Oletus',
            inherit_from_group: 'Inherit from Ryhmä',
            use_local_override: 'Käytä paikallista ohitusta',
            local_override_json: 'Paikallinen Ohita (JSON)',
            save_override: 'Tallenna Ohita',
            saving: 'Tallennetaan...',
            sr_loading: 'Ladataan asetusten perintöä',
            sr_loading_settings: 'Ladataan ryhmän asetuksia',
            sr_enabled: 'Käytössä',
            sr_disabled: 'Ei käytössä',
            sr_override_active: 'Paikallinen ohitus aktiivinen',
            sr_inherited: 'Peritty vanhemmalta',
            danger_zone: 'Vaaravyöhyke',
            sharing_options: 'Jakamisasetukset',
            advanced_options: 'Lisäasetukset',
            max_member_orgs: 'Maximum member organizations',
        },
        status_active: 'Aktiivinen',
        status_dissolved: 'Liuennut',
        status_pending: 'Odottaa',
        status_suspended: 'Keskeytetty',
    },
    integrations: {
        nav: {
            webhooks: 'Webhookit',
            domains: 'Verkkotunnukset',
            export_schedules: 'Vientiaikataulut',
            email_log: 'Sähköpostiloki',
        },
    },
    platform: {
        abandoned_uploads: {
            title: 'Abandoned uploads',
            description: 'Files uploaded on a form nobody saved, past the grace window. Reading this changes nothing.',
            grace_window: 'Grace window',
            hours: '{n} hours',
            as_of: 'As of',
            candidates: 'Uploads no record claimed',
            retained_held: 'Kept — legal hold',
            file: 'File',
            uploaded_for: 'Uploaded for',
            uploaded_by: 'Uploaded by',
            uploaded_at: 'Uploaded',
            past_window_by: 'Past the window by',
            hours_short: '{n} h',
            run_sweep: 'Delete abandoned uploads',
            running: 'Deleting...',
            sweep_success: '{swept} upload(s) deleted, {held} kept under a legal hold.',
            sweep_failed: 'The sweep could not be run. Please try again.',
            load_failed: 'The abandoned-upload register could not be read. Please try again.',
            reload: 'Reload',
            empty: 'No upload is past the grace window unclaimed.',
            restore_note: 'A deleted upload is recoverable: it is soft-deleted and can be restored from this page.',
        },
        nav: {
            users: 'Käyttäjät',
            roles: 'Roolit',
            profile: 'Oma profiili',
        },
        security: {
            mfa: 'MFA-asetukset',
            sso: 'SSO-asetukset',
            api_keys: 'API-avaimet',
            service_accounts: 'Palvelutilit',
            ip_allowlist: 'IP-sallittujen lista',
            delegations: 'Delegoinnit',
            break_glass: 'Hätäpääsy',
            session_settings: 'Istuntoasetukset',
            active_sessions: 'Aktiiviset istunnot',
            access_reviews: 'Pääsyn tarkistukset',
            my_reviews: 'Omat tarkistukset',
            role_analytics: 'Roolianalytiikka',
            attestation_schedules: 'Vahvistusaikataulut',
            sod_rules: 'SoD-säännöt',
            sod_violations: 'SoD-rikkomukset',
        },
        module_config: {
            title: 'Moduulien asetukset',
            subtitle: 'Ota käyttöön tai poista käytöstä ominaisuusmoduuleja organisaatiollesi. Käytöstä poistetut moduulit piilotetaan navigaatiosta.',
            enabled: 'Käytössä',
            disabled: 'Ei käytössä',
            save: 'Tallenna asetukset',
            saving: 'Tallennetaan...',
            saved: 'Asetukset tallennettu onnistuneesti',
            nav_label: 'Moduulien asetukset',
            settings: 'Asetukset',
            webhooks: 'Webhookit',
            domains: 'Verkkotunnukset',
            export_schedules: 'Vientiaikataulut',
            email_log: 'Sähköpostiloki',
            announce_loaded: 'Module configuration loaded',
        },
        files_nav: {
            section: 'Tiedostot',
            configuration: 'Kokoonpano',
            browser: 'Tiedostoselain',
            dashboard: 'Tiedostojen hallintapaneeli',
        },
        immutable_audit_trails: {
            nav_group_label: 'Tarkastus ja todiste',
            nav_settings: 'Asetukset',
            nav_status: 'Todistuksen tila',
            nav_integrity_center: 'Integrity Center',
            nav_usage: 'Käyttö ja hyvitykset',
            settings_title: 'Muuttumattomat seurantapolut — Asetukset',
            settings_subtitle: 'Organisaatiokohtaiset asetukset Immutable Audit Trails -lisäosalle.',
            settings_banner: 'Muuttumaton seurantapolku -lisäosa ei ole käytössä organisaatiossasi. Ota se käyttöön tässä aloittaaksesi tason A- ja B-tarkastustapahtumien todisteiden ankkuroimisen.',
            status_title: 'Muuttumattomat kirjauspolut — todisteen tila',
            status_subtitle: 'Tapahtumakohtaiset todisteet moduulien välillä.',
            status_banner: 'Muuttumaton seurantapolku -lisäosa ei ole käytössä organisaatiossasi. Ota se käyttöön siirtymällä Asetuksiin.',
            integrity_center_title: 'Muuttumattomat seurantapolut — Integrity Center',
            integrity_center_subtitle: 'Tarkista todisteet ja vie Integrity Packs.',
            integrity_center_banner: 'Muuttumaton seurantapolku -lisäosa ei ole käytössä organisaatiossasi. Ota se käyttöön asetuksissa ennen vahvistuksen suorittamista tai Integrity Packs -pakettien vientiä.',
            usage_title: 'Muuttumattomat seurantapolut — käyttö ja hyvitykset',
            usage_subtitle: 'Luottosaldo, pakettiostot ja käyttöhistoria.',
            usage_banner: 'Ota muuttumattomat seurantapolut käyttöön asetuksissa, jotta voit alkaa kuluttaa todisteita.',
            module_tips_placeholder: 'Vinkkejä ja ohjeita tälle pinnalle on saatavilla ohjekeskuksessa.',
            column_label: 'Todistus',
            no_proof_aria: 'Todisteita ei tallennettu',
            proof_status_receipt_label: 'kuitti {id}',
            in_progress_hint: 'Käynnissä — päivittyy automaattisesti',
            status_pills: {
                pending: 'Odottaa',
                submitted: 'Lähetetty',
                anchored: 'Ankkuroitu',
                confirmed: 'Vahvistettu',
                retrying: 'Yritetään uudelleen',
                paused_cap: 'Keskeytetty (Cap)',
                failed: 'Epäonnistui',
                failed_auth: 'Todennus epäonnistui',
            },
            status_tooltips: {
                pending: 'Odottaa — jonossa vedospalveluun. Siirtyy pian kohtaan Lähetetty; toimenpiteitä ei tarvita.',
                submitted: 'Lähetetty – tapahtumasi lähetettiin todistepalveluun. Erätilassa se odottaa sulkemista ja ankkuroimista ketjuun. Päivittyy automaattisesti; toimenpiteitä ei tarvita.',
                anchored: 'Ankkuroitu — erä on kirjoitettu ketjuun ja odottaa lopullista vahvistusta. Päivittyy automaattisesti; toimenpiteitä ei tarvita.',
                confirmed: 'Vahvistettu — todiste on ankkuroitu ketjuun ja tarkistettavissa.',
                retrying: 'Yritetään uudelleen – tapahtui ohimenevä virhe. Täsmäytysohjelma yrittää uudelleen automaattisesti; toimenpiteitä ei tarvita.',
                paused_cap: 'Keskeytetty – kuukausittainen luottorajasi on saavutettu. Lunasta lisää luottoja tai odota seuraavaan laskutuskuukauteen.',
                failed: 'Epäonnistui – ankkurointi ei onnistunut. Täsmäytysohjelma yrittää uudelleen automaattisesti; ota yhteyttä tukeen, jos se jatkuu.',
                failed_auth: 'Todennus epäonnistui – todistuspalvelu hylkäsi valtuustietomme. Tarkista muuttumattomien seurantapolkujen asetukset -sivu.',
            },
            mode_pills: {
                direct: 'Suora',
                batch: 'Erä',
            },
            drawer: {
                title: 'Todistuksen yksityiskohdat',
                close: 'Sulje',
                section_proof: 'Todistus',
                section_batch: 'Eräjäsenyys',
                status: 'Tila',
                proof_reference: 'Todistusviittaus',
                receipt_id: 'Kuitin tunnus',
                taxonomy_class: 'Taksonomian luokka',
                units_consumed: 'Kulutetut yksiköt',
                created_at: 'Luotu',
                verification_ref: 'Ketjun vahvistusviite',
                chain: 'Ketju',
                tx_hash: 'Tx hash',
                block_ref: 'Block ref',
                batch_reference: 'Eräviite',
                leaf_index: 'Lehtiindeksi',
                local_event_hash: 'Paikallinen tapahtuma hash',
                show_inclusion: 'Näytä sisällyttämispolku',
                hide_inclusion: 'Piilota sisällyttämispolku',
                privacy_note: 'Hyötykuorma on hajautettu; ComplianceOne ei jättänyt henkilötietoja tälle tapahtumalle.',
                verify_now: 'Vahvista nyt',
                verify_pending: 'Vahvistus käynnissä…',
                verify_verified: 'Vahvistettu – paikalliset + ketjupuolen tarkastukset läpäisty.',
                verify_issue_prefix: 'Vahvistusongelma: ',
                verify_failed: 'Vahvistuspuhelu epäonnistui.',
                verify_result_prefix: 'Vahvistuspäätös:',
                verify_pending_note: 'Varmistus odottaa: tämä todistus odottaa edelleen ketjun ankkuroimista. Yritä uudelleen, kun erä on suljettu.',
                awaiting_anchor_note: 'Tämä todiste on osa avointa erää. Vahvistus tulee saataville, kun erä on sinetöity ja ankkuroitu ketjuun – yleensä organisaation määritetyn eräikkunan sisällä. Tilamerkki päivittyy automaattisesti.',
                terminal_error_note: 'Tämä todiste ei onnistunut ankkuroitumaan. Todistuksen tilan täsmäytysohjelma yrittää uudelleen automaattisesti; manuaalisessa vahvistuksessa ei ole vielä mitään tarkistettavaa.',
            },
            integrity_center: {
                section_run: 'Suorita vahvistus',
                section_progress: 'Suorita edistyminen',
                section_issues: 'Ongelmat',
                period_start: 'Jakson alku',
                period_end: 'Kauden loppu',
                module_filter: 'Moduulit (pilkuilla erotettu, valinnainen)',
                modules_placeholder: 'rights-requests, incidents',
                run_button: 'Suorita vahvistus',
                export_button: 'Vie Integrity Pack',
                export_success: 'Integrity Pack vietiin.',
                export_failed: 'Vienti epäonnistui.',
                empty_disabled: 'Ota muuttumattomat seurantareitit käyttöön asetuksissa suorittaaksesi vahvistuspassit.',
                error_period_required: 'Valitse aloitus- ja lopetuspäivä.',
                error_start_run: 'Juoksun aloittaminen epäonnistui.',
                processed: 'Käsitelty: {processed} / {total}',
                col_verdict: 'Tuomio',
                col_count: 'Count',
                col_proof_link: 'Todistuslinkki',
                col_module: 'Moduuli',
                col_event_type: 'Tapahtuman tyyppi',
                col_expected: 'Odotettu hash',
                col_recomputed: 'Uudelleen laskettu hash',
                col_raised_at: 'Nostettu klo',
                any_verdict: 'Mikä tahansa tuomio',
                no_issues: 'Ei ongelmia.',
            },
            settings_page: {
                load_error: 'Asetusten lataaminen epäonnistui.',
                save: 'Tallenna',
                save_success: 'Asetukset tallennettu.',
                save_error: 'Tallennus epäonnistui.',
                discard: 'Hylkää muutokset',
                read_only_banner: 'Sinulla on vain luku -oikeus. Ota yhteyttä alustan järjestelmänvalvojaan muutosten tekemiseksi.',
                section_status: 'Tila ja käyttöönotto',
                enabled_label: 'Lisäosa käytössä',
                enabled_help: 'Kun tämä on käytössä, jokainen tason A ja tason B tarkastustapahtuma tiivistetään ja ankkuroidaan.',
                section_mode: 'Oletustodistustila',
                mode_direct: 'Suora (yksi ankkuri tapahtumaa kohden)',
                mode_batch: 'Erä (Merkle-juuren ankkuri ryhmää kohti)',
                mode_hybrid: 'Hybridi (luokkakohtainen rekisterioletus)',
                batch_window_label: 'Eräikkuna (minuutit, 1–60)',
                section_taxonomy: 'Suojattu tapahtumataksonomia',
                taxonomy_help: 'Tapahtuma-luokan ohitukset. Taso A → suora tila, taso B → erätila, pois päältä → ei varmennettu.',
                taxonomy_search: 'Haku ohittaa',
                taxonomy_reset: 'Palauta oletusasetukset',
                taxonomy_event_class: 'Tapahtumaluokka',
                taxonomy_override: 'Ohita',
                taxonomy_actions: 'Toiminnot',
                taxonomy_clear: 'Selkeä',
                taxonomy_no_overrides: 'Ei ohituksia — käytetään rekisterin oletusasetuksia.',
                taxonomy_add_class: 'event_class.koodi',
                taxonomy_add: 'Lisää ohitus',
                section_caps: 'Rajat ja rajat',
                cap_label: 'Kuukausikatto (yksikköä; tyhjä = rajoittamaton)',
                warn_label: 'Varoituskynnys (yksiköt; on oltava pienempi kuin yläraja)',
                auto_pause_label: 'Keskeytä uudet vedokset automaattisesti, kun enimmäismäärä on saavutettu',
                section_credentials: 'Valtuustiedot',
                credential_id: 'Valtuustietotunnus',
                credential_rotated_at: 'Viimeksi käännetty',
                rotate_button: 'Kierrä tunnistetietoja',
                rotate_success: 'Tunnuksia kierretty. Vanhat kirjautumistiedot ovat voimassa vielä {n} minuuttia.',
                rotate_failed: 'Tunnistetietojen kierto epäonnistui.',
                rotate_modal_title: 'Kierretäänkö AesirX-kirjautumistietoja?',
                rotate_modal_body: 'Rotaatio antaa uuden valtuustodistuksen. AesirX hyväksyy vanhat valtuustiedot 60 minuutin lisäajan ajaksi, jotta lennolla olevat työntekijät voivat lopettaa nykyisen työnsä. 60 minuutin kuluttua vanha valtuutus peruutetaan.',
                rotate_modal_confirm: 'Pyöritä nyt',
                tier_a_off_modal_title: 'Poistetaanko tapahtumaluokan tarkistus käytöstä?',
                tier_a_off_modal_body: 'Tapahtumaluokan asettaminen tilaan "Pois" estää todistusta ankkuroimasta näitä tapahtumia. Arkkitehtuuri §6 selittää sääntelyn vaikutuksen: A-tason tapahtumat ovat sääntelyn kannalta tärkeitä; niiden poistaminen käytöstä voi rikkoa kirjausketjun puolustettavuuden.',
                tier_a_off_modal_confirm: 'Ymmärrän – käytä ohitusta',
                error_warn_lt_cap: 'Varoitusrajan on oltava pienempi kuin kuukausiraja.',
                error_window_range: 'Eräikkunan on oltava 1–60 minuuttia.',
                error_run_in_progress: 'Varmistusajo on käynnissä; lisäosaa ei voi poistaa käytöstä juuri nyt.',
            },
            status_page: {
                filter_status: 'Tila',
                filter_mode: 'Todistustila',
                filter_module: 'Moduuli',
                filter_source_collection: 'Lähdekokoelma',
                filter_from_date: 'From',
                filter_to_date: 'Vastaanottaja',
                filter_search: 'Etsi',
                any_status: 'Mikä tahansa tila',
                any_mode: 'Mikä tahansa tila',
                col_module: 'Moduuli',
                col_event_type: 'Tapahtuman tyyppi',
                col_proof_status: 'Tila',
                col_proof_mode: 'tila',
                col_created_at: 'Luotu',
                col_units: 'Yksiköt',
                empty_disabled: 'Muuttumaton seurantapolku -lisäosa ei ole käytössä organisaatiossasi. Ota se käyttöön siirtymällä Asetuksiin.',
                empty_enabled_no_rows: 'Mikään todistelinkki ei vastaa nykyisiä suodattimia.',
                export_csv: 'Vie CSV',
                export_xlsx: 'Vie XLSX',
                export_success: 'Vienti valmis',
                export_failed: 'Vienti epäonnistui',
            },
            usage_page: {
                section_alerts: 'Hälytykset',
                alert_dismiss: 'Hylkää',
                alert_dismiss_aria: 'Hylkää ilmoitus {id}',
                alert_dismissed: 'Hälytys hylätty',
                alert_dismiss_failed: 'Hälytyksen hylkääminen epäonnistui',
                section_balance: 'Luottosaldo',
                balance_label: 'Saatavilla olevat krediitit',
                balance_hint: 'Yksiköt jäljellä (1 yksikkö = 1 suojattu tapahtumavarma).',
                section_current_month: 'Nykyinen kuukausi',
                consumed_label: 'Kulutettu',
                granted_label: 'Myönnetty',
                refunded_label: 'Hyvitetty',
                net_label: 'Net',
                section_history: 'Käyttöhistoria',
                history_kind: 'Ystävällinen',
                history_units: 'Yksiköt',
                history_balance_after: 'Saldo jälkeen',
                history_notes: 'Huomautuksia',
                history_at: 'Milloin',
                cap_warning_banner: 'Olet ylittänyt tämän laskutuskuukauden varoitusrajan. Harkitse lisäkrediittien lunastamista.',
                cap_paused_banner: 'Kuukausikatto saavutettu. Todisteiden toimittaminen keskeytetään seuraavaan kuukauteen tai kunnes enimmäismäärää nostetaan.',
                section_redeem: 'Lunasta luottopaketti',
                redeem_purchase_id: 'Laskun numero',
                redeem_package: 'Paketti',
                redeem_button: 'Lunasta',
                redeem_pending: 'Lunastetaan…',
                redeem_success: 'Krediitit lunastettu',
                redeem_failed: 'Lunastus epäonnistui',
                redeem_option_template: '{name} — {units} {units_label} — {price}',
                redeem_option_units: 'yksiköitä',
                alert_dismissing: 'Hylätään…',
                alert_kind: {
                    cap_reached: 'Kuukausikatto saavutettu',
                    low_balance: 'Matala tasapaino',
                    auth_failure: 'Todennus epäonnistui',
                    merkle_mismatch: 'Merklen yhteensopimattomuus',
                    batch_terminal_failure: 'Eräpäätteen vika',
                    direct_terminal_failure: 'Suoran lähetyksen päätevirhe',
                },
                usage_kind: {
                    consume: 'Kuluttaa',
                    refund: 'Hyvitys',
                    grant: 'Grant',
                    adjustment: 'Säätö',
                    reservation: 'Varaus',
                    credit_grant: 'Credit granted',
                    consumption: 'Proof consumed',
                    refund_failed_proof: 'Refund (failed proof)',
                },
                package_starter: 'Aloittaja',
                package_growth: 'Kasvu',
                package_enterprise: 'Yritys',
                // Sprint 48.5.x — self-service "Buy More" billing (Flow A + B).
                buy_more: {
                    buy_button: 'Osta lisää krediittejä',
                    purchase_title: 'Osta lisää krediittejä',
                    purchase_subtitle: 'Valitse paketti. Sinut ohjataan turvalliseen Stripe-maksuun, ja laskusi saapuu sähköpostitse.',
                    checkout_button: 'Jatka maksamaan',
                    checkout_pending: 'Avataan maksua…',
                    cancel_button: 'Peruuta',
                    purchase_error: 'Maksua ei voitu aloittaa. Yritä pian uudelleen.',
                    redeem_subtitle: 'Jo maksettu? Valitse ostamasi paketti ja syötä laskun tunnus sähköpostistasi lisätäksesi krediitit.',
                    error_not_found: 'Kyseisellä numerolla ei löytynyt laskua. Tarkista laskun numero ja yritä uudelleen.',
                    error_not_paid: 'Tätä laskua ei ole vielä merkitty maksetuksi.',
                    error_org_mismatch: 'Tätä laskua ei ole myönnetty organisaatiollesi.',
                    error_package_mismatch: 'Tämä lasku ei vastaa valittua pakettia.',
                    error_billing_unavailable: 'Laskutuspalveluun ei saatu yhteyttä. Yritä pian uudelleen.',
                },
                cap_over_banner: 'The monthly cap has been reached. Proofs keep being submitted because pausing at the cap is switched off.',
                resume_paused_button: 'Resume paused proofs',
                resume_paused_hint: 'Proofs held back when the monthly cap was reached are released by themselves when the cap is raised or removed, or when pausing at the cap is switched off. Use this once a new month has begun.',
                resume_paused_success: '{count} paused proofs released.',
                resume_paused_none: 'No paused proofs to release.',
                resume_paused_failed: 'The paused proofs could not be released.',
            },
        },
        files_config: {
            title: 'Tiedostojen asetukset',
            subtitle: 'Määritä tiedostojen latausrajat, säilytyskäytännöt ja omistajakohtaiset ohitukset alustan liitepinnalle.',
            section_global: 'Globaalit oletusasetukset',
            section_global_help: 'Nämä rajoitukset koskevat kaikkia organisaatiosi käyttäjien lataamia tiedostoja, ellei alla oleva omistajakohtainen ohitus muuta niitä.',
            section_overrides: 'Omistajakohtaiset ohitukset',
            section_overrides_help: 'Ohita tietyn omistajatyypin yleiset rajoitukset. Omistajakohtaiset arvot voittaa aina globaalin asetuksen.',
            max_file_size_label: 'Tiedoston enimmäiskoko',
            max_file_size_hint: 'Yksittäinen lataus (MiB)',
            max_total_attachments_label: 'Liitteiden enimmäismäärä',
            max_total_attachments_hint: 'Koko organisaatiossa',
            max_total_size_label: 'Tallennustilan enimmäismäärä yhteensä',
            max_total_size_hint: 'Koko organisaatiossa (MiB)',
            allowed_mime_types_label: 'Sallitut tiedostotyypit',
            allowed_mime_types_placeholder: 'Valitse MIME-tyypit',
            retention_days_label: 'Säilytysaika (päiviä)',
            retention_days_hint: 'Kuinka kauan tiedostoja on säilytettävä, ennen kuin ne voidaan poistaa. Jätä tyhjäksi, jos et halua säilyttää säilytyskäytäntöä.',
            legal_hold_label: 'Laillinen pito',
            legal_hold_hint: 'Kun tämä on käytössä, tämän omistajatyypin tiedoston poistaminen vaatii laillisen säilytyksen ohitusoikeuden ja operaattorin nimenomaisen valinnan.',
            save: 'Tallenna muutokset',
            saving: 'Tallennetaan…',
            reset: 'Nollaa',
            save_success: 'Asetukset tallennettu',
            empty_state_title: 'Käytä alustan oletusasetuksia',
            empty_state_body: 'Organisaatiosi käyttää tällä hetkellä alustan oletustiedostokäytäntöä. Luo organisaatiotason ohitus muokkaamalla mitä tahansa alla olevaa arvoa.',
            read_only_banner: 'Sinulla on vain luku -oikeus tiedostokokoonpanoon. Ota yhteyttä alustan järjestelmänvalvojaan muutosten tekemiseksi.',
            owner_type_default_badge: 'Oletus',
            owner_type_overridden_badge: 'Ohitettu',
            owner_type_label: 'Omistajan tyyppi',
            owner_type_collapse: 'Kutista',
            owner_type_expand: 'Laajenna',
            override_save: 'Tallenna ohitus',
            override_remove: 'Poista ohitus',
            mime_labels: {
                'application/pdf': 'PDF asiakirja',
                'image/png': 'PNG-kuva',
                'image/jpeg': 'JPEG-kuva',
                'image/jpg': 'JPG-kuva',
                'application/msword': 'Word-asiakirja (vanha)',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word-asiakirja',
                'application/vnd.ms-excel': 'Excel-laskentataulukko (vanha)',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel-laskentataulukko',
                'text/csv': 'CSV tiedosto',
                'text/plain': 'Pelkkää tekstiä',
                'message/rfc822': 'Sähköpostiviesti',
            },
            global_legal_hold_label: 'Organization-wide legal hold',
            global_legal_hold_hint: 'When enabled, NO file across any owner type can be hard-deleted unless the operator has the legal_hold_override permission and explicitly opts in.',
            global_retention_days_label: 'Default retention period (days)',
            global_retention_days_hint: 'How long files must be kept before they become eligible for deletion. Leave blank for no retention policy. Per-owner-type overrides below can tighten.',
            global_abandoned_grace_label: 'Abandoned upload grace window (hours)',
            global_abandoned_grace_hint: 'How long a file that no saved record has claimed is left alone before the abandoned-upload sweep soft-deletes it. At least 24 hours, so a form that is still open is never affected.',
            global_abandoned_grace_invalid: 'Enter a whole number of hours from {min} to {max}.',
        },
        files_browser: {
            title: 'Tiedostoselain',
            subtitle: 'Hae, suodata ja hallitse kaikkia organisaatioon ladattuja liitteitä.',
            filter_owner_type: 'Omistajan tyyppi',
            filter_uploader: 'Lähettäjä',
            filter_from: 'Ladattu jälkeen',
            filter_to: 'Ladattu aiemmin',
            filter_size_min: 'Minimikoko (MiB)',
            filter_size_max: 'Suurin koko (MiB)',
            filter_mime: 'Tiedostotyyppi',
            filter_deleted_state: 'Poistotila',
            filter_deleted_any: 'Kaikki tiedostot',
            filter_deleted_only: 'Vain poistettu',
            filter_deleted_exclude: 'Sulje pois poistettu',
            filter_orphaned: 'Vain orvot',
            filter_search_placeholder: 'Etsi tiedostonimi…',
            filter_apply: 'Käytä suodattimia',
            filter_clear: 'Selkeät suodattimet',
            col_filename: 'Tiedostonimi',
            col_owner_type: 'Omistajan tyyppi',
            col_owner_entity: 'Omistaja',
            col_uploader: 'Lähettäjä',
            col_uploaded_at: 'Ladattu',
            col_size: 'Koko',
            col_mime: 'Kirjoita',
            col_state: 'Tila',
            col_actions: 'Toiminnot',
            state_active: 'Aktiivinen',
            state_deleted: 'Poistettu',
            state_orphaned: 'Orvoksi jäänyt',
            action_view_detail: 'Näytä tiedot',
            action_download: 'Lataa',
            action_ai_edits: 'AI Muokkaukset',
            action_soft_delete: 'Pehmeä poisto',
            action_restore: 'Palauta',
            action_hard_delete: 'Pysyv? poisto',
            bulk_soft_delete: 'Pehmeä poisto valittu',
            bulk_export_csv: 'Vie CSV',
            bulk_select_all: 'Valitse kaikki',
            reason_label: 'Syy',
            reason_min_10_chars: 'Syy vaaditaan (vähintään 10 merkkiä)',
            reason_min_20_chars: 'Syy vaaditaan (vähintään 20 merkkiä)',
            legal_hold_override_label: 'Ohita laillinen säilytysvaatimus',
            legal_hold_override_warning: 'Tämä tiedosto on säilytysvaatimuksen alainen. Kova poistaminen kirjataan kirjausketjuun syyn ja ohituslipun kanssa.',
            empty_state_title: 'Yksikään tiedosto ei vastaa suodattimiasi',
            empty_state_body: 'Säädä suodattimiasi nähdäksesi tulokset tai tyhjennä ne nähdäksesi kaikki tiedostot.',
            pagination_prev: 'Edellinen',
            pagination_next: 'Seuraavaksi',
            pagination_page: 'Sivu {page}/{total}',
            pagination_size_label: 'per sivu',
            pagination_total: 'Yhteensä: {count}',
            toast_soft_deleted: 'Tiedosto poistettu pehmeästi',
            toast_restored: 'Tiedosto palautettu',
            toast_hard_deleted: 'Tiedosto poistettu pysyvästi',
            toast_bulk_deleted: '{count} tiedosto(t) poistettu',
            toast_error: 'Toiminto epäonnistui',
            confirm_soft_delete_title: 'Pehmeä poista tiedosto',
            confirm_restore_title: 'Palauta tiedosto',
            confirm_hard_delete_title: 'Poista tiedosto pysyvästi',
            confirm_bulk_soft_delete_title: 'Valittujen tiedostojen pehmeä poistaminen',
            confirm_submit: 'Vahvista',
            confirm_cancel: 'Peruuta',
            col_view_entity: 'View',
        },
        files_detail: {
            modal_title: 'Tiedoston tiedot',
            close: 'Sulje',
            meta_filename: 'Tiedostonimi',
            meta_size: 'Koko',
            meta_mime: 'Kirjoita',
            meta_owner_type: 'Omistajan tyyppi',
            meta_owner_entity: 'Omistajakokonaisuus',
            meta_uploader: 'Lähettäjä',
            meta_uploaded_at: 'Ladattu',
            meta_storage_path: 'Tallennuspolku',
            meta_deleted_state: 'Poistotila',
            audit_section_title: 'Kirjausketju',
            audit_load_more: 'Lataa lisää tapahtumia',
            audit_empty: 'Tälle tiedostolle ei ole tallennettu tarkastustapahtumia.',
            audit_config_changed: 'Kokoonpano muutettu',
            audit_soft_deleted: 'Pehmeä-poistettu',
            audit_restored: 'Palautettu',
            audit_hard_deleted: 'Poistettu pysyvästi',
            audit_downloaded: 'Ladattu',
            audit_quota_breached: 'Kiintiön rikkomista yritettiin',
            audit_owner_type_download_denied: 'Nouto evätty',
            /*
             * The audit row as ONE composed string, not three JSX fragments glued
             * with English.
             *
             * The row used to render `{' by '}` and `{' on '}` as literals, so every
             * locale read "Endgültig gelöscht by Anna Schmidt on 11. Apr 2026" — the
             * event label and the actor came from `t` and the user map, the
             * connective words did not. A locale that orders these differently
             * cannot be rendered correctly by concatenation at all, which is why
             * this is a template rather than two `by`/`on` keys.
             *
             * `{actor}` is a link, so the page splits this on the placeholders and
             * substitutes nodes rather than calling `.replace`.
             */
            audit_row: '{event}, {actor}, {at}',
            action_download: 'Lataa',
            action_soft_delete: 'Pehmeä poisto',
            action_restore: 'Palauta',
            action_hard_delete: 'Pysyv? poisto',
            effective_settings_title: 'Tehokas politiikka',
            effective_max_file_size: 'Tiedoston enimmäiskoko',
            effective_retention_days: 'Säilytys',
            effective_legal_hold: 'Laillinen pito',
            effective_legal_hold_yes: 'Aktiivinen',
            effective_legal_hold_no: 'Ei mitään',
        },
        files_dashboard: {
            title: 'Tiedostojen hallintapaneeli',
            subtitle: 'Tallennustilan käyttö, kasvu ja orvot tiedostot koko organisaatiossa.',
            kpi_total_files: 'Tiedostot yhteensä',
            kpi_total_size: 'Tallennustila yhteensä',
            kpi_near_quota: 'Kiintiön käyttö',
            kpi_orphaned: 'Orvot tiedostot',
            chart_by_owner_type: 'Tiedostot omistajatyypin mukaan',
            chart_growth_30d: '30 päivän kasvu',
            toggle_count: 'Laskun mukaan',
            toggle_size: 'Koon mukaan',
            orphaned_panel_title: 'Orvot tiedostot (20 parasta)',
            orphaned_view_all: 'Näytä kaikki orvot tiedostot',
            orphaned_empty: 'Orpotiedostoja ei löytynyt.',
            growth_empty: 'Ei lataustoimintaa viimeisen 30 päivän aikana.',
            retention_n_days: '{days} päivää',
            retention_none: 'Ei politiikkaa',
        },
    },
    category_labels: {
        'user-guide': 'Käyttöopas',
        'admin-guide': 'Järjestelmänvalvojan opas',
        'getting-started': 'Aloitus',
        faq: 'FAQ',
    },
    tag_labels: {
        faq: 'FAQ',
        dsar: 'DSAR',
        dsr: 'DSR',
        cmp: 'CMP',
        hmac: 'HMAC',
        sso: 'SSO',
        mfa: 'MFA',
        rbac: 'RBAC',
        api: 'API',
        'api-keys': 'API Näppäimet',
        webhook: 'Webhook',
        webhooks: 'Webhooks',
        admin: 'Admin',
        aesirx: 'AesirX',
        purposes: 'Tarkoitukset',
        preferences: 'Asetukset',
        onboarding: 'Käyttöönotto',
        'first-steps': 'Ensimmäiset askeleet',
        login: 'Kirjaudu sisään',
        navigation: 'Navigointi',
        users: 'Käyttäjät',
        user: 'Käyttäjä',
        roles: 'Roolit',
        role: 'Rooli',
        'access-control': 'Kulunvalvonta',
        'access-management': 'Käyttöoikeuksien hallinta',
        permissions: 'Käyttöoikeudet',
        permission: 'Lupa',
        consent: 'Suostumus',
        'consent-management': 'Suostumuksen hallinta',
        privacy: 'Yksityisyys',
        pdpl: 'PDPL',
        gdpr: 'GDPR',
        iso27001: 'ISO 27001',
        audit: 'Audit',
        auditing: 'Tilintarkastus',
        breach: 'Rikkominen',
        incident: 'Tapahtuma',
        incidents: 'Tapahtumat',
        notification: 'Ilmoitus',
        notifications: 'Ilmoitukset',
        reporting: 'Raportointi',
        risk: 'Riski',
        vendor: 'Myyjä',
        vendors: 'Myyjät',
        'vendor-risk': 'Myyjän riski',
        assessment: 'Arviointi',
        assessments: 'Arvioinnit',
        dpia: 'DPIA',
        'cross-border': 'Rajat ylittävä',
        transfer: 'Siirto',
        transfers: 'Siirrot',
        deletion: 'Poistaminen',
        retention: 'Säilytys',
        'data-mapping': 'Tietojen kartoitus',
        discovery: 'Löytö',
        classification: 'Luokittelu',
        workflow: 'Työnkulku',
        workflows: 'Työnkulut',
        template: 'Malli',
        templates: 'Mallit',
        catalog: 'Katalogi',
        monitoring: 'Valvonta',
        tasks: 'Tehtävät',
        task: 'Tehtävä',
        'task-management': 'Tehtävien hallinta',
        organizations: 'Organisaatiot',
        org: 'Organisaatio',
        governance: 'Hallinto',
        compliance: 'Vaatimustenmukaisuus',
        forms: 'Lomakkeet',
        form: 'Lomake',
        connectors: 'Liittimet',
        connector: 'Liitin',
        integration: 'Integrointi',
        integrations: 'Integraatiot',
        security: 'Turvallisuus',
        encryption: 'Salaus',
        backup: 'Varmuuskopio',
        recovery: 'Toipuminen',
        maintenance: 'Huolto',
        settings: 'Asetukset',
        configuration: 'Kokoonpano',
        sla: 'SLA',
        escalation: 'Eskaloituminen',
        remediation: 'Korjaus',
        evidence: 'Todisteet',
        'case-management': 'Tapausten hallinta',
        profile: 'Profiili',
        delegation: 'valtuuskunta',
        'service-accounts': 'Palvelutilit',
        license: 'Lisenssi',
        'license-management': 'Lisenssien hallinta',
        plan: 'Suunnittele',
        plans: 'Suunnitelmat',
        dashboard: 'Kojelauta',
        'web-consent': 'Verkkosuostumus',
        'partner-marketplace': 'Kumppanimarkkinapaikka',
        partner: 'Kumppani',
        partners: 'Yhteistyökumppanit',
        deliverables: 'Toimitettavat tuotteet',
        'service-tiers': 'Palvelutasot',
        'support-tickets': 'Tukiliput',
        'audit-trail': 'Audit Trail',
        'first-time-setup': 'Ensiasennus',
        setup: 'Asennus',
        quickstart: 'Pika-aloitus',
        tour: 'Tour',
        tours: 'Matkat',
        'sample-data': 'Esimerkkitiedot',
        'data-subject': 'Rekisteröity',
        'data-subjects': 'Rekisteröidyt',
        pseudonymous: 'Pseudonyymi',
        identified: 'Tunnistettu',
        'help-center': 'Ohjekeskus',
        article: 'Artikkeli',
        articles: 'Artikkelit',
        search: 'Etsi',
        'platform-settings': 'Alustan asetukset',
        'platform-config': 'Alustan asetukset',
        'module-config': 'Moduulin konfigurointi',
        'module-configuration': 'Moduulin konfigurointi',
        'rights-requests': 'Oikeuspyynnöt',
        'access-accountability': 'Käytä vastuullisuutta',
        'data-discovery': 'Data Discovery',
        'incident-ops': 'Tapahtumaoperaatiot',
        'deletion-orchestration': 'Poiston orkestrointi',
        'compliance-forms': 'Vaatimustenmukaisuuslomakkeet',
        'program-governance': 'Ohjelman hallinto',
        localization: 'Lokalisointi',
        'privacy-scanner': 'Yksityisyysskanneri',
        'consent-properties': 'Suostumuksen ominaisuudet',
        acceptances: 'Hyväksynnät',
        access: 'Pääsy',
        'access-review': 'Access Review',
        'access-reviews': 'Pääsy arvosteluihin',
        accountability: 'Vastuullisuus',
        acknowledgment: 'Tunnustus',
        'action-plans': 'Toimintasuunnitelmat',
        activation: 'Aktivointi',
        activations: 'Aktivoinnit',
        'active-response': 'Aktiivinen vastaus',
        activity: 'Toiminta',
        adequacy: 'Riittävyys',
        'admin-guide': 'Järjestelmänvalvojan opas',
        aggregation: 'Aggregointi',
        agreements: 'sopimukset',
        ai: 'AI',
        alerts: 'Hälytykset',
        analytics: 'Analytics',
        anchoring: 'Ankkurointi',
        approval: 'Hyväksyntä',
        approvals: 'Hyväksynnät',
        apps: 'Sovellukset',
        assets: 'Omaisuus',
        assignments: 'Tehtävät',
        attestation: 'Todistus',
        'audit-log': 'Tarkastusloki',
        'audit-pack': 'Tarkastuspaketti',
        authentication: 'Todennus',
        authority: 'Viranomainen',
        'auto-play': 'Automaattinen toisto',
        automated: 'Automatisoitu',
        automation: 'Automaatio',
        baseline: 'Perustaso',
        batches: 'Erät',
        bidirectional: 'Kaksisuuntainen',
        bilingual: 'Kaksikielinen',
        bindings: 'Sidokset',
        blockchain: 'Blockchain',
        board: 'taulu',
        brand: 'Tuotemerkki',
        brds: 'BRD:t',
        'break-glass': 'Break-Glass',
        builder: 'Rakentaja',
        bulk: 'Bulkki',
        'bulk-sync': 'Joukkosynkronointi',
        bundles: 'Kimput',
        calendar: 'Kalenteri',
        campaigns: 'Kampanjat',
        cases: 'Tapaukset',
        categories: 'Luokat',
        category: 'Luokka',
        certification: 'Sertifiointi',
        chain: 'Ketju',
        change: 'Muuta',
        'change-awareness': 'Muutostietoisuus',
        'change-log': 'Muutosloki',
        'change-management': 'Muutosten hallinta',
        'change-requests': 'Muutospyynnöt',
        'cmp-validation': 'CMP Vahvistus',
        collection: 'Kokoelma',
        comparison: 'Vertailu',
        completeness: 'Täydellisyys',
        completion: 'Valmistuminen',
        'compliance-posture': 'Vaatimustenmukaisuusasento',
        'compliance-proof': 'Todiste vaatimustenmukaisuudesta',
        'compliance-reviews': 'Vaatimustenmukaisuusarvostelut',
        config: 'Konfig',
        confirmation: 'Vahvistus',
        conflicts: 'Konfliktit',
        consolidation: 'Konsolidointi',
        continuous: 'Jatkuva',
        contracts: 'Sopimukset',
        controls: 'Säätimet',
        cookies: 'Evästeet',
        cooperation: 'Yhteistyö',
        coordination: 'Koordinointi',
        'corporate-governance': 'Yrityshallinto',
        'corrective-actions': 'Korjaavat toimet',
        coverage: 'Kattavuus',
        'credential-vault': 'Valtuustietoholvi',
        credentials: 'Valtuustiedot',
        cycles: 'Pyörät',
        dashboards: 'Kojelaudat',
        'data-assets': 'Tietovarat',
        'data-flows': 'Tietovirrat',
        'data-sensitivity': 'Tietojen herkkyys',
        'data-steward': 'Tietojen valvoja',
        deadlines: 'Määräajat',
        'decree-356': 'Asetus 356',
        dedup: 'Dedup',
        definitions: 'Määritelmät',
        deliverable: 'Toimitettava',
        departments: 'Osastot',
        'dependency-order': 'Riippuvuusjärjestys',
        deployment: 'Käyttöönotto',
        detection: 'Havaitseminen',
        diff: 'Diff',
        'digital-properties': 'Digitaaliset ominaisuudet',
        dimensions: 'Mitat',
        disable: 'Poista käytöstä',
        'disaster-recovery': 'Katastrofipalautus',
        disclosure: 'Tietojen paljastaminen',
        documentation: 'Dokumentaatio',
        documents: 'Asiakirjat',
        domains: 'Verkkotunnukset',
        dossier: 'Asiakirja',
        dpa: 'DPA',
        drift: 'Drift',
        'drift-detection': 'Poikkeaman tunnistus',
        'due-diligence': 'Due Diligence',
        email: 'S?hk?posti',
        emergency: 'Hätä',
        enable: 'Ota käyttöön',
        'enterprise-map': 'Yrityskartta',
        erasure: 'Poista',
        evaluation: 'Arviointi',
        'event-bus': 'Tapahtuman bussi',
        exceptions: 'Poikkeukset',
        execution: 'Toteutus',
        executive: 'Executive',
        exercises: 'Harjoitukset',
        export: 'Vie',
        exports: 'Vienti',
        filing: 'Arkistointi',
        filter: 'Suodata',
        filtering: 'Suodatus',
        findings: 'Havainnot',
        flows: 'Virtaukset',
        'follow-up': 'Seuranta',
        forensic: 'Oikeuslääketieteen',
        forensics: 'Forensics',
        'form-filler': 'Lomakkeen täyttö',
        framework: 'viitekehys',
        frameworks: 'Kehykset',
        'gap-analysis': 'Aukon analyysi',
        'gap-report': 'Aukkoraportti',
        gaps: 'aukkoja',
        gates: 'Portit',
        'getting-started': 'Aloitus',
        'golden-templates': 'Kultaiset mallit',
        grc: 'GRC',
        'group-management': 'Ryhmän hallinta',
        groups: 'ryhmät',
        'guided-tours': 'Opastetut kierrokset',
        guides: 'Oppaat',
        handling: 'Käsittely',
        'hands-on': 'Hands-On',
        health: 'Terveys',
        help: 'Apua',
        hierarchy: 'Hierarkia',
        history: 'Historia',
        i18n: 'i18n',
        iam: 'IAM',
        'identity-provider': 'Henkilöllisyyden tarjoaja',
        implementation: 'Toteutus',
        import: 'Tuo',
        improvement: 'Parantaminen',
        'incident-response': 'Tapahtumavastaus',
        inconsistencies: 'Epäjohdonmukaisuudet',
        instances: 'Esineet',
        intake: 'Saanti',
        'intake-key': 'Imuavain',
        interactive: 'Interaktiivinen',
        international: 'Kansainvälinen',
        inventory: 'Varasto',
        investigation: 'Tutkinta',
        investigations: 'Tutkimukset',
        'ip-allowlist': 'IP-sallitut lista',
        issues: 'Ongelmat',
        justification: 'Perustelut',
        justifications: 'Perustelut',
        kanban: 'Kanban',
        keyboard: 'Näppäimistö',
        keys: 'Avaimet',
        'knowledge-base': 'Tietokanta',
        koncernstyring: 'Corporate Governance',
        kpi: 'KPI',
        labeling: 'Merkinnät',
        language: 'Kieli',
        'language-packs': 'Kielipaketit',
        languages: 'Kielet',
        lawfulness: 'Laillisuus',
        layout: 'Asettelu',
        'legal-basis': 'Oikeusperusta',
        'lessons-learned': 'Oppitunnit',
        levels: 'Tasot',
        library: 'Kirjasto',
        lifecycle: 'Elinkaari',
        lineage: 'Sukuperä',
        links: 'Linkit',
        listings: 'Listaukset',
        live: 'Livenä',
        locale: 'Alue',
        log: 'Log',
        logging: 'Kirjaaminen',
        'managed-service': 'Hallittu palvelu',
        mapping: 'Kartoitus',
        mappings: 'Kartoitukset',
        'market-packs': 'Markkinapaketit',
        marketplace: 'Markkinapaikka',
        maturity: 'Kypsyys',
        metrics: 'Mittarit',
        models: 'Mallit',
        module: 'Moduuli',
        modules: 'Moduulit',
        mps: 'MPS',
        'multi-factor': 'Monitekijä',
        'multi-market': 'Multi-Market',
        'multi-org': 'Multi-Org',
        'multi-property': 'Monikiinteistö',
        'my-tasks': 'Omat tehtäväni',
        obligations: 'Velvollisuudet',
        offboarding: 'Offboarding',
        oidc: 'OIDC',
        orchestration: 'Orkestrointi',
        'org-groups': 'Organisaatioryhmät',
        organization: 'Organisaatio',
        overdue: 'Myöhässä',
        overview: 'Yleiskatsaus',
        'owner-review': 'Omistajan arvostelu',
        package: 'Paketti',
        packages: 'Paketit',
        packs: 'Pakkaukset',
        pagination: 'Sivutus',
        'parent-child': 'Vanhempi-lapsi',
        patterns: 'kuvioita',
        performance: 'Suorituskyky',
        periodic: 'Jaksottainen',
        periods: 'Jaksot',
        personal: 'Henkilökohtainen',
        'personal-data': 'Henkilötiedot',
        personas: 'Henkilöt',
        planning: 'Suunnittelu',
        platform: 'Alusta',
        playbooks: 'Toimintakirjat',
        poc: 'PoC',
        'point-in-time': 'Point-in-Time',
        policies: 'Käytännöt',
        portfolio: 'Portfolio',
        'post-incident': 'Tapahtuman jälkeinen',
        'power-user': 'Tehokäyttäjä',
        practice: 'Harjoittele',
        pricing: 'Hinnoittelu',
        'privacy-impact': 'Tietosuojavaikutus',
        procedures: 'Menettelyt',
        processes: 'Prosessit',
        processing: 'Käsittely',
        'processing-activities': 'Käsittelytoiminnot',
        procurement: 'Hankinta',
        productivity: 'Tuottavuus',
        profiles: 'Profiilit',
        programs: 'Ohjelmat',
        progress: 'Edistyminen',
        proof: 'Todistus',
        propagation: 'Lisääntyminen',
        properties: 'Ominaisuudet',
        'pub-sub': 'Pub-Sub',
        'public-form': 'Julkinen lomake',
        quality: 'Laatu',
        questionnaires: 'Kyselylomakkeet',
        'quick-links': 'Pikalinkit',
        'quick-start': 'Pika-aloitus',
        'quick-starts': 'Pikakäynnistykset',
        raci: 'RACI',
        readiness: 'Valmius',
        'real-time': 'Reaaliaikainen',
        reassessments: 'Uudelleenarvioinnit',
        reconciliation: 'Sovittelu',
        record: 'Record',
        records: 'Records',
        recurring: 'Toistuva',
        register: 'Rekisteröidy',
        registry: 'Rekisteri',
        regulations: 'määräyksiä',
        regulator: 'Säädin',
        regulatory: 'Sääntely',
        'regulatory-frameworks': 'Sääntelykehykset',
        remap: 'Reap',
        reminders: 'Muistutukset',
        reports: 'Raportit',
        requests: 'Pyynnöt',
        requirements: 'Vaatimukset',
        response: 'Vastaus',
        'response-plans': 'Vastaussuunnitelmat',
        'response-team': 'Vastausryhmä',
        'response-templates': 'Vastausmallit',
        'response-time': 'Vastausaika',
        responses: 'Vastaukset',
        responsibility: 'Vastuullisuus',
        results: 'Tulokset',
        review: 'Tarkista',
        reviews: 'Arvostelut',
        'right-to-delete': 'Oikeus poistaa',
        rights: 'Oikeudet',
        'risk-tiers': 'Riskitasot',
        rollouts: 'Käyttöönotto',
        rollup: 'Rollup',
        ropa: 'ROPA',
        routing: 'Reititys',
        rpo: 'RPO',
        rto: 'RTO',
        rules: 'säännöt',
        runs: 'Juoksee',
        saml: 'SAML',
        scan: 'Skannaa',
        scanning: 'Skannaus',
        scc: 'SCC',
        schedule: 'Aikataulu',
        scheduled: 'Aikataulutettu',
        schedules: 'Aikataulut',
        scheduling: 'Ajoitus',
        scores: 'Pisteet',
        scoring: 'Pisteytys',
        scripts: 'Käsikirjoitukset',
        'security-reviews': 'Turvallisuusarvostelut',
        'sensitive-data': 'Arkaluonteiset tiedot',
        sensitivity: 'Herkkyys',
        'separation-of-duties': 'Tehtävien erottaminen',
        service: 'Palvelu',
        sessions: 'Istunnot',
        'setup-wizard': 'Ohjattu asennustoiminto',
        severity: 'Vakavuus',
        'shared-governance': 'Jaettu hallinto',
        'shared-settings': 'Jaetut asetukset',
        shortcuts: 'Pikanäppäimet',
        sidebar: 'Sivupalkki',
        signals: 'Signaalit',
        'single-sign-on': 'Kertakirjautuminen',
        snapshots: 'Tilannekuvat',
        sod: 'SoD',
        sorting: 'Lajittelu',
        sources: 'Lähteet',
        staging: 'Lavastus',
        standards: 'Standardit',
        status: 'Tila',
        structure: 'Rakenne',
        'sub-processor': 'Aliprosessori',
        'sub-processors': 'Aliprosessorit',
        submissions: 'Lähetykset',
        subprocessors: 'Aliprosessorit',
        subscription: 'Tilaus',
        suggestions: 'ehdotuksia',
        summaries: 'Yhteenvedot',
        supplements: 'Lisäravinteet',
        support: 'Tuki',
        sync: 'Synkronoi',
        system: 'Järjestelmä',
        systems: 'Järjestelmät',
        tables: 'Taulukot',
        tagging: 'Merkintä',
        'tamper-proof': 'Peukaloinnin kestävä',
        taxonomies: 'Taksonomiat',
        taxonomy: 'Taksonomia',
        tenants: 'Vuokralaiset',
        termination: 'Irtisanominen',
        'third-party': 'Kolmas osapuoli',
        'ticket-sync': 'Lippujen synkronointi',
        tickets: 'Liput',
        tiers: 'Tasot',
        tokens: 'Tokenit',
        totp: 'TOTP',
        traceability: 'Jäljitettävyys',
        tracking: 'Seuranta',
        trail: 'Polku',
        training: 'Koulutus',
        translation: 'Käännös',
        translations: 'Käännökset',
        transparency: 'Läpinäkyvyys',
        trends: 'Trendit',
        unified: 'Yhtenäinen',
        updates: 'Päivitykset',
        validation: 'Validointi',
        vault: 'Holvi',
        'vendor-requests': 'Myyjän pyynnöt',
        verification: 'Vahvistus',
        versioning: 'Versiointi',
        versions: 'Versiot',
        vietnam: 'Vietnam',
        violations: 'Rikkomukset',
        visualization: 'Visualisointi',
        waivers: 'Poikkeukset',
        'web-records': 'Web Records',
        website: 'Verkkosivusto',
        websites: 'Verkkosivustot',
        weights: 'Painot',
        'white-label': 'Valkoinen etiketti',
        withdrawal: 'Nosto',
    },
    module_labels: {
        // Sprint 62.57.0 Slice E — the audit module of the certificate register.
        service_certificates: 'Processing Service Certificate',
        // Sprint 63.25.0 (spec G2) — the source modules that had no label.
        compliance_rules: 'Compliance Rules',
        access_review: 'Access Reviews',
        consolidation: 'Consolidation',
        case_audit: 'Module Audit Trail',
        rights_requests: 'Tietopyynnöt',
        data_mapping: 'Tietokartoitus',
        governance: 'Hallinto',
        consent: 'Suostumushallinto',
        consent_properties: 'Suostumuksen ominaisuudet',
        assessments: 'Vaikutustenarvioinnit',
        classification: 'Tietojen luokitus',
        monitoring: 'Valvontaohjelmat',
        program: 'Valvontaohjelma',
        incidents: 'Tapausten hallinta',
        connectors: 'Liittimet',
        vendor_risk: 'Myyjän riski',
        deletion_orchestration: 'Poisto-orkestrointi',
        discovery: 'Tietojen löytäminen',
        incident_ops: 'Poikkeamatoiminnot',
        program_governance: 'Ohjelmahallinto',
        access_accountability: 'Pääsyvastuu',
        localization: 'Lokalisointi',
        sector_overlays: 'Sector Overlays',
        partner_workspace: 'Kumppanityötila',
        privacy_scanner: 'Tietosuojaskanneri',
        onboarding: 'Käyttöönotto',
        compliance_forms: 'Vaatimustenmukaisuuslomakkeet',
        audit_trail: 'Tarkastuspolku',
        data_subjects: 'Rekisteröidyt',
        organizations: 'Organisaatiot',
        task_management: 'Tehtävien hallinta',
        deletion_orch: 'Poisto-orkestrointi',
        forms: 'Vaatimustenmukaisuuslomakkeet',
        partner_marketplace: 'Kumppanimarkkinapaikka',
        dashboard: 'Kojelauta',
        platform_settings: 'Alustan asetukset',
        help_center: 'Ohjekeskus',
    },
    module_descriptions: {
        rights_requests: 'Hallinnoi rekisteröityjen oikeuspyyntöjä, mukaan lukien pääsy, poistaminen ja siirrettävyys. Seuraa pyyntöjen elinkaarta, osoita käsittelijät ja täytä lakisääteiset määräajat.',
        data_mapping: 'Kartoita tietovirrat organisaatiossasi. Seuraa käsittelytoimia, tietokategorioita, tarkoituksia, säilytyskäytäntöjä ja järjestelmäintegraatioita.',
        governance: 'Valvonta ja sitä tukeva näyttö. Hallinnoi toimittajarekisteriä ja sen elinkaarta, hyväksy päätökset, joita tämä moduuli valvoo, seuraa vaatimustenmukaisuuden poikkeamia ja ylläpidä luottamuksen ja identiteetin, sääntelyartefaktien, maksujen ja eKYC:n sekä alustan näytön rekistereitä.',
        consent: 'Hallinnoi suostumuksen keräämistä, tallentamista ja peruuttamista. Seuraa suostumustietueita, tarkoituksia ja integrointia suostumuksenhallinnan alustoihin.',
        consent_properties: 'Määritä suostumusominaisuudet, ryhmät ja kultaiset mallit. Luo uudelleenkäytettäviä suostumuskonfiguraatioita yhtenäiseen käyttöönottoon.',
        assessments: 'Suorita tietosuojan vaikutustenarviointeja (DPIA) ja yksityisyysriskien arviointeja. Arvioi käsittelytoimien vaatimustenmukaisuusriskejä.',
        classification: 'Luokittele tietovarat herkkyystason mukaan. Käytä taksonomiatunnisteita, seuraa elinkaaren tietueita ja tunnista luokittelupuutteet.',
        monitoring: 'Perusta valvontaohjelmia vaatimustenmukaisuusmittareiden, keskeisten riski-indikaattorien ja valvonnan tehokkuuden seuraamiseen ajan kuluessa.',
        incidents: 'Raportoi ja hallinnoi yksityisyystapauksia ja tietomurtoja. Seuraa tutkinta-, ilmoitus- ja korjausprosesseja.',
        connectors: 'Määritä tietolähdekonnektorit ja integraatiot. Yhdistä ulkoiset järjestelmät automaattiseen tietojen löytämiseen ja valvontaan.',
        vendor_risk: 'Arvioi ja valvo kolmannen osapuolen toimittajariskejä. Pisteytä toimittajat, seuraa riskien vähentämistä ja hallinnoi toimittajariskikonfiguraatioita.',
        deletion_orchestration: 'Orkestroi tietojen poisto järjestelmien välillä. Seuraa poistopyyntöjä, todenna todistusketjut ja varmista tietojen täydellinen poistaminen.',
        discovery: 'Löydä henkilötiedot yhdistetyissä järjestelmissä. Skannaa tietolähteet kattavan tietoinventaarion rakentamiseksi.',
        incident_ops: 'Operatiivinen tapaustenhallinta pelikirjoilla, eskalointityönkuluilla ja SLA-seurannalla tapausten ratkaisemiseksi.',
        program_governance: 'Seuraa tietosuojaohjelman yleistä kypsyyttä, vaatimustenmukaisuuspisteitä, todistusaukkoja ja johdon raportointia koko organisaatiossasi.',
        access_accountability: 'Tutki ja tarkasta käyttäjien pääsymalleja. Seuraa pääsykonfiguraatioita, valvo epäilyttävää toimintaa ja varmista vastuullisuus.',
        localization: 'Asenna ja hallinnoi markkinoiden lokalisointipaketteja. Lisää sääntelykehyksiä, kielipaketteja ja aluekohtaista vaatimustenmukaisuussisältöä.',
        sector_overlays: 'Install and manage optional sector overlays, and open the sector registers an activated overlay brings into use, organised by sector. Available in the Enterprise package.',
        partner_workspace: 'Tee yhteistyötä ulkoisten kumppanien kanssa. Jaa vaatimustenmukaisuustietoja, seuraa kumppanivelvoitteita ja hallinnoi kumppanisuhteita.',
        privacy_scanner: 'Skannaa verkkosivustoja yksityisyyden vaatimustenmukaisuusongelmien varalta. Valvo evästeiden käyttöä, seurantaskriptejä ja tietosuojakäytännön läsnäoloa reaaliaikaisesti.',
        onboarding: 'Opasta uusia organisaatioita alustan käyttöönotossa. Vaiheittainen käyttöönotto-opas alkumääritykseen ja tietojen tuontiin.',
        compliance_forms: 'Luo ja hallinnoi vaatimustenmukaisuuslomakkeita mallikatalogista. Täytä sääntelylomakkeet, seuraa lähetyksiä ja luo raportteja.',
        audit_trail: 'Näytä täydellinen tarkastuspolku kaikista järjestelmätoiminnoista. Seuraa kuka teki mitä, milloin ja missä kaikissa moduuleissa.',
        organizations: 'Hallinnoi organisaatioita ja monen vuokralaisen asetuksia. Määritä organisaatioprofiilit, hierarkiat ja organisaatioiden väliset suhteet.',
    },
    privacy_scanner: {
        anomaly_explain: {
            explain_label: 'Selitä epämuodostumat',
            explaining: 'Selitetään…',
            results_heading: 'Poikkeamien selitykset ({severity})',
            tokens_label: '≈ {count}-merkkejä',
            items_label: '{count} kohdetta',
            remediation_label: 'Korjausvaiheet',
            items_detail_label: 'Kohteet, joita asia koskee',
            accept_label: 'Hyväksy selitys',
            reject_label: 'Hylkää selitys',
            error_generic: 'Ei voinut selittää poikkeavuuksia. Yritä uudelleen tai tarkista, että skannaus on suoritettu onnistuneesti.',
        },
        cookie_insight: {
            insight_label: 'Eväste- ja seurantatieto AI',
            producing: 'Tuotetaan oivalluksia…',
            results_heading: 'Eväste- ja seurantatiedot ({severity})',
            tokens_label: '≈ {count}-merkkejä',
            items_label: '{count} kohdetta',
            classification_label: 'Luokittelu',
            severity_label: 'Vakavuus',
            banner_copy_label: 'Suositeltu bannerikopio',
            retention_label: 'Säilytysohjeet',
            citations_label: 'Lainaukset',
            kind_cookie: 'Eväste',
            kind_tracker: 'Tracker',
            class_necessary: 'Välttämätön',
            class_analytics: 'Analytics',
            class_marketing: 'Markkinointi',
            class_social: 'Sosiaalinen',
            accept_label: 'Hyväksy oivallus',
            reject_label: 'Hylkää oivallus',
            no_items: 'Tässä skannauksessa ei havaittu evästeitä tai seurantalaitteita.',
            error_generic: 'Evästetietoja ei voitu tuottaa. Yritä uudelleen tai tarkista, että skannaus on suoritettu onnistuneesti.',
        },
        monitoring: {
            title: 'Tietosuojavalvonta',
            new_scan: 'Uusi skannaus',
            scan_url: 'Skannattava URL',
            scan_frequency: 'Skannaustaajuus',
            last_scan: 'Viimeisin skannaus',
            score: 'Tietosuojapisteet',
            issues: 'Löydetyt ongelmat',
            history: 'Skannaushistoria',
            settings: 'Valvonta-asetukset',
        },
        real_time: {
            title: 'Reaaliaikaskanneri',
            scan_now: 'Skannaa nyt',
            scanning: 'Skannataan...',
            results: 'Skannauksen tulokset',
            no_results: 'Ei skannaustuloksia vielä.',
            cookies: 'Evästeet',
            trackers: 'Seurantakomentosarjat',
            consent_banners: 'Suostumusbannerit',
            third_party: 'Kolmannen osapuolen komentosarjat',
            archive: 'Tulosarkisto',
            settings: 'Skannerin asetukset',
        },
        dashboard: {
            page_title: 'Yksityisyyden valvonnan hallintapaneeli',
            privacy_monitoring: 'Yksityisyyden valvonta',
            plan_label: '{package} suunnitelma',
            expires_label: 'vanhenee {date}',
            settings: 'Asetukset',
            refresh: 'Päivitä',
            refreshing_dashboard: 'Virkistävä valvontakojelauta',
            refresh_aria: 'Päivitä valvontamittaristo',
            not_configured_title: 'Yksityisyyden valvontaa ei ole määritetty',
            not_configured_desc: 'Yhdistä AesirX Privacy Monitoring -palvelusi, jotta voit alkaa seurata verkkosivuston noudattamista.',
            configure_monitoring: 'Määritä valvonta',
            license_expired_title: 'Lisenssi vanhentunut',
            license_expired_desc: 'AesirX Privacy Monitoring -lisenssisi on vanhentunut. Uusi se palvelun keskeytymisen välttämiseksi.',
            compliance_score: 'Vaatimustenmukaisuuspisteet',
            cookies_detected: 'Evästeet havaittu',
            trackers_label: 'Jäljittäjät',
            open_issues: 'Avoimet ongelmat',
            score_good: 'Hyvä',
            score_needs_improvement: 'Tarvitsee parannusta',
            score_critical: 'Kriittinen',
            action_required: 'Toimenpide vaaditaan',
            cookies_sr: 'evästeitä havaittu',
            trackers_sr: 'jäljittäjä löytyy',
            open_issues_sr: 'avoimia kysymyksiä',
            action_required_sr: 'tarvitaan toimenpiteitä',
            metric_label: 'Metrinen',
            value_label: 'Arvo',
            status_label: 'Tila',
            aria_metrics: 'Yksityisyyden valvontamittarit',
            plan_limit_warning: 'Skannataan URL: {current}/{max} — olet saavuttanut suunnitelmasi rajan.',
            manage_domains: 'Hallinnoi verkkotunnuksia tai päivitä suunnitelmasi.',
            monitored_domains: 'Valvotut verkkotunnukset',
            domain_added: 'Verkkotunnus {domain} lisätty',
            domain_removed: 'Verkkotunnus {domain} poistettu',
            failed_add_domain: 'Verkkotunnuksen lisääminen epäonnistui',
            failed_remove_domain: 'Verkkotunnuksen poistaminen epäonnistui',
            failed_load: 'Valvonnan hallintapaneelin lataaminen epäonnistui',
            new_domain_aria: 'Uusi verkkotunnus URL',
            add_domain: 'Lisää verkkotunnus',
            adding_domain: 'Lisätään…',
            no_domains: 'Valvottuja verkkotunnuksia ei ole vielä määritetty.',
            aria_domains_table: 'Valvotut verkkotunnukset',
            col_domain: 'Verkkotunnus',
            col_compliance_score: 'Vaatimustenmukaisuuspisteet',
            col_status: 'Tila',
            col_cookies: 'Evästeet',
            col_trackers: 'Jäljittäjät',
            col_issues: 'Ongelmat',
            col_last_scan: 'Viimeinen skannaus',
            col_action: 'Toiminta',
            compliance_status_sr: 'vaatimustenmukaisuuden tila',
            showing_chart: 'Näytetään karttanäkymä',
            showing_table: 'Näytetään tietotaulukkonäkymä',
            loading_sr: 'Valvonnan kojelautaa ladataan',
            col_framework_tags: 'Framework Tagit',
            col_last_monitored: 'Viimeksi tarkkailtu',
            framework_tags_sr: 'sovellettavia sääntelypuitteita',
            last_monitored_sr: 'jatkuvan seurannan aikaleima',
            audit_trail_heading: 'Audit Trail',
            audit_trail_info: 'Jokainen valvontaskannaus on aikaleimattu ja kirjattu lokiin. Verkkotunnustason vaatimustenmukaisuusmuutokset tuottavat muuttumattoman tarkastustietueen, joka voidaan viedä viranomaistarkastuksia varten.',
            cybersecurity_law_info_heading: 'Kyberturvallisuuslain havaitseminen',
            cybersecurity_law_info: 'Valvotut verkkotunnukset tarkistetaan automaattisesti kyberturvallisuuslain vaatimusten (esim. Vietnamin kyberturvallisuuslain, NIS2 direktiivin) suhteen. Framework tagit osoittavat, mitkä määräykset ovat voimassa. Ongelmat luokitellaan kohdennetun korjaamisen sääntelykehyksen mukaan.',
        },
        scan: {
            page_title: 'Reaaliaikainen skannaus',
            title: 'Reaaliaikainen yksityisyysskanneri',
            settings: 'Asetukset',
            results_archive: 'Tulosarkisto',
            launch_scan: 'Käynnistä Scan',
            target_url: 'Kohde URL',
            protocol_aria: 'pöytäkirja',
            url_required: 'URL vaaditaan',
            url_whitespace: 'Poista välilyönnit kohteesta URL',
            url_invalid: 'Anna kelvollinen verkkotunnus (esim. esimerkki.fi)',
            add_subpages: 'Lisää alasivuja',
            add_subpages_aria: 'Lisää alasivuja, {count} lisätty',
            start_scan: 'Aloita skannaus',
            launching: 'Käynnistetään…',
            scan_launched: 'Skannaus käynnistyi onnistuneesti',
            scan_failed: 'Skannauksen käynnistäminen epäonnistui',
            scan_progress: 'Skannauksen edistyminen',
            scanning_in_progress: 'Skannaus käynnissä…',
            scan_id: 'Skannaustunnus',
            status_label: 'Tila',
            status_unknown: 'unknown',
            progress_label: 'Edistyminen',
            pages_label: 'Sivut',
            refresh_status: 'Päivitä tila',
            new_scan: 'Uusi skannaus',
            scan_completed: 'Skannaus valmis, tulokset saatavilla',
            failed_check_status: 'Skannauksen tilan tarkistaminen epäonnistui',
            scan_results: 'Skannaustulokset',
            main_page: 'Pääsivu',
            page_n: 'Sivu {n}',
            result_pages_nav: 'Tulossivut',
            page_url: 'Sivu URL',
            risk_score: 'Riskipisteet',
            risk_score_sr: 'riskipisteet',
            cookies_label: 'Evästeet',
            cookies_sr: 'evästeitä havaittu',
            scripts_label: 'Käsikirjoitukset',
            scripts_sr: 'skriptejä havaittu',
            issues_label: 'Ongelmat',
            issues_sr: 'ongelmia löydetty',
            issues_found: 'Ongelmia löydetty',
            aria_issues_table: 'Skannauksen aikana löytyi ongelmia',
            col_severity: 'Vakavuus',
            col_category: 'Luokka',
            col_title: 'Otsikko',
            col_remediation: 'Korjaus',
            severity_sr: 'severity',
            scan_result_metrics: 'Tarkista tulosmittarit',
            view_full_detail: 'Näytä täydelliset tiedot',
            col_consent_category: 'Suostumusluokka',
            consent_category_sr: 'suostumuksen luokittelu',
            compliance_evidence_heading: 'Todisteet vaatimustenmukaisuudesta',
            compliance_evidence_info: 'Skannaustulokset linkitetään vaatimustenmukaisuustapauksiin automaattisesti. Jokainen ongelma voidaan jäljittää suostumusluokkaan (olennainen, toiminnallinen, analytiikka, markkinointi), ja se tuottaa auditointivalmiita todisteita viranomaistarkastuksia varten.',
            rule_config_heading: 'Säännön määritys',
            rule_config_info: 'Skannaussäännöt ovat määritettävissä sääntelykehyksen mukaan. Evästeiden tarkistukset, komentosarjan tunnistus ja toimittaja-analyysi voidaan ottaa käyttöön tai poistaa käytöstä asetuksista. Mukautetut säännöt voidaan määrittää vastaamaan organisaatiosi vaatimustenmukaisuusvaatimuksia.',
            case_linkage_heading: 'Compliance Case Linkage',
            case_linkage_info: 'Reaaliaikaisten tarkistusten aikana löydetyt ongelmat voidaan linkittää vaatimustenmukaisuustapauksiin seurantaa ja korjaamista varten. Siirry Skannaustuloksen tiedot -sivulle nähdäksesi tapausyhteydet.',
            subpages_modal_title: 'Lisää alasivuja',
            subpages_modal_desc: 'Lisää enintään viisi alasivun URL-osoitetta skannattavaksi pääverkkotunnuksen rinnalla.',
            subpage_n: 'Alasivu {n}',
        },
        result_detail: {
            page_title: 'Skannaustuloksen tiedot',
            title: 'Skannauksen tulos',
            back_to_archive: 'Takaisin arkistoon',
            failed_load: 'Skannaustuloksen lataaminen epäonnistui',
            loading_sr: 'Ladataan skannaustulosta…',
            scan_id: 'Skannaustunnus',
            target_url: 'Kohde URL',
            status_label: 'Tila',
            status_sr: 'status',
            started: 'Aloitettu',
            completed: 'Valmis',
            pages_scanned: 'Sivut skannatut',
            https_label: 'HTTPS',
            highest_risk_score: 'Korkein riskipiste',
            most_cookies: 'Useimmat evästeet',
            most_vendors: 'Useimmat kolmannen osapuolen toimittajat',
            most_issues: 'Useimmat ongelmat',
            across_pages: 'sivujen yli',
            highest_values_aria: 'Korkeimmat arvot kaikilla sivuilla',
            scanned_pages_nav: 'Skannatut sivut',
            main_page: 'Pääsivu',
            page_n: 'Sivu {n}',
            page_url: 'Sivu URL',
            risk_score: 'Riskipisteet',
            overall_risk_sr: 'kokonaisriskipisteet',
            cookies_label: 'Evästeet',
            cookies_sr: 'evästeitä havaittu',
            vendors_label: 'Kolmannen osapuolen toimittajat',
            vendors_sr: 'kolmannen osapuolen toimittajat havaittu',
            issues_label: 'Ongelmat',
            issues_sr: 'ongelmia löydetty',
            risk_breakdown_aria: 'Riskipisteiden erittely',
            issues_found: 'Ongelmia löydetty',
            aria_issues_table: 'Skannauksen aikana löytyi ongelmia',
            col_severity: 'Vakavuus',
            col_category: 'Luokka',
            col_title: 'Otsikko',
            col_remediation: 'Korjaus',
            severity_sr: 'severity',
            cookies_heading: 'Evästeet ({count})',
            aria_cookies_table: 'Evästeet havaittu',
            col_name: 'Nimi',
            col_domain: 'Verkkotunnus',
            col_path: 'Polku',
            col_secure: 'Turvallinen',
            col_httponly: 'HttpOnly',
            col_samesite: 'Samasivusto',
            vendors_heading: 'Kolmannen osapuolen toimittajat ({count})',
            aria_vendors_table: 'Kolmannen osapuolen toimittajat havaittu',
            col_hostname: 'Isäntänimi',
            scripts_heading: 'Skriptit/majakat ({count})',
            aria_scripts_table: 'Skriptit ja majakat havaittu',
            col_url: 'URL',
            col_type: 'Kirjoita',
            links_heading: 'Linkit',
            first_party: 'Ensimmäinen osapuoli',
            third_party: 'Kolmannen osapuolen',
            col_risk_category: 'Riskiluokka',
            col_regulatory_framework: 'Sääntelykehys',
            col_actionable_detail: 'Toimiva yksityiskohta',
            risk_category_sr: 'sääntelyn riskiluokka',
            risk_category_mapping_heading: 'Riskikategorian kartoitus',
            risk_category_mapping_info: 'Jokainen skannauslöydös on kartoitettu lakisääteiseen riskiluokkaan (tiedonkeruu, rajat ylittävä siirto, suostumuksen hallinta, tietojen säilyttäminen, turvatarkastukset). Tämä kartoitus mahdollistaa kohdennetun kunnostuksen, joka priorisoidaan sääntelyvaikutusten mukaan.',
            compliance_case_heading: 'Compliance Case Association',
            compliance_case_info: 'Skannaustulokset linkitetään vaatimustenmukaisuustapauksiin päästä päähän -seurantaa varten. Ongelmat voidaan siirtää suoraan tarkistustuloksista vaatimustenmukaisuustapausten työnkulkuun.',
        },
        results_archive: {
            page_title: 'Skannaustulosarkisto',
            title: 'Skannaustulosarkisto',
            back_to_scanner: 'Takaisin skanneriin',
            failed_load: 'Skannaustulosten lataaminen epäonnistui',
            results_loaded: 'Skannaustulokset ladattu, {count} tietueita',
            total_label: 'Yhteensä: {count}',
            aria_table: 'Tarkista tulosarkisto',
            col_scan_date: 'Skannauspäivämäärä',
            col_target_url: 'Kohde URL',
            col_duration: 'Kesto',
            col_issues_found: 'Ongelmia löydetty',
            col_risk_score: 'Riskipisteet',
            col_status: 'Tila',
            col_actions: 'Toiminnot',
            scan_status_sr: 'skannauksen tila',
            view_result: 'Näytä',
            no_results: 'Skannaustuloksia ei löytynyt.',
            loading_sr: 'Ladataan skannaustuloksia',
            pagination_aria: 'Skannaa tulosten sivutus',
            col_risk_category: 'Riskiluokka',
            risk_category_sr: 'sääntelyn riskiluokka',
            archive_retention_heading: 'Arkiston säilyttäminen',
            archive_retention_info: 'Skannaustuloksia säilytetään organisaatiosi tietojen säilytyskäytännössä määritellyn ajan. Arkistoidut tulokset sisältävät koko skannauskontekstin (kehystunnisteet, riskiluokat, korjausohjeet), ja ne voidaan viedä muodossa CSV säännösten mukaiseksi todisteeksi.',
            export_context_info: 'Viedyt CSV-tiedostot sisältävät säädöskontekstin: riskiluokan, sovellettavat puitteet ja kunkin havainnon korjaustoimenpiteitä koskevat tiedot.',
        },
        scanner_settings: {
            page_title: 'Skannerin asetukset',
            title: 'Skannerin asetukset',
            back_to_scanner: 'Takaisin skanneriin',
            failed_load: 'Skannerin määrityksen lataaminen epäonnistui',
            endpoint_url: 'Päätepiste URL',
            save_success: 'Skannerin määritykset tallennettu onnistuneesti.',
            save_failed: 'Skannerin asetusten tallentaminen epäonnistui',
            test_connection: 'Testaa yhteys',
            testing: 'Testataan...',
            saving: 'Tallennetaan...',
            loading_sr: 'Ladataan skannerin asetuksia',
            endpoint_fixed_hint: 'The scanner address is fixed by the platform and cannot be changed here. Test Connection checks the AesirX credentials saved under Monitoring Settings.',
        },
        monitoring_settings: {
            page_title: 'Yksityisyyden valvonta-asetukset',
            title: 'Valvonta-asetukset',
            back_to_dashboard: 'Takaisin hallintapaneeliin',
            failed_load: 'Valvontamäärityksen lataaminen epäonnistui',
            credentials_heading: 'AesirX Valtuustiedot',
            connected_as: 'Yhdistetty nimellä {username}',
            credentials_desc: 'Anna AesirX-tilisi kirjautumistiedot yhdistääksesi Privacy Monitoringin. Löydät jäsentunnuksesi AesirX-tilisi profiilisivulta.',
            client_id: 'Asiakastunnus',
            client_secret: 'Asiakkaan salaisuus',
            username: 'AesirX Käyttäjätunnus (sähköposti)',
            password: 'AesirX Salasana',
            placeholder_keep: '(jätä tyhjäksi säilyttääksesi olemassa)',
            credential_form_aria: 'AesirX valtakirjalomake',
            test_connection: 'Testaa yhteys',
            testing: 'Testataan…',
            test_aria: 'Testaa AesirX-yhteyttä',
            testing_aria: 'Testaa yhteyttä',
            save_configuration: 'Tallenna asetukset',
            saving: 'Tallennetaan…',
            save_aria: 'Tallenna tunnistetiedot',
            saving_aria: 'Tallennetaan kirjautumistietoja',
            credentials_saved: 'AesirX tunnistetiedot tallennettu onnistuneesti.',
            credentials_saving: 'AesirX kirjautumistiedot tallennettu. Ladataan määritystä uudelleen…',
            credentials_save_failed: 'Tunnistetietojen tallentaminen epäonnistui',
            client_id_required: 'Asiakastunnus ja käyttäjätunnus vaaditaan.',
            all_fields_required: 'Kaikki tunnistetiedot ovat pakollisia.',
            email_heading: 'Sähköposti-ilmoitukset',
            receive_emails: 'Vastaanota skannausraporttisähköposteja',
            email_toggle_aria: 'Vaihda skannausraporttisähköpostit',
            only_on_change: 'Lähetä sähköpostia vain, kun riskitilanne muuttuu',
            only_on_change_aria: 'Vaihda sähköposti vain riskitilan muutoksen yhteydessä',
            emails_enabled: 'Tarkista raporttisähköpostit käytössä',
            emails_disabled: 'Tarkista raporttisähköpostit pois käytöstä',
            only_change_on: 'Sähköposti vain riskien muutoksista',
            only_change_off: 'Sähköposti jokaisella skannauksella',
            email_failed: 'Sähköpostiasetusten päivittäminen epäonnistui',
            email_settings_aria: 'Sähköposti-ilmoitusasetukset',
            loading_sr: 'Ladataan valvontaasetuksia',
        },
        entity_types: {
            user: 'Käyttäjä',
            case: 'Asia',
            incident: 'Tapahtuma',
            assessment: 'Arviointi',
            vendor: 'Myyjä',
            consent: 'Suostumus',
            policy: 'politiikka',
            data_map: 'Tietokartta',
        },
        actions: {
            create: 'Luo',
            update: 'Päivitä',
            delete: 'Poista',
            view: 'Näytä',
            export: 'Vie',
            status_change: 'Tilan muutos',
        },
    },
    forms: {
        // fix-sprint-20260905-2135 F33/F34 — keys that tKey() call sites asked for and
        // no locale declared, so each fell through to its hardcoded English third
        // argument and rendered English in all 16 locales. The two DPIA row-action
        // tooltips were worse: their fallbacks are themselves translated keys, so the
        // primary CTA read 'Edit' where the code intends 'Continue', and the in-review
        // action read 'Approve/Reject' where it intends 'Review'.
        title: 'Form',
        dashboard: {
            title: 'Lomakkeiden yhteenveto',
            total_templates: 'Mallipohjia yhteensä',
            installed: 'Asennettu',
            available: 'Saatavilla',
            by_market: 'Markkina-alueittain',
            back_dashboard: 'Takaisin to Kojelauta',
            browse_templates: 'Browse Mallit',
            catalog_coverage: 'Catalog Kattavuus',
            quick_start: 'Quick Aloita',
            recent_instances: 'Viimeaikainen Instances',
            total_instances: 'Instances yhteensä',
            fetch_failed: 'Epäonnistui: fetch kojelauta tiedot. Yritä uudelleen.',
            no_instances: 'Ei instances found. löytynyt',
            form_metrics: 'Lomakemittareiden yleiskatsaus',
            templates_total: 'mallia yhteensä',
            instances_total: 'instanssia yhteensä',
            installed_of_available: '{installed} / {available} asennettu',
            templates_in_category: 'mallia luokassa {category}',
            uncategorized: 'Uncategorized',
            other_framework: 'Other',
            category_templates_aria: '{category} templates',
            no_templates: 'No form templates available.',
        },
        templates: {
            title: 'Lomakemallipohjat',
            new: 'Uusi mallipohja',
            edit: 'Muokkaa mallipohjaa',
            name: 'Mallipohjan nimi',
            form_type: 'Lomaketyyppi',
            market: 'Markkina-alue',
            language: 'Kieli',
            schema: 'Lomakeskeema',
            preview: 'Esikatselu',
            duplicate: 'Monista mallipohja',
            grid_view: 'Ruudukko Näytä',
            list_view: 'Lista Näytä',
            fetch_failed: 'Epäonnistui: fetch mallit. Yritä uudelleen.',
            filter_templates: 'Suodata mallit',
            version_history: 'Versiohistoria',
            no_version_history: 'Ei versiohistoriaa — tämä on alkuperäinen versio.',
            form_id: 'Lomakkeen tunnus',
            source_instrument: 'Lähdeinstrumentti',
            issuer: 'Myöntäjä',
            all_frameworks: 'Kaikki kehykset',
            view_mode: 'Näkymätila',
            classification: 'Luokittelu',
        },
        catalog: {
            title: 'Lomakekatalogi',
            install: 'Asenna',
            uninstall: 'Poista asennus',
            open_form: 'Open form',
            install_all: 'Asenna kaikki',
            installed_tab: 'Asennetut',
            catalog_tab: 'Katalogi',
            search_placeholder: 'Hae mallipohjia...',
            no_templates: 'Mallipohjia ei löytynyt.',
            template_count: '{count} mallipohjaa',
            all_markets: 'Kaikki markkinat',
            sections: 'Osiot',
            all_categories: 'Kaikki kategoriat',
            category: 'Kategoria',
            category_install_failed: 'Epäonnistui: install kategoria mallit. Yritä uudelleen.',
            category_install_success: 'Kategoria mallit installed.',
            fetch_failed: 'Epäonnistui: fetch mallit. Yritä uudelleen.',
            fetch_installed_failed: 'Epäonnistui: fetch installed mallit. Yritä uudelleen.',
            fields: 'Kentät',
            install_failed: 'Epäonnistui: install malli. Yritä uudelleen.',
            install_success: 'Malli installed successfully.',
            installing: 'Asennetaan...',
            removing: 'Poistetaan...',
            uninstall_confirm: 'Haluatko varmasti uninstall this template?',
            uninstall_failed: 'Epäonnistui: uninstall malli. Yritä uudelleen.',
            uninstall_success: 'Malli uninstalled successfully.',
            installation_summary: 'Asennuksen yhteenveto',
            filter_catalog: 'Suodata luettelo',
            all_frameworks: 'Kaikki kehykset',
            templates_installed_count: '{count} malli(t) asennettu onnistuneesti',
            all_already_installed: 'Kaikki tämän luokan mallit on jo asennettu',
            category_install_partial: '{installed} of {requested} template(s) installed. {refused} restricted register(s) were not installed — adding those requires the regulatory-artifact custody permission.',
            category_install_none: 'No templates were installed. {refused} restricted register(s) in this category require the regulatory-artifact custody permission.',
            form_id: 'Lomakkeen tunnus',
            issuer: 'Myöntäjä',
            catalog_tabs: 'Lomakeluettelon välilehdet',
        },
        filler: {
            back_templates: 'Takaisin to Mallit',
            no_sections: 'Ei sections found. löytynyt',
            draft_created: 'Luonnos created.',
            draft_saved: 'Luonnos saved.',
            fetch_template_failed: 'Epäonnistui: fetch malli. Yritä uudelleen.',
            save_draft: 'Tallenna Luonnos',
            save_draft_failed: 'Epäonnistui: save draft. Yritä uudelleen.',
            saving: 'Tallennetaan...',
            select_option: 'Valitse an option',
            submit: 'Lähetä',
            submit_failed: 'Epäonnistui: submit. Yritä uudelleen.',
            submitting: 'Lähetetään...',
            title: 'Lomakeopas',
            form_id_label: 'Lomakkeen tunnus',
            schema_label: 'Skeema',
            form_number_label: 'Form',
            framework_label: 'Kehys',
            issuer_label: 'Myöntäjä',
            source_label: 'Lähde',
            market_label: 'Markkina',
            section_of: 'Osio {current}/{total}',
            form_navigation: 'Lomakenavigaatio',
            form_progress: 'Lomakkeen eteneminen: Osio {current}/{total}',
            removed_item: 'Kohde {index} poistettu kentästä {field}',
            added_item: 'Uusi kohde lisätty kenttään {field}',
            go_to_previous: 'Siirry edelliseen osioon, osio {current}/{total}',
            go_to_next: 'Siirry seuraavaan osioon, osio {current}/{total}',
            risk_matrix_placeholder: '1–5',
        },
        filing: {
            title: 'Hakemuksen tila',
            advance: 'Päivitä hakemuksen tila...',
            not_started: 'Ei aloitettu',
            drafting: 'Luonnostelu',
            internal_review: 'Sisäinen tarkistus',
            approved_for_submission: 'Hyväksytty jätettäväksi',
            submitted_to_authority: 'Jätetty viranomaiselle',
            supplement_requested: 'Täydennys pyydetty',
            supplement_in_progress: 'Täydennys käynnissä',
            resubmitted: 'Jätetty uudelleen',
            accepted: 'Hyväksytty',
            rejected: 'Hylätty',
            closed: 'Suljettu',
            // Sprint 63.37.0 (Stream J) — the filing milestones the instance page lists.
            milestone_submitted_at: 'Submitted',
            milestone_supplement_requested_at: 'Supplement requested',
            milestone_supplement_submitted_at: 'Supplement submitted',
            milestone_accepted_at: 'Accepted',
            milestone_rejected_at: 'Rejected',
            milestone_closed_at: 'Closed',
        },
        instances: {
            approved_by: 'Hyväksynyt',
            form_answers: 'Lomake Answers',
            column_header: 'Form',
            no_answers: 'Ei answers found. löytynyt',
            view_outputs: 'Näytä tulosteet',
            approve: 'Hyväksy',
            approve_failed: 'Epäonnistui: approve. Yritä uudelleen.',
            approving: 'Hyväksytään...',
            detail_title: 'Instance Yksityiskohta',
            fetch_failed: 'Epäonnistui: fetch instances. Yritä uudelleen.',
            generate_failed: 'Epäonnistui: generate output. Yritä uudelleen.',
            generate_output: 'Luo Tuloste',
            generating: 'Luodaan...',
            submitted_at: 'Lähetetty',
            instance_metadata: 'Lomakeinstanssin metatiedot',
            filing_status_steps: 'Arkistointitilan vaiheet',
            current_status: 'nykyinen tila',
            filing_status_update_failed: 'Hakemuksen tilan päivittäminen epäonnistui',
            // Sprint 63.37.0 (I1) — the export buttons and the sample notice of an instance.
            form_number: 'Form number',
            export_pdf: 'Export PDF',
            export_docx: 'Export Word (DOCX)',
            exporting: 'Exporting {format}…',
            export_done: '{format} exported. The download has started.',
            export_failed: 'The {format} could not be exported.',
            export_not_approved: 'This form cannot be exported yet. Approve it first; a form inside a record is exported once it is completed or finalized.',
            sample_notice: 'This form is sample data. Every page of its exports carries the stamp "{stamp}".',
            // Sprint 63.37.0 (Stream J) — the finalize action and the form actions a parent record's row offers.
            finalize: 'Finalize',
            finalized: 'Form finalized.',
            finalize_failed: 'The form could not be finalized.',
            edit_form: 'Edit',
            view_form: 'View',
            open_instance: 'Open {form}',
            // Sprint 63.40.0 (decision 8) — the page description and the notification dossier.
            detail_description: 'The answers of one form, its exports and, where the form opens one, the notification dossier prepared from it.',
            notice_dossier: {
                title: 'Notification dossier',
                description: 'The platform prepares this dossier from the completed form. It does not file it: your organisation files it with the receiving authority.',
                prepare: 'Prepare the dossier for {form}',
                preparing: 'Preparing the dossier for {form}…',
                prepared: 'The dossier for {form} is prepared.',
                prepare_failed: 'The dossier could not be prepared.',
                prepare_after_final: 'Complete and approve the form to prepare its dossier.',
                load_failed: 'The dossier could not be loaded.',
                reload: 'Load the dossier again',
                loading: 'Loading the dossier…',
                reference: 'Dossier reference',
                receiving_authority: 'Receiving authority',
                receiving_authority_hint: 'As the installed form states it.',
                addressee_label: 'Name of the receiving body',
                addressee_hint: 'The installed form names a class of body, not one body. Enter the body you address this dossier to. Where the completed form already names it on its own addressee line, the dossier starts with that name. A name you enter or clear here is kept and does not change the form. You can leave it empty until it is decided.',
                addressee_recorded: 'Recorded name',
                not_recorded: 'Not recorded',
                statements_title: 'Your statements',
                statements_description: 'Only you, as the applicant, can make these statements. The platform records your answer and does not decide it.',
                statement_basis: 'Basis: {basis}',
                stated_yes: 'This applies',
                stated_no: 'This does not apply',
                stated_unanswered: 'Not answered',
                authority_step_title: "The receiving body's step",
                authority_step_note: "This step is the receiving body's. No deadline is set against your organisation.",
                save: 'Save the dossier',
                saving: 'Saving the dossier…',
                saved: 'The dossier is saved.',
                save_failed: 'The dossier could not be saved.',
                export_title: 'Export the dossier',
                export_hint: "The submission slip prints the receiving body's name as saved on the dossier. Until a name is saved, its addressee line is left blank.",
                export_open: 'Preview the submission slip',
            },
        },
        outputs: {
            back_instance: 'Takaisin to Instance',
            no_outputs_msg: 'Tulosteita ei ole vielä luotu.',
            no_outputs_title: 'No Tulosteet',
            output_name: 'Tuloste Nimi',
            download: 'Lataa',
            download_failed: 'Epäonnistui: download. Yritä uudelleen.',
            fetch_failed: 'Epäonnistui: fetch outputs. Yritä uudelleen.',
            format: 'Muoto',
            generated_at: 'Luotu',
            generated_by: 'Luonut',
            size: 'Koko',
            title: 'Lomake Tulosteet',
            print: 'Tulosta',
            print_outputs: 'Tulosta lomakkeen tulosteet',
            printing: 'Tulostetaan lomakkeen tulosteet',
            pdf_preview: 'PDF-esikatselu',
            downloading: 'Ladataan {name}',
            preview_in_new_window: 'Esikatsele {name} ({format}) uudessa ikkunassa',
            // Sprint 62.45.0 AC9 — column headers of the form print export (PDF table).
            print_col_field: 'Field',
            print_col_value: 'Value',
            // Sprint 62.45.0 AC9 — metadata lines of the form print export.
            print_meta_instance: 'Instance ID: {id}',
            print_meta_status: 'Status: {status}',
            print_meta_generated: 'Generated: {timestamp}',
            // Sprint 63.37.0 (H2) — the header lines of a printed official form.
            official_header_form_number: 'Form number',
            official_header_instrument: 'Source instrument',
            official_header_issuer: 'Issued by',
            official_header_authority: 'Authority',
            // Sprint 62.58.0 (AC-A4) — the draft-instrument notice an output prints before its title.
            draft_notice_frame: 'Prepared under a draft instrument that is not in force. Nothing in this document is an obligation.',
            draft_notice_framework: '{name} — pack version {version}',
            draft_notice_regenerate: 'Generated before the draft notice was added — generate it again.',
            // Sprint 62.58.0 gate E12.5 pass 2 (P3-5) — an output whose printed notices no longer match the frameworks' state.
            draft_notice_status_changed: "The framework's status has changed since this was generated — generate it again.",
        },
        // Sprint 62.58.0 (AC-A3) — what an instance surface of a draft instrument's template adds.
        draft_notice: {
            instance_note: 'Filling in this form does not make anything in it an obligation.',
        },
        cybersec_domains: {
            scope: 'Laajuus ja soveltuvuus',
            risk: 'Riskinarviointi ja kontrollikartoitus',
            policy: 'Organisaation turvallisuuspolitiikat',
            localization: 'Datan lokalisointi ja paikallinen läsnäolo',
            vendor: 'Kolmannet osapuolet ja toimitusketju',
            authority: 'Viranomaispyyntöjen käsittely',
            incident: 'Poikkeamien hallinta ja todisteet',
            training: 'Koulutus ja valmius',
        },
        market_finalization_domains: {
            data_law: 'Tietolaki — Hallinto ja Operatiiviset Kontrollit',
            pdpl_2025: 'PDPL 2025 — Yksityisyyden Hallinta ja Rekisteröityjen Oikeudet',
            decree_356: 'Asetus 356/2025 — Toimittaja- ja Rajat Ylittävät Kontrollit',
            telecom: 'Viestintälaki — Luottamuksellisuus ja Viranomaisvaatimukset',
        },
        nav: {
            filing_case_links: 'Arkistointitapauslinkit',
            section_routing: 'Osion reititys',
            submission_packages: 'Lähetyspaketit',
        },
        filing_case_links: {
            title: 'Arkistointitapauslinkit',
            link_form: 'Linkitä lomake arkistointitapaukseen',
            filing_case_type: 'Arkistointitapaustyyppi',
            form_instance: 'Lomakeinstanssi',
            select_form_instance: 'Valitse lomakeinstanssi...',
            select_filing_case: 'Valitse arkistointitapaus...',
            no_links_found: 'Arkistointitapauslinkkejä ei löytynyt.',
            linked: 'Linkitetty',
            filing_case_id: 'Arkistointitapauksen tunnus',
            link_created: 'Arkistointitapauksen linkki luotu',
            link_deleted: 'Arkistointitapauslinkki poistettu',
            aria_delete_link: 'Poista linkki {name}',
            case_type_dpia_dossier: 'DPIA-asiakirjakansio',
            case_type_cross_border_filing: 'Rajat ylittävä arkistointi',
            case_type_breach_notification: 'Tietoturvaloukkausilmoitus',
            no_instances_guidance: 'Ei vielä lomakeinstansseja. Luo yksi Lomakemalleista.',
            no_filings_guidance: 'Ei vielä arkistointitapauksia. Luo yksi Arvioinneista.',
        },
        section_routing: {
            title: 'Osion reititys',
            assign_section: 'Määritä osio',
            department: 'Osasto',
            select_department: 'Valitse osasto...',
            assignee: 'Vastuuhenkilö',
            select_assignee: 'Valitse vastuuhenkilö...',
            section: 'Osio',
            select_section: 'Valitse osio...',
            select_form_instance: 'Valitse lomakeinstanssi...',
            no_assignments_found: 'Osion määrityksiä ei löytynyt.',
            select_instance_prompt: 'Valitse lomakeinstanssi nähdäksesi osion kohdistukset.',
            complete_section: 'Viimeistele osio',
            status_pending: 'Odottaa',
            status_in_progress: 'Käynnissä',
            status_completed: 'Valmis',
            status_reviewed: 'Tarkistettu',
            form_instance_id: 'Lomakeinstanssin tunnus',
            assignment_created: 'Osiokohdistus luotu',
            assignment_deleted: 'Osiokohdistus poistettu',
            status_updated: 'Osion tila päivitetty',
            aria_delete_assignment: 'Poista kohdistus {name}',
            deadline: 'Määräaika',
            deadline_help: 'Valinnainen. Tämän päivämäärän jälkeiset kohdistukset näytetään myöhässä olevina.',
            overdue: 'Myöhässä',
            days_remaining: 'päivää jäljellä',
            approval_requested: 'Lähetetty tarkistettavaksi',
            form_approved: 'Lomake hyväksytty',
            form_rejected: 'Lomake hylätty',
            no_departments_guidance: 'Osastoja ei ole määritetty. Määritä ne Organisaation asetuksissa.',
            no_sections_guidance: 'Mallissa ei ole osioita. Varmista, että lomakemallissa on osioita.',
            select_instance_first: 'Valitse ensin lomakeinstanssi ladataksesi käytettävissä olevat osiot.',
        },
        submission_packages: {
            supporting_documents: 'Supporting Documents',
            supporting_documents_help: 'Files filed with this package. Each is stored against the package and listed on its record.',
            no_supporting_documents: 'No supporting documents attached yet.',
            attach_supporting_document: 'Attach a supporting document',
            supporting_document_attached: 'Supporting document attached',
            title: 'Lähetyspaketit',
            assemble_package: 'Kokoa lähetyspaketti',
            dossier: 'Asiakirjakansio',
            select_dossier: 'Valitse asiakirjakansio...',
            package_label: 'Paketin nimi',
            cover_letter: 'Saatekirje',
            checklist: 'Tarkistuslista',
            evidence_index: 'Todistusaineistoluettelo',
            completeness: 'Kattavuus',
            incomplete_warning: 'Kokoaminen ei onnistu: lomakkeessa on keskeneräisiä osioita.',
            no_packages_found: 'Lähetyspaketteja ei löytynyt.',
            version: 'Versio',
            package_assembled: 'Lähetyspaketti koottu',
            package_deleted: 'Lähetyspaketti poistettu',
            aria_delete_package: 'Poista paketti {name}',
            status_draft: 'Luonnos',
            status_complete: 'Valmis',
            status_submitted: 'Lähetetty',
            status_accepted: 'Hyväksytty',
            status_rejected: 'Hylätty',
            package_updated: 'Lähetyspaketti päivitetty',
            document_generated: 'Asiakirja luotu',
            documents: 'Asiakirjat',
            generate: 'Luo',
            preview: 'Esikatselu',
            download: 'Lataa',
            download_all: 'Lataa kaikki',
            no_dossiers_guidance: 'Ei vielä asiakirja-aineistoja. Luo yksi Arvioinneista.',
            package_plan_help: 'Sisällytä pakettiin (automaattinen asiakirjojen luonti käytettävissä):',
            cover_letter_help: 'Virallinen saatekirje vastaanottavalle viranomaiselle',
            checklist_help: 'Vaatimustenmukaisuuden tarkistuslista, joka varmistaa kaikkien lähetysvaatimusten täyttymisen',
            evidence_index_help: 'Lähetyksessä viitattujen todistusasiakirjojen hakemisto',
            completeness_skipped: 'Kattavuustarkistus ohitettu — tähän asiakirja-aineistoon ei ole linkitetty lomakeinstanssia.',
            completeness_unavailable: 'Kattavuustarkistus ei käytettävissä — jatketaan paketin kokoamista.',
            duplicate_package_error: 'Tälle asiakirja-aineistolle on jo olemassa lähetyspaketti.',
            doc_cover_letter: 'Saatekirje',
            doc_checklist: 'Tarkistuslista',
            doc_evidence_index: 'Todistusaineiston hakemisto',
            export_csv: 'Vie CSV',
            export_success: 'Arkistointitapausten linkit viety onnistuneesti',
            no_assignments_guidance: 'Ei vielä osiotoimeksiantoja. Määritä osiot osastoille ja tiimin jäsenille aloittaaksesi reitityksen.',
            completeness_hint: 'Completeness is the share of the six dossier fields a package cannot be filed without that the dossier holds: dossier type, framework, authority, submission method, applicant organisation and applicant contact.',
            completeness_not_measured: 'Not measured: assembled before completeness was computed',
            missing_fields_label: 'Missing from the dossier',
            dossier_field_dossier_type: 'Dossier type',
            dossier_field_regulatory_framework_code: 'Framework',
            dossier_field_authority: 'Authority',
            dossier_field_submission_method: 'Submission method',
            dossier_field_applicant_org_name: 'Applicant organisation',
            dossier_field_applicant_contact: 'Applicant contact',
        },
        // Sprint 63.37.0 (D4) — the one official-standing badge (`OfficialFormBadge`).
        official_standing: {
            official: 'Official form',
            internal: 'Internal form',
        },
        template_picker: {
            title: 'Valitse malli',
            hint: 'Valitse sääntelylomakepohja tai aloita tyhjällä lomakkeella.',
            market_filter: 'Suodata markkinan mukaan',
            framework_filter: 'Suodata kehyksen mukaan',
            all_markets: 'Kaikki markkinat',
            all_frameworks: 'Kaikki kehykset',
            search_placeholder: 'Hae malleja...',
            blank_form: 'Tyhjä / yleinen lomake',
            blank_form_desc: 'Luo ilman mallia — täytä kentät manuaalisesti.',
            no_templates: 'Malleja ei löytynyt tällä suodattimella.',
            templates_unreadable: 'Not every template for this form could be loaded, so the list below may be empty or incomplete. That does not mean there are none.',
            templates_read_subject: 'the form templates',
            filters_unreadable: 'The market and framework filters are not shown: their choices could not be loaded.',
            select: 'Valitse',
            link_error: 'Mallin linkitys epäonnistui — voit linkittää mallin myöhemmin Muokkausnäkymästä.',
            internal_badge: 'Sisäinen malli',
            legacy_badge: 'Vanhentunut',
            draft_badge: 'Luonnos',
            upcoming_badge: 'Tuleva',
            hidden_badge: 'Piilotettu',
            show_hidden_toggle: 'Näytä piilotetut / vanhentuneet mallit',
            show_hidden_toggle_hint: 'Järjestelmänvalvojan näkymä: sisältää internal_only, hidden, legacy tai draft merkityt mallit.',
        },
        cover_letter_modal: {
            title: 'Saatekirje: esikatselu ja muokkaus',
            iframe_title: 'Saatekirjeen esikatselu',
            stage_label: 'Vaihe',
            stage_initial: 'Ensimmäinen toimitus',
            stage_supplement: 'Täydennysvastaus',
            stage_resubmission: 'Uudelleentoimitus',
            subject_label: 'Aihe',
            body_label: 'Runkokappale',
            notes_label: 'Lisämuistiinpanot',
            refresh_preview: 'Päivitä esikatselu',
            confirm: 'Vahvista ja tallenna',
            regenerate: 'Luo uudelleen',
            snapshot_saved: 'Saatekirje tallennettu',
        },
        related_panel: {
            title: 'Liittyvät sääntelylomakkeet',
            open_form: 'Täytä lomake',
            no_forms: 'Liittyviä lomakkeita ei löytynyt.',
            hint_vn: 'VN-kohtaiset sääntelylomakkeet saatavilla — valitse lomake avataksesi vaatimustenmukaisuusavustajan.',
            internal_badge: 'Sisäinen',
            official_badge: 'Virallinen',
            loading: 'Ladataan liittyviä lomakkeita...',
            error: 'Liittyvien lomakkeiden lataaminen epäonnistui.',
            form_id: 'Lomakkeen tunnus',
            form_name: 'Lomakkeen nimi',
            framework: 'Sääntelykehys',
        },
        view_form: 'Näytä lomake',
        edit_form: 'Muokkaa lomaketta',
        save_draft: 'Tallenna luonnos',
        module_settings: 'Moduuliasetukset',
        validation_errors: 'Korjaa validointivirheet',
        custom_name_help: 'Valinnainen — oletuksena mallin nimi, jos jätetään tyhjäksi',
        select_placeholder: 'Valitse...',
        // Gate E12.5 pass 3 (QA nit) — the stored-text fallback of a date-and-time field.
        datetime_stored_text_hint: 'Recorded before this field took a date and time; kept as written.',
        previous_section: 'Edellinen',
        next_section: 'Seuraava',
        loading: 'Ladataan...',
        array_add: 'Lisää',
        array_remove: 'Poista',
        array_item_label: '{label} kohde {index}',
        array_add_item: 'Lisää kohde kohteeseen {label}',
        array_remove_item: 'Poista kohde {index} kohteesta {label}',
        legacy_banner: 'Tämä tietue luotiin ennen yhtenäisiä lomakkeita. Jotkin tiedot voivat erota entiteetin ja lomakeinstanssin välillä.',
        field_required: '{label} on pakollinen',
        section_announcement: 'Osio {current}/{total}: {title}',
        sections_nav_label: 'Lomakeosat',
        no_sections: 'Malliosioita ei ole saatavilla.',
        section_label_tpl: 'Osio {index}: {title}',
        field_shown_announcement: '{field} näytetään nyt.',
        field_required_announcement: '{field} on nyt pakollinen.',
        submit_blocked_hint_one: '1 pakollinen kenttä vaatii vielä vastauksen.',
        submit_blocked_hint_many: '{count} pakollista kenttää vaatii vielä vastauksen.',
        module_settings_auto_applied: 'Moduulikohtaiset asetukset otetaan käyttöön automaattisesti.',
        operation_failed: 'Toiminto epäonnistui',
        failed_to_load_form: 'Lomakkeen lataaminen epäonnistui',
        failed_to_save_form: 'Lomakkeen tallentaminen epäonnistui',
        failed_to_submit_form: 'Lomakkeen lähettäminen epäonnistui',
        failed_to_load_template: 'Mallin lataaminen epäonnistui',
        failed_to_save_draft: 'Luonnoksen tallentaminen epäonnistui',
        failed_to_create: 'Luominen epäonnistui',
        no_content_generated: 'No content generated',
    },
    pack_catalog: {
        title: 'Pakettikatalogi',
        install: 'Asenna paketti',
        uninstall: 'Poista paketin asennus',
        install_batch: 'Asenna pakettiryhmä',
        installed_tab: 'Asennetut',
        catalog_tab: 'Katalogi',
        pack_details: 'Paketin tiedot',
        frameworks: 'Sääntelykehykset',
        requirements: 'Vaatimukset',
        artifacts: 'Artefaktit',
        language_pack: 'Kielipaketti',
        dependencies: 'Riippuvuudet',
        no_packs: 'Paketteja ei löytynyt.',
    },
    platform_pages: {
        users: {
            title: 'Käyttäjät',
            new: 'Uusi käyttäjä',
            invite: 'Kutsu käyttäjä',
            edit: 'Muokkaa käyttäjää',
            name: 'Nimi',
            email: 'Sähköposti',
            role: 'Rooli',
            status: 'Tila',
            identity_type: 'Identiteettityyppi',
            initial_password: 'Initial password',
            require_password_change_action: 'Require password change',
            require_password_change_confirm: '{name} will reach only the change-password step, from their next request, until they choose a new password. Continue?',
            require_password_change_done: 'The user must now change their password.',
            require_password_change_failed: 'The password change could not be required.',
            password_change_pending: 'Password change required',
            initial_password_note: 'Share it through a secure channel. While a password change is required, the user can do nothing but choose a new password.',
            last_login: 'Viimeisin kirjautuminen',
            failed_login_attempts: 'Failed Login Attempts',
            never_signed_in: 'Never',
            deactivate: 'Poista käytöstä',
            reactivate: 'Palauta käyttöön',
            unlock: 'Avaa lukitus',
            reset_password: 'Nollaa salasana',
            remove: 'Poista',
            remove_confirm: 'Poistetaanko "{name}"? Tämä toiminto on pysyvä ja käyttäjä menettää kaiken käyttöoikeuden. Tietue säilytetään tarkastustarkoituksia varten.',
            user_details_loaded: 'User details loaded',
            password_required: 'Password is required',
            password_min_length: 'Password must be at least 12 characters',
            password_uppercase: 'Password must contain at least one uppercase letter',
            password_lowercase: 'Password must contain at least one lowercase letter',
            password_digit: 'Password must contain at least one digit',
            password_special: 'Password must contain at least one special character',
            deactivate_failed: 'Failed to deactivate user',
            reactivate_failed: 'Failed to reactivate user',
            remove_failed: 'Failed to remove user',
            // fix-sprint-20261017-1500 F4 — UserDetailPage's error toasts read these, not English literals.
            update_failed: 'Failed to update user',
            unlock_failed: 'Failed to unlock user',
            assign_role_failed: 'Failed to assign role',
            revoke_role_failed: 'Failed to revoke role',
            create_failed: 'Failed to create user',
            count_one: '{total} user',
            count_other: '{total} users',
            announce_loaded: 'Users loaded',
        },
        roles: {
            title: 'Roolit',
            new: 'Uusi rooli',
            edit: 'Muokkaa roolia',
            name: 'Roolin nimi',
            description: 'Kuvaus',
            permissions: 'Käyttöoikeudet',
            users_count: 'Käyttäjät',
            detail: 'Roolin tiedot',
            all_tiers: 'Kaikki tasot',
            tier: 'Taso',
            type_system: 'Järjestelmä',
            type_custom: 'Mukautettu',
            role_name_placeholder: 'esim. alueellinen vaatimustenmukaisuusjohtaja',
            permissions_heading: 'Permissions',
            permissions_selected: '({count} selected)',
            select_all_offered: 'Select all',
            clear_all: 'Clear all',
            grant_bounds_hint: 'You can grant only permissions you hold yourself. Permissions that no system role holds by design, such as AML case access, can be granted only by someone who holds the role-assignment permission in their own right.',
            permission_not_held_summary: 'You do not hold these permissions, so you cannot grant them:',
            permission_not_held_item: 'You do not hold this permission',
            system_role_fixed: 'A system role’s permissions are fixed. Create a custom role to grant other permissions.',
            permission_required: 'At least one permission is required.',
            system_role_no_delete: 'System roles cannot be deleted.',
            create_failed: 'Failed to create role',
            delete_failed: 'Failed to delete role',
            update_failed: 'Failed to update role',
            delete_blocked_active_users: 'This role is still assigned to {count} user(s). Move them to another role, then delete this one.',
            delete_blocked_sso_config: 'Single sign-on still uses this role ({names}), as the default role for new users or in its group mapping. Change that setting first, then delete this role.',
            announce_loaded: 'Roles loaded',
            announce_detail_loaded: 'Role details loaded',
        },
        profile: {
            title: 'Oma profiili',
            name: 'Nimi',
            display_name: 'Näyttönimi',
            email: 'Sähköposti',
            phone: 'Puhelin',
            department: 'Osasto',
            job_title: 'Tehtävänimike',
            language: 'Kieli',
            role: 'Rooli',
            persona: 'Tunnistettu persoona',
            last_login: 'Viimeisin kirjautuminen',
            save: 'Tallenna muutokset',
            save_success: 'Profiili päivitetty onnistuneesti',
            module_tips_section: 'Moduulivinkit',
            tips_toggle: 'Näytä sääntelyvinkit moduuleissa',
            dismissed_tips: 'Ohitetut vinkit',
            reset_dismissed: 'Nollaa kaikki ohitetut vinkit',
            notification_prefs: 'Ilmoitusasetukset',
            notification_prefs_link: 'Hallitse ilmoitusasetuksia →',
            change_password: 'Vaihda salasana',
            current_password: 'Nykyinen salasana',
            new_password: 'Uusi salasana',
            confirm_password: 'Vahvista salasana',
            accessibility: 'Esteettomyys',
            high_contrast: 'Suuren kontrastin tila',
            high_contrast_desc: 'Lisaa kontrastia luettavuuden parantamiseksi. Tayttaa WCAG 2.1 AAA -vaatimukset.',
            passwords_no_match: 'Salasanat eivät täsmää',
            password_changed_logout: 'Salasana vaihdettu. Sinut kirjataan ulos.',
            failed_update: 'Profiilin päivitys epäonnistui',
            failed_change_password: 'Salasanan vaihto epäonnistui',
            none: 'Ei mitään',
            never: 'Ei koskaan',
            password_requirements: 'Vähintään 12 merkkiä, iso kirjain, pieni kirjain, numero, erikoismerkki',
            announce_updated: 'Profile updated',
            announce_loaded: 'Profile loaded',
        },
        security: {
            mfa_title: 'MFA-asetukset',
            mfa_enable: 'Ota MFA käyttöön',
            mfa_disable: 'Poista MFA käytöstä',
            mfa_setup: 'Skannaa tämä QR-koodi todennussovelluksellasi',
            sso_title: 'SSO-asetukset',
            sso_provider: 'Palveluntarjoaja',
            sso_client_id: 'Asiakastunnus',
            sso_redirect: 'Uudelleenohjauksen URI',
            sso_idp_section: 'Identity Provider',
            sso_idp_entity_id: 'Identity provider entity ID',
            sso_idp_sso_url: 'Identity provider sign-in URL',
            sso_idp_binding: 'Request binding',
            sso_binding_redirect: 'HTTP Redirect',
            sso_binding_post: 'HTTP POST',
            sso_allow_idp_initiated: 'Accept sign-ins started at the identity provider',
            sso_jit_provisioning: 'Create a user on first sign-in',
            sso_default_role: 'Role for users created on first sign-in',
            sso_default_role_none: 'No default — refuse the sign-in',
            sso_assertions_signed: 'Signed assertions are always required.',
            sso_acs_hint: 'Give your identity provider this assertion consumer URL:',
            // ── Sprint 63.6.1 — OpenID Connect ────────────────────────────
            sso_oidc_issuer: 'Issuer',
            sso_oidc_issuer_hint: 'The issuer identifier your provider publishes, exactly as it appears in its discovery document. The platform reads that document from this address and refuses a configuration whose issuer does not match.',
            sso_oidc_redirect_uri: 'Redirect URI to register',
            sso_oidc_redirect_uri_hint: 'Register this exact address with your provider. The platform decides it from the configuration, so it cannot be edited here.',
            sso_oidc_redirect_uri_pending: 'Available once the configuration is saved',
            sso_oidc_client_auth: 'Client authentication',
            sso_oidc_client_auth_basic: 'Client secret, in the Authorization header',
            sso_oidc_client_auth_post: 'Client secret, in the request body',
            sso_oidc_scopes: 'Scopes',
            sso_oidc_scopes_hint: 'Separate with spaces. Leave empty for openid, profile and email. The openid scope is always requested: without it the provider returns no identity token.',
            sso_oidc_scopes_placeholder: 'openid profile email',
            sso_oidc_identity_claim_note: 'Identity comes from the email claim, and the address must be one your provider reports as verified. No other claim is read, and no claim grants a permission.',
            sso_certificates: 'Signing certificates',
            sso_certificates_empty: 'No signing certificate. A sign-in cannot be verified until one is added.',
            sso_certificate_pem: 'Certificate (PEM)',
            sso_certificate_add: 'Add certificate',
            sso_certificate_expires: 'Expires',
            sso_certificate_remove: 'Remove certificate',
            sso_certificate_limit: 'Two certificates are held at most, so a rotation needs no downtime.',
            sso_verify: 'Verify configuration',
            sso_verify_hint: 'Runs a full sign-in against this configuration and reports every check. It creates no session and no user, so you stay signed in.',
            sso_verify_report: 'Verification report',
            sso_verify_passed: 'Every check passed.',
            sso_verify_failed: 'Some checks failed.',
            sso_check_passed: 'Passed',
            sso_check_failed: 'Failed',
            sso_enforcement: 'SSO-only sign-in',
            sso_enforcement_off: 'Off — everyone can sign in with a password.',
            sso_enforcement_on: 'On — only exempt administrators can sign in with a password.',
            sso_enforcement_activate: 'Turn on SSO-only sign-in',
            sso_enforcement_deactivate: 'Turn off SSO-only sign-in',
            sso_enforcement_exempt: 'Administrators who keep password sign-in',
            sso_enforcement_before: 'Before you can turn this on:',
            sso_enforcement_need_verify: 'Verify this configuration and complete a sign-in through it, within the last 15 minutes.',
            sso_enforcement_need_exempt: 'Name at least one active administrator who has a password.',
            sso_enforcement_recovery: 'If the identity provider stops working, an exempt administrator can sign in with a password and turn this off; deactivating the configuration also restores password sign-in for everyone.',
            sso_mfa_heading: "Accept this provider's MFA",
            sso_mfa_intro: "By default the platform asks for its own MFA after every sign-in through this provider. Turn this on only if you trust this provider's MFA to stand in for the platform's.",
            sso_mfa_accept: "Accept this provider's MFA instead of the platform's",
            sso_mfa_state_off: "Off. The platform's own MFA applies to every sign-in through this provider.",
            sso_mfa_state_on: "On. A sign-in counts as MFA-verified only when the values the provider reports match the ones you list below. Every other sign-in is held at the platform's own MFA.",
            sso_mfa_no_meaning: "The platform attaches no meaning to any value. Only values you enter here, matched exactly and with the same capitalisation, count. A value you have not listed, a sign-in older than the maximum age and a missing value all fall back to the platform's own MFA.",
            sso_mfa_amr: 'Accepted authentication method values (amr)',
            sso_mfa_amr_hint: 'The exact amr values your provider reports for an MFA sign-in, separated by spaces or commas. OpenID Connect only.',
            sso_mfa_acr_oidc: 'Accepted authentication context values (acr)',
            sso_mfa_acr_saml: 'Accepted authentication context values (AuthnContextClassRef)',
            sso_mfa_acr_hint: 'The exact values your provider reports for an MFA sign-in, separated by spaces or commas.',
            sso_mfa_lists_rule: 'List at least one value, or nothing will count. When you list more than one kind of value, a sign-in must match every kind.',
            sso_mfa_empty_warning: 'Nothing is listed, so no sign-in through this provider will count as MFA-verified.',
            sso_mfa_max_age: 'Maximum authentication age (seconds)',
            sso_mfa_max_age_hint: 'A sign-in at the provider older than this does not count. Leave empty for no age check.',
            sso_mfa_max_age_invalid: 'Enter a whole number of seconds greater than zero, or leave the field empty.',
            sso_revoke_sessions: "End this provider's sessions",
            sso_revoke_confirm_title: "End this provider's sessions?",
            sso_revoke_confirm_message: 'Everyone currently signed in through {name} is signed out and must sign in again. Sessions that arrived by password or through another provider are not ended. Federated sessions created before sessions recorded which configuration they came from cannot be attributed to one provider, so they are ended as well.',
            sso_revoke_confirm_button: 'End sessions',
            sso_revoke_working: 'Ending sessions…',
            sso_revoke_result_heading: 'Sessions ended',
            sso_revoke_result_for: 'Provider: {name}',
            sso_revoke_count_total: 'Sessions ended',
            sso_revoke_count_legacy: 'Of these, sessions that could not be attributed to a configuration',
            sso_revoke_legacy_note: "These sessions pre-date the field that records which configuration a session came from, so they are ended whichever provider's sessions you end.",
            sso_revoke_announce: 'Sessions ended: {count}. Of these, {legacy} could not be attributed to a configuration because they pre-date the field that records it.',
            sso_revoke_failed: "Failed to end this provider's sessions.",
            sso_mfa_accepted_badge: 'Provider MFA accepted',
            api_keys_title: 'API-avaimet',
            api_keys_new: 'Uusi API-avain',
            api_keys_name: 'Avaimen nimi',
            api_keys_last_used: 'Viimeksi käytetty',
            api_keys_expires: 'Vanhenee',
            api_keys_revoke: 'Peruuta',
            service_accounts_title: 'Palvelutilit',
            ip_allowlist_title: 'IP-sallittujen lista',
            ip_add: 'Lisää IP-osoite',
            ip_address: 'IP-osoite',
            ip_description: 'Kuvaus',
            delegations_title: 'Delegoinnit',
            delegations_new: 'Uusi delegointi',
            delegations_from: 'Käyttäjältä',
            delegations_to: 'Käyttäjälle',
            delegations_expires: 'Vanhenee',
            break_glass_title: 'Hätäpääsy',
            break_glass_requester_only: 'You can request break-glass access here and follow your own requests below. Other people\u2019s requests, active sessions and history are shown to approvers.',
            break_glass_my_requests: 'Your requests',
            break_glass_request: 'Pyydä pääsy',
            break_glass_reason: 'Pääsyn syy',
            break_glass_duration: 'Kesto',
            session_settings_title: 'Istuntoasetukset',
            eviction_policy_section: 'Häätöpolitiikka',
            session_timeout: 'Istunnon aikakatkaisu (minuuttia)',
            session_max_concurrent: 'Samanaikaisten istuntojen enimmäismäärä',
            session_inactivity_warning: 'Inactivity Warning (minutes)',
            session_timeout_hint: 'Sessions will expire after this duration of total time. Valid range: 5-480 minutes.',
            session_max_concurrent_hint: 'Maximum number of active sessions per user. Valid range: 1-20.',
            session_inactivity_warning_hint: 'Show a warning after this many minutes of inactivity. Must be less than the session timeout.',
            session_timeout_range_error: 'Session timeout must be between 5 and 480 minutes.',
            session_max_concurrent_range_error: 'Max concurrent sessions must be between 1 and 20.',
            session_inactivity_warning_range_error: 'Inactivity warning must be between 1 minute and less than the session timeout.',
            session_settings_load_error: 'Failed to load session settings.',
            session_settings_save_error: 'Failed to save session settings.',
            eviction_policy_hint: 'What happens when a user exceeds the max concurrent sessions limit.',
            eviction_policy_reject: 'Reject New Login - Prevent new login until an existing session is terminated',
            eviction_policy_evict_oldest: 'Evict Oldest Session - Automatically terminate the oldest session to allow new login',
            active_sessions_title: 'Aktiiviset istunnot',
            active_sessions_device: 'Laite',
            active_sessions_ip: 'IP-osoite',
            active_sessions_started: 'Aloitettu',
            active_sessions_revoke: 'Peruuta istunto',
            active_sessions_confirm_terminate: 'Haluatko varmasti lopettaa tämän istunnon?',
            active_sessions_confirm_terminate_current: 'Tämä on nykyinen istuntosi. Sen lopettaminen kirjaa sinut ulos välittömästi. Jatkaa?',
            active_sessions_confirm_terminate_all: 'Haluatko varmasti lopettaa kaikki muut istunnot? Vain nykyinen istuntosi pysyy aktiivisena.',
            active_sessions_terminated: 'Istunto lopetettu onnistuneesti.',
            active_sessions_all_terminated: 'Kaikki muut istunnot on lopetettu.',
            active_sessions_terminate_all: 'Lopeta kaikki muut istunnot',
            active_sessions_last_activity: 'Viimeinen toiminta',
            active_sessions_current: 'Nykyinen istunto',
            active_sessions_revoke_selected: 'Peruuta valitut',
            active_sessions_confirm_terminate_selected: 'Haluatko varmasti lopettaa valitut istunnot?',
            active_sessions_selected_terminated: 'Valitut istunnot lopetettu.',
            ip_allowlist_desc: 'Hallinnoi sallittuja IP-osoitteita API:lle.',
            ip_no_entries: 'IP-osoitteita ei ole määritetty.',
            session_settings_desc: 'Määritä istunnon aikakatkaisu ja samanaikaisten istuntojen rajoitukset.',
            session_settings_save_policy: 'Tallenna istuntokäytäntö',
            session_settings_saved: 'Istuntoasetusten päivitys onnistui.',
            session_settings_idle_timeout: 'Idle Timeout (minuutit)',
            session_settings_absolute_timeout: 'Absoluuttinen aikakatkaisu (minuutteja)',
            session_settings_help_idle: 'Istunto vanhenee tämän epäaktiivisuuden jälkeen.',
            session_settings_help_absolute: 'Istunto vanhenee tämän kokonaiskeston jälkeen aktiviteetista riippumatta.',
            session_settings_help_max: 'Samanaikaisten istuntojen enimmäismäärä käyttäjää kohti.',
            sso_note: 'SSO-määrityksen muutokset voivat edellyttää käyttäjien todentamista uudelleen.',
            service_accounts_warning: 'Tämä API-näppäin näytetään vain kerran. Kopioi se nyt ja säilytä se turvallisesti.',
            service_accounts_new: 'Uusi palvelutili',
            service_accounts_name: 'Tilin nimi',
            service_accounts_role: 'Rooli',
            service_accounts_created: 'Luotu',
            service_accounts_desc: 'Hallinnoi koneiden välisiä palvelutilejä API-integraatioille.',
            break_glass_warning: 'Rikkoutuvien lasien käyttö auditoidaan ja kirjataan. Käytä vain aidoissa hätätilanteissa.',
            break_glass_min_chars: '{count} / {min} vähintään merkkiä',
            break_glass_permission_count: '{count} lupa(t) valittu',
            break_glass_submitted: 'Lasin rikkomista koskeva pyyntö lähetetty. Odottaa hyväksyntää.',
            break_glass_approved: 'Lasin rikkoutumispyyntö hyväksytty.',
            break_glass_denied: 'Lasin rikkomista koskeva pyyntö evätty.',
            break_glass_confirm_activate: 'Approve this break-glass request? The approval is recorded with the permissions requested and the time it expires. It does not itself change what the requester can do.',
            break_glass_confirm_title: 'Vahvista aktivointi',
            break_glass_pending: 'Odottavat pyynnöt',
            break_glass_active_sessions: 'Approved Break-Glass Requests',
            break_glass_time_remaining: 'Aikaa jäljellä',
            break_glass_expired: 'Vanhentunut',
            break_glass_approved_denied_by: 'Hyväksytty/hylätty',
            delegations_desc: 'Hallinnoi väliaikaisia käyttöoikeuksien delegointeja käyttäjien välillä.',
            delegations_my: 'Minun valtuuskuntani',
            delegations_to_me: 'Delegoitu minulle',
            delegations_scope_hint: 'An “all my permissions” delegation carries what you hold today. It does not change if your role changes later.',
            delegations_window_hint: 'The delegate holds these permissions from 00:00 on the start day until 23:59 on the end day, in your time zone, or until you revoke the delegation, checked on every request. Each use is recorded in the security audit trail.',
            delegations_start_in_past: 'The start cannot be before today.',
            delegations_end_before_start: 'The end must be after the start.',
            delegations_legacy_all: 'Grants nothing: made before delegations recorded what they carry. Revoke it and create a new one.',
            delegations_not_delegable_hint: 'Permissions held only through a named custom role or by no organisation, and single sign-on and group-user administration, cannot be delegated and are not listed.',
            delegations_reason: 'Reason',
            delegations_start: 'Start',
            delegations_end: 'End',
            delegations_scope_all: 'Kaikki käyttöoikeudet',
            delegations_scope_specific: 'Erityiset käyttöoikeudet',
            delegations_created: 'Delegaatio luotiin onnistuneesti.',
            delegations_revoked: 'Valtuutus peruutettu.',
            delegations_confirm_revoke: 'Haluatko varmasti peruuttaa tämän valtuutuksen?',
            delegations_confirm_revoke_title: 'Vahvista peruutus',
            delegations_permission_count: '{count} lupa(t)',
            delegations_no_longer_delegable: 'No longer delegable, so not granted: {permissions}',
            api_keys_status: 'Tila',
            api_keys_prefix: 'Avaimen etuliite',
            api_keys_permissions_count: 'Käyttöoikeudet',
            api_keys_ip_count: 'IP-rajoitukset',
            api_keys_created: 'Luotu',
            api_keys_confirm_revoke_title: 'Vahvista peruutus',
            api_keys_confirm_revoke_message: 'Haluatko varmasti peruuttaa API-avaimen "{name}"? Tätä toimintoa ei voi kumota.',
            api_keys_confirm_rotate_title: 'Vahvista kierto',
            api_keys_confirm_rotate_message: 'Rotate the API key "{name}"? A new key is issued now; the current key keeps working for 24 hours so you can replace it, then stops. If the key may be compromised, revoke it instead.',
            api_keys_created_success: 'API-avain luotiin onnistuneesti.',
            api_keys_revoked_success: 'API avain "{name}" on peruutettu.',
            api_keys_rotated_success: 'API-näppäintä "{name}" on käännetty. Kopioi uusi avain alla.',
            api_keys_load_error: 'API-avainten lataaminen epäonnistui.',
            api_keys_create_error: 'Avaimen API luominen epäonnistui.',
            api_keys_revoke_error: 'API-avaimen peruuttaminen epäonnistui.',
            api_keys_rotate_error: 'API-näppäimen kiertäminen epäonnistui.',
            api_keys_allowed_origins: 'Allowed origins',
            api_keys_allowed_origins_hint: 'One origin per line: scheme, host and optional port, e.g. https://app.example.com. When set, a request must carry an Origin header that matches one entry exactly, or it is refused. Leave empty for a server-to-server key.',
            api_keys_rate_limit: 'Requests per minute',
            api_keys_rate_limit_hint: 'Leave empty for no limit. Over the limit, a request is refused with 429 and a Retry-After header until the next minute starts.',
            api_keys_rate_limit_invalid: 'Requests per minute must be a whole number from 1 to 10,000.',
            api_keys_status_at_creation: 'Status at creation',
            api_keys_status_option_active: 'Active: the key works as soon as it is created',
            api_keys_status_option_inactive: 'Inactive: the key authenticates nothing until you activate it',
            api_keys_col_origins: 'Origins',
            api_keys_col_rate_limit: 'Limit per minute',
            api_keys_no_limit: 'No limit',
            api_keys_edit_title: 'Edit API key',
            api_keys_save: 'Save changes',
            api_keys_updated_success: 'API key "{name}" has been updated.',
            api_keys_update_error: 'Failed to update API key.',
            api_keys_edit_action: 'Edit key {name}',
            api_keys_activate_action: 'Activate key {name}',
            api_keys_pause_action: 'Pause key {name}',
            api_keys_rotate_action: 'Rotate key {name}',
            api_keys_revoke_action: 'Revoke key {name}',
            api_keys_copy_raw_key: 'Copy API key to clipboard',
            api_keys_activated_success: 'API key "{name}" is active.',
            api_keys_paused_success: 'API key "{name}" is paused. It authenticates nothing until it is activated.',
            api_keys_status_error: 'Failed to change the API key status.',
            api_keys_status_rotated: 'Rotated',
            api_keys_rotated_grace_until: 'Still works until {time}. Revoke it to stop it now.',
            service_accounts_load_error: 'Failed to load service accounts.',
            service_accounts_created_success: 'Service account created successfully.',
            service_accounts_create_error: 'Failed to create service account.',
            service_accounts_confirm_deactivate_title: 'Confirm Deactivation',
            service_accounts_confirm_deactivate_message: 'Are you sure you want to deactivate the service account "{name}"? All associated API keys will stop working.',
            service_accounts_deactivated_success: 'Service account "{name}" has been deactivated.',
            service_accounts_deactivate_error: 'Failed to deactivate service account.',
            service_accounts_deactivate_action: 'Deactivate service account {name}',
            service_accounts_table_label: 'Service accounts',
            service_accounts_name_placeholder: 'e.g., Data Sync Bot',
            service_accounts_purpose_placeholder: 'e.g., Automated data synchronization',
            permission_picker_selected_count: '{count} selected',
            permission_picker_result_count: '{count} of {total} permissions shown',
            permission_picker_selected_heading: 'Selected permissions',
            permission_picker_remove: 'Remove {permission}',
            break_glass_duration_15: '15 minuuttia',
            break_glass_duration_30: '30 minuuttia',
            break_glass_duration_60: '60 minuuttia (1h)',
            break_glass_duration_120: '120 minuuttia (2h)',
            break_glass_duration_240: '240 minuuttia (4h)',
            sod_rules_title: 'Tehtävien erottelusäännöt',
            sod_violations_report: 'Rikkomusraportti',
            sod_create_rule: 'Luo sääntö',
            sod_col_name: 'Nimi',
            sod_col_description: 'Kuvaus',
            sod_col_conflict_count: 'Konfliktien määrä',
            sod_col_severity: 'Vakavuus',
            sod_col_status: 'Tila',
            sod_col_actions: 'Toiminnot',
            sod_no_rules: 'SoD-sääntöjä ei löytynyt',
            sod_severity_warning: 'Varoitus',
            sod_severity_blocking: 'Estäminen',
            sod_active: 'Aktiivinen',
            sod_inactive: 'Ei-aktiivinen',
            sod_edit: 'Muokkaa',
            sod_exceptions: 'Poikkeukset',
            sod_deactivate: 'Poista käytöstä',
            sod_confirm_deactivate_title: 'Vahvista deaktivointi',
            sod_confirm_deactivate_message: 'Poistetaanko sääntö {name}? Se ei enää merkitse rikkomuksia.',
            sod_deactivate_error: 'Säännön poistaminen käytöstä epäonnistui',
            sod_rule_count: '{count} rules',
            active_sessions_loaded: 'Active sessions loaded',
            api_keys_loaded: 'API keys loaded',
            backup_codes_regenerated: 'Backup codes regenerated successfully.',
            break_glass_loaded: 'Break glass data loaded',
            delegations_loaded: 'Delegations loaded',
            ip_added: 'IP address added.',
            ip_allowlist_loaded: 'IP allowlist loaded',
            ip_removed: 'IP address removed.',
            mfa_disabled: 'MFA has been disabled.',
            mfa_status_loaded: 'MFA status loaded',
            service_accounts_loaded: 'Service accounts loaded',
            session_settings_loaded: 'Session settings loaded',
            // ── Sprint 63.7.0: organisation-level MFA enforcement + IP allowlist enforcement ──
            mfa_org_enforcement_heading: 'Organisation enforcement',
            // fix-sprint-20261017-1500 F6 — a failed policy read is stated, with a retry.
            mfa_org_policy_read_failed: "The organisation's MFA policy could not be read, so its current level is not shown.",
            mfa_org_policy_retry_subject: "the organisation's MFA policy",
            mfa_org_enforcement_intro: 'Choose whether this organisation requires a second factor. The default is Optional, which is what the platform has always done: people who set up an authenticator are asked for a code, and people who do not are not. Raising the level never locks anyone out — a person who is required to have a second factor and does not yet have one is asked to set one up at their next sign-in, and can complete that themselves.',
            mfa_level_optional: 'Optional',
            mfa_level_optional_desc: 'Each person decides. Anyone who has set up an authenticator is asked for a code; anyone who has not signs in with their password alone.',
            mfa_level_admin_required: 'Required for administrators',
            mfa_level_admin_required_desc: "Anyone whose role can change this organisation's security settings must have a second factor. This is decided by what the role can do, not by what the role is called, so your own custom roles are covered too.",
            mfa_level_all_required: 'Required for everyone',
            mfa_level_all_required_desc: 'Everyone in this organisation must have a second factor.',
            mfa_enforcement_current: 'Current',
            mfa_enforcement_apply: 'Use this level',
            mfa_unenrolled_count: '{count} people would be asked to set up an authenticator at their next sign-in.',
            mfa_enforcement_confirm_title: 'Change the enforcement level?',
            mfa_enforcement_confirm_message: 'This sets the level to {level}. {count} people will be asked to set up an authenticator the next time they sign in. They can complete it themselves and nobody is locked out.',
            mfa_enforcement_saved: 'Enforcement level updated.',
            mfa_enforcement_save_failed: 'The enforcement level could not be changed.',
            mfa_enforcement_no_lockout: 'Raising the level cannot lock this organisation out. Someone who is required to have a second factor and does not yet have one still signs in; they are taken to a setup screen and can finish it without help.',
            mfa_admin_heading: 'Two-factor authentication',
            mfa_admin_enrolled: 'Set up',
            mfa_admin_not_enrolled: 'Not set up',
            mfa_admin_backup_codes_remaining: '{count} unused backup codes',
            mfa_admin_reason_label: 'Reason',
            mfa_admin_reason_hint: 'At least 20 characters. This is recorded in the audit trail and is the only lasting record of why.',
            mfa_admin_reset: 'Reset',
            mfa_admin_disable: 'Turn off',
            mfa_admin_revoke_codes: 'Revoke backup codes',
            mfa_admin_action_done: 'Done. This person has been notified.',
            mfa_admin_action_failed: 'That could not be completed.',
            // ── fix-sprint-20260921-2110: EN placeholders pending Phase 25 (R-57) ──
            mfa_page_intro: 'Set up and manage the second verification step for your own account. If your organisation requires one, the level it has chosen is shown below.',
            mfa_not_enrolled_intro: 'Protect your account with a second verification step.',
            mfa_scan_instruction: 'Scan the QR code with your authenticator app, then enter the 6-digit code it shows to confirm.',
            mfa_qr_alt: 'QR code holding the setup key for your authenticator app. If you cannot scan it, the same key is written out in the field below.',
            mfa_secret_manual_label: 'Or enter this setup key manually',
            mfa_confirm_code_label: 'Enter the 6-digit code from your authenticator app',
            mfa_enrolled_description: 'This account is protected by a second verification step.',
            mfa_enabled_since: 'In use since {date}.',
            mfa_status_load_failed: 'Your two-factor status could not be loaded.',
            mfa_enroll_failed: 'Setting up a second factor could not be started.',
            mfa_invalid_code: 'That code was not accepted. Check your authenticator app and enter the code it shows now.',
            mfa_regenerate_failed: 'New backup codes could not be generated.',
            mfa_disable_confirm_title: 'Turn off two-factor authentication?',
            mfa_disable_confirm_message: 'Your account will be protected by its password alone, which makes it easier for someone else to sign in as you. You can set up a second factor again at any time.',
            mfa_disable_failed: 'Two-factor authentication could not be turned off.',
            mfa_backup_codes_heading: 'Your backup codes',
            mfa_backup_codes_save_now: 'These codes are shown once and cannot be shown again. Save them somewhere safe now — each one signs you in if you lose your authenticator, and each works only once.',
            mfa_backup_codes_file_heading: 'AesirX ComplianceOne — MFA backup codes',
            mfa_backup_codes_file_notice: 'Store these codes in a safe place. Each code can only be used once.',
            mfa_backup_codes_file_generated: 'Generated: {date}',
            mfa_enforcement_apply_named: 'Use this level: {level}',
            mfa_admin_no_read_notice: 'You can see whether this person has a second factor and you can take it away. You cannot see what it is, and you cannot set one up on their behalf.',
            ip_enforcement_heading: 'Enforcement',
            ip_enforcement_dry_run: 'This list is not refusing anything. It is being checked against every signed-in request and the results are recorded, so you can see who would be refused before anyone is.',
            ip_enforcement_active: 'Enforcing since {date}.',
            ip_enforcement_would_block: '{count} recorded occasions in the last 30 days where a signed-in request came from an address this list does not cover.',
            ip_enforcement_would_block_zero: 'No signed-in request from an address outside this list has been recorded in the last 30 days. That can also mean nobody has signed in, so it is a reason to look rather than an all-clear.',
            ip_enforcement_confirm_button: 'Start enforcing',
            ip_enforcement_withdraw_button: 'Stop enforcing',
            ip_enforcement_confirm_title: 'Start refusing requests from outside this list?',
            ip_enforcement_confirm_message: 'From now on, a signed-in request from an address this list does not cover is refused.',
            ip_enforcement_confirmed: 'This list is now being enforced.',
            ip_enforcement_withdrawn: 'This list is no longer being enforced.',
            ip_enforcement_sessions_note: 'Turning this on does not sign anybody out. Each request is judged as it arrives, so a session from an address that is no longer covered simply stops working. To end sessions now, use Active Sessions.',
            ip_enforcement_edit_withdraws: 'Editing the ranges stops enforcement until you confirm again, because the recorded figures you confirmed against were about the old list.',
            ip_configure: 'Configure',
            ip_configure_title: 'Configure IP Allowlist',
            ip_configure_enabled_hint: 'When enabled, only requests from listed IP addresses will be allowed.',
            ip_enforcement_mode_label: 'Enforcement Mode',
            ip_enforcement_mode_all_access: 'All Access — restrict both UI and API',
            ip_enforcement_mode_api_keys_only: 'API Keys Only — only restrict API key access',
            ip_address_placeholder: '203.0.113.1 or 192.168.1.0/24',
            ip_description_placeholder: 'e.g., Office network',
            ip_entries_table_label: 'IP allowlist entries',
            ip_remove_action: 'Remove {cidr} from allowlist',
            ip_remove_confirm_title: 'Confirm Remove',
            ip_remove_confirm_message: 'Are you sure you want to remove {cidr} from the allowlist?',
            ip_load_failed: 'Failed to load IP allowlist settings.',
            ip_cidr_invalid: 'Invalid format. Enter a single IP (203.0.113.1) or CIDR range (192.168.1.0/24)',
            ip_cidr_duplicate: 'This CIDR is already in the allowlist.',
            ip_add_failed: 'Failed to add IP address.',
            ip_remove_failed: 'Failed to remove IP address.',
            ip_save_failed: 'Failed to save settings.',
            ip_enforcement_confirm_caller_covered: 'You are connecting from {ip}, and this list covers it, so you will not lose access to this screen.',
            ip_enforcement_confirm_caller_not_covered: 'You are connecting from {ip}, which this list does not cover. Confirming is refused rather than locking you out, so add this address to the list first.',
            ip_enforcement_confirm_caller_unknown: 'Whether this list covers the address you are connecting from could not be established on this screen. It is checked again when you confirm, and a confirmation that would lock you out is refused.',
            ip_enforcement_readiness_unreadable: 'The 30-day record of what this list would have refused could not be read, so this screen cannot show you the figure.',
            sso_metadata_url: 'Metadata / discovery URL',
            sso_metadata_url_hint: 'The OpenID Connect discovery or metadata document published by the identity provider. This is not the redirect URI: the redirect URI to register with the provider is shown above, is derived by the platform, and cannot be typed here.',
            sso_client_secret: 'Client Secret',
            sso_issuer_placeholder: 'https://login.example.com',
            sso_sp_entity_id_placeholder: 'https://grc.example.com/saml/metadata',
            sso_configs_table_label: 'SSO configurations',
            sso_configs_unreadable: 'The SSO configurations could not be read, so this screen cannot say whether this organisation has any.',
            sso_configs_retry: 'Reload',
            sso_configs_retry_busy: 'Reloading…',
            sso_configs_retry_subject: 'the SSO configurations',
            sso_load_failed: 'Failed to load SSO configurations.',
            sso_report_read_failed: 'Failed to read the verification report.',
            sso_save_failed: 'Failed to save SSO configuration.',
            sso_delete_failed: 'Failed to delete SSO configuration.',
            sso_certificate_add_failed: 'Failed to add the certificate.',
            sso_verify_start_failed: 'Failed to start the verification run.',
            sso_enforcement_on_failed: 'Failed to turn on SSO-only sign-in.',
            sso_enforcement_off_failed: 'Failed to turn off SSO-only sign-in.',
            sso_updated: 'SSO configuration updated successfully.',
            sso_created: 'SSO configuration created successfully.',
            sso_delete_confirm_title: 'Confirm Delete',
            sso_delete_confirm_message: 'Are you sure you want to delete the SSO configuration "{name}"?',
            sso_deleted: 'SSO configuration "{name}" deleted.',
            sso_delete_end_sessions: "Also end this provider's sessions",
            sso_delete_end_sessions_hint: 'Deleting a configuration stops new sign-ins through it but does not end the sessions it has already started. Leave this ticked to end them now. If you untick it, you can end them later under "Deleted configurations".',
            sso_delete_sessions_not_ended: 'The configuration was deleted, but its sessions could not be ended. End them under "Deleted configurations".',
            sso_deleted_heading: 'Deleted configurations',
            sso_deleted_intro: 'These configurations no longer accept sign-ins. Deleting a configuration does not end the sessions it started; end them here with "End this provider\'s sessions".',
            sso_deleted_table_label: 'Deleted SSO configurations',
            sso_deleted_unreadable: 'The deleted SSO configurations could not be read.',
            sso_deleted_retry_subject: 'the deleted SSO configurations',
            sso_configs_loaded: 'SSO configurations loaded',
            active_sessions_select_aria: 'Select session from {ip}',
            active_sessions_terminate_aria: 'Terminate session from {ip}',
            active_sessions_terminate_current_aria: 'Terminate session from {ip} (current session)',
            active_sessions_unknown_ip: 'unknown IP',
            active_sessions_select_all_aria: 'Select all sessions',
        },
        access_reviews: {
            title: 'Pääsyn tarkistukset',
            campaigns: 'Tarkistuskampanjat',
            new_campaign: 'Uusi kampanja',
            campaign_name: 'Kampanjan nimi',
            scope: 'Laajuus',
            reviewers: 'Tarkastajat',
            due_date: 'Määräaika',
            progress: 'Edistyminen',
            my_reviews: 'Omat tarkistukset',
            approve: 'Hyväksy',
            revoke: 'Peruuta',
            analytics: 'Roolianalytiikka',
            analytics_title: 'Roolianalytiikka',
            permission_usage: 'Käyttöoikeuksien käyttö',
            role_coverage: 'Roolien kattavuus',
            campaign_detail: 'Kampanjan tiedot',
            campaign_description: 'Kuvaus',
            campaign_status: 'Tila',
            campaign_deadline: 'Määräaika',
            campaign_scope_type: 'Laajuustyyppi',
            campaign_completion: 'Valmistuminen',
            campaign_total_items: 'Tuotteita yhteensä',
            campaign_pending_items: 'Odottaa',
            campaign_confirmed_items: 'Vahvistettu',
            campaign_revoked_items: 'Peruutettu',
            campaign_escalated_items: 'Eskaloitunut',
            campaign_launch: 'Käynnistä kampanja',
            campaign_cancel: 'Peruuta kampanja',
            campaign_confirm_launch: 'Haluatko varmasti käynnistää tämän kampanjan?',
            campaign_confirm_cancel: 'Haluatko varmasti peruuttaa tämän kampanjan?',
            campaign_launched: 'Kampanja käynnistetty onnistuneesti.',
            campaign_cancelled: 'Kampanja peruttu.',
            review_items: 'Tarkista kohteet',
            review_item_user: 'Käyttäjä',
            review_item_role: 'Rooli',
            review_item_reviewer: 'Arvostelija',
            review_item_decision: 'Päätös',
            review_item_justify: 'Perustelut',
            review_item_confirm: 'Vahvista',
            review_item_revoke: 'Peruuta',
            review_item_escalate: 'Eskaloida',
            all_decisions: 'Kaikki päätökset',
            bulk_action: 'Joukkotoiminta',
            bulk_confirm: 'Joukkovahvista',
            bulk_revoke: 'Joukko peruutus',
            selected_count: '{count} valittu',
            justify_required: 'Perustelut vaaditaan',
            scope_all_users: 'Kaikki käyttäjät',
            scope_role_owner: 'Roolin omistaja',
            scope_department: 'osasto',
            back_to_campaigns: 'Takaisin Kampanjoihin',
            recommendations: 'Suositukset',
            refreshing: 'Virkistävää...',
            decide: 'Päätä',
            decision_by: 'Päätös Tekijä',
            loading_items: 'Ladataan kohteita...',
            loading_review_items: 'Ladataan arvostelukohteita...',
            campaign_not_found: 'Kampanjaa ei löydy',
            loading_campaign: 'Ladataan kampanjan tietoja...',
            campaign_completed: 'Kampanja suoritettu onnistuneesti.',
            confirm_cancel_title: 'Vahvista Peruuta',
            justify_bulk_confirm: 'Joukkokäytön perustelu vahvista:',
            justify_bulk_revoke: 'Perusteet joukkoperuuttamiselle:',
            items_selected: '{count} kohde(t) valittu',
            justify_bulk_required: 'Joukkotoimille vaaditaan perustelut',
            create_campaign_title: 'Luo käyttöoikeuksien tarkistuskampanja',
            name_required: 'Nimi vaaditaan',
            scope_type_required: 'Laajuustyyppi vaaditaan',
            reviewer_strategy_required: 'Arvioijastrategia vaaditaan',
            deadline_required: 'Määräaika vaaditaan',
            reviewer_id_required: 'Tarkastajan tunnus vaaditaan tietylle käyttäjästrategialle',
            invalid_json: 'Virheellinen JSON',
            scope_filter_label: 'Laajuussuodatin (JSON)',
            reviewer_strategy_label: 'Arvostelijastrategia',
            reviewer_id_label: 'Arvostelijan tunnus',
            deadline_label: 'Määräaika',
            create_campaign_btn: 'Luo kampanja',
            scope_type_label: 'Laajuustyyppi',
            scope_by_role: 'Rolen mukaan',
            scope_by_department: 'Osaston mukaan',
            scope_custom: 'Mukautettu',
            reviewer_manager: 'Manager',
            reviewer_specific_user: 'Tietty käyttäjä',
            enter_json_filter: 'Anna JSON-suodatin',
            hint_label: 'Vihje:',
            role_usage: 'Roolin käyttö',
            over_privileged_users: 'Ylioikeutetut käyttäjät',
            role_name_col: 'Roolin nimi',
            assigned_users: 'Määritetyt käyttäjät',
            active_users_30d: 'Aktiiviset käyttäjät (30 pv)',
            total_perms: 'Permit yhteensä',
            used_perms: 'Käytetyt Permit',
            unused_perms: 'Käyttämättömät Permit',
            no_data_available: 'Tietoja ei ole saatavilla',
            roles_col: 'Roolit',
            total_col: 'Yhteensä',
            used_col: 'Käytetty',
            unused_pct: 'Käyttämätön %',
            current_count: 'Nykyinen määrä',
            recommended_count: 'Suositeltu määrä',
            rationale: 'Perustelut',
            loading_analytics: 'Ladataan analytiikkaa...',
            loading_role_analytics: 'Ladataan roolin analytiikkatietoja...',
            computed_at: 'Laskettu osoitteessa:',
            form_errors: 'Lomakkeessa on {count} virhettä. Korjaa ne.',
            scope_filter_invalid_json: 'Laajuussuodatin sisältää virheellisen JSON',
            scope_hint_all_users: 'Suodatinta ei tarvita – kaikki käyttäjät otetaan mukaan.',
            review_type_label: 'Tarkistustyyppi',
            review_type_required: 'Tarkistustyyppi on pakollinen',
            reviewer_ids_label: 'Tarkastajan tunnukset',
            reviewer_ids_hint: 'Syötä käyttäjätunnukset pilkuilla eroteltuina',
            reviewer_ids_required: 'Vähintään yksi tarkastajan tunnus vaaditaan',
            start_date_label: 'Alkamispäivä',
            start_date_required: 'Alkamispäivä on pakollinen',
            end_date_label: 'Päättymispäivä',
            end_date_required: 'Päättymispäivä on pakollinen',
            review_type_user_access: 'Käyttäjän pääsy',
            review_type_role_permissions: 'Roolin oikeudet',
            review_type_service_accounts: 'Palvelutilit',
            review_type_privileged_access: 'Etuoikeutettu pääsy',
            review_type_application_access: 'Sovelluksen pääsy',
            auto_revoke_label: 'Automaattinen peruutus kieltäytymisellä',
            campaigns_title: 'Tarkastele kampanjoita',
            create_campaign_label: '+ Create Campaign',
            no_campaigns_found: 'No campaigns found',
            loading_campaigns: 'Loading campaigns...',
            no_reviews_assigned: 'Sinulle ei ole määrätty arvosteluja',
            loading_reviews: 'Loading reviews...',
            review_decision_title: 'Review Decision',
            justification_label: 'Perustelut',
            announce_loaded_review_items: 'Ladattu {count} arvostelukohdetta',
            announce_loaded_campaigns: 'Ladattu {count} kampanjaa',
            announce_decision_submitted: 'Päätös "{decision} lähetettiin tarkistettavaksi',
            announce_decision_submitted_for: 'Päätös "{decision}" lähetetty {name}',
            announce_bulk_decision: 'Joukko {decision} käytetty {count} kohteelle',
        },
        attestation: {
            training_ids_label: 'Training Covered',
            training_ids_help: 'The training courses this attestation covers. The link is carried into each campaign the schedule generates.',
            training_ids_none: 'No training linked',
            training_ids_count: '{count} course(s)',
            training_ids_load_error: 'Training courses could not be read. The list may be incomplete.',
            title: 'Vahvistusaikataulut',
            new: 'Uusi aikataulu',
            edit: 'Muokkaa aikataulua',
            create_title: 'Luo vahvistusaikataulu',
            edit_title: 'Muokkaa vahvistusaikataulua',
            schedule_history: 'Aikatauluhistoria',
            next_run_date: 'Seuraava suorituspäivä',
            schedule_name: 'Aikataulun nimi',
            frequency: 'Tiheys',
            next_run: 'Seuraava suoritus',
            last_run: 'Viimeisin suoritus',
            history: 'Vahvistushistoria',
            description: 'Kuvaus',
            scope: 'Laajuus',
            status: 'Tila',
            created_at: 'Luotu',
            updated_at: 'Päivitetty',
            trigger_now: 'Trigger Now',
            back_to_schedules: 'Back to Schedules',
            confirm_trigger: 'Trigger this schedule now?',
            confirm_delete: 'Delete this schedule?',
            triggered: 'Aikataulu käynnistyi onnistuneesti.',
            deleted: 'Aikataulu poistettu.',
            saved: 'Aikataulu tallennettu onnistuneesti.',
            campaigns_generated: 'Luodut kampanjat',
            run_date: 'Suorituspäivämäärä',
            run_status: 'Suorita tila',
            create_schedule: 'Luo aikataulu',
            view_history: 'Näytä historia',
            deactivate: 'Poista käytöstä',
            overdue: 'OVERDUE',
            overdue_sr: 'Aikataulu on myöhässä',
            confirm_trigger_title: 'Vahvista toiminto',
            confirm_trigger_message: 'Are you sure you want to trigger this schedule now?',
            confirm_deactivate_title: 'Vahvista deaktivointi',
            confirm_deactivate_message: 'Haluatko varmasti poistaa tämän aikataulun käytöstä?',
            triggered_success: 'Aikataulu käynnistyi onnistuneesti.',
            triggered_unmapped_reviewer: 'The cycle was started, but the reviewer rule "{strategy}" has no source in the platform, so the campaign names no reviewer: whoever launches it reviews its items.',
            trigger_scope_unresolvable: 'This schedule’s scope cannot be turned into a review campaign. Edit the schedule so its scope filter names at least one role, department or user, or choose a supported scope.',
            trigger_reviewer_not_found: 'The reviewer named on this schedule is no longer an active user of this organisation. Edit the schedule and choose another reviewer.',
            reviewer_not_found: 'The reviewer ID does not name an active user of this organisation',
            scope_unsupported: 'This scope type is not supported: a review campaign scopes by user, role and department only',
            deactivated_success: 'Aikataulu poistettu käytöstä.',
            name_label: 'nimi *',
            frequency_label: 'Taajuus *',
            scope_type_label: 'Laajuustyyppi *',
            scope_filter_label: 'Laajuussuodatin (JSON)',
            reviewer_strategy_label: 'Arvostelijastrategia *',
            reviewer_id_label: 'Arvostelijan tunnus *',
            frequency_quarterly: 'Neljännesvuosittain',
            frequency_semi_annual: 'Puolivuosittain',
            frequency_annual: 'Vuosittainen',
            scope_all_users: 'Kaikki käyttäjät',
            scope_by_role: 'Rolen mukaan',
            scope_by_department: 'Osaston mukaan',
            scope_custom: 'Mukautettu',
            reviewer_manager: 'Manager',
            reviewer_role_owner: 'Roolin omistaja',
            reviewer_specific_user: 'Tietty käyttäjä',
            computed_next_run_date: 'Laskettu seuraavan ajon päivämäärä',
            current_scheduled_date: 'Nykyinen suunniteltu päivämäärä',
            recomputed_frequency_changed: '(uudelleen laskettu -- taajuus muutettu)',
            estimated_days_hint: 'Arvioitu {days} päivän perusteella tästä päivästä',
            validation_name_required: 'Nimi vaaditaan',
            validation_frequency_required: 'Taajuus vaaditaan',
            validation_scope_type_required: 'Laajuustyyppi vaaditaan',
            validation_reviewer_strategy_required: 'Arvioijastrategia vaaditaan',
            validation_reviewer_id_required: 'Tarkastajan tunnus vaaditaan tietylle käyttäjästrategialle',
            validation_invalid_json: 'Virheellinen JSON',
            validation_scope_filter_invalid_json: 'Laajuussuodatin sisältää virheellisen JSON',
            form_errors: 'Lomakkeessa on {count} virhettä. Korjaa ne.',
            create_schedule_btn: 'Luo aikataulu',
            schedule_history_title: 'Aikatauluhistoria',
            campaign_name_col: 'Kampanjan nimi',
            items_col: 'Tuotteet',
            no_campaigns_yet: 'Kampanjoita ei ole vielä luotu',
            loading_history: 'Ladataan historiaa...',
            loading_schedule: 'Ladataan aikataulua...',
            total_campaigns: 'Kampanjoita yhteensä: {count}',
            results_per_page: 'Tulokset per sivu',
            page_size_option: '{size} / sivu',
        },
        sod: {
            title: 'Tehtävien eriyttämissäännöt (SoD)',
            new: 'Uusi sääntö',
            edit: 'Muokkaa sääntöä',
            create_title: 'Luo SoD-sääntö',
            edit_title: 'Muokkaa SoD-sääntöä',
            rule_name: 'Säännön nimi',
            conflicting_permissions: 'Ristiriitaiset käyttöoikeudet',
            permission_a: 'Käyttöoikeus A',
            permission_b: 'Käyttöoikeus B',
            violations: 'SoD-rikkomukset',
            violation_user: 'Käyttäjä',
            violation_rule: 'Rikottu sääntö',
            violation_date: 'Havaittu',
            exceptions: 'SoD-poikkeukset',
            exception_reason: 'Poikkeuksen syy',
            exception_approver: 'Hyväksyjä',
            exception_status: 'Poikkeustila',
            has_exception: 'On poikkeus',
            no_exception: 'Ei poikkeusta',
            grant_exception: 'Apuraha poikkeus',
            granting: 'Myönnetään...',
            grant_new_exception: 'Myönnä uusi poikkeus',
            exceptions_for: 'Poikkeukset: {name}',
            back_to_rules: 'Takaisin sääntöihin',
            no_violations: 'Rikkomuksia ei löytynyt',
            no_exceptions_found: 'Tälle säännölle ei löytynyt poikkeuksia',
            all_exception_statuses: 'Kaikki poikkeustilat',
            user_id_label: 'Käyttäjätunnus',
            justification_label: 'Perustelut',
            compensating_controls_label: 'Kompensoivat säätimet',
            expires_at_label: 'Päättyy klo',
            granting_exception_for: 'Poikkeuksen myöntäminen säännölle: {name}',
            severity: 'Vakavuus',
            severity_warning: 'Varoitus',
            severity_blocking: 'Estäminen',
            violation_count: '{count} rikkomukset',
            exception_count: '{count} poikkeusta',
            rule_description: 'Kuvaus',
            confirm_delete: 'Haluatko varmasti poistaa tämän säännön?',
            deleted: 'Sääntö poistettu.',
            framework_code: 'Saantelykehys',
            name_label: 'nimi *',
            severity_label: 'Vakavuus *',
            conflict_pairs_legend: 'Ristiriitalupaparit',
            conflict_pairs_hint: 'Jokainen pari määrittelee kaksi käyttöoikeutta, joita samalla käyttäjällä ei pitäisi olla.',
            placeholder_permission_a: '-- Lupa A --',
            placeholder_permission_b: '-- Lupa B --',
            permission_a_for_pair: 'Lupa A parille {index}',
            permission_b_for_pair: 'Lupa B parille {index}',
            conflict_pair_label: 'Ristiriitapari {index}',
            remove_conflict_pair: 'Poista ristiriitapari {index}',
            add_pair: '+ Lisää pari',
            placeholder_name: 'esim. Maksuhyväksynnän erottaminen',
            create_rule: 'Luo sääntö',
            loading_rule: 'Ladataan SoD-sääntöä...',
            failed_load_rule: 'Säännön lataaminen epäonnistui.',
            failed_create_rule: 'SoD-säännön luominen epäonnistui.',
            failed_update_rule: 'SoD-säännön päivittäminen epäonnistui.',
            validation_name_required: 'Nimi vaaditaan.',
            validation_pair_required: 'Vähintään yksi ristiriitalupapari vaaditaan.',
            validation_pair_both_required: 'Yhdistä {index}: Molemmat käyttöoikeudet on valittava.',
            validation_pair_must_differ: 'Pari {index}: Luvan A ja B:n on oltava eri.',
            added_conflict_pair: 'Lisätty ristiriitapari {index}',
            removed_conflict_pair: 'Poistettu ristiriitapari {index}',
            announce_loaded_violations: 'Ladattu {count} rikkomuksia',
            announce_loaded_exceptions: 'Ladattu {count} poikkeusta',
            announce_loaded_rules: 'Ladattu {count} SoD-säännöt',
            announce_loading_exceptions: 'Ladataan poikkeuksia…',
            regulatory_info: {
                sod_context: 'SoD-saannot toteuttavat PDPL:n vaatiman roolien erottamisen. framework_code-tunnisteeseen liitetyt rikkomukset voidaan sisallyttaa kehyskohtaisiin todistepaketteihin (UC-VN-09).',
                violation_export_context: 'SoD-rikkomusvienti sisaltaa framework_code, ristiriitaiset roolit, vakavuuden, luojan ja havaitsemisajankohdan todistepaketteihin (P-VN-10).',
                exception_context: 'SoD-poikkeukset tuottavat tarkastusnayttoa: hyvaksyja, perustelu, korvaavat kontrollit ja vanhentumispaiva. Vanhentuneet poikkeukset pysyvat nakyvissa (UC-VN-09 Vaihe 5).',
            },
        },
        regulatory_info: {
            attestation_context: 'Attestation schedules repeat an access review on a cycle (quarterly, semi-annual or annual). Each cycle creates a review campaign for all users, for named roles or departments, or for named users, and a named reviewer answers it. A reviewer rule the campaign model cannot express (manager or role owner) is shown when the cycle is started, and whoever launches the campaign then reviews its items. Schedule history lists the campaigns each schedule has generated.',
            api_keys_context: 'API key telemetry constitutes account evidence per VN_CYBERSEC_LAW_2025 Section B.5. Each key record includes last_used_at, ip_allowlist, permissions, status, and expires_at.',
            session_context: 'Session management enforces access controls per Telecom Law confidentiality requirements (Section B.4). Session events produce audit trail entries.',
            break_glass_context: 'Break-glass procedures produce regulatory justification evidence: requestor identity, justification, permissions, duration, approver identity, and approval/denial timestamps.',
            license_context: 'License settings gate modules by tier using PACKAGE_DEFINITIONS. Framework-specific modules require appropriate license tiers. Tier changes produce audit trail entries.',
            module_config_context: 'Module configuration supports framework-driven enablement. Disabling a module required by an active regulatory framework may break evidence completeness.',
            notification_context: 'Compliance-critical notifications (breach deadlines, attestation due dates) are marked with a compliance_critical flag and cannot be fully suppressed.',
            delegation_context: 'Delegation governance produces audit-grade evidence: delegator_id, delegate_id, permission scope, start/end dates, and status. Expired delegations remain visible.',
            access_control_context: 'Access control configuration meets Cybersecurity Law requirements (VN_CYBERSEC_LAW_2025): IP allowlist, MFA, service accounts, and SSO changes are audit-trailed.',
        },
    },
    cmp: {
        integration: {
            title: 'CMP-integraatio',
            status: 'Yhteyden tila',
            endpoint: 'CMP-päätepiste',
            last_sync: 'Viimeisin synkronointi',
            configure: 'Määritä',
        },
        purpose_mapping: {
            title: 'Tarkoitusten kytkentä',
            cmp_purpose: 'CMP-tarkoitus',
            grc_purpose: 'GRC-tarkoitus',
            mapping_status: 'Kytkennän tila',
            auto_map: 'Automaattinen kytkentä',
            new_mapping: 'Uusi kytkentä',
        },
        webhook: {
            title: 'Webhook-asetukset',
            webhook_url: 'Webhook-URL',
            secret: 'Webhook-salaisuus',
            events: 'Tapahtumatyypit',
            test_webhook: 'Testaa webhook',
        },
        bulk_sync: {
            title: 'Joukkosynkronointi',
            sync_now: 'Synkronoi nyt',
            last_run: 'Viimeisin suoritus',
            records_synced: 'Synkronoidut tietueet',
            sync_history: 'Synkronointihistoria',
            syncing: 'Synkronoidaan...',
        },
        sync_settings: {
            sync_failures: 'Sync Epäonnistuminen',
            sync_secret_warning: 'Sync Salaisuus (save this now — it will not be shown again):',
            push_description: 'Määritä the GRC Suite to push case tila updates to AesirX CMP.',
            technical_description: 'Tämä määritys hallitsee sitä, kuinka GRC Suite lähettää tapauksen tilapäivitykset AesirX:iin CMP.',
            direction_label: 'Suunta',
            direction_value: 'GRC Suite → AesirX CMP (lähtevä)',
            auth_label: 'Todennus',
            auth_value: 'HMAC-SHA256-allekirjoitus X-GRC-Signature-otsikossa',
            retry_label: 'Yritä uudelleen Käytäntö',
            retry_value: '3 yritystä eksponentiaalisella viiveellä (30s, 2min, 10min)',
            inbound_label: 'Saapuva konfiguraatio',
            inbound_value: 'Saapuva webhook (CMP → GRC) on määritetty',
            cmp_webhook_settings: 'CMP Webhook Asetukset',
            page_title: 'CMP Synkronointiasetukset',
            outbound_sync_configured: 'Lähtevä synkronointi määritetty.',
            configure_sync_failed: 'Synkronoinnin määritys epäonnistui',
            configuration_updated: 'Määritys päivitetty.',
            update_configuration_failed: 'Määrityksen päivitys epäonnistui',
            connection_test_failed: 'Yhteystesti epäonnistui',
            test_result_message: 'Testi lähetetty osoitteeseen {endpoint}. Tila: {status}',
            endpoint_placeholder: 'https://your-cmp-domain/api/grc-sync',
            events: {
                new_to_under_review: 'Uusi → Under Katselmointia (assigned)',
                under_review_to_identity_verification: 'Tarkistettavana → Henkilöllisyyden vahvistus',
                under_review_to_in_progress: 'Tarkistettavana → Käynnissä',
                in_progress_to_completed: 'In Progress → Valmis',
                in_progress_to_denied: 'Käynnissä → Hylätty',
                any_to_closed: 'Mikä tahansa → Suljettu',
            },
        },
    },
    settings_pages: {
        webhooks: {
            title: 'Webhookit',
            new: 'Uusi webhook',
            url: 'URL',
            url_placeholder: 'https://example.com/webhook',
            events: 'Tapahtumat',
            secret: 'Salaisuus',
            active: 'Aktiivinen',
            test: 'Testaa',
            create_failed: 'Epäonnistui: create webhook. Yritä uudelleen.',
            create_success: 'Webhook luotiin onnistuneesti.',
            deactivate_failed: 'Epäonnistui: deactivate webhook. Yritä uudelleen.',
            deactivate_success: 'Webhook poistettu käytöstä.',
            fetch_failed: 'Epäonnistui: fetch webhooks. Yritä uudelleen.',
            send_test_failed: 'Epäonnistui: send test. Yritä uudelleen.',
            test_failed: 'Webhook-testi epäonnistui.',
            test_success: 'Webhook-testi onnistui.',
            edit_title: 'Muokkaa webhookia',
            announce_loaded: 'Webhookit ladattu',
            update_success: 'Webhook päivitetty onnistuneesti.',
            update_failed: 'Webhookin päivitys epäonnistui.',
            activate_success: 'Webhook aktivoitu.',
            activate_failed: 'Webhookin aktivointi epäonnistui.',
        },
        domains: {
            title: 'Verkkotunnukset',
            new: 'Uusi verkkotunnus',
            domain_name: 'Verkkotunnuksen nimi',
            verified: 'Vahvistettu',
            verify: 'Vahvista',
            add_failed: 'Epäonnistui: add domain. Yritä uudelleen.',
            add_success: 'Verkkotunnus added successfully.',
            domain_placeholder: 'esim. grc.example.com',
            fetch_failed: 'Epäonnistui: fetch domains. Yritä uudelleen.',
            remove_failed: 'Epäonnistui: remove domain. Yritä uudelleen.',
            remove_success: 'Verkkotunnus removed successfully.',
            txt_record: 'TXT Tietue',
            verify_dns_failed: 'DNS verification epäonnistunut.',
            verify_failed: 'Epäonnistui: verify domain. Yritä uudelleen.',
            verify_success: 'Verkkotunnus verified successfully.',
            announce_loaded: 'Domains loaded',
        },
        export_schedules: {
            title: 'Vientiaikataulut',
            new: 'Uusi aikataulu',
            schedule_name: 'Nimi',
            frequency: 'Tiheys',
            format: 'Muoto',
            last_run: 'Viimeisin suoritus',
            next_run: 'Seuraava suoritus',
            create_failed: 'Epäonnistui: create schedule. Yritä uudelleen.',
            create_success: 'Aikataulu created successfully.',
            day_of_month: 'Kuukauden päivä',
            day_of_week: 'Viikonpäivä',
            deactivate_failed: 'Epäonnistui: deactivate schedule. Yritä uudelleen.',
            deactivate_success: 'Aikataulu deactivated.',
            fetch_failed: 'Epäonnistui: fetch schedules. Yritä uudelleen.',
            freq_daily: 'Päivittäin',
            freq_monthly: 'Kuukausittain',
            freq_weekly: 'Viikoittain',
            recipients: 'Vastaanottajat',
            time_utc: 'Aika (UTC)',
            trigger_failed: 'Epäonnistui: trigger export. Yritä uudelleen.',
            trigger_success: 'Vie triggered successfully.',
            type_audit_trail: 'Tarkastus Trail',
            type_compliance_report: 'Vaatimustenmukaisuus Raportti',
            type_custom: 'Mukautettu',
            type_incident_report: 'Vaaratilanteita Raportti',
            type_ropa: 'ROPA',
            announce_loaded: 'Export schedules loaded',
            execution_note: "Each run produces the file as the schedule's run actor, shown under Runs as: the person who created the schedule, or who last changed what it produces, with that person's current permissions. Each run is recorded below. Recipients receive an e-mail with a link to this page; the file downloads under each reader's own permissions.",
            paused_reason_label: 'Paused by the platform',
            type_withdrawn: 'Withdrawn type',
            runs: 'Runs',
            runs_for: 'Runs for {name}',
            runs_empty: 'This schedule has not run yet.',
            runs_fetch_failed: 'Failed to load the runs.',
            close_runs: 'Close runs',
            run_status: 'Status',
            run_scheduled_for: 'Run for',
            run_trigger: 'Started by',
            run_attempts: 'Attempts',
            run_reason: 'Reason',
            run_file: 'File',
            run_status_running: 'Running',
            run_status_completed: 'Completed',
            run_status_failed: 'Failed',
            run_trigger_scheduled: 'Schedule',
            run_trigger_manual: 'Manual trigger',
            run_will_retry: 'It will be tried again.',
            download: 'Download',
            triggering: 'Exporting…',
            disabling: 'Disabling…',
            trigger_completed: 'Export completed. The file is in the run list.',
            trigger_run_failed: 'The export ran and failed: {reason}',
            failure_actor_inactive: 'The person this schedule runs as is no longer an active member, so the export cannot run as them.',
            failure_actor_lacks_permission: 'The person this schedule runs as no longer holds the permission this export needs.',
            failure_export_type_withdrawn: 'This export type was withdrawn, so the schedule cannot produce it. To export again, a new schedule with another export type is needed.',
            failure_export_too_large: 'The export selects more rows than one file can hold.',
            failure_generation_failed: 'The file could not be generated.',
            failure_interrupted: 'The run was interrupted before it finished.',
            failure_never_executed: 'Requested before scheduled exports were executed, so this run never ran and produced no file.',
            report: {
                incident_title: 'Incident report',
                compliance_title: 'Compliance report — framework coverage',
                window: 'Period: {start} to {end}',
                count: 'Rows: {count}',
                overall: 'Overall mapped coverage: {pct}',
                overall_none: 'Overall mapped coverage: not measurable (no installed framework declares requirements)',
                col_reference: 'Reference',
                col_title: 'Title',
                col_status: 'Status',
                col_severity: 'Severity',
                col_created: 'Created',
                col_closed: 'Closed',
                col_framework: 'Framework',
                col_requirements: 'Requirements',
                col_mapped: 'Mapped',
                col_coverage: 'Coverage',
                col_open_gaps: 'Open gaps',
            },
            email: {
                subject: 'Scheduled export ready: {name}',
                body: 'The scheduled export "{name}" ran for {date}. Open the run list to download the file: {link}',
                body_html: 'The scheduled export "{name}" ran for {date}. Open the run list to download the file:',
            },
            type_none_held: 'You hold none of the permissions an export type needs, so you cannot create a schedule: a schedule runs as the person who creates it.',
            type_offered_hint: 'Only the export types you may export yourself are offered: the schedule runs as you.',
            grant_ropa: 'the permission to export the record of processing activities as PDF',
            grant_audit_trail: 'the permission to export audit events',
            grant_compliance_report: 'the permission to read regulatory frameworks',
            grant_incident_report: 'the permission to run incident exports',
            refusal_permission_not_held: 'You do not hold {permission}, which this export needs, so you cannot make this schedule produce it.',
            refusal_permission_not_held_generic: 'You do not hold the permission this export needs.',
            refusal_schedule_inactive: 'This schedule is switched off, so it cannot be run. Enable it first.',
            refusal_export_type_withdrawn: "This schedule's export type was withdrawn, so it cannot be run or switched on. To keep exporting, create a schedule with another export type using New Schedule.",
            col_runs_as: 'Runs as',
            run_executed_as: 'Ran as',
            run_delivered_to: 'E-mail queued for',
            run_delivered_none: 'No e-mail queued',
            run_delivered_hint: 'An address under E-mail queued for means the e-mail was placed in the sending queue, not that it arrived. The Notification Admin Queue, open to administrators of notifications, shows whether each e-mail was sent.',
            enable: 'Enable',
            enabling: 'Enabling…',
            enable_success: 'Schedule enabled. Its next run is counted from now.',
            enable_failed: 'The schedule could not be enabled.',
            refusal_run_actor_cannot_run: 'This schedule runs as {name}, who cannot run it now: they are no longer an active member, or no longer hold the permission its export needs. It can be switched on once {name} can run it again: ask an administrator to restore their membership or permission. To export now, create a schedule with New Schedule: it runs as you.',
            refusal_run_actor_inactive: 'This schedule runs as {name}, who is no longer an active member of the organisation, so its runs would be refused and it cannot be switched on. It can be switched on once {name} is an active member again: ask an administrator to restore their membership. To export now, create a schedule with New Schedule: it runs as you.',
            refusal_run_actor_lacks_permission: 'This schedule runs as {name}, who no longer holds {permission}, which its export needs, so it cannot be switched on. It can be switched on once {name} holds it again: ask an administrator to give {name} that permission. To export now, create a schedule with New Schedule: it runs as you.',
            run_actor_unnamed: 'the person shown as Runs as',
            people_names_failed: 'The user directory could not be loaded, so a person may be shown by their account identifier instead of their name.',
        },
        email_log: {
            title: 'Sähköpostiloki',
            recipient: 'Vastaanottaja',
            subject: 'Aihe',
            sent_at: 'Lähetetty',
            status: 'Tila',
            resend: 'Lähetä uudelleen',
            fetch_failed: 'Epäonnistui: fetch sähköposti loki. Yritä uudelleen.',
            announce_loaded: 'Email log loaded',
        },
    },
    organizations: {
        title: 'Organisaatiot',
        create: 'Luo Organisaatio',
        edit: 'Muokkaa Organisaatio',
        name: 'Nimi',
        slug: 'Tunniste',
        timezone: 'Aikavyöhyke',
        locale: 'Oletus Maa-asetus',
        parent: 'Parent Organisaatio',
        group: 'Ryhmä',
        org_group: 'Organisaatioryhmä',
        no_group: '(Ei mitään - ei kuulu ryhmään)',
        created: 'Luotu',
        no_parent: '(Ei mitään - itsenäinen)',
        no_orgs: 'Ei organizations löytynyt',
        load_error: 'Epäonnistui: load organizations. Yritä uudelleen.',
        save_error: 'Epäonnistui: save organization. Yritä uudelleen.',
        created_msg: 'Organisaatio created',
        updated: 'Organisaatio updated',
        deactivate: 'Poista käytöstä',
        deactivated: 'Organisaatio deactivated',
        confirm_deactivate: 'Haluatko varmasti deactivate this organization?',
        loaded_results: 'Organizations ladattu, {count} tulosta',
        form_loaded: 'Organization-lomake ladattu',
        locale_vi_vn: 'Tiếng Việt (vi-VN)',
        locale_en_us: 'englanti (en-US)',
        // fix-sprint #431 — the organisation's country (ISO 3166-1 alpha-2).
        country: 'Country',
        country_not_recorded: 'Not recorded',
        country_hint: 'The country this organisation is established in. The cross-border transfer check on rights requests compares a data subject’s country with it; while it is not recorded, that check is not evaluated.',
        // Gate E12.5 remediation 2 (fix-sprint #431) — the row Edit reads the record first.
        edit_reading: 'Reading the organisation’s record…',
        edit_read_error: 'The organisation’s record could not be read, so its current values are not shown and it cannot be saved here.',
        no_head: 'No Head',
        all_frameworks: 'Kaikki kehykset',
        assigned_at: 'Määritetty',
    },
    intake_configs: {
        title: 'Julkiset vastaanottokonfiguraatiot',
        create: 'Luo Config',
        edit: 'Muokkaa Intake Config',
        label_col: 'Tunniste',
        key: 'Intake Avain',
        key_hint: 'Julkinen URL on: /rights-request?key=',
        mapped_org: 'Tavoite Organisaatio',
        status: 'Tila',
        enabled: 'Käytössä',
        disabled: 'Pois käytöstä',
        request_types: 'Käytössä Pyyntö Tyypit',
        language: 'Oletus Kieli',
        public_url: 'Julkinen URL',
        no_configs: 'Ei intake configs found. luo one to enable public rights request forms. löytynyt',
        load_error: 'Epäonnistui: load intake configs. Yritä uudelleen.',
        save_error: 'Epäonnistui: save. Yritä uudelleen.',
        delete_error: 'Epäonnistui: delete. Yritä uudelleen.',
        created_msg: 'Konfiguraatio luotu',
        updated: 'Konfiguraatio päivitetty',
        deleted: 'Vastaanottokonfiguraatio poistettu',
        confirm_delete: 'Poista this intake config?',
        select_types: 'Valitse at least one request tyyppi',
        select_org: 'Valitse a target organization',
        routing_mode: 'Reititystila',
        single_org: 'Yksittäinen Organisaatio',
        org_group_mode: 'Organisaatioryhmä',
        org_group: 'Organisaatioryhmä',
        select_group: 'Valitse organisaatioryhmä...',
        group_root_hint: 'Asetetaan automaattisesti ryhmän juuriorganisaatioksi, kun ryhmä valitaan.',
        loaded_results: 'Intake configurations ladattu, {count} tulosta',
        form_loaded: 'Intake configuration-lomake ladattu',
    },
    frequency: {
        weekly: 'Viikoittain',
        monthly: 'Kuukausittain',
        quarterly: 'Neljännesvuosittain',
        semi_annually: 'Puolivuosittain',
        annually: 'Vuosittain',
        custom: 'Mukautettu',
    },
    /**
     * Sprint 65.6.0 (AC8) — DPO-01 and PUB-01, the privacy office's two registers.
     *
     * One block with two children rather than two top-level blocks: the
     * publication vocabulary (`channels`, `publication_states`, the target
     * editor's labels) is shared by both registers, and a second spelling of it
     * would be free to drift from the first — the defect `PUBLICATION_CHANNELS`
     * is declared once in shared to avoid.
     *
     * R-57: EN and VI are authored. The other fourteen locales carry the EN
     * string for structural parity and are translated by the Phase 25 batch —
     * stated here rather than left to be inferred from the text.
     */
    privacy_office: {
        publication_states: {
            not_published: 'Not published',
            published: 'Published',
            withdrawn: 'Withdrawn',
        },
        channels: {
            public_website: 'Public website',
            mobile_app: 'Mobile app',
            intranet: 'Intranet',
            physical_notice: 'Physical notice',
            direct_notice: 'Direct notice to people',
            other: 'Other',
        },
        filter_publication_state: 'Publication',
        targets_label: 'Where it was published',
        targets_hint: 'At least one place a person can go and read it. A publication with no location is a claim nobody can check.',
        target_channel: 'Channel',
        target_locator: 'Address or location',
        target_locator_hint: 'A web address for a website, a screen name for an app, or a described location for a notice board.',
        target_locale: 'Language (optional)',
        target_add: 'Add another location',
        target_remove: 'Remove this location',
        field_published_on: 'Published on',
        field_published_on_hint: 'The date it actually became public, not the date you are entering it here.',
        appointments: {
            title: 'Data Protection Officer appointments',
            subtitle: 'Record who is appointed, the decision that appointed them, and where the appointment was made public.',
            module_guide: 'Record the appointment of your data protection officer as a register entry rather than a filled-in form: who is appointed, who decided it and when, when the appointment takes effect, and where you said so publicly. An appointment is saved as a draft and raises no duty until you bring it into effect — that transition is the one that says your organisation has a named officer in post. Only an appointment that is in effect can be published, and a publication must say where a person can go and read it.',
            nav: 'DPO appointments',
            record: 'Record appointment',
            table_label: 'Data protection officer appointments',
            sr_loading: 'Loading appointments',
            announce_loaded: '{count} appointments loaded',
            none: 'No appointments match these filters.',
            filters_label: 'Filter appointments',
            filter_status: 'Status',
            col_appointee: 'Appointed person',
            col_position: 'Position',
            col_decision: 'Appointing decision',
            col_takes_effect: 'Takes effect',
            col_status: 'Status',
            col_publication: 'Publication',
            takes_effect_unknown: 'Not stated',
            statuses: {
                draft: 'Draft',
                in_effect: 'In effect',
                ended: 'Ended',
                superseded: 'Superseded',
            },
            action_take_effect: 'Bring into effect',
            action_publish: 'Record publication',
            action_end: 'End appointment',
            modal_record_title: 'Record a DPO appointment',
            modal_record_hint: 'Record a decision your organisation has already taken. The appointment is saved as a draft and raises no duty until you bring it into effect.',
            field_full_name: 'Full name',
            field_position_title: 'Position title',
            field_contact_email: 'Contact email',
            field_contact_email_hint: 'Written as name@example.com. This is the address the public sees.',
            field_contact_phone: 'Contact phone (optional)',
            field_decided_by: 'Decided by',
            field_decided_by_hint: 'The body or person with the authority to appoint.',
            field_decided_on: 'Decision date',
            field_decision_reference: 'Your reference for the decision (optional)',
            field_takes_effect_on: 'Takes effect on (optional)',
            field_takes_effect_hint: 'Leave this empty if the start date is not settled. The duty is then raised undated with that reason, rather than dated from the day you typed it in.',
            field_ends_on: 'Ends on (optional)',
            modal_take_effect_title: 'Bring the appointment into effect',
            modal_take_effect_hint: 'From this point your organisation has a named officer in post. State the date it started if it is not already recorded.',
            field_correction_note: 'Note about the correction (optional)',
            modal_publish_title: 'Record where the appointment was published',
            modal_publish_hint: 'Only an appointment that is in effect can be published — a person is not named publicly as your officer before your organisation says they are one.',
            modal_end_title: 'End the appointment',
            modal_end_hint: 'An appointment that ran its course and one replaced by a newer appointment are different answers to "why is this person no longer the officer". Both are kept.',
            field_end_outcome: 'Outcome',
            field_end_reason: 'Reason',
            field_end_date: 'End date (optional)',
            toast_recorded: 'Appointment recorded.',
            toast_in_effect: 'Appointment is now in effect.',
            toast_published: 'Publication recorded.',
            toast_ended: 'Appointment ended.',
        },
        notices: {
            title: 'Privacy notices',
            subtitle: 'Draft a revision of a privacy notice, for the public or for named data subjects, have it approved, and record where it was published.',
            module_guide: 'Keep a register of the privacy notices your organisation gives, to the public or to named data subjects of its processing activities: which revision is live, who approved the text, and where a person can read it. Approval and publication are two separate acts, usually by two different people, so the register can show a notice that was approved and never actually published — the failure this record exists to make visible. A notice cannot go straight from draft to published, and a published notice that is taken down keeps its publication history with the reason it was withdrawn.',
            nav: 'Privacy notices',
            draft: 'New revision',
            table_label: 'Privacy notices',
            sr_loading: 'Loading privacy notices',
            announce_loaded: '{count} privacy notices loaded',
            none: 'No privacy notices match these filters.',
            filters_label: 'Filter privacy notices',
            filter_status: 'Status',
            col_title: 'Title',
            col_revision: 'Revision',
            col_locales: 'Languages',
            col_status: 'Status',
            col_approval: 'Approved by',
            col_publication: 'Publication',
            approval_none: 'Not approved',
            statuses: {
                draft: 'Draft',
                approved: 'Approved',
                published: 'Published',
                withdrawn: 'Withdrawn',
                superseded: 'Superseded',
            },
            action_approve: 'Approve',
            action_publish: 'Publish',
            action_withdraw: 'Withdraw',
            action_supersede: 'Supersede',
            modal_draft_title: 'New privacy notice revision',
            modal_draft_hint: 'The revision is the version number your published text carries. It is not a version this platform counts for you.',
            field_notice_title: 'Title',
            field_revision: 'Revision',
            field_content_url: 'Where the text lives',
            field_content_url_hint: 'The address of the notice text itself — the page, document or file a reader would open.',
            field_locales: 'Languages',
            field_locales_hint: 'One language code per line, or separated by commas.',
            field_supersedes: 'Replaces which notice (optional)',
            modal_approve_title: 'Approve the notice',
            modal_approve_hint: 'Approving records who signed the text off. It does not give the notice to its audience — publishing is a separate act.',
            field_approved_by: 'Approved by (optional)',
            field_approved_by_hint: 'Leave this empty to record yourself as the approver.',
            field_approval_reference: 'Your reference for the approval (optional)',
            modal_publish_title: 'Publish the notice',
            modal_publish_hint: 'Record that the approved text is now given to its audience, and where a person can read it.',
            publish_needs_approval: 'This notice carries no recorded approval, so it cannot be published. Record who approved the text first.',
            modal_withdraw_title: 'Withdraw the notice',
            modal_withdraw_hint: 'Withdrawing keeps the publication history: "we published this and took it down" is a different answer from "we never published this".',
            field_withdraw_reason: 'Reason for withdrawing',
            modal_supersede_title: 'Supersede the notice',
            modal_supersede_hint: 'Superseding closes this revision because a newer one has replaced it, and it is the last thing that can happen to it in this register.',
            field_supersede_reason: 'Reason for superseding',
            toast_drafted: 'Privacy notice revision created.',
            toast_approved: 'Privacy notice approved.',
            toast_published: 'Privacy notice published.',
            toast_withdrawn: 'Privacy notice withdrawn.',
            toast_superseded: 'Privacy notice superseded.',
        },
    },
    /* Sprint 65.18.0 — continuity (IRP-01). English text held for structural
       parity; real translation deferred to Phase 25 per R-57. */
    /* Sprint 65.19.0 -- legal matters (toolkit row LEG-01). */
    /* 2026-09-24 owner ruling — legacy instrument notice. English text held for
       structural parity; real translation deferred to Phase 25 per R-57. */
    framework_lifecycle_notice: {
        legacy_chip: 'Legacy — no longer in force',
        legacy_notice_label: 'Legacy instrument notice',
        legacy_body: 'This instrument has lapsed or been replaced. Results determined under it may still apply under transition rules. Check the current instrument before relying on it.',
        suspended_chip: 'Suspended — not an obligation',
        suspended_notice_label: 'Suspended instrument notice',
        suspended_body: 'The application of this instrument is suspended, so its duties are not owed while the suspension lasts. Check what ends the suspension before relying on anything recorded under it.',
    },
    framework_articles: {
        title: 'Articles',
        article: 'Article',
        heading: 'Heading',
        state: 'Platform coverage',
        totals_summary: '{total} articles in this instrument — {mapped} implemented by a requirement, {declared} recorded as imposing no duty, {unmapped} not yet mapped.',
        not_article_structured: 'This instrument is not divided into articles — it uses control identifiers — so there is no article map to show. That is correct for this framework and not a gap.',
        not_mapped_yet: 'This instrument is divided into articles and the installed pack does not yet carry them. Until it does, the platform cannot say which of its provisions are implemented.',
        structure_unverified: 'Nobody has read this instrument\u2019s official text yet, so the platform does not know how it divides its provisions and cannot say which of them are implemented. This is a statement about the platform, not about the instrument.',
        structure_not_declared: 'This record does not say how its instrument divides its provisions, so the platform does not know whether it has an article map. That is a gap in the installed pack, not a statement about the instrument.',
        structure_not_required: 'This record comes from a market pack, and a market pack is not required to declare how its instrument divides its provisions, so the platform has no article map to show for it. That is not a gap in the installed pack.',
        structure_origin_unknown: 'This record does not say how its instrument divides its provisions, and the platform cannot tell which kind of pack it was installed from, so it does not know whether an article map should exist.',
        coverage_line: 'Article coverage: {coverage}',
        coverage_levels: {
            mapped: 'Every article is accounted for',
            partial: 'Some articles are not yet mapped',
            not_assessed: 'Not assessed',
        },
        load_failed: 'The articles for this framework could not be read.',
        recorded_text: {
            column: 'Recorded text',
            read: 'Read',
            read_aria: 'Read the recorded text of Article {article}',
            dialog_title: 'Article {article} — recorded text',
            load_failed: 'The recorded text of this article could not be read.',
            load_more: 'Show more',
            source_line: 'Source: {title}',
            page_single: 'page {page} of the source',
            pages_range: 'pages {start}–{end} of the source',
            extraction: {
                ocr: 'read by OCR from the signed scan',
                pdf_text_layer: 'extracted from the PDF text layer',
                docx_text: 'extracted from the DOCX original',
                html_text: 'extracted from the official web page',
                unrecorded: 'extraction method not recorded',
            },
            missing: 'The recorded source does not carry this article\u2019s text, so none is shown. Only its heading is known.',
            partial: 'The source ends without a closing mark after this article, so the text may run on into material that follows it.',
            notice: 'This is the instrument\u2019s original Vietnamese text as recorded from its signed source. It may contain recognition errors, it does not certify current legal effect, and no translation is provided.',
        },
        source_tab: {
            title: 'Source',
            intro: 'The instrument\u2019s recorded text, read in full: chapters, articles, annexes and page marks as the signed source carries them.',
            source_select: 'Recorded source',
            page_mark: 'Page {page}',
            progress: 'Showing {shown} of {total} characters',
            load_more: 'Show more',
            load_all: 'Show the rest',
            load_failed: 'The recorded source text could not be read.',
            no_recorded_source: 'No recorded source text is held for this framework. Its requirements and articles are on the other tabs.',
            composite: 'This framework groups other instruments and has no text of its own. Read the source of each of them:',
            superseded_draft: 'This consultation draft was later promulgated, and its own text is not held. Read the instrument that replaced it:',
            open_in_source: 'In source',
            open_in_source_aria: 'Show Article {article} in the full source',
            article_not_in_source: 'The recorded source does not carry Article {article}\u2019s heading, so it cannot be shown in place.',
        },
        states: {
            mapped: 'Implemented',
            no_duty_declared: 'No duty on the controller',
            not_yet_mapped: 'Not yet mapped',
        },
    },
    credit_information: {
        title: 'Credit-Information Uses',
        scope_note: 'Law 91/2025/QH15 Điều 27(1)(b) prohibits using a data subject’s credit information for credit scoring, credit rating, credit-information assessment or creditworthiness assessment unless that data subject has consented. Decree 356/2025/NĐ-CP Điều 8 details the Article: consent must state the purposes — including those four where they are carried out — the collection sources and sharing parties, the retention period and how consent may be withdrawn; compliance is assessed once every 01 year; and disclosure or loss of sensitive data in this sector is notified to the authority and the data subject within 72 hours.',
        boundary_heading: 'Scope and boundary — from your installed frameworks',
        boundary_frame: 'These statements come from the frameworks installed for your organisation, shown as each framework states them. This register records that a use took place, for which purpose, and the consent it rested on. The platform computes no credit score, produces no rating, assesses no creditworthiness and makes no lending decision. A use no consent covers is refused here rather than recorded with a warning beside it.',
        boundary_none: 'No installed framework declares a boundary statement for this register. This describes the content installed for your organisation, not your organisation’s scope.',
        boundary_load_error: 'The installed frameworks’ boundary statements could not be read, so none is shown. This does not mean that no framework declares one.',
        boundary_retry_subject: 'the boundary statements',
        purposes_load_failed: 'The purposes your installed frameworks declare could not be read, so a use cannot be recorded yet. This does not mean that none is declared.',
        purposes_retry_subject: 'the declared purposes',
        consent_check_timing: 'The consent is checked as it stands when you record the use, not as it stood at the time of use. A consent withdrawn, or given, between those two moments is judged as it is now.',
        kpi_label: 'Register summary',
        kpi_total: 'Total',
        kpi_total_hint: 'all uses recorded in this register',
        kpi_on_this_page: 'on this page',
        kpi_not_loaded: 'not loaded',
        uses: 'Recorded uses',
        record: 'Record a use',
        data_subject_ref: 'Data subject reference',
        information_description: 'What credit information was used',
        purpose: 'Purpose',
        used_at: 'Used at',
        consent_evidence_id: 'Consent evidence',
        consent_evidence_hint: 'The consent-evidence entry this use rests on. It is resolved and checked on the server — naming one that does not cover this purpose is refused.',
        consent_purpose_id: 'Which purpose on that consent',
        consent_purpose_hint: 'Which purpose named on that consent covers this use. Your organisation decides which of its own purposes corresponds; the platform checks that the one you name is genuinely on that consent.',
        consent_state: 'Consent',
        record_withdrawal: 'Record a withdrawal',
        withdrawn_at: 'Withdrawn on',
        no_purposes_installed: 'No framework installed for your organisation declares a credit-information purpose, so a use cannot be recorded against one here.',
        empty_uses: 'No credit-information use has been recorded yet.',
        load_failed: 'The credit-information register could not be read.',
        record_failed: 'The use could not be recorded.',
        recorded: 'Use recorded.',
        withdrawal_recorded: 'Withdrawal recorded.',
        withdrawal_failed: 'The withdrawal could not be recorded.',
        states: {
            consent_in_force: 'In force',
            consent_withdrawn: 'Withdrawn',
        },
    },
    legal_matters: {
        title: 'Legal Matters',
        scope_note: 'No installed instrument requires the controller to operate this register. Law 91/2025/QH15 Điều 4(1)(đ) declares that a data subject may complain and may denounce, and Decree 356/2025/NĐ-CP Điều 36(6) assigns the resolution of complaints and denunciations to the state authority — so a right is declared, and no duty to receive it falls on you. Running this register is a capability your organisation chooses, and any rhythm shown here is your own rather than a statutory deadline.',
        matters: 'Matters',
        receive: 'Record a matter',
        kind: 'Kind',
        origin: 'Rests on',
        right_code: 'Which right',
        raised_by: 'Raised by',
        subject: 'What it concerns',
        state_label: 'State',
        received_at: 'Received',
        acknowledged_at: 'Acknowledged',
        acknowledge: 'Record the acknowledgement',
        resolve: 'Record the conclusion',
        resolution_summary: 'What was concluded',
        concluded_at: 'Concluded on',
        not_acknowledged: 'Not yet acknowledged',
        // Sprint 63.31.0 (OD-E) — an intake handled in Legal Matters.
        intake_from: 'From rights request {reference}',
        right_not_recorded: 'The intake did not say which right it raises; a person records it.',
        record_right: 'Record the right',
        record_right_hint: 'Choose the right this matter rests on, from those the installed frameworks declare at the provision the intake was made under.',
        right_recorded: 'Right recorded',
        // fix-sprint-20261026-0900 Finding 6 — what a matter awaiting its right rests on,
        // the related-matters column, and the refusals of Record the right.
        rests_on_provision: 'At {basis} of {framework}',
        rests_on_provision_no_basis: 'Under {framework}; no provision is stated',
        related_matters: 'Related matters',
        no_related_matters: 'None',
        record_right_closed: 'This matter is closed, so the right it rests on can no longer be recorded.',
        record_right_not_at_provision: 'That right does not rest on the provision the intake was made under. Choose one of the rights offered.',
        record_right_not_declared: 'No installed framework declares that right. Choose another.',
        record_right_already_recorded: 'The right this matter rests on has already been recorded.',
        suggested_link: 'Possibly the same matter as “{subject}”: an earlier request gave the same email address, which is not verified.',
        suggested_link_open: 'Open that matter',
        suggested_link_accept: 'Same matter',
        suggested_link_dismiss: 'Not the same matter',
        suggested_link_accepted: 'A person recorded this as the same matter as “{subject}”.',
        suggested_link_dismissed: 'A person recorded this as not the same matter as “{subject}”.',
        suggested_link_decided: 'Decision recorded',
        linked_matter_not_found: 'The legal matter this link points to could not be read.',
        linked_matter_shown: 'The legal matter the link points to is shown first.',
        no_rights_installed: 'No framework installed for your organisation declares a right, so a matter cannot rest on one here.',
        rights_loading: 'Reading the rights your installed frameworks declare…',
        rights_load_failed: 'The rights your installed frameworks declare could not be read, so a matter cannot rest on one until they are.',
        rights_retry_subject: 'the declared rights',
        matters_retry_subject: 'the legal-matters register',
        loading: 'Loading the legal-matters register',
        right_placeholder: 'Choose the right',
        withdrawn_on_by: 'Withdrawn at {at}, recorded by {by}.',
        withdrawal_reason_not_recorded: 'No reason was recorded for this withdrawal.',
        timeline_acknowledged_before_received: 'The acknowledgement cannot be earlier than when the matter was received.',
        timeline_concluded_before_received: 'The conclusion cannot be earlier than when the matter was received.',
        timeline_concluded_before_acknowledged: 'The conclusion cannot be earlier than the acknowledgement.',
        kpi_row_label: 'Legal-matters summary',
        kpi_not_loaded: 'Not loaded',
        kpi_total: 'Matters',
        kpi_total_hint: 'in the register',
        kpi_on_this_page: 'on this page',
        withdraw: 'Record a withdrawal',
        withdrawal_reason: 'Why it was withdrawn',
        withdrawn: 'Withdrawal recorded.',
        empty_matters: 'No matter has been recorded yet.',
        load_failed: 'The legal-matters register could not be read.',
        create_failed: 'The record could not be saved.',
        created: 'Recorded.',
        acknowledged: 'Acknowledgement recorded.',
        resolved: 'Conclusion recorded.',
        kinds: {
            complaint: 'Complaint',
            denunciation: 'Denunciation',
            claim: 'Claim',
            litigation: 'Litigation',
            regulatory_penalty: 'Regulatory penalty',
            other: 'Other',
        },
        origins: {
            statutory_right: 'A right an installed framework declares',
            elective: 'A matter your organisation chose to track',
        },
        states: {
            received: 'Received',
            acknowledged: 'Acknowledged',
            resolved: 'Resolved',
            withdrawn: 'Withdrawn',
        },
        raised_by_options: {
            data_subject: 'Data subject',
            employee: 'Employee',
            regulator: 'Regulator',
            counterparty: 'Counterparty',
            other: 'Other',
        },
    },
    continuity: {
        title: 'Continuity Exercises',
        scope_note: 'No installed instrument requires continuity exercises. Law 91/2025/QH15 and Decree 356/2025/NĐ-CP were both read in full and neither imposes this control, so any rhythm shown here is your organisation’s own and never a statutory deadline. An exercise that raised improvement actions is not finished until each has been retested.',
        plans: 'Continuity plans',
        exercises: 'Exercises',
        plan_name: 'Plan name',
        plan_scope: 'Scope',
        plan_owner: 'Owner',
        scope_note_label: 'Say what it covers',
        create_plan: 'Add a plan',
        schedule: 'Schedule an exercise',
        kind: 'Kind',
        status_label: 'Status',
        outcome_label: 'Outcome',
        held_at: 'Held on',
        participants: 'Participants',
        open_loops: 'Improvement actions awaiting retest',
        no_open_loops: 'All retested',
        last_reviewed: 'Last reviewed',
        never_reviewed: 'Never reviewed',
        empty_plans: 'No continuity plan has been recorded yet.',
        empty_exercises: 'No exercise has been recorded yet.',
        complete: 'Complete the exercise',
        cancel: 'Cancel the exercise',
        cancel_reason: 'Why it was cancelled',
        completed: 'Exercise completed.',
        cancelled: 'Exercise cancelled.',
        retested: 'Retest recorded.',
        record_retest: 'Record a retest',
        retested_at: 'Retested on',
        retest_note: 'What the retest showed',
        participant_name: 'Who led it',
        participant_role: 'Their role',
        summary: 'What happened',
        improvement_action: 'Improvement action (optional)',
        action_owner: 'Action owner',
        load_failed: 'The continuity register could not be read.',
        create_failed: 'The record could not be saved.',
        created: 'Recorded.',
        retry_plans_subject: 'the continuity plans',
        retry_exercises_subject: 'the exercises',
        complete_for: 'Complete the exercise: {exercise}',
        cancel_for: 'Cancel the exercise: {exercise}',
        record_retest_for: 'Record a retest for {exercise}: {action}',
        completing_heading: 'Completing the exercise: {exercise}',
        cancelling_heading: 'Cancelling the exercise: {exercise}',
        retest_heading: 'Recording a retest for {exercise}: {action}',
        scopes: {
            systems: 'Systems',
            premises: 'Premises',
            people: 'People',
            suppliers: 'Suppliers',
            data_recovery: 'Data recovery',
            other: 'Other',
        },
        kinds: {
            tabletop: 'Tabletop',
            simulation: 'Simulation',
            live: 'Live',
            other: 'Other',
        },
        outcomes: {
            objectives_met: 'Objectives met',
            objectives_partially_met: 'Objectives partially met',
            objectives_not_met: 'Objectives not met',
            not_completed: 'Not completed',
        },
        statuses: {
            scheduled: 'Scheduled',
            completed: 'Completed',
            cancelled: 'Cancelled',
        },
    },
    /* Sprint 65.17.0 — de-identification runs (ANON-01). English text held for
     structural parity; real translation deferred to Phase 25 per R-57. */
    deidentification: {
        title: 'De-identification Runs',
        scope_note: 'Decree 356/2025/NĐ-CP Điều 10(6) requires that a data subject be able to rectify, anonymise and delete identification records in artificial-intelligence and metaverse systems. Outside that scope this is a control your organisation chooses to run. A run is not complete until somebody has judged what remains re-identifiable.',
        population: 'Population transformed',
        method: 'Method',
        method_note: 'Name the method',
        create: 'Record a run',
        created: 'De-identification run recorded.',
        empty: 'No de-identification run has been recorded yet.',
        status_label: 'Status',
        sample_label: 'Sample',
        residual_risk_label: 'Residual re-identification risk',
        load_failed: 'The de-identification runs could not be read.',
        create_failed: 'The run could not be recorded.',
        residual_risk_absent: 'Not yet judged',
        record_judgement: 'Record the sample and judgement',
        record_judgement_for: 'Record the sample and judgement for {population}',
        sample_outcome: 'Sample outcome',
        records_examined: 'Records examined',
        sample_finding: 'What the sample found',
        residual_risk_reasoning: 'Residual re-identification risk — your reasoning',
        residual_risk_hint: 'The platform derives no figure. What remains re-identifiable depends on data your organisation does not hold, so this is recorded as your judgement, against your account and the moment you save it.',
        updated: 'De-identification run updated.',
        update_failed: 'The run could not be updated.',
        approve_for: 'Approve the run for {population}',
        reject: 'Reject the run',
        reject_for: 'Reject the run for {population}',
        reject_heading_for: 'Rejecting the run for {population}',
        decision_note: 'Why the run was rejected',
        approved: 'De-identification run approved.',
        rejected: 'De-identification run rejected.',
        decide_failed: 'The decision could not be recorded.',
        total_runs: '{count} run(s) recorded',
        methods: {
            suppression: 'Suppression',
            generalisation: 'Generalisation',
            pseudonymisation: 'Pseudonymisation',
            aggregation: 'Aggregation',
            noise_addition: 'Noise addition',
            hashing_with_deleted_key: 'Hashing with the key deleted',
            other: 'Other',
        },
        sample_outcomes: {
            not_sampled: 'Not sampled',
            sample_clean: 'Sample found no identifiers',
            sample_found_identifiers: 'Sample found identifiers',
        },
        statuses: {
            draft: 'Draft',
            awaiting_approval: 'Awaiting approval',
            approved: 'Approved',
            rejected: 'Rejected',
        },
    },
    training: {
        title: 'Training & Awareness',
        subtitle: 'Assign training to named people and record each completion against the evidence that shows it happened.',
        module_guide: 'Define the training your organisation requires, assign it to named people with a deadline, and close each assignment against evidence — a completed form, an uploaded certificate or a recorded attestation. An assignment cannot be marked complete on its own; exempting someone is a decision recorded against the person who took it, with a reason. Completion rates here feed the training component of your audit-readiness score.',
        nav_assignments: 'Training & Awareness',
        new_course: 'New course',
        assign: 'Assign training',
        summary_open: 'Open',
        summary_completed: 'Completed',
        summary_overdue: 'Overdue',
        summary_exempted: 'Exempted',
        filters_label: 'Filter training assignments',
        filter_status: 'Status',
        filter_overdue_only: 'Overdue only',
        focused_heading: 'Assignment:',
        sr_loading: 'Loading training assignments',
        no_assignments: 'No training assignments match these filters.',
        table_label: 'Training assignments',
        col_course: 'Course',
        col_assignee: 'Assignee',
        col_due: 'Due',
        col_status: 'Status',
        col_evidence: 'Evidence',
        overdue_badge: 'Overdue',
        evidence_none: 'None recorded',
        exempted_by_label: 'Exempted by',
        cancelled_by_label: 'Withdrawn by',
        cancellation_reason_not_recorded: 'Withdrawn — no reason was stored',
        action_start: 'Start',
        action_complete: 'Record completion',
        action_exempt: 'Exempt',
        action_cancel: 'Withdraw',
        modal_course_title: 'New training course',
        field_course_title: 'Course title',
        field_course_description: 'Description',
        field_recurrence_months: 'Repeat every (months)',
        field_recurrence_hint: 'Leave empty for one-off training. This is an internal cadence, not a statutory deadline.',
        modal_assign_title: 'Assign training',
        field_course: 'Course',
        no_courses_hint: 'No active courses yet. Define a course before assigning training.',
        courses_heading: 'Courses',
        courses_table_label: 'Active training courses',
        no_courses: 'No active courses yet.',
        col_description: 'Description',
        col_recurrence: 'Repeats',
        recurrence_one_off: 'One-off',
        recurrence_every: 'Every {count} months',
        action_assign_course: 'Assign',
        field_assignees: 'People',
        field_assignees_hint: 'One user id per line, or separated by commas. Each person gets their own assignment and their own task.',
        field_due_at: 'Due date',
        modal_complete_title: 'Record completion',
        modal_complete_hint: 'A completion must cite the evidence that shows the training was done — a submitted form, an uploaded file or a recorded attestation. The assignment cannot be closed without it.',
        field_evidence_kind: 'Evidence type',
        field_evidence_reference: 'Evidence reference',
        modal_exempt_title: 'Exempt from training',
        modal_exempt_hint: 'An exemption is recorded against you, with the reason you give. It is never counted as a completion.',
        field_exemption_reason: 'Reason for the exemption',
        modal_cancel_title: 'Withdraw assignment',
        field_cancel_reason: 'Reason for withdrawing',
        toast_started: 'Training assignment started.',
        toast_course_created: 'Training course created.',
        toast_assigned: 'Training assigned.',
        toast_completed: 'Completion recorded.',
        toast_exempted: 'Exemption recorded.',
        toast_cancelled: 'Assignment withdrawn.',
        announce_loaded: '{count} training assignments loaded',
        statuses: {
            assigned: 'Assigned',
            in_progress: 'In progress',
            completed: 'Completed',
            exempted: 'Exempted',
            cancelled: 'Withdrawn',
        },
        evidence_kinds: {
            form_instance: 'Completed form',
            attachment: 'Uploaded file',
            attestation: 'Recorded attestation',
        },
    },
    task_management: {
        // Sprint 63.32.0 (#543) — the module a task came from, in the reader's language.
        source_modules: {
            rights_requests: 'Rights Requests',
            assessments: 'DPIA & Assessments',
            incidents: 'Incident Response',
            incident_ops: 'Incident Operations',
            monitoring: 'Monitoring Programs',
            vendor_risk: 'Vendor Risk',
            governance: 'Governance',
            deletion_orchestration: 'Deletion Orchestration',
            access_review: 'Access Reviews',
            consent: 'Consent Governance',
            consent_properties: 'Consent Properties',
            forms: 'Compliance Forms',
            localization: 'Localization',
            program_governance: 'Program Governance',
            privacy_scanner: 'Privacy Scanner',
            classification: 'Data Classification',
            discovery: 'Data Discovery',
            data_mapping: 'Data Mapping',
            compliance_operations: 'Compliance Work Programme',
            training: 'Training',
            privacy_office: 'Privacy Office',
            authority_operations: 'Authority Operations',
            ai_governance: 'AI Governance',
            regulatory_classification: 'Regulatory Classification',
            org_settings: 'Organization Profile',
            customs_tax_administration: 'Customs Tax Administration',
            service_certificates: 'Processing Service Certificate',
            manual: 'Manual',
        },
        nav: {
            calendar: 'Kalenteri',
            all_tasks: 'Kaikki tehtävät',
            my_tasks: 'Omat tehtävät',
            overdue: 'Myöhässä',
            board: 'Taulu',
            workflows: 'Työnkulut',
        },
        list: {
            title: 'Kaikki tehtävät',
            my_title: 'Omat tehtävät',
            overdue_title: 'Myöhässä olevat tehtävät',
            new_task: 'Luo tehtävä',
            no_tasks: 'Tehtäviä ei löytynyt.',
            empty_state: 'Ei tehtäviä. Tehtävät luodaan moduulien työnkuluista, kuten arvioinneista, poikkeamista ja tarkistuskampanjoista.',
            total: 'Tehtäviä yhteensä',
            search_placeholder: 'Hae tehtäviä...',
            bulk_actions: 'Massatoiminnot',
            change_status: 'Vaihda tila',
            change_priority: 'Vaihda prioriteetti',
            assign: 'Määritä',
            sr_my_tasks_count: '{count} tehtävää sinulle',
            sr_days_overdue: '{count} päivää myöhässä',
            sla_label: 'SLA',
        },
        detail: {
            workflow_step: 'Workflow step',
            title: 'Tehtävän tiedot',
            description: 'Kuvaus',
            workflow_progress: 'Työnkulun edistyminen',
            checklist: 'Tarkistuslista',
            dependencies: 'Riippuvuudet',
            comments: 'Kommentit',
            activity_timeline: 'Toiminnan aikajana',
            assignees: 'Vastuuhenkilöt',
            add_assignee: 'Lisää vastuuhenkilö',
            remove_assignee: 'Poista vastuuhenkilö',
            due_date: 'Eräpäivä',
            start_date: 'Aloituspäivä',
            sla_status: 'SLA-tila',
            sla_on_track: 'Aikataulussa',
            sla_at_risk: 'Vaarassa',
            sla_breached: 'Rikottu',
            tags: 'Tunnisteet',
            source_entity: 'Lähde-entiteetti',
            view_source: 'Näytä lähde',
            add_dependency: 'Lisää riippuvuus',
            remove_dependency: 'Poista riippuvuus',
            write_comment: 'Kirjoita kommentti...',
            post_comment: 'Lähetä',
            edit_comment: 'Muokkaa',
            delete_comment: 'Poista',
            add_checklist_item: 'Lisää kohde',
            no_dependencies: 'Ei riippuvuuksia',
            no_comments: 'Ei kommentteja vielä',
            blocks: 'Estää',
            blocked_by: 'Estetty',
            related: 'Liittyvä',
            working_days_remain: 'työpäivää jäljellä',
            created_by: 'Luonut',
            completed_at: 'Valmistunut',
            remove_assignee_aria: 'Remove assignee: {name}',
            // Sprint 63.32.0 (#543) — the seeded role names an assignment can carry.
            assignee_role_labels: {
                platform_admin: 'Platform Admin',
                compliance_owner: 'Compliance Owner',
                case_manager: 'Case Manager',
                auditor: 'Auditor',
                approver: 'Approver',
                partner_external_counsel: 'External Counsel (Partner)',
                processor_liaison: 'Processor Liaison',
            },
            remove_dependency_aria: 'Remove dependency: {name}',
            no_assignees: 'Ei vastuuhenkilöitä',
            add_assignee_placeholder: 'Syötä käyttäjätunnus...',
            edited: 'muokattu',
            timeline: 'Toiminnan aikajana',
            no_timeline: 'Ei toimintaa vielä',
            timeline_reason: 'Reason',
            no_workflow: 'Ei työnkulkua määritettynä',
            add_dependency_placeholder: 'Syötä tehtävätunnus...',
            sr_status_updated: 'Tila päivitetty {status}',
            sr_priority_updated: 'Prioriteetti päivitetty arvoon {priority}',
            // Sprint 63.30.0 (#590, #574) — due date edit and Escalate.
            save_due_date: 'Save due date',
            due_date_saved: 'Due date set to {date}',
            due_date_save_failed: 'The due date could not be saved.',
            escalate: 'Escalate',
            escalate_title: 'Escalate this task',
            escalate_description: 'Escalating moves the task to Escalated. Your reason is recorded with the status change and shown beside it in the Activity Timeline.',
            escalate_reason: 'Reason for escalating',
            escalate_reason_required: 'Enter a reason for escalating.',
            escalating: 'Escalating...',
            sr_escalated: 'Task escalated',
            escalate_failed: 'The task could not be escalated.',
            convert_to_workflow: 'Suorita työnkulkuna',
            convert_modal_title: 'Muunna tehtävä työnkulkuajoksi',
            convert_workflow_label: 'Työnkulku',
            convert_workflow_placeholder: 'Valitse työnkulku…',
            convert_attachments_label: 'Liitteet',
            convert_attachments_empty: 'Ei liitteitä tähän tehtävään.',
            convert_persona_label: 'Personan ohitus (valinnainen)',
            convert_submit: 'Aloita työnkulun ajo',
            convert_starting: 'Aloitetaan työnkulkua…',
            convert_started: 'Työnkulkuajo aloitettu – avausajo…',
            convert_disabled_in_flight: 'Tästä tehtävästä suoritettava työnkulku on jo käynnissä.',
            linked_workflow_chip: 'Lähettäjä {workflow_title}',
            linked_chat_chip: 'Chatista AI',
            linked_more: '+{count} lisää',
        },
        calendar: {
            title: 'Tehtäväkalenteri',
            page_title: 'Kalenteri',
            create_task: 'Luo tehtävä',
            month: 'Kuukausi',
            week: 'Viikko',
            day: 'Päivä',
            today: 'Tänään',
            more_tasks: '+{count} lisää',
            no_tasks_day: 'Ei tehtäviä tänä päivänä',
            all_day: 'Koko päivä',
            quick_create_tooltip: 'Luo tehtävä tälle päivälle',
            view_changed: 'Kalenterinäkymä vaihdettu: {view}',
            tasks_in_view: '{count} tehtävää näkymässä',
            task_in_view: '{count} tehtävä näkyvissä',
            filter_my_tasks: 'Omat tehtävät',
            filter_overdue: 'Myöhässä',
            filter_high_priority: 'Korkea prioriteetti',
            filter_all: 'Kaikki',
        },
        filters: {
            assignee: 'Vastuuhenkilö',
            me: 'Minä',
            role: 'Rooli',
            status: 'Tila',
            priority: 'Prioriteetti',
            task_type: 'Tehtävätyyppi',
            source_module: 'Lähdemoduuli',
            organization: 'Organisaatio',
            sla_breached_only: 'Näytä vain SLA-rikotut',
            clear_filters: 'Tyhjennä suodattimet',
            apply_filters: 'Käytä',
            source_label: 'lähde',
            source_manual: 'Manuaalinen',
            source_module_kind: 'Moduuli luotu',
            source_chat: 'Chatista AI',
            source_workflow_run: 'Alkaen AI Workflow Run',
        },
        status: {
            pending: 'Odottava',
            in_progress: 'Käynnissä',
            blocked: 'Estetty',
            completed: 'Valmis',
            overdue: 'Myöhässä',
            escalated: 'Eskaloitu',
            cancelled: 'Peruutettu',
        },
        priority: {
            critical: 'Kriittinen',
            high: 'Korkea',
            medium: 'Keskitaso',
            low: 'Matala',
        },
        task_types: {
            manual_task: 'Manuaalinen tehtävä',
            dsr_case: 'DSR-tapaus',
            dsr_review: 'DSR-tarkistus',
            dsr_escalation: 'DSR-eskalointi',
            dpia_review: 'DPIA-tarkistus',
            dpia_approval: 'DPIA-hyväksyntä',
            transfer_review: 'Siirtotarkistus',
            incident_task: 'Poikkeamatehtävä',
            incident_notification: 'Poikkeamailmoitus',
            playbook_step: 'Ohjekirjan vaihe',
            regulator_deadline: 'Viranomaisaikamäärä',
            monitoring_task: 'Valvontatehtävä',
            kpi_alert: 'KPI-hälytys',
            vendor_review: 'Toimittajatarkistus',
            vendor_questionnaire: 'Toimittajakysely',
            governance_review: 'Hallintotarkistus',
            drift_alert: 'Poikkeamahälytys',
            governance_approval: 'Hallintohyväksyntä',
            deletion_task: 'Poistotehtävä',
            deletion_verification: 'Poistovarmistus',
            access_review: 'Pääsytarkistus',
            sod_alert: 'SoD-hälytys',
            consent_workflow: 'Suostumustyönkulku',
            consent_alert: 'Suostumushälytys',
            property_review: 'Ominaisuustarkistus',
            inconsistency_alert: 'Epäjohdonmukaisuushälytys',
            form_review: 'Lomaketarkistus',
            dossier_deadline: 'Kansioaikamäärä',
            submission_task: 'Lähetystehtävä',
            translation_review: 'Käännöstarkistus',
            governance_task: 'Hallintotehtävä',
            reporting_deadline: 'Raportointiaikamäärä',
            scanner_alert: 'Skannerihälytys',
            classification_review: 'Luokittelutarkistus',
            lifecycle_task: 'Elinkaaritehtävä',
            discovery_review: 'Löytämistarkistus',
            forseti_task: 'Forseti Task',
        },
        training_assignment: 'Koulutustehtävä',
        task_type_groups: {
            training: 'Koulutus',
            manual: 'Manuaalinen',
            dsr: 'DSR / Rekisteröidyn oikeudet',
            assessments: 'Arvioinnit',
            incidents: 'Poikkeamat',
            incident_ops: 'Poikkeamatoiminnot',
            monitoring: 'Valvonta',
            vendor_risk: 'Toimittajariski',
            governance: 'Hallinto',
            deletion: 'Poisto',
            access_review: 'Pääsytarkistus',
            consent: 'Suostumus',
            consent_properties: 'Suostumuksen ominaisuudet',
            forms: 'Lomakkeet',
            localization: 'Lokalisointi',
            program_governance: 'Ohjelmahallinto',
            privacy_scanner: 'Tietosuojaskanneri',
            classification: 'Luokittelu',
            discovery: 'Löytäminen',
            forseti: 'Forseti',
        },
        create: {
            title: 'Luo tehtävä',
            task_title: 'Otsikko',
            task_description: 'Kuvaus',
            task_type: 'Tehtävätyyppi',
            task_type_label: 'Tehtävätyyppi',
            task_type_placeholder: 'Valitse tehtävätyyppi',
            priority: 'Prioriteetti',
            assignees: 'Vastuuhenkilöt',
            assignee_search_placeholder: 'Hae käyttäjiä...',
            assignee_search_no_results: 'No users found.',
            assignee_search_loading: 'Loading…',
            tags_placeholder: 'tag1, tag2',
            remove_assignee: 'Poista {name}',
            due_date: 'Eräpäivä',
            start_date: 'Aloituspäivä',
            checklist: 'Tarkistuslista',
            tags: 'Tunnisteet',
            add_checklist_item: 'Lisää tarkistuslistakohde',
            validation: {
                title_required: 'Otsikko on pakollinen',
                title_max_length: 'Otsikko saa olla enintään 500 merkkiä',
                description_max_length: 'Kuvaus saa olla enintään 5000 merkkiä',
                due_date_before_start: 'Eräpäivän on oltava aloituspäivän jälkeen',
                max_reminders: 'Enintään 5 muistutusta tehtävää kohti',
            },
            reminders: {
                title: 'Muistutukset',
                add: 'Lisää muistutus',
                at_due_date: 'Eräpäivänä',
                minutes_before: '{count} minuuttia ennen',
                hours_before: '{count} tuntia ennen',
                days_before: '{count} päivää ennen',
                weeks_before: '{count} viikkoa ennen',
                custom: 'Mukautettu',
                channel_platform: 'Alusta',
                channel_email: 'Sähköposti',
                channel_both: 'Molemmat',
                offset_label: 'Muistutus {n}: ajankohta',
                channel_label: 'Muistutus {n}: kanava',
                remove_label: 'Poista muistutus {n}',
            },
            workflow: {
                label: 'Työnkulku',
                none: 'Ei mitään (manuaalinen tilanhallinta)',
                steps_count: '{count} askelta',
                preview_label: 'Työnkulun vaiheet',
            },
        },
        errors: {
            fetch_failed: 'Tehtävien lataaminen epäonnistui.',
            save_failed: 'Tehtävän tallentaminen epäonnistui.',
            delete_failed: 'Tehtävän poistaminen epäonnistui.',
            status_change_failed: 'Tehtävän tilan vaihtaminen epäonnistui.',
            assign_failed: 'Tehtävän määrittäminen epäonnistui.',
            comment_failed: 'Kommentin tallentaminen epäonnistui.',
            checklist_failed: 'Tarkistuslistan päivittäminen epäonnistui.',
            dependency_failed: 'Riippuvuuden päivittäminen epäonnistui.',
            workflow_failed: 'Työnkulun tallentaminen epäonnistui.',
            workflow_activate_failed: 'Työnkulun aktivointi epäonnistui.',
            workflow_delete_failed: 'Työnkulun poistaminen epäonnistui.',
            template_install_failed: 'Mallin asentaminen epäonnistui.',
            search_failed: 'Tehtävien hakeminen epäonnistui.',
            convert_failed: 'Työnkulkua ei voitu käynnistää.',
            convert_quota_exhausted: 'Token bucket alhainen. Täytä ennen uudelleenkäynnistystä.',
        },
        dependency_types: {
            blocks: 'Estää',
            blocked_by: 'Estetty',
            related: 'Liittyvä',
        },
        workflow: {
            current_step: 'Nykyinen vaihe',
            transition: 'Siirtymä',
            history: 'Vaihehistoria',
            no_workflow: 'Ei työnkulkua määritettynä',
            detail_title: 'Työnkulun tiedot',
            list_title: 'Työnkulkumääritykset',
            create_workflow: 'Luo työnkulku',
            browse_templates: 'Selaa malleja',
            template_catalog_title: 'Työnkulkumallikatalogi',
            name: 'Nimi',
            description: 'Kuvaus',
            task_types: 'Tehtävätyypit',
            steps: 'Vaiheet',
            version: 'Versio',
            created: 'Luotu',
            actions: 'Toiminnot',
            status_draft: 'Luonnos',
            status_active: 'Aktiivinen',
            status_archived: 'Arkistoitu',
            activate: 'Aktivoi',
            archive: 'Arkistoi',
            save: 'Tallenna',
            edit: 'Muokkaa',
            delete: 'Poista',
            clone: 'Kopioi',
            install: 'Asenna',
            installed: 'Asennettu',
            system: 'Järjestelmä',
            general_settings: 'Yleiset asetukset',
            default_assignment: 'Oletustoimeksianto',
            default_sla: 'Oletus-SLA',
            workflow_steps: 'Työnkulun vaiheet',
            add_step: 'Lisää vaihe',
            step_name: 'Vaiheen nimi',
            step_status: 'Tila-arvo',
            assignment_strategy: 'Toimeksiantostrategia',
            assignment_role: 'Toimeksiantorooli',
            sla_hours: 'SLA-tunnit',
            sla_calendar_type: 'Kalenterityyppi',
            working_days: 'Työpäivät',
            calendar_days: 'Kalenteripäivät',
            is_terminal: 'Päätösvaihe',
            required_fields: 'Pakolliset kentät',
            checklist_template: 'Tarkistuslistamalli',
            transitions: 'Siirtymät',
            add_transition: 'Lisää siirtymä',
            from_step: 'Mistä',
            to_step: 'Mihin',
            permission: 'Oikeus',
            sla_action: 'SLA-toiminto',
            notify: 'Ilmoita',
            escalation_rules: 'Eskalointisäännöt',
            add_rule: 'Lisää sääntö',
            trigger: 'Laukaisin',
            threshold: 'Kynnysarvo',
            action: 'Toiminto',
            target_role: 'Kohderooli',
            trigger_sla_threshold: 'SLA-kynnysarvo',
            trigger_sla_breach: 'SLA-rikkomus',
            trigger_blocked_duration: 'Estetty kesto',
            trigger_manual: 'Manuaalinen',
            action_notify: 'Ilmoita',
            action_escalate: 'Eskaloi taso',
            action_reassign: 'Uudelleenmääritä',
            sla_start: 'Aloita',
            sla_pause: 'Keskeytä',
            sla_resume: 'Jatka',
            sla_stop: 'Lopeta',
            strategy_manual: 'Manuaalinen',
            strategy_role_based: 'Roolipohjainen',
            strategy_round_robin: 'Round Robin',
            strategy_least_loaded: 'Vähiten kuormitettu',
            strategy_creator: 'Luoja',
            confirm_delete: 'Haluatko varmasti poistaa tämän työnkulun?',
            confirm_archive: 'Haluatko varmasti arkistoida tämän työnkulun?',
            cannot_delete_active: 'Aktiivisia työnkulkuja ei voi poistaa. Arkistoi ensin.',
            validation_errors: 'Työnkulun validointivirheet',
            step_count: '{count} askelta',
            no_workflows: 'Työnkulkuja ei löytynyt.',
            no_templates: 'Malleja ei saatavilla.',
            sr_templates_count: '{count} malleja saatavilla',
            template_installed: 'Malli asennettu onnistuneesti.',
            activate_success: 'Työnkulku aktivoitu onnistuneesti.',
            archive_success: 'Työnkulku arkistoitu onnistuneesti.',
            save_success: 'Työnkulku tallennettu onnistuneesti.',
            delete_success: 'Työnkulku poistettu onnistuneesti.',
            applicable_task_types: 'Soveltuvat tehtävätyypit',
            steps_column: 'Vaiheet',
            status_value_hint: 'Työnkulun tila-avain, jota siirtymät käyttävät. Pienet kirjaimet, numerot ja alaviivat.',
            from_status: 'Lähtötila',
            to_status: 'Kohdetila',
            required_permissions: 'Vaaditut oikeudet',
            transition_required_fields: 'Pakolliset kentät (siirtymä)',
            auto_assign: 'Automaattinen määritys',
            auto_assign_role: 'Automaattisen määrityksen rooli',
            threshold_percent: 'Kynnys (%)',
            blocked_hours: 'Estetyt tunnit',
            notify_roles: 'Ilmoitettavat roolit',
            escalate_to_role: 'Eskaloi roolille',
            notification_template: 'Ilmoituspohja',
            action_escalate_tier: 'Eskaloi tasoa',
            create_modal_title: 'Luo työnkulku',
            create_modal_description: 'Määritä vaiheet, siirtymät ja eskalointisäännöt. Työnkulku luodaan luonnoksena.',
            create_modal_footer_note: 'Luodaan luonnoksena. Aktivoi se, kun kaavio on validi.',
            validation_name_required: 'Työnkulun nimi vaaditaan.',
            validation_task_types_required: 'Valitse vähintään yksi soveltuva tehtävätyyppi.',
            validation_min_one_step: 'Lisää vähintään yksi vaihe.',
            validation_min_one_transition: 'Lisää vähintään yksi siirtymä.',
            validation_step_name_required: 'Vaiheen nimi vaaditaan.',
            validation_status_value_required: 'Tila-arvo vaaditaan.',
            validation_status_value_duplicate: 'Tila-arvon on oltava yksilöllinen vaiheiden välillä.',
            validation_transition_endpoints_required: 'Valitse sekä lähtö- että kohdetila.',
            validation_transition_unknown_status: 'Tämä siirtymä viittaa tilaan, jota mikään vaihe ei määritä.',
            validation_sla_positive: 'SLA-tuntien on oltava suurempi kuin nolla. Jätä tyhjäksi, jos ei SLA:ta.',
            validation_threshold_percent_range: 'Kynnyksen on oltava välillä 0–100.',
            validation_blocked_hours_positive: 'Estettyjen tuntien on oltava suurempi kuin nolla.',
            warning_no_terminal_step: 'Ei päätevaihetta. Aktivointi epäonnistuu, kunnes yksi vaihe on merkitty päätevaiheeksi.',
            warning_step_no_outgoing: 'Ei-päätevaiheet ilman lähteviä siirtymiä: {statuses}',
            warning_unreachable_steps: 'Vaiheet, joita ei voi saavuttaa ensimmäisestä vaiheesta: {statuses}',
            orphaned_transitions_warning: '{count} siirtymää viittaa poistettuun vaiheeseen.',
            remove_orphaned_transitions: 'Poista orvot siirtymät',
            transitions_renamed_announcement: '{count} siirtymää päivitetty uuteen tila-arvoon.',
            add_checklist_item: 'Lisää tarkistuslistan kohta',
            checklist_item_label: 'Tarkistuslistan kohta',
        },
        columns: {
            priority: 'Prioriteetti',
            title: 'Otsikko',
            status: 'Tila',
            type: 'Tyyppi',
            assignees: 'Vastuuhenkilö(t)',
            due_date: 'Eräpäivä',
            source: 'Lähde',
            actions: 'Toiminnot',
        },
        groups: {
            overdue: 'Myöhässä',
            due_today: 'Erääntyy tänään',
            due_this_week: 'Erääntyy tällä viikolla',
            due_later: 'Erääntyy myöhemmin',
            no_due_date: 'Ei eräpäivää',
            closed: 'Valmiit tai peruutetut',
        },
        board: {
            title: 'Tehtävätaulu',
            drag_hint: 'Vedä tehtäviä sarakkeiden välillä tilan vaihtamiseksi',
            empty_column: 'Ei tehtäviä',
            keyboard_hint: 'Näppäimistö: nuolinäppäimet navigointiin, välilyönti poimii tehtävän, vasen/oikea nuoli valitsee sarakkeen, Enter pudottaa, Escape peruuttaa.',
            sr_moving: 'Siirretään {title}. Kohdassa {column}. Paina Enter pudottaaksesi, Escape peruuttaaksesi.',
            sr_tasks_shown: '{count} tehtävää näytetty',
            sr_picked_up: '{title} poimittu',
            sr_returned: '{title} palasi {column}',
            sr_dropped: '{title} pudotettu sarakkeeseen {column}, sijainti {position}/{total}',
            sr_move_cancelled: 'Siirto peruutettu',
            sr_over_column: 'Kohdassa {column}',
            sr_moved_to: '{title} siirretty sarakkeeseen {column}',
            invalid_transition: 'Tehtavaa ei voi siirtaa tilasta {from} tilaan {to}.',
            terminal_status: 'Tama tehtava on paatostilassa eika sita voi siirtaa.',
            invalid_drop_target: 'Virheellinen pudotuskohde',
        },
        team: {
            title: 'Tiimin työkuorma',
            assignee: 'Vastuuhenkilö',
            active: 'Aktiivinen',
            overdue: 'Myöhässä',
            blocked: 'Estetty',
            completed_7d: 'Valmis (7p)',
            load: 'Kuorma',
            load_high: 'Korkea',
            load_medium: 'Keskitaso',
            load_normal: 'Normaali',
        },
        graph: {
            title: 'Riippuvuuskaavio',
            list_view: 'Luettelonäkymä',
            graph_view: 'Kaavionäkymä',
            zoom_in: 'Lähennä',
            zoom_out: 'Loitonna',
            reset_zoom: 'Nollaa',
            blocks_label: 'blocks',
            related_label: 'related',
            no_dependencies: 'Ei visualisoitavia riippuvuuksia.',
        },
        add_dependency_modal: {
            title: 'Lisää riippuvuus',
            search_placeholder: 'Hae tehtäviä otsikon tai tunnuksen perusteella...',
            no_results: 'Tehtäviä ei löytynyt.',
            this_blocks: 'Tämä tehtävä estää valitut',
            this_blocked_by: 'Valittu on estänyt tämän tehtävän',
            related: 'Liittyvä',
            max_dependencies: 'Enintään 10 riippuvuutta tehtävää kohden.',
            circular_error: 'Ei voi lisätä: luo pyöreän riippuvuuden.',
        },
        conflict_detection: {
            title: 'Ristiriitojen tunnistus',
            unresolved: 'ratkaisematon',
            no_conflicts: 'Ei ristiriitoja havaittu',
            section: 'Osio',
            field: 'Kenttä',
            dept_a: 'Osasto A',
            value_a: 'Arvo A',
            dept_b: 'Osasto B',
            value_b: 'Arvo B',
            severity: 'Vakavuus',
            status: 'Tila',
            resolved: 'Ratkaistu',
            open: 'Avoin',
            resolve: 'Ratkaise',
            load_failed: 'load conflicts epäonnistui',
            resolve_success: 'Ristiriita ratkaistu onnistuneesti',
            resolve_failed: 'resolve conflict epäonnistui',
            severity_critical: 'Kriittinen',
            severity_warning: 'Varoitus',
            severity_info: 'Tietoja',
        },
        consolidation: {
            title: 'Konsolidoinnin eteneminen',
            add_child: 'Lisää alatehtävä',
            child_task_id: 'Alatehtävän tunnus',
            department_id: 'Osaston tunnus',
            link: 'Linkitä',
            overall_completion: 'Kokonaisvalmistuminen',
            departments_complete: 'Osastot valmiit',
            overdue: 'Myöhässä',
            ready: 'Valmis',
            not_ready: 'Ei valmis',
            master_status: 'Päätila',
            department_breakdown: 'Osastokohtainen erittely',
            department: 'Osasto',
            status: 'Tila',
            completion: 'Valmistuminen',
            due_date: 'Eräpäivä',
            overdue_flag: 'Myöhässä',
            child_tasks: 'Alatehtävät',
            task: 'Tehtävä',
            load_failed: 'load consolidation data epäonnistui',
            child_linked: 'Alatehtävä linkitetty onnistuneesti',
            link_failed: 'link child task epäonnistui',
            confirm_unlink: 'Haluatko varmasti poistaa tämän lapsitehtävän linkityksen?',
            child_unlinked: 'Lapsitehtävän linkitys poistettu',
            unlink_failed: 'Alatason tehtävän linkityksen poistaminen epäonnistui',
            unlink: 'Poista linkitys',
            status_completed: 'Valmis',
            status_in_progress: 'Käynnissä',
            status_pending: 'Odottaa',
            select_child_task: 'Valitse alitehtävä',
            select_department: 'Valitse osasto',
        },
        section_assignments: {
            title: 'Osiokohdistukset',
            assign_section: 'Kohdista osio',
            section_ref: 'Osion viite',
            section_label: 'Osion tunniste',
            department_id: 'Osaston tunnus',
            department: 'osasto',
            assignee_id: 'Vastaanottajan tunnus',
            assignee: 'Valtuutettu',
            mandatory: 'Pakollinen',
            assign: 'Kohdista',
            no_assignments: 'Ei osiokohdistuksia',
            status: 'Tila',
            completion: 'Valmistuminen',
            mark_complete: 'Valmis',
            load_failed: 'load section assignments epäonnistui',
            assign_success: 'Osio kohdistettu onnistuneesti',
            assign_failed: 'assign section epäonnistui',
            status_updated: 'Tila päivitetty',
            status_update_failed: 'Tilan päivittäminen epäonnistui',
            section_ref_placeholder: 'esim. 3.2',
            status_completed: 'Valmis',
            status_in_progress: 'Meneillään',
            status_pending: 'Odottaa',
            select_department: 'Valitse osasto',
            select_assignee: 'Valitse valtuutettu',
        },
        overdue_columns: {
            days_overdue: 'Paivaa myohassa',
        },
        regulatory_info: {
            deadline_context: 'Luotaessa DPIA-tehtavia eraapaivien tulisi vastata PDPL-jaettoaikoja: osastosyote = 10 tyopaivaa. Kyberturvallisuus- ja televiestintamaeraaajat ovat myos voimassa.',
            overdue_deadline_context: 'Saantelymaeraaajat pakettidatasta: PDPL osastosyote = 10 tyopaivaa, lisavastaus = 15 tyopaivaa, uudelleenlahetys = 10 tyopaivaa. SLA-rikkomukset korostetaan visuaalisesti.',
            section_assignment_context: 'DPIA monen osaston tyonkulku (PDPL-P1): DPO osoittaa asiakirjaosiot osastoille. Osastosyoteen maeraaika: 10 tyopaivaa.',
            template_catalog_context: 'Jarjestelmamallit kattavat kehyskohtaiset prosessit: PDPL DPIA, tietolain todistus, kyberturvallisuusvastaus, televiestintaviranomaispyynnot ja AI-rekisterointi. Jarjestelmamallit eivat ole muokattavissa.',
            workflow_instantiation_context: 'Asennetut mallit voidaan luoda aktiivisiksi tyonkuluiksi. Tehtavat luodaan automaattisesti lasketuilla eraapaivilla.',
        },
        overdue: {
            days_overdue: 'päivää myöhässä',
        },
    },
    notifications: {
        bell: {
            title: 'Ilmoitukset',
            unread_count: 'unread',
        },
        drawer: {
            title: 'Ilmoitukset',
            mark_all_read: 'Merkitse kaikki luetuiksi',
            no_notifications: 'Ei ilmoituksia',
            filter: {
                all: 'All',
                unread: 'Lukematon',
                tasks: 'Tehtävät',
                approvals: 'Hyväksynnät',
                alerts: 'Hälytykset',
            },
        },
        preferences: {
            title: 'Ilmoitusasetukset',
            description: 'Määritä, miten saat ilmoituksia',
            category: {
                label: 'Luokka',
                task_assigned: 'Tehtävä määrätty',
                task_sla_approaching: 'SLA Lähestymme',
                task_overdue: 'Tehtävä myöhässä',
                task_completed: 'Tehtävä suoritettu',
                task_escalated: 'Tehtävä Eskaloitu',
                workflow_transition: 'Työnkulun siirtyminen',
                approval_requested: 'Hyväksyntää pyydetty',
                comment_added: 'Kommentti lisätty',
                weekly_digest: 'Viikoittainen tiivistelmä',
                sla_breach_report: 'SLA Rikkomusraportti',
                system_alert: 'Järjestelmän hälytys',
                // English until Phase 25 (R-57).
                rights_request_received: 'New Rights Request',
                // English until Phase 25 (R-57).
                acknowledgment_reminder: 'Acknowledgment Reminder',
                approval_expired: 'Approval Request Expired',
                scheduled_export_delivered: 'Scheduled Export Delivered',
                cross_border_transfer_added: 'Cross-Border Transfer Added',
            },
            channel: {
                platform: 'Alusta',
                email: 'S?hk?posti',
            },
            quiet_hours: {
                title: 'Hiljaiset tunnit',
                start: 'Aloita',
                end: 'End',
                timezone: 'Aikavyöhyke',
            },
            digest: {
                title: 'Digest Frequency',
                daily: 'Päivittäin',
                weekly: 'viikoittain',
                none: 'Ei mitään',
            },
            watched: {
                title: 'Katsotut kokonaisuudet',
                empty: 'Ei katsottuja kokonaisuuksia',
            },
            save: 'Tallenna asetukset',
            saving: 'Tallennetaan...',
            saved_success: 'Asetukset tallennettu onnistuneesti',
            load_error: 'Ei voitu ladata tallennettuja asetuksia. Näytetään oletukset – muutokset tallennetaan, kun napsautat Tallenna.',
            enabled: 'Käytössä',
            disabled: 'Ei käytössä',
            unwatch: 'Poista katselu',
            announce_loaded: 'Notification preferences loaded',
            save_error: 'Could not save your notification preferences. Your changes are still on the page; try saving again.',
        },
        admin_queue: {
            title: 'Ilmoitusten hallintajono',
            description: 'Seuraa sähköpostin toimitustilaa ja yritä epäonnistuneita viestejä uudelleen',
            load_error: 'Sähköpostijonon tietojen lataaminen epäonnistui',
            retry_error: 'Sähköpostin uudelleenyritys epäonnistui',
            filter_status: 'Suodata tilan mukaan',
            all_statuses: 'Kaikki',
            refresh: 'Päivitä',
            status_queued: 'Jonossa',
            status_sent: 'Lähetetty',
            status_failed: 'Epäonnistunut',
            status_bounced: 'Palautunut',
            status_sending: 'Sending',
            col_recipient: 'Vastaanottaja',
            col_category: 'Kategoria',
            col_template: 'Malli',
            col_status: 'Tila',
            col_retries: 'Uudelleenyritykset',
            col_created: 'Luotu',
            col_sent: 'Lähetetty',
            col_actions: 'Toiminnot',
            no_items: 'Sähköpostijonossa ei ole kohteita',
            retry: 'Yritä uudelleen',
            retrying: 'Yritetään uudelleen...',
            prev: 'Edellinen',
            next: 'Seuraava',
            access_denied_title: 'Järjestelmänvalvojan käyttöoikeus vaaditaan',
            access_denied: 'Tämä sivu on käytettävissä vain järjestelmänvalvojille. Ota yhteyttä järjestelmänvalvojaasi, jos tarvitset käyttöoikeuden.',
            announce_queue_loaded: 'Sähköpostijono ladattu, {count} kohdetta',
            announce_processed: 'Käsitelty {count} sähköposti(ta)',
            process_now: 'Käsittele nyt',
            processing_ellipsis: 'Käsitellään…',
        },
    },
    ui_assist: {
        toolbar: {
            title: 'UI Assist',
            activate: 'Aktivoi',
            deactivate: 'Poista käytöstä',
            connected: 'Yhdistetty',
            disconnected: 'Yhteys katkaistu',
            disconnected_hint: "Yhteys katkaistu — suorita `npm run relay' muodostaaksesi yhteyden Claude CLI:hen",
            open_panel: 'Avaa UI Assist -paneeli',
            history: 'Historia',
            settings: 'Asetukset',
        },
        prompt: {
            placeholder: 'Kuvaile muutos, jonka haluat tehdä…',
            send: 'Lähetä',
            cancel: 'Peruuta',
            char_count: '{count} / {max}',
        },
        session: {
            streaming: 'Käsitellään…',
            complete: 'Täydellinen',
            error: 'Virhe',
            abort: 'Keskeytä',
            abort_confirm: 'Oletko varma, että haluat keskeyttää?',
            aborted_hint: 'Istunto keskeytettiin — voit jatkaa tästä.',
            undo: 'Kumoa',
            redo: 'Toista',
            retry: 'Yritä uudelleen',
            dismiss: 'Hylkää',
        },
        label: {
            component: 'Komponentti',
            element: 'Elementti',
            file: 'Tiedosto',
        },
        response: {
            you: 'You',
            claude: 'Claude',
            empty: 'Ei viestejä vielä. Valitse elementit ja lähetä kehote aloittaaksesi.',
        },
        panel: {
            tab_conversation: 'Chat',
            tab_tasks: 'Tehtävät',
            tab_sprint: 'Sprintti',
            tab_sessions: 'Istunnot',
            tab_lobby: 'Aula',
            follow_up: 'Seuraa',
            followup_placeholder: 'Esitä jatkokysymys…',
            interrupt_placeholder: 'Kirjoita keskeyttääksesi ja uudelleenohjataksesi…',
            send_interrupt: 'Lähetä & Keskeytä',
            will_interrupt: 'Keskeyttää nykyisen vastauksen ja lähettää tämän sen sijaan',
            expand: 'Laajenna koko näytölle',
            collapse: 'Pienennä sivupaneeliin',
            no_sessions: 'Ei aiempia istuntoja. Valitse elementtejä ja lähetä ohje aloittaaksesi.',
            session_active: 'Aktiivinen',
            session_ended: 'Päättynyt',
            session_messages: '{count} viestiä',
            switch_session: 'Vaihda tähän istuntoon',
            load_session: 'Lataa istuntohistoria',
            session_restored: 'Istunto palautettu historiasta',
            archive_session: 'Arkiston istunto',
            archive_confirm: 'Arkistoidaanko tämä istunto? Istuntotiedosto poistetaan.',
            new_session: 'Uusi istunto',
            new_session_desc: 'Käytä Aktivoi valitaksesi käyttöliittymäelementin ja aloittaaksesi uuden istunnon',
            start_new_session: 'Aloita uusi istunto',
            no_active_session_chat: 'Aloita uusi istunto tai valitse sivulta käyttöliittymäelementti Aktivoi-painikkeella.',
            no_active_session_tasks: 'Aloita uusi istunto tai luo tehtäviä valitsemalla olemassa oleva istunto.',
            no_active_session_sprint: 'Aloita uusi istunto tai valitse olemassa oleva istunto suunnitellaksesi sprintin.',
            previous_sessions: 'Edelliset istunnot',
        },
        tasks: {
            none_found: 'Vastauksesta ei löytynyt tehtäviä. Pyydä Claudea ehdottamaan tehtäviä.',
            selected: 'selected',
            create_selected: 'Luo tehtäviä',
            execute_tasks: 'Suorita tehtävät',
            creating: 'Luodaan…',
            created: 'luotuja tehtäviä',
            create_error: 'Tehtävän luominen epäonnistui',
            plan_sprint: 'Suunnittele Sprint',
            priority_critical_desc: 'Estävä — korjaa heti',
            priority_high_desc: 'Tärkeä — käsittele tässä sprintissä',
            priority_medium_desc: 'Normaali — suunnittele tulevaan sprinttiin',
            priority_low_desc: 'Mukava — backlog-ehdokas',
            add_custom: 'Lisää tehtävä',
            add_title_placeholder: 'Tehtävän otsikko…',
            add_desc_placeholder: 'Kuvaus (valinnainen)',
            add_save: 'Lisää',
            add_cancel: 'Peruuta',
        },
        sprint: {
            title: 'Sprintin suunnittelija',
            assigned: 'assigned',
            new_placeholder: 'Sprintin nimi (esim. Sprint 18.1.0)',
            add: 'Add',
            backlog: 'Ruuhka',
            move_to: 'Siirrä kohteeseen…',
            empty: 'Luo sprinttiryhmä ja määritä sille tehtäviä.',
            create_all: 'Luo kaikki tehtävät',
            remove: 'Poista',
            drop_here: 'Anna tehtäviä alla olevasta ruuhkasta',
            add_task: 'Lisää tehtävä ruuhkasta…',
        },
        feature_disabled: 'UI Assist ei ole käytössä',
        permission_denied: 'Sinulla ei ole lupaa käyttää UI Assist -sovellusta',
    },
    license: {
        title: 'Lisenssi & Paketti',
        current_license: 'Nykyinen lisenssi',
        license_key: 'Lisenssiavain',
        activate: 'Aktivoi Lisenssi',
        deactivate: 'Poista lisenssi käytöstä',
        validate_now: 'Vahvista nyt',
        status: 'Lisenssin tila',
        active: 'Aktiivinen',
        expired: 'Vanhentunut',
        suspended: 'Keskeytetty',
        invalid: 'Virheellinen',
        pending: 'Odottaa',
        no_license: 'Ei lisenssiä',
        expires_at: 'Vanhenee',
        last_validated: 'Viimeksi vahvistettu',
        domain: 'Lisensoitu verkkotunnus',
        activation_success: 'Lisenssi aktivoitu onnistuneesti.',
        activation_failed: 'Lisenssin aktivointi epäonnistui.',
        domain_mismatch: 'Lisenssiverkkotunnus ei vastaa nykyistä verkkotunnusta.',
        deactivation_success: 'Lisenssi deaktivoitu.',
        validation_success: 'Lisenssi validoitu onnistuneesti.',
        validation_failed: 'Lisenssin vahvistus epäonnistui.',
        enter_key: 'Anna lisenssiavaimesi',
        confirm_deactivate: 'Haluatko varmasti poistaa tämän lisenssin käytöstä? Moduulirajoituksia sovelletaan oletustason mukaan.',
        announce_loaded: 'License settings loaded',
        verification_stale: 'This licence has not been verified with the licence service for more than {days} days, so it reads as unverified. It reads as valid again once a revalidation succeeds.',
        scope_installation: 'The licence and its package apply to the whole installation: every organisation on it. Only a platform administrator can activate, deactivate or change them.',
    },
    package_mgmt: {
        title: 'Paketti',
        module_column: 'Moduuli',
        current_tier: 'Nykyinen paketti',
        light: 'Kevyt',
        standard: 'Standardi',
        enterprise_unlimited: 'Enterprise Unlimited',
        change_package: 'Vaihda paketti',
        upgrade: 'Päivitä',
        downgrade: 'Vähennä',
        target_tier: 'Kohdepaketti',
        new_license_key: 'Kohdepaketin lisenssiavain',
        confirm_change: 'Vahvista paketin muutos',
        change_success: 'Paketti vaihdettu onnistuneesti.',
        change_failed: 'Paketin vaihto epäonnistui.',
        modules_included: 'Mukana moduulit',
        modules_restricted: 'Ei saatavilla nykyisessä paketissa',
        upgrade_to_enable: 'Päivitä ottaaksesi tämän moduulin käyttöön.',
        module_availability: 'Moduulien saatavuus',
        comparison: 'Pakettien vertailu',
        power_users: 'Tehokäyttäjät',
        unlimited: 'Rajoittamaton',
        impact_warning: 'Vanhempaan versioon poistaminen poistaa käytöstä seuraavat moduulit:',
        invalid_license: 'Annettu lisenssiavain ei kelpaa tälle paketille.',
        tier_mismatch: 'Lisenssiavain on eri pakettitasolle.',
    },
    board_report: {
        title: 'Hallituksen vaatimustenmukaisuusraportti',
        subtitle: 'Yhteenveto vaatimustenmukaisuudesta kaikissa moduuleissa',
        date_from: 'Alkaen',
        date_to: 'Päättyen',
        language: 'Raportin kieli',
        generate: 'Luo raportti',
        generating: 'Luodaan raporttia...',
        generate_requires_export: 'Generating a report needs the permission to export governance reports. Ask an administrator to grant it.',
        download_pdf: 'Lataa PDF',
        close: 'Sulje raportti',
        open_board_report: 'Luo hallitusraportti',
        close_board_report: 'Sulje hallitusraportti',
        report_period: 'Raporttikausi',
        generated_at: 'Luotu',
        generated_by: 'Luonut',
        organization: 'Organisaatio',
        section_rights_requests: 'Oikeuspyynnöt',
        section_vendors: 'Toimittajat',
        section_dpias: 'DPIA:t',
        section_consent: 'Suostumuskäytännöt',
        section_data_mapping: 'Tietokartoitus',
        section_incidents: 'Tapahtumat',
        section_classification: 'Tietojen luokittelu',
        section_governance: 'Hallinto',
        open: 'Avoin',
        closed: 'Suljettu',
        sla_compliance: 'SLA-vaatimustenmukaisuus',
        total: 'Yhteensä',
        expired_dpa: 'Vanhentunut DPA',
        high_risk: 'Korkea riski',
        completed: 'Valmis',
        overdue: 'Myöhässä',
        policies_active: 'Aktiiviset käytännöt',
        coverage: 'Kattavuus',
        systems: 'Järjestelmät',
        completeness: 'Täydellisyys',
        avg_resolution_days: 'Keskim. ratkaisuaika (päivää)',
        labeled: 'Merkitty',
        unlabeled_systems: 'Merkitsemättömät järjestelmät',
        compliance_score: 'Vaatimustenmukaisuuspisteet',
        drift_events_open: 'Avoimet poikkeamat',
        no_data: 'Ei tietoja valitulle ajanjaksolle.',
        error_date_range: 'Valitse sekä alku- että loppupäivä.',
        table_category: 'Kategoria',
        table_metric: 'Mittari',
        table_value: 'Arvo',
        showing_chart_view: 'Näytetään kaavionäkymä',
        showing_table_view: 'Näytetään taulukonäkymä',
        error_generate: 'Hallitusraportin luominen epäonnistui.',
        playbook_evidence_title: 'Playbook-suoritusten todistusaineisto',
        playbook_evidence_description: 'Playbook-kohtainen suoritusaktiivisuus ja liitetyt todistustiedostot raportointijaksolta.',
        playbook_name: 'Pelikirja',
        playbook_execution_count: 'Suoritukset',
        playbook_steps_total: 'Vaiheet',
        playbook_steps_required: 'Todistusaineistoa vaaditaan',
        playbook_steps_attached: 'Todistusaineisto liitetty',
        playbook_coverage_pct: 'Kattavuus',
        playbook_evidence_files: 'Todistustiedostot',
        playbook_no_evidence: 'Tältä ajanjaksolta ei ole playbook-suorituksia.',
        playbook_download_file: 'Lataa tiedosto',
    },
    framework_module_links: {
        title: 'Kehyslinkitys',
        modules: 'Linkitetyt moduulit',
        add_module: 'Lisää moduuli',
        module_code: 'Moduulikoodi',
        required: 'Pakollinen',
        notes: 'Muistiinpanot',
        link: 'Linkki',
        no_modules: 'Ei linkitettyjä moduuleja',
        personas: 'Linkitetyt henkilöt',
        add_persona: 'Lisää henkilö',
        persona_id: 'Henkilötunnus',
        role_description: 'Roolin kuvaus',
        no_personas: 'Ei linkitettyjä henkilöitä',
    },
    framework_channels: {
        title: 'Lähetyskanavat & Määräajat',
        channels: 'Lähetyskanavat',
        add_channel: 'Lisää kanava',
        channel_type: 'Tyyppi',
        name: 'Nimi',
        instructions: 'Ohjeet',
        active: 'Aktiivinen',
        no_channels: 'Ei lähetyskanavia määritelty',
        portal: 'Portaali',
        in_person: 'Henkilökohtaisesti',
        postal: 'Posti',
        email: 'Sähköposti',
        add: 'Lisää',
        deadlines: 'Sääntelymääräajat',
        add_deadline: 'Lisää määräaika',
        label: 'Tunniste',
        days: 'Päivät',
        day_type: 'Päivätyyppi',
        calendar_days: 'Kalenteripäivät',
        working_days: 'Työpäivät',
        description: 'Kuvaus',
        no_deadlines: 'Ei määräaikoja määritelty',
    },
    regulatory_updates: {
        title: 'Sääntelypäivitykset & Kehysdashboard',
        coverage_dashboard: 'Kehyksen kattavuusdashboard',
        no_frameworks: 'Kehyksiä ei löydy',
        framework: 'Kehys',
        status: 'Tila',
        modules: 'Moduulit',
        templates: 'Mallipohjat',
        forms: 'Lomakkeet',
        channels: 'Kanavat',
        deadlines: 'Määräajat',
        pending: 'Odottavat päivitykset',
        updates: 'Sääntelypäivitykset',
        create_update: 'Luo päivitys',
        framework_id: 'Kehystunnus',
        update_title: 'Otsikko',
        description: 'Kuvaus',
        effective_date: 'Voimaantulopäivä',
        create: 'Luo',
        no_updates: 'Ei sääntelypäivityksiä',
        impact: 'Vaikutus',
        assess: 'Arvioi',
    },
    vehicle_monitoring: enVehicleMonitoring,
    record_links: enRecordLinks,
    module_tips: fiModuleTips,
    framework_rights: {
        be_informed: 'Oikeus saada tietoa',
        consent: 'Oikeus suostumukseen',
        refuse_consent: 'Oikeus kieltäytyä suostumuksesta',
        withdraw_consent: 'Oikeus peruuttaa suostumus',
        access: 'Oikeus saada pääsy tietoihin',
        rectification: 'Oikeus oikaisuun',
        data_provision: 'Oikeus tietojen toimittamiseen',
        deletion: 'Oikeus poistamiseen',
        restriction_of_processing: 'Oikeus käsittelyn rajoittamiseen',
        object_to_processing: 'Oikeus vastustaa käsittelyä',
        complaint: 'Oikeus tehdä valitus',
        denunciation: 'Oikeus ilmiantoon',
        lawsuit: 'Oikeus nostaa kanne',
        claim_damages: 'Oikeus vaatia vahingonkorvausta',
        request_protection_measures: 'Oikeus pyytää suojatoimenpiteitä',
    },
    framework_content: {
        EU_EPRIVACY_DIRECTIVE: {
            display_name: 'Direktiivi 2002/58/EY (sähköisen viestinnän tietosuojadirektiivi)',
            description: 'Päädirektiivi sähköisen viestinnän tietosuojan kansallisesta täytäntöönpanosta. Kattaa evästeet, sähköisen viestinnän tietosuojan ja suoramarkkinoinnin. Jokaisen jäsenvaltion on saatettava osa kansallista lainsäädäntöään.',
        },
        EU_GDPR: {
            display_name: 'Yleinen tietosuoja-asetus (EU) 2016/679',
            description: 'Yleinen tietosuoja-asetus (GDPR) on EU:n tietosuoja- ja yksityisyysasetus. Sitä sovelletaan suoraan kaikissa EU/ETA-maissa.',
        },
        EU_NIS2_DIRECTIVE: {
            display_name: 'Direktiivi (EU) 2022/2555 (NIS2)',
            description: 'Päädirektiivi kansallisille täytäntöönpanotoimille NIS2. Kattaa kyberturvallisuusriskien hallinnan, vaaratilanteiden raportoinnin, toimitusketjun turvallisuuden olennaisille ja tärkeille kokonaisuuksille.',
        },
        VN_AI_LAW_2026: {
            display_name: 'Vietnamin AI laki (134/2025/QH15) – voimaan 1.3.2026',
            description: 'Vietnamin AI laki (Luật Trí tuệ nhân tạo 134/2025/QH15), joka säätelee tekoälyjärjestelmien kehitystä, käyttöönottoa ja käyttöä. Perustaa riskiluokitus-, avoimuus- ja merkintävelvollisuudet, vaaratilanteiden raportointivaatimukset, vaikutustenarviointivelvoitteet ja valvotun testausmekanismin (hiekkalaatikko) AI:n toiminnalle Vietnamissa.',
        },
        VN_AI_DECREE_142_2026: {
            display_name: 'Decree 142/2026/NĐ-CP — AI Law Implementation',
            description: 'Decree 142/2026/NĐ-CP (Nghị định số 142/2026/NĐ-CP) detailing certain articles and implementing measures of the Vietnam AI Law (Law 134/2025/QH15). Issued 2026-04-30, effective 2026-05-01. Lead authority: Ministry of Science and Technology. Operationalises AI risk classification, classification dossier, reclassification review, high-risk conformity assessment, high-risk risk-management system, transparency / explanation / technical marking / visible labeling, serious AI incident reporting (72h / 5wd / 15d), state-agency AI impact assessment (Mẫu AI02), and the controlled testing / AI sandbox lifecycle (Mẫu AI03a-AI09b). Submission via the AI one-stop e-portal or MoST transitional channel. No statutory fines.',
        },
        VN_CYBERSEC_LAW: {
            display_name: 'Vietnamin kyberturvallisuuslaki (24/2018 aikakausi)',
            description: 'Vietnamin kyberturvallisuuslaki (laki nro 24/2018/QH14), jossa vahvistetaan kyberturvallisuutta, tietojen lokalisointia ja tapahtumaraportointia koskevat vaatimukset. Voimassa, kunnes se korvataan lailla 116/2025/QH15 voimassa 2026-07-01.',
        },
        VN_CYBERSEC_DECREE_53: {
            display_name: 'Asetus 53 – Kyberturvallisuuslain täytäntöönpano',
            description: 'Asetus 53/2022/ND-CP kyberturvallisuuslain täytäntöönpanosta, joka kattaa soveltamisalan määrittämisen, tietojen lokalisoinnin liipaisimet, paikallisen läsnäolon vaatimukset ja viranomaisten yhteistyön työnkulkuja.',
        },
        VN_CYBERSEC_LAW_2025: {
            display_name: 'Kyberturvallisuuslaki (116/2025/QH15)',
            description: 'Vietnamin konsolidoitu kyberturvallisuuslaki (laki 116/2025/QH15), joka korvaa lain 24/2018/QH14, voimaan 1.7.2026. Konsolidoidaan asetuksen 53/2022/NĐ-CP (katso VN_CYBERSEC_DECREE_53).',
        },
        VN_DATA_LAW: {
            display_name: 'Vietnamin tietolaki',
            description: 'Vietnamin tietolaki (laki 60/2024/QH15), jossa vahvistetaan kattavat tiedonhallintavaatimukset, mukaan lukien tietojen kartoitus, luokittelu, jakamisen hallinta ja rajat ylittävän tiedonsiirron hallinto. Päällekkäinen VN_PDPL_LAW_2025 (henkilötietonäkökohdat) ja VN_CYBERSEC_LAW_2025 (tietojen lokalisointi/luokitus).',
        },
        VN_DATA_LAW_DECREE_165_2025: {
            display_name: 'Asetus 165/2025/NĐ-CPĐ-CP (tietolain täytäntöönpano)',
            description: 'Asetus 165/2025/NĐ-CP Vietnamin tietolain täytäntöönpanosta (laki 60/2024/QH15). Tarjoaa viralliset lomakkeet rajat ylittävän tiedonsiirron vaikutustenarviointiin, vuotuiseen riskinarviointiraportointiin ja kansalliseen tietokantaan. Määrittää 11 virallista Mẫu số -lomaketta.',
        },
        VN_DATA_EXCHANGES_DRAFT_2026: {
            display_name: 'Datapörssejä koskeva asetusluonnos 2026',
            description: 'Asetusluonnos tiedonvaihtoalustojen toiminnasta (Dự thảo Nghị định quy định về hoạt động của sàn dữ liệu). Säätelee alustan luokittelua, osallistujien kelpoisuutta, datatuotteiden ja palveluiden listauksen hallintaa, hiekkalaatikkotestausta, transaktioiden työnkulkua, sähköistä sopimusta, tietoihin liittyviä omistusoikeuksia koskevia varmenteita ja valitusten/riitojen käsittelyä. Parhaillaan julkisessa kuulemisessa; ei ole vielä julkaistu. Viittaa tietosuojalakiin 60/2024/QH15, henkilötietojen suojaa koskevaan lakiin 91/2025/QH15, kyberturvalakiin 116/2025/QH15 ja lakiin sähköisistä liiketoimista 20/2023/QH15.',
        },
        VN_ECOMMERCE_LAW_2025: {
            display_name: 'Sähköistä kaupankäyntiä koskeva laki (122/2025/QH15)',
            description: 'Vietnamin sähköistä kaupankäyntiä koskeva laki (Luật Thương mại điện tử 122/2025/QH15), jolla perustetaan yhtenäinen kansallinen sähköistä kaupankäyntiä koskeva järjestelmä, joka kattaa kotimaiset ja rajat ylittävät alustatoiminnot, alustan hallinnan ja vastuuvelvollisuuden, kuluttajansuojan, myyjien ja sidosryhmien hallinnolliset menettelyt, sisällönkäsittelyn ja sidosyritysten käsittelyn, modeemin ja sidosyritysten käsittelyn valvonnan. valtion sähköisen kaupankäynnin hallintajärjestelmä.',
        },
        VN_PDPD_DECREE_13: {
            display_name: 'Asetus 13/2023/NĐ-CP',
            description: 'Asetus 13/2023/ND-CP, operatiivinen väline PDPL:n tehtäviin, mukaan lukien asiakirja-aineiston arkistointi, hallintoartefaktit, rajat ylittävä mekaniikka ja MoPS-hallinnolliset menettelyt.',
        },
        VN_PDPL_LAW_2025: {
            display_name: 'Henkilötietojen suojalaki (91/2025/QH15)',
            description: 'Vietnamin henkilötietosuojalaki (laki 91/2025/QH15), jolla luodaan kattavat henkilötietojen suojapuitteet, voimaan 1. tammikuuta 2026. Kattaa noudattamisteemat A–J: lailliset käsittelyperusteet, arkaluonteisten tietojen suostumus, rekisteröidyn oikeudet (pääsy/oikaisu/poisto/rajoitus/vastuu/siirrettävyys), rikkomuksen kohteiden ilmoitus viranomaisille ja rekisteröidyille, DPIA korkean riskin käsittelyyn, rajat ylittävän siirron hallinnointi, DPO nimeäminen, tiedot käsittelytoimista, käsittelijän sopimusvelvoitteet. Viranomaisten ilmoitusvelvollisuudet MoPS:n kautta (VN_PDPL_ADMIN_PROCEDURES_778_2026). Ristiviitteet: VN_PDPL_DECREE_356_2025 (täytäntöönpanoasetus), VN_CYBERSEC_LAW_2025 (kyberturvallisuuden päällekkäisyydet), VN_DATA_LAW (tietojen hallinta).',
        },
        VN_PDPL_DECREE_356_2025: {
            display_name: 'Asetus 356/2025/ND-CP (PDPL täytäntöönpano)',
            description: 'Asetus 356/2025/ND-CP henkilötietojen suojalain (laki 91/2025/QH15) täytäntöönpanosta. Kattaa asiakirja-aineiston arkistoinnin, hallintoartefaktit, rajat ylittävän mekaniikan ja palveluntarjoajan sertifioinnin. Liitelomakeluettelo: Phụ lục I–VIII (vaikutusten arviointi, rajat ylittävä siirto, sertifiointi). MoPS-hallinnolliset menettelyt VN_PDPL_ADMIN_PROCEDURES_778_2026:n kautta (päätös 778/QĐ-BCA-A05). Sertifikaatin työnkulku: hakemus → myöntäminen → muutospyyntö → uudelleen myöntäminen → peruutus. Ristiviitteet: VN_PDPL_LAW_2025 (ylälaki), VN_CYBERSEC_LAW_2025 (tietojen lokalisointi), VN_DATA_LAW (tietojen hallinnan päällekkäisyydet).',
        },
        VN_PDPL_ADMIN_PROCEDURES_778_2026: {
            display_name: 'MPS A05 hallintomenettelyt (päätös 778/QĐ-BCA-A05)',
            description: 'Päätös 778/QĐ-BCA-A05 (helmikuu 2026) – Henkilötietojen suojaa koskevat hallinnolliset menettelyt kohdassa A05 (MPS). Määrittää asiakirja-aineiston toimittamisen, käsittelyn ja tulosten myöntämismenettelyt, mukaan lukien DLCN-HC-lomakkeet ja Mẫu số lakisääteiset lomakkeet.',
        },
        VN_PDPL_ENFORCEMENT_DRAFT_2026: {
            display_name: 'Täytäntöönpanoasetusluonnos 2026',
            description: 'Luonnos täytäntöönpano- ja seuraamusasetukseksi henkilötietosuojalain (laki 91/2025/QH15 rikkomisesta). Määrittää rangaistusalueet, raskauttavat/lieventävät tekijät ja täytäntöönpanomenettelyt. Tällä hetkellä luonnostilassa odottaa julkaisemista.',
        },
        VN_MOPS_ADMIN_SIMPLIFICATION_NQ22_2026: {
            display_name: 'Government Resolution NQ22/2026 — MoPS Administrative Simplification (Temporary Overlay)',
            description: 'Temporary procedural overlay (effective 2026-04-29, expiring 2027-03-01) introducing three official channels (National Public Service Portal, direct submission to MoPS, postal submission to MoPS) for PDPL Decree 356/2025 and Decision 778/2026 dossier submissions. Email is NOT a NQ22 official channel — a separate unofficial email-ZIP submission guidance (VN_PDPL_GUIDANCE_EMAIL_ZIP_DOSSIER_SUBMISSION_2026) is shipped with requires_legal_review: true. Procedural overlay only — does NOT alter substantive obligations, fine ranges, or the binding force of underlying frameworks. Review task scheduled 2027-02-01.',
        },
        VN_TELECOM_LAW_2023: {
            display_name: 'Vietnamin televiestintälaki 2023',
            description: 'Vietnamin televiestintälaki 2023 (Luật Viễn thông), televiestintä-, Internet-palveluntarjoaja- tai alustayritysten alakohtainen peitto, joka koskee luottamuksellisuutta, tietojen luovuttamista ja viranomaispyyntöjen käsittelyä. Ristiviitteet: VN_CYBERSEC_LAW_2025 (tietojärjestelmän turvallisuus), VN_PDPL_LAW_2025 (käyttäjätietojen käsittely), VN_DATA_LAW (tietojen hallinta).',
        },
        VN_TELECOM_DECREE_163_2024: {
            display_name: 'Asetus 163/2024/NĐ-CPĐ-CP (televiestintälain täytäntöönpano)',
            description: 'Asetus 163/2024/NĐ-CP televiestintälain täytäntöönpanosta (laki 24/2023/QH15). Tarjoaa viralliset lomakkeet telepalveluilmoitus-, lisensointi-, rekisteröinti- ja datakeskusten kehityssuunnitelmia varten. Määrittää 7 virallista Mẫu số -lomaketta.',
        },
    },
    framework_requirements: {
        vn_dp_draft_f: {
            title: 'Valitukset, riidat ja riskienhallinta',
            description: 'Alustan operaattoreiden on tarjottava valitusten käsittely, riitojen käsittelyn työnkulku todisteiden säilyttämisellä, riskien tunnistaminen ja varhaisvaroitushallinta sekä yksityisyyden, kyberturvallisuuden ja pääsyn hallinnan riskien säännöllinen tarkastelu.',
        },
        vn_dp_draft_e: {
            title: 'Transaktion työnkulku ja todistus omistusoikeuksista tiedoille',
            description: 'Tapahtumat edellyttävät tilin rekisteröintiä, listauksen hyväksymistä, sähköistä sopimusta, maksuvahvistusta, toimitustietuetta ja tarvittaessa tietojen omistusoikeustodistuksen myöntämistä. Tapahtumahistoria ja jäljitettävyys on säilytettävä tapahtumarekisterissä.',
        },
        vn_dp_draft_d: {
            title: 'Hiekkalaatikon ja testausympäristön hallintalaitteet',
            description: 'Testiympäristöt on eristettävä, niihin on pääsy rajoitettu, niitä on valvottava ja tarkastettava ennen tulosteen purkamista. Testiympäristöstä ei saa poimia lähdetietoja, ja testiistuntojen sulkeminen/siivoaminen on tallennettava.',
        },
        vn_dp_draft_c: {
            title: 'Tietojen tuote- ja palveluluettelon hallinta',
            description: 'Tietojen tueksi on esitettävä todisteet laillisista lähteistä ja omistus-/käyttöoikeuksista, tekniset validointitietueet, laatutiedot ja alustan puolen listauksen tarkistus- ja hyväksyntitietueet. Kielletyt tiedot on suodatettava ennen listaamista.',
        },
        vn_dp_draft_b: {
            title: 'Osallistumiskelpoisuus ja osallistuminen',
            description: 'Alustan ylläpitäjien on tarkistettava myyjien ja ostajien oikeudellinen asema, oikeuskelpoisuus ja ehdolliset liiketoimintavaatimukset, rekisteröitävä tapahtuma- ja maksutilit ja ylläpidettävä osallistujarekisteriä.',
        },
        vn_dp_draft_a: {
            title: 'Tietoalustan luokitus ja toimintamalli',
            description: 'Alustan operaattoreiden on luotava alustatyypin luokitus (kansallinen tietoalusta vs. muut tietoalustat), määriteltävä operaattorin kelpoisuus ja vastuut sekä dokumentoitava hinnoittelu, maksut, toimintasäännöt ja tekninen hallinto.',
        },
        d165_r5: {
            title: 'Pääsy kansallisiin tietokantoihin',
            description: 'Valtakunnallisen digitaalisen tietokannan tietojen poimia/käyttöä koskevat pyynnöt käyttävät määrättyjä lomakkeita (Mẫu số 07a/07b).',
        },
        d165_r4: {
            title: 'Vuotuinen riskinarviointiraportti',
            description: 'Vuotuiset riskinarviointiraportit on laadittava määrätyillä lomakkeilla (Mẫu số 05a/05b, 06).',
        },
        d165_r3: {
            title: 'Ilmoitus rajat ylittävän siirron irtisanomisesta',
            description: 'Rajat ylittävän siirron päättymisestä on ilmoitettava määrätyillä lomakkeilla (Mẫu số 04a/04b).',
        },
        d165_r2: {
            title: 'Ilmoitus arvioinnin tuloksista',
            description: 'Viranomaiset antavat arvioinnin tulosilmoitukset määrätyllä lomakkeella (Mẫu số 03).',
        },
        d165_r1: {
            title: 'Rajat ylittävän tiedonsiirron vaikutustenarvioinnin arkistointi',
            description: 'Organisaatioiden on toimitettava rajat ylittävän tiedonsiirron/käsittelyn vaikutustenarviointiasiakirjat määrätyillä lomakkeilla (Mẫu số 01a/01b, 02).',
        },
        a964190d_8d1d_4284_bc7d_4023e2866751: {
            title: 'Suostumus evästeille ja vastaaville teknologioille',
            description: 'Tietojen tallentaminen tai niihin pääsy päätelaitteessa on sallittu vain ennakkoon ilmoitettuun suostumukseen, lukuun ottamatta teknisesti tarpeellista tallennusta.',
        },
        '8437d96d_cf01_4f87_89f6_4faaf8eefdf6': {
            title: 'Viestinnän luottamuksellisuus',
            description: 'Jäsenvaltioiden on varmistettava yleisen viestintäverkon ja yleisesti saatavilla olevien sähköisten viestintäpalvelujen avulla tapahtuvan viestinnän ja siihen liittyvien liikennetietojen luottamuksellisuus.',
        },
        c5420073_50c6_4981_816a_398460e5f546: {
            title: 'Suostumus suoramarkkinointiin (opt-in)',
            description: 'Sähköisten viestintäjärjestelmien käyttö suoramarkkinointiin edellyttää tilaajan ennakkosuostumusta.',
        },
        c8bf67d2_874b_4647_92d7_39aaa3230819: {
            title: 'Pehmeä opt-in olemassa oleville asiakkaille',
            description: 'Samankaltaisten tuotteiden/palveluiden suoramarkkinointi olemassa oleville asiakkaille on sallittua ilman suostumusta, jos kieltäydytään.',
        },
        b546df40_9217_4204_b71c_5af5022a0ff9: {
            title: 'Rikkomusilmoitus teleoperaattoreille',
            description: 'Yleisesti saatavilla olevien sähköisten viestintäpalvelujen tarjoajien on ilmoitettava toimivaltaiselle kansalliselle viranomaiselle henkilötietojen tietoturvaloukkauksista.',
        },
        '5bd66ae4_dee3_4611_9104_71742df55484': {
            title: 'Oikeus vastustaa suoramarkkinointia',
            description: 'Tilaajille on annettava mahdollisuus milloin tahansa ja maksutta vastustaa suoramarkkinointiviestien vastaanottamista.',
        },
        a10af31f_ba28_494e_ab50_f8679e86a0b0: {
            title: 'Liikennetietojen säilyttäminen ja poistaminen',
            description: 'Tilaajia ja käyttäjiä koskevat liikennetiedot on poistettava tai tehtävä anonyymeiksi, kun niitä ei enää tarvita siirtoon, lukuun ottamatta laskutusta tai yhteenliittämismaksuja.',
        },
        '8577e60f_66a8_4651_8336_d561db9929ad': {
            title: 'Sijaintitietojen käsittelyehdot',
            description: 'Muita sijaintitietoja kuin liikennetietoja voidaan käsitellä vain anonyymeiksi tehtyinä tai käyttäjän suostumuksella, siinä laajuudessa ja niin kauan kuin se on tarpeellista.',
        },
        '7f3abb41_8713_4276_93b5_6902f419a016': {
            title: 'Käsittelyn laillisuus – suostumus',
            description: 'Käsittely on laillista vain, jos rekisteröity on antanut suostumuksensa henkilötietojensa käsittelyyn yhtä tai useampaa erityistä tarkoitusta varten.',
        },
        '1c2a945a_8eac_428d_8105_e80e95288ea4': {
            title: 'Käsittelyn laillisuus – sopimus',
            description: 'Käsittely on tarpeen sellaisen sopimuksen täyttämiseksi, jonka osapuoli rekisteröity on.',
        },
        '2b50c0fb_ee96_412e_a092_c3c1f3f817e8': {
            title: 'Käsittelyn laillisuus — lakisääteinen velvoite',
            description: 'Käsittely on välttämätöntä rekisterinpitäjän lakisääteisen velvoitteen noudattamiseksi.',
        },
        '702ea760_9e5c_4c32_babc_0b5094ca9735': {
            title: 'Käsittelyn laillisuus – elintärkeät edut',
            description: 'Käsittely on tarpeen rekisteröidyn tai muun luonnollisen henkilön elintärkeiden etujen suojaamiseksi.',
        },
        '8b55ce88_2d5e_467b_a55c_8c5b0efab67e': {
            title: 'Käsittelyn laillisuus – yleinen etu',
            description: 'Käsittely on tarpeen yleisen edun mukaisen tehtävän suorittamiseksi tai julkista valtaa käytettäessä.',
        },
        '8a72dd16_ee3c_4947_b5cd_9484719adad8': {
            title: 'Käsittelyn laillisuus – oikeutetut edut',
            description: 'Käsittely on tarpeen rekisterinpitäjän tai kolmannen osapuolen oikeutettujen etujen toteuttamiseksi.',
        },
        afefc965_8f83_4d75_a9fc_92d4d4f549d6: {
            title: 'Erityisluokkien käsittely (arkaluonteiset tiedot)',
            description: 'Erityisten henkilötietojen (rotuperä, poliittiset mielipiteet, terveys jne.) käsittely on kiellettyä, ellei Art. 9(2) täyttyvät.',
        },
        '10a2d63f_744d_4222_91f6_86a5a4cdaefd': {
            title: 'Suostumuksen edellytykset',
            description: 'Rekisterinpitäjän on osoitettava, että suostumus on annettu. Suostumus on annettava vapaasti, täsmällinen, tietoinen ja yksiselitteinen. Peruuttamisen tulee olla yhtä helppoa kuin suostumuksen antaminen.',
        },
        '27697f99_efef_49e6_b8e7_c2dc550d1791': {
            title: 'Lapsen suostumus – digitaaliset palvelut',
            description: 'Alle 16-vuotiaan (tai jäsenvaltion asettaman alemman iän, ei alle 13-vuotiaan) lapsen henkilötietojen käsittely edellyttää vanhemman suostumusta tietoyhteiskunnan palveluihin.',
        },
        '53883480_508e_4f8e_984f_9354c18de285': {
            title: 'Läpinäkyvä tiedotus ja viestintä',
            description: 'Rekisterinpitäjän on annettava käsittelyyn liittyvät tiedot tiiviissä, läpinäkyvässä, ymmärrettävässä ja helposti saatavilla olevassa muodossa.',
        },
        b5eb22b7_78f2_4c55_b342_827d5f8ac90c: {
            title: 'Tiedot suoraa keräystä varten',
            description: 'Jos rekisteröidyltä kerätään henkilötietoja, rekisterinpitäjän on annettava tietyt tiedot keräyksen yhteydessä.',
        },
        e55f7dbf_cd2a_4713_887e_8c64744e2d95: {
            title: 'Tietoja epäsuoraan keräämiseen',
            description: 'Jos henkilötietoja ei ole saatu rekisteröidyltä, rekisterinpitäjän on annettava tietyt tiedot kohtuullisessa ajassa.',
        },
        '6bc5ec07_0589_4593_882a_13f41c31e1f6': {
            title: 'Käyttöoikeus',
            description: 'Rekisteröidyllä on oikeus saada vahvistus käsittelystä sekä pääsy henkilötietoihin ja lisätietoihin.',
        },
        '0e011e25_dcea_4a1e_ae55_583e683cea44': {
            title: 'Oikeus oikaisuun',
            description: 'Rekisteröidyllä on oikeus saada virheelliset henkilötiedot oikaistaan ilman aiheetonta viivytystä.',
        },
        e8ec5a55_f657_4a99_9b92_3e9861067d28: {
            title: 'Oikeus poistaa (oikeus tulla unohdetuksi)',
            description: 'Rekisteröidyllä on oikeus vaatia henkilötietojensa poistamista ilman aiheetonta viivytystä, jos siihen on erityisiä syitä.',
        },
        '5b0c92fd_cd08_47be_bfd2_0540ad73f84f': {
            title: 'Oikeus käsittelyn rajoittamiseen',
            description: 'Rekisteröidyllä on oikeus saada käsittelyn rajoitus tietyin edellytyksin.',
        },
        '3b829862_39f4_41be_8f2b_d469469a801b': {
            title: 'Ilmoitusvelvollisuus – oikaisu/poistaminen/rajoitus',
            description: 'Rekisterinpitäjä ilmoittaa kaikista oikaisuista, poistoista tai rajoituksista jokaiselle vastaanottajalle, jolle tietoja on luovutettu.',
        },
        '9cf34be6_121b_4fdf_ad7c_c4773696522e': {
            title: 'Oikeus tietojen siirrettävyyteen',
            description: 'Rekisteröidyllä on oikeus saada henkilötietonsa jäsennellyssä, yleisesti käytetyssä, koneellisesti luettavassa muodossa.',
        },
        becae433_90b9_4cc0_bd33_df50976c1561: {
            title: 'Oikeus vastustaa',
            description: 'Rekisteröidyllä on oikeus vastustaa yleiseen etuun tai oikeutettuihin etuihin perustuvaa käsittelyä, mukaan lukien profilointi.',
        },
        '4ef387f1_439c_4579_82a3_01565e6dc05d': {
            title: 'Automatisoitu yksilöllinen päätöksenteko, mukaan lukien profilointi',
            description: 'Rekisteröidyllä on oikeus olla joutumatta sellaisen päätöksen kohteeksi, joka perustuu yksinomaan automaattiseen käsittelyyn ja jolla on oikeudellisia tai vastaavia merkittäviä vaikutuksia.',
        },
        '667ae5eb_a39a_4809_a032_923cc804eaed': {
            title: 'Rikkomusilmoitus valvontaviranomaiselle (72h)',
            description: 'Tietoturvaloukkauksesta rekisterinpitäjän on ilmoitettava siitä valvontaviranomaiselle 72 tunnin kuluessa tietoonsa saamisesta, ellei siitä todennäköisesti aiheudu riskiä.',
        },
        '40e4c2ad_bc85_436c_814f_265f02c8c3b1': {
            title: 'Rikkomusilmoitus rekisteröidyille (suuri riski)',
            description: 'Jos loukkaus todennäköisesti aiheuttaa suuren riskin oikeuksille ja vapauksille, rekisterinpitäjän on ilmoitettava loukkauksesta rekisteröidylle ilman aiheetonta viivytystä.',
        },
        '9dbb47bd_172f_4335_b7f3_4558476c4192': {
            title: 'Rekisteröityjen pyyntöihin vastaaminen (30 päivää)',
            description: 'Rekisterinpitäjä vastaa rekisteröityjen pyyntöihin ilman aiheetonta viivytystä ja kuukauden kuluessa vastaanottamisesta, jota voidaan jatkaa kahdella kuukaudella monimutkaisissa pyynnöissä.',
        },
        '175d16b7_a0fc_44ff_bb9e_5ccb5d3de5a9': {
            title: 'Tietosuojavaikutusten arviointi',
            description: 'Jos käsittely todennäköisesti johtaa suureen riskiin oikeuksille ja vapauksille, rekisterinpitäjän on suoritettava DPIA ennen käsittelyä.',
        },
        e54a7b09_33dd_4cda_8e21_d10a51e5e925: {
            title: 'Ennakkoneuvottelut valvontaviranomaisen kanssa',
            description: 'Rekisterinpitäjän on kuultava valvontaviranomaista, jos DPIA osoittaa, että käsittely johtaisi suureen riskiin ilman lieventäviä toimenpiteitä.',
        },
        '047b7838_bae5_4861_9c38_c99fedec9c96': {
            title: 'DPIA pakollinen käsittelyluettelo',
            description: 'DPIA tarvitaan systemaattiseen arviointiin/profilointiin, erityisluokkien laajamittaiseen käsittelyyn ja yleisten alueiden järjestelmälliseen seurantaan.',
        },
        aa08dc83_7c1a_441d_a8e7_960f65c8de87: {
            title: 'Käsittelytoimia koskevat tiedot (rekisterinpitäjä)',
            description: 'Jokaisen rekisterinpitäjän on pidettävä kirjaa vastuullaan olevista käsittelytoimista, mukaan lukien tarkoitukset, luokat, vastaanottajat, siirrot ja säilyttäminen.',
        },
        b7eb6296_5962_449c_b267_d322d7919122: {
            title: 'Käsittelytoimia koskevat tiedot (käsittelijä)',
            description: 'Jokaisen käsittelijän on pidettävä kirjaa kaikista rekisterinpitäjän puolesta suoritetuista käsittelytoimista.',
        },
        '0d57e6d5_bbf5_4ea6_9a89_0f57bed61b5b': {
            title: 'Käsittelyn turvallisuus',
            description: 'Rekisterinpitäjän ja käsittelijän on toteutettava asianmukaiset tekniset ja organisatoriset toimenpiteet riskiin sopivan turvallisuuden varmistamiseksi.',
        },
        ad85edfc_3c92_4ce4_a414_3ba6186cae98: {
            title: 'Tietosuoja on suunniteltu ja oletusarvoisesti',
            description: 'Rekisterinpitäjä toteuttaa asianmukaiset toimenpiteet sen varmistamiseksi, että vain kuhunkin tiettyyn tarkoitukseen tarpeellisia henkilötietoja käsitellään (tietojen minimointi).',
        },
        '933b438d_53b8_4b6a_b1db_8e7f1b429ede': {
            title: 'Tietosuojavastaavan nimitys',
            description: 'Rekisterinpitäjän ja henkilötietojen käsittelijän on nimettävä DPO, jos käsittely on viranomaisen suorittamaa, säännöllistä ja järjestelmällistä seurantaa tai erityisluokkien laajamittaista käsittelyä.',
        },
        '28810a5d_d818_411a_9e8c_792d1b2c9b2f': {
            title: 'Siirtojen yleinen periaate',
            description: 'Henkilötietojen siirto kolmanteen maahan saa tapahtua vain, jos V luvun ehtoja noudatetaan.',
        },
        '60add64b_bb0c_44e7_baaf_666af65b2d0f': {
            title: 'Siirrot riittävyyspäätöksen perusteella',
            description: 'Siirto voi tapahtua, jos komissio on päättänyt, että kolmas maa takaa riittävän suojelun tason.',
        },
        cde8c7cd_3727_40ce_a321_e6b72b335179: {
            title: 'Siirrot asianmukaisin suojatoimin',
            description: 'Jos riittävyyttä koskevaa päätöstä ei ole tehty, siirrot voivat tapahtua, jos asianmukaisia suojatoimia tarjotaan, mukaan lukien SCC:t tai BCR:t.',
        },
        '0e8edaa1_2156_44d9_95f2_1b284f581658': {
            title: 'Vakiosopimuslausekkeet',
            description: 'Komission tai valvontaviranomaisen hyväksymät vakiosopimuslausekkeet tarjoavat asianmukaiset takeet kansainvälisille siirroille.',
        },
        '608a8de7_6c75_4f88_bd4c_1cc308780ab9': {
            title: 'Yrityksen sitovat säännöt',
            description: 'Toimivaltaisen valvontaviranomaisen hyväksymät sitovat yrityssäännöt tarjoavat asianmukaiset suojat konsernin sisäisille kansainvälisille siirroille.',
        },
        '420ced80_0d3b_4a28_adad_14ba54df3e40': {
            title: 'Poikkeukset erityistilanteita varten',
            description: 'Riittävyyden tai takeiden puuttuessa siirrot voivat tapahtua nimenomaisen suostumuksen, sopimuksen välttämättömyyden, yleisen edun, oikeudellisten vaatimusten, elintärkeiden etujen tai julkisen rekisterin perusteella.',
        },
        c86fbc0d_7188_486c_8ea5_96b3fe957c6f: {
            title: 'Hallintoelimen vastuu ja koulutus',
            description: 'Olennaisten ja tärkeiden yksiköiden johtoelinten tulee hyväksyä kyberturvallisuuden riskienhallintatoimenpiteet ja käydä säännöllisesti koulutusta.',
        },
        ffb95a09_993c_4db0_a4bb_c48000e43b33: {
            title: 'Kyberturvallisuuden riskienhallintatoimenpiteet',
            description: 'Olennaisten ja tärkeiden tahojen on toteutettava asianmukaiset ja oikeasuhteiset tekniset, toiminnalliset ja organisatoriset toimenpiteet kyberturvallisuusriskien hallitsemiseksi.',
        },
        '6a7c8c8f_4b81_4b47_adb7_c6abd12acd4b': {
            title: 'Tapahtumankäsittelykäytännöt ja -menettelyt',
            description: 'Yhteisöjen on otettava käyttöön vaaratilanteiden käsittelyä koskevat käytännöt, mukaan lukien havaitsemis-, analysointi-, eristys- ja reagointimenettelyt.',
        },
        de019eab_9992_4361_be7c_3e592f209d76: {
            title: 'Liiketoiminnan jatkuvuus ja kriisinhallinta',
            description: 'Yhteisöjen tulee toteuttaa toiminnan jatkuvuustoimenpiteitä, mukaan lukien varmuuskopiointi, katastrofipalautus ja kriisinhallinta.',
        },
        '6b5f482f_301c_4f69_bd91_f9ae1b21de6c': {
            title: 'Toimitusketjun turvallisuus',
            description: 'Yhteisöjen on käsiteltävä turvallisuuteen liittyviä näkökohtia suhteissa suorien toimittajien ja palveluntarjoajien kanssa, mukaan lukien toimitusketjun riskinarvioinnit.',
        },
        c8e7738b_358c_4c79_9fc2_a564f15a6e37: {
            title: 'Tietoturva verkko- ja tietojärjestelmissä',
            description: 'Yhteisöjen tulee toteuttaa tietoturva verkko- ja tietojärjestelmien hankinnassa, kehittämisessä ja ylläpidossa, mukaan lukien haavoittuvuuksien käsittely.',
        },
        '72520ed1_51ff_4983_a785_9be1f72cf7dd': {
            title: 'Haavoittuvuuden käsittely ja paljastaminen',
            description: 'Yhteisöjen on otettava käyttöön politiikkaa ja menettelyjä haavoittuvuuden koordinoitua paljastamista ja käsittelyä varten.',
        },
        f009e65d_d55e_4c98_9532_63c150694698: {
            title: 'Kyberturvallisuusriskien arvioinnin tehokkuus',
            description: 'Yhteisöjen on otettava käyttöön politiikkaa ja menettelyjä kyberturvallisuuden riskienhallintatoimenpiteiden tehokkuuden arvioimiseksi.',
        },
        '00e88824_675d_4b37_960c_757b3176c079': {
            title: 'Salaus- ja salauskäytännöt',
            description: 'Yhteisöjen on otettava käyttöön kryptografian ja tarvittaessa salauksen käyttöä koskevat periaatteet ja menettelyt.',
        },
        de29d287_81f1_46b1_b327_9cbf06ddad3d: {
            title: 'HR turvallisuus, kulunvalvonta, omaisuudenhallinta',
            description: 'Yksiköt toteuttavat henkilöstöturvallisuutta, kulunvalvontakäytäntöjä ja omaisuudenhallintaa.',
        },
        '750f4459_9ba4_415e_bc47_5b0ec27d35a2': {
            title: 'Monivaiheinen todennus',
            description: 'Yksiköiden on käytettävä monitekijätodennus- tai jatkuvatodennusratkaisuja, suojattua ääni-/video-/tekstiviestintää ja suojattuja hätäviestintäjärjestelmiä.',
        },
        '359474f9_1820_4e3e_9031_a593b66ccde4': {
            title: 'Varhainen varoitus CSIRT:lle (24h)',
            description: 'Yhteisöjen on annettava toimivaltaiselle CSIRT:lle ennakkovaroitus 24 tunnin kuluessa siitä, kun ne ovat havainneet merkittävän tapahtuman.',
        },
        '66acd92c_3091_4148_b935_68b0d24308e7': {
            title: 'Tapahtumailmoitus CSIRT:lle (72h)',
            description: 'Yhteisöjen on toimitettava vaaratilanteesta ilmoitus toimivaltaiselle CSIRT:lle 72 tunnin kuluessa siitä, kun ne ovat havainneet merkittävän vaaratilanteen.',
        },
        '68269688_06b2_43ea_8e3e_a84532086557': {
            title: 'Loppuraportti CSIRT:lle (1 kuukausi)',
            description: 'Yhteisöjen on toimitettava loppuraportti toimivaltaiselle CSIRT:lle viimeistään kuukauden kuluttua tapahtumailmoituksesta.',
        },
        c8512bde_b064_4926_b333_06b9c95cecd7: {
            title: 'Soveltuvuuden määrittäminen AI:n toimiin',
            description: 'Selvitä, harjoittaako organisaatio AI Vietnamin AI lain alaista toimintaa ja dokumentoi sovellettavuuspäätös.',
        },
        '3a87ad63_179a_45ba_8743_98d865df80ab': {
            title: 'AI järjestelmän riskiluokitus riskitason mukaan',
            description: 'Luokittele jokainen AI-järjestelmä riskitason mukaan (minimaalinen, rajoitettu, korkea, ei-hyväksyttävä) käyttämällä ohjattua luokitusmenetelmää.',
        },
        '0f4475d3_77a6_4b41_bfe2_d4b299b1bd45': {
            title: 'AI Järjestelmärekisterin merkintöjen ylläpito',
            description: 'Ylläpidä AI-järjestelmien rekisteriä, jossa on järjestelmän nimi, omistaja, tarkoitus, käyttöönottokonteksti, riskitaso ja toimittajan osallistuminen.',
        },
        '2a73ce94_906a_4c95_8a45_a5bdf2a8118c': {
            title: 'AI Järjestelmäaineisto, jossa on esineiden tarkistuslista ja todisteita',
            description: 'Rakenna ja ylläpidä järjestelmäaineistoa, joka sisältää vaaditun artefaktien tarkistuslistan, todistekirjaston ja versiohistorian.',
        },
        e92cfb42_d91b_444f_adbc_c03903844eac: {
            title: 'Avoimuusvelvoitteet AI verkonhaltijoille',
            description: 'Toteuta avoimuustoimenpiteitä AI-järjestelmille, mukaan lukien käyttäjäilmoitukset, AI osallistumisen paljastaminen ja järjestelmädokumentaatio.',
        },
        '45eb43d7_9b63_4166_aa0d_fa6aeeaa70ba': {
            title: 'AI tulosten merkitseminen, vesileima ja metatietojen allekirjoitus',
            description: 'Ota AI:n luomalle sisällölle merkitseminen ja vesileima, mukaan lukien metatietojen allekirjoitus tarvittaessa.',
        },
        '9566c94d_0dd3_419c_869f_6707d2a972f9': {
            title: 'AI:n luoman sisällön paljastamisvaatimukset',
            description: 'Ilmoita käyttäjille, kun sisältö on AI:n luomaa, ja tarjoa käyttäjille mekanismeja AI:n osallistumisen tunnistamiseen.',
        },
        '21d39ea5_df78_45c6_8710_b203db229e25': {
            title: 'Selittävyys ja käyttäjäkohtaiset läpinäkyvyystehtävät',
            description: 'Tarjoa selitettäviä tuloksia AI päätöksille, jotka vaikuttavat yksilöihin, ja ylläpidä käyttäjille suunnattua avoimuusdokumentaatiota.',
        },
        f523fbc2_156d_466d_ad50_6a0c51b3ac9d: {
            title: 'Ihmisten valvontavelvollisuudet korkean riskin AI järjestelmissä',
            description: 'Varmista, että riskialttiille AI-järjestelmille on olemassa ihmisen valvontamekanismit, mukaan lukien eskalointi- ja ohitusominaisuudet.',
        },
        bd180a89_4976_4790_9e50_f1c5c97dc576: {
            title: 'AI tapahtumien valvonta ja havaitseminen',
            description: 'Ota käyttöön valvontaominaisuudet havaitaksesi AI järjestelmähäiriöt, toimintahäiriöt ja haitalliset seuraukset.',
        },
        e7c1c60b_856c_4d3d_b6c9_092cde9b56bd: {
            title: 'Vakavan AI tapauksen ilmoittaminen viranomaisille',
            description: 'Ilmoita vakavista AI vaaratilanteista toimivaltaiselle viranomaiselle säädetyn ajan (72 tunnin) kuluessa.',
        },
        '97f15adc_558a_49f2_bb06_86597cb9579b': {
            title: 'AI tapausilmoitusten aikataulun noudattaminen',
            description: 'Noudata AI-tapahtumien ilmoitusaikatauluja, mukaan lukien alustavat ilmoitukset, päivitykset ja sulkemisraportit.',
        },
        '96c33d7e_fe52_4730_adac_70c8c477c88e': {
            title: 'AI vaikutustenarviointi korkean riskin järjestelmistä',
            description: 'Tee vaikutustenarviointeja suuririskisille AI-järjestelmille, jotka kattavat riskit, lieventämiset, inhimillisen valvonnan ja seurantasuunnitelmat.',
        },
        '0f3b72c0_1030_44ff_ba53_596f45601cd7': {
            title: 'Kontrolloitu testaus / hiekkalaatikkokelpoisuuden määrittäminen',
            description: 'Selvitä kelpoisuus kontrolloituun testaukseen (hiekkalaatikkoon) ja dokumentoi kelpoisuusarviointi.',
        },
        '4a6aed20_f655_4182_a760_aef1f329d5df': {
            title: 'Sandbox-hakemusten jättämisen ja hyväksynnän seuranta',
            description: 'Lähetä hiekkalaatikkohakemukset toimivaltaiselle viranomaiselle ja seuraa hyväksynnän tilaa toimituksen elinkaaren ajan.',
        },
        '8376988d_cd32_491e_a5b8_3340d3973358': {
            title: 'Hiekkalaatikon valvontapisteet ja valmistuminen/poistuminen',
            description: 'Suorita säännöllisiä seurantatarkastuksia hiekkalaatikkoon osallistumisen aikana ja valmistele valmistumis-/poistumisraportit.',
        },
        '7cb2b544_0512_4702_a092_9f1c10872376': {
            title: 'Poikkeus-/poikkeusasiakirjojen merkitseminen perusteluineen',
            description: 'Dokumentoi kaikki merkintöjä koskevat poikkeukset ja perustelut ja todisteet.',
        },
        dccd7fbf_872a_4716_87a4_b9c5ce8e2387: {
            title: 'Tietojen lokalisointi vietnamilaisille käyttäjille',
            description: 'Vietnamilaisten käyttäjien tiedot Vietnamissa tarjotuista palveluista on säilytettävä Vietnamin alueella.',
        },
        '9eb86a03_e165_4963_8813_2272b638d529': {
            title: 'Kyberturvallisuustapahtumien raportointi',
            description: 'Kyberturvallisuushäiriöistä on ilmoitettava toimivaltaisille viranomaisille hyvissä ajoin.',
        },
        '736ac7de_7a6a_4bfd_b2be_c594e290038a': {
            title: 'Tietojärjestelmän luokitus',
            description: 'Tietojärjestelmät on luokiteltava tärkeysasteen mukaan ja sovellettava asianmukaisia ​​turvatoimia.',
        },
        ffb807cf_f121_4b52_8e50_e091c33180c9: {
            title: 'Kyberturvallisuusriskien arviointi',
            description: 'Tietojärjestelmille on tehtävä säännöllisiä kyberturvallisuusriskien arviointeja.',
        },
        a4de518b_a5f1_481f_9b08_f8d2bc9db7e5: {
            title: 'Soveltamisalan määritys (päätöspuu)',
            description: 'Organisaatioiden on määritettävä määrätyn päätöspuun avulla, kuuluvatko ne asetuksen 53 soveltamisalaan.',
        },
        '01f99e9a_5e30_4d14_aae5_a2ce322a700b': {
            title: 'Tietojen lokalisoinnin arviointi ja valmius',
            description: 'Organisaatioiden on arvioitava tietojen lokalisointivaatimukset ja osoitettava valmius.',
        },
        cf24f011_dc23_4d84_aef5_994dde440571: {
            title: 'Varastointi- ja säilytysvalmiuden tarkistuslista',
            description: 'Tallennus- ja säilytysvalmiuden tarkistuslista on täytettävä tietojen lokalisoinnin vaatimustenmukaisuuden varmistamiseksi.',
        },
        b3884cb8_c785_4258_9517_9e393be4f4e1: {
            title: 'Paikallinen läsnäolovaatimuksen arviointi',
            description: 'Organisaatioiden on arvioitava, sovelletaanko paikallisia läsnäolovaatimuksia, ja suunnitella niiden mukaisesti.',
        },
        c1dc645b_cb1a_42a6_8ca5_9b15cfa84156: {
            title: 'Viranomaiset pyytävät vastaanottoa ja vahvistusta',
            description: 'Viranomaisten pyynnöt on vastaanotettava, tarkistettava ja käsiteltävä määrättyjen menettelyjen mukaisesti.',
        },
        d4571651_f39d_4c51_8f2d_7248c37f7b14: {
            title: 'Viranomainen pyytää vastausta määräajassa',
            description: 'Viranomaisten pyyntöihin on vastattava säädetyssä määräajassa.',
        },
        '66c1df70_3456_40e9_b645_6c6bd3be702e': {
            title: 'Viranomaisten pyyntöjen käsittelyloki',
            description: 'Kaikista viranomaispyyntöjen käsittelytoimista on pidettävä lokia.',
        },
        '04bbba2a_9804_4199_a517_054b6f4689fb': {
            title: 'Soveltuvuus ja soveltamisalan määrittäminen (konsolidoitu)',
            description: 'Organisaatioiden on selvitettävä, kuuluvatko ne konsolidoidun kyberturvallisuuslain soveltamisalaan, joka kattaa sekä vanhan lain 24/2018 että asetuksen 53/2022.',
        },
        '89a21eaf_cf09_4fec_bbb4_a1dadd652f6e': {
            title: 'Tietojärjestelmän luokittelu tärkeystason mukaan',
            description: 'Tietojärjestelmät on luokiteltava tärkeysasteen mukaan (yleinen, tärkeä, kriittinen) ja vastaavat turvallisuusvelvoitteet.',
        },
        '83ad981e_23ff_4a92_bebd_cd24487e912f': {
            title: 'Kyberturvallisuuden hallintorakenne ja vastuullinen henkilöstö',
            description: 'Organisaatioiden on perustettava kyberturvallisuuden hallintorakenteet ja nimettävä vastuuhenkilöt.',
        },
        '98d9a523_fefb_400b_bb7f_ebaf609a6ed4': {
            title: 'Säännöllinen kyberturvallisuusriskien arviointi',
            description: 'Kaikille turvaluokiteltuille tietojärjestelmille on tehtävä säännöllisiä kyberturvallisuusriskien arviointeja.',
        },
        '5236539f_4c1a_4eff_b2eb_fc11eb4eab23': {
            title: 'Turvatoimenpiteet tietojärjestelmän luokituksen mukaan',
            description: 'Teknisiä turvatoimenpiteitä tulee soveltaa kunkin tietojärjestelmän luokitustason mukaisesti.',
        },
        b69a8be2_ae0c_408c_b8f1_73b573eca876: {
            title: 'Kulunvalvonta ja identiteetin hallinta',
            description: 'Tietojärjestelmiin tulee ottaa käyttöön kulunvalvonta- ja identiteetinhallintatoimenpiteet.',
        },
        ac774c26_feb7_4123_b799_c54f77bd87b5: {
            title: 'Kirjaaminen, seuranta ja kirjausketju',
            description: 'Tietojärjestelmien on ylläpidettävä kirjaus-, seuranta- ja kirjausketjuominaisuudet.',
        },
        a86cb01d_239b_44e2_9e09_e6973bcf9658: {
            title: 'Haavoittuvuuksien hallinta ja korjaus',
            description: 'Haavoittuvuuksien hallinta- ja korjausprosessit on perustettava ja ylläpidettävä.',
        },
        '826a789f_0c03_4918_81a1_6aa1cc2c205c': {
            title: 'Vietnamilaisten käyttäjien tietojen lokalisointi',
            description: 'Vietnamilaisten käyttäjien tiedot Vietnamissa tarjotuista palveluista on säilytettävä Vietnamin alueella.',
        },
        '8bee2b51_ace8_4118_89c8_010b96640016': {
            title: 'Paikallinen läsnäolovaatimukset ulkomaisille palveluntarjoajille',
            description: 'Vietnamissa toimivien ulkomaisten palveluntarjoajien on ylläpidettävä paikallista läsnäoloa määräysten mukaisesti.',
        },
        '74d89250_b45f_41b6_bfe3_f915071580de': {
            title: 'Paikallinen tallennusarkkitehtuuri ja rajat ylittävän virtauksen dokumentaatio',
            description: 'Paikallinen tallennusarkkitehtuuri on dokumentoitava ja rajat ylittävät tietovirrat on kartoitettava ja ohjattava.',
        },
        '04dec6f8_3280_494c_8506_292804b16d4d': {
            title: 'Kyberturvallisuushäiriöiden havaitseminen ja reagointi',
            description: 'Organisaatioilla on oltava valmiudet havaita kyberturvallisuushäiriöitä ja reagoida niihin.',
        },
        faeda71b_5050_497d_b08b_e3f3f5313ad4: {
            title: 'Pakollinen vaaratilanteesta ilmoittaminen toimivaltaiselle viranomaiselle',
            description: 'Hyväksytyt kyberturvallisuushäiriöt on ilmoitettava osoitteeseen MPS/MIC määrätyissä aikatauluissa.',
        },
        d732b5a2_2b2c_48a0_955d_97a39daf132d: {
            title: 'Tapahtumailmoitusten aikataulun noudattaminen',
            description: 'Tapahtumailmoitusten tulee noudattaa määrättyjä aikatauluvaatimuksia (alkuraportti 24 tunnin sisällä, täydellinen raportti 72 tunnin sisällä).',
        },
        '8e2dffb0_3481_44f0_baf2_130ddd8cfe12': {
            title: 'Viranomaisten pyyntöjen käsittely ja yhteistyö',
            description: 'Viranomaisten pyynnöt on käsiteltävä ripeästi ja yhteistyö on tehtävä määrättyjen menettelyjen mukaisesti.',
        },
        e4c5b870_1d61_48fe_a6c2_1b9f5a458ea3: {
            title: 'Viranomaisten pyyntöjen tapausrekisteri ja tiedonantoloki',
            description: 'Kaikki viranomaisten pyynnöt ja ilmoitukset on kirjattava tapausrekisteriin, jossa on täydellinen kirjausketju.',
        },
        '8eff8b17_65bc_4f94_8ece_c509a9fd9abb': {
            title: 'Toimittajan ja kolmannen osapuolen turvallisuusarviointi',
            description: 'Kolmannen osapuolen toimittajien, joilla on pääsy tietojärjestelmiin, on suoritettava turvallisuusarviointi.',
        },
        '69891e1b_97f6_464c_9cce_b1276794fe4a': {
            title: 'Toimittajan tietojen käyttö- ja tapahtumailmoitusten hallinta',
            description: 'Toimittajan tietojen käyttö on dokumentoitava ja sopimuskohtaiset tapahtumat ilmoittavat velvoitteet.',
        },
        d89b6bc0_7a9e_4816_b31e_7c0abc02e90c: {
            title: 'Turvallisuustietoisuuden koulutus ja harjoitukset',
            description: 'Säännöllisiä kyberturvallisuustietoisuuskoulutuksia ja pöytäharjoituksia on suoritettava.',
        },
        f9c5d3cc_5bb2_4b30_a5fb_ada34e407981: {
            title: 'Säännöllinen kynätesti ja turvatarkastus',
            description: 'Turvaluokiteltuille tietojärjestelmille on suoritettava määräajoin tunkeutumistestejä ja turvatarkastuksia.',
        },
        '88dd9877_d2e2_4e41_a248_ae39b1ff478f': {
            title: 'Ylläpidä digitaalista tietovarastoa',
            description: 'Organisaatioiden on ylläpidettävä kattavaa digitaalista tietovarastoa, joka luetteloi kaikki tietovarat.',
        },
        '18e4bfd1_42cf_4d87_870f_5a34242962ef': {
            title: 'Tietojen luokitus herkkyyden ja tärkeyden mukaan',
            description: 'Tiedot on luokiteltava tietolain määrittelemien herkkyys- ja tärkeystasojen mukaan.',
        },
        b89981ac_7559_46f9_a74c_a143976dde4e: {
            title: 'Määritä käsittelysäännöt luokitustason mukaan',
            description: 'Käsittelysäännöt on vahvistettava kullekin tiedon luokitustasolle.',
        },
        '3bf8019d_b72c_41d7_82cf_fc367efd9526': {
            title: 'Seuraa tietojen jakamista ja luovuttamista hyväksynnöillä',
            description: 'Kaikkea tietojen jakamista ja luovuttamista on seurattava asianmukaisilla hyväksynnöillä.',
        },
        '8623eca2_4cf9_41bd_bfbd_2300c06ca1f3': {
            title: 'Rajat ylittävän tiedonsiirron hallinto',
            description: 'Rajat ylittävän tiedonsiirron on täytettävä tietolain mukaiset hallintovaatimukset.',
        },
        '006b8aa6_9379_4eff_b8ab_935f9a087601': {
            title: 'Säilytä hallintoperiaatteet ja vastuut',
            description: 'Organisaatioiden on ylläpidettävä tiedonhallintakäytäntöjä ja jaettava hallinnointivastuita.',
        },
        f12fc090_5c3e_4fa8_9813_74bea0359c5c: {
            title: 'Vuosittainen hallintotodistus',
            description: 'Jatkuvan vaatimustenmukaisuuden vahvistamiseksi on täytettävä vuosittainen hallintotodistus.',
        },
        f6c3060a_5e52_4f97_b1af_c6a5f00bfaee: {
            title: 'Tietojen riskinarviointi',
            description: 'Tietoriskien arviointi on suoritettava tietojen hallinnointiin liittyvien riskien tunnistamiseksi ja vähentämiseksi.',
        },
        '6f228998_1c61_49cf_a585_3d3e9baff55a': {
            title: 'Alustan operaattorin vastuuvelvollisuus ja hallintovelvollisuudet',
            description: 'Perustetaan alustan ylläpitäjien vastuullisuusrakenteet, mukaan lukien hallintokäytännöt, valvontavastuut ja täytäntöönpanovelvoitteet.',
        },
        eee1d423_3783_4530_bc64_eef6fd9d02dc: {
            title: 'Alustasääntöjen julkaiseminen ja tiedon avoimuus',
            description: 'Julkaise alustan säännöt, kaupankäynnin ehdot ja toiminnalliset tiedot avoimesti ja helposti saatavilla olevalla tavalla.',
        },
        '50b57433_7a51_4e92_9c84_c748d6865b70': {
            title: 'Verkkokaupan myyntisivusto/sovellusilmoitus MOITille',
            description: 'Lähetä MOITille ilmoitus verkkokaupan myyntisivustoista ja sovelluksista Online.gov.vn-osoitteen tai tiettyjen kanavien kautta.',
        },
        '92854abe_6654_4155_8495_f3419a8a8da0': {
            title: 'Verkkokaupan palvelualustan/markkinapaikan rekisteröinti MOIT:iin',
            description: 'Rekisteröi sähköisen kaupankäynnin palvelualustoja ja markkinapaikkoja MOIT-palveluun mukaan lukien aineistojen toimittamisen ja hyväksynnän seuranta.',
        },
        b8bff437_805e_4a4a_a62e_89cb90b89d3f: {
            title: 'Myyjärekisterin ylläpito (kotimainen ja ulkomainen)',
            description: 'Ylläpidä rekisteriä kaikista alustalla olevista myyjistä, mukaan lukien kotimaiset ja ulkomaiset myyjät sisäänkäynnin, todentamisen ja elinkaaren seurannan avulla.',
        },
        '75778639_5235_4d13_a728_fa772c27fc0c': {
            title: 'Suoratoiston myyjärekisteri ja vaatimustenmukaisuuden valvonta',
            description: 'Ylläpidä livestream-myyjien rekisteriä henkilöllisyyden vahvistamisen, toiminnan seurannan ja vaatimustenmukaisuuden valvonnan avulla.',
        },
        '405f2932_742a_4bcb_9784_1a3d81f2e714': {
            title: 'Affiliate- ja markkinointikumppanirekisteri',
            description: 'Ylläpidä kumppanimarkkinoijien ja suosituskumppaneiden rekisteriä, jolla on sopimuksen tila, sisältöohjeet ja vaatimustenmukaisuusvahvistukset.',
        },
        cce5a4eb_a576_459c_95b5_bdd0e8ed30a8: {
            title: 'Tuoteluettelo ja kiellettyjen/ehdollisten tuotteiden valvonta',
            description: 'Ota käyttöön tuotelistauksen valvontaa, mukaan lukien kiellettyjen ja ehdollisten tavaroiden seulonta-, valvonta- ja täytäntöönpanotoimet.',
        },
        '9fc01e19_18dd_4d60_b95a_c1f4bcdbb6ae': {
            title: 'Kuluttajavalitukset kirjataan sisääntuloon, toimenpiteisiin ja kirjausketjuun',
            description: 'Ylläpidä valitusrekisteriä vastaanottorekistereistä, tutkintatoimista, ratkaisutuloksista ja täydellisestä kirjausketjusta.',
        },
        '8a21764e_e5f4_4934_9913_c235f4c462ca': {
            title: 'Valituksen käsittelymenettely ja ratkaisuaikataulut',
            description: 'Luo valitusten käsittelymenettelyt määritellyillä kuittaus-, tutkinta- ja ratkaisuaikatauluilla.',
        },
        '8406373c_ec31_4bf8_810f_72d9d73b3de8': {
            title: 'Sisällön valvonta, seuranta ja poistamisen valvonta',
            description: 'Ota käyttöön sisällön valvonta-, valvonta- ja poistomenettelyjä laittoman, kielletyn tai loukkaavan sisällön osalta.',
        },
        '6f32c315_323b_4ce6_a4bb_3262a5b56afe': {
            title: 'Poisto- ja täytäntöönpanotoimet rekisteröidään todisteiden säilyttämisessä',
            description: 'Pidä rekisteriä poisto- ja täytäntöönpanotoimista päätösrekistereillä, todisteiden säilyttämisellä ja myyjäilmoituksilla.',
        },
        c0bbbfbf_29bd_4813_89f1_956eab9f1a23: {
            title: 'Alustapolitiikka ja tiedonantorekisteri (versiot, julkaisupäivät, hyväksyntä)',
            description: 'Pidä rekisteriä alustan käytännöistä ja ilmoituksista versiohistorian, julkaisupäivämäärien ja hyväksymistodistusten kanssa.',
        },
        c9fcbe90_622d_43ca_9ec6_db26c0eda490: {
            title: 'Kuluttajatiedotusvelvollisuudet - tarkat tuotetiedot ja petoksen esto',
            description: 'Varmista tarkat, selkeät tuote- ja palvelutiedot ja toteuta toimenpiteitä petollisten käytäntöjen estämiseksi alustalla.',
        },
        '8f49603b_454c_4470_ab07_2f845a447595': {
            title: 'Ulkomaisen alustan toimintaehdot ja rekisteröinti/ilmoitus',
            description: 'Noudata vietnamilaisia käyttäjiä palvelevien ulkomaisten alustojen käyttöehtoja, mukaan lukien rekisteröinti- tai ilmoitusvaatimukset.',
        },
        e0dc85cc_5d2a_4142_b1a6_cc2fde891f7b: {
            title: 'Paikallinen edustus tai valtuutus ulkomaisille alustoille',
            description: 'Nimeä paikallinen edustaja tai valtuutettu taho Vietnamissa ulkomaisille alustoille lain edellyttämällä tavalla.',
        },
        '5719f47c_f298_429e_90c3_df6ceed856e1': {
            title: 'Rajatylittävän myyjän ja valitusten käsittelyn koordinointi',
            description: 'Koordinoi rajat ylittävää myyjien käsittelyä ja valitusten ratkaisua, mukaan lukien eskalointi ja todisteiden hallinta.',
        },
        c0b3db29_9161_4cef_a889_94e3b85d78fe: {
            title: 'Valtion verkkokaupan hallintajärjestelmän vuorovaikutus ja raportointivalmius',
            description: 'Valmistaudu vuorovaikutukseen valtion sähköisen kaupan hallintajärjestelmän kanssa, mukaan lukien tietokenttien kartoitus ja raportointiominaisuus.',
        },
        '145a7016_720a_4a06_badd_00145842821e': {
            title: 'Hyväksynnän jälkeinen materiaalimuutosilmoitus',
            description: 'Lähetä ilmoitukset alustan tietojen, käytäntöjen tai toimintojen olennaisista muutoksista ensimmäisen hyväksynnän jälkeen.',
        },
        '4bc0ee2a_610d_490d_8122_d6d1afadaa22': {
            title: 'Ilmoitus irtisanomisesta ja deaktivoinnista',
            description: 'Lähetä irtisanomis- tai deaktivointiilmoitukset, kun lopetat sähköisen kaupankäynnin, mukaan lukien julkiset ilmoitukset ja kuluttajansuojakäsittely.',
        },
        be95bc85_2b25_4de7_a3e7_154b780289e4: {
            title: 'Luottamusmerkki- ja uskottavuusluokitusohjelman kelpoisuus ja ylläpito',
            description: 'Arvioi kelpoisuus luottamusmerkki- ja uskottavuusluokitusohjelmiin ja säilytä vaatimustenmukaisuustodistus jatkuvasta osallistumisesta.',
        },
        f639afe2_18f2_4c0a_82d9_626308994267: {
            title: 'Suostumus henkilötietojen käsittelyyn',
            description: 'Henkilötietojen käsittely edellyttää rekisteröidyn suostumusta, ellei poikkeusta sovelleta.',
        },
        '5dd8e1b9_ac2d_48af_8c62_538937f0a4f7': {
            title: 'Sensitive personal data: tell the data subject the data is sensitive',
            description: 'For processing sensitive personal data, the data subject must be told that the data to be processed is sensitive personal data (Art. 11(8)). Art. 28 adds the measures for protecting it: applying the measures of Art. 26(2) and Art. 27; designating a unit with the function of protecting personal data and staff in charge of it, and exchanging their details with the specialised personal data protection agency; and notifying the data subject that their sensitive personal data is being processed, except in the cases of Art. 13(4), Art. 17 and Art. 18.',
        },
        '5a958c76_6943_4449_a15c_ddb503ce526f': {
            title: 'Käyttöoikeus',
            description: 'Rekisteröidyillä on oikeus tutustua rekisterinpitäjän hallussa oleviin henkilötietoihinsa.',
        },
        '1ae0a58b_65a5_4a32_9b2c_0baed7799a32': {
            title: 'Oikeus oikaisuun',
            description: 'Rekisteröidyillä on oikeus pyytää virheellisten henkilötietojen oikaisua.',
        },
        '25c61e57_273f_48cb_a318_d6315ad744f1': {
            title: 'Oikeus poistaa',
            description: 'Rekisteröidyillä on oikeus pyytää henkilötietojensa poistamista.',
        },
        '0eafe047_ed8b_4a9a_bda9_b4ef2ae353c2': {
            title: 'Oikeus rajoittaa käsittelyä',
            description: 'Rekisteröidyillä on oikeus pyytää henkilötietojensa käsittelyn rajoittamista.',
        },
        '77f9677a_f436_43ec_a947_3bdc44fc192a': {
            title: 'Oikeus vastustaa',
            description: 'Rekisteröidyillä on oikeus vastustaa henkilötietojensa käsittelyä.',
        },
        c8b19e70_782c_49c9_8a71_14bef4147d6b: {
            title: 'Oikeus peruuttaa suostumus',
            description: 'Rekisteröidyillä on oikeus peruuttaa aiemmin antamansa suostumus milloin tahansa.',
        },
        '7f472a1c_b37f_498d_adc7_df8d8eafbae9': {
            title: 'Rikkomusilmoitus viranomaiselle (72h)',
            description: 'Rekisterinpitäjän tulee ilmoittaa tietosuojaministeriölle 72 tunnin kuluessa henkilötietojen tietoturvaloukkauksen havaitsemisesta.',
        },
        '98bed7ab_e814_43f5_8e70_e95c413266a1': {
            title: 'Rikkomusilmoitus rekisteröidyille',
            description: 'Rekisterinpitäjän tulee ilman aiheetonta viivytystä ilmoittaa rekisteröidyille henkilötietojen tietoturvaloukkauksesta.',
        },
        '404a3576_dd84_4bac_acf4_29719615c45e': {
            title: 'DPIA suuren riskin käsittelyyn',
            description: 'Tietosuojavaikutusten arviointi on suoritettava käsittelytoimista, jotka todennäköisesti johtavat suureen riskiin.',
        },
        '29d9ed94_9360_457c_9aca_37aefac66969': {
            title: 'Rajat ylittävien siirtojen arviointi',
            description: 'Vaikutustenarviointi on tehtävä ennen henkilötietojen siirtämistä Vietnamin ulkopuolelle.',
        },
        '9620ff08_b83c_4e1f_9350_eba835b145fe': {
            title: 'Kirjaa käsittelytoimista',
            description: 'Rekisterinpitäjien on ylläpidettävä käsittelytoimien rekisteriä (RoPA), joka dokumentoi kaikki käsittelytoimet.',
        },
        '96ee0ff3_3ead_431e_aa57_08ccff4821fc': {
            title: 'Tietojenkäsittelysopimustietueet',
            description: 'Käsittelijöiden ja alikäsittelijöiden kanssa tehdyistä tietojenkäsittelysopimuksista on pidettävä kirjaa.',
        },
        '434dc319_1922_49e4_b757_37622135c4b2': {
            title: 'Rajat ylittävien siirtojen suojatoimet',
            description: 'Henkilötietojen siirto Vietnamin ulkopuolelle edellyttää asianmukaisia suojatoimia ja dokumentoituja arvioita.',
        },
        '3372c651_d5a8_4cae_ae57_2fc684ad0768': {
            title: 'Kuittaus 2 työpäivän sisällä',
            description: 'Rekisteröidyn pyyntö on kuitattava 2 työpäivän kuluessa vastaanottamisesta.',
        },
        b15fa62d_3f52_4e42_8442_f4969e08883a: {
            title: 'Valmistuminen 15 työpäivän kuluessa',
            description: 'Rekisteröityjen pyynnöt on täytettävä 15 työpäivän kuluessa, ja sitä voidaan jatkaa 5 työpäivällä.',
        },
        '5d81233c_f671_4827_b874_827762edfc20': {
            title: 'MoPS vaikutustenarviointiaineiston arkistointi',
            description: 'Korkean riskin käsittelyä suorittavien organisaatioiden on toimitettava vaikutustenarviointiasiakirja yleisen turvallisuuden ministeriölle määrättyä hallintomenettelyä noudattaen.',
        },
        '83bf5bf8_edbd_4f0a_9f8e_5f47db01324d': {
            title: 'MoPS rajat ylittävää siirtoa koskevien asiakirjojen arkistointi',
            description: 'Organisaatioiden, jotka siirtävät henkilötietoja Vietnamin ulkopuolelle, on toimitettava rajat ylittävän siirron vaikutustenarviointiasiakirja yleisen turvallisuuden ministeriölle.',
        },
        '9454e465_e618_4b6f_80b3_d79fdd9cc5cb': {
            title: 'Palveluntarjoajan sertifiointia käsitellään',
            description: 'Henkilötietojen käsittelyä palveluna tarjoavien organisaatioiden on hankittava palvelun käsittelytodistus yleisen turvallisuuden ministeriöltä.',
        },
        b88aec34_046a_4e0a_9c77_ddad27ea4bc4: {
            title: 'Vaikutustenarviointiaineiston käsittely, kun laukaisut täyttyvät',
            description: 'Käsittelyvaikutusten arviointiasiakirjat on toimitettava, kun säädetyt triggerit täyttyvät.',
        },
        '9d34a206_b85e_4649_a23b_e2c6f1398402': {
            title: 'Rajat ylittävien siirtojen vaikutusasiakirja',
            description: 'Kansainvälistä tiedonsiirtoa varten on laadittava ja arkistoitava rajat ylittävän tiedonsiirron vaikutusasiakirja.',
        },
        '210d3ec3_e6f2_424b_ae25_cad76bc13320': {
            title: 'Tietosuojavastaavan nimitys',
            description: 'Tietosuojavastaava on nimettävä asetuksen 13 mukaisesti.',
        },
        '776183b2_2797_40e8_8d81_c4f512c566d2': {
            title: 'Hallintoartefaktit (käytännöt, roolit, menettelyt)',
            description: 'Hallintoartefaktit, mukaan lukien politiikat, roolit ja menettelyt, on säilytettävä.',
        },
        '4f1385f1_6dfc_4368_b4af_1040cd3f77b7': {
            title: 'Todistuspaketin täydellisyys ja muuttumaton kirjausketju',
            description: 'Todistuspakettien on oltava täydellisiä, ja muuttumaton kirjausketju on säilytettävä.',
        },
        f6826e26_8991_4a0f_9117_481490230bdd: {
            title: 'Arkistoinnin työnkulun tuki',
            description: 'Tapausten arkistointityönkulkua on tuettava vaikutusten arvioinnissa ja rajat ylittävissä asiakirjoissa.',
        },
        '8a28ee33_305a_491f_a93f_90e59f8da91b': {
            title: 'Prosessorin sopimusvaatimukset',
            description: 'Tietojen käsittelijöille on asetettava sopimusvaatimukset ja niitä on valvottava.',
        },
        '4fd36e83_8286_40e0_b9b2_a4bc5e8377b3': {
            title: 'Vuotuinen vaatimustenmukaisuustarkastus',
            description: 'Vuosittainen vaatimustenmukaisuustarkastus on suoritettava jatkuvan noudattamisen varmistamiseksi.',
        },
        '8b6b6c13_0d32_4439_819f_4bd8cff02217': {
            title: 'Viranomaisten hakemusten tietueet',
            description: 'Kaikista viranomaisilmoituksista on säilytettävä kirjaa.',
        },
        e7ce6c23_03dc_42dd_bccd_fa571c3afee3: {
            title: 'Henkilötietojen käsittelyn lailliset perusteet',
            description: 'Henkilötietojen käsittelyn tulee perustua PDPL:n määrittelemiin laillisiin perusteisiin.',
        },
        c919c66c_6a74_4403_bb97_04b773d270bf: {
            title: 'Sensitive personal data: identify it, protect it, and say so when seeking consent',
            description: "Law 91/2025/QH15 defines sensitive personal data in Article 2(3) as personal data tied to an individual's privacy which, when infringed, directly affects the lawful rights and interests of agencies, organisations and individuals, on a list the Government issues; Decree 356/2025/NĐ-CP Article 4(1) is that list. The Law attaches no consent rule to sensitive personal data as a class; sector articles carry their own. Article 26(1)(a) requires the data subject's consent in collecting and processing personal data for health information and in insurance business, except in the Article 19(1) cases. Article 27(1)(b) forbids using a data subject's credit information for credit scoring, credit rating, credit information assessment or creditworthiness assessment without their consent. Article 31(3)(a) forbids location tracking by radio-frequency identification and other technologies, except with the data subject's consent, at the request of a competent authority as provided by law, or where the law provides otherwise. Among what the instruments attach to sensitive data: in processing it, an agency or organisation must set rules limiting access rights, a processing procedure and security measures (Decree 356 Article 4(2)); when consent to process it is sought, the data subject must be told that the data to be processed is sensitive personal data (Decree 356 Article 6(4)); a transfer of it must carry physical security for storage and transmission devices, encryption, anonymisation and other security measures (Decree 356 Article 7(2)); an organisation or individual in finance, banking or credit information must fully comply with the rules on protecting sensitive personal data (Law 91 Article 27(1)(a)), and where a data subject's sensitive data in that sector is disclosed or lost, whoever directly collected it must notify the specialised personal data protection agency and the data subject within 72 hours of discovering it (Decree 356 Article 8(3)); and a small business, start-up, household business or micro-enterprise that directly processes sensitive personal data does not have the option or exemption Law 91 Article 38(2)–(3) and Decree 356 Article 41 give from Articles 21 and 22 and Article 33(2) of the Law. Record which of the organisation's personal data falls on that list.",
        },
        '419cc7c3_a257_4293_a054_304dc2fcb8df': {
            title: 'Rekisteröidyn oikeudet (pääsy, oikaisu, poistaminen, rajoittaminen, kohde, siirrettävyys)',
            description: 'Rekisteröidyillä on kattavat oikeudet, mukaan lukien pääsy, oikaisu, poistaminen, rajoittaminen, vastustaminen ja siirrettävyys.',
        },
        '638e6e25_3a74_4864_8bff_8e21ab82860b': {
            title: 'Rikkomusilmoitus viranomaiselle',
            description: 'Rekisterinpitäjien on ilmoitettava toimivaltaiselle viranomaiselle henkilötietojen tietoturvaloukkauksista säädetyssä määräajassa.',
        },
        '6e75c111_66b1_4726_8c27_6537846edb30': {
            title: 'Rikkomusilmoitus rekisteröidyille',
            description: 'Rekisterinpitäjien on ilmoitettava henkilötietojen loukkauksista niille rekisteröidyille, joita asia koskee.',
        },
        f686123d_3bdd_4a61_a53c_efe5b52d9058: {
            title: 'Tietosuojavaikutusten arviointi',
            description: 'Korkean riskin käsittelytoimista on tehtävä tietosuojavaikutusten arviointi.',
        },
        '554ea5b6_092d_4295_a57a_1bd9b2eef291': {
            title: 'Rajat ylittävien siirtojen hallinto',
            description: 'Henkilötietojen rajat ylittävän siirron on noudatettava hallintovaatimuksia.',
        },
        cf9a5b29_46fd_4072_b877_13b8a69df82c: {
            title: 'Tietosuojavastaavan nimitys',
            description: 'Organisaatioiden on tarvittaessa nimettävä tietosuojavastaava.',
        },
        f5a4965d_eb0c_47df_9933_f185ae46463b: {
            title: 'Käsittelytoimien tiedot',
            description: 'Organisaatioiden on pidettävä kirjaa kaikista käsittelytoimista.',
        },
        ce8986ff_684d_41ec_8147_b3f2a1c5206c: {
            title: 'Käsittelijän sopimusvelvoitteet',
            description: 'Tietojen käsittelijöiden kanssa on sovittava sopimusvelvoitteista.',
        },
        '7a3d7554_7b6f_49d7_8864_4a5cd71fd6bd': {
            title: 'Vaikutustenarviointiaineiston arkistointi',
            description: 'Vaikutustenarviointiasiakirjat on toimitettava toimivaltaiselle viranomaiselle määräysten mukaisesti.',
        },
        '65dba62f_0788_44c1_89e3_09d802e28eb9': {
            title: 'Rajat ylittävien siirtojen vaikutusasiakirja',
            description: 'Kansainvälistä tiedonsiirtoa varten on laadittava ja arkistoitava rajat ylittävän tiedonsiirron vaikutusasiakirja.',
        },
        '0eccb520_b1fa_4fa3_a3ab_2d30823dd00e': {
            title: 'DPO tapaaminen ja ilmoitus',
            description: 'Tietosuojavastaava on nimettävä ja viranomaiselle on ilmoitettava asiasta.',
        },
        '43ead19e_982a_4615_bafd_e6a3a4e80b3a': {
            title: 'Hallintoperiaatteet ja -menettelyt',
            description: 'Hallintopolitiikat ja -menettelyt on laadittava ja niitä on ylläpidettävä.',
        },
        '9ebc7a42_e1f1_49c4_ac90_22a691fd2219': {
            title: 'Todistuspaketti ja muuttumaton kirjausketju',
            description: 'Todistuspakettien on oltava täydellisiä, ja muuttumaton kirjausketju on säilytettävä.',
        },
        '5b0ba1c0_7b86_4c80_b9f9_26e4ff9652c2': {
            title: 'Arkistoinnin elinkaari',
            description: 'Arkistointiasioiden elinkaari on hallittava määrättyjen vaiheiden kautta.',
        },
        '83f2f346_47bb_4377_a711_3cef26742f39': {
            title: 'Prosessorin sopimusvaatimukset',
            description: 'Tietojen käsittelijöille on asetettava sopimusvaatimukset ja niitä on valvottava.',
        },
        cb972e06_f773_492e_8ad5_f65d1e999b79: {
            title: 'Palvelunkäsittelyn tarjoajan sertifiointi',
            description: 'Tietojenkäsittelypalveluita tarjoavien organisaatioiden on hankittava sertifiointi.',
        },
        '4ba36ab5_39f0_4d79_927e_c39587bed226': {
            title: 'Viranomaisten hakemusten tietueet',
            description: 'Kaikista viranomaisilmoituksista on säilytettävä kirjaa.',
        },
        f7a62c88_3cda_4031_bc52_1598661a5128: {
            title: 'Vuotuinen vaatimustenmukaisuustarkastus',
            description: 'Vuosittainen vaatimustenmukaisuustarkastus on suoritettava jatkuvan noudattamisen varmistamiseksi.',
        },
        '3e97f4d4_60ad_46a8_ad2b_615d4ca04aee': {
            title: 'Tekniset tietoturvatoimenpiteet',
            description: 'Tietoturvan tekniset toimenpiteet on toteutettava ja ylläpidettävä.',
        },
        '45f4e745_a923_4879_94ce_5a2b41b7e886': {
            title: 'Asetuksen 356 viralliset Mẫu số -lomakevaatimukset',
            description: 'Asetuksella 356/2025/NĐ-CPĐ-CP säädettyjä 13 virallista Mẫu số hallintomenettelylomaketta (Mẫu số 01a–10) on käytettävä vastaaviin ilmoituksiin yleisen turvallisuuden ministeriölle. Phụ lục I–VIII kohteet ovat sisäisiä tietorakennekartoituksia, eivät virallisia säädöslomakkeita.',
        },
        ff452604_7461_4c12_aeab_cb7dd1fb5ba3: {
            title: 'DPIA asiakirjan lähettäminen kohtaan A05',
            description: 'Organisaatioiden on toimitettava DPIA-asiakirjat A05/MPS:lle käsittelytoimista, joihin liittyy arkaluonteisia henkilötietoja tai laajamittaista käsittelyä.',
        },
        '003600ae_7a0f_4034_b206_72f59409b8f3': {
            title: 'Rajat ylittävää siirtoa koskevien asiakirjojen toimittaminen',
            description: 'Organisaatioiden on toimitettava rajat ylittävää tiedonsiirtoa koskevat asiakirjat A05/MPS tarkastettavaksi ja hyväksyttäväksi.',
        },
        '4632b20c_293a_4c7e_968e_4ce178c3b7d8': {
            title: 'Palveluvarmennehakemusta käsitellään',
            description: 'Tietojenkäsittelypalvelun tarjoajien on haettava varmenteita alkaen A05/MPS.',
        },
        be96add4_ebdb_4b6c_9426_4dc87b7cd2ce: {
            title: 'Ilmoitus henkilötietojen käsittelystä',
            description: 'Rekisterinpitäjien on ilmoitettava A05/MPS henkilötietojen käsittelystä määrätyillä lomakkeilla (Mẫu số 01a/01b).',
        },
        '39f50471_27cc_46bd_acff_9aa2211d43ab': {
            title: 'Vuosittainen tilanneraportti',
            description: 'Rekisterinpitäjien on toimitettava vuosittain henkilötietojen käsittelyn tilanneraportti (Mẫu số 09).',
        },
        '3b45c6fa_d66a_448a_9ff1_597caa055e09': {
            title: 'Käyttäjätietojen luottamuksellisuus oletuksena',
            description: 'Käyttäjätietoja on käsiteltävä oletusarvoisesti luottamuksellisina.',
        },
        '2994510e_b1f8_4cdd_947c_d9542746433b': {
            title: 'Organisaation ja teknisen suojauksen vähimmäisvalvonta',
            description: 'Organisaation ja teknisen suojan vähimmäisvalvonta on toteutettava.',
        },
        '8643160f_9a10_4d93_baf5_082c6a7c768a': {
            title: 'Ilmoittaminen vain asianmukaisin perustein',
            description: 'Käyttäjätietojen luovuttaminen on sallittua vain asianmukaisen laillisen perustan perusteella.',
        },
        c1fb7d85_fdc0_402e_ab0c_ff134bd72dfa: {
            title: 'Pakollinen tiedonkeruu ja auditoitavuus',
            description: 'Kaikki tiedot on kirjattava ja tarkastettavissa.',
        },
        efa36bc0_abae_417a_b676_9509af37f89d: {
            title: 'Sisäinen hyväksynnän työnkulku ilmoituksia varten',
            description: 'Kaikille ilmoituksille on luotava sisäinen hyväksynnän työnkulku.',
        },
        d086fdcd_9a23_4500_893c_db8ac62bd787: {
            title: 'Viranomaisten pyyntöjen vastausten käsittely',
            description: 'Viranomaisten pyynnöt on käsiteltävä ja niihin on vastattava määrättyjen menettelyjen mukaisesti.',
        },
    },
    framework_rights_descriptions: {
        be_informed: 'Rekisteröidyllä on oikeus saada tietoa henkilötietojen käsittelystä.',
        consent: 'Rekisteröidyllä on oikeus suostua henkilötietojensa käsittelyyn.',
        refuse_consent: 'Rekisteröidyllä on oikeus olla suostumatta henkilötietojensa käsittelyyn.',
        withdraw_consent: 'Rekisteröidyllä on oikeus pyytää aiemmin antamansa suostumuksen peruuttamista.',
        access: 'Rekisteröidyllä on oikeus nähdä ja päästä käsiksi rekisterinpitäjän hallussa oleviin henkilötietoihinsa.',
        rectification: 'Rekisteröidyllä on oikeus oikaista tai pyytää oikaisemaan virheelliset henkilötiedot.',
        data_provision: 'Rekisteröidyllä on oikeus pyytää henkilötietojensa toimittamista (yêu cầu cung cấp dữ liệu cá nhân).',
        deletion: 'Rekisteröidyllä on oikeus vaatia henkilötietojensa poistamista tai tuhoamista lain poikkeuksin.',
        restriction_of_processing: 'Rekisteröidyllä on oikeus pyytää henkilötietojensa käsittelyn rajoittamista.',
        object_to_processing: 'Rekisteröidyllä on oikeus lähettää pyyntö vastustaa henkilötietojensa käsittelyä.',
        complaint: 'Rekisteröidyllä on oikeus tehdä valitus (khiếu nại) henkilötietojen käsittelystä.',
        denunciation: 'Rekisteröidyllä on oikeus irtisanoa (tố cáo) henkilötietojen suojan loukkauksista.',
        lawsuit: 'Rekisteröidyllä on oikeus nostaa kanne (khởi kiện) tietosuojaoikeuksien loukkaamisesta.',
        claim_damages: 'Rekisteröidyllä on oikeus vaatia korvausta henkilötietojen suojan loukkauksista aiheutuneista vahingoista.',
        request_protection_measures: 'Rekisteröidyllä on oikeus pyytää toimivaltaisia viranomaisia tai asianomaisia organisaatioita/henkilöitä toteuttamaan toimenpiteitä henkilötietojensa suojaamiseksi.',
    },
    framework_procedures: {
        VN_DP_DRAFT_P7: {
            name: 'Riskienhallinta ja seuranta (luonnos)',
            steps: {
                '1': 'Tunnista riskiluokat',
                '2': 'Säilytä varhaisvaroitussäätimet',
                '3': 'Tarkista keräys-, tallennus-, käsittely-, jakamis- ja tapahtumariskit',
                '4': 'Tallenna tietosuoja-, kyberturvallisuus- ja pääsynhallintariskit',
                '5': 'Käynnistä korjaustoimenpiteet',
                '6': 'Säilytä arvosteluhistoria',
            },
        },
        VN_DP_DRAFT_P6: {
            name: 'Valitukset ja kiistat (luonnos)',
            steps: {
                '1': 'Vastaanota valitus/riita',
                '2': 'Tarkista vaadittujen tietojen täydellisyys; pyydä lisäystä 24 tunnin kuluessa, jos se on puutteellinen',
                '3': 'Ilmoita osapuolille käsittelytavasta ja odotetusta aikataulusta kolmen työpäivän kuluessa täydellisen pyynnön vastaanottamisesta',
                '4': 'Pyydä vastauksia ja asiakirjoja',
                '5': 'Arvioi kiistan tosiasiat ja alustan vastuu',
                '6': 'Ennätysresoluutiotulos',
                '7': 'Säilytä todisteet ja sulje tapaus',
            },
        },
        VN_DP_DRAFT_P5: {
            name: 'Tietojen omistusoikeuden toimitus ja todistus (luonnos)',
            steps: {
                '1': 'Toimita tuote/palvelu sopimustavan mukaisesti',
                '2': 'Tallenna toimitustapahtuma ja aika',
                '3': 'Luo tietojen omistusoikeustodistus',
                '4': 'Rekisteröi tapahtuman suorittaminen',
                '5': 'Ota myöhempi haku/vahvistus käyttöön tarvittaessa',
            },
        },
        VN_DP_DRAFT_P4: {
            name: 'Kaupan aloittaminen ja sopimus (luonnos)',
            steps: {
                '1': 'Tarkista listaus ja arvioinnin tulos',
                '2': 'Aloita osto-, neuvottelu- tai huutokauppatyönkulku',
                '3': 'Luo sähköinen sopimus',
                '4': 'Varmista molempien osapuolten hyväksyntä',
                '5': 'Kirjaa maksun suorittaminen',
                '6': 'Valtuuta toimitus',
            },
        },
        VN_DP_DRAFT_P3: {
            name: 'Hiekkalaatikon/testiympäristön käyttöoikeus (luonnos)',
            steps: {
                '1': 'Lähetä hiekkalaatikkopyyntö',
                '2': 'Määritä laajuus, sallitut tiedot, ajanjakso ja käsittelyoikeudet',
                '3': 'Tarjoa eristetty tekninen ympäristö',
                '4': 'Tarkkaile turvallisuutta ja pääsyä testauksen aikana',
                '5': 'Tarkista tulos ennen poistamista',
                '6': 'Hyväksy louhinta vain, jos se on sallittua',
                '7': 'Sulje testiistunto ja puhdista ympäristö',
            },
        },
        VN_DP_DRAFT_P2: {
            name: 'Tuote-/palvelutiedot (luonnos)',
            steps: {
                '1': 'Luo listaussovellus',
                '2': 'Tallenna lähde- ja omistus-/käyttöoikeustodisteet',
                '3': 'Tallenna tekniset ominaisuudet, muoto, API tiedot ja rajoitukset',
                '4': 'Ennätykselliset tiedot',
                '5': 'Lähetä listausaineisto alustan tarkastettavaksi',
                '6': 'Alusta suorittaa täydellisyys-, laillisuus- ja tekniset tarkastukset',
                '7': 'Hyväksy tai hylkää luettelo',
                '8': 'Jos hyväksytään, julkaise luettelo ja liitteenä arviointitulos',
            },
        },
        VN_DP_DRAFT_P1: {
            name: 'Osallistujan käyttöönotto (luonnos)',
            steps: {
                '1': 'Määritä osallistujan tyyppi: myyjä tai ostaja',
                '2': 'Tarkista oikeusasema ja oikeuskelpoisuus',
                '3': 'Tarkista lisäehdot, jos ehdollisia liiketoimintalinjoja sovelletaan',
                '4': 'Rekisteröi tapahtumatili',
                '5': 'Linkitä maksutili',
                '6': 'Hyväksy alustan osallistuminen',
                '7': 'Tallenna osallistujatietue ja kirjausketju',
            },
        },
        AI_A1: {
            name: 'AI Järjestelmän rekisteröinti/ilmoitus',
            steps: {
                '1': 'Soveltuvuuden tarkistus ja AI järjestelmäluokitus',
                '2': 'Luo rekisteröinti-/ilmoitusaineisto',
                '3': 'Sisäinen hyväksyntä',
                '4': 'Lähetä kanavan kautta',
                '5': 'Muutossilmukka – jos palautetaan, päivitä ja lähetä uudelleen',
                '6': 'Sulje ja tallenna vahvistus',
            },
        },
        AI_A2: {
            name: 'AI Riskiluokituksen työnkulku',
            steps: {
                '1': 'Luo AI järjestelmärekisterimerkintä',
                '2': 'Suorita ohjattu luokittelutoiminto',
                '3': 'Liitä todisteet',
                '4': 'Hyväksy luokitus',
                '5': 'Linkitä tulosteet järjestelmän aineistoon',
            },
        },
        AI_A3: {
            name: 'Merkintöjen ja paljastamisen vaatimustenmukaisuus',
            steps: {
                '1': 'Tunnista merkintävelvollisuudet järjestelmän ja käyttötapauksen mukaan',
                '2': 'Ota käyttöön merkintäilmoitukset',
                '3': 'Kaappaa todisteita – kuvakaappauksia, käyttöliittymäkopioita, teknisiä metatietoja',
                '4': 'Käsittele poikkeukset ja kirjaa perustelut',
                '5': 'Hyväksy ja versio',
            },
        },
        AI_A4: {
            name: 'Raportointi vakavista tapauksista',
            steps: {
                '1': 'Luo AI tapahtumatietue',
                '2': 'Luokittele vakavuus',
                '3': 'Suorita eristämistoimia ja todisteiden talteenotto',
                '4': 'Luo vakavan tapauksen raporttimallin vienti',
                '5': 'Lähetä viranomaiselle kanavan kautta',
                '6': 'Seuraa viranomaisten vastauksia ja sulkemista',
            },
        },
        AI_A5: {
            name: 'AI Vaikutusten arviointi',
            steps: {
                '1': 'Luo arviointitietue',
                '2': 'Täytä vaaditut osat',
                '3': 'Liitä todisteet – tietovirrat, mallidokumentaatiot, kontrollit',
                '4': 'Sisäinen hyväksyntä',
                '5': 'Vie raportti ja säilytä asiakirjassa',
            },
        },
        AI_A6: {
            name: 'Kontrolloitu testaus / hiekkalaatikkoon osallistuminen',
            steps: {
                '1': 'Täydellinen kelpoisuus- ja valmiustodistus',
                '2': 'Valmistele hakemus ja vie',
                '3': 'Lähetä kanavan kautta',
                '4': 'Raidan hyväksyntä',
                '5': 'Suorita valvontatarkastuspisteet ja lokit',
                '6': 'Täydellinen valmistumisraportti ja sulkemispaketti',
            },
        },
        CYBERSEC_C1: {
            name: 'Kriittisen tietojärjestelmän turvallisuusarviointi',
            steps: {
                '1': 'Luokittele tietojärjestelmät kansallisten kyberturvallisuusvaatimusten mukaan',
                '2': 'Suorita turvaluokiteltujen järjestelmien turvallisuusarviointi ja riskiarviointi',
                '3': 'Ota käyttöön tarvittavat turvatoimenpiteet ja perussäädöt',
                '4': 'Dokumentoi todisteet vaatimustenmukaisuudesta ja ajoita säännölliset tarkistukset',
            },
        },
        CYBERSEC_C2: {
            name: 'Ilmoitus kyberturvallisuustapahtumasta',
            steps: {
                '1': 'Havaitse, luokittele ja arvioi kyberturvallisuushäiriö',
                '2': 'Suorita välittömät reagointitoimet ja todisteiden säilyttäminen',
                '3': 'Raportoi viranomaisille ja osallistu valtakunnalliseen vaaratilanteiden hallintaan',
                '4': 'Suorita tapahtuman jälkeinen tarkistus ja dokumentoi saadut opetukset',
            },
        },
        CYBERSEC_C3: {
            name: 'Tietojen lokalisoinnin vaatimustenmukaisuuden tarkistus',
            steps: {
                '1': 'Arvioi, ovatko datan lokalisointikäynnistimet käytössä palvelutyypin ja tietoluokkien perusteella',
                '2': 'Varmista, että paikallinen tallennusarkkitehtuuri ja rajat ylittävät virtauksen säädöt ovat käytössä',
                '3': 'Arvioi paikalliset läsnäolovaatimukset (sivukonttori / edustusto) tarvittaessa',
                '4': 'Dokumentoi vaatimustenmukaisuustodistus ja säilytä viranomaisen tarkastelua varten',
            },
        },
        CYBERSEC_C4: {
            name: 'Vastaus viranomaisen yhteistyöpyyntöön',
            steps: {
                '1': 'Vastaanota ja tarkista viranomaispyynnön laillisuus ja laajuus',
                '2': 'Kerää ja valmistele pyydetyt tiedot tai suorita vaaditut toimenpiteet',
                '3': 'Lähetä vastaus viranomaiselle vaaditussa määräajassa',
                '4': 'Kirjaa ilmoitus ja sulje tapaus',
            },
        },
        CYBERSEC_C5: {
            name: 'Kyberturvallisuushenkilöstön pätevyyden tarkistus',
            steps: {
                '1': 'Arvioi toimittajan turvallisuusasenne kyberturvallisuuden arviointikehyksen avulla',
                '2': 'Tarkista toimittajan tietojen käyttöoikeus ja rekisteröidy toimittajan tietojen käyttölokiin',
                '3': 'Tarkista toimittajan tapausten ilmoituslauseke ja sopimuskohtaiset kyberturvallisuusvaatimukset',
                '4': 'Suunnittele säännölliset toimittajan tietoturvatarkastukset ja seuraa vaatimustenmukaisuutta',
            },
        },
        DATA_D1: {
            name: 'Tietovaraston ja luokittelun asetukset',
            steps: {
                '1': 'Luetteloi kaikki tietovarat tyypeineen ja lähteineen tietovarastorekisteriin',
                '2': 'Luokittele jokainen omaisuus herkkyystason mukaan',
                '3': 'Määritä käsittelysäännöt luokitustasoittain',
                '4': 'Tallenna todisteet – kuvakaappaukset, arkkitehtuurikaaviot, kulunvalvonta',
                '5': 'Suunnittele säännölliset tarkastukset',
            },
        },
        DATA_D2: {
            name: 'Tietojen jakamisen hallinta',
            steps: {
                '1': 'Arvioi uusia jakamisjärjestelyjä tiedonjaon riskiarvioinnin avulla',
                '2': 'Suorita sisäinen hyväksyntätyönkulku',
                '3': 'Tallenna jakamistapahtuma tietojen jakamis-/paljastuslokissa',
                '4': 'Taltioi todisteet – hyväksymisasiakirjat, sopimusasiakirjat',
                '5': 'Jakamisjärjestelyjen säännöllinen tarkistus',
            },
        },
        DATA_D3: {
            name: 'Rajat ylittävän tiedonsiirron arviointi',
            steps: {
                '1': 'Tunnista rajat ylittävät siirrot tietovarastosta',
                '2': 'Suorita siirtoriskin arviointi',
                '3': 'Käytä suojatoimia ja hallintotoimenpiteitä',
                '4': 'Asiakirja valtioiden rajat ylittävässä hallintotodistuksessa',
                '5': 'Viennin arviointipaketti auditointiin',
            },
        },
        DATA_D4: {
            name: 'Vuosittainen hallintotodistus',
            steps: {
                '1': 'Tarkista hallinto-RACI-matriisi',
                '2': 'Vahvista käytännön tila (hallinto, luokittelu, jakaminen, säilyttäminen)',
                '3': 'Täydellinen todistuspaketti',
                '4': 'Hanki sisäinen hyväksyntä',
                '5': 'Vienti- ja arkistointitodistus',
            },
        },
        DATA_D5: {
            name: 'Tietotapahtumien käsittely',
            steps: {
                '1': 'Luokittele tapahtuma päätöstietueen avulla',
                '2': 'Suorita tapausvastaus pelikirjan mukaan',
                '3': 'Kaappaa todisteita – lokeja, kuvakaappauksia, viestintää',
                '4': 'Suorita tapahtuman jälkeinen tarkistus',
                '5': 'Jos vaaditaan viranomaisen ilmoitus: luo lähetystapaus ja lähetä osoitteeseen MIC',
                '6': 'Sulje ja arkistoi',
            },
        },
        ECOM_P1: {
            name: 'Ilmoitus (myyntisivusto/sovellus)',
            steps: {
                '1': 'Määritä luokitus: myyntisivusto/sovellus vs. markkinapaikka/palvelualusta',
                '2': 'Luo sisäinen asiakirjatietue, joka sisältää yrityksen identiteetin, verkkosivuston/sovelluksen tiedot ja vaaditut tiedot',
                '3': 'Luo todistepaketti – kuvakaappauksia ilmoituksista, käytäntöjen URL-osoitteista, versiohistoriasta',
                '4': 'Lähetä osoitteessa Online.gov.vn – lataa asiakirjat, tallenna aikaleima ja vahvistus',
                '5': 'Seuraa käsittelyä – jos palautetaan korjattavaksi, tallenna syyt ja lähetä uudelleen',
                '6': 'Sulje — tallenna vahvistus, linkitä todisteet ja kirjausketju',
            },
        },
        ECOM_P2: {
            name: 'Rekisteröityminen (palvelualustat / markkinapaikat)',
            steps: {
                '1': 'Määritä alustamalli ja velvoitteet – markkinapaikka vs. sekakauppa vs. sosiaalinen kaupankäynti',
                '2': 'Rakenna rekisteröintiaineisto — hakemuslomake, palveluntarjoamisjärjestelmä, käyttöehdot, alustasäännöt',
                '3': 'Sisäiset hyväksynnät — määritä laillinen/vaatimustenmukaisuusomistaja, hyväksy lähetys',
                '4': 'Lähetä osoitteessa Online.gov.vn — lataa aineisto, tallenna aikaleima',
                '5': 'Käsittelysilmukka – jos palautetaan korjattavaksi, päivitä ja lähetä uudelleen',
                '6': 'Hyväksyminen ja käyttöönotto — säilytä vahvistus, ylläpidä jatkuvaa vaatimustenmukaisuusrekisteriä',
            },
        },
        ECOM_P3: {
            name: 'Päivitys/muutosilmoitus',
            steps: {
                '1': 'Tunnista muutoksen laukaiseva tekijä – materiaalin muutos asiakirja-aineistoon',
                '2': 'Luo muutospaketti — muutosyhteenveto, päivitetyt asiakirjat, todisteiden päivitykset',
                '3': 'Lähetä päivitys ja tallenna lähetyksen tila',
                '4': 'Seuraa käsittelyä ja sulje',
            },
        },
        ECOM_P4: {
            name: 'Päättäminen / deaktivointi',
            steps: {
                '1': 'Valmistele irtisanomispaketti – lopetuspäivämäärä, syy, kuluttajansuojakäsittelysuunnitelma, todiste julkisesta ilmoituksesta',
                '2': 'Lähetä irtisanominen — tallenna aikaleima ja vahvistus',
                '3': 'Sulje — tallenna lopullinen tila ja todisteet',
            },
        },
        ECOM_P5: {
            name: 'Trustmark / uskottavuusluokitusohjelma',
            steps: {
                '1': 'Kelpoisuus- ja valmiustarkastus',
                '2': 'Valmistele asiakirja virallisella lomakkeella',
                '3': 'Lähetä ja seuraa',
                '4': 'Säilytä jatkuva näyttö vaatimustenmukaisuudesta',
            },
        },
        PDPL_P1: {
            name: 'DPIA Lähetyksen arkistointi osoitteeseen MPS',
            steps: {
                '1': 'Täytä DPIA käyttämällä Decree 356 -lomakepolkua (Mẫu số 02a/02b)',
                '2': 'Kokoa kansiasiakirjat ja niitä tukevat todisteet',
                '5': 'Säilytä asiakirjat ja todisteet toimittamisesta kirjausketjua varten',
            },
        },
        PDPL_P2: {
            name: 'Rajat ylittävä siirtohakemus',
            steps: {
                '1': 'Täytä rajat ylittävää siirtoa koskeva asiakirja-aineisto Decree 356 -lomakkeilla (Mẫu số 01a/01b, Mẫu số 09)',
            },
        },
        PDPL_P3: {
            name: 'Tietojenkäsittelypalvelun varmennehakemus',
            steps: {
                '1': 'Valmistele sertifiointihakemusasiakirjat asetuksen 356 vaatimusten mukaisesti',
                '2': 'Lähetä hakemus osoitteeseen MPS ja vaadi tarvittavat asiakirjat',
                '3': 'Vastaa MPS:n tarkistuskommentteihin ja täydennyspyyntöihin',
            },
        },
        PDPL_P4: {
            name: 'Rekisteröidyn oikeuksia koskevan pyynnön käsittely',
            steps: {
                '1': 'Vastaanota ja kirjaa pyyntö; tarkistaa rekisteröidyn henkilöllisyyden 2 työpäivän kuluessa',
                '2': 'Arvioi pyyntötyyppi ja määritä sovellettava määräaika (10/15/20 päivää tyypin mukaan)',
                '3': 'Suorita toteutustoimia asianmukaisissa järjestelmissä ja osastoissa',
                '4': 'Valmistele ja lähetä rekisteröidylle vastaus tuloksen ja perusteluineen',
                '5': 'Sulje tapaus ja säilytä todisteet tarkastusta varten; dokumentoi mahdolliset kieltäytymisen perusteet',
            },
        },
        PDPL_P5: {
            name: 'Rikkomusilmoitus henkilölle MPS',
            steps: {
                '1': 'Havaitse ja arvioi rikkomus; määritä, koskeeko ilmoitusvelvollisuus tapausta, ja selvitä sovellettava ilmoitusmääräaika asennetuista määräaikatietueista',
                '2': 'Valmistele ilmoituspaketti PDPL vaatimusten mukaisesti',
                '3': 'Lähetä ilmoitus MPS:lle sovellettavan ilmoitusmääräajan kuluessa, joka selviää asennetuista määräaikatietueista, havaitsemisesta laskettuna',
                '4': 'Vastaa MPS-seurantapyyntöihin; toimittaa täydennyksiä ja koordinointitodisteita',
            },
        },
        TELECOM_T1: {
            name: 'Viranomaisten pyyntöjen käsittely',
            steps: {
                '1': 'Vastaanota ja kirjaa viranomaispyyntö ottotietueen kautta',
                '2': 'Tarkista viranomaisen henkilöllisyys, oikeusperusta ja pyynnön laajuus',
                '3': 'Suorita sisäinen oikeudellinen tarkastus ja hanki johdon hyväksyntä',
                '4': 'Valmistele vastauspaketti kootuilla todisteilla',
                '5': 'Laadi ja hyväksy vastauskirje',
                '6': 'Luo lähetystapaus ja lähetä vastaus kanavan kautta',
                '7': 'Lokin paljastaminen ja seurantavaltuutuksen vahvistus',
                '8': 'Sulje tapaus ja säilytä todisteet kirjausketjua varten',
            },
        },
        TELECOM_T2: {
            name: 'Tietojen hallinta',
            steps: {
                '1': 'Vastaanota luovutuspyyntö (käyttäjän suostumus, lakisääteinen velvoite tai viranomainenpyyntö)',
                '2': 'Suorita tietojen hyväksymisen työnkulku loppuun',
                '3': 'Suorita julkistaminen ja kirjaa lokirekisteriin',
                '4': 'Taltioi todisteet – hyväksymisasiakirjat, viestintätietueet',
                '5': 'Ilmoituslokin määräaikainen tarkistus',
            },
        },
        TELECOM_T3: {
            name: 'Luottamuksellisuuskäytäntö ja kulunvalvontaasetukset',
            steps: {
                '1': 'Luonnostele ja hyväksy salassapitopolitiikka',
                '2': 'Laadi ja hyväksy kulunvalvonta- ja lokikäytäntö',
                '3': 'Asiakirjojen käsittelytoiminta käyttäjätietojen käsittelyrekisterissä',
                '4': 'Kerää todisteita – politiikka-asiakirjat, täytäntöönpanon todisteet',
                '5': 'Suunnittele säännölliset tarkastukset',
            },
        },
        TELECOM_T4: {
            name: 'Televiestintätapahtuman arviointi',
            steps: {
                '1': 'Luokittele ja arvioi tapahtuma',
                '2': 'Määritä ilmoitusvaatimukset',
                '3': 'Jos viranomaisen ilmoitus vaaditaan: luo lähetystapaus',
                '4': 'Ota ja säilytä todisteita',
                '5': 'Päätös ja sulkeminen',
            },
        },
        TELECOM_T5: {
            name: 'Säilytyksen ja poistamisen hallinta',
            steps: {
                '1': 'Määritä säilytys- ja poistoaikataulu tietokategorioittain',
                '2': 'Ota poistomenettelyt käyttöön',
                '3': 'Kaappaa todisteet - poista tietueet',
                '4': 'Aikataulujen säännöllinen tarkistus',
            },
        },
    },
    framework_fines: {
        VN_CYBERSEC_LAW: {},
        VN_ECOMMERCE_LAW_2025: {
            'ECOM-FINE-A': 'Rekisteröinti / Ilmoitus / Hallinnollinen valmius',
            'ECOM-FINE-B': 'Valitukset ja kuluttajansuoja',
            'ECOM-FINE-C': 'Valvonta ja poistamisen täytäntöönpano',
            'ECOM-FINE-D': 'Rajat ylittävät toiminnot',
            'ECOM-FINE-E': 'Alustan säännöt ja avoimuus',
        },
        VN_TELECOM_LAW_2023: {
            'TELECOM-FINE-A': 'Viranomaisten pyyntöjen käsittelyvirheet',
            'TELECOM-FINE-B': 'Luottamuksellisuus ja luvaton paljastaminen',
            'TELECOM-FINE-C': 'Kulunvalvonta- ja kirjausvirheet',
            'TELECOM-FINE-D': 'Säilytys- ja poistamisrikkomukset',
            'TELECOM-FINE-E': 'Televiestintätapahtumien arvioinnin epäonnistumiset',
        },
    },
    framework_fines_detail: {
        VN_TELECOM_LAW_2023: {
            'TELECOM-FINE-E': {
                fine_individual: 'Jopa 100 miljoonaa VND',
                fine_org: 'Jopa 200 miljoonaa VND',
                deadlines: 'Välittömästi havaitsemisen jälkeen',
                remediation: [
                    'Mandatory incident classification',
                    'Mandatory severity and decision records',
                    'Mandatory evidence preservation for confidentiality incidents',
                    'Mandatory post-incident closure',
                ],
            },
            'TELECOM-FINE-D': {
                fine_individual: 'Jopa 100 miljoonaa VND',
                fine_org: 'Jopa 200 miljoonaa VND',
                deadlines: 'Jatkuva velvoite',
                remediation: [
                    'Mandatory retention schedule implementation',
                    'Mandatory deletion evidence preservation',
                    'Mandatory retention exception decision records',
                ],
            },
            'TELECOM-FINE-C': {
                fine_individual: 'Jopa 100 miljoonaa VND',
                fine_org: 'Jopa 200 miljoonaa VND',
                deadlines: 'Jatkuva velvoite',
                remediation: [
                    'Mandatory access-control policy implementation',
                    'Mandatory logging of access events',
                    'Mandatory review of access rights',
                ],
            },
            'TELECOM-FINE-B': {
                fine_individual: 'Jopa 100 miljoonaa VND',
                fine_org: 'Jopa 200 miljoonaa VND',
                deadlines: 'Jatkuva velvoite',
                remediation: [
                    'Mandatory confidentiality policy implementation',
                    'Mandatory disclosure approval workflow',
                    'Mandatory disclosure trail maintenance',
                    'Mandatory user information access controls',
                ],
            },
            'TELECOM-FINE-A': {
                fine_individual: 'Jopa 100 miljoonaa VND',
                fine_org: 'Jopa 200 miljoonaa VND',
                deadlines: 'Kuten viranomaispyynnössä on ilmoitettu',
                remediation: [
                    'Mandatory intake and verification of authority request',
                    'Mandatory response within prescribed timeline',
                    'Mandatory disclosure log entry',
                    'Mandatory evidence preservation',
                ],
            },
        },
        VN_ECOMMERCE_LAW_2025: {
            'ECOM-FINE-E': {
                fine_individual: 'Jopa 80 miljoonaa VND',
                fine_org: 'Jopa 160 miljoonaa VND',
                deadlines: 'Jatkuva velvoite',
                remediation: [
                    'Mandatory platform rules publication and versioning',
                    'Mandatory user-facing disclosures',
                    'Mandatory seller/affiliate/livestream obligations evidenced',
                    'Mandatory control history and approval chain',
                ],
            },
            'ECOM-FINE-D': {
                fine_individual: 'Jopa 80 miljoonaa VND',
                fine_org: 'Jopa 160 miljoonaa VND',
                deadlines: 'Jatkuva velvoite',
                remediation: [
                    'Mandatory cross-border obligations assessment',
                    'Mandatory authorized representative readiness',
                    'Mandatory cross-border operations register',
                    'Mandatory evidence of compliance action',
                ],
            },
            'ECOM-FINE-C': {
                fine_individual: 'Jopa 80 miljoonaa VND',
                fine_org: 'Jopa 160 miljoonaa VND',
                deadlines: 'Jatkuva velvoite',
                remediation: [
                    'Mandatory review of prohibited or unlawful listings',
                    'Mandatory takedown decision records',
                    'Mandatory evidence preservation',
                    'Mandatory seller notification and closure trail',
                ],
            },
            'ECOM-FINE-B': {
                fine_individual: 'Jopa 80 miljoonaa VND',
                fine_org: 'Jopa 160 miljoonaa VND',
                deadlines: 'Valituksen käsittelyvelvollisuuksien mukaisesti',
                remediation: [
                    'Mandatory complaint intake procedure',
                    'Mandatory resolution record',
                    'Mandatory complaint audit trail',
                    'Mandatory escalation for cross-border seller issues',
                ],
            },
            'ECOM-FINE-A': {
                fine_individual: 'Jopa 80 miljoonaa VND',
                fine_org: 'Jopa 160 miljoonaa VND',
                deadlines: 'Ennen sähköisen kaupankäynnin aloittamista',
                remediation: [
                    'Mandatory platform classification',
                    'Mandatory notification or registration completion',
                    'Mandatory evidence of portal submission',
                    'Mandatory update/termination tracking',
                ],
            },
        },
        VN_CYBERSEC_LAW: {},
    },
    framework_procedures_detail: {
        VN_DP_DRAFT_P7: {
            trigger: 'Alustan jatkuva toiminta',
            outputs: [
                'Risk assessment record',
                'Early warning log',
                'Remediation record',
                'Review trail',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Alustan käyttäjä / riski',
                '2': 'Alustan käyttäjä/turvallisuus',
                '3': 'Alustan käyttäjä / riski',
                '4': 'Alustan käyttäjä/turvallisuus',
                '5': 'Alustan operaattori',
                '6': 'Vaatimustenmukaisuus',
            },
            step_outputs: {
                '1': ['Risk assessment record'],
                '2': ['Early warning log'],
                '3': ['Risk review findings'],
                '4': ['Risk register entries'],
                '5': ['Remediation record'],
                '6': ['Review trail'],
            },
        },
        VN_DP_DRAFT_P6: {
            trigger: 'Osallistuja tekee valituksen tai kiistapyynnön',
            outputs: [
                'Complaint intake record',
                'Dispute resolution record',
                'Evidence pack',
                'Case closure record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Alustan operaattori',
                '2': 'Alustan operaattori',
                '3': 'Alustan operaattori',
                '4': 'Alustan operaattori',
                '5': 'Alustan operaattori / lakimies',
                '6': 'Alustan operaattori',
                '7': 'Vaatimustenmukaisuus',
            },
            step_outputs: {
                '1': ['Complaint intake record'],
                '2': ['Completeness check', 'Supplement request (if any)'],
                '3': ['Handling approach notice'],
                '4': ['Evidence pack'],
                '5': ['Assessment findings'],
                '6': ['Dispute resolution record'],
                '7': ['Case closure record', 'Archived evidence pack'],
            },
        },
        VN_DP_DRAFT_P5: {
            trigger: 'Toimitus päättyy sopimuksen ja maksuehtojen täyttyessä',
            outputs: [
                'Delivery record',
                'Certificate of property rights over data',
                'Transaction register entry',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Myyjä / alustan operaattori',
                '2': 'Alustan operaattori',
                '3': 'Alustan operaattori',
                '4': 'Alustan operaattori',
                '5': 'Alustan operaattori',
            },
            step_outputs: {
                '1': ['Delivery record'],
                '2': ['Delivery event log entry'],
                '3': ['Property rights certificate'],
                '4': ['Transaction register entry'],
                '5': ['Verification access record'],
            },
        },
        VN_DP_DRAFT_P4: {
            trigger: 'Ostaja aloittaa tiedon, tuotteen tai palvelun tapahtuman',
            outputs: [
                'Transaction initiation record',
                'Electronic contract',
                'Payment confirmation',
                'Delivery authorization record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Ostaja',
                '2': 'Ostaja / alustan operaattori',
                '3': 'Alustan operaattori',
                '4': 'Ostaja / Myyjä',
                '5': 'Alustan operaattori / maksu',
                '6': 'Alustan operaattori',
            },
            step_outputs: {
                '1': ['Listing review decision'],
                '2': ['Transaction initiation record'],
                '3': ['Electronic contract record'],
                '4': ['Contract acceptance record'],
                '5': ['Payment confirmation'],
                '6': ['Delivery authorization record'],
            },
        },
        VN_DP_DRAFT_P3: {
            trigger: 'Osallistuja haluaa arvioida datatuotteita/palveluita eristetyssä ympäristössä',
            outputs: [
                'Sandbox request',
                'Sandbox scope record',
                'Output review record',
                'Sandbox termination record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Osallistuja',
                '2': 'Alustan operaattori / osallistuja',
                '3': 'Alustan operaattori / IT',
                '4': 'Alustan käyttäjä/turvallisuus',
                '5': 'Alustan operaattori',
                '6': 'Alustan operaattori',
                '7': 'Alustan operaattori / IT',
            },
            step_outputs: {
                '1': ['Sandbox request'],
                '2': ['Sandbox scope record'],
                '3': ['Provisioned environment'],
                '4': ['Security monitoring log'],
                '5': ['Output review record'],
                '6': ['Extraction approval decision'],
                '7': ['Sandbox termination record'],
            },
        },
        VN_DP_DRAFT_P2: {
            trigger: 'Myyjä pyrkii listaamaan dataa, tuotetta tai datapalvelua kaupankäyntiä varten',
            outputs: [
                'Listing application',
                'Lawful-source verification record',
                'Technical validation record',
                'Quality disclosure record',
                'Listing approval record',
                'Listing register entry',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Myyjä',
                '2': 'Myyjä/lakimies',
                '3': 'Myyjä / IT',
                '4': 'Myyjä',
                '5': 'Myyjä',
                '6': 'Alustan operaattori',
                '7': 'Alustan operaattori',
                '8': 'Alustan operaattori',
            },
            step_outputs: {
                '1': ['Listing application'],
                '2': ['Lawful-source verification record'],
                '3': ['Technical validation record'],
                '4': ['Quality disclosure record'],
                '5': ['Submitted listing dossier'],
                '6': ['Review findings'],
                '7': ['Listing approval record'],
                '8': ['Published listing', 'Listing register entry'],
            },
        },
        VN_DP_DRAFT_P1: {
            trigger: 'Myyjä tai ostaja liittyy tietoalustaan',
            outputs: [
                'Seller / buyer onboarding record',
                'Participant register entry',
                'Account setup evidence',
                'Approval record',
            ],
            channels: ['Platform workflow'],
            responsible: {
                '1': 'Alustan operaattori',
                '2': 'Alustan operaattori / lakimies',
                '3': 'Alustan operaattori / lakimies',
                '4': 'Alustan operaattori',
                '5': 'Alustan operaattori',
                '6': 'Alustan operaattori',
                '7': 'Vaatimustenmukaisuus',
            },
            step_outputs: {
                '1': ['Participant type decision'],
                '2': ['Legal capacity verification record'],
                '3': ['Conditional-business check record'],
                '4': ['Transaction account registration record'],
                '5': ['Payment account link record'],
                '6': ['Participation approval record'],
                '7': ['Participant register entry', 'Audit trail'],
            },
        },
        PDPL_P1: {
            trigger: 'Vaikutustenarviointia vaativan henkilötietojen käsittelyn aloittaminen',
            outputs: ['DPIA dossier', 'Submission confirmation', 'Supplement package'],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                '1': 'DPO / Tietosuojatiimi',
                '2': 'Tietosuojatiimi',
                '3': 'Laki / vaatimustenmukaisuus',
                '4': 'DPO / Tietosuojatiimi',
                '5': 'Compliance Team',
            },
            step_outputs: {
                '1': ['Completed DPIA dossier'],
                '2': ['Dossier package', 'Cover letter'],
                '3': ['Submission confirmation', 'Tracking reference'],
                '4': ['Supplement package', 'Updated dossier'],
                '5': ['Archived dossier', 'Audit evidence pack'],
            },
        },
        PDPL_P2: {
            trigger: 'Aikomus siirtää henkilötietoja Vietnamin ulkopuolelle',
            outputs: ['Transfer dossier', 'Submission confirmation', 'Supplement response'],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                '1': 'DPO / Tietosuojatiimi',
                '2': 'Laki / vaatimustenmukaisuus',
                '3': 'DPO / Tietosuojatiimi',
            },
            step_outputs: {
                '1': ['Transfer dossier package'],
                '2': ['Submission confirmation', 'Tracking reference'],
                '3': ['Supplement response', 'Updated dossier'],
            },
        },
        PDPL_P3: {
            trigger: 'Sertifiointia vaativia tietojenkäsittelypalveluita tarjoava organisaatio',
            outputs: ['Certification dossier', 'Certificate', 'Re-issue and exchange record'],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                '1': 'Laki / vaatimustenmukaisuus',
                '2': 'Laki / vaatimustenmukaisuus',
                '3': 'DPO / Tietosuojatiimi',
                '4': 'Compliance Team',
            },
            step_outputs: {
                '1': ['Certification application dossier'],
                '2': ['Submission confirmation'],
                '3': ['Supplement response'],
                '4': ['Certificate record', 'Re-issue and exchange record'],
            },
        },
        PDPL_P4: {
            trigger: 'Rekisteröidyn oikeuksia koskevan pyynnön vastaanotto',
            outputs: [
                'DSR intake record',
                'Identity verification',
                'Response letter',
                'Case closure report',
            ],
            channels: ['Online portal', 'Email', 'In-person'],
            responsible: {
                '1': 'Tietosuojatiimi / Asiakaspalvelu',
                '2': 'DPO / Tietosuojatiimi',
                '3': 'Osastonjohtajat / IT',
                '4': 'DPO / Tietosuojatiimi',
                '5': 'Compliance Team',
            },
            step_outputs: {
                '1': ['DSR intake record', 'Identity verification record'],
                '2': ['Request assessment', 'Deadline assignment'],
                '3': ['Fulfillment evidence', 'Department confirmations'],
                '4': ['Response letter'],
                '5': ['Case closure report', 'Audit evidence pack'],
            },
        },
        PDPL_P5: {
            trigger: 'Henkilötietosuojarikkomuksen tai -loukkauksen havaitseminen',
            outputs: [
                'Breach assessment record',
                'Notification package',
                'Submission confirmation',
                'Supplement package',
            ],
            channels: ['MPS Portal', 'In-person filing at MPS A05'],
            responsible: {
                '1': 'Tapahtumavalvontatiimi / DPO',
                '2': 'DPO / Tietosuojatiimi',
                '3': 'Laki / vaatimustenmukaisuus',
                '4': 'DPO / Välikohtausryhmä',
            },
            step_outputs: {
                '1': ['Breach assessment record', 'Notification decision record'],
                '2': ['Breach notification package'],
                '3': ['Submission confirmation', 'Tracking reference'],
                '4': ['Supplement package', 'Coordination evidence'],
            },
        },
    },
    framework_guidance_references: {
        'Authority practice (unofficial) — email-ZIP submission of PDPL evidence packs during NQ22 era': 'Authority practice (unofficial) — email-ZIP submission of PDPL evidence packs during NQ22 era',
        'Cross-reference frameworks listed on VN_ECOMMERCE_DECREE_248_2026.': 'Cross-reference frameworks listed on VN_ECOMMERCE_DECREE_248_2026.',
        'Cross-reference frameworks on VN_DIGITAL_TECH_INDUSTRY_LAW_2025.': 'Cross-reference frameworks on VN_DIGITAL_TECH_INDUSTRY_LAW_2025.',
        'Cross-reference frameworks on VN_DIGITAL_TRANSFORMATION_LAW_2025.': 'Cross-reference frameworks on VN_DIGITAL_TRANSFORMATION_LAW_2025.',
        'Government/MOIT public reporting.': 'Government/MOIT public reporting.',
        'Government/MOIT public reporting; deadline VN_ECOM248_DEADLINE_TAKEDOWN_RESPONSE.': 'Government/MOIT public reporting; deadline VN_ECOM248_DEADLINE_TAKEDOWN_RESPONSE.',
        'MPS consultation dossier — draft information-systems protection decree.': 'MPS consultation dossier — draft information-systems protection decree.',
        'MPS consultation — draft general implementing decree.': 'MPS consultation — draft general implementing decree.',
        'Sector overlay install model (mirrors VN_BANKING_CIRCULAR_83_2025).': 'Sector overlay install model (mirrors VN_BANKING_CIRCULAR_83_2025).',
        'Shared draft enforcement overlay VN_CYBER_PDPL_ENFORCEMENT_DRAFT_2026 (home pack: vn-pdpl).': 'Shared draft enforcement overlay VN_CYBER_PDPL_ENFORCEMENT_DRAFT_2026 (home pack: vn-pdpl).',
    },
    framework_deadline_references: {
        'ComplianceOne internal review': 'ComplianceOne internal review',
        'ComplianceOne internal review date': 'ComplianceOne internal review date',
        'Draft personnel provisions': 'Draft personnel provisions',
        'Draft specialist provisions': 'Draft specialist provisions',
        'Internal child-instrument review.': 'Internal child-instrument review.',
        'Internal platform review.': 'Internal platform review.',
        'Internal recommendation based on draft lifecycle governance': 'Internal recommendation based on draft lifecycle governance',
        'Internal recommended digital-service procurement/leasing vendor review (before procurement/leasing and on material change).': 'Internal recommended digital-service procurement/leasing vendor review (before procurement/leasing and on material change).',
        'Internal recommended digital-transformation control review.': 'Internal recommended digital-transformation control review.',
        'Internal recommended review of digital-system minimum requirements (annual or on material change).': 'Internal recommended review of digital-system minimum requirements (annual or on material change).',
        'National digital technology industry database — ad-hoc update at authority request (where direct scope is confirmed).': 'National digital technology industry database — ad-hoc update at authority request (where direct scope is confirmed).',
        'National digital technology industry database — quarterly update (where direct scope is confirmed).': 'National digital technology industry database — quarterly update (where direct scope is confirmed).',
        'Official MPS consultation page': 'Official MPS consultation page',
        'Platform operational control — not a statutory deadline': 'Platform operational control — not a statutory deadline',
        'PDPL Art. 9': 'PDPL Art. 9',
        'PDPL Art. 17': 'PDPL Art. 17',
        'PDPL Art. 23': 'PDPL Art. 23',
        'Decree 13 Art. 24': 'Asetus 13 Art. 24',
        'MoPS Admin Procedures': 'MoPS-järjestelmänvalvojan menettelyt',
        'Annual cycle': 'Vuotuinen sykli',
        'Decree 13/2023/NĐ-CP Art. 24': 'Asetus 13/2023/NĐ-CPĐ-CP Art. 24',
        'Decree 356/2025/NĐ-CP Art. 25(4)': 'Asetus 356/2025/NĐ-CPĐ-CP Art. 25(4)',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 2': 'Asetus 356/2025/NĐ-CPĐ-CP Art. 5 Cl. 2',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 2–5': 'Asetus 356/2025/NĐ-CPĐ-CP Art. 5 Cl. 2–5',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 3': 'Asetus 356/2025/NĐ-CPĐ-CP Art. 5 Cl. 3',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 4': 'Asetus 356/2025/NĐ-CPĐ-CP Art. 5 Cl. 4',
        'Decree 356/2025/NĐ-CP Art. 5 Cl. 5': 'Asetus 356/2025/NĐ-CPĐ-CP Art. 5 Cl. 5',
        'Decree 356/2025/NĐ-CP Art. 8(3)': 'Asetus 356/2025/NĐ-CPĐ-CP Art. 8(3)',
        'Draft Decree on Data Platforms 2026': 'Tietoalustoja koskeva asetusluonnos 2026',
        'Law 122/2025/QH15 Ch. III; NĐ 98/2020; NĐ 24/2025': 'Laki 122/2025/QH15 Ch. III; NĐ 98/2020; NĐ 24/2025',
        'Law 122/2025/QH15 Ch. IV (Consumer Protection)': 'Laki 122/2025/QH15 Ch. IV (kuluttajansuoja)',
        'Law 122/2025/QH15 — Online.gov.vn operational procedure': 'Laki 122/2025/QH15 — Online.gov.vn:n toimintamenettely',
        'Law 134/2025/QH15 Article 12': 'Lain 134/2025/QH15 12 artikla',
        'Law 134/2025/QH15 Article 21': 'Lain 134/2025/QH15 21 artikla',
        'Law 134/2025/QH15 Article 27': 'Lain 134/2025/QH15 27 artikla',
        'Law 134/2025/QH15 Articles 9–10': 'Laki 134/2025/QH15 9–10 artikla',
        'Law 24/2023/QH15 — article mapping pending': 'Laki 24/2023/QH15 — artikkelikartoitus vireillä',
        'Law 60/2024/QH15 (internal control target)': 'Laki 60/2024/QH15 (sisäisen valvonnan tavoite)',
        'Law 91/2025/QH15 Art. 21': 'Laki 91/2025/QH15 Art. 21',
        'Law 91/2025/QH15 Art. 23': 'Laki 91/2025/QH15 Art. 23',
        'Policy-defined': 'Käytännön määrittelemä',
        Quarterly: 'Neljännesvuosittain',
        'QĐ 778/QĐ-BCA-A05 administrative procedure': 'QĐ 778/QĐ-BCA-A05 hallinnollinen menettely',
        'internal control target (Law 116/2025/QH15 Art. 24(2)(b), 41(5) — exact timeline pending implementing instrument)': 'sisäisen valvonnan tavoite (laki 116/2025/QH15 24 artiklan 2 kohdan b alakohta, 41 artiklan 5 kohta – tarkka aikataulu odottaa täytäntöönpanosäädöstä)',
        'internal control target (Law 116/2025/QH15 Art. 41(3) — exact timeline pending implementing instrument)': 'sisäisen valvonnan tavoite (laki 116/2025/QH15 41 artiklan 3 kohta – tarkka aikataulu odottaa täytäntöönpanosäädöstä)',
        'source verification pending': 'lähteen vahvistus odottaa',
        'Circular 83/2025/TT-NHNN — Ad-hoc Internal Audit Reporting to SBV (Layer 5)': 'Circular 83/2025/TT-NHNN ? Ad hoc -sis?isen tarkastuksen raportointi SBV:lle (kerros 5)',
        'Circular 83/2025/TT-NHNN — Annual ICAAP Report (Phụ lục III) (Layer 5)': 'Circular 83/2025/TT-NHNN ? Vuotuinen ICAAP-raportti (Ph? l?c III) (kerros 5)',
        'Circular 83/2025/TT-NHNN — Annual Phụ lục I (Layer 5)': 'Circular 83/2025/TT-NHNN ? Vuotuinen Ph? l?c I (kerros 5)',
        'Circular 83/2025/TT-NHNN — Annual Phụ lục IV (Layer 5)': 'Circular 83/2025/TT-NHNN ? Vuotuinen Ph? l?c IV (kerros 5)',
        'Circular 83/2025/TT-NHNN — Annual Risk Management Report (Phụ lục II) (Layer 5)': 'Circular 83/2025/TT-NHNN ? Vuotuinen riskienhallintaraportti (Ph? l?c II) (kerros 5)',
        'Circular 83/2025/TT-NHNN — Asset Management Responsibility (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN ? Vastuu varainhallinnasta (kerros 2, teema C)',
        "Circular 83/2025/TT-NHNN — Board / Members' Council Oversight (Layer 2, theme A)": 'Circular 83/2025/TT-NHNN ? Hallituksen / j?senten neuvoston valvonta (kerros 2, teema A)',
        'Circular 83/2025/TT-NHNN — Board Committees (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN ? Hallituksen valiokunnat (kerros 2, teema A)',
        'Circular 83/2025/TT-NHNN — Board Decisions and Minutes (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN ? Hallituksen p??t?kset ja p?yt?kirjat (kerros 2, teema A)',
        'Circular 83/2025/TT-NHNN — Branch Control Procedures (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN ? Sivukonttorien valvontamenettelyt (kerros 2, teema C)',
        'Circular 83/2025/TT-NHNN — Compliance Assessment of Internal Rules (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN ? Sis?isten s??nt?jen vaatimustenmukaisuuden arviointi (kerros 2, teema D)',
        'Circular 83/2025/TT-NHNN — Compliance Function (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN ? Compliance-toiminto (kerros 2, teema D)',
        'Circular 83/2025/TT-NHNN — Compliance Remediation Monitoring (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN ? Compliance-korjausten seuranta (kerros 2, teema D)',
        'Circular 83/2025/TT-NHNN — Concentration Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN ? Keskittym?riski (kerros 3, teema E)',
        'Circular 83/2025/TT-NHNN — Conflict of Interest (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN ? Eturistiriita (kerros 2, teema C)',
        'Circular 83/2025/TT-NHNN — Control Culture (Layer 2, theme A/B)': 'Circular 83/2025/TT-NHNN ? Valvontakulttuuri (kerros 2, teema A/B)',
        'Circular 83/2025/TT-NHNN — Credit Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN ? Luottoriski (kerros 3, teema E)',
        'Circular 83/2025/TT-NHNN — Delegated Authority (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN ? Delegoitu toimivalta (kerros 2, teema C)',
        'Circular 83/2025/TT-NHNN — Early Implementation Notification (Layer 5)': 'Circular 83/2025/TT-NHNN ? Ilmoitus ennenaikaisesta k?ytt??notosta (kerros 5)',
        'Circular 83/2025/TT-NHNN — General Director Responsibility (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN ? P??johtajan vastuu (kerros 2, teema A)',
        'Circular 83/2025/TT-NHNN — ICAAP (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN ? ICAAP (kerros 3, teema E)',
        'Circular 83/2025/TT-NHNN — Interest-Rate Risk in the Banking Book (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN ? Pankkikirjan korkoriski (kerros 3, teema E)',
        'Circular 83/2025/TT-NHNN — Internal Audit Charter (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN ? Sis?isen tarkastuksen toimintaohje (kerros 2, teema H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Engagements (Theme H)': 'Circular 83/2025/TT-NHNN ? Sis?isen tarkastuksen toimeksiannot (teema H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Findings Communication (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN ? Sis?isen tarkastuksen havaintojen viestint? (kerros 2, teema H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Findings and Corrective Actions (Theme H)': 'Circular 83/2025/TT-NHNN ? Sis?isen tarkastuksen havainnot ja korjaavat toimet (teema H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Follow-Up (Theme H)': 'Circular 83/2025/TT-NHNN ? Sis?isen tarkastuksen jatkoseuranta (teema H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Governance': 'Circular 83/2025/TT-NHNN ? Sis?isen tarkastuksen hallintamalli',
        'Circular 83/2025/TT-NHNN — Internal Audit Independence (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN ? Sis?isen tarkastuksen riippumattomuus (kerros 2, teema H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Plan (Layer 2/Theme H)': 'Circular 83/2025/TT-NHNN ? Sis?isen tarkastuksen suunnitelma (kerros 2/teema H)',
        'Circular 83/2025/TT-NHNN — Internal Audit Reporting to SBV (Layer 2, theme H)': 'Circular 83/2025/TT-NHNN ? Sis?isen tarkastuksen raportointi SBV:lle (kerros 2, teema H)',
        'Circular 83/2025/TT-NHNN — Internal Control System Governance (Layer 2)': 'Circular 83/2025/TT-NHNN ? Sis?isen valvontaj?rjestelm?n hallintamalli (kerros 2)',
        'Circular 83/2025/TT-NHNN — Internal Reporting Procedure (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN ? Sis?isen raportoinnin menettely (kerros 2, teema C)',
        'Circular 83/2025/TT-NHNN — Internal Reports Register (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN ? Sis?isten raporttien rekisteri (kerros 4, teema F)',
        'Circular 83/2025/TT-NHNN — Law-Change Monitoring (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN ? Lains??d?nt?muutosten seuranta (kerros 2, teema D)',
        'Circular 83/2025/TT-NHNN — Legal and Reputational Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN ? Oikeudellinen ja maineeseen liittyv? riski (kerros 3, teema E)',
        'Circular 83/2025/TT-NHNN — Limit Breach Escalation (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN ? Rajaylityksen eskalointi (kerros 3, teema E)',
        'Circular 83/2025/TT-NHNN — Liquidity Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN ? Likviditeettiriski (kerros 3, teema E)',
        'Circular 83/2025/TT-NHNN — MIS Annual Review (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN ? MIS:n vuosikatselmus (kerros 4, teema F)',
        'Circular 83/2025/TT-NHNN — MIS Backup and Continuity (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN ? MIS:n varmuuskopiointi ja jatkuvuus (kerros 4, teema F)',
        'Circular 83/2025/TT-NHNN — MIS Escalation Mechanisms (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN ? MIS:n eskalointimekanismit (kerros 4, teema F)',
        'Circular 83/2025/TT-NHNN — MIS Reliability and Confidentiality (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN ? MIS:n luotettavuus ja luottamuksellisuus (kerros 4, teema F)',
        'Circular 83/2025/TT-NHNN — MIS Risk Data Sources (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN ? MIS:n riskidatan l?hteet (kerros 4, teema F)',
        'Circular 83/2025/TT-NHNN — Management Information System (Layer 4)': 'Circular 83/2025/TT-NHNN ? Johdon tietoj?rjestelm? (kerros 4)',
        'Circular 83/2025/TT-NHNN — Market Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN ? Markkinariski (kerros 3, teema E)',
        'Circular 83/2025/TT-NHNN — Material Risk Register (Layer 3)': 'Circular 83/2025/TT-NHNN ? Olennaisten riskien rekisteri (kerros 3)',
        'Circular 83/2025/TT-NHNN — Model Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN ? Malliriski (kerros 3, teema E)',
        'Circular 83/2025/TT-NHNN — New Product Review (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN ? Uuden tuotteen arviointi (kerros 3, teema E)',
        'Circular 83/2025/TT-NHNN — Operational Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN ? Operatiivinen riski (kerros 3, teema E)',
        'Circular 83/2025/TT-NHNN — Outsourcing BCP (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN ? Ulkoistamisen jatkuvuussuunnitelma (kerros 2, teema G)',
        'Circular 83/2025/TT-NHNN — Outsourcing Contracts (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN ? Ulkoistamissopimukset (kerros 2, teema G)',
        'Circular 83/2025/TT-NHNN — Outsourcing Controls': 'Circular 83/2025/TT-NHNN ? Ulkoistamisen valvontatoimet',
        'Circular 83/2025/TT-NHNN — Outsourcing Due Diligence (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN ? Ulkoistamisen due diligence (kerros 2, teema G)',
        'Circular 83/2025/TT-NHNN — Outsourcing Fallback Provider (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN ? Ulkoistamisen varatoimittaja (kerros 2, teema G)',
        'Circular 83/2025/TT-NHNN — Outsourcing Materiality (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN ? Ulkoistamisen olennaisuus (kerros 2, teema G)',
        'Circular 83/2025/TT-NHNN — Outsourcing Monitoring (Layer 2, theme G)': 'Circular 83/2025/TT-NHNN ? Ulkoistamisen seuranta (kerros 2, teema G)',
        'Circular 83/2025/TT-NHNN — Phụ lục V/VI Technical References (Layer 5)': 'Circular 83/2025/TT-NHNN ? Ph? l?c V/VI -tekniset viitteet (kerros 5)',
        'Circular 83/2025/TT-NHNN — Policy Register (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN ? K?yt?nt?rekisteri (kerros 2, teema C)',
        'Circular 83/2025/TT-NHNN — Professional Ethics (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN ? Ammattieettiset periaatteet (kerros 2, teema C)',
        'Circular 83/2025/TT-NHNN — Risk Appetite (Layer 3)': 'Circular 83/2025/TT-NHNN ? Riskinottohalukkuus (kerros 3)',
        'Circular 83/2025/TT-NHNN — Risk Committee Coordination (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN ? Riskikomitean koordinointi (kerros 2, teema E)',
        'Circular 83/2025/TT-NHNN — Risk Data Governance (Layer 3, theme E/F) — phased to 2028-01-01': 'Circular 83/2025/TT-NHNN ? Riskidatan hallintamalli (kerros 3, teema E/F) ? vaiheistettu 2028-01-01 asti',
        'Circular 83/2025/TT-NHNN — Risk Limit Review (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN ? Riskirajojen katselmointi (kerros 3, teema E)',
        'Circular 83/2025/TT-NHNN — Risk Limits Framework (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN ? Riskirajojen viitekehys (kerros 3, teema E)',
        'Circular 83/2025/TT-NHNN — Risk Management Function (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN ? Riskienhallintatoiminto (kerros 2, teema E)',
        'Circular 83/2025/TT-NHNN — Risk Methodology and Models (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN ? Riskimenetelm?t ja -mallit (kerros 2, teema E)',
        'Circular 83/2025/TT-NHNN — Risk Reporting (Layer 2, theme E)': 'Circular 83/2025/TT-NHNN ? Riskiraportointi (kerros 2, teema E)',
        'Circular 83/2025/TT-NHNN — Risk Reporting Cadence (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN ? Riskiraportoinnin rytmi (kerros 4, teema F)',
        'Circular 83/2025/TT-NHNN — SBV Remediation Submission (Layer 5)': 'Circular 83/2025/TT-NHNN ? Korjaustoimien toimittaminen SBV:lle (kerros 5)',
        'Circular 83/2025/TT-NHNN — SBV Submission Channels (Layer 5)': 'Circular 83/2025/TT-NHNN ? SBV:n toimituskanavat (kerros 5)',
        'Circular 83/2025/TT-NHNN — Sector Applicability (Layer 1)': 'Circular 83/2025/TT-NHNN ? Toimialakohtainen soveltuvuus (kerros 1)',
        'Circular 83/2025/TT-NHNN — Segregation of Duties (Layer 2, theme C)': 'Circular 83/2025/TT-NHNN ? Teht?vien eriytt?minen (kerros 2, teema C)',
        'Circular 83/2025/TT-NHNN — Senior Management Supervision (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN ? Ylimm?n johdon valvonta (kerros 2, teema A)',
        'Circular 83/2025/TT-NHNN — Strategic Risk (Layer 3, theme E)': 'Circular 83/2025/TT-NHNN ? Strateginen riski (kerros 3, teema E)',
        'Circular 83/2025/TT-NHNN — Stress Testing (Layer 3, theme E) — phased to 2028-01-01': 'Circular 83/2025/TT-NHNN ? Stressitestaus (kerros 3, teema E) ? vaiheistettu 2028-01-01 asti',
        'Circular 83/2025/TT-NHNN — Supervisory Board (Layer 2, theme A)': 'Circular 83/2025/TT-NHNN ? Valvontaneuvosto (kerros 2, teema A)',
        'Circular 83/2025/TT-NHNN — Supervisory Findings Register (Layer 4, theme F)': 'Circular 83/2025/TT-NHNN ? Valvontahavaintojen rekisteri (kerros 4, teema F)',
        'Circular 83/2025/TT-NHNN — Three Lines Roles and Escalation (Layer 2, theme B)': 'Circular 83/2025/TT-NHNN ? Kolmen puolustuslinjan roolit ja eskalointi (kerros 2, teema B)',
        'Circular 83/2025/TT-NHNN — Three Lines of Defence (Layer 2)': 'Circular 83/2025/TT-NHNN ? Kolme puolustuslinjaa (kerros 2)',
        'Circular 83/2025/TT-NHNN — Violation Escalation (Layer 2, theme D)': 'Circular 83/2025/TT-NHNN ? Rikkomuksen eskalointi (kerros 2, teema D)',
        // Sprint 62.49.1 (E12.5 pass 1, P3) — consumer-rights and food-traceability references.
        'Law 19/2023/QH15 — Điều 31 khoản 2': 'Law 19/2023/QH15 — Điều 31 khoản 2',
        'Law 19/2023/QH15 and Decree 55/2024/ND-CP official Government portal metadata.': 'Law 19/2023/QH15 and Decree 55/2024/ND-CP official Government portal metadata.',
        'Circular 11/2026/TT-BCT — Điều 6 khoản 2': 'Circular 11/2026/TT-BCT — Điều 6 khoản 2',
        'Circular 11/2026/TT-BCT — Điều 12 khoản 2': 'Circular 11/2026/TT-BCT — Điều 12 khoản 2',
        'Circular 11/2026/TT-BCT — Điều 15 khoản 1': 'Circular 11/2026/TT-BCT — Điều 15 khoản 1',
        'Circular 11/2026/TT-BCT — Điều 15 khoản 2': 'Circular 11/2026/TT-BCT — Điều 15 khoản 2',
        'Circular 63/2026/TT-BCT — Điều 10 khoản 2': 'Circular 63/2026/TT-BCT — Điều 10 khoản 2',
        'Circular 63/2026/TT-BCT — Điều 10 khoản 3': 'Circular 63/2026/TT-BCT — Điều 10 khoản 3',
        'Circular 63/2026/TT-BCT — Điều 19 khoản 1': 'Circular 63/2026/TT-BCT — Điều 19 khoản 1',
    },
    framework_authorities: {
        'Quốc hội (National Assembly)': 'Quốc hội (kansalliskokous)',
        'Ministry of Public Security / A05 Department': 'Yleisen turvallisuuden ministeriö / A05-osasto',
        'Ministry of Public Security': 'Yleisen turvallisuuden ministeriö',
        'Ministry of Information and Communications': 'Tieto- ja viestintäministeriö',
        'Government of Vietnam': 'Vietnamin hallitus',
        'European Data Protection Board (EDPB)': 'Euroopan tietosuojalautakunta (EDPB)',
        'ENISA / National CSIRTs': 'ENISA / kansalliset CSIRT-ryhmät',
        'State Bank of Vietnam': 'Vietnamin valtionpankki',
    },
    tour_titles: {
        access_accountability: 'Pääsy Accountability Tour',
        assessments: 'Arviointikierros',
        audit_trail: 'Audit Trail -esittely',
        classification: 'Luokittelukierros',
        compliance_forms: 'Vaatimustenmukaisuuslomakkeiden esittely',
        connectors: 'Liitinten esittely',
        consent_properties: 'Suostumuksen kiinteistöjen esittely',
        consent: 'Suostumuskierros',
        dashboard: 'Kojelautakierros',
        data_mapping: 'Tietojen kartoituskierros',
        data_subjects: 'Rekisteröityjen kierros',
        deletion_orchestration: 'Poiston orkestraatiokierros',
        discovery: 'Tietojen l?ydett?vyyden esittely',
        governance: 'Hallintokierros',
        help_center: 'Ohjekeskuksen esittely',
        incident_ops: 'Tapahtuma-operaatioiden esittely',
        incidents: 'Tapahtumien esittely',
        localization: 'Lokalisointikierros',
        monitoring: 'Seurantakierros',
        onboarding: 'Aloituskierros',
        organizations: 'Organisaatioiden esittely',
        partner_workspace: 'Kumppanity?tilan esittely',
        platform_settings: 'Alustan asetusten esittely',
        privacy_scanner: 'Tietosuojaskannerin esittely',
        program_governance: 'Ohjelman hallintokierros',
        rights_requests: 'Oikeuspyyntöjen esittely',
        task_management: 'Tehtävienhallintakierros',
        vendor_risk: 'Myyjän riskikierros',
    },
    tour_descriptions: {
        access_accountability: 'Sisäisen tarkastuksen johtajana (P-VN-10) tarkasta käyttötodisteet, tarkastuslupien käyttö ja tutki poikkeavia malleja osoittaaksesi GDPR artiklan 5(1)(f) eheyden ja luottamuksellisuuden.',
        assessments: 'Yritys DPO (P-VN-01) suorittaa DPIA:n asetuksen 356 Mau so 02a mukaisesti, tunnistaa noudattamispuutteet ja arvioi rajat ylittävän siirron riskit kohdassa PDPL.',
        audit_trail: 'Sisäisen tarkastuksen päähenkilönä (P-VN-10) voit tiedustella moduulien välistä todistetta, koota tarkastuspaketteja ja viedä peukalointilokeja säädöstenmukaisia lähetyksiä varten.',
        classification: 'Tietojen valvojana (P-VN-05) määritä tietojen luokittelutaksonomiat, määritä herkkyystunnisteet, valvo säilytyskäytäntöjä ja ratkaise PDPL:n noudattamisen edellyttämät kattavuusaukot.',
        compliance_forms: 'Yrityksenä DPO (P-VN-01) selaa, täytä ja lähetä virallisia Vietnamin sääntelylomakkeita, mukaan lukien Phu luc I-, Phu luc II- ja Mau so -malleja PDP-komitean hakemuksia varten.',
        connectors: 'IT-järjestelmien johtajana (P-VN-05) ota huomioon integraatioiden liiketoimintavaatimukset, yhdistä liittimet vaatimustenmukaisiin tietovirtoihin ja varmista POC-valmius ennen julkaisemista.',
        consent_properties: 'Yritys DPO (P-VN-01) määritä CMP-käyttöönotto: digitaalinen omaisuuden rekisteröinti, kultaiset mallit johdonmukaisia PDPL-suostumusbannereita varten ja sidontamatriisit, jotka linkittävät ominaisuuksia käsittelytoimintoihin.',
        consent: 'Yrityksenä DPO (P-VN-01) hallitse suostumuskäytännön elinkaarta: määritä keräämistarkoitukset, pane PDPL-peruuttamisoikeudet täytäntöön ja integroi suostumuksen hallintaalustaan.',
        dashboard: 'Yritys DPO (P-VN-01) suuntaudu vaatimustenmukaisuusasennon komentokeskukseen, joka paljastaa PDPL-määräajat, rikkomukset SLA-sopimukset ja auditointivalmiuden KPI:t.',
        data_mapping: 'Tietojen valvojana (P-VN-05) rakenna ROPA-inventaarisi, kartoita rajat ylittäviä tietovirtoja ja ylläpidä PDPL:n edellyttämää käsittelytoimintarekisteriä.',
        data_subjects: 'Yrityksen DPO tai Education Compliance Lead -johtajana hallitse yhtenäisiä rekisteröityjen profiileja, jotka linkittävät suostumustietueet, oikeuspyynnöt ja aikajanan toiminnan alustalla.',
        deletion_orchestration: 'Kuten DPO (P-VN-01), varmista päästä päähän tietojen poistaminen sisäisistä järjestelmistä ja kolmansien osapuolien toimittajista salausvarmistusketjuilla GDPR artiklan 17 poisto-oikeuden noudattamiseksi.',
        discovery: 'Tietojen valvojana (P-VN-05) suorita automaattisia tarkistuksia löytääksesi henkilökohtaisia tietoja yhdistetyistä järjestelmistä, tarkista vaiheittaiset löydökset ja syötä tulokset PDPL-merkintöjä varten luokittelumoduuliin.',
        governance: 'Myyjän riskipäällikkönä (P-VN-08) ylläpitää toimittajarekisteriä, seurata aliprosessorin ketjuja, arvioida rajat ylittävien siirtojen riskejä ja valvoa hallinnointiohjeita PDPL:ssa.',
        help_center: 'Kaikille henkilöille – tietosuojavastaaville, tarkastajille, IT-johtajille ja vaatimustenmukaisuuspäälliköille – löydät vastaukset, ohjeet ja moduulikohtaiset asiakirjat päivittäisten vaatimustenmukaisuuden työnkulkujen tueksi.',
        incident_ops: 'Tapahtumakomentajana (P-VN-09) aktivoi vastausleikit, seuraa SLA 72 tunnin PDPL ilmoitusikkunan noudattamista, hallitse eskalaatioita ja koordinoi toimittajan vastausta.',
        incidents: 'Incident Commander (P-VN-09) havaitse ja raportoi tietoturvaloukkauksista 72 tunnin PDPL ilmoitusvelvollisuudella PDP-komitealle, luokittele vakavuuden mukaan ja rakenna tarkastustason tapausasiakirjat.',
        localization: 'Yritys DPO (P-VN-01) hallitse sääntelykehyksen elinkaarta: asenna vietnamilaiset markkinapaketit, aktivoi PDPL- ja Decree 13 -kehykset, yhdistä vaatimukset alustamoduuleihin ja hallitse käännöksiä.',
        monitoring: 'Yritys DPO (P-VN-01) perustaa jatkuvat vaatimustenmukaisuuden seurantaohjelmat, seuraa säädösten muutoksia, mittaa KPI:itä ja hallitse säännösten mukaisia vuorovaikutustapauksia PDPL:n mukaisesti.',
        onboarding: 'Compliance Trainer (P-VN-02) anna koulutusta opastetuilla kierroksilla, lataa realistisia näytetietoja käytännön harjoituksia varten ja suorita ohjattu asennustoiminto pika-aloitusskenaarioihin.',
        organizations: 'DPO (P-VN-01) määritä usean organisaation vaatimustenmukaisuusrakenteet, määritä osastohierarkiat ja reititä rekisteröidyt pyynnöt koko yritysryhmässäsi.',
        partner_workspace: 'Kumppanitilipäällikkönä osallistu kumppaniorganisaatioihin, määritä palvelutasoja, seuraa vaatimustenmukaisuuden toimituksia ja hallitse kumppanimarkkinoita.',
        platform_settings: 'Alustan järjestelmänvalvojana hallitset kaikkia toiminnallisia määrityksiä – identiteettiä, suojausta, integraatioita ja moduulien käyttöoikeuksia, jotka tukevat vaatimustenmukaisuusohjelmaasi.',
        privacy_scanner: 'Tietosuojaanalyytikona (P-VN-06) suorita jatkuvaa valvontaskannausta evästeiden suostumuksen rikkomusten, luvattomien seurantalaitteiden ja käytäntöjen aukkojen havaitsemiseksi. Näin saat todisteita sähköisen viestinnän tietosuojasta ja GDPR noudattamisesta.',
        program_governance: 'Selaa kypsyysmallien, arvioiden, todisteiden aukkojen, yhteenvetojen ja muiden välillä. Jokainen alamoduuli antaa kokonaisvaltaisen näkemyksen sisäisestä ohjelman tilasta.',
        rights_requests: 'Yrityksenä DPO (P-VN-01) hallitse PDPL-kohdassa olevia rekisteröidyn oikeuksia koskevia pyyntöjä 15 päivän lakisääteisillä määräajoilla, henkilöllisyyden todentamisella ja tilintarkastustason ratkaisupoluilla.',
        task_management: 'Ohjaa vaatimustenmukaisuustehtävät henkilöiden välillä, seuraa SLA määräaikoja ja automatisoi työnkulkuja, jotka pitävät tiimisi vastuullisena.',
        vendor_risk: 'Toimittajan riskipäällikkönä (P-VN-08) arvioi kolmannen osapuolen riskejä, pakota porttitarkastuksia ja käynnistä uudelleenarviointeja – eri kuin ohjelman hallinta, joka seuraa sisäistä kypsyytesi.',
    },
    consolidation: {
        title: 'Konsolidointitapaukset',
        loading_cases: 'Loading consolidation cases',
        loading_case_detail: 'Loading case detail',
        new_case: 'Luo konsolidointitapaus',
        case_count: 'case',
        created: 'Konsolidointitapaus luotu',
        create: 'Luo tapaus',
        creating: 'Luodaan...',
        cancel: 'Peruuta',
        no_cases: 'Konsolidointitapauksia ei löytynyt.',
        all_statuses: 'Kaikki tilat',
        all_purposes: 'Kaikki käyttötarkoitukset',
        filter_status: 'Suodata tilan mukaan',
        filter_purpose: 'Suodata käyttötarkoituksen mukaan',
        col_title: 'Otsikko',
        col_purpose: 'Tarkoitus',
        col_status: 'Tila',
        col_progress: 'Edistyminen',
        col_deadline: 'Määräaika',
        col_department: 'osasto',
        col_version: 'Versio',
        col_evidence: 'Todisteet',
        sections_accepted: 'accepted',
        prev: 'Edellinen',
        next: 'Seuraavaksi',
        page: 'Sivu',
        prev_page: 'Edellinen sivu',
        next_page: 'Seuraava sivu',
        field_title: 'Otsikko',
        field_purpose: 'Tarkoitus',
        field_framework: 'Puitekoodi',
        field_deadline: 'Määräaika',
        field_reviewer: 'Tarkistajan käyttäjätunnus',
        title_placeholder: 'Anna tapauksen otsikko',
        framework_placeholder: 'esim. PDPD-2023',
        reviewer_placeholder: 'Anna arvioijan käyttäjätunnus',
        // fix-sprint-20260821-2140 F6 — structural parity only. R-57 defers real
        // translation of the 14 non-EN/VI locales to Phase 25 (sprint-57.7.0); these
        // land EN-identical and are counted there rather than repaired here.
        field_summary: 'Summary',
        field_details: 'Details',
        field_risk_assessment: 'Risk Assessment',
        field_mitigation_measures: 'Mitigation Measures',
        purposes: {
            dpia_filing: 'DPIA Arkistointi',
            cross_border_filing: 'Rajat ylittävä arkistointi',
            annual_governance_attestation: 'Vuosittainen hallintotodistus',
            breach_package: 'Rikkomuspaketti',
            audit_inspection_pack: 'Audit Inspection Pack',
        },
        statuses: {
            draft: 'Luonnos',
            collecting: 'Keräily',
            consolidating: 'Konsolidoituu',
            review: 'Tarkista',
            corrections: 'Korjaukset',
            pending_approval: 'Odottaa hyväksyntää',
            approved: 'Hyväksytty',
            completed: 'Valmis',
            closed: 'Suljettu',
            rejected: 'Hylätty',
        },
        section_statuses: {
            pending: 'Odottaa',
            in_progress: 'Meneillään',
            submitted: 'Lähetetty',
            accepted: 'Hyväksytty',
            needs_correction: 'Vaatii korjausta',
            corrected: 'Korjattu',
        },
        flag_types: {
            missing_section: 'Osa puuttuu',
            conflicting_entry: 'Ristiriitainen sisääntulo',
            stale_entry: 'Vanhentunut sisääntulo',
            missing_evidence: 'Puuttuva todiste',
            incomplete_data: 'Puutteelliset tiedot',
            duplicate_entry: 'Päällekkäinen merkintä',
        },
        flag_severities: {
            critical: 'Kriittinen',
            warning: 'Varoitus',
            info: 'Tietoja',
        },
        sections_title: 'Osat',
        no_sections: 'Osioita ei ole vielä määritetty.',
        flags_title: 'Liput',
        no_flags: 'Lippuja ei havaittu.',
        open: 'open',
        approval_chain: 'Hyväksyntäketju',
        pending: 'Odottaa',
        audit_trail: 'Audit Trail',
        no_audit: 'Ei tarkastustapahtumia.',
        auto_assign: 'Määritä osiot automaattisesti',
        start_collection: 'Aloita kokoelma',
        review_case: 'Tarkista tapaus',
        generate_export: 'Luo vienti',
        review_title: 'Konsolidointikatsaus',
        advance_approval: 'Ennakkohyväksyntä',
        back_to_case: 'Takaisin tapaukseen',
        accept: 'Hyväksy',
        return_section: 'Palaa',
        return_comment: 'Palauta kommentti...',
        resolve: 'Ratkaise',
        dismiss: 'Hylkää',
        resolution_notes: 'Päätösmuistiinpanot...',
        notes: 'Huomautuksia',
        section_accepted: 'Osio hyväksytty',
        section_returned: 'Osa palautettiin korjattavaksi',
        advanced_to_approval: 'Tapaus on edennyt odottavaan hyväksyntään',
        section_data: 'Osion tiedot',
        save_progress: 'Tallenna edistyminen',
        submit_section: 'Lähetä osio',
        submitting: 'Lähetetään...',
        resubmit: 'Lähetä uudelleen',
        progress_saved: 'Edistyminen tallennettu',
        section_submitted: 'Osio lähetetty',
        correction_required: 'Korjaus vaaditaan',
        evidence_title: 'Todisteet',
        no_evidence: 'Ei todisteita liitteenä.',
        add_evidence: 'Lisää todisteita',
        evidence_added: 'Todisteet lisätty',
        evidence_files: 'todisteet tiedostot',
        uploaded_by: 'ladannut',
        comments_title: 'Kommentit',
        no_comments: 'Ei vielä kommentteja.',
        comment_placeholder: 'Lisää kommentti...',
        add_comment: 'Lisää kommentti',
        comment_added: 'Kommentti lisätty',
        view_data: 'Näytä lähetetyt tiedot',
        export_title: 'Asiakirjojen vienti',
        export_generated: 'Vientipaketti luotu',
        regenerate: 'Luo uudelleen',
        regenerating: 'Regeneroidaan...',
        generated_at: 'Luotu klo',
        artifacts_title: 'Vie esineitä',
        artifact_type: 'Artefaktti',
        file_name: 'Tiedoston nimi',
        file_size: 'Koko',
        generated: 'Luotu',
        download: 'Lataa',
        artifacts: {
            master_report: 'Pääraportti',
            evidence_bundle: 'Todistuspaketti (ZIP)',
            contributor_lineage: 'Contributor Lineage',
            conflict_resolution: 'Konfliktinratkaisu',
            approval_history: 'Hyväksyntähistoria',
        },
        official_forms: 'Viralliset lomakkeet',
        forms_description: 'Seuraavat viralliset lomakkeet voidaan muodostaa yhdistetyistä tiedoista. Viralliset lomakkeet ovat erillisiä sisäisistä työpapereista.',
        announce_loaded_cases: 'Ladattu {count} konsolidointitapausta',
        announce_case_detail_loaded: 'Asiakirjan tiedot ladattu',
        announce_case_transitioned: 'Tapaus siirretty {status}',
        announce_flag_resolved: 'Lippu ratkaistu',
        announce_flag_dismissed: 'Lippu hylätty',
        case_detail_loaded: 'Asiakirjan tiedot ladattu',
        sections_auto_assigned: 'Osastohierarkiasta automaattisesti määrätyt osiot',
        errors: {
            load_failed: 'Tietojen lataaminen epäonnistui. Yritä uudelleen.',
            create_failed: 'Tapauksen luominen epäonnistui. Yritä uudelleen.',
            required_fields: 'Täytä kaikki vaaditut kentät.',
            not_found: 'Ei löytynyt.',
            comment_required: 'Kommentti vaaditaan osiota palautettaessa.',
            notes_required: 'Huomautuksia tarvitaan, kun lippua ratkaistaan tai hylätään.',
        },
    },
    rights_requests: {
        import: {
            title: 'Tuo tapauksia',
            nav_title: 'Massatuonti',
            description: 'Lataa CSV-tiedosto oikeuspyyntötapausten massatuontia varten.',
            select_file: 'Valitse CSV-tiedosto',
            file_help: 'CSV otsikoineen, jotka vastaavat tapauskenttiä.',
            column_count_help: 'A row whose number of values differs from the number of columns in the header is not imported; it is listed under Errors with its row number.',
            // fix-sprint #431 — was hard-coded English on the Import page.
            required_columns: 'Required columns:',
            file_too_large: 'Tiedosto on liian suuri. Enimmäiskoko on 2 Mt.',
            preview: 'Esikatselu',
            columns_detected: 'Havaitut sarakkeet: {count}',
            column_mapping: 'Sarakemääritys',
            import_button: 'Tuo',
            import_complete: 'Tuonti valmis',
            import_error: 'Tuonti epäonnistui',
            result: 'Tuontitulos',
            total: 'Yhteensä',
            imported: 'Tuotu',
            skipped: 'Ohitettu',
            failed: 'Epäonnistunut',
            errors: 'Virheet',
            error_row: 'Rivi',
            error_field: 'Kenttä',
            error_message: 'Viesti',
            history: 'Tuontihistoria',
            no_history: 'Ei tuontihistoriaa.',
            history_file: 'Tiedostonimi',
            history_date: 'Päivämäärä',
        },
        batch: {
            nav_title: 'Eräprosessi',
            page_title: 'DSR eräprosessi',
            page_subtitle: 'Lataa CSV odottavia DSR-tiedostoja. Forseti laatii pyyntökohtaisen kuittausvastauksen ja asettaa jonoon vahvistustehtävän riviä kohden. DPO-arvostelut + hyväksyy jokaisen ennen lähettämistä.',
            upload_heading: 'Lataa',
            csv_hint: 'Pakolliset sarakkeet: request_id, requester_email, request_type. Valinnainen: määräaika (ISO-8601), huomautukset.',
            rows_heading: '{count} riviä jäsennetty',
            submit_label: 'Suorita erä',
            submitting: 'Luonnostetaan…',
            col_request_id: 'Pyydä tunnus',
            col_email: 'Pyydä sähköposti',
            col_type: 'Kirjoita',
            col_status: 'Tila',
            col_actions: 'Toiminnot',
            status_queued: 'Jonossa',
            status_drafted: 'Laadittu',
            status_failed: 'Epäonnistui',
            status_accepted: 'Hyväksytty',
            status_rejected: 'Hylätty',
            status_parsing_error: 'Jäsennysvirhe',
            templates: {
                access: 'Acknowledging your data subject access request. We will respond by {deadline} with a copy of the personal data we process about you, the purposes and the recipients, per [{frameworks}]. Please reply with any clarifying detail; otherwise we will proceed with full disclosure.',
                deletion: 'Acknowledging your erasure request under [{frameworks}]. We will identify the records in scope, apply any retention that still applies (a legal hold, a contractual obligation) and confirm completion by {deadline}.',
                correction: 'Acknowledging your correction request under [{frameworks}]. Please reply with the field or fields you want corrected and the new values; we will route the request to the relevant business unit and confirm by {deadline}.',
                objection: 'Acknowledging your objection to processing under [{frameworks}]. We will pause the relevant processing while we review your reasons and respond by {deadline}.',
                portability: 'Acknowledging your data portability request under [{frameworks}]. We will export your data in scope in a structured, commonly used, machine-readable format and confirm by {deadline}.',
                restriction: 'Acknowledging your restriction request under [{frameworks}]. We will mark the relevant records as restricted and respond by {deadline}.',
                consent_withdrawal: 'Acknowledging your withdrawal of consent under [{frameworks}]. Future processing that relies solely on the withdrawn consent will stop; processing on another basis (a legal obligation, a contract) continues unless you object to it separately. We will confirm by {deadline}.',
                protective_measures: 'Acknowledging your request for personal data protection measures and solutions under [{frameworks}]. We will assess and implement the applicable safeguards and confirm by {deadline}.',
                processing_information: 'Acknowledging your request to be informed about the processing of your personal data under [{frameworks}]. We will tell you what personal data we process about you, why, and who receives it, and respond by {deadline}.',
                consent_decision: 'Acknowledging your decision on consent to the processing of your personal data under [{frameworks}]. We will record it and apply it to the processing that relies on your consent, and confirm by {deadline}.',
                deadline_fallback: 'the deadline set by the law that applies to your request',
            },
            errors: {
                csv_empty: 'The CSV file is empty.',
                csv_missing_header: 'The CSV file is missing the required column: {header}.',
                row_missing_field: 'A required field is missing (request_id, requester_email or request_type).',
                row_unknown_type: 'Unknown request type: {type}.',
                read_failed: 'The CSV file could not be read.',
                run_failed: 'The batch could not be run.',
                decision_failed: 'The decision could not be saved.',
            },
            synthesis_statuses: {
                model: 'Drafted by the AI model',
                template_parse_failed: 'Standard template: the AI model returned no usable text',
                template_provider_failed: 'Standard template: the AI model call failed',
                template_not_configured: 'Standard template: no AI model is configured',
                template_by_design: 'Standard template: this workflow does not use the AI model for this field',
            },
            csv_picker_label: 'CSV file',
            draft_source: 'Draft source',
            draft_language: 'Draft language: {locale}',
            accept: 'Hyväksy',
            reject: 'Hylkää',
            permission_denied: 'You need both the case import and Forseti chat permissions to run a batch.',
            col_draft: 'Drafted response',
            view_draft: 'View drafted response',
            frameworks_cited: 'Frameworks cited',
        },
    },
    assessment_triggers: {
        title: 'Arviointikäynnistimet',
        subtitle: 'Määritä automaattisen arvioinnin luominen ja DPO ilmoitukset tietosuojatyönkulkua varten.',
        auto_create_dpia_label: 'Luo DPIA automaattisesti, kun vaaditaan DPIA-merkityn toiminnan käsittely',
        auto_notify_new_purpose_label: 'Ilmoita automaattisesti DPO, kun uusi tarkoitus luodaan arkaluontoisten tietoluokkien avulla',
        auto_notify_cross_border_label: 'Ilmoita automaattisesti DPO, kun rajat ylittävä siirto lisätään käsittelytoimintaan',
        default_dpo_label: 'Oletus DPO',
        default_dpo_placeholder: 'Valitse oletusarvo DPO...',
        notification_channels_label: 'Ilmoituskanavat',
        channel_in_app: 'Sovelluksen sisäinen',
        channel_email: 'S?hk?posti',
        save_success: 'Arviointikäynnistimen asetukset tallennettu.',
        save_error: 'Arviointikäynnistimen asetusten tallentaminen epäonnistui.',
        fetch_error: 'Arviointikäynnistimen asetusten lataaminen epäonnistui.',
        fetch_users_error: 'Käyttäjien lataaminen epäonnistui.',
        announce_loaded: 'Arviointikäynnistimen asetukset ladattu',
        dpia_status: 'DPIA Tila',
        dpia_linked: 'DPIA Linkitetty',
        dpia_required_no_dpia: 'DPIA Pakollinen – Ei luotu',
        create_dpia: 'Luo DPIA',
        create_dpia_success: 'DPIA luotu ja linkitetty toimintaan.',
        create_dpia_error: 'DPIA:n luominen toiminnalle epäonnistui.',
        view_dpia: 'Näytä DPIA',
        dpo_warning_title: 'The default DPO is not an active user',
        dpo_warning_body: 'The default DPO is no longer an active user of this organisation. New DPIAs are created with no reviewer named, so any user who can approve DPIAs may decide them. Choose an active user as the default DPO.',
        auto_notify_cross_border_help: 'When a processing activity is saved with a cross-border destination it did not list before, the default DPO is notified on the channels below. With no default DPO, the Compliance Owners are notified.',
        auto_notify_processor_change_label: 'Raise a DPIA reassessment when a processor changes',
        auto_notify_processor_change_help: "A change to a vendor, a sub-processor or a processing activity's processor details raises one reassessment task, with a checklist of the evidence to re-check, for each DPIA that names that vendor or activity. A DPIA whose previous reassessment task is still open does not get a second one.",
        notification_channels_help: "Where the cross-border notification is sent. Each recipient's own notification preferences still apply.",
    },
    data_subjects: {
        title: 'Rekisteröityjen profiilit',
        nav_title: 'Rekisteröidyt',
        nav: {
            profiles: 'Profiilit',
        },
        create: 'Luo profiili',
        edit: 'Muokkaa profiilia',
        delete: 'Poista profiili',
        delete_confirm: 'Haluatko varmasti poistaa tämän rekisteröidyn profiilin?',
        display_name: 'Näyttönimi',
        email: 'S?hk?posti',
        phone: 'Puhelin',
        employee_id: 'Työntekijän tunnus',
        student_id: 'Opiskelijatunnus',
        notes: 'Huomautuksia',
        external_refs: 'Ulkoiset viittaukset',
        cases_count: 'Tapaukset',
        workflows_count: 'Suostumustiedot',
        last_activity: 'Viimeinen toiminta',
        timeline: 'Aikajana',
        link_case: 'Link Case',
        link_workflow: 'Link Workflow',
        no_profiles: 'Rekisteröidyn profiileja ei löytynyt.',
        search_placeholder: 'Hae nimellä, sähköpostilla, puhelimella tai henkilötunnuksella...',
        create_success: 'Rekisteröidyn profiili luotu.',
        create_error: 'Rekisteröidyn profiilin luominen epäonnistui.',
        update_success: 'Rekisteröidyn profiili päivitetty.',
        update_error: 'Rekisteröidyn profiilin päivitys epäonnistui.',
        delete_success: 'Rekisteröidyn profiili poistettu.',
        delete_error: 'Rekisteröidyn profiilin poistaminen epäonnistui.',
        link_case_success: 'Tapaus linkitetty profiiliin.',
        link_case_error: 'Tapauksen linkittäminen profiiliin epäonnistui.',
        link_workflow_success: 'Työnkulku linkitetty profiiliin.',
        link_workflow_error: 'Työnkulun linkittäminen profiiliin epäonnistui.',
        load_error: 'Rekisteröidyn profiilien lataaminen epäonnistui.',
        detail_load_error: 'Rekisteröidyn profiilin lataaminen epäonnistui.',
        timeline_load_error: 'Aikajanan lataaminen epäonnistui.',
        tab_cases: 'DSR-tapaukset',
        tab_consent: 'Suostumustiedot',
        tab_timeline: 'Aikajana',
        tab_consent_workflows: 'Suostumustyönkulut',
        tab_web_consent: 'CMP Suostumus',
        web_consent_empty: 'Tälle aiheelle ei löytynyt CMP suostumustietueita.',
        web_consent_pagination: 'CMP consent record pages',
        web_consent_actions: {
            grant: 'Granted',
            revoke: 'Revoked',
            update: 'Updated',
            reject_all: 'Rejected all',
            accept_all: 'Accepted all',
        },
        web_consent_load_error: 'CMP suostumustietueiden lataaminen epäonnistui.',
        col_policy_version: 'Käytännön versio',
        col_versions: 'Versiot',
        col_consent_version: 'Suostumus',
        col_cookie_declaration_version: 'Evästeilmoitus',
        col_purposes: 'Tarkoitukset',
        purpose_granted: 'Myönnetty',
        purpose_denied: 'Kielletty',
        granted_domains: 'Myönnetty',
        denied_domains: 'Kielletty',
        col_action: 'Toiminta',
        col_timestamp: 'Aikaleima',
        col_domain: 'Verkkotunnus',
        no_cases: 'Ei yhdistettyjä tapauksia.',
        no_workflows: 'Ei linkitettyjä suostumustyönkulkuja.',
        no_timeline: 'Ei aikajanatapahtumia.',
        case_id: 'Tapaustunnus',
        case_status: 'Tila',
        case_type: 'Pyynnön tyyppi',
        case_created: 'Luotu',
        workflow_id: 'Työnkulun tunnus',
        workflow_type: 'Kirjoita',
        workflow_status: 'Tila',
        workflow_created: 'Luotu',
        timeline_date: 'Päivämäärä',
        timeline_event: 'Tapahtuma',
        timeline_details: 'Yksityiskohdat',
        timeline_time: 'Aika',
        timeline_type: 'Kirjoita',
        timeline_summary: 'Yhteenveto',
        type_case: 'DSR-kotelo',
        type_consent_workflow: 'Suostumustyönkulku',
        profile_info: 'Profiilin tiedot',
        announce_loaded: 'Rekisteröidyn profiilit ladattu',
        announce_detail_loaded: 'Rekisteröidyn profiili ladattu',
        select_case: '— Valitse tapaus —',
        select_workflow: '— Valitse työnkulku —',
        no_cases_hint: 'Linkitä tapauksia nähdäksesi ne täällä.',
        no_workflows_hint: 'Linkitä suostumustyönkulkuja nähdäksesi ne täällä.',
        case_options_read_subject: 'the cases offered for linking',
        workflow_options_read_subject: 'the consent workflows offered for linking',
        external_identifiers: 'External identifiers',
        external_identifiers_desc: 'The identifiers the installed record kinds declare for a data subject. An erasure request is checked against the records issued under them.',
        external_identifiers_none_declared: 'No installed record kind declares an external identifier for a data subject.',
        external_identifiers_load_error: 'The external identifier fields could not be read.',
        external_identifiers_edit_unavailable: 'The external identifier fields could not be read, so they are not changed by this save.',
        external_identifier_hint: 'Leave blank to remove it.',
        external_ref_conflicts_heading: 'Identifier conflicts',
        external_ref_conflict: 'On {date}, a {kind} record was created for this profile with a different {label} from the one recorded here. The recorded value was kept; check which is correct.',
        external_ref_conflict_unlabelled: 'On {date}, an issued record was created for this profile with a different {label} from the one recorded here. The recorded value was kept; check which is correct.',
        external_ref_conflict_open_record: 'Open the issued record',
        profile_stale: 'This profile was changed after you opened it, so your change was not saved. The profile has been reloaded; make your change again.',
        identifier_produced_by_record: 'The {label} was recorded by an issued record, so it cannot be removed or changed here.',
    },
    flows: {
        map: {
            description_prefix: '{systems} järjestelmät · {flows} virrat · {crossBorder} rajat ylittävät · {highRisk} suuri/kriittinen riski',
            switch_to_list: 'Vaihda luettelonäkymään',
            switch_to_list_aria: 'Vaihda taulukkonäkymään',
            svg_aria: 'Tietovirtakartta: {systems} järjestelmät, {flows} virrat, {crossBorder} rajat ylittävät',
            selected_system: 'Valittu järjestelmä',
            drill_hint: 'Avaa luettelo-välilehti muokataksesi tämän järjestelmän linkitettyjä kulkuja, DPIA:ita ja toimittajamäärityksiä.',
        },
    },
    packages: {
        light: {
            name: 'Kevyt',
            description: 'Olennaiset yksityisyyden vaatimustenmukaisuustyökalut pienille tiimeille',
        },
        standard: {
            name: 'Vakio',
            description: 'Täysin varustettu vaatimustenmukaisuusalusta kasvaville organisaatioille',
        },
        enterprise_unlimited: {
            name: 'Enterprise Unlimited',
            description: 'Yritystason vaatimustenmukaisuus monen organisaation tuella ja erityispalveluilla',
        },
    },
    forseti: {
        // Sprint 63.32.0 (Q1, #543) — the record summary's facts, rendered on screen;
        // the model prompt keeps the English summary.
        summary_fields: {
            accepted_until: 'Accepted until',
            active_timers: 'Active timers',
            actual_release: 'Actual release',
            ad_hoc_answered: 'Ad-hoc reports answered',
            ad_hoc_report_entries: 'Ad-hoc report entries',
            affected_systems: 'Affected systems',
            amended_on: 'Last amended',
            amendments: 'Amendments',
            answered_on: 'Answered',
            approval_mode: 'Approval mode',
            approved: 'Approved',
            approved_on: 'Approved',
            approved_risk_tier: 'Approved risk tier',
            artifact_kind: 'Artefact kind',
            authorisation_kind: 'Authorisation kind',
            authority: 'Authority',
            authority_reference_recorded: 'Authority reference recorded',
            authority_request: 'Authority request',
            authority_requests: 'Authority requests',
            batches: 'Batches',
            breach_notification: 'Breach notification',
            categories: 'Category tags',
            channel: 'Channel',
            claimant: 'Claimant',
            classification: 'Classification',
            classifications: 'Classifications',
            closed_on: 'Closed',
            closure_approved_on: 'Closure approved',
            completed_on: 'Completed',
            conformity_records: 'Conformity records',
            conformity_route: 'Conformity route',
            consent_cover_state: 'Consent cover',
            consent_evidence_referenced: 'Consent evidence referenced',
            corrective_measures: 'Corrective measures',
            country: 'Country',
            covered_services: 'Covered services',
            credentials: 'Credentials',
            cross_border_transfers: 'Cross-border transfers',
            cross_mapping_recorded_on: 'Cross-mapping recorded',
            cryptographic_assets: 'Cryptographic assets',
            current_version: 'Current version',
            data_categories: 'Data categories',
            data_element: 'Data element',
            data_tier: 'Data tier',
            dataset: 'Dataset',
            deadline: 'Deadline',
            decided_on: 'Decided',
            declared_on: 'Declared',
            declared_quantity: 'Declared quantity',
            deployment_context: 'Deployment',
            deposit_submission_recorded: 'Deposit submission recorded',
            description_recorded: 'Description recorded',
            destination_country: 'Destination country',
            details_recorded: 'Details recorded',
            dictionary_version: 'Dictionary version',
            document_type: 'Document type',
            documents: 'Documents',
            domains_evaluated: 'Domains evaluated',
            dpa_status: 'DPA status',
            dpo_assigned: 'DPO assigned',
            due: 'Due',
            effective_on: 'Effective',
            evidence: 'Evidence references',
            evidence_attachments: 'Evidence attachments',
            evidence_pack_linked: 'Evidence pack linked',
            expires_on: 'Expires',
            filing_linked: 'Filing record linked',
            filing_type: 'Filing type',
            financial_crime_signed: 'Financial-crime function signed',
            fine_areas: 'Fine areas cited',
            form_type: 'Form',
            format: 'Format',
            framework: 'Framework',
            gate_kind: 'Deposit gate kind',
            generated_tasks: 'Generated tasks',
            identity_linked: 'Linked to an identity record',
            identity_verification: 'Identity verification',
            incident: 'Incident',
            incidents: 'Incidents',
            intake_evidence: 'Intake evidence',
            intake_kind: 'Intake kind',
            issued_on: 'Issued',
            jurisdiction: 'Jurisdiction',
            kind: 'Kind',
            kind_declared_by_pack: 'Kind declared by an installed pack',
            legal_basis: 'Legal basis',
            legal_review_status: 'Legal review',
            legal_reviews: 'Legal reviews',
            licence_kind: 'Licence kind',
            lifecycle_events: 'Lifecycle events',
            lifecycle_stage: 'Lifecycle stage',
            linked_activities: 'Linked activities',
            linked_authority_requests: 'Linked authority requests',
            linked_dpia: 'Linked DPIA',
            linked_frameworks: 'Linked frameworks',
            location: 'Location',
            lodged_by_role: 'Lodged by',
            lodged_on: 'Lodged',
            model_version: 'Model version',
            name: 'Name',
            notification_entries: 'Notification entries',
            notify_decision: 'Notification decision',
            offerings: 'Named offerings',
            onboarding_status: 'Onboarding',
            open_findings: 'Open findings',
            ot_asset: 'OT asset',
            outcome_reports: 'Outcome reports',
            overall_score: 'Overall score',
            parcel: 'Parcel',
            period_end: 'Period end',
            period_start: 'Period start',
            periodic_report_entries: 'Periodic report entries',
            personal_data: 'Personal data',
            phases_completed: 'Phases completed',
            phases_total: 'Phases',
            planned_release: 'Planned release',
            playbook_version: 'Playbook version',
            pre_existing_on_commencement: 'Pre-existing on commencement',
            priority: 'Priority',
            privacy_signed: 'Privacy function signed',
            procedure_reference: 'Procedure reference',
            product: 'Product',
            products: 'Products',
            proof_attachments: 'Proof attachments',
            provider: 'Provider',
            provider_type: 'Provider type',
            publication_evidence: 'Publication evidence',
            published_on: 'Published',
            purpose_recorded: 'Purpose recorded',
            purposes: 'Purposes recorded',
            questions_answered: 'Questions answered',
            questions_total: 'Questions',
            raised_on: 'Raised',
            rationale_recorded: 'Rationale recorded',
            readiness_record: 'Readiness record',
            recall_scope: 'Recall scope',
            receipt_recorded: 'Receipt recorded',
            received_on: 'Received',
            recipient: 'Recipient',
            recipient_class: 'Recipient class',
            reclassification_changed_fields: 'Inputs changed since the approved run',
            reclassification_review: 'Reclassification prompt review',
            reclassification_signal_since: 'Reclassification signal since',
            reconciliation_exceptions: 'Reconciliation exceptions',
            record_kind: 'Record kind',
            recorded_on: 'Recorded',
            reference: 'Reference',
            releasable_marked_on: 'Marked releasable',
            relied_on_events: 'Parcel events relied on',
            remediation_plans: 'Remediation plans',
            remediation_tasks: 'Remediation tasks raised',
            renewal_of_earlier: 'Renewal of an earlier credential',
            replaces: 'Replaces',
            reporting_years: 'Reporting years',
            representative_consents: 'Representative consent records',
            request_type: 'Request type',
            requester_recorded: 'Requester on record',
            responses: 'Responses',
            responsible_person_recorded: 'Responsible person recorded',
            retention_duty: 'Retention duty',
            review_due: 'Review due',
            revoked_on: 'Revoked',
            rights_evidence: 'Rights evidence entries',
            risk_level: 'Risk level',
            root_cause_recorded: 'Root cause recorded',
            rule_type: 'Rule type',
            sellers: 'Sellers',
            serial_reference: 'Serial reference',
            service_type: 'Service type',
            sharing_events: 'Sharing events',
            sharing_refusals: 'Refused sharing attempts',
            shipment: 'Shipment',
            sla_state: 'Deadline clock',
            source_country: 'Source country',
            stage: 'Stage',
            state: 'State',
            status: 'Status',
            sub_status: 'Sub-status',
            subject: 'Subject',
            subject_kind: 'Subject kind',
            submission_kind: 'Submission kind',
            submitted_on: 'Submitted',
            successor_recorded: 'Successor credential recorded',
            supplied_on: 'Supplied',
            takedown_reason_recorded: 'Takedown reason recorded',
            taken_down_on: 'Taken down',
            target_date: 'Target date',
            technical_actions: 'Technical actions',
            technical_regulation: 'Technical regulation',
            title: 'Title',
            transfer_assessment_referenced: 'Transfer assessment referenced',
            transfer_mechanism: 'Transfer mechanism',
            transfer_review_state: 'Transfer review',
            transfer_source_recorded: 'Transfer source institution recorded',
            valid_from: 'Valid from',
            valid_until: 'Valid until',
            vendor: 'Vendor',
            vendor_type: 'Vendor type',
            version: 'Version',
            versions: 'Versions recorded',
            withdrawal_determination_recorded: 'Withdrawal determination recorded',
        },
        summary_codes: {
            acknowledged: 'Acknowledged',
            active: 'Active',
            all: 'All approvers',
            any: 'Any approver',
            approved: 'Approved',
            archived: 'Archived',
            blocked: 'Blocked',
            breached: 'Breached',
            cancelled: 'Cancelled',
            closed: 'Closed',
            completed: 'Completed',
            critical: 'Critical',
            declared: 'Declared',
            do_not_notify: 'Do not notify',
            draft: 'Draft',
            escalated: 'Escalated',
            expired: 'Expired',
            filed: 'Filed',
            high: 'High',
            in_progress: 'In progress',
            in_review: 'In review',
            inactive: 'Inactive',
            known_data_subject: 'A known data subject',
            labelled_claimant: 'A labelled claimant',
            lodged: 'Lodged',
            low: 'Low',
            medium: 'Medium',
            new: 'New',
            not_started: 'Not started',
            notify: 'Notify',
            open: 'Open',
            overdue: 'Overdue',
            paused: 'Paused',
            pending: 'Pending',
            personal_data: 'Personal data',
            published: 'Published',
            received: 'Received',
            rejected: 'Rejected',
            resolved: 'Resolved',
            revoked: 'Revoked',
            signed: 'Signed',
            special_category: 'Special-category personal data',
            submitted: 'Submitted',
            suspended: 'Suspended',
            taken_down: 'Taken down',
            unclassified: 'Unclassified',
            undecided: 'Undecided',
            under_review: 'Under review',
            unverified: 'Unverified',
            verified: 'Verified',
            withdrawn: 'Withdrawn',
        },
        summary_flag_yes: 'Yes',
        summary_flag_no: 'No',
        summary_not_recorded: 'Not recorded',
        summary_fields_label: 'Record facts',
        providerSettings: {
            title: 'Forseti AI Palveluntarjoajan asetukset',
            subtitle: 'Määritä Claude-palveluntarjoaja Forseti AI Legal Advisorille.',
            healthHeading: 'Terveyden tarjoaja',
            statusLabel: 'Tila',
            statusOk: 'OK',
            statusFailed: 'Ei saatavilla',
            providerLabel: 'Palveluntarjoaja',
            providerClaudeApi: 'Claude API',
            providerCliRelay: 'Claude CLI rele',
            providerAesirManaged: 'Aesirin hallinnoimat rahakkeet',
            lastCheckedLabel: 'Viimeksi tarkistettu',
            reasonLabel: 'Syy',
            refreshButton: 'Päivitä',
            apiKeyHeading: 'Claude API-avain',
            apiKeyHint: 'API-näppäimen asettaminen aktivoi Claude API-tilan. Jätä määrittämättä, jos haluat käyttää paikallista CLI-välitystä (kehittäjä / itseisännöity tila).',
            apiKeyLabel: 'API-näppäintä',
            apiKeyMinLength: 'Vähintään 32 merkkiä. Salattu levossa; ei koskaan näytetty uudelleen.',
            apiKeyTooShort: 'API-avaimessa on oltava vähintään 32 merkkiä.',
            apiKeySaved: 'API avain tallennettu. Virkistävää terveyttä…',
            apiKeyCleared: 'API avain tyhjennetty. Forseti käyttää nyt paikallista CLI-välitystä.',
            setKeyButton: 'Tallenna API-avain',
            clearKeyButton: 'Tyhjennä API avain (käytä CLI-relettä)',
        },
        // Owner ruling 2026-10-10 — Forseti → AI Model (provider, model, effort).
        aiModel: {
            title: 'AI Model',
            sidebarLabel: 'AI Model',
            subtitle: 'Choose the AI provider, model and effort behind every Forseti request. Your organisation connects its own provider account with its own API key, and that provider bills you directly for what Forseti uses.',
            loadError: 'The AI model settings could not be read.',
            retrySubject: 'the AI model settings',
            generalHeading: 'General',
            generalDescription: 'The active provider answers every Forseti chat, workflow, tabular review and redline. Only a provider with a saved API key can be made active.',
            activeProviderLabel: 'Active provider',
            activeProviderHint: 'A provider without a saved API key cannot be selected. Save its key in its card below first.',
            providerNoKey: '{provider} (no API key saved)',
            thinkingLabel: 'Show the reasoning summary in chat',
            thinkingHint: 'When the model returns a summary of its reasoning, Forseti shows it above the answer in interactive chat. Workflows and reviews are not affected.',
            relayProviderLabel: 'Local CLI relay (development)',
            relayUrlLabel: 'Relay address',
            relayUrlHint: 'The address of the local CLI relay. Used only in development and self-hosted installations.',
            saveGeneral: 'Save general settings',
            generalSaved: 'General settings saved.',
            providersHeading: 'Providers',
            providersDescription: 'Each provider keeps its own API key, model and effort. Saving a provider does not make it active unless you tick Make this the active provider.',
            cardDescription: "Models from {provider}, called with your organisation's own API key.",
            cardDescriptionCompatible: "Any endpoint that implements the OpenAI API, such as a model gateway or a self-hosted model server, called with that endpoint's API key.",
            activeBadge: 'Active',
            keySetBadge: 'API key saved',
            keyMissingBadge: 'No API key',
            apiKeyLabel: 'API key',
            apiKeySavedHint: 'A key ending {hint} is saved. Leave this blank to keep the saved key.',
            apiKeyNoneHint: 'No key is saved. The key is stored encrypted and is never shown again after you save it.',
            removeKey: 'Remove key',
            removeKeyTitle: 'Remove the saved API key?',
            removeKeyMessage: 'Forseti cannot call {provider} again until a new key is saved. The model and effort settings stay as they are.',
            removeKeyConfirm: 'Remove key',
            removing: 'Removing the key…',
            keyRemoved: 'The {provider} API key was removed.',
            modelLabel: 'Model',
            modelHint: 'Choose a suggested model or type any model id {provider} offers.',
            modelHintCompatible: 'Type the model id exactly as the endpoint expects it.',
            modelRequired: 'Enter a model.',
            fastModelLabel: 'Fast model (optional)',
            fastModelHint: 'Used for conversation titles and tabular extraction. Leave blank to use the main model.',
            effortLabel: 'Effort',
            effortHint: 'How much reasoning the model spends on each answer. Levels the selected model does not accept are unavailable.',
            effortUnsupported: '{effort} is not available for this model.',
            efforts: {
                default: 'Model default',
                low: 'Low',
                medium: 'Medium',
                high: 'High',
                xhigh: 'Extra high',
                max: 'Maximum',
            },
            apiModeLabel: 'API mode',
            apiModeHint: 'Automatic uses the Responses API for models that require it and Chat Completions for the rest.',
            apiModes: {
                auto: 'Automatic',
                responses: 'Responses API',
                chat_completions: 'Chat Completions API',
            },
            reasoningModeLabel: 'Reasoning mode',
            reasoningModeHint: 'Pro spends more reasoning on each answer and costs more. It is offered only for models that support it.',
            reasoningModeUnavailable: 'Pro is not available for the selected model.',
            reasoningModes: {
                standard: 'Standard',
                pro: 'Pro',
            },
            serviceTierLabel: 'Service tier',
            serviceTierHint: 'Project default uses the tier set on your provider project.',
            serviceTiers: {
                auto: 'Project default',
                default: 'Standard',
            },
            baseUrlLabel: 'Base URL',
            baseUrlHint: "The endpoint's API root over HTTPS, for example https://openrouter.ai/api/v1.",
            baseUrlRequired: "Enter the endpoint's base URL.",
            makeActiveLabel: 'Make this the active provider',
            saveButton: 'Save',
            saved: '{provider} settings saved.',
            testButton: 'Test connection',
            testing: 'Testing…',
            testNote: 'Tests the saved settings, so save your changes first. The test sends one short request containing no customer data, which your provider may charge for.',
            unsavedNote: 'This card has unsaved changes. The test uses the saved settings.',
            lastTestHeading: 'Last test',
            testNever: 'Not tested yet.',
            testPassed: 'Passed',
            testFailed: 'Failed',
            testCheckedAt: 'Checked {time}',
            testError: 'The connection test could not be run.',
            saveError: 'The settings could not be saved.',
            removeError: 'The API key could not be removed.',
            errors: {
                api_key_required: 'Save an API key for this provider before making it active.',
                api_key_required_for_new_host: 'The base URL now names another host. Enter the API key for that host before saving; the saved key is not sent to a new host.',
                invalid_api_key: 'The provider rejected this API key.',
                invalid_api_key_format: 'This does not look like a key for this provider. Check that you copied the whole key.',
                unsupported_model_effort: 'The selected model does not accept this effort level.',
                model_requires_responses: 'This model needs the Responses API. Choose Automatic or Responses API.',
                base_url_required: "Enter the endpoint's base URL.",
                invalid_base_url: 'Enter a valid HTTPS URL.',
                unsupported_provider_options: 'These options are not available for this provider.',
            },
        },
        usage: {
            title: 'Käyttö',
            recentHeading: 'Viimeaikainen toiminta',
            historyHeading: 'Viimeiset 12 jaksoa',
            noActivity: 'Ei toimintaa vielä tällä ajanjaksolla.',
            featureLabel: 'Ominaisuus',
            whenLabel: 'Milloin',
            periodLabel: 'Kausi',
            byFeatureHeading: 'Tässä kuussa ominaisuuden mukaan',
            byFeatureEmpty: 'Ei ominaisuustoimintaa vielä tällä ajanjaksolla.',
            featureColumn: 'Ominaisuus',
            featureUnitsColumn: 'Yksiköt',
            featureRunsColumn: 'Juoksee',
            byUserHeading: 'Tämä kuukausi käyttäjän mukaan (järjestelmänvalvojan näkymä)',
            byUserEmpty: 'Ei käyttäjäkohtaista toimintaa vielä tällä ajanjaksolla.',
            userColumn: 'Käyttäjä',
            reconciliationLinkLabel: 'Näytä täsmäytysraportti',
            reconciliationLinkHint: 'Aesir - vain sisäinen järjestelmänvalvoja.',
            // Owner ruling 2026-10-10 — consumption by provider, model and effort.
            subtitleProvider: "What Forseti has used, and the provider, model and effort each request ran on. Your organisation's AI provider bills you directly for this consumption.",
            routingHeading: 'Current routing',
            routingDescription: 'The provider, model and effort each Forseti feature uses now.',
            routingProviderLabel: 'Active provider',
            routingFeatureColumn: 'Feature',
            routingModelColumn: 'Model',
            routingEffortColumn: 'Effort',
            routingChangeLink: 'Change the AI model settings',
            routingLoadError: 'The current routing could not be read.',
            routingRetrySubject: 'the current routing',
            periodHeading: 'Consumption',
            periodDescription: 'Requests and tokens for the period you choose. The current month is shown first.',
            fromLabel: 'From',
            toLabel: 'To',
            applyPeriod: 'Show period',
            periodInvalid: 'The start date must be on or before the end date.',
            summaryLoadError: 'Consumption for this period could not be read.',
            summaryRetrySubject: 'consumption for this period',
            totalsCalls: 'Requests',
            totalsInput: 'Input tokens',
            totalsOutput: 'Output tokens',
            totalsCached: 'Cached input tokens',
            byModelHeading: 'By provider, model and effort',
            byFeaturePeriodHeading: 'By feature',
            providerColumn: 'Provider',
            modelColumn: 'Model',
            effortColumn: 'Effort',
            callsColumn: 'Requests',
            inputColumn: 'Input tokens',
            outputColumn: 'Output tokens',
            cachedColumn: 'Cached input tokens',
            tokensColumn: 'Tokens (in / out)',
            timeColumn: 'Time',
            noConsumption: 'Forseti made no requests in this period.',
            notRecorded: 'Not recorded',
            recentDescription: 'The most recent requests in the selected period.',
            recentLoadError: 'Recent activity could not be read.',
            recentRetrySubject: 'recent activity',
            byUserPeriodHeading: 'By user',
            byUserBasis: 'Counted over the {count} most recent requests in this period.',
            historyMonthlyHeading: 'Monthly consumption',
            historyConsumedColumn: 'Consumed units',
            historyLoadError: 'Monthly consumption could not be read.',
            historyRetrySubject: 'monthly consumption',
            historyEmpty: 'No earlier months recorded yet.',
            featureLabels: {
                chat: 'Chat',
                workflow: 'Työnkulkuajo',
                tabular: 'Taulukkopoisto',
                redline: 'Redline arvostelu',
                title: 'Keskustelun otsikko',
                unknown: 'muu',
            },
        },
        reconciliation: {
            title: 'Forseti-kiintiön täsmäytys',
            subtitle: 'Drift between the fast quota counter cache and the authoritative billing ledger. The reconciler runs every 5 minutes and records any drift to the structured log channel. Aesir-internal admin only.',
            backLink: 'Takaisin Forsetin käyttöön',
            stubHeading: 'Where reconciliation results are recorded',
            stubBody: 'This page does not show run history. Each reconciliation run writes its drift figures to the structured log channel; read them there.',
            permissionDenied: 'Tämä sivu on rajoitettu Aesirin sisäisille järjestelmänvalvojille. Jos uskot, että sinulla pitäisi olla pääsy, ota yhteyttä AesirX edustajaan.',
        },
        projects: {
            title: 'Projektit',
            subtitle: 'Työtilat asiakirjoja, keskusteluja, työnkulkuja ja arvosteluja varten.',
            modeLabel: 'Näytä',
            tabMine: 'Minun projektini',
            tabShared: 'Jaettu kanssani',
            searchLabel: 'Etsi projekteja',
            searchPlaceholder: 'Hae…',
            newButton: 'Uusi projekti',
            colName: 'Nimi',
            colVisibility: 'Näkyvyys',
            colDocs: 'Asiakirjat',
            colMembers: 'jäsenet',
            colUpdated: 'Päivitetty',
            visibilityOwn: 'Vain oma',
            visibilityShared: 'Jaettu',
            emptyMine: 'Ei vielä projekteja. Aloita luomalla sellainen.',
            emptyShared: 'Sinun kanssasi ei ole jaettu mitään projekteja.',
            pageHint: 'Näytetään {shown} / {total}.',
            createTitle: 'Uusi projekti',
            createSubmit: 'Luo projekti',
            fieldName: 'Nimi',
            fieldDescription: 'Kuvaus (valinnainen)',
            fieldCmNumber: 'Tapauksen/asian numero (valinnainen)',
            fieldVisibility: 'Näkyvyys',
            errorNameRequired: 'Nimi vaaditaan.',
            errorSelectorRequired: 'Valitse lisättävä käyttäjä tai organisaatioryhmä.',
            renameButton: 'Nimeä uudelleen',
            renamePrompt: 'Nimeä projekti uudelleen',
            deleteButton: 'Poista',
            deleteConfirm: 'Delete this project? It will disappear from your projects and cannot be restored from the product.',
            deleteFolderConfirm: 'Poistetaanko tämä kansio? Sisällä olevat asiakirjat jäävät projektiin, vain arkistoimatta.',
            removeMemberConfirm: 'Poistetaanko tämä jäsen?',
            foldersHeading: 'Kansiot',
            foldersEmpty: 'Ei kansioita vielä.',
            addFolderButton: 'Uusi kansio',
            addFolderTitle: 'Uusi kansio',
            documentsHeading: 'Asiakirjat',
            documentsEmpty: 'Asiakirjoja ei ole vielä liitetty.',
            colFilename: 'Tiedostonimi',
            colAdded: 'Lisätty',
            unattachButton: 'Irrota',
            membersHeading: 'jäsenet',
            membersEmpty: 'Vain omistaja.',
            addMemberButton: 'Lisää jäsen',
            addMemberTitle: 'Lisää jäsen',
            addMemberSubmit: 'Lisää jäsen',
            colMember: 'jäsen',
            colRole: 'Rooli',
            selectorTypeLabel: 'Lisää',
            selectorUser: 'Käyttäjätunnus',
            selectorGroup: 'Organisaatioryhmän tunnus',
            fieldUserId: 'Käyttäjä',
            fieldGroupId: 'Organisaatioryhmä',
            fieldRole: 'Rooli',
            roleAdmin: 'Admin',
            roleEditor: 'Toimittaja',
            roleViewer: 'Katsoja',
            notFound: 'Projektia ei löydy.',
            selectUserPlaceholder: 'Valitse käyttäjä…',
            selectGroupPlaceholder: 'Valitse organisaatioryhmä…',
            noUsersAvailable: 'Käyttäjiä ei ole saatavilla.',
            noGroupsAvailable: 'Organisaatioryhmiä ei ole saatavilla.',
            loadFailed: 'Vaihtoehtojen lataaminen epäonnistui.',
            deleteProjectTitle: 'Poistetaanko projekti?',
            deleteFolderTitle: 'Poistetaanko kansio?',
            removeMemberTitle: 'Poistetaanko jäsen?',
            removeMemberConfirmGeneric: 'Poistetaanko tämä jäsen projektista?',
            addDocumentButton: 'Lisää asiakirja',
            addDocumentTitle: 'Lisää asiakirja projektiin',
            fieldDocument: 'Valitse asiakirja',
            selectDocPlaceholder: 'Etsi asiakirjaa...',
            ownerLabel: 'Järjestelmän omistaja',
        },
        chat: {
            sidebarLabel: 'Chatit',
            overviewTitle: 'Forseti',
            overviewSubtitle: 'AI Legal & Compliance Advisor – perustuu vuokraajatietoihisi ja asennettuihin säädöspaketteihin.',
            personaSelectorLabel: 'Persoona',
            personaHeading: 'Valitse persoona',
            startChatButton: 'Aloita uusi chat',
            startingChat: 'Aloitetaan chatti…',
            recentHeading: 'Viimeaikaiset keskustelut',
            recentEmpty: 'Ei vielä keskusteluja – aloita yksi ylhäältä.',
            untitledChat: 'Uusi chat',
            chatLoadFailed: 'Forseti could not load this chat.',
            streamFailed: 'Forseti could not finish this reply. Your message is still in the box.',
            personaSwitchFailed: 'The persona could not be changed.',
            renameFailed: 'The chat could not be renamed.',
            deleteFailed: 'The chat could not be deleted.',
            messageListLabel: 'Keskustelu',
            detailSubtitle: 'Persoona: {persona}',
            composeHeading: 'Lähetä viesti',
            composeLabel: 'Viesti Forsetille',
            composeDisabledPlaceholder: 'This chat is read-only.',
            composeDisabledHint: 'You can read this conversation but not add to it.',
            switchPersonaHeading: 'Persoona',
            presetPersona: 'Esiasetettu persoona',
            changePersona: 'Vaihda persoonaa',
            hidePersonaOptions: 'Piilota persoonavaihtoehdot',
            starterHeading: 'Aloita jollakin näistä toiminnoista',
            renameButton: 'Nimeä uudelleen',
            renamePrompt: 'Nimeä chat uudelleen',
            deleteButton: 'Poista',
            deleteConfirm: 'Delete this chat? It will disappear from your chats and cannot be restored from the product.',
            notFound: 'Chattia ei löydy.',
            emptyChat: 'Ei vielä viestejä – aloita keskustelu tervehtimällä.',
            roleUser: 'sinä',
            roleAssistant: 'Forseti',
            roleSystem: 'Järjestelmä',
            thinkingLabel: 'Ajatteleminen',
            toolCallPlaceholder: 'Työkalukutsu:',
            toolResultPlaceholder: 'Tool result hidden.',
            docEditPlaceholder: 'Ehdotetut asiakirjan muokkaukset (V{version}).',
            disclaimer: 'Forseti tukee harkintasi - se ei korvaa lisensoitua asianajajaa.',
            sendButton: 'Lähetä',
            sending: 'Lähetetään…',
            composePlaceholder: 'Kysy Forsetilta…',
            composingPlaceholder: 'Forseti vastaa…',
            streamingPlaceholder: 'Forseti kirjoittaa…',
            promptInjectionTitle: 'Forseti pudotti joitakin ohjausmerkkejä.',
            promptInjectionBody: 'Raidatut kuviot: {patterns}. Forseti näkee vain viestisi tekstin.',
            rateLimitTitle: 'Lähetät viestejä liian nopeasti.',
            rateLimitRetryAfter: 'Yritä uudelleen {seconds}s kuluttua.',
            messageLength: '{count} / {max} characters',
            messageTooLong: 'Messages to Forseti are limited to {max} characters. Shorten the message or split it in two.',
            counselReviewTitle: 'Forseti on epävarma tässä.',
            counselReviewBody: 'Vahvista valtuutetulta asianajajalta ennen kuin toimit tämän vastauksen mukaisesti.',
            legalConclusionRefused: 'Forseti kieltäytyi antamasta oikeudellista johtopäätöstä perustelematta. Kokeile ensin search_regulatory tai lähetä neuvoa.',
            toolCallDone: 'Tehty.',
            toolCallArgsLabel: 'Argumentit',
            toolCallResultLabel: 'Tulos',
            toolResultLogged: 'Työkalun tulos tallennettu tarkastuslokiin.',
            toolResultAria: 'työkalun tulos',
            documentEditAria: 'asiakirjan muokkaus',
            createTaskFromMessage: 'Luo tehtävä tästä',
            createTaskHeading: 'Luo tehtävä tästä viestistä',
            createTaskTitleLabel: 'Otsikko',
            createTaskTitleRequired: 'Otsikko vaaditaan.',
            createTaskDescriptionLabel: 'Kuvaus',
            createTaskAssigneeLabel: 'Valtuutettu',
            createTaskAssigneePlaceholder: 'Valitse valtuutettu…',
            createTaskAssigneeNone: '— määräämätön —',
            createTaskDueDateLabel: 'Eräpäivä',
            createTaskWorkflowLabel: 'Linkitetty työnkulku (valinnainen)',
            createTaskWorkflowPlaceholder: 'Valitse työnkulku…',
            createTaskWorkflowNone: '- ei yhtään -',
            renameModalSubmit: 'Nimeä uudelleen',
            renameModalSaving: 'Tallennetaan…',
            deleteConfirmTitle: 'Poistetaanko chat?',
            createTaskSubmit: 'Luo tehtävä',
            createTaskSubmitting: 'Luodaan…',
            cancelButton: 'Peruuta',
            autoRouteHintMessage: 'Tämä kysymys on enemmän {persona}:n ohjaushytissä.',
            autoRouteSwitchButton: 'Vaihda palveluun {persona}',
            autoRouteSwitching: 'Vaihdetaan…',
            handoffSuggestionMessage: 'Tai avaa uusi chatti {persona}:n kanssa ja siirrä tätä kysymystä eteenpäin.',
            handoffSuggestionButton: 'Aloita uusi keskustelu käyttäjän {persona} kanssa',
            handoffSuggestionWorking: 'Avataan…',
            handoffSuggestionDismiss: 'Ei nyt',
            handoffSuggestionFailed: 'The new chat could not be opened.',
            persona_display: {
                'forseti-generalist': 'Forseti',
                'forseti-compliance': 'Vaatimustenmukaisuuden asiantuntija',
                'forseti-legal': 'Lakiasiantuntija',
                'forseti-dpo': 'Tietosuojavastaava-neuvonantaja',
                'forseti-risk': 'Riskianalyytikko',
                'forseti-auditor': 'Tilintarkastajan näkemys',
            },
            exportButton: 'Vie PDF',
            exportRunning: 'Viedään…',
            exportSuccess: 'Viety: {filename} (avaa tiedostoselain).',
            exportOpenFile: 'Avaa tiedostoselaimessa',
            exportDownload: 'Lataa PDF',
            citation: {
                verifyBadge: '[VAHVISTA]',
                verifyAria: 'lainausta ei voitu vahvistaa – tarkista ennen luottamista',
                popoverAria: 'Lähde: {label}',
                openSource: 'Avoimen lähdekoodin sivu',
            },
        },
        auditTrail: {
            forsetiBadge: 'Forseti',
            forsetiBadgeTooltip: 'AI luonut tapahtuman Forsetin kirjausketjusta.',
        },
        rowAction: {
            ask: 'Kysy Forsetilta',
            tooltipOpen: 'Kysy Forsetilta tästä {recordType}',
            tooltipLocked: 'Tämä {recordType} on lukittu — Forseti voi vastata kysymyksiin, mutta ei voi tehdä muutoksia.',
        },
        // ── Sprint 63.22.0 — the global Forseti launcher and its launchpad ──
        launcher: {
            openLabel: 'Open Forseti',
            tooltip: 'Ask Forseti (Ctrl+Shift+F)',
            modalTitle: 'Forseti',
            personaHeading: 'Choose who to ask',
            scopeHeading: 'What the chat is about',
            askAboutRecord: 'Ask about this {record}',
            generalChat: 'General chat',
            attachRecord: 'Attach a record',
            attachRecordRequired: 'Choose a record to attach, or choose General chat.',
            recordScopeLost: 'This page no longer shows a record, so this will be a general chat.',
            recentChats: 'Recent chats',
            recentEmpty: 'You have no chats yet.',
            resumeChat: 'Resume {title} — {persona}, {date}',
            startChat: 'Start chat',
            starting: 'Starting chat…',
            openFullView: 'Open in full view',
            loadFailed: 'Forseti could not load your personas and recent chats.',
            noPersonas: 'No Forseti persona is enabled for your organisation.',
        },
        advisorRunPanel: {
            // fix-sprint-20260627-2217-followup F11 — the chat's back control names
            // where it goes (WCAG 2.4.6); it returns to the launchpad.
            backToOptions: 'Back to Forseti options',
            title: 'Forseti — tästä levystä',
            heading: 'Forseti — {recordType}',
            summaryLabel: 'Yhteenveto',
            suggestedPersona: 'Ehdotettu persoona',
            askForseti: 'Kysy Forsetilta',
            runWorkflow: 'Suorita työnkulku',
            viewLineage: 'Näytä Audit Lineage',
            createPdplEvidencePack: 'Luo PDPL-todistepaketti',
            prepareBreachDraft: 'Valmistele ilmoitusluonnos',
            primaryActions: {
                projectSummary: 'Tiivistä projektiasiakirja',
                supplementSummary: 'Tiivistä lisäpyyntö',
                authoritySummary: 'Tiivistä viranomaispyyntö',
                rightsRequestResponse: 'Laadi DSR-vastaus',
                dpiaQuickReview: 'Suorita DPIA-pikatarkistus',
                taskControlWalkthrough: 'Käy läpi kontrollitodisteet',
                vendorRiskNarrative: 'Luo toimittajariskin narratiivi',
                incidentLetter: 'Laadi PDPL-loukkauskirje',
                ropaSufficiency: 'Suorita RoPA-riittävyyden tarkistus',
                vendorDpaReview: 'Suorita toimittajan DPA-tarkistus',
            },
            builtinWorkflowViews: {
                ropaSufficiency: {
                    title: 'RoPA Sufficiency Check',
                    notice: 'Runs the existing RoPA sufficiency workflow for this processing activity. The run may create draft follow-up tasks; operators still review and execute them.',
                },
                vendorDpaReview: {
                    title: 'Vendor DPA Review',
                    notice: 'Runs the existing Vendor DPA Review workflow for this vendor contract. The run may create draft follow-up tasks; operators still review every finding and task.',
                },
                vendorRiskNarrative: {
                    title: 'Vendor Risk Narrative',
                    notice: 'Runs the existing vendor risk narrative workflow for this vendor. Output remains draft evidence; the human risk owner reviews and approves it before use.',
                },
                incidentLetter: {
                    title: 'PDPL Breach Notification Letter',
                    notice: 'Runs the existing VN PDPL breach-notification letter workflow for this incident or playbook activation. Output remains draft only; formal regulator filing still requires human sign-off.',
                },
                projectSummary: {
                    title: 'Project Document Summary',
                    notice: 'Runs the existing uploaded-document summary workflow for this Forseti project. The workflow is read-only and does not create tasks, evidence, or record updates.',
                },
                supplementSummary: {
                    title: 'Supplement Request Summary',
                    notice: 'Runs the existing uploaded-document summary workflow for this supplement request. The workflow is read-only and does not create tasks, evidence, record updates, or filing decisions.',
                },
                authoritySummary: {
                    title: 'Authority Request Summary',
                    notice: 'Runs the existing uploaded-document summary workflow for this authority request. The workflow is read-only and does not create tasks, evidence, record updates, filing decisions, or authority-response drafts.',
                },
                rightsRequestResponse: {
                    title: 'DSR Response Draft',
                    notice: 'Runs the existing DSR response draft workflow for this rights request. Output remains draft only; sending and case updates still require the existing human-reviewed rights-request flow.',
                },
                dpiaQuickReview: {
                    title: 'DPIA Quick Review',
                    notice: 'Runs the existing DPIA Quick Review workflow for this DPIA. The run may create draft follow-up tasks; operators still review every finding and task.',
                },
                taskControlWalkthrough: {
                    title: 'Control Evidence Walkthrough',
                    notice: 'Runs the existing compliance-control walkthrough for this task. The workflow must retrieve regulatory anchors before citing them, does not issue legal conclusions or mark controls/evidence approved, and only creates draft evidence placeholders when the operator explicitly requests them.',
                },
            },
            breachDraftNotice: 'Forseti valmistelee vain linkitetyn Mẫu số 08 -lomakkeen luonnossisällön. Se ei päätä ilmoitusvelvollisuudesta eikä toimita tai lähetä lomaketta.',
            breachDraftReady: 'Mẫu số 08 -luonnos valmis tarkistettavaksi',
            breachDraftReviewBeforeSelecting: 'Tarkista ennen valintaa',
            workflowHeading: 'Olennaiset työnkulut',
            workflowLoading: 'Ladataan olennaisia työnkulkuja…',
            workflowEmpty: 'Sopivaa työnkulkua ei löytynyt. Luo tälle tietueelle ohjattu työnkulku.',
            workflowError: 'Olennaisia työnkulkuja ei voitu ladata.',
            selectWorkflow: 'Valitse {title}',
            workflowSelected: 'Työnkulku on valittu ja valmis suoritettavaksi tälle tietueelle.',
            workflowRunSelected: 'Suorita valittu työnkulku',
            workflowStarting: 'Käynnistetään työnkulkua…',
            workflowRunError: 'Työnkulkua ei voitu käynnistää.',
            workflowCreateGuided: 'Luo tälle tietueelle ohjattu työnkulku',
            lineageHeading: 'Tämän tietueen tarkastusketju',
            lineageLoading: 'Ladataan tarkastusketjua…',
            lineageEmpty: 'Tälle tietueelle ei ole tallennettu Forseti-ketjutapahtumia.',
            lineageError: 'Tarkastusketjua ei voitu ladata.',
            retry: 'Yritä uudelleen',
            chatContextLoading: 'Ladataan tietueen kontekstia…',
            chatGuidance: {
                general: 'Forseti voi tehdä tietueesta yhteenvedon, tunnistaa puuttuvat tiedot ja ehdottaa tarkistusvaiheita.',
                privacy: 'Forseti voi tarkistaa tietosuojatiedot ja asennettujen sääntelykehysten velvoitteet sekä auttaa laatimaan luonnoksen ihmisen tarkistettavaksi.',
                compliance: 'Forseti voi tunnistaa vaatimustenmukaisuuden puutteet, perustaa vaatimukset asennettuihin lähteisiin ja ehdottaa vastuullisia jatkotoimia.',
                legal: 'Forseti voi tehdä tietueesta yhteenvedon, etsiä perusteltua lähdeaineistoa ja auttaa laatimaan luonnoksen oikeudellista tarkistusta varten.',
                risk: 'Forseti voi arvioida kirjattuja riskejä, merkitä puuttuvat tosiseikat ja ehdottaa oikeasuhtaisia tarkistusvaiheita.',
                audit: 'Forseti voi tarkastella todistusaineiston puutteita, yhdistää tietueen asennettuihin kontrolleihin ja ehdottaa auditointivalmiita jatkotoimia.',
            },
            starterPrompts: {
                identifyGaps: 'Mitä tietoja tai todistusaineistoa tästä tietueesta puuttuu?',
                reviewFrameworks: 'Mitkä asennettujen sääntelykehystemme velvoitteet minun tulisi tarkistaa tämän tietueen osalta?',
                prepareDraft: 'Auta minua laatimaan luonnos, joka perustuu vain tähän tietueeseen ja perusteltuihin asennettuihin lähteisiin.',
                nextSteps: 'Mitkä tarkistusvaiheet minun tulisi tehdä seuraavaksi?',
                summarizeRecord: 'Tee tietueesta yhteenveto ja merkitse ihmisen tarkistusta vaativat kohdat.',
                assessRisk: 'Arvioi kirjatut riskit, tunnista puuttuvat tosiseikat ja ehdota tarkistusvaiheita.',
            },
            disclaimer: 'Forseti tukee harkintasi - se ei korvaa lisensoitua asianajajaa.',
            recordType: {
                project: 'Projekti',
                dpia: 'DPIA',
                dossier_filing: 'Asiakirjojen arkistointi',
                transfer_assessment: 'Rajat ylittävä siirto',
                vendor: 'Myyjä',
                vendor_contract: 'Myyjäsopimus',
                vendor_acceptance: 'Myyjän riskin hyväksyminen',
                incident: 'Tapahtuma',
                incident_op: 'Tapahtumaoperaatio',
                breach_notification: 'Rikkomusilmoitus',
                authority_request: 'Viranomaisten pyyntö',
                supplement_request: 'Täydennyspyyntö',
                rights_request: 'Oikeuspyyntö',
                contract: 'Sopimus',
                task: 'Tehtävä',
                data_mapping_activity: 'RoPA-toiminta',
                compliance_rule: 'Vaatimustenmukaisuussääntö',
                program: 'Ohjelma',
                governance_document: 'Hallinto-asiakirja',
                remediation_plan: 'Kunnostussuunnitelma',
                cross_border_filing: 'Rajat ylittävä arkistointi',
                enforcement_case: 'Enforcement Case',
                authority_direction: 'Authority Direction',
                regulated_account: 'Regulated Account',
                regulatory_placeholder: 'Regulatory Placeholder',
                sector_licence: 'Sector Licence',
                credit_information_supply: 'Credit-Information Supply',
                data_service_provider: 'Data-Service Provider Record',
                regulatory_disclosure: 'Regulatory Disclosure',
                issued_record: 'Student Record',
                publication: 'Publication',
            },
        },
        autocomplete: {
            // fix-sprint-20260627-2217-followup F9, verifier round 1 — the 409 refusal
            // when no installed framework grounds the draft (raw English before).
            error_no_grounding_scope: 'Forseti cannot draft this DPIA: no framework for this frame is installed with a usable status, so there is no regulatory text to ground the draft on. Nothing was drafted.',
            button_label: 'Pyydä Forsetiä laatimaan tämä',
            drafting: 'Luonnostetaan…',
            hint_pick_activity: 'Valitse linkitetty käsittelytoiminto ottaaksesi Forsetin luonnoksen käyttöön.',
            draft_origin_label: 'Forseti luonnos - tarkastelu kenttäkohtaisesti',
            tokens_label: '≈ {count} tokeneita kuukausittain',
            accept_selected: 'Hyväksy valitut',
            reject_all: 'Hylkää luonnos',
            field_checkbox_label: 'Käytä Forsetin luonnosta {field}',
            field_labels: {
                description: 'Kuvaus',
                purpose: 'Tarkoitus',
                data_subjects: 'Rekisteröidyt',
                data_types: 'Tietotyypit',
                necessity_justification: 'Välttämättömyys ja suhteellisuus',
                consultation_notes: 'Konsultaatiomuistiinpanot',
                dpo_opinion: 'DPO mielipide',
                system_ids: 'Linkitetyt järjestelmät',
                vendor_ids: 'Linkitetyt toimittajat',
            },
            error_generic: 'Forseti ei voinut luonnostella tätä DPIA. Yritä uudelleen tai tarkenna toimintatietuetta.',
            variant_legend: 'Runkokehys',
            variant_label_gdpr: 'GDPR',
            variant_none_available: 'Asennettua sääntelykehystä ei ole saatavilla',
            variant_label_pdpl: 'PDPL',
            variant_label_nis2: 'NIS2',
            variant_label_iso27001: 'ISO 27001',
            variant_label_soc2: 'SOC 2',
            conflict_banner: 'Huomio: {count} kehysten välistä ristiriitaa koskee tätä versiota. Tarkista ennen hyväksymistä.',
        },
        formDraft: {
            button_label: 'Pyydä Forsetia laatimaan tämä lomake',
            drafting: 'Laaditaan…',
            draft_origin_label: 'Forsetin luonnos — tarkista jokainen kenttä',
            tokens_label: '≈ {count} tokenia kuukausikiintiöstäsi',
            accept_selected: 'Hyväksy valitut',
            reject_all: 'Hylkää luonnos',
            field_checkbox_label: 'Käytä Forsetin luonnosta kentälle {field}',
            excluded_note: '{count} kenttää ei voi laatia (viittaukset, liitteet tai vain luku).',
            error_generic: 'Forseti ei voinut laatia tätä lomaketta. Yritä uudelleen.',
            empty_draft: 'Forseti ei tuottanut kenttäluonnoksia tälle lomakkeelle.',
        },
        tia: {
            // fix-sprint-20260627-2217-followup F9, verifier round 1 — the 409 refusal
            // when no installed framework grounds the draft (raw English before).
            error_no_grounding_scope: 'Forseti cannot draft this TIA: no framework that governs this transfer is installed with a usable status, so there is no regulatory text to ground the draft on. Nothing was drafted.',
            button_label: 'Pyydä Forsetiä laatimaan tämä TIA',
            drafting: 'Laaditaan TIA:ta…',
            hint_pick_destination: 'Aseta kohdemaa siirtotietueeseen ennen kuin Forseti voi luonnostaa.',
            draft_origin_label: 'Forseti TIA luonnos – tarkistus kenttäkohtaisesti',
            tokens_label: '≈ {count} tokeneita kuukausittain',
            accept_selected: 'Hyväksy valitut',
            reject_all: 'Hylkää luonnos',
            field_checkbox_label: 'Käytä Forsetin luonnosta {field}',
            error_generic: 'Forseti ei voinut laatia tätä TIA:ta. Yritä uudelleen tai tarkenna siirtotietuetta.',
            usage_details: 'Tekoälyn käyttötiedot',
            applied_status: 'Draft accepted. The selected fields are saved to the transfer record.',
            recommended_updates_title: 'Suositellut tietueen päivitykset',
            mechanism_recommendation: 'Siirtomekanismi: {value}',
            dpo_review_recommendation: 'Ask your DPO to review this sensitive transfer.',
            review_date_warning: 'Aseta tarkistuksen määräaika ennen tämän arkaluonteisen siirron päivittämistä.',
            processing_activity_warning: 'Liitä vähintään yksi käsittelytoimi / ROPA-tietue ennen TIA:n valmistumista.',
            go_to_field: 'Siirry kenttään',
            adequacy_label: 'Kohdejärjestelmä',
            adequacy_label_source_route: 'Transfer route',
            adequacy_status_adequacy_decision: 'EU adequacy decision (exports from the EU/EEA)',
            adequacy_status_non_adequacy_with_sccs: 'No EU adequacy decision — GDPR Art. 46 safeguard for exports from the EU/EEA',
            adequacy_status_derogation: 'GDPR Art. 49 derogation (exports from the EU/EEA)',
            adequacy_needs_action: 'Action needed',
            adequacy_for_information: 'For information',
            adequacy_status_transitional: 'Siirtymäaika/ETA-pikakuvake',
            adequacy_status_unknown: 'Tuntematon — aseta määränpää',
            adequacy_status_source_jurisdiction_route: 'Transfer out of Vietnam — impact-assessment dossier route',
            adequacy_summary_no_destination: 'No destination country is set. Select the destination before Forseti drafts a TIA.',
            adequacy_summary_intra_eea: 'Transfer within the EEA ({code}) — for an export from the EU/EEA, no third-country TIA is required under GDPR Arts. 44–50. A transfer out of Vietnam is assessed under Law 91/2025/QH15 Art. 20 and Decree 356/2025/NĐ-CP Arts. 17–18 unless an evidenced exception applies.',
            adequacy_summary_inbound: 'Vietnam destination — an exporter in the EU/UK establishes its own transfer mechanism; the Vietnam-origin transfer impact-assessment rules do not apply to an inbound route. GDPR applies only where the record establishes an EU/EEA jurisdiction trigger.',
            adequacy_summary_us_dpf: 'US recipient certified under the Data Privacy Framework — for an export from the EU/EEA, covered by the European Commission adequacy decision of 10 July 2023 while the recipient stays on the active DPF list. Confirm the certification at https://www.dataprivacyframework.gov before relying on it.',
            adequacy_summary_us_no_dpf: 'US destination not under the DPF — for an export from the EU/EEA, no EU adequacy decision covers a non-certified recipient, and the Schrems II surveillance-law concerns (FISA §702, EO 12333) call for supplementary measures.',
            adequacy_summary_eu_adequacy: '{code} has an active European Commission adequacy decision — an export from the EU/EEA may proceed without SCCs or supplementary measures, subject to the Commission’s ongoing monitoring.',
            adequacy_summary_eu_no_adequacy: '{code} has no active EU adequacy decision. An export from the EU/EEA may rely on GDPR Art. 46 safeguards (standard contractual clauses, BCRs, certification or a code of conduct) with a Schrems II assessment of the destination, or on a GDPR Art. 49 derogation.',
            adequacy_summary_source_jurisdiction_route: 'Transfer out of Vietnam to {code} — assessed on the route Law 91/2025/QH15 Art. 20 states: the transfer impact-assessment dossier (Decree 356/2025/NĐ-CP Arts. 17–18), unless an exception those instruments state is evidenced. The EU adequacy classification is not used to frame this route; where the record establishes that the GDPR reaches the exporter for this processing (Art. 3), its Chapter V applies in addition.',
            adequacy_summary_source_jurisdiction_route_exemption: 'Transfer out of Vietnam to {code} — assessed on the route Law 91/2025/QH15 Art. 20 states: the transfer impact-assessment dossier (Decree 356/2025/NĐ-CP Arts. 17–18), unless an exception those instruments state is evidenced. A possible exception is flagged from the record; it is not a confirmed exception and needs affirmative evidence and Compliance/Legal review. The EU adequacy classification is not used to frame this route; where the record establishes that the GDPR reaches the exporter for this processing (Art. 3), its Chapter V applies in addition.',
            conflict_banner: 'Huomio: {count} kehysten välistä ristiriitaa koskee tätä siirtojärjestelmää. Tarkista ennen hyväksymistä.',
            field_label_description: 'Description',
            field_label_purpose: 'Purpose',
            field_label_transfer_mechanism_detail: 'Transfer mechanism detail',
            field_label_recipient_name: 'Recipient name',
            field_label_recipient_details: 'Recipient details',
            field_label_data_subjects: 'Data subjects',
            field_label_data_types: 'Data types',
        },
        // Sprint 48.3.6 — shared draft output-guardrail labels (TIA + DPIA + all draft workflows).
        guardrails: {
            missing_facts_title: 'Puuttuvat pakolliset tiedot',
            missing_facts_hint: 'Forseti merkitsi tietoja, joita ei ole tietueessa. Niistä riippuvia kenttiä ei ole valittu valmiiksi — täytä tiedot tai hyväksy kenttä tietoisesti.',
            risk_label: 'Kalibroitu riski',
            risk_low: 'Matala',
            risk_medium: 'Keskitaso',
            risk_high: 'Korkea',
            jurisdiction_label: 'Lainkäyttöalueen laukaisimet',
            jurisdiction_none_installed: 'Mikään asennettu kehys ei kata tätä arviointia — mitään oikeusjärjestelmää ei arvioitu. Asenna sopiva sääntelypaketti ennen kuin luotat tämän luonnoksen oikeudellisiin johtopäätöksiin.',
            jurisdiction_none_assessed: 'Forseti ei palauttanut lainkäyttöalueen arviointia. Mitään ei arvioitu — tämä ei ole havainto siitä, ettei mikään järjestelmä soveltuisi.',
            jurisdiction_inactive: 'The framework covering this assessment is installed but no longer in force — so no legal regime was assessed. There is nothing to install: activate the current instrument, or the one that replaces it, before relying on the legal conclusions here.',
            jurisdiction_resolving: 'Ladataan arvioitujen järjestelmien nimiä…',
            trigger_applicable: 'Soveltuu',
            trigger_not_applicable: 'Ei laukaistu',
            trigger_unknown: 'Tuntematon — tieto puuttuu',
            recipient_role_label: 'Vastaanottajan rooli',
            recipient_role_controller: 'Rekisterinpitäjä',
            recipient_role_processor: 'Käsittelijä',
            recipient_role_joint_controller: 'Yhteisrekisterinpitäjä',
            recipient_role_unknown: 'Tuntematon — ei ilmoitettu',
            opt_in_hint: 'Oikeudellinen johtopäätös — valitse vain, jos hyväksyt sen käytettävissä olevilla tiedoilla.',
        },
        mcp: {
            pageTitle: 'MCP-liitinavaimet',
            pageSubtitle: 'Connect Claude, Slack agents and other MCP clients to Forseti. A connected client reads your organisation’s records within the access you grant, and anything it proposes to change waits in the approval queue until an administrator approves it.',
            issueButton: 'Anna uusi avain',
            issueModalTitle: 'Anna MCP API-avain',
            labelField: 'Label',
            labelHint: 'Lyhyt kuvaus (esim. "AI kykylaboratorio – sääntelytutkimus").',
            permittedToolsField: 'Sallitut työkalut',
            expiresAtField: 'Vanhenee (valinnainen)',
            expiresAtHint: 'Jätä tyhjäksi, jos se ei vanhene.',
            submit: 'Ongelman avain',
            cancel: 'Peruuta',
            keyDisplayedOnce: 'Kopioi tämä avain nyt – et näe sitä enää. Tallenna se salaiseen hallintaan (1Password, AWS Secrets Manager, Vault jne.).',
            keyDisplayedOnceClose: 'Kopioin sen - sulje',
            colLabel: 'Label',
            colPrefix: 'Etuliite',
            colStatus: 'Tila',
            colTools: 'Työkalut',
            colCreatedAt: 'Luotu',
            colLastUsed: 'Viimeksi käytetty',
            colCalls: 'Puhelut',
            colActions: 'Toiminnot',
            statusActive: 'Aktiivinen',
            statusRevoked: 'Peruutettu',
            statusExpired: 'Vanhentunut',
            revokeAction: 'Peruuta',
            rotateAction: 'Kierrä',
            confirmRevokeTitle: 'Perutaanko tämä MCP-avain?',
            confirmRevokeBody: 'Tätä avainta käyttävät ulkoiset Claude-asiakkaat menettävät pääsyn välittömästi. Tätä ei voi kumota.',
            confirmRevoke: 'Peruuta avain',
            rotatedTitle: 'Avainta kierretty',
            rotatedBody: 'Uusi selväkieliavain on alla. Vanha avain jatkaa toimintaansa 5 minuuttia, joten kaikki lennon aikana soitetut puhelut päättyvät.',
            emptyState: 'MCP-avaimia ei ole vielä myönnetty.',
            errorIssue: 'MCP-avainta ei voitu antaa.',
            errorList: 'MCP-avaimia ei voitu ladata.',
            errorRevoke: 'Avainta ei voitu peruuttaa.',
            errorRotate: 'Avainta ei voitu kääntää.',
            neverUsed: '—',
            neverExpires: 'Ei koskaan',
            // Sprint 63.23.0 — the connector page leads with how to connect.
            reloadKeys: 'Reload API keys',
            issuing: 'Issuing…',
            labelRequired: 'Enter a label for the key.',
            toolsRequired: 'Choose at least one tool.',
            webhookField: 'Status callback URL (optional)',
            webhookHint: 'ComplianceOne posts the outcome of each queued write to this address, signed with a secret that is shown once.',
            webhookInvalid: 'Enter a full address, for example https://example.com/hooks/mcp.',
            webhookSecretLabel: 'Webhook signing secret',
            webhookSecretOnce: 'Copy the signing secret now. It is shown once.',
            toolGroupRead: 'Read tools',
            toolGroupWrite: 'Write tools',
            toolGroupWriteHint: 'A write tool never changes a record directly. Each call goes to the approval queue and runs only after an administrator approves it.',
            toolGroupMemory: 'Organisation memory',
            toolGroupSubagent: 'Persona handoff',
            toolQueued: 'goes to the approval queue',
            allToolsInScope: 'Every tool its scopes allow',
            copy: 'Copy',
            copyValue: 'Copy {label}',
            copiedValue: '{label} copied',
            copyFailed: 'Could not copy {label}. Select the value and copy it yourself.',
            apiKeyLabel: 'API key',
            registers: {
                clients: 'OAuth clients',
                grants: 'Connected apps',
                keys: 'API keys',
            },
            connection: {
                title: 'Connection details',
                intro: 'Every client needs the server URL. OAuth clients also use the addresses below; clients that use an API key send it as a bearer token.',
                serverUrl: 'Server URL',
                transport: 'Transport',
                transportStreamableHttp: 'Streamable HTTP',
                protectedResourceMetadataUrl: 'Protected resource metadata URL',
                asMetadataUrl: 'Authorization server metadata URL',
                authorizeUrl: 'Authorize URL',
                tokenUrl: 'Token URL',
                registrationUrl: 'Client registration URL',
                revokeUrl: 'Revocation URL',
                scopes: 'Scopes',
                loading: 'Loading connection details…',
                error: 'The connection details could not be read, so no server URL is shown.',
                reload: 'Reload connection details',
            },
            dcr: {
                label: 'Let clients register themselves',
                hint: 'When this is on, a client such as Claude can register itself and ask a signed-in user to approve it. It has no access until a user of this organisation approves it on the consent screen. Switching it off disconnects apps that registered themselves at their next call; clients registered below and API keys keep working. Switching it back on lets those apps work again.',
                stateOn: 'On — clients can register themselves.',
                stateOff: 'Off — only clients registered below can connect.',
                saving: 'Saving…',
                error: 'Could not change whether clients can register themselves.',
                confirmOffTitle: 'Switch off self-registration?',
                confirmOffBody: 'Apps that registered themselves, such as Claude, are disconnected at their next call for everyone in this organisation, and nobody can approve a new one. Clients registered below and API keys keep working. The connections are suspended, not revoked: switching self-registration back on lets them work again.',
                confirmOff: 'Switch off',
            },
            tabs: {
                label: 'How to connect',
                claude: 'Claude',
                slack: 'Slack and other agent builders',
                apiKey: 'API key',
            },
            claudeTab: {
                step1: 'In Claude, add a custom connector.',
                step2: 'Paste the server URL. Claude finds out how to sign in by itself.',
                step3: 'Sign in to ComplianceOne when asked, then read the consent screen and approve it.',
                consentHeading: 'What the consent screen shows',
                consentItemClient: 'the app’s name and the address it will send you back to;',
                consentItemOrg: 'your organisation;',
                consentItemScopes: 'what it may do — read your organisation’s records, and propose changes for approval if that is requested — and the tools that reach;',
                consentItemOrigin: 'whether the app registered itself or an administrator registered it.',
                actsAsUser: 'Claude then acts as the person who approved it, with no more access than that person’s own role gives them.',
                registrationOff: 'Clients cannot register themselves in this organisation, so Claude cannot connect with the server URL alone. Switch self-registration on, or register a client for Claude under OAuth clients.',
            },
            slackTab: {
                intro: 'Agent builders such as Slack ask for OAuth fields. Register a client under OAuth clients, then copy each value into the builder’s form.',
                colField: 'Field in the agent builder',
                colValue: 'Value',
                fieldServerUrl: 'MCP server URL',
                fieldClientId: 'Client ID',
                fieldClientSecret: 'Client secret',
                fieldAuthorizeUrl: 'Authorize URL',
                fieldTokenUrl: 'Token URL',
                fieldScopes: 'Scopes',
                valueClientId: 'Shown on the client’s row under OAuth clients.',
                valueScopes: 'Enter only the scopes shown on your client’s row under OAuth clients. A scope the client was not registered with is refused.',
                valueClientSecret: 'Shown once, when you register the client or rotate its secret. Store it in the agent builder straight away.',
                redirectNote: 'The agent builder shows the redirect URI it uses. Add it to the client exactly as shown: ComplianceOne sends people back only to a redirect URI registered on the client.',
                registerCta: 'Register a client',
            },
            apiKeyTab: {
                intro: 'Scripts and custom clients can use an API key instead of OAuth. Issue a key under API keys and send it on every request.',
                headerLabel: 'Header',
                configLabel: 'Client configuration (JSON)',
                curlLabel: 'Check the connection with curl',
                actsAsIssuer: 'A call made with a key acts as the administrator who issued it, limited to the tools chosen on the key.',
            },
            clients: {
                errorRevokedClient: 'This client has been revoked, so it has no secret to rotate.',
                paginationLabel: 'OAuth clients pages',
                intro: 'Clients that agent builders use with a client ID and secret. Clients that registered themselves are listed here too.',
                registerButton: 'Register client',
                empty: 'No OAuth clients yet.',
                error: 'The OAuth clients could not be read.',
                reload: 'Reload OAuth clients',
                colName: 'Name',
                colClientId: 'Client ID',
                colType: 'Type',
                colOrigin: 'Registered by',
                colRedirects: 'Redirect URIs',
                colScopes: 'Scopes',
                colTools: 'Tools',
                colLastUsed: 'Last used',
                colStatus: 'Status',
                colActions: 'Actions',
                typeConfidential: 'Confidential',
                typePublic: 'Public',
                originAdmin: 'An administrator',
                originDynamic: 'The client itself',
                statusActive: 'Active',
                statusRevoked: 'Revoked',
                rotateSecret: 'Rotate secret',
                revoke: 'Revoke',
                modalTitle: 'Register OAuth client',
                nameField: 'Name',
                nameHint: 'People see this name on the consent screen.',
                nameRequired: 'Enter a name.',
                typeField: 'Client type',
                typeConfidentialHint: 'Confidential — the client keeps a secret on a server. Use this for Slack and other agent builders.',
                typePublicHint: 'Public — the client cannot keep a secret (a desktop or browser app) and proves itself with PKCE instead.',
                redirectField: 'Redirect URIs',
                redirectHint: 'The exact addresses the client sends people back to. A loopback address such as localhost accepts any port.',
                redirectUriLabel: 'Redirect URI {n}',
                addRedirect: 'Add another redirect URI',
                removeRedirect: 'Remove redirect URI {n}',
                redirectInvalid: 'Enter a full address, for example https://example.com/callback.',
                redirectRequired: 'Add at least one redirect URI.',
                scopesField: 'Scopes',
                scopesRequired: 'Choose at least one scope.',
                toolsField: 'Limit to these tools (optional)',
                toolsHint: 'Leave every box clear to allow each tool the chosen scopes reach.',
                submit: 'Register client',
                saving: 'Registering…',
                errorCreate: 'Could not register the client.',
                createdTitle: 'Client registered',
                secretTitle: 'New client secret',
                secretOnce: 'Copy the client secret now. It is shown once and cannot be shown again; rotate the secret to get a new one.',
                publicNoSecret: 'This is a public client, so it has no secret. It signs in with PKCE.',
                clientIdLabel: 'Client ID',
                clientSecretLabel: 'Client secret',
                confirmRotateTitle: 'Rotate the client secret?',
                confirmRotateBody: 'The current secret stops working now. Put the new secret into the agent builder straight away.',
                errorRotate: 'Could not rotate the client secret.',
                confirmRevokeTitle: 'Revoke this OAuth client?',
                confirmRevokeBody: 'Every app connected through this client loses access now, and its connections are revoked. This cannot be undone.',
                confirmRevoke: 'Revoke client',
                errorRevoke: 'Could not revoke the client.',
            },
            grants: {
                errorNotFound: 'This connection no longer exists; it may already have been revoked.',
                paginationLabel: 'Connected apps pages',
                intro: 'Apps that people in this organisation approved on the consent screen. Revoking one stops its next call.',
                empty: 'No connected apps.',
                error: 'The connected apps could not be read.',
                reload: 'Reload connected apps',
                colApp: 'App',
                colUser: 'Approved by',
                colScopes: 'Scopes',
                colCreated: 'Connected',
                colLastUsed: 'Last used',
                colActions: 'Actions',
                revoke: 'Revoke',
                confirmRevokeTitle: 'Revoke this connection?',
                confirmRevokeBody: 'The app loses access now. The person can connect it again by approving it on the consent screen.',
                confirmRevoke: 'Revoke connection',
                errorRevoke: 'Could not revoke the connection.',
            },
            myApps: {
                errorNotFound: 'This app is no longer connected; it may already have been revoked.',
                title: 'Connected apps',
                intro: 'Apps you have allowed to use ComplianceOne on your behalf. Each acts as you, with no more access than your role gives you.',
                empty: 'You have not connected any apps.',
                error: 'Your connected apps could not be read.',
                reload: 'Reload connected apps',
                colApp: 'App',
                colScopes: 'Scopes',
                colCreated: 'Connected',
                colLastUsed: 'Last used',
                colActions: 'Actions',
                revoke: 'Revoke',
                confirmRevokeTitle: 'Revoke this app?',
                confirmRevokeBody: 'The app loses access now. You can connect it again by approving it on the consent screen.',
                confirmRevoke: 'Revoke app',
                errorRevoke: 'Could not revoke the app.',
            },
        },
        memory: {
            pageTitle: 'Forsetin muisti',
            pageSubtitle: 'Selaa ja kuratoi organisaatiokohtaista kontekstia, jonka Forsetti on kerännyt keskusteluista ja päätöksistä. Kuroidut merkinnät estyvät iltaisin säilymiseltä ja pysyvät jokaisen henkilön saatavilla.',
            filterCategory: 'Luokka',
            filterAllCategories: 'Kaikki luokat',
            totalCount: '{count} merkintää',
            colAnchor: 'Ankkuri',
            colCategory: 'Luokka',
            colSource: 'lähde',
            colContent: 'Sisältö',
            colCreatedAt: 'Luotu',
            colActions: 'Toiminnot',
            editButton: 'Muokkaa',
            deleteButton: 'Poista',
            promoteButton: 'Ylennä kuratoiduksi',
            emptyState: 'Ei vielä muistoja – Forseti kerää kontekstia keskustelun aikana.',
            categories: {
                framework_interpretation: 'Viitekehyksen tulkinta',
                process_preference: 'Prosessiasetus',
                escalation_threshold: 'Eskalaatiokynnys',
                decision_context: 'Päätöksen konteksti',
                glossary_term: 'Sanasto termi',
                auto_inferred: 'Automaattinen päätelmä',
            },
            sources: {
                admin_curated: 'Kuroitu',
                user_explicit: 'Selkeä',
                auto: 'Päätelty',
            },
            deleteConfirmTitle: 'Poistetaanko tämä muisti?',
            deleteConfirmBody: 'Poistaminen poistaa tämän ankkurin kaikista tulevista Forseti-chat-käynnistyksistä. Ankkuri: {anchor}',
            deleteConfirmLabel: 'Poista',
            deleteCancelLabel: 'Peruuta',
            editModalTitle: 'Muokkaa muistia',
            editAnchorLabel: 'Ankkuri',
            editCategoryLabel: 'Luokka',
            editContentLabel: 'Sisältö',
            editContentCount: '{n}/{max} merkkiä',
            editEmptyError: 'Sisältö ei voi olla tyhjä.',
            editTooLongError: 'Sisällön tulee olla ≤1000 merkkiä.',
            editAutoPromoteHint: 'Tämän automaattisesti päätellyn muistin tallentaminen merkitsee sen käyttäjän vahvistamaksi ja poistaa sen säilytyspyyhkäisystä.',
            editCancelButton: 'Peruuta',
            editSaveButton: 'Tallenna',
            editSaving: 'Tallennetaan…',
            loadFailed: 'The memories could not be loaded.',
            deleteFailed: 'The memory could not be deleted.',
            promoteFailed: 'The memory could not be promoted to curated.',
            saveFailed: 'The memory could not be saved.',
        },
        auditLineage: {
            title: 'Tarkastuslinja',
            subtitle: 'Jäljitä jokainen AI:n luoma artefakti, joka on liitetty tietueeseen. Chatit, työkalukutsut, tehtävät, todisteluonnokset ja asiakirjaversiot.',
            filterForm: 'Suodattimet',
            recordType: 'Tietueen tyyppi',
            recordId: 'Tietueen tunnus',
            dateStart: 'From',
            dateEnd: 'Vastaanottaja',
            search: 'Etsi',
            loading: 'Ladataan…',
            exportPdf: 'Vie PDF',
            exportCsv: 'Vie CSV',
            nodesHeading: 'Sukulinjan solmut',
            colType: 'Kirjoita',
            colLabel: 'Label',
            colOccurredAt: 'Tapahtui klo',
            colActor: 'näyttelijä',
            colId: 'ID',
            noResults: 'Ei neuvonantajatoimintaa tälle tietueelle.',
            draftBadge: 'AI luotu luonnos',
            prev: 'Edellinen',
            next: 'Seuraavaksi',
            viewToggleTable: 'Näytä taulukkona',
            viewToggleGraph: 'Näytä kaaviona',
            graphAriaLabel: 'Linjakuvaaja (visuaalinen). Käytä taulukkonäkymää aputekniikkaan.',
            graphLegendDraft: 'Luonnos AI todisteista',
            graphEmpty: 'Ei solmuja kaavioita varten.',
            paginationAriaLabel: 'Sivutus',
            exportSuccessTemplate: '{format}: {filename}',
            exportErrorTemplate: 'Vienti epäonnistui: {message}',
            truncatedNotice: 'Tämä jäljitys ei ole täydellinen. Alusta lukee kustakin lähteestä enintään kiinteän määrän rivejä, ja raja saavutettiin lähteissä: {sources}. Rajan ylittäviä rivejä, jos niitä on, ei näytetä. Rajaa päivämääräväliä, jotta ne tulevat mukaan.',
            truncatedChatLinksNotice: 'This lineage is not complete: only the {limit} newest chats of this record are used to find the tool calls, workflow runs, tasks, evidence and document versions that were started from a chat. Anything reached only through an older chat is not shown, and narrowing the date range does not bring it in.',
            recordTypes: {
                project: 'Projekti',
                dpia: 'DPIA',
                transfer_assessment: 'Cross-border transfer assessment',
                dossier_filing: 'Dossier filing',
                cross_border_filing: 'Cross-border filing',
                vendor: 'Myyjä',
                incident: 'Tapahtuma',
                incident_op: 'Tapahtumatoiminta',
                rights_request: 'Oikeuspyyntö',
                contract: 'Sopimus',
                task: 'Tehtävä',
                data_mapping_activity: 'Tietojen kartoitustoiminta',
                compliance_rule: 'Vaatimustenmukaisuussääntö',
                program: 'Ohjelma',
            },
            nodeTypes: {
                chat: 'Chat',
                tool_call: 'Työkalukutsu',
                workflow_run: 'Työnkulkuajo',
                task: 'Tehtävä',
                evidence: 'Todisteet',
                document_version: 'Asiakirjan versio',
            },
            recordIdManualHint: "This record type has no list here. Enter the record's own id, as it appears in the address of its page.",
            recordIdManualLabel: 'Record ID',
            colDetails: 'Details',
        },
        integrityCenter: {
            verdicts: {
                ok: 'OK',
                drifted: 'Ajautui',
                hash_mismatch: 'Hash-yhteensopivuus',
                merkle_mismatch: 'Merklen yhteensopimattomuus',
                source_missing: 'Lähde puuttuu',
                not_found: 'Ei löytynyt',
                chain_unreachable: 'Ketju tavoittamaton',
                service_unreachable: 'Palvelu tavoittamaton',
                pending: 'Odottaa ankkurointia',
            },
        },
        evidenceDraft: {
            draftBadge: 'AI luonut LUONNOS',
            pendingReviewBadge: 'Odottaa tarkistusta',
            approvedBadge: 'Hyväksytty',
            rejectedBadge: 'Hylätty',
            moveToReviewButton: 'Merkitse valmiiksi tarkastettavaksi',
            approveButton: 'Hyväksy',
            rejectButton: 'Hylkää',
            rejectReasonLabel: 'Hylkäämisen syy',
            humanSignoffBanner: 'Forseti tukee harkintasi - se ei korvaa lisensoitua asianajajaa. AI:n tuottama todiste vaatii ihmisen hyväksynnän, ennen kuin sitä lasketaan viralliseksi todisteeksi.',
            pageTitle: 'AI todisteluonnokset',
            pageSubtitle: 'Tarkista, hyväksy tai hylkää AI:n luomat todisteet. Ihmisen on hyväksyttävä luonnokset, ennen kuin niitä voidaan pitää muodollisina todisteina.',
            filterAll: 'Kaikki toimintakelpoisia',
            filterDrafts: 'Vain luonnokset',
            filterPending: 'Odottaa tarkistusta',
            filterApproved: 'Äskettäin hyväksytty',
            filterRejected: 'Äskettäin hylätty',
            colId: 'Todistustunnus',
            colStatus: 'Tila',
            colSource: 'lähde',
            colCreatedAt: 'Luotu',
            colActions: 'Toiminnot',
            empty: 'Ei AI todisteluonnoksia tässä tilassa.',
            confirmApprove: 'Hyväksytäänkö tämä AI:n luoma todiste muodolliseksi?',
            confirmReject: 'Hylkää tämä todisteluonnos. Tätä ei voi kumota.',
            reasonRequired: 'Hylkäämisen syy vaaditaan.',
            rejectModalTitle: 'Hylkää todisteluonnos',
            approveModalTitle: 'Hyväksy todisteluonnos',
            operationFailed: 'Toiminto epäonnistui. Yritä uudelleen.',
            moveToReviewSuccess: 'Merkitty valmiiksi tarkastettavaksi.',
            approveSuccess: 'Hyväksytty.',
            rejectSuccess: 'Hylätty.',
            sourceLabelWorkflowRunTitle: 'Työnkulku: {title}',
            sourceLabelWorkflowRunId: 'Työnkulkuajo {id}',
            sourceLabelChatTitle: 'Chat: {title}',
            sourceLabelChatId: 'Forseti-chat {id}',
            sourceLabelEmpty: '—',
            filtersAriaLabel: 'Suodattimen tila',
        },
        workflows: {
            pageTitle: 'AI-työnkulut',
            pageSubtitle: 'Tallennetut AI-promptipohjat, jotka tuottavat tehtäviä ja luonnostodisteita.',
            newWorkflow: 'Uusi työnkulku',
            newWorkflowHeading: 'Uusi AI-työnkulku',
            tabAll: 'Kaikki',
            tabBuiltin: 'Sisäänrakennettu paketti',
            tabMine: 'Omat työnkulut',
            tabDetail: 'Tiedot',
            tabLinkedTasks: 'Linkitetyt tehtävät',
            builtinBadge: 'Sisäänrakennettu',
            builtinSubtitle: 'Sisäänrakennettu työnkulku.',
            userSubtitle: 'Mukautettu työnkulku.',
            emptyHint: 'Ei vielä työnkulkuja.',
            loadTimeoutError: 'Työnkulkuja ei voitu ladata 10 sekunnissa. Yritä uudelleen tai tarkista selaimen Verkko-välilehti jumiutuneesta pyynnöstä.',
            loadError: 'Työnkulkuja ei voitu ladata.',
            hideError: 'Piilotus epäonnistui.',
            unhideError: 'Näyttäminen epäonnistui.',
            retry: 'Yritä uudelleen',
            producesTasks: 'Tuottaa tehtäviä',
            producesEvidence: 'Tuottaa luonnostodisteita',
            hideButton: 'Piilota listaltani',
            unhideButton: 'Näytä',
            runButton: 'Suorita',
            editButton: 'Muokkaa',
            shareButton: 'Jaa',
            backToList: 'Takaisin listaan',
            requireTitleAndPrompt: 'Otsikko ja prompt vaaditaan.',
            fieldTitle: 'Otsikko',
            fieldDescription: 'Kuvaus',
            fieldType: 'Tyyppi',
            fieldPersona: 'Persoonan affiniteetti',
            fieldPractice: 'Käytäntö',
            fieldPrompt: 'Kehote (merkintä)',
            fieldProducesTasks: 'Tuottaa tehtäviä',
            fieldProducesEvidence: 'Tuottaa luonnostodisteita',
            fieldProjectId: 'Projekti (valinnainen)',
            fieldAttachmentIds: 'Liitteet',
            fieldParentRecord: 'Yläsisältö (valinnainen)',
            fieldPersonaOverride: 'Persoonan ohitus (valinnainen)',
            typeAssistant: 'Avustaja',
            typeTabular: 'Taulukkomainen',
            personaNone: '— ei mitään —',
            parentNone: '— ei mitään —',
            parentIdPlaceholder: 'Yläsisällön tunniste',
            selectProjectPlaceholder: 'Valitse projekti…',
            selectProjectFirst: 'Valitse projekti valitaksesi liitteet.',
            selectAttachmentsPlaceholder: 'Valitse asiakirjat…',
            noAttachmentsInProject: 'Tällä projektilla ei ole liitteitä.',
            selectParentRecordPlaceholder: 'Valitse tietue…',
            selectParentRecordFirst: 'Valitse ensin tietuetyyppi.',
            noRecordsForType: 'Tämän tyyppisiä tietueita ei ole saatavilla.',
            shareUserSelectorLabel: 'Vastaanottajat (käyttäjät)',
            shareGroupSelectorLabel: 'Vastaanottajat (organisaatioryhmät)',
            selectUsersPlaceholder: 'Valitse käyttäjät…',
            selectOrgGroupsPlaceholder: 'Valitse organisaatioryhmät…',
            loadingOptions: 'Ladataan…',
            loadFailed: 'Vaihtoehtojen lataus epäonnistui.',
            parentRecordTypes: {
                dpia: 'DPIA',
                vendor: 'Toimittaja',
                incident: 'Tapahtuma',
                rights_request: 'Oikeuspyyntö',
                contract: 'Sopimus',
                data_mapping_activity: 'RoPA-toiminto',
                compliance_rule: 'Vaatimussääntö',
                program: 'Ohjelma',
            },
            cancelButton: 'Peruuta',
            closeButton: 'Sulje',
            createButton: 'Luo',
            creating: 'Luodaan…',
            saveButton: 'Tallenna',
            saving: 'Tallennetaan…',
            shareWorkflowHeading: 'Jaa työnkulku',
            shareIntro: 'Jaetaan: {title}',
            shareUserIds: 'Käyttäjätunnukset (välilyönnillä tai pilkulla erotettuna)',
            shareGroupIds: 'Organisaatioryhmätunnukset (välilyönnillä tai pilkulla erotettuna)',
            shareAllowEdit: 'Salli vastaanottajien muokata',
            shareRequireRecipient: 'Anna vähintään yksi käyttäjä- tai ryhmätunnus.',
            shareSuccess: 'Jaettu.',
            sharing: 'Jaetaan…',
            runWorkflowHeading: 'Suorita: {title}',
            runStarting: 'Käynnistetään…',
            runStatusLabel: 'Tila: {status}',
            runProducedCounts: 'Tehtävät: {tasks} · Todistusaineistoluonnokset: {evidence} · Liitteet: {attachments}',
            runPanelHeading: 'Työnkulun ajo',
            runPanelHeadingWithTitle: 'Työnkulun ajo: {title}',
            runViewTasks: 'Näytä luodut tehtävät',
            runViewEvidence: 'Näytä luonnostodisteet',
            runResultHeading: 'Neuvojan tulos',
            runNoResult: 'Ajo päättyi, mutta se ei palauttanut tarkasteltavaa tulosta. Tehtäviä, todisteita tai liitteitä ei luotu. Yritä uudelleen valittu tietue tai vaadittavat asiakirjat mukana.',
            runCitationWarning: 'Joitakin lainauksia ei voitu vahvistaa automaattisesti. Tarkista ne, ennen kuin luotat tähän tulokseen.',
            runToolCallWarning: 'Forseti attempted a tool this workflow could not run, and returned the attempt as text. Nothing was produced from it.',
            runNoEnvelopeWarning: 'Forseti returned a result this workflow could not read, so no tasks or evidence were created from it.',
            runNoArtefactsWarning: 'This run completed without creating any tasks or draft evidence. Review the result before treating the work as done.',
            runLoadFailed: 'Työnkulun ajoa ei voitu ladata.',
            runStreamFailed: 'Yhteys työnkulun etenemistietoihin katkesi.',
            runStatusValues: {
                pending: 'Odottaa',
                running: 'Käynnissä',
                completed: 'Valmis',
                succeeded: 'Onnistui',
                failed: 'Epäonnistui',
                cancelled: 'Peruttu',
            },
            runProgressStatus: 'Tila: {status}',
            runProgressTaskCreated: 'Tehtävä luotu.',
            runProgressEvidenceDrafted: 'Luonnostodiste luotu.',
            runProgressAttachmentGenerated: 'Liite luotu.',
            guidedWorkflowTitle: 'Ohjattu työnkulku: {recordType}',
            guidedWorkflowDescription: 'Luotu tyypin {recordType} tietueille. Tarkista ja mukauta työnkulkua ennen tallentamista.',
            guidedWorkflowPrompt: 'Tarkista annettu tyypin {recordType} tietue, tunnista puuttuvat tosiseikat ja ehdota perusteltuja jatkotoimia ihmisen tarkistettavaksi.',
            promptHeading: 'Kehote',
            howItWorksHeading: 'Mitä tapahtuu, kun suoritat tämän työnkulun',
            builtinGuidance: 'Forseti tarkastelee valitsemasi tietueet ja asiakirjat, palauttaa käyttäjän tarkasteltavissa olevan tuloksen ja luo luonnostehtäviä tai -todisteita vain, jos tulos tukee niitä. Mitään ei hyväksytä tai lähetetä automaattisesti.',
            editWorkflowHeading: 'Muokkaa työnkulkua',
            linkedTasksHint: 'Tasks created by this workflow show up in the Tasks list, filtered by this workflow’s ID.',
            notFound: 'Työnkulkua ei löytynyt.',
            schedules: {
                tabTitle: 'Aikataulut',
                sectionHeading: 'Ajastetut ajot',
                empty: 'Ei vielä aikatauluja. Luo aikataulu tämän työnkulun ajamiseksi automaattisesti.',
                addButton: 'Lisää aikataulu',
                addHeading: 'Aikatauluta työnkulku',
                editHeading: 'Muokkaa aikataulua',
                fieldFrequency: 'Toistuvuus',
                fieldDayOfWeek: 'Viikonpäivä',
                fieldDayOfMonth: 'Kuukauden päivä',
                fieldTime: 'Aika (UTC)',
                fieldTimezone: 'Aikavyöhyke',
                fieldProject: 'Projekti (valinnainen)',
                fieldAttachments: 'Oletusliitteet',
                fieldPersonaOverride: 'Persoonan ohitus (valinnainen)',
                frequencyDaily: 'Päivittäin',
                frequencyWeekly: 'Viikoittain',
                frequencyMonthly: 'Kuukausittain',
                weekdaySun: 'Sunnuntai',
                weekdayMon: 'Maanantai',
                weekdayTue: 'Tiistai',
                weekdayWed: 'Keskiviikko',
                weekdayThu: 'Torstai',
                weekdayFri: 'Perjantai',
                weekdaySat: 'Lauantai',
                statusActive: 'Aktiivinen',
                statusPaused: 'Tauotettu',
                nextRunLabel: 'Seuraava ajo',
                lastRunLabel: 'Viimeinen ajo',
                pauseButton: 'Tauko',
                resumeButton: 'Jatka',
                deleteButton: 'Poista',
                triggerButton: 'Suorita nyt',
                savingButton: 'Tallennetaan…',
                saveButton: 'Tallenna',
                triggered: 'Ajo aloitettu — tarkista ajohistoria.',
            },
            branching: {
                helpHeading: 'Ehdollinen prompt-syntaksi',
                helpBody: 'Use {% if persona.slug == "forseti-legal" %}…{% endif %} to vary the prompt by context. Variables with a value: persona.slug (the persona chosen when the run starts, otherwise the workflow’s persona), org.country and org.locale (your organisation’s country and default language), and project.name (the project the run belongs to). attachment.framework_code, attachment.locale and project.tier are accepted but currently have no source, so {{ }} on them renders empty and a condition on them is always false. Nested {% if %} blocks are not supported.',
            },
            runCancelButton: 'Cancel run',
            runCancelling: 'Cancelling…',
            runCancelRequested: 'Cancellation requested. The model request already under way cannot be recalled, but when it returns its answer is discarded and no tasks, draft evidence or files are created.',
            runCancelledNote: 'This run was cancelled. No tasks, draft evidence or files were created from it.',
            runCancelFailed: 'Could not cancel the run.',
        },
        crossFrameworkConflicts: {
            pageTitle: 'Kehysten väliset ristiriidat',
            pageSubtitle: 'Kuroitu luettelo siitä, missä asennetut säädöspaketit ovat vuorovaikutuksessa ristiriitaisilla tavoilla. Forseti pinnat; ihmiset päättävät.',
            noInstalledPacks: 'Tälle organisaatiolle ei ole asennettu sääntelypaketteja – asenna vähintään kaksi pakettia kehysten välisten ristiriitojen ratkaisemiseksi.',
            noConflicts: 'Mikään ristiriita ei vastaa nykyisiä suodattimia.',
            allAcknowledged: 'Jokainen näkyvä ristiriita on tunnustettu. Hienoa työtä.',
            columnSeverity: 'Vakavuus',
            columnTitle: 'Otsikko',
            columnFrameworks: 'Kehykset',
            columnTopic: 'Aihe',
            columnStatus: 'Tila',
            filterFrameworkPlaceholder: 'Kaikki puitteet',
            filterTopicPlaceholder: 'Kaikki aiheet',
            filterSeverityPlaceholder: 'Kaikki vakavuusasteet',
            filterStatusPlaceholder: 'Kaikki tilat',
            filterStatusAcknowledged: 'Tunnustettu',
            filterStatusUnacknowledged: 'Tunnustamaton',
            severityCritical: 'Kriittinen',
            severityHigh: 'Korkea',
            severityMedium: 'Keskikokoinen',
            severityLow: 'Matala',
            statusAcknowledged: 'Tunnustettu',
            statusInReview: 'Tarkastuksessa',
            statusResolved: 'Ratkaistu',
            statusWontFix: 'Ei korjaa',
            statusUnacknowledged: 'Tunnustamaton',
            drawerSummaryHeading: 'Yhteenveto',
            drawerSideAHeading: 'puoli A',
            drawerSideBHeading: 'Sivu B',
            drawerRequirementsLabel: 'Mainitut vaatimukset',
            drawerProvisionsLabel: 'Mainitut määräykset',
            drawerSuggestedResolutionHeading: 'Ehdotettu ratkaisu',
            drawerLegalCounselBadge: 'Tarvitaan lakimiehen tarkastus',
            drawerAckHeading: 'Tunnustus',
            drawerAckStatusLabel: 'Tila',
            drawerAckAssigneeLabel: 'Määrätty',
            drawerAckDueDateLabel: 'Eräpäivä',
            drawerAckNotesLabel: 'Huomautuksia',
            drawerAckButtonAcknowledge: 'Tunnusta',
            drawerAckButtonUpdate: 'Päivitä',
            drawerAckButtonUnacknowledge: 'Peruuta tunnustaminen',
            // fix-sprint-20260912-2123 F23 — R-57: structural parity, EN value carried.
            drawerCloseLabel: 'Close',
            drawerAckAssigneePlaceholder: 'User ID',
            drawerAckSaveFailed: 'Could not save the acknowledgement.',
            drawerAckUnacknowledgeFailed: 'Could not remove the acknowledgement.',
            loadFailed: 'Could not load conflicts.',
            searchPlaceholder: 'Hae otsikkoa tai yhteenvetoa…',
            searchButton: 'Etsi',
            searchButtonAriaLabel: 'Suorita ristiriitahaku',
            exportSuccessTemplate: 'CSV: {filename}',
            exportSuccessTemplateXlsx: 'XLSX: {filename}',
            exportErrorTemplate: 'Vienti epäonnistui: {message}',
            exportAriaLabel: 'Vientiristiriidat',
            // fix-sprint D1 F23 — the page-level load failure now renders through
            // `withRetry`, which needs the control's label, its busy label and the
            // subject a screen reader hears after it.
            reload: 'Reload',
            reloading: 'Reloading…',
            retrySubject: 'the conflict list',
        },
        tabularReviews: {
            pageTitle: 'Tabulaariset arvioinnit',
            pageSubtitle: 'Vertaa N asiakirjaa M attribuutin yli perustellulla viittauksella.',
            newReview: 'Uusi arviointi',
            newReviewHeading: 'Uusi tabulaarinen arviointi',
            emptyHint: 'Ei vielä tabulaarisia arviointeja.',
            docsLabel: '{n} asiakirjaa',
            colsLabel: '{n} saraketta',
            detailSubtitle: '{docs} asiakirjaa · {cols} saraketta',
            addColumn: 'Lisää sarake',
            addColumnHeading: 'Lisää sarake',
            columnInfo: 'Näytä kehote ja muoto',
            columnInfoAria: 'Näytä sarakkeen {name} kehote ja muoto',
            columnInfoPrompt: 'Kehote',
            columnInfoFormat: 'Palautusmuoto',
            columnInfoPresets: 'Sallitut arvot',
            columnInfoCitations: 'Lähdeviitteet',
            columnInfoCitationsRequired: 'Pakollinen',
            removeColumn: 'Poista sarake',
            removeColumnAria: 'Poista sarake {name}',
            removeColumnConfirm: 'Poistetaanko sarake "{name}"? Sen poimitut solut poistetaan.',
            deleteReview: 'Poista',
            deleteReviewAria: 'Poista tarkastelu {title}',
            deleteReviewConfirm: 'Poistetaanko tarkastelu "{title}"? Tämä poistaa pysyvästi sen sarakkeet, solut ja keskustelut.',
            runAll: 'Suorita kaikki',
            running: 'Käynnissä…',
            cancel: 'Peruuta',
            exportXlsx: 'Vie Excel',
            exportCsv: 'Vie CSV',
            exporting: 'Viedään…',
            backToList: 'Takaisin listaan',
            notFound: 'Tabulaarista arviointia ei löytynyt.',
            loadFailed: 'The tabular review could not be read. Reload to try again.',
            emptyPayload: 'The server answered without the tabular review. Reload to try again.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retrySubject: 'this tabular review',
            cancelFailed: 'The run could not be cancelled.',
            removeColumnFailed: 'The column could not be removed.',
            exportFailed: 'The export could not be produced.',
            noDocuments: 'Liitä asiakirjoja aloittaaksesi arvioinnin.',
            noColumns: 'Lisää sarakkeita määritelläksesi mitä poimitaan.',
            tableAriaLabel: 'Poimintaverkko',
            threadsCountAria: '{n} keskusteluviestiä',
            startDiscussionAria: 'Aloita keskustelu',
            docColumn: 'Asiakirja',
            statusPending: 'Odottaa',
            statusRunning: 'Käynnissä…',
            statusFailed: 'Epäonnistui: {msg}',
            statusCancelled: 'Peruttu',
            citationsCount: '{n} viittausta',
            citationsView: 'Näytä lähdekohdat',
            citationPage: 'Sivu {n}',
            titleRequired: 'Otsikko vaaditaan.',
            fieldTitle: 'Otsikko',
            fieldProjectId: 'Projekti (valinnainen)',
            fieldAttachmentIds: 'Liitteet',
            fieldColumnName: 'Sarakkeen nimi',
            fieldColumnNameHelp: 'Lyhyt, kuvaava tunniste tälle sarakkeelle poimintaruudukossa.',
            columnNamePlaceholder: 'esim. Sopimuksen alkupäivä',
            fieldExtractionPrompt: 'Poimintaprompti',
            fieldExtractionPromptHelp: 'Kuvaile poimittavat tiedot. Ole täsmällinen saadaksesi parempia tuloksia.',
            columnPromptPlaceholder: 'Mitä Forsetin tulisi etsiä asiakirjoista?',
            fieldReturnFormat: 'Palautusmuoto',
            fieldReturnFormatHelp: 'Ohjaa, miten tekoäly muotoilee vastauksensa (esim. päivämääränä tai numerona).',
            fieldPresets: 'Sallitut arvot (yksi per rivi)',
            fieldPresetsPlaceholder: 'kyllä\nei\nosittain',
            fieldCitationsRequired: 'Viittaus vaaditaan',
            formatShortText: 'Lyhyt teksti',
            formatLongText: 'Pitkä teksti',
            formatEnum: 'Enum',
            formatNumber: 'Numero',
            formatDate: 'Päivämäärä',
            formatList: 'Lista',
            columnRequireFields: 'Nimi ja prompt vaaditaan.',
            addColumnError: 'Sarakkeen lisääminen epäonnistui.',
            cancelButton: 'Peruuta',
            createButton: 'Luo',
            creating: 'Luodaan…',
            addButton: 'Lisää',
            adding: 'Lisätään…',
            runConfirmHeading: 'Suorita poiminta',
            estimateBody: 'Arvioitu ~{units} laskutettavaa yksikköä ({confidence} luottamus).',
            starting: 'Käynnistetään…',
            confirmRun: 'Vahvista ajo',
            charts: {
                title: 'Sarakekaaviot',
                tabsAria: 'Kaaviosarakkeet',
                enumAria: 'Enum-arvojen jakauma',
                numberAria: 'Numerojakauman histogrammi',
                dateAria: 'Päivämääräaikajana',
                enumEmpty: 'Ei valmiita soluja.',
                numberEmpty: 'Ei numeerisia arvoja.',
                numberExcluded: '{n} solua poissuljettu (tyhjät / ei-numeeriset / ei valmiit).',
                dateEmpty: 'Ei päivämääräarvoja.',
                dateExcluded: '{n} solua poissuljettu.',
                unsupportedFormat: 'Ei tuettu sarakemuoto.',
            },
            threads: {
                drawerTitle: 'Keskustelu',
                loading: 'Ladataan…',
                empty: 'Ei vielä viestejä. Aloita keskustelu.',
                composerLabel: 'Uusi kommentti',
                composerPlaceholder: 'Lisää kommentti…',
                post: 'Lähetä',
                posting: 'Lähetetään…',
                edit: 'Muokkaa',
                editLabel: 'Muokkaa viestiä',
                delete: 'Poista',
                save: 'Tallenna',
                cancel: 'Peruuta',
                close: 'Sulje',
                editedBadge: 'muokattu',
                loadError: 'Ketjun lataus epäonnistui',
                postError: 'Lähetys epäonnistui',
                editError: 'Muokkaus epäonnistui',
                deleteError: 'Poisto epäonnistui',
            },
            compare: {
                pageTitle: 'Vertaa arviointeja',
                leftReviewLabel: 'Vasen arviointi',
                rightReviewLabel: 'Oikea arviointi',
                pickReview: 'Valitse arviointi vertailtavaksi',
                noSharedDocuments: 'Näillä arvioinneilla ei ole yhteisiä asiakirjoja — vertailu vaatii vähintään yhden yhteisen liitteen.',
                sharedDocumentsHeading: 'Yhteiset asiakirjat',
                leftOnlyHeading: 'Vain vasemmassa arvioinnissa olevat sarakkeet',
                rightOnlyHeading: 'Vain oikeassa arvioinnissa olevat sarakkeet',
                differs: 'Eroaa',
                identical: 'Identtinen',
                emptyValue: '—',
                compareButton: 'Vertaa kohteeseen…',
                documentHeading: 'Asiakirja',
                errorMissingIds: 'Sekä vasemman että oikean arvioinnin tunnisteet vaaditaan.',
                errorComparisonFailed: 'Vertailu epäonnistui.',
                errorLoadFailed: 'Arviointien lataus epäonnistui.',
                pickerLoading: 'Ladataan…',
                pickerNoOtherReviews: 'Ei muita arviointeja saatavilla vertailtavaksi.',
                pickerCancel: 'Peruuta',
            },
        },
        documentReview: {
            pageTitle: 'Asiakirjan tarkistus',
            pageSubtitle: 'Hyväksy tai hylkää AI:n ehdottamat muutokset tähän asiakirjaan.',
            sidePanelLabel: 'AI punaviivat',
            sidePanelHeading: 'AI punaviivat',
            noPendingEdits: 'Ei odottavia muokkauksia.',
            pendingCount: '{count} odottaa',
            acceptAllButton: 'Hyväksy kaikki',
            rejectAllButton: 'Hylkää kaikki',
            acceptAllTitle: 'Hyväksytäänkö kaikki odottavat muokkaukset?',
            acceptAllConfirm: 'Tämä hyväksyy {count} odottavat muokkaukset ja luo yhden tai useamman uuden version. Jatkaa?',
            editsHeading: 'Muokkaukset',
            editsEmpty: 'Ei tarkistettavia muokkauksia.',
            versionsHeading: 'Versiohistoria',
            versionsEmpty: 'Ei vielä versioita.',
            editLabel: 'Muokkaa',
            editLabelWithId: 'Muokkaa #{id}',
            acceptButton: 'Hyväksy',
            rejectButton: 'Hylkää',
            accepting: 'Hyväksytään…',
            rejecting: 'Hylätään…',
            diffDeletedAria: 'Poistettu',
            diffInsertedAria: 'Lisätty',
            statusPending: 'Odottaa',
            statusAccepted: 'Hyväksytty',
            statusRejected: 'Hylätty',
            versionLabel: 'Versio',
            versionAriaLabel: 'Versio {n}',
            versionAriaLabelCurrent: 'Versio {n} ({current})',
            versionCurrentTag: 'nykyinen',
            versionSourceLabel: 'lähde',
            versionCreatedLabel: 'Luotu',
            versionModelLabel: 'Malli',
            sourceUpload: 'Lataa',
            sourceUserUpload: 'Käyttäjän lataus',
            sourceAssistantEdit: 'Assistant-muokkaus',
            sourceUserAccept: 'Käyttäjä hyväksyi muokkauksen',
            sourceUserReject: 'Käyttäjä hylkäsi muokkauksen',
            sourceGenerated: 'Luotu',
            extractionExtracted: 'Teksti poimittu',
            extractionFailed: 'Tekstin poiminta epäonnistui',
            extractionUnsupported: 'Tekstiä ei voitu poimia',
            extractionPages: '{n} sivua',
            extractionWords: '{n} sanaa',
            extractionReasonScanned: 'Tämä näyttää skannatulta tai pelkkiä kuvia sisältävältä asiakirjalta. Forseti ei osaa vielä lukea sitä — lataa tekstipohjainen tiedosto tai liitä teksti suoraan.',
            extractionReasonEncrypted: 'Tiedosto on salasanasuojattu. Poista suojaus ja lataa se uudelleen.',
            extractionReasonMalformed: 'Tiedostoa ei voitu jäsentää, ja se saattaa olla vioittunut.',
            extractionReasonParserUnavailable: 'The server’s PDF reader is not installed correctly, so the text could not be read. This says nothing about your file. Ask an administrator to check the installation.',
            extractionReasonTooLarge: 'Tiedosto on liian suuri käsiteltäväksi.',
            extractionReasonTimeout: 'Käsittely kesti liian kauan. Kokeile pienempää tai yksinkertaisempaa tiedostoa.',
            extractionReasonUnknown: 'Lisätietoja ei ole saatavilla.',
            // Sprint B - Forseti actions on the document review detail page.
            // EN values pending the next 56.x locale fan-out sprint.
            askForsetiButton: 'Ask Forseti',
            runRedlineButton: 'Run redline',
            forsetiModalTitleAsk: 'Ask Forseti — {name}',
            forsetiModalTitleRedline: 'Run redline — {name}',
            forsetiGuidanceAsk: 'Ask Forseti about this document. Pick a starter below or type your own question.',
            forsetiGuidanceRedline: 'Forseti will read this document and propose redline edits. Pick the redline starter below to begin — edits appear in the panel for you to accept or reject.',
            forsetiStarterRedline: 'Read attachment {id} and propose redline edits for legal and compliance issues',
            forsetiStarterSummarise: 'Summarise the key obligations in attachment {id}',
            forsetiStarterRisks: 'Review attachment {id} for legal risks and missing clauses',
            forsetiStarterFrameworks: 'Which compliance frameworks apply to attachment {id}?',
            textPaneLabel: 'Asiakirjan teksti',
            textPaneTitle: 'Asiakirjan teksti',
            textPaneVersion: 'Versio {n}',
            textPaneEmpty: 'Tälle versiolle ei ole saatavilla tekstiä. Skannatuissa PDF-tiedostoissa ja joissakin asiakirjatyypeissä ei ole poimittavaa tekstiä.',
            downloadVersionAria: 'Download version {n}',
            downloadVersionButton: 'Download',
            rejectAllConfirm: 'This rejects {count} pending edits. The reason you give is recorded on each one.',
            rejectAllTitle: 'Reject all pending edits?',
            rejectReasonShown: 'Reason',
            rejectConfirmButton: 'Confirm rejection',
            loadRedlinesFailed: 'Failed to load redlines',
            acceptEditsFailed: 'Failed to accept edits',
            rejectEditsFailed: 'Failed to reject edits',
            batchPartialFailure: '{failed} of {total} failed: {reason}',
            actionFailed: 'Action failed',
            rejectReasonLabel: 'Reason for rejecting (optional)',
        },
        documentReviewIndex: {
            sidebarLabel: 'Asiakirjojen arviointi',
            pageTitle: 'Asiakirjojen arviointi',
            pageSubtitle: 'Organisaatiosi asiakirjat, joissa on AI:n ehdottamia muokkauksia odottamassa arviointia tai Forseti-pohjaisia versioita.',
            colDocument: 'Asiakirja',
            colLinkedRecord: 'Linkitetty tietue',
            colPendingEdits: 'Odottavat muokkaukset',
            colLastActivity: 'Viimeisin toiminta',
            colOpen: 'Avaa',
            open: 'Avaa',
            empty: 'Ei vielä asiakirjoja Forseti-arvioinnissa. Asiakirjat tulevat tähän jonoon, kun Forseti ehdottaa muokkausta keskustelussa, kun AI-työnkulku tuottaa redlinen, tai kun lähetät asiakirjan arviointiin yllä olevalla painikkeella.',
            loading: 'Ladataan…',
            loadError: 'Arvioinnissa olevien asiakirjojen lataus epäonnistui.',
            retryButton: 'Yritä uudelleen',
            noLinkedRecord: '—',
            pendingZero: 'Ei yhtään',
            sendForReviewButton: 'Lähetä asiakirja arviointiin',
            sendForReviewTitle: 'Lähetä asiakirja arviointiin',
            sendForReviewSubtitle: 'Lataa .txt-, .md-, .pdf- tai .docx-asiakirja (enintään 50 Mt). Se lisätään arviointijonoon ja Forseti tekee siihen redline-ajon.',
            sendForReviewTabUpload: 'Lataa uusi',
            sendForReviewPickerLabel: 'Etsi liitteistä',
            sendForReviewPickerPlaceholder: 'Kirjoita hakeaksesi…',
            sendForReviewPickerEmpty: 'Ei vastaavia liitteitä. Yritä toista hakua.',
            sendForReviewSubmit: 'Lähetä arviointiin',
            sendForReviewCancel: 'Peruuta',
            sendForReviewSubmitting: 'Lähetetään…',
            sendForReviewError: 'Asiakirjan lähetys arviointiin epäonnistui. Yritä uudelleen.',
            sendForReviewSuccess: 'Asiakirja lähetetty arviointiin.',
            sendForReviewAcceptedExt: 'Hyväksytyt: {ext}.',
            sendForReviewMaxSize: 'Enimmäiskoko {mb} MB.',
        },
        chats: {
            exportError: 'Vienti epäonnistui.',
        },
    },
    pdplEvidencePacks: {
        title: 'PDPL-todistuspaketit',
        subtitle: 'Asiakkaan hallitsema toimitus MoPS A05 -viranomaiselle',
        nav: { emailProviders: 'Sähköpostipalveluntarjoajat' },
        providers: {
            pageTitle: 'Sähköpostipalveluntarjoajat',
            pageSubtitle: 'Määritä organisaatiosi lähtevän sähköpostin palveluntarjoaja PDPL-todistuspakettien toimittamiseksi MoPS A05 -viranomaiselle.',
            architectureRuleHeading: 'Arkkitehtuurisääntö (lukittu)',
            architectureRuleBody: 'AesirX ei ole todistepakettisähköpostien lähettäjä. Jokainen lähetys käyttää palveluntarjoajaa, joka on sinun omistuksessasi ja hallinnassasi. AesirX ei koskaan lähetä puolestasi.',
            empty: 'Palveluntarjoajia ei ole vielä määritetty.',
            createCta: 'Luo palveluntarjoaja',
            statusPendingConsent: 'Suostumusta odottaa',
            statusPendingConnectionCheck: 'Yhteystestiä odottaa',
            statusActive: 'Aktiivinen',
            statusConnectionCheckFailed: 'Yhteystesti epäonnistui',
            statusDisabled: 'Poistettu käytöstä',
            acceptConsentCta: 'Hyväksy suostumus',
            runConnectionCheckCta: 'Suorita yhteystesti',
            disableCta: 'Poista käytöstä',
            disableConfirm: 'Tämän palveluntarjoajan poistaminen käytöstä pysäyttää suorat lähetykset, kunnes uusi palveluntarjoaja on määritetty ja suostumus vahvistettu uudelleen. Jatketaanko?',
            columnDisplayName: 'Nimi',
            columnFromAddress: 'Lähettäjäosoite',
            columnStatus: 'Tila',
            columnLastCheck: 'Viimeisin testi',
            columnActions: 'Toiminnot',
            createModalTitle: 'Uusi SMTP-palveluntarjoaja',
            createDisplayNameLabel: 'Näyttönimi',
            createFromAddressLabel: 'Lähettäjäosoite',
            createReplyToLabel: 'Vastausosoite (valinnainen)',
            createSmtpHostLabel: 'SMTP-isäntä',
            createSmtpPortLabel: 'SMTP-portti',
            createSmtpUsernameLabel: 'SMTP-käyttäjätunnus',
            createSmtpPasswordLabel: 'SMTP-salasana',
            createSmtpEncryptionLabel: 'Salaus',
            createSmtpEncryptionStarttls: 'STARTTLS',
            createSmtpEncryptionTls: 'TLS (implisiittinen)',
            createSmtpEncryptionNone: 'Ei mitään',
            createOauthM365Tab: 'Microsoft 365',
            createOauthGoogleTab: 'Google Workspace',
            createOauthDisabledBadge: 'Tulossa pian',
            createOauthDisabledHint: 'OAuth-palveluntarjoajia ei ole vielä otettu käyttöön. Käytä toistaiseksi SMTP:tä.',
            createSubmitCta: 'Luo palveluntarjoaja',
            createCancelCta: 'Peruuta',
            createErrorInvalidFromAddress: 'Lähettäjäosoitteen on oltava kelvollinen sähköpostilaatikko.',
            createErrorDuplicateFromAddress: 'Toinen palveluntarjoaja on jo määritetty tälle lähettäjäosoitteelle.',
            createErrorGeneric: 'Palveluntarjoajan luonti epäonnistui. Tarkista lomake ja yritä uudelleen.',
            consentModalTitle: 'Vahvista arkkitehtuurisääntö',
            consentLocaleToggleEn: 'Englanti',
            consentLocaleToggleVi: 'Vietnam',
            consentAcknowledgeCta: 'Vahvistan — hyväksy suostumus',
            consentCancelCta: 'Peruuta',
            consentErrorMismatch: 'Suostumusteksti on muutettu eikä vastaa enää kanonista vastuuvapauslauseketta. Avaa valintaikkuna uudelleen ja lähetä uudelleen.',
            testModalTitle: 'Yhteystesti',
            testRunningCta: 'Testataan yhteyttä…',
            testRunCta: 'Suorita yhteystesti',
            testRetryCta: 'Yritä yhteystestiä uudelleen',
            testSuccessHeading: 'Yhteys onnistui',
            testSuccessBody: 'Todennettu kohteeseen {host}:{port} {latencyMs} ms:ssa. Palveluntarjoaja on valmis suoria lähetyksiä varten.',
            testFailureHeading: 'Yhteys epäonnistui',
            testFailureBody: 'Testiä ei voitu suorittaa loppuun. Tarkista syy alta ja yritä uudelleen korjauksen jälkeen.',
            testFailureReasonLabel: 'Virheen syy',
            testCloseCta: 'Sulje',
            detailDrawerTitle: 'Palveluntarjoajan tiedot',
            detailDrawerHistoryHeading: 'Yhteystestien historia',
            detailDrawerConsentHeading: 'Suostumusvahvistus',
            detailDrawerNoHistory: 'Yhteystestejä ei ole vielä kirjattu.',
            detailDrawerNoConsent: 'Suostumusta ei ole vielä vahvistettu.',
            outcomeSuccess: 'Onnistui',
            outcomeFailure: 'Epäonnistui',
        },
        builder: {
            pageTitle: 'Kokoa todistuspaketti',
            pageSubtitle: 'Lataa 6 osatiedostoa, valitse paketin tyyppi ja kokoa deterministinen 8-osainen ZIP toimitusta varten.',
            packTypeLabel: 'Paketin tyyppi',
            packTypeDpia: 'Processing impact-assessment pack (notice 02a/02b; report 10)',
            packTypeOtia: 'Cross-border impact-assessment pack (notice 01a/01b; report 09)',
            part01CoverLetter: 'Osa 01 — Saatekirje (PDF)',
            part02AuthorityForm: 'Osa 02 — Viranomaislomake (PDF)',
            part03Supporting: 'Osa 03 — Tukitodisteet (≥1)',
            part04Safeguards: 'Osa 04 — Suojatoimet',
            part04SafeguardsRequiredOtia: '(pakollinen OTIA:lle)',
            part05PriorCorrespondence: 'Osa 05 — Aiempi kirjeenvaihto (valinnainen)',
            part06Signature: 'Osa 06 — Allekirjoitus- ja leimatodisteet (≥1)',
            uploadPickFile: 'Valitse tiedosto',
            uploadPickFiles: 'Valitse tiedostot',
            uploadRemoveFile: 'Poista',
            requiredMissing: 'Pakollinen osa puuttuu',
            assembleCta: 'Kokoa paketti',
            assembling: 'Kootaan…',
            assembleSuccessHeading: 'Paketti koottu',
            assembleSuccessSummary: '{fileCount} tiedostoa · {bytes} tavua · SHA-256 {shaPrefix}…',
            composeNextCta: 'Laadi sähköpostiluonnos',
            warningsHeading: 'Validointivaroitukset',
            assembleError: 'Pakettia ei voitu koota. Tarkista syötteet ja yritä uudelleen.',
            forsetiAssistHeading: 'Forseti AI-apu (valinnainen)',
            forsetiAssistHint: 'Suorita PDPL Evidence Pack Assembly Advisor tuodaksesi esiin puuttuvat osat, laatiaksesi saatekirjeen tekstin ja merkitäksesi suojausaukkoja. AesirX ei ole lähettäjä — tekoäly vain neuvoo; sinä hyväksyt jokaisen tuotoksen.',
            forsetiAssistCta: 'Avaa Forseti-työnkulkukirjasto',
            forsetiAiRunIdLabel: 'Linkitetty Forseti AI-ajon tunnus (liitä rekisteröidäksesi tekoälyavun)',
        },
        draft: {
            pageTitle: 'Sähköpostiluonnos',
            pageSubtitle: 'Laadi ja muokkaa sähköpostiluonnosta. Suora lähetys tulee myöhemmässä versiossa.',
            composeCta: 'Laadi luonnos',
            composing: 'Laaditaan…',
            noDraftYet: 'Ei vielä luonnosta. Laadi sellainen aloittaaksesi.',
            localeToggleLabel: 'Kieli',
            localeEn: 'Englanti',
            localeVi: 'Vietnam',
            subjectLabel: 'Aihe',
            bodyLabel: 'Sisältö',
            saveCta: 'Tallenna muutokset',
            saving: 'Tallennetaan…',
            saveError: 'Luonnosta ei voitu tallentaa. Yritä uudelleen.',
            manifestSummaryLabel: 'Manifestin yhteenveto',
            coverLetterLabel: 'Saatekirje (PDF)',
            coverLetterDownloadCta: 'Lataa PDF',
            editHistoryHeading: 'Muokkaushistoria',
            editHistoryEmpty: 'Ei vielä muokkauksia.',
            editHistoryEntry: 'v{version} · {fields} · {when} · {who}',
            unofficialChannelHeading: 'NQ22 epävirallinen kanava -ilmoitus',
            uploadSentProofCta: 'Lataa manuaalinen lähetystodiste',
            versionLabel: 'Versio',
            statusDraft: 'Luonnos',
            statusEdited: 'Muokattu',
            statusFinalized: 'Valmis',
        },
        sentProof: {
            modalTitle: 'Lataa manuaalinen lähetystodiste',
            architectureLockNote: 'AesirX ei ole lähettäjä. Tämä todiste tallentaa manuaalisen lähetyksen, jonka teit oman sähköpostiohjelmasi kautta.',
            fileLabel: 'Todistetiedosto (.eml, .png, .jpg)',
            envelopeFromLabel: 'Lähettäjä',
            envelopeToLabel: 'Vastaanottaja',
            envelopeSubjectLabel: 'Aihe (lähetettäessä)',
            envelopeSentAtLabel: 'Lähetetty',
            envelopeAttachmentShaLabel: 'Liite SHA-256 (lähetettäessä)',
            uploadCta: 'Lataa todiste',
            uploading: 'Ladataan…',
            uploadSuccess: 'Lähetystodiste ladattu.',
            uploadErrorInvalidMime: 'Tiedostotyyppi ei sallittu. Käytä .eml, .png, .jpg tai .jpeg.',
            uploadErrorGeneric: 'Todistetta ei voitu ladata. Yritä uudelleen.',
            existingProofsHeading: 'Rekisteröidyt lähetystodisteet',
            existingProofsEmpty: 'Lähetystodisteita ei ole vielä rekisteröity.',
            proofEntry: '{when} · {filename}',
        },
        packDetail: {
            pageTitle: 'Todistuspaketti',
            notFoundMessage: 'Pakettia ei löytynyt – se on saatettu poistaa tai linkki on virheellinen.',
            manifestHeading: 'Manifesti',
            dossierLabel: 'Asia',
            packShaLabel: 'Paketin SHA-256',
            fileCountLabel: 'Tiedostot',
            bytesLabel: 'Koko',
            composeDraftCta: 'Laadi luonnos',
            sendComingSoonCta: 'Suora lähetys: tulossa pian',
            sendComingSoonHint: 'Suoran lähetyksen toiminto on aktiivisessa kehityksessä. Käytä luonnossivulla kohtaa "Lataa manuaalinen lähetystodiste" siihen asti.',
            sendCta: 'Lähetä määritetyn palveluntarjoajan kautta',
            sendDisabledNoProviderHint: 'Määritä ensin asiakkaan hallinnoima sähköpostipalveluntarjoaja. AesirX ei koskaan lähetä puolestasi.',
            timelineHeading: 'Lähetyshistoria',
        },
        // Sprint 58.4.2 (Phase 25 / Sprint E2) — direct-send + timeline + authority response + hard-copy follow-up.
        send: {
            // Send dialog
            dialogTitle: 'Lähetä todistepaketti',
            providerPickerLabel: 'Määritetty sähköpostipalveluntarjoaja',
            providerPickerHint: 'AesirX ei koskaan lähetä puolestasi. Valitse jokin määritetyistä palveluntarjoajistasi alta.',
            providerPickerEmpty: 'Aktiivisia palveluntarjoajia ei ole määritetty. Määritä yksi sähköpostipalveluntarjoajien sivulla ennen lähettämistä.',
            providerPickerEmptyCta: 'Avaa sähköpostipalveluntarjoajat',
            draftPickerLabel: 'Sähköpostiluonnos',
            draftPickerOption: '{locale} · v{version}',
            draftPickerEmpty: 'Tälle paketille ei ole vielä laadittu luonnoksia. Laadi ensin luonnos luonnossivulla.',
            recipientToLabel: 'Vastaanottaja',
            recipientToHint: 'Yksi tai useampi sähköpostiosoite pilkulla erotettuna.',
            recipientCcLabel: 'Kopio (pilkuin eroteltu)',
            recipientCcHint: 'Valinnainen. Pilkulla eroteltu lista sähköpostiosoitteista.',
            recipientBccLabel: 'Piilokopio (pilkuin eroteltu)',
            recipientBccHint: 'Valinnainen. Pilkulla eroteltu lista sähköpostiosoitteista.',
            consentReaffirmHeading: 'Vahvista palveluntarjoajan suostumus uudelleen',
            submitCta: 'Lähetä nyt',
            sending: 'Lähetetään…',
            cancelCta: 'Peruuta',
            sendSuccess: 'Lähetys kirjattu. Paketti on lähetetty määritetyn palveluntarjoajasi kautta.',
            sendErrorGeneric: 'Pakettia ei voitu lähettää. Tarkista alla oleva virhe ja yritä uudelleen.',
            sendErrorAlreadyFinalized: 'Tätä luonnosta on jo käytetty onnistuneeseen lähetykseen. Laadi uusi luonnos ennen kuin lähetät uudelleen.',
            sendErrorInvalidRecipients: 'Yksi tai useampi vastaanottajaosoite ei ole kelvollinen sähköpostilaatikko.',
            // Timeline
            timelineEmpty: 'Lähetyksiä ei ole vielä kirjattu. Käytä yllä olevaa "Lähetä määritetyn palveluntarjoajan kautta" -toimintoa lähettääksesi asiakkaan hallinnoiman SMTP-, Microsoft 365- tai Google Workspace -palveluntarjoajan kautta.',
            timelineProviderLabel: 'Lähetetty määritetyn palveluntarjoajasi kautta',
            timelineSentAtLabel: 'Lähetetty',
            timelineRecipientLabel: 'Vastaanottaja',
            timelineOutcomeSent: 'Lähetetty',
            timelineOutcomeFailed: 'Epäonnistui',
            timelineViewDetailsCta: 'Näytä tiedot',
            // Authority-response chips
            authorityChipNone: 'Odotetaan vastausta',
            authorityChipAcknowledgement: 'Vastaanottoilmoitus',
            authorityChipAcceptance: 'Hyväksyntä',
            authorityChipRejection: 'Hylkäys',
            authorityChipSupplementRequest: 'Täydennyspyyntö',
            authorityChipNoResponse: 'Ei vastausta',
            // Hard-copy chips
            hardCopyChipNone: 'Ei paperista jatkokäsittelyä',
            hardCopyChipPending: 'Postitettu — toimitus kesken',
            hardCopyChipDelivered: 'Toimitettu',
            // Drawer
            drawerTitle: 'Lähetystietue',
            drawerEnvelopeHeading: 'Kirjekuori',
            drawerEnvelopeFromLabel: 'Lähettäjä',
            drawerEnvelopeToLabel: 'Vastaanottaja',
            drawerEnvelopeCcLabel: 'Kopio',
            drawerEnvelopeBccLabel: 'Piilokopio',
            drawerEnvelopeSubjectLabel: 'Aihe',
            drawerEnvelopeMessageIdLabel: 'Viesti-ID',
            drawerOutcomeHeading: 'Tulos',
            drawerSnapshotHeading: 'Säilytysketjun tilannekuva',
            drawerSnapshotPackShaLabel: 'Paketin SHA-256 lähetyshetkellä',
            drawerSnapshotDraftVersionLabel: 'Luonnoksen versio lähetyshetkellä',
            drawerSnapshotAttachmentLabel: 'Lähetetty ZIP',
            drawerSnapshotAttachmentDownloadCta: 'Lataa ZIP',
            drawerAuthorityResponseHeading: 'Viranomaisen vastaus',
            drawerHardCopyHeading: 'Paperinen jatkokäsittely',
            drawerSentByLabel: 'Lähettäjä',
            drawerSendIdLabel: 'Lähetystietueen tunnus',
            drawerSmtpResponseLabel: 'SMTP-vastaus',
            drawerFailureReasonLabel: 'Epäonnistumisen syy',
            drawerLatencyLabel: 'Viive',
            drawerLatencyValue: '{ms} ms',
            drawerAcceptedLabel: 'Hyväksytty',
            drawerRejectedLabel: 'Hylätty',
            drawerCloseCta: 'Sulje',
            // Authority-response panel
            authorityRecordCta: 'Kirjaa vastaus',
            authorityReclassifyCta: 'Luokittele uudelleen',
            authorityClassificationLabel: 'Luokittelu',
            authorityReceivedAtLabel: 'Vastaanotettu',
            authorityFreeTextLabel: 'Huomautukset (valinnainen)',
            authorityEvidenceLabel: 'Todistetiedosto (.eml / .pdf / .png / .jpg — valinnainen)',
            authorityReasonLabel: 'Uudelleenluokittelun syy',
            authorityHistoryHeading: 'Uudelleenluokitteluhistoria',
            authorityHistoryEmpty: 'Uudelleenluokitteluja ei ole kirjattu.',
            authorityHistoryEntry: '{when} · {previous} → {next} · {who}{reason}',
            authoritySubmitCta: 'Tallenna vastaus',
            authoritySubmitting: 'Tallennetaan…',
            authorityErrorInvalidMime: 'Todistetiedoston tyyppi ei ole sallittu. Käytä muotoa .eml, .pdf, .png tai .jpg.',
            authorityErrorGeneric: 'Vastausta ei voitu tallentaa. Yritä uudelleen.',
            authorityRecordedAtLabel: 'Kirjattu',
            authorityRecordedByLabel: 'Kirjaaja',
            authorityEvidenceFilenameLabel: 'Todiste',
            authorityEvidenceDownloadCta: 'Lataa todiste',
            // Hard-copy panel
            hardCopyRecordCta: 'Kirjaa paperinen jatkokäsittely',
            hardCopyConfirmCta: 'Vahvista toimitus',
            hardCopyEditNotesCta: 'Muokkaa toimitushuomautuksia',
            hardCopyPostedAtLabel: 'Postitettu',
            hardCopyCourierLabel: 'Kuriiri',
            hardCopyTrackingLabel: 'Seurantanumero',
            hardCopyRecipientLabel: 'Vastaanottaja etiketissä',
            hardCopyReceiptLabel: 'Kuittitodiste (.pdf / .png / .jpg — valinnainen)',
            hardCopyReceiptDownloadCta: 'Lataa kuitti',
            hardCopyDeliveredAtLabel: 'Toimitettu',
            hardCopyDeliveryNotesLabel: 'Toimitushuomautukset (valinnainen)',
            hardCopySubmitCta: 'Tallenna jatkokäsittely',
            hardCopySubmitting: 'Tallennetaan…',
            hardCopyErrorInvalidMime: 'Kuittitiedoston tyyppi ei ole sallittu. Käytä muotoa .pdf, .png tai .jpg.',
            hardCopyErrorGeneric: 'Jatkokäsittelyä ei voitu tallentaa. Yritä uudelleen.',
        },
    },
    // Sprint 62.53.0 — organisation-profile statements: the recorded attributes'
    // names and the applicability review a statement of "unknown" raises.
    org_profile_statements: {
        attribute_names: {
            data_exploitation_routes: 'Data exploitation routes',
            regulated_entity_types: 'Regulated entity types',
            personal_data_processing_services: 'Personal data processing services your organization provides to others as a business',
        },
        review_task: {
            title: 'Applicability review: {attribute}',
            description: 'Your organization stated that it does not know its answer for “{attribute}”. The requirements that depend on it await an applicability review and are not treated as out of scope. Record the answer on the Organization profile page.',
        },
        nav_label: 'Organization profile',
        page: {
            title: 'Organization profile',
            description: 'What your organization states about itself: the industry sectors it operates in, the data exploitation routes it takes part in and the regulated-entity role it holds. Your installed frameworks read these statements to decide which of their requirements apply to you. A requirement that depends on an answer you have not given, or have stated as unknown, awaits an applicability review and is never treated as out of scope.',
            sectors_heading: 'Industry sectors',
        },
        section: {
            // Sprint 62.57.0 Slice E (§12.6.1) — the installed declarations' item text under each value.
            value_sources_label: 'In the installed framework',
            state_legend: 'Your answer',
            state_selected: 'Selected',
            state_selected_help: 'One or more of the options below apply to your organization. Tick each one that does.',
            state_none: 'None of these',
            state_none_help: 'None of the options apply. Requirements that depend only on them read as out of scope.',
            state_unknown: 'Unknown — review needed',
            state_unknown_help: 'You do not know yet. An applicability review is added to your task list, and the requirements that depend on this answer are never treated as out of scope.',
            values_legend: 'Options that apply',
            values_disabled_hint: 'Choose “Selected” to pick the options that apply.',
            values_required: 'Choose at least one option, or choose another answer.',
            used_by_label: 'Used by',
            used_by_none: 'No installed framework names this option.',
            used_by_item: '{framework} ({count} requirements)',
            used_by_item_one: '{framework} (1 requirement)',
            used_by_item_reference: '{framework} ({count} requirements, installed for reference only)',
            used_by_item_reference_one: '{framework} (1 requirement, installed for reference only)',
            note_label: 'Note',
            note_hint: 'Optional. The basis for this answer, in your own words (up to {max} characters).',
            evidence_heading: 'Evidence',
            evidence_hint: 'Files that support this answer. A file named by a saved statement is kept for as long as the statement history exists.',
            evidence_none: 'No evidence attached.',
            evidence_not_held: 'File no longer held',
            evidence_upload_label: 'Attach evidence',
            evidence_unbind: 'Remove from statement',
            evidence_unbind_label: 'Remove {filename} from this statement',
            evidence_unbind_not_held_label: 'Remove the file that is no longer held from this statement',
            evidence_limit: 'A statement can name at most {max} files.',
            current_none: 'No statement recorded yet. Requirements that depend on this answer await an applicability review.',
            current_revision: 'Current statement: revision {revision}, recorded {date}.',
            save: 'Save statement',
            saving: 'Saving…',
            reset: 'Reset',
            saved: 'Statement saved.',
            save_failed: 'The statement could not be saved.',
            conflict: 'Someone else changed this statement while you were editing it (you read revision {expected}; it is now revision {current}). Nothing was saved, and your changes are still on screen.',
            reload_current: 'Reload the current statement',
            evidence_not_held_error: 'Some evidence files named in this statement are no longer held ({count}). Remove them from the statement and save again.',
            load_failed: 'This statement could not be read.',
            reload: 'Reload',
            reloading: 'Reloading…',
            retry_subject: 'the statement for {attribute}',
            read_only: 'You can view this statement but not change it.',
            history_show: 'Show statement history ({count})',
            history_hide: 'Hide statement history',
            history_caption: 'Statement history, newest first',
            history_revision: 'Revision',
            history_date: 'Recorded',
            history_answer: 'Answer',
            history_values: 'Options',
            history_note: 'Note',
            history_evidence: 'Evidence files',
            history_none: '—',
        },
        attribute_descriptions: {
            data_exploitation_routes: 'The routes by which your organization takes part in the exploitation of data held or offered by the State. Requirements that depend on a route apply only when you select it.',
            regulated_entity_types: 'The regulated-entity role your organization holds in the credit-cooperative and deposit-protection system, if any. Requirements addressed to a role apply only when you select it.',
            personal_data_processing_services: 'The personal data processing services your organization provides, or intends to provide, to other organizations as a business. Select only what your organization does: nothing is filled in from your other records. Requirements that depend on a service apply only when you select it.',
        },
        value_labels: {
            data_exploitation_routes: {
                public_data_access_request: 'Requesting access to State-held data',
                state_data_centre_exploitation: 'Exploiting data at a State data centre',
                state_data_incubator: 'Taking part in a State data incubator',
                data_controlled_testing: 'Controlled testing of a data solution',
                state_data_centre_data_sharing: 'Sharing data with a State data centre',
                state_data_centre_investment: 'Investing in or sponsoring a State data centre',
                foreign_party_data_cooperation: 'Data cooperation with a foreign party',
                state_data_incentive_application: 'Applying for a State data incentive',
            },
            regulated_entity_types: {
                credit_cooperative: 'People’s credit fund',
                cooperative_central_bank: 'Cooperative bank',
                deposit_insurer: 'Deposit insurer',
                microfinance_institution: 'Microfinance institution',
            },
            personal_data_processing_services: {
                automated_processing_on_behalf_of_controllers: 'Providing and operating automated systems or software that process personal data on behalf of a controller',
                creditworthiness_scoring: 'Scoring or rating creditworthiness from personal data',
                online_collection_and_processing: 'Collecting and processing personal data online for others',
                health_service_data_processing: 'Processing personal data in health services',
                education_monitoring_data_processing: 'Processing personal data to monitor learners in education services',
                personal_data_analytics_and_mining: 'Analysing or mining personal data',
                personal_data_encryption: 'Encrypting personal data for others',
                automated_processing_emerging_technologies: 'Automated processing of personal data with emerging technologies',
                location_data_platform: 'Operating a platform that processes location data',
            },
        },
        value_help: {
            data_exploitation_routes: {
                public_data_access_request: 'Your organization requests access to, or the exploitation of, data held by State bodies through a formal request.',
                state_data_centre_exploitation: 'Your organization exploits data that a State data creation and exploitation centre makes available.',
                state_data_incubator: 'Your organization takes part in a data incubation programme the State runs.',
                data_controlled_testing: 'Your organization tests a data product, service or solution under a controlled-testing mechanism.',
                state_data_centre_data_sharing: 'Your organization supplies or shares data with a State data centre, without exploiting the centre’s data.',
                state_data_centre_investment: 'Your organization invests in, contributes assets to or sponsors a State data centre, without exploiting the centre’s data.',
                foreign_party_data_cooperation: 'Your organization cooperates with a foreign organization or individual in these data activities.',
                state_data_incentive_application: 'Your organization applies for, or receives, a State incentive or support for data activities. Applying is not exploiting data.',
            },
            regulated_entity_types: {
                credit_cooperative: 'Your organization is a credit institution organised as a cooperative that takes deposits from and lends to its members.',
                cooperative_central_bank: 'Your organization is the cooperative bank of the credit-cooperative system.',
                deposit_insurer: 'Your organization insures the deposits held at credit institutions.',
                microfinance_institution: 'Your organization is a microfinance institution.',
            },
            personal_data_processing_services: {
                automated_processing_on_behalf_of_controllers: 'Your organization provides or operates a system or software that processes personal data automatically for a controller.',
                creditworthiness_scoring: 'Your organization scores or rates the creditworthiness of people for others, using their personal data.',
                online_collection_and_processing: 'Your organization collects and processes personal data online as a service to other organizations.',
                health_service_data_processing: 'Your organization processes personal data as part of the health services it provides to others.',
                education_monitoring_data_processing: 'Your organization processes personal data to monitor learners as part of the education services it provides to others.',
                personal_data_analytics_and_mining: 'Your organization analyses or mines personal data as a service to other organizations.',
                personal_data_encryption: 'Your organization encrypts personal data as a service to other organizations.',
                automated_processing_emerging_technologies: 'Your organization processes personal data automatically using emerging technologies, as a service to others.',
                location_data_platform: 'Your organization operates a platform that processes location data for others.',
            },
        },
        review: {
            title: 'Applicability review',
            description: 'Requirements of your installed frameworks that wait on an answer on this page. Each one applies, or is out of scope, once the answer is recorded; until then it is not treated as out of scope.',
            state_not_stated: 'No statement recorded',
            state_selected: 'Selected',
            state_none: 'None of these',
            state_unknown: 'Unknown — review needed',
            group_state: 'Current answer: {state}',
            group_count: '{count} requirements await this answer.',
            group_count_one: '1 requirement awaits this answer.',
            group_empty: 'No requirement of an installed framework awaits this answer.',
            group_empty_reference: 'No requirement of a directly applicable framework awaits this answer.',
            reference_intro: 'These frameworks are installed for reference only, so their requirements are not applied to your organization. If they were installed as directly applicable, these requirements would await this answer:',
            reference_item: '{framework} ({count} requirements)',
            reference_item_one: '{framework} (1 requirement)',
            column_framework: 'Framework',
            column_requirement: 'Requirement',
            load_failed: 'The applicability review could not be read.',
            retry_subject: 'the applicability review',
        },
        outcome: {
            // Sprint 62.57.0 Slice E (D48) — an unstated condition read as out of scope.
            out_of_scope_not_stated: 'Applies only where your organization has recorded it (none recorded)',
            column: 'Applies to your organization',
            applicable: 'Applies',
            out_of_scope: 'Out of scope',
            not_applicable: 'Reference only — not assigned to any organization',
            installed_for_reference: 'Installed for reference only — not applied to your organization',
            not_entitled: 'Not in your package — not applied to your organization',
            not_declared: 'Not declared',
            profile_incomplete: 'Awaiting your organization profile',
            profile_incomplete_named: 'Awaiting your organization profile: {attributes}',
            attributes: {
                sector: 'industry sectors',
                data_exploitation_routes: 'data exploitation routes',
                regulated_entity_types: 'regulated entity types',
                personal_data_processing_services: 'personal data processing services provided to others',
            },
        },
    },
    // Sprint 63.37.0 (E0, E5) — English until the Phase 25 locale pass (R-57).
    org_identity: {
        nav_label: 'Organisation identity',
        module_guide: 'Organisation identity: the legal name, offices, registration, tax code, contacts and legal representative that official forms and cover letters are filled from.',
        title: 'Organisation identity',
        description: "Record your organisation's legal name, offices, registration, tax code, contacts and legal representative once. Official forms are filled from this record when you choose Fill from record, and cover letters read it too.",
        section_names: 'Names and offices',
        section_registration: 'Registration',
        section_contact: 'Contact details',
        section_representative: 'Legal representative',
        field_legal_name: 'Legal name',
        field_legal_name_help: 'The name exactly as it appears on the registration certificate.',
        field_trading_name: 'Trading name',
        field_trading_name_help: 'A short or trading name, if the organisation uses one.',
        field_head_office_address: 'Head office address',
        field_transaction_office_address: 'Transaction office address',
        field_transaction_office_address_help: 'Leave blank if it is the same as the head office.',
        field_registration_number: 'Registration Number',
        field_registration_instrument: 'Registration document',
        field_registration_instrument_help: 'The document that records the registration, for example a business registration certificate or an establishment decision.',
        field_registration_issuer: 'Issued by',
        field_registration_issue_date: 'Date of issue',
        field_registration_issue_place: 'Place of issue',
        field_tax_code: 'Tax Code',
        field_tax_code_help: 'The tax code is recorded separately from the registration number, even where the two are the same.',
        field_phone: 'Phone',
        field_email: 'Email',
        field_website: 'Website',
        field_website_help: 'Starting with https:// or http://.',
        field_legal_representative_name: 'Full name',
        field_legal_representative_title: 'Position',
        save: 'Save identity',
        saving: 'Saving…',
        saved: 'Organisation identity saved.',
        save_failed: 'The organisation identity could not be saved.',
        fix_fields: 'Some fields need correcting before the identity can be saved.',
        load_failed: 'The organisation identity could not be read.',
        retry: 'Retry',
        conflict_message: 'Someone else saved the organisation identity after you opened this page. Your changes were not saved. Reload the saved identity, then make your changes again.',
        conflict_reload: 'Reload the saved identity',
        conflict_reloaded: 'The saved identity is shown. Make your changes again, then save.',
        loading: 'Loading the organisation identity',
        read_only: 'You can view the organisation identity. Saving it needs the settings write permission.',
        restricted_title: 'You do not have access to the organisation identity',
        restricted_message: 'Ask an administrator for the settings read permission.',
        last_saved: 'Last saved {date}',
        never_saved: 'Nothing has been recorded yet. Fields left blank are left blank on forms too.',
        error_too_long: 'This value is too long.',
        error_invalid_date: 'Enter a real date.',
        error_invalid_email: 'Enter an email address, for example name@example.com.',
        error_invalid_url: 'Enter a web address starting with https:// or http://.',
        error_invalid_phone: 'Use digits, spaces and + ( ) . - only.',
        error_invalid: 'This value is not accepted.',
    },
    form_prefill: {
        draft_undeclared: "The form does not say where {count} of the draft's fields go, so they were not drafted.",
        fill_from_record: 'Fill from record',
        fill_from_named_record: 'Fill from {record}',
        filling: 'Filling…',
        result: 'Filled {filled} fields; {differs} differ from the record.',
        failed: 'The form could not be filled from the record.',
        from_source: 'From {source}',
        not_recorded_in_source: 'Not recorded in {source}',
        family_organisation: 'the organisation identity',
        family_dpo: 'the DPO appointment',
        family_dpia: 'the DPIA',
        family_transfer: 'the transfer assessment',
        family_incident: 'the incident',
        family_dossier: 'the dossier filing',
        record_generic: 'the record',
        hint: 'Fills empty fields only. A value you entered is never replaced.',
    },
    // Owner rulings 2026-10-26 — idea #717 and OQ-86-6. English until the
    // Phase 25 locale work (R-57); structural parity with en.ts.
    customs_tax_exports: {
        titles: {
            dossiers: 'Customs tax dossiers and documents received',
            payments: 'Customs tax payments',
            duty_free_lists: 'Duty-free lists',
            examinations: 'Customs tax examinations',
            debt_instruments: 'Customs tax debt instruments',
            transition_statement: 'Transition statement',
        },
        sections: {
            dossiers: 'Dossiers',
            documents_received: 'Documents received',
            figures_side_by_side: 'Stated figures side by side',
            deductions: 'Deductions as customs recorded them',
            usage_notifications: 'Usage notifications recorded',
            steps: 'Steps',
            office_examinations: 'At the customs office',
            determinations: 'Determinations recorded',
            refund_records: 'Refund records',
            enforcement_notices: 'Enforcement notices received',
            instalment_payments: 'Payments recorded against instalment notices (each as its document states it; no total)',
            noted_alternatives: 'Noted alternative readings of the usage schedule',
        },
        columns: {
            paper_form_number: 'Paper form number',
            electronic_indicator_set: 'Electronic indicator set',
            matched_by_title: 'Matched by title',
            issued_by_authority: 'Issued by the authority',
            dossier: 'Dossier',
            procedure: 'Procedure',
            register_role: 'Register role',
            customs_records: 'Customs records',
            channel: 'Channel',
            signed_off_on: 'Signed off on',
            lodged_on: 'Lodged on',
            confirmation_answer: 'Confirmation answer',
            install_mode_when_made: 'Install mode when made',
            review_approval: 'Review approval',
            document: 'Document',
            kind: 'Kind',
            number_as_printed: 'Number as printed',
            received_on: 'Received on',
            stated_figures: 'Stated figures',
            stated_deadlines: 'Stated deadlines',
            reply: 'Reply',
            payment: 'Payment',
            paid_on: 'Paid on',
            stated_amount: 'Stated amount',
            payer_class: 'Payer class',
            payment_date_basis: 'Payment-date basis',
            document_number: 'Document number',
            pays: 'Pays',
            receipt: 'Receipt',
            received_document: 'Received document',
            figure: 'Figure',
            payment_figure_as_stated: 'Payment figure (as stated)',
            document_figure_as_stated: 'Document figure (as stated)',
            comparison: 'Comparison',
            closed: 'Closed',
            list: 'List',
            reference: 'Reference',
            acknowledged_on: 'Acknowledged on',
            imports_ended_on: 'Imports ended on',
            expires_on: 'Expires on',
            deduction: 'Deduction',
            customs_record: 'Customs record',
            goods_as_stated: 'Goods as stated',
            unit_as_stated: 'Unit as stated',
            cleared_as_stated: 'Cleared (as stated)',
            remaining_as_stated: 'Remaining (as stated)',
            notification: 'Notification',
            notice_kind: 'Notice kind',
            financial_year_ended_on: 'Financial year ended on',
            notified_on: 'Notified on',
            examination: 'Examination',
            purpose: 'Purpose',
            decision_signed_on: 'Decision signed on',
            decision_sent_on: 'Decision sent on',
            announced_on: 'Announced on',
            re_examination_of: 'Re-examination of',
            step: 'Step',
            occurred_on: 'Occurred on',
            refused_to_sign: 'Refused to sign',
            record: 'Record',
            authority_request: 'Authority request',
            explanation_sent_on: 'Explanation sent on',
            outcome_on: 'Outcome on',
            instrument: 'Instrument',
            role: 'Role',
            effective_on: 'Effective on',
            instalment_band: 'Instalment band',
            approved_on: 'Approved on',
            stated_debt_figure: 'Stated debt figure',
            guarantee_valid_until: 'Guarantee valid until',
            record_type: 'Record type',
            governing_date: 'Governing date',
            outcome: 'Outcome',
            reading: 'Reading',
            open: 'Open',
            determination: 'Determination',
            determined_on: 'Determined on',
            refund_record: 'Refund record',
            dossier_register_role: 'Dossier register role',
            classification: 'Classification',
            decision_document: 'Decision document',
            offset_notice_document: 'Offset notice received',
            post_refund_examination: 'Post-refund examination',
            stated_case: 'Case as the decision states it',
            stated_case_source: 'Case stated in',
            stated_case_citation: 'Provision cited for the case',
            received_as: 'Received as',
            document_date: 'Document date',
            amount_stated_for_notice: 'Amount stated for this notice',
            financial_year_reported: 'Financial year reported (ending)',
            noted_alternative_year: 'Noted alternative financial year (ending)',
            note: 'Note',
            reading_kind: 'Kind of reading',
            classification_case: 'Case the classification notice states',
            acceptance_notice: "Acceptance notice (the authority's clock)",
            handling_time: "Handling time (the authority's clock)",
            stated_case_choices: 'Installed cases matched (as stated)',
            stated_due_on: 'Due date as stated',
            period_ceiling: 'Longest instalment period',
            stated_plan_end: 'Latest end the Law allows',
            enforcement_from: "Enforcement threshold (the authority's clock)",
        },
        cells: {
            review_approved: 'Approved by {name} on {date}',
            review_approved_earlier_version: 'Approved by {name} on {date}, against an earlier version of the record',
            register_role_refund: 'Refund',
            register_role_overpaid_refund: 'Refund of an overpaid amount',
            noted_not_due: 'Noted reading, not a due date',
            received_as_third_party: 'Received as a third party',
            received_as_enforced_taxpayer: 'The taxpayer under enforcement',
            clock_authority_ends: "The authority's clock ends {date} ({clock})",
            clock_authority_ends_before_excluded: "The authority's clock ends {date} before the time not counted is added ({clock})",
            clock_authority_no_date: "The authority's clock; no date, because the event it runs from is not recorded ({clock})",
            clock_period_ends: 'The period ends {date} ({clock})',
            clock_closed: 'Closed on {date}',
            clock_not_applicable: 'Not applicable to this record ({clock})',
            clock_not_live: 'Not in force yet ({clock})',
            clock_classification_not_recorded: 'No date: the classification the authority notified is not recorded',
            clock_not_declared: 'No installed clock declares it',
            clock_instrument_not_installed: 'Stated in an instrument that is not installed ({instruments}); no date',
            clock_no_date: 'No date ({clock})',
            plan_end_by_ceiling: "{date}, the end of the longest period; a shorter schedule in the written acceptance is the plan's own",
            plan_end_by_guarantee: "{date}, the end of the guarantee's validity, which comes first; a shorter schedule in the written acceptance is the plan's own",
            clock_not_counted_before_commencement: 'Not counted by the platform: debts outstanding at the end of {outstanding} follow the Law ({reference}), but no point is shown for a due date before {activation}',
            clock_not_counted_before_commencement_unnamed: 'Not counted by the platform: no point is shown for a due date before {activation}',
            meta_boundary: 'Boundary ({framework}): {summary}',
            meta_boundary_none: 'Boundary: no installed framework declares a boundary statement for these registers.',
            meta_amounts_as_stated: 'Every amount is a figure a person entered from a document; nothing here is computed.',
            meta_statement_for: 'Statement for: {date}',
            meta_nothing_remains: 'Nothing remains open under the earlier rules.',
            meta_records_remain: 'Records remain open under the earlier rules.',
            meta_transition_unavailable: 'The transition reading is not available on this installation.',
            role_notified_debt: 'Tax debt as notified',
            role_freeze: 'Freeze',
            role_freeze_end: 'End of a freeze',
            role_write_off: 'Write-off',
            role_restoration: 'Restoration',
            role_instalment_request: 'Instalment request',
            role_instalment_notice: 'Instalment notice',
            received_document_label: '{kind} {number}, dated {date}',
            received_document_label_no_number: '{kind}, received {date}',
            case_no_longer_installed: 'A case from an instrument that is no longer installed',
        },
    },
    cross_border_filing_fence: {
        register_customs_tax_dossiers: 'Tax Dossiers and Received Documents',
        refused: 'This form is recorded in {register}, not as a cross-border filing. Record the dossier there.',
        open_register: 'Open {register}',
        option_owned_elsewhere: '{form} — recorded in {register}',
        picker_hint: 'Some installed forms are recorded in {register} and cannot be chosen here.',
        advisory_title: 'This form is now recorded in {register}',
        advisory_body: 'This filing was recorded here before forms like it moved to {register}. It stays readable here and has not been changed. Record new dossiers on this form in {register}.',
        list_marker: 'Now recorded in {register}',
    },
    // Sprint 63.43.0 — English for key parity until Phase 25 (R-57; sprint 57.7.0).
    sector_overlays_module: {
        nav: {
            overview: 'Overview',
        },
        overview: {
            title: 'Sector Overlays',
            subtitle: 'What your active sector overlays bring into use, grouped by sector.',
            loading: 'Loading sector overlays…',
            load_failed: 'Sector overlays could not be loaded.',
            not_entitled_title: 'Sector Overlays is not in your package',
            not_entitled_body: "Sector Overlays is an Enterprise module. Your organization's package does not include it, so no sector overlay is shown here.",
            empty_title: 'No sector overlay is active',
            empty_body: 'Install an overlay in the Overlay Catalog and choose Direct to bring its views into use here. An overlay installed for reference only adds no views.',
            open_catalog: 'Open the Overlay Catalog',
            organization_profile: 'Organization profile',
            organization_profile_hint: "Your organization's sectors decide which overlays apply to you directly.",
            provided_by: 'Provided by',
            views_heading: 'Views in this sector',
            shared_views_heading: 'Shared views this sector uses',
            shared_view_location: 'In {location}',
            open_view_aria: '{view} — {location}',
            no_visible_views: "None of this sector's views is open to your role.",
            sector_count_one: '1 overlay active',
            sector_count: '{count} overlays active',
        },
    },
    // Sprint 62.47.0 — English for key parity until Phase 25 (R-57; sprint 57.7.0).
    product_identification: {
        common: {
            retry: 'Retry',
            loading: 'Loading…',
            cancel: 'Cancel',
            close: 'Close',
            no_access_title: 'No access',
            no_access_message: 'Your role cannot read this register.',
            install_direct: 'Installed Direct',
            install_reference: 'Installed for reference',
            reference_mode_body: 'The overlay that declares this register is installed for reference. Scope determinations can still be recorded and read here, and the sector watch read; no duty and no clock is run from them until the overlay is installed Direct from the Overlay Catalog.',
        },
        participation: {
            compulsory: 'Compulsory',
            voluntary: 'Voluntary',
            outside_scope: 'Outside the scope',
            compulsory_rules_disapplied: 'Compulsory rules disapplied',
            not_determined: 'Open determination',
            unclassified: 'No class declared',
        },
        scope: {
            title: 'Product Scope Determinations',
            subtitle: 'A person’s recorded reading, per product line, of whether the identification and traceability regime reaches it, with the instrument relied on and a second person’s review. This register records the reading; it never decides it.',
            page_guide: 'Record one determination per product line from the options the installed pack declares. Nothing is preselected or suggested. Recording again keeps the earlier version, and compulsory and voluntary lines are always counted separately.',
            load_failed: 'The scope determinations could not be loaded.',
            none_declared: 'No installed framework declares the scope-determination register.',
            applicability_heading: 'Who the regime reaches, as the installed pack states it',
            mandatory_scope: 'Compulsory',
            voluntary_scope: 'Voluntary',
            out_of_scope: 'Outside the scope',
            mandatory_rules_disapplied: 'Compulsory rules disapplied',
            list_status_note: 'Status of the lists',
            counts_heading: 'Product lines by class',
            filter_label: 'Class',
            filter_all: 'All classes',
            table_caption: 'Current determination per product line',
            col_product: 'Product line',
            col_class: 'Class',
            col_option: 'Determination',
            col_instrument: 'Instrument relied on',
            col_determined_by: 'Determined by',
            col_reviewed_by: 'Reviewed by',
            col_determined_on: 'Determined on',
            col_version: 'Version',
            col_actions: 'Actions',
            empty: 'No determination is recorded yet.',
            empty_filtered: 'No product line is in this class.',
            record_button: 'Record a determination',
            record_again: 'Record again',
            history_button: 'History',
            duties_button: 'Duties',
            form_title: 'Record a scope determination',
            field_product: 'Product line',
            field_framework: 'Framework',
            choose: 'Choose…',
            no_default_note: 'Nothing is preselected and nothing is suggested: the reading is yours.',
            instrument_required: 'Required for the determination chosen.',
            cited_basis_note_heading: 'The installed pack’s reading of the lists',
            reviewer_same: 'The reviewer must be a person other than the one who determined it.',
            field_required: 'This field is required.',
            save: 'Save',
            saving: 'Saving…',
            save_failed: 'The determination could not be saved.',
            saved: 'Determination recorded.',
            products_failed: 'The product lines could not be loaded.',
            no_products: 'No product line is in the product register yet.',
            conflict: 'Someone recorded a determination for this line meanwhile. Reload the register and try again.',
            history_title: 'Determination history — {product}',
            history_failed: 'The history could not be loaded.',
            status_current: 'Current',
            status_superseded: 'Superseded',
            duties_title: 'Duties for {product}',
            duties_failed: 'The duties could not be loaded.',
            duties_no_task: "No task or deadline is created here from these duties. Where the decree states a period, the requirement's own text states it.",
            duties_reference: 'Installed for reference: no duty is shown. Install the overlay Direct to run them.',
            duties_compulsory_heading: 'Compulsory',
            duties_voluntary_heading: 'Voluntary',
            duties_role_heading: 'Attached to a role',
            duties_open: 'Open determination: the scope of this line is still to be determined. This is not a finding that no duty applies.',
            duties_counts: 'Compulsory: {applies} · Applies to how you take part (voluntary): {voluntary}',
            duties_none: 'No requirement is declared.',
            col_requirement: 'Requirement',
            col_state: 'Status for this line',
            col_reference: 'Reference',
        },
        duty_state: {
            applies: 'Applies (compulsory)',
            applies_voluntarily: 'Applies to how you take part (voluntary)',
            not_raised_by_option: 'Not raised by this determination',
            open_determination: 'Open determination',
            role_attached: 'Attached to a role; read its condition',
            voluntary_practice: 'Voluntary practice',
        },
        watch: {
            title: 'Sector Watch',
            subtitle: 'What the installed pack tracks as still to be issued by an authority, with the status it records and when it must be checked again, and the clocks the pack places on authorities. Nothing here is owed by your organisation.',
            page_guide: 'Read each tracked output as the installed pack states it. A recheck date that has passed reads as recheck overdue; nothing is re-dated. The clocks on authorities run on the authority, not on your organisation.',
            load_failed: 'The sector watch could not be loaded.',
            items_heading: 'Outputs still to be issued',
            items_caption: 'Tracked outputs, as the installed pack states them',
            col_output: 'Output',
            col_status: 'Status',
            col_basis: 'Basis of the status',
            col_checked: 'Checked on',
            col_recheck: 'Recheck due',
            col_install: 'Install mode',
            status_not_issued: 'Not issued',
            status_not_retrieved: 'Named, text not retrieved',
            status_announced_operational: 'Announced operational',
            status_issued: 'Issued',
            recheck_overdue: 'Recheck overdue',
            existing_basis: 'Instruments recorded as the existing basis',
            note_label: 'Note',
            items_none: 'No installed framework tracks an output for this register.',
            clocks_heading: 'Clocks on authorities and periods not stated',
            clocks_caption: 'Records the installed pack places on authorities, or whose period it does not state',
            col_clock: 'Record',
            col_kind: 'What it means for your organisation',
            col_period: 'Period as stated',
            col_binds: 'Applies from',
            authority_clock: 'Clock on the ministry — nothing is owed by the organisation',
            set_by_instrument: 'Set by the ministry instrument or sector law',
            no_period: 'No period stated',
            period_months: '{n} months',
            period_days: '{n} days',
            period_other: '{n} ({unit})',
            not_stated: 'Not stated',
            clocks_none: 'No clock on an authority is declared.',
        },
    },
    // Sprint 62.57.0 Slice E (§12.6) — the processing-service certificate register.
    service_certificates: enServiceCertificates,
    evidence_reports: {
        intendedFilingReadinessHint: 'A prospective filing date is required for statutory readiness for a new dossier. You may leave it blank to save a draft or gap report. An existing dossier filing can supply the basis.',
        captureUnconfirmed: 'Capture may still be running. Retry this same capture to check or recover it; a completed version is reused.',
        retryCapture: 'Check or retry this capture',
        sampleEvidenceLimitation: 'Required content may be complete, but admitted evidence is synthetic sample data and cannot establish readiness for statutory filing. Internal or training exports remain available.',
        referenceRendition: 'Reference rendition',
        languagePreservationHint: 'Language selection changes headings and labels. Saved answers and evidence retain their authored language. This output does not translate or certify the supplied text.',
        title: 'Prepare an evidence report',
        intro: 'Define the question and scope, review actual records, and freeze an attributable report. Blank templates do not demonstrate completed work.',
        legacy: 'Legacy catalog export',
        legacyHint: 'Catalog exports contain framework references. They are not reviewed evidence reports.',
        name: 'Report title',
        framework: 'Installed framework',
        purpose: 'Purpose',
        internal_readiness: 'Internal readiness review',
        authority_request: 'Response to an authority request',
        processing_assessment: 'Processing assessment dossier',
        transfer_assessment: 'Transfer assessment dossier',
        dossier_update: 'Dossier update',
        assessmentKind: 'Assessment type',
        dpia: 'Processing assessment',
        otia: 'Transfer assessment',
        applicant: 'Applicant',
        organization: 'Organization',
        individual: 'Individual',
        role: 'Organization role',
        controller: 'Controller',
        controller_processor: 'Controller and processor',
        processor: 'Processor',
        other: 'Other',
        coverage: 'Coverage',
        bounded: 'Selected activity / bounded scope',
        organization_complete: 'Complete organization and processing lifetime',
        coverageStatement: 'Scope and coverage statement',
        coverageHint: 'State what this report covers and excludes. A selected activity does not establish organization-wide statutory coverage.',
        recipient: 'Intended recipient',
        questions: 'Request questions (one per line)',
        language: 'Report language',
        en: 'English',
        vi: 'Vietnamese',
        asOf: 'Evidence as of',
        timezone: 'IANA timezone',
        periodStart: 'Period start (optional)',
        periodEnd: 'Period end (optional)',
        intendedFiling: 'Intended filing date',
        filing: 'Existing dossier filing',
        sources: 'Select actual source records',
        sourceKind: 'Source type',
        sourceSearch: 'Search authorized source records',
        sourceHint: 'Only existing records you may read are offered. Select none for an honest gap report. Search or change page to reach further records.',
        search: 'Search',
        next: 'Next page',
        previous: 'Previous page',
        remove: 'Remove',
        review: 'Review evidence and requirements',
        preview: 'Refresh review and preview',
        freeze: 'Freeze report and generate files',
        busy: 'Working…',
        failed: 'The request failed. Your entries have been retained.',
        required: 'Complete the required fields before continuing.',
        emptySources: 'No source records selected. Missing content remains explicit.',
        candidate: 'Source evidence',
        noCandidates: 'No accessible evidence supplied. This report contains gaps, not proof of completed work.',
        openSource: 'Open source record',
        inspect: 'Inspect captured content',
        sourceRevision: 'Source revision',
        state: 'Source state',
        include: 'Include',
        exclude: 'Exclude',
        decision: 'Evidence decision',
        reason: 'Reason for decision',
        requirementLinks: 'Requirements supported',
        requirements: 'Requirement assessment',
        applicability: 'Applicability',
        applicable: 'Applicable',
        not_applicable: 'Not applicable',
        undetermined: 'Undetermined',
        adequacy: 'Content adequacy',
        unreviewed: 'Unreviewed',
        adequate: 'Adequate',
        partial: 'Partial',
        unsupported: 'Unsupported',
        missing: 'Missing content',
        present: 'Content present',
        supported: 'Supported requirements',
        blockers: 'Mandatory blockers',
        ready: 'Ready for selected purpose',
        notReady: 'Not ready for selected purpose',
        statutoryReady: 'Statutory content readiness',
        readinessHint: 'Content readiness, internal approval, dispatch, official receipt and authority outcome are separate decisions. An adverse finding can be part of a complete assessment.',
        diagnostics: 'Profile and source limitations',
        supplement: 'Supplemental assessment content',
        conclusions: 'Assessment conclusions and residual risks',
        limitations: 'Limitations and unresolved questions',
        disclosure: 'Release decision',
        full: 'Release full reviewed content',
        redacted_release: 'Release selected evidence only',
        releaseReason: 'Reason and recipient disclosure review',
        releaseEvidence: 'Evidence permitted in the released copy',
        gapTask: 'Assign gap action',
        taskTitle: 'Action title',
        assignee: 'Responsible person',
        dueDate: 'Due date',
        createTask: 'Create task',
        taskCreated: 'Gap action created',
        openTask: 'Open task',
        taskHint: 'Completing a task does not establish adequate evidence. Return and refresh after the source record has been corrected.',
        previewTitle: 'Readable report preview',
        version: 'Version',
        snapshot: 'Frozen report',
        capturedAt: 'Captured at',
        capturedBy: 'Captured by',
        reviewedBy: 'Reviewed by',
        manifestHash: 'Snapshot manifest hash',
        files: 'Report and evidence files',
        download: 'Download',
        noFiles: 'Files are not available for this version.',
        newVersion: 'Revise as a new version',
        frozenHint: 'This is the frozen version. Changes require a new preview and version; existing approval applies only to its exact version.',
        pending: 'Files pending',
        finalized: 'Files finalized',
        failedState: 'File generation failed',
        sourceContent: 'Captured source content',
        attachments: 'Source attachments',
        close: 'Close',
        saveReview: 'Apply review to preview',
        profile: 'Assessment profile',
        noProfile: 'No supported assessment profile is installed for this selection.',
        returnReview: 'Return to review',
        create: 'Create report',
        cancel: 'Cancel',
        dpiaSource: 'Processing assessments',
        transfer_assessmentSource: 'Transfer assessments',
        processing_activitySource: 'Processing activities',
        systemSource: 'Systems',
        vendorSource: 'Vendors',
        consentSource: 'Consent records',
        rights_requestSource: 'Rights requests',
        incidentSource: 'Incidents',
        form_instanceSource: 'Completed / draft form instances',
        dossierFormsSource: 'Valmiit asiakirja-aineiston raportit ja ilmoitukset',
        attachmentSource: 'Attachments',
        evidenceSource: 'Evidence records',
        selected: 'Selected source records',
        sourceUnavailable: 'Source unavailable or permission restricted',
        supplementHint: 'Provide the missing substantive assessment content. Empty content remains a gap.',
        choose: 'Choose…',
        page: 'Page',
        exemption: 'Assert an exemption',
        exemptionReason: 'Exemption grounds and supporting evidence',
        approvalStatus: 'Internal approval / dispatch status',
        scope: 'Report scope',
        sourceOutcomes: 'Source retrieval outcomes',
        openReportForm: 'Complete the assessment report form',
        openNoticeForm: 'Complete the applicant notice',
        formHint: 'Complete and finalize these installed forms, then select their saved instances as source records. Opening a blank form does not supply evidence.',
        reportTitle: 'Evidence report',
        final: 'Final saved content',
        draft: 'Draft content',
        historical: 'Historical content',
        unverified: 'Unverified source',
        sourceStatusOther: 'Other status',
        captureCurrent: 'Capture current saved content',
        captureHint: 'New or corrected records saved after this instant are outside this report. Keep this time for a historical report, or update it deliberately to include current saved content.',
        sourceFacts: 'Recorded facts',
        provenance: 'Source provenance',
        approveReportConfirm: 'Approve version {version} for release to the report’s intended recipient? This records your approval; it does not record statutory filing, official receipt or an authority decision.',
        consent_evidenceSource: 'Consent receipts and proofs',
        documentRoles: 'Supporting document components',
        historicalUse: 'Use historical evidence for this reporting period',
        historicalHint: 'Historical evidence can support a bounded internal review or authority response only when dated admitted supporting files establish validity throughout the selected period. It does not establish current statutory readiness.',
        historicalReason: 'Reason for using historical evidence',
        validFrom: 'Valid from (ISO date and time with offset)',
        validUntil: 'Valid until (ISO date and time with offset)',
        historicalProof: 'Dated supporting files admitted to this report',
        savedContentErrors: 'Saved form content requires correction',
        historicalCoverageError: 'The stated validity range must cover the entire reporting period.',
        linkTask: 'Link an existing task',
    },
};
//# sourceMappingURL=fi.js.map